import { z } from 'zod';

// src/types/orbital.ts
var ID_PREFIXES = {
  orbital: "orb_",
  entity: "ent_",
  trait: "trt_",
  event: "evt_",
  page: "pag_",
  service: "svc_",
  theme: "thm_",
  palette: "pal_"
};
function brand(value) {
  return value;
}
function makeIdKind(kind) {
  const prefix = ID_PREFIXES[kind];
  const is = (value) => value.startsWith(prefix) && value.length > prefix.length;
  const as = (value) => {
    if (!is(value)) {
      throw new Error(
        `Expected ${kind} id (prefix "${prefix}"), got: ${JSON.stringify(value)}`
      );
    }
    return brand(value);
  };
  return { prefix, is, as };
}
var orbitalKind = makeIdKind("orbital");
var entityKind = makeIdKind("entity");
var traitKind = makeIdKind("trait");
var eventKind = makeIdKind("event");
var pageKind = makeIdKind("page");
var serviceKind = makeIdKind("service");
var themeKind = makeIdKind("theme");
var paletteKind = makeIdKind("palette");
var isOrbitalId = orbitalKind.is;
var asOrbitalId = orbitalKind.as;
var isEntityId = entityKind.is;
var asEntityId = entityKind.as;
var isTraitId = traitKind.is;
var asTraitId = traitKind.as;
var isEventId = eventKind.is;
var asEventId = eventKind.as;
var isPageId = pageKind.is;
var asPageId = pageKind.as;
var isServiceId = serviceKind.is;
var asServiceId = serviceKind.as;
var isThemeId = themeKind.is;
var asThemeId = themeKind.as;
var isPaletteEntryId = paletteKind.is;
var asPaletteEntryId = paletteKind.as;
function idPrefix(kind) {
  return ID_PREFIXES[kind];
}
function idKindOf(id) {
  for (const kind of Object.keys(ID_PREFIXES)) {
    const prefix = ID_PREFIXES[kind];
    if (id.startsWith(prefix) && id.length > prefix.length) return kind;
  }
  return null;
}
var CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
var TIME_LEN = 10;
var RANDOM_LEN = 16;
function randomBytes(len) {
  const g = globalThis;
  if (g.crypto?.getRandomValues) {
    return g.crypto.getRandomValues(new Uint8Array(len));
  }
  throw new Error("mintId: no crypto.getRandomValues available in this runtime");
}
function encodeTime(now) {
  let value = now;
  let out = "";
  for (let i = 0; i < TIME_LEN; i++) {
    const mod = value % 32;
    out = CROCKFORD[mod] + out;
    value = (value - mod) / 32;
  }
  return out;
}
function encodeRandom() {
  const bytes = randomBytes(RANDOM_LEN);
  let out = "";
  for (let i = 0; i < RANDOM_LEN; i++) {
    out += CROCKFORD[bytes[i] & 31];
  }
  return out;
}
function ulid() {
  return encodeTime(Date.now()) + encodeRandom();
}
function mintId(kind) {
  return brand(ID_PREFIXES[kind] + ulid());
}
var U64_MASK = (1n << 64n) - 1n;
var BODY_TIME_MASK = (1n << 50n) - 1n;
var BODY_RANDOM_MASK = (1n << 80n) - 1n;
var FNV_PRIME_64 = 0x100000001b3n;
var FNV_OFFSET_1 = 0xcbf29ce484222325n;
var FNV_OFFSET_2 = (0x100000001b3n ^ 0xdeadbeefn) & U64_MASK;
function splitmix64(x) {
  let z27 = x + 0x9e3779b97f4a7c15n & U64_MASK;
  z27 = (z27 ^ z27 >> 30n) * 0xbf58476d1ce4e5b9n & U64_MASK;
  z27 = (z27 ^ z27 >> 27n) * 0x94d049bb133111ebn & U64_MASK;
  return (z27 ^ z27 >> 31n) & U64_MASK;
}
function fnv1a64(data, offset) {
  let h = offset & U64_MASK;
  for (const b of data) {
    h = (h ^ BigInt(b)) & U64_MASK;
    h = h * FNV_PRIME_64 & U64_MASK;
  }
  return h;
}
function pushBase32(value, nChars) {
  let v = value;
  const chars = new Array(nChars);
  for (let i = nChars - 1; i >= 0; i--) {
    chars[i] = CROCKFORD[Number(v & 0x1fn)];
    v >>= 5n;
  }
  return chars.join("");
}
function deriveId(parentId, discriminator) {
  const kind = idKindOf(parentId) ?? "trait";
  const encoder = new TextEncoder();
  const parentBytes = encoder.encode(parentId);
  const discBytes = encoder.encode(discriminator);
  const data = new Uint8Array(parentBytes.length + 1 + discBytes.length);
  data.set(parentBytes, 0);
  data[parentBytes.length] = 31;
  data.set(discBytes, parentBytes.length + 1);
  const h1 = splitmix64(fnv1a64(data, FNV_OFFSET_1));
  const h2 = splitmix64(fnv1a64(data, FNV_OFFSET_2));
  const timeBits = (h1 << 64n | h2) & BODY_TIME_MASK;
  const randBits = (h2 << 64n | h1) & BODY_RANDOM_MASK;
  return ID_PREFIXES[kind] + pushBase32(timeBits, 10) + pushBase32(randBits, 16);
}
function ledgerResolveName(ledger, kind, name) {
  for (const [id, entry] of Object.entries(ledger.entries)) {
    if (entry.kind === kind && entry.curName === name) return id;
  }
  return null;
}
function ledgerRename(ledger, id, to, at) {
  const entry = ledger.entries[id];
  if (entry === void 0 || entry.curName === to) return ledger;
  const updated = {
    ...entry,
    curName: to,
    renames: [...entry.renames, { from: entry.curName, to, at }]
  };
  return { ...ledger, entries: { ...ledger.entries, [id]: updated } };
}
function ledgerCurName(ledger, id) {
  return ledger.entries[id]?.curName ?? null;
}
var OrbitalIdSchema = z.string().refine(isOrbitalId, { message: `Expected an orbital id (prefix "${ID_PREFIXES.orbital}")` });
var EntityIdSchema = z.string().refine(isEntityId, { message: `Expected an entity id (prefix "${ID_PREFIXES.entity}")` });
var TraitIdSchema = z.string().refine(isTraitId, { message: `Expected a trait id (prefix "${ID_PREFIXES.trait}")` });
var EventIdSchema = z.string().refine(isEventId, { message: `Expected an event id (prefix "${ID_PREFIXES.event}")` });
var PageIdSchema = z.string().refine(isPageId, { message: `Expected a page id (prefix "${ID_PREFIXES.page}")` });
var ServiceIdSchema = z.string().refine(isServiceId, { message: `Expected a service id (prefix "${ID_PREFIXES.service}")` });
var ThemeIdSchema = z.string().refine(isThemeId, { message: `Expected a theme id (prefix "${ID_PREFIXES.theme}")` });
var PaletteEntryIdSchema = z.string().refine(isPaletteEntryId, { message: `Expected a palette-entry id (prefix "${ID_PREFIXES.palette}")` });
var LedgerKindSchema = z.enum([
  "orbital",
  "entity",
  "trait",
  "event",
  "page",
  "service",
  "theme"
]);
var LedgerEntrySchema = z.object({
  id: z.string(),
  kind: LedgerKindSchema,
  bakedName: z.string(),
  curName: z.string(),
  renames: z.array(
    z.object({ from: z.string(), to: z.string(), at: z.string() })
  ),
  owner: z.enum(["std", "io", "workspace"]),
  parent: TraitIdSchema.optional()
});
var IdentityLedgerSchema = z.object({
  schemaVersion: z.literal(1),
  entries: z.record(LedgerEntrySchema)
});
var FieldProjectionSchema = z.object({
  type: z.string().min(1),
  field: z.string().min(1)
});
var JsonValueSchema = z.lazy(
  () => z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValueSchema), z.record(JsonValueSchema)])
);
function isJsonPrimitive(value) {
  return value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}
function isJsonObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function isJsonArray(value) {
  return Array.isArray(value);
}
function toPayloadValue(value) {
  if (value === null || value === void 0) return value;
  return JsonValueSchema.parse(JSON.parse(JSON.stringify(value)));
}

// src/types/field.ts
var FIELD_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
  "timestamp",
  "datetime",
  "email",
  "url",
  "phone",
  "uuid",
  "image",
  "money",
  "file",
  "array",
  "object",
  "enum",
  "relation",
  "trait",
  "orbital",
  "behavior",
  "slot",
  "pattern",
  "node",
  "event",
  "EventAddress",
  "scalar",
  "SExpr",
  "union",
  "tuple"
];
var SEMANTIC_STRING_TYPES = ["email", "url", "phone", "uuid", "image"];
function isSemanticStringType(type) {
  return SEMANTIC_STRING_TYPES.includes(type);
}
var FieldTypeSchema = z.enum(FIELD_TYPES);
var TYPE_POSITION_ONLY = [
  "Map",
  "any",
  "asset",
  "bool",
  "component",
  "duration",
  "entity",
  "float",
  "icon",
  "int",
  "null",
  "secret",
  "void"
];
var RelationCardinalitySchema = z.enum([
  "one",
  "many",
  "one-to-many",
  "many-to-one",
  "many-to-many"
]);
var RelationConfigSchema = z.object({
  entity: z.string().min(1, "Target entity is required"),
  entityId: EntityIdSchema.optional(),
  field: z.string().optional(),
  cardinality: RelationCardinalitySchema.optional(),
  onDelete: z.enum(["cascade", "nullify", "restrict"]).optional(),
  // Legacy compatibility fields
  foreignKey: z.string().optional(),
  target: z.string().optional(),
  type: RelationCardinalitySchema.optional()
}).transform((data) => {
  const cardinality = data.cardinality || data.type;
  const normalized = {
    entity: data.entity || data.target || "",
    ...data.entityId !== void 0 ? { entityId: data.entityId } : {},
    ...cardinality !== void 0 ? { cardinality } : {},
    ...data.field !== void 0 ? { field: data.field } : {},
    ...data.onDelete !== void 0 ? { onDelete: data.onDelete } : {}
  };
  return normalized;
});
var EMAIL_RE = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;
var URL_RE = /^https?:\/\/[^\s]+$/;
var PHONE_RE = /^[+]?[\d\s().-]{7,}$/;
var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isEmailValue(value) {
  return EMAIL_RE.test(value);
}
function isUrlValue(value) {
  return URL_RE.test(value);
}
function isPhoneValue(value) {
  return PHONE_RE.test(value);
}
function isUuidValue(value) {
  return UUID_RE.test(value);
}
var FileValueSchema = z.object({
  name: z.string(),
  url: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number()
});
function isFileValue(value) {
  return FileValueSchema.safeParse(value).success;
}
function isSemanticStringValue(type, value) {
  switch (type) {
    case "email":
      return isEmailValue(value);
    case "url":
    case "image":
      return isUrlValue(value);
    case "phone":
      return isPhoneValue(value);
    case "uuid":
      return isUuidValue(value);
  }
}
var FIELD_TYPE_ALIASES = {
  text: "string",
  int: "number",
  float: "number",
  ts: "timestamp"
};
var EntityFieldSchema = z.lazy(() => {
  const baseFieldShape = {
    name: z.string().min(1, "Field name is required").optional(),
    required: z.boolean().optional(),
    primaryKey: z.boolean().optional(),
    default: JsonValueSchema.optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    properties: z.record(EntityFieldSchema).optional(),
    intrinsic: z.boolean().optional(),
    description: z.string().optional(),
    synonyms: z.string().optional(),
    mock: z.string().optional(),
    mergedFrom: z.string().optional(),
    projectedFrom: FieldProjectionSchema.optional()
  };
  function scalarVariant(t) {
    return z.object({
      ...baseFieldShape,
      type: z.literal(t),
      values: z.array(z.string()).optional()
    });
  }
  return z.preprocess(
    (input) => {
      if (input === null || typeof input !== "object" || !("type" in input) || typeof input.type !== "string") {
        return input;
      }
      const obj = input;
      const next = { ...obj };
      const aliased = FIELD_TYPE_ALIASES[obj.type];
      if (aliased !== void 0) next.type = aliased;
      if (next.enum !== void 0 && next.values === void 0) {
        next.values = next.enum;
      }
      delete next.enum;
      return next;
    },
    z.discriminatedUnion("type", [
      scalarVariant("string"),
      scalarVariant("number"),
      scalarVariant("boolean"),
      scalarVariant("date"),
      scalarVariant("timestamp"),
      scalarVariant("datetime"),
      scalarVariant("email"),
      scalarVariant("url"),
      scalarVariant("phone"),
      scalarVariant("uuid"),
      scalarVariant("image"),
      scalarVariant("trait"),
      scalarVariant("orbital"),
      scalarVariant("behavior"),
      scalarVariant("slot"),
      scalarVariant("pattern"),
      scalarVariant("node"),
      scalarVariant("event"),
      scalarVariant("EventAddress"),
      scalarVariant("money"),
      scalarVariant("file"),
      scalarVariant("scalar"),
      scalarVariant("SExpr"),
      // Enum variant — REQUIRES non-empty values.
      z.object({
        ...baseFieldShape,
        type: z.literal("enum"),
        values: z.array(z.string()).min(1, "Enum field requires a non-empty `values` array")
      }),
      // Union variant — tagged struct union; values = variant names,
      // per-variant shapes in `properties` (absent on named recursion).
      z.object({
        ...baseFieldShape,
        type: z.literal("union"),
        values: z.array(z.string()).min(1, "Union field requires a non-empty `values` array")
      }),
      // Relation variant — REQUIRES relation config.
      z.object({
        ...baseFieldShape,
        type: z.literal("relation"),
        relation: RelationConfigSchema
      }),
      // Array variant — items optional to match relaxed TS shape.
      z.object({
        ...baseFieldShape,
        type: z.literal("array"),
        items: EntityFieldSchema.optional()
      }),
      // Tuple variant — slots in `properties`, keyed by index (at least one).
      z.object({
        ...baseFieldShape,
        type: z.literal("tuple"),
        properties: z.record(EntityFieldSchema).refine((p) => Object.keys(p).length > 0, "Tuple field requires slots in `properties`")
      }),
      // Object variant — fixed-key struct (`properties`) or dynamic-key
      // map (`Map K V`, uniform value schema in `items`).
      z.object({
        ...baseFieldShape,
        type: z.literal("object"),
        items: EntityFieldSchema.optional(),
        values: z.array(z.string()).optional()
      })
    ])
  );
});
var FieldSchema = EntityFieldSchema;
var ENTITY_ROLES = [
  "player",
  "enemy",
  "npc",
  "item",
  "tile",
  "projectile",
  "effect",
  "ui",
  "decoration",
  "vehicle"
];
var EntityRoleSchema = z.enum(ENTITY_ROLES);
var VISUAL_STYLES = ["pixel", "vector", "hd", "1-bit", "isometric"];
var VisualStyleSchema = z.enum(VISUAL_STYLES);
var ASSET_DIMENSIONS = ["2d", "3d"];
var AssetDimensionSchema = z.enum(ASSET_DIMENSIONS);
var ASSET_ASPECTS = ["1:1", "16:9", "5:7", "8:1", "4:1", "5:3", "1:2"];
var AssetAspectSchema = z.enum(ASSET_ASPECTS);
var ANIMATION_NAMES = ["idle", "walk", "skate", "jump", "fall", "attack", "hit", "death"];
var AnimationNameSchema = z.enum(ANIMATION_NAMES);
var SPRITE_DIRECTIONS = ["se", "sw", "ne", "nw"];
var SpriteDirectionSchema = z.enum(SPRITE_DIRECTIONS);
var SHEET_PROJECTIONS = ["iso", "front", "back", "top", "perspective"];
var SheetProjectionSchema = z.enum(SHEET_PROJECTIONS);
var AnimationDefSchema = z.object({
  row: z.number().int().nonnegative(),
  frames: z.number().int().positive(),
  frameRate: z.number().positive(),
  loop: z.boolean()
});
var SpriteSheetAtlasSchema = z.object({
  unit: z.string().optional(),
  type: z.string().optional(),
  frameWidth: z.number().positive(),
  frameHeight: z.number().positive(),
  columns: z.number().int().positive(),
  rows: z.number().int().positive(),
  directions: z.array(SpriteDirectionSchema),
  sheets: z.record(SpriteDirectionSchema, z.string()),
  animations: z.record(z.string(), AnimationDefSchema),
  projections: z.object({
    iso: z.record(SpriteDirectionSchema, z.string()).optional(),
    front: z.string().optional(),
    back: z.string().optional(),
    top: z.string().optional(),
    perspective: z.string().optional()
  }).optional()
});
var SubTextureSchema = z.object({
  x: z.number().nonnegative(),
  y: z.number().nonnegative(),
  width: z.number().positive(),
  height: z.number().positive(),
  frameX: z.number().optional(),
  frameY: z.number().optional(),
  frameWidth: z.number().positive().optional(),
  frameHeight: z.number().positive().optional()
});
var TextureAtlasSchema = z.object({
  imagePath: z.string(),
  subTextures: z.record(z.string(), SubTextureSchema)
});
var TilesheetSchema = z.object({
  imagePath: z.string(),
  tileWidth: z.number().positive(),
  tileHeight: z.number().positive(),
  columns: z.number().int().positive(),
  rows: z.number().int().positive(),
  margin: z.number().nonnegative().optional(),
  spacing: z.number().nonnegative().optional(),
  names: z.array(z.string()).optional()
});
var SemanticAssetRefSchema = z.object({
  role: z.string().min(1),
  category: z.string().min(1),
  animations: z.array(z.string()).optional(),
  style: VisualStyleSchema.optional(),
  variant: z.string().optional(),
  dimension: AssetDimensionSchema.optional(),
  aspect: AssetAspectSchema.optional()
});
var AssetSchema = SemanticAssetRefSchema.extend({
  url: z.string(),
  atlas: z.string().optional(),
  sprite: z.string().optional(),
  name: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  prompt: z.string().optional(),
  choreography: z.record(z.string(), z.array(z.string())).optional()
});
var AssetCatalogEntrySchema = z.object({
  url: z.string(),
  name: z.string(),
  category: z.string(),
  kind: z.enum(["image", "spritesheet", "audio", "scene", "portrait", "model", "other"]),
  thumbnailUrl: z.string().optional(),
  dimension: AssetDimensionSchema.optional(),
  aspect: AssetAspectSchema.optional()
});
var AssetCatalogSchema = z.array(AssetCatalogEntrySchema);
var ScenePosSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().optional()
});
var CAMERA_MODES = ["isometric", "perspective", "top-down", "front", "follow", "chase"];
var CameraModeSchema = z.enum(CAMERA_MODES);
var CameraSchema = z.object({
  pos: ScenePosSchema.optional(),
  target: ScenePosSchema.optional(),
  zoom: z.number().optional(),
  fov: z.number().optional(),
  mode: CameraModeSchema.optional(),
  azimuth: z.number().optional(),
  elevation: z.number().optional(),
  controls: z.boolean().optional()
});
function createAssetKey(role, category) {
  return `${role}:${category}`;
}
function parseAssetKey(key) {
  const parts = key.split(":");
  if (parts.length !== 2) return null;
  return { role: parts[0], category: parts[1] };
}
function getDefaultAnimationsForRole(role) {
  switch (role) {
    case "player":
      return ["idle", "run", "jump", "fall", "attack", "hurt", "die"];
    case "enemy":
      return ["idle", "walk", "attack", "hurt", "die"];
    case "npc":
      return ["idle", "walk", "talk"];
    case "item":
      return ["idle", "collected"];
    case "tile":
      return ["static"];
    case "projectile":
      return ["fly", "hit", "expire"];
    case "effect":
      return ["play"];
    case "ui":
      return ["normal", "hover", "pressed", "disabled"];
    case "decoration":
      return ["idle"];
    case "vehicle":
      return ["idle", "move", "brake"];
    default:
      return ["idle"];
  }
}
function validateAssetAnimations(assetRef, requiredAnimations) {
  const provided = assetRef.animations || [];
  const missing = requiredAnimations.filter((anim) => !provided.includes(anim));
  return { valid: missing.length === 0, missing };
}
var SPRITE_SHEET_LAYOUT = {
  frameWidth: 256,
  frameHeight: 256,
  columns: 8,
  rows: 5,
  background: "#20202E"
};
var DEFAULT_UNIT_ANIMATION_ROWS = [
  { name: "idle", row: 0, frames: 4, loop: true, frameRate: 6 },
  { name: "walk", row: 1, frames: 8, loop: true, frameRate: 10 },
  { name: "attack", row: 2, frames: 6, loop: false, frameRate: 12 },
  { name: "hit", row: 3, frames: 3, loop: false, frameRate: 8 },
  { name: "death", row: 4, frames: 6, loop: false, frameRate: 8 }
];
function defaultUnitAtlas(sheets, opts) {
  const directions = opts?.directions ?? SPRITE_DIRECTIONS.filter((direction) => sheets[direction] !== void 0);
  const animations = {};
  for (const { name, ...def } of DEFAULT_UNIT_ANIMATION_ROWS) {
    animations[name] = def;
  }
  return {
    frameWidth: SPRITE_SHEET_LAYOUT.frameWidth,
    frameHeight: SPRITE_SHEET_LAYOUT.frameHeight,
    columns: SPRITE_SHEET_LAYOUT.columns,
    rows: SPRITE_SHEET_LAYOUT.rows,
    directions,
    sheets,
    animations
  };
}
var MANIFEST_ENTRY_KINDS = ["model", "image", "spritesheet", "audio", "json"];
var ManifestEntryKindSchema = z.enum(MANIFEST_ENTRY_KINDS);
var SoundEntrySchema = z.object({
  path: z.union([z.string(), z.array(z.string())]),
  volume: z.number().optional(),
  loop: z.boolean().optional(),
  poolSize: z.number().optional(),
  autostart: z.boolean().optional(),
  crossfade: z.boolean().optional(),
  crossfadeDurationMs: z.number().optional()
});
var AudioManifestSchema = z.record(z.string(), SoundEntrySchema);
var MANIFEST_CANVAS_AFFINITIES = ["isometric", "hex", "flat", "side", "3d", "none"];
var ManifestCanvasAffinitySchema = z.enum(MANIFEST_CANVAS_AFFINITIES);
var MANIFEST_SOURCE_CATALOGS = ["kenny", "kekec-assets", "iram-assets", "trait-wars-assets", "kflow-assets"];
var ManifestSourceCatalogSchema = z.enum(MANIFEST_SOURCE_CATALOGS);
var MANIFEST_ASSET_LICENSES = ["CC0", "proprietary"];
var ManifestAssetLicenseSchema = z.enum(MANIFEST_ASSET_LICENSES);
var ManifestFrameSpecSchema = z.union([
  z.object({ kind: z.literal("iso-tile"), w: z.number(), h: z.number() }),
  z.object({ kind: z.literal("sprite-sheet"), frame: z.number(), cols: z.number(), rows: z.number() }),
  z.string().min(1),
  z.null()
]);
var ManifestEntrySchema = z.object({
  url: z.string(),
  name: z.string(),
  category: z.string(),
  kind: ManifestEntryKindSchema,
  width: z.number(),
  height: z.number(),
  canvasAffinity: ManifestCanvasAffinitySchema,
  genreAffinity: z.array(z.string()),
  swapClass: z.string(),
  sourceCatalog: ManifestSourceCatalogSchema,
  frameSpec: ManifestFrameSpecSchema,
  license: ManifestAssetLicenseSchema.optional()
});
var ASSET_CATALOG_ENTRY_KINDS = [
  "image",
  "spritesheet",
  "audio",
  "scene",
  "portrait",
  "model",
  "other"
];
var MODEL_URL_EXTENSIONS = [".glb", ".gltf"];
var AUDIO_URL_EXTENSIONS = [".mp3", ".ogg", ".wav"];
function urlEndsWithAny(url, extensions) {
  const lower = url.toLowerCase();
  return extensions.some((extension) => lower.endsWith(extension));
}
function deriveManifestAssetKind(entry) {
  if (typeof entry.frameSpec === "string" || entry.frameSpec?.kind === "sprite-sheet" || entry.kind === "spritesheet") return "spritesheet";
  if (entry.kind === "model" || urlEndsWithAny(entry.url, MODEL_URL_EXTENSIONS)) return "model";
  if (entry.kind === "audio" || urlEndsWithAny(entry.url, AUDIO_URL_EXTENSIONS)) return "audio";
  if (entry.kind === "image") return "image";
  return "other";
}
function parseAssetAspectRatio(aspect) {
  const [w, h] = aspect.split(":").map(Number);
  return w / h;
}
var ASSET_ASPECT_RATIO_TOLERANCE = 0.01;
function deriveManifestAssetAspect(width, height) {
  if (width <= 0 || height <= 0) return void 0;
  const ratio = width / height;
  for (const aspect of ASSET_ASPECTS) {
    const expected = parseAssetAspectRatio(aspect);
    if (Math.abs(ratio / expected - 1) <= ASSET_ASPECT_RATIO_TOLERANCE) return aspect;
  }
  return void 0;
}
var ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
function resolveManifestUrl(url, cdnPrefix) {
  if (!cdnPrefix || ABSOLUTE_URL_PATTERN.test(url)) return url;
  const prefix = cdnPrefix.endsWith("/") ? cdnPrefix : `${cdnPrefix}/`;
  const rest = url.startsWith("/") ? url.slice(1) : url;
  return `${prefix}${rest}`;
}
function manifestToAssetCatalog(entries, opts) {
  return entries.map((entry) => {
    const kind = deriveManifestAssetKind(entry);
    const dimension = entry.canvasAffinity === "3d" ? "3d" : "2d";
    const aspect = deriveManifestAssetAspect(entry.width, entry.height);
    const url = resolveManifestUrl(entry.url, opts?.cdnPrefix);
    const catalogEntry = {
      url,
      name: entry.name,
      category: entry.category,
      kind,
      dimension
    };
    if (aspect !== void 0) catalogEntry.aspect = aspect;
    if (kind === "image" || kind === "spritesheet") catalogEntry.thumbnailUrl = url;
    return catalogEntry;
  });
}
var ASSET_QUERY_TYPE_PREFIX = "t:";
var ASSET_QUERY_LABEL_PREFIX = "l:";
function isAssetCatalogEntryKind(value) {
  return ASSET_CATALOG_ENTRY_KINDS.some((kind) => kind === value);
}
function parseAssetQueryTokens(q) {
  const tokens = q.trim().length > 0 ? q.trim().split(/\s+/) : [];
  const textParts = [];
  const labels = [];
  let kind;
  let unknownKind = false;
  for (const token of tokens) {
    if (token.startsWith(ASSET_QUERY_TYPE_PREFIX)) {
      const value = token.slice(ASSET_QUERY_TYPE_PREFIX.length);
      if (isAssetCatalogEntryKind(value)) kind = value;
      else unknownKind = true;
    } else if (token.startsWith(ASSET_QUERY_LABEL_PREFIX)) {
      const value = token.slice(ASSET_QUERY_LABEL_PREFIX.length);
      if (value.length > 0) labels.push(value);
    } else {
      textParts.push(token);
    }
  }
  return { text: textParts.join(" "), kind, labels, unknownKind };
}
function parseAssetQuery(q) {
  const parsed = parseAssetQueryTokens(q);
  return parsed.kind === void 0 ? { text: parsed.text, labels: parsed.labels } : { text: parsed.text, kind: parsed.kind, labels: parsed.labels };
}
function matchAssetQuery(entry, q) {
  const parsed = parseAssetQueryTokens(q);
  if (parsed.unknownKind) return false;
  if (parsed.kind !== void 0 && entry.kind !== parsed.kind) return false;
  for (const label of parsed.labels) {
    if (entry.category.toLowerCase() !== label.toLowerCase()) return false;
  }
  if (parsed.text.length > 0) {
    const haystack = `${entry.name} ${entry.category}`.toLowerCase();
    if (!haystack.includes(parsed.text.toLowerCase())) return false;
  }
  return true;
}
var SExprDataSchema = z.lazy(
  () => z.union([SExprAtomSchema, z.array(SExprDataSchema)])
);
var SExprAtomSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.record(SExprDataSchema)
  // Objects for payload data
]);
var SExprSchema = z.lazy(
  () => z.union([
    SExprAtomSchema,
    z.array(SExprDataSchema).min(1).refine(
      (arr) => typeof arr[0] === "string",
      { message: "S-expression array must have a string operator as first element" }
    )
  ])
);
var ExpressionSchema = SExprSchema;
function isSExpr(value) {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === "string";
}
function isSExprAtom(value) {
  if (value === null) return true;
  if (Array.isArray(value)) return false;
  const type = typeof value;
  return type === "string" || type === "number" || type === "boolean" || type === "object";
}
function isBinding(value) {
  return typeof value === "string" && value.startsWith("@");
}
function isSExprCall(value) {
  return isSExpr(value);
}
var CORE_BINDINGS = ["entity", "payload", "state", "now", "config", "computed", "trait", "user", "callsitePayload", "pages", "currentTheme"];
function parseBinding(binding) {
  if (!binding.startsWith("@")) {
    return null;
  }
  const withoutPrefix = binding.slice(1);
  const parts = withoutPrefix.split(".");
  if (parts.length === 0 || parts[0] === "") {
    return null;
  }
  const root = parts[0];
  const path = parts.slice(1);
  const isCore = CORE_BINDINGS.includes(root);
  return {
    type: isCore ? "core" : "entity",
    root,
    path,
    original: binding
  };
}
function isValidBinding(binding) {
  const parsed = parseBinding(binding);
  if (!parsed) return false;
  if (parsed.type === "core") {
    if (parsed.root === "state" || parsed.root === "now") {
      return parsed.path.length === 0;
    }
    return true;
  }
  return parsed.path.length > 0;
}
function getOperator(expr) {
  if (!isSExpr(expr)) return null;
  return expr[0];
}
function formatSExpr(expr) {
  if (expr === null) return "null";
  if (typeof expr === "string") return isBinding(expr) ? expr : JSON.stringify(expr);
  if (typeof expr === "number" || typeof expr === "boolean") return String(expr);
  if (Array.isArray(expr)) {
    const quoted = quoteBodyOf(expr);
    if (quoted !== void 0) return `(${QUOTE_HEAD} ${formatSExpr(decodeQuoteBody(quoted))})`;
    const qq = quasiquoteOf(expr);
    if (qq !== void 0) return `(${QUASIQUOTE_HEAD} ${formatTemplate(decodeQuoteBody(qq.body), qq.holes)})`;
    const op = getOperator(expr);
    if (op !== null) return `(${[op, ...expr.slice(1).map(formatSExpr)].join(" ")})`;
    return `[${expr.map(formatSExpr).join(" ")}]`;
  }
  const entries = Object.entries(expr).map(([k, v]) => `${k}: ${formatSExpr(v)}`);
  return entries.length === 0 ? "{}" : `{ ${entries.join(", ")} }`;
}
function getArgs(expr) {
  if (!isSExpr(expr)) return [];
  return expr.slice(1);
}
function sexpr(operator, ...args) {
  return [operator, ...args];
}
function walkSExpr(expr, visitor, parent = null, index = 0) {
  visitor(expr, parent, index);
  if (isSExpr(expr)) {
    for (let i = 0; i < expr.length; i++) {
      walkSExpr(expr[i], visitor, expr, i);
    }
  }
}
function collectBindings(expr) {
  const bindings = [];
  walkSExpr(expr, (node) => {
    if (isBinding(node)) {
      bindings.push(node);
    }
  });
  return bindings;
}
function isEventPayloadValue(value) {
  if (value === null || value === void 0) return true;
  const kind = typeof value;
  if (kind === "string" || kind === "number" || kind === "boolean") return true;
  if (value instanceof Date) return true;
  if (Array.isArray(value)) return value.every((item) => isEventPayloadValue(item));
  if (kind === "object" && value !== null && typeof value === "object") {
    return Object.values(value).every((item) => isEventPayloadValue(item));
  }
  return false;
}
var EventPayloadValueSchema = z.lazy(
  () => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.null(),
    z.undefined(),
    EventPayloadSchema,
    z.array(EventPayloadValueSchema)
  ])
);
var EventPayloadSchema = z.lazy(() => z.record(EventPayloadValueSchema));
function isStaticallyFalse(expr, resolve) {
  const known = (e) => {
    if (typeof e === "string" && e.startsWith("@") && resolve !== void 0) {
      const value2 = resolve(e);
      return value2 === void 0 ? e : value2;
    }
    return e;
  };
  const isLiteral = (e) => e === null || typeof e === "boolean" || typeof e === "number" || typeof e === "string" && !e.startsWith("@");
  const value = known(expr);
  if (value === false || value === null) return true;
  if (typeof value === "number") return value === 0;
  if (typeof value === "string") return value === "";
  if (Array.isArray(value)) {
    const op = value[0];
    if (typeof op !== "string") return value.length === 0;
    const args = value.slice(1);
    switch (op) {
      case "and":
        return args.some((a) => isStaticallyFalse(a, resolve));
      case "or":
        return args.length > 0 && args.every((a) => isStaticallyFalse(a, resolve));
      case "not":
        return args.length === 1 && known(args[0]) === true;
      case "==":
      case "!=": {
        if (args.length !== 2) return false;
        const [l, r] = [known(args[0]), known(args[1])];
        if (!isLiteral(l) || !isLiteral(r)) return false;
        return l === r === (op === "!=");
      }
      default:
        return false;
    }
  }
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
var QUOTE_HEAD = "quote";
function canonicalQuoteValue(expr) {
  if (Array.isArray(expr)) return expr.map(canonicalQuoteValue);
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const key of Object.keys(expr).sort()) out[key] = canonicalQuoteValue(expr[key]);
    return out;
  }
  return expr;
}
function encodeQuoteBody(expr) {
  return JSON.stringify(canonicalQuoteValue(expr)).replace(/@/g, "\\u0040");
}
function decodeQuoteBody(body) {
  const value = JSON.parse(body);
  return value;
}
function quoteExpr(expr) {
  return [QUOTE_HEAD, encodeQuoteBody(expr)];
}
function quoteBodyOf(expr) {
  return Array.isArray(expr) && expr.length === 2 && expr[0] === QUOTE_HEAD && typeof expr[1] === "string" ? expr[1] : void 0;
}
var QUASIQUOTE_HEAD = "quasiquote";
var UNQUOTE_HEAD = "unquote";
function isHeadedCall(expr, head) {
  return Array.isArray(expr) && expr.length > 0 && expr[0] === head;
}
function containsHead(expr, head) {
  if (isHeadedCall(expr, head)) return true;
  if (Array.isArray(expr)) return expr.some((e) => containsHead(e, head));
  if (expr !== null && typeof expr === "object") return Object.values(expr).some((e) => containsHead(e, head));
  return false;
}
function extractHoles(expr, holes) {
  if (isHeadedCall(expr, QUASIQUOTE_HEAD)) throw new Error("quasiquote: nested quasiquote is not supported");
  if (isHeadedCall(expr, UNQUOTE_HEAD)) {
    if (expr.length !== 2) throw new Error("quasiquote: (unquote e) takes exactly one expression");
    if (containsHead(expr[1], UNQUOTE_HEAD)) throw new Error("quasiquote: unquote inside an unquote");
    holes.push(expr[1]);
    return [UNQUOTE_HEAD, holes.length - 1];
  }
  if (Array.isArray(expr)) return expr.map((e) => extractHoles(e, holes));
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const k of Object.keys(expr).sort()) out[k] = extractHoles(expr[k], holes);
    return out;
  }
  return expr;
}
function quasiquoteExpr(template) {
  const holes = [];
  const body = encodeQuoteBody(extractHoles(template, holes));
  return [QUASIQUOTE_HEAD, body, ...holes];
}
function quasiquoteOf(expr) {
  if (!Array.isArray(expr) || expr.length < 2 || expr[0] !== QUASIQUOTE_HEAD || typeof expr[1] !== "string") {
    return void 0;
  }
  return { body: expr[1], holes: expr.slice(2) };
}
function countMarkers(expr) {
  if (isHeadedCall(expr, UNQUOTE_HEAD) && expr.length === 2 && typeof expr[1] === "number") return 1;
  if (Array.isArray(expr)) return expr.reduce((n, e) => n + countMarkers(e), 0);
  if (expr !== null && typeof expr === "object") return Object.values(expr).reduce((n, e) => n + countMarkers(e), 0);
  return 0;
}
function fillMarkers(expr, values) {
  if (isHeadedCall(expr, UNQUOTE_HEAD) && expr.length === 2 && typeof expr[1] === "number") return values[expr[1]];
  if (Array.isArray(expr)) return expr.map((e) => fillMarkers(e, values));
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const [k, v] of Object.entries(expr)) out[k] = fillMarkers(v, values);
    return out;
  }
  return expr;
}
function toProgramData(value) {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (Array.isArray(value)) return value.map((v) => toProgramData(v));
  if (typeof value === "object" && !(value instanceof Date) && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = toProgramData(v);
    return out;
  }
  throw new Error(`quasiquote: a hole evaluated to a ${typeof value} value, which cannot be held as program data`);
}
function instantiateQuasiquote(body, values) {
  const template = decodeQuoteBody(body);
  const holes = countMarkers(template);
  if (holes !== values.length) {
    throw new Error(`quasiquote: template has ${holes} hole(s), got ${values.length} value(s)`);
  }
  return fillMarkers(template, values);
}
function formatTemplate(expr, holes) {
  if (isHeadedCall(expr, UNQUOTE_HEAD) && expr.length === 2 && typeof expr[1] === "number") {
    return `(${UNQUOTE_HEAD} ${formatSExpr(holes[expr[1]])})`;
  }
  if (Array.isArray(expr)) {
    const op = getOperator(expr);
    if (op !== null) return `(${[op, ...expr.slice(1).map((e) => formatTemplate(e, holes))].join(" ")})`;
    return `[${expr.map((e) => formatTemplate(e, holes)).join(" ")}]`;
  }
  if (expr !== null && typeof expr === "object") {
    const entries = Object.entries(expr).map(([k, v]) => `${k}: ${formatTemplate(v, holes)}`);
    return entries.length === 0 ? "{}" : `{ ${entries.join(", ")} }`;
  }
  return formatSExpr(expr);
}

// src/types/entity.ts
var EntityPersistenceSchema = z.enum([
  "persistent",
  "runtime"
]);
var OrbitalEntitySchema = z.object({
  name: z.string().min(1, "Entity name is required"),
  // V4 arena id (`EntityDefinition.id`); declared so the strip-mode zod gate
  // carries an imported entity's id through instead of erasing it.
  id: EntityIdSchema.optional(),
  persistence: EntityPersistenceSchema.default("persistent"),
  shared: z.boolean().optional(),
  // Must stay in step with the Rust serde field (`EntityDefinition.identity`,
  // orbital-core/src/schema/types.rs). `z.object()` STRIPS unknown keys rather
  // than rejecting them, so a Rust-only field works on the compiled path and
  // silently vanishes on the interpreter path.
  identity: z.boolean().optional(),
  // Rust twins: `EntityDefinition.{local,seed_mock}` (serde `seedMock`).
  local: z.boolean().optional(),
  seedMock: z.boolean().optional(),
  // Same "must stay in step with Rust serde" hazard as `identity` above —
  // these four mirror `EntityDefinition.{read,create,update,delete}_policy`.
  // Rust has no `rename_all` on this struct, so the wire key IS snake_case
  // (unlike this file's other camelCase fields, which are Rust-side-absent
  // and so never hit this cross-language constraint — see `visual_prompt`
  // just below for the existing precedent).
  read_policy: SExprSchema.optional(),
  create_policy: SExprSchema.optional(),
  update_policy: SExprSchema.optional(),
  delete_policy: SExprSchema.optional(),
  // Snake_case for the same cross-language reason as the four policies above.
  access_waivers: z.object({
    read: z.string().optional(),
    create: z.string().optional(),
    update: z.string().optional(),
    delete: z.string().optional()
  }).optional(),
  collection: z.string().optional(),
  fields: z.array(EntityFieldSchema).min(1, "At least one field is required"),
  instances: z.array(z.record(JsonValueSchema)).optional(),
  localeInstances: z.record(z.array(z.record(JsonValueSchema))).optional(),
  timestamps: z.boolean().optional(),
  softDelete: z.boolean().optional(),
  description: z.string().optional(),
  visual_prompt: z.string().optional(),
  assetRef: SemanticAssetRefSchema.optional()
});
var EntitySchema = OrbitalEntitySchema;
function instancesForLocale(entity, locale) {
  const localized = locale !== void 0 ? entity.localeInstances?.[locale] : void 0;
  return localized ?? entity.instances ?? [];
}
function deriveCollection(entity) {
  if (entity.persistence !== "persistent") {
    return void 0;
  }
  return entity.name.toLowerCase() + "s";
}
function isRuntimeEntity(entity) {
  return entity.persistence === "runtime";
}
function isClientResident(entity) {
  return entity.persistence === "runtime" || entity.local === true;
}
function storesRowsInBrowser(entity) {
  return entity.local === true;
}
function persistenceModeAllowsOverrides(persistence, dataBearing = false) {
  return persistence === "persistent" || persistence === void 0 || persistence === "runtime" && dataBearing;
}
var FieldValueSchema = z.lazy(
  () => z.union([z.string(), z.number(), z.boolean(), z.date(), z.null(), z.array(FieldValueSchema), z.record(FieldValueSchema.optional())])
);
function isFieldValue(value) {
  if (value === null) return true;
  const kind = typeof value;
  if (kind === "string" || kind === "number" || kind === "boolean") return true;
  if (value instanceof Date) return true;
  if (Array.isArray(value)) return value.every((item) => isFieldValue(item));
  if (kind === "object" && value !== null && typeof value === "object") {
    return Object.values(value).every((item) => item === void 0 || isFieldValue(item));
  }
  return false;
}
var EntityRowSchema = z.object({ id: z.string().optional() }).catchall(FieldValueSchema.optional());
var SKELETON_VARIANTS = ["header", "table", "list", "grid", "detail", "stats", "form", "card", "text"];
var SkeletonVariantSchema = z.enum(SKELETON_VARIANTS);
var SkeletonShapeSchema = z.object({
  variant: SkeletonVariantSchema,
  rows: z.number().int().positive().optional(),
  columns: z.number().int().positive().optional(),
  fields: z.number().int().positive().optional()
});
var SkeletonSpecSchema = z.union([SkeletonVariantSchema, z.literal("none"), SkeletonShapeSchema]);
var SkeletonNodeSchema = z.lazy(
  () => z.union([z.object({ shape: SkeletonShapeSchema }), z.object({ stack: z.array(SkeletonNodeSchema) })])
);
var AwaitRenderSchema = z.object({
  trait: z.string(),
  slot: z.string(),
  skeleton: SkeletonNodeSchema
});
var UI_SLOTS = [
  // App slots
  "main",
  "sidebar",
  "dock",
  // Right inline panel that pushes main (narrow screens: a drawer)
  "modal",
  "drawer",
  "overlay",
  "center",
  "toast",
  "floating",
  "system",
  // For invisible system components (InputListener, CollisionDetector)
  "content",
  "screen",
  // Game HUD slots
  "hud",
  "hud-top",
  "hud-bottom",
  "hud.health",
  "hud.score",
  "hud.inventory",
  "hud.stamina",
  // Game overlay slots
  "overlay.inventory",
  "overlay.dialogue",
  "overlay.menu",
  "overlay.pause"
];
var RENDERED_SLOTS = UI_SLOTS.filter(
  (slot) => !slot.includes(".") && slot !== "hud" && slot !== "screen"
);
var UISlotSchema = z.enum(UI_SLOTS);
function isRowObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);
}
function isPersistBatchOperation(value) {
  if (!Array.isArray(value) || typeof value[1] !== "string") return false;
  switch (value[0]) {
    case "create":
      return value.length === 2 || value.length === 3 && isRowObject(value[2]);
    case "update":
      return typeof value[2] === "string" && (value.length === 3 || value.length === 4 && isRowObject(value[3]));
    case "delete":
      return value.length === 3 && typeof value[2] === "string";
    default:
      return false;
  }
}
var EFFECT_OPERATORS = [
  "render-ui",
  "navigate",
  "navigate-back",
  "emit",
  "set",
  "persist",
  "call-service",
  "cancel-call",
  "spawn",
  "despawn",
  "do",
  "fetch",
  "fetch-stream",
  "if",
  "when",
  "let",
  "log",
  "wait",
  "ref",
  "deref",
  "swap",
  "watch",
  "atomic",
  "async/delay",
  "async/debounce",
  "async/throttle",
  "async/interval",
  "async/race",
  "async/all",
  "async/sequence",
  "forward",
  "train",
  "evaluate",
  "checkpoint/save",
  "checkpoint/load",
  "notify",
  "send-server",
  "async/timeout",
  "async/retry",
  "nn/setWeights",
  "train/loop",
  "train/step",
  "train/clipGradients",
  "train/clipWeights",
  "train/sgd",
  "train/adam",
  "prob/seed",
  "prob/condition",
  "workspace/write-orbital",
  "workspace/write-file",
  "workspace/write-schema",
  "workspace/write-plan",
  "workspace/archive-orbital",
  "behavior/catalog",
  "behavior/describe",
  "behavior/source",
  "program/read",
  "program/print",
  "program/eval",
  "program/compose",
  "integration/http",
  "integration/github-get-repo",
  "integration/github-create-issue"
];
var EFFECT_OPERATOR_FAMILIES = [
  "agent",
  "os",
  "browser",
  "llm",
  "session",
  "trace",
  "memory",
  "application"
];
var SERVER_REPORTED_EFFECTS = [
  "set",
  "persist",
  "call-service",
  "fetch",
  "fetch-stream",
  "ref",
  "deref",
  "swap",
  "atomic",
  "emit"
];
var EffectShapeSchema = z.array(SExprDataSchema).min(1).refine(
  (arr) => typeof arr[0] === "string",
  { message: "Effect must be an S-expression with a string operator as first element" }
);
var EffectSchema = z.custom(
  (value) => EffectShapeSchema.safeParse(value).success,
  { message: "Effect must be an S-expression with a string operator as first element" }
);
function isEffect(value) {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === "string";
}
var isSExprEffect = isEffect;
function set(binding, value) {
  return ["set", binding, value];
}
function emit(event, payload) {
  return payload ? ["emit", event, payload] : ["emit", event];
}
function navigate(path, params, options) {
  if (params && options) return ["navigate", path, params, options];
  return params ? ["navigate", path, params] : ["navigate", path];
}
function navigateBack() {
  return ["navigate-back"];
}
function renderUI(target, pattern, props) {
  return props ? ["render-ui", target, pattern, props] : ["render-ui", target, pattern];
}
function persist(action, entity, data) {
  if (action === "create" || action === "update") {
    return ["persist", action, entity, data];
  }
  return data ? ["persist", action, entity, data] : ["persist", action, entity];
}
function callService(serviceName, config) {
  return ["call-service", serviceName, config];
}
function spawn(entity, initialState) {
  return initialState ? ["spawn", entity, initialState] : ["spawn", entity];
}
function despawn(entityId) {
  return ["despawn", entityId];
}
function doEffects(...effects) {
  return ["do", ...effects];
}
function ref(binding, selector) {
  return selector ? ["ref", binding, selector] : ["ref", binding];
}
function deref(binding, selector) {
  return selector ? ["deref", binding, selector] : ["deref", binding];
}
function swap(binding, transform) {
  return ["swap", binding, transform];
}
function watch(binding, event, options) {
  return options ? ["watch", binding, event, options] : ["watch", binding, event];
}
function atomic(...effects) {
  return ["atomic", ...effects];
}
var PatternConfigSchema = z.custom(
  (value) => value === null || typeof value === "object" && value !== null && !Array.isArray(value),
  { message: "Expected a pattern config object or null" }
);
var PatternObjectSchema = z.custom(
  (value) => typeof value === "object" && value !== null && !Array.isArray(value),
  { message: "Expected a pattern config object" }
);
var ResolvedPatternPropsSchema = z.custom(
  (value) => typeof value === "object" && value !== null && !Array.isArray(value),
  { message: "Expected a resolved pattern props object" }
);

// src/types/state-machine.ts
var StateSchema = z.object({
  name: z.string().min(1, "State name is required"),
  isInitial: z.boolean().optional(),
  isTerminal: z.boolean().optional(),
  isFinal: z.boolean().optional(),
  description: z.string().optional(),
  onEntry: z.array(z.string()).optional(),
  onExit: z.array(z.string()).optional()
});
var PayloadTypeWhenSchema = z.object({
  knob: z.string().min(1),
  equals: z.string().min(1),
  type: z.string().min(1)
});
function payloadTypeContainer(type) {
  if (type === "array") return { kind: "array", element: "" };
  if (type === "object") return { kind: "object" };
  if (type.length > 2 && type.startsWith("[") && type.endsWith("]")) {
    return { kind: "array", element: type.slice(1, -1) };
  }
  return { kind: "opaque" };
}
var PayloadFieldSchema = z.object({
  name: z.string().min(1),
  type: z.string().min(1),
  required: z.boolean().optional(),
  values: z.array(z.string()).optional(),
  properties: z.lazy(() => z.array(PayloadFieldSchema)).optional(),
  typeWhen: z.array(PayloadTypeWhenSchema).optional(),
  entity: z.string().min(1).optional(),
  projectedFrom: FieldProjectionSchema.optional()
});
var EventSchema = z.object({
  key: z.string().min(1, "Event key is required"),
  id: EventIdSchema.optional(),
  name: z.string().min(1, "Event name is required"),
  description: z.string().optional(),
  synonyms: z.string().optional(),
  tier: z.string().optional(),
  payloadSchema: z.array(PayloadFieldSchema).optional(),
  payloadEntity: z.string().optional(),
  external: z.boolean().optional(),
  classification: z.enum(["domain", "system"]).optional(),
  semanticRole: z.string().optional()
});
var GuardSchema = z.object({
  name: z.string().min(1, "Guard name is required"),
  expression: ExpressionSchema,
  description: z.string().optional()
});
var TransitionSchema = z.object({
  from: z.string().min(1, "Transition source state is required"),
  to: z.string().min(1, "Transition target state is required"),
  event: z.string().min(1, "Transition event is required"),
  eventId: EventIdSchema.optional(),
  guard: ExpressionSchema.nullish(),
  effects: z.array(EffectSchema).optional(),
  description: z.string().nullish(),
  awaitRender: z.array(AwaitRenderSchema).optional()
});
var StateMachineSchema = z.object({
  states: z.array(StateSchema).min(1, "At least one state is required"),
  events: z.array(EventSchema),
  transitions: z.array(TransitionSchema),
  guards: z.array(GuardSchema).optional()
});
function isCircuitEvent(event) {
  return !event.startsWith("_");
}

// src/types/trait.ts
var TraitConfigValueSchema = z.lazy(
  () => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(TraitConfigValueSchema),
    z.record(TraitConfigValueSchema)
  ])
);
var TraitConfigSchema = z.record(TraitConfigValueSchema);
function isCallSiteConfigDeclaration(entry) {
  return typeof entry === "object" && entry !== null && !Array.isArray(entry) && "type" in entry && typeof entry.type === "string" && "default" in entry;
}
function isConfigRedeclaration(entry) {
  if (!isCallSiteConfigDeclaration(entry)) return false;
  return entry.type !== "unknown" || entry.label !== void 0 || entry.description !== void 0 || entry.synonyms !== void 0 || entry.tier !== void 0;
}
var CONFIG_DECLARATION_META_KEYS = ["label", "description", "tier", "synonyms", "values"];
function isConfigFieldSchema(entry) {
  if (typeof entry !== "object" || entry === null || Array.isArray(entry)) return false;
  if (!("type" in entry) || typeof entry.type !== "string") return false;
  return "default" in entry || CONFIG_DECLARATION_META_KEYS.some((k) => k in entry);
}
function normalizeCallSiteConfigToValues(config) {
  if (config === void 0) {
    return void 0;
  }
  const out = {};
  let hasAny = false;
  for (const [key, entry] of Object.entries(config)) {
    const value = isConfigFieldSchema(entry) ? entry.default : entry;
    if (value !== void 0) {
      out[key] = value;
      hasAny = true;
    }
  }
  return hasAny ? out : void 0;
}
function mergeCallSiteConfigOverrides(base, overrides) {
  const next = {};
  for (const [k, entry] of Object.entries(base)) {
    next[k] = isConfigFieldSchema(entry) ? entry : { type: "unknown", default: entry };
  }
  for (const [k, v] of Object.entries(overrides)) {
    if (isCallSiteConfigDeclaration(v)) {
      next[k] = v;
      continue;
    }
    const existing = base[k];
    next[k] = existing !== void 0 && isConfigFieldSchema(existing) ? { ...existing, default: v } : { type: "unknown", default: v };
  }
  return next;
}
var REFERENCE_CONFIG_TYPES = ["entity", "trait", "event"];
function isReferenceConfigType(type) {
  return REFERENCE_CONFIG_TYPES.includes(type);
}
var SECRET_CONFIG_TYPES = ["secret"];
function isSecretConfigType(type) {
  return SECRET_CONFIG_TYPES.includes(type);
}
function maskSecretConfigValues(config, values) {
  const masked = {};
  for (const [key, value] of Object.entries(values)) {
    const decl = config[key];
    if (decl !== void 0 && isSecretConfigType(decl.type) && typeof value === "string" && value.length > 0) {
      masked[key] = value.length > 4 ? `\u2022\u2022\u2022\u2022${value.slice(-4)}` : "\u2022\u2022\u2022\u2022";
    } else {
      masked[key] = value;
    }
  }
  return masked;
}
function canonicalizeUnboundTraitOverride(fieldType, value) {
  return fieldType === "trait" && value === "none" ? null : value;
}
function overrideDeclaredKnobs(declared, overrides) {
  const out = { ...declared };
  for (const key of Object.keys(overrides)) {
    const field = declared[key];
    if (field === void 0) {
      throw new Error(
        `overrideDeclaredKnobs: config override "${key}" is not a declared knob (ORB_O_CONFIG_UNKNOWN_KEY). Declared: ${Object.keys(declared).join(", ") || "(none)"}`
      );
    }
    out[key] = { ...field, default: canonicalizeUnboundTraitOverride(field.type, overrides[key]) };
  }
  return out;
}
var ConfigFieldItemsDeclarationSchema = z.lazy(
  () => z.object({
    type: z.string().optional(),
    properties: z.record(TraitEntityFieldSchema).optional(),
    items: ConfigFieldItemsDeclarationSchema.optional(),
    values: z.array(z.string()).optional()
  })
);
var ConfigFieldDeclarationSchema = z.object({
  type: z.string(),
  default: TraitConfigValueSchema.optional(),
  refId: z.string().optional(),
  required: z.boolean().optional(),
  label: z.string().optional(),
  description: z.string().optional(),
  tier: z.string().optional(),
  values: z.array(z.string()).optional(),
  synonyms: z.string().optional(),
  items: ConfigFieldItemsDeclarationSchema.optional(),
  properties: z.lazy(() => z.record(TraitEntityFieldSchema)).optional(),
  forwardedFrom: z.string().optional(),
  nestedForwardedFrom: z.array(z.string()).optional(),
  payloadFor: z.string().optional(),
  projectedFrom: FieldProjectionSchema.optional()
});
var DeclaredTraitConfigSchema = z.record(
  ConfigFieldDeclarationSchema
);
var TraitCategorySchema = z.enum([
  "lifecycle",
  "temporal",
  "validation",
  "notification",
  "integration",
  "interaction",
  "agent",
  "game-core",
  "game-character",
  "game-ai",
  "game-combat",
  "game-items",
  "game-cards",
  "game-board",
  "game-puzzle"
]);
var TRAIT_FIELD_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
  "array",
  "object",
  "timestamp",
  "datetime",
  "enum",
  "email",
  "url",
  "phone",
  "uuid",
  "image",
  "trait",
  "slot",
  "pattern",
  "node",
  "event",
  "EventAddress",
  "scalar",
  "SExpr",
  "union",
  "tuple"
];
var TraitEntityFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(TRAIT_FIELD_TYPES),
  required: z.boolean().optional(),
  default: TraitConfigValueSchema.optional(),
  values: z.array(z.string()).optional(),
  items: ConfigFieldItemsDeclarationSchema.optional(),
  properties: z.lazy(() => z.record(TraitEntityFieldSchema)).optional()
});
var TraitDataEntitySchema = z.object({
  name: z.string().min(1),
  collection: z.string().optional(),
  fields: z.array(TraitEntityFieldSchema).min(1),
  timestamps: z.boolean().optional(),
  description: z.string().optional(),
  runtime: z.boolean().optional(),
  singleton: z.boolean().optional(),
  pages: z.array(z.string()).optional()
});
var DURATION_INTERVAL = /^\d+(ms|s|m|h|d)$/;
var CRON_INTERVAL = /^\S+(\s+\S+){4}$/;
var TickIntervalSchema = z.union([
  z.literal("frame"),
  z.number().positive(),
  z.string().refine((v) => DURATION_INTERVAL.test(v) || CRON_INTERVAL.test(v), {
    message: 'interval must be "frame", a positive number of milliseconds, a duration ("500ms"/"5s"/"1m"/"1h"/"1d"), or a 5-field cron expression ("0 9 * * *")'
  })
]);
var TraitTickSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  priority: z.number().optional(),
  interval: TickIntervalSchema,
  appliesTo: z.array(z.string()).optional(),
  runsInBackground: z.boolean().optional(),
  pages: z.array(z.string()).optional(),
  pageIds: z.array(PageIdSchema).optional(),
  guard: ExpressionSchema.optional(),
  effects: z.array(EffectSchema).min(1),
  emits: z.array(z.string()).optional(),
  emitIds: z.array(EventIdSchema).optional()
});
var EventScopeSchema = z.enum(["internal", "external"]);
var EventPayloadFieldSchema = z.object({
  name: z.string().min(1),
  /**
   * Field type. Mirrors the Rust validator's acceptance: any non-empty
   * string. Primitives ('string' | 'number' | 'boolean' | 'object' |
   * 'array') are the canonical values; entity-name references like
   * 'ModalRecord' and array-of-entity references like '[ModalRecord]'
   * are also valid because the Rust IR's PayloadField.field_type is
   * just a String. Only enforced narrowly at the call site (e.g.
   * emit-literal type-mismatch warnings in
   * orbital-compiler/phases/validation/emit_payload.rs) — not here.
   */
  type: z.string().min(1),
  required: z.boolean().optional(),
  values: z.array(z.string()).optional(),
  description: z.string().optional(),
  entity: z.string().optional(),
  properties: z.lazy(() => z.array(EventPayloadFieldSchema)).optional(),
  typeWhen: z.array(PayloadTypeWhenSchema).optional(),
  projectedFrom: FieldProjectionSchema.optional()
});
var CONFIG_REF_EVENT_PATTERN = /^@config\.[A-Za-z_][A-Za-z0-9_]*$/;
function configRefEventKnob(event) {
  return CONFIG_REF_EVENT_PATTERN.test(event) ? event.slice("@config.".length) : void 0;
}
function resolveConfigRefEventName(event, declaredConfig, effectiveConfig) {
  const knob = configRefEventKnob(event);
  if (knob === void 0) return { ok: false, error: "unknown-knob" };
  const field = declaredConfig?.[knob];
  if (field === void 0) return { ok: false, error: "unknown-knob" };
  if (field.type !== "string" && field.type !== "event") return { ok: false, error: "not-string" };
  if (field.default === void 0) return { ok: false, error: "no-default" };
  const value = effectiveConfig[knob];
  if (typeof value !== "string") return { ok: false, error: "not-string" };
  return { ok: true, value };
}
var TraitEventContractSchema = z.object({
  /**
   * Event name. Mirrors the Rust validator's `is_valid_event_identifier`:
   * starts with a letter, then any letters / digits / underscores. Both
   * UPPER_SNAKE_CASE and PascalCase shapes are valid identifiers in the
   * post-Phase 2.5 nominal-event type system (events declared via
   * `type X = Event<T>`). A pre-resolution `@config.<knob>` reference
   * (Option B) is also legal — inline/resolve substitutes it with the
   * knob's effective literal before codegen/runtime consume it.
   */
  event: z.string().min(1).regex(
    /^([A-Za-z][A-Za-z0-9_]*|@config\.[A-Za-z_][A-Za-z0-9_]*)$/,
    "Event name must start with a letter and contain only letters, digits, and underscores, or be a `@config.<knob>` reference"
  ),
  eventId: EventIdSchema.optional(),
  description: z.string().optional(),
  synonyms: z.string().optional(),
  tier: z.string().optional(),
  definerKnob: z.string().optional(),
  scopeOverridden: z.boolean().optional(),
  payloadSchema: z.array(EventPayloadFieldSchema).optional(),
  payloadEntity: z.string().optional(),
  scope: EventScopeSchema.optional()
});
var ListenSourceSchema = z.union([
  z.object({ kind: z.literal("any") }),
  z.object({
    kind: z.literal("trait"),
    trait: z.string().min(1),
    traitId: TraitIdSchema.optional()
  }),
  z.object({
    kind: z.literal("orbital"),
    orbital: z.string().min(1),
    trait: z.string().min(1),
    orbitalId: OrbitalIdSchema.optional(),
    traitId: TraitIdSchema.optional()
  })
]);
function splitEventAddress(raw) {
  const parts = raw.split(".");
  if (parts.length <= 1) return { event: raw };
  if (parts.length === 2) {
    const [src, event] = parts;
    return src === "*" ? { event, source: { kind: "any" } } : { event, source: { kind: "trait", trait: src } };
  }
  const n = parts.length;
  return {
    event: parts[n - 1],
    source: { kind: "orbital", orbital: parts.slice(0, n - 2).join("."), trait: parts[n - 2] }
  };
}
function joinEventAddress(event, source) {
  if (source === void 0) return event;
  switch (source.kind) {
    case "any":
      return `*.${event}`;
    case "trait":
      return `${source.trait}.${event}`;
    case "orbital":
      return `${source.orbital}.${source.trait}.${event}`;
  }
}
var TraitEventListenerSchema = z.object({
  event: z.string().min(1),
  eventId: EventIdSchema.optional(),
  triggers: z.string().min(1),
  triggersId: EventIdSchema.optional(),
  description: z.string().optional(),
  synonyms: z.string().optional(),
  tier: z.string().optional(),
  guard: ExpressionSchema.optional(),
  scope: EventScopeSchema.optional(),
  payloadMapping: z.record(SExprSchema).optional(),
  source: ListenSourceSchema.optional()
});
var RequiredFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(TRAIT_FIELD_TYPES),
  description: z.string().optional()
});
var TraitReferenceObjectSchema = z.object({
  ref: z.string().min(1),
  refId: TraitIdSchema.optional(),
  // V4 local declaration id (see the interface doc) — declared so the
  // strip-mode zod gate carries it through instead of dropping it.
  id: TraitIdSchema.optional(),
  // Phase 1.2: optional registry path disambiguator, pairs with `ref`.
  from: z.string().optional(),
  linkedEntity: z.string().optional(),
  linkedEntityId: EntityIdSchema.optional(),
  name: z.string().optional(),
  events: z.record(
    z.string().min(1, "events key (atom event name) must be non-empty"),
    z.string().min(1, "events value (caller event name) must be non-empty")
  ).optional(),
  eventIds: z.record(z.string().min(1), EventIdSchema).optional(),
  _resolved: z.lazy(() => TraitSchema).optional(),
  // 3-II type-parameter arguments (see `TraitReference.typeArgs`).
  typeArgs: z.record(
    z.string().min(1, "typeArgs key (declared param name) must be non-empty"),
    z.string().min(1, "typeArgs value (type name) must be non-empty")
  ).optional(),
  fields: z.record(
    z.string().min(1, "fields key (canonical field name) must be non-empty"),
    z.string().min(1, "fields value (consumer field name) must be non-empty")
  ).optional(),
  // Each value is either a plain wiring value (TraitConfigValue) or an
  // annotated ConfigFieldDeclaration. Declaration form is tried first
  // (it's more specific — has a `"type"` string key); plain values fall
  // through to the recursive TraitConfigValue union.
  config: z.record(z.union([ConfigFieldDeclarationSchema, TraitConfigValueSchema])).optional(),
  appliesTo: z.array(z.string()).optional(),
  // Phase F.7: caller-supplied listen entries are already-resolved
  // structured definitions (see `TraitReference.listens`).
  listens: z.array(TraitEventListenerSchema).optional(),
  emitsScope: z.enum(["internal", "external"]).optional(),
  // Phase F.8: per-transition effects override. The keys are event
  // names (the transition triggers AFTER renames); values are SExpr
  // effect tuples.
  effects: z.record(
    z.string().min(1, "effects override key (event name) must be non-empty"),
    z.array(SExprSchema)
  ).optional()
});
var TraitReferenceSchema = TraitReferenceObjectSchema.refine(
  (ref2) => {
    if (!ref2.events) return true;
    for (const [from, to] of Object.entries(ref2.events)) {
      if (from.length === 0 || to.length === 0) return false;
    }
    return true;
  },
  {
    message: 'TraitReference "events" entries must have non-empty atom and caller event names',
    path: ["events"]
  }
);
var TraitUIBindingSchema = z.record(
  z.object({
    presentation: z.enum(["modal", "drawer", "popover", "inline", "confirm-dialog"]),
    content: z.union([PatternObjectSchema, z.array(PatternObjectSchema)]),
    props: z.object({
      size: z.enum(["sm", "md", "lg", "xl", "full"]).optional(),
      position: z.enum(["left", "right", "top", "bottom", "center"]).optional(),
      title: z.string().optional(),
      closable: z.boolean().optional(),
      width: z.string().optional(),
      showProgress: z.boolean().optional(),
      step: z.number().optional(),
      totalSteps: z.number().optional()
    }).optional()
  })
);
var TraitScopeSchema = z.enum(["instance", "collection"]);
var EntityFieldContractSchema = z.object({
  requires: z.array(z.string()),
  provides: z.array(z.string())
});
var EffectSiteSchema = z.enum(["transition", "tick"]);
var EffectUseSchema = z.object({
  kind: z.string(),
  resource: z.string().optional(),
  resolved: z.boolean().optional(),
  site: EffectSiteSchema.optional()
});
var EffectRowSchema = z.array(EffectUseSchema);
var DispatchModeSchema = z.enum(["persistedAwaited", "hybridClientOnly", "runtimeOptimistic"]);
function computeDispatchMode(local, entityIsRuntime, touchesServer) {
  if (local) {
    return "hybridClientOnly";
  }
  if (entityIsRuntime) {
    return touchesServer ? "runtimeOptimistic" : "hybridClientOnly";
  }
  return "persistedAwaited";
}
function computeTraitDispatchMode(trait, entity, touchesServer) {
  return computeDispatchMode(trait.local ?? false, entity !== void 0 && isClientResident(entity), touchesServer);
}
var SourceBehaviorMetadataSchema = z.object({
  behavior: z.string().min(1),
  alias: z.string().min(1),
  originalName: z.string().min(1)
});
var TraitTypeParamDefSchema = z.object({
  name: z.string().min(1),
  kind: z.string().min(1),
  default: z.string().optional()
});
var TraitSchema = z.object({
  id: TraitIdSchema.optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  description_visual_prompt: z.string().optional(),
  category: TraitCategorySchema.optional(),
  entityRebindable: z.boolean().optional(),
  entityContract: EntityFieldContractSchema.optional(),
  effectRow: EffectRowSchema.optional(),
  entityBindingDescription: z.string().optional(),
  entityBindingSynonyms: z.string().optional(),
  capabilities: z.array(z.string()).optional(),
  scope: TraitScopeSchema,
  /** `local` trait-header flag — declares client-only dispatch (mirrors Rust `OirTrait::local`). Undeclared = server-gated dispatch. */
  local: z.boolean().optional(),
  linkedEntity: z.string().optional(),
  linkedEntityId: EntityIdSchema.optional(),
  entityRefIds: z.record(z.string().min(1), EntityIdSchema).optional(),
  traitEmbedIds: z.record(z.string().min(1), TraitIdSchema).optional(),
  requiredFields: z.array(RequiredFieldSchema).optional(),
  dataEntities: z.array(TraitDataEntitySchema).optional(),
  stateMachine: StateMachineSchema.optional(),
  initialEffects: z.array(EffectSchema).optional(),
  ticks: z.array(TraitTickSchema).optional(),
  emits: z.array(TraitEventContractSchema).optional(),
  listens: z.array(TraitEventListenerSchema).optional(),
  /** Call-site rename map (`events { ACTION: CONFIRM_VOID }`) — mirrors `Trait.events`. */
  events: z.record(z.string().min(1), z.string().min(1)).optional(),
  ui: TraitUIBindingSchema.optional(),
  config: DeclaredTraitConfigSchema.optional(),
  sourceBehavior: SourceBehaviorMetadataSchema.optional(),
  sourceEntityDefinition: EntitySchema.optional(),
  typeParams: z.array(TraitTypeParamDefSchema).optional()
});
var TraitRefSchema = z.union([
  z.string().min(1),
  TraitReferenceSchema,
  TraitSchema
  // Allow inline trait definitions
]);
function isInlineTrait(traitRef) {
  return typeof traitRef === "object" && "name" in traitRef && !("ref" in traitRef);
}
function getTraitName(traitRef) {
  if (typeof traitRef === "string") {
    return traitRef;
  }
  if (isInlineTrait(traitRef)) {
    return traitRef.name;
  }
  return traitRef.ref;
}
function getTraitConfig(traitRef) {
  if (typeof traitRef === "string") {
    return void 0;
  }
  if (isInlineTrait(traitRef)) {
    return void 0;
  }
  return traitRef.config;
}
function normalizeTraitRef(traitRef) {
  if (typeof traitRef === "string") {
    return { ref: traitRef };
  }
  if (isInlineTrait(traitRef)) {
    return { ref: traitRef.name };
  }
  return traitRef;
}
var OrbitalTraitRefSchema = TraitRefSchema;

// src/types/page.ts
var ViewTypeSchema = z.enum([
  "list",
  "detail",
  "create",
  "edit",
  "dashboard",
  "custom"
]);
var PageTraitRefSchema = z.object({
  ref: z.string().min(1, "Trait ref is required"),
  refId: TraitIdSchema.optional(),
  linkedEntity: z.string().optional(),
  linkedEntityId: EntityIdSchema.optional(),
  config: TraitConfigSchema.optional()
});
var PageAccessSchema = z.enum(["public", "authenticated"]);
var PageIndexingSchema = z.enum(["index", "noindex"]);
var PageMetaSchema = z.union([
  z.string(),
  z.tuple([z.literal("i18n/t"), z.string().min(1)])
]);
function resolvePageMeta(meta, lookup) {
  if (meta === void 0) return void 0;
  return typeof meta === "string" ? meta : lookup(meta[1]);
}
var PageModifiersSchema = z.object({
  access: PageAccessSchema.optional(),
  indexing: PageIndexingSchema.optional(),
  title: PageMetaSchema.optional(),
  description: PageMetaSchema.optional(),
  translationOf: z.string().min(1).optional(),
  label: z.string().optional(),
  icon: z.string().optional(),
  roles: z.array(z.string().min(1)).min(1).optional()
}).strict();
var OrbitalPageStrictSchema = z.object({
  id: PageIdSchema.optional(),
  name: z.string().min(1, "Page name is required"),
  path: z.string().min(1, "Page path is required").startsWith("/", "Path must start with /"),
  primaryEntity: z.string().min(1, "Primary entity is required"),
  traits: z.array(PageTraitRefSchema).min(1, "Page must have at least one trait"),
  title: PageMetaSchema.optional(),
  description: PageMetaSchema.optional(),
  access: PageAccessSchema.optional(),
  indexing: PageIndexingSchema.optional(),
  sourcePage: z.string().min(1).optional(),
  translationOf: z.string().min(1).optional(),
  icon: z.string().optional(),
  label: z.string().optional(),
  roles: z.array(z.string().min(1)).min(1).optional()
}).strict();
var OrbitalPageSchema = z.object({
  id: PageIdSchema.optional(),
  name: z.string().min(1, "Page name is required"),
  path: z.string().min(1, "Page path is required").startsWith("/", "Path must start with /"),
  viewType: ViewTypeSchema.optional(),
  title: PageMetaSchema.optional(),
  description: PageMetaSchema.optional(),
  access: PageAccessSchema.optional(),
  indexing: PageIndexingSchema.optional(),
  sourcePage: z.string().min(1).optional(),
  translationOf: z.string().min(1).optional(),
  primaryEntity: z.string().optional(),
  traits: z.array(PageTraitRefSchema).optional(),
  isInitial: z.boolean().optional(),
  icon: z.string().optional(),
  label: z.string().optional(),
  roles: z.array(z.string().min(1)).min(1).optional()
}).strict();
var PageSchema = OrbitalPageSchema;
var DomainCategorySchema = z.enum([
  "healthcare",
  "education",
  "finance",
  "ecommerce",
  "real-estate",
  "logistics",
  "hospitality",
  "hr-management",
  "project-management",
  "social",
  "content-management",
  "iot",
  "analytics",
  "game",
  "custom"
]);
var AGENT_DOMAIN_CATEGORIES = [
  "game",
  "business",
  "dashboard",
  "form",
  "content",
  "social",
  "ecommerce",
  "workflow"
];
var AgentDomainCategorySchema = z.enum([...AGENT_DOMAIN_CATEGORIES]);
var GameSubCategorySchema = z.enum([
  "platformer",
  "shooter",
  "puzzle",
  "rpg",
  "board",
  "racing",
  "fighting",
  "tower-defense",
  "endless-runner",
  "simulation",
  "arcade",
  "adventure"
]);
var NodeClassificationSchema = z.enum(["domain", "system"]);
var StateSemanticRoleSchema = z.enum([
  "pending",
  "active",
  "completed",
  "cancelled",
  "error",
  "suspended",
  "blocked",
  "domain_workflow",
  "domain_status",
  "system_loading",
  "system_error",
  "system_idle",
  "system_editing",
  "system_confirming"
]);
var EventSemanticRoleSchema = z.enum([
  "domain_action",
  "domain_trigger",
  "system_crud",
  "system_navigation",
  "system_form",
  "system_ui"
]);
var EntitySemanticRoleSchema = z.enum([
  "domain_core",
  "domain_supporting",
  "domain_reference",
  "system_user",
  "system_config",
  "system_audit"
]);
var DomainVocabularySchema = z.record(z.string(), z.string()).optional();
var UserPersonaSchema = z.object({
  name: z.string().min(1),
  role: z.string().optional(),
  device: z.enum(["mobile", "tablet", "desktop"]).optional()
});
var DomainContextSchema = z.object({
  request: z.string().min(1, "Original request is required"),
  requestFragment: z.string().optional(),
  category: AgentDomainCategorySchema,
  subDomain: z.string().optional(),
  personas: z.array(UserPersonaSchema).optional(),
  vocabulary: DomainVocabularySchema.optional()
});
var UXHintsSchema = z.object({
  flowPattern: z.string().optional(),
  listPattern: z.string().optional(),
  formPattern: z.string().optional(),
  detailPattern: z.string().optional(),
  relatedLinks: z.array(z.lazy(() => RelatedLinkSchema)).optional()
});
var RelatedLinkSchema = z.object({
  relation: z.string().min(1),
  label: z.string().min(1),
  targetView: z.enum(["list", "detail"]).optional()
});
var SuggestedGuardSchema = z.object({
  id: z.string().min(1),
  description: z.string().min(1),
  appliesTo: z.array(z.string().min(1))
});
var DesignPreferencesSchema = z.object({
  style: z.enum(["minimal", "modern", "playful", "data-driven", "immersive"]).optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be valid hex color").optional(),
  device: z.enum(["mobile", "tablet", "desktop", "all"]).optional(),
  darkMode: z.boolean().optional(),
  uxHints: UXHintsSchema.optional()
});
var SpacingScaleSchema = z.object({
  space0: z.string().optional(),
  space1: z.string().optional(),
  space2: z.string().optional(),
  space3: z.string().optional(),
  space4: z.string().optional(),
  space5: z.string().optional(),
  space6: z.string().optional(),
  space7: z.string().optional(),
  space8: z.string().optional(),
  space9: z.string().optional(),
  space10: z.string().optional(),
  space11: z.string().optional(),
  space12: z.string().optional()
});
var DensityTokensSchema = z.object({
  spacing: SpacingScaleSchema.optional(),
  buttonHeightSm: z.string().optional(),
  buttonHeightMd: z.string().optional(),
  buttonHeightLg: z.string().optional(),
  inputHeightSm: z.string().optional(),
  inputHeightMd: z.string().optional(),
  inputHeightLg: z.string().optional(),
  rowHeightCompact: z.string().optional(),
  rowHeightNormal: z.string().optional(),
  rowHeightSpacious: z.string().optional(),
  cardPaddingSm: z.string().optional(),
  cardPaddingMd: z.string().optional(),
  cardPaddingLg: z.string().optional(),
  dialogPadding: z.string().optional(),
  sectionGap: z.string().optional()
});
var TypeScaleEntrySchema = z.object({
  size: z.string(),
  lineHeight: z.string()
});
var TypeSlotSchema = z.enum(["display", "body", "mono"]);
var TypeSizeKeySchema = z.enum([
  "xs",
  "sm",
  "base",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl",
  "display-1",
  "display-2"
]);
var TypeWeightSchema = z.enum(["normal", "medium", "bold"]);
var TypeIntentSchema = z.object({
  slot: TypeSlotSchema,
  size: TypeSizeKeySchema,
  weight: TypeWeightSchema
});
var TypeScaleSchema = z.object({
  xs: TypeScaleEntrySchema.optional(),
  sm: TypeScaleEntrySchema.optional(),
  base: TypeScaleEntrySchema.optional(),
  lg: TypeScaleEntrySchema.optional(),
  xl: TypeScaleEntrySchema.optional(),
  "2xl": TypeScaleEntrySchema.optional(),
  "3xl": TypeScaleEntrySchema.optional(),
  "4xl": TypeScaleEntrySchema.optional(),
  "display-1": TypeScaleEntrySchema.optional(),
  "display-2": TypeScaleEntrySchema.optional()
});
var TypeIntentMapSchema = z.object({
  headingMajor: TypeIntentSchema.optional(),
  headingMinor: TypeIntentSchema.optional(),
  bodyEmphasis: TypeIntentSchema.optional(),
  bodyDefault: TypeIntentSchema.optional(),
  bodyQuiet: TypeIntentSchema.optional(),
  caption: TypeIntentSchema.optional(),
  numeric: TypeIntentSchema.optional()
});
function isWellFormedFontStack(stack) {
  const items = [];
  let current = "";
  let quote = null;
  let depth = 0;
  for (const ch of stack) {
    if (quote) {
      current += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      items.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  if (quote || depth !== 0) return false;
  items.push(current.trim());
  return items.every(
    (item) => /^"[^"]+"$/.test(item) || /^'[^']+'$/.test(item) || /^-?[A-Za-z][\w-]*( [A-Za-z0-9][\w-]*)*$/.test(item) || /^var\(--[\w-]+(,.*)?\)$/.test(item)
  );
}
var FontStackSchema = z.string().refine(isWellFormedFontStack, {
  message: "Malformed CSS font-family stack (each family must be comma-separated and fully quoted or bare)"
});
var LegacyTypographySchema = z.record(z.string(), z.string()).superRefine((map, ctx) => {
  for (const [key, value] of Object.entries(map)) {
    if (key.startsWith("font-family") && !isWellFormedFontStack(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: `Malformed CSS font-family stack: ${value}` });
    }
  }
});
var TypeScaleTokensSchema = z.object({
  displayFamily: FontStackSchema.optional(),
  bodyFamily: FontStackSchema.optional(),
  monoFamily: FontStackSchema.optional(),
  fontImport: z.string().optional(),
  headingWeight: z.string().optional(),
  headingTransform: z.string().optional(),
  headingTracking: z.string().optional(),
  headingStyle: z.string().optional(),
  headingShadow: z.string().optional(),
  scale: TypeScaleSchema.optional(),
  intents: TypeIntentMapSchema.optional()
});
var MotionDurationKeySchema = z.enum([
  "instant",
  "fast",
  "normal",
  "slow",
  "dramatic"
]);
var MotionEasingKeySchema = z.enum([
  "linear",
  "standard",
  "emphasized",
  "spring"
]);
var MotionIntentSchema = z.object({
  duration: MotionDurationKeySchema,
  easing: MotionEasingKeySchema
});
var MotionDurationPaletteSchema = z.object({
  instant: z.string().optional(),
  fast: z.string().optional(),
  normal: z.string().optional(),
  slow: z.string().optional(),
  dramatic: z.string().optional()
});
var MotionEasingPaletteSchema = z.object({
  linear: z.string().optional(),
  standard: z.string().optional(),
  emphasized: z.string().optional(),
  spring: z.string().optional()
});
var MotionIntentMapSchema = z.object({
  enter: MotionIntentSchema.optional(),
  exit: MotionIntentSchema.optional(),
  hover: MotionIntentSchema.optional(),
  press: MotionIntentSchema.optional(),
  expand: MotionIntentSchema.optional(),
  transition: MotionIntentSchema.optional()
});
var MotionShapeTokensSchema = z.object({
  modalEnter: z.string().optional(),
  modalExit: z.string().optional(),
  popoverEnter: z.string().optional(),
  popoverExit: z.string().optional(),
  toastEnter: z.string().optional(),
  toastExit: z.string().optional(),
  pageEnter: z.string().optional(),
  enterRise: z.string().optional(),
  enterScale: z.string().optional(),
  enterSlide: z.string().optional()
});
var ENTER_ANIMATIONS = ["none", "fade", "rise", "scale", "slide"];
var EnterAnimationSchema = z.enum(ENTER_ANIMATIONS);
var ENTER_DELAY_STEPS = [1, 2, 3, 4, 5, 6, 7, 8];
var MotionEntryTokensSchema = z.object({
  default: EnterAnimationSchema.optional(),
  stagger: z.string().optional()
});
var MotionTokensSchema = z.object({
  durations: MotionDurationPaletteSchema.optional(),
  easings: MotionEasingPaletteSchema.optional(),
  intents: MotionIntentMapSchema.optional(),
  shapes: MotionShapeTokensSchema.optional(),
  entry: MotionEntryTokensSchema.optional(),
  busyDelay: z.string().optional(),
  awaitingSkeleton: z.boolean().optional()
});
var IconFamilySchema = z.enum([
  "lucide",
  "phosphor-outline",
  "phosphor-fill",
  "phosphor-duotone",
  "tabler",
  "fa-solid"
]);
var IconographyTokensSchema = z.object({
  family: IconFamilySchema.optional(),
  strokeWidth: z.string().optional(),
  defaultSize: z.string().optional()
});
var ElevationTokensSchema = z.object({
  cardElevation: z.string().optional(),
  popoverElevation: z.string().optional(),
  dialogElevation: z.string().optional(),
  toastElevation: z.string().optional(),
  interactiveElevation: z.string().optional(),
  pressedElevation: z.string().optional()
});
var GeometryTokensSchema = z.object({
  radiusContainer: z.string().optional(),
  radiusInteractive: z.string().optional(),
  radiusPill: z.string().optional(),
  borderHairline: z.string().optional(),
  borderStandard: z.string().optional(),
  borderHeavy: z.string().optional(),
  cornerShape: z.string().optional(),
  cornerShapePill: z.string().optional(),
  cornerShapeInteractive: z.string().optional(),
  borderStyle: z.string().optional(),
  borderStyleInteractive: z.string().optional()
});
var SurfaceTokensSchema = z.object({
  backdrop: z.string().optional(),
  cardImage: z.string().optional(),
  pageImage: z.string().optional(),
  pageImageSize: z.string().optional(),
  diagram: z.string().optional(),
  edgeMask: z.string().optional(),
  edgeHeight: z.string().optional(),
  textureMask: z.string().optional(),
  textureSize: z.string().optional(),
  accentImage: z.string().optional(),
  scrim: z.string().optional()
});
var DiagramLineCapSchema = z.enum(["butt", "round", "square"]);
var DiagramLineJoinSchema = z.enum(["miter", "round", "bevel"]);
var DiagramFillStyleSchema = z.enum(["solid", "tint", "outline", "hatch"]);
var DiagramMarkerSchema = z.enum(["triangle", "open", "line", "dot"]);
var DiagramLabelFontSchema = z.enum(["body", "display", "mono"]);
var DiagramLabelCaseSchema = z.enum(["none", "uppercase"]);
var DIAGRAM_TOKEN_KEY_LIST = [
  "strokeThin",
  "strokeNormal",
  "strokeBold",
  "lineCap",
  "lineJoin",
  "dash",
  "dot",
  "fillStyle",
  "fillOpacity",
  "roughness",
  "glow",
  "shadow",
  "marker",
  "labelFont",
  "labelSize",
  "labelWeight",
  "labelCase",
  "corner"
];
var DIAGRAM_TOKEN_KEYS = DIAGRAM_TOKEN_KEY_LIST;
var DiagramTokensSchema = z.object({
  strokeThin: z.string().optional(),
  strokeNormal: z.string().optional(),
  strokeBold: z.string().optional(),
  lineCap: DiagramLineCapSchema.optional(),
  lineJoin: DiagramLineJoinSchema.optional(),
  dash: z.string().optional(),
  dot: z.string().optional(),
  fillStyle: DiagramFillStyleSchema.optional(),
  fillOpacity: z.string().optional(),
  roughness: z.string().optional(),
  glow: z.string().optional(),
  shadow: z.string().optional(),
  marker: DiagramMarkerSchema.optional(),
  labelFont: DiagramLabelFontSchema.optional(),
  labelSize: z.string().optional(),
  labelWeight: z.string().optional(),
  labelCase: DiagramLabelCaseSchema.optional(),
  corner: z.string().optional()
});
var SCENE_TOKEN_KEY_LIST = [
  "materialRoughness",
  "materialMetalness",
  "materialFlat",
  "materialOutline",
  "lightAmbient",
  "lightKey",
  "lightKeyColor",
  "fog"
];
var SCENE_TOKEN_KEYS = SCENE_TOKEN_KEY_LIST;
var SceneTokensSchema = z.object({
  materialRoughness: z.string().optional(),
  materialMetalness: z.string().optional(),
  materialFlat: z.string().optional(),
  materialOutline: z.string().optional(),
  lightAmbient: z.string().optional(),
  lightKey: z.string().optional(),
  lightKeyColor: z.string().optional(),
  fog: z.string().optional()
});
var COLOR_TOKEN_KEY_LIST = [
  "primary",
  "primaryHover",
  "primaryForeground",
  "secondary",
  "secondaryHover",
  "secondaryForeground",
  "accent",
  "accentForeground",
  "muted",
  "mutedForeground",
  "background",
  "foreground",
  "card",
  "cardForeground",
  "surface",
  "border",
  "input",
  "ring",
  "error",
  "errorForeground",
  "success",
  "successForeground",
  "warning",
  "warningForeground",
  "info",
  "infoForeground",
  "tableHeader",
  "tableBorder",
  "tableRowHover",
  "surfaceHover",
  "borderHover",
  "placeholder",
  "scrim"
];
var COLOR_TOKEN_KEYS = COLOR_TOKEN_KEY_LIST;
var ColorTokensSchema = z.object(
  Object.fromEntries(COLOR_TOKEN_KEYS.map((key) => [key, z.string().optional()]))
);
var IllustrationStyleSchema = z.enum([
  "minimal",
  "illustrated",
  "photo",
  "text-only",
  "mascot"
]);
var ILLUSTRATION_TOKEN_KEY_LIST = [
  "style",
  "emptyAsset",
  "loadingAsset",
  "errorAsset",
  "onboardingAsset"
];
var ILLUSTRATION_TOKEN_KEYS = ILLUSTRATION_TOKEN_KEY_LIST;
var IllustrationTokensSchema = z.object({
  style: IllustrationStyleSchema.optional(),
  emptyAsset: z.string().optional(),
  loadingAsset: z.string().optional(),
  errorAsset: z.string().optional(),
  onboardingAsset: z.string().optional()
});
var ThemeTokensSchema = z.object({
  color: ColorTokensSchema.optional(),
  density: DensityTokensSchema.optional(),
  typeScale: TypeScaleTokensSchema.optional(),
  motion: MotionTokensSchema.optional(),
  iconography: IconographyTokensSchema.optional(),
  elevation: ElevationTokensSchema.optional(),
  geometry: GeometryTokensSchema.optional(),
  illustration: IllustrationTokensSchema.optional(),
  surface: SurfaceTokensSchema.optional(),
  diagram: DiagramTokensSchema.optional(),
  scene: SceneTokensSchema.optional(),
  // Legacy
  colors: z.record(z.string(), z.string()).optional(),
  radii: z.record(z.string(), z.string()).optional(),
  spacing: z.record(z.string(), z.string()).optional(),
  typography: LegacyTypographySchema.optional(),
  shadows: z.record(z.string(), z.string()).optional()
});
var ThemeVariantSchema = z.object({
  color: ColorTokensSchema.optional(),
  density: DensityTokensSchema.optional(),
  typeScale: TypeScaleTokensSchema.optional(),
  motion: MotionTokensSchema.optional(),
  iconography: IconographyTokensSchema.optional(),
  elevation: ElevationTokensSchema.optional(),
  geometry: GeometryTokensSchema.optional(),
  illustration: IllustrationTokensSchema.optional(),
  surface: SurfaceTokensSchema.optional(),
  diagram: DiagramTokensSchema.optional(),
  scene: SceneTokensSchema.optional(),
  colors: z.record(z.string(), z.string()).optional(),
  radii: z.record(z.string(), z.string()).optional(),
  spacing: z.record(z.string(), z.string()).optional(),
  typography: LegacyTypographySchema.optional(),
  shadows: z.record(z.string(), z.string()).optional()
});
var ThemeDefinitionSchema = z.object({
  name: z.string().min(1, "Theme name is required"),
  displayName: z.string().optional(),
  tokens: ThemeTokensSchema,
  variants: z.record(z.string(), ThemeVariantSchema).optional()
});
function isThemeReference(theme) {
  return typeof theme === "string";
}
var THEME_REGISTRY_KEY_PATTERN = "[a-z][a-z0-9]*(-[a-z0-9]+)*";
var THEME_REGISTRY_KEY = new RegExp(`^${THEME_REGISTRY_KEY_PATTERN}$`);
function isThemeRegistryKey(theme) {
  return typeof theme === "string" && THEME_REGISTRY_KEY.test(theme);
}
function themeDataKey(theme) {
  if (theme === void 0) return "";
  if (typeof theme === "string") return theme;
  return theme.name;
}
var ThemeRefStringSchema = z.string().regex(
  new RegExp(`^([A-Z][a-zA-Z0-9]*\\.theme|${THEME_REGISTRY_KEY_PATTERN})$`),
  'Theme reference must be "Alias.theme" or a registry theme key (e.g., "linear-clean-light")'
);
var ThemeRefSchema = z.union([
  ThemeDefinitionSchema,
  ThemeRefStringSchema
]);
var SkinSpecSchema = ThemeDefinitionSchema;
var ColorSliceSchema = ColorTokensSchema;
var DensitySliceSchema = DensityTokensSchema;
var TypeSliceSchema = TypeScaleTokensSchema;
var GeometrySliceSchema = GeometryTokensSchema;
var ElevationSliceSchema = ElevationTokensSchema;
var MotionSliceSchema = MotionTokensSchema;
var IconographySliceSchema = IconographyTokensSchema;
var IllustrationSliceSchema = IllustrationTokensSchema;
var SurfaceSliceSchema = SurfaceTokensSchema;
var DesignTokensSchema = z.record(z.string(), z.record(z.string(), z.string())).optional();
var ALLOWED_CUSTOM_COMPONENTS = [
  "div",
  "span",
  "button",
  "a",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "footer",
  "section",
  "article",
  "nav",
  "main",
  "aside",
  "ul",
  "ol",
  "li",
  "img",
  "label",
  "input",
  "form"
];
var CustomPatternDefinitionSchema = z.object({
  type: z.literal("custom"),
  component: z.enum(ALLOWED_CUSTOM_COMPONENTS),
  className: z.string(),
  slots: z.array(z.string()).optional(),
  props: z.array(z.string()).optional()
});
var CustomPatternMapSchema = z.record(z.string(), CustomPatternDefinitionSchema).optional();
var SERVICE_TYPES = ["rest", "socket", "mcp"];
var ServiceTypeSchema = z.enum(SERVICE_TYPES);
var RestAuthConfigSchema = z.object({
  type: z.enum(["api-key", "bearer", "basic", "oauth2"]),
  keyName: z.string().optional(),
  location: z.enum(["query", "header"]).optional(),
  secretEnv: z.string().optional()
});
var RestServiceDefSchema = z.object({
  name: z.string().min(1, "Service name is required"),
  type: z.literal("rest"),
  description: z.string().optional(),
  baseUrl: z.string().url("Base URL must be a valid URL"),
  headers: z.record(z.string()).optional(),
  auth: RestAuthConfigSchema.optional(),
  timeout: z.number().positive().optional()
});
var SocketEventsSchema = z.object({
  inbound: z.array(z.string()),
  outbound: z.array(z.string())
});
var SocketServiceDefSchema = z.object({
  name: z.string().min(1, "Service name is required"),
  type: z.literal("socket"),
  description: z.string().optional(),
  url: z.string().url("WebSocket URL must be valid"),
  events: SocketEventsSchema,
  reconnect: z.object({
    enabled: z.boolean(),
    maxAttempts: z.number().positive().optional(),
    delayMs: z.number().positive().optional()
  }).optional()
});
var McpServiceDefSchema = z.object({
  name: z.string().min(1, "Service name is required"),
  type: z.literal("mcp"),
  description: z.string().optional(),
  serverPath: z.string().min(1, "Server path is required"),
  capabilities: z.array(z.string()).min(1, "At least one capability is required"),
  env: z.record(z.string()).optional()
});
var ServiceDefinitionSchema = z.discriminatedUnion("type", [
  RestServiceDefSchema,
  SocketServiceDefSchema,
  McpServiceDefSchema
]);
function isServiceReference(service) {
  return typeof service === "string";
}
function isServiceReferenceObject(service) {
  return typeof service === "object" && service !== null && "ref" in service && !("type" in service);
}
var ServiceRefStringSchema = z.string().regex(
  /^[A-Z][a-zA-Z0-9]*\.services\.[a-zA-Z][a-zA-Z0-9]*$/,
  'Service reference must be in format "Alias.services.ServiceName" (e.g., "Weather.services.openweather")'
);
var ServiceRefObjectSchema = z.object({
  ref: ServiceRefStringSchema,
  description: z.string().optional(),
  baseUrl: z.string().url("baseUrl override must be a valid URL").optional(),
  headers: z.record(z.string()).optional(),
  url: z.string().url("url override must be a valid URL").optional(),
  serverPath: z.string().optional()
});
var ServiceRefSchema = z.union([
  ServiceDefinitionSchema,
  ServiceRefObjectSchema,
  ServiceRefStringSchema
]);
function parseServiceRef(ref2) {
  const match = ref2.match(
    /^([A-Z][a-zA-Z0-9]*)\.services\.([a-zA-Z][a-zA-Z0-9]*)$/
  );
  if (!match) return null;
  return { alias: match[1], serviceName: match[2] };
}
function isRestService(service) {
  return service.type === "rest";
}
function isSocketService(service) {
  return service.type === "socket";
}
function isMcpService(service) {
  return service.type === "mcp";
}
function getServiceNames(services) {
  return services.map((s) => s.name);
}
function findService(services, name) {
  return services.find((s) => s.name.toLowerCase() === name.toLowerCase());
}
function hasService(services, name) {
  return services.some((s) => s.name.toLowerCase() === name.toLowerCase());
}

// src/types/orbital.ts
var UseDeclarationSchema = z.object({
  from: z.string().min(1, "Import source is required"),
  as: z.string().min(1, "Alias is required").regex(
    /^[A-Z][a-zA-Z0-9]*$/,
    'Alias must be PascalCase (e.g., "Health", "GameCore")'
  ),
  config: DeclaredTraitConfigSchema.optional(),
  lazy: z.boolean().optional()
});
var LazyPageSchema = z.object({
  path: z.string().min(1),
  orbital: z.string().min(1),
  orbRef: z.string().min(1)
});
function expectedEntityName(decl) {
  switch (decl.kind) {
    case "entity":
      return decl.name;
    case "identity":
      return decl.name;
    case "event":
    case "page":
      return void 0;
  }
}
var ExpectDeclarationSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("identity"),
    name: z.string().optional(),
    shape: z.array(EntityFieldSchema).optional()
  }),
  z.object({
    kind: z.literal("entity"),
    name: z.string().min(1, "Expected entity name is required"),
    shape: z.array(EntityFieldSchema).optional()
  }),
  z.object({
    kind: z.literal("event"),
    traitName: z.string().min(1, "Expected event trait name is required"),
    event: z.string().min(1, "Expected event name is required")
  }),
  z.object({
    kind: z.literal("page"),
    path: z.string().min(1, "Expected page path is required")
  })
]);
var ExpectationDiagnosticSchema = z.object({
  kind: z.enum(["unknown-orbital", "identity-field-not-declared", "entity-field-not-declared", "event-source-unknown"]),
  orbital: z.string(),
  entity: z.string().optional(),
  field: z.string().optional()
}).strict();
var OrganismExpectationsSchema = z.record(
  z.object({ expectations: z.array(ExpectDeclarationSchema), diagnostics: z.array(ExpectationDiagnosticSchema) }).strict()
);
function isEntityReference(entity) {
  return typeof entity === "string";
}
function isEntityCall(entity) {
  return typeof entity === "object" && entity !== null && "extends" in entity;
}
function isEntityReferenceAny(entity) {
  return isEntityReference(entity) || isEntityCall(entity);
}
var EntityRefStringSchema = z.string().regex(
  /^[A-Z][a-zA-Z0-9]*(\.orbitals\.[A-Z][a-zA-Z0-9]*)?\.entity$/,
  'Entity reference must be in format "Alias.entity" (e.g., "Goblin.entity") or "Alias.orbitals.Name.entity"'
);
var EntityCallSchema = z.object({
  extends: z.string().regex(
    /^[A-Z][a-zA-Z0-9]*\.entity$/,
    'EntityCall "extends" must be in format "Alias.entity"'
  ),
  name: z.string().optional(),
  fields: z.array(EntityFieldSchema).optional(),
  persistence: EntityPersistenceSchema.optional(),
  collection: z.string().optional()
}).refine(
  (call) => {
    if (!call.fields) return true;
    const seen = /* @__PURE__ */ new Set();
    for (const field of call.fields) {
      if (field.name === void 0) continue;
      if (seen.has(field.name)) return false;
      seen.add(field.name);
    }
    return true;
  },
  {
    message: 'EntityCall "fields" override list must not contain duplicate field names',
    path: ["fields"]
  }
);
var EntityRefSchema = z.union([
  EntitySchema,
  EntityRefStringSchema,
  EntityCallSchema
]);
function isPageReferenceString(page) {
  return typeof page === "string";
}
function isPageReferenceObject(page) {
  return typeof page === "object" && "ref" in page && !("name" in page);
}
function isPageReference(page) {
  return isPageReferenceString(page) || isPageReferenceObject(page);
}
var PageRefStringSchema = z.string().regex(
  /^[A-Z][a-zA-Z0-9]*\.pages\.[A-Z][a-zA-Z0-9]*$/,
  'Page reference must be in format "Alias.pages.PageName" (e.g., "User.pages.Profile")'
);
var PageRefObjectSchema = z.object({
  ref: PageRefStringSchema,
  refId: PageIdSchema.optional(),
  // Phase 1.2: optional registry path disambiguator, pairs with `ref`.
  from: z.string().optional(),
  path: z.string().startsWith("/").optional(),
  linkedEntity: z.string().optional(),
  linkedEntityId: EntityIdSchema.optional(),
  traits: z.array(TraitRefSchema).optional(),
  traitRefIds: z.array(TraitIdSchema).optional(),
  access: PageAccessSchema.optional(),
  indexing: PageIndexingSchema.optional(),
  title: PageMetaSchema.optional(),
  description: PageMetaSchema.optional(),
  translationOf: z.string().min(1).optional(),
  label: z.string().optional(),
  icon: z.string().optional(),
  roles: z.array(z.string().min(1)).min(1).optional()
});
var PageRefSchema = z.union([
  PageSchema,
  PageRefStringSchema,
  PageRefObjectSchema
]);
var OrbitalRefStringSchema = z.string().regex(
  /^[A-Z][a-zA-Z0-9]*\.orbitals\.[A-Z][a-zA-Z0-9]*$/,
  'Orbital reference must be "Alias.orbitals.OrbitalName"'
);
var OrbitalRefObjectSchema = z.object({
  ref: OrbitalRefStringSchema,
  refId: OrbitalIdSchema.optional(),
  entity: z.string().optional(),
  fields: z.record(z.string()).optional(),
  pages: z.record(z.string()).optional(),
  pageModifiers: z.record(z.string(), PageModifiersSchema).optional(),
  omit: z.array(z.string()).optional(),
  only: z.array(z.string()).optional(),
  config: DeclaredTraitConfigSchema.optional(),
  events: z.record(z.string()).optional(),
  roles: z.record(z.string(), z.array(z.string())).optional(),
  entities: z.record(z.string(), z.string()).optional(),
  mounts: z.record(z.string(), z.array(z.string())).optional(),
  extend: z.array(EntityFieldSchema).optional(),
  mock: z.record(z.string(), z.string()).optional(),
  retype: z.array(EntityFieldSchema).optional(),
  persistence: EntityPersistenceSchema.optional(),
  collection: z.string().optional(),
  shared: z.boolean().optional(),
  identity: z.boolean().optional(),
  local: z.boolean().optional(),
  seedMock: z.boolean().optional(),
  traits: z.record(
    z.string(),
    z.object({
      linkedEntity: z.string().optional(),
      config: DeclaredTraitConfigSchema.optional(),
      events: z.record(z.string()).optional(),
      fields: z.record(z.string()).optional(),
      listens: z.array(TraitEventListenerSchema).optional(),
      emitsScope: EventScopeSchema.optional()
    })
  ).optional(),
  listens: z.array(TraitEventListenerSchema.extend({ trait: z.string() })).optional()
});
function parseOrbitalRef(ref2) {
  const match = ref2.match(/^([A-Z][a-zA-Z0-9]*)\.orbitals\.([A-Z][a-zA-Z0-9]*)$/);
  if (!match) return null;
  return { alias: match[1], orbitalName: match[2] };
}
z.string().regex(
  /^([A-Z][a-zA-Z0-9]*\.traits\.)?[A-Z][a-zA-Z0-9]*$/,
  'Trait reference must be "TraitName" or "Alias.traits.TraitName"'
);
function isImportedTraitRef(ref2) {
  return /^[A-Z][a-zA-Z0-9]*\.traits\.[A-Z][a-zA-Z0-9]*$/.test(ref2);
}
function parseImportedTraitRef(ref2) {
  const match = ref2.match(/^([A-Z][a-zA-Z0-9]*)\.traits\.([A-Z][a-zA-Z0-9]*)$/);
  if (!match) return null;
  return { alias: match[1], traitName: match[2] };
}
function parseEntityRef(ref2) {
  const match = ref2.match(/^([A-Z][a-zA-Z0-9]*)\.entity$/);
  if (!match) return null;
  return { alias: match[1] };
}
function parsePageRef(ref2) {
  const match = ref2.match(/^([A-Z][a-zA-Z0-9]*)\.pages\.([A-Z][a-zA-Z0-9]*)$/);
  if (!match) return null;
  return { alias: match[1], pageName: match[2] };
}
var EventListenerSchema = z.object({
  event: z.string().min(1),
  triggers: z.string().min(1),
  guard: z.string().optional()
});
var EventSourceSchema = z.object({
  trait: z.string().min(1),
  transition: z.string().optional(),
  tick: z.string().optional()
});
var ComputedEventContractSchema = z.object({
  event: z.string().min(1),
  originalEvent: z.string().min(1),
  source: EventSourceSchema,
  description: z.string().optional(),
  payload: z.array(EventPayloadFieldSchema).optional()
});
var ComputedEventListenerSchema = z.object({
  event: z.string().min(1),
  source: EventSourceSchema,
  triggers: z.string().min(1),
  guard: ExpressionSchema.optional(),
  payloadMapping: z.record(SExprSchema).optional()
});
var OrbitalDefinitionSchema = z.object({
  id: OrbitalIdSchema.optional(),
  name: z.string().min(1, "Orbital name is required"),
  description: z.string().optional(),
  visual_prompt: z.string().optional(),
  // Import system
  uses: z.array(UseDeclarationSchema).optional(),
  // Consumer-side requirement declarations (`.lolo` `expects ...`)
  expects: z.array(ExpectDeclarationSchema).optional(),
  // Theme & Services
  theme: ThemeRefSchema.optional(),
  services: z.array(ServiceRefSchema).optional(),
  // Components (inline or reference)
  entity: EntityRefSchema,
  // Mirrors the `OrbitalDefinition.auxiliaryEntities` type field — WAS
  // undeclared here, so `OrbitalSchemaSchema.safeParse` (every load through
  // `external-loader.ts`) silently stripped it on every externally-loaded
  // orbital (zod drops unrecognized keys by default), while a schema parsed
  // directly via `JSON.parse` kept it. Found via the entity-field auto-merge
  // rebind-target fallback landing on the wrong entity for any RECURSIVELY
  // loaded `uses` file (`packages/almadar-runtime/src/resolver/
  // reference-resolver.ts`'s `mergeImportedEntityFieldsIntoOrbital`).
  auxiliaryEntities: z.array(EntityRefSchema).optional(),
  traits: z.array(TraitRefSchema),
  // A reference-form orbital's pages come from its upstream until inline.
  pages: z.array(PageRefSchema).default([]),
  // Event interface (trait-centric model) - computed by resolver
  emits: z.array(ComputedEventContractSchema).optional(),
  listens: z.array(ComputedEventListenerSchema).optional(),
  // Filter for exposed events (trait-centric model)
  exposes: z.array(z.string()).optional(),
  // This orbital's own declared knobs (§4.5)
  config: DeclaredTraitConfigSchema.optional(),
  // Transient — set by lowering, cleared by inline/resolve
  reference: OrbitalRefObjectSchema.optional(),
  // Context fields - persisted throughout orbital lifecycle
  domainContext: DomainContextSchema.optional(),
  design: DesignPreferencesSchema.optional(),
  suggestedGuards: z.array(SuggestedGuardSchema).optional(),
  types: z.record(z.string(), z.array(EventPayloadFieldSchema)).optional()
});
var OrbitalSchema = OrbitalDefinitionSchema;
function isOrbitalDefinition(orbital) {
  return "entity" in orbital;
}
var OrbitalUnitSchema = OrbitalSchema;
var HOST_SIGN_IN_ROUTE = "/login";
function isHostSignInRoute(path) {
  return path.replace(/\/+$/, "") === HOST_SIGN_IN_ROUTE;
}
var SiteMetaSchema = z.object({
  origin: z.string().url().optional(),
  icon: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  signIn: z.string().startsWith("/").optional()
}).strict();
var ConfigProvenanceRecordSchema = z.object({
  trait: z.string(),
  patternPath: z.string().optional(),
  prop: z.string(),
  knob: z.string()
});
var SchemaMetadataSchema = z.object({
  version: z.number().optional(),
  createdAt: z.number().optional(),
  source: z.string().optional(),
  updatedAt: z.number().optional(),
  configProvenance: z.record(z.array(ConfigProvenanceRecordSchema)).optional(),
  messageSources: z.record(z.string()).optional()
});
var OrbitalSchemaSchema = z.object({
  name: z.string().min(1, "Schema name is required"),
  description: z.string().optional(),
  theme: ThemeRefSchema.optional(),
  summary: z.string().optional(),
  version: z.string().optional(),
  domainContext: DomainContextSchema.optional(),
  design: DesignPreferencesSchema.optional(),
  designTokens: DesignTokensSchema,
  customPatterns: CustomPatternMapSchema,
  orbitals: z.array(OrbitalSchema).min(1, "At least one orbital is required"),
  locales: z.array(z.string().min(1)).optional(),
  publicDir: z.string().min(1).optional(),
  site: SiteMetaSchema.optional(),
  lazyPages: z.array(LazyPageSchema).optional(),
  services: z.array(ServiceDefinitionSchema).optional(),
  config: DeclaredTraitConfigSchema.optional(),
  _metadata: SchemaMetadataSchema.optional(),
  // V4 identity — optional/dual-carry until the Phase-7 flip. Present on
  // id-carrying `.orb` files so `parseOrbitalSchema` preserves them instead
  // of stripping unknown keys.
  schemaVersion: z.number().optional(),
  ledger: IdentityLedgerSchema.optional()
});
function parseOrbitalSchema(data) {
  return OrbitalSchemaSchema.parse(data);
}
function safeParseOrbitalSchema(data) {
  return OrbitalSchemaSchema.safeParse(data);
}

// src/types/observability.ts
var CALL_KINDS = ["client", "server", "db", "queue", "external"];
var LOG_SEVERITIES = ["DEBUG", "INFO", "WARNING", "ERROR"];
function isCallKind(value) {
  return typeof value === "string" && CALL_KINDS.some((k) => k === value);
}
function isSeverity(value) {
  return typeof value === "string" && LOG_SEVERITIES.some((s) => s === value);
}
function optionalString(value) {
  return value === void 0 || typeof value === "string";
}
function isCallRecord(value) {
  if (value === void 0 || !isJsonObject(value)) return false;
  const { kind, service, op, durationMs, ok, error } = value;
  if (!isCallKind(kind) || typeof service !== "string" || typeof op !== "string") return false;
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs < 0) return false;
  if (typeof ok !== "boolean" || !optionalString(error)) return false;
  return !(ok && error !== void 0);
}
function isStructuredLogEntry(value) {
  if (!isJsonObject(value)) return false;
  const { severity, time, namespace, message, data, call, appId, deploymentCommit } = value;
  if (!isSeverity(severity) || typeof time !== "string" || typeof namespace !== "string" || typeof message !== "string") return false;
  if (data !== void 0 && !isJsonObject(data)) return false;
  if (call !== void 0 && !isCallRecord(call)) return false;
  return optionalString(appId) && optionalString(deploymentCommit);
}
var BindingSchema = z.string().refine(
  (val) => {
    if (!val.startsWith("@")) return false;
    const parts = val.slice(1).split(".");
    if (parts.length === 0 || parts[0] === "") return false;
    return parts.every((part) => /^[\p{L}_][\p{L}0-9_]*$/u.test(part));
  },
  { message: "Invalid binding format. Must be @name or @name.path.to.field" }
);
var BINDING_DOCS = {
  entity: {
    description: "Reference to the linked entity for this trait",
    examples: ["@entity.health", "@entity.x", "@entity.status"],
    requiresPath: true
  },
  payload: {
    description: "Reference to the event payload data",
    examples: ["@payload.amount", "@payload.targetId", "@payload.action"],
    requiresPath: true
  },
  state: {
    description: "Current state machine state name",
    examples: ["@state"],
    requiresPath: false
  },
  now: {
    description: "Current timestamp in milliseconds",
    examples: ["@now"],
    requiresPath: false
  },
  config: {
    description: "Trait configuration values",
    examples: ["@config.apiEndpoint", "@config.theme"],
    requiresPath: true
  },
  computed: {
    description: "Computed/calculated values",
    examples: ["@computed.total", "@computed.isValid"],
    requiresPath: true
  },
  trait: {
    description: "Trait context data",
    examples: ["@trait.name", "@trait.category"],
    requiresPath: true
  },
  user: {
    description: "Authenticated user / agent context for ownership and role-based gating",
    examples: ["@user.id", "@user.role"],
    requiresPath: true
  },
  callsitePayload: {
    description: "Call-site-captured event payload \u2014 emitted by the compiler's inline-trait hoisting when an extracted render block captured @payload; resolved at the composing effect by the runtime BindingResolver",
    examples: ["@callsitePayload.error", "@callsitePayload.row"],
    requiresPath: true
  },
  pages: {
    description: "Render-resolved schema sigil \u2014 the host orbital's pages as a NavItem[] array (href = page.path, label = page.name). Substituted from the OrbitalSchema before codegen; never survives as a live binding.",
    examples: ["@pages"],
    requiresPath: false
  },
  currentTheme: {
    description: "Render-resolved schema sigil \u2014 the host orbital's active data-theme key, derived from Orbital.theme. Substituted from the OrbitalSchema before codegen; never survives as a live binding.",
    examples: ["@currentTheme"],
    requiresPath: false
  },
  locale: {
    description: "The active locale code (`en`, `ar`, `sl`, \u2026) \u2014 the locale whose catalog `i18n/t` reads, set by the host from the route.",
    examples: ["@locale"],
    requiresPath: false
  },
  event: {
    description: "The delivery being processed: { event, payload, source } \u2014 for a listens delivery the source emit, with its BusEventSource envelope (Runtime Spec Clause 5.5)",
    examples: ["@event.event", "@event.payload.n", "@event.source.trait", "@event.source.tick"],
    requiresPath: false
  },
  prevEvents: {
    description: "The deliveries this trait already received in this dispatch, in order, each shaped like @event",
    examples: ["@prevEvents"],
    requiresPath: false
  },
  prevStates: {
    description: "The states this trait left in this dispatch, in order",
    examples: ["@prevStates"],
    requiresPath: false
  },
  fromState: {
    description: "The transition's source state",
    examples: ["@fromState"],
    requiresPath: false
  },
  toState: {
    description: "The transition's target state (null in a listens `when`, where no transition is chosen yet)",
    examples: ["@toState"],
    requiresPath: false
  }
};
var BINDING_CONTEXT_RULES = {
  guard: {
    allowed: ["entity", "payload", "state", "now", "config", "user", "event", "prevEvents", "prevStates", "fromState", "toState"],
    description: `Guards can read the delivery being processed and this dispatch's log (@event, @prevEvents, @prevStates) and the transition's ends (@fromState, @toState; Runtime Spec Clause 5.5). Guards can access entity fields, event payload, current state, time, the call-site trait config (@config.X), and the authenticated user context (@user.id, @user.role) for ownership / role gates. Config access lets atoms write mode-aware guards \u2014 e.g. std-modal's OPEN can require @payload.row only when @config.mode equals "edit", letting create-mode legitimately fire OPEN with no row. Like effects, @config.X is substituted at molecule/organism inline time with the literal call-site value; at atom-scope validate, @config is allowed-but-unresolved.`
  },
  effect: {
    allowed: ["entity", "payload", "state", "now", "trait", "config", "user", "callsitePayload", "pages", "currentTheme", "locale", "event", "prevEvents", "prevStates", "fromState", "toState"],
    description: "Effects can access and modify entity fields, use payload data, embed another trait's live frame via @trait.X inside render-ui children, read trait config values (@config.X) for atoms parameterized by their call-site, and read the authenticated user context (@user.id, @user.role). At molecule/organism inline time, @config.X is substituted with the literal value from the call-site config block; at atom-scope validate, @config is allowed-but-unresolved. @callsitePayload.X is the call-site-captured event payload emitted by the compiler's inline-trait hoisting (a hoisted render block that captured @payload); it is resolved at the composing effect by the runtime BindingResolver."
  },
  tick: {
    allowed: ["entity", "state", "now", "config", "user"],
    description: "Ticks can access entity fields, current state, time, trait config (@config.X) for parameterized atoms, and the authenticated user context (@user.id, @user.role). Same substitution semantics as guards/effects."
  }
};
var RENDER_BINDING_MARKER = "$renderBinding";
function isRenderBindingMarker(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && RENDER_BINDING_MARKER in value && value[RENDER_BINDING_MARKER] === true;
}
var ENTITY_BINDING_RE = /@entity(?=[.\[\]]|$)/;
var PAYLOAD_BINDING_RE = /@(?:callsitePayload|payload)(?=[.\[\]]|$)/;
function containsEntityBinding(value) {
  if (typeof value === "string") return ENTITY_BINDING_RE.test(value);
  if (Array.isArray(value)) return value.some(containsEntityBinding);
  if (value !== null && typeof value === "object") {
    if (isRenderBindingMarker(value)) return true;
    return Object.values(value).some(containsEntityBinding);
  }
  return false;
}
function containsPayloadBinding(value) {
  if (typeof value === "string") return PAYLOAD_BINDING_RE.test(value);
  if (Array.isArray(value)) return value.some(containsPayloadBinding);
  if (value !== null && typeof value === "object") {
    return Object.values(value).some(containsPayloadBinding);
  }
  return false;
}
function validateBindingInContext(binding, context) {
  const rules = BINDING_CONTEXT_RULES[context];
  if (binding.type === "core") {
    if (!rules.allowed.includes(binding.root)) {
      return `Binding @${binding.root} is not allowed in ${context} context. Allowed: ${rules.allowed.join(", ")}`;
    }
  }
  return null;
}
function getBindingExamples(context) {
  const rules = BINDING_CONTEXT_RULES[context];
  const examples = [];
  for (const binding of rules.allowed) {
    const doc = BINDING_DOCS[binding];
    examples.push(...doc.examples);
  }
  examples.push("@GameConfig.gravity", "@Filter.searchTerm");
  return examples;
}
var BINDING_ROOTS = [
  "entity",
  "payload",
  "state",
  "now",
  "config",
  "trait",
  "user",
  "callsitePayload",
  "pages",
  "currentTheme",
  "locale",
  "event",
  "prevEvents",
  "prevStates",
  "fromState",
  "toState",
  "other"
];
var KNOWN_ROOTS = new Set(BINDING_ROOTS.filter((r) => r !== "other"));
function toBindingRoot(root) {
  return KNOWN_ROOTS.has(root) ? root : "other";
}
var TRAIT_REF_PATTERN = /^@trait\.[A-Z][a-zA-Z0-9]*$/;
function isTraitFieldRef(value) {
  return typeof value === "string" && TRAIT_REF_PATTERN.test(value);
}
var TraitFieldRefSchema = z.string().regex(TRAIT_REF_PATTERN, "expected `@trait.<TraitName>` reference");
var ANONYMOUS_USER = {
  id: "anonymous",
  role: "anonymous",
  permissions: []
};
var RawUserClaimsSchema = z.object({
  id: z.string().optional(),
  uid: z.string().optional(),
  email: z.string().nullable().optional(),
  name: z.string().optional(),
  displayName: z.string().nullable().optional(),
  role: z.string().optional(),
  permissions: z.array(z.string()).optional()
}).catchall(FieldValueSchema.optional());
var DERIVED_CLAIM_KEYS = ["id", "name", "displayName", "email"];
function normalizeUserContext(claims) {
  if (!claims) return void 0;
  const id = claims.id ?? claims.uid;
  if (typeof id !== "string" || id.length === 0) return void 0;
  const user = { id };
  for (const [key, value] of Object.entries(claims)) {
    if (value === void 0 || DERIVED_CLAIM_KEYS.includes(key)) continue;
    user[key] = value;
  }
  const name = claims.name ?? claims.displayName;
  if (typeof name === "string" && name.length > 0) user.name = name;
  if (typeof claims.email === "string" && claims.email.length > 0) user.email = claims.email;
  return user;
}
function personaFromIdentityRow(row) {
  const id = row["id"];
  if (typeof id !== "string" || id.length === 0) return void 0;
  const persona = { id };
  for (const [key, value] of Object.entries(row)) {
    if (key === "id" || value === void 0) continue;
    persona[key] = value;
  }
  return persona;
}
var DEFAULT_VIEWER = {
  id: "viewer-1",
  name: "Dev Viewer",
  email: "viewer@example.com",
  role: ""
};
function resolveDefaultViewer(roster) {
  return roster.length > 0 ? roster[0] : DEFAULT_VIEWER;
}
function findPersonaInRoster(roster, idOrRole) {
  return roster.find((p) => p.id === idOrRole) ?? roster.find((p) => p.role === idOrRole);
}
function resolvePersonaSpec(spec, roster) {
  const raw = spec.trim();
  if (!raw.startsWith("{")) {
    const declared = findPersonaInRoster(roster, raw);
    if (!declared) {
      const known = roster.map((p) => `${p.id}/${p.role ?? "-"}`).join(", ");
      throw new Error(
        `Persona "${raw}" is not a declared persona id or role. ` + (roster.length > 0 ? `Known: ${known}` : "This app declares no [identity] entity, so only the JSON persona form is accepted.")
      );
    }
    return declared;
  }
  let claims;
  try {
    claims = JSON.parse(raw);
  } catch {
    throw new Error(`Persona is not valid JSON: ${raw}`);
  }
  const user = normalizeUserContext(claims);
  if (!user) {
    throw new Error(`Persona needs an "id" (or "uid"): ${raw}`);
  }
  return user;
}

// src/types/agent.ts
function isSessionHistoryEntry(value) {
  if (typeof value !== "object" || value === null) return false;
  return "role" in value && typeof value.role === "string" && "content" in value && typeof value.content === "string" && "timestamp" in value && typeof value.timestamp === "number";
}

// src/types/plan.ts
function isPlanSnapshot(value) {
  if (typeof value !== "object" || value === null) return false;
  const statuses = ["proposed", "confirmed", "built", "failed"];
  return "schemaVersion" in value && value.schemaVersion === 1 && "status" in value && statuses.some((s) => s === value.status) && "builtAt" in value && typeof value.builtAt === "string" && "orbitals" in value && Array.isArray(value.orbitals) && "renames" in value && Array.isArray(value.renames) && "deletedOrbitals" in value && Array.isArray(value.deletedOrbitals);
}
var InteractionModelSchema = z.object({
  createFlow: z.enum(["modal", "page", "inline", "none"]),
  editFlow: z.enum(["modal", "page", "inline", "none"]),
  viewFlow: z.enum(["drawer", "page", "modal", "inline", "none"]),
  deleteFlow: z.enum(["confirm", "instant", "none"]),
  listInteraction: z.enum(["click-to-view", "click-to-edit", "inline-edit"]).optional(),
  bulkActions: z.boolean().optional(),
  realtime: z.boolean().optional()
});
var DEFAULT_INTERACTION_MODELS = {
  business: {
    createFlow: "modal",
    editFlow: "modal",
    viewFlow: "drawer",
    deleteFlow: "confirm",
    listInteraction: "click-to-view",
    bulkActions: true
  },
  game: {
    createFlow: "none",
    editFlow: "none",
    viewFlow: "none",
    deleteFlow: "none",
    realtime: true
  },
  form: {
    createFlow: "inline",
    editFlow: "inline",
    viewFlow: "none",
    deleteFlow: "none"
  },
  dashboard: {
    createFlow: "modal",
    editFlow: "modal",
    viewFlow: "drawer",
    deleteFlow: "confirm",
    listInteraction: "click-to-view"
  },
  content: {
    createFlow: "page",
    editFlow: "page",
    viewFlow: "page",
    deleteFlow: "confirm",
    listInteraction: "click-to-view"
  }
};
function getInteractionModelForDomain(domain) {
  return DEFAULT_INTERACTION_MODELS[domain] ?? DEFAULT_INTERACTION_MODELS.business;
}

// src/patterns/patterns-registry.json
var patterns_registry_default = {
  version: "1.0.0",
  exportedAt: "2026-10-10T01:07:16.949Z",
  patterns: {
    "detail-panel": {
      type: "detail-panel",
      category: "display",
      sourceFile: "components/core/organisms/DetailPanel.tsx",
      tier: "organisms",
      family: "core",
      description: "Detail view panel for drawers/sidebars",
      fieldsContract: "display",
      suggestedFor: [
        "drawer content",
        "side panels",
        "quick view"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "detail",
          tier: "presentation"
        },
        entity: {
          types: [
            "object"
          ],
          description: "RECORD-cardinality: renders ONE record (see body collapse below).",
          kind: "entity",
          cardinality: "record"
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        onTitleCommit: {
          types: [
            "function"
          ],
          description: "When bound, the title becomes editable in place: clicking it swaps the heading for an input, Enter or blur commits, Escape reverts. Receives the committed title and the record's id so the host can persist a partial update. Unbound, the title stays a plain heading.",
          kind: "callback",
          callbackArgs: [
            {
              name: "title",
              type: "string"
            },
            {
              name: "id",
              type: "string"
            }
          ],
          default: ""
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "subtitle prop"
        },
        status: {
          types: [
            "object"
          ],
          description: "status prop",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "default",
                "success",
                "warning",
                "danger",
                "info"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        avatar: {
          types: [
            "node"
          ],
          description: "avatar prop"
        },
        sections: {
          types: [
            "array"
          ],
          description: "sections prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              title: {
                types: [
                  "string"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object",
                    "string"
                  ],
                  properties: {
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    value: {
                      types: [
                        "node"
                      ]
                    },
                    icon: {
                      types: [
                        "icon",
                        "string"
                      ]
                    },
                    copyable: {
                      types: [
                        "boolean"
                      ]
                    }
                  },
                  required: [
                    "label",
                    "value"
                  ]
                }
              }
            },
            required: [
              "title",
              "fields"
            ]
          }
        },
        actions: {
          types: [
            "array"
          ],
          description: "Unified actions array - first action with variant='primary' is the main action",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: `Max inline action buttons before the rest collapse into a "\u22EF" overflow menu (mirrors DataGrid's maxInlineActions). Defaults to 2 \u2014 primary + secondary visible, the rest under the menu \u2014 so every detail panel carries the same action pattern.`,
          default: 2
        },
        backAction: {
          types: [
            "object"
          ],
          description: "Navigation-back affordance. Renders top-LEFT before the title (OS/back convention \u2014 Almadar_UX \xA78.4), spatially separated from the right-aligned action buttons + close X. Never inferred from actions[] labels.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            icon: {
              types: [
                "icon",
                "string"
              ]
            },
            onClick: {
              types: [
                "function"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            },
            navigatesTo: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "primary",
                "secondary",
                "ghost",
                "danger"
              ]
            },
            when: {
              types: [
                "sexpr",
                "function"
              ]
            },
            payload: {
              types: [
                "sexpr",
                "function"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "The event of the declared action that dismisses the panel. The matching action renders as the header close \xD7; omitted, no \xD7 is drawn.",
          kind: "event"
        },
        footer: {
          types: [
            "node"
          ],
          description: "footer prop"
        },
        slideOver: {
          types: [
            "boolean"
          ],
          description: "slideOver prop",
          default: false
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out. Inline mode only; the slide-over is itself a surface.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        fields: {
          types: [
            "array"
          ],
          description: "Fields to display: field names, or declared fields ({ name, label, variant, format, colorMap, labels })",
          required: true,
          items: {
            types: [
              "string",
              "object"
            ],
            nonAuthorable: true,
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              type: {
                types: [
                  "string"
                ]
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        fieldNames: {
          types: [
            "array"
          ],
          description: "Alias for fields - backwards compatibility",
          items: {
            types: [
              "string"
            ]
          }
        },
        initialData: {
          types: [
            "object"
          ],
          description: "Initial data for edit mode (passed by compiler)",
          kind: "entity",
          cardinality: "record"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Display mode (passed by compiler)"
        },
        position: {
          types: [
            "string"
          ],
          description: "Panel position (for drawer/sidebar placement)",
          enumValues: [
            "left",
            "right"
          ]
        },
        width: {
          types: [
            "string"
          ],
          description: "Panel width (CSS value, e.g., '400px', '50%')"
        },
        displayFields: {
          types: [
            "array"
          ],
          description: "Display fields (alias for fields)",
          items: {
            types: [
              "string"
            ]
          }
        },
        showActions: {
          types: [
            "boolean"
          ],
          description: "When false, hides the action buttons + overflow menu (and the close \xD7). Defaults to true.",
          default: true
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        look: {
          types: [
            "string"
          ],
          description: "Page variant: panel (labelled fields, default), profile (person/member hero), showcase (image-first product/listing), workflow (lifecycle stepper + activity), map (location hero), ledger (document with line items + totals), conversation (thread beside the record), workspace (tabbed record).",
          enumValues: [
            "panel",
            "profile",
            "showcase",
            "workflow",
            "map",
            "ledger",
            "conversation",
            "workspace"
          ],
          default: "panel"
        },
        avatarField: {
          types: [
            "string"
          ],
          description: "Record field holding the avatar image. The profile look always shows an avatar, with the title's initials when this is unset or empty."
        },
        coverField: {
          types: [
            "string"
          ],
          description: "Record field holding the cover image (profile)."
        },
        mediaField: {
          types: [
            "string"
          ],
          description: "Record field holding the image, or list of images, the showcase look leads with."
        },
        stageField: {
          types: [
            "string"
          ],
          description: "Record field holding the current lifecycle stage (workflow)."
        },
        stages: {
          types: [
            "array"
          ],
          description: "The lifecycle in order, `{ value, label }` per stage (workflow).",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        activity: {
          types: [
            "array"
          ],
          description: "Activity entries shown under the record (workflow).",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              date: {
                types: [
                  "string"
                ]
              },
              status: {
                types: [
                  "string"
                ],
                enumValues: [
                  "complete",
                  "active",
                  "pending",
                  "error"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              tags: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "id",
              "title"
            ]
          }
        },
        activityContent: {
          types: [
            "node"
          ],
          description: "Activity composed in instead of `activity` \u2014 e.g. the record's own history trait (workflow)."
        },
        latitudeField: {
          types: [
            "string"
          ],
          description: "Record fields holding the location (map)."
        },
        longitudeField: {
          types: [
            "string"
          ],
          description: "longitudeField prop"
        },
        lineItems: {
          types: [
            "object",
            "array"
          ],
          description: "Child rows shown as the document's line items (ledger).",
          kind: "entity",
          cardinality: "collection"
        },
        lineItemColumns: {
          types: [
            "array"
          ],
          description: "Columns of the line-items table (ledger).",
          items: {
            types: [
              "object"
            ],
            properties: {
              key: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "string"
                ],
                enumValues: [
                  "normal",
                  "medium",
                  "semibold"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "badge",
                  "date",
                  "currency",
                  "number",
                  "percent",
                  "boolean"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              sortable: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "key"
            ]
          }
        },
        lineItemsContent: {
          types: [
            "node"
          ],
          description: "Line items composed in instead of `lineItems` \u2014 e.g. the child rows' own trait (ledger)."
        },
        totals: {
          types: [
            "array"
          ],
          description: "Record fields shown as the document's totals, in order (ledger).",
          items: {
            types: [
              "string",
              "object"
            ],
            nonAuthorable: true,
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              type: {
                types: [
                  "string"
                ]
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        thread: {
          types: [
            "object",
            "array"
          ],
          description: "The thread shown as the main surface (conversation).",
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "authorName",
            "content"
          ],
          properties: {
            authorName: {
              types: [
                "string"
              ]
            },
            content: {
              types: [
                "string"
              ]
            },
            authorAvatarUrl: {
              types: [
                "string"
              ]
            },
            postedAt: {
              types: [
                "string"
              ]
            },
            voteCount: {
              types: [
                "number"
              ]
            },
            userVote: {
              types: [
                "string"
              ],
              enumValues: [
                "up",
                "down",
                "none"
              ]
            },
            collapsed: {
              types: [
                "boolean"
              ]
            },
            replies: {
              types: [
                "object",
                "array"
              ],
              cyclic: true,
              cyclicTypeName: "ReplyNodeRow"
            }
          }
        },
        threadContent: {
          types: [
            "node"
          ],
          description: "The thread composed in instead of `thread` \u2014 e.g. the replies' own trait with its composer (conversation)."
        },
        tabs: {
          types: [
            "array"
          ],
          description: "Tabs after the overview tab, each listing declared field names (workspace).",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              content: {
                types: [
                  "node"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "page-header": {
      type: "page-header",
      category: "header",
      sourceFile: "components/core/molecules/PageHeader.tsx",
      tier: "molecules",
      family: "core",
      description: "Page title with optional breadcrumb and action buttons",
      suggestedFor: [
        "list pages",
        "detail pages",
        "dashboards"
      ],
      typicalSize: "tiny",
      propsSchema: {
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "The shape an empty slot shows while this header's server render is in flight (`none` opts out). @default 'header'",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "header",
          tier: "presentation"
        },
        title: {
          types: [
            "string",
            "number"
          ],
          description: "Page title - accepts string or number from generated code accessing dynamic entity data"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon shown before the title."
        },
        subtitle: {
          types: [
            "string",
            "number"
          ],
          description: "Optional subtitle/description"
        },
        showBack: {
          types: [
            "boolean"
          ],
          description: "Show back button",
          default: false
        },
        backEvent: {
          types: [
            "string"
          ],
          description: "Event to emit when back is clicked (default: BACK)",
          kind: "event-ref",
          emitPayloadSchema: [],
          default: "BACK"
        },
        breadcrumbs: {
          types: [
            "array"
          ],
          description: "Breadcrumbs",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        status: {
          types: [
            "object",
            "string"
          ],
          description: "Status badge \u2014 a bare string (an entity's status field bound directly, e.g. `status: @entity.status`) renders as a default-variant badge",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "default",
                "success",
                "warning",
                "danger",
                "info"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions array - first action with variant='primary' (or first action) is the main action",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              loading: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        tabs: {
          types: [
            "array"
          ],
          description: "Tabs for sub-navigation",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              count: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        activeTab: {
          types: [
            "string"
          ],
          description: "activeTab prop"
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "onTabChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Custom content in the header"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    form: {
      type: "form",
      category: "form",
      sourceFile: "components/core/organisms/Form.tsx",
      tier: "organisms",
      family: "core",
      description: "Complete form with fields and actions",
      fieldsContract: "form",
      suggestedFor: [
        "create forms",
        "edit forms",
        "data entry"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface (with card padding) behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "The shape an empty slot shows while this form's server render is in flight (`none` opts out). @default 'form'",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "form",
          tier: "presentation"
        },
        children: {
          types: [
            "node"
          ],
          description: "Form fields (traditional React children)"
        },
        onSubmit: {
          types: [
            "string"
          ],
          description: "Submit event name for trait dispatch (emitted via eventBus as UI:{onSubmit})"
        },
        onCancel: {
          types: [
            "string"
          ],
          description: "Cancel event name for trait dispatch (emitted via eventBus as UI:{onCancel})"
        },
        layout: {
          types: [
            "string"
          ],
          description: "Form layout",
          enumValues: [
            "vertical",
            "horizontal",
            "inline"
          ],
          default: "vertical"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between fields",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema-based props",
          kind: "entity",
          cardinality: "collection"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Form mode \u2014 'create' for new records, 'edit' for updating existing. Accepts `string` so schema-driven callers (whose `config.mode` is typed as `string` per the trait's declared config block) compile cleanly. The runtime treats anything other than 'edit' as 'create'."
        },
        fields: {
          types: [
            "array"
          ],
          description: "Fields definition (schema format) - accepts readonly for generated const arrays",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              inputType: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              required: {
                types: [
                  "boolean"
                ]
              },
              defaultValue: {
                types: [
                  "object"
                ],
                controlValue: true
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    value: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "value",
                    "label"
                  ]
                }
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  displayField: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              pattern: {
                types: [
                  "string"
                ]
              },
              validation: {
                types: [
                  "object"
                ],
                properties: {
                  enum: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              readonly: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        initialData: {
          types: [
            "object"
          ],
          description: "Initial form data",
          kind: "entity",
          cardinality: "record"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        submitLabel: {
          types: [
            "string"
          ],
          description: "Submit button label"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Cancel button label (if provided, shows cancel button)"
        },
        showCancel: {
          types: [
            "boolean"
          ],
          description: "Show cancel button (defaults to true for schema forms)"
        },
        showSubmit: {
          types: [
            "boolean"
          ],
          description: "Show submit button (defaults to true for schema forms). Set false when a parent atom owns the submit action externally (e.g. wizard footers).",
          default: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Form title (used by ModalSlot to extract title)"
        },
        submitEvent: {
          types: [
            "string"
          ],
          description: "Event dispatch props (for trait state machine integration)",
          kind: "event-ref",
          entityRowField: "data",
          emitPayloadSchema: [
            {
              name: "data",
              type: "@entity",
              required: true
            }
          ],
          default: "SAVE"
        },
        cancelEvent: {
          types: [
            "string"
          ],
          description: "Event to dispatch on cancel (defaults to 'CANCEL')",
          kind: "event",
          default: "CANCEL"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation data props",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          },
          default: {}
        },
        relationsLoading: {
          types: [
            "object"
          ],
          description: "Loading state for relation data: { fieldName: boolean }",
          mapValue: {
            types: [
              "boolean"
            ]
          },
          default: {}
        },
        fieldOverrides: {
          types: [
            "array"
          ],
          description: "Per-field display-copy overrides (label/placeholder/hint), merged by field name over the schema-enriched fields.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        conditionalFields: {
          types: [
            "object",
            "boolean"
          ],
          description: "Inspection form extensions",
          mapValue: {
            types: [
              "sexpr"
            ]
          }
        },
        hiddenCalculations: {
          types: [
            "array",
            "boolean"
          ],
          description: "Hidden calculations that emit GLOBAL_VARIABLE_SET on field change (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              variableName: {
                types: [
                  "string"
                ]
              },
              expression: {
                types: [
                  "sexpr"
                ]
              },
              triggerFields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "variableName",
              "expression",
              "triggerFields"
            ]
          }
        },
        violationTriggers: {
          types: [
            "array",
            "boolean"
          ],
          description: "Violation conditions that emit VIOLATION_DETECTED when met (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              condition: {
                types: [
                  "sexpr"
                ]
              },
              violation: {
                types: [
                  "object"
                ],
                properties: {
                  law: {
                    types: [
                      "string"
                    ]
                  },
                  article: {
                    types: [
                      "string"
                    ]
                  },
                  actionType: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "measure",
                      "admin",
                      "penalty"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "law",
                  "article",
                  "actionType",
                  "message"
                ]
              },
              fieldId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "condition",
              "violation"
            ]
          }
        },
        evaluationContext: {
          types: [
            "object"
          ],
          description: "Context for S-expression evaluation",
          properties: {
            formValues: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            globalVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            localVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            entity: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            }
          },
          propertyRequired: [
            "formValues",
            "globalVariables"
          ]
        },
        sections: {
          types: [
            "array"
          ],
          description: "Nested form sections with optional conditions",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              condition: {
                types: [
                  "sexpr"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    field: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    inputType: {
                      types: [
                        "string"
                      ]
                    },
                    placeholder: {
                      types: [
                        "string"
                      ]
                    },
                    required: {
                      types: [
                        "boolean"
                      ]
                    },
                    defaultValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    },
                    options: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          value: {
                            types: [
                              "string"
                            ]
                          },
                          label: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "value",
                          "label"
                        ]
                      }
                    },
                    values: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    labels: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    relation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        displayField: {
                          types: [
                            "string"
                          ]
                        },
                        cardinality: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "one",
                            "many",
                            "one-to-many",
                            "many-to-one",
                            "many-to-many"
                          ]
                        }
                      },
                      required: [
                        "entity"
                      ]
                    },
                    min: {
                      types: [
                        "number"
                      ]
                    },
                    max: {
                      types: [
                        "number"
                      ]
                    },
                    pattern: {
                      types: [
                        "string"
                      ]
                    },
                    validation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        enum: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "string"
                            ]
                          }
                        }
                      }
                    },
                    readonly: {
                      types: [
                        "boolean"
                      ]
                    },
                    disabled: {
                      types: [
                        "boolean"
                      ]
                    },
                    hint: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              collapsible: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "title",
              "fields"
            ]
          },
          default: []
        },
        onFieldChange: {
          types: [
            "function"
          ],
          description: "Callback when any field value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "change",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  fieldId: {
                    types: [
                      "string"
                    ]
                  },
                  value: {
                    types: [
                      "string",
                      "number",
                      "boolean",
                      "array",
                      "object"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    },
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      url: {
                        types: [
                          "string"
                        ]
                      },
                      mimeType: {
                        types: [
                          "string"
                        ]
                      },
                      sizeBytes: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "url",
                      "mimeType",
                      "sizeBytes"
                    ]
                  },
                  formValues: {
                    types: [
                      "object"
                    ],
                    mapValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    }
                  }
                },
                required: [
                  "fieldId",
                  "value",
                  "formValues"
                ]
              }
            }
          ]
        },
        configPath: {
          types: [
            "string"
          ],
          description: "Config path for form configuration (schema-driven)"
        },
        repeatable: {
          types: [
            "boolean"
          ],
          description: "Whether the form supports repeatable entries"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-section": {
      type: "form-section",
      category: "form",
      sourceFile: "components/core/organisms/Form.tsx",
      tier: "organisms",
      family: "core",
      description: "Alias for form \u2014 Complete form with fields and actions",
      fieldsContract: "form",
      suggestedFor: [
        "create forms",
        "edit forms",
        "data entry"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface (with card padding) behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "The shape an empty slot shows while this form's server render is in flight (`none` opts out). @default 'form'",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "form",
          tier: "presentation"
        },
        children: {
          types: [
            "node"
          ],
          description: "Form fields (traditional React children)"
        },
        onSubmit: {
          types: [
            "string"
          ],
          description: "Submit event name for trait dispatch (emitted via eventBus as UI:{onSubmit})"
        },
        onCancel: {
          types: [
            "string"
          ],
          description: "Cancel event name for trait dispatch (emitted via eventBus as UI:{onCancel})"
        },
        layout: {
          types: [
            "string"
          ],
          description: "Form layout",
          enumValues: [
            "vertical",
            "horizontal",
            "inline"
          ],
          default: "vertical"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between fields",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema-based props",
          kind: "entity",
          cardinality: "collection"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Form mode \u2014 'create' for new records, 'edit' for updating existing. Accepts `string` so schema-driven callers (whose `config.mode` is typed as `string` per the trait's declared config block) compile cleanly. The runtime treats anything other than 'edit' as 'create'."
        },
        fields: {
          types: [
            "array"
          ],
          description: "Fields definition (schema format) - accepts readonly for generated const arrays",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              inputType: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              required: {
                types: [
                  "boolean"
                ]
              },
              defaultValue: {
                types: [
                  "object"
                ],
                controlValue: true
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    value: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "value",
                    "label"
                  ]
                }
              },
              values: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              relation: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  displayField: {
                    types: [
                      "string"
                    ]
                  },
                  cardinality: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "one",
                      "many",
                      "one-to-many",
                      "many-to-one",
                      "many-to-many"
                    ]
                  }
                },
                required: [
                  "entity"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              pattern: {
                types: [
                  "string"
                ]
              },
              validation: {
                types: [
                  "object"
                ],
                properties: {
                  enum: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              readonly: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        initialData: {
          types: [
            "object"
          ],
          description: "Initial form data",
          kind: "entity",
          cardinality: "record"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        submitLabel: {
          types: [
            "string"
          ],
          description: "Submit button label"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Cancel button label (if provided, shows cancel button)"
        },
        showCancel: {
          types: [
            "boolean"
          ],
          description: "Show cancel button (defaults to true for schema forms)"
        },
        showSubmit: {
          types: [
            "boolean"
          ],
          description: "Show submit button (defaults to true for schema forms). Set false when a parent atom owns the submit action externally (e.g. wizard footers).",
          default: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Form title (used by ModalSlot to extract title)"
        },
        submitEvent: {
          types: [
            "string"
          ],
          description: "Event dispatch props (for trait state machine integration)",
          kind: "event-ref",
          entityRowField: "data",
          emitPayloadSchema: [
            {
              name: "data",
              type: "@entity",
              required: true
            }
          ],
          default: "SAVE"
        },
        cancelEvent: {
          types: [
            "string"
          ],
          description: "Event to dispatch on cancel (defaults to 'CANCEL')",
          kind: "event",
          default: "CANCEL"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation data props",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          },
          default: {}
        },
        relationsLoading: {
          types: [
            "object"
          ],
          description: "Loading state for relation data: { fieldName: boolean }",
          mapValue: {
            types: [
              "boolean"
            ]
          },
          default: {}
        },
        fieldOverrides: {
          types: [
            "array"
          ],
          description: "Per-field display-copy overrides (label/placeholder/hint), merged by field name over the schema-enriched fields.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              placeholder: {
                types: [
                  "string"
                ]
              },
              hint: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        conditionalFields: {
          types: [
            "object",
            "boolean"
          ],
          description: "Inspection form extensions",
          mapValue: {
            types: [
              "sexpr"
            ]
          }
        },
        hiddenCalculations: {
          types: [
            "array",
            "boolean"
          ],
          description: "Hidden calculations that emit GLOBAL_VARIABLE_SET on field change (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              variableName: {
                types: [
                  "string"
                ]
              },
              expression: {
                types: [
                  "sexpr"
                ]
              },
              triggerFields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "variableName",
              "expression",
              "triggerFields"
            ]
          }
        },
        violationTriggers: {
          types: [
            "array",
            "boolean"
          ],
          description: "Violation conditions that emit VIOLATION_DETECTED when met (boolean true means enabled but config loaded separately)",
          items: {
            types: [
              "object"
            ],
            properties: {
              condition: {
                types: [
                  "sexpr"
                ]
              },
              violation: {
                types: [
                  "object"
                ],
                properties: {
                  law: {
                    types: [
                      "string"
                    ]
                  },
                  article: {
                    types: [
                      "string"
                    ]
                  },
                  actionType: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "measure",
                      "admin",
                      "penalty"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "law",
                  "article",
                  "actionType",
                  "message"
                ]
              },
              fieldId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "condition",
              "violation"
            ]
          }
        },
        evaluationContext: {
          types: [
            "object"
          ],
          description: "Context for S-expression evaluation",
          properties: {
            formValues: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            globalVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            localVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            entity: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            }
          },
          propertyRequired: [
            "formValues",
            "globalVariables"
          ]
        },
        sections: {
          types: [
            "array"
          ],
          description: "Nested form sections with optional conditions",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              condition: {
                types: [
                  "sexpr"
                ]
              },
              fields: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    field: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    inputType: {
                      types: [
                        "string"
                      ]
                    },
                    placeholder: {
                      types: [
                        "string"
                      ]
                    },
                    required: {
                      types: [
                        "boolean"
                      ]
                    },
                    defaultValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    },
                    options: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          value: {
                            types: [
                              "string"
                            ]
                          },
                          label: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "value",
                          "label"
                        ]
                      }
                    },
                    values: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    labels: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    relation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        displayField: {
                          types: [
                            "string"
                          ]
                        },
                        cardinality: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "one",
                            "many",
                            "one-to-many",
                            "many-to-one",
                            "many-to-many"
                          ]
                        }
                      },
                      required: [
                        "entity"
                      ]
                    },
                    min: {
                      types: [
                        "number"
                      ]
                    },
                    max: {
                      types: [
                        "number"
                      ]
                    },
                    pattern: {
                      types: [
                        "string"
                      ]
                    },
                    validation: {
                      types: [
                        "object"
                      ],
                      properties: {
                        enum: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "string"
                            ]
                          }
                        }
                      }
                    },
                    readonly: {
                      types: [
                        "boolean"
                      ]
                    },
                    disabled: {
                      types: [
                        "boolean"
                      ]
                    },
                    hint: {
                      types: [
                        "string"
                      ]
                    }
                  }
                }
              },
              collapsible: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "title",
              "fields"
            ]
          },
          default: []
        },
        onFieldChange: {
          types: [
            "function"
          ],
          description: "Callback when any field value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "change",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  fieldId: {
                    types: [
                      "string"
                    ]
                  },
                  value: {
                    types: [
                      "string",
                      "number",
                      "boolean",
                      "array",
                      "object"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    },
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      url: {
                        types: [
                          "string"
                        ]
                      },
                      mimeType: {
                        types: [
                          "string"
                        ]
                      },
                      sizeBytes: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "url",
                      "mimeType",
                      "sizeBytes"
                    ]
                  },
                  formValues: {
                    types: [
                      "object"
                    ],
                    mapValue: {
                      types: [
                        "object"
                      ],
                      controlValue: true
                    }
                  }
                },
                required: [
                  "fieldId",
                  "value",
                  "formValues"
                ]
              }
            }
          ]
        },
        configPath: {
          types: [
            "string"
          ],
          description: "Config path for form configuration (schema-driven)"
        },
        repeatable: {
          types: [
            "boolean"
          ],
          description: "Whether the form supports repeatable entries"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "search-input": {
      type: "search-input",
      category: "filter",
      sourceFile: "components/core/molecules/SearchInput.tsx",
      tier: "molecules",
      family: "core",
      description: "Search input with events",
      minUsableWidth: 160,
      suggestedFor: [
        "search functionality",
        "quick filters"
      ],
      typicalSize: "tiny",
      propsSchema: {
        value: {
          types: [
            "string"
          ],
          description: "Search value (controlled mode)"
        },
        onSearch: {
          types: [
            "function"
          ],
          description: "Callback when search value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        debounceMs: {
          types: [
            "number"
          ],
          description: "Debounce delay in milliseconds",
          default: 300
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Show loading state",
          default: false
        },
        placeholder: {
          types: [
            "string"
          ],
          description: 'Placeholder text (the translated "Search" when omitted)'
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show clear button",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        event: {
          types: [
            "string"
          ],
          description: "Declarative search event \u2014 emits UI:{event} via eventBus on query submit (alongside canonical UI:SEARCH)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "searchTerm",
              type: "string",
              required: true
            },
            {
              name: "entity",
              type: "string",
              required: false
            }
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity type for context-aware search. When provided, search events include entity context."
        },
        query: {
          types: [
            "string"
          ],
          description: 'Query singleton binding for state management. When provided, syncs search state with the query singleton. Example: "@TaskQuery"'
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "filter-group": {
      type: "filter-group",
      category: "filter",
      sourceFile: "components/core/molecules/FilterGroup.tsx",
      tier: "molecules",
      family: "core",
      description: "FilterGroup \u2014 a panel of filter controls that narrows a collection by field values.",
      capabilities: "search refinement panel, facet filters, admin list filters, records filter sidebar, filter chips panel",
      suggestedFor: [
        "list pages with filterable data"
      ],
      typicalSize: "small",
      propsSchema: {
        entity: {
          types: [
            "string"
          ],
          description: "Entity name to filter",
          required: true
        },
        filters: {
          types: [
            "array"
          ],
          description: "Filter definitions from schema",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              filterType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "field",
              "label"
            ]
          }
        },
        onFilterChange: {
          types: [
            "function"
          ],
          description: "Callback when a filter changes - for EntityStore integration",
          kind: "callback",
          callbackArgs: [
            {
              name: "field",
              type: "string"
            },
            {
              name: "value",
              type: "string"
            }
          ]
        },
        onClearAll: {
          types: [
            "function"
          ],
          description: "Callback to clear all filters",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant style",
          enumValues: [
            "default",
            "compact",
            "pills",
            "vertical"
          ],
          default: "default"
        },
        showIcon: {
          types: [
            "boolean"
          ],
          description: "Show filter icon",
          default: true
        },
        query: {
          types: [
            "string"
          ],
          description: 'Query singleton binding for state management. When provided, syncs filter state with the query singleton. Example: "@TaskQuery"'
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant.",
          enumValues: [
            "toolbar",
            "chips",
            "pills",
            "popover-trigger",
            "inline-column-header"
          ],
          default: "toolbar"
        },
        event: {
          types: [
            "string"
          ],
          description: "Declarative filter event \u2014 emits UI:{event} via eventBus on filter change (alongside canonical UI:FILTER)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "entity",
              type: "string",
              required: true
            },
            {
              name: "field",
              type: "string",
              required: true
            },
            {
              name: "value",
              type: "string",
              required: true
            },
            {
              name: "query",
              type: "string",
              required: false
            }
          ]
        },
        clearEvent: {
          types: [
            "string"
          ],
          description: "Declarative clear event \u2014 emits UI:{clearEvent} via eventBus on clear-all (alongside canonical UI:CLEAR_FILTERS)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "entity",
              type: "string",
              required: true
            },
            {
              name: "query",
              type: "string",
              required: false
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    tabs: {
      type: "tabs",
      category: "navigation",
      sourceFile: "components/core/molecules/Tabs.tsx",
      tier: "molecules",
      family: "core",
      description: "Tab navigation within page",
      suggestedFor: [
        "pages with multiple views",
        "filtered lists"
      ],
      typicalSize: "tiny",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Tab items",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              iconAsset: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              active: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        tabs: {
          types: [
            "array"
          ],
          description: "Tab items (alias for items - used by generated code)",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              iconAsset: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              active: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        defaultActiveTab: {
          types: [
            "string"
          ],
          description: "Default active tab ID"
        },
        activeTab: {
          types: [
            "string"
          ],
          description: "Controlled active tab ID"
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "Callback when tab changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "tabId",
              type: "string"
            }
          ]
        },
        tabChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative tab change event \u2014 emits UI:{tabChangeEvent} with { tabId }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "tabId",
              type: "string",
              required: true
            }
          ]
        },
        variant: {
          types: [
            "string"
          ],
          description: "Tab variant",
          enumValues: [
            "default",
            "pills",
            "underline"
          ],
          default: "default"
        },
        orientation: {
          types: [
            "string"
          ],
          description: "Tab orientation",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    breadcrumb: {
      type: "breadcrumb",
      category: "navigation",
      sourceFile: "components/core/molecules/Breadcrumb.tsx",
      tier: "molecules",
      family: "core",
      description: "Breadcrumb navigation",
      suggestedFor: [
        "hierarchical navigation",
        "detail pages"
      ],
      typicalSize: "tiny",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Breadcrumb items. Omit together with `fromNavStack` to render the orbital-scoped navigation stack instead of an authored trail.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              path: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              isCurrent: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              }
            },
            required: [
              "label"
            ]
          },
          example: [
            {
              label: "Home",
              href: "/"
            },
            {
              label: "Page"
            }
          ]
        },
        fromNavStack: {
          types: [
            "boolean"
          ],
          description: "Render the current orbital's navigation stack (NavStackProvider) as the trail: one crumb per visited/declared level, last entry current. The stack is client-session state both execution paths maintain; outside a provider this renders nothing.",
          default: false
        },
        itemEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a non-current crumb is clicked, with payload `{ label, href, index }` \u2014 the trait handles it with `(navigate ?href)`. Without it, stack crumbs navigate directly.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "label",
              type: "string",
              required: true
            },
            {
              name: "href",
              type: "string",
              required: false
            },
            {
              name: "index",
              type: "number",
              required: true
            }
          ]
        },
        separator: {
          types: [
            "icon",
            "string"
          ],
          description: "Separator icon (canonical kebab-case name or LucideIcon component)",
          default: "chevron-right"
        },
        maxItems: {
          types: [
            "number"
          ],
          description: "Maximum items to show (truncates with ellipsis)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "wizard-progress": {
      type: "wizard-progress",
      category: "navigation",
      sourceFile: "components/core/molecules/WizardProgress.tsx",
      tier: "molecules",
      family: "core",
      description: "Wizard step progress indicator",
      suggestedFor: [
        "multi-step forms",
        "onboarding"
      ],
      typicalSize: "tiny",
      propsSchema: {
        steps: {
          types: [
            "array"
          ],
          description: "Step definitions (compatible with WizardContainer's WizardStep). A string is shorthand for `{ id: str, title: str }`.",
          items: {
            types: [
              "object",
              "string"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "title"
            ]
          }
        },
        currentStep: {
          types: [
            "number"
          ],
          description: "Current step index (0-based)",
          required: true
        },
        onStepClick: {
          types: [
            "function"
          ],
          description: "Callback when a completed step is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "stepIndex",
              type: "number"
            }
          ]
        },
        allowNavigation: {
          types: [
            "boolean"
          ],
          description: "Allow clicking on completed steps to navigate back",
          default: true
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact mode (smaller, no titles)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        stepClickEvent: {
          types: [
            "string"
          ],
          description: "Declarative step click event \u2014 emits UI:{stepClickEvent} with { stepIndex }. Setting it requires declaring that event (with a `{ stepIndex }` payload) and a transition handling it in the same trait; omit this prop for non-clickable progress indicators.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stepIndex",
              type: "number",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "wizard-navigation": {
      type: "wizard-navigation",
      category: "navigation",
      sourceFile: "components/core/molecules/WizardNavigation.tsx",
      tier: "molecules",
      family: "core",
      description: "Wizard navigation buttons",
      suggestedFor: [
        "multi-step forms",
        "wizards"
      ],
      typicalSize: "tiny",
      propsSchema: {
        currentStep: {
          types: [
            "number"
          ],
          description: "Current step index (0-based)",
          required: true
        },
        totalSteps: {
          types: [
            "number"
          ],
          description: "Total number of steps",
          required: true
        },
        isValid: {
          types: [
            "boolean"
          ],
          description: "Whether the current step is valid (enables Next/Complete)",
          default: true
        },
        showBack: {
          types: [
            "boolean"
          ],
          description: "Show the Back button",
          default: true
        },
        showNext: {
          types: [
            "boolean"
          ],
          description: "Show the Next button",
          default: true
        },
        showComplete: {
          types: [
            "boolean"
          ],
          description: "Show the Complete button (on last step)",
          default: true
        },
        backLabel: {
          types: [
            "string"
          ],
          description: "Custom label for Back button"
        },
        nextLabel: {
          types: [
            "string"
          ],
          description: "Custom label for Next button"
        },
        completeLabel: {
          types: [
            "string"
          ],
          description: "Custom label for Complete button"
        },
        onBack: {
          types: [
            "string"
          ],
          description: "Event to emit on Back click \u2014 emits UI:{onBack} with { currentStep, totalSteps }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "currentStep",
              type: "number",
              required: true
            },
            {
              name: "totalSteps",
              type: "number",
              required: true
            }
          ],
          default: "WIZARD_BACK"
        },
        onNext: {
          types: [
            "string"
          ],
          description: "Event to emit on Next click \u2014 emits UI:{onNext} with { currentStep, totalSteps }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "currentStep",
              type: "number",
              required: true
            },
            {
              name: "totalSteps",
              type: "number",
              required: true
            }
          ],
          default: "WIZARD_NEXT"
        },
        onComplete: {
          types: [
            "string"
          ],
          description: "Event to emit on Complete click \u2014 emits UI:{onComplete} with { currentStep, totalSteps }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "currentStep",
              type: "number",
              required: true
            },
            {
              name: "totalSteps",
              type: "number",
              required: true
            }
          ],
          default: "WIZARD_COMPLETE"
        },
        onBackClick: {
          types: [
            "function"
          ],
          description: "Direct callback for Back (alternative to event)",
          kind: "callback",
          callbackArgs: []
        },
        onNextClick: {
          types: [
            "function"
          ],
          description: "Direct callback for Next (alternative to event)",
          kind: "callback",
          callbackArgs: []
        },
        onCompleteClick: {
          types: [
            "function"
          ],
          description: "Direct callback for Complete (alternative to event)",
          kind: "callback",
          callbackArgs: []
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact mode (smaller padding)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "wizard-container": {
      type: "wizard-container",
      category: "navigation",
      sourceFile: "components/core/molecules/WizardContainer.tsx",
      tier: "molecules",
      family: "core",
      description: "Multi-step wizard container",
      suggestedFor: [
        "multi-step forms",
        "wizards",
        "onboarding flows"
      ],
      typicalSize: "large",
      propsSchema: {
        steps: {
          types: [
            "array"
          ],
          description: "Wizard steps",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              tabId: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              optional: {
                types: [
                  "boolean"
                ]
              },
              isValid: {
                types: [
                  "function"
                ]
              },
              sections: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    description: {
                      types: [
                        "string"
                      ]
                    },
                    fields: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          id: {
                            types: [
                              "string"
                            ]
                          },
                          type: {
                            types: [
                              "string"
                            ]
                          },
                          label: {
                            types: [
                              "string"
                            ]
                          },
                          required: {
                            types: [
                              "boolean"
                            ]
                          },
                          repeatable: {
                            types: [
                              "boolean"
                            ]
                          },
                          options: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                value: {
                                  types: [
                                    "string"
                                  ]
                                },
                                label: {
                                  types: [
                                    "string"
                                  ]
                                },
                                isDefault: {
                                  types: [
                                    "boolean"
                                  ]
                                }
                              },
                              required: [
                                "value",
                                "label"
                              ]
                            }
                          },
                          defaultValue: {
                            types: [
                              "string",
                              "number",
                              "boolean",
                              "array",
                              "object"
                            ],
                            items: {
                              types: [
                                "string"
                              ]
                            },
                            properties: {
                              name: {
                                types: [
                                  "string"
                                ]
                              },
                              url: {
                                types: [
                                  "string"
                                ]
                              },
                              mimeType: {
                                types: [
                                  "string"
                                ]
                              },
                              sizeBytes: {
                                types: [
                                  "number"
                                ]
                              }
                            },
                            required: [
                              "name",
                              "url",
                              "mimeType",
                              "sizeBytes"
                            ]
                          },
                          condition: {
                            types: [
                              "sexpr"
                            ]
                          },
                          placeholder: {
                            types: [
                              "string"
                            ]
                          },
                          entityField: {
                            types: [
                              "string"
                            ]
                          },
                          minLength: {
                            types: [
                              "number"
                            ]
                          },
                          maxLength: {
                            types: [
                              "number"
                            ]
                          },
                          dataSource: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          displayFields: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          searchConfig: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          hiddenCalculations: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                variable: {
                                  types: [
                                    "string"
                                  ]
                                },
                                expression: {
                                  types: [
                                    "string"
                                  ]
                                },
                                scope: {
                                  types: [
                                    "string"
                                  ]
                                }
                              },
                              required: [
                                "variable",
                                "expression"
                              ]
                            }
                          },
                          signatureConfig: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          displayTemplate: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          lawReference: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          contextMenu: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          calculated: {
                            types: [
                              "object"
                            ],
                            mapValue: {
                              types: [
                                "object"
                              ],
                              controlValue: true
                            }
                          },
                          readOnly: {
                            types: [
                              "boolean"
                            ]
                          },
                          minDate: {
                            types: [
                              "string"
                            ]
                          },
                          stats: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                label: {
                                  types: [
                                    "string"
                                  ]
                                },
                                value: {
                                  types: [
                                    "string",
                                    "number",
                                    "boolean",
                                    "array",
                                    "object"
                                  ],
                                  items: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  properties: {
                                    name: {
                                      types: [
                                        "string"
                                      ]
                                    },
                                    url: {
                                      types: [
                                        "string"
                                      ]
                                    },
                                    mimeType: {
                                      types: [
                                        "string"
                                      ]
                                    },
                                    sizeBytes: {
                                      types: [
                                        "number"
                                      ]
                                    }
                                  },
                                  required: [
                                    "name",
                                    "url",
                                    "mimeType",
                                    "sizeBytes"
                                  ]
                                },
                                icon: {
                                  types: [
                                    "string"
                                  ]
                                }
                              },
                              required: [
                                "label",
                                "value"
                              ]
                            }
                          },
                          items: {
                            types: [
                              "array"
                            ],
                            items: {
                              types: [
                                "object"
                              ],
                              properties: {
                                id: {
                                  types: [
                                    "string"
                                  ]
                                },
                                label: {
                                  types: [
                                    "string"
                                  ]
                                },
                                autoCheck: {
                                  types: [
                                    "boolean"
                                  ]
                                }
                              },
                              required: [
                                "id",
                                "label"
                              ]
                            }
                          }
                        },
                        required: [
                          "id",
                          "type"
                        ]
                      }
                    },
                    subsections: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        cyclic: true,
                        cyclicTypeName: "WizardSection"
                      }
                    },
                    condition: {
                      types: [
                        "sexpr"
                      ]
                    },
                    repeatable: {
                      types: [
                        "boolean"
                      ]
                    },
                    minItems: {
                      types: [
                        "number"
                      ]
                    },
                    addButtonLabel: {
                      types: [
                        "string"
                      ]
                    },
                    hiddenCalculations: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          variable: {
                            types: [
                              "string"
                            ]
                          },
                          expression: {
                            types: [
                              "string"
                            ]
                          },
                          scope: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "variable",
                          "expression"
                        ]
                      }
                    },
                    dataSource: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "object"
                        ],
                        controlValue: true
                      }
                    },
                    readOnly: {
                      types: [
                        "boolean"
                      ]
                    }
                  },
                  required: [
                    "id"
                  ]
                }
              },
              globalVariablesRequired: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              globalVariablesSet: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              localVariables: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              entityMapping: {
                types: [
                  "object"
                ],
                properties: {
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  mode: {
                    types: [
                      "string"
                    ]
                  },
                  parentField: {
                    types: [
                      "string"
                    ]
                  },
                  idField: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "entity",
                  "mode"
                ]
              },
              validationRules: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    condition: {
                      types: [
                        "sexpr"
                      ]
                    },
                    message: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "condition",
                    "message"
                  ]
                }
              },
              lawReferences: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    law: {
                      types: [
                        "string"
                      ]
                    },
                    article: {
                      types: [
                        "string"
                      ]
                    },
                    description: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "law",
                    "article"
                  ]
                }
              },
              phase: {
                types: [
                  "string"
                ]
              },
              contextMenu: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          }
        },
        currentStep: {
          types: [
            "number",
            "string"
          ],
          description: "Current step index (controlled) - accepts number or string for generated code compatibility"
        },
        onStepChange: {
          types: [
            "function"
          ],
          description: "Callback when step changes",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "stepIndex",
              type: "number"
            }
          ]
        },
        onComplete: {
          types: [
            "function"
          ],
          description: "Callback when wizard is completed",
          kind: "callback",
          callbackArgs: []
        },
        showProgress: {
          types: [
            "boolean"
          ],
          description: "Show progress indicator",
          default: true
        },
        allowBack: {
          types: [
            "boolean"
          ],
          description: "Allow navigation to previous steps",
          default: true
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Modal mode (compact header, no padding)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        nextEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{nextEvent} when advancing to next step",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "step",
              type: "number",
              required: true
            }
          ]
        },
        backEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{backEvent} when going to previous step",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "step",
              type: "number",
              required: true
            }
          ]
        },
        completeEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{completeEvent} when wizard completes",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    pagination: {
      type: "pagination",
      category: "navigation",
      sourceFile: "components/core/molecules/Pagination.tsx",
      tier: "molecules",
      family: "core",
      description: "Page navigation controls",
      suggestedFor: [
        "paginated lists",
        "tables"
      ],
      typicalSize: "tiny",
      propsSchema: {
        currentPage: {
          types: [
            "number"
          ],
          description: "Current page (1-indexed)",
          required: true
        },
        totalPages: {
          types: [
            "number"
          ],
          description: "Total number of pages",
          required: true
        },
        onPageChange: {
          types: [
            "function"
          ],
          description: "Callback when page changes (optional - can be a no-op if not interactive)",
          kind: "callback",
          callbackArgs: [
            {
              name: "page",
              type: "number"
            }
          ]
        },
        showPageSize: {
          types: [
            "boolean"
          ],
          description: "Show page size selector",
          default: false
        },
        pageSizeOptions: {
          types: [
            "array"
          ],
          description: "Page size options",
          items: {
            types: [
              "number"
            ]
          },
          default: [
            10,
            20,
            50,
            100
          ]
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Current page size"
        },
        onPageSizeChange: {
          types: [
            "function"
          ],
          description: "Callback when page size changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "size",
              type: "number"
            }
          ]
        },
        showJumpToPage: {
          types: [
            "boolean"
          ],
          description: "Show jump to page input",
          default: false
        },
        showTotal: {
          types: [
            "boolean"
          ],
          description: "Show total count",
          default: false
        },
        totalItems: {
          types: [
            "number"
          ],
          description: "Total items count"
        },
        maxVisiblePages: {
          types: [
            "number"
          ],
          description: "Maximum number of page buttons to show",
          default: 7
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        pageChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative page change event \u2014 emits UI:{pageChangeEvent} with { page }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "page",
              type: "number",
              required: true
            }
          ]
        },
        pageSizeChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative page size change event \u2014 emits UI:{pageSizeChangeEvent} with { pageSize }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "pageSize",
              type: "number",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "empty-state": {
      type: "empty-state",
      category: "state",
      sourceFile: "components/core/molecules/EmptyState.tsx",
      tier: "molecules",
      family: "core",
      description: "Message when no data exists",
      suggestedFor: [
        "list pages",
        "search results"
      ],
      typicalSize: "tiny",
      propsSchema: {
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: 'Icon to display. Accepts either: - A Lucide icon component (LucideIcon) - A string icon name (e.g., "check-circle", "x-circle")'
        },
        title: {
          types: [
            "string"
          ],
          description: "Primary text to display - use title or message (message is alias for backwards compat)"
        },
        message: {
          types: [
            "string"
          ],
          description: "Alias for title - used by schema patterns"
        },
        description: {
          types: [
            "string"
          ],
          description: "description prop"
        },
        actionLabel: {
          types: [
            "string"
          ],
          description: "actionLabel prop"
        },
        onAction: {
          types: [
            "function"
          ],
          description: "onAction prop",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        destructive: {
          types: [
            "boolean"
          ],
          description: "Destructive styling for confirmation dialogs"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant for color styling",
          enumValues: [
            "default",
            "success",
            "error",
            "warning",
            "info"
          ]
        },
        actionEvent: {
          types: [
            "string"
          ],
          description: "Declarative action event \u2014 emits UI:{actionEvent} via eventBus when action button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant (which carries status color).",
          enumValues: [
            "illustrated",
            "icon-only",
            "text-only",
            "mascot"
          ],
          default: "icon-only"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "loading-state": {
      type: "loading-state",
      category: "state",
      sourceFile: "components/core/molecules/LoadingState.tsx",
      tier: "molecules",
      family: "core",
      description: "Loading indicator",
      suggestedFor: [
        "any page with async data"
      ],
      typicalSize: "tiny",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        message: {
          types: [
            "string"
          ],
          description: "message prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        fullPage: {
          types: [
            "boolean"
          ],
          description: "Center over the whole viewport (fix: inset-0 overlay) instead of inline.",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "error-state": {
      type: "error-state",
      category: "state",
      sourceFile: "components/core/molecules/ErrorState.tsx",
      tier: "molecules",
      family: "core",
      description: "Error message display",
      suggestedFor: [
        "error handling",
        "failed requests"
      ],
      typicalSize: "small",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        message: {
          types: [
            "string"
          ],
          description: "Error message to display"
        },
        description: {
          types: [
            "string"
          ],
          description: "Alias for message (schema compatibility)"
        },
        onRetry: {
          types: [
            "function"
          ],
          description: "onRetry prop",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        retryEvent: {
          types: [
            "string"
          ],
          description: "Declarative retry event \u2014 emits UI:{retryEvent} via eventBus when retry button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    notification: {
      type: "notification",
      category: "state",
      sourceFile: "components/core/molecules/Toast.tsx",
      tier: "molecules",
      family: "core",
      description: "Toast notification message",
      suggestedFor: [
        "success messages",
        "error alerts",
        "info messages"
      ],
      typicalSize: "tiny",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Toast variant",
          enumValues: [
            "success",
            "error",
            "info",
            "warning"
          ],
          default: "info"
        },
        message: {
          types: [
            "string"
          ],
          description: "Toast message",
          required: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Toast title (optional)"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Auto-dismiss duration in milliseconds (0 = no auto-dismiss). The timer pauses while the toast is hovered or focused, and a toast carrying an action (`actionLabel` with `onAction`/`actionEvent`) never auto-dismisses.",
          default: 5e3
        },
        dismissible: {
          types: [
            "boolean"
          ],
          description: "Show dismiss button",
          default: true
        },
        onDismiss: {
          types: [
            "function"
          ],
          description: "Callback when toast is dismissed",
          kind: "callback",
          callbackArgs: []
        },
        actionLabel: {
          types: [
            "string"
          ],
          description: "Action button label"
        },
        onAction: {
          types: [
            "function"
          ],
          description: "Action button click handler",
          kind: "callback",
          callbackArgs: []
        },
        badge: {
          types: [
            "string",
            "number"
          ],
          description: "Badge count (optional)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        dismissEvent: {
          types: [
            "string"
          ],
          description: "Declarative dismiss event \u2014 emits UI:{dismissEvent} via eventBus when toast is dismissed",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        actionEvent: {
          types: [
            "string"
          ],
          description: "Declarative action event \u2014 emits UI:{actionEvent} via eventBus when action button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    modal: {
      type: "modal",
      category: "container",
      sourceFile: "components/core/molecules/Modal.tsx",
      tier: "molecules",
      family: "core",
      description: "Modal dialog overlay",
      suggestedFor: [
        "dialogs",
        "confirmations",
        "forms"
      ],
      typicalSize: "medium",
      propsSchema: {
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the modal is open (defaults to true when rendered by slot wrapper)",
          default: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Callback when modal should close (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fires after the exit animation completes (the modal is about to unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        children: {
          types: [
            "node"
          ],
          description: "Modal content (can be empty if using slot content)",
          default: null
        },
        footer: {
          types: [
            "node"
          ],
          description: "footer prop"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "md"
        },
        showCloseButton: {
          types: [
            "boolean"
          ],
          description: "showCloseButton prop",
          default: true
        },
        closeOnOverlayClick: {
          types: [
            "boolean"
          ],
          description: "closeOnOverlayClick prop",
          default: true
        },
        closeOnEscape: {
          types: [
            "boolean"
          ],
          description: "closeOnEscape prop",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Declarative close event \u2014 emits UI:{closeEvent} via eventBus when modal should close",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        swipeDownToClose: {
          types: [
            "boolean"
          ],
          description: "Enable swipe-down-to-close on mobile bottom sheet (default: true)",
          default: true
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant.",
          enumValues: [
            "centered-card",
            "top-sheet",
            "side-drawer",
            "full-screen"
          ],
          default: "centered-card"
        },
        contained: {
          types: [
            "boolean"
          ],
          description: "Render inside the nearest positioned ancestor (a preview frame) instead of portaling over the whole window; page scroll is left alone.",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    drawer: {
      type: "drawer",
      category: "container",
      sourceFile: "components/core/molecules/Drawer.tsx",
      tier: "molecules",
      family: "core",
      description: "Side drawer panel",
      suggestedFor: [
        "detail panels",
        "settings",
        "side navigation"
      ],
      typicalSize: "medium",
      propsSchema: {
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the drawer is open (defaults to true when rendered by slot wrapper)",
          default: true
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fires after the exit animation completes (the drawer is about to unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        contained: {
          types: [
            "boolean"
          ],
          description: "Render inside the nearest positioned ancestor (a preview frame) instead of over the window.",
          default: false
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Callback when drawer should close (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        title: {
          types: [
            "node"
          ],
          description: "Drawer title (text or any node, e.g. a brand logo + name)."
        },
        children: {
          types: [
            "node"
          ],
          description: "Drawer content (can be empty if using slot content)",
          default: null
        },
        footer: {
          types: [
            "node"
          ],
          description: "Footer content"
        },
        position: {
          types: [
            "string"
          ],
          description: "Position (left or right)",
          enumValues: [
            "left",
            "right"
          ],
          default: "right"
        },
        width: {
          types: [
            "string"
          ],
          description: "Width (CSS value or preset size)",
          default: "md"
        },
        showCloseButton: {
          types: [
            "boolean"
          ],
          description: "Show close button",
          default: true
        },
        closeOnOverlayClick: {
          types: [
            "boolean"
          ],
          description: "Close on overlay click",
          default: true
        },
        closeOnEscape: {
          types: [
            "boolean"
          ],
          description: "Close on escape key",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class name"
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Declarative close event \u2014 emits UI:{closeEvent} via eventBus when drawer should close",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "confirm-dialog": {
      type: "confirm-dialog",
      category: "container",
      sourceFile: "components/core/molecules/ConfirmDialog.tsx",
      tier: "molecules",
      family: "core",
      description: "Confirmation dialog",
      suggestedFor: [
        "destructive actions",
        "confirmations"
      ],
      typicalSize: "small",
      propsSchema: {
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the dialog is open (defaults to true when rendered by slot wrapper)",
          default: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Callback when dialog is closed (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fires after the exit animation completes (the dialog is about to unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        onConfirm: {
          types: [
            "function"
          ],
          description: "Callback when action is confirmed (injected by slot wrapper)",
          kind: "callback",
          callbackArgs: []
        },
        title: {
          types: [
            "string"
          ],
          description: "Dialog title",
          required: true
        },
        message: {
          types: [
            "string",
            "node"
          ],
          description: "Dialog message/description"
        },
        description: {
          types: [
            "string",
            "node"
          ],
          description: "Alias for message (schema compatibility)"
        },
        confirmText: {
          types: [
            "string"
          ],
          description: "Confirm button text"
        },
        confirmLabel: {
          types: [
            "string"
          ],
          description: "Alias for confirmText (schema compatibility)"
        },
        cancelText: {
          types: [
            "string"
          ],
          description: "Cancel button text"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Alias for cancelText (schema compatibility)"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Dialog variant",
          enumValues: [
            "danger",
            "warning",
            "info",
            "default"
          ],
          default: "danger"
        },
        size: {
          types: [
            "string"
          ],
          description: "Dialog size",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "sm"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state for confirm button",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    box: {
      type: "box",
      category: "layout",
      sourceFile: "components/core/atoms/Box.tsx",
      tier: "atoms",
      family: "core",
      description: "Generic styled container with padding, borders, and backgrounds",
      suggestedFor: [
        "card containers",
        "sections",
        "styled wrappers",
        "content boxes"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        "data-theme": {
          types: [
            "string"
          ],
          description: "Data-theme attribute applied to the root element for CSS theme scoping (e.g. almadar-website-dark)."
        },
        padding: {
          types: [
            "string"
          ],
          description: "Padding on all sides",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          example: "md"
        },
        paddingX: {
          types: [
            "string"
          ],
          description: "Horizontal padding (overrides padding for x-axis)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        paddingY: {
          types: [
            "string"
          ],
          description: "Vertical padding (overrides padding for y-axis)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        margin: {
          types: [
            "string"
          ],
          description: "Margin on all sides",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ]
        },
        marginX: {
          types: [
            "string"
          ],
          description: "Horizontal margin",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ]
        },
        marginY: {
          types: [
            "string"
          ],
          description: "Vertical margin",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ]
        },
        bg: {
          types: [
            "string"
          ],
          description: "Background color",
          enumValues: [
            "transparent",
            "primary",
            "secondary",
            "muted",
            "accent",
            "surface",
            "overlay",
            "gradient",
            "inverse"
          ],
          default: "transparent"
        },
        border: {
          types: [
            "boolean"
          ],
          description: "Show border",
          default: false,
          example: true
        },
        rounded: {
          types: [
            "string"
          ],
          description: "Border radius",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ],
          default: "none"
        },
        shadow: {
          types: [
            "string"
          ],
          description: "Box shadow",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "none"
        },
        display: {
          types: [
            "string"
          ],
          description: "Display type",
          enumValues: [
            "block",
            "inline",
            "inline-block",
            "flex",
            "inline-flex",
            "grid"
          ]
        },
        fullWidth: {
          types: [
            "boolean"
          ],
          description: "Fill available width",
          default: false
        },
        fullHeight: {
          types: [
            "boolean"
          ],
          description: "Fill available height",
          default: false
        },
        overflow: {
          types: [
            "string"
          ],
          description: "Overflow behavior",
          enumValues: [
            "auto",
            "hidden",
            "visible",
            "scroll"
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Position",
          enumValues: [
            "relative",
            "absolute",
            "fixed",
            "sticky"
          ]
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        hoverEvent: {
          types: [
            "string"
          ],
          description: "Declarative hover event \u2014 emits UI:{hoverEvent} with { hovered: true/false } on mouseEnter/mouseLeave",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "hovered",
              type: "boolean",
              required: true
            }
          ]
        },
        tapReveal: {
          types: [
            "boolean"
          ],
          description: "When true (default), a touch/pen tap also fires `hoverEvent` (toggling hovered) so hover-only reveals work on touch.",
          default: true
        },
        maxWidth: {
          types: [
            "string"
          ],
          description: 'Maximum width (CSS value, e.g., "550px", "80rem")'
        },
        edgeTop: {
          types: [
            "string"
          ],
          description: `Shape cut into the top edge of a full-width band; "curve" uses the theme's shape`,
          enumValues: [
            "none",
            "curve",
            "wave",
            "arc",
            "tilt",
            "step",
            "scallop"
          ],
          default: "none"
        },
        edgeBottom: {
          types: [
            "string"
          ],
          description: `Shape cut into the bottom edge of a full-width band; "curve" uses the theme's shape`,
          enumValues: [
            "none",
            "curve",
            "wave",
            "arc",
            "tilt",
            "step",
            "scallop"
          ],
          default: "none"
        },
        edgeColor: {
          types: [
            "string"
          ],
          description: "Surface of the neighbouring band, which fills the cut",
          enumValues: [
            "background",
            "muted",
            "surface",
            "gradient",
            "inverse"
          ],
          default: "background"
        },
        edgeFlip: {
          types: [
            "boolean"
          ],
          description: "Mirror the edge shapes horizontally",
          default: false
        },
        texture: {
          types: [
            "boolean"
          ],
          description: "Lay the theme's texture over the band",
          default: false
        },
        animateChanges: {
          types: [
            "boolean"
          ],
          description: "Content that appears or changes inside the box eases in instead of switching in one frame (new rows, swapped views, changed text), played in presentation motion (the theme's dramatic duration, standard easing) so it can be watched. Off under reduced motion.",
          default: false
        },
        backdrop: {
          types: [
            "node"
          ],
          description: "Scene behind the band's content (photo, illustration, pattern); never takes clicks"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    grid: {
      type: "grid",
      category: "layout",
      sourceFile: "components/core/molecules/Grid.tsx",
      tier: "molecules",
      family: "core",
      description: "CSS Grid layout for multi-column content",
      suggestedFor: [
        "dashboards",
        "card grids",
        "multi-column layouts",
        "responsive grids"
      ],
      typicalSize: "large",
      propsSchema: {
        cols: {
          types: [
            "number",
            "string",
            "object"
          ],
          description: "Number of columns (can be responsive object)",
          enumValues: [
            "none"
          ],
          properties: {
            base: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            sm: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            md: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            lg: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            },
            xl: {
              types: [
                "number",
                "string"
              ],
              enumValues: [
                "none"
              ]
            }
          },
          default: 1
        },
        rows: {
          types: [
            "number"
          ],
          description: "Number of rows"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between items",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        rowGap: {
          types: [
            "string"
          ],
          description: "Row gap (overrides gap for rows)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        colGap: {
          types: [
            "string"
          ],
          description: "Column gap (overrides gap for columns)",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ]
        },
        alignItems: {
          types: [
            "string"
          ],
          description: "Align items on block axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ]
        },
        justifyItems: {
          types: [
            "string"
          ],
          description: "Justify items on inline axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch"
          ]
        },
        flow: {
          types: [
            "string"
          ],
          description: "Auto-flow direction",
          enumValues: [
            "row",
            "col",
            "row-dense",
            "col-dense"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    center: {
      type: "center",
      category: "layout",
      sourceFile: "components/core/atoms/Center.tsx",
      tier: "atoms",
      family: "core",
      description: "Centers content horizontally and vertically",
      suggestedFor: [
        "centered modals",
        "loading states",
        "empty states",
        "hero sections"
      ],
      typicalSize: "medium",
      propsSchema: {
        inline: {
          types: [
            "boolean"
          ],
          description: "Center inline (width fits content) vs block (full width)",
          default: false
        },
        horizontal: {
          types: [
            "boolean"
          ],
          description: "Center only horizontally",
          default: true
        },
        vertical: {
          types: [
            "boolean"
          ],
          description: "Center only vertically",
          default: true
        },
        minHeight: {
          types: [
            "string",
            "number"
          ],
          description: "Minimum height (useful for vertical centering)"
        },
        fullHeight: {
          types: [
            "boolean"
          ],
          description: "Fill available height",
          default: false
        },
        fullWidth: {
          types: [
            "boolean"
          ],
          description: "Fill available width",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    spacer: {
      type: "spacer",
      category: "layout",
      sourceFile: "components/core/atoms/Spacer.tsx",
      tier: "atoms",
      family: "core",
      description: "Flexible space that expands to fill available room",
      suggestedFor: [
        "pushing elements apart",
        "flexible spacing",
        "layout adjustment"
      ],
      typicalSize: "tiny",
      propsSchema: {
        size: {
          types: [
            "string"
          ],
          description: "Fixed size (auto = flex grow)",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "auto"
          ],
          default: "auto"
        },
        axis: {
          types: [
            "string"
          ],
          description: "Orientation (for fixed sizes)",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    divider: {
      type: "divider",
      category: "layout",
      sourceFile: "components/core/atoms/Divider.tsx",
      tier: "atoms",
      family: "core",
      description: "Visual separator between content sections",
      suggestedFor: [
        "section breaks",
        "content separation",
        "visual grouping"
      ],
      typicalSize: "tiny",
      propsSchema: {
        orientation: {
          types: [
            "string"
          ],
          description: "Orientation of the divider",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        label: {
          types: [
            "string"
          ],
          description: "Text label to display in the divider"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Line style variant",
          enumValues: [
            "solid",
            "dashed",
            "dotted"
          ],
          default: "solid"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "runtime-debugger": {
      type: "runtime-debugger",
      category: "debug",
      sourceFile: "components/core/organisms/debug/RuntimeDebugger.tsx",
      tier: "organisms",
      family: "core",
      description: "Debug overlay showing FPS, entity states, event logs",
      suggestedFor: [
        "development",
        "debugging",
        "testing"
      ],
      typicalSize: "small",
      propsSchema: {
        position: {
          types: [
            "string"
          ],
          description: "Initial position",
          enumValues: [
            "bottom-right",
            "bottom-left",
            "top-right",
            "top-left"
          ],
          default: "bottom-right"
        },
        defaultCollapsed: {
          types: [
            "boolean"
          ],
          description: "Initial collapsed state",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Display mode: floating (fixed overlay), inline (block element), or verify (always-visible compact overlay for verification runs)",
          enumValues: [
            "floating",
            "inline",
            "verify"
          ],
          default: "floating"
        },
        defaultTab: {
          types: [
            "string"
          ],
          description: "Default active tab id"
        },
        schema: {
          types: [
            "sexpr"
          ],
          description: "Raw schema for EventDispatcherTab payload extraction",
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    button: {
      type: "button",
      category: "component",
      sourceFile: "components/core/atoms/Button.tsx",
      tier: "atoms",
      family: "core",
      description: "Clickable button that emits events",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "actions",
        "form submission",
        "navigation triggers"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "primary",
            "secondary",
            "ghost",
            "danger",
            "success",
            "warning",
            "default",
            "link"
          ],
          default: "primary"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop",
          default: false
        },
        leftIcon: {
          types: [
            "icon",
            "string"
          ],
          description: 'Left icon: a Lucide component or a canonical icon name string (e.g. "plus", "trash").'
        },
        rightIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Right icon: a Lucide component or a canonical icon name string."
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Alias for leftIcon"
        },
        iconRight: {
          types: [
            "icon",
            "string"
          ],
          description: "Alias for rightIcon"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Asset image rendered as the leading icon; takes precedence over icon/leftIcon when provided.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        label: {
          types: [
            "string"
          ],
          description: "Button label text (alternative to children for schema-driven rendering)",
          example: "Button"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable the button (greys out, blocks click events)"
        },
        href: {
          types: [
            "string"
          ],
          description: "Makes the button a link: `#anchor` scrolls, an absolute URL loads, a path navigates in-app"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Runs on activation, on the button or, with `href`, on the link; calling `preventDefault()` there keeps a link from following its `href`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "event",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        "data-testid": {
          types: [
            "string"
          ],
          description: "Test identifier for automated tests"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    badge: {
      type: "badge",
      category: "component",
      sourceFile: "components/core/atoms/Badge.tsx",
      tier: "atoms",
      family: "core",
      description: "Small label for status or count",
      suggestedFor: [
        "status indicators",
        "counts",
        "tags"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "default",
            "primary",
            "secondary",
            "success",
            "warning",
            "danger",
            "error",
            "info",
            "neutral"
          ],
          default: "default"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        amount: {
          types: [
            "number"
          ],
          description: "Numeric count or amount to display in badge"
        },
        label: {
          types: [
            "string",
            "number"
          ],
          description: "Badge label text (alternative to children for schema-driven rendering). Numeric values are auto-coerced to string for rendering \u2014 common case is unread counts, error counts, status codes, etc."
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component or canonical kebab-case icon name string"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Asset image rendered as the badge icon; takes precedence over icon when provided.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        onRemove: {
          types: [
            "function"
          ],
          description: 'When set, renders a small X button on the right of the badge that invokes this handler \u2014 turns the badge into a removable chip. Used by the TagInput molecule and other "list of removable values" surfaces.',
          kind: "callback",
          callbackArgs: [],
          default: ""
        },
        removeLabel: {
          types: [
            "string"
          ],
          description: 'Accessible label for the remove button. Defaults to "Remove".'
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    avatar: {
      type: "avatar",
      category: "component",
      sourceFile: "components/core/atoms/Avatar.tsx",
      tier: "atoms",
      family: "core",
      description: "User avatar image or initials",
      accessibleName: [
        "alt",
        "name"
      ],
      suggestedFor: [
        "user profiles",
        "comments",
        "team lists"
      ],
      typicalSize: "tiny",
      propsSchema: {
        src: {
          types: [
            "asset"
          ],
          description: "Image source URL"
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alt text for the image"
        },
        name: {
          types: [
            "string"
          ],
          description: "Full name - initials will be generated automatically"
        },
        initials: {
          types: [
            "string"
          ],
          description: 'Initials to display (e.g., "JD" for John Doe) If not provided but name is, initials will be auto-generated'
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon to display when no image or initials. Accepts a Lucide component directly, or a canonical kebab-case icon name string (resolved via `resolveIcon`) so trait/factory authors can pass an icon by name."
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the avatar",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        status: {
          types: [
            "string"
          ],
          description: "Status indicator",
          enumValues: [
            "online",
            "offline",
            "away",
            "busy"
          ]
        },
        badge: {
          types: [
            "string",
            "number"
          ],
          description: "Badge content (e.g., notification count)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: []
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    icon: {
      type: "icon",
      category: "component",
      sourceFile: "components/core/atoms/Icon.tsx",
      tier: "atoms",
      family: "core",
      description: "Icon from Lucide icon set",
      suggestedFor: [
        "decorative icons",
        "status indicators",
        "buttons"
      ],
      typicalSize: "tiny",
      propsSchema: {
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component (preferred for type-safe usage), OR a canonical kebab-case icon name string \u2014 a string is treated exactly like `name` so trait/factory authors can pass an icon by name through the `icon` prop."
        },
        name: {
          types: [
            "string"
          ],
          description: "Icon name as string (resolved from iconMap)"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the icon",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        color: {
          types: [
            "string"
          ],
          description: "Semantic palette token or an arbitrary Tailwind color class."
        },
        animation: {
          types: [
            "string"
          ],
          description: "Animation type",
          enumValues: [
            "spin",
            "pulse",
            "none"
          ],
          default: "none"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Icon stroke width - uses theme default if not specified"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline style",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    card: {
      type: "card",
      category: "component",
      sourceFile: "components/core/atoms/Card.tsx",
      tier: "atoms",
      family: "core",
      description: "Card container with optional header and footer",
      suggestedFor: [
        "content cards",
        "product cards",
        "info cards"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "default",
            "bordered",
            "elevated",
            "interactive"
          ],
          default: "bordered"
        },
        padding: {
          types: [
            "string"
          ],
          description: "padding prop",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        title: {
          types: [
            "string"
          ],
          description: "Card title - renders in header if provided"
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Card subtitle - renders below title"
        },
        shadow: {
          types: [
            "string"
          ],
          description: "Shadow size override",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg"
          ]
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the semantic variant.",
          enumValues: [
            "elevated",
            "flat-bordered",
            "borderless-divider",
            "ticket",
            "invoice",
            "chip",
            "tile-image-first"
          ],
          default: "elevated"
        },
        children: {
          types: [
            "node"
          ],
          description: "Card content"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event key emitted on click for trait dispatch",
          kind: "event"
        },
        loading: {
          types: [
            "boolean"
          ],
          description: "Shows a skeleton/spinner overlay while true."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "progress-bar": {
      type: "progress-bar",
      category: "component",
      sourceFile: "components/core/atoms/ProgressBar.tsx",
      tier: "atoms",
      family: "core",
      description: "Progress indicator bar",
      suggestedFor: [
        "progress tracking",
        "loading",
        "completion status"
      ],
      typicalSize: "tiny",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Progress value (0-100)",
          required: true
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value (for calculating percentage)",
          default: 100
        },
        progressType: {
          types: [
            "string"
          ],
          description: "Type of the progress bar (linear, circular, stepped)",
          enumValues: [
            "linear",
            "circular",
            "stepped"
          ],
          default: "linear"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant/color of the progress bar",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "error",
            "danger"
          ],
          default: "primary"
        },
        color: {
          types: [
            "string"
          ],
          description: "Color variant (alias for variant; `variant` applies when omitted)",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "error",
            "danger"
          ]
        },
        showPercentage: {
          types: [
            "boolean"
          ],
          description: "Show percentage text",
          default: false
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: "Alias for showPercentage (pattern compatibility)",
          default: false
        },
        label: {
          types: [
            "string"
          ],
          description: "Label text"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size (for circular variant)",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        steps: {
          types: [
            "number"
          ],
          description: "Number of steps (for stepped variant)",
          default: 5
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    spinner: {
      type: "spinner",
      category: "component",
      sourceFile: "components/core/atoms/Spinner.tsx",
      tier: "atoms",
      family: "core",
      description: "Loading spinner animation",
      suggestedFor: [
        "loading states",
        "async operations"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        overlay: {
          types: [
            "boolean"
          ],
          description: "Renders a centered overlay backdrop instead of inline."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    input: {
      type: "input",
      category: "component",
      sourceFile: "components/core/atoms/Input.tsx",
      tier: "atoms",
      family: "core",
      description: "Text input field",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "forms",
        "search",
        "data entry"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        value: {
          types: [
            "string",
            "number"
          ],
          description: "Current value"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether input is disabled"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              typeWhen: [
                {
                  knob: "inputType",
                  equals: "number",
                  type: "number"
                }
              ]
            }
          ]
        },
        inputType: {
          types: [
            "string"
          ],
          description: "Input type \u2014 selects the field's data mode. Use 'password' for masked credentials / secret / passphrase entry (there is no separate password pattern); 'email', 'tel', 'url', 'number', 'search', 'date', and 'time' for their respective values; and 'select' / 'textarea' for choice and multi-line entry in addition to the standard single-line types.",
          enumValues: [
            "text",
            "email",
            "password",
            "number",
            "tel",
            "url",
            "search",
            "date",
            "datetime-local",
            "time",
            "checkbox",
            "select",
            "textarea"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "helperText prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "error prop"
        },
        leftIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "leftIcon prop"
        },
        rightIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "rightIcon prop"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component or canonical kebab-case icon name string for left side"
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show clear button when input has value"
        },
        onClear: {
          types: [
            "function",
            "string"
          ],
          description: "Callback or declarative event key when clear button is clicked"
        },
        options: {
          types: [
            "array"
          ],
          description: "Options for select type",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        rows: {
          types: [
            "number"
          ],
          description: "Rows for textarea type",
          default: 3
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "_payload",
              type: "object",
              required: false
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    textarea: {
      type: "textarea",
      category: "component",
      sourceFile: "components/core/atoms/Textarea.tsx",
      tier: "atoms",
      family: "core",
      description: "Multi-line text input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "long text",
        "descriptions",
        "comments"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        label: {
          types: [
            "string"
          ],
          description: "Visible label naming the field."
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        rows: {
          types: [
            "number"
          ],
          description: "Number of visible rows"
        },
        value: {
          types: [
            "string"
          ],
          description: "Controlled value, for a field the program fills or clears."
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event: fires on \u2318/Ctrl+Enter with `{ value }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text describing the field."
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    select: {
      type: "select",
      category: "component",
      sourceFile: "components/core/atoms/Select.tsx",
      tier: "atoms",
      family: "core",
      description: "Dropdown select input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "option selection",
        "filters",
        "forms"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        label: {
          types: [
            "string"
          ],
          description: "Visible label naming the control."
        },
        options: {
          types: [
            "array"
          ],
          description: "Select options (flat list)",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        groups: {
          types: [
            "array"
          ],
          description: "Grouped options \u2014 rendered as <optgroup> in native mode, sections in rich mode.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    value: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "value",
                    "label"
                  ]
                }
              }
            },
            required: [
              "label",
              "options"
            ]
          }
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        value: {
          types: [
            "string",
            "array"
          ],
          description: "Current value (string for single, string[] for multiple)",
          items: {
            types: [
              "string"
            ]
          }
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch \u2014 emits `{ value }` on selection commit (same gestures as `onChange`).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              schema: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        multiple: {
          types: [
            "boolean"
          ],
          description: "Allow selecting multiple values \u2014 activates the rich dropdown."
        },
        searchable: {
          types: [
            "boolean"
          ],
          description: "Show a search input inside the dropdown \u2014 activates the rich dropdown."
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show a clear button when a value is selected."
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler (native ChangeEvent) or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              schema: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        onValueChange: {
          types: [
            "function",
            "string"
          ],
          description: "Value-based change: a React callback (internal use) OR a declarative event key that emits `{ value }` on the bus (render-ui / lolo authoring). Mirrors the `onChange` handler|event convention so it's an event-emitting prop, not a bare callback.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              schema: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    checkbox: {
      type: "checkbox",
      category: "component",
      sourceFile: "components/core/atoms/Checkbox.tsx",
      tier: "atoms",
      family: "core",
      description: "Checkbox input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "boolean options",
        "multi-select",
        "toggles"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        checked: {
          types: [
            "boolean"
          ],
          description: "Whether the checkbox is checked"
        },
        defaultChecked: {
          types: [
            "boolean"
          ],
          description: "Default checked state (uncontrolled)"
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "checked",
              type: "boolean",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    radio: {
      type: "radio",
      category: "component",
      sourceFile: "components/core/atoms/Radio.tsx",
      tier: "atoms",
      family: "core",
      description: "Radio button input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "single selection",
        "options"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        options: {
          types: [
            "array"
          ],
          description: "Radio options (string array or SelectOption array)",
          items: {
            types: [
              "string"
            ]
          }
        },
        value: {
          types: [
            "string"
          ],
          description: "Current selected value"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Label text displayed next to the radio button"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text displayed below the radio button"
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message displayed below the radio button"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the radio button",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    switch: {
      type: "switch",
      category: "component",
      sourceFile: "components/core/atoms/Switch.tsx",
      tier: "atoms",
      family: "core",
      description: "Toggle switch input",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "on/off settings",
        "toggles",
        "preferences"
      ],
      typicalSize: "tiny",
      propsSchema: {
        checked: {
          types: [
            "boolean"
          ],
          description: "checked prop"
        },
        defaultChecked: {
          types: [
            "boolean"
          ],
          description: "defaultChecked prop",
          default: false
        },
        onChange: {
          types: [
            "function"
          ],
          description: "onChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "checked",
              type: "boolean"
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "disabled prop",
          default: false
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        id: {
          types: [
            "string"
          ],
          description: "id prop"
        },
        name: {
          types: [
            "string"
          ],
          description: "name prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    label: {
      type: "label",
      category: "component",
      sourceFile: "components/core/atoms/Label.tsx",
      tier: "atoms",
      family: "core",
      description: "Form field label",
      suggestedFor: [
        "form fields",
        "input labels"
      ],
      typicalSize: "tiny",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        text: {
          types: [
            "string"
          ],
          description: "Label text content"
        },
        htmlFor: {
          types: [
            "string"
          ],
          description: "Associated input element ID"
        },
        required: {
          types: [
            "boolean"
          ],
          description: "Show required indicator (decorative; the field itself carries aria-required)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    alert: {
      type: "alert",
      category: "component",
      sourceFile: "components/core/molecules/Alert.tsx",
      tier: "molecules",
      family: "core",
      description: "Alert message box",
      suggestedFor: [
        "warnings",
        "errors",
        "info messages",
        "success messages"
      ],
      typicalSize: "small",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Alert content (children or message)"
        },
        message: {
          types: [
            "string"
          ],
          description: "Alert message (alias for children)"
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "info",
            "success",
            "warning",
            "error"
          ],
          default: "info"
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        dismissible: {
          types: [
            "boolean"
          ],
          description: "dismissible prop",
          default: false
        },
        onDismiss: {
          types: [
            "function"
          ],
          description: "onDismiss prop",
          kind: "callback",
          callbackArgs: []
        },
        onClose: {
          types: [
            "function"
          ],
          description: "onClose prop",
          kind: "callback",
          callbackArgs: []
        },
        actions: {
          types: [
            "node"
          ],
          description: "actions prop"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon shown instead of the variant's default (Lucide name or component)"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        dismissEvent: {
          types: [
            "string"
          ],
          description: "Declarative dismiss event \u2014 emits UI:{dismissEvent} via eventBus when alert is dismissed",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    tooltip: {
      type: "tooltip",
      category: "component",
      sourceFile: "components/core/molecules/Tooltip.tsx",
      tier: "molecules",
      family: "core",
      description: "Hover tooltip",
      suggestedFor: [
        "hints",
        "extra info",
        "help text"
      ],
      typicalSize: "tiny",
      propsSchema: {
        content: {
          types: [
            "node"
          ],
          description: "Tooltip content",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Tooltip trigger element (ReactElement or ReactNode that will be wrapped in span)",
          required: true
        },
        position: {
          types: [
            "string"
          ],
          description: "Tooltip position",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "top"
        },
        delay: {
          types: [
            "number"
          ],
          description: "Show delay in milliseconds",
          default: 200
        },
        hideDelay: {
          types: [
            "number"
          ],
          description: "Hide delay in milliseconds",
          default: 0
        },
        showArrow: {
          types: [
            "boolean"
          ],
          description: "Show arrow",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    popover: {
      type: "popover",
      category: "component",
      sourceFile: "components/core/molecules/Popover.tsx",
      tier: "molecules",
      family: "core",
      description: "Click-triggered popover",
      suggestedFor: [
        "menus",
        "additional info",
        "actions"
      ],
      typicalSize: "small",
      propsSchema: {
        content: {
          types: [
            "node"
          ],
          description: "Popover content",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Popover trigger element (ReactElement or ReactNode that will be wrapped in span)",
          required: true
        },
        position: {
          types: [
            "string"
          ],
          description: "Popover position",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "bottom"
        },
        trigger: {
          types: [
            "string"
          ],
          description: "Trigger type",
          enumValues: [
            "click",
            "hover"
          ],
          default: "click"
        },
        showArrow: {
          types: [
            "boolean"
          ],
          description: "Show arrow",
          default: true
        },
        open: {
          types: [
            "boolean"
          ],
          description: "Controlled open state. When set, the host owns visibility and the popover reports intent through onOpenChange instead of toggling itself."
        },
        onOpenChange: {
          types: [
            "function"
          ],
          description: "Fired when the popover wants to change visibility (trigger click, outside click)",
          kind: "callback",
          callbackArgs: [
            {
              name: "open",
              type: "boolean"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    menu: {
      type: "menu",
      category: "component",
      sourceFile: "components/core/molecules/Menu.tsx",
      tier: "molecules",
      family: "core",
      description: "Dropdown menu",
      suggestedFor: [
        "actions menu",
        "context menu",
        "dropdown"
      ],
      typicalSize: "small",
      propsSchema: {
        trigger: {
          types: [
            "node"
          ],
          description: "Menu trigger element",
          required: true
        },
        items: {
          types: [
            "array"
          ],
          description: "Menu items",
          required: true,
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "item",
                  "divider"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              href: {
                types: [
                  "string"
                ]
              },
              url: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "danger"
                ]
              },
              subMenu: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "MenuItem"
                }
              }
            },
            required: [
              "label"
            ]
          }
        },
        position: {
          types: [
            "string"
          ],
          description: "Menu position",
          enumValues: [
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
            "top-start",
            "top-end",
            "bottom-start",
            "bottom-end"
          ],
          default: "bottom-left"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        header: {
          types: [
            "node"
          ],
          description: "Optional slot rendered above the items."
        },
        footer: {
          types: [
            "node"
          ],
          description: "Optional slot rendered below the items."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    accordion: {
      type: "accordion",
      category: "component",
      sourceFile: "components/core/molecules/Accordion.tsx",
      tier: "molecules",
      family: "core",
      description: "Collapsible accordion sections",
      suggestedFor: [
        "FAQs",
        "collapsible content",
        "settings"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Accordion items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "node"
                ]
              },
              title: {
                types: [
                  "node"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              defaultOpen: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "content"
            ]
          }
        },
        multiple: {
          types: [
            "boolean"
          ],
          description: "Allow multiple items open at once",
          default: false
        },
        defaultOpenItems: {
          types: [
            "array"
          ],
          description: "Default open items (IDs)",
          items: {
            types: [
              "string"
            ]
          }
        },
        defaultOpen: {
          types: [
            "array"
          ],
          description: "Default open items by index (pattern compatibility)",
          items: {
            types: [
              "number"
            ]
          }
        },
        openItems: {
          types: [
            "array"
          ],
          description: "Controlled open items (IDs)",
          items: {
            types: [
              "string"
            ]
          }
        },
        onItemToggle: {
          types: [
            "function"
          ],
          description: "Callback when item opens/closes",
          kind: "callback",
          callbackArgs: [
            {
              name: "itemId",
              type: "string"
            },
            {
              name: "isOpen",
              type: "boolean"
            }
          ]
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        toggleEvent: {
          types: [
            "string"
          ],
          description: "Declarative toggle event \u2014 emits UI:{toggleEvent} with { itemId, isOpen }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "itemId",
              type: "string",
              required: true
            },
            {
              name: "isOpen",
              type: "boolean",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    container: {
      type: "container",
      category: "component",
      sourceFile: "components/core/molecules/Container.tsx",
      tier: "molecules",
      family: "core",
      description: "Centered max-width container",
      suggestedFor: [
        "page content",
        "centered layouts"
      ],
      typicalSize: "medium",
      propsSchema: {
        size: {
          types: [
            "string"
          ],
          description: "Maximum width",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ]
        },
        maxWidth: {
          types: [
            "string"
          ],
          description: "Alias for size (pattern compatibility)",
          enumValues: [
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ]
        },
        padding: {
          types: [
            "string"
          ],
          description: "Horizontal padding",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        center: {
          types: [
            "boolean"
          ],
          description: "Center horizontally",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "simple-grid": {
      type: "simple-grid",
      category: "component",
      sourceFile: "components/core/molecules/SimpleGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "Simple responsive grid with auto columns",
      suggestedFor: [
        "card grids",
        "gallery",
        "responsive layouts"
      ],
      typicalSize: "medium",
      propsSchema: {
        minChildWidth: {
          types: [
            "number",
            "string"
          ],
          description: 'Minimum width of each child (e.g., 200, "200px", "15rem")',
          default: 250
        },
        maxCols: {
          types: [
            "number"
          ],
          description: "Maximum number of columns",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        cols: {
          types: [
            "number"
          ],
          description: "Exact number of columns (disables auto-fit)",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between items",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id forwarded to the grid element."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "repeatable-form-section": {
      type: "repeatable-form-section",
      category: "form",
      sourceFile: "components/core/molecules/RepeatableFormSection.tsx",
      tier: "molecules",
      family: "core",
      description: "RepeatableFormSection \u2014 a form section that repeats a fixed group of fields, letting the user add or remove entries.",
      capabilities: "dynamic line items, repeatable field group, add/remove entry rows, multi-entry form block",
      suggestedFor: [
        "repeating fields",
        "dynamic forms",
        "multi-entry sections"
      ],
      typicalSize: "medium",
      propsSchema: {
        sectionType: {
          types: [
            "string"
          ],
          description: "Section type identifier",
          required: true
        },
        title: {
          types: [
            "string"
          ],
          description: "Section title",
          required: true
        },
        items: {
          types: [
            "array"
          ],
          description: "Items in the section",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              addedInState: {
                types: [
                  "string"
                ]
              },
              addedAt: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        renderItem: {
          types: [
            "function"
          ],
          description: "Render function for each item. In .lolo: renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.",
          required: true,
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  addedInState: {
                    types: [
                      "string"
                    ]
                  },
                  addedAt: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        minItems: {
          types: [
            "number"
          ],
          description: "Minimum items required",
          default: 0
        },
        maxItems: {
          types: [
            "number"
          ],
          description: "Maximum items allowed"
        },
        allowReorder: {
          types: [
            "boolean"
          ],
          description: "Allow reordering",
          default: false
        },
        addLabel: {
          types: [
            "string"
          ],
          description: "Add button label"
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: "Empty state message"
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "Read-only mode",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        onAdd: {
          types: [
            "function"
          ],
          description: "Add handler",
          kind: "callback",
          callbackArgs: []
        },
        onRemove: {
          types: [
            "function"
          ],
          description: "Remove handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "itemId",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ]
        },
        onReorder: {
          types: [
            "function"
          ],
          description: "Reorder handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "fromIndex",
              type: "number"
            },
            {
              name: "toIndex",
              type: "number"
            }
          ]
        },
        trackAddedInState: {
          types: [
            "boolean"
          ],
          description: "Inspection-specific enhancements",
          default: false
        },
        currentState: {
          types: [
            "string"
          ],
          description: "Current inspection state (used when trackAddedInState is true)"
        },
        showAuditInfo: {
          types: [
            "boolean"
          ],
          description: "Show audit metadata (addedInState, addedAt)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "conditional-wrapper": {
      type: "conditional-wrapper",
      category: "component",
      sourceFile: "components/core/atoms/ConditionalWrapper.tsx",
      tier: "atoms",
      family: "core",
      description: "ConditionalWrapper Atom Component  A wrapper component that conditionally renders its children based on S-expression evaluation. Used for dynamic field visibility in inspection forms.",
      suggestedFor: [
        "conditional",
        "wrapper",
        "conditional wrapper"
      ],
      typicalSize: "small",
      propsSchema: {
        condition: {
          types: [
            "sexpr"
          ],
          description: "The S-expression condition to evaluate"
        },
        context: {
          types: [
            "object"
          ],
          description: "Context for evaluating the condition",
          required: true,
          properties: {
            formValues: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            globalVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            localVariables: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            },
            entity: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                controlValue: true
              }
            }
          },
          propertyRequired: [
            "formValues",
            "globalVariables"
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Children to render when condition is true (or when no condition is provided)",
          required: true
        },
        fallback: {
          types: [
            "node"
          ],
          description: "Optional fallback to render when condition is false",
          default: null
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Whether to animate the transition (uses CSS transitions)",
          default: false
        }
      }
    },
    "law-reference-tooltip": {
      type: "law-reference-tooltip",
      category: "component",
      sourceFile: "components/core/atoms/LawReferenceTooltip.tsx",
      tier: "atoms",
      family: "core",
      description: "LawReferenceTooltip Atom Component  A specialized tooltip for displaying law references in inspection forms. Shows law name, article number, and relevant clause text.",
      suggestedFor: [
        "law",
        "reference",
        "tooltip",
        "law reference tooltip"
      ],
      typicalSize: "small",
      propsSchema: {
        reference: {
          types: [
            "object"
          ],
          description: "The law reference to display",
          required: true,
          properties: {
            law: {
              types: [
                "string"
              ]
            },
            lawName: {
              types: [
                "string"
              ]
            },
            article: {
              types: [
                "string"
              ]
            },
            clause: {
              types: [
                "string"
              ]
            },
            link: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "law",
            "article"
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Children element that triggers the tooltip",
          required: true
        },
        position: {
          types: [
            "string"
          ],
          description: "Tooltip position",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "top"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    overlay: {
      type: "overlay",
      category: "container",
      sourceFile: "components/core/atoms/Overlay.tsx",
      tier: "atoms",
      family: "core",
      description: "Overlay Atom Component  A fixed backdrop for modals and drawers.",
      suggestedFor: [
        "overlay"
      ],
      typicalSize: "small",
      propsSchema: {
        isVisible: {
          types: [
            "boolean"
          ],
          description: "isVisible prop",
          default: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "onClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        blur: {
          types: [
            "boolean"
          ],
          description: "blur prop",
          default: false
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    stack: {
      type: "stack",
      category: "component",
      sourceFile: "components/core/atoms/Stack.tsx",
      tier: "atoms",
      family: "core",
      description: "Stack Component  A layout primitive for arranging children in a vertical or horizontal stack with consistent spacing. Includes convenience exports VStack and HStack for common use cases.",
      suggestedFor: [
        "stack"
      ],
      typicalSize: "small",
      propsSchema: {
        direction: {
          types: [
            "string"
          ],
          description: "Stack direction",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "vertical"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between children",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items on the cross axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify items on the main axis",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        wrap: {
          types: [
            "boolean"
          ],
          description: "Allow items to wrap",
          default: false
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order of children",
          default: false
        },
        flex: {
          types: [
            "boolean"
          ],
          description: "Fill available space (flex: 1)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        onKeyDown: {
          types: [
            "function"
          ],
          description: "Keyboard handler",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        responsive: {
          types: [
            "boolean"
          ],
          description: "When true, horizontal stacks flip to vertical below the md breakpoint (768px)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "text-highlight": {
      type: "text-highlight",
      category: "component",
      sourceFile: "components/core/atoms/TextHighlight.tsx",
      tier: "atoms",
      family: "core",
      description: "TextHighlight Atom Component  A styled span component for highlighting text with annotations (questions or notes). Uses different colors for different annotation types: - Questions: Blue highlight - Notes: Yellow highlight",
      suggestedFor: [
        "text",
        "highlight",
        "text highlight"
      ],
      typicalSize: "small",
      propsSchema: {
        highlightType: {
          types: [
            "string"
          ],
          description: "Type of highlight (determines color)",
          required: true,
          enumValues: [
            "question",
            "note"
          ]
        },
        isActive: {
          types: [
            "boolean"
          ],
          description: "Whether the highlight is currently active/focused",
          default: false
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Callback when highlight is clicked",
          kind: "callback",
          callbackArgs: []
        },
        onMouseEnter: {
          types: [
            "function"
          ],
          description: "Callback when highlight is hovered",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        onMouseLeave: {
          types: [
            "function"
          ],
          description: "Callback when hover ends",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        annotationId: {
          types: [
            "string"
          ],
          description: "Unique ID for the annotation"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Highlighted text content",
          required: true
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        hoverEvent: {
          types: [
            "string"
          ],
          description: "Declarative hover event \u2014 emits UI:{hoverEvent} with { hovered, annotationId }",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "hovered",
              type: "boolean",
              required: true
            },
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "theme-toggle": {
      type: "theme-toggle",
      category: "component",
      sourceFile: "components/core/atoms/ThemeToggle.tsx",
      tier: "atoms",
      family: "core",
      description: "ThemeToggle Atom Component  A button that toggles between light and dark themes. Uses Sun and Moon icons to indicate current/target theme.",
      suggestedFor: [
        "theme",
        "toggle",
        "theme toggle"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: "Show label text",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    typography: {
      type: "typography",
      category: "component",
      sourceFile: "components/core/atoms/Typography.tsx",
      tier: "atoms",
      family: "core",
      description: "Typography Atom Component  Text elements following the KFlow design system with theme-aware styling.",
      suggestedFor: [
        "typography"
      ],
      typicalSize: "small",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Typography variant",
          enumValues: [
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "heading",
            "subheading",
            "body1",
            "body2",
            "body",
            "caption",
            "overline",
            "small",
            "large",
            "label"
          ]
        },
        level: {
          types: [
            "number"
          ],
          description: "Heading level (1-6) - alternative to variant for headings",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        color: {
          types: [
            "string"
          ],
          description: "Text color",
          enumValues: [
            "primary",
            "secondary",
            "muted",
            "error",
            "success",
            "warning",
            "inherit"
          ],
          default: "primary"
        },
        align: {
          types: [
            "string"
          ],
          description: "Text alignment",
          enumValues: [
            "left",
            "center",
            "right"
          ]
        },
        weight: {
          types: [
            "string"
          ],
          description: "Font weight override \u2014 `none` = no override, the variant's baked weight applies",
          enumValues: [
            "none",
            "light",
            "normal",
            "medium",
            "semibold",
            "bold"
          ]
        },
        size: {
          types: [
            "string"
          ],
          description: "Font size override",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "3xl"
          ]
        },
        truncate: {
          types: [
            "boolean"
          ],
          description: "Truncate with ellipsis (single line)",
          default: false
        },
        overflow: {
          types: [
            "string"
          ],
          description: "Overflow handling mode",
          enumValues: [
            "visible",
            "hidden",
            "wrap",
            "clamp-2",
            "clamp-3"
          ]
        },
        as: {
          types: [
            "string"
          ],
          description: "Custom HTML element",
          enumValues: [
            "a",
            "abbr",
            "address",
            "area",
            "article",
            "aside",
            "audio",
            "b",
            "base",
            "bdi",
            "bdo",
            "big",
            "blockquote",
            "body",
            "br",
            "button",
            "canvas",
            "caption",
            "center",
            "cite",
            "code",
            "col",
            "colgroup",
            "data",
            "datalist",
            "dd",
            "del",
            "details",
            "dfn",
            "dialog",
            "div",
            "dl",
            "dt",
            "em",
            "embed",
            "fieldset",
            "figcaption",
            "figure",
            "footer",
            "form",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "head",
            "header",
            "hgroup",
            "hr",
            "html",
            "i",
            "iframe",
            "img",
            "input",
            "ins",
            "kbd",
            "keygen",
            "label",
            "legend",
            "li",
            "link",
            "main",
            "map",
            "mark",
            "menu",
            "menuitem",
            "meta",
            "meter",
            "nav",
            "noindex",
            "noscript",
            "object",
            "ol",
            "optgroup",
            "option",
            "output",
            "p",
            "param",
            "picture",
            "pre",
            "progress",
            "q",
            "rp",
            "rt",
            "ruby",
            "s",
            "samp",
            "search",
            "slot",
            "script",
            "section",
            "select",
            "small",
            "source",
            "span",
            "strong",
            "style",
            "sub",
            "summary",
            "sup",
            "table",
            "template",
            "tbody",
            "td",
            "textarea",
            "tfoot",
            "th",
            "thead",
            "time",
            "title",
            "tr",
            "track",
            "u",
            "ul",
            "var",
            "video",
            "wbr",
            "webview",
            "svg",
            "animate",
            "animateMotion",
            "animateTransform",
            "circle",
            "clipPath",
            "defs",
            "desc",
            "ellipse",
            "feBlend",
            "feColorMatrix",
            "feComponentTransfer",
            "feComposite",
            "feConvolveMatrix",
            "feDiffuseLighting",
            "feDisplacementMap",
            "feDistantLight",
            "feDropShadow",
            "feFlood",
            "feFuncA",
            "feFuncB",
            "feFuncG",
            "feFuncR",
            "feGaussianBlur",
            "feImage",
            "feMerge",
            "feMergeNode",
            "feMorphology",
            "feOffset",
            "fePointLight",
            "feSpecularLighting",
            "feSpotLight",
            "feTile",
            "feTurbulence",
            "filter",
            "foreignObject",
            "g",
            "image",
            "line",
            "linearGradient",
            "marker",
            "mask",
            "metadata",
            "mpath",
            "path",
            "pattern",
            "polygon",
            "polyline",
            "radialGradient",
            "rect",
            "set",
            "stop",
            "switch",
            "symbol",
            "text",
            "textPath",
            "tspan",
            "use",
            "view"
          ]
        },
        id: {
          types: [
            "string"
          ],
          description: "HTML id attribute"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline style",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        format: {
          types: [
            "string"
          ],
          description: 'Value formatting applied to string/number/Date content \u2014 `none` (the default) renders content as-is. Covers the raw-ISO-date class: a bound datetime renders human-readable via `format="date"|"time"|"datetime"`.',
          enumValues: [
            "none",
            "date",
            "time",
            "datetime",
            "number",
            "currency",
            "percent"
          ]
        },
        content: {
          types: [
            "node"
          ],
          description: "Text content (alternative to children)"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-actions": {
      type: "form-actions",
      category: "component",
      sourceFile: "components/core/molecules/ButtonGroup.tsx",
      tier: "molecules",
      family: "core",
      description: "ButtonGroup Molecule Component  A component for grouping buttons together with connected styling. Supports both children-based and form-actions pattern (primary/secondary) usage. Uses Button atoms.",
      suggestedFor: [
        "button",
        "group",
        "button group"
      ],
      typicalSize: "medium",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Button group content (Button components) - use this OR primary/secondary"
        },
        primary: {
          types: [
            "object"
          ],
          description: "Primary action button config (for form-actions pattern) Accepts Readonly for compatibility with generated const objects",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            actionType: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            },
            navigatesTo: {
              types: [
                "string"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        secondary: {
          types: [
            "array"
          ],
          description: "Secondary action buttons config (for form-actions pattern) Accepts readonly array for compatibility with generated const arrays",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              actionType: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          enumValues: [
            "default",
            "segmented",
            "toggle"
          ],
          default: "default"
        },
        orientation: {
          types: [
            "string"
          ],
          description: "Orientation",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity type for filter-group pattern (schema metadata)"
        },
        filters: {
          types: [
            "array"
          ],
          description: "Filter definitions for filter-group pattern",
          items: {
            types: [
              "object"
            ],
            properties: {
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              filterType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "select",
                  "toggle",
                  "checkbox",
                  "date",
                  "daterange",
                  "date-range",
                  "numberrange",
                  "number-range"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "field",
              "label"
            ]
          }
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment for the action-cluster (form-actions) pattern.",
          enumValues: [
            "right-aligned-buttons",
            "floating-bar",
            "inline-row",
            "dropdown-menu",
            "command-palette-trigger"
          ],
          default: "right-aligned-buttons"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    flex: {
      type: "flex",
      category: "component",
      sourceFile: "components/core/molecules/Flex.tsx",
      tier: "molecules",
      family: "core",
      description: "Flex Component  A flexbox wrapper with all common flex properties exposed as props. More explicit than Stack for when you need full flex control.",
      suggestedFor: [
        "flex"
      ],
      typicalSize: "medium",
      propsSchema: {
        direction: {
          types: [
            "string"
          ],
          description: "Flex direction",
          enumValues: [
            "row",
            "row-reverse",
            "col",
            "col-reverse"
          ],
          default: "row"
        },
        wrap: {
          types: [
            "string"
          ],
          description: "Flex wrap",
          enumValues: [
            "nowrap",
            "wrap",
            "wrap-reverse"
          ],
          default: "nowrap"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify content",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between items",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "none"
        },
        inline: {
          types: [
            "boolean"
          ],
          description: "Inline flex",
          default: false
        },
        grow: {
          types: [
            "boolean",
            "number"
          ],
          description: "Flex grow"
        },
        shrink: {
          types: [
            "boolean",
            "number"
          ],
          description: "Flex shrink"
        },
        basis: {
          types: [
            "string",
            "number"
          ],
          description: "Flex basis"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "floating-action-button": {
      type: "floating-action-button",
      category: "component",
      sourceFile: "components/core/molecules/FloatingActionButton.tsx",
      tier: "molecules",
      family: "core",
      description: "FloatingActionButton Molecule Component  A floating action button that can expand into multiple actions vertically. Uses Button atom.",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "floating",
        "action",
        "button",
        "floating action button"
      ],
      typicalSize: "medium",
      propsSchema: {
        action: {
          types: [
            "string"
          ],
          description: 'Declarative event name. When set, clicking the FAB emits `UI:{action}` via the event bus and (if also provided) calls `onClick`. Mirrors the Button atom\'s `action` prop so schemas can write `{ type: "floating-action-button", action: "INIT" }` uniformly.',
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the dispatched action event.",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        actions: {
          types: [
            "array"
          ],
          description: "Multiple actions. When provided, the button expands to show all of them.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "success",
                  "danger",
                  "warning"
                ]
              }
            },
            required: [
              "id",
              "label",
              "icon"
            ]
          }
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon: a canonical icon name string (resolved by Button) or a Lucide component."
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Optional direct click handler. Runs after the action emit when both are present.",
          kind: "callback",
          callbackArgs: []
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant.",
          enumValues: [
            "primary",
            "secondary",
            "success",
            "danger",
            "warning"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Optional label shown via `aria-label` (visually hidden in single-action mode; rendered as a tooltip beside expanded actions in multi-action mode)."
        },
        position: {
          types: [
            "string"
          ],
          description: "Button position",
          default: "bottom-right"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-field": {
      type: "form-field",
      category: "form",
      sourceFile: "components/core/molecules/FormField.tsx",
      tier: "molecules",
      family: "core",
      description: "FormField Molecule Component  A form field wrapper with label, hint, and error message support. **Atomic Design**: Composed using Label and Typography atoms.",
      suggestedFor: [
        "form",
        "field",
        "form field"
      ],
      typicalSize: "medium",
      propsSchema: {
        label: {
          types: [
            "string"
          ],
          description: "label prop",
          required: true
        },
        required: {
          types: [
            "boolean"
          ],
          description: "required prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "error prop"
        },
        hint: {
          types: [
            "string"
          ],
          description: "hint prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-section-header": {
      type: "form-section-header",
      category: "form",
      sourceFile: "components/core/molecules/FormSectionHeader.tsx",
      tier: "molecules",
      family: "core",
      description: "FormSectionHeader  Header component for collapsible form sections. Provides consistent styling and interaction for section headers.",
      suggestedFor: [
        "form",
        "section",
        "header",
        "form section header"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Section title",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Section subtitle"
        },
        isCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether section is collapsed",
          default: false
        },
        onToggle: {
          types: [
            "function"
          ],
          description: "Toggle collapse handler (makes header clickable)",
          kind: "callback",
          callbackArgs: []
        },
        badge: {
          types: [
            "string"
          ],
          description: 'Badge text (e.g., "3 fields", "Required", "Complete")'
        },
        badgeVariant: {
          types: [
            "string"
          ],
          description: "Badge variant",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "danger"
          ],
          default: "default"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon name or component to show before title"
        },
        hasErrors: {
          types: [
            "boolean"
          ],
          description: "Whether section has validation errors",
          default: false
        },
        isComplete: {
          types: [
            "boolean"
          ],
          description: "Whether section is complete",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "input-group": {
      type: "input-group",
      category: "form",
      sourceFile: "components/core/molecules/InputGroup.tsx",
      tier: "molecules",
      family: "core",
      description: "InputGroup Molecule Component  A component for grouping input with addons (icons, buttons, text). Uses Input, Button, Icon, and Typography atoms.",
      suggestedFor: [
        "input",
        "group",
        "input group"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        value: {
          types: [
            "string",
            "number"
          ],
          description: "Current value"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether input is disabled"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for trait dispatch.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true,
              typeWhen: [
                {
                  knob: "inputType",
                  equals: "number",
                  type: "number"
                }
              ]
            }
          ]
        },
        inputType: {
          types: [
            "string"
          ],
          description: "Input type \u2014 selects the field's data mode. Use 'password' for masked credentials / secret / passphrase entry (there is no separate password pattern); 'email', 'tel', 'url', 'number', 'search', 'date', and 'time' for their respective values; and 'select' / 'textarea' for choice and multi-line entry in addition to the standard single-line types.",
          enumValues: [
            "text",
            "email",
            "password",
            "number",
            "tel",
            "url",
            "search",
            "date",
            "datetime-local",
            "time",
            "checkbox",
            "select",
            "textarea"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "helperText prop"
        },
        error: {
          types: [
            "string"
          ],
          description: "error prop"
        },
        leftIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "leftIcon prop"
        },
        rightIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "rightIcon prop"
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Show clear button when input has value"
        },
        onClear: {
          types: [
            "function",
            "string"
          ],
          description: "Callback or declarative event key when clear button is clicked"
        },
        options: {
          types: [
            "array"
          ],
          description: "Options for select type",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        rows: {
          types: [
            "number"
          ],
          description: "Rows for textarea type"
        },
        onChange: {
          types: [
            "function",
            "string"
          ],
          description: "onChange handler or declarative event key for trait dispatch",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "_payload",
              type: "object",
              required: false
            }
          ]
        },
        leftAddon: {
          types: [
            "node",
            "icon"
          ],
          description: "Left addon (icon, button, or text)"
        },
        rightAddon: {
          types: [
            "node",
            "icon"
          ],
          description: "Right addon (icon, button, or text)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "relation-select": {
      type: "relation-select",
      category: "form",
      sourceFile: "components/core/molecules/RelationSelect.tsx",
      tier: "molecules",
      family: "core",
      description: "RelationSelect Molecule Component  A searchable select component for relation fields. Allows users to search and select from related entities.  Composed from: Box, HStack, VStack, Input, Button, Spinner, Typography atoms",
      suggestedFor: [
        "relation",
        "select",
        "relation select"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "string"
          ],
          description: "Current value (ID)"
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Callback when value changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        options: {
          types: [
            "array"
          ],
          description: "Available options - accepts readonly for compatibility with generated const arrays",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          },
          default: []
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text"
        },
        required: {
          types: [
            "boolean"
          ],
          description: "Whether the field is required",
          default: false
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the field is disabled",
          default: false
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Whether data is loading",
          default: false
        },
        error: {
          types: [
            "string"
          ],
          description: "Error message"
        },
        clearable: {
          types: [
            "boolean"
          ],
          description: "Allow clearing the selection",
          default: true
        },
        name: {
          types: [
            "string"
          ],
          description: "Name attribute for forms"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        searchPlaceholder: {
          types: [
            "string"
          ],
          description: "Search placeholder"
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: "Empty state message"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "side-panel": {
      type: "side-panel",
      category: "component",
      sourceFile: "components/core/molecules/SidePanel.tsx",
      tier: "molecules",
      family: "core",
      description: "SidePanel Molecule Component  A side panel that slides in from the right (or left) with header and content. Uses Button, Typography atoms.",
      suggestedFor: [
        "side",
        "panel",
        "side panel"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Panel title",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Panel content",
          required: true
        },
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Is panel open",
          required: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "On close handler",
          required: true,
          kind: "callback",
          callbackArgs: []
        },
        width: {
          types: [
            "string"
          ],
          description: "Panel width as Tailwind class string. Default fills the viewport on mobile and snaps to `w-96` (384 px) at `sm:` and above so phones don't lose content to a fixed 384 px column. Consumers passing a custom value should include a `w-full` mobile fallback in the same string (e.g. `\"w-full sm:w-[480px]\"`) \u2014 Tailwind's JIT can't see dynamically-concatenated class names.",
          default: "w-full sm:w-96"
        },
        position: {
          types: [
            "string"
          ],
          description: "Panel position",
          enumValues: [
            "left",
            "right"
          ],
          default: "right"
        },
        showOverlay: {
          types: [
            "boolean"
          ],
          description: "Show overlay on mobile",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Declarative close event \u2014 emits UI:{closeEvent} via eventBus when panel should close",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "violation-alert": {
      type: "violation-alert",
      category: "state",
      sourceFile: "components/core/molecules/ViolationAlert.tsx",
      tier: "molecules",
      family: "core",
      description: "ViolationAlert  Displays inspection violations with law references and action types. Used in inspection forms to show detected compliance violations.  Action Types: - measure: Corrective measure required (warning) - admin: Administrative action (error) - penalty: Penalty proceedings (error, severe)",
      suggestedFor: [
        "violation",
        "alert",
        "violation alert"
      ],
      typicalSize: "medium",
      propsSchema: {
        violation: {
          types: [
            "object"
          ],
          description: "Violation data",
          required: true,
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            law: {
              types: [
                "string"
              ]
            },
            article: {
              types: [
                "string"
              ]
            },
            message: {
              types: [
                "string"
              ]
            },
            actionType: {
              types: [
                "string"
              ],
              enumValues: [
                "measure",
                "admin",
                "penalty"
              ]
            },
            adminAction: {
              types: [
                "string"
              ]
            },
            penaltyAction: {
              types: [
                "string"
              ]
            },
            fieldId: {
              types: [
                "string"
              ]
            },
            tabId: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "id",
            "law",
            "article",
            "message",
            "actionType"
          ]
        },
        severity: {
          types: [
            "string"
          ],
          description: "Visual severity (derived from actionType if not specified)",
          enumValues: [
            "warning",
            "error"
          ]
        },
        dismissible: {
          types: [
            "boolean"
          ],
          description: "Dismissible alert",
          default: false
        },
        onDismiss: {
          types: [
            "function"
          ],
          description: "Dismiss handler",
          kind: "callback",
          callbackArgs: []
        },
        onNavigateToField: {
          types: [
            "function"
          ],
          description: "Navigate to the field that caused violation",
          kind: "callback",
          callbackArgs: [
            {
              name: "fieldId",
              type: "string"
            }
          ]
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact display mode",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        message: {
          types: [
            "string"
          ],
          description: "Fallback message from playground render-ui"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "drawer-slot": {
      type: "drawer-slot",
      category: "layout",
      sourceFile: "components/core/organisms/DrawerSlot.tsx",
      tier: "organisms",
      family: "core",
      description: "DrawerSlot Component  Wraps drawer slot content in a proper Drawer component. Used by trait-driven pages to display drawer UI from render_ui effects.  Handles: - Auto-open when content is present - Dispatches CLOSE/CANCEL events when closed - Extracts title from Form or DetailPanel components - Configurable position and size",
      suggestedFor: [
        "drawer",
        "slot",
        "drawer slot"
      ],
      typicalSize: "large",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to display in the drawer"
        },
        title: {
          types: [
            "string"
          ],
          description: "Override drawer title (extracted from children if not provided)"
        },
        position: {
          types: [
            "string"
          ],
          description: "Drawer position",
          enumValues: [
            "left",
            "right"
          ],
          default: "right"
        },
        size: {
          types: [
            "string"
          ],
          description: "Drawer size",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for qualified event emission"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    header: {
      type: "header",
      category: "header",
      sourceFile: "components/core/molecules/Header.tsx",
      tier: "molecules",
      family: "core",
      description: "Header Organism Component  A header component for mobile/responsive layouts with menu toggle, brand, and user avatar. Styled to match the main Layout component's mobile header.",
      suggestedFor: [
        "header"
      ],
      typicalSize: "large",
      propsSchema: {
        logo: {
          types: [
            "node"
          ],
          description: "Logo/Brand content"
        },
        logoSrc: {
          types: [
            "asset"
          ],
          description: "Logo image source"
        },
        brandName: {
          types: [
            "string"
          ],
          description: "Brand/App name",
          default: "KFlow"
        },
        navigationItems: {
          types: [
            "array"
          ],
          description: "Navigation items (for desktop header variant)",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              active: {
                types: [
                  "boolean"
                ]
              },
              children: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "HeaderNavigationItem"
                }
              }
            },
            required: [
              "label"
            ]
          }
        },
        showMenuToggle: {
          types: [
            "boolean"
          ],
          description: "Show menu toggle button",
          default: true
        },
        isMenuOpen: {
          types: [
            "boolean"
          ],
          description: "Is menu open (for toggle icon)",
          default: false
        },
        onMenuToggle: {
          types: [
            "function"
          ],
          description: "Menu toggle callback",
          kind: "callback",
          callbackArgs: []
        },
        showSearch: {
          types: [
            "boolean"
          ],
          description: "Show search input",
          default: false
        },
        searchPlaceholder: {
          types: [
            "string"
          ],
          description: "Search placeholder"
        },
        onSearch: {
          types: [
            "function"
          ],
          description: "Search callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        userAvatar: {
          types: [
            "object"
          ],
          description: "User avatar configuration",
          properties: {
            src: {
              types: [
                "asset"
              ]
            },
            alt: {
              types: [
                "string"
              ]
            },
            initials: {
              types: [
                "string"
              ]
            }
          }
        },
        userName: {
          types: [
            "string"
          ],
          description: "User name (display name or email)"
        },
        onUserClick: {
          types: [
            "function"
          ],
          description: "Callback when user avatar is clicked",
          kind: "callback",
          callbackArgs: []
        },
        actions: {
          types: [
            "node"
          ],
          description: "Action buttons (right side)"
        },
        sticky: {
          types: [
            "boolean"
          ],
          description: "Sticky header",
          default: true
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant - mobile shows menu toggle, desktop shows full nav",
          enumValues: [
            "mobile",
            "desktop"
          ],
          default: "mobile"
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 orthogonal to the structural variant (which currently flips mobile vs desktop).",
          enumValues: [
            "hero",
            "compact-bar",
            "breadcrumb",
            "contextual",
            "editorial-banner"
          ],
          default: "compact-bar"
        },
        onLogoClick: {
          types: [
            "function"
          ],
          description: "Callback when logo/brand is clicked",
          kind: "callback",
          callbackArgs: []
        },
        brandHref: {
          types: [
            "string"
          ],
          description: "Where the brand (logo + name) links to, usually the home page; empty leaves it unlinked"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator (closed circuit)"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (closed circuit)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "modal-slot": {
      type: "modal-slot",
      category: "layout",
      sourceFile: "components/core/organisms/ModalSlot.tsx",
      tier: "organisms",
      family: "core",
      description: "ModalSlot Component  Wraps modal slot content in a proper Modal component. Used by trait-driven pages to display modal UI from render_ui effects.  Handles: - Auto-open when content is present - Dispatches CLOSE/CANCEL events when closed - Extracts title from Form components",
      suggestedFor: [
        "modal",
        "slot",
        "modal slot"
      ],
      typicalSize: "large",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to display in the modal"
        },
        title: {
          types: [
            "string"
          ],
          description: "Override modal title (extracted from children if not provided)"
        },
        size: {
          types: [
            "string"
          ],
          description: "Modal size",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl",
            "full"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for qualified event emission"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    navigation: {
      type: "navigation",
      category: "navigation",
      sourceFile: "components/core/molecules/Navigation.tsx",
      tier: "molecules",
      family: "core",
      description: "Navigation Organism Component  A navigation component with items, active indicators, icons, and badges. Uses Menu, ButtonGroup molecules and Button, Icon, Badge, Typography, Divider atoms.",
      suggestedFor: [
        "navigation"
      ],
      typicalSize: "large",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Navigation items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              isActive: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              subMenu: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "NavigationItem"
                }
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        orientation: {
          types: [
            "string"
          ],
          description: "Navigation orientation",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "orbital-visualization": {
      type: "orbital-visualization",
      category: "display",
      sourceFile: "components/core/molecules/OrbitalVisualization.tsx",
      tier: "molecules",
      family: "core",
      description: "OrbitalVisualization Component  Visualizes KFlow schemas as atomic orbitals based on complexity. Uses CSS 3D transforms for lightweight rendering without Three.js.  Orbital Types (based on complexity score): - 1s (1-3): Simple sphere - Red - 2s (4-8): Larger sphere - Orange - 2p (9-15): Dumbbell shape - Yellow - 3s (16-25): Sphere with node - Green - 3p (26-40): Complex dumbbell - Blue - 3d (41-60): Cloverleaf - Indigo - 4f (61+): Multi-lobe - Violet",
      suggestedFor: [
        "orbital",
        "visualization",
        "orbital visualization"
      ],
      typicalSize: "large",
      propsSchema: {
        schema: {
          types: [
            "object"
          ],
          description: "Complexity-scoring summary of a KFlow schema (counts only \u2014 see {@link OrbitalVisualizationSchemaSummary}).",
          properties: {
            dataEntities: {
              types: [
                "number"
              ]
            },
            ui: {
              types: [
                "object"
              ],
              properties: {
                pages: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      sections: {
                        types: [
                          "number"
                        ]
                      }
                    }
                  }
                }
              }
            },
            traits: {
              types: [
                "number"
              ]
            }
          }
        },
        complexity: {
          types: [
            "number"
          ],
          description: "Direct complexity override (1-100+)"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the visualization",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: "Show complexity label",
          default: true
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "Animation enabled",
          default: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes",
          default: ""
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    section: {
      type: "section",
      category: "display",
      sourceFile: "components/core/molecules/Section.tsx",
      tier: "molecules",
      family: "core",
      description: "Section Component  A semantic section wrapper with optional title, description, and action. Perfect for grouping related content with consistent spacing.",
      suggestedFor: [
        "section"
      ],
      typicalSize: "large",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Section title"
        },
        description: {
          types: [
            "string"
          ],
          description: "Section subtitle/description"
        },
        action: {
          types: [
            "node"
          ],
          description: "Action element (e.g., button, link)"
        },
        padding: {
          types: [
            "string"
          ],
          description: "Padding amount",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          enumValues: [
            "default",
            "card",
            "bordered",
            "filled"
          ],
          default: "default"
        },
        divider: {
          types: [
            "boolean"
          ],
          description: "Show divider below header",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements",
          required: true
        },
        headerClassName: {
          types: [
            "string"
          ],
          description: "Header custom class name"
        },
        contentClassName: {
          types: [
            "string"
          ],
          description: "Content custom class name"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    sidebar: {
      type: "sidebar",
      category: "navigation",
      sourceFile: "components/core/molecules/Sidebar.tsx",
      tier: "molecules",
      family: "core",
      description: "Sidebar Organism Component  A sidebar component with logo, navigation items, user section, and collapse/expand. Styled to match the main Layout component with theme-aware CSS variables.",
      suggestedFor: [
        "sidebar"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        logo: {
          types: [
            "node"
          ],
          description: "Logo/Brand content - can be a ReactNode or logo config"
        },
        logoSrc: {
          types: [
            "asset"
          ],
          description: "Logo image source"
        },
        brandName: {
          types: [
            "string"
          ],
          description: "Brand/App name",
          default: "KFlow"
        },
        items: {
          types: [
            "array"
          ],
          description: "Navigation items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              onClick: {
                types: [
                  "function"
                ]
              },
              active: {
                types: [
                  "boolean"
                ]
              },
              isActive: {
                types: [
                  "boolean"
                ]
              },
              subItems: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "SidebarItem"
                }
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        userSection: {
          types: [
            "node"
          ],
          description: "User section content"
        },
        footerContent: {
          types: [
            "node"
          ],
          description: "Footer content (e.g., theme toggle)"
        },
        collapsed: {
          types: [
            "boolean"
          ],
          description: "Collapsed state (controlled)"
        },
        defaultCollapsed: {
          types: [
            "boolean"
          ],
          description: "Default collapsed state",
          default: false
        },
        collapseChangeEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when collapse state changes, payload: { collapsed: boolean }",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "collapsed",
              type: "boolean",
              required: true
            }
          ]
        },
        hideCollapseButton: {
          types: [
            "boolean"
          ],
          description: "Hide the collapse/expand button",
          default: false
        },
        showCloseButton: {
          types: [
            "boolean"
          ],
          description: "Show a close button (for mobile)",
          default: false
        },
        closeEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when close button is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        logoClickEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when logo/brand is clicked",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    split: {
      type: "split",
      category: "layout",
      sourceFile: "components/core/molecules/Split.tsx",
      tier: "molecules",
      family: "core",
      description: "Split Component  A two-column layout with configurable ratios. Perfect for sidebar/content layouts or side-by-side comparisons.",
      suggestedFor: [
        "split"
      ],
      typicalSize: "large",
      propsSchema: {
        ratio: {
          types: [
            "string"
          ],
          description: "Size ratio between left and right panels",
          enumValues: [
            "1:1",
            "1:2",
            "2:1",
            "1:3",
            "3:1",
            "1:4",
            "4:1",
            "2:3",
            "3:2"
          ],
          default: "1:1"
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between panels",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order (right first)",
          default: false
        },
        stackOnMobile: {
          types: [
            "boolean"
          ],
          description: "Stack vertically on mobile",
          default: true
        },
        stackBreakpoint: {
          types: [
            "string"
          ],
          description: "Breakpoint to switch from stacked to side-by-side",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items vertically",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch"
          ],
          default: "stretch"
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        leftClassName: {
          types: [
            "string"
          ],
          description: "Left/first panel class name"
        },
        rightClassName: {
          types: [
            "string"
          ],
          description: "Right/second panel class name"
        },
        children: {
          types: [
            "array"
          ],
          description: "Exactly two children: [left, right]",
          required: true,
          tupleItems: [
            {
              types: [
                "node"
              ]
            },
            {
              types: [
                "node"
              ]
            }
          ],
          items: {
            types: [
              "node"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "toast-slot": {
      type: "toast-slot",
      category: "layout",
      sourceFile: "components/core/organisms/ToastSlot.tsx",
      tier: "organisms",
      family: "core",
      description: "ToastSlot Component  Wraps toast slot content in a proper Toast component with positioning. Used by trait-driven pages to display toast UI from render_ui effects.  Handles: - Auto-show when content is present - Dispatches DISMISS event when dismissed - Fixed positioning in corner of screen",
      suggestedFor: [
        "toast",
        "slot",
        "toast slot"
      ],
      typicalSize: "large",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to display in the toast (message or ReactNode)"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Toast variant",
          enumValues: [
            "success",
            "error",
            "info",
            "warning"
          ]
        },
        title: {
          types: [
            "string"
          ],
          description: "Toast title"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Auto-dismiss duration in ms (0 = no auto-dismiss)",
          default: 5e3
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for qualified event emission"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "auth-layout": {
      type: "auth-layout",
      category: "template",
      sourceFile: "components/core/templates/AuthLayout.tsx",
      tier: "templates",
      family: "core",
      description: "AuthLayout component",
      suggestedFor: [
        "auth",
        "layout",
        "auth layout"
      ],
      typicalSize: "full",
      propsSchema: {
        appName: {
          types: [
            "string"
          ],
          description: "App name"
        },
        logo: {
          types: [
            "node"
          ],
          description: "Logo component or URL"
        },
        backgroundImage: {
          types: [
            "asset"
          ],
          description: "Background image URL"
        },
        showBranding: {
          types: [
            "boolean"
          ],
          description: "Show branding panel on the side",
          default: true
        },
        brandingContent: {
          types: [
            "node"
          ],
          description: "Branding panel content"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "counter-template": {
      type: "counter-template",
      category: "template",
      sourceFile: "components/core/templates/CounterTemplate.tsx",
      tier: "templates",
      family: "core",
      description: "CounterTemplate  A presentational template for counter/incrementer features. Supports increment, decrement, and reset operations.",
      suggestedFor: [
        "counter",
        "template",
        "counter template"
      ],
      typicalSize: "full",
      propsSchema: {
        entity: {
          types: [
            "object"
          ],
          description: "Entity data, the sole source of runtime state",
          required: true,
          kind: "entity",
          requiredFields: [
            "id",
            "count"
          ],
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            count: {
              types: [
                "number"
              ]
            },
            decrementDisabled: {
              types: [
                "boolean"
              ]
            },
            incrementDisabled: {
              types: [
                "boolean"
              ]
            },
            decrementLabel: {
              types: [
                "string"
              ]
            },
            incrementLabel: {
              types: [
                "string"
              ]
            },
            rangeText: {
              types: [
                "string"
              ]
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "External styling override"
        },
        onIncrement: {
          types: [
            "function"
          ],
          description: "Called when increment is clicked",
          kind: "callback",
          callbackArgs: []
        },
        onDecrement: {
          types: [
            "function"
          ],
          description: "Called when decrement is clicked",
          kind: "callback",
          callbackArgs: []
        },
        onReset: {
          types: [
            "function"
          ],
          description: "Called when reset is clicked",
          kind: "callback",
          callbackArgs: []
        },
        incrementEvent: {
          types: [
            "string"
          ],
          description: "Declarative event key \u2014 emits UI:{incrementEvent} via bus on increment",
          kind: "event"
        },
        decrementEvent: {
          types: [
            "string"
          ],
          description: "Declarative event key \u2014 emits UI:{decrementEvent} via bus on decrement",
          kind: "event"
        },
        resetEvent: {
          types: [
            "string"
          ],
          description: "Declarative event key \u2014 emits UI:{resetEvent} via bus on reset",
          kind: "event"
        },
        title: {
          types: [
            "string"
          ],
          description: "Title displayed above the counter"
        },
        showReset: {
          types: [
            "boolean"
          ],
          description: "Show reset button",
          default: true
        },
        size: {
          types: [
            "string"
          ],
          description: "Counter display size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Template variant",
          enumValues: [
            "minimal",
            "standard",
            "full"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dashboard-layout": {
      type: "dashboard-layout",
      category: "template",
      sourceFile: "components/core/templates/DashboardLayout.tsx",
      tier: "templates",
      family: "core",
      description: "DashboardLayout component",
      suggestedFor: [
        "dashboard",
        "layout",
        "dashboard layout"
      ],
      typicalSize: "full",
      propsSchema: {
        appName: {
          types: [
            "string"
          ],
          description: "App name shown in sidebar"
        },
        logo: {
          types: [
            "node"
          ],
          description: "Logo component or URL"
        },
        navItems: {
          types: [
            "array"
          ],
          description: "Navigation items. Apps that need a Settings page should add it as a navItems entry, not depend on baked-in chrome.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              children: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "NavItem"
                }
              }
            },
            required: [
              "label",
              "href"
            ]
          },
          default: []
        },
        user: {
          types: [
            "object"
          ],
          description: "Current user info (optional - auto-populated from auth context if not provided). Accepts `null` as well as `undefined`: the `.lolo` substrate's signed-out spelling is `(if @config.viewerName {\u2026} null)`, and the compiled path passes that `null` through verbatim.",
          properties: {
            name: {
              types: [
                "string"
              ]
            },
            email: {
              types: [
                "string"
              ]
            },
            avatar: {
              types: [
                "asset"
              ]
            }
          },
          propertyRequired: [
            "name"
          ]
        },
        headerActions: {
          types: [
            "node"
          ],
          description: "Header actions (extra slots beyond bell/search/theme)."
        },
        showSearch: {
          types: [
            "boolean"
          ],
          description: "Show the top-bar search box. Default `false` \u2014 opt in by setting `searchEvent` (any truthy value implies showSearch) or this flag.",
          default: false
        },
        searchEvent: {
          types: [
            "string"
          ],
          description: "Declarative search event \u2014 fires `UI:{searchEvent}` on the bus when the user submits the search box (Enter key). Setting this implies `showSearch=true`. Use `onSearchSubmit` for direct React usage instead.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        onSearchSubmit: {
          types: [
            "function"
          ],
          description: "React-side search submit callback. Used when the host wires the layout directly (not via render-ui pattern resolution).",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        topBarActions: {
          types: [
            "array"
          ],
          description: "Generic top-bar action buttons rendered between the search bar and the notifications bell. Each entry: `{icon, label?, event, badge?, variant?}`. Domain-agnostic \u2014 consumers add a cart icon, profile button, help-bubble, or any other top-bar action by appending entries. Empty array (default) renders nothing.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              badge: {
                types: [
                  "number",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "primary",
                  "danger"
                ]
              }
            },
            required: [
              "icon"
            ]
          },
          default: []
        },
        userMenuItems: {
          types: [
            "array"
          ],
          description: "Account-menu entries listed above Sign out (Settings, a profile page), each `{icon, label, event?, navigatesTo?, variant?}` like `topBarActions`.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              badge: {
                types: [
                  "number",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "primary",
                  "danger"
                ]
              }
            },
            required: [
              "icon"
            ]
          },
          default: []
        },
        fullBleed: {
          types: [
            "boolean"
          ],
          description: "Content fills the area edge to edge \u2014 no padding, no max width, full height (workspaces, canvases, maps).",
          default: false
        },
        notifications: {
          types: [
            "array"
          ],
          description: "Notification list. Pass an empty array to show the bell with no badge; omit / pass null to hide the bell entirely.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              createdAt: {
                types: [
                  "string"
                ]
              },
              read: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "message"
            ]
          }
        },
        notificationClickEvent: {
          types: [
            "string"
          ],
          description: "Declarative bell click event \u2014 fires `UI:{notificationClickEvent}` on the bus with an empty payload when the user clicks the bell.",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        onNotificationClick: {
          types: [
            "function"
          ],
          description: "React-side bell click callback.",
          kind: "callback",
          callbackArgs: []
        },
        showThemeToggle: {
          types: [
            "boolean"
          ],
          description: "Show the theme toggle button in the header. Default `true` \u2014 universally useful for a11y / dark mode.",
          default: true
        },
        sidebarFooter: {
          types: [
            "node"
          ],
          description: "Custom sidebar footer (optional). When omitted, the sidebar has no footer \u2014 apps that need Settings/etc. add them via navItems."
        },
        sidebarContent: {
          types: [
            "node"
          ],
          description: "Optional content region rendered between the nav links and the footer \u2014 a page tree or any consumer navigation surface; scrolls independently and takes the sidebar's remaining height. Hidden in the collapsed rail."
        },
        currentPath: {
          types: [
            "string"
          ],
          description: "Active path used to highlight the matching nav item. Falls back to `useCurrentPagePath()` (set by `CurrentPagePathProvider`), then to `useLocation().pathname`. Production deploys can omit it; the studio preview passes the in-frame `?page=` value so highlighting works inside the embedded playground where `pathname` would be the studio route."
        },
        onSignOut: {
          types: [
            "function"
          ],
          description: "Callback when user clicks sign out (optional - uses auth context signOut if not provided)",
          kind: "callback",
          callbackArgs: []
        },
        layoutMode: {
          types: [
            "string"
          ],
          description: 'Layout mode. Default `"sidebar"`. - `sidebar`: Left sidebar nav + top header (classic dashboard) - `topnav`: Horizontal nav in header, no sidebar - `bottomnav`: Bottom tab bar, no sidebar - `minimal`: No nav chrome, content only - `drawer`: Sidebar nav behind a menu button at every width; content gets the full width',
          enumValues: [
            "sidebar",
            "topnav",
            "bottomnav",
            "minimal",
            "drawer"
          ],
          default: "sidebar"
        },
        children: {
          types: [
            "node"
          ],
          description: "Page content rendered inside the main area"
        }
      }
    },
    "generic-app-template": {
      type: "generic-app-template",
      category: "template",
      sourceFile: "components/core/templates/GenericAppTemplate.tsx",
      tier: "templates",
      family: "core",
      description: "GenericAppTemplate  A simple, generic template for any application. Includes a header with title and actions, and a main content area. **Atomic Design**: Composed using Box, Typography, and Button atoms.",
      suggestedFor: [
        "generic",
        "app",
        "template",
        "generic app template"
      ],
      typicalSize: "full",
      propsSchema: {
        entity: {
          types: [
            "object"
          ],
          description: "Entity data, the sole source of runtime state",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class name"
        },
        title: {
          types: [
            "string"
          ],
          description: "Page title",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Subtitle or description"
        },
        children: {
          types: [
            "node"
          ],
          description: "Main content",
          required: true
        },
        headerActions: {
          types: [
            "node"
          ],
          description: "Header actions (buttons, links)"
        },
        footer: {
          types: [
            "node"
          ],
          description: "Footer content"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-shell": {
      type: "game-shell",
      category: "game",
      sourceFile: "components/game/templates/GameShell.tsx",
      tier: "templates",
      family: "game",
      description: 'GameShell  A full-screen layout wrapper for game applications. Replaces DashboardLayout for game clients \u2014 no sidebar nav, just full-viewport rendering with an optional HUD overlay bar.  Used as a React Router layout route element: <Route element={<GameShell appName="TraitWars" />}> <Route index element={<WorldMapPage />} /> ... </Route>',
      suggestedFor: [
        "game",
        "shell",
        "game shell"
      ],
      typicalSize: "full",
      propsSchema: {
        appName: {
          types: [
            "string"
          ],
          description: "Application / game title shown as a floating chip"
        },
        hud: {
          types: [
            "node"
          ],
          description: 'Stat chips row \u2014 floats along the top edge. Legacy chrome surface: new behaviors emit HUD chrome via `render-ui "hud-top"` instead.'
        },
        addons: {
          types: [
            "node"
          ],
          description: 'Action cluster \u2014 floats bottom-right (End Turn, Fire, Reset, \u2026). Legacy chrome surface: new behaviors emit action chrome via `render-ui "hud-bottom"` or `"floating"` instead.'
        },
        controls: {
          types: [
            "node"
          ],
          description: 'Movement controls \u2014 floats bottom-left (d-pad / control-grid). Legacy chrome surface: new behaviors emit movement controls via `render-ui "hud-bottom"` instead.'
        },
        overlay: {
          types: [
            "node"
          ],
          description: 'Centered overlay layer (victory/defeat banners, dialogs). Legacy chrome surface: new behaviors emit overlays via `render-ui "overlay"` (clear with `render-ui "overlay" null` on every exit) instead.'
        },
        className: {
          types: [
            "string"
          ],
          description: "Extra class name on the root container"
        },
        showTopBar: {
          types: [
            "boolean"
          ],
          description: "Whether to show the title chip (default: true)",
          default: true
        },
        children: {
          types: [
            "node"
          ],
          description: "Game content \u2014 fills the whole surface underneath the overlays"
        },
        backgroundAsset: {
          types: [
            "object"
          ],
          description: "Pattern slice tiled at low opacity across the surface (never cover-stretched).",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        hudBackgroundAsset: {
          types: [
            "object"
          ],
          description: "Per-call-site 9-sliced panel override. Chrome normally comes from the active theme; most callers leave this unset.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        fontFamily: {
          types: [
            "string"
          ],
          description: "Game display-font key (pixel | fredoka | future | future-narrow | blocks | mini) or a CSS font-family. Scoped override of the theme contract's --font-family-display slot: titles and numerics take this face, body text follows the active theme. Unset = the theme token decides."
        },
        "data-theme": {
          types: [
            "string"
          ],
          description: 'Scopes an `@almadar/ui` theme (e.g. "game-sci-fi-dark") to this shell\'s subtree.'
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    chart: {
      type: "chart",
      category: "visualization",
      sourceFile: "components/core/molecules/Chart.tsx",
      tier: "molecules",
      family: "core",
      description: "Data visualization chart supporting bar, line, pie, area, and donut types",
      suggestedFor: [
        "dashboards",
        "analytics",
        "data visualization",
        "reporting",
        "bar chart",
        "line chart",
        "pie chart",
        "area chart",
        "donut chart",
        "bar",
        "line",
        "pie",
        "area",
        "donut"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        title: {
          types: [
            "string"
          ],
          description: "Chart title"
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Chart subtitle / description"
        },
        chartType: {
          types: [
            "string"
          ],
          description: "Chart type (legacy alias for `look`).",
          enumValues: [
            "bar",
            "line",
            "pie",
            "area",
            "donut",
            "scatter",
            "histogram"
          ],
          synonyms: "chart style, graph type, bar chart, line chart, pie chart, area chart, donut chart, scatter plot, histogram",
          tier: "domain"
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 drives the chart sub-renderer (bar / line / pie / etc.).",
          enumValues: [
            "bar-vertical",
            "bar-horizontal",
            "line",
            "area",
            "pie",
            "donut",
            "scatter",
            "histogram"
          ],
          synonyms: "chart style, render style, visual treatment",
          tier: "presentation"
        },
        series: {
          types: [
            "array"
          ],
          description: "Multi-series data",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              data: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    value: {
                      types: [
                        "number"
                      ]
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "label",
                    "value"
                  ]
                }
              },
              color: {
                types: [
                  "string"
                ]
              },
              dashed: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "name",
              "data"
            ]
          }
        },
        data: {
          types: [
            "array"
          ],
          description: "Simple single-series shorthand (bar/line/pie/area/donut/histogram)",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        scatterData: {
          types: [
            "array"
          ],
          description: "Raw {x,y} points for scatter",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          }
        },
        height: {
          types: [
            "number"
          ],
          description: "Chart height in px",
          default: 200
        },
        showLegend: {
          types: [
            "boolean"
          ],
          description: "Show legend",
          default: true
        },
        showValues: {
          types: [
            "boolean"
          ],
          description: "Show values on chart",
          default: false
        },
        stack: {
          types: [
            "string"
          ],
          description: "Stack mode for bar / area",
          enumValues: [
            "none",
            "stack",
            "normalize"
          ],
          default: "none"
        },
        timeAxis: {
          types: [
            "boolean"
          ],
          description: 'Format X-axis labels as time (ISO date in \u2192 "Mar 2026"-style label out)',
          default: false
        },
        period: {
          types: [
            "string"
          ],
          description: 'Bucket granularity of a time axis \u2014 labels read "Oct 2", "Week of Sep 28", "Oct 2026", "Q4 2026" or "2026"',
          enumValues: [
            "day",
            "week",
            "month",
            "quarter",
            "year"
          ]
        },
        drillEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted as `UI:{drillEvent}` with `{ label, value, seriesLabel? }` on data-point click"
        },
        actions: {
          types: [
            "array"
          ],
          description: "Top-level chart actions (export, refresh, etc.)",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "card",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    meter: {
      type: "meter",
      category: "visualization",
      sourceFile: "components/core/molecules/Meter.tsx",
      tier: "molecules",
      family: "core",
      description: "Gauge/meter component for displaying a value within a range with thresholds",
      suggestedFor: [
        "dashboards",
        "progress tracking",
        "credit/quota displays",
        "KPI indicators"
      ],
      typicalSize: "small",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Current value",
          required: true
        },
        min: {
          types: [
            "number"
          ],
          description: "Minimum value",
          default: 0
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value",
          default: 100
        },
        label: {
          types: [
            "string"
          ],
          description: "Display label"
        },
        unit: {
          types: [
            "string"
          ],
          description: "Unit suffix (e.g., '%', 'MB', 'credits')"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Display variant",
          enumValues: [
            "linear",
            "radial",
            "segmented"
          ],
          default: "linear"
        },
        thresholds: {
          types: [
            "array"
          ],
          description: "Color thresholds",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "color"
            ]
          }
        },
        segments: {
          types: [
            "number"
          ],
          description: "Number of segments (for segmented variant)",
          default: 5
        },
        showValue: {
          types: [
            "boolean"
          ],
          description: "Show value text",
          default: true
        },
        size: {
          types: [
            "string"
          ],
          description: "Size (for radial variant)",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    timeline: {
      type: "timeline",
      category: "display",
      sourceFile: "components/core/organisms/Timeline.tsx",
      tier: "organisms",
      family: "core",
      description: "Vertical timeline for displaying chronological events with status indicators",
      suggestedFor: [
        "activity logs",
        "process tracking",
        "history views",
        "workflow steps"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "list",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema entity data typed against @almadar/core's EntityRow. Items from `entity` are normalised into `items` when `items` is omitted. UI-specific TimelineItem fields (`icon`, callbacks) cannot round-trip through the event bus, so decorative stories that need them pass `items` directly.",
          kind: "entity",
          cardinality: "collection"
        },
        title: {
          types: [
            "string"
          ],
          description: "Timeline title"
        },
        items: {
          types: [
            "array"
          ],
          description: "Timeline items",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              date: {
                types: [
                  "string"
                ]
              },
              status: {
                types: [
                  "string"
                ],
                enumValues: [
                  "complete",
                  "active",
                  "pending",
                  "error"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              tags: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "id",
              "title"
            ]
          }
        },
        fields: {
          types: [
            "array"
          ],
          description: "Entity fields the timeline draws; each slot is named by its own `*Field` prop.",
          items: {
            types: [
              "string"
            ]
          }
        },
        titleField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's title."
        },
        descriptionField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's description."
        },
        dateField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's date."
        },
        statusField: {
          types: [
            "string"
          ],
          description: "Entity field holding each item's status (complete | active | pending | error)."
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Actions per item",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment.",
          enumValues: [
            "vertical-compact",
            "vertical-spacious",
            "horizontal",
            "swimlane"
          ],
          default: "vertical-spacious"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "media-gallery": {
      type: "media-gallery",
      category: "media",
      sourceFile: "components/core/organisms/MediaGallery.tsx",
      tier: "organisms",
      family: "core",
      description: "Grid gallery for images and media with lightbox, selection, and upload",
      suggestedFor: [
        "photo galleries",
        "image management",
        "media libraries",
        "portfolio displays"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Entity rows to display (collection cardinality).",
          kind: "entity",
          cardinality: "collection"
        },
        title: {
          types: [
            "string"
          ],
          description: "Gallery title"
        },
        items: {
          types: [
            "array"
          ],
          description: "Media items",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              src: {
                types: [
                  "asset"
                ]
              },
              alt: {
                types: [
                  "string"
                ]
              },
              thumbnail: {
                types: [
                  "asset"
                ]
              },
              mediaType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "image",
                  "video"
                ]
              },
              caption: {
                types: [
                  "string"
                ]
              },
              fileSize: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        columns: {
          types: [
            "number"
          ],
          description: "Column count",
          numericEnumValues: [
            2,
            3,
            4,
            5,
            6
          ],
          default: 3
        },
        selectable: {
          types: [
            "boolean"
          ],
          description: "Enable item selection",
          default: false
        },
        selectedItems: {
          types: [
            "array"
          ],
          description: "Selected item IDs",
          items: {
            types: [
              "string"
            ]
          },
          default: []
        },
        selectionEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted when selection changes (emitted as UI:{selectionEvent})",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selection",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ],
          default: "SELECTION_CHANGE"
        },
        showUpload: {
          types: [
            "boolean"
          ],
          description: "Show upload button",
          default: false
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        aspectRatio: {
          types: [
            "string"
          ],
          description: "Aspect ratio for thumbnails",
          enumValues: [
            "square",
            "landscape",
            "portrait"
          ],
          default: "square"
        },
        srcField: {
          types: [
            "string"
          ],
          description: "Record field holding each image's URL (entity mode). Default `src`.",
          default: "src"
        },
        captionField: {
          types: [
            "string"
          ],
          description: "Record field holding each image's caption (entity mode). Default `caption`.",
          default: "caption"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "signature-pad": {
      type: "signature-pad",
      category: "form",
      sourceFile: "components/core/molecules/SignaturePad.tsx",
      tier: "molecules",
      family: "core",
      description: "SignaturePad \u2014 a draw-to-sign canvas that captures a handwritten signature or initials.",
      capabilities: "e-signature capture, sign-here field, consent signature, initial-here field, wet-signature substitute",
      suggestedFor: [
        "form signing",
        "approval workflows",
        "document signing",
        "consent forms"
      ],
      typicalSize: "small",
      propsSchema: {
        label: {
          types: [
            "string"
          ],
          description: "Label above the pad"
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text"
        },
        strokeColor: {
          types: [
            "string"
          ],
          description: "Stroke color"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width",
          default: 2
        },
        height: {
          types: [
            "number"
          ],
          description: "Pad height",
          default: 200
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "Whether the pad is read-only",
          default: false
        },
        value: {
          types: [
            "string"
          ],
          description: "Existing signature image URL"
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Callback when signature changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "dataUrl",
              type: "string"
            }
          ]
        },
        signEvent: {
          types: [
            "string"
          ],
          description: "Event to emit on sign",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "signature",
              type: "string",
              required: true
            }
          ]
        },
        clearEvent: {
          types: [
            "string"
          ],
          description: "Event to emit on clear",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "document-viewer": {
      type: "document-viewer",
      category: "display",
      sourceFile: "components/core/molecules/DocumentViewer.tsx",
      tier: "molecules",
      family: "core",
      description: "Document viewer for PDFs, text, HTML, and markdown with zoom and pagination",
      suggestedFor: [
        "document management",
        "PDF viewing",
        "content preview",
        "report viewing"
      ],
      typicalSize: "large",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        title: {
          types: [
            "string"
          ],
          description: "Document title"
        },
        src: {
          types: [
            "asset"
          ],
          description: "Document URL (for PDF/external documents)"
        },
        content: {
          types: [
            "string"
          ],
          description: "Document content (for text/html/markdown)"
        },
        documentType: {
          types: [
            "string"
          ],
          description: "Document type",
          enumValues: [
            "pdf",
            "text",
            "html",
            "markdown"
          ],
          default: "pdf"
        },
        currentPage: {
          types: [
            "number"
          ],
          description: "Current page (for multi-page documents)"
        },
        totalPages: {
          types: [
            "number"
          ],
          description: "Total pages"
        },
        height: {
          types: [
            "number",
            "string"
          ],
          description: "Viewer height",
          default: 600
        },
        showToolbar: {
          types: [
            "boolean"
          ],
          description: "Show toolbar",
          default: true
        },
        showDownload: {
          types: [
            "boolean"
          ],
          description: "Show download button",
          default: false
        },
        showPrint: {
          types: [
            "boolean"
          ],
          description: "Show print button",
          default: false
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        documents: {
          types: [
            "array"
          ],
          description: "Multiple documents (tabbed view)",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              src: {
                types: [
                  "asset"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              },
              documentType: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pdf",
                  "text",
                  "html",
                  "markdown"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "graph-canvas": {
      type: "graph-canvas",
      category: "game",
      sourceFile: "components/core/molecules/GraphCanvas.tsx",
      tier: "molecules",
      family: "core",
      description: "Force-directed graph visualization for node-link data with interactive zoom, pan, and layout",
      suggestedFor: [
        "knowledge graphs",
        "network visualization",
        "relationship mapping",
        "dependency graphs"
      ],
      typicalSize: "large",
      propsSchema: {
        description: {
          types: [
            "string"
          ],
          description: "Text alternative for the graph, rendered as visually hidden text and linked by aria-describedby."
        },
        title: {
          types: [
            "string"
          ],
          description: "Graph title"
        },
        nodes: {
          types: [
            "array"
          ],
          description: "Graph nodes",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              group: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              badge: {
                types: [
                  "number"
                ]
              },
              mark: {
                types: [
                  "object"
                ],
                variants: [
                  {
                    types: [
                      "object"
                    ],
                    properties: {
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "suggested"
                        ]
                      },
                      suggestionId: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "kind",
                      "suggestionId"
                    ]
                  },
                  {
                    types: [
                      "object"
                    ],
                    properties: {
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "proposed"
                        ]
                      },
                      suggestionId: {
                        types: [
                          "string"
                        ]
                      },
                      tint: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "kind",
                      "suggestionId"
                    ]
                  }
                ],
                discriminator: "kind"
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        edges: {
          types: [
            "array"
          ],
          description: "Graph edges (the only rendered links)",
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "source",
              "target"
            ]
          }
        },
        proposedEdges: {
          types: [
            "array"
          ],
          description: "Uncommitted proposal links \u2014 rendered dashed/faint in a separate pass, never folded into the layout springs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "source",
              "target"
            ]
          }
        },
        similarity: {
          types: [
            "array"
          ],
          description: "All-pairs similarity (cosine 0\u20131) used ONLY for layout, never drawn. It is the secondary macro-layout: non-connected pairs get a weak spring (higher cosine \u21D2 closer) so clusters arrange relative to each other; drawn `edges` stay the primary tight structure. When omitted, edges alone drive the layout.",
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "source",
              "target",
              "weight"
            ]
          }
        },
        height: {
          types: [
            "number"
          ],
          description: "Canvas height",
          default: 400
        },
        showLabels: {
          types: [
            "boolean"
          ],
          description: "Show node labels",
          default: true
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "Enable zoom/pan",
          default: true
        },
        draggable: {
          types: [
            "boolean"
          ],
          description: "Enable node dragging",
          default: true
        },
        actions: {
          types: [
            "array"
          ],
          description: "Actions",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "On node click",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onMarkClick: {
          types: [
            "function"
          ],
          description: "On marked-node click (suggestion/proposal target). Takes priority over onNodeClick.",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onNodeDoubleClick: {
          types: [
            "function"
          ],
          description: "On node double-click (e.g. to drill in)",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onBadgeClick: {
          types: [
            "function"
          ],
          description: "On node badge click (e.g. to expand a merged cluster).",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  badge: {
                    types: [
                      "number"
                    ]
                  },
                  mark: {
                    types: [
                      "object"
                    ],
                    variants: [
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "suggested"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      },
                      {
                        types: [
                          "object"
                        ],
                        properties: {
                          kind: {
                            types: [
                              "string"
                            ],
                            enumValues: [
                              "proposed"
                            ]
                          },
                          suggestionId: {
                            types: [
                              "string"
                            ]
                          },
                          tint: {
                            types: [
                              "string"
                            ]
                          }
                        },
                        required: [
                          "kind",
                          "suggestionId"
                        ]
                      }
                    ],
                    discriminator: "kind"
                  },
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        nodeClickEvent: {
          types: [
            "string"
          ],
          description: "Node click event",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        selectedNodeId: {
          types: [
            "string"
          ],
          description: "Currently selected node id (rendered emphasized)"
        },
        repulsion: {
          types: [
            "number"
          ],
          description: "Force-sim repulsion strength between nodes (larger \u21D2 more spread out)",
          default: 800
        },
        linkDistance: {
          types: [
            "number"
          ],
          description: "Force-sim target edge length (larger \u21D2 more spread out)",
          default: 100
        },
        nodeSpacing: {
          types: [
            "number"
          ],
          description: "Minimum empty gap (px) enforced between node edges so nodes never overlap.",
          default: 28
        },
        linkOpacity: {
          types: [
            "number"
          ],
          description: "Base opacity for links when nothing is hovered (kept faint so dense graphs stay readable; incident links brighten on hover).",
          default: 0.18
        },
        layout: {
          types: [
            "string"
          ],
          description: "Layout algorithm",
          enumValues: [
            "force",
            "circular",
            "grid"
          ],
          default: "force"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "error-boundary": {
      type: "error-boundary",
      category: "state",
      sourceFile: "components/core/molecules/ErrorBoundary.tsx",
      tier: "molecules",
      family: "core",
      description: "ErrorBoundary component",
      suggestedFor: [
        "error",
        "boundary",
        "error boundary"
      ],
      typicalSize: "medium",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "Content to render when no error",
          required: true
        },
        fallback: {
          types: [
            "node",
            "function"
          ],
          description: "Fallback UI when an error is caught \u2014 ReactNode or render function"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes for the wrapper"
        },
        onError: {
          types: [
            "function"
          ],
          description: "Called when an error is caught (for logging/telemetry)",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "error",
              type: "object"
            },
            {
              name: "errorInfo",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    skeleton: {
      type: "skeleton",
      category: "component",
      sourceFile: "components/core/molecules/Skeleton.tsx",
      tier: "molecules",
      family: "core",
      description: "Skeleton component",
      suggestedFor: [
        "skeleton"
      ],
      typicalSize: "medium",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "The skeleton variant to render",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text"
          ],
          default: "text"
        },
        spec: {
          types: [
            "string"
          ],
          description: "A declared `skeleton` prop value (shape name, sized shape or `none`); overrides variant/rows/columns/fields",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ]
        },
        rows: {
          types: [
            "number"
          ],
          description: "Rows for table/list/text, cards for grid, field pairs for detail",
          default: 5
        },
        columns: {
          types: [
            "number"
          ],
          description: "Columns for table, tiles for stats",
          default: 4
        },
        fields: {
          types: [
            "number"
          ],
          description: "Number of fields for form variant",
          default: 4
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "quiz-block": {
      type: "quiz-block",
      category: "component",
      sourceFile: "components/core/molecules/QuizBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "QuizBlock \u2014 a single quiz question with answer choices and submit/reveal feedback.",
      capabilities: "multiple-choice quiz, test question, exam item, assessment question, knowledge check, trivia question",
      suggestedFor: [
        "quiz",
        "block",
        "quiz block"
      ],
      typicalSize: "medium",
      propsSchema: {
        question: {
          types: [
            "string"
          ],
          description: "The quiz question",
          required: true
        },
        answer: {
          types: [
            "string"
          ],
          description: "The quiz answer (revealed on toggle)",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "code-block": {
      type: "code-block",
      category: "component",
      sourceFile: "components/core/molecules/markdown/CodeBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "CodeBlock Molecule Component  A syntax-highlighted code block with copy-to-clipboard functionality. Preserves scroll position during re-renders.  Event Contract: - Emits: UI:COPY_CODE { language, success }",
      suggestedFor: [
        "code",
        "block",
        "code block"
      ],
      typicalSize: "medium",
      propsSchema: {
        code: {
          types: [
            "string"
          ],
          description: "The code content to display"
        },
        language: {
          types: [
            "string"
          ],
          description: "Programming language for syntax highlighting (any Prism id; loaded on demand if not pre-registered)",
          default: "text"
        },
        showCopyButton: {
          types: [
            "boolean"
          ],
          description: "Show the copy button",
          default: true
        },
        showLanguageBadge: {
          types: [
            "boolean"
          ],
          description: "Show the language badge",
          default: true
        },
        maxHeight: {
          types: [
            "string",
            "number"
          ],
          description: "Maximum height before scrolling",
          default: "60vh"
        },
        foldable: {
          types: [
            "boolean"
          ],
          description: "Enable brace-based code folding of multi-line `{}`/`[]` blocks (default: true)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "When true, render an editable surface that composes a transparent `Textarea` over a Prism-highlighted overlay. The overlay re-tokenizes on each keystroke (driven from a local mirror of the textarea value), so users see syntax-highlighted code while still being able to type. Folding is skipped in editable mode. History: GAP-51 first-cut shipped plain (no-highlighting) editable text; GAP-77 (2026-04-12) added the Prism overlay layer. Default: false (existing read-only behavior unchanged).",
          default: false
        },
        onChange: {
          types: [
            "function"
          ],
          description: "GAP-51: called with the new code on every keystroke when `editable === true`. Consumers should debounce + parse downstream \u2014 `CodeBlock` does not.",
          kind: "callback",
          callbackArgs: [
            {
              name: "code",
              type: "string"
            }
          ]
        },
        errorLines: {
          types: [
            "object"
          ],
          description: "GAP-80: line-level error/warning highlights. Map of 1-based line number \u2192 severity. Paints a colored background on each line: error = red, warning = yellow. Honored by the editable overlay and (GAP-84) by viewer mode's non-diff highlight; pass undefined (default) to disable. The consumer is responsible for computing the path \u2192 line map from the schema + validation results.",
          nonAuthorable: true
        },
        title: {
          types: [
            "string"
          ],
          description: "\u2500\u2500 Viewer props (absorbed from CodeViewer / DocCodeBlock) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Diff or plain-code display mode",
          enumValues: [
            "code",
            "diff"
          ],
          default: "code"
        },
        diff: {
          types: [
            "array"
          ],
          description: "Pre-computed diff lines",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "added",
                  "removed",
                  "unchanged",
                  "context"
                ]
              },
              beforeLineNumber: {
                types: [
                  "number"
                ]
              },
              afterLineNumber: {
                types: [
                  "number"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "type",
              "content"
            ]
          }
        },
        oldValue: {
          types: [
            "string"
          ],
          description: "Old source text \u2014 generates diff when combined with newValue"
        },
        newValue: {
          types: [
            "string"
          ],
          description: "New source text \u2014 generates diff when combined with oldValue"
        },
        showLineNumbers: {
          types: [
            "boolean"
          ],
          description: "Show line numbers in code / diff mode",
          default: false
        },
        wordWrap: {
          types: [
            "boolean"
          ],
          description: "Enable word-wrap in code / diff mode",
          default: false
        },
        files: {
          types: [
            "array"
          ],
          description: "Multiple files shown as tabs",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              code: {
                types: [
                  "string"
                ]
              },
              language: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "json",
                  "javascript",
                  "js",
                  "typescript",
                  "ts",
                  "jsx",
                  "tsx",
                  "css",
                  "markdown",
                  "md",
                  "bash",
                  "shell",
                  "sh",
                  "yaml",
                  "yml",
                  "rust",
                  "python",
                  "py",
                  "sql",
                  "diff",
                  "toml",
                  "go",
                  "graphql",
                  "html",
                  "xml",
                  "clojure",
                  "clj",
                  "edn",
                  "haskell",
                  "hs",
                  "lisp",
                  "scheme",
                  "scala",
                  "elixir",
                  "ex",
                  "exs",
                  "erlang",
                  "erl",
                  "elm",
                  "fsharp",
                  "fs",
                  "fsx",
                  "ocaml",
                  "ml",
                  "java",
                  "c",
                  "cpp",
                  "c++",
                  "cc",
                  "cxx",
                  "hpp",
                  "h",
                  "csharp",
                  "cs",
                  "objectivec",
                  "objc",
                  "php",
                  "ruby",
                  "rb",
                  "swift",
                  "kotlin",
                  "kt",
                  "lua",
                  "r",
                  "dart",
                  "julia",
                  "fortran",
                  "f90",
                  "f95",
                  "perl",
                  "pl",
                  "powershell",
                  "ps1",
                  "makefile",
                  "make",
                  "nginx",
                  "ini",
                  "orb",
                  "lolo"
                ]
              }
            },
            required: [
              "label",
              "code"
            ]
          }
        },
        naturalLanguages: {
          types: [
            "array"
          ],
          description: "Show the program in these natural languages as tabs, translating on the fly. Only meaningful for `language` 'lolo' / 'orb'; ignored otherwise. Fewer than two entries renders exactly as without the prop. `editable` wins: editing a translation is not a thing, so the English source is shown and this is ignored.",
          items: {
            types: [
              "object"
            ]
          }
        },
        actions: {
          types: [
            "array"
          ],
          description: "Action badges in the toolbar",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        showCopy: {
          types: [
            "boolean"
          ],
          description: "Show copy button (viewer alias for showCopyButton)"
        },
        editorId: {
          types: [
            "string"
          ],
          description: "Stable identity for the keyboard router / plugin host to target this editor. Unset = this instance never receives capability events.",
          tier: "presentation"
        },
        onEditorFocus: {
          types: [
            "string"
          ],
          description: "Declarative bus emit fired when this editor gains focus.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            }
          ],
          default: "EDITOR_FOCUS",
          tier: "presentation"
        },
        onEditorBlur: {
          types: [
            "string"
          ],
          description: "Declarative bus emit fired when this editor loses focus.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            }
          ],
          default: "EDITOR_BLUR",
          tier: "presentation"
        },
        onMotion: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: move the cursor per the vim-style motion vocabulary.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "motion",
              type: "string",
              required: true,
              enumValues: [
                "left",
                "right",
                "up",
                "down",
                "word-forward",
                "word-back",
                "word-end",
                "line-start",
                "line-end",
                "first-nonblank",
                "doc-start",
                "doc-end",
                "paragraph-forward",
                "paragraph-back",
                "line",
                "selection",
                "match-bracket"
              ]
            },
            {
              name: "count",
              type: "number",
              required: true
            }
          ],
          default: "MOTION",
          tier: "presentation"
        },
        onOperate: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: apply a text operator over a motion's range.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "operator",
              type: "string",
              required: true,
              enumValues: [
                "delete",
                "yank",
                "change",
                "put",
                "put-before",
                "undo",
                "redo",
                "join",
                "toggle-case",
                "indent",
                "dedent",
                "replace"
              ]
            },
            {
              name: "motion",
              type: "string",
              required: true,
              enumValues: [
                "left",
                "right",
                "up",
                "down",
                "word-forward",
                "word-back",
                "word-end",
                "line-start",
                "line-end",
                "first-nonblank",
                "doc-start",
                "doc-end",
                "paragraph-forward",
                "paragraph-back",
                "line",
                "selection",
                "match-bracket"
              ]
            },
            {
              name: "count",
              type: "number",
              required: true
            }
          ],
          default: "OPERATE",
          tier: "presentation"
        },
        onInsertText: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: insert literal text at the cursor.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "text",
              type: "string",
              required: true
            }
          ],
          default: "INSERT_TEXT",
          tier: "presentation"
        },
        onSetMode: {
          types: [
            "string"
          ],
          description: "Declarative bus listen: switch editor mode and caret style.",
          kind: "event-listen",
          listenPayloadSchema: [
            {
              name: "editorId",
              type: "string",
              required: true
            },
            {
              name: "mode",
              type: "string",
              required: true
            },
            {
              name: "caret",
              type: "string",
              required: true,
              enumValues: [
                "bar",
                "block",
                "underline"
              ]
            }
          ],
          default: "SET_MODE",
          tier: "presentation"
        },
        motions: {
          types: [
            "array"
          ],
          description: "Motion vocabulary this editor accepts. Default: the full `EditorMotion` set.",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "left",
              "right",
              "up",
              "down",
              "word-forward",
              "word-back",
              "word-end",
              "line-start",
              "line-end",
              "first-nonblank",
              "doc-start",
              "doc-end",
              "paragraph-forward",
              "paragraph-back",
              "line",
              "selection",
              "match-bracket"
            ]
          },
          default: [
            "left",
            "right",
            "up",
            "down",
            "word-forward",
            "word-back",
            "word-end",
            "line-start",
            "line-end",
            "first-nonblank",
            "doc-start",
            "doc-end",
            "paragraph-forward",
            "paragraph-back",
            "line",
            "selection",
            "match-bracket"
          ],
          tier: "presentation"
        },
        operators: {
          types: [
            "array"
          ],
          description: "Operator vocabulary this editor accepts. Default: the full `EditorOperator` set.",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "delete",
              "yank",
              "change",
              "put",
              "put-before",
              "undo",
              "redo",
              "join",
              "toggle-case",
              "indent",
              "dedent",
              "replace"
            ]
          },
          default: [
            "delete",
            "yank",
            "change",
            "put",
            "put-before",
            "undo",
            "redo",
            "join",
            "toggle-case",
            "indent",
            "dedent",
            "replace"
          ],
          tier: "presentation"
        },
        completions: {
          types: [
            "function"
          ],
          description: "Editable only: answers the caret's valid continuations after a keystroke (and on Ctrl-Space); they show under the editor and Tab or a click inserts one.",
          callbackArgs: [
            {
              name: "code",
              type: "string"
            },
            {
              name: "offset",
              type: "number"
            }
          ],
          renderCallback: true,
          tier: "presentation"
        },
        assist: {
          types: [
            "function"
          ],
          description: "Editable only: model-assisted editing. Asked after a pause (and on Ctrl-Space) for a completion at the caret, shown as ghost text; on \u2318/Ctrl-. for fixes, listed under the editor. Tab accepts the next, \u2318/Ctrl-Enter accepts all, Escape dismisses.",
          callbackArgs: [
            {
              name: "request",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  offset: {
                    types: [
                      "number"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "complete",
                      "fix"
                    ]
                  },
                  trigger: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "idle",
                      "explicit"
                    ]
                  }
                },
                required: [
                  "code",
                  "offset",
                  "kind",
                  "trigger"
                ]
              }
            }
          ],
          renderCallback: true,
          tier: "presentation"
        },
        problems: {
          types: [
            "number"
          ],
          description: "Editable only: how many problems the host knows the code has (some, like a parse error, have no line to mark). With `assist`, the editor offers to fix them.",
          tier: "presentation"
        },
        diagnostics: {
          types: [
            "array"
          ],
          description: "Editable only: problems located in the code (1-based line/column in characters, `endColumn` just past the last one), each underlined exactly \u2014 red wavy for an error, amber for a warning, dotted where the position is approximate. The message of the one under the caret shows below the editor.",
          items: {
            types: [
              "object"
            ],
            properties: {
              line: {
                types: [
                  "number"
                ]
              },
              column: {
                types: [
                  "number"
                ]
              },
              endLine: {
                types: [
                  "number"
                ]
              },
              endColumn: {
                types: [
                  "number"
                ]
              },
              severity: {
                types: [
                  "string"
                ],
                enumValues: [
                  "error",
                  "warning"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              approximate: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "line",
              "severity",
              "message"
            ]
          },
          tier: "presentation"
        },
        definitionAt: {
          types: [
            "function"
          ],
          description: "Editable only: go to definition. Whether the identifier (a dotted path is one) leads somewhere; with Cmd/Ctrl held the one under the pointer is underlined when it does. Comments and strings never qualify.",
          callbackArgs: [
            {
              name: "identifier",
              type: "string"
            }
          ],
          renderCallback: true
        },
        onGoToDefinition: {
          types: [
            "function"
          ],
          description: "Editable only: Cmd/Ctrl+click, or Cmd/Ctrl+Enter at the caret, on an identifier `definitionAt` accepts.",
          kind: "callback",
          callbackArgs: [
            {
              name: "identifier",
              type: "string"
            }
          ]
        },
        diagnosticsCode: {
          types: [
            "string"
          ],
          description: "The code `diagnostics` were computed on. When the editor's text has moved on, each range is carried over the edit; a range the edit touches is dropped until the code is checked again. Absent: they describe the current text.",
          tier: "presentation"
        },
        lineNumbers: {
          types: [
            "boolean"
          ],
          description: "Editable only: number the lines in a gutter (lines with errors in red). Default on.",
          tier: "presentation"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "markdown-content": {
      type: "markdown-content",
      category: "component",
      sourceFile: "components/core/molecules/markdown/MarkdownContent.tsx",
      tier: "molecules",
      family: "core",
      description: "MarkdownContent Molecule Component  Renders markdown content with support for GFM (GitHub Flavored Markdown), math equations (KaTeX), and raw HTML. Handles inline code only \u2014 fenced code blocks should be parsed out and rendered with CodeBlock component.  Event Contract: - No events emitted (display-only component) - entityAware: false  NOTE: react-markdown's `components` override API requires native HTML elements \u2014 this is the same library-boundary exception as `<iframe>` in DocumentViewer and `<svg>` in JazariStateMachine/StateMachineView.",
      suggestedFor: [
        "markdown",
        "content",
        "markdown content"
      ],
      typicalSize: "medium",
      propsSchema: {
        content: {
          types: [
            "string"
          ],
          description: "The markdown content to render",
          required: true
        },
        direction: {
          types: [
            "string"
          ],
          description: "Text direction (defaults to ltr; `ltr` is first so the generated config seeds it)",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        askEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Ask on selected text (as `UI:<askEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        noteEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Note on selected text (as `UI:<noteEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        askLabel: {
          types: [
            "string"
          ],
          description: "Ask action label (default: translated)"
        },
        noteLabel: {
          types: [
            "string"
          ],
          description: "Note action label (default: translated)"
        },
        annotations: {
          types: [
            "array"
          ],
          description: "Passages to highlight. Each `text` is matched exactly, first occurrence in document order inside a single rendered text node (outside code); a passage that spans formatting boundaries is not highlighted.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "question",
                  "note"
                ]
              }
            },
            required: [
              "id",
              "text",
              "kind"
            ]
          }
        },
        annotationEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a highlighted passage is clicked (as `UI:<annotationEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "content-renderer": {
      type: "content-renderer",
      category: "display",
      sourceFile: "components/core/molecules/ContentRenderer.tsx",
      tier: "molecules",
      family: "core",
      description: "ContentRenderer Organism  Renders rich content as a sequence of typed segments: markdown, code, orbital diagrams (via JazariStateMachine), and quiz blocks. Accepts either raw content string (auto-parsed) or pre-parsed segments.  Event Contract: - Delegates to child components (CodeBlock -> UI:COPY_CODE) - entityAware: false",
      suggestedFor: [
        "content",
        "renderer",
        "content renderer"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "\u2500\u2500 Display hints (set by trait via render-ui, never written by organism) \u2500\u2500"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number (1-indexed)"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items (for pagination display)"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters, keyed by field name. Scalar-only (`string | number | boolean`) rather than the full `FieldValue` domain: `FieldValue` is a self-referential union (adds `Date`, nested arrays/objects) the pattern-sync scanner can't shape into a `Map string V` knob, so it always degraded to `json`. This narrows out date/array/nested-object filter values \u2014 no current call site in this package constructs this prop, so nothing here breaks, but a caller wiring a date-range or multi-select filter through this prop would need those. Flagged for the coordinator; revert to `Record<string, FieldValue>` if that capability is needed.",
          mapValue: {
            types: [
              "string",
              "number",
              "boolean"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        content: {
          types: [
            "string"
          ],
          description: "Raw content string \u2014 auto-parsed into segments"
        },
        segments: {
          types: [
            "array"
          ],
          description: "Pre-parsed segments (overrides content)",
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "markdown"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "code"
                    ]
                  },
                  language: {
                    types: [
                      "string"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "language",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital"
                    ]
                  },
                  language: {
                    types: [
                      "string"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  schema: {
                    types: [
                      "sexpr"
                    ]
                  }
                },
                required: [
                  "type",
                  "language",
                  "content",
                  "schema"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "quiz"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  answer: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "question",
                  "answer"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Text direction for markdown",
          enumValues: [
            "rtl",
            "ltr"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "jazari-state-machine": {
      type: "jazari-state-machine",
      category: "display",
      sourceFile: "components/core/molecules/JazariStateMachine.tsx",
      tier: "molecules",
      family: "core",
      description: "JazariStateMachine component",
      suggestedFor: [
        "jazari",
        "state",
        "machine",
        "jazari state machine"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        schema: {
          types: [
            "sexpr"
          ],
          description: "Full schema \u2014 extracts first trait's state machine",
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        trait: {
          types: [
            "object"
          ],
          description: "Or pass a single trait directly",
          nonAuthorable: true
        },
        traitIndex: {
          types: [
            "number"
          ],
          description: "Which trait to visualize (default: 0)",
          default: 0
        },
        entityFields: {
          types: [
            "array"
          ],
          description: "Override entity field labels",
          items: {
            types: [
              "string"
            ]
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Text direction (default: 'ltr')",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "state-machine-view": {
      type: "state-machine-view",
      category: "display",
      sourceFile: "components/core/organisms/StateMachineView.tsx",
      tier: "organisms",
      family: "core",
      description: "StateMachineView component",
      suggestedFor: [
        "state",
        "machine",
        "view",
        "state machine view"
      ],
      typicalSize: "large",
      propsSchema: {
        activeState: {
          types: [
            "string"
          ],
          description: "State execution is currently in."
        },
        activeTransition: {
          types: [
            "number"
          ],
          description: "`index` of the transition that just fired."
        },
        visitedStates: {
          types: [
            "array"
          ],
          description: "States already passed through, in order.",
          items: {
            types: [
              "string"
            ]
          }
        },
        stateClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{stateClickEvent} with { stateId } when a state is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stateId",
              type: "string",
              required: true
            }
          ]
        },
        transitionClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{transitionClickEvent} with { index, event, from, to } when a transition label is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "event",
              type: "string",
              required: true
            },
            {
              name: "from",
              type: "string",
              required: true
            },
            {
              name: "to",
              type: "string",
              required: true
            }
          ]
        },
        onTransitionClick: {
          types: [
            "function"
          ],
          description: "Direct transition-click callback, for React hosts (e.g. a canvas node).",
          kind: "callback",
          callbackArgs: [
            {
              name: "transition",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  index: {
                    types: [
                      "number"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "index",
                  "event",
                  "from",
                  "to"
                ]
              }
            }
          ]
        },
        selectedState: {
          types: [
            "string"
          ],
          description: "State picked in an editor (first click)."
        },
        pendingSourceState: {
          types: [
            "string"
          ],
          description: "Source of a transition being drawn \u2014 the next state click picks its target."
        },
        nodeShape: {
          types: [
            "string"
          ],
          description: "Node silhouette. @default 'pill'",
          enumValues: [
            "pill",
            "gear"
          ],
          default: "pill"
        },
        entityFields: {
          types: [
            "array"
          ],
          description: "Entity field names listed in the header.",
          items: {
            types: [
              "string"
            ]
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Flow direction. @default 'ltr'",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        showHeader: {
          types: [
            "boolean"
          ],
          description: "Show the trait header (name, entity, listens/emits). @default true",
          default: true
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations shown in a popover on hover: a note per state name, per transition event and per effect type.",
          properties: {
            states: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            transitions: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            effects: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            }
          },
          example: {
            states: {
              pending: {
                title: "Unpaid",
                body: "Every order starts here."
              }
            },
            transitions: {
              PAY: {
                body: "Only fires when the amount is positive."
              }
            },
            effects: {
              persist: {
                body: "Saves the order."
              }
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        trait: {
          types: [
            "object"
          ],
          description: "The trait to draw (see `parseTraitLevel` / `traitLevelFromTrait`).",
          properties: {
            name: {
              types: [
                "string"
              ]
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            states: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  isInitial: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  },
                  isTerminal: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  }
                },
                required: [
                  "name"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  guard: {
                    types: [
                      "sexpr"
                    ],
                    nullable: true
                  },
                  effects: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        args: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "sexpr"
                            ]
                          }
                        }
                      },
                      required: [
                        "type",
                        "args"
                      ]
                    }
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "from",
                  "to",
                  "event",
                  "effects",
                  "index"
                ]
              }
            },
            emittedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listenedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            }
          },
          propertyRequired: [
            "name",
            "linkedEntity",
            "states",
            "transitions",
            "emittedEvents",
            "listenedEvents"
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "scaled-diagram": {
      type: "scaled-diagram",
      category: "component",
      sourceFile: "components/core/molecules/ScaledDiagram.tsx",
      tier: "molecules",
      family: "core",
      description: "ScaledDiagram Molecule  Wraps a fixed-size diagram (like JazariStateMachine / StateMachineView) and CSS-scales it to fit the parent container width.  The diagram renders at its natural (large) size. We observe the content element and once the diagram is measured we apply transform:scale() with a corrected container height so surrounding layout flows correctly.  Event Contract: - No events emitted (layout-only wrapper) - entityAware: false",
      suggestedFor: [
        "scaled",
        "diagram",
        "scaled diagram"
      ],
      typicalSize: "medium",
      propsSchema: {
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "day-cell": {
      type: "day-cell",
      category: "component",
      sourceFile: "components/core/atoms/DayCell.tsx",
      tier: "atoms",
      family: "core",
      description: "DayCell component",
      suggestedFor: [
        "day",
        "cell",
        "day cell"
      ],
      typicalSize: "small",
      propsSchema: {
        date: {
          types: [
            "date"
          ],
          description: "The date this cell represents. Optional at the dynamic render edge: an unbound `@config.date` arrives as `undefined`, so the cell falls back to today."
        },
        isToday: {
          types: [
            "boolean"
          ],
          description: "Whether this date is today",
          default: false
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Called when the day is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "date",
              type: "date"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "flip-container": {
      type: "flip-container",
      category: "layout",
      sourceFile: "components/core/atoms/FlipContainer.tsx",
      tier: "atoms",
      family: "core",
      description: "FlipContainer component",
      suggestedFor: [
        "flip",
        "container",
        "flip container"
      ],
      typicalSize: "small",
      propsSchema: {
        flipped: {
          types: [
            "boolean"
          ],
          description: "Whether the container is flipped (rotateY 180deg)",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Optional className for the outer wrapper"
        },
        children: {
          types: [
            "node"
          ],
          description: "The two face elements (front and back)",
          required: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler on the outer wrapper",
          kind: "callback",
          callbackArgs: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "time-slot-cell": {
      type: "time-slot-cell",
      category: "layout",
      sourceFile: "components/core/atoms/TimeSlotCell.tsx",
      tier: "atoms",
      family: "core",
      description: "TimeSlotCell component",
      suggestedFor: [
        "time",
        "slot",
        "cell",
        "time slot cell"
      ],
      typicalSize: "small",
      propsSchema: {
        time: {
          types: [
            "string"
          ],
          description: 'Time label for this slot (e.g. "09:00")',
          required: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Called when the slot is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "time",
              type: "string"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Event content placed inside the slot"
        },
        isOccupied: {
          types: [
            "boolean"
          ],
          description: "Whether this slot contains an event",
          default: false
        },
        onPointerDown: {
          types: [
            "function"
          ],
          description: "onPointerDown prop",
          kind: "callback",
          callbackArgs: []
        },
        onPointerUp: {
          types: [
            "function"
          ],
          description: "onPointerUp prop",
          kind: "callback",
          callbackArgs: []
        },
        onPointerCancel: {
          types: [
            "function"
          ],
          description: "onPointerCancel prop",
          kind: "callback",
          callbackArgs: []
        },
        onPointerLeave: {
          types: [
            "function"
          ],
          description: "onPointerLeave prop",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        "data-testid": {
          types: [
            "string"
          ],
          description: "data-testid prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "calendar-grid": {
      type: "calendar-grid",
      category: "display",
      sourceFile: "components/core/molecules/CalendarGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "CalendarGrid component",
      suggestedFor: [
        "calendar",
        "grid",
        "calendar grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        weekStart: {
          types: [
            "date"
          ],
          description: "Start of the week (defaults to current week's Monday)"
        },
        timeSlots: {
          types: [
            "array"
          ],
          description: "Time slot labels (defaults to 09:00-17:00)",
          items: {
            types: [
              "string"
            ]
          }
        },
        events: {
          types: [
            "object",
            "array"
          ],
          description: "Events to display on the grid",
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "title",
            "startTime"
          ],
          properties: {
            title: {
              types: [
                "string"
              ]
            },
            startTime: {
              types: [
                "string"
              ]
            },
            endTime: {
              types: [
                "string"
              ]
            },
            color: {
              types: [
                "string"
              ]
            }
          },
          default: []
        },
        onSlotClick: {
          types: [
            "function"
          ],
          description: "Called when a time slot is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "day",
              type: "date"
            },
            {
              name: "time",
              type: "string"
            }
          ]
        },
        onDayClick: {
          types: [
            "function"
          ],
          description: "Called when a day header is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "day",
              type: "date"
            }
          ]
        },
        onEventClick: {
          types: [
            "function"
          ],
          description: "Called when an event is clicked",
          kind: "callback",
          entityRowField: "event",
          callbackArgs: [
            {
              name: "event",
              type: "object"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        longPressEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on long-press of a time slot: UI:{longPressEvent} with { date, time, ...longPressPayload }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "date",
              type: "string",
              required: true
            },
            {
              name: "time",
              type: "string",
              required: false
            }
          ]
        },
        longPressPayload: {
          types: [
            "object"
          ],
          description: "Additional payload for long-press events",
          payloadFor: "longPressEvent",
          eventPayloadBrand: true
        },
        swipeLeftEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on swipe left (next week): UI:{swipeLeftEvent}",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        swipeRightEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on swipe right (prev week): UI:{swipeRightEvent}",
          kind: "event-ref",
          emitPayloadSchema: []
        },
        dayWindow: {
          types: [
            "number",
            "string"
          ],
          description: "Override the viewport-driven day window. `'auto'` (default) tracks `window.innerWidth` \u2014 1 day on mobile, 3 on tablet, 7 on laptop+. Pass an explicit number to force a fixed window (useful for print layouts or screenshot tests).",
          enumValues: [
            "auto"
          ],
          default: "auto"
        },
        titleField: {
          types: [
            "string"
          ],
          description: "Row field holding the chip label. Defaults to `title`. Lets a host bound to its own entity name the column instead of being forced to rename its fields to this grid's contract (same accessor idiom as `DataList`'s `senderField` / `DataGrid`'s `imageField`).",
          default: "title"
        },
        startField: {
          types: [
            "string"
          ],
          description: "Row field holding the start timestamp (ISO or epoch). Defaults to `startTime`.",
          default: "startTime"
        },
        endField: {
          types: [
            "string"
          ],
          description: "Row field holding the end timestamp (ISO or epoch). Defaults to `endTime`. When a row carries an end, the event's chip renders in its start slot and every further slot it covers shows a subdued continuation bar \u2014 without this, multi-hour events silently collapsed to their first hour.",
          default: "endTime"
        },
        colorField: {
          types: [
            "string"
          ],
          description: "Row field holding the chip's Tailwind colour class. Defaults to `color`.",
          default: "color"
        },
        children: {
          types: [
            "function"
          ],
          description: "Render function for one event chip's content. Receives the row and its index in the materialised events array. The grid keeps ownership of placement, colour and the click target; this replaces only what is drawn INSIDE the chip, so a host can show a time + instructor + badge stack instead of the default single label.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-event render function (schema-level alias for the children render prop). In `.orb`: `["fn", "item", { pattern tree with @item.field bindings }]`. In `.lolo`: `renderItem: (fn item <Component \u2026={@item.field}/>)` \u2014 the same contract `data-list` / `data-grid` / `carousel` already use.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "chart-legend": {
      type: "chart-legend",
      category: "display",
      sourceFile: "components/core/molecules/ChartLegend.tsx",
      tier: "molecules",
      family: "core",
      description: "ChartLegend component",
      suggestedFor: [
        "chart",
        "legend",
        "chart legend"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Legend items with label and color",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "color"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        direction: {
          types: [
            "string"
          ],
          description: "Layout direction",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "date-range-selector": {
      type: "date-range-selector",
      category: "form",
      sourceFile: "components/core/molecules/DateRangeSelector.tsx",
      tier: "molecules",
      family: "core",
      description: "DateRangeSelector component",
      suggestedFor: [
        "date",
        "range",
        "selector",
        "date range selector"
      ],
      typicalSize: "medium",
      propsSchema: {
        options: {
          types: [
            "array"
          ],
          description: "Available range options",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        selected: {
          types: [
            "string"
          ],
          description: "Currently selected value",
          default: "month"
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Callback when a range is selected",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "flip-card": {
      type: "flip-card",
      category: "display",
      sourceFile: "components/core/molecules/FlipCard.tsx",
      tier: "molecules",
      family: "core",
      description: "FlipCard \u2014 flip card that reveals a hidden back face on tap or click, toggling between a front and back content slot.",
      capabilities: "flashcard, study deck, spaced-repetition review card, memorization drill, quiz reveal card, question/answer card, before/after reveal, term-and-definition card",
      suggestedFor: [
        "flip",
        "card",
        "flip card"
      ],
      typicalSize: "medium",
      propsSchema: {
        front: {
          types: [
            "node"
          ],
          description: "Content rendered on the front face",
          required: true
        },
        back: {
          types: [
            "node"
          ],
          description: "Content rendered on the back face",
          required: true
        },
        flipped: {
          types: [
            "boolean"
          ],
          description: "Controlled flip state",
          default: false
        },
        onFlip: {
          types: [
            "function"
          ],
          description: "Callback when the card is clicked to flip",
          kind: "callback",
          callbackArgs: []
        },
        className: {
          types: [
            "string"
          ],
          description: "Optional className for the outer container"
        },
        height: {
          types: [
            "string"
          ],
          description: "Card height as a Tailwind class (default: 'h-64')",
          default: "h-64"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "graph-view": {
      type: "graph-view",
      category: "component",
      sourceFile: "components/core/molecules/GraphView.tsx",
      tier: "molecules",
      family: "core",
      description: "GraphView component",
      suggestedFor: [
        "graph",
        "view",
        "graph view"
      ],
      typicalSize: "medium",
      propsSchema: {
        nodes: {
          types: [
            "array"
          ],
          description: "Graph nodes",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              group: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        edges: {
          types: [
            "array"
          ],
          description: "Graph edges",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              source: {
                types: [
                  "string"
                ]
              },
              target: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "source",
              "target"
            ]
          }
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "Callback when a node is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        onNodeHover: {
          types: [
            "function"
          ],
          description: "Callback when a node is hovered (null on leave)",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  size: {
                    types: [
                      "number"
                    ]
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id"
                ]
              }
            }
          ]
        },
        width: {
          types: [
            "number"
          ],
          description: "Width in pixels"
        },
        height: {
          types: [
            "number"
          ],
          description: "Height in pixels"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        showLabels: {
          types: [
            "boolean"
          ],
          description: "Show node labels (default true)",
          default: true
        },
        zoomToFit: {
          types: [
            "boolean"
          ],
          description: "Auto zoom-to-fit after layout settles (default true)",
          default: true
        },
        layout: {
          types: [
            "string"
          ],
          description: "Layout mode. force = physics simulation (default); flow = layered left-to-right process flow; tree = top-down hierarchy tiers from the roots; radial = concentric rings by depth (a pure cycle renders as a single ring). Non-force layouts are deterministic.",
          enumValues: [
            "force",
            "flow",
            "tree",
            "radial"
          ],
          default: "force",
          synonyms: "layout mode, flow layout, tree layout, radial layout, hierarchy layout",
          tier: "domain"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "line-chart": {
      type: "line-chart",
      category: "display",
      sourceFile: "components/core/molecules/LineChart.tsx",
      tier: "molecules",
      family: "core",
      description: "LineChart component",
      suggestedFor: [
        "line",
        "chart",
        "line chart"
      ],
      typicalSize: "medium",
      propsSchema: {
        data: {
          types: [
            "array"
          ],
          description: "Data points to plot",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              date: {
                types: [
                  "string",
                  "date"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value"
            ]
          }
        },
        width: {
          types: [
            "number"
          ],
          description: "SVG viewBox width",
          default: 400
        },
        height: {
          types: [
            "number"
          ],
          description: "SVG viewBox height",
          default: 200
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "Show horizontal grid lines at 25/50/75%",
          default: true
        },
        showValues: {
          types: [
            "boolean"
          ],
          description: "Show value labels near data points",
          default: false
        },
        showArea: {
          types: [
            "boolean"
          ],
          description: "Fill area under line with gradient",
          default: true
        },
        lineColor: {
          types: [
            "string"
          ],
          description: "Line stroke color",
          default: "var(--color-primary)"
        },
        areaColor: {
          types: [
            "string"
          ],
          description: "Area fill color (used for gradient)",
          default: "var(--color-primary)"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "progress-dots": {
      type: "progress-dots",
      category: "component",
      sourceFile: "components/core/molecules/ProgressDots.tsx",
      tier: "molecules",
      family: "core",
      description: "ProgressDots component",
      suggestedFor: [
        "progress",
        "dots",
        "progress dots"
      ],
      typicalSize: "medium",
      propsSchema: {
        count: {
          types: [
            "number"
          ],
          description: "Total number of dots",
          required: true
        },
        currentIndex: {
          types: [
            "number"
          ],
          description: "Current active index (0-based)",
          required: true
        },
        getState: {
          types: [
            "function"
          ],
          description: "Custom state resolver per dot index",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        onDotClick: {
          types: [
            "function"
          ],
          description: "Callback when a dot is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        size: {
          types: [
            "string"
          ],
          description: "Dot size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "data-grid": {
      type: "data-grid",
      category: "display",
      sourceFile: "components/core/molecules/DataGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "DataGrid \u2014 structured records grid rendering rows over configurable columns, with sort, select, and drag-reorder.",
      capabilities: "admin table, records grid, user list, CRUD list, manage-records view, spreadsheet-style data grid, sortable columns",
      fieldsContract: "display",
      minUsableWidth: 200,
      suggestedFor: [
        "data",
        "grid",
        "data grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        dragGroup: {
          types: [
            "string"
          ],
          description: "When set, items in this container are draggable and announce this group as their source"
        },
        accepts: {
          types: [
            "string"
          ],
          description: "When set, the container accepts drops whose dragGroup matches this value (or '*' for any group)"
        },
        sortable: {
          types: [
            "boolean"
          ],
          description: "Enable in-container reorder; items can be dragged to new positions among siblings"
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on cross-container drop. Payload: { id, sourceGroup, targetGroup, newIndex }"
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on in-container reorder. Payload: { id, oldIndex, newIndex }"
        },
        positionEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired N times per drop (one per item in affected zones) so the trait can persist new positions for the WHOLE zone, not just the moved item. Payload: { id, position }"
        },
        dndItemIdField: {
          types: [
            "string"
          ],
          description: "Row field used as the @dnd-kit draggable id (default 'id')"
        },
        dndRoot: {
          types: [
            "boolean"
          ],
          description: "Mark this container as the DnD root for its subtree \u2014 descendant zones share one DndContext (cross-container drag). Use on an outer container whose own items are not draggable."
        },
        emptyIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Domain icon for the empty state."
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: 'Specific title ("No orders yet").'
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "What will appear here once rows exist."
        },
        emptyAction: {
          types: [
            "object"
          ],
          description: "Create-the-first-item action.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: 'Schema entity data \u2014 the collection of rows to render. pattern-sync tags it `kind:"entity", cardinality:"collection"` so consumers bind the domain entity without name-matching the prop.',
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        fields: {
          types: [
            "array"
          ],
          description: "Field definitions for rendering each card. The pattern contract in `@almadar/core/patterns` documents `columns` as the wire-format alias the compiler emits \u2014 both names resolve to the same shape here. Pass either.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        columns: {
          types: [
            "array"
          ],
          description: "Alias for `fields` \u2014 the compiler emits `columns` for field defs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Per-item action buttons",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        itemClickEvent: {
          types: [
            "string"
          ],
          description: "When set, clicking a card emits UI:{itemClickEvent} with { id, row }. Omit = cards are not clickable.",
          kind: "event-ref",
          entityRowField: "row",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "row",
              type: "@entity",
              required: true
            }
          ]
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: 'Max inline primary action buttons before the rest collapse into a "\u22EF" overflow menu. Omit = all inline.'
        },
        scrollX: {
          types: [
            "boolean"
          ],
          description: "Lay items in a single horizontally-scrolling row (kanban columns) sized to `minCardWidth` instead of a wrapping grid.",
          default: false
        },
        cols: {
          types: [
            "number"
          ],
          description: "Number of columns (uses auto-fit if omitted)",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6
          ]
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between cards",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        minCardWidth: {
          types: [
            "number"
          ],
          description: "Minimum card width in pixels (used when cols is not set, default 280)",
          default: 280
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "grid",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        imageField: {
          types: [
            "string"
          ],
          description: "Entity field name containing an image URL for card thumbnails"
        },
        selectable: {
          types: [
            "boolean"
          ],
          description: "Enable multi-select with checkboxes",
          default: false
        },
        selectionEvent: {
          types: [
            "string"
          ],
          description: "Selection change event name (emits UI:{selectionEvent} with { selectedIds: string[] })",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedIds",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        infiniteScroll: {
          types: [
            "boolean"
          ],
          description: "Enable infinite scroll loading"
        },
        loadMoreEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when more items needed: UI:{loadMoreEvent}",
          kind: "event"
        },
        hasMore: {
          types: [
            "boolean"
          ],
          description: "Whether more items are available for infinite scroll"
        },
        children: {
          types: [
            "function"
          ],
          description: "Render prop for custom per-card content. When provided, `fields` are ignored; `itemActions` render as a footer beneath the custom content.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-item render function (schema-level alias for children render prop). In .orb schemas: ["fn", "item", { pattern tree with @item.field bindings }] The compiler converts this to the children render prop. In .lolo, author the per-item renderer as renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        itemEnter: {
          types: [
            "string"
          ],
          description: "Entrance animation each card plays when it mounts; a card that changes place glides there. Lists rendered inside the grid share its movement, so a card moved between them glides across",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ]
        },
        pageSize: {
          types: [
            "number"
          ],
          description: 'Max items to show before "Show More" button. Defaults to 0 (disabled).',
          default: 0
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 mirrors the `entity-table` look enum so data-grid / data-list / entity-table share one knob name from authors.",
          enumValues: [
            "dense",
            "spacious",
            "striped",
            "borderless",
            "card-rows"
          ],
          default: "dense"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names for a field whose type is relation. Same contract DetailPanel takes.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "data-list": {
      type: "data-list",
      category: "display",
      sourceFile: "components/core/molecules/DataList.tsx",
      tier: "molecules",
      family: "core",
      description: "DataList component",
      fieldsContract: "display",
      minUsableWidth: 200,
      suggestedFor: [
        "data",
        "list",
        "data list"
      ],
      typicalSize: "medium",
      propsSchema: {
        dragGroup: {
          types: [
            "string"
          ],
          description: "When set, items in this container are draggable and announce this group as their source"
        },
        accepts: {
          types: [
            "string"
          ],
          description: "When set, the container accepts drops whose dragGroup matches this value (or '*' for any group)"
        },
        sortable: {
          types: [
            "boolean"
          ],
          description: "Enable in-container reorder; items can be dragged to new positions among siblings"
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on cross-container drop. Payload: { id, sourceGroup, targetGroup, newIndex }"
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on reorder: UI:{reorderEvent} with { fromIndex, toIndex }",
          kind: "event"
        },
        positionEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired N times per drop (one per item in affected zones) so the trait can persist new positions for the WHOLE zone, not just the moved item. Payload: { id, position }"
        },
        dndItemIdField: {
          types: [
            "string"
          ],
          description: "Row field used as the @dnd-kit draggable id (default 'id')"
        },
        dndRoot: {
          types: [
            "boolean"
          ],
          description: "Mark this container as the DnD root for its subtree \u2014 descendant zones share one DndContext (cross-container drag). Use on an outer container whose own items are not draggable."
        },
        emptyIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Domain icon for the empty state."
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: 'Specific title ("No orders yet").'
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "What will appear here once rows exist."
        },
        emptyAction: {
          types: [
            "object"
          ],
          description: "Create-the-first-item action.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema entity data \u2014 the collection of rows to render.",
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        fields: {
          types: [
            "array"
          ],
          description: "Field definitions for rendering each row. The pattern contract in `@almadar/core/patterns` documents `columns` as the wire-format alias the compiler emits \u2014 both names resolve to the same shape here. Pass either.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        columns: {
          types: [
            "array"
          ],
          description: "Alias for `fields` \u2014 the compiler emits `columns` for field defs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4",
                  "overline",
                  "avatar",
                  "body",
                  "caption",
                  "badge",
                  "small",
                  "progress"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "time",
                  "datetime",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "name"
            ]
          }
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Per-item action buttons",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        itemEnter: {
          types: [
            "string"
          ],
          description: "Entrance animation each row plays when it mounts, so an item arriving in this list (moved from another) animates in",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ]
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: 'Max inline action buttons before the rest collapse into a "\u22EF" overflow menu. Omit = all inline.'
        },
        itemClickEvent: {
          types: [
            "string"
          ],
          description: "When set, the whole row is clickable and emits UI:{itemClickEvent} with { id, row } (action-button clicks stopPropagation so they still win).",
          kind: "event-ref",
          entityRowField: "row",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "row",
              type: "@entity",
              required: true
            }
          ]
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between rows",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg"
          ],
          default: "none"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          enumValues: [
            "default",
            "card",
            "compact",
            "message"
          ],
          default: "default"
        },
        groupBy: {
          types: [
            "string"
          ],
          description: "Group items by a field value (renders section headers between groups)"
        },
        groupFormat: {
          types: [
            "string"
          ],
          description: "How a `groupBy` value is bucketed and labelled (`date` = one section per day)",
          enumValues: [
            "date",
            "time",
            "datetime",
            "currency",
            "number",
            "boolean",
            "percent"
          ]
        },
        senderField: {
          types: [
            "string"
          ],
          description: 'Field name identifying the sender (used with variant: "message")'
        },
        senderLabelField: {
          types: [
            "string"
          ],
          description: 'Field holding the sender\'s human-readable name (used with variant: "message"). Defaults to `senderField`. Set this when `senderField` holds an id \u2014 an id-keyed thread otherwise labels bubbles `staff-1` instead of `Sam Staff`.'
        },
        currentUser: {
          types: [
            "string"
          ],
          description: 'Current user identifier; messages matching this value align right (used with variant: "message")'
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: 'Message shown when there are no rows. Falls back to the generic "no items" translation. `TableView` already exposes this; an agenda or an inbox wants to say what "empty" means in its own words.'
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "list",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        reorderable: {
          types: [
            "boolean"
          ],
          description: "Enable drag-to-reorder with grip handles"
        },
        swipeLeftEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on left swipe: UI:{swipeLeftEvent} with { row }",
          kind: "event"
        },
        swipeLeftActions: {
          types: [
            "array"
          ],
          description: "Actions revealed on left swipe",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        swipeRightEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on right swipe: UI:{swipeRightEvent} with { row }",
          kind: "event"
        },
        swipeRightActions: {
          types: [
            "array"
          ],
          description: "Actions revealed on right swipe",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        longPressEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on long press: UI:{longPressEvent} with { row }",
          kind: "event"
        },
        infiniteScroll: {
          types: [
            "boolean"
          ],
          description: "Enable infinite scroll loading"
        },
        loadMoreEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when more items needed: UI:{loadMoreEvent}",
          kind: "event"
        },
        hasMore: {
          types: [
            "boolean"
          ],
          description: "Whether more items are available for infinite scroll"
        },
        children: {
          types: [
            "function"
          ],
          description: "Render prop for custom per-item content. When provided, `fields` and `itemActions` are ignored.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-item render function (schema-level alias for children render prop). In .orb schemas: ["fn", "item", { pattern tree with @item.field bindings }] The compiler converts this to the children render prop. In .lolo, author the per-item renderer as renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        pageSize: {
          types: [
            "number"
          ],
          description: 'Max items to show before "Show More" button. Defaults to 5. Set to 0 to disable.',
          default: 5
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Field name to order rows by. Values are compared by their own type \u2014 numbers numerically, date-like strings chronologically, everything else alphabetically \u2014 never by what the field is called. Empty cells sort last. Unset leaves the incoming order untouched. Orders only the rows already fetched: a collection larger than its `limit` still needs ordering at the data layer."
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Direction for `sortBy`. Defaults to ascending.",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 mirrors the `entity-table` look enum so data-grid / data-list / entity-table share one knob name from authors.",
          enumValues: [
            "dense",
            "spacious",
            "striped",
            "borderless",
            "card-rows"
          ],
          default: "dense"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names for a field whose type is relation. Same contract DetailPanel takes.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "map-view": {
      type: "map-view",
      category: "component",
      sourceFile: "components/core/molecules/MapView.tsx",
      tier: "molecules",
      family: "core",
      description: "MapView component",
      suggestedFor: [
        "map",
        "view",
        "map view"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        markers: {
          types: [
            "array"
          ],
          description: "Array of markers to display",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string",
                  "number"
                ]
              },
              lat: {
                types: [
                  "number"
                ]
              },
              lng: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "lat",
              "lng"
            ]
          },
          default: []
        },
        routes: {
          types: [
            "array"
          ],
          description: "Routes (polylines with optional popups) drawn over the tile layer",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string",
                  "number"
                ]
              },
              waypoints: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    lat: {
                      types: [
                        "number"
                      ]
                    },
                    lng: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "lat",
                    "lng"
                  ]
                }
              },
              color: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              dashArray: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "waypoints"
            ]
          },
          default: []
        },
        centerLat: {
          types: [
            "number"
          ],
          description: "Map center latitude",
          default: 51.505
        },
        centerLng: {
          types: [
            "number"
          ],
          description: "Map center longitude",
          default: -0.09
        },
        zoom: {
          types: [
            "number"
          ],
          description: "Zoom level (1-18, default 13)",
          default: 13
        },
        height: {
          types: [
            "string"
          ],
          description: 'Height of the map container (default "400px")',
          default: "400px"
        },
        onMarkerClick: {
          types: [
            "function"
          ],
          description: "Callback when a marker is clicked (programmatic use)",
          kind: "callback",
          callbackArgs: [
            {
              name: "marker",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string",
                      "number"
                    ]
                  },
                  lat: {
                    types: [
                      "number"
                    ]
                  },
                  lng: {
                    types: [
                      "number"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "id",
                  "lat",
                  "lng"
                ]
              }
            }
          ]
        },
        onMapClick: {
          types: [
            "function"
          ],
          description: "Callback when the map is clicked (programmatic use)",
          kind: "callback",
          callbackArgs: [
            {
              name: "lat",
              type: "number"
            },
            {
              name: "lng",
              type: "number"
            }
          ]
        },
        mapClickEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the map is clicked. Payload: { latitude, longitude }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "latitude",
              type: "number",
              required: true
            },
            {
              name: "longitude",
              type: "number",
              required: true
            }
          ]
        },
        markerClickEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when a marker is clicked. Payload: full MapMarkerData spread.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "lat",
              type: "number",
              required: true
            },
            {
              name: "lng",
              type: "number",
              required: true
            },
            {
              name: "label",
              type: "string",
              required: false
            },
            {
              name: "category",
              type: "string",
              required: false
            }
          ]
        },
        showClickedPin: {
          types: [
            "boolean"
          ],
          description: "Whether to show a pin at the clicked location",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        showAttribution: {
          types: [
            "boolean"
          ],
          description: "Show attribution (default true)",
          default: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "range-slider": {
      type: "range-slider",
      category: "component",
      sourceFile: "components/core/atoms/RangeSlider.tsx",
      tier: "atoms",
      family: "core",
      description: "RangeSlider component",
      suggestedFor: [
        "range",
        "slider",
        "range slider"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        min: {
          types: [
            "number"
          ],
          description: "Minimum value",
          default: 0
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value",
          default: 100
        },
        value: {
          types: [
            "number"
          ],
          description: "Current value",
          default: 0
        },
        step: {
          types: [
            "number"
          ],
          description: "Step increment",
          default: 1
        },
        showTooltip: {
          types: [
            "boolean"
          ],
          description: "Show tooltip with current value on drag",
          default: false
        },
        showTicks: {
          types: [
            "boolean"
          ],
          description: "Show tick marks at step intervals",
          default: false
        },
        buffered: {
          types: [
            "number"
          ],
          description: "Buffered range (0-100), for media seek bars"
        },
        size: {
          types: [
            "string"
          ],
          description: "Slider size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for value changes",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct onChange callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        formatValue: {
          types: [
            "function"
          ],
          description: "Format function for tooltip display",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ],
          renderCallback: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "status-dot": {
      type: "status-dot",
      category: "display",
      sourceFile: "components/core/atoms/StatusDot.tsx",
      tier: "atoms",
      family: "core",
      description: "StatusDot component",
      suggestedFor: [
        "status",
        "dot",
        "status dot"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        status: {
          types: [
            "string"
          ],
          description: "Status determines the dot color",
          enumValues: [
            "online",
            "offline",
            "away",
            "busy",
            "warning",
            "critical"
          ],
          default: "offline"
        },
        pulse: {
          types: [
            "boolean"
          ],
          description: "Enable pulse animation for active/critical states",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Dot size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label (rendered as sr-only text)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "trend-indicator": {
      type: "trend-indicator",
      category: "component",
      sourceFile: "components/core/atoms/TrendIndicator.tsx",
      tier: "atoms",
      family: "core",
      description: "TrendIndicator component",
      suggestedFor: [
        "trend",
        "indicator",
        "trend indicator"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        value: {
          types: [
            "number"
          ],
          description: "Numeric value to display (e.g., 12.5 for +12.5%)"
        },
        direction: {
          types: [
            "string"
          ],
          description: "Override automatic direction detection (positive=up, negative=down, zero=flat)",
          enumValues: [
            "up",
            "down",
            "flat"
          ]
        },
        showValue: {
          types: [
            "boolean"
          ],
          description: "Show the formatted value text next to the arrow",
          default: true
        },
        invert: {
          types: [
            "boolean"
          ],
          description: "Invert color logic (for metrics where down is good, e.g., error rate, bounce rate)",
          default: false
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label override"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size of the indicator",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    lightbox: {
      type: "lightbox",
      category: "component",
      sourceFile: "components/core/molecules/Lightbox.tsx",
      tier: "molecules",
      family: "core",
      description: "Lightbox component",
      suggestedFor: [
        "lightbox"
      ],
      typicalSize: "medium",
      propsSchema: {
        images: {
          types: [
            "array"
          ],
          description: "Array of images to display",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              src: {
                types: [
                  "asset"
                ]
              },
              alt: {
                types: [
                  "string"
                ]
              },
              caption: {
                types: [
                  "string"
                ]
              }
            }
          },
          default: []
        },
        currentIndex: {
          types: [
            "number"
          ],
          description: "Current image index",
          default: 0
        },
        isOpen: {
          types: [
            "boolean"
          ],
          description: "Whether the lightbox is open",
          default: false
        },
        showCounter: {
          types: [
            "boolean"
          ],
          description: 'Show image counter (e.g., "3 of 12")',
          default: true
        },
        closeAction: {
          types: [
            "string"
          ],
          description: "Declarative close event name"
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Direct onClose callback",
          kind: "callback",
          callbackArgs: []
        },
        onIndexChange: {
          types: [
            "function"
          ],
          description: "Direct onIndexChange callback",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "number-stepper": {
      type: "number-stepper",
      category: "component",
      sourceFile: "components/core/molecules/NumberStepper.tsx",
      tier: "molecules",
      family: "core",
      description: "NumberStepper component",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "number",
        "stepper",
        "number stepper"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Current value",
          default: 0
        },
        min: {
          types: [
            "number"
          ],
          description: "Minimum value"
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value"
        },
        step: {
          types: [
            "number"
          ],
          description: "Step increment",
          default: 1
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct onChange callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for value changes",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "star-rating": {
      type: "star-rating",
      category: "component",
      sourceFile: "components/core/molecules/StarRating.tsx",
      tier: "molecules",
      family: "core",
      description: "StarRating component",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "star",
        "rating",
        "star rating"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "number"
          ],
          description: "Current rating value",
          default: 0
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum number of stars",
          default: 5
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "Read-only display mode",
          default: false
        },
        precision: {
          types: [
            "string"
          ],
          description: "Full or half-star precision",
          enumValues: [
            "full",
            "half"
          ],
          default: "full"
        },
        size: {
          types: [
            "string"
          ],
          description: "Star size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for rating changes",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct onChange callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "upload-drop-zone": {
      type: "upload-drop-zone",
      category: "component",
      sourceFile: "components/core/molecules/UploadDropZone.tsx",
      tier: "molecules",
      family: "core",
      description: "UploadDropZone component",
      suggestedFor: [
        "upload",
        "drop",
        "zone",
        "upload drop zone"
      ],
      typicalSize: "medium",
      propsSchema: {
        accept: {
          types: [
            "string"
          ],
          description: 'Accepted MIME types (e.g., "image/*", "application/pdf")'
        },
        maxSize: {
          types: [
            "number"
          ],
          description: "Max file size in bytes"
        },
        maxFiles: {
          types: [
            "number"
          ],
          description: "Max number of files",
          default: 1
        },
        label: {
          types: [
            "string"
          ],
          description: "Label text"
        },
        description: {
          types: [
            "string"
          ],
          description: "Description text below label"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon name or component"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        readAs: {
          types: [
            "string"
          ],
          description: "How the file is read into `content`: a base64 data URL (default) or its UTF-8 text",
          enumValues: [
            "dataUrl",
            "text"
          ],
          default: "dataUrl"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name for file selection",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "files",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    size: {
                      types: [
                        "number"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    content: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "name",
                    "size",
                    "type",
                    "content"
                  ]
                }
              }
            }
          ]
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        onFiles: {
          types: [
            "function"
          ],
          description: "Direct onFiles callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "files",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ]
                }
              }
            }
          ],
          nonEmittable: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "animated-counter": {
      type: "animated-counter",
      category: "component",
      sourceFile: "components/core/molecules/AnimatedCounter.tsx",
      tier: "molecules",
      family: "core",
      description: "AnimatedCounter component",
      suggestedFor: [
        "animated",
        "counter",
        "animated counter"
      ],
      typicalSize: "small",
      propsSchema: {
        value: {
          types: [
            "string",
            "number"
          ],
          description: 'Target value. Strings may carry display affixes (e.g. "500+", "99.9%", "$1.2M").',
          required: true
        },
        label: {
          types: [
            "string"
          ],
          description: "Label displayed below the number"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Animation duration in ms",
          default: 1500
        },
        prefix: {
          types: [
            "string"
          ],
          description: "Text displayed before the number"
        },
        suffix: {
          types: [
            "string"
          ],
          description: "Text displayed after the number"
        },
        format: {
          types: [
            "string"
          ],
          description: `"number" (locale grouping), "currency" (locale currency), "percent" (rounded %). Unset keeps the value's own decimals.`
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "confetti-effect": {
      type: "confetti-effect",
      category: "component",
      sourceFile: "components/core/atoms/ConfettiEffect.tsx",
      tier: "atoms",
      family: "core",
      description: "ConfettiEffect component",
      suggestedFor: [
        "confetti",
        "effect",
        "confetti effect"
      ],
      typicalSize: "small",
      propsSchema: {
        trigger: {
          types: [
            "boolean"
          ],
          description: "When this changes from false to true, a burst of particles is spawned",
          required: true
        },
        duration: {
          types: [
            "number"
          ],
          description: "How long the animation lasts in milliseconds",
          default: 2e3
        },
        particleCount: {
          types: [
            "number"
          ],
          description: "Number of particles to spawn",
          default: 30
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "infinite-scroll-sentinel": {
      type: "infinite-scroll-sentinel",
      category: "component",
      sourceFile: "components/core/atoms/InfiniteScrollSentinel.tsx",
      tier: "atoms",
      family: "core",
      description: "InfiniteScrollSentinel component",
      suggestedFor: [
        "infinite",
        "scroll",
        "sentinel",
        "infinite scroll sentinel"
      ],
      typicalSize: "small",
      propsSchema: {
        loadMoreEvent: {
          types: [
            "string"
          ],
          description: "Event name to emit when load-more is triggered (emitted as UI:{loadMoreEvent})",
          required: true,
          kind: "event"
        },
        loadMorePayload: {
          types: [
            "object"
          ],
          description: "Optional payload to include with the load-more event",
          payloadFor: "loadMoreEvent",
          eventPayloadBrand: true
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Whether a load operation is currently in progress",
          default: false
        },
        hasMore: {
          types: [
            "boolean"
          ],
          description: "Whether there are more items to load",
          default: true
        },
        threshold: {
          types: [
            "string"
          ],
          description: "IntersectionObserver rootMargin for early trigger",
          default: "200px"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "typewriter-text": {
      type: "typewriter-text",
      category: "component",
      sourceFile: "components/core/atoms/TypewriterText.tsx",
      tier: "atoms",
      family: "core",
      description: "TypewriterText component",
      suggestedFor: [
        "typewriter",
        "text",
        "typewriter text"
      ],
      typicalSize: "small",
      propsSchema: {
        text: {
          types: [
            "string"
          ],
          description: "The full text to reveal",
          required: true
        },
        speed: {
          types: [
            "number"
          ],
          description: "Milliseconds per character",
          default: 40
        },
        startDelay: {
          types: [
            "number"
          ],
          description: "Delay before typing starts (ms)",
          default: 0
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        onComplete: {
          types: [
            "function"
          ],
          description: "Called when the entire text has been revealed",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    carousel: {
      type: "carousel",
      category: "component",
      sourceFile: "components/core/molecules/Carousel.tsx",
      tier: "molecules",
      family: "core",
      description: "Carousel component",
      suggestedFor: [
        "carousel"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Array of items to display as slides",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              image: {
                types: [
                  "asset"
                ]
              }
            }
          }
        },
        renderItem: {
          types: [
            "function"
          ],
          description: "Render function for each slide. In .lolo: renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  image: {
                    types: [
                      "asset"
                    ]
                  }
                }
              }
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        children: {
          types: [
            "function"
          ],
          description: "Children-as-function fallback for renderItem (compiler compatibility)",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  image: {
                    types: [
                      "asset"
                    ]
                  }
                }
              }
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        autoPlay: {
          types: [
            "boolean"
          ],
          description: "Enable auto-play rotation",
          default: false
        },
        autoPlayInterval: {
          types: [
            "number"
          ],
          description: "Auto-play interval in milliseconds (default: 5000)",
          default: 5e3
        },
        showDots: {
          types: [
            "boolean"
          ],
          description: "Show dot indicators",
          default: true
        },
        showArrows: {
          types: [
            "boolean"
          ],
          description: "Show prev/next arrow buttons",
          default: true
        },
        loop: {
          types: [
            "boolean"
          ],
          description: "Enable infinite loop",
          default: false
        },
        slideChangeEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name for slide change",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            }
          ]
        },
        slideChangePayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the slide change event",
          payloadFor: "slideChangeEvent",
          mapValue: {
            types: [
              "object"
            ],
            jsonValueBrand: true
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "pull-to-refresh": {
      type: "pull-to-refresh",
      category: "component",
      sourceFile: "components/core/molecules/PullToRefresh.tsx",
      tier: "molecules",
      family: "core",
      description: "PullToRefresh component",
      suggestedFor: [
        "pull",
        "refresh",
        "pull to refresh"
      ],
      typicalSize: "medium",
      propsSchema: {
        refreshEvent: {
          types: [
            "string"
          ],
          description: "Event name to emit on refresh (emitted as UI:{refreshEvent})",
          required: true,
          kind: "event"
        },
        refreshPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the refresh event",
          payloadFor: "refreshEvent",
          eventPayloadBrand: true
        },
        threshold: {
          types: [
            "number"
          ],
          description: "Pull distance threshold to trigger refresh in px (default: 60)",
          default: 60
        },
        children: {
          types: [
            "node"
          ],
          description: "Content to wrap",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "sortable-list": {
      type: "sortable-list",
      category: "display",
      sourceFile: "components/core/molecules/SortableList.tsx",
      tier: "molecules",
      family: "core",
      description: "SortableList \u2014 a drag-and-drop reorderable list of items.",
      capabilities: "checklist, task list, to-do list, priority list, ranked list, drag-to-reorder queue",
      suggestedFor: [
        "sortable",
        "list",
        "sortable list"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "object",
            "array"
          ],
          description: "items prop",
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        renderItem: {
          types: [
            "function"
          ],
          description: "Render function for each item. In .lolo: renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.",
          required: true,
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "reorderEvent prop",
          required: true,
          kind: "event-ref",
          entityRowField: "item",
          emitPayloadSchema: [
            {
              name: "fromIndex",
              type: "number",
              required: true
            },
            {
              name: "toIndex",
              type: "number",
              required: true
            },
            {
              name: "item",
              type: "@entity",
              required: true
            }
          ]
        },
        reorderPayload: {
          types: [
            "object"
          ],
          description: "reorderPayload prop",
          payloadFor: "reorderEvent",
          eventPayloadBrand: true
        },
        dragHandlePosition: {
          types: [
            "string"
          ],
          description: "dragHandlePosition prop",
          enumValues: [
            "left",
            "right"
          ],
          default: "left"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "swipeable-row": {
      type: "swipeable-row",
      category: "component",
      sourceFile: "components/core/molecules/SwipeableRow.tsx",
      tier: "molecules",
      family: "core",
      description: "SwipeableRow component",
      suggestedFor: [
        "swipeable",
        "row",
        "swipeable row"
      ],
      typicalSize: "medium",
      propsSchema: {
        leftActions: {
          types: [
            "array"
          ],
          description: "leftActions prop",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              eventPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "event"
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        rightActions: {
          types: [
            "array"
          ],
          description: "rightActions prop",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "danger",
                  "ghost"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              eventPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "event"
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        threshold: {
          types: [
            "number"
          ],
          description: "threshold prop",
          default: 80
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        itemData: {
          types: [
            "object"
          ],
          description: "itemData prop",
          kind: "entity",
          cardinality: "record"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "stat-display": {
      type: "stat-display",
      category: "display",
      sourceFile: "components/core/molecules/StatDisplay.tsx",
      tier: "molecules",
      family: "core",
      description: "StatDisplay component",
      suggestedFor: [
        "stat",
        "display",
        "stat display"
      ],
      typicalSize: "medium",
      propsSchema: {
        label: {
          types: [
            "string"
          ],
          description: 'Display label (e.g., "Total", "Remaining")',
          required: true
        },
        value: {
          types: [
            "number",
            "string"
          ],
          description: "Primary value (number or formatted string)",
          required: true
        },
        max: {
          types: [
            "number"
          ],
          description: 'Optional denominator. >0 renders "value / max"; 0 or omitted hides the divider.'
        },
        target: {
          types: [
            "number"
          ],
          description: "Optional progress target. >0 renders a progress bar at value/target."
        },
        trend: {
          types: [
            "number"
          ],
          description: "Signed delta vs previous period. >0 renders \u2191 green, <0 renders \u2193 red, 0 hides. Polarity reversal via `trendPolarity`."
        },
        trendPolarity: {
          types: [
            "string"
          ],
          description: 'Controls whether `trend > 0` is "good" (default) or "bad". `lower-is-better` inverts the color logic.',
          enumValues: [
            "higher-is-better",
            "lower-is-better"
          ],
          default: "higher-is-better"
        },
        trendFormat: {
          types: [
            "string"
          ],
          description: "When `'percent'`, renders trend with a `%` suffix (e.g. `\u2193 3.3%`); default is `'absolute'`.",
          enumValues: [
            "absolute",
            "percent"
          ],
          default: "absolute"
        },
        sparklineData: {
          types: [
            "array"
          ],
          description: "Optional inline sparkline data appended to the trend row. Length < 2 renders nothing.",
          items: {
            types: [
              "number"
            ]
          }
        },
        clickEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted as `UI:{clickEvent}` with `{ metricLabel }` when the card is clicked."
        },
        prefix: {
          types: [
            "string"
          ],
          description: 'Prefix prepended to the formatted value (e.g. "\u2248 ").'
        },
        suffix: {
          types: [
            "string"
          ],
          description: 'Suffix appended to the formatted value (e.g. " /mo", " ms").'
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon name or component"
        },
        iconBg: {
          types: [
            "string"
          ],
          description: "Icon background color class",
          default: "bg-muted"
        },
        iconColor: {
          types: [
            "string"
          ],
          description: "Icon color class",
          default: "text-foreground"
        },
        format: {
          types: [
            "string"
          ],
          description: 'Display format: "number", "currency", "percent"'
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Color variant",
          enumValues: [
            "default",
            "primary",
            "success",
            "warning",
            "error",
            "info"
          ],
          default: "default"
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact mode (inline, no card wrapper)",
          default: false
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment.",
          enumValues: [
            "elevated",
            "flat",
            "progress-backed",
            "gauge",
            "sparkline"
          ],
          default: "elevated"
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: {
            variant: "stats",
            columns: 1
          },
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "section-header": {
      type: "section-header",
      category: "header",
      sourceFile: "components/core/atoms/SectionHeader.tsx",
      tier: "atoms",
      family: "core",
      description: "SectionHeader component",
      suggestedFor: [
        "section",
        "header",
        "section header"
      ],
      typicalSize: "small",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Section title text",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Optional subtitle text"
        },
        align: {
          types: [
            "string"
          ],
          description: "Text alignment",
          enumValues: [
            "center",
            "left",
            "right"
          ],
          default: "center"
        },
        level: {
          types: [
            "number"
          ],
          description: "Heading level (1, 2, or 3)",
          numericEnumValues: [
            1,
            2,
            3
          ],
          default: 2
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "feature-card": {
      type: "feature-card",
      category: "display",
      sourceFile: "components/core/molecules/FeatureCard.tsx",
      tier: "molecules",
      family: "core",
      description: "FeatureCard \u2014 an icon-led card pairing a title and description to call out a single product feature.",
      capabilities: "feature highlight, benefit callout, product capability card, feature-grid tile",
      suggestedFor: [
        "feature",
        "card",
        "feature card"
      ],
      typicalSize: "medium",
      propsSchema: {
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "icon prop"
        },
        title: {
          types: [
            "string"
          ],
          description: "Feature title",
          required: true
        },
        description: {
          types: [
            "string"
          ],
          description: "Feature description",
          required: true
        },
        href: {
          types: [
            "string"
          ],
          description: "Optional link URL"
        },
        linkLabel: {
          types: [
            "string"
          ],
          description: "Label for the link button"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Card visual variant",
          enumValues: [
            "default",
            "bordered",
            "elevated",
            "interactive"
          ],
          default: "bordered"
        },
        size: {
          types: [
            "string"
          ],
          description: "Card size affecting icon and spacing",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "feature-grid": {
      type: "feature-grid",
      category: "display",
      sourceFile: "components/core/molecules/FeatureGrid.tsx",
      tier: "molecules",
      family: "core",
      description: "FeatureGrid component",
      suggestedFor: [
        "feature",
        "grid",
        "feature grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Array of feature card configurations",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              linkLabel: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "bordered",
                  "elevated",
                  "interactive"
                ]
              },
              size: {
                types: [
                  "string"
                ],
                enumValues: [
                  "sm",
                  "md",
                  "lg"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "title",
              "description"
            ]
          }
        },
        columns: {
          types: [
            "number"
          ],
          description: "Number of grid columns",
          numericEnumValues: [
            2,
            3,
            4,
            6
          ],
          default: 3
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between grid items",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "social-proof": {
      type: "social-proof",
      category: "component",
      sourceFile: "components/core/molecules/SocialProof.tsx",
      tier: "molecules",
      family: "core",
      description: "SocialProof component",
      suggestedFor: [
        "social",
        "proof",
        "social proof"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "List of social proof items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              logo: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              quote: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name"
            ]
          }
        },
        variant: {
          types: [
            "string"
          ],
          description: "Display variant",
          enumValues: [
            "logos",
            "quotes",
            "badges"
          ],
          default: "logos"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "animated-graphic": {
      type: "animated-graphic",
      category: "component",
      sourceFile: "components/core/atoms/AnimatedGraphic.tsx",
      tier: "atoms",
      family: "core",
      description: "AnimatedGraphic component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "animated",
        "graphic",
        "animated graphic"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        src: {
          types: [
            "asset"
          ],
          description: "URL to an SVG file. Fetched and inlined to enable stroke/fill animations."
        },
        svgContent: {
          types: [
            "string"
          ],
          description: "Inline SVG string. Takes precedence over src if both provided."
        },
        animation: {
          types: [
            "string"
          ],
          description: "Animation type applied to SVG paths/shapes",
          enumValues: [
            "draw",
            "fill",
            "pulse",
            "morph"
          ],
          default: "draw"
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Whether to run the animation (default: false). Controlled externally or via AnimatedReveal.",
          default: false
        },
        duration: {
          types: [
            "number"
          ],
          description: "Animation duration in ms (default: 1200)",
          default: 1200
        },
        delay: {
          types: [
            "number"
          ],
          description: "Delay before animation starts in ms (default: 0)",
          default: 0
        },
        easing: {
          types: [
            "string"
          ],
          description: "Easing function (default: cubic-bezier(0.16, 1, 0.3, 1))",
          default: "cubic-bezier(0.16, 1, 0.3, 1)"
        },
        width: {
          types: [
            "string",
            "number"
          ],
          description: "Width of the graphic container"
        },
        height: {
          types: [
            "string",
            "number"
          ],
          description: "Height of the graphic container"
        },
        strokeColor: {
          types: [
            "string"
          ],
          description: "Stroke color override for SVG paths"
        },
        fillColor: {
          types: [
            "string"
          ],
          description: "Fill color for the 'fill' animation end state"
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alt text for accessibility"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "animated-reveal": {
      type: "animated-reveal",
      category: "component",
      sourceFile: "components/core/atoms/AnimatedReveal.tsx",
      tier: "atoms",
      family: "core",
      description: "AnimatedReveal component",
      suggestedFor: [
        "animated",
        "reveal",
        "animated reveal"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        trigger: {
          types: [
            "string"
          ],
          description: "What triggers the animation",
          enumValues: [
            "scroll",
            "hover",
            "manual"
          ],
          default: "scroll"
        },
        animation: {
          types: [
            "string"
          ],
          description: "Built-in animation preset",
          enumValues: [
            "fade-up",
            "fade-down",
            "fade-in",
            "fade-left",
            "fade-right",
            "scale",
            "scale-up",
            "none"
          ],
          default: "fade-up"
        },
        duration: {
          types: [
            "number"
          ],
          description: "Animation duration in ms (default: 600)",
          default: 600
        },
        delay: {
          types: [
            "number"
          ],
          description: "Delay before animation starts in ms (default: 0)",
          default: 0
        },
        threshold: {
          types: [
            "number"
          ],
          description: "How much of the element must be visible before triggering, 0-1 (default: 0.15)",
          default: 0.15
        },
        revealEvent: {
          types: [
            "string"
          ],
          description: "scroll trigger: event emitted once (as UI:{revealEvent}) the first time the element scrolls into view; never under reduced motion",
          kind: "event",
          notification: true
        },
        enterEvent: {
          types: [
            "string"
          ],
          description: "scroll trigger: event emitted (as UI:{enterEvent}) every time the element enters the view",
          kind: "event",
          notification: true
        },
        leaveEvent: {
          types: [
            "string"
          ],
          description: "scroll trigger: event emitted (as UI:{leaveEvent}) every time the element leaves the view after having entered it",
          kind: "event",
          notification: true
        },
        once: {
          types: [
            "boolean"
          ],
          description: "Animate only the first time the element enters the viewport (default: true)",
          default: true
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Manual control: when trigger='manual', set this to true to animate"
        },
        easing: {
          types: [
            "string"
          ],
          description: "Easing function (default: cubic-bezier(0.16, 1, 0.3, 1))",
          default: "cubic-bezier(0.16, 1, 0.3, 1)"
        },
        children: {
          types: [
            "node",
            "function"
          ],
          description: "Children: ReactNode or render function receiving animated state",
          required: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "pattern-tile": {
      type: "pattern-tile",
      category: "game",
      sourceFile: "components/core/atoms/PatternTile.tsx",
      tier: "atoms",
      family: "core",
      description: "PatternTile component",
      suggestedFor: [
        "pattern",
        "tile",
        "pattern tile"
      ],
      typicalSize: "small",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Which geometric pattern to render",
          enumValues: [
            "star8",
            "star6",
            "khatam",
            "star10",
            "star12",
            "rosette-double",
            "rosette-filled",
            "seigaiha",
            "greek-key",
            "celtic-knot",
            "kolam",
            "arch",
            "arabesque-vine",
            "arabesque-net"
          ],
          default: "star8"
        },
        size: {
          types: [
            "number"
          ],
          description: "Tile unit size in SVG units",
          default: 60
        },
        color: {
          types: [
            "string"
          ],
          description: "Stroke color (CSS variable or hex)",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Line thickness",
          default: 0.5
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "edge-decoration": {
      type: "edge-decoration",
      category: "component",
      sourceFile: "components/core/molecules/EdgeDecoration.tsx",
      tier: "molecules",
      family: "core",
      description: "EdgeDecoration component",
      suggestedFor: [
        "edge",
        "decoration",
        "edge decoration"
      ],
      typicalSize: "medium",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Which decorative element",
          enumValues: [
            "arch",
            "vine",
            "lattice"
          ],
          default: "arch"
        },
        side: {
          types: [
            "string"
          ],
          description: "Which side(s) to place the decoration",
          enumValues: [
            "left",
            "right",
            "both"
          ],
          default: "both"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "Overall opacity (default: 0.15)",
          default: 0.15
        },
        color: {
          types: [
            "string"
          ],
          description: "Semantic palette token or a raw CSS color value for the stroke.",
          default: "primary"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width",
          default: 0.5
        },
        width: {
          types: [
            "number"
          ],
          description: "Width of the decoration area as percentage of container (default: 15)",
          default: 15
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "ui-slot-renderer": {
      type: "ui-slot-renderer",
      category: "layout",
      sourceFile: "components/core/organisms/UISlotRenderer.tsx",
      tier: "organisms",
      family: "core",
      description: "UISlotRenderer component",
      suggestedFor: [
        "slot",
        "renderer",
        "u i slot renderer"
      ],
      typicalSize: "large",
      propsSchema: {
        includeHud: {
          types: [
            "boolean"
          ],
          description: "Include HUD slots",
          default: false
        },
        hudMode: {
          types: [
            "string"
          ],
          description: "HUD positioning mode: 'fixed' (default, viewport-relative) or 'inline' (container-relative, uses sticky)",
          enumValues: [
            "fixed",
            "inline"
          ],
          default: "fixed"
        },
        includeFloating: {
          types: [
            "boolean"
          ],
          description: "Include floating slot",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class name for the container"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "Entity name for schema-driven auto-fetch"
        },
        suspense: {
          types: [
            "boolean",
            "object"
          ],
          description: "Enable Suspense boundaries around each slot. When true, each inline slot is wrapped in `<ErrorBoundary><Suspense>` with Skeleton fallbacks. Opt-in \u2014 existing isLoading prop pattern still works.",
          properties: {
            enabled: {
              types: [
                "boolean"
              ]
            },
            slotFallbacks: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "node"
                ]
              }
            }
          },
          propertyRequired: [
            "enabled"
          ]
        },
        pageKey: {
          types: [
            "string"
          ],
          description: "The current page (path or name). When set, the main slot's content animates in on every page change (`PageTransition`); a layout's own content region claims the transition so its chrome stays still."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "trait-frame": {
      type: "trait-frame",
      category: "component",
      sourceFile: "components/core/atoms/TraitFrame.tsx",
      tier: "atoms",
      family: "core",
      description: "TraitFrame \u2014 renders the current frame of a referenced trait.  Resolves the `@trait.X[.slot]` binding at render time by looking up the referenced trait's last `render-ui` payload from the orbital's `UISlotManager`. Re-renders automatically when that trait transitions via the per-trait subscription channel (`subscribeTrait`).  This is the single rendering primitive both runtime paths converge on:  - **Interpreted path** \u2014 the trait-binding resolver in `renderer/trait-binding-resolver.ts` walks pattern trees at render time, substituting `\"@trait.X\"` string children with this component. - **Compiled path** \u2014 the TypeScript shell codegen in `orbital-shell-typescript/src/backend.rs::pattern_to_jsx_with_data` emits this component directly into generated JSX wherever a `@trait.*` string appears in a pattern tree.  Embedding is passive: the referenced trait's state machine runs unchanged; TraitFrame is only a read-only lens on its current frame. Event propagation happens over the shared event bus, not through this component.",
      suggestedFor: [
        "trait",
        "frame",
        "trait frame"
      ],
      typicalSize: "small",
      propsSchema: {
        traitName: {
          types: [
            "string"
          ],
          description: "Name of the trait whose current frame to embed. Must match a trait declared in the current orbital. Compiler validates that the name exists at build time (see `ORB_BINDING_TRAIT_UNKNOWN`).",
          required: true
        },
        fallback: {
          types: [
            "node"
          ],
          description: "Rendered when the referenced trait has not (yet) emitted any render-ui. Use a skeleton, spinner, or message. Defaults to `null` (renders nothing), so an unfulfilled reference drops cleanly out of a parent `children:` array.",
          default: null
        }
      }
    },
    "file-tree": {
      type: "file-tree",
      category: "component",
      sourceFile: "components/core/molecules/FileTree.tsx",
      tier: "molecules",
      family: "core",
      description: "FileTree component",
      suggestedFor: [
        "file",
        "tree",
        "file tree"
      ],
      typicalSize: "medium",
      propsSchema: {
        tree: {
          types: [
            "array"
          ],
          description: "The tree data (pre-nested). Ignored when `items` is provided.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              path: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "file",
                  "dir"
                ]
              },
              children: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "FileTreeNode"
                }
              },
              note: {
                types: [
                  "string"
                ]
              },
              expandable: {
                types: [
                  "boolean"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              language: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "name",
              "path",
              "type"
            ]
          }
        },
        items: {
          types: [
            "array"
          ],
          description: "Flat node list, nested by `parentId` at render time \u2014 takes precedence over `tree` when provided. A `parentId` with no matching row renders that node as a root rather than dropping it.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              parentId: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        selectedPath: {
          types: [
            "string"
          ],
          description: "Currently selected file path"
        },
        selectedId: {
          types: [
            "string"
          ],
          description: 'Currently selected node id, in flat `items` mode: the row highlights and every ancestor auto-expands so the selection is always visible \u2014 the "you are here" a navigation tree needs.'
        },
        look: {
          types: [
            "string"
          ],
          description: "Visual treatment for flat `items` mode: `files` (default) keeps the monospace file-browser look; `nav` renders a sidebar navigation tree \u2014 regular font, document icons, roomier hit areas.",
          enumValues: [
            "files",
            "nav"
          ],
          default: "files"
        },
        onFileSelect: {
          types: [
            "function"
          ],
          description: "Called when a file is clicked",
          kind: "callback",
          callbackArgs: [
            {
              name: "path",
              type: "string"
            }
          ]
        },
        onNodeExpand: {
          types: [
            "function"
          ],
          description: "Called the first time a node marked `expandable` is expanded, with its path, so its children can be loaded.",
          kind: "callback",
          callbackArgs: [
            {
              name: "path",
              type: "string"
            }
          ]
        },
        expandedPaths: {
          types: [
            "array"
          ],
          description: "Paths to show expanded (e.g. to reveal a node): each is expanded when it appears here, loading its children if needed.",
          items: {
            types: [
              "string"
            ]
          }
        },
        onNodeSelect: {
          types: [
            "function"
          ],
          description: "Called when a node is clicked, in flat `items` mode; carries the selected node's id. Presence-gated: binding this prop is what turns on the click-to-select affordance, mirroring Badge's `onRemove`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ],
          default: ""
        },
        onNodeAction: {
          types: [
            "function"
          ],
          description: "Called from the row's hover action button, in flat `items` mode; carries that node's id. Presence-gated: binding it renders the action.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ],
          default: ""
        },
        nodeActionIcon: {
          types: [
            "string"
          ],
          description: "Icon for the row hover action (default: plus)."
        },
        nodeActionLabel: {
          types: [
            "string"
          ],
          description: "Accessible label/tooltip for the row hover action."
        },
        onNodeReorder: {
          types: [
            "function"
          ],
          description: "Called when a row is dropped onto another, in flat `items` mode; carries the moved node's id, its new parent id (`null` for a root drop), and its index within the new parent's children. Presence-gated: binding this prop is what turns on row dragging.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "newParentId",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          default: ""
        },
        className: {
          types: [
            "string"
          ],
          description: "CSS class"
        },
        indent: {
          types: [
            "number"
          ],
          description: "Indent size per level in px (default: 16)",
          default: 16
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "filter-pill": {
      type: "filter-pill",
      category: "filter",
      sourceFile: "components/core/atoms/FilterPill.tsx",
      tier: "atoms",
      family: "core",
      description: "FilterPill component",
      suggestedFor: [
        "filter",
        "pill",
        "filter pill"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "default",
            "primary",
            "secondary",
            "success",
            "warning",
            "danger",
            "info",
            "neutral"
          ],
          default: "default"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        label: {
          types: [
            "string",
            "number"
          ],
          description: "Pill label text (alternative to children for schema-driven rendering)."
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide icon component or canonical kebab-case icon name string"
        },
        onRemove: {
          types: [
            "function"
          ],
          description: "Called when the user clicks the remove (\xD7) button",
          kind: "callback",
          callbackArgs: [],
          default: ""
        },
        removable: {
          types: [
            "boolean"
          ],
          description: "Disable the remove button (renders without \xD7 control)",
          default: true
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler for the body of the pill (filter toggle)",
          kind: "callback",
          callbackArgs: []
        },
        clickEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the pill body is clicked. Payload: { label }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "label",
              type: "string",
              required: true
            }
          ]
        },
        removeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the remove (\xD7) button is clicked. Payload: { label }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "label",
              type: "string",
              required: true
            }
          ],
          default: ""
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "likert-scale": {
      type: "likert-scale",
      category: "component",
      sourceFile: "components/core/molecules/LikertScale.tsx",
      tier: "molecules",
      family: "core",
      description: "LikertScale component",
      suggestedFor: [
        "likert",
        "scale",
        "likert scale"
      ],
      typicalSize: "medium",
      propsSchema: {
        question: {
          types: [
            "string"
          ],
          description: "Optional row prompt above the scale"
        },
        options: {
          types: [
            "array"
          ],
          description: "Scale points (defaults to a translated 5-point agree/disagree set)",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        value: {
          types: [
            "number",
            "string"
          ],
          description: "Selected value (controlled)",
          default: null
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Change callback",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "number"
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus on change. Payload: { value: number | string }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant: classic radios or pill segmented buttons",
          enumValues: [
            "radios",
            "buttons"
          ],
          default: "radios"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "matrix-question": {
      type: "matrix-question",
      category: "component",
      sourceFile: "components/core/molecules/MatrixQuestion.tsx",
      tier: "molecules",
      family: "core",
      description: "MatrixQuestion component",
      suggestedFor: [
        "matrix",
        "question",
        "matrix question"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Optional title rendered above the matrix"
        },
        rows: {
          types: [
            "array"
          ],
          description: "Question rows",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        columns: {
          types: [
            "array"
          ],
          description: "Column definitions; defaults to 5-point Likert",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value",
              "label"
            ]
          }
        },
        values: {
          types: [
            "object"
          ],
          description: "Selected value per row, keyed by rowId",
          mapValue: {
            types: [
              "number",
              "string"
            ]
          }
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Change handler invoked with rowId and selected column value",
          kind: "callback",
          callbackArgs: [
            {
              name: "rowId",
              type: "string"
            },
            {
              name: "value",
              type: "number"
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when a row's value changes. Payload: { rowId, value }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "rowId",
              type: "string",
              required: true
            },
            {
              name: "value",
              type: "number",
              required: true
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable all inputs",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Visual size",
          enumValues: [
            "sm",
            "md"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "qr-scanner": {
      type: "qr-scanner",
      category: "component",
      sourceFile: "components/core/molecules/QrScanner.tsx",
      tier: "molecules",
      family: "core",
      description: "QrScanner component",
      suggestedFor: [
        "scanner",
        "qr scanner"
      ],
      typicalSize: "medium",
      propsSchema: {
        onScan: {
          types: [
            "function"
          ],
          description: "onScan prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "result",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  text: {
                    types: [
                      "string"
                    ]
                  },
                  format: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "text",
                  "format",
                  "timestamp"
                ]
              }
            }
          ]
        },
        scanEvent: {
          types: [
            "string"
          ],
          description: "scanEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "text",
              type: "string",
              required: true
            },
            {
              name: "format",
              type: "string",
              required: true
            },
            {
              name: "timestamp",
              type: "number",
              required: true
            }
          ]
        },
        onError: {
          types: [
            "function"
          ],
          description: "onError prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "error",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        facingMode: {
          types: [
            "string"
          ],
          description: "facingMode prop",
          enumValues: [
            "environment",
            "user"
          ],
          default: "environment"
        },
        paused: {
          types: [
            "boolean"
          ],
          description: "paused prop",
          default: false
        },
        showOverlay: {
          types: [
            "boolean"
          ],
          description: "showOverlay prop",
          default: true
        },
        showCameraControls: {
          types: [
            "boolean"
          ],
          description: "showCameraControls prop",
          default: true
        },
        fallback: {
          types: [
            "node"
          ],
          description: "fallback prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "vote-stack": {
      type: "vote-stack",
      category: "component",
      sourceFile: "components/core/molecules/VoteStack.tsx",
      tier: "molecules",
      family: "core",
      description: "VoteStack component",
      suggestedFor: [
        "vote",
        "stack",
        "vote stack"
      ],
      typicalSize: "medium",
      propsSchema: {
        count: {
          types: [
            "number"
          ],
          description: "Current tally",
          required: true
        },
        userVote: {
          types: [
            "string"
          ],
          description: "Current user's vote (null = no vote cast)",
          enumValues: [
            "up",
            "down",
            "none"
          ],
          default: null
        },
        onVote: {
          types: [
            "function"
          ],
          description: "Toggle handler. Clicking the same arrow clears (emits null).",
          kind: "callback",
          callbackArgs: [
            {
              name: "next",
              type: "string"
            }
          ]
        },
        voteEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched on the bus when a vote is cast. Payload: { next: VoteValue }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "next",
              type: "string",
              required: true,
              enumValues: [
                "up",
                "down",
                "none"
              ]
            }
          ]
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disabled state",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Layout orientation. `vertical` = forum/Q&A column; `horizontal` = compact row.",
          enumValues: [
            "vertical",
            "horizontal"
          ],
          default: "vertical"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        label: {
          types: [
            "string"
          ],
          description: "Accessible label for the group"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "branching-logic-builder": {
      type: "branching-logic-builder",
      category: "display",
      sourceFile: "components/core/molecules/BranchingLogicBuilder.tsx",
      tier: "molecules",
      family: "core",
      description: "BranchingLogicBuilder component",
      suggestedFor: [
        "branching",
        "logic",
        "builder",
        "branching logic builder"
      ],
      typicalSize: "large",
      propsSchema: {
        questions: {
          types: [
            "array",
            "object"
          ],
          description: "questions prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              optionValues: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        rules: {
          types: [
            "array",
            "object"
          ],
          description: "Rules. Accepts either a typed array (direct consumers) or the runtime payload shape from a render-ui binding (`@payload.data`). Narrowed to `[]` internally when the value isn't an array.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              sourceQuestionId: {
                types: [
                  "string"
                ]
              },
              operator: {
                types: [
                  "string"
                ],
                enumValues: [
                  "equals",
                  "not-equals",
                  "contains",
                  "in"
                ]
              },
              value: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              targetQuestionId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "sourceQuestionId",
              "operator",
              "value",
              "targetQuestionId"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        onRulesChange: {
          types: [
            "function"
          ],
          description: "onRulesChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "rules",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    sourceQuestionId: {
                      types: [
                        "string"
                      ]
                    },
                    operator: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "equals",
                        "not-equals",
                        "contains",
                        "in"
                      ]
                    },
                    value: {
                      types: [
                        "string",
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    targetQuestionId: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "id",
                    "sourceQuestionId",
                    "operator",
                    "value",
                    "targetQuestionId"
                  ]
                }
              }
            }
          ]
        },
        rulesChangeEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when rules change. Payload: `{ rules }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "rules",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    sourceQuestionId: {
                      types: [
                        "string"
                      ]
                    },
                    operator: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "equals",
                        "not-equals",
                        "contains",
                        "in"
                      ]
                    },
                    value: {
                      types: [
                        "string",
                        "array"
                      ],
                      items: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    targetQuestionId: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "id",
                    "sourceQuestionId",
                    "operator",
                    "value",
                    "targetQuestionId"
                  ]
                }
              }
            }
          ]
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "readOnly prop",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "reply-tree": {
      type: "reply-tree",
      category: "display",
      sourceFile: "components/core/molecules/ReplyTree.tsx",
      tier: "molecules",
      family: "core",
      description: "ReplyTree component",
      suggestedFor: [
        "reply",
        "tree",
        "reply tree"
      ],
      typicalSize: "large",
      propsSchema: {
        nodes: {
          types: [
            "object",
            "array"
          ],
          description: "nodes prop",
          required: true,
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "authorName",
            "content"
          ],
          properties: {
            authorName: {
              types: [
                "string"
              ]
            },
            content: {
              types: [
                "string"
              ]
            },
            authorAvatarUrl: {
              types: [
                "string"
              ]
            },
            postedAt: {
              types: [
                "string"
              ]
            },
            voteCount: {
              types: [
                "number"
              ]
            },
            userVote: {
              types: [
                "string"
              ],
              enumValues: [
                "up",
                "down",
                "none"
              ]
            },
            collapsed: {
              types: [
                "boolean"
              ]
            },
            replies: {
              types: [
                "object",
                "array"
              ],
              cyclic: true,
              cyclicTypeName: "ReplyNodeRow"
            }
          }
        },
        maxDepth: {
          types: [
            "number"
          ],
          description: "maxDepth prop",
          default: 6
        },
        onVote: {
          types: [
            "function"
          ],
          description: "onVote prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "nodeId",
              type: "string"
            },
            {
              name: "vote",
              type: "string"
            }
          ]
        },
        onReply: {
          types: [
            "function"
          ],
          description: "onReply prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "parentNodeId",
              type: "string"
            }
          ]
        },
        onFlag: {
          types: [
            "function"
          ],
          description: "onFlag prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "nodeId",
              type: "string"
            }
          ]
        },
        onContinueThread: {
          types: [
            "function"
          ],
          description: "onContinueThread prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "nodeId",
              type: "string"
            }
          ]
        },
        voteEvent: {
          types: [
            "string"
          ],
          description: "voteEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "nodeId",
              type: "string",
              required: true
            },
            {
              name: "vote",
              type: "string",
              required: true,
              enumValues: [
                "up",
                "down",
                "none"
              ]
            }
          ]
        },
        replyEvent: {
          types: [
            "string"
          ],
          description: "replyEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "parentNodeId",
              type: "string",
              required: true
            },
            {
              name: "content",
              type: "string",
              required: true
            }
          ]
        },
        flagEvent: {
          types: [
            "string"
          ],
          description: "flagEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "nodeId",
              type: "string",
              required: true
            }
          ]
        },
        continueThreadEvent: {
          types: [
            "string"
          ],
          description: "continueThreadEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "nodeId",
              type: "string",
              required: true
            }
          ]
        },
        showActions: {
          types: [
            "boolean"
          ],
          description: "showActions prop",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "version-diff": {
      type: "version-diff",
      category: "display",
      sourceFile: "components/core/molecules/VersionDiff.tsx",
      tier: "molecules",
      family: "core",
      description: "A line diff between two versions \u2014 two whole texts, or a pre-computed diff such as one commit.",
      capabilities: "version diff, compare revisions, what changed, before and after, code diff, commit diff, revision comparison",
      suggestedFor: [
        "version",
        "diff",
        "version diff"
      ],
      typicalSize: "large",
      propsSchema: {
        revisions: {
          types: [
            "array",
            "object"
          ],
          description: "All available revisions (at least 2). Accepts either a typed array (direct consumers) or the runtime payload shape from a render-ui binding (`@payload.revisions`). Narrowed to `[]` internally when the value isn't an array.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              author: {
                types: [
                  "string"
                ]
              },
              timestamp: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "label",
              "content"
            ]
          }
        },
        hunks: {
          types: [
            "array"
          ],
          description: "A pre-computed diff to render instead of diffing `revisions` (no revision pickers).",
          items: {
            types: [
              "object"
            ],
            properties: {
              header: {
                types: [
                  "string"
                ]
              },
              lines: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    type: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "added",
                        "removed",
                        "unchanged",
                        "context"
                      ]
                    },
                    beforeLineNumber: {
                      types: [
                        "number"
                      ]
                    },
                    afterLineNumber: {
                      types: [
                        "number"
                      ]
                    },
                    content: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "type",
                    "content"
                  ]
                }
              }
            },
            required: [
              "header",
              "lines"
            ]
          }
        },
        beforeId: {
          types: [
            "string"
          ],
          description: 'Currently selected "before" revision id.'
        },
        afterId: {
          types: [
            "string"
          ],
          description: 'Currently selected "after" revision id.'
        },
        view: {
          types: [
            "string"
          ],
          description: "Display mode to start in; the toggle switches it.",
          enumValues: [
            "side-by-side",
            "inline"
          ],
          default: "side-by-side"
        },
        onSelectBefore: {
          types: [
            "function"
          ],
          description: 'Called when the user picks a different "before" revision.',
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onSelectAfter: {
          types: [
            "function"
          ],
          description: 'Called when the user picks a different "after" revision.',
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onRevert: {
          types: [
            "function"
          ],
          description: 'Called when the user clicks the revert button (passes the "before" id).',
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        selectBeforeEvent: {
          types: [
            "string"
          ],
          description: 'Event name dispatched via event bus when the "before" revision changes. Payload: { id }.',
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        selectAfterEvent: {
          types: [
            "string"
          ],
          description: 'Event name dispatched via event bus when the "after" revision changes. Payload: { id }.',
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        revertEvent: {
          types: [
            "string"
          ],
          description: "Event name dispatched via event bus when the user clicks revert. Payload: { id }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        language: {
          types: [
            "string"
          ],
          description: "Language label (informational)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "option-constraint-group": {
      type: "option-constraint-group",
      category: "component",
      sourceFile: "components/core/molecules/OptionConstraintGroup.tsx",
      tier: "molecules",
      family: "core",
      description: 'OptionConstraintGroup Molecule Component  Generic constrained option selector (radio for "single", checkbox for "multi") with required/optional/multi constraints. Renders radio inputs for `single` constraint and checkboxes for `multi`. Reports validation errors inline when the current selection violates the constraint.  **Atomic Design**: Composed using Typography atom and native form controls.',
      suggestedFor: [
        "option",
        "constraint",
        "group",
        "option constraint group"
      ],
      typicalSize: "medium",
      propsSchema: {
        groupId: {
          types: [
            "string"
          ],
          description: "groupId prop",
          required: true
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop",
          required: true
        },
        description: {
          types: [
            "string"
          ],
          description: "description prop"
        },
        options: {
          types: [
            "array"
          ],
          description: "options prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              priceDelta: {
                types: [
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              outOfStock: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        constraint: {
          types: [
            "object"
          ],
          description: "Selection constraint. Optional \u2014 defaults to single-select (the generic factory can't author a discriminated-union value; an author overrides it).",
          variants: [
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "single"
                  ]
                },
                required: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "type"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "multi"
                  ]
                },
                min: {
                  types: [
                    "number"
                  ]
                },
                max: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type"
              ]
            }
          ],
          discriminator: "type",
          default: {
            type: "single"
          }
        },
        selected: {
          types: [
            "array"
          ],
          description: "selected prop",
          items: {
            types: [
              "string"
            ]
          },
          default: []
        },
        onChange: {
          types: [
            "function"
          ],
          description: "onChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "selected",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "changeEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selected",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "positioned-canvas": {
      type: "positioned-canvas",
      category: "game",
      sourceFile: "components/core/molecules/PositionedCanvas.tsx",
      tier: "molecules",
      family: "core",
      description: "PositionedCanvas component",
      suggestedFor: [
        "positioned",
        "canvas",
        "positioned canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "object",
            "array"
          ],
          description: "Items to render. The molecule narrows non-array values to `[]` and reads each row's `id` / `x` / `y` / `label` fields at render time.",
          required: true,
          kind: "entity",
          cardinality: "collection",
          requiredFields: [
            "label",
            "x",
            "y",
            "capacity"
          ],
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            capacity: {
              types: [
                "number"
              ]
            },
            partySize: {
              types: [
                "number"
              ]
            },
            serverName: {
              types: [
                "string"
              ]
            },
            status: {
              types: [
                "string"
              ],
              enumValues: [
                "empty",
                "seated",
                "ordered",
                "awaiting-bill",
                "cleaning"
              ]
            },
            shape: {
              types: [
                "string"
              ],
              enumValues: [
                "round",
                "rectangle",
                "square"
              ]
            }
          }
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 800
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 600
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "selectedId prop",
          default: null
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "editable prop",
          default: false
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "onSelect prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onMove: {
          types: [
            "function"
          ],
          description: "onMove prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "x",
              type: "number"
            },
            {
              name: "y",
              type: "number"
            }
          ]
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "selectEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        moveEvent: {
          types: [
            "string"
          ],
          description: "moveEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "choice-button": {
      type: "choice-button",
      category: "game",
      sourceFile: "components/game/atoms/ChoiceButton.tsx",
      tier: "atoms",
      family: "game",
      description: "ChoiceButton component",
      suggestedFor: [
        "choice",
        "button",
        "choice button"
      ],
      typicalSize: "small",
      propsSchema: {
        text: {
          types: [
            "string"
          ],
          description: "Choice text content",
          required: true
        },
        index: {
          types: [
            "number"
          ],
          description: "Choice index number (displayed as prefix)"
        },
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon displayed before the text"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the choice is disabled",
          default: false
        },
        selected: {
          types: [
            "boolean"
          ],
          description: "Whether the choice is currently selected",
          default: false
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: []
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        payload: {
          types: [
            "object"
          ],
          description: "Semantic payload emitted with the action (e.g. `{ nextId, choiceIndex }` for a branching choice).",
          mapValue: {
            types: [
              "string",
              "number",
              "boolean"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "control-button": {
      type: "control-button",
      category: "game",
      sourceFile: "components/game/atoms/ControlButton.tsx",
      tier: "atoms",
      family: "game",
      description: "ControlButton component",
      suggestedFor: [
        "control",
        "button",
        "control button"
      ],
      typicalSize: "small",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Button label text"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon component or emoji"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          default: "md"
        },
        shape: {
          types: [
            "string"
          ],
          description: "Shape variant",
          default: "circle"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          default: "secondary"
        },
        onPress: {
          types: [
            "function"
          ],
          description: "Called when button is pressed",
          kind: "callback",
          callbackArgs: []
        },
        onRelease: {
          types: [
            "function"
          ],
          description: "Called when button is released",
          kind: "callback",
          callbackArgs: []
        },
        pressEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name emitted on press via useEventBus",
          kind: "event"
        },
        releaseEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name emitted on release via useEventBus",
          kind: "event"
        },
        pressed: {
          types: [
            "boolean"
          ],
          description: "Whether the button is currently pressed"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the button is disabled"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dialogue-bubble": {
      type: "dialogue-bubble",
      category: "game",
      sourceFile: "components/game/atoms/DialogueBubble.tsx",
      tier: "atoms",
      family: "game",
      description: "DialogueBubble component",
      suggestedFor: [
        "dialogue",
        "bubble",
        "dialogue bubble"
      ],
      typicalSize: "small",
      propsSchema: {
        speaker: {
          types: [
            "string"
          ],
          description: "Speaker name displayed at the top"
        },
        text: {
          types: [
            "string"
          ],
          description: "Dialogue text content",
          required: true
        },
        portrait: {
          types: [
            "object"
          ],
          description: "Speaker portrait asset",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Position of the bubble on screen",
          enumValues: [
            "top",
            "bottom"
          ],
          default: "bottom"
        },
        mood: {
          types: [
            "string"
          ],
          description: "Speaker mood \u2014 drives a portrait-ring accent; LOLO sets this per dialogue node/transition.",
          enumValues: [
            "neutral",
            "happy",
            "concerned",
            "angry"
          ],
          default: "neutral"
        },
        revealedChars: {
          types: [
            "number"
          ],
          description: "Number of `text` characters to reveal (typewriter effect). Omit to show the full string \u2014 LOLO drives this from `@entity.revealedChars` via a tick, incrementing it until it reaches `text.length`."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "health-bar": {
      type: "health-bar",
      category: "game",
      sourceFile: "components/game/atoms/HealthBar.tsx",
      tier: "atoms",
      family: "game",
      description: "HealthBar component",
      suggestedFor: [
        "health",
        "bar",
        "health bar"
      ],
      typicalSize: "small",
      propsSchema: {
        current: {
          types: [
            "number"
          ],
          description: "Current value",
          required: true,
          default: 3
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value",
          required: true,
          default: 5
        },
        format: {
          types: [
            "string"
          ],
          description: "Display format. - `hearts` / `bar` / `numeric` \u2014 health display - `progress` \u2014 XP / generic progress bar with optional level badge and value label",
          enumValues: [
            "hearts",
            "bar",
            "numeric",
            "progress"
          ],
          default: "hearts"
        },
        level: {
          types: [
            "number"
          ],
          description: 'Current level \u2014 shown as "Lv.N" badge when format="progress"'
        },
        showLabel: {
          types: [
            "boolean"
          ],
          description: 'Show value label below bar when format="progress"',
          default: true
        },
        labelSuffix: {
          types: [
            "string"
          ],
          description: 'Label suffix shown next to the value (e.g. "XP") when showLabel=true'
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "Animation on change",
          default: true
        },
        frameAsset: {
          types: [
            "object"
          ],
          description: "Sprite image used as the bar track/frame border for bar/progress formats. Falls back to CSS rounded bg-muted.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        fillAsset: {
          types: [
            "object"
          ],
          description: "Sprite image tiled across the filled portion of bar/progress formats. Falls back to CSS color gradient.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "score-display": {
      type: "score-display",
      category: "game",
      sourceFile: "components/game/atoms/ScoreDisplay.tsx",
      tier: "atoms",
      family: "game",
      description: "ScoreDisplay component",
      suggestedFor: [
        "score",
        "display",
        "score display"
      ],
      typicalSize: "small",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        value: {
          types: [
            "number"
          ],
          description: "Current score value",
          required: true
        },
        score: {
          types: [
            "number"
          ],
          description: "Alias for value \u2014 common schema binding name"
        },
        label: {
          types: [
            "string"
          ],
          description: "Label to display before score"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon component or emoji"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        locale: {
          types: [
            "string"
          ],
          description: "Number formatting locale"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "timer-display": {
      type: "timer-display",
      category: "game",
      sourceFile: "components/game/atoms/TimerDisplay.tsx",
      tier: "atoms",
      family: "game",
      description: "TimerDisplay component",
      suggestedFor: [
        "timer",
        "display",
        "timer display"
      ],
      typicalSize: "small",
      propsSchema: {
        seconds: {
          types: [
            "number"
          ],
          description: "Time in seconds",
          required: true,
          default: 90
        },
        running: {
          types: [
            "boolean"
          ],
          description: "Whether the timer is running",
          default: true
        },
        format: {
          types: [
            "string"
          ],
          description: "Display format",
          enumValues: [
            "mm:ss",
            "ss",
            "countdown"
          ],
          default: "mm:ss"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        lowThreshold: {
          types: [
            "number"
          ],
          description: "Seconds below which to pulse red"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Icon asset rendered to the left of the time value. Falls back to no icon.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-branch": {
      type: "svg-branch",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgBranch.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgBranch component",
      suggestedFor: [
        "svg",
        "branch",
        "svg branch"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 5
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 50
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "fork",
            "merge",
            "diamond"
          ],
          default: "fork"
        },
        branches: {
          types: [
            "number"
          ],
          description: "branches prop",
          default: 2
        },
        size: {
          types: [
            "number"
          ],
          description: "size prop",
          default: 1
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-connection": {
      type: "svg-connection",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgConnection.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgConnection component",
      suggestedFor: [
        "svg",
        "connection",
        "svg connection"
      ],
      typicalSize: "small",
      propsSchema: {
        x1: {
          types: [
            "number"
          ],
          description: "x1 prop",
          default: 10
        },
        y1: {
          types: [
            "number"
          ],
          description: "y1 prop",
          default: 50
        },
        x2: {
          types: [
            "number"
          ],
          description: "x2 prop",
          default: 90
        },
        y2: {
          types: [
            "number"
          ],
          description: "y2 prop",
          default: 50
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "solid",
            "dashed",
            "animated"
          ],
          default: "solid"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop",
          default: 1.5
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-flow": {
      type: "svg-flow",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgFlow.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgFlow component",
      suggestedFor: [
        "svg",
        "flow",
        "svg flow"
      ],
      typicalSize: "small",
      propsSchema: {
        points: {
          types: [
            "array"
          ],
          description: "points prop",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            },
            tupleItems: [
              {
                types: [
                  "number"
                ]
              },
              {
                types: [
                  "number"
                ]
              }
            ]
          }
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop",
          default: 1.5
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "animated prop",
          default: false
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-grid": {
      type: "svg-grid",
      category: "display",
      sourceFile: "components/core/atoms/svg/SvgGrid.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgGrid component",
      suggestedFor: [
        "svg",
        "grid",
        "svg grid"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 10
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 10
        },
        cols: {
          types: [
            "number"
          ],
          description: "cols prop",
          default: 4
        },
        rows: {
          types: [
            "number"
          ],
          description: "rows prop",
          default: 3
        },
        spacing: {
          types: [
            "number"
          ],
          description: "spacing prop",
          default: 20
        },
        nodeRadius: {
          types: [
            "number"
          ],
          description: "nodeRadius prop",
          default: 3
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        highlights: {
          types: [
            "array"
          ],
          description: "highlights prop",
          items: {
            types: [
              "number"
            ]
          },
          default: []
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-lobe": {
      type: "svg-lobe",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgLobe.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgLobe component",
      suggestedFor: [
        "svg",
        "lobe",
        "svg lobe"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 50
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 50
        },
        rx: {
          types: [
            "number"
          ],
          description: "rx prop",
          default: 14
        },
        ry: {
          types: [
            "number"
          ],
          description: "ry prop",
          default: 20
        },
        rotation: {
          types: [
            "number"
          ],
          description: "rotation prop",
          default: 0
        },
        shells: {
          types: [
            "number"
          ],
          description: "shells prop",
          default: 2
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-mesh": {
      type: "svg-mesh",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgMesh.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgMesh component",
      suggestedFor: [
        "svg",
        "mesh",
        "svg mesh"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 60
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 60
        },
        nodes: {
          types: [
            "number"
          ],
          description: "nodes prop",
          default: 6
        },
        radius: {
          types: [
            "number"
          ],
          description: "radius prop",
          default: 50
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        connectionDensity: {
          types: [
            "number"
          ],
          description: "connectionDensity prop",
          default: 0.5
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 120
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 120
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-morph": {
      type: "svg-morph",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgMorph.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgMorph component",
      suggestedFor: [
        "svg",
        "morph",
        "svg morph"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 5
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 10
        },
        size: {
          types: [
            "number"
          ],
          description: "size prop",
          default: 1
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "text-to-code",
            "code-to-app",
            "generic"
          ],
          default: "generic"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 130
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 50
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-node": {
      type: "svg-node",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgNode.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgNode component",
      suggestedFor: [
        "svg",
        "node",
        "svg node"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 50
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 50
        },
        r: {
          types: [
            "number"
          ],
          description: "r prop",
          default: 6
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "filled",
            "stroked",
            "pulse"
          ],
          default: "filled"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-pulse": {
      type: "svg-pulse",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgPulse.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgPulse component",
      suggestedFor: [
        "svg",
        "pulse",
        "svg pulse"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 70
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 70
        },
        rings: {
          types: [
            "number"
          ],
          description: "rings prop",
          default: 3
        },
        maxRadius: {
          types: [
            "number"
          ],
          description: "maxRadius prop",
          default: 60
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "animated prop",
          default: true
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 140
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 140
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-ring": {
      type: "svg-ring",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgRing.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgRing component",
      suggestedFor: [
        "svg",
        "ring",
        "svg ring"
      ],
      typicalSize: "small",
      propsSchema: {
        cx: {
          types: [
            "number"
          ],
          description: "cx prop",
          default: 50
        },
        cy: {
          types: [
            "number"
          ],
          description: "cy prop",
          default: 50
        },
        r: {
          types: [
            "number"
          ],
          description: "r prop",
          default: 40
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "solid",
            "dashed",
            "glow"
          ],
          default: "solid"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop",
          default: 1.5
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        label: {
          types: [
            "string"
          ],
          description: "label prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-shield": {
      type: "svg-shield",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgShield.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgShield component",
      suggestedFor: [
        "svg",
        "shield",
        "svg shield"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 50
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 50
        },
        size: {
          types: [
            "number"
          ],
          description: "size prop",
          default: 1
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "outline",
            "filled",
            "check"
          ],
          default: "outline"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 100
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 100
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "svg-stack": {
      type: "svg-stack",
      category: "component",
      sourceFile: "components/core/atoms/svg/SvgStack.tsx",
      tier: "atoms",
      family: "core",
      description: "SvgStack component",
      suggestedFor: [
        "svg",
        "stack",
        "svg stack"
      ],
      typicalSize: "small",
      propsSchema: {
        x: {
          types: [
            "number"
          ],
          description: "x prop",
          default: 10
        },
        y: {
          types: [
            "number"
          ],
          description: "y prop",
          default: 40
        },
        layers: {
          types: [
            "number"
          ],
          description: "layers prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 60
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 40
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          default: "var(--color-primary)"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop",
          default: 1
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        labels: {
          types: [
            "array"
          ],
          description: "labels prop",
          items: {
            types: [
              "string"
            ]
          }
        },
        asRoot: {
          types: [
            "boolean"
          ],
          description: "When true (default), wraps in a standalone <svg> so the shape is visible without a parent SVG context.",
          default: true
        },
        svgWidth: {
          types: [
            "number"
          ],
          description: "svgWidth prop",
          default: 90
        },
        svgHeight: {
          types: [
            "number"
          ],
          description: "svgHeight prop",
          default: 80
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-breadcrumb": {
      type: "doc-breadcrumb",
      category: "navigation",
      sourceFile: "components/core/molecules/DocBreadcrumb.tsx",
      tier: "molecules",
      family: "core",
      description: "DocBreadcrumb component",
      suggestedFor: [
        "doc",
        "breadcrumb",
        "doc breadcrumb"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Breadcrumb path items, last item is treated as current page",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-pagination": {
      type: "doc-pagination",
      category: "navigation",
      sourceFile: "components/core/molecules/DocPagination.tsx",
      tier: "molecules",
      family: "core",
      description: "DocPagination component",
      suggestedFor: [
        "doc",
        "pagination",
        "doc pagination"
      ],
      typicalSize: "medium",
      propsSchema: {
        prev: {
          types: [
            "object"
          ],
          description: "Previous page link",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            href: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "label",
            "href"
          ]
        },
        next: {
          types: [
            "object"
          ],
          description: "Next page link",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            href: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "label",
            "href"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-search": {
      type: "doc-search",
      category: "filter",
      sourceFile: "components/core/molecules/DocSearch.tsx",
      tier: "molecules",
      family: "core",
      description: "DocSearch component",
      suggestedFor: [
        "doc",
        "search",
        "doc search"
      ],
      typicalSize: "medium",
      propsSchema: {
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder text for the input"
        },
        onSearch: {
          types: [
            "function"
          ],
          description: "Callback invoked with the search query, returns results",
          callbackArgs: [
            {
              name: "query",
              type: "string"
            }
          ],
          renderCallback: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-sidebar": {
      type: "doc-sidebar",
      category: "navigation",
      sourceFile: "components/core/molecules/DocSidebar.tsx",
      tier: "molecules",
      family: "core",
      description: "DocSidebar component",
      suggestedFor: [
        "doc",
        "sidebar",
        "doc sidebar"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Sidebar navigation items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              href: {
                types: [
                  "string"
                ]
              },
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  cyclic: true,
                  cyclicTypeName: "DocSidebarItem"
                }
              },
              active: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "doc-toc": {
      type: "doc-toc",
      category: "component",
      sourceFile: "components/core/molecules/DocTOC.tsx",
      tier: "molecules",
      family: "core",
      description: "DocTOC component",
      suggestedFor: [
        "doc",
        "doc t o c"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Table of contents items",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              level: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "id",
              "label",
              "level"
            ]
          }
        },
        activeId: {
          types: [
            "string"
          ],
          description: "Currently active section id"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "gradient-divider": {
      type: "gradient-divider",
      category: "component",
      sourceFile: "components/core/molecules/GradientDivider.tsx",
      tier: "molecules",
      family: "core",
      description: "GradientDivider component",
      suggestedFor: [
        "gradient",
        "divider",
        "gradient divider"
      ],
      typicalSize: "medium",
      propsSchema: {
        color: {
          types: [
            "string"
          ],
          description: "Semantic palette token or a raw CSS color value. Defaults to 'primary'."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "behavior-view": {
      type: "behavior-view",
      category: "component",
      sourceFile: "components/avl/molecules/BehaviorView.tsx",
      tier: "molecules",
      family: "avl",
      description: "BehaviorView component",
      suggestedFor: [
        "behavior",
        "view",
        "behavior view"
      ],
      typicalSize: "medium",
      propsSchema: {
        data: {
          types: [
            "object"
          ],
          description: "The orbital to summarize.",
          required: true,
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "object"
              ]
            },
            fields: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  required: {
                    types: [
                      "boolean"
                    ]
                  },
                  hasDefault: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "name",
                  "type",
                  "required",
                  "hasDefault"
                ]
              }
            },
            traits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  stateCount: {
                    types: [
                      "number"
                    ]
                  },
                  eventCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  emits: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listens: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "stateCount",
                  "eventCount",
                  "transitionCount",
                  "emits",
                  "listens"
                ]
              }
            },
            pages: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  route: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "route"
                ]
              }
            },
            traitDetails: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  linkedEntity: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        isInitial: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        },
                        isTerminal: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        }
                      },
                      required: [
                        "name"
                      ]
                    }
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guard: {
                          types: [
                            "sexpr"
                          ],
                          nullable: true
                        },
                        effects: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ]
                              },
                              args: {
                                types: [
                                  "array"
                                ],
                                items: {
                                  types: [
                                    "sexpr"
                                  ]
                                }
                              }
                            },
                            required: [
                              "type",
                              "args"
                            ]
                          }
                        },
                        index: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event",
                        "effects",
                        "index"
                      ]
                    }
                  },
                  emittedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listenedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "linkedEntity",
                  "states",
                  "transitions",
                  "emittedEvents",
                  "listenedEvents"
                ]
              }
            },
            externalLinks: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  targetOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  eventName: {
                    types: [
                      "string"
                    ]
                  },
                  direction: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "targetOrbital",
                  "eventName",
                  "direction",
                  "traitName"
                ]
              }
            }
          },
          propertyRequired: [
            "orbitalName",
            "entityName",
            "persistence",
            "fields",
            "traits",
            "pages",
            "traitDetails",
            "externalLinks"
          ],
          example: {
            orbitalName: "OrderOrbital",
            entityName: "Order",
            persistence: "persistent",
            fields: [
              {
                name: "customer",
                type: "string",
                required: true,
                hasDefault: false
              },
              {
                name: "qty",
                type: "number",
                required: true,
                hasDefault: true
              },
              {
                name: "status",
                type: "string",
                required: false,
                hasDefault: true
              }
            ],
            traits: [
              {
                name: "OrderFlow",
                stateCount: 5,
                eventCount: 7,
                transitionCount: 7,
                emits: [
                  "ORDER_SAVED"
                ],
                listens: [
                  "PAYMENT_OK"
                ]
              }
            ],
            pages: [
              {
                name: "Orders",
                route: "/orders"
              }
            ],
            traitDetails: {
              OrderFlow: {
                name: "OrderFlow",
                linkedEntity: "Order",
                states: [
                  {
                    name: "browsing",
                    isInitial: true,
                    isTerminal: false
                  },
                  {
                    name: "editing",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "saving",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "confirmed",
                    isInitial: false,
                    isTerminal: true
                  },
                  {
                    name: "failed",
                    isInitial: false,
                    isTerminal: false
                  }
                ],
                transitions: [
                  {
                    from: "browsing",
                    to: "editing",
                    event: "EDIT",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 0
                  },
                  {
                    from: "editing",
                    to: "saving",
                    event: "SAVE",
                    effects: [
                      {
                        type: "persist",
                        args: []
                      },
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 1
                  },
                  {
                    from: "saving",
                    to: "confirmed",
                    event: "SAVED",
                    effects: [
                      {
                        type: "emit",
                        args: []
                      },
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 2
                  },
                  {
                    from: "saving",
                    to: "failed",
                    event: "SAVE_FAILED",
                    effects: [
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 3
                  },
                  {
                    from: "failed",
                    to: "editing",
                    event: "RETRY",
                    effects: [],
                    index: 4
                  },
                  {
                    from: "editing",
                    to: "browsing",
                    event: "CANCEL",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 5
                  },
                  {
                    from: "confirmed",
                    to: "browsing",
                    event: "DONE",
                    effects: [
                      {
                        type: "navigate",
                        args: []
                      }
                    ],
                    index: 6
                  }
                ],
                emittedEvents: [
                  "ORDER_SAVED"
                ],
                listenedEvents: [
                  "PAYMENT_OK"
                ]
              }
            },
            externalLinks: [
              {
                targetOrbital: "PaymentOrbital",
                eventName: "PAYMENT_OK",
                direction: "in",
                traitName: "OrderFlow"
              }
            ]
          }
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "module-card": {
      type: "module-card",
      category: "display",
      sourceFile: "components/avl/molecules/ModuleCard.tsx",
      tier: "molecules",
      family: "avl",
      description: "ModuleCard \u2014 a compact summary card for one orbital module: its entity and fields, a mini state machine per trait, and its pages.",
      capabilities: "orbital module summary card, entity and trait overview card, app architecture node card",
      suggestedFor: [
        "module",
        "card",
        "module card"
      ],
      typicalSize: "medium",
      propsSchema: {
        data: {
          types: [
            "object"
          ],
          description: "The orbital to summarize.",
          required: true,
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "object"
              ]
            },
            fields: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  required: {
                    types: [
                      "boolean"
                    ]
                  },
                  hasDefault: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "name",
                  "type",
                  "required",
                  "hasDefault"
                ]
              }
            },
            traits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  stateCount: {
                    types: [
                      "number"
                    ]
                  },
                  eventCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  emits: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listens: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "stateCount",
                  "eventCount",
                  "transitionCount",
                  "emits",
                  "listens"
                ]
              }
            },
            pages: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  route: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "route"
                ]
              }
            },
            traitDetails: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  linkedEntity: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        isInitial: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        },
                        isTerminal: {
                          types: [
                            "boolean"
                          ],
                          nullable: true
                        }
                      },
                      required: [
                        "name"
                      ]
                    }
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guard: {
                          types: [
                            "sexpr"
                          ],
                          nullable: true
                        },
                        effects: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ]
                              },
                              args: {
                                types: [
                                  "array"
                                ],
                                items: {
                                  types: [
                                    "sexpr"
                                  ]
                                }
                              }
                            },
                            required: [
                              "type",
                              "args"
                            ]
                          }
                        },
                        index: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event",
                        "effects",
                        "index"
                      ]
                    }
                  },
                  emittedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listenedEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "name",
                  "linkedEntity",
                  "states",
                  "transitions",
                  "emittedEvents",
                  "listenedEvents"
                ]
              }
            },
            externalLinks: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  targetOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  eventName: {
                    types: [
                      "string"
                    ]
                  },
                  direction: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "targetOrbital",
                  "eventName",
                  "direction",
                  "traitName"
                ]
              }
            }
          },
          propertyRequired: [
            "orbitalName",
            "entityName",
            "persistence",
            "fields",
            "traits",
            "pages",
            "traitDetails",
            "externalLinks"
          ],
          example: {
            orbitalName: "OrderOrbital",
            entityName: "Order",
            persistence: "persistent",
            fields: [
              {
                name: "customer",
                type: "string",
                required: true,
                hasDefault: false
              },
              {
                name: "qty",
                type: "number",
                required: true,
                hasDefault: true
              },
              {
                name: "status",
                type: "string",
                required: false,
                hasDefault: true
              }
            ],
            traits: [
              {
                name: "OrderFlow",
                stateCount: 5,
                eventCount: 7,
                transitionCount: 7,
                emits: [
                  "ORDER_SAVED"
                ],
                listens: [
                  "PAYMENT_OK"
                ]
              }
            ],
            pages: [
              {
                name: "Orders",
                route: "/orders"
              }
            ],
            traitDetails: {
              OrderFlow: {
                name: "OrderFlow",
                linkedEntity: "Order",
                states: [
                  {
                    name: "browsing",
                    isInitial: true,
                    isTerminal: false
                  },
                  {
                    name: "editing",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "saving",
                    isInitial: false,
                    isTerminal: false
                  },
                  {
                    name: "confirmed",
                    isInitial: false,
                    isTerminal: true
                  },
                  {
                    name: "failed",
                    isInitial: false,
                    isTerminal: false
                  }
                ],
                transitions: [
                  {
                    from: "browsing",
                    to: "editing",
                    event: "EDIT",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 0
                  },
                  {
                    from: "editing",
                    to: "saving",
                    event: "SAVE",
                    effects: [
                      {
                        type: "persist",
                        args: []
                      },
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 1
                  },
                  {
                    from: "saving",
                    to: "confirmed",
                    event: "SAVED",
                    effects: [
                      {
                        type: "emit",
                        args: []
                      },
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 2
                  },
                  {
                    from: "saving",
                    to: "failed",
                    event: "SAVE_FAILED",
                    effects: [
                      {
                        type: "notify",
                        args: []
                      }
                    ],
                    index: 3
                  },
                  {
                    from: "failed",
                    to: "editing",
                    event: "RETRY",
                    effects: [],
                    index: 4
                  },
                  {
                    from: "editing",
                    to: "browsing",
                    event: "CANCEL",
                    effects: [
                      {
                        type: "render-ui",
                        args: []
                      }
                    ],
                    index: 5
                  },
                  {
                    from: "confirmed",
                    to: "browsing",
                    event: "DONE",
                    effects: [
                      {
                        type: "navigate",
                        args: []
                      }
                    ],
                    index: 6
                  }
                ],
                emittedEvents: [
                  "ORDER_SAVED"
                ],
                listenedEvents: [
                  "PAYMENT_OK"
                ]
              }
            },
            externalLinks: [
              {
                targetOrbital: "PaymentOrbital",
                eventName: "PAYMENT_OK",
                direction: "in",
                traitName: "OrderFlow"
              }
            ]
          }
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations per trait name (states, transition events, effect types), shown in a popover on hover.",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              states: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    body: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "body"
                  ]
                }
              },
              transitions: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    body: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "body"
                  ]
                }
              },
              effects: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    title: {
                      types: [
                        "string"
                      ]
                    },
                    body: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "body"
                  ]
                }
              }
            }
          },
          example: {
            OrderFlow: {
              states: {
                saving: {
                  body: "Waiting for the server to confirm."
                }
              },
              effects: {
                persist: {
                  body: "Saves the order."
                }
              }
            }
          }
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-hud": {
      type: "game-hud",
      category: "game",
      sourceFile: "components/game/molecules/GameHud.tsx",
      tier: "molecules",
      family: "game",
      description: "GameHud component",
      suggestedFor: [
        "game",
        "hud",
        "game hud"
      ],
      typicalSize: "medium",
      propsSchema: {
        position: {
          types: [
            "string"
          ],
          description: "Position of the HUD"
        },
        stats: {
          types: [
            "array"
          ],
          description: "Stats to display - accepts readonly for compatibility with generated const arrays",
          items: {
            types: [
              "object"
            ],
            properties: {
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              source: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        items: {
          types: [
            "array"
          ],
          description: "Alias for stats (schema compatibility)",
          items: {
            types: [
              "object"
            ],
            properties: {
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              source: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        elements: {
          types: [
            "array"
          ],
          description: "Schema-style elements array (alternative to stats). Converted to stats internally for backwards compatibility.",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ]
              },
              bind: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number",
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              }
            }
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: 'Layout variant: "floating" (default) renders a compact pill cluster that hugs its content width; "bar" preserves the legacy full-width split bar.',
          enumValues: [
            "floating",
            "bar"
          ],
          default: "floating"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        transparent: {
          types: [
            "boolean"
          ],
          description: "Whether to use a semi-transparent background",
          default: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-menu": {
      type: "game-menu",
      category: "game",
      sourceFile: "components/game/molecules/GameMenu.tsx",
      tier: "molecules",
      family: "game",
      description: "GameMenu component",
      suggestedFor: [
        "game",
        "menu",
        "game menu"
      ],
      typicalSize: "medium",
      propsSchema: {
        title: {
          types: [
            "string"
          ],
          description: "Menu title",
          required: true
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Optional subtitle or version"
        },
        options: {
          types: [
            "array"
          ],
          description: "Menu options - accepts readonly for compatibility with generated const arrays",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              subLabel: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        menuItems: {
          types: [
            "array"
          ],
          description: "Alias for options (schema compatibility)",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              navigatesTo: {
                types: [
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              subLabel: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Called when an option is selected (legacy callback, prefer event bus)",
          kind: "callback",
          callbackArgs: [
            {
              name: "option",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ],
                    kind: "event"
                  },
                  navigatesTo: {
                    types: [
                      "string"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  disabled: {
                    types: [
                      "boolean"
                    ]
                  },
                  subLabel: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "label"
                ]
              }
            }
          ]
        },
        background: {
          types: [
            "string"
          ],
          description: "Background image or gradient"
        },
        logo: {
          types: [
            "object"
          ],
          description: "Logo asset",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "stat-badge": {
      type: "stat-badge",
      category: "game",
      sourceFile: "components/game/molecules/StatBadge.tsx",
      tier: "molecules",
      family: "game",
      description: "StatBadge component",
      suggestedFor: [
        "stat",
        "badge",
        "stat badge"
      ],
      typicalSize: "medium",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Sprite asset \u2014 takes precedence over icon when provided",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        iconUrl: {
          types: [
            "object"
          ],
          description: "Asset image (board ui/ PNG); takes precedence over the Lucide icon.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        label: {
          types: [
            "string"
          ],
          description: "Stat label",
          required: true
        },
        value: {
          types: [
            "number",
            "string"
          ],
          description: "Current value (defaults to 0 if not provided)"
        },
        max: {
          types: [
            "number"
          ],
          description: "Maximum value (for bar/hearts format)"
        },
        source: {
          types: [
            "string"
          ],
          description: "Data source entity name (for schema config)"
        },
        field: {
          types: [
            "string"
          ],
          description: "Field name in the source (for schema config)"
        },
        format: {
          types: [
            "string"
          ],
          description: "Display format",
          default: "number"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon component or emoji"
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          default: "md"
        },
        variant: {
          types: [
            "string"
          ],
          description: "Visual variant",
          default: "default"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-audio-toggle": {
      type: "game-audio-toggle",
      category: "game",
      sourceFile: "components/game/atoms/GameAudioToggle.tsx",
      tier: "atoms",
      family: "game",
      description: "GameAudioToggle component",
      suggestedFor: [
        "game",
        "audio",
        "toggle",
        "game audio toggle"
      ],
      typicalSize: "large",
      propsSchema: {
        size: {
          types: [
            "string"
          ],
          description: "Button size",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state (passed through)"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (passed through)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        onAsset: {
          types: [
            "object"
          ],
          description: "Asset rendered when sound is ON. Falls back to \u{1F50A} emoji.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        offAsset: {
          types: [
            "object"
          ],
          description: "Asset rendered when sound is OFF (muted). Falls back to \u{1F507} emoji.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        toggleEvent: {
          types: [
            "string"
          ],
          description: "Event dispatched via event bus on toggle, carrying the new muted state.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "muted",
              type: "boolean",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "trait-slot": {
      type: "trait-slot",
      category: "game",
      sourceFile: "components/game/molecules/TraitSlot.tsx",
      tier: "molecules",
      family: "game",
      description: "Event Contract: Emits: UI:CLICK Emits: UI:REMOVE",
      suggestedFor: [
        "trait",
        "slot",
        "trait slot"
      ],
      typicalSize: "large",
      propsSchema: {
        slotNumber: {
          types: [
            "number"
          ],
          description: "Slot index (1-based)",
          required: true
        },
        equippedItem: {
          types: [
            "object"
          ],
          description: "Currently equipped item, if any",
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            description: {
              types: [
                "string"
              ]
            },
            iconEmoji: {
              types: [
                "string"
              ]
            },
            iconUrl: {
              types: [
                "object"
              ],
              properties: {
                url: {
                  types: [
                    "asset"
                  ]
                },
                role: {
                  types: [
                    "string"
                  ]
                },
                category: {
                  types: [
                    "string"
                  ]
                },
                animations: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                style: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "pixel",
                    "vector",
                    "hd",
                    "1-bit",
                    "isometric"
                  ]
                },
                variant: {
                  types: [
                    "string"
                  ]
                },
                dimension: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "2d",
                    "3d"
                  ]
                },
                aspect: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "1:1",
                    "16:9",
                    "5:7",
                    "8:1",
                    "4:1",
                    "5:3",
                    "1:2"
                  ]
                },
                atlas: {
                  types: [
                    "asset"
                  ]
                },
                sprite: {
                  types: [
                    "string"
                  ]
                },
                name: {
                  types: [
                    "string"
                  ]
                },
                thumbnailUrl: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "url",
                "role",
                "category"
              ]
            },
            stateMachine: {
              types: [
                "object"
              ],
              properties: {
                name: {
                  types: [
                    "string"
                  ]
                },
                states: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                currentState: {
                  types: [
                    "string"
                  ]
                },
                transitions: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      from: {
                        types: [
                          "string"
                        ]
                      },
                      to: {
                        types: [
                          "string"
                        ]
                      },
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      guardHint: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "from",
                      "to",
                      "event"
                    ]
                  }
                },
                description: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "name",
                "states",
                "currentState",
                "transitions"
              ]
            }
          },
          propertyRequired: [
            "id",
            "name",
            "category"
          ]
        },
        locked: {
          types: [
            "boolean"
          ],
          description: "Whether slot is locked",
          default: false
        },
        lockLabel: {
          types: [
            "string"
          ],
          description: "Label shown when locked"
        },
        selected: {
          types: [
            "boolean"
          ],
          description: "Whether slot is selected",
          default: false
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        showTooltip: {
          types: [
            "boolean"
          ],
          description: "Show tooltip on hover",
          default: true
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        tooltipFrameUrl: {
          types: [
            "object"
          ],
          description: "Optional tooltip frame image asset",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        onItemDrop: {
          types: [
            "function"
          ],
          description: "-- Drag and drop --",
          kind: "callback",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  },
                  iconEmoji: {
                    types: [
                      "string"
                    ]
                  },
                  iconUrl: {
                    types: [
                      "object"
                    ],
                    properties: {
                      url: {
                        types: [
                          "asset"
                        ]
                      },
                      role: {
                        types: [
                          "string"
                        ]
                      },
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      animations: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      style: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "pixel",
                          "vector",
                          "hd",
                          "1-bit",
                          "isometric"
                        ]
                      },
                      variant: {
                        types: [
                          "string"
                        ]
                      },
                      dimension: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "2d",
                          "3d"
                        ]
                      },
                      aspect: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "1:1",
                          "16:9",
                          "5:7",
                          "8:1",
                          "4:1",
                          "5:3",
                          "1:2"
                        ]
                      },
                      atlas: {
                        types: [
                          "asset"
                        ]
                      },
                      sprite: {
                        types: [
                          "string"
                        ]
                      },
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      thumbnailUrl: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "url",
                      "role",
                      "category"
                    ]
                  },
                  stateMachine: {
                    types: [
                      "object"
                    ],
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      states: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      currentState: {
                        types: [
                          "string"
                        ]
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            from: {
                              types: [
                                "string"
                              ]
                            },
                            to: {
                              types: [
                                "string"
                              ]
                            },
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            guardHint: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "from",
                            "to",
                            "event"
                          ]
                        }
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "states",
                      "currentState",
                      "transitions"
                    ]
                  }
                },
                required: [
                  "id",
                  "name",
                  "category"
                ]
              }
            }
          ]
        },
        draggable: {
          types: [
            "boolean"
          ],
          description: "Whether this slot's equipped item is draggable",
          default: false
        },
        onDragStart: {
          types: [
            "function"
          ],
          description: "Called when drag starts from this slot",
          kind: "callback",
          callbackArgs: [
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  },
                  iconEmoji: {
                    types: [
                      "string"
                    ]
                  },
                  iconUrl: {
                    types: [
                      "object"
                    ],
                    properties: {
                      url: {
                        types: [
                          "asset"
                        ]
                      },
                      role: {
                        types: [
                          "string"
                        ]
                      },
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      animations: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      style: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "pixel",
                          "vector",
                          "hd",
                          "1-bit",
                          "isometric"
                        ]
                      },
                      variant: {
                        types: [
                          "string"
                        ]
                      },
                      dimension: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "2d",
                          "3d"
                        ]
                      },
                      aspect: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "1:1",
                          "16:9",
                          "5:7",
                          "8:1",
                          "4:1",
                          "5:3",
                          "1:2"
                        ]
                      },
                      atlas: {
                        types: [
                          "asset"
                        ]
                      },
                      sprite: {
                        types: [
                          "string"
                        ]
                      },
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      thumbnailUrl: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "url",
                      "role",
                      "category"
                    ]
                  },
                  stateMachine: {
                    types: [
                      "object"
                    ],
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      states: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      currentState: {
                        types: [
                          "string"
                        ]
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            from: {
                              types: [
                                "string"
                              ]
                            },
                            to: {
                              types: [
                                "string"
                              ]
                            },
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            guardHint: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "from",
                            "to",
                            "event"
                          ]
                        }
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "states",
                      "currentState",
                      "transitions"
                    ]
                  }
                },
                required: [
                  "id",
                  "name",
                  "category"
                ]
              }
            }
          ]
        },
        feedback: {
          types: [
            "string"
          ],
          description: "Per-slot correctness feedback after a failed attempt",
          enumValues: [
            "correct",
            "wrong"
          ]
        },
        onClick: {
          types: [
            "function"
          ],
          description: "-- Callbacks --",
          kind: "callback",
          callbackArgs: []
        },
        onRemove: {
          types: [
            "function"
          ],
          description: "Remove handler",
          kind: "callback",
          callbackArgs: [],
          default: ""
        },
        clickEvent: {
          types: [
            "string"
          ],
          description: "-- Declarative events --",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            }
          ]
        },
        removeEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{removeEvent} with { slotNumber }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            }
          ],
          default: ""
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{dropEvent} with { slotNumber, itemId } when an item is dropped into this slot",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            },
            {
              name: "itemId",
              type: "string",
              required: true
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "action-palette": {
      type: "action-palette",
      category: "game",
      sourceFile: "components/game/molecules/ActionPalette.tsx",
      tier: "molecules",
      family: "game",
      description: "ActionPalette Component  Grid of draggable ActionTile components for the Sequencer tier. Kids pick from these to build their sequence.",
      suggestedFor: [
        "action",
        "palette",
        "action palette"
      ],
      typicalSize: "large",
      propsSchema: {
        actions: {
          types: [
            "array"
          ],
          description: "Available actions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              iconEmoji: {
                types: [
                  "string"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              stateMachine: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  currentState: {
                    types: [
                      "string"
                    ]
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guardHint: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event"
                      ]
                    }
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "states",
                  "currentState",
                  "transitions"
                ]
              }
            },
            required: [
              "id",
              "name",
              "category"
            ]
          }
        },
        usedActionIds: {
          types: [
            "array"
          ],
          description: "IDs of actions that are already used (shown as disabled)",
          items: {
            types: [
              "string"
            ]
          },
          default: []
        },
        allowDuplicates: {
          types: [
            "boolean"
          ],
          description: "Whether each action can be used multiple times",
          default: true
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        label: {
          types: [
            "string"
          ],
          description: "Label above the palette"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "action-tile": {
      type: "action-tile",
      category: "game",
      sourceFile: "components/game/molecules/ActionTile.tsx",
      tier: "molecules",
      family: "game",
      description: "ActionTile Component  A draggable action tile for the Sequencer tier (ages 5-8). Kids drag these from the ActionPalette into SequenceBar slots. Sets SlotItemData on dataTransfer for TraitSlot compatibility.",
      suggestedFor: [
        "action",
        "tile",
        "action tile"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        action: {
          types: [
            "object"
          ],
          description: "The action data",
          required: true,
          properties: {
            id: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            description: {
              types: [
                "string"
              ]
            },
            iconEmoji: {
              types: [
                "string"
              ]
            },
            iconUrl: {
              types: [
                "object"
              ],
              properties: {
                url: {
                  types: [
                    "asset"
                  ]
                },
                role: {
                  types: [
                    "string"
                  ]
                },
                category: {
                  types: [
                    "string"
                  ]
                },
                animations: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                style: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "pixel",
                    "vector",
                    "hd",
                    "1-bit",
                    "isometric"
                  ]
                },
                variant: {
                  types: [
                    "string"
                  ]
                },
                dimension: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "2d",
                    "3d"
                  ]
                },
                aspect: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "1:1",
                    "16:9",
                    "5:7",
                    "8:1",
                    "4:1",
                    "5:3",
                    "1:2"
                  ]
                },
                atlas: {
                  types: [
                    "asset"
                  ]
                },
                sprite: {
                  types: [
                    "string"
                  ]
                },
                name: {
                  types: [
                    "string"
                  ]
                },
                thumbnailUrl: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "url",
                "role",
                "category"
              ]
            },
            stateMachine: {
              types: [
                "object"
              ],
              properties: {
                name: {
                  types: [
                    "string"
                  ]
                },
                states: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                currentState: {
                  types: [
                    "string"
                  ]
                },
                transitions: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      from: {
                        types: [
                          "string"
                        ]
                      },
                      to: {
                        types: [
                          "string"
                        ]
                      },
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      guardHint: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "from",
                      "to",
                      "event"
                    ]
                  }
                },
                description: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "name",
                "states",
                "currentState",
                "transitions"
              ]
            }
          },
          propertyRequired: [
            "id",
            "name",
            "category"
          ]
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Whether the tile is disabled / already used",
          default: false
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "sequence-bar": {
      type: "sequence-bar",
      category: "game",
      sourceFile: "components/game/molecules/SequenceBar.tsx",
      tier: "molecules",
      family: "game",
      description: "SequenceBar Component  A row of TraitSlot components forming the action sequence for the Sequencer tier (ages 5-8). Kids drag ActionTiles from the palette into these slots to build their sequence.",
      suggestedFor: [
        "sequence",
        "bar",
        "sequence bar"
      ],
      typicalSize: "large",
      propsSchema: {
        slots: {
          types: [
            "array"
          ],
          description: "The current sequence (sparse \u2014 undefined means empty slot)",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              iconEmoji: {
                types: [
                  "string"
                ]
              },
              iconUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              stateMachine: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  states: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  currentState: {
                    types: [
                      "string"
                    ]
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        },
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        guardHint: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to",
                        "event"
                      ]
                    }
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "name",
                  "states",
                  "currentState",
                  "transitions"
                ]
              }
            },
            required: [
              "id",
              "name",
              "category"
            ]
          }
        },
        maxSlots: {
          types: [
            "number"
          ],
          description: "Max number of slots",
          required: true
        },
        onSlotDrop: {
          types: [
            "function"
          ],
          description: "Called when an item is dropped into slot at index (callback path)",
          kind: "callback",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            },
            {
              name: "item",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  },
                  iconEmoji: {
                    types: [
                      "string"
                    ]
                  },
                  iconUrl: {
                    types: [
                      "object"
                    ],
                    properties: {
                      url: {
                        types: [
                          "asset"
                        ]
                      },
                      role: {
                        types: [
                          "string"
                        ]
                      },
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      animations: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      style: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "pixel",
                          "vector",
                          "hd",
                          "1-bit",
                          "isometric"
                        ]
                      },
                      variant: {
                        types: [
                          "string"
                        ]
                      },
                      dimension: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "2d",
                          "3d"
                        ]
                      },
                      aspect: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "1:1",
                          "16:9",
                          "5:7",
                          "8:1",
                          "4:1",
                          "5:3",
                          "1:2"
                        ]
                      },
                      atlas: {
                        types: [
                          "asset"
                        ]
                      },
                      sprite: {
                        types: [
                          "string"
                        ]
                      },
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      thumbnailUrl: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "url",
                      "role",
                      "category"
                    ]
                  },
                  stateMachine: {
                    types: [
                      "object"
                    ],
                    properties: {
                      name: {
                        types: [
                          "string"
                        ]
                      },
                      states: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      currentState: {
                        types: [
                          "string"
                        ]
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            from: {
                              types: [
                                "string"
                              ]
                            },
                            to: {
                              types: [
                                "string"
                              ]
                            },
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            guardHint: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "from",
                            "to",
                            "event"
                          ]
                        }
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "name",
                      "states",
                      "currentState",
                      "transitions"
                    ]
                  }
                },
                required: [
                  "id",
                  "name",
                  "category"
                ]
              }
            }
          ]
        },
        onSlotRemove: {
          types: [
            "function"
          ],
          description: "Called when a slot is cleared (callback path)",
          kind: "callback",
          callbackArgs: [
            {
              name: "index",
              type: "number"
            }
          ]
        },
        slotDropEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{slotDropEvent} with { slotNumber, itemId } when an item is dropped (declarative path)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            },
            {
              name: "itemId",
              type: "string",
              required: true
            }
          ]
        },
        slotRemoveEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{slotRemoveEvent} with { slotNumber } when a slot is cleared (declarative path)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "slotNumber",
              type: "number",
              required: true
            }
          ]
        },
        playing: {
          types: [
            "boolean"
          ],
          description: "Whether the sequence is currently playing (disable interaction)",
          default: false
        },
        currentStep: {
          types: [
            "number"
          ],
          description: "Current step index during playback (-1 = not playing)",
          default: -1
        },
        categoryColors: {
          types: [
            "object"
          ],
          description: "Category \u2192 color mapping",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              bg: {
                types: [
                  "string"
                ]
              },
              border: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "bg",
              "border"
            ]
          }
        },
        slotFeedback: {
          types: [
            "array"
          ],
          description: "Per-slot correctness feedback shown after a failed attempt",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "correct",
              "wrong"
            ]
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "Size variant",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "lg"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dashboard-grid": {
      type: "dashboard-grid",
      category: "game",
      sourceFile: "components/core/organisms/layout/DashboardGrid.tsx",
      tier: "organisms",
      family: "core",
      description: "DashboardGrid Component  Multi-column grid for widgets and stats cards. Supports cell spanning for flexible dashboard layouts.  Uses wireframe theme styling (high contrast, sharp edges).",
      suggestedFor: [
        "dashboard",
        "grid",
        "dashboard grid"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out. Each cell is a tile.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        columns: {
          types: [
            "number"
          ],
          description: "Number of columns",
          numericEnumValues: [
            2,
            3,
            4
          ],
          default: 3
        },
        gap: {
          types: [
            "string"
          ],
          description: "Gap between cells",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        cells: {
          types: [
            "array"
          ],
          description: "Cell definitions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              colSpan: {
                types: [
                  "number"
                ],
                numericEnumValues: [
                  1,
                  2,
                  3,
                  4
                ]
              },
              rowSpan: {
                types: [
                  "number"
                ],
                numericEnumValues: [
                  1,
                  2
                ]
              }
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "split-pane": {
      type: "split-pane",
      category: "layout",
      sourceFile: "components/core/organisms/layout/SplitPane.tsx",
      tier: "organisms",
      family: "core",
      description: "SplitPane component",
      suggestedFor: [
        "split",
        "pane",
        "split pane"
      ],
      typicalSize: "large",
      propsSchema: {
        direction: {
          types: [
            "string"
          ],
          description: "Direction of the split",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        ratio: {
          types: [
            "number"
          ],
          description: "Initial ratio (0-100, percentage of first pane)"
        },
        minSize: {
          types: [
            "number"
          ],
          description: "Minimum size of either pane in pixels",
          default: 100
        },
        resizable: {
          types: [
            "boolean"
          ],
          description: "Allow user resizing",
          default: true
        },
        left: {
          types: [
            "node"
          ],
          description: "Content for the left/top pane",
          required: true
        },
        right: {
          types: [
            "node"
          ],
          description: "Content for the right/bottom pane",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        leftClassName: {
          types: [
            "string"
          ],
          description: "Class for left/top pane"
        },
        rightClassName: {
          types: [
            "string"
          ],
          description: "Class for right/bottom pane"
        },
        onRatioChange: {
          types: [
            "function"
          ],
          description: "Called with the new ratio while dragging. Providing this makes `ratio` controlled \u2014 the rendered split always tracks the prop instead of internal state, mirroring a controlled input. Omit for uncontrolled (self-tracked) behavior.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "ratio",
              type: "number"
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "tabbed-container": {
      type: "tabbed-container",
      category: "navigation",
      sourceFile: "components/core/organisms/layout/TabbedContainer.tsx",
      tier: "organisms",
      family: "core",
      description: "TabbedContainer component",
      suggestedFor: [
        "tabbed",
        "container",
        "tabbed container"
      ],
      typicalSize: "large",
      propsSchema: {
        tabs: {
          types: [
            "array"
          ],
          description: "Tab definitions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              content: {
                types: [
                  "node"
                ]
              },
              sectionId: {
                types: [
                  "string"
                ]
              },
              badge: {
                types: [
                  "string",
                  "number"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        defaultTab: {
          types: [
            "string"
          ],
          description: "Default active tab ID"
        },
        activeTab: {
          types: [
            "string"
          ],
          description: "Controlled active tab"
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "Callback when tab changes",
          kind: "callback",
          callbackArgs: [
            {
              name: "tabId",
              type: "string"
            }
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Tab position",
          enumValues: [
            "top",
            "left"
          ],
          default: "top"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "master-detail-layout": {
      type: "master-detail-layout",
      category: "display",
      sourceFile: "components/core/organisms/layout/MasterDetailLayout.tsx",
      tier: "organisms",
      family: "core",
      description: "MasterDetail Component  Classic master-detail pattern with a list on the left and selected item detail on the right.  Uses wireframe theme styling (high contrast, sharp edges).",
      suggestedFor: [
        "master",
        "detail",
        "layout",
        "master detail layout"
      ],
      typicalSize: "large",
      propsSchema: {
        master: {
          types: [
            "node"
          ],
          description: "Master panel content (usually a list)",
          required: true
        },
        detail: {
          types: [
            "node"
          ],
          description: "Detail panel content",
          required: true
        },
        emptyDetail: {
          types: [
            "node"
          ],
          description: "Content shown when nothing is selected"
        },
        hasSelection: {
          types: [
            "boolean"
          ],
          description: "Whether an item is currently selected",
          default: false
        },
        masterWidth: {
          types: [
            "string"
          ],
          description: "Width of master panel (e.g., '350px', '30%')",
          default: "350px"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        masterClassName: {
          types: [
            "string"
          ],
          description: "Class for master pane"
        },
        detailClassName: {
          types: [
            "string"
          ],
          description: "Class for detail pane"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    sparkline: {
      type: "sparkline",
      category: "component",
      sourceFile: "components/core/atoms/Sparkline.tsx",
      tier: "atoms",
      family: "core",
      description: "Sparkline Atom Component  Minimal inline trend chart. Pure SVG polyline over a numeric series. Extracted from StatCard's sparkline path so any KPI or list row can include a trend visualization without compositing the whole card.",
      suggestedFor: [
        "sparkline"
      ],
      typicalSize: "small",
      propsSchema: {
        data: {
          types: [
            "array"
          ],
          description: "Numeric series to plot. Length < 2 renders nothing.",
          required: true,
          items: {
            types: [
              "number"
            ]
          }
        },
        color: {
          types: [
            "string"
          ],
          description: "Stroke color. `auto` (default): success when the series trends up (last \u2265 first), error when it trends down.",
          enumValues: [
            "auto",
            "primary",
            "success",
            "warning",
            "error",
            "info",
            "muted"
          ],
          default: "auto"
        },
        width: {
          types: [
            "number"
          ],
          description: "SVG width in px. Default 80.",
          default: 80
        },
        height: {
          types: [
            "number"
          ],
          description: "SVG height in px. Default 32.",
          default: 32
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width in px. Default 2.",
          default: 2
        },
        fill: {
          types: [
            "boolean"
          ],
          description: "Fill the area under the line with the stroke color at low opacity.",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "date-range-picker": {
      type: "date-range-picker",
      category: "component",
      sourceFile: "components/core/molecules/DateRangePicker.tsx",
      tier: "molecules",
      family: "core",
      description: "DateRangePicker component",
      suggestedFor: [
        "date",
        "range",
        "picker",
        "date range picker"
      ],
      typicalSize: "medium",
      propsSchema: {
        from: {
          types: [
            "string"
          ],
          description: "Controlled `from` (ISO date YYYY-MM-DD)."
        },
        to: {
          types: [
            "string"
          ],
          description: "Controlled `to` (ISO date YYYY-MM-DD)."
        },
        event: {
          types: [
            "string"
          ],
          description: "Event name emitted as `UI:{event}` with payload `{ from, to }`. Optional \u2014 when omitted, only the `onChange` callback fires.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "from",
              type: "string",
              required: true
            },
            {
              name: "to",
              type: "string",
              required: true
            }
          ]
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Callback fired alongside the bus emit.",
          kind: "callback",
          callbackArgs: [
            {
              name: "range",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "from",
                  "to"
                ]
              }
            }
          ]
        },
        presets: {
          types: [
            "array"
          ],
          description: "Preset shortcuts. Defaults to the translated standard finance set (Last 7d / Last 30d / This Month / This Quarter / YTD). Pass an empty array to hide presets entirely.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string"
                ]
              },
              range: {
                types: [
                  "function",
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "from",
                  "to"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        fromLabel: {
          types: [
            "string"
          ],
          description: "From-input label."
        },
        toLabel: {
          types: [
            "string"
          ],
          description: "To-input label."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    aside: {
      type: "aside",
      category: "component",
      sourceFile: "components/core/atoms/Aside.tsx",
      tier: "atoms",
      family: "core",
      description: "Aside \u2014 a secondary side panel that hosts navigation links or supplementary content alongside a main content area.",
      capabilities: "settings navigation sidebar, preferences menu panel, account settings nav, secondary panel, side navigation rail",
      suggestedFor: [
        "aside"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Aside contents"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    dialog: {
      type: "dialog",
      category: "container",
      sourceFile: "components/core/atoms/Dialog.tsx",
      tier: "atoms",
      family: "core",
      description: 'Dialog Atom Component  Semantic wrapper for the native `<dialog>` element. Owns the raw HTML rendering so molecules (Modal, ConfirmDialog, etc.) can compose the dialog primitive without dropping back to raw HTML.  Defaults `role="dialog"` and `aria-modal="true"`; both overridable for non-modal use cases.',
      suggestedFor: [
        "dialog"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        children: {
          types: [
            "node"
          ],
          description: "Dialog contents"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "tag-input": {
      type: "tag-input",
      category: "form",
      sourceFile: "components/core/molecules/TagInput.tsx",
      tier: "molecules",
      family: "core",
      description: "TagInput component",
      suggestedFor: [
        "tag",
        "input",
        "tag input"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "array"
          ],
          description: "Current list of tags.",
          required: true,
          items: {
            types: [
              "string"
            ]
          }
        },
        onChange: {
          types: [
            "function"
          ],
          description: "Direct callback emitted on every change. Stays as the Storybook / non-trait contract; trait-driven schemas should prefer the bus events below.",
          kind: "callback",
          callbackArgs: [
            {
              name: "next",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        placeholder: {
          types: [
            "string"
          ],
          description: 'Placeholder for the entry input. Default: `"Type and press Enter\u2026"`.'
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable add + remove interactions.",
          default: false
        },
        variant: {
          types: [
            "string"
          ],
          description: "Variant applied to each chip Badge. Default: `'default'`.",
          enumValues: [
            "default",
            "primary",
            "secondary",
            "success",
            "warning",
            "danger",
            "error",
            "info",
            "neutral"
          ],
          default: "default"
        },
        unique: {
          types: [
            "boolean"
          ],
          description: "Suppress duplicate entries. Default: `true`.",
          default: true
        },
        helperText: {
          types: [
            "string"
          ],
          description: "Helper text rendered under the input."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the outer container."
        },
        addEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a tag is added: `UI:{addEvent}` with payload `{ tag: string, value: string[] }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "tag",
              type: "string",
              required: true
            },
            {
              name: "value",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        removeEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a tag is removed: `UI:{removeEvent}` with payload `{ tag: string, index: number, value: string[] }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "tag",
              type: "string",
              required: true
            },
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "value",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "table-view": {
      type: "table-view",
      category: "display",
      sourceFile: "components/core/molecules/TableView.tsx",
      tier: "molecules",
      family: "core",
      description: "TableView \u2014 sortable, selectable data table rendering rows over configurable columns, with inline row actions and grouping.",
      capabilities: "admin console table, records list, CRUD data table, user list, manage-users grid, sortable columns, row selection with bulk actions, grouped list view",
      fieldsContract: "display",
      minUsableWidth: 240,
      suggestedFor: [
        "table",
        "view",
        "table view"
      ],
      typicalSize: "medium",
      propsSchema: {
        dragGroup: {
          types: [
            "string"
          ],
          description: "When set, items in this container are draggable and announce this group as their source"
        },
        accepts: {
          types: [
            "string"
          ],
          description: "When set, the container accepts drops whose dragGroup matches this value (or '*' for any group)"
        },
        sortable: {
          types: [
            "boolean"
          ],
          description: "Enable in-container reorder; items can be dragged to new positions among siblings"
        },
        dropEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on cross-container drop. Payload: { id, sourceGroup, targetGroup, newIndex }"
        },
        reorderEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired on in-container reorder. Payload: { id, oldIndex, newIndex }"
        },
        positionEvent: {
          types: [
            "string"
          ],
          description: "Bus event fired N times per drop (one per item in affected zones) so the trait can persist new positions for the WHOLE zone, not just the moved item. Payload: { id, position }"
        },
        dndItemIdField: {
          types: [
            "string"
          ],
          description: "Row field used as the @dnd-kit draggable id (default 'id')"
        },
        dndRoot: {
          types: [
            "boolean"
          ],
          description: "Mark this container as the DnD root for its subtree \u2014 descendant zones share one DndContext (cross-container drag). Use on an outer container whose own items are not draggable."
        },
        emptyIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Domain icon for the empty state."
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: 'Specific title ("No orders yet").'
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "What will appear here once rows exist."
        },
        emptyAction: {
          types: [
            "object"
          ],
          description: "Create-the-first-item action.",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ],
              kind: "event"
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Schema entity data \u2014 the collection of rows to render.",
          required: true,
          kind: "entity",
          cardinality: "collection"
        },
        columns: {
          types: [
            "array"
          ],
          description: "Column definitions. The compiler emits `columns`; `fields` is the alias.",
          items: {
            types: [
              "object"
            ],
            properties: {
              key: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "string"
                ],
                enumValues: [
                  "normal",
                  "medium",
                  "semibold"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "badge",
                  "date",
                  "currency",
                  "number",
                  "percent",
                  "boolean"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              sortable: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "key"
            ]
          }
        },
        fields: {
          types: [
            "array"
          ],
          description: "Alias for `columns`.",
          items: {
            types: [
              "object"
            ],
            properties: {
              key: {
                types: [
                  "string"
                ]
              },
              field: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              className: {
                types: [
                  "string"
                ]
              },
              weight: {
                types: [
                  "string"
                ],
                enumValues: [
                  "normal",
                  "medium",
                  "semibold"
                ]
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "badge",
                  "date",
                  "currency",
                  "number",
                  "percent",
                  "boolean"
                ]
              },
              colorMap: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "default",
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "danger",
                    "error",
                    "info",
                    "neutral",
                    "destructive"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "h3",
                  "h4"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              sortable: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "key"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        itemActions: {
          types: [
            "array"
          ],
          description: "Per-row actions, collected under a trailing kebab overflow menu.",
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              },
              when: {
                types: [
                  "sexpr",
                  "function"
                ]
              },
              payload: {
                types: [
                  "sexpr",
                  "function"
                ]
              }
            },
            required: [
              "label",
              "event"
            ]
          }
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: "maxInlineActions prop"
        },
        itemClickEvent: {
          types: [
            "string"
          ],
          description: "When set, the whole row is clickable and emits UI:{itemClickEvent} with { id, row } (action-button clicks stopPropagation so they still win). Mirrors DataList's contract. Omit = rows are not clickable.",
          kind: "event-ref",
          entityRowField: "row",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "row",
              type: "@entity",
              required: true
            }
          ]
        },
        selectable: {
          types: [
            "boolean"
          ],
          description: "Render a leading checkbox column. Selection changes emit `selectEvent`.",
          default: false
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on selection change: UI:{selectEvent} with { selectedIds }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedIds",
              type: "array",
              required: true,
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently-selected ids (controlled). Falls back to local state when omitted.",
          items: {
            types: [
              "string"
            ]
          }
        },
        sortEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on sortable-header click: UI:{sortEvent} with { column, direction }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "column",
              type: "string",
              required: true
            },
            {
              name: "direction",
              type: "string",
              required: true,
              enumValues: [
                "asc",
                "desc"
              ]
            }
          ]
        },
        sortColumn: {
          types: [
            "string"
          ],
          description: "Current sort column (display hint for the active header arrow)."
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction (display hint).",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the table root."
        },
        emptyMessage: {
          types: [
            "string"
          ],
          description: "Message shown when there are no rows."
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state.",
          default: false
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton drawn while loading, and the shape an empty slot shows while this element's server render is in flight (`none` opts out).",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          default: "table",
          tier: "presentation"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state.",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        groupBy: {
          types: [
            "string"
          ],
          description: "Group rows under section headers by a field value."
        },
        pageSize: {
          types: [
            "number"
          ],
          description: 'Max rows before a "Show More" button. Defaults to 0 (show all).',
          default: 0
        },
        children: {
          types: [
            "function"
          ],
          description: "Custom per-row render. When provided, the whole row content is delegated to this function (columns are ignored for the body; the header still renders). Mirrors DataList's children render prop.",
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        renderItem: {
          types: [
            "function"
          ],
          description: 'Per-row render function (schema alias). In .orb: ["fn","item",{...}]. The compiler converts this to the children render prop. In .lolo, author the per-row renderer as renderItem: (fn item <Component \u2026={@item.field}/>), binding per-item fields via @item.field.',
          callbackArgs: [
            {
              name: "item",
              type: "object"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        look: {
          types: [
            "string"
          ],
          description: "Layer 2 visual treatment \u2014 mirrors the data-list / entity-table look enum so authors share one knob name across row renderers.",
          enumValues: [
            "dense",
            "spacious",
            "striped",
            "borderless",
            "bordered"
          ],
          default: "dense"
        },
        relationsData: {
          types: [
            "object"
          ],
          description: "Relation display data: { fieldName: [{value, label}] } \u2014 injected server-side by the runtime (relation-option injection) or bound by compiled codegen; resolves stored foreign ids to display names for a column whose field is relation-typed. Same contract DetailPanel takes.",
          mapValue: {
            types: [
              "array"
            ],
            items: {
              types: [
                "object"
              ],
              properties: {
                value: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                },
                description: {
                  types: [
                    "string"
                  ]
                },
                disabled: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "value",
                "label"
              ]
            }
          }
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "stat-card": {
      type: "stat-card",
      category: "display",
      sourceFile: "components/core/organisms/StatCard.tsx",
      tier: "organisms",
      family: "core",
      description: "StatCard component",
      suggestedFor: [
        "stat",
        "card",
        "stat card"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        entity: {
          types: [
            "object",
            "array"
          ],
          description: "Entity data (single record or collection) used to compute metrics.",
          kind: "entity",
          cardinality: "collection"
        },
        label: {
          types: [
            "string"
          ],
          description: "Main label"
        },
        title: {
          types: [
            "string"
          ],
          description: "Title (alias for label)"
        },
        value: {
          types: [
            "string",
            "number",
            "array"
          ],
          description: "Primary value - accepts array from generated code (will use first element or length)",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        previousValue: {
          types: [
            "number"
          ],
          description: "Previous value for comparison"
        },
        currentValue: {
          types: [
            "number"
          ],
          description: "Current value as number for trend calculation"
        },
        trend: {
          types: [
            "number"
          ],
          description: "Manual trend percentage (overrides calculation)"
        },
        trendDirection: {
          types: [
            "string"
          ],
          description: "Trend direction (overrides calculation)",
          enumValues: [
            "up",
            "down",
            "neutral"
          ]
        },
        invertTrend: {
          types: [
            "boolean"
          ],
          description: "Whether up is good (green) or bad (red)",
          default: false
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon to display (Lucide component or icon name string)"
        },
        iconBg: {
          types: [
            "string"
          ],
          description: "Icon background color",
          default: "bg-muted"
        },
        iconColor: {
          types: [
            "string"
          ],
          description: "Icon color",
          default: "text-foreground"
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Subtitle or description"
        },
        action: {
          types: [
            "object"
          ],
          description: "Action button",
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ]
            },
            navigatesTo: {
              types: [
                "string"
              ]
            },
            onClick: {
              types: [
                "function"
              ]
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        metrics: {
          types: [
            "array"
          ],
          description: "Metrics to display (schema format) - accepts readonly for compatibility with generated const arrays",
          items: {
            types: [
              "object"
            ],
            properties: {
              field: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              format: {
                types: [
                  "string"
                ]
              },
              aggregate: {
                types: [
                  "string"
                ],
                enumValues: [
                  "count",
                  "sum"
                ]
              },
              filter: {
                types: [
                  "object"
                ],
                properties: {
                  field: {
                    types: [
                      "string"
                    ]
                  },
                  equals: {
                    types: [
                      "string",
                      "number",
                      "boolean"
                    ]
                  }
                },
                required: [
                  "field",
                  "equals"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "Compact display mode"
        },
        sparklineData: {
          types: [
            "array"
          ],
          description: "Sparkline data points for an inline trend chart",
          items: {
            types: [
              "number"
            ]
          }
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "activation-block": {
      type: "activation-block",
      category: "component",
      sourceFile: "components/core/molecules/ActivationBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "ActivationBlock component",
      suggestedFor: [
        "activation",
        "block",
        "activation block"
      ],
      typicalSize: "medium",
      propsSchema: {
        question: {
          types: [
            "string"
          ],
          description: "The prior-knowledge question",
          required: true
        },
        savedResponse: {
          types: [
            "string"
          ],
          description: "Pre-filled response from saved state"
        },
        saveEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted on save/skip (as `UI:<saveEvent>`). Defaults to 'SAVE_ACTIVATION'.",
          default: "SAVE_ACTIVATION"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "bloom-quiz-block": {
      type: "bloom-quiz-block",
      category: "component",
      sourceFile: "components/core/molecules/BloomQuizBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "BloomQuizBlock component",
      suggestedFor: [
        "bloom",
        "quiz",
        "block",
        "bloom quiz block"
      ],
      typicalSize: "medium",
      propsSchema: {
        level: {
          types: [
            "string"
          ],
          description: "level prop",
          required: true,
          enumValues: [
            "remember",
            "understand",
            "apply",
            "analyze",
            "evaluate",
            "create"
          ]
        },
        question: {
          types: [
            "string"
          ],
          description: "question prop",
          required: true
        },
        answer: {
          types: [
            "string"
          ],
          description: "answer prop",
          required: true
        },
        index: {
          types: [
            "number"
          ],
          description: "Zero-based index (used in the emitted event payload)"
        },
        isAnswered: {
          types: [
            "boolean"
          ],
          description: "Whether the learner has already answered"
        },
        answerEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted on first reveal (as `UI:<answerEvent>`). Defaults to 'ANSWER_BLOOM'.",
          default: "ANSWER_BLOOM"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "connection-block": {
      type: "connection-block",
      category: "component",
      sourceFile: "components/core/molecules/ConnectionBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "ConnectionBlock component",
      suggestedFor: [
        "connection",
        "block",
        "connection block"
      ],
      typicalSize: "medium",
      propsSchema: {
        content: {
          types: [
            "string"
          ],
          description: "Markdown content summarising what the learner already knows",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "reflection-block": {
      type: "reflection-block",
      category: "component",
      sourceFile: "components/core/molecules/ReflectionBlock.tsx",
      tier: "molecules",
      family: "core",
      description: "ReflectionBlock component",
      suggestedFor: [
        "reflection",
        "block",
        "reflection block"
      ],
      typicalSize: "medium",
      propsSchema: {
        prompt: {
          types: [
            "string"
          ],
          description: "The reflection prompt",
          required: true
        },
        index: {
          types: [
            "number"
          ],
          description: "Zero-based index of this block (used in the emitted event payload)",
          required: true
        },
        savedNote: {
          types: [
            "string"
          ],
          description: "Pre-filled note from saved state"
        },
        saveEvent: {
          types: [
            "string"
          ],
          description: "Event name emitted on save (as `UI:<saveEvent>`). Defaults to 'SAVE_REFLECTION'.",
          default: "SAVE_REFLECTION"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "code-runner-panel": {
      type: "code-runner-panel",
      category: "display",
      sourceFile: "components/core/organisms/CodeRunnerPanel.tsx",
      tier: "organisms",
      family: "core",
      description: "CodeRunnerPanel component",
      suggestedFor: [
        "code",
        "runner",
        "panel",
        "code runner panel"
      ],
      typicalSize: "large",
      propsSchema: {
        code: {
          types: [
            "string"
          ],
          description: "Initial code content",
          required: true
        },
        language: {
          types: [
            "string"
          ],
          description: "Programming language for syntax highlighting",
          required: true
        },
        runnable: {
          types: [
            "boolean"
          ],
          description: "Whether the panel is editable and runnable (default false = read-only code block)",
          default: false
        },
        runEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on Run as `UI:<runEvent>` with `{ code, language, runId }`. Defaults to 'RUN_CODE'.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "code",
              type: "string",
              required: true
            },
            {
              name: "language",
              type: "string",
              required: true
            },
            {
              name: "runId",
              type: "string",
              required: true
            }
          ],
          default: "RUN_CODE"
        },
        resetEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on Reset as `UI:<resetEvent>` with `{ runId }`; Reset only restores the code when absent.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "runId",
              type: "string",
              required: true
            }
          ]
        },
        runId: {
          types: [
            "string"
          ],
          description: "Identifies this panel in its run/reset payloads (e.g. a lesson segment); empty when one panel stands alone.",
          default: ""
        },
        output: {
          types: [
            "object"
          ],
          description: "The program's result for the last run, bound back into the panel.",
          properties: {
            stdout: {
              types: [
                "string"
              ]
            },
            stderr: {
              types: [
                "string"
              ]
            },
            exitCode: {
              types: [
                "number"
              ]
            },
            testResults: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  input: {
                    types: [
                      "string"
                    ]
                  },
                  expectedOutput: {
                    types: [
                      "string"
                    ]
                  },
                  actualOutput: {
                    types: [
                      "string"
                    ]
                  },
                  passed: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "input",
                  "expectedOutput",
                  "actualOutput",
                  "passed"
                ]
              }
            }
          },
          propertyRequired: [
            "stdout",
            "stderr",
            "exitCode",
            "testResults"
          ],
          default: null
        },
        running: {
          types: [
            "boolean"
          ],
          description: "True while the program is running the code.",
          default: false
        },
        error: {
          types: [
            "string"
          ],
          description: "A run failure message from the program; replaces the output body.",
          default: null
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "segment-renderer": {
      type: "segment-renderer",
      category: "display",
      sourceFile: "components/core/organisms/SegmentRenderer.tsx",
      tier: "organisms",
      family: "core",
      description: "SegmentRenderer component",
      suggestedFor: [
        "segment",
        "renderer",
        "segment renderer"
      ],
      typicalSize: "large",
      propsSchema: {
        segments: {
          types: [
            "array"
          ],
          description: "Parsed lesson segments (see `parseLessonSegments`). Ignored when `lesson` is given.",
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "markdown"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "code"
                    ]
                  },
                  language: {
                    types: [
                      "string"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  runnable: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "language",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "quiz"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  answer: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "question",
                  "answer"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "activate"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "question"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "connect"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "content"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "reflect"
                    ]
                  },
                  prompt: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "prompt"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "bloom"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "remember",
                      "understand",
                      "apply",
                      "analyze",
                      "evaluate",
                      "create"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  answer: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "question",
                  "answer"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "visualization"
                    ]
                  },
                  visualizationType: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "algorithms",
                      "math",
                      "physics",
                      "biology",
                      "chemistry",
                      "probability"
                    ]
                  },
                  description: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "type",
                  "visualizationType",
                  "description"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        lesson: {
          types: [
            "string"
          ],
          description: "Raw lesson markdown with learning tags; parsed with `parseLessonSegments` and rendered"
        },
        activationSaveEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the activation prompt is saved or skipped (as `UI:<activationSaveEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "response",
              type: "string",
              required: true
            }
          ]
        },
        reflectionSaveEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a reflection note is saved (as `UI:<reflectionSaveEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "note",
              type: "string",
              required: true
            }
          ]
        },
        bloomAnswerEvent: {
          types: [
            "string"
          ],
          description: "Event emitted on the first reveal of a Bloom question (as `UI:<bloomAnswerEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "level",
              type: "string",
              required: true,
              enumValues: [
                "remember",
                "understand",
                "apply",
                "analyze",
                "evaluate",
                "create"
              ]
            }
          ]
        },
        askEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Ask on selected text (as `UI:<askEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        noteEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when the reader picks Note on selected text (as `UI:<noteEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "selectedText",
              type: "string",
              required: true
            }
          ]
        },
        askLabel: {
          types: [
            "string"
          ],
          description: "Ask action label (default: translated)"
        },
        noteLabel: {
          types: [
            "string"
          ],
          description: "Note action label (default: translated)"
        },
        annotations: {
          types: [
            "array"
          ],
          description: "Passages to highlight. Each `text` is matched exactly against the markdown segments, first segment whose source contains it, first occurrence inside one rendered text node (outside code); one that spans formatting is not highlighted.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "question",
                  "note"
                ]
              }
            },
            required: [
              "id",
              "text",
              "kind"
            ]
          }
        },
        annotationEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a highlighted passage is clicked (as `UI:<annotationEvent>`)",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "annotationId",
              type: "string",
              required: false
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes for the root container"
        },
        containerClassName: {
          types: [
            "string"
          ],
          description: "CSS classes for the outer wrapping div"
        },
        userProgress: {
          types: [
            "object"
          ],
          description: "User progress for restoring activation/reflection state",
          properties: {
            activationResponse: {
              types: [
                "string"
              ]
            },
            reflectionNotes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            bloomAnswered: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "boolean"
                ]
              }
            }
          }
        },
        runCodeEvent: {
          types: [
            "string"
          ],
          description: "Event a runnable code block emits on Run (`{ code, language, runId }`, runId = the segment index). Omit to render runnable blocks read-only.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "code",
              type: "string",
              required: true
            },
            {
              name: "language",
              type: "string",
              required: true
            },
            {
              name: "runId",
              type: "string",
              required: true
            }
          ]
        },
        resetCodeEvent: {
          types: [
            "string"
          ],
          description: "Event a runnable code block emits on Reset (`{ runId }`).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "runId",
              type: "string",
              required: true
            }
          ]
        },
        codeOutputs: {
          types: [
            "object"
          ],
          description: "The program's run results, keyed by segment runId.",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              stdout: {
                types: [
                  "string"
                ]
              },
              stderr: {
                types: [
                  "string"
                ]
              },
              exitCode: {
                types: [
                  "number"
                ]
              },
              testResults: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    input: {
                      types: [
                        "string"
                      ]
                    },
                    expectedOutput: {
                      types: [
                        "string"
                      ]
                    },
                    actualOutput: {
                      types: [
                        "string"
                      ]
                    },
                    passed: {
                      types: [
                        "boolean"
                      ]
                    }
                  },
                  required: [
                    "input",
                    "expectedOutput",
                    "actualOutput",
                    "passed"
                  ]
                }
              }
            },
            required: [
              "stdout",
              "stderr",
              "exitCode",
              "testResults"
            ]
          }
        },
        runningCode: {
          types: [
            "string"
          ],
          description: "The runId the program is currently running."
        },
        onRenderVisualization: {
          types: [
            "function"
          ],
          description: "Optional render slot for `visualization` segment types. When not provided, visualization segments are silently skipped. Callers can wire this to any custom component or orbital generator.",
          callbackArgs: [
            {
              name: "type",
              type: "string"
            },
            {
              name: "description",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ],
          renderCallback: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "chat-bar": {
      type: "chat-bar",
      category: "display",
      sourceFile: "components/core/organisms/ChatBar.tsx",
      tier: "organisms",
      family: "core",
      description: "ChatBar component",
      suggestedFor: [
        "chat",
        "bar",
        "chat bar"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        status: {
          types: [
            "string"
          ],
          description: "Pipeline status",
          enumValues: [
            "idle",
            "running",
            "paused",
            "complete",
            "error"
          ],
          default: "idle"
        },
        activeGate: {
          types: [
            "string"
          ],
          description: 'Currently active gate label, e.g. "Gate 2: State machines"'
        },
        jepaValidity: {
          types: [
            "number"
          ],
          description: "JEPA validity probability 0-1"
        },
        runningIndicator: {
          types: [
            "node"
          ],
          description: "Shown beside the gate while `status` is running; defaults to a Spinner (a host passes its brand mark)."
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Input placeholder text"
        },
        context: {
          types: [
            "string"
          ],
          description: "Agent context description"
        },
        sendEvent: {
          types: [
            "string"
          ],
          description: 'The event a message is sent as \u2014 `UI:{sendEvent}` with `{ message }`, from Enter or the send button. @default "CHAT_SEND"',
          kind: "event",
          default: "CHAT_SEND"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "subagent-trace-panel": {
      type: "subagent-trace-panel",
      category: "display",
      sourceFile: "components/core/organisms/SubagentTracePanel.tsx",
      tier: "organisms",
      family: "core",
      description: "SubagentTracePanel component",
      suggestedFor: [
        "subagent",
        "trace",
        "panel",
        "subagent trace panel"
      ],
      typicalSize: "large",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state (UiError)",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "Current sort field"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        subagents: {
          types: [
            "array"
          ],
          description: "All known subagents from the runner.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              role: {
                types: [
                  "string"
                ]
              },
              orbitalName: {
                types: [
                  "string"
                ]
              },
              parentId: {
                types: [
                  "string"
                ]
              },
              status: {
                types: [
                  "string"
                ],
                enumValues: [
                  "running",
                  "complete",
                  "error"
                ]
              },
              task: {
                types: [
                  "string"
                ]
              },
              messages: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    message: {
                      types: [
                        "string"
                      ]
                    },
                    tool: {
                      types: [
                        "string"
                      ]
                    },
                    timestamp: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "message",
                    "timestamp"
                  ]
                }
              },
              durationMs: {
                types: [
                  "number"
                ]
              },
              timeline: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  variants: [
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "message"
                          ]
                        },
                        role: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "user",
                            "assistant",
                            "system",
                            "tool"
                          ]
                        },
                        content: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        },
                        isStreaming: {
                          types: [
                            "boolean"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "role",
                        "content",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "tool_call"
                          ]
                        },
                        tool: {
                          types: [
                            "string"
                          ]
                        },
                        argsText: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        },
                        isExecuting: {
                          types: [
                            "boolean"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "tool",
                        "argsText",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "tool_result"
                          ]
                        },
                        tool: {
                          types: [
                            "string"
                          ]
                        },
                        resultText: {
                          types: [
                            "string"
                          ]
                        },
                        success: {
                          types: [
                            "boolean"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        },
                        durationMs: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "tool",
                        "resultText",
                        "success",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "file_operation"
                          ]
                        },
                        operation: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "ls",
                            "read_file",
                            "write_file",
                            "edit_file"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        success: {
                          types: [
                            "boolean"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "operation",
                        "path",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "schema_diff"
                          ]
                        },
                        filePath: {
                          types: [
                            "string"
                          ]
                        },
                        hunks: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              oldStart: {
                                types: [
                                  "number"
                                ]
                              },
                              oldLines: {
                                types: [
                                  "number"
                                ]
                              },
                              newStart: {
                                types: [
                                  "number"
                                ]
                              },
                              newLines: {
                                types: [
                                  "number"
                                ]
                              },
                              lines: {
                                types: [
                                  "array"
                                ],
                                items: {
                                  types: [
                                    "object"
                                  ],
                                  properties: {
                                    type: {
                                      types: [
                                        "string"
                                      ],
                                      enumValues: [
                                        "add",
                                        "remove",
                                        "context"
                                      ]
                                    },
                                    content: {
                                      types: [
                                        "string"
                                      ]
                                    }
                                  },
                                  required: [
                                    "type",
                                    "content"
                                  ]
                                }
                              }
                            },
                            required: [
                              "oldStart",
                              "oldLines",
                              "newStart",
                              "newLines",
                              "lines"
                            ]
                          }
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "filePath",
                        "hunks",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "error"
                          ]
                        },
                        message: {
                          types: [
                            "string"
                          ]
                        },
                        code: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "message",
                        "timestamp"
                      ]
                    },
                    {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "milestone"
                          ]
                        },
                        milestone: {
                          types: [
                            "string"
                          ]
                        },
                        summary: {
                          types: [
                            "string"
                          ]
                        },
                        timestamp: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "type",
                        "milestone",
                        "timestamp"
                      ]
                    }
                  ],
                  discriminator: "type"
                }
              }
            },
            required: [
              "id",
              "name",
              "role",
              "status",
              "task",
              "messages"
            ]
          }
        },
        focusedOrbital: {
          types: [
            "string"
          ],
          description: "Current canvas focus orbital \u2014 only used by overlay mode. Tab mode ignores this."
        },
        disclosureLevel: {
          types: [
            "number"
          ],
          description: "Drives density: 1-2 \u2192 compact, 3-4 \u2192 full activity embed (when mode='overlay').",
          required: true,
          numericEnumValues: [
            1,
            2,
            3,
            4
          ]
        },
        open: {
          types: [
            "boolean"
          ],
          description: "Whether the panel is visible. Typically auto-opens during generation in overlay mode.",
          required: true
        },
        onClose: {
          types: [
            "function"
          ],
          description: "Optional dismiss handler.",
          kind: "callback",
          callbackArgs: []
        },
        coordinatorActivities: {
          types: [
            "array"
          ],
          description: "Full coordinator activities (tool calls, results, messages, errors).",
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "message"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "user",
                      "assistant",
                      "system"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isStreaming: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "role",
                  "content",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_call"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  argsText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isExecuting: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "argsText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_result"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  detail: {
                    types: [
                      "string"
                    ]
                  },
                  rows: {
                    types: [
                      "number"
                    ]
                  },
                  resultText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "success",
                  "resultText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "file_operation"
                    ]
                  },
                  operation: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ls",
                      "read_file",
                      "write_file",
                      "edit_file"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "operation",
                  "path",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_diff"
                    ]
                  },
                  filePath: {
                    types: [
                      "string"
                    ]
                  },
                  hunks: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        oldStart: {
                          types: [
                            "number"
                          ]
                        },
                        oldLines: {
                          types: [
                            "number"
                          ]
                        },
                        newStart: {
                          types: [
                            "number"
                          ]
                        },
                        newLines: {
                          types: [
                            "number"
                          ]
                        },
                        lines: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ],
                                enumValues: [
                                  "add",
                                  "remove",
                                  "context"
                                ]
                              },
                              content: {
                                types: [
                                  "string"
                                ]
                              }
                            },
                            required: [
                              "type",
                              "content"
                            ]
                          }
                        }
                      },
                      required: [
                        "oldStart",
                        "oldLines",
                        "newStart",
                        "newLines",
                        "lines"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "filePath",
                  "hunks",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "error"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "coordinator_decision"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  reason: {
                    types: [
                      "string"
                    ]
                  },
                  priorOrganism: {
                    types: [
                      "string"
                    ],
                    nullable: true
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "reason",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "plan_committed"
                    ]
                  },
                  orbitals: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitals",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pending_question"
                    ]
                  },
                  questionId: {
                    types: [
                      "string"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "questionId",
                  "question",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "clarification_question"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "organism",
                      "molecule",
                      "atom_trait"
                    ]
                  },
                  scope: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      }
                    }
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  candidates: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        id: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        description: {
                          types: [
                            "string"
                          ]
                        },
                        whyThisFits: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "id",
                        "label",
                        "description",
                        "whyThisFits"
                      ]
                    }
                  },
                  skipDefault: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "scope",
                  "question",
                  "candidates",
                  "skipDefault",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "analysis"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  organismReason: {
                    types: [
                      "string"
                    ]
                  },
                  complexity: {
                    types: [
                      "object"
                    ],
                    properties: {
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      reasoning: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "category",
                      "reasoning"
                    ]
                  },
                  renames: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to"
                      ]
                    }
                  },
                  deletes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "renames",
                  "deletes",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_started"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_done"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_change"
                    ]
                  },
                  changeKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital-added",
                      "orbital-removed",
                      "entity-fields-changed",
                      "trait-added",
                      "trait-removed",
                      "trait-config-changed",
                      "state-machine-changed",
                      "guard-changed",
                      "effect-changed",
                      "render-ui-changed",
                      "event-wiring-changed",
                      "page-changed",
                      "theme-changed",
                      "behavior-composed"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "changeKind",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "done"
                    ]
                  },
                  orbitalCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalCount",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "cancelled"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "llm_response"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  llm: {
                    types: [
                      "object"
                    ],
                    properties: {
                      service: {
                        types: [
                          "string"
                        ]
                      },
                      provider: {
                        types: [
                          "string"
                        ]
                      },
                      model: {
                        types: [
                          "string"
                        ]
                      },
                      systemPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      userPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      toolCallsJson: {
                        types: [
                          "string"
                        ]
                      },
                      attempt: {
                        types: [
                          "number"
                        ]
                      },
                      durationMs: {
                        types: [
                          "number"
                        ]
                      },
                      promptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      completionTokens: {
                        types: [
                          "number"
                        ]
                      },
                      cachedPromptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      costUSD: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "service",
                      "provider",
                      "model",
                      "systemPrompt",
                      "userPrompt",
                      "durationMs"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "content",
                  "timestamp"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        coordinatorMessages: {
          types: [
            "array"
          ],
          description: "Canonical Coordinator conversation \u2014 same shape live and on reload. When present, takes precedence over `coordinatorActivities` for the Coordinator section.",
          items: {
            types: [
              "object"
            ],
            properties: {
              role: {
                types: [
                  "string"
                ],
                enumValues: [
                  "system",
                  "user",
                  "assistant",
                  "tool"
                ]
              },
              content: {
                types: [
                  "string"
                ]
              },
              toolCalls: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    id: {
                      types: [
                        "string"
                      ]
                    },
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    args: {
                      types: [
                        "object"
                      ],
                      mapValue: {
                        types: [
                          "object"
                        ],
                        jsonValueBrand: true
                      }
                    }
                  },
                  required: [
                    "id",
                    "name",
                    "args"
                  ]
                }
              },
              toolCallId: {
                types: [
                  "string"
                ]
              },
              toolName: {
                types: [
                  "string"
                ]
              },
              reasoningContent: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "role",
              "content"
            ]
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Rendering mode. - `'overlay'` (default): floating side panel positioned absolutely over the canvas. Density follows `disclosureLevel` (compact L1/L2, rich L3+). The `open` flag actually hides the panel when false. - `'tab'`: full-width content for a dedicated 'trace' supplemental tab. Always renders rich (regardless of disclosure level), always visible (the `open` flag is ignored), and includes an empty state when no subagents are present so users see the surface even before generation starts.",
          enumValues: [
            "overlay",
            "tab"
          ],
          default: "overlay"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "canvas-2d": {
      type: "canvas-2d",
      category: "game",
      sourceFile: "components/game/molecules/Canvas2D.tsx",
      tier: "molecules",
      family: "game",
      description: "Canvas2D component",
      suggestedFor: [
        "canvas2",
        "canvas2 d"
      ],
      typicalSize: "medium",
      propsSchema: {
        description: {
          types: [
            "string"
          ],
          description: "Text alternative for the scene, rendered as visually hidden text and linked by aria-describedby."
        },
        className: {
          types: [
            "string"
          ],
          description: "--- Closed-circuit ---"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        projection: {
          types: [
            "string"
          ],
          description: "--- Projection ---",
          enumValues: [
            "isometric",
            "hex",
            "flat",
            "free",
            "side"
          ],
          default: "isometric"
        },
        drawables: {
          types: [
            "array"
          ],
          description: "--- Scene ---",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        backgroundImage: {
          types: [
            "asset",
            "object"
          ],
          description: "Background image (tiled behind iso/hex/flat/free; cover-scaled for atlas slices). A bare URL string is accepted as shorthand for a standalone backdrop.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        tileClickEvent: {
          types: [
            "string"
          ],
          description: "--- Declarative events back to LOLO ---",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        unitClickEvent: {
          types: [
            "string"
          ],
          description: "Unit-click event. The neutral drawable host cannot resolve a click to a per-entity id (descriptors carry only a `ScenePos`, no id), so this prop is accepted but not yet emitted \u2014 the id-hit-test is a tracked fork (docs/Almadar_Std_Game_V2_PLAN.md).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "unitId",
              type: "string",
              required: true
            }
          ]
        },
        tileHoverEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{tileHoverEvent} with the unprojected scene { x, y } on hover.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        tileLeaveEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{tileLeaveEvent} with {} on pointer leave.",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: []
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event (device-agnostic input), emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event, emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "--- Scene-edit mode (a game studio selecting/dragging drawables). Purely",
          default: false
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "The currently-selected drawable id (controlled); `null`/undefined = none.",
          default: null
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Fired on a click that changes selection \u2014 before `selectEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onMove: {
          types: [
            "function"
          ],
          description: "Fired once per drag gesture, on drop \u2014 before `moveEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "x",
              type: "number"
            },
            {
              name: "y",
              type: "number"
            }
          ]
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Declarative selection event, `UI:{selectEvent} { id }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        moveEvent: {
          types: [
            "string"
          ],
          description: "Declarative move event, `UI:{moveEvent} { id, x, y }` \u2014 fired once on drop.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        camera: {
          types: [
            "string"
          ],
          description: "--- View config (pure render) ---",
          enumValues: [
            "pan-zoom",
            "follow",
            "fixed"
          ],
          default: "pan-zoom"
        },
        scale: {
          types: [
            "number"
          ],
          description: "Render scale, legacy-squared semantics: on-screen cell \u2248 `256 \xD7 scale\xB2` px (the authored contract every board tuned its value for). Converted internally to the single camera zoom against the board's native tile width, so the cell pitch follows the asset while the on-screen size stays as authored. Ignored when `fit` is on; passed through raw for `free`/`side` (world-pixel-direct).",
          default: 0.4
        },
        tileWidth: {
          types: [
            "number"
          ],
          description: "Native tile/cell width in source px for this board's asset (e.g. 16 for Kenney tiny-dungeon, ~128 for iso blocks). The grid cell pitch follows the asset, so tile textures map 1:1 (crisp, no stretch). Defaults to the detected atlas tile width, else 256."
        },
        fit: {
          types: [
            "boolean"
          ],
          description: "Auto-fit the board's grid extent to the viewport (default false \u2014 boards render at their authored `scale` and overflow \u2192 pan). Opt in for whole-board-overview boards. User wheel/pinch zoom always wins after the initial fit.",
          default: false
        },
        showMinimap: {
          types: [
            "boolean"
          ],
          description: "Toggle minimap overlay.",
          default: true
        },
        followTarget: {
          types: [
            "object"
          ],
          description: "Follow-camera target in scene space (the neutral core `Camera.target`). When `camera:'follow'` the host lerps to keep this point centered.",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        cameraPos: {
          types: [
            "object"
          ],
          description: "Initial camera position in scene space (the neutral core `Camera.pos`). The host projects this to screen space and centers the viewport on it for the first render; ignored once the user pans or a follow target takes over.",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        bgColor: {
          types: [
            "string"
          ],
          description: "Solid backdrop colour \u2014 the base layer under any `backgroundImage`."
        },
        children: {
          types: [
            "node"
          ],
          description: "Declarative JSX drawable children (`<DrawShape .../>` composed in paint order). When `drawables` is empty, each child registers its descriptor via the drawable registry context and the host paints them."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawable: true,
      drawHost: true
    },
    "control-grid": {
      type: "control-grid",
      category: "game",
      sourceFile: "components/game/molecules/ControlGrid.tsx",
      tier: "molecules",
      family: "game",
      description: "ControlGrid component",
      suggestedFor: [
        "control",
        "grid",
        "control grid"
      ],
      typicalSize: "medium",
      propsSchema: {
        kind: {
          types: [
            "string"
          ],
          description: "kind prop",
          required: true,
          enumValues: [
            "dpad",
            "actions"
          ]
        },
        buttons: {
          types: [
            "array"
          ],
          description: 'action buttons (kind="actions")',
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              assetUrl: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id"
            ]
          }
        },
        layout: {
          types: [
            "string"
          ],
          description: 'Layout for kind="actions"',
          enumValues: [
            "horizontal",
            "vertical",
            "diamond"
          ],
          default: "horizontal"
        },
        includeDiagonals: {
          types: [
            "boolean"
          ],
          description: 'Whether to include diagonal buttons (kind="dpad")',
          default: false
        },
        onAction: {
          types: [
            "function"
          ],
          description: "Called when a button/direction is pressed or released",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "pressed",
              type: "boolean"
            }
          ]
        },
        actionEvent: {
          types: [
            "string"
          ],
          description: "Event-bus event emitted with `{ id, pressed }` on press/release",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "pressed",
              type: "boolean",
              required: true
            }
          ]
        },
        onDirection: {
          types: [
            "function"
          ],
          description: 'Called when a direction is pressed or released (kind="dpad" convenience overload)',
          kind: "callback",
          callbackArgs: [
            {
              name: "direction",
              type: "string"
            },
            {
              name: "pressed",
              type: "boolean"
            }
          ]
        },
        directionEvent: {
          types: [
            "string"
          ],
          description: 'Event-bus event emitted with `{ direction, pressed }` (kind="dpad")',
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "direction",
              type: "string",
              required: true,
              enumValues: [
                "up",
                "down",
                "left",
                "right"
              ]
            },
            {
              name: "pressed",
              type: "boolean",
              required: true
            }
          ]
        },
        directionEvents: {
          types: [
            "object"
          ],
          description: 'Per-direction PRESS \u2192 the board\'s SEMANTIC event (kind="dpad"), e.g. `{ left: "LEFT", right: "RIGHT", up: "JUMP" }`. Lets the d-pad emit the SAME intent events as the keyboard so the FSM stays device-agnostic.',
          mapKeyEnum: [
            "up",
            "down",
            "left",
            "right"
          ],
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        directionReleaseEvents: {
          types: [
            "object"
          ],
          description: 'Per-direction RELEASE \u2192 semantic event, e.g. `{ left: "STOP", right: "STOP" }`. Omit a direction to ignore its release.',
          mapKeyEnum: [
            "up",
            "down",
            "left",
            "right"
          ],
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        directionAssets: {
          types: [
            "object"
          ],
          description: 'Per-direction sprite assets for kind="dpad" buttons (e.g. Kenny arrow PNGs). Falls back to arrow emoji.',
          mapKeyEnum: [
            "up",
            "down",
            "left",
            "right"
          ],
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              url: {
                types: [
                  "asset"
                ]
              },
              role: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              animations: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              style: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pixel",
                  "vector",
                  "hd",
                  "1-bit",
                  "isometric"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              dimension: {
                types: [
                  "string"
                ],
                enumValues: [
                  "2d",
                  "3d"
                ]
              },
              aspect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "1:1",
                  "16:9",
                  "5:7",
                  "8:1",
                  "4:1",
                  "5:3",
                  "1:2"
                ]
              },
              atlas: {
                types: [
                  "asset"
                ]
              },
              sprite: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              thumbnailUrl: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "url",
              "role",
              "category"
            ]
          }
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "disabled prop"
        },
        visibility: {
          types: [
            "string"
          ],
          description: '"auto" (default) renders only on coarse-pointer (touch) devices; "always" renders everywhere.',
          enumValues: [
            "auto",
            "always"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-icon": {
      type: "game-icon",
      category: "game",
      sourceFile: "components/core/atoms/GameIcon.tsx",
      tier: "atoms",
      family: "core",
      description: "GameIcon component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "game",
        "icon",
        "game icon"
      ],
      typicalSize: "small",
      propsSchema: {
        assetUrl: {
          types: [
            "object"
          ],
          description: "Asset \u2014 when url is present, renders an img (Asset-primary).",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Lucide component or kebab-case icon name (fallback when no assetUrl).",
          required: true
        },
        size: {
          types: [
            "number",
            "string"
          ],
          description: "Pixel size or named variant.",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alt text for the img (defaults to assetUrl.category)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    vstack: {
      type: "vstack",
      category: "component",
      sourceFile: "components/core/atoms/Stack.tsx",
      tier: "atoms",
      family: "core",
      description: "VStack - Vertical stack shorthand",
      suggestedFor: [
        "stack",
        "v stack"
      ],
      typicalSize: "small",
      propsSchema: {
        gap: {
          types: [
            "string"
          ],
          description: "Gap between children",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items on the cross axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify items on the main axis",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        wrap: {
          types: [
            "boolean"
          ],
          description: "Allow items to wrap",
          default: false
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order of children",
          default: false
        },
        flex: {
          types: [
            "boolean"
          ],
          description: "Fill available space (flex: 1)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        onKeyDown: {
          types: [
            "function"
          ],
          description: "Keyboard handler",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        responsive: {
          types: [
            "boolean"
          ],
          description: "When true, horizontal stacks flip to vertical below the md breakpoint (768px)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    hstack: {
      type: "hstack",
      category: "component",
      sourceFile: "components/core/atoms/Stack.tsx",
      tier: "atoms",
      family: "core",
      description: "HStack - Horizontal stack shorthand",
      suggestedFor: [
        "stack",
        "h stack"
      ],
      typicalSize: "small",
      propsSchema: {
        gap: {
          types: [
            "string"
          ],
          description: "Gap between children",
          enumValues: [
            "none",
            "xs",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl"
          ],
          default: "md"
        },
        align: {
          types: [
            "string"
          ],
          description: "Align items on the cross axis",
          enumValues: [
            "start",
            "center",
            "end",
            "stretch",
            "baseline"
          ],
          default: "stretch"
        },
        justify: {
          types: [
            "string"
          ],
          description: "Justify items on the main axis",
          enumValues: [
            "start",
            "center",
            "end",
            "between",
            "around",
            "evenly"
          ],
          default: "start"
        },
        wrap: {
          types: [
            "boolean"
          ],
          description: "Allow items to wrap",
          default: false
        },
        reverse: {
          types: [
            "boolean"
          ],
          description: "Reverse the order of children",
          default: false
        },
        flex: {
          types: [
            "boolean"
          ],
          description: "Fill available space (flex: 1)",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Custom class name"
        },
        style: {
          types: [
            "object"
          ],
          description: "Inline styles",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "Children elements"
        },
        as: {
          types: [
            "component"
          ],
          description: "HTML element to render as"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Click handler",
          kind: "callback",
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        onKeyDown: {
          types: [
            "function"
          ],
          description: "Keyboard handler",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "e",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        id: {
          types: [
            "string"
          ],
          description: "Element id"
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        responsive: {
          types: [
            "boolean"
          ],
          description: "When true, horizontal stacks flip to vertical below the md breakpoint (768px)",
          default: false
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "form-layout": {
      type: "form-layout",
      category: "form",
      sourceFile: "components/core/molecules/FormSection.tsx",
      tier: "molecules",
      family: "core",
      description: "Form layout with multiple sections",
      suggestedFor: [
        "form",
        "layout",
        "form layout"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state indicator"
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        sortBy: {
          types: [
            "string"
          ],
          description: "\u2500\u2500 Display hints (set by trait via render-ui, never written by organism) \u2500\u2500"
        },
        sortDirection: {
          types: [
            "string"
          ],
          description: "Current sort direction",
          enumValues: [
            "asc",
            "desc"
          ]
        },
        searchValue: {
          types: [
            "string"
          ],
          description: "Current search query value"
        },
        page: {
          types: [
            "number"
          ],
          description: "Current page number (1-indexed)"
        },
        pageSize: {
          types: [
            "number"
          ],
          description: "Number of items per page"
        },
        totalCount: {
          types: [
            "number"
          ],
          description: "Total number of items (for pagination display)"
        },
        activeFilters: {
          types: [
            "object"
          ],
          description: "Active filters, keyed by field name. Scalar-only (`string | number | boolean`) rather than the full `FieldValue` domain: `FieldValue` is a self-referential union (adds `Date`, nested arrays/objects) the pattern-sync scanner can't shape into a `Map string V` knob, so it always degraded to `json`. This narrows out date/array/nested-object filter values \u2014 no current call site in this package constructs this prop, so nothing here breaks, but a caller wiring a date-range or multi-select filter through this prop would need those. Flagged for the coordinator; revert to `Record<string, FieldValue>` if that capability is needed.",
          mapValue: {
            types: [
              "string",
              "number",
              "boolean"
            ]
          }
        },
        selectedIds: {
          types: [
            "array"
          ],
          description: "Currently selected item IDs",
          items: {
            types: [
              "string",
              "number"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        dividers: {
          types: [
            "boolean"
          ],
          description: "Show section dividers",
          default: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "learning-canvas": {
      type: "learning-canvas",
      category: "learning",
      sourceFile: "components/learning/atoms/LearningCanvas.tsx",
      tier: "atoms",
      family: "learning",
      description: "LearningCanvas component",
      suggestedFor: [
        "learning",
        "canvas",
        "learning canvas"
      ],
      typicalSize: "small",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes."
        },
        width: {
          types: [
            "number"
          ],
          description: "Canvas width in CSS pixels.",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "Canvas height in CSS pixels.",
          default: 400
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "Canvas ground: a tone name, `var()` token or literal (default the theme's `--surface-diagram`)."
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette for this canvas: `series-1\u2026N` resolve to these colors instead. For real-world conventions (CPK atom colors, resistor bands); unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        fontFamily: {
          types: [
            "string"
          ],
          description: "Canvas text font family. Falls back to the theme's --font-family-body."
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Declarative shapes to draw.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        drawables: {
          types: [
            "array"
          ],
          description: "Neutral game-canvas drawables (e.g. `draw-sprite`, `draw-fx-layer`) painted in world coordinates via the supplied `projector`. This lets the learning canvas reuse the game drawable vocabulary without reimplementing sprites/FX.",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        projector: {
          types: [
            "object"
          ],
          description: "World-to-pixel projector for `drawables`. When absent, `drawables` are ignored.",
          properties: {
            tileWidth: {
              types: [
                "number"
              ]
            },
            floorHeight: {
              types: [
                "number"
              ]
            },
            diamondTopY: {
              types: [
                "number"
              ]
            },
            squareGrid: {
              types: [
                "boolean"
              ]
            },
            worldPixelDirect: {
              types: [
                "boolean"
              ]
            }
          },
          propertyRequired: [
            "tileWidth",
            "floorHeight",
            "diamondTopY",
            "squareGrid",
            "worldPixelDirect"
          ]
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row (live measurements, scores, counters).",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          },
          synonyms: "chips, stats, measurements"
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels stacked from the bottom-right corner.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          },
          synonyms: "sparkline, time series, history plot"
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` \u2014 to a SEMANTIC event emitted as `UI:{event}` (e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`); keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "Enable pointer interaction (click/hover).",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Enable continuous redraw loop.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "Clicked shape payload: { id?, type?, index }.",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        onShapeHover: {
          types: [
            "function"
          ],
          description: "Hovered shape payload: { id?, type?, index }.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        description: {
          types: [
            "string"
          ],
          description: "Text alternative for the canvas, rendered as visually hidden text and linked by aria-describedby."
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state."
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state.",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawHost: true
    },
    "biology-canvas": {
      type: "biology-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/BiologyCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "BiologyCanvas component",
      suggestedFor: [
        "biology",
        "canvas",
        "biology canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D raster (default) or 3D mesh scene via the lazy three.js host.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        camera: {
          types: [
            "object"
          ],
          description: "3D only: neutral camera pose ({ mode, zoom, fov, azimuth, target }).",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D only: scene light rig as data.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D only: post-processing stack (bloom/vignette).",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        nodes: {
          types: [
            "array"
          ],
          description: "nodes prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "cell",
                  "organelle",
                  "molecule",
                  "organism"
                ]
              },
              shape: {
                types: [
                  "string"
                ],
                enumValues: [
                  "box",
                  "sphere",
                  "capsule",
                  "cylinder",
                  "cone",
                  "torus",
                  "plane",
                  "circle",
                  "polyhedron"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "highlight",
                  "muted"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        edges: {
          types: [
            "array"
          ],
          description: "edges prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              directed: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        compartments: {
          types: [
            "array"
          ],
          description: "Organelle/membrane/cell-boundary ellipses (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "dashed",
                  "dotted"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y",
              "width",
              "height"
            ]
          },
          default: [],
          synonyms: "membrane, organelle region, cell boundary"
        },
        bands: {
          types: [
            "array"
          ],
          description: "Full-width horizontal background stripes, drawn first (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            }
          },
          default: [],
          synonyms: "trophic levels, zones, layers"
        },
        stages: {
          types: [
            "array"
          ],
          description: "Phase/step chips (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pending",
                  "active",
                  "done"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          },
          default: [],
          synonyms: "phases, timeline, cycle"
        },
        stageStyle: {
          types: [
            "string"
          ],
          description: "Layout for `stages`: a bottom timeline strip (default) or a radial ring.",
          enumValues: [
            "timeline",
            "ring"
          ],
          default: "timeline"
        },
        helix: {
          types: [
            "object"
          ],
          description: "A DNA double-helix ladder panel (2D only).",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            width: {
              types: [
                "number"
              ]
            },
            height: {
              types: [
                "number"
              ]
            },
            rungs: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  a: {
                    types: [
                      "string"
                    ]
                  },
                  b: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "paired",
                      "open",
                      "new"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                }
              }
            },
            fork: {
              types: [
                "number"
              ]
            },
            colorA: {
              types: [
                "string"
              ]
            },
            colorB: {
              types: [
                "string"
              ]
            },
            rungColor: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "rungs"
          ],
          synonyms: "DNA ladder, replication fork, base pairs"
        },
        helix3d: {
          types: [
            "object"
          ],
          description: "A 3D DNA double-helix ladder. 3D only.",
          properties: {
            count: {
              types: [
                "number"
              ]
            },
            rungs: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                }
              }
            },
            radius: {
              types: [
                "number"
              ]
            },
            rise: {
              types: [
                "number"
              ]
            },
            twistDeg: {
              types: [
                "number"
              ]
            },
            strandAColor: {
              types: [
                "string"
              ]
            },
            strandBColor: {
              types: [
                "string"
              ]
            },
            backboneRadius: {
              types: [
                "number"
              ]
            },
            rungRadius: {
              types: [
                "number"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            },
            unwoundCount: {
              types: [
                "number"
              ]
            },
            unwindSpread: {
              types: [
                "number"
              ]
            }
          }
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates (2D mode only \u2014 ignored in 3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D only: show the ground grid (default off)."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "3D only: enable shadows. Omitted \u2192 the host default."
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "2D: pointer interaction (click/hover). 3D: orbit camera controls."
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "2D only: continuous redraw loop. 3D motion is entity-state driven.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "chemistry-canvas": {
      type: "chemistry-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/ChemistryCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "ChemistryCanvas component",
      suggestedFor: [
        "chemistry",
        "canvas",
        "chemistry canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D raster (default) or 3D mesh scene via the lazy three.js host.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        camera: {
          types: [
            "object"
          ],
          description: "3D only: neutral camera pose ({ mode, zoom, fov, azimuth, target }).",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D only: scene light rig as data.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D only: post-processing stack (bloom/vignette).",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        atoms: {
          types: [
            "array"
          ],
          description: "atoms prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              element: {
                types: [
                  "string"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              charge: {
                types: [
                  "string"
                ]
              },
              lonePairs: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        bonds: {
          types: [
            "array"
          ],
          description: "bonds prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "single",
                  "double",
                  "triple"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "forming",
                  "breaking",
                  "highlight"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        arrows: {
          types: [
            "array"
          ],
          description: "arrows prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              angle: {
                types: [
                  "number"
                ]
              },
              length: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        bondStyle: {
          types: [
            "string"
          ],
          description: "Bond line rendering: 'thick' varies stroke width by order (default), 'parallel' draws offset parallel strokes. 2D only.",
          enumValues: [
            "thick",
            "parallel"
          ],
          default: "thick"
        },
        containers: {
          types: [
            "array"
          ],
          description: "Beaker/flask/container outlines (2D only).",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              divider: {
                types: [
                  "string"
                ],
                enumValues: [
                  "none",
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              dividerColor: {
                types: [
                  "string"
                ]
              },
              leftLabel: {
                types: [
                  "string"
                ]
              },
              rightLabel: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              level: {
                types: [
                  "number"
                ]
              },
              levelColor: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "width",
              "height"
            ]
          },
          default: [],
          synonyms: "beaker, flask, box, membrane, burette"
        },
        equation: {
          types: [
            "string"
          ],
          description: "Reaction equation text centered at the top of the canvas. 2D only.",
          synonyms: "reaction equation, formula"
        },
        equationColor: {
          types: [
            "string"
          ],
          description: "Equation text color: a tone name, token or literal (default the `ink` tone)."
        },
        lattice3d: {
          types: [
            "object"
          ],
          description: "A 3D crystal lattice block (simple cubic, BCC, FCC, rock salt, diamond, ...). 3D only.",
          properties: {
            basis: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  key: {
                    types: [
                      "string"
                    ]
                  },
                  dx: {
                    types: [
                      "number"
                    ]
                  },
                  dy: {
                    types: [
                      "number"
                    ]
                  },
                  dz: {
                    types: [
                      "number"
                    ]
                  },
                  element: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  xEdge: {
                    types: [
                      "boolean"
                    ]
                  },
                  yEdge: {
                    types: [
                      "boolean"
                    ]
                  },
                  zEdge: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "key",
                  "dx",
                  "dy",
                  "dz"
                ]
              }
            },
            nx: {
              types: [
                "number"
              ]
            },
            ny: {
              types: [
                "number"
              ]
            },
            nz: {
              types: [
                "number"
              ]
            },
            latticeConstant: {
              types: [
                "number"
              ]
            },
            bonds: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  },
                  dx: {
                    types: [
                      "number"
                    ]
                  },
                  dy: {
                    types: [
                      "number"
                    ]
                  },
                  dz: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "from",
                  "to"
                ]
              }
            },
            bondRadius: {
              types: [
                "number"
              ]
            },
            highlightCell: {
              types: [
                "boolean"
              ]
            },
            dimColor: {
              types: [
                "string"
              ]
            },
            showLabels: {
              types: [
                "boolean"
              ]
            },
            selectedId: {
              types: [
                "string"
              ]
            },
            selectedColor: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "basis"
          ]
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates (2D mode only \u2014 ignored in 3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D only: show the ground grid (default off)."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "3D only: enable shadows. Omitted \u2192 the host default."
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "2D: pointer interaction (click/hover). 3D: orbit camera controls."
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "2D only: continuous redraw loop. 3D motion is entity-state driven.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "math-canvas": {
      type: "math-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/MathCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "MathCanvas component",
      suggestedFor: [
        "math",
        "canvas",
        "math canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        xMin: {
          types: [
            "number"
          ],
          description: "xMin prop",
          default: -10
        },
        xMax: {
          types: [
            "number"
          ],
          description: "xMax prop",
          default: 10
        },
        yMin: {
          types: [
            "number"
          ],
          description: "yMin prop",
          default: -10
        },
        yMax: {
          types: [
            "number"
          ],
          description: "yMax prop",
          default: 10
        },
        showAxes: {
          types: [
            "boolean"
          ],
          description: "showAxes prop",
          default: true
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "showGrid prop",
          default: true
        },
        gridStep: {
          types: [
            "number"
          ],
          description: "gridStep prop",
          default: 1
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "Canvas ground: a tone name, `var()` token or literal. Default the theme's `--surface-diagram`."
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        gridColor: {
          types: [
            "string"
          ],
          description: "Grid line color: a tone name, token or literal (default the `grid` tone).",
          default: "grid"
        },
        axisColor: {
          types: [
            "string"
          ],
          description: "Axis line color: a tone name, token or literal (default the `axis` tone).",
          default: "axis"
        },
        aspect: {
          types: [
            "string"
          ],
          description: "How the x and y ranges map to the canvas. `fit` stretches each range to fill the plot (function plots); `equal` uses one scale for both axes so circles stay round (geometry). Default `fit`.",
          enumValues: [
            "fit",
            "equal"
          ],
          default: "fit"
        },
        showTickLabels: {
          types: [
            "boolean"
          ],
          description: "Draw numeric labels on grid lines (default false).",
          default: false
        },
        tickLabelFontSize: {
          types: [
            "number"
          ],
          description: "Font size in px for axis tick labels (default the theme's `xs` step)."
        },
        labelFontSize: {
          types: [
            "number"
          ],
          description: "Font size in px for all other canvas annotations (default the theme's diagram label size)."
        },
        fontFamily: {
          types: [
            "string"
          ],
          description: 'Canvas text font family. Accepts a known game-font key (e.g. "future-narrow") or a CSS font-family string.'
        },
        showCurveLabels: {
          types: [
            "boolean"
          ],
          description: "Draw each curve's `label` at its last in-range sample (default false).",
          default: false
        },
        curves: {
          types: [
            "array"
          ],
          description: "curves prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              samples: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              }
            },
            required: [
              "samples"
            ]
          },
          default: []
        },
        points: {
          types: [
            "array"
          ],
          description: "points prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              style: {
                types: [
                  "string"
                ],
                enumValues: [
                  "closed",
                  "open"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        vectors: {
          types: [
            "array"
          ],
          description: "vectors prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "vx",
              "vy"
            ]
          },
          default: []
        },
        regions: {
          types: [
            "array"
          ],
          description: "Filled areas under curves or between curve pairs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              samples: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              samples2: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              baseline: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "samples"
            ]
          },
          default: []
        },
        bars: {
          types: [
            "array"
          ],
          description: "Riemann/histogram bar strips.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              y0: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "width",
              "y1"
            ]
          },
          default: []
        },
        guides: {
          types: [
            "array"
          ],
          description: "Fixed reference lines (vline/hline).",
          items: {
            types: [
              "object"
            ],
            properties: {
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "vline",
                  "hline"
                ]
              },
              at: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "kind",
              "at"
            ]
          },
          default: []
        },
        angles: {
          types: [
            "array"
          ],
          description: "Angle-sweep arcs at a vertex.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "from",
              "to"
            ]
          },
          default: []
        },
        hops: {
          types: [
            "array"
          ],
          description: "Number-line jump arcs.",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        drawables: {
          types: [
            "array"
          ],
          description: "Game-canvas drawables painted in math world coordinates (e.g. `draw-sprite` for a character sprite on the graph). The canvas maps their `ScenePos` through the same x/y world\u2192pixel transform used for curves/points.",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chips, forwarded verbatim to LearningCanvas.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded verbatim to LearningCanvas.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "interactive prop",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "animate prop",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event (device-agnostic input), emitted as `UI:{event}` \u2014 same contract as the game canvas keyMap; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event, emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawHost: true
    },
    "physics-canvas": {
      type: "physics-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/PhysicsCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "PhysicsCanvas component",
      suggestedFor: [
        "physics",
        "canvas",
        "physics canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D raster (default) or 3D mesh scene via the lazy three.js host.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        camera: {
          types: [
            "object"
          ],
          description: "3D only: neutral camera pose ({ mode, zoom, fov, azimuth, target }).",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D only: scene light rig as data.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D only: post-processing stack (bloom/vignette).",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        bodies: {
          types: [
            "array"
          ],
          description: "bodies prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              },
              fx: {
                types: [
                  "number"
                ]
              },
              fy: {
                types: [
                  "number"
                ]
              },
              fz: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          },
          default: []
        },
        constraints: {
          types: [
            "array"
          ],
          description: "constraints prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "rod",
                  "spring",
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        showVelocity: {
          types: [
            "boolean"
          ],
          description: "showVelocity prop",
          default: true
        },
        showForces: {
          types: [
            "boolean"
          ],
          description: "showForces prop",
          default: false
        },
        velocityScale: {
          types: [
            "number"
          ],
          description: "velocityScale prop",
          default: 20
        },
        forceScale: {
          types: [
            "number"
          ],
          description: "forceScale prop",
          default: 20
        },
        sceneObjects: {
          types: [
            "array"
          ],
          description: "Ground/wall/ramp/box/pivot scene fixtures. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ground",
                  "wall",
                  "ramp",
                  "box",
                  "pivot"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "kind"
            ]
          },
          default: []
        },
        trails: {
          types: [
            "array"
          ],
          description: "Fading trajectory polylines (2D) / trajectory tubes (3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    },
                    z: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              color: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              fade: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "points"
            ]
          },
          default: []
        },
        vectors: {
          types: [
            "array"
          ],
          description: "Named force/velocity/free-body arrows, anchored by point or by body id. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              body: {
                types: [
                  "string"
                ]
              },
              dx: {
                types: [
                  "number"
                ]
              },
              dy: {
                types: [
                  "number"
                ]
              },
              scale: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "dashed",
                  "dotted"
                ]
              }
            },
            required: [
              "dx",
              "dy"
            ]
          },
          default: []
        },
        surface3d: {
          types: [
            "object"
          ],
          description: "A 3D height-field surface mesh (wave, terrain, potential well). 3D only.",
          properties: {
            nx: {
              types: [
                "number"
              ]
            },
            ny: {
              types: [
                "number"
              ]
            },
            heights: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "number"
                ]
              }
            },
            spacing: {
              types: [
                "number"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            bands: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  min: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "color"
                ]
              }
            },
            color: {
              types: [
                "string"
              ]
            },
            opacity: {
              types: [
                "number"
              ]
            },
            flatShading: {
              types: [
                "boolean"
              ]
            }
          },
          propertyRequired: [
            "nx",
            "ny",
            "heights"
          ]
        },
        vectors3d: {
          types: [
            "array"
          ],
          description: "Labeled 3D arrows (field arrows, axis triads). 3D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              dx: {
                types: [
                  "number"
                ]
              },
              dy: {
                types: [
                  "number"
                ]
              },
              dz: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y",
              "dx",
              "dy"
            ]
          },
          default: []
        },
        vectorScale: {
          types: [
            "number"
          ],
          description: "Multiplies every `vectors3d` entry's `dx`/`dy`/`dz` (default 1). 3D only.",
          default: 1
        },
        angles: {
          types: [
            "array"
          ],
          description: "Angle arcs between two bearings. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "x",
              "y",
              "from",
              "to"
            ]
          },
          default: []
        },
        field: {
          types: [
            "object"
          ],
          description: "A single uniform vector/into/out field lattice. 2D only.",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "arrows",
                "into",
                "out"
              ]
            },
            spacing: {
              types: [
                "number"
              ]
            },
            angle: {
              types: [
                "number"
              ]
            },
            size: {
              types: [
                "number"
              ]
            },
            color: {
              types: [
                "string"
              ]
            },
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            width: {
              types: [
                "number"
              ]
            },
            height: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "kind"
          ]
        },
        meters: {
          types: [
            "array"
          ],
          description: "Bottom-left labeled bar gauges. 2D only.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          },
          default: []
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates (2D mode only \u2014 ignored in 3D).",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        readouts: {
          types: [
            "array"
          ],
          description: "Top-right status chip row, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label",
              "value"
            ]
          }
        },
        traces: {
          types: [
            "array"
          ],
          description: "Inset sparkline panels, forwarded to LearningCanvas verbatim.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              series: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    samples: {
                      types: [
                        "array"
                      ],
                      items: {
                        types: [
                          "object"
                        ],
                        properties: {
                          x: {
                            types: [
                              "number"
                            ]
                          },
                          y: {
                            types: [
                              "number"
                            ]
                          }
                        },
                        required: [
                          "x",
                          "y"
                        ]
                      }
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    },
                    label: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "samples"
                  ]
                }
              },
              xLabel: {
                types: [
                  "string"
                ]
              },
              yLabel: {
                types: [
                  "string"
                ]
              },
              frameColor: {
                types: [
                  "string"
                ]
              },
              backgroundColor: {
                types: [
                  "string"
                ]
              },
              backgroundOpacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "series"
            ]
          }
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D only: show the ground grid (default off)."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "3D only: enable shadows. Omitted \u2192 the host default."
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "2D: pointer interaction (click/hover). 3D: orbit camera controls."
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "2D only: continuous redraw loop. 3D motion is entity-state driven.",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "state-graph": {
      type: "state-graph",
      category: "game",
      sourceFile: "components/game/molecules/StateGraph.tsx",
      tier: "molecules",
      family: "game",
      description: `StateGraph (molecule)  Dumb visual renderer for a state-machine graph: lays states out on a circle, draws the player's transitions as SVG arrows, and emits a node-click event. All state (which transitions exist, the pending "from" selection, test results) lives in the .lolo FSM \u2014 this molecule only renders and reports clicks.`,
      suggestedFor: [
        "state",
        "graph",
        "state graph"
      ],
      typicalSize: "medium",
      propsSchema: {
        states: {
          types: [
            "array"
          ],
          description: "All states in the machine (node labels).",
          required: true,
          items: {
            types: [
              "string"
            ]
          }
        },
        transitions: {
          types: [
            "array"
          ],
          description: "Player-built transitions rendered as arrows.",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ]
              },
              guardHint: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to",
              "event"
            ]
          },
          default: []
        },
        currentState: {
          types: [
            "string"
          ],
          description: "State highlighted as current (test playback / initial)."
        },
        selectedState: {
          types: [
            "string"
          ],
          description: "State the player has selected (first click)."
        },
        addingFrom: {
          types: [
            "string"
          ],
          description: 'When set, the graph is in "pick a target" mode from this state.'
        },
        initialState: {
          types: [
            "string"
          ],
          description: "The machine's initial state (ring-marked)."
        },
        width: {
          types: [
            "number"
          ],
          description: "Graph canvas width.",
          default: 500
        },
        height: {
          types: [
            "number"
          ],
          description: "Graph canvas height.",
          default: 400
        },
        nodeClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{nodeClickEvent} with { stateId } when a node is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stateId",
              type: "string",
              required: true
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "state-json-view": {
      type: "state-json-view",
      category: "game",
      sourceFile: "components/game/molecules/StateJsonView.tsx",
      tier: "molecules",
      family: "game",
      description: "StateJsonView Component  Shows a state machine's definition as an expandable code block, so the learner sees the declarative schema behind the visual graph. Dumb molecule: takes the machine as plain typed fields (name/states/initialState/transitions) \u2014 the .lolo organism owns the entity and the molecule renders + stringifies it.",
      suggestedFor: [
        "state",
        "json",
        "view",
        "state json view"
      ],
      typicalSize: "medium",
      propsSchema: {
        name: {
          types: [
            "string"
          ],
          description: "Machine name",
          required: true
        },
        initialState: {
          types: [
            "string"
          ],
          description: "Initial state",
          required: true
        },
        states: {
          types: [
            "array"
          ],
          description: "All state names",
          required: true,
          items: {
            types: [
              "string"
            ]
          }
        },
        transitions: {
          types: [
            "array"
          ],
          description: "The player's wired transitions",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to",
              "event"
            ]
          }
        },
        label: {
          types: [
            "string"
          ],
          description: "Header label"
        },
        defaultExpanded: {
          types: [
            "boolean"
          ],
          description: "Whether the code block is expanded by default",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "algorithm-canvas": {
      type: "algorithm-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/AlgorithmCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "AlgorithmCanvas component",
      suggestedFor: [
        "algorithm",
        "canvas",
        "algorithm canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        bars: {
          types: [
            "array"
          ],
          description: "Sorting/histogram bars; laid out left-to-right, height proportional to value.",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value"
            ]
          },
          default: []
        },
        cells: {
          types: [
            "array"
          ],
          description: "Grid/array/DP cells; laid out on a row/col lattice sized to the extent.",
          items: {
            types: [
              "object"
            ],
            properties: {
              row: {
                types: [
                  "number"
                ]
              },
              col: {
                types: [
                  "number"
                ]
              },
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              corner: {
                types: [
                  "object"
                ],
                properties: {
                  tl: {
                    types: [
                      "string"
                    ]
                  },
                  tr: {
                    types: [
                      "string"
                    ]
                  },
                  bl: {
                    types: [
                      "string"
                    ]
                  },
                  br: {
                    types: [
                      "string"
                    ]
                  }
                }
              }
            },
            required: [
              "row",
              "col"
            ]
          },
          default: []
        },
        pointers: {
          types: [
            "array"
          ],
          description: "Index cursors (i/j/lo/hi/mid); drawn as markers beneath the referenced bar/slot column.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "index"
            ]
          },
          default: []
        },
        ranges: {
          types: [
            "array"
          ],
          description: "Highlighted index ranges over the bars panel (translucent fill bands or labeled brackets).",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "number"
                ]
              },
              to: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "fill",
                  "bracket"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        slots: {
          types: [
            "array"
          ],
          description: "Discrete value slots (registers/variables); laid out in a row or a vertical stack.",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "string",
                  "number"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "empty",
                  "filled",
                  "highlight"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            }
          },
          default: []
        },
        slotOrientation: {
          types: [
            "string"
          ],
          description: "Slot stack direction; horizontal lays slots left-to-right, vertical stacks bottom-up.",
          enumValues: [
            "horizontal",
            "vertical"
          ],
          default: "horizontal"
        },
        frames: {
          types: [
            "array"
          ],
          description: "Recursion/call-stack frames; laid out bottom-up like a real call stack.",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              detail: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "active",
                  "returning",
                  "done"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "label"
            ]
          },
          default: []
        },
        buckets: {
          types: [
            "array"
          ],
          description: "Hash-table buckets; each row is an index chip plus its chained entries.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              entries: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    label: {
                      types: [
                        "string"
                      ]
                    },
                    state: {
                      types: [
                        "string"
                      ],
                      enumValues: [
                        "default",
                        "highlight",
                        "probing"
                      ]
                    },
                    color: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "label"
                  ]
                }
              }
            },
            required: [
              "index",
              "entries"
            ]
          },
          default: []
        },
        auxBars: {
          types: [
            "array"
          ],
          description: "Secondary bar strip inside the bars panel (e.g. a running total beside the primary values).",
          items: {
            types: [
              "object"
            ],
            properties: {
              value: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "value"
            ]
          },
          default: []
        },
        rowLabels: {
          types: [
            "array"
          ],
          description: "Row-header labels drawn to the left of the cells grid.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "index",
              "text"
            ]
          },
          default: []
        },
        colLabels: {
          types: [
            "array"
          ],
          description: "Column-header labels drawn above the cells grid.",
          items: {
            types: [
              "object"
            ],
            properties: {
              index: {
                types: [
                  "number"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "index",
              "text"
            ]
          },
          default: []
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "interactive prop",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "animate prop",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "atlas-image": {
      type: "atlas-image",
      category: "component",
      sourceFile: "components/core/atoms/AtlasImage.tsx",
      tier: "atoms",
      family: "core",
      description: "AtlasImage component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "atlas",
        "image",
        "atlas image"
      ],
      typicalSize: "small",
      propsSchema: {
        asset: {
          types: [
            "object"
          ],
          description: "asset prop",
          properties: {
            url: {
              types: [
                "string"
              ]
            },
            atlas: {
              types: [
                "string"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          }
        },
        size: {
          types: [
            "number"
          ],
          description: "Square pixel size (icon usage). Ignored when `fill`."
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop"
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop"
        },
        fill: {
          types: [
            "boolean"
          ],
          description: "Absolutely fill the nearest positioned ancestor (background usage).",
          default: false
        },
        fit: {
          types: [
            "string"
          ],
          description: "fit prop",
          enumValues: [
            "contain",
            "cover",
            "fill"
          ],
          default: "contain"
        },
        alt: {
          types: [
            "string"
          ],
          description: "alt prop"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        style: {
          types: [
            "object"
          ],
          description: "style prop",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "aria-hidden prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "atlas-panel": {
      type: "atlas-panel",
      category: "component",
      sourceFile: "components/core/atoms/AtlasImage.tsx",
      tier: "atoms",
      family: "core",
      description: "AtlasPanel component",
      suggestedFor: [
        "atlas",
        "panel",
        "atlas panel"
      ],
      typicalSize: "small",
      propsSchema: {
        asset: {
          types: [
            "object"
          ],
          description: "asset prop",
          properties: {
            url: {
              types: [
                "string"
              ]
            },
            atlas: {
              types: [
                "string"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            }
          }
        },
        borderSlice: {
          types: [
            "number"
          ],
          description: "Border thickness (source pixels) for the 9-slice cut.",
          default: 16
        },
        borderWidth: {
          types: [
            "number"
          ],
          description: "Rendered border width in CSS px.",
          default: 16
        },
        mode: {
          types: [
            "string"
          ],
          description: "'nineSlice' scales edges crisp (panels/buttons); 'repeat' tiles the slice (pattern fills).",
          enumValues: [
            "nineSlice",
            "repeat"
          ],
          default: "nineSlice"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        style: {
          types: [
            "object"
          ],
          description: "style prop",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop"
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "aria-hidden prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "draw-shape": {
      type: "draw-shape",
      category: "game",
      sourceFile: "components/game/atoms/DrawShape.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawShape component",
      suggestedFor: [
        "draw",
        "shape",
        "draw shape"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-shape"
          ]
        },
        shape: {
          types: [
            "string"
          ],
          description: "shape prop",
          required: true,
          enumValues: [
            "cell",
            "rect",
            "ellipse",
            "poly",
            "path"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        anchor: {
          types: [
            "string"
          ],
          description: "How the shape aligns to its projected position. Default varies by `shape`.",
          enumValues: [
            "top-left",
            "ground",
            "center"
          ]
        },
        width: {
          types: [
            "number"
          ],
          description: "Rect width in world units (fractions of `projector.tileWidth`)."
        },
        height: {
          types: [
            "number"
          ],
          description: "Rect height in world units (fractions of `projector.tileWidth`)."
        },
        radiusX: {
          types: [
            "number"
          ],
          description: "Ellipse horizontal radius in world units (fractions of `projector.tileWidth`)."
        },
        radiusY: {
          types: [
            "number"
          ],
          description: "Ellipse vertical radius in world units; omitted \u2192 `radiusX` (a circle)."
        },
        offsetX: {
          types: [
            "number"
          ],
          description: "Fine nudge in world units \u2014 applies to every shape kind except `cell` (rect/ellipse from their anchor point; poly/path from the projected top-left)."
        },
        offsetY: {
          types: [
            "number"
          ],
          description: "offsetY prop"
        },
        points: {
          types: [
            "array"
          ],
          description: "Poly vertices as world-unit offsets relative to the cell's projected top-left.",
          items: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          }
        },
        d: {
          types: [
            "string"
          ],
          description: "SVG path data in world units relative to the cell's projected top-left \u2014 same coordinate convention as `points`."
        },
        fill: {
          types: [
            "string"
          ],
          description: "fill prop"
        },
        gradient: {
          types: [
            "object"
          ],
          description: "Gradient fill in world units; overrides `fill` when present.",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "linear",
                "radial",
                "conic"
              ]
            },
            from: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            to: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            center: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            radius: {
              types: [
                "number"
              ]
            },
            angle: {
              types: [
                "number"
              ]
            },
            stops: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  offset: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "offset",
                  "color"
                ]
              }
            }
          },
          propertyRequired: [
            "kind",
            "stops"
          ]
        },
        fillPattern: {
          types: [
            "object"
          ],
          description: "Repeating texture painted over the fill (grain / tiling image).",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "noise",
                "image"
              ]
            },
            url: {
              types: [
                "string"
              ]
            },
            scale: {
              types: [
                "number"
              ]
            },
            alpha: {
              types: [
                "number"
              ]
            },
            color: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "kind"
          ]
        },
        stroke: {
          types: [
            "string"
          ],
          description: "stroke prop"
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "strokeWidth prop"
        },
        strokeGradient: {
          types: [
            "object"
          ],
          description: "Gradient stroke in world units; overrides `stroke` when present.",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "linear",
                "radial",
                "conic"
              ]
            },
            from: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            to: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            center: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            radius: {
              types: [
                "number"
              ]
            },
            angle: {
              types: [
                "number"
              ]
            },
            stops: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  offset: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "offset",
                  "color"
                ]
              }
            }
          },
          propertyRequired: [
            "kind",
            "stops"
          ]
        },
        strokeDash: {
          types: [
            "array"
          ],
          description: "Dash pattern in painter-px (constant under group scale, like `strokeWidth`).",
          items: {
            types: [
              "number"
            ]
          }
        },
        strokeDashOffset: {
          types: [
            "number"
          ],
          description: "Dash phase offset in painter-px; animatable (marching ants)."
        },
        blendMode: {
          types: [
            "string"
          ],
          description: "Composite/blend mode for this shape's draws (`lighter`, `multiply`, `screen`, \u2026).",
          enumValues: [
            "source-over",
            "lighter",
            "multiply",
            "screen",
            "overlay",
            "darken",
            "lighten",
            "color-dodge",
            "color-burn",
            "hard-light",
            "soft-light",
            "difference",
            "exclusion",
            "hue",
            "saturation",
            "color",
            "luminosity",
            "source-atop",
            "destination-over",
            "destination-out",
            "xor"
          ]
        },
        blur: {
          types: [
            "number"
          ],
          description: "Gaussian blur in painter-px. Expensive \u2014 forces an intermediate raster; use sparingly."
        },
        rotate: {
          types: [
            "number"
          ],
          description: "Rotation in radians (painter units, same as `draw-group`), about `pivot`."
        },
        pivot: {
          types: [
            "object"
          ],
          description: "Rotation pivot in world units relative to the cell's projected top-left; default `{x:0.5, y:0.5}` (cell center).",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ]
        },
        shadow: {
          types: [
            "object"
          ],
          description: "Soft glow / drop-shadow behind the shape; `blur` in world units.",
          properties: {
            color: {
              types: [
                "string"
              ]
            },
            blur: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "color",
            "blur"
          ]
        },
        animation: {
          types: [
            "object"
          ],
          description: "Keyframe animation over this shape's props; the host runs a paint clock while present.",
          properties: {
            durationMs: {
              types: [
                "number"
              ]
            },
            loop: {
              types: [
                "boolean"
              ]
            },
            keyframes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  at: {
                    types: [
                      "number"
                    ]
                  },
                  offsetX: {
                    types: [
                      "number"
                    ]
                  },
                  offsetY: {
                    types: [
                      "number"
                    ]
                  },
                  rotate: {
                    types: [
                      "number"
                    ]
                  },
                  opacity: {
                    types: [
                      "number"
                    ]
                  },
                  radiusX: {
                    types: [
                      "number"
                    ]
                  },
                  radiusY: {
                    types: [
                      "number"
                    ]
                  },
                  width: {
                    types: [
                      "number"
                    ]
                  },
                  height: {
                    types: [
                      "number"
                    ]
                  },
                  strokeWidth: {
                    types: [
                      "number"
                    ]
                  },
                  strokeDashOffset: {
                    types: [
                      "number"
                    ]
                  },
                  blur: {
                    types: [
                      "number"
                    ]
                  },
                  fill: {
                    types: [
                      "string"
                    ]
                  },
                  stroke: {
                    types: [
                      "string"
                    ]
                  },
                  shadow: {
                    types: [
                      "object"
                    ],
                    properties: {
                      color: {
                        types: [
                          "string"
                        ]
                      },
                      blur: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "color",
                      "blur"
                    ]
                  }
                },
                required: [
                  "at"
                ]
              }
            }
          },
          propertyRequired: [
            "durationMs",
            "keyframes"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        }
      },
      drawable: true
    },
    "draw-sprite": {
      type: "draw-sprite",
      category: "game",
      sourceFile: "components/game/atoms/DrawSprite.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawSprite component",
      suggestedFor: [
        "draw",
        "sprite",
        "draw sprite"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-sprite"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        asset: {
          types: [
            "object"
          ],
          description: "The image / atlas reference to blit.",
          required: true,
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        anchor: {
          types: [
            "string"
          ],
          description: "How the sprite aligns to its projected position. Default `'top-left'`.",
          enumValues: [
            "top-left",
            "ground",
            "center"
          ]
        },
        width: {
          types: [
            "number"
          ],
          description: "Draw width in world units (fractions of `projector.tileWidth`). Omitted \u2192 one cell on tile grids (`flat`/`iso`/`hex`); native source px on `free`/`side`."
        },
        height: {
          types: [
            "number"
          ],
          description: "Draw height in world units (fractions of `projector.tileWidth`). Omitted \u2192 one cell on tile grids (`flat`/`iso`/`hex`); native source px on `free`/`side`."
        },
        frame: {
          types: [
            "object"
          ],
          description: "Explicit atlas sub-rect override (px); omitted \u2192 resolved from `asset.atlas`/`asset.sprite`.",
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            w: {
              types: [
                "number"
              ]
            },
            h: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y",
            "w",
            "h"
          ]
        },
        animation: {
          types: [
            "string"
          ],
          description: "Animation to play. 3D backend: a named GLB clip (matched case-insensitively). 2D painter: when `asset.atlas` resolves to a `SpriteSheetAtlas` manifest, the canonical row of that name (idle/walk/attack/hit/death) plays off the host paint clock; an explicit `frame` rect overrides."
        },
        loop: {
          types: [
            "boolean"
          ],
          description: "Override the sheet row's loop flag. The paint clock is wall-time, so one-shot rows hold their final frame \u2014 preview pages set `true` to cycle attack/hit/death continuously."
        },
        clockMs: {
          types: [
            "number"
          ],
          description: "Animation clock override in ms (e.g. an fx entry's age since spawn). Omitted \u2192 the host paint clock; one-shot rows anchored to wall-time would otherwise sit on their final frame."
        },
        flipX: {
          types: [
            "boolean"
          ],
          description: "Mirror horizontally (facing)."
        },
        rotation: {
          types: [
            "number"
          ],
          description: "Rotation in radians about the sprite's center."
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        },
        shadow: {
          types: [
            "object"
          ],
          description: "Drop shadow (e.g. a team-colored glow).",
          properties: {
            color: {
              types: [
                "string"
              ]
            },
            blur: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "color",
            "blur"
          ]
        }
      },
      drawable: true
    },
    "draw-text": {
      type: "draw-text",
      category: "game",
      sourceFile: "components/game/atoms/DrawText.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawText component",
      suggestedFor: [
        "draw",
        "text",
        "draw text"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-text"
          ]
        },
        text: {
          types: [
            "string"
          ],
          description: "text prop",
          required: true
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        anchor: {
          types: [
            "string"
          ],
          description: "anchor prop",
          enumValues: [
            "top-left",
            "ground",
            "center"
          ]
        },
        offsetX: {
          types: [
            "number"
          ],
          description: "offsetX prop"
        },
        offsetY: {
          types: [
            "number"
          ],
          description: "offsetY prop"
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop",
          required: true
        },
        font: {
          types: [
            "string"
          ],
          description: "font prop"
        },
        align: {
          types: [
            "string"
          ],
          description: "align prop",
          enumValues: [
            "center",
            "end",
            "left",
            "right",
            "start"
          ]
        },
        baseline: {
          types: [
            "string"
          ],
          description: "baseline prop",
          enumValues: [
            "alphabetic",
            "bottom",
            "hanging",
            "ideographic",
            "middle",
            "top"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "opacity prop"
        }
      },
      drawable: true
    },
    "draw-shape-layer": {
      type: "draw-shape-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawShapeLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawShapeLayer component",
      suggestedFor: [
        "draw",
        "shape",
        "layer",
        "draw shape layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-shape-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: "items prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "draw-shape"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              shape: {
                types: [
                  "string"
                ],
                enumValues: [
                  "cell",
                  "rect",
                  "ellipse",
                  "poly",
                  "path"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              anchor: {
                types: [
                  "string"
                ],
                enumValues: [
                  "top-left",
                  "ground",
                  "center"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              radiusX: {
                types: [
                  "number"
                ]
              },
              radiusY: {
                types: [
                  "number"
                ]
              },
              offsetX: {
                types: [
                  "number"
                ]
              },
              offsetY: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              d: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              gradient: {
                types: [
                  "object"
                ],
                properties: {
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "linear",
                      "radial",
                      "conic"
                    ]
                  },
                  from: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  to: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  center: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  angle: {
                    types: [
                      "number"
                    ]
                  },
                  stops: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        offset: {
                          types: [
                            "number"
                          ]
                        },
                        color: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "offset",
                        "color"
                      ]
                    }
                  }
                },
                required: [
                  "kind",
                  "stops"
                ]
              },
              fillPattern: {
                types: [
                  "object"
                ],
                properties: {
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "noise",
                      "image"
                    ]
                  },
                  url: {
                    types: [
                      "string"
                    ]
                  },
                  scale: {
                    types: [
                      "number"
                    ]
                  },
                  alpha: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "kind"
                ]
              },
              stroke: {
                types: [
                  "string"
                ]
              },
              strokeWidth: {
                types: [
                  "number"
                ]
              },
              strokeGradient: {
                types: [
                  "object"
                ],
                properties: {
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "linear",
                      "radial",
                      "conic"
                    ]
                  },
                  from: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  to: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  center: {
                    types: [
                      "object"
                    ],
                    properties: {
                      x: {
                        types: [
                          "number"
                        ]
                      },
                      y: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "x",
                      "y"
                    ]
                  },
                  radius: {
                    types: [
                      "number"
                    ]
                  },
                  angle: {
                    types: [
                      "number"
                    ]
                  },
                  stops: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        offset: {
                          types: [
                            "number"
                          ]
                        },
                        color: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "offset",
                        "color"
                      ]
                    }
                  }
                },
                required: [
                  "kind",
                  "stops"
                ]
              },
              strokeDash: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              },
              strokeDashOffset: {
                types: [
                  "number"
                ]
              },
              blendMode: {
                types: [
                  "string"
                ],
                enumValues: [
                  "source-over",
                  "lighter",
                  "multiply",
                  "screen",
                  "overlay",
                  "darken",
                  "lighten",
                  "color-dodge",
                  "color-burn",
                  "hard-light",
                  "soft-light",
                  "difference",
                  "exclusion",
                  "hue",
                  "saturation",
                  "color",
                  "luminosity",
                  "source-atop",
                  "destination-over",
                  "destination-out",
                  "xor"
                ]
              },
              blur: {
                types: [
                  "number"
                ]
              },
              rotate: {
                types: [
                  "number"
                ]
              },
              pivot: {
                types: [
                  "object"
                ],
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              shadow: {
                types: [
                  "object"
                ],
                properties: {
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  blur: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "color",
                  "blur"
                ]
              },
              animation: {
                types: [
                  "object"
                ],
                properties: {
                  durationMs: {
                    types: [
                      "number"
                    ]
                  },
                  loop: {
                    types: [
                      "boolean"
                    ]
                  },
                  keyframes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        at: {
                          types: [
                            "number"
                          ]
                        },
                        offsetX: {
                          types: [
                            "number"
                          ]
                        },
                        offsetY: {
                          types: [
                            "number"
                          ]
                        },
                        rotate: {
                          types: [
                            "number"
                          ]
                        },
                        opacity: {
                          types: [
                            "number"
                          ]
                        },
                        radiusX: {
                          types: [
                            "number"
                          ]
                        },
                        radiusY: {
                          types: [
                            "number"
                          ]
                        },
                        width: {
                          types: [
                            "number"
                          ]
                        },
                        height: {
                          types: [
                            "number"
                          ]
                        },
                        strokeWidth: {
                          types: [
                            "number"
                          ]
                        },
                        strokeDashOffset: {
                          types: [
                            "number"
                          ]
                        },
                        blur: {
                          types: [
                            "number"
                          ]
                        },
                        fill: {
                          types: [
                            "string"
                          ]
                        },
                        stroke: {
                          types: [
                            "string"
                          ]
                        },
                        shadow: {
                          types: [
                            "object"
                          ],
                          properties: {
                            color: {
                              types: [
                                "string"
                              ]
                            },
                            blur: {
                              types: [
                                "number"
                              ]
                            }
                          },
                          required: [
                            "color",
                            "blur"
                          ]
                        }
                      },
                      required: [
                        "at"
                      ]
                    }
                  }
                },
                required: [
                  "durationMs",
                  "keyframes"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "type",
              "shape",
              "position"
            ]
          }
        }
      },
      drawable: true
    },
    "draw-sprite-layer": {
      type: "draw-sprite-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawSpriteLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawSpriteLayer component",
      suggestedFor: [
        "draw",
        "sprite",
        "layer",
        "draw sprite layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-sprite-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: "Sprites painted in array order; z-ordering is the caller's responsibility.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "draw-sprite"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              asset: {
                types: [
                  "object"
                ],
                properties: {
                  url: {
                    types: [
                      "asset"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ]
                  },
                  category: {
                    types: [
                      "string"
                    ]
                  },
                  animations: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  style: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pixel",
                      "vector",
                      "hd",
                      "1-bit",
                      "isometric"
                    ]
                  },
                  variant: {
                    types: [
                      "string"
                    ]
                  },
                  dimension: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "2d",
                      "3d"
                    ]
                  },
                  aspect: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "1:1",
                      "16:9",
                      "5:7",
                      "8:1",
                      "4:1",
                      "5:3",
                      "1:2"
                    ]
                  },
                  atlas: {
                    types: [
                      "asset"
                    ]
                  },
                  sprite: {
                    types: [
                      "string"
                    ]
                  },
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  thumbnailUrl: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "url",
                  "role",
                  "category"
                ]
              },
              anchor: {
                types: [
                  "string"
                ],
                enumValues: [
                  "top-left",
                  "ground",
                  "center"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              frame: {
                types: [
                  "object"
                ],
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  w: {
                    types: [
                      "number"
                    ]
                  },
                  h: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y",
                  "w",
                  "h"
                ]
              },
              animation: {
                types: [
                  "string"
                ]
              },
              loop: {
                types: [
                  "boolean"
                ]
              },
              clockMs: {
                types: [
                  "number"
                ]
              },
              flipX: {
                types: [
                  "boolean"
                ]
              },
              rotation: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              shadow: {
                types: [
                  "object"
                ],
                properties: {
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  blur: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "color",
                  "blur"
                ]
              }
            },
            required: [
              "type",
              "position",
              "asset"
            ]
          }
        }
      },
      drawable: true
    },
    "draw-text-layer": {
      type: "draw-text-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawTextLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawTextLayer component",
      suggestedFor: [
        "draw",
        "text",
        "layer",
        "draw text layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-text-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: "items prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "draw-text"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              anchor: {
                types: [
                  "string"
                ],
                enumValues: [
                  "top-left",
                  "ground",
                  "center"
                ]
              },
              offsetX: {
                types: [
                  "number"
                ]
              },
              offsetY: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              font: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "center",
                  "end",
                  "left",
                  "right",
                  "start"
                ]
              },
              baseline: {
                types: [
                  "string"
                ],
                enumValues: [
                  "alphabetic",
                  "bottom",
                  "hanging",
                  "ideographic",
                  "middle",
                  "top"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "type",
              "text",
              "position",
              "color"
            ]
          }
        }
      },
      drawable: true
    },
    canvas: {
      type: "canvas",
      category: "game",
      sourceFile: "components/game/molecules/Canvas.tsx",
      tier: "molecules",
      family: "game",
      description: "Canvas component",
      suggestedFor: [
        "canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        mode: {
          types: [
            "string"
          ],
          description: "Painter: 2D sprite/shape/text raster, or 3D mesh. Default '2d'.",
          enumValues: [
            "2d",
            "3d"
          ],
          default: "2d"
        },
        drawables: {
          types: [
            "array"
          ],
          description: "Neutral drawable descriptors \u2014 the `draw-*` children. Routed to the host's painter.",
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        },
        camera: {
          types: [
            "object"
          ],
          description: "Neutral camera pose. Mapped to the underlying host's framing + zoom.",
          properties: {
            pos: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            target: {
              types: [
                "object"
              ],
              properties: {
                x: {
                  types: [
                    "number"
                  ]
                },
                y: {
                  types: [
                    "number"
                  ]
                },
                z: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "x",
                "y"
              ]
            },
            zoom: {
              types: [
                "number"
              ]
            },
            fov: {
              types: [
                "number"
              ]
            },
            mode: {
              types: [
                "string"
              ],
              enumValues: [
                "isometric",
                "perspective",
                "top-down",
                "front",
                "follow",
                "chase"
              ]
            },
            azimuth: {
              types: [
                "number"
              ]
            },
            elevation: {
              types: [
                "number"
              ]
            },
            controls: {
              types: [
                "boolean"
              ]
            }
          }
        },
        projection: {
          types: [
            "string"
          ],
          description: "2D grid layout (2D mode only): isometric / hex / flat / free / side.",
          enumValues: [
            "isometric",
            "hex",
            "flat",
            "free",
            "side"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "--- Shared view config ---"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        unitScale: {
          types: [
            "number"
          ],
          description: "Unit draw-size multiplier (both painters)."
        },
        showMinimap: {
          types: [
            "boolean"
          ],
          description: "Minimap overlay (2D)."
        },
        fit: {
          types: [
            "boolean"
          ],
          description: "Auto-fit the board's grid extent to the viewport (2D grid layouts; default false \u2014 boards render at their authored `camera.zoom` scale)."
        },
        tileWidth: {
          types: [
            "number"
          ],
          description: "Native tile/cell width in source px of the board's asset (2D grid layouts; defaults to the detected atlas tile width, else 256)."
        },
        backgroundImage: {
          types: [
            "asset",
            "object"
          ],
          description: "Backdrop image (2D iso/hex/flat/free/side).",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "Solid backdrop colour \u2014 2D `side` fill and 3D scene clear colour."
        },
        worldWidth: {
          types: [
            "number"
          ],
          description: "Side/free world bounds (2D `side`) and 3D world extent."
        },
        worldHeight: {
          types: [
            "number"
          ],
          description: "worldHeight prop"
        },
        pixelsPerUnit: {
          types: [
            "number"
          ],
          description: "3D world-unit \u2192 pixel scale (perspective/side depth). 3D only."
        },
        showGrid: {
          types: [
            "boolean"
          ],
          description: "3D-only presentation."
        },
        shadows: {
          types: [
            "boolean"
          ],
          description: "shadows prop"
        },
        showCoordinates: {
          types: [
            "boolean"
          ],
          description: "showCoordinates prop"
        },
        showTileInfo: {
          types: [
            "boolean"
          ],
          description: "showTileInfo prop"
        },
        fogOfWar: {
          types: [
            "array"
          ],
          description: "fogOfWar prop",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "boolean"
              ]
            }
          }
        },
        lighting: {
          types: [
            "object"
          ],
          description: "3D scene light rig as data \u2014 ambient/directional/hemisphere/point lights + optional 'room' environment for PBR reflections. Omitted \u2192 the standard fixed rig.",
          properties: {
            ambient: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            directional: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                position: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "number"
                    ]
                  },
                  tupleItems: [
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    },
                    {
                      types: [
                        "number"
                      ]
                    }
                  ]
                }
              }
            },
            hemisphere: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                color: {
                  types: [
                    "string"
                  ]
                },
                groundColor: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            points: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  intensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  position: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "number"
                      ]
                    },
                    tupleItems: [
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      },
                      {
                        types: [
                          "number"
                        ]
                      }
                    ]
                  },
                  distance: {
                    types: [
                      "number"
                    ]
                  },
                  decay: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "position"
                ]
              }
            },
            environment: {
              types: [
                "string"
              ],
              enumValues: [
                "room",
                "none"
              ]
            },
            toneMapping: {
              types: [
                "string"
              ],
              enumValues: [
                "aces",
                "none"
              ]
            }
          }
        },
        post: {
          types: [
            "object"
          ],
          description: "3D post-processing stack \u2014 bloom (emissive glow / neon) and vignette. Omitted \u2192 no post pass.",
          properties: {
            bloom: {
              types: [
                "object"
              ],
              properties: {
                intensity: {
                  types: [
                    "number"
                  ]
                },
                threshold: {
                  types: [
                    "number"
                  ]
                },
                smoothing: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            vignette: {
              types: [
                "object"
              ],
              properties: {
                offset: {
                  types: [
                    "number"
                  ]
                },
                darkness: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          }
        },
        tileClickEvent: {
          types: [
            "string"
          ],
          description: "--- Shared interaction (LOLO-owned). The painter emitted at runtime depends on",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: false
            },
            {
              name: "y",
              type: "number",
              required: false
            },
            {
              name: "tileId",
              type: "string",
              required: false
            },
            {
              name: "z",
              type: "number",
              required: false
            }
          ]
        },
        unitClickEvent: {
          types: [
            "string"
          ],
          description: "unitClickEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "unitId",
              type: "string",
              required: false
            },
            {
              name: "x",
              type: "number",
              required: false
            },
            {
              name: "z",
              type: "number",
              required: false
            }
          ]
        },
        tileHoverEvent: {
          types: [
            "string"
          ],
          description: "tileHoverEvent prop",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: [
            {
              name: "x",
              type: "number",
              required: false
            },
            {
              name: "y",
              type: "number",
              required: false
            },
            {
              name: "tileId",
              type: "string",
              required: false
            },
            {
              name: "z",
              type: "number",
              required: false
            }
          ]
        },
        tileLeaveEvent: {
          types: [
            "string"
          ],
          description: "tileLeaveEvent prop",
          kind: "event-ref",
          notification: true,
          emitPayloadSchema: []
        },
        featureClickEvent: {
          types: [
            "string"
          ],
          description: "Feature-click event (3D only).",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "featureId",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "z",
              type: "number",
              required: true
            },
            {
              name: "type",
              type: "string",
              required: false
            },
            {
              name: "elevation",
              type: "number",
              required: false
            }
          ]
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event (device-agnostic input), emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` \u2014 optionally prefixed `Mod+` (\u2318/Ctrl), `Shift+`, `Alt+` in that order \u2014 to the board's SEMANTIC event, emitted as `UI:{event}`; keystrokes inside inputs/textareas never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "--- Scene-edit mode (a game studio selecting/dragging drawables). Purely"
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "The currently-selected drawable id (controlled); `null`/undefined = none."
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Fired on a click that changes selection \u2014 before `selectEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onMove: {
          types: [
            "function"
          ],
          description: "Fired once per drag gesture, on drop \u2014 before `moveEvent`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "x",
              type: "number"
            },
            {
              name: "y",
              type: "number"
            }
          ]
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Declarative selection event, `UI:{selectEvent} { id }`.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        moveEvent: {
          types: [
            "string"
          ],
          description: "Declarative move event, `UI:{moveEvent} { id, x, y }` \u2014 fired once on drop.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "x",
              type: "number",
              required: true
            },
            {
              name: "y",
              type: "number",
              required: true
            }
          ]
        },
        children: {
          types: [
            "node"
          ],
          description: "Declarative JSX drawable children (`<DrawShape .../>` composed in paint order). Forwarded to the 2D host, which collects them via drawable registry context."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      },
      drawHost: true
    },
    presence: {
      type: "presence",
      category: "component",
      sourceFile: "components/core/atoms/Presence.tsx",
      tier: "atoms",
      family: "core",
      description: "Presence component",
      suggestedFor: [
        "presence"
      ],
      typicalSize: "small",
      propsSchema: {
        animation: {
          types: [
            "string"
          ],
          description: "Which motion preset to use (maps to `animate-<name>-in/out`).",
          required: true,
          enumValues: [
            "modal",
            "overlay",
            "slide-up",
            "drawer",
            "popover",
            "toast",
            "fade",
            "page"
          ]
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "Per-instance opt-out. Global opt-out is `--motion-enable` / reduced-motion. @default true",
          default: true
        },
        onExited: {
          types: [
            "function"
          ],
          description: "Fired after the exit animation completes (before unmount).",
          kind: "callback",
          notification: true,
          callbackArgs: []
        },
        show: {
          types: [
            "boolean"
          ],
          description: "When false, the exit animation runs, then children unmount.",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "page-transition": {
      type: "page-transition",
      category: "component",
      sourceFile: "components/core/molecules/PageTransition.tsx",
      tier: "molecules",
      family: "core",
      description: "PageTransition component",
      suggestedFor: [
        "page",
        "transition",
        "page transition"
      ],
      typicalSize: "medium",
      propsSchema: {
        locationKey: {
          types: [
            "string"
          ],
          description: "Value that changes on navigation (a page path).",
          required: true
        },
        children: {
          types: [
            "node"
          ],
          description: "children prop",
          required: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "import-preview-tree": {
      type: "import-preview-tree",
      category: "component",
      sourceFile: "components/core/molecules/import/ImportPreviewTree.tsx",
      tier: "molecules",
      family: "core",
      description: "ImportPreviewTree component",
      suggestedFor: [
        "import",
        "preview",
        "tree",
        "import preview tree"
      ],
      typicalSize: "medium",
      propsSchema: {
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind each group tile unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        units: {
          types: [
            "array"
          ],
          description: "Staged units to preview",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              ref: {
                types: [
                  "string"
                ]
              },
              targetEntity: {
                types: [
                  "string"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              fields: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  controlValue: true
                }
              },
              parentRef: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "ref",
              "targetEntity",
              "fields"
            ]
          }
        },
        skipped: {
          types: [
            "array"
          ],
          description: "Elements skipped during extraction, with reasons",
          items: {
            types: [
              "object"
            ],
            properties: {
              ref: {
                types: [
                  "string"
                ]
              },
              reason: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "ref",
              "reason"
            ]
          },
          default: []
        },
        entityDisplay: {
          types: [
            "object"
          ],
          description: "Entity name \u2192 display labels; falls back to the entity name",
          required: true,
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              singular: {
                types: [
                  "string"
                ]
              },
              plural: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "singular",
              "plural"
            ]
          }
        },
        onConfirm: {
          types: [
            "function"
          ],
          description: "Called when the user confirms the staged import",
          kind: "callback",
          callbackArgs: []
        },
        onCancel: {
          types: [
            "function"
          ],
          description: "Called when the user cancels the staged import",
          kind: "callback",
          callbackArgs: []
        },
        confirmLabel: {
          types: [
            "string"
          ],
          description: "Confirm button label"
        },
        cancelLabel: {
          types: [
            "string"
          ],
          description: "Cancel button label"
        },
        indent: {
          types: [
            "number"
          ],
          description: "Indent per tree depth in px (default: 16)",
          default: 16
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "import-progress": {
      type: "import-progress",
      category: "component",
      sourceFile: "components/core/molecules/import/ImportProgress.tsx",
      tier: "molecules",
      family: "core",
      description: "ImportProgress component",
      suggestedFor: [
        "import",
        "progress",
        "import progress"
      ],
      typicalSize: "medium",
      propsSchema: {
        step: {
          types: [
            "string"
          ],
          description: "Current lifecycle step",
          required: true,
          enumValues: [
            "fetching",
            "mapping",
            "reviewing",
            "committing",
            "done",
            "failed"
          ]
        },
        counts: {
          types: [
            "object"
          ],
          description: "Unit counts",
          properties: {
            staged: {
              types: [
                "number"
              ]
            },
            committed: {
              types: [
                "number"
              ]
            },
            failed: {
              types: [
                "number"
              ]
            }
          }
        },
        labels: {
          types: [
            "object"
          ],
          description: "Step label overrides",
          mapKeyEnum: [
            "fetching",
            "mapping",
            "reviewing",
            "committing",
            "done",
            "failed"
          ],
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "import-source-picker": {
      type: "import-source-picker",
      category: "component",
      sourceFile: "components/core/molecules/import/ImportSourcePicker.tsx",
      tier: "molecules",
      family: "core",
      description: "ImportSourcePicker component",
      suggestedFor: [
        "import",
        "source",
        "picker",
        "import source picker"
      ],
      typicalSize: "medium",
      propsSchema: {
        sources: {
          types: [
            "array"
          ],
          description: "Source options to render",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "action",
                  "file"
                ]
              },
              accept: {
                types: [
                  "string"
                ]
              },
              multiple: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Called with the source id when an 'action' option is picked",
          kind: "callback",
          callbackArgs: [
            {
              name: "sourceId",
              type: "string"
            }
          ]
        },
        onFilesSelected: {
          types: [
            "function"
          ],
          description: "Called with the chosen files, read as text, when a 'file' option resolves",
          kind: "callback",
          callbackArgs: [
            {
              name: "files",
              type: "array",
              schema: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    name: {
                      types: [
                        "string"
                      ]
                    },
                    size: {
                      types: [
                        "number"
                      ]
                    },
                    type: {
                      types: [
                        "string"
                      ]
                    },
                    text: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "name",
                    "size",
                    "type",
                    "text"
                  ]
                }
              }
            }
          ]
        },
        title: {
          types: [
            "string"
          ],
          description: "Optional heading above the options"
        },
        moreSources: {
          types: [
            "node"
          ],
          description: "Generic slot for additional sources rendered below the options"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "draw-group": {
      type: "draw-group",
      category: "game",
      sourceFile: "components/game/atoms/DrawGroup.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawGroup component",
      suggestedFor: [
        "draw",
        "group",
        "draw group"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-group"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; the projector maps it to pixels / world.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        scale: {
          types: [
            "number"
          ],
          description: "Uniform scale applied to the whole group."
        },
        rotate: {
          types: [
            "number"
          ],
          description: "Rotation in radians (painter units)."
        },
        rotation: {
          types: [
            "array"
          ],
          description: "Euler rotation `[x, y, z]` in radians (world axes) \u2014 per-axis joint rotation for the 3D backend; a group placed at a joint with children authored joint-local IS a bone. The 2D painter applies only the in-plane `z` component, after `rotate`.",
          tupleItems: [
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            }
          ],
          items: {
            types: [
              "number"
            ]
          }
        },
        bone: {
          types: [
            "string"
          ],
          description: "Skeleton bone name. A named group is addressable by a `draw-mesh` `skin` block (smooth skinning): the skinned mesh deforms with this group's animated transform. Names resolve within the nearest enclosing `skeleton: true` group (falling back to the canvas), so multiple instances of one rig never collide. Distinct from `id` (the hit-test handle)."
        },
        skeleton: {
          types: [
            "boolean"
          ],
          description: "Marks this group as a skeleton SCOPE: `bone` names and `skin` bindings in its subtree resolve against each other only \u2014 one scope per rig instance."
        },
        animation: {
          types: [
            "object"
          ],
          description: "Keyframe animation over the group TRANSFORM tracks (`offsetX/Y/Z` in cells, `rotateX/Y/Z` in radians, `scale`; the 2D painter also honors `opacity`) \u2014 children inherit it, so nested animated groups form a joint chain. Material tracks belong on leaf meshes.",
          properties: {
            durationMs: {
              types: [
                "number"
              ]
            },
            loop: {
              types: [
                "boolean"
              ]
            },
            keyframes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  at: {
                    types: [
                      "number"
                    ]
                  },
                  offsetX: {
                    types: [
                      "number"
                    ]
                  },
                  offsetY: {
                    types: [
                      "number"
                    ]
                  },
                  offsetZ: {
                    types: [
                      "number"
                    ]
                  },
                  rotateX: {
                    types: [
                      "number"
                    ]
                  },
                  rotateY: {
                    types: [
                      "number"
                    ]
                  },
                  rotateZ: {
                    types: [
                      "number"
                    ]
                  },
                  scale: {
                    types: [
                      "number"
                    ]
                  },
                  opacity: {
                    types: [
                      "number"
                    ]
                  },
                  emissiveIntensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  emissive: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "at"
                ]
              }
            }
          },
          propertyRequired: [
            "durationMs",
            "keyframes"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        },
        clip: {
          types: [
            "string"
          ],
          description: "Clip the group's items to an SVG path (`d` syntax) in world units relative to the group origin \u2014 masked reveals, meters, portraits."
        },
        items: {
          types: [
            "array"
          ],
          description: "The grouped drawables, painted through the normal dispatch.",
          required: true,
          items: {
            types: [
              "object"
            ],
            drawableSlot: true
          }
        }
      },
      drawable: true,
      drawHost: true
    },
    "emoji-picker": {
      type: "emoji-picker",
      category: "component",
      sourceFile: "components/core/molecules/EmojiPicker.tsx",
      tier: "molecules",
      family: "core",
      description: "EmojiPicker component",
      suggestedFor: [
        "emoji",
        "picker",
        "emoji picker"
      ],
      typicalSize: "medium",
      propsSchema: {
        pickEvent: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 picking an emoji emits UI:{pickEvent} with { emoji } via eventBus",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "emoji",
              type: "string",
              required: true
            }
          ]
        },
        position: {
          types: [
            "string"
          ],
          description: "Which side of the trigger the panel opens on",
          enumValues: [
            "top",
            "bottom"
          ],
          default: "top"
        },
        triggerIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "Icon shown on the trigger button",
          default: "smile"
        },
        triggerLabel: {
          types: [
            "string"
          ],
          description: 'Accessible label for the trigger button (the translated "Add emoji" when omitted)'
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the trigger button"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "draw-mesh": {
      type: "draw-mesh",
      category: "game",
      sourceFile: "components/game/atoms/DrawMesh.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawMesh component",
      suggestedFor: [
        "draw",
        "mesh",
        "draw mesh"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-mesh"
          ]
        },
        shape: {
          types: [
            "string"
          ],
          description: "shape prop",
          required: true,
          enumValues: [
            "box",
            "sphere",
            "capsule",
            "cylinder",
            "cone",
            "torus",
            "plane",
            "circle",
            "polyhedron"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; `z` is height in cells (projected to world Y).",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        width: {
          types: [
            "number"
          ],
          description: "Box/plane X extent in cells (default 1)."
        },
        height: {
          types: [
            "number"
          ],
          description: "Box Y (height) extent / capsule-cylinder-cone length in cells (default 1)."
        },
        depth: {
          types: [
            "number"
          ],
          description: "Box/plane Z extent in cells (default `width`)."
        },
        radius: {
          types: [
            "number"
          ],
          description: "Sphere/capsule/cone/torus/circle radius in cells (default 0.4)."
        },
        radiusTop: {
          types: [
            "number"
          ],
          description: "Cylinder top radius (default `radius`)."
        },
        radiusBottom: {
          types: [
            "number"
          ],
          description: "Cylinder bottom radius (default `radius`)."
        },
        tube: {
          types: [
            "number"
          ],
          description: "Torus tube radius in cells (default `radius` / 3)."
        },
        segments: {
          types: [
            "number"
          ],
          description: "Curved-surface tessellation, clamped 3..64 (default 24). 4\u20138 + `flatShading` = low-poly."
        },
        vertices: {
          types: [
            "array"
          ],
          description: "`polyhedron` only: raw vertices as scene-frame `[x, y, z]` triples in cells, `z` = up.",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            }
          }
        },
        faces: {
          types: [
            "array"
          ],
          description: "`polyhedron` only: triangle index triples into `vertices`, wound counter-clockwise seen from OUTSIDE in the scene frame.",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            }
          }
        },
        uvs: {
          types: [
            "array"
          ],
          description: "`polyhedron` only: per-vertex `[u, v]` texture coordinates parallel to `vertices`, glTF convention (`v` grows DOWN from the image's top-left). Pairs with `material.map`.",
          items: {
            types: [
              "array"
            ],
            items: {
              types: [
                "number"
              ]
            }
          }
        },
        skin: {
          types: [
            "object"
          ],
          description: "`polyhedron` only: smooth-skinning weights binding the vertices to named bone groups. Vertices must then be authored in BIND POSE model space; the bones' animated transforms deform them.",
          properties: {
            bones: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            inverseBindMatrices: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            indices: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              }
            },
            weights: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          },
          propertyRequired: [
            "bones",
            "inverseBindMatrices",
            "indices",
            "weights"
          ]
        },
        rotation: {
          types: [
            "array"
          ],
          description: "Euler rotation `[x, y, z]` in radians.",
          tupleItems: [
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            },
            {
              types: [
                "number"
              ]
            }
          ],
          items: {
            types: [
              "number"
            ]
          }
        },
        pivot: {
          types: [
            "string"
          ],
          description: "`bottom` (default) rests the mesh on its position's ground plane; `center` centers it there. For `polyhedron`, `bottom` lifts by \u2212min(z) and `center` applies no offset \u2014 the vertices are authoritative.",
          enumValues: [
            "bottom",
            "center"
          ]
        },
        material: {
          types: [
            "object"
          ],
          description: "material prop",
          properties: {
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "standard",
                "physical",
                "toon",
                "basic"
              ]
            },
            color: {
              types: [
                "string"
              ]
            },
            metalness: {
              types: [
                "number"
              ]
            },
            roughness: {
              types: [
                "number"
              ]
            },
            emissive: {
              types: [
                "string"
              ]
            },
            emissiveIntensity: {
              types: [
                "number"
              ]
            },
            opacity: {
              types: [
                "number"
              ]
            },
            transmission: {
              types: [
                "number"
              ]
            },
            ior: {
              types: [
                "number"
              ]
            },
            flatShading: {
              types: [
                "boolean"
              ]
            },
            side: {
              types: [
                "string"
              ],
              enumValues: [
                "front",
                "back",
                "double"
              ]
            },
            map: {
              types: [
                "string"
              ]
            }
          }
        },
        outline: {
          types: [
            "object"
          ],
          description: "outline prop",
          properties: {
            color: {
              types: [
                "string"
              ]
            },
            width: {
              types: [
                "number"
              ]
            }
          }
        },
        castShadow: {
          types: [
            "boolean"
          ],
          description: "castShadow prop"
        },
        receiveShadow: {
          types: [
            "boolean"
          ],
          description: "receiveShadow prop"
        },
        animation: {
          types: [
            "object"
          ],
          description: "Keyframe animation over transform/material tracks; the 3D host runs it per frame.",
          properties: {
            durationMs: {
              types: [
                "number"
              ]
            },
            loop: {
              types: [
                "boolean"
              ]
            },
            keyframes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  at: {
                    types: [
                      "number"
                    ]
                  },
                  offsetX: {
                    types: [
                      "number"
                    ]
                  },
                  offsetY: {
                    types: [
                      "number"
                    ]
                  },
                  offsetZ: {
                    types: [
                      "number"
                    ]
                  },
                  rotateX: {
                    types: [
                      "number"
                    ]
                  },
                  rotateY: {
                    types: [
                      "number"
                    ]
                  },
                  rotateZ: {
                    types: [
                      "number"
                    ]
                  },
                  scale: {
                    types: [
                      "number"
                    ]
                  },
                  opacity: {
                    types: [
                      "number"
                    ]
                  },
                  emissiveIntensity: {
                    types: [
                      "number"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  },
                  emissive: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "at"
                ]
              }
            }
          },
          propertyRequired: [
            "durationMs",
            "keyframes"
          ]
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity; multiplies the material and enclosing group opacity."
        }
      },
      drawable: true
    },
    "algo-graph-canvas": {
      type: "algo-graph-canvas",
      category: "learning",
      sourceFile: "components/learning/molecules/AlgoGraphCanvas.tsx",
      tier: "molecules",
      family: "learning",
      description: "AlgoGraphCanvas component",
      suggestedFor: [
        "algo",
        "graph",
        "canvas",
        "algo graph canvas"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number"
          ],
          description: "width prop",
          default: 600
        },
        height: {
          types: [
            "number"
          ],
          description: "height prop",
          default: 400
        },
        title: {
          types: [
            "string"
          ],
          description: "title prop"
        },
        backgroundColor: {
          types: [
            "string"
          ],
          description: "backgroundColor prop"
        },
        series: {
          types: [
            "array"
          ],
          description: "Overrides the theme's categorical palette (`series-1\u2026N`) for this canvas \u2014 real-world conventions like CPK atom colors or resistor bands. Unset, the theme's `--color-series-*` apply.",
          items: {
            types: [
              "string"
            ]
          }
        },
        keyMap: {
          types: [
            "object"
          ],
          description: "Maps a keydown `e.code` (optionally `Mod+`/`Shift+`/`Alt+` prefixed) to a SEMANTIC event emitted as `UI:{event}` \u2014 e.g. `{ Space: TOGGLE_RUN, ArrowRight: STEP, KeyR: RESET }`; keystrokes inside inputs never route.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        keyUpMap: {
          types: [
            "object"
          ],
          description: "Maps a keyup `e.code` to a semantic event emitted as `UI:{event}`.",
          kind: "event-map",
          mapValue: {
            types: [
              "string"
            ],
            kind: "event"
          }
        },
        nodes: {
          types: [
            "array"
          ],
          description: "nodes prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "unvisited",
                  "frontier",
                  "current",
                  "visited",
                  "goal",
                  "path"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              badge: {
                types: [
                  "object"
                ],
                properties: {
                  text: {
                    types: [
                      "string"
                    ]
                  },
                  color: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "text"
                ]
              }
            },
            required: [
              "id"
            ]
          },
          default: []
        },
        edges: {
          types: [
            "array"
          ],
          description: "edges prop",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              },
              directed: {
                types: [
                  "boolean"
                ]
              },
              weight: {
                types: [
                  "number"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              state: {
                types: [
                  "string"
                ],
                enumValues: [
                  "default",
                  "tree",
                  "relaxed",
                  "candidate",
                  "path"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        layout: {
          types: [
            "string"
          ],
          description: "Deterministic layout to compute node positions; `manual` passes through `x`/`y`.",
          enumValues: [
            "manual",
            "tree",
            "layered",
            "circle"
          ],
          default: "manual"
        },
        root: {
          types: [
            "string"
          ],
          description: "`tree` layout only: preferred root id. Falls back to the first indegree-0 node."
        },
        shapes: {
          types: [
            "array"
          ],
          description: "Extra declarative shapes in canvas pixel coordinates.",
          items: {
            types: [
              "object"
            ],
            properties: {
              tone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              fillTone: {
                types: [
                  "string"
                ],
                enumValues: [
                  "ink",
                  "label",
                  "guide",
                  "axis",
                  "grid",
                  "highlight",
                  "fill",
                  "series-1",
                  "series-2",
                  "series-3",
                  "series-4",
                  "series-5",
                  "series-6",
                  "series-7",
                  "series-8",
                  "primary",
                  "accent",
                  "success",
                  "warning",
                  "error",
                  "info",
                  "muted"
                ]
              },
              stroke: {
                types: [
                  "string"
                ],
                enumValues: [
                  "thin",
                  "normal",
                  "bold"
                ]
              },
              textSize: {
                types: [
                  "string"
                ],
                enumValues: [
                  "xs",
                  "sm",
                  "base",
                  "lg",
                  "xl",
                  "2xl"
                ]
              },
              font: {
                types: [
                  "string"
                ],
                enumValues: [
                  "body",
                  "display",
                  "mono"
                ]
              },
              textCase: {
                types: [
                  "string"
                ],
                enumValues: [
                  "theme",
                  "verbatim"
                ]
              },
              fillStyle: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "tint",
                  "outline",
                  "hatch"
                ]
              },
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "line",
                  "arrow",
                  "circle",
                  "ellipse",
                  "rect",
                  "polygon",
                  "path",
                  "text",
                  "axis",
                  "grid",
                  "venn-region"
                ]
              },
              id: {
                types: [
                  "string"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              x1: {
                types: [
                  "number"
                ]
              },
              y1: {
                types: [
                  "number"
                ]
              },
              x2: {
                types: [
                  "number"
                ]
              },
              y2: {
                types: [
                  "number"
                ]
              },
              radius: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              },
              height: {
                types: [
                  "number"
                ]
              },
              points: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              },
              path: {
                types: [
                  "string"
                ]
              },
              text: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              fontSize: {
                types: [
                  "number"
                ]
              },
              fontFamily: {
                types: [
                  "string"
                ]
              },
              align: {
                types: [
                  "string"
                ],
                enumValues: [
                  "left",
                  "center",
                  "right"
                ]
              },
              axis: {
                types: [
                  "string"
                ],
                enumValues: [
                  "x",
                  "y"
                ]
              },
              min: {
                types: [
                  "number"
                ]
              },
              max: {
                types: [
                  "number"
                ]
              },
              step: {
                types: [
                  "number"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              fill: {
                types: [
                  "string"
                ]
              },
              lineWidth: {
                types: [
                  "number"
                ]
              },
              opacity: {
                types: [
                  "number"
                ]
              },
              startAngle: {
                types: [
                  "number"
                ]
              },
              endAngle: {
                types: [
                  "number"
                ]
              },
              dash: {
                types: [
                  "string"
                ],
                enumValues: [
                  "solid",
                  "dashed",
                  "dotted"
                ]
              },
              inside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              outside: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            required: [
              "type"
            ]
          },
          default: []
        },
        interactive: {
          types: [
            "boolean"
          ],
          description: "interactive prop",
          default: false
        },
        animate: {
          types: [
            "boolean"
          ],
          description: "animate prop",
          default: false
        },
        onShapeClick: {
          types: [
            "function"
          ],
          description: "onShapeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "index"
                ]
              }
            }
          ]
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "Fires when a node circle is clicked, alongside `onShapeClick`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "payload",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "id",
                  "index"
                ]
              }
            }
          ]
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "fx-overlay": {
      type: "fx-overlay",
      category: "container",
      sourceFile: "components/core/molecules/FxOverlay.tsx",
      tier: "molecules",
      family: "core",
      description: "FxOverlay component",
      suggestedFor: [
        "overlay",
        "fx overlay"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Live screen-space fx entries (the mechanic's fx list filtered to `space == \"screen\"`); mount starts each item's animation, ttl decay unmounts it.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              effect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "confetti",
                  "flash",
                  "shake",
                  "sparkle",
                  "float-text",
                  "streak-glow"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              ttl: {
                types: [
                  "number"
                ]
              },
              maxTtl: {
                types: [
                  "number"
                ]
              },
              bornAt: {
                types: [
                  "number"
                ]
              },
              space: {
                types: [
                  "string"
                ],
                enumValues: [
                  "world",
                  "screen"
                ]
              },
              particleCount: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "id",
              "type",
              "ttl"
            ]
          }
        },
        tickMs: {
          types: [
            "number"
          ],
          description: "The fx mechanic's decay tick period in ms; per-item animation duration = maxTtl \xD7 tickMs. Default 500."
        },
        children: {
          types: [
            "node"
          ],
          description: "Wrapped content \u2014 the `shake` target and the overlay's bounds."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names on the outermost element."
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "draw-fx-layer": {
      type: "draw-fx-layer",
      category: "game",
      sourceFile: "components/game/molecules/DrawFxLayer.tsx",
      tier: "molecules",
      family: "game",
      description: "DrawFxLayer component",
      suggestedFor: [
        "draw",
        "layer",
        "draw fx layer"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-fx-layer"
          ]
        },
        items: {
          types: [
            "array"
          ],
          description: 'The live fx entries \u2014 the mechanic\'s `@entity.fx` list passed whole; `space: "screen"` entries are skipped (the overlay owns those).',
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              },
              position: {
                types: [
                  "object"
                ],
                scenePos: true,
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  z: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "x",
                  "y"
                ]
              },
              x: {
                types: [
                  "number"
                ]
              },
              z: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              message: {
                types: [
                  "string"
                ]
              },
              ttl: {
                types: [
                  "number"
                ]
              },
              maxTtl: {
                types: [
                  "number"
                ]
              },
              bornAt: {
                types: [
                  "number"
                ]
              },
              space: {
                types: [
                  "string"
                ],
                enumValues: [
                  "world",
                  "screen"
                ]
              },
              effect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "confetti",
                  "flash",
                  "shake",
                  "sparkle",
                  "float-text",
                  "streak-glow"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              },
              particleCount: {
                types: [
                  "number"
                ]
              },
              animation: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "type",
              "x",
              "ttl"
            ]
          }
        },
        presets: {
          types: [
            "array"
          ],
          description: "Consumer-declared effect vocabulary \u2014 one row per fx `type` (look + recipe). Unknown types fall back to a neutral spark burst.",
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ]
              },
              color: {
                types: [
                  "string"
                ]
              },
              color2: {
                types: [
                  "string"
                ]
              },
              size: {
                types: [
                  "number"
                ]
              },
              shape: {
                types: [
                  "string"
                ],
                enumValues: [
                  "spark",
                  "ring",
                  "puff",
                  "streak"
                ]
              },
              count: {
                types: [
                  "number"
                ]
              },
              glow: {
                types: [
                  "number"
                ]
              },
              gravity: {
                types: [
                  "number"
                ]
              },
              vx: {
                types: [
                  "number"
                ]
              },
              vy: {
                types: [
                  "number"
                ]
              },
              vz: {
                types: [
                  "number"
                ]
              },
              space: {
                types: [
                  "string"
                ],
                enumValues: [
                  "world",
                  "screen"
                ]
              },
              effect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "confetti",
                  "flash",
                  "shake",
                  "sparkle",
                  "float-text",
                  "streak-glow"
                ]
              },
              particleCount: {
                types: [
                  "number"
                ]
              },
              animation: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "type"
            ]
          }
        },
        art: {
          types: [
            "object"
          ],
          description: "Vector art per fx type (an asset-art map: type \u2192 anim-state \u2192 drawables); a type's `idle` state wins over sprites/procedural.",
          mapValue: {
            types: [
              "object"
            ],
            mapValue: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                drawableSlot: true
              }
            }
          }
        },
        sprites: {
          types: [
            "object"
          ],
          description: "Sprite asset per fx type (an asset-manifest effects map) \u2014 used when no vector art matches.",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              url: {
                types: [
                  "asset"
                ]
              },
              role: {
                types: [
                  "string"
                ]
              },
              category: {
                types: [
                  "string"
                ]
              },
              animations: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              style: {
                types: [
                  "string"
                ],
                enumValues: [
                  "pixel",
                  "vector",
                  "hd",
                  "1-bit",
                  "isometric"
                ]
              },
              variant: {
                types: [
                  "string"
                ]
              },
              dimension: {
                types: [
                  "string"
                ],
                enumValues: [
                  "2d",
                  "3d"
                ]
              },
              aspect: {
                types: [
                  "string"
                ],
                enumValues: [
                  "1:1",
                  "16:9",
                  "5:7",
                  "8:1",
                  "4:1",
                  "5:3",
                  "1:2"
                ]
              },
              atlas: {
                types: [
                  "asset"
                ]
              },
              sprite: {
                types: [
                  "string"
                ]
              },
              name: {
                types: [
                  "string"
                ]
              },
              thumbnailUrl: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "url",
              "role",
              "category"
            ]
          }
        },
        tickMs: {
          types: [
            "number"
          ],
          description: "The fx mechanic's decay tick period in ms; lifetime = maxTtl \xD7 tickMs. Default 500."
        },
        textColor: {
          types: [
            "string"
          ],
          description: "Floating-message text color when an item declares none. Default `#ffe066`."
        }
      },
      drawable: true,
      drawHost: true
    },
    "rich-text-editor": {
      type: "rich-text-editor",
      category: "component",
      sourceFile: "components/core/molecules/RichTextEditor.tsx",
      tier: "molecules",
      family: "core",
      description: "RichTextEditor component",
      suggestedFor: [
        "rich",
        "text",
        "editor",
        "rich text editor"
      ],
      typicalSize: "medium",
      propsSchema: {
        value: {
          types: [
            "string"
          ],
          description: "The rich text content as HTML. Sanitized before rendering and on every change."
        },
        onChange: {
          types: [
            "function"
          ],
          description: "onChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "value",
              type: "string"
            }
          ]
        },
        changeEvent: {
          types: [
            "string"
          ],
          description: "changeEvent prop",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        readOnly: {
          types: [
            "boolean"
          ],
          description: "readOnly prop",
          default: false
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "placeholder prop"
        },
        showToolbar: {
          types: [
            "boolean"
          ],
          description: "showToolbar prop",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "document-panel": {
      type: "document-panel",
      category: "component",
      sourceFile: "components/core/molecules/DocumentPanel.tsx",
      tier: "molecules",
      family: "core",
      description: "DocumentPanel \u2014 a record rendered as one continuous document: editable title over the rich-text body, editing controls tucked into the header corner.",
      capabilities: "document page, note editor, wiki page, article editor, writing surface, rich text document, content editing canvas",
      suggestedFor: [
        "document",
        "panel",
        "document panel"
      ],
      typicalSize: "medium",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "The record's id \u2014 carried on every commit and action event."
        },
        title: {
          types: [
            "string"
          ],
          description: 'Document title (falls back to "Untitled" for a blank draft)'
        },
        subtitle: {
          types: [
            "string"
          ],
          description: "Muted byline under the title; hidden when empty"
        },
        coverImage: {
          types: [
            "string"
          ],
          description: "Cover/hero image URL painted as a full-bleed banner above the header row (Notion/article-hero shape); hidden when empty"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "Document icon rendered beside the title (a file glyph, a domain icon); hidden when empty"
        },
        value: {
          types: [
            "string"
          ],
          description: "The document body as rich-text HTML"
        },
        actions: {
          types: [
            "array"
          ],
          description: 'Header actions (settings, history, \u2026). At most one explicit primary stays inline; the rest live under a single "\u22EF" menu \u2014 the title owns its row.',
          kind: "event-list",
          eventField: "event",
          items: {
            types: [
              "object"
            ],
            properties: {
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              variant: {
                types: [
                  "string"
                ],
                enumValues: [
                  "primary",
                  "secondary",
                  "ghost",
                  "danger"
                ]
              }
            },
            required: [
              "label"
            ]
          }
        },
        maxInlineActions: {
          types: [
            "number"
          ],
          description: "0 collapses even the primary action into the menu"
        },
        editing: {
          types: [
            "boolean"
          ],
          description: "false = read surface with click-to-edit body; true = live editor",
          default: false
        },
        titleCommitEvent: {
          types: [
            "string"
          ],
          description: "Emitted when the title commits: { title, id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "title",
              type: "string",
              required: true
            },
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        contentChangeEvent: {
          types: [
            "string"
          ],
          description: "Forwarded to the embedded RichTextEditor's autosave stream: { value }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "value",
              type: "string",
              required: true
            }
          ]
        },
        editEvent: {
          types: [
            "string"
          ],
          description: "Emitted by the header Edit affordance and a click on the read body: { id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        doneEvent: {
          types: [
            "string"
          ],
          description: "Emitted by the Done button that ends the editing session: { id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        editLabel: {
          types: [
            "string"
          ],
          description: "editLabel prop"
        },
        doneLabel: {
          types: [
            "string"
          ],
          description: "doneLabel prop"
        },
        autosaveHint: {
          types: [
            "string"
          ],
          description: "Muted caption beside Done telling the author work persists on its own"
        },
        placeholder: {
          types: [
            "string"
          ],
          description: "Placeholder for an empty body"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "document-details": {
      type: "document-details",
      category: "display",
      sourceFile: "components/core/molecules/DocumentDetails.tsx",
      tier: "molecules",
      family: "core",
      description: "DocumentDetails \u2014 the document's metadata as a compact settings card for the rail beside a DocumentPanel, each property committing in place.",
      capabilities: "document settings, page details, post settings sidebar, metadata panel, document properties, publish settings",
      fieldsContract: "display",
      suggestedFor: [
        "document",
        "details",
        "document details"
      ],
      typicalSize: "medium",
      propsSchema: {
        entity: {
          types: [
            "object"
          ],
          description: "The loaded record \u2014 supplies the id every commit carries and the values the rows read.",
          kind: "entity",
          cardinality: "record"
        },
        fields: {
          types: [
            "array"
          ],
          description: "The properties shown; empty renders nothing.",
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              key: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              header: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              kind: {
                types: [
                  "string"
                ],
                enumValues: [
                  "text",
                  "boolean",
                  "select",
                  "readonly",
                  "image"
                ]
              },
              options: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              labels: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              },
              format: {
                types: [
                  "string"
                ],
                enumValues: [
                  "date",
                  "currency",
                  "number",
                  "boolean",
                  "percent"
                ]
              },
              type: {
                types: [
                  "string"
                ]
              }
            }
          }
        },
        metaCommitEvent: {
          types: [
            "string"
          ],
          description: "Emitted when a property commits: { id, patch } \u2014 patch carries the id plus the one changed field, ready for a partial persist update.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "patch",
              type: "object",
              required: true,
              schema: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  controlValue: true
                }
              }
            }
          ]
        },
        relationEvent: {
          types: [
            "string"
          ],
          description: "Makes relation chips clickable: emitted with { field, id, name } when a hydrated relation value (or one row of a many-relation) is clicked \u2014 the host routes it to the linked record's own surface.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "field",
              type: "string",
              required: true
            },
            {
              name: "id",
              type: "string",
              required: true
            },
            {
              name: "name",
              type: "string",
              required: true
            }
          ]
        },
        title: {
          types: [
            "string"
          ],
          description: 'Card heading (defaults to the localized "Details")'
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "floating-toolbar": {
      type: "floating-toolbar",
      category: "component",
      sourceFile: "components/core/molecules/FloatingToolbar.tsx",
      tier: "molecules",
      family: "core",
      description: "FloatingToolbar component",
      suggestedFor: [
        "floating",
        "toolbar",
        "floating toolbar"
      ],
      typicalSize: "medium",
      propsSchema: {
        items: {
          types: [
            "array"
          ],
          description: "Tool items rendered as icon buttons",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              action: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              actionPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "action"
              },
              active: {
                types: [
                  "boolean"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              testId: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "id",
              "icon",
              "label"
            ]
          }
        },
        position: {
          types: [
            "string"
          ],
          description: "Strip position within the app frame.",
          enumValues: [
            "bottom-center",
            "bottom-left",
            "bottom-right",
            "top-center",
            "top-left",
            "top-right"
          ],
          default: "bottom-center"
        },
        children: {
          types: [
            "node"
          ],
          description: "Custom cells appended after the items, separated by a divider"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes on the positioned root"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "dock-layout": {
      type: "dock-layout",
      category: "layout",
      sourceFile: "components/core/organisms/layout/DockLayout.tsx",
      tier: "organisms",
      family: "core",
      description: "DockLayout component",
      suggestedFor: [
        "dock",
        "layout",
        "dock layout"
      ],
      typicalSize: "large",
      propsSchema: {
        rail: {
          types: [
            "node"
          ],
          description: "Fixed-width far-left vertical strip (e.g. an icon nav rail)."
        },
        sidebar: {
          types: [
            "node"
          ],
          description: "Collapsible, resizable left sidebar."
        },
        main: {
          types: [
            "node"
          ],
          description: "Required center region.",
          required: true
        },
        bottomPanel: {
          types: [
            "node"
          ],
          description: "Collapsible, resizable-height bottom panel."
        },
        statusBar: {
          types: [
            "node"
          ],
          description: "Slim strip pinned to the bottom of the frame, below the bottom panel."
        },
        secondarySidebar: {
          types: [
            "node"
          ],
          description: "Collapsible right sidebar."
        },
        railWidth: {
          types: [
            "number"
          ],
          description: "Width of `rail` in pixels; the rail sizes the column itself when omitted."
        },
        secondarySidebarWidth: {
          types: [
            "number"
          ],
          description: "Width of `secondarySidebar` in pixels (fixed \u2014 not resizable). @default 280",
          default: 280
        },
        sidebarCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether `sidebar` is collapsed (controlled; the layout keeps its own state when omitted)."
        },
        onSidebarCollapsedChange: {
          types: [
            "function"
          ],
          description: "onSidebarCollapsedChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "collapsed",
              type: "boolean"
            }
          ]
        },
        sidebarWidth: {
          types: [
            "number"
          ],
          description: "Sidebar size as a SplitPane ratio (0-100, percentage of the sidebar/main split). @default 20",
          default: 20
        },
        onSidebarWidthChange: {
          types: [
            "function"
          ],
          description: "onSidebarWidthChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "width",
              type: "number"
            }
          ]
        },
        sidebarMinSize: {
          types: [
            "number"
          ],
          description: "Minimum sidebar size in pixels, forwarded to SplitPane's `minSize`. @default 160",
          default: 160
        },
        bottomPanelCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether `bottomPanel` is collapsed (controlled; the layout keeps its own state when omitted)."
        },
        onBottomPanelCollapsedChange: {
          types: [
            "function"
          ],
          description: "onBottomPanelCollapsedChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "collapsed",
              type: "boolean"
            }
          ]
        },
        bottomPanelHeight: {
          types: [
            "number"
          ],
          description: "Bottom panel size as a SplitPane ratio (0-100, percentage given to the panel). @default 30",
          default: 30
        },
        onBottomPanelHeightChange: {
          types: [
            "function"
          ],
          description: "onBottomPanelHeightChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "height",
              type: "number"
            }
          ]
        },
        bottomPanelMinSize: {
          types: [
            "number"
          ],
          description: "Minimum bottom panel size in pixels, forwarded to SplitPane's `minSize`. @default 120",
          default: 120
        },
        secondarySidebarCollapsed: {
          types: [
            "boolean"
          ],
          description: "Whether `secondarySidebar` is collapsed (controlled; the layout keeps its own state when omitted)."
        },
        onSecondarySidebarCollapsedChange: {
          types: [
            "function"
          ],
          description: "onSecondarySidebarCollapsedChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "collapsed",
              type: "boolean"
            }
          ]
        },
        compact: {
          types: [
            "boolean"
          ],
          description: "The mobile variant; decided by the viewport (narrower than 1024px) when omitted."
        },
        secondarySidebarLabel: {
          types: [
            "string"
          ],
          description: 'Compact: the top-bar button that opens `secondarySidebar` (e.g. "Inspector", "Chat").'
        },
        secondarySidebarIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "secondarySidebarIcon prop"
        },
        bottomPanelLabel: {
          types: [
            "string"
          ],
          description: "Compact: the top-bar button that opens `bottomPanel`."
        },
        bottomPanelIcon: {
          types: [
            "icon",
            "string"
          ],
          description: "bottomPanelIcon prop"
        },
        topBarActions: {
          types: [
            "node"
          ],
          description: "Compact: the host's own actions at the end of the top bar (e.g. Run)."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes on the root frame."
        },
        railClassName: {
          types: [
            "string"
          ],
          description: "railClassName prop"
        },
        sidebarClassName: {
          types: [
            "string"
          ],
          description: "sidebarClassName prop"
        },
        mainClassName: {
          types: [
            "string"
          ],
          description: "mainClassName prop"
        },
        bottomPanelClassName: {
          types: [
            "string"
          ],
          description: "bottomPanelClassName prop"
        },
        statusBarClassName: {
          types: [
            "string"
          ],
          description: "statusBarClassName prop"
        },
        secondarySidebarClassName: {
          types: [
            "string"
          ],
          description: "secondarySidebarClassName prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "command-palette": {
      type: "command-palette",
      category: "component",
      sourceFile: "components/core/molecules/CommandPalette.tsx",
      tier: "molecules",
      family: "core",
      description: "CommandPalette component",
      suggestedFor: [
        "command",
        "palette",
        "command palette"
      ],
      typicalSize: "medium",
      propsSchema: {
        open: {
          types: [
            "boolean"
          ],
          description: "Whether the palette overlay is open",
          required: true
        },
        onOpenChange: {
          types: [
            "function"
          ],
          description: "Called to open/close the overlay (Escape, overlay click, or a selection)",
          required: true,
          kind: "callback",
          callbackArgs: [
            {
              name: "open",
              type: "boolean"
            }
          ]
        },
        commands: {
          types: [
            "array"
          ],
          description: "Commands listed in the palette, filtered as the user types",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              id: {
                types: [
                  "string"
                ]
              },
              label: {
                types: [
                  "string"
                ]
              },
              icon: {
                types: [
                  "icon",
                  "string"
                ]
              },
              shortcut: {
                types: [
                  "string"
                ]
              },
              keywords: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              group: {
                types: [
                  "string"
                ]
              },
              disabled: {
                types: [
                  "boolean"
                ]
              },
              event: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              action: {
                types: [
                  "string"
                ],
                kind: "event"
              },
              actionPayload: {
                types: [
                  "object"
                ],
                eventPayloadBrand: true,
                payloadFor: "action"
              }
            },
            required: [
              "id",
              "label"
            ]
          }
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "Called with the selected command (click, or Enter on the highlighted row)",
          kind: "callback",
          callbackArgs: [
            {
              name: "command",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  id: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  icon: {
                    types: [
                      "icon",
                      "string"
                    ]
                  },
                  shortcut: {
                    types: [
                      "string"
                    ]
                  },
                  keywords: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  group: {
                    types: [
                      "string"
                    ]
                  },
                  disabled: {
                    types: [
                      "boolean"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ],
                    kind: "event"
                  },
                  action: {
                    types: [
                      "string"
                    ],
                    kind: "event"
                  },
                  actionPayload: {
                    types: [
                      "object"
                    ],
                    eventPayloadBrand: true,
                    payloadFor: "action"
                  }
                },
                required: [
                  "id",
                  "label"
                ]
              }
            }
          ]
        },
        placeholder: {
          types: [
            "string"
          ],
          description: 'Search field placeholder. Defaults to the translated "Type a command...".'
        },
        emptyLabel: {
          types: [
            "string"
          ],
          description: "Label shown when no command matches the query. Defaults to the translated message."
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes on the overlay's dialog"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "game-audio-cue": {
      type: "game-audio-cue",
      category: "game",
      sourceFile: "components/game/atoms/GameAudioCue.tsx",
      tier: "atoms",
      family: "game",
      description: "GameAudioCue component",
      suggestedFor: [
        "game",
        "audio",
        "cue",
        "game audio cue"
      ],
      typicalSize: "small",
      propsSchema: {
        cue: {
          types: [
            "string"
          ],
          description: "Manifest key played as a one-shot SFX each time `cueSeq` advances."
        },
        cueSeq: {
          types: [
            "number"
          ],
          description: "Monotonically increasing counter \u2014 bumping it re-triggers `cue`."
        },
        music: {
          types: [
            "string"
          ],
          description: "Manifest key to crossfade to as looping background music; unset/empty stops music."
        },
        muted: {
          types: [
            "boolean"
          ],
          description: "Muted state, owned externally and mirrored into the hook."
        },
        volume: {
          types: [
            "number"
          ],
          description: "Master volume 0-1, owned externally and mirrored into the hook."
        },
        manifest: {
          types: [
            "object"
          ],
          description: "Sound manifest \u2014 keys mapped to sound definitions (path, volume, loop, ...).",
          required: true,
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              path: {
                types: [
                  "string",
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              },
              volume: {
                types: [
                  "number"
                ]
              },
              loop: {
                types: [
                  "boolean"
                ]
              },
              poolSize: {
                types: [
                  "number"
                ]
              },
              autostart: {
                types: [
                  "boolean"
                ]
              },
              crossfade: {
                types: [
                  "boolean"
                ]
              },
              crossfadeDurationMs: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "path"
            ]
          }
        },
        baseUrl: {
          types: [
            "string"
          ],
          description: "Base URL prepended to every manifest path."
        }
      }
    },
    "ui-slot-component": {
      type: "ui-slot-component",
      category: "layout",
      sourceFile: "components/core/organisms/UISlotRenderer.tsx",
      tier: "organisms",
      family: "core",
      description: "UISlotComponent component",
      suggestedFor: [
        "slot",
        "component",
        "u i slot component"
      ],
      typicalSize: "large",
      propsSchema: {
        slot: {
          types: [
            "object"
          ],
          description: "slot prop",
          required: true
        },
        portal: {
          types: [
            "boolean"
          ],
          description: "portal prop",
          default: false
        },
        position: {
          types: [
            "string"
          ],
          description: "position prop",
          enumValues: [
            "left",
            "right",
            "top-right",
            "top-left",
            "bottom-right",
            "bottom-left"
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        draggable: {
          types: [
            "boolean"
          ],
          description: "draggable prop"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        error: {
          types: [
            "object"
          ],
          description: "error prop",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ]
        },
        entity: {
          types: [
            "string"
          ],
          description: "entity prop"
        },
        children: {
          types: [
            "node",
            "function"
          ],
          description: "Compiled mode: render children directly instead of resolving from context. A function receives the slot's presence \u2014 compiled self-overlaying content (Modal / ConfirmDialog) takes it as `isOpen`/`onExited` to play its exit."
        },
        pattern: {
          types: [
            "string"
          ],
          description: "Pattern type for data-pattern attribute (compiled mode)"
        },
        sourceTrait: {
          types: [
            "string"
          ],
          description: "Source trait name for data-source-trait attribute (compiled mode)"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        awaitingSkeleton: {
          types: [
            "object"
          ],
          description: "Compiled mode: the predicted skeleton while this trait's server round trip is in flight (the View selects it by the pending transition). Shown only while the slot is empty. Runtime mode reads it from `AwaitingSkeletonContext`."
        },
        fallback: {
          types: [
            "node"
          ],
          description: "Host-supplied stock content for a slot-host region (Studio V4 \xA714 Part I2). Ignored whenever `children` is passed \u2014 `children` (compiled mode) always wins, so a compiled organism's slot can never be silently shadowed by a host `fallback`. In `portal` mode `fallback` never portals: it paints inline at this component's own position in the tree, while slot content (if present) still portals as usual \u2014 a plugin filling `modal` doesn't relocate the host's inline empty-state."
        },
        mode: {
          types: [
            "string"
          ],
          description: "`'replace'` (default): slot content when present, else `fallback`. `'append'`: `fallback` first, then slot content when present \u2014 both render together, so `fallback` is a fixture, not just a placeholder.",
          enumValues: [
            "replace",
            "append"
          ],
          default: "replace"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "draw-skinned-mesh": {
      type: "draw-skinned-mesh",
      category: "game",
      sourceFile: "components/game/atoms/DrawSkinnedMesh.tsx",
      tier: "atoms",
      family: "game",
      description: "DrawSkinnedMesh component",
      suggestedFor: [
        "draw",
        "skinned",
        "mesh",
        "draw skinned mesh"
      ],
      typicalSize: "small",
      propsSchema: {
        id: {
          types: [
            "string"
          ],
          description: "Optional source-tagged hit-test handle \u2014 the host indexes the drawable's ScenePos\u2192id so a pointer/raycast at that cell resolves to this id (unit/entity click). No id \u2192 the host emits only the coordinate (tile click)."
        },
        type: {
          types: [
            "string"
          ],
          description: "type prop",
          required: true,
          enumValues: [
            "draw-skinned-mesh"
          ]
        },
        position: {
          types: [
            "object"
          ],
          description: "Logical scene position; mesh local coords are world units relative to its projected top-left.",
          required: true,
          properties: {
            x: {
              types: [
                "number"
              ]
            },
            y: {
              types: [
                "number"
              ]
            },
            z: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "x",
            "y"
          ],
          scenePos: true
        },
        asset: {
          types: [
            "object"
          ],
          description: "The texture the mesh UVs cut into.",
          required: true,
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        mesh: {
          types: [
            "object"
          ],
          description: "Inline bind mesh; wins over `meshUrl`.",
          properties: {
            vertices: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  x: {
                    types: [
                      "number"
                    ]
                  },
                  y: {
                    types: [
                      "number"
                    ]
                  },
                  u: {
                    types: [
                      "number"
                    ]
                  },
                  v: {
                    types: [
                      "number"
                    ]
                  },
                  weights: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        bone: {
                          types: [
                            "number"
                          ]
                        },
                        w: {
                          types: [
                            "number"
                          ]
                        }
                      },
                      required: [
                        "bone",
                        "w"
                      ]
                    }
                  }
                },
                required: [
                  "x",
                  "y",
                  "u",
                  "v",
                  "weights"
                ]
              }
            },
            triangles: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                },
                tupleItems: [
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  }
                ]
              }
            }
          },
          propertyRequired: [
            "vertices",
            "triangles"
          ]
        },
        meshUrl: {
          types: [
            "string"
          ],
          description: "URL of a JSON `SkinMesh` (fetched + cached); used when `mesh` is absent."
        },
        bones: {
          types: [
            "array"
          ],
          description: "The skeleton (bind pose; FK chain via `parent` names). Falls back to the rigbundle's `bones` when empty.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              name: {
                types: [
                  "string"
                ]
              },
              parent: {
                types: [
                  "string"
                ],
                nullable: true
              },
              pivot: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                },
                tupleItems: [
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  }
                ]
              },
              tip: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "number"
                  ]
                },
                tupleItems: [
                  {
                    types: [
                      "number"
                    ]
                  },
                  {
                    types: [
                      "number"
                    ]
                  }
                ]
              }
            },
            required: [
              "name",
              "pivot",
              "tip"
            ]
          }
        },
        pose: {
          types: [
            "object"
          ],
          description: "Static pose (bone name \u2192 degrees relative to bind). Overridden by `clip` + `frame`.",
          mapValue: {
            types: [
              "number"
            ]
          }
        },
        clip: {
          types: [
            "object"
          ],
          description: "Pose animation clip; with `frame`, pose = `frames[frame % frames.length]`.",
          properties: {
            frames: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "number"
                  ]
                }
              }
            }
          },
          propertyRequired: [
            "frames"
          ]
        },
        clipName: {
          types: [
            "string"
          ],
          description: "Named clip inside the rigbundle at `meshUrl` (`clips[clipName].frames`); used when `clip` is absent. Empty/unknown = no clip."
        },
        frame: {
          types: [
            "number"
          ],
          description: "Clip frame counter (host-driven paint clock frame)."
        },
        opacity: {
          types: [
            "number"
          ],
          description: "0..1 opacity."
        },
        weightsOverlay: {
          types: [
            "boolean"
          ],
          description: "Paint per-triangle dominant-bone colors instead of the texture (rig inspection)."
        },
        ghost: {
          types: [
            "object"
          ],
          description: "Onion skin: render the mesh at this clip frame underneath, at this opacity.",
          properties: {
            frame: {
              types: [
                "number"
              ]
            },
            opacity: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "frame",
            "opacity"
          ]
        }
      },
      drawable: true
    },
    gantt: {
      type: "gantt",
      category: "component",
      sourceFile: "components/core/molecules/Gantt.tsx",
      tier: "molecules",
      family: "core",
      description: "Gantt \u2014 view-only task schedule rendering rows as bars on a day axis.",
      capabilities: "gantt chart, project timeline, schedule view, task bars, dependency arrows, roadmap, milestone plan",
      fieldsContract: "display",
      suggestedFor: [
        "gantt"
      ],
      typicalSize: "medium",
      propsSchema: {
        tasks: {
          types: [
            "object",
            "array"
          ],
          description: 'Schema entity data \u2014 the task rows to place on the axis. pattern-sync tags it `kind:"entity", cardinality:"collection"` so consumers bind the domain entity without name-matching the prop.',
          kind: "entity",
          cardinality: "collection",
          default: []
        },
        links: {
          types: [
            "array"
          ],
          description: "Dependency arrows between task ids",
          items: {
            types: [
              "object"
            ],
            properties: {
              from: {
                types: [
                  "string"
                ]
              },
              to: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "from",
              "to"
            ]
          },
          default: []
        },
        titleField: {
          types: [
            "string"
          ],
          description: "Row field holding the bar label. Defaults to `title`.",
          default: "title"
        },
        startField: {
          types: [
            "string"
          ],
          description: "Row field holding the start timestamp (ISO or epoch). Defaults to `start`.",
          default: "start"
        },
        endField: {
          types: [
            "string"
          ],
          description: "Row field holding the end timestamp (ISO or epoch). Defaults to `end`. When absent, `durationField` (days) is used instead.",
          default: "end"
        },
        durationField: {
          types: [
            "string"
          ],
          description: "Row field holding the task length in days, used when the row has no end."
        },
        statusField: {
          types: [
            "string"
          ],
          description: "Row field holding the bar status. Defaults to `status`.",
          default: "status"
        },
        statusColorMap: {
          types: [
            "object"
          ],
          description: "Bar colour per exact status value; unmapped statuses are primary bars.",
          mapValue: {
            types: [
              "string"
            ],
            enumValues: [
              "default",
              "primary",
              "secondary",
              "success",
              "warning",
              "danger",
              "error",
              "info",
              "neutral",
              "destructive"
            ]
          }
        },
        groupField: {
          types: [
            "string"
          ],
          description: "Row field rows are grouped under header rows by. Empty (default) = flat list.",
          default: ""
        },
        rangeStart: {
          types: [
            "string",
            "date"
          ],
          description: "First visible day (ISO or Date). Defaults to 2 days before the earliest task."
        },
        rangeEnd: {
          types: [
            "string",
            "date"
          ],
          description: "Last visible day (ISO or Date). Defaults to 2 days after the latest task end."
        },
        showToday: {
          types: [
            "boolean"
          ],
          description: "Paint the today marker line when today falls inside the range (default true).",
          default: true
        },
        dayWidth: {
          types: [
            "number"
          ],
          description: "Pixels per day on the axis (default 28)",
          default: 28
        },
        barClickEvent: {
          types: [
            "string"
          ],
          description: "Event emitted when a bar is clicked: UI:{barClickEvent} with { id }",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "id",
              type: "string",
              required: true
            }
          ]
        },
        surface: {
          types: [
            "string"
          ],
          description: "Content surface: `auto` paints the theme's surface behind this block unless it already sits on one (a card, dialog or another block); `none` opts out.",
          enumValues: [
            "auto",
            "none"
          ],
          default: "auto"
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "Loading state",
          default: false
        },
        error: {
          types: [
            "object"
          ],
          description: "Error state",
          properties: {
            message: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            code: {
              types: [
                "string"
              ]
            },
            stack: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "message"
          ],
          default: null
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    image: {
      type: "image",
      category: "component",
      sourceFile: "components/core/atoms/Image.tsx",
      tier: "atoms",
      family: "core",
      description: "Image component",
      accessibleName: [
        "alt"
      ],
      suggestedFor: [
        "image"
      ],
      typicalSize: "small",
      propsSchema: {
        src: {
          types: [
            "asset"
          ],
          description: "Image source URL",
          required: true
        },
        alt: {
          types: [
            "string"
          ],
          description: "Alternative text; empty string marks the image decorative",
          required: true
        },
        fit: {
          types: [
            "string"
          ],
          description: "How the image fills its box",
          enumValues: [
            "cover",
            "contain",
            "fill",
            "none"
          ],
          default: "cover"
        },
        aspect: {
          types: [
            "string"
          ],
          description: `Fixed aspect ratio of the box; "auto" keeps the image's own`,
          enumValues: [
            "auto",
            "1/1",
            "4/3",
            "3/2",
            "16/9",
            "21/9"
          ],
          default: "auto"
        },
        rounded: {
          types: [
            "string"
          ],
          description: "Corner rounding",
          enumValues: [
            "none",
            "sm",
            "md",
            "lg",
            "xl",
            "2xl",
            "full"
          ],
          default: "md"
        },
        bordered: {
          types: [
            "boolean"
          ],
          description: "Draw a subtle border around the image",
          default: false
        },
        loading: {
          types: [
            "string"
          ],
          description: '"lazy" defers off-screen images; "eager" for above-the-fold media',
          enumValues: [
            "lazy",
            "eager"
          ],
          default: "lazy"
        },
        position: {
          types: [
            "string"
          ],
          description: "Which part of the image stays visible when it is cropped",
          enumValues: [
            "center",
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "center"
        },
        srcSet: {
          types: [
            "string"
          ],
          description: "Candidate sources at different widths (the HTML `srcset`)"
        },
        sizes: {
          types: [
            "string"
          ],
          description: "Rendered widths at each breakpoint (the HTML `sizes`)"
        },
        priority: {
          types: [
            "boolean"
          ],
          description: "The page's main image: load it first",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "Additional class names"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "geometric-pattern": {
      type: "geometric-pattern",
      category: "component",
      sourceFile: "components/core/molecules/GeometricPattern.tsx",
      tier: "molecules",
      family: "core",
      description: "GeometricPattern component",
      suggestedFor: [
        "geometric",
        "pattern",
        "geometric pattern"
      ],
      typicalSize: "medium",
      propsSchema: {
        variant: {
          types: [
            "string"
          ],
          description: "Which geometric tile design",
          enumValues: [
            "star8",
            "star6",
            "khatam",
            "star10",
            "star12",
            "rosette-double",
            "rosette-filled",
            "seigaiha",
            "greek-key",
            "celtic-knot",
            "kolam",
            "arch",
            "arabesque-vine",
            "arabesque-net"
          ],
          default: "star8"
        },
        mode: {
          types: [
            "string"
          ],
          description: "Layout mode",
          enumValues: [
            "background",
            "left",
            "right",
            "dual",
            "around",
            "frame"
          ],
          default: "background"
        },
        opacity: {
          types: [
            "number"
          ],
          description: "Overall opacity (default: 0.06)",
          default: 0.06
        },
        color: {
          types: [
            "string"
          ],
          description: "Stroke color passed to PatternTile",
          default: "var(--color-primary)"
        },
        scale: {
          types: [
            "number"
          ],
          description: "Tile scale multiplier (default: 1)",
          default: 1
        },
        strokeWidth: {
          types: [
            "number"
          ],
          description: "Stroke width passed to PatternTile",
          default: 0.5
        },
        children: {
          types: [
            "node"
          ],
          description: "Content to wrap (used in frame mode)"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "icon-button": {
      type: "icon-button",
      category: "component",
      sourceFile: "components/core/molecules/IconButton.tsx",
      tier: "molecules",
      family: "core",
      description: "IconButton component",
      accessibleName: [
        "label"
      ],
      suggestedFor: [
        "icon",
        "button",
        "icon button"
      ],
      typicalSize: "medium",
      propsSchema: {
        className: {
          types: [
            "string"
          ],
          description: "Additional CSS classes applied to the root element."
        },
        variant: {
          types: [
            "string"
          ],
          description: "variant prop",
          enumValues: [
            "primary",
            "secondary",
            "ghost",
            "danger",
            "success",
            "warning",
            "default",
            "link"
          ],
          default: "ghost"
        },
        size: {
          types: [
            "string"
          ],
          description: "size prop",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "sm"
        },
        isLoading: {
          types: [
            "boolean"
          ],
          description: "isLoading prop"
        },
        iconAsset: {
          types: [
            "object"
          ],
          description: "Asset image rendered as the leading icon; takes precedence over icon/leftIcon when provided.",
          properties: {
            url: {
              types: [
                "asset"
              ]
            },
            role: {
              types: [
                "string"
              ]
            },
            category: {
              types: [
                "string"
              ]
            },
            animations: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            style: {
              types: [
                "string"
              ],
              enumValues: [
                "pixel",
                "vector",
                "hd",
                "1-bit",
                "isometric"
              ]
            },
            variant: {
              types: [
                "string"
              ]
            },
            dimension: {
              types: [
                "string"
              ],
              enumValues: [
                "2d",
                "3d"
              ]
            },
            aspect: {
              types: [
                "string"
              ],
              enumValues: [
                "1:1",
                "16:9",
                "5:7",
                "8:1",
                "4:1",
                "5:3",
                "1:2"
              ]
            },
            atlas: {
              types: [
                "asset"
              ]
            },
            sprite: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            },
            thumbnailUrl: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "url",
            "role",
            "category"
          ]
        },
        action: {
          types: [
            "string"
          ],
          description: "Declarative event name \u2014 emits UI:{action} via eventBus on click",
          kind: "event"
        },
        actionPayload: {
          types: [
            "object"
          ],
          description: "Payload to include with the action event",
          payloadFor: "action",
          eventPayloadBrand: true
        },
        disabled: {
          types: [
            "boolean"
          ],
          description: "Disable the button (greys out, blocks click events)"
        },
        href: {
          types: [
            "string"
          ],
          description: "Makes the button a link: `#anchor` scrolls, an absolute URL loads, a path navigates in-app"
        },
        onClick: {
          types: [
            "function"
          ],
          description: "Runs on activation, on the button or, with `href`, on the link; calling `preventDefault()` there keeps a link from following its `href`.",
          kind: "callback",
          callbackArgs: [
            {
              name: "event",
              type: "object"
            }
          ],
          nonEmittable: true
        },
        "data-testid": {
          types: [
            "string"
          ],
          description: "Test identifier for automated tests"
        },
        icon: {
          types: [
            "icon",
            "string"
          ],
          description: "icon prop",
          required: true
        },
        label: {
          types: [
            "string"
          ],
          description: "What the button does \u2014 its accessible name and its tooltip.",
          required: true
        },
        tooltipPosition: {
          types: [
            "string"
          ],
          description: "Where the tooltip opens.",
          enumValues: [
            "top",
            "bottom",
            "left",
            "right"
          ],
          default: "right"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "top-nav-item": {
      type: "top-nav-item",
      category: "navigation",
      sourceFile: "components/core/molecules/TopNavItem.tsx",
      tier: "molecules",
      family: "core",
      description: "TopNavItem component",
      suggestedFor: [
        "top",
        "nav",
        "item",
        "top nav item"
      ],
      typicalSize: "medium",
      propsSchema: {
        item: {
          types: [
            "object"
          ],
          description: "item prop",
          required: true,
          properties: {
            label: {
              types: [
                "string"
              ]
            },
            href: {
              types: [
                "string"
              ]
            },
            icon: {
              types: [
                "icon",
                "string"
              ]
            },
            badge: {
              types: [
                "string",
                "number"
              ]
            },
            active: {
              types: [
                "boolean"
              ]
            },
            onClick: {
              types: [
                "function"
              ]
            },
            children: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                cyclic: true,
                cyclicTypeName: "TopNavItemData"
              }
            }
          },
          propertyRequired: [
            "label"
          ]
        },
        activeHref: {
          types: [
            "string"
          ],
          description: "The href of the active page, used to mark the item (or its parent) active."
        },
        tone: {
          types: [
            "string"
          ],
          description: "Active styling: `solid` fills with the primary colour (dashboard top bar); `soft` tints it (site header).",
          enumValues: [
            "solid",
            "soft"
          ],
          default: "solid"
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "change-list": {
      type: "change-list",
      category: "display",
      sourceFile: "components/core/molecules/ChangeList.tsx",
      tier: "molecules",
      family: "core",
      description: "The plain-language changes in one version (added / modified / removed).",
      capabilities: "change summary, what changed, version changes, diff summary, release notes list, modification list",
      suggestedFor: [
        "change",
        "list",
        "change list"
      ],
      typicalSize: "medium",
      propsSchema: {
        changes: {
          types: [
            "array",
            "object"
          ],
          description: "The changes. Accepts a typed list or a render-ui payload binding.",
          required: true,
          items: {
            types: [
              "object"
            ],
            properties: {
              type: {
                types: [
                  "string"
                ],
                enumValues: [
                  "added",
                  "modified",
                  "removed"
                ]
              },
              title: {
                types: [
                  "string"
                ]
              },
              description: {
                types: [
                  "string"
                ]
              },
              before: {
                types: [
                  "string"
                ]
              },
              after: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "type",
              "title"
            ]
          }
        },
        inspectEvent: {
          types: [
            "string"
          ],
          description: "Emitted by a change's Inspect button. Payload: { title, type }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "title",
              type: "string",
              required: true
            },
            {
              name: "type",
              type: "string",
              required: true,
              enumValues: [
                "added",
                "modified",
                "removed"
              ]
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "commit-row": {
      type: "commit-row",
      category: "component",
      sourceFile: "components/core/molecules/CommitRow.tsx",
      tier: "molecules",
      family: "core",
      description: "One version in a history list \u2014 a commit row with select and restore.",
      capabilities: "version history row, commit entry, revision list item, change log entry, restore point, undo history item",
      suggestedFor: [
        "commit",
        "row",
        "commit row"
      ],
      typicalSize: "medium",
      propsSchema: {
        sha: {
          types: [
            "string"
          ],
          description: "Commit id; the first 7 characters are shown, the whole id on hover.",
          required: true
        },
        message: {
          types: [
            "string"
          ],
          description: "Commit message.",
          required: true
        },
        author: {
          types: [
            "string"
          ],
          description: "author prop"
        },
        timestamp: {
          types: [
            "string"
          ],
          description: 'Display time (already formatted, e.g. "2 min ago").'
        },
        kind: {
          types: [
            "string"
          ],
          description: "What made this version (a user save, an agent turn, a restore, an undo/redo, a snapshot, a merge of a teammate's work).",
          enumValues: [
            "user",
            "agent",
            "restore",
            "undo",
            "redo",
            "snapshot",
            "merge"
          ]
        },
        additions: {
          types: [
            "number"
          ],
          description: "additions prop"
        },
        deletions: {
          types: [
            "number"
          ],
          description: "deletions prop"
        },
        selected: {
          types: [
            "boolean"
          ],
          description: "The row the user picked (shows its detail).",
          default: false
        },
        current: {
          types: [
            "boolean"
          ],
          description: "The version the app is at now \u2014 it cannot be restored to.",
          default: false
        },
        selectEvent: {
          types: [
            "string"
          ],
          description: "Emitted when the row is picked. Payload: { sha }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "sha",
              type: "string",
              required: true
            }
          ]
        },
        restoreEvent: {
          types: [
            "string"
          ],
          description: "Emitted by the Restore button. Payload: { sha }.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "sha",
              type: "string",
              required: true
            }
          ]
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "avl-state-machine": {
      type: "avl-state-machine",
      category: "display",
      sourceFile: "components/avl/molecules/AvlStateMachine.tsx",
      tier: "molecules",
      family: "avl",
      description: "AvlStateMachine component",
      suggestedFor: [
        "avl",
        "state",
        "machine",
        "avl state machine"
      ],
      typicalSize: "medium",
      propsSchema: {
        trait: {
          types: [
            "object"
          ],
          description: "The trait to draw (see `parseTraitLevel`).",
          required: true,
          properties: {
            name: {
              types: [
                "string"
              ]
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            states: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  name: {
                    types: [
                      "string"
                    ]
                  },
                  isInitial: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  },
                  isTerminal: {
                    types: [
                      "boolean"
                    ],
                    nullable: true
                  }
                },
                required: [
                  "name"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  guard: {
                    types: [
                      "sexpr"
                    ],
                    nullable: true
                  },
                  effects: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        args: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "sexpr"
                            ]
                          }
                        }
                      },
                      required: [
                        "type",
                        "args"
                      ]
                    }
                  },
                  index: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "from",
                  "to",
                  "event",
                  "effects",
                  "index"
                ]
              }
            },
            emittedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listenedEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            }
          },
          propertyRequired: [
            "name",
            "linkedEntity",
            "states",
            "transitions",
            "emittedEvents",
            "listenedEvents"
          ],
          example: {
            name: "OrderFlow",
            linkedEntity: "Order",
            states: [
              {
                name: "browsing",
                isInitial: true,
                isTerminal: false
              },
              {
                name: "editing",
                isInitial: false,
                isTerminal: false
              },
              {
                name: "saving",
                isInitial: false,
                isTerminal: false
              },
              {
                name: "confirmed",
                isInitial: false,
                isTerminal: true
              },
              {
                name: "failed",
                isInitial: false,
                isTerminal: false
              }
            ],
            transitions: [
              {
                from: "browsing",
                to: "editing",
                event: "EDIT",
                effects: [
                  {
                    type: "render-ui",
                    args: []
                  }
                ],
                index: 0
              },
              {
                from: "editing",
                to: "saving",
                event: "SAVE",
                effects: [
                  {
                    type: "persist",
                    args: []
                  },
                  {
                    type: "notify",
                    args: []
                  }
                ],
                index: 1
              },
              {
                from: "saving",
                to: "confirmed",
                event: "SAVED",
                effects: [
                  {
                    type: "emit",
                    args: []
                  },
                  {
                    type: "render-ui",
                    args: []
                  }
                ],
                index: 2
              },
              {
                from: "saving",
                to: "failed",
                event: "SAVE_FAILED",
                effects: [
                  {
                    type: "notify",
                    args: []
                  }
                ],
                index: 3
              },
              {
                from: "failed",
                to: "editing",
                event: "RETRY",
                effects: [],
                index: 4
              },
              {
                from: "editing",
                to: "browsing",
                event: "CANCEL",
                effects: [
                  {
                    type: "render-ui",
                    args: []
                  }
                ],
                index: 5
              },
              {
                from: "confirmed",
                to: "browsing",
                event: "DONE",
                effects: [
                  {
                    type: "navigate",
                    args: []
                  }
                ],
                index: 6
              }
            ],
            emittedEvents: [
              "ORDER_SAVED"
            ],
            listenedEvents: [
              "PAYMENT_OK"
            ]
          }
        },
        activeState: {
          types: [
            "string"
          ],
          description: "State execution is currently in."
        },
        activeTransition: {
          types: [
            "number"
          ],
          description: "`index` of the transition that just fired."
        },
        visitedStates: {
          types: [
            "array"
          ],
          description: "States already passed through, in order.",
          items: {
            types: [
              "string"
            ]
          }
        },
        stateClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{stateClickEvent} with { stateId } when a state is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "stateId",
              type: "string",
              required: true
            }
          ]
        },
        transitionClickEvent: {
          types: [
            "string"
          ],
          description: "Emits UI:{transitionClickEvent} with { index, event, from, to } when a transition label is clicked.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "index",
              type: "number",
              required: true
            },
            {
              name: "event",
              type: "string",
              required: true
            },
            {
              name: "from",
              type: "string",
              required: true
            },
            {
              name: "to",
              type: "string",
              required: true
            }
          ]
        },
        onTransitionClick: {
          types: [
            "function"
          ],
          description: "Direct transition-click callback, for React hosts (e.g. a canvas node).",
          kind: "callback",
          callbackArgs: [
            {
              name: "transition",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  index: {
                    types: [
                      "number"
                    ]
                  },
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  to: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "index",
                  "event",
                  "from",
                  "to"
                ]
              }
            }
          ]
        },
        selectedState: {
          types: [
            "string"
          ],
          description: "State picked in an editor (first click)."
        },
        pendingSourceState: {
          types: [
            "string"
          ],
          description: "Source of a transition being drawn \u2014 the next state click picks its target."
        },
        nodeShape: {
          types: [
            "string"
          ],
          description: "Node silhouette. @default 'pill'",
          enumValues: [
            "pill",
            "gear"
          ],
          default: "pill"
        },
        entityFields: {
          types: [
            "array"
          ],
          description: "Entity field names listed in the header.",
          items: {
            types: [
              "string"
            ]
          }
        },
        direction: {
          types: [
            "string"
          ],
          description: "Flow direction. @default 'ltr'",
          enumValues: [
            "ltr",
            "rtl"
          ],
          default: "ltr"
        },
        showHeader: {
          types: [
            "boolean"
          ],
          description: "Show the trait header (name, entity, listens/emits). @default true",
          default: true
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations shown in a popover on hover: a note per state name, per transition event and per effect type.",
          properties: {
            states: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            transitions: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            effects: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            }
          },
          example: {
            states: {
              pending: {
                title: "Unpaid",
                body: "Every order starts here."
              }
            },
            transitions: {
              PAY: {
                body: "Only fires when the amount is positive."
              }
            },
            effects: {
              persist: {
                body: "Saves the order."
              }
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "avl-glyph": {
      type: "avl-glyph",
      category: "component",
      sourceFile: "components/avl/molecules/AvlGlyph.tsx",
      tier: "molecules",
      family: "avl",
      description: "AvlGlyph component",
      suggestedFor: [
        "avl",
        "glyph",
        "avl glyph"
      ],
      typicalSize: "medium",
      propsSchema: {
        kind: {
          types: [
            "string"
          ],
          description: "Which notation symbol to draw.",
          required: true,
          enumValues: [
            "application",
            "orbital",
            "entity",
            "trait",
            "page",
            "state",
            "transition",
            "event",
            "guard",
            "effect",
            "field",
            "fieldType",
            "binding",
            "bindingRef",
            "persistence",
            "operator",
            "sexpr",
            "literal"
          ],
          example: "state"
        },
        label: {
          types: [
            "string"
          ],
          description: "Caption, and the drawn text for operator / literal / bindingRef symbols."
        },
        effectType: {
          types: [
            "string"
          ],
          description: "Effect symbol variant. @default 'set'",
          default: "set"
        },
        fieldType: {
          types: [
            "object"
          ],
          description: "Field-type symbol variant. @default 'string'",
          default: "string"
        },
        persistence: {
          types: [
            "object"
          ],
          description: "Persistence symbol variant (also the entity's ring). @default 'persistent'",
          default: "persistent"
        },
        namespace: {
          types: [
            "object"
          ],
          description: "Operator category tint. @default 'arithmetic'",
          default: "arithmetic"
        },
        size: {
          types: [
            "string"
          ],
          description: "Symbol height. @default 'md'",
          enumValues: [
            "sm",
            "md",
            "lg"
          ],
          default: "md"
        },
        showCaption: {
          types: [
            "boolean"
          ],
          description: "Show the caption under the symbol. @default true",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "agent-activity-feed": {
      type: "agent-activity-feed",
      category: "component",
      sourceFile: "components/core/molecules/AgentActivityFeed.tsx",
      tier: "molecules",
      family: "core",
      description: "AgentActivityFeed component",
      suggestedFor: [
        "agent",
        "activity",
        "feed",
        "agent activity feed"
      ],
      typicalSize: "medium",
      propsSchema: {
        activities: {
          types: [
            "array"
          ],
          description: "activities prop",
          required: true,
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "message"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "user",
                      "assistant",
                      "system"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isStreaming: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "role",
                  "content",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_call"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  argsText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isExecuting: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "argsText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_result"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  detail: {
                    types: [
                      "string"
                    ]
                  },
                  rows: {
                    types: [
                      "number"
                    ]
                  },
                  resultText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "success",
                  "resultText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "file_operation"
                    ]
                  },
                  operation: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ls",
                      "read_file",
                      "write_file",
                      "edit_file"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "operation",
                  "path",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_diff"
                    ]
                  },
                  filePath: {
                    types: [
                      "string"
                    ]
                  },
                  hunks: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        oldStart: {
                          types: [
                            "number"
                          ]
                        },
                        oldLines: {
                          types: [
                            "number"
                          ]
                        },
                        newStart: {
                          types: [
                            "number"
                          ]
                        },
                        newLines: {
                          types: [
                            "number"
                          ]
                        },
                        lines: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ],
                                enumValues: [
                                  "add",
                                  "remove",
                                  "context"
                                ]
                              },
                              content: {
                                types: [
                                  "string"
                                ]
                              }
                            },
                            required: [
                              "type",
                              "content"
                            ]
                          }
                        }
                      },
                      required: [
                        "oldStart",
                        "oldLines",
                        "newStart",
                        "newLines",
                        "lines"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "filePath",
                  "hunks",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "error"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "coordinator_decision"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  reason: {
                    types: [
                      "string"
                    ]
                  },
                  priorOrganism: {
                    types: [
                      "string"
                    ],
                    nullable: true
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "reason",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "plan_committed"
                    ]
                  },
                  orbitals: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitals",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pending_question"
                    ]
                  },
                  questionId: {
                    types: [
                      "string"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "questionId",
                  "question",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "clarification_question"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "organism",
                      "molecule",
                      "atom_trait"
                    ]
                  },
                  scope: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      }
                    }
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  candidates: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        id: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        description: {
                          types: [
                            "string"
                          ]
                        },
                        whyThisFits: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "id",
                        "label",
                        "description",
                        "whyThisFits"
                      ]
                    }
                  },
                  skipDefault: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "scope",
                  "question",
                  "candidates",
                  "skipDefault",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "analysis"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  organismReason: {
                    types: [
                      "string"
                    ]
                  },
                  complexity: {
                    types: [
                      "object"
                    ],
                    properties: {
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      reasoning: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "category",
                      "reasoning"
                    ]
                  },
                  renames: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to"
                      ]
                    }
                  },
                  deletes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "renames",
                  "deletes",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_started"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_done"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_change"
                    ]
                  },
                  changeKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital-added",
                      "orbital-removed",
                      "entity-fields-changed",
                      "trait-added",
                      "trait-removed",
                      "trait-config-changed",
                      "state-machine-changed",
                      "guard-changed",
                      "effect-changed",
                      "render-ui-changed",
                      "event-wiring-changed",
                      "page-changed",
                      "theme-changed",
                      "behavior-composed"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "changeKind",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "done"
                    ]
                  },
                  orbitalCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalCount",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "cancelled"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "llm_response"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  llm: {
                    types: [
                      "object"
                    ],
                    properties: {
                      service: {
                        types: [
                          "string"
                        ]
                      },
                      provider: {
                        types: [
                          "string"
                        ]
                      },
                      model: {
                        types: [
                          "string"
                        ]
                      },
                      systemPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      userPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      toolCallsJson: {
                        types: [
                          "string"
                        ]
                      },
                      attempt: {
                        types: [
                          "number"
                        ]
                      },
                      durationMs: {
                        types: [
                          "number"
                        ]
                      },
                      promptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      completionTokens: {
                        types: [
                          "number"
                        ]
                      },
                      cachedPromptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      costUSD: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "service",
                      "provider",
                      "model",
                      "systemPrompt",
                      "userPrompt",
                      "durationMs"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "content",
                  "timestamp"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        working: {
          types: [
            "boolean"
          ],
          description: "The agent is running: show the thinking row after the last activity.",
          default: false
        },
        onJump: {
          types: [
            "function"
          ],
          description: "onJump prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "target",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "orbital"
                ]
              }
            }
          ]
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: "emptyTitle prop"
        },
        emptyDescription: {
          types: [
            "string"
          ],
          description: "emptyDescription prop"
        },
        followLatest: {
          types: [
            "boolean"
          ],
          description: "Scroll the latest row into view as rows arrive. Off for a list of feeds (a run history). @default true",
          default: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "agent-activity-row": {
      type: "agent-activity-row",
      category: "component",
      sourceFile: "components/core/molecules/AgentActivityFeed.tsx",
      tier: "molecules",
      family: "core",
      description: "AgentActivityRow component",
      suggestedFor: [
        "agent",
        "activity",
        "row",
        "agent activity row"
      ],
      typicalSize: "medium",
      propsSchema: {
        activity: {
          types: [
            "object"
          ],
          description: "activity prop",
          required: true,
          variants: [
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "message"
                  ]
                },
                role: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "user",
                    "assistant",
                    "system"
                  ]
                },
                content: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                },
                isStreaming: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "type",
                "role",
                "content",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "tool_call"
                  ]
                },
                tool: {
                  types: [
                    "string"
                  ]
                },
                kind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "input",
                    "read",
                    "invalid"
                  ]
                },
                argsText: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                },
                isExecuting: {
                  types: [
                    "boolean"
                  ]
                }
              },
              required: [
                "type",
                "tool",
                "argsText",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "tool_result"
                  ]
                },
                tool: {
                  types: [
                    "string"
                  ]
                },
                kind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "input",
                    "read",
                    "invalid"
                  ]
                },
                success: {
                  types: [
                    "boolean"
                  ]
                },
                detail: {
                  types: [
                    "string"
                  ]
                },
                rows: {
                  types: [
                    "number"
                  ]
                },
                resultText: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "tool",
                "success",
                "resultText",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "file_operation"
                  ]
                },
                operation: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "ls",
                    "read_file",
                    "write_file",
                    "edit_file"
                  ]
                },
                path: {
                  types: [
                    "string"
                  ]
                },
                success: {
                  types: [
                    "boolean"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "operation",
                "path",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "schema_diff"
                  ]
                },
                filePath: {
                  types: [
                    "string"
                  ]
                },
                hunks: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      oldStart: {
                        types: [
                          "number"
                        ]
                      },
                      oldLines: {
                        types: [
                          "number"
                        ]
                      },
                      newStart: {
                        types: [
                          "number"
                        ]
                      },
                      newLines: {
                        types: [
                          "number"
                        ]
                      },
                      lines: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            type: {
                              types: [
                                "string"
                              ],
                              enumValues: [
                                "add",
                                "remove",
                                "context"
                              ]
                            },
                            content: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "type",
                            "content"
                          ]
                        }
                      }
                    },
                    required: [
                      "oldStart",
                      "oldLines",
                      "newStart",
                      "newLines",
                      "lines"
                    ]
                  }
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "filePath",
                "hunks",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "error"
                  ]
                },
                message: {
                  types: [
                    "string"
                  ]
                },
                code: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "message",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "coordinator_decision"
                  ]
                },
                organism: {
                  types: [
                    "string"
                  ]
                },
                reason: {
                  types: [
                    "string"
                  ]
                },
                priorOrganism: {
                  types: [
                    "string"
                  ],
                  nullable: true
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "organism",
                "reason",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "plan_committed"
                  ]
                },
                orbitals: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitals",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "pending_question"
                  ]
                },
                questionId: {
                  types: [
                    "string"
                  ]
                },
                question: {
                  types: [
                    "string"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "questionId",
                "question",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "clarification_question"
                  ]
                },
                level: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "organism",
                    "molecule",
                    "atom_trait"
                  ]
                },
                scope: {
                  types: [
                    "object"
                  ],
                  properties: {
                    orbitalName: {
                      types: [
                        "string"
                      ]
                    },
                    traitName: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                question: {
                  types: [
                    "string"
                  ]
                },
                candidates: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      id: {
                        types: [
                          "string"
                        ]
                      },
                      label: {
                        types: [
                          "string"
                        ]
                      },
                      description: {
                        types: [
                          "string"
                        ]
                      },
                      whyThisFits: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "id",
                      "label",
                      "description",
                      "whyThisFits"
                    ]
                  }
                },
                skipDefault: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "level",
                "scope",
                "question",
                "candidates",
                "skipDefault",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "analysis"
                  ]
                },
                organism: {
                  types: [
                    "string"
                  ]
                },
                organismReason: {
                  types: [
                    "string"
                  ]
                },
                complexity: {
                  types: [
                    "object"
                  ],
                  properties: {
                    category: {
                      types: [
                        "string"
                      ]
                    },
                    reasoning: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  required: [
                    "category",
                    "reasoning"
                  ]
                },
                renames: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      from: {
                        types: [
                          "string"
                        ]
                      },
                      to: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "from",
                      "to"
                    ]
                  }
                },
                deletes: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "organism",
                "renames",
                "deletes",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "orbital_started"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitalName",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "orbital_done"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                traitCount: {
                  types: [
                    "number"
                  ]
                },
                transitionCount: {
                  types: [
                    "number"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitalName",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "schema_change"
                  ]
                },
                changeKind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "orbital-added",
                    "orbital-removed",
                    "entity-fields-changed",
                    "trait-added",
                    "trait-removed",
                    "trait-config-changed",
                    "state-machine-changed",
                    "guard-changed",
                    "effect-changed",
                    "render-ui-changed",
                    "event-wiring-changed",
                    "page-changed",
                    "theme-changed",
                    "behavior-composed"
                  ]
                },
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                traitName: {
                  types: [
                    "string"
                  ]
                },
                transitionEvent: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "changeKind",
                "orbitalName",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "done"
                  ]
                },
                orbitalCount: {
                  types: [
                    "number"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "orbitalCount",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "cancelled"
                  ]
                },
                message: {
                  types: [
                    "string"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "message",
                "timestamp"
              ]
            },
            {
              types: [
                "object"
              ],
              properties: {
                type: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "llm_response"
                  ]
                },
                content: {
                  types: [
                    "string"
                  ]
                },
                llm: {
                  types: [
                    "object"
                  ],
                  properties: {
                    service: {
                      types: [
                        "string"
                      ]
                    },
                    provider: {
                      types: [
                        "string"
                      ]
                    },
                    model: {
                      types: [
                        "string"
                      ]
                    },
                    systemPrompt: {
                      types: [
                        "string"
                      ]
                    },
                    userPrompt: {
                      types: [
                        "string"
                      ]
                    },
                    toolCallsJson: {
                      types: [
                        "string"
                      ]
                    },
                    attempt: {
                      types: [
                        "number"
                      ]
                    },
                    durationMs: {
                      types: [
                        "number"
                      ]
                    },
                    promptTokens: {
                      types: [
                        "number"
                      ]
                    },
                    completionTokens: {
                      types: [
                        "number"
                      ]
                    },
                    cachedPromptTokens: {
                      types: [
                        "number"
                      ]
                    },
                    costUSD: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "service",
                    "provider",
                    "model",
                    "systemPrompt",
                    "userPrompt",
                    "durationMs"
                  ]
                },
                timestamp: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "type",
                "content",
                "timestamp"
              ]
            }
          ],
          discriminator: "type"
        },
        onJump: {
          types: [
            "function"
          ],
          description: '"Jump to" affordance \u2014 rendered only when supplied and the activity names an orbital.',
          kind: "callback",
          callbackArgs: [
            {
              name: "target",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "orbital"
                ]
              }
            }
          ]
        },
        facts: {
          types: [
            "object"
          ],
          description: "What the row knows from its list (`activityRowFacts`).",
          required: true,
          properties: {
            settled: {
              types: [
                "boolean"
              ]
            },
            readyCount: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "settled",
            "readyCount"
          ]
        }
      }
    },
    "agent-chat-feed": {
      type: "agent-chat-feed",
      category: "component",
      sourceFile: "components/core/molecules/AgentChatFeed.tsx",
      tier: "molecules",
      family: "core",
      description: "AgentChatFeed \u2014 a chat region's view of an agent turn: the activities the host's audience selected, rendered by `AgentActivityFeed`, with raw LLM responses behind a \"Raw LLM\" switch (off by default, so the conversation stays readable until they're wanted). The switch is remembered per host.",
      suggestedFor: [
        "agent",
        "chat",
        "feed",
        "agent chat feed"
      ],
      typicalSize: "medium",
      propsSchema: {
        activities: {
          types: [
            "array"
          ],
          description: "Activities already selected by the host's manifest audience.",
          required: true,
          items: {
            types: [
              "object"
            ],
            variants: [
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "message"
                    ]
                  },
                  role: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "user",
                      "assistant",
                      "system"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isStreaming: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "role",
                  "content",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_call"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  argsText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  },
                  isExecuting: {
                    types: [
                      "boolean"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "argsText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "tool_result"
                    ]
                  },
                  tool: {
                    types: [
                      "string"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "input",
                      "read",
                      "invalid"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  detail: {
                    types: [
                      "string"
                    ]
                  },
                  rows: {
                    types: [
                      "number"
                    ]
                  },
                  resultText: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "tool",
                  "success",
                  "resultText",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "file_operation"
                    ]
                  },
                  operation: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ls",
                      "read_file",
                      "write_file",
                      "edit_file"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  success: {
                    types: [
                      "boolean"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "operation",
                  "path",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_diff"
                    ]
                  },
                  filePath: {
                    types: [
                      "string"
                    ]
                  },
                  hunks: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        oldStart: {
                          types: [
                            "number"
                          ]
                        },
                        oldLines: {
                          types: [
                            "number"
                          ]
                        },
                        newStart: {
                          types: [
                            "number"
                          ]
                        },
                        newLines: {
                          types: [
                            "number"
                          ]
                        },
                        lines: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              type: {
                                types: [
                                  "string"
                                ],
                                enumValues: [
                                  "add",
                                  "remove",
                                  "context"
                                ]
                              },
                              content: {
                                types: [
                                  "string"
                                ]
                              }
                            },
                            required: [
                              "type",
                              "content"
                            ]
                          }
                        }
                      },
                      required: [
                        "oldStart",
                        "oldLines",
                        "newStart",
                        "newLines",
                        "lines"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "filePath",
                  "hunks",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "error"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  code: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "coordinator_decision"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  reason: {
                    types: [
                      "string"
                    ]
                  },
                  priorOrganism: {
                    types: [
                      "string"
                    ],
                    nullable: true
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "reason",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "plan_committed"
                    ]
                  },
                  orbitals: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitals",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "pending_question"
                    ]
                  },
                  questionId: {
                    types: [
                      "string"
                    ]
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "questionId",
                  "question",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "clarification_question"
                    ]
                  },
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "organism",
                      "molecule",
                      "atom_trait"
                    ]
                  },
                  scope: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      }
                    }
                  },
                  question: {
                    types: [
                      "string"
                    ]
                  },
                  candidates: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        id: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        description: {
                          types: [
                            "string"
                          ]
                        },
                        whyThisFits: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "id",
                        "label",
                        "description",
                        "whyThisFits"
                      ]
                    }
                  },
                  skipDefault: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "level",
                  "scope",
                  "question",
                  "candidates",
                  "skipDefault",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "analysis"
                    ]
                  },
                  organism: {
                    types: [
                      "string"
                    ]
                  },
                  organismReason: {
                    types: [
                      "string"
                    ]
                  },
                  complexity: {
                    types: [
                      "object"
                    ],
                    properties: {
                      category: {
                        types: [
                          "string"
                        ]
                      },
                      reasoning: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "category",
                      "reasoning"
                    ]
                  },
                  renames: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        from: {
                          types: [
                            "string"
                          ]
                        },
                        to: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "from",
                        "to"
                      ]
                    }
                  },
                  deletes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "organism",
                  "renames",
                  "deletes",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_started"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital_done"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  transitionCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "schema_change"
                    ]
                  },
                  changeKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "orbital-added",
                      "orbital-removed",
                      "entity-fields-changed",
                      "trait-added",
                      "trait-removed",
                      "trait-config-changed",
                      "state-machine-changed",
                      "guard-changed",
                      "effect-changed",
                      "render-ui-changed",
                      "event-wiring-changed",
                      "page-changed",
                      "theme-changed",
                      "behavior-composed"
                    ]
                  },
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "changeKind",
                  "orbitalName",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "done"
                    ]
                  },
                  orbitalCount: {
                    types: [
                      "number"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "orbitalCount",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "cancelled"
                    ]
                  },
                  message: {
                    types: [
                      "string"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "message",
                  "timestamp"
                ]
              },
              {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "llm_response"
                    ]
                  },
                  content: {
                    types: [
                      "string"
                    ]
                  },
                  llm: {
                    types: [
                      "object"
                    ],
                    properties: {
                      service: {
                        types: [
                          "string"
                        ]
                      },
                      provider: {
                        types: [
                          "string"
                        ]
                      },
                      model: {
                        types: [
                          "string"
                        ]
                      },
                      systemPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      userPrompt: {
                        types: [
                          "string"
                        ]
                      },
                      toolCallsJson: {
                        types: [
                          "string"
                        ]
                      },
                      attempt: {
                        types: [
                          "number"
                        ]
                      },
                      durationMs: {
                        types: [
                          "number"
                        ]
                      },
                      promptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      completionTokens: {
                        types: [
                          "number"
                        ]
                      },
                      cachedPromptTokens: {
                        types: [
                          "number"
                        ]
                      },
                      costUSD: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "service",
                      "provider",
                      "model",
                      "systemPrompt",
                      "userPrompt",
                      "durationMs"
                    ]
                  },
                  timestamp: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "type",
                  "content",
                  "timestamp"
                ]
              }
            ],
            discriminator: "type"
          }
        },
        emptyTitle: {
          types: [
            "string"
          ],
          description: "emptyTitle prop",
          required: true
        },
        rawLlmPreferenceKey: {
          types: [
            "string"
          ],
          description: "localStorage key remembering the Raw LLM switch for this host.",
          required: true
        },
        idPrefix: {
          types: [
            "string"
          ],
          description: "Prefix for the scroller test id and the switch's element id.",
          required: true
        },
        working: {
          types: [
            "boolean"
          ],
          description: "The agent is running \u2014 the feed shows it is thinking.",
          default: false
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "avl-transition-explainer": {
      type: "avl-transition-explainer",
      category: "component",
      sourceFile: "components/avl/molecules/AvlTransitionExplainer.tsx",
      tier: "molecules",
      family: "avl",
      description: "One transition explained step by step: from-state, event and to-state, the guard that must hold, and each effect it runs in order.",
      capabilities: "transition explainer, event guard effects walkthrough, what happens when an event fires",
      suggestedFor: [
        "avl",
        "transition",
        "explainer",
        "avl transition explainer"
      ],
      typicalSize: "medium",
      propsSchema: {
        orbitalName: {
          types: [
            "string"
          ],
          description: "Orbital the transition belongs to.",
          required: true
        },
        traitName: {
          types: [
            "string"
          ],
          description: "Trait the transition belongs to.",
          required: true
        },
        transition: {
          types: [
            "object"
          ],
          description: "The transition to explain.",
          required: true,
          properties: {
            from: {
              types: [
                "string"
              ]
            },
            to: {
              types: [
                "string"
              ]
            },
            event: {
              types: [
                "string"
              ]
            },
            guard: {
              types: [
                "sexpr"
              ]
            },
            effects: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  type: {
                    types: [
                      "string"
                    ]
                  },
                  args: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "sexpr"
                      ]
                    }
                  }
                },
                required: [
                  "type",
                  "args"
                ]
              }
            },
            index: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "from",
            "to",
            "event",
            "effects",
            "index"
          ],
          example: {
            from: "pending",
            to: "paid",
            event: "PAY",
            effects: [
              {
                type: "set",
                args: []
              },
              {
                type: "persist",
                args: []
              }
            ],
            index: 0
          }
        },
        annotations: {
          types: [
            "object"
          ],
          description: "Author explanations shown in a popover on hover: the event's note and per-effect-type notes.",
          properties: {
            states: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            transitions: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            },
            effects: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  title: {
                    types: [
                      "string"
                    ]
                  },
                  body: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "body"
                ]
              }
            }
          },
          example: {
            transitions: {
              PAY: {
                title: "Paying",
                body: "The customer pays the order total."
              }
            },
            effects: {
              persist: {
                body: "Saves the order."
              }
            }
          }
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        },
        "aria-label": {
          types: [
            "string"
          ],
          description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)."
        },
        "aria-labelledby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text names this one."
        },
        "aria-describedby": {
          types: [
            "string"
          ],
          description: "Id of the element whose text describes this one (help text, error)."
        },
        "aria-live": {
          types: [
            "string"
          ],
          description: "Announce changes to this region: polite waits for a pause, assertive interrupts.",
          enumValues: [
            "off",
            "polite",
            "assertive"
          ]
        },
        "aria-current": {
          types: [
            "string"
          ],
          description: "Marks the current item in a set: the current page, step, location, date or time.",
          enumValues: [
            "page",
            "step",
            "location",
            "date",
            "time",
            "true",
            "false"
          ]
        },
        "aria-pressed": {
          types: [
            "string"
          ],
          description: "Toggle-button state.",
          enumValues: [
            "true",
            "false",
            "mixed"
          ]
        },
        "aria-expanded": {
          types: [
            "boolean"
          ],
          description: "Whether the element it controls is expanded."
        },
        "aria-selected": {
          types: [
            "boolean"
          ],
          description: "Whether this item is selected (tabs, options, grid cells)."
        },
        "aria-hidden": {
          types: [
            "boolean"
          ],
          description: "Hide from assistive technology (decorative content only)."
        },
        "aria-busy": {
          types: [
            "boolean"
          ],
          description: "This region is updating; assistive technology waits before announcing it."
        },
        role: {
          types: [
            "string"
          ],
          description: "WAI-ARIA role when the element's native semantics don't describe it.",
          enumValues: [
            "alert",
            "alertdialog",
            "application",
            "article",
            "banner",
            "blockquote",
            "button",
            "caption",
            "cell",
            "checkbox",
            "code",
            "columnheader",
            "combobox",
            "complementary",
            "contentinfo",
            "definition",
            "deletion",
            "dialog",
            "document",
            "emphasis",
            "feed",
            "figure",
            "form",
            "generic",
            "grid",
            "gridcell",
            "group",
            "heading",
            "img",
            "insertion",
            "link",
            "list",
            "listbox",
            "listitem",
            "log",
            "main",
            "marquee",
            "math",
            "menu",
            "menubar",
            "menuitem",
            "menuitemcheckbox",
            "menuitemradio",
            "meter",
            "navigation",
            "none",
            "note",
            "option",
            "paragraph",
            "presentation",
            "progressbar",
            "radio",
            "radiogroup",
            "region",
            "row",
            "rowgroup",
            "rowheader",
            "scrollbar",
            "search",
            "searchbox",
            "separator",
            "slider",
            "spinbutton",
            "status",
            "strong",
            "subscript",
            "superscript",
            "switch",
            "tab",
            "table",
            "tablist",
            "tabpanel",
            "term",
            "textbox",
            "time",
            "timer",
            "toolbar",
            "tooltip",
            "tree",
            "treegrid",
            "treeitem"
          ]
        },
        lang: {
          types: [
            "string"
          ],
          description: "Language of this element's content (BCP 47, e.g. ar, sl, en)."
        },
        dir: {
          types: [
            "string"
          ],
          description: "Text direction of this element's content.",
          enumValues: [
            "ltr",
            "rtl",
            "auto"
          ]
        },
        tabIndex: {
          types: [
            "number"
          ],
          description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically.",
          numericEnumValues: [
            -1,
            0
          ]
        }
      }
    },
    "flow-canvas": {
      type: "flow-canvas",
      category: "display",
      sourceFile: "components/avl/organisms/FlowCanvas.tsx",
      tier: "organisms",
      family: "avl",
      description: "FlowCanvas component",
      suggestedFor: [
        "flow",
        "canvas",
        "flow canvas"
      ],
      typicalSize: "large",
      propsSchema: {
        schema: {
          types: [
            "sexpr",
            "string"
          ],
          description: "schema prop",
          required: true,
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core",
            acceptsString: true
          }
        },
        mockData: {
          types: [
            "object"
          ],
          description: "mockData prop",
          nonAuthorable: true
        },
        className: {
          types: [
            "string"
          ],
          description: "className prop"
        },
        width: {
          types: [
            "number",
            "string"
          ],
          description: "width prop",
          default: "100%"
        },
        height: {
          types: [
            "number",
            "string"
          ],
          description: "height prop",
          default: 500
        },
        onNodeClick: {
          types: [
            "function"
          ],
          description: "onNodeClick prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "context",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "overview",
                      "trait-expanded",
                      "system",
                      "code",
                      "transition"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  transitionIndex: {
                    types: [
                      "number"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital"
                ]
              }
            }
          ]
        },
        systemMap: {
          types: [
            "object"
          ],
          description: 'With `initialLevel="system"`: what an orbital chip does (drill in, hover preview, open code, preview).',
          properties: {
            openTraits: {
              types: [
                "function"
              ]
            },
            renderPreview: {
              types: [
                "function"
              ]
            },
            openCodeEvent: {
              types: [
                "string"
              ]
            },
            previewEvent: {
              types: [
                "string"
              ]
            },
            highlighted: {
              types: [
                "string"
              ]
            },
            selectDependency: {
              types: [
                "function"
              ]
            }
          },
          propertyRequired: [
            "openTraits"
          ]
        },
        systemDependencies: {
          types: [
            "object"
          ],
          description: 'With `initialLevel="system"`: the app\'s imports, for the Dependencies lens (absent: Events only).',
          properties: {
            orbitals: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "array"
                ],
                items: {
                  types: [
                    "string"
                  ]
                }
              }
            },
            behaviors: {
              types: [
                "object"
              ],
              mapValue: {
                types: [
                  "object"
                ],
                properties: {
                  layer: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "io",
                      "std",
                      "primitive",
                      "unknown"
                    ]
                  },
                  imports: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  }
                },
                required: [
                  "layer",
                  "imports"
                ]
              }
            }
          },
          propertyRequired: [
            "orbitals",
            "behaviors"
          ]
        },
        traitView: {
          types: [
            "object"
          ],
          description: "Trait level: which lens (and trait filter) shows \u2014 pass with `onTraitViewChange` to keep it across remounts.",
          properties: {
            lens: {
              types: [
                "string"
              ],
              enumValues: [
                "flow",
                "traits"
              ]
            },
            filter: {
              types: [
                "object"
              ],
              properties: {
                unit: {
                  types: [
                    "string"
                  ]
                },
                traits: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                }
              },
              required: [
                "unit",
                "traits"
              ],
              nullable: true
            }
          },
          propertyRequired: [
            "lens"
          ]
        },
        onTraitViewChange: {
          types: [
            "function"
          ],
          description: "onTraitViewChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "view",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  lens: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "flow",
                      "traits"
                    ]
                  },
                  filter: {
                    types: [
                      "object"
                    ],
                    properties: {
                      unit: {
                        types: [
                          "string"
                        ]
                      },
                      traits: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      }
                    },
                    required: [
                      "unit",
                      "traits"
                    ],
                    nullable: true
                  }
                },
                required: [
                  "lens"
                ]
              }
            }
          ]
        },
        openBehaviorEvent: {
          types: [
            "string"
          ],
          description: "Dependencies lens: emits UI:{openBehaviorEvent} with { name } to open a selected behavior's source.",
          kind: "event-ref",
          emitPayloadSchema: [
            {
              name: "name",
              type: "string",
              required: true
            }
          ]
        },
        scene: {
          types: [
            "object"
          ],
          description: 'With `initialLevel="trait-expanded"`: played steps lighting each trait card\'s state machine.',
          properties: {
            steps: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  from: {
                    types: [
                      "string"
                    ]
                  },
                  payload: {
                    types: [
                      "object"
                    ],
                    eventPayloadBrand: true
                  },
                  result: {
                    types: [
                      "object"
                    ]
                  }
                },
                required: [
                  "orbital",
                  "trait",
                  "from",
                  "payload",
                  "result"
                ]
              }
            },
            cursor: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "steps",
            "cursor"
          ]
        },
        onFocusChange: {
          types: [
            "function"
          ],
          description: "Fired when the focused orbital, the scope, or the focused card's state changes.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "focus",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  scope: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "local",
                      "world"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "orbital",
                  "scope",
                  "state"
                ]
              }
            }
          ]
        },
        initialOrbital: {
          types: [
            "string"
          ],
          description: 'The orbital cards open on (local view) \u2014 or, with `initialLevel="trait-expanded"`, the orbital whose traits are shown.'
        },
        initialLevel: {
          types: [
            "string"
          ],
          description: "`trait-expanded`: one card per trait of `initialOrbital` (cosmic). Default: orbital cards.",
          enumValues: [
            "overview",
            "trait-expanded",
            "system"
          ]
        },
        focusedOrbital: {
          types: [
            "string"
          ],
          description: "Focus this orbital (e.g. picked in a Layers panel); changing it moves the focus without remounting."
        },
        initialSelectedNode: {
          types: [
            "object"
          ],
          description: "Pre-select a node on mount (opens OrbInspector).",
          decoder: {
            name: "decodePreviewNodeData",
            from: "@almadar/ui/avl"
          },
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            traitName: {
              types: [
                "string"
              ]
            },
            stateName: {
              types: [
                "string"
              ]
            },
            transitionEvent: {
              types: [
                "string"
              ]
            },
            fromState: {
              types: [
                "string"
              ]
            },
            toState: {
              types: [
                "string"
              ]
            },
            cardWidth: {
              types: [
                "number"
              ]
            },
            cardLabel: {
              types: [
                "string"
              ],
              enumValues: [
                "screen"
              ]
            },
            enteredBy: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            patterns: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  pattern: {
                    types: [
                      "sexpr"
                    ]
                  }
                },
                required: [
                  "slot",
                  "pattern"
                ]
              }
            },
            eventSources: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  positionHint: {
                    types: [
                      "number"
                    ]
                  },
                  payloadFields: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        required: {
                          types: [
                            "boolean"
                          ]
                        }
                      },
                      required: [
                        "name",
                        "type"
                      ]
                    }
                  }
                },
                required: [
                  "event",
                  "patternType",
                  "path",
                  "positionHint"
                ]
              }
            },
            layer: {
              types: [
                "string"
              ]
            },
            stateRole: {
              types: [
                "string"
              ],
              enumValues: [
                "initial",
                "terminal",
                "hub",
                "default"
              ]
            },
            effectTypes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            guard: {
              types: [
                "sexpr"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "string"
              ]
            },
            fieldCount: {
              types: [
                "number"
              ]
            },
            traitCount: {
              types: [
                "number"
              ]
            },
            pageRoutes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            status: {
              types: [
                "string"
              ],
              enumValues: [
                "idle",
                "running",
                "success",
                "error"
              ]
            },
            sourceSchemaName: {
              types: [
                "string"
              ]
            },
            focused: {
              types: [
                "boolean"
              ]
            },
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "trait-card",
                "system-orbital",
                "system-band",
                "dependency",
                "dependency-column"
              ]
            },
            dependencyColumn: {
              types: [
                "string"
              ],
              enumValues: [
                "app",
                "own",
                "io",
                "std",
                "primitive",
                "unknown",
                "client",
                "host",
                "service",
                "store",
                "external"
              ]
            },
            dependencyRole: {
              types: [
                "string"
              ],
              enumValues: [
                "selected",
                "upstream",
                "downstream",
                "both",
                "dim"
              ]
            },
            flowTraits: {
              types: [
                "number"
              ]
            },
            flowRenderPieces: {
              types: [
                "number"
              ]
            },
            unitStatus: {
              types: [
                "string"
              ],
              enumValues: [
                "ok",
                "degraded",
                "down",
                "idle"
              ]
            },
            unitMetric: {
              types: [
                "string"
              ]
            },
            bandKind: {
              types: [
                "string"
              ],
              enumValues: [
                "connected",
                "standalone"
              ]
            },
            bandCount: {
              types: [
                "number"
              ]
            },
            wireEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  fromState: {
                    types: [
                      "string"
                    ]
                  },
                  toState: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "event",
                  "fromState",
                  "toState"
                ]
              }
            },
            emits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listens: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            _fullSchema: {
              types: [
                "sexpr"
              ]
            },
            _mockData: {
              types: [
                "object"
              ],
              nonAuthorable: true
            }
          },
          propertyRequired: [
            "orbitalName",
            "patterns",
            "eventSources"
          ]
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "Enable editing in the inspector. When true, fields become inputs."
        },
        onSchemaChange: {
          types: [
            "function"
          ],
          description: "Called when the user edits the schema via the inspector.",
          kind: "callback",
          callbackArgs: [
            {
              name: "schema",
              type: "sexpr"
            }
          ]
        },
        onPatternDelete: {
          types: [
            "function"
          ],
          description: "Called when the user presses Delete/Backspace with patterns selected (all of a multi-select).",
          kind: "callback",
          callbackArgs: [
            {
              name: "context",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  patternIds: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  nodeData: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      },
                      stateName: {
                        types: [
                          "string"
                        ]
                      },
                      transitionEvent: {
                        types: [
                          "string"
                        ]
                      },
                      fromState: {
                        types: [
                          "string"
                        ]
                      },
                      toState: {
                        types: [
                          "string"
                        ]
                      },
                      cardWidth: {
                        types: [
                          "number"
                        ]
                      },
                      cardLabel: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "screen"
                        ]
                      },
                      enteredBy: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      patterns: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            slot: {
                              types: [
                                "string"
                              ]
                            },
                            pattern: {
                              types: [
                                "sexpr"
                              ]
                            }
                          },
                          required: [
                            "slot",
                            "pattern"
                          ]
                        }
                      },
                      eventSources: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            patternType: {
                              types: [
                                "string"
                              ]
                            },
                            label: {
                              types: [
                                "string"
                              ]
                            },
                            path: {
                              types: [
                                "string"
                              ]
                            },
                            positionHint: {
                              types: [
                                "number"
                              ]
                            },
                            payloadFields: {
                              types: [
                                "array"
                              ],
                              items: {
                                types: [
                                  "object"
                                ],
                                properties: {
                                  name: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  type: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  required: {
                                    types: [
                                      "boolean"
                                    ]
                                  }
                                },
                                required: [
                                  "name",
                                  "type"
                                ]
                              }
                            }
                          },
                          required: [
                            "event",
                            "patternType",
                            "path",
                            "positionHint"
                          ]
                        }
                      },
                      layer: {
                        types: [
                          "string"
                        ]
                      },
                      stateRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "initial",
                          "terminal",
                          "hub",
                          "default"
                        ]
                      },
                      effectTypes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      guard: {
                        types: [
                          "sexpr"
                        ]
                      },
                      entityName: {
                        types: [
                          "string"
                        ]
                      },
                      persistence: {
                        types: [
                          "string"
                        ]
                      },
                      fieldCount: {
                        types: [
                          "number"
                        ]
                      },
                      traitCount: {
                        types: [
                          "number"
                        ]
                      },
                      pageRoutes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      status: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "idle",
                          "running",
                          "success",
                          "error"
                        ]
                      },
                      sourceSchemaName: {
                        types: [
                          "string"
                        ]
                      },
                      focused: {
                        types: [
                          "boolean"
                        ]
                      },
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "trait-card",
                          "system-orbital",
                          "system-band",
                          "dependency",
                          "dependency-column"
                        ]
                      },
                      dependencyColumn: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "app",
                          "own",
                          "io",
                          "std",
                          "primitive",
                          "unknown",
                          "client",
                          "host",
                          "service",
                          "store",
                          "external"
                        ]
                      },
                      dependencyRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "selected",
                          "upstream",
                          "downstream",
                          "both",
                          "dim"
                        ]
                      },
                      flowTraits: {
                        types: [
                          "number"
                        ]
                      },
                      flowRenderPieces: {
                        types: [
                          "number"
                        ]
                      },
                      unitStatus: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "ok",
                          "degraded",
                          "down",
                          "idle"
                        ]
                      },
                      unitMetric: {
                        types: [
                          "string"
                        ]
                      },
                      bandKind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "connected",
                          "standalone"
                        ]
                      },
                      bandCount: {
                        types: [
                          "number"
                        ]
                      },
                      wireEvents: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            fromState: {
                              types: [
                                "string"
                              ]
                            },
                            toState: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "event",
                            "fromState",
                            "toState"
                          ]
                        }
                      },
                      emits: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      listens: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      linkedEntity: {
                        types: [
                          "string"
                        ]
                      },
                      _fullSchema: {
                        types: [
                          "sexpr"
                        ]
                      },
                      _mockData: {
                        types: [
                          "object"
                        ],
                        nonAuthorable: true
                      }
                    },
                    required: [
                      "orbitalName",
                      "patterns",
                      "eventSources"
                    ]
                  },
                  elements: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        level: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "node",
                            "slot",
                            "field",
                            "effect",
                            "trait",
                            "page",
                            "orbital"
                          ]
                        },
                        orbital: {
                          types: [
                            "string"
                          ]
                        },
                        trait: {
                          types: [
                            "string"
                          ]
                        },
                        transition: {
                          types: [
                            "string"
                          ]
                        },
                        state: {
                          types: [
                            "string"
                          ]
                        },
                        slot: {
                          types: [
                            "string"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        patternType: {
                          types: [
                            "string"
                          ]
                        },
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        source: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "level",
                        "orbital",
                        "label"
                      ]
                    }
                  }
                },
                required: [
                  "patternIds",
                  "nodeData"
                ]
              }
            }
          ]
        },
        tools: {
          types: [
            "array"
          ],
          description: "Editing tools the canvas turns on (a persona's shell manifest declares them); all by default.",
          items: {
            types: [
              "string"
            ],
            enumValues: [
              "multiSelect",
              "clipboard",
              "inlineText",
              "zoomToSelection",
              "designHandles",
              "autoLayout",
              "sizeLimits",
              "absolute",
              "guides",
              "measure"
            ]
          }
        },
        elementAccess: {
          types: [
            "function"
          ],
          description: "What each element lets the user change (knob, data-bound, fixed by a behavior); every prop is editable without it.",
          callbackArgs: [
            {
              name: "focus",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "node",
                      "slot",
                      "field",
                      "effect",
                      "trait",
                      "page",
                      "orbital"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  },
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  source: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital",
                  "label"
                ]
              }
            }
          ],
          renderCallback: true
        },
        onEventWire: {
          types: [
            "function"
          ],
          description: "Called when the user drags from a source handle to a target handle (event wiring).",
          kind: "callback",
          callbackArgs: [
            {
              name: "wire",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  eventName: {
                    types: [
                      "string"
                    ]
                  },
                  sourceOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  targetOrbital: {
                    types: [
                      "string"
                    ]
                  },
                  sourceTraitName: {
                    types: [
                      "string"
                    ]
                  },
                  targetTraitName: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "eventName",
                  "sourceOrbital",
                  "targetOrbital"
                ]
              }
            }
          ]
        },
        behaviorMeta: {
          types: [
            "object"
          ],
          description: "Behavior layer metadata for node styling (layer color bands).",
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              layer: {
                types: [
                  "string"
                ]
              }
            },
            required: [
              "layer"
            ]
          }
        },
        orbitalStatus: {
          types: [
            "object"
          ],
          description: "Per-orbital generation status. Keyed by orbital name. Drives the spinner + accent border treatment on the overview node and (future) the hover trace surface. Defaults to `'idle'` for missing entries. Consumers derive this from agent subagent_start/complete SSE events.",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        onOrbitalHover: {
          types: [
            "function"
          ],
          description: "Hover handler for overview-level orbital nodes. Fires with the orbital name on enter and `null` on leave. Reserved for the upcoming trace tooltip: consumers will use it to anchor a popover that shows the subagent's live trace + reasoning for the hovered orbital.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "orbitalName",
              type: "string"
            }
          ]
        },
        layoutHint: {
          types: [
            "string"
          ],
          description: "Layout hint: 'pipeline' renders nodes left-to-right, 'grid' (default) uses sqrt-based grid.",
          enumValues: [
            "pipeline",
            "grid"
          ]
        },
        onNodeSelect: {
          types: [
            "function"
          ],
          description: "Called when the user clicks a node in overview level (for composition hints).",
          kind: "callback",
          callbackArgs: [
            {
              name: "orbitalName",
              type: "string"
            }
          ]
        },
        onZoomChange: {
          types: [
            "function"
          ],
          description: "onZoomChange prop",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "level",
              type: "string"
            },
            {
              name: "context",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "string"
                  ]
                }
              }
            }
          ]
        },
        focusTarget: {
          types: [
            "object"
          ],
          description: "focusTarget prop",
          properties: {
            type: {
              types: [
                "string"
              ]
            },
            name: {
              types: [
                "string"
              ]
            }
          },
          propertyRequired: [
            "type",
            "name"
          ]
        },
        color: {
          types: [
            "string"
          ],
          description: "color prop"
        },
        animated: {
          types: [
            "boolean"
          ],
          description: "animated prop"
        },
        initialTrait: {
          types: [
            "string"
          ],
          description: "initialTrait prop"
        },
        stateCoverage: {
          types: [
            "object"
          ],
          description: "stateCoverage prop",
          mapValue: {
            types: [
              "string"
            ]
          }
        },
        userType: {
          types: [
            "string"
          ],
          description: "Studio persona viewing the canvas. Drives `OrbInspector` tab/section visibility \u2014 designers and builders hide the raw `code` tab and the architect-only Entity / raw-guard / raw-effects sections; architects see everything. Default `'builder'` preserves pre-Phase-2 behavior.",
          enumValues: [
            "builder",
            "designer",
            "architect"
          ],
          default: "builder"
        },
        themeManifest: {
          types: [
            "object"
          ],
          description: "Project theme tokens (Design System tab only). Forwarded to `OrbInspector` so the Styles tab can render an editable token list when the selection originates from the synthesized `__design_system__` schema."
        },
        nodePositions: {
          types: [
            "object"
          ],
          description: "Persisted node positions keyed by node id. When the node set changes (level/schema switch), any id present here overrides the computed layout position, restoring a user's manual drag arrangement. Node ids are unique per view level, so a single flat map covers overview + expanded. A saved `width` restores a card frame the designer resized.",
          decoder: {
            name: "decodeNodePlacements",
            from: "@almadar/ui/avl"
          },
          mapValue: {
            types: [
              "object"
            ],
            properties: {
              x: {
                types: [
                  "number"
                ]
              },
              y: {
                types: [
                  "number"
                ]
              },
              width: {
                types: [
                  "number"
                ]
              }
            },
            required: [
              "x",
              "y"
            ]
          }
        },
        onPositionsChange: {
          types: [
            "function"
          ],
          description: "Fired on node drag-stop and card resize with the full {id \u2192 placement} map of the current node set, so the consumer can persist the arrangement.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "positions",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                mapValue: {
                  types: [
                    "object"
                  ],
                  properties: {
                    x: {
                      types: [
                        "number"
                      ]
                    },
                    y: {
                      types: [
                        "number"
                      ]
                    },
                    width: {
                      types: [
                        "number"
                      ]
                    }
                  },
                  required: [
                    "x",
                    "y"
                  ]
                }
              }
            }
          ]
        },
        onSelectedNodeChange: {
          types: [
            "function"
          ],
          description: "Fired whenever the internal `selectedNode` changes \u2014 select, clear-on- escape, clear-on-level-change, and pattern-selection sync all route through this. Lets a consumer mirror selection into a persistent properties panel rendered outside the canvas (see `externalInspector`).",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "node",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  orbitalName: {
                    types: [
                      "string"
                    ]
                  },
                  traitName: {
                    types: [
                      "string"
                    ]
                  },
                  stateName: {
                    types: [
                      "string"
                    ]
                  },
                  transitionEvent: {
                    types: [
                      "string"
                    ]
                  },
                  fromState: {
                    types: [
                      "string"
                    ]
                  },
                  toState: {
                    types: [
                      "string"
                    ]
                  },
                  cardWidth: {
                    types: [
                      "number"
                    ]
                  },
                  cardLabel: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "screen"
                    ]
                  },
                  enteredBy: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  patterns: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        slot: {
                          types: [
                            "string"
                          ]
                        },
                        pattern: {
                          types: [
                            "sexpr"
                          ]
                        }
                      },
                      required: [
                        "slot",
                        "pattern"
                      ]
                    }
                  },
                  eventSources: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        patternType: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        positionHint: {
                          types: [
                            "number"
                          ]
                        },
                        payloadFields: {
                          types: [
                            "array"
                          ],
                          items: {
                            types: [
                              "object"
                            ],
                            properties: {
                              name: {
                                types: [
                                  "string"
                                ]
                              },
                              type: {
                                types: [
                                  "string"
                                ]
                              },
                              required: {
                                types: [
                                  "boolean"
                                ]
                              }
                            },
                            required: [
                              "name",
                              "type"
                            ]
                          }
                        }
                      },
                      required: [
                        "event",
                        "patternType",
                        "path",
                        "positionHint"
                      ]
                    }
                  },
                  layer: {
                    types: [
                      "string"
                    ]
                  },
                  stateRole: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "initial",
                      "terminal",
                      "hub",
                      "default"
                    ]
                  },
                  effectTypes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  guard: {
                    types: [
                      "sexpr"
                    ]
                  },
                  entityName: {
                    types: [
                      "string"
                    ]
                  },
                  persistence: {
                    types: [
                      "string"
                    ]
                  },
                  fieldCount: {
                    types: [
                      "number"
                    ]
                  },
                  traitCount: {
                    types: [
                      "number"
                    ]
                  },
                  pageRoutes: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  status: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "idle",
                      "running",
                      "success",
                      "error"
                    ]
                  },
                  sourceSchemaName: {
                    types: [
                      "string"
                    ]
                  },
                  focused: {
                    types: [
                      "boolean"
                    ]
                  },
                  kind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "trait-card",
                      "system-orbital",
                      "system-band",
                      "dependency",
                      "dependency-column"
                    ]
                  },
                  dependencyColumn: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "app",
                      "own",
                      "io",
                      "std",
                      "primitive",
                      "unknown",
                      "client",
                      "host",
                      "service",
                      "store",
                      "external"
                    ]
                  },
                  dependencyRole: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "selected",
                      "upstream",
                      "downstream",
                      "both",
                      "dim"
                    ]
                  },
                  flowTraits: {
                    types: [
                      "number"
                    ]
                  },
                  flowRenderPieces: {
                    types: [
                      "number"
                    ]
                  },
                  unitStatus: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "ok",
                      "degraded",
                      "down",
                      "idle"
                    ]
                  },
                  unitMetric: {
                    types: [
                      "string"
                    ]
                  },
                  bandKind: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "connected",
                      "standalone"
                    ]
                  },
                  bandCount: {
                    types: [
                      "number"
                    ]
                  },
                  wireEvents: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  transitions: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        event: {
                          types: [
                            "string"
                          ]
                        },
                        fromState: {
                          types: [
                            "string"
                          ]
                        },
                        toState: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "event",
                        "fromState",
                        "toState"
                      ]
                    }
                  },
                  emits: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  listens: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  linkedEntity: {
                    types: [
                      "string"
                    ]
                  },
                  _fullSchema: {
                    types: [
                      "sexpr"
                    ]
                  },
                  _mockData: {
                    types: [
                      "object"
                    ],
                    nonAuthorable: true
                  }
                },
                required: [
                  "orbitalName",
                  "patterns",
                  "eventSources"
                ]
              }
            }
          ]
        },
        onSelectedPatternChange: {
          types: [
            "function"
          ],
          description: "Fired whenever the in-node pattern selection changes (a pattern click inside `OrbPreviewNode`, cleared on background click / Escape / pattern delete). `PatternSelectionContext` is internal to this component's own render tree, so an externally-rendered `OrbInspector` (see `externalInspector`) has no other way to learn which pattern the user picked \u2014 pass this straight through to `OrbInspector`'s `selectedPattern` prop to keep Design-tab prop editing working outside the canvas.",
          kind: "callback",
          notification: true,
          callbackArgs: [
            {
              name: "pattern",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  patternId: {
                    types: [
                      "string"
                    ]
                  },
                  nodeData: {
                    types: [
                      "object"
                    ],
                    properties: {
                      orbitalName: {
                        types: [
                          "string"
                        ]
                      },
                      traitName: {
                        types: [
                          "string"
                        ]
                      },
                      stateName: {
                        types: [
                          "string"
                        ]
                      },
                      transitionEvent: {
                        types: [
                          "string"
                        ]
                      },
                      fromState: {
                        types: [
                          "string"
                        ]
                      },
                      toState: {
                        types: [
                          "string"
                        ]
                      },
                      cardWidth: {
                        types: [
                          "number"
                        ]
                      },
                      cardLabel: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "screen"
                        ]
                      },
                      enteredBy: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      patterns: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            slot: {
                              types: [
                                "string"
                              ]
                            },
                            pattern: {
                              types: [
                                "sexpr"
                              ]
                            }
                          },
                          required: [
                            "slot",
                            "pattern"
                          ]
                        }
                      },
                      eventSources: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            patternType: {
                              types: [
                                "string"
                              ]
                            },
                            label: {
                              types: [
                                "string"
                              ]
                            },
                            path: {
                              types: [
                                "string"
                              ]
                            },
                            positionHint: {
                              types: [
                                "number"
                              ]
                            },
                            payloadFields: {
                              types: [
                                "array"
                              ],
                              items: {
                                types: [
                                  "object"
                                ],
                                properties: {
                                  name: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  type: {
                                    types: [
                                      "string"
                                    ]
                                  },
                                  required: {
                                    types: [
                                      "boolean"
                                    ]
                                  }
                                },
                                required: [
                                  "name",
                                  "type"
                                ]
                              }
                            }
                          },
                          required: [
                            "event",
                            "patternType",
                            "path",
                            "positionHint"
                          ]
                        }
                      },
                      layer: {
                        types: [
                          "string"
                        ]
                      },
                      stateRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "initial",
                          "terminal",
                          "hub",
                          "default"
                        ]
                      },
                      effectTypes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      guard: {
                        types: [
                          "sexpr"
                        ]
                      },
                      entityName: {
                        types: [
                          "string"
                        ]
                      },
                      persistence: {
                        types: [
                          "string"
                        ]
                      },
                      fieldCount: {
                        types: [
                          "number"
                        ]
                      },
                      traitCount: {
                        types: [
                          "number"
                        ]
                      },
                      pageRoutes: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      status: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "idle",
                          "running",
                          "success",
                          "error"
                        ]
                      },
                      sourceSchemaName: {
                        types: [
                          "string"
                        ]
                      },
                      focused: {
                        types: [
                          "boolean"
                        ]
                      },
                      kind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "trait-card",
                          "system-orbital",
                          "system-band",
                          "dependency",
                          "dependency-column"
                        ]
                      },
                      dependencyColumn: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "app",
                          "own",
                          "io",
                          "std",
                          "primitive",
                          "unknown",
                          "client",
                          "host",
                          "service",
                          "store",
                          "external"
                        ]
                      },
                      dependencyRole: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "selected",
                          "upstream",
                          "downstream",
                          "both",
                          "dim"
                        ]
                      },
                      flowTraits: {
                        types: [
                          "number"
                        ]
                      },
                      flowRenderPieces: {
                        types: [
                          "number"
                        ]
                      },
                      unitStatus: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "ok",
                          "degraded",
                          "down",
                          "idle"
                        ]
                      },
                      unitMetric: {
                        types: [
                          "string"
                        ]
                      },
                      bandKind: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "connected",
                          "standalone"
                        ]
                      },
                      bandCount: {
                        types: [
                          "number"
                        ]
                      },
                      wireEvents: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      transitions: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            event: {
                              types: [
                                "string"
                              ]
                            },
                            fromState: {
                              types: [
                                "string"
                              ]
                            },
                            toState: {
                              types: [
                                "string"
                              ]
                            }
                          },
                          required: [
                            "event",
                            "fromState",
                            "toState"
                          ]
                        }
                      },
                      emits: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      listens: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      linkedEntity: {
                        types: [
                          "string"
                        ]
                      },
                      _fullSchema: {
                        types: [
                          "sexpr"
                        ]
                      },
                      _mockData: {
                        types: [
                          "object"
                        ],
                        nonAuthorable: true
                      }
                    },
                    required: [
                      "orbitalName",
                      "patterns",
                      "eventSources"
                    ]
                  },
                  sourceTrait: {
                    types: [
                      "string"
                    ]
                  },
                  rect: {
                    types: [
                      "object"
                    ],
                    properties: {
                      top: {
                        types: [
                          "number"
                        ]
                      },
                      left: {
                        types: [
                          "number"
                        ]
                      },
                      width: {
                        types: [
                          "number"
                        ]
                      },
                      height: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "top",
                      "left",
                      "width",
                      "height"
                    ]
                  },
                  selection: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "string"
                      ]
                    }
                  },
                  offsetInParent: {
                    types: [
                      "object"
                    ],
                    properties: {
                      left: {
                        types: [
                          "number"
                        ]
                      },
                      top: {
                        types: [
                          "number"
                        ]
                      },
                      right: {
                        types: [
                          "number"
                        ]
                      },
                      bottom: {
                        types: [
                          "number"
                        ]
                      },
                      parentWidth: {
                        types: [
                          "number"
                        ]
                      },
                      parentHeight: {
                        types: [
                          "number"
                        ]
                      }
                    },
                    required: [
                      "left",
                      "top",
                      "right",
                      "bottom",
                      "parentWidth",
                      "parentHeight"
                    ]
                  },
                  focus: {
                    types: [
                      "object"
                    ],
                    properties: {
                      level: {
                        types: [
                          "string"
                        ],
                        enumValues: [
                          "node",
                          "slot",
                          "field",
                          "effect",
                          "trait",
                          "page",
                          "orbital"
                        ]
                      },
                      orbital: {
                        types: [
                          "string"
                        ]
                      },
                      trait: {
                        types: [
                          "string"
                        ]
                      },
                      transition: {
                        types: [
                          "string"
                        ]
                      },
                      state: {
                        types: [
                          "string"
                        ]
                      },
                      slot: {
                        types: [
                          "string"
                        ]
                      },
                      path: {
                        types: [
                          "string"
                        ]
                      },
                      patternType: {
                        types: [
                          "string"
                        ]
                      },
                      entity: {
                        types: [
                          "string"
                        ]
                      },
                      source: {
                        types: [
                          "string"
                        ]
                      },
                      label: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "level",
                      "orbital",
                      "label"
                    ]
                  },
                  elements: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        level: {
                          types: [
                            "string"
                          ],
                          enumValues: [
                            "node",
                            "slot",
                            "field",
                            "effect",
                            "trait",
                            "page",
                            "orbital"
                          ]
                        },
                        orbital: {
                          types: [
                            "string"
                          ]
                        },
                        trait: {
                          types: [
                            "string"
                          ]
                        },
                        transition: {
                          types: [
                            "string"
                          ]
                        },
                        state: {
                          types: [
                            "string"
                          ]
                        },
                        slot: {
                          types: [
                            "string"
                          ]
                        },
                        path: {
                          types: [
                            "string"
                          ]
                        },
                        patternType: {
                          types: [
                            "string"
                          ]
                        },
                        entity: {
                          types: [
                            "string"
                          ]
                        },
                        source: {
                          types: [
                            "string"
                          ]
                        },
                        label: {
                          types: [
                            "string"
                          ]
                        }
                      },
                      required: [
                        "level",
                        "orbital",
                        "label"
                      ]
                    }
                  }
                },
                required: [
                  "patternType",
                  "nodeData"
                ]
              }
            }
          ]
        },
        externalInspector: {
          types: [
            "boolean"
          ],
          description: "When true, FlowCanvas does not render its inline `OrbInspector` \u2014 the consumer is expected to render one externally, fed by `onSelectedNodeChange` + `onSelectedPatternChange`. Selection highlighting, keyboard delete, and every other behavior is unchanged. Default `false` preserves the built-in inline panel.",
          default: false
        },
        verify: {
          types: [
            "object"
          ],
          description: "A host that verifies from the canvas: cards show verify controls and the trait's verdict, and follow `UI:CANVAS_SHOW_STATE` to the states a run reaches.",
          properties: {
            verdictOf: {
              types: [
                "function"
              ]
            },
            playing: {
              types: [
                "function"
              ]
            }
          },
          propertyRequired: [
            "verdictOf",
            "playing"
          ]
        },
        enter: {
          types: [
            "string"
          ],
          description: "How this element animates in when it mounts (theme-styled); overrides the theme's default slot entry. `none` opts out.",
          enumValues: [
            "none",
            "fade",
            "rise",
            "scale",
            "slide"
          ],
          kind: "entry"
        },
        enterDelay: {
          types: [
            "number"
          ],
          description: "Stagger step for the entry: waits this many `--motion-enter-stagger` steps before animating.",
          numericEnumValues: [
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ],
          kind: "entry"
        },
        skeleton: {
          types: [
            "string",
            "object"
          ],
          description: "Skeleton shown while this element's server render is in flight: a shape (`table`, `list`, \u2026), `{ variant, rows?, columns?, fields? }`, or `none`.",
          enumValues: [
            "header",
            "table",
            "list",
            "grid",
            "detail",
            "stats",
            "form",
            "card",
            "text",
            "none"
          ],
          properties: {
            variant: {
              types: [
                "string"
              ],
              enumValues: [
                "header",
                "table",
                "list",
                "grid",
                "detail",
                "stats",
                "form",
                "card",
                "text"
              ]
            },
            rows: {
              types: [
                "number"
              ]
            },
            columns: {
              types: [
                "number"
              ]
            },
            fields: {
              types: [
                "number"
              ]
            }
          },
          propertyRequired: [
            "variant"
          ],
          kind: "skeleton",
          tier: "presentation"
        }
      }
    },
    "layers-panel": {
      type: "layers-panel",
      category: "display",
      sourceFile: "components/avl/organisms/LayersPanel.tsx",
      tier: "organisms",
      family: "avl",
      description: "LayersPanel component",
      suggestedFor: [
        "layers",
        "panel",
        "layers panel"
      ],
      typicalSize: "large",
      propsSchema: {
        schema: {
          types: [
            "sexpr"
          ],
          description: "schema prop",
          required: true,
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        selectedId: {
          types: [
            "string"
          ],
          description: "Currently selected node id (any level of the ID scheme above)."
        },
        onSelect: {
          types: [
            "function"
          ],
          description: "onSelect prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            }
          ]
        },
        onReorder: {
          types: [
            "function"
          ],
          description: "Reorder a node within the tree. Threaded to FileTree's `onNodeReorder` seam \u2014 presence-gated there, so omitting it just means no dragging.",
          kind: "callback",
          callbackArgs: [
            {
              name: "id",
              type: "string"
            },
            {
              name: "newParentId",
              type: "string"
            },
            {
              name: "index",
              type: "number"
            }
          ]
        }
      }
    },
    "orb-inspector": {
      type: "orb-inspector",
      category: "debug",
      sourceFile: "components/avl/organisms/OrbInspector.tsx",
      tier: "organisms",
      family: "avl",
      description: "OrbInspector component",
      suggestedFor: [
        "orb",
        "inspector",
        "orb inspector"
      ],
      typicalSize: "large",
      propsSchema: {
        node: {
          types: [
            "object"
          ],
          description: "node prop",
          required: true,
          decoder: {
            name: "decodePreviewNodeData",
            from: "@almadar/ui/avl"
          },
          properties: {
            orbitalName: {
              types: [
                "string"
              ]
            },
            traitName: {
              types: [
                "string"
              ]
            },
            stateName: {
              types: [
                "string"
              ]
            },
            transitionEvent: {
              types: [
                "string"
              ]
            },
            fromState: {
              types: [
                "string"
              ]
            },
            toState: {
              types: [
                "string"
              ]
            },
            cardWidth: {
              types: [
                "number"
              ]
            },
            cardLabel: {
              types: [
                "string"
              ],
              enumValues: [
                "screen"
              ]
            },
            enteredBy: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            patterns: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  pattern: {
                    types: [
                      "sexpr"
                    ]
                  }
                },
                required: [
                  "slot",
                  "pattern"
                ]
              }
            },
            eventSources: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  positionHint: {
                    types: [
                      "number"
                    ]
                  },
                  payloadFields: {
                    types: [
                      "array"
                    ],
                    items: {
                      types: [
                        "object"
                      ],
                      properties: {
                        name: {
                          types: [
                            "string"
                          ]
                        },
                        type: {
                          types: [
                            "string"
                          ]
                        },
                        required: {
                          types: [
                            "boolean"
                          ]
                        }
                      },
                      required: [
                        "name",
                        "type"
                      ]
                    }
                  }
                },
                required: [
                  "event",
                  "patternType",
                  "path",
                  "positionHint"
                ]
              }
            },
            layer: {
              types: [
                "string"
              ]
            },
            stateRole: {
              types: [
                "string"
              ],
              enumValues: [
                "initial",
                "terminal",
                "hub",
                "default"
              ]
            },
            effectTypes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            guard: {
              types: [
                "sexpr"
              ]
            },
            entityName: {
              types: [
                "string"
              ]
            },
            persistence: {
              types: [
                "string"
              ]
            },
            fieldCount: {
              types: [
                "number"
              ]
            },
            traitCount: {
              types: [
                "number"
              ]
            },
            pageRoutes: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            status: {
              types: [
                "string"
              ],
              enumValues: [
                "idle",
                "running",
                "success",
                "error"
              ]
            },
            sourceSchemaName: {
              types: [
                "string"
              ]
            },
            focused: {
              types: [
                "boolean"
              ]
            },
            kind: {
              types: [
                "string"
              ],
              enumValues: [
                "trait-card",
                "system-orbital",
                "system-band",
                "dependency",
                "dependency-column"
              ]
            },
            dependencyColumn: {
              types: [
                "string"
              ],
              enumValues: [
                "app",
                "own",
                "io",
                "std",
                "primitive",
                "unknown",
                "client",
                "host",
                "service",
                "store",
                "external"
              ]
            },
            dependencyRole: {
              types: [
                "string"
              ],
              enumValues: [
                "selected",
                "upstream",
                "downstream",
                "both",
                "dim"
              ]
            },
            flowTraits: {
              types: [
                "number"
              ]
            },
            flowRenderPieces: {
              types: [
                "number"
              ]
            },
            unitStatus: {
              types: [
                "string"
              ],
              enumValues: [
                "ok",
                "degraded",
                "down",
                "idle"
              ]
            },
            unitMetric: {
              types: [
                "string"
              ]
            },
            bandKind: {
              types: [
                "string"
              ],
              enumValues: [
                "connected",
                "standalone"
              ]
            },
            bandCount: {
              types: [
                "number"
              ]
            },
            wireEvents: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            transitions: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  event: {
                    types: [
                      "string"
                    ]
                  },
                  fromState: {
                    types: [
                      "string"
                    ]
                  },
                  toState: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "event",
                  "fromState",
                  "toState"
                ]
              }
            },
            emits: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            listens: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            linkedEntity: {
              types: [
                "string"
              ]
            },
            _fullSchema: {
              types: [
                "sexpr"
              ]
            },
            _mockData: {
              types: [
                "object"
              ],
              nonAuthorable: true
            }
          },
          propertyRequired: [
            "orbitalName",
            "patterns",
            "eventSources"
          ]
        },
        schema: {
          types: [
            "sexpr"
          ],
          description: "schema prop",
          required: true,
          decoder: {
            name: "parseOrbitalSchema",
            from: "@almadar/core"
          }
        },
        editable: {
          types: [
            "boolean"
          ],
          description: "editable prop",
          default: false
        },
        userType: {
          types: [
            "string"
          ],
          description: "Studio persona viewing the inspector. Controls tab visibility (Code is architect-only, Styles is universal) and section visibility (Entity, raw guard, raw effects are architect-only). Default `'builder'` matches pre-Phase-2 behavior except for the new Styles tab, which every persona gets.",
          enumValues: [
            "builder",
            "designer",
            "architect"
          ],
          default: "builder"
        },
        themeManifest: {
          types: [
            "object"
          ],
          description: "Project theme tokens. The Styles tab reads them for Global edits (and, on the synthesized `__design_system__` schema, lists them all); those edits emit `UI:PROP_CHANGE` with `scope: 'global'` + `tokenGroup`/`tokenKey`."
        },
        defaultTab: {
          types: [
            "string"
          ],
          description: "Initial tab on mount. Local state still owns the active tab after that \u2014 this does not make the component controlled. Enables deep-linking into a specific tab (e.g. from a LayersPanel selection).",
          enumValues: [
            "inspector",
            "design",
            "prototype",
            "code"
          ]
        },
        onTabChange: {
          types: [
            "function"
          ],
          description: "Fires whenever the tab-bar selection changes.",
          kind: "callback",
          callbackArgs: [
            {
              name: "tab",
              type: "string"
            }
          ]
        },
        onSchemaChange: {
          types: [
            "function"
          ],
          description: "onSchemaChange prop",
          kind: "callback",
          callbackArgs: [
            {
              name: "schema",
              type: "sexpr"
            }
          ]
        },
        onClose: {
          types: [
            "function"
          ],
          description: "onClose prop",
          required: true,
          kind: "callback",
          callbackArgs: []
        },
        selectedPattern: {
          types: [
            "object"
          ],
          description: "In-node pattern selection, when this inspector is rendered OUTSIDE `FlowCanvas` (see `FlowCanvas`'s `externalInspector` + `onSelectedPatternChange`). `PatternSelectionContext` only reaches descendants of `FlowCanvas`'s own render tree, so a standalone consumer has no other way to feed pattern-level selection in \u2014 pass `FlowCanvas`'s `onSelectedPatternChange` payload straight through here. When omitted (the inline, built-in-inspector usage), falls back to `PatternSelectionContext`, unchanged.",
          decoder: {
            name: "decodeSelectedPattern",
            from: "@almadar/ui/avl"
          },
          properties: {
            patternType: {
              types: [
                "string"
              ]
            },
            patternId: {
              types: [
                "string"
              ]
            },
            nodeData: {
              types: [
                "object"
              ],
              properties: {
                orbitalName: {
                  types: [
                    "string"
                  ]
                },
                traitName: {
                  types: [
                    "string"
                  ]
                },
                stateName: {
                  types: [
                    "string"
                  ]
                },
                transitionEvent: {
                  types: [
                    "string"
                  ]
                },
                fromState: {
                  types: [
                    "string"
                  ]
                },
                toState: {
                  types: [
                    "string"
                  ]
                },
                cardWidth: {
                  types: [
                    "number"
                  ]
                },
                cardLabel: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "screen"
                  ]
                },
                enteredBy: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                patterns: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      slot: {
                        types: [
                          "string"
                        ]
                      },
                      pattern: {
                        types: [
                          "sexpr"
                        ]
                      }
                    },
                    required: [
                      "slot",
                      "pattern"
                    ]
                  }
                },
                eventSources: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      patternType: {
                        types: [
                          "string"
                        ]
                      },
                      label: {
                        types: [
                          "string"
                        ]
                      },
                      path: {
                        types: [
                          "string"
                        ]
                      },
                      positionHint: {
                        types: [
                          "number"
                        ]
                      },
                      payloadFields: {
                        types: [
                          "array"
                        ],
                        items: {
                          types: [
                            "object"
                          ],
                          properties: {
                            name: {
                              types: [
                                "string"
                              ]
                            },
                            type: {
                              types: [
                                "string"
                              ]
                            },
                            required: {
                              types: [
                                "boolean"
                              ]
                            }
                          },
                          required: [
                            "name",
                            "type"
                          ]
                        }
                      }
                    },
                    required: [
                      "event",
                      "patternType",
                      "path",
                      "positionHint"
                    ]
                  }
                },
                layer: {
                  types: [
                    "string"
                  ]
                },
                stateRole: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "initial",
                    "terminal",
                    "hub",
                    "default"
                  ]
                },
                effectTypes: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                guard: {
                  types: [
                    "sexpr"
                  ]
                },
                entityName: {
                  types: [
                    "string"
                  ]
                },
                persistence: {
                  types: [
                    "string"
                  ]
                },
                fieldCount: {
                  types: [
                    "number"
                  ]
                },
                traitCount: {
                  types: [
                    "number"
                  ]
                },
                pageRoutes: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                status: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "idle",
                    "running",
                    "success",
                    "error"
                  ]
                },
                sourceSchemaName: {
                  types: [
                    "string"
                  ]
                },
                focused: {
                  types: [
                    "boolean"
                  ]
                },
                kind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "trait-card",
                    "system-orbital",
                    "system-band",
                    "dependency",
                    "dependency-column"
                  ]
                },
                dependencyColumn: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "app",
                    "own",
                    "io",
                    "std",
                    "primitive",
                    "unknown",
                    "client",
                    "host",
                    "service",
                    "store",
                    "external"
                  ]
                },
                dependencyRole: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "selected",
                    "upstream",
                    "downstream",
                    "both",
                    "dim"
                  ]
                },
                flowTraits: {
                  types: [
                    "number"
                  ]
                },
                flowRenderPieces: {
                  types: [
                    "number"
                  ]
                },
                unitStatus: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "ok",
                    "degraded",
                    "down",
                    "idle"
                  ]
                },
                unitMetric: {
                  types: [
                    "string"
                  ]
                },
                bandKind: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "connected",
                    "standalone"
                  ]
                },
                bandCount: {
                  types: [
                    "number"
                  ]
                },
                wireEvents: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                transitions: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "object"
                    ],
                    properties: {
                      event: {
                        types: [
                          "string"
                        ]
                      },
                      fromState: {
                        types: [
                          "string"
                        ]
                      },
                      toState: {
                        types: [
                          "string"
                        ]
                      }
                    },
                    required: [
                      "event",
                      "fromState",
                      "toState"
                    ]
                  }
                },
                emits: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                listens: {
                  types: [
                    "array"
                  ],
                  items: {
                    types: [
                      "string"
                    ]
                  }
                },
                linkedEntity: {
                  types: [
                    "string"
                  ]
                },
                _fullSchema: {
                  types: [
                    "sexpr"
                  ]
                },
                _mockData: {
                  types: [
                    "object"
                  ],
                  nonAuthorable: true
                }
              },
              required: [
                "orbitalName",
                "patterns",
                "eventSources"
              ]
            },
            sourceTrait: {
              types: [
                "string"
              ]
            },
            rect: {
              types: [
                "object"
              ],
              properties: {
                top: {
                  types: [
                    "number"
                  ]
                },
                left: {
                  types: [
                    "number"
                  ]
                },
                width: {
                  types: [
                    "number"
                  ]
                },
                height: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "top",
                "left",
                "width",
                "height"
              ]
            },
            selection: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "string"
                ]
              }
            },
            offsetInParent: {
              types: [
                "object"
              ],
              properties: {
                left: {
                  types: [
                    "number"
                  ]
                },
                top: {
                  types: [
                    "number"
                  ]
                },
                right: {
                  types: [
                    "number"
                  ]
                },
                bottom: {
                  types: [
                    "number"
                  ]
                },
                parentWidth: {
                  types: [
                    "number"
                  ]
                },
                parentHeight: {
                  types: [
                    "number"
                  ]
                }
              },
              required: [
                "left",
                "top",
                "right",
                "bottom",
                "parentWidth",
                "parentHeight"
              ]
            },
            focus: {
              types: [
                "object"
              ],
              properties: {
                level: {
                  types: [
                    "string"
                  ],
                  enumValues: [
                    "node",
                    "slot",
                    "field",
                    "effect",
                    "trait",
                    "page",
                    "orbital"
                  ]
                },
                orbital: {
                  types: [
                    "string"
                  ]
                },
                trait: {
                  types: [
                    "string"
                  ]
                },
                transition: {
                  types: [
                    "string"
                  ]
                },
                state: {
                  types: [
                    "string"
                  ]
                },
                slot: {
                  types: [
                    "string"
                  ]
                },
                path: {
                  types: [
                    "string"
                  ]
                },
                patternType: {
                  types: [
                    "string"
                  ]
                },
                entity: {
                  types: [
                    "string"
                  ]
                },
                source: {
                  types: [
                    "string"
                  ]
                },
                label: {
                  types: [
                    "string"
                  ]
                }
              },
              required: [
                "level",
                "orbital",
                "label"
              ]
            },
            elements: {
              types: [
                "array"
              ],
              items: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "node",
                      "slot",
                      "field",
                      "effect",
                      "trait",
                      "page",
                      "orbital"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  },
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  source: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital",
                  "label"
                ]
              }
            }
          },
          propertyRequired: [
            "patternType",
            "nodeData"
          ]
        },
        elementAccess: {
          types: [
            "function"
          ],
          description: "What the selected element lets the user change; `ElementEditAccessContext` (inside a canvas) when omitted.",
          callbackArgs: [
            {
              name: "focus",
              type: "object",
              schema: {
                types: [
                  "object"
                ],
                properties: {
                  level: {
                    types: [
                      "string"
                    ],
                    enumValues: [
                      "node",
                      "slot",
                      "field",
                      "effect",
                      "trait",
                      "page",
                      "orbital"
                    ]
                  },
                  orbital: {
                    types: [
                      "string"
                    ]
                  },
                  trait: {
                    types: [
                      "string"
                    ]
                  },
                  transition: {
                    types: [
                      "string"
                    ]
                  },
                  state: {
                    types: [
                      "string"
                    ]
                  },
                  slot: {
                    types: [
                      "string"
                    ]
                  },
                  path: {
                    types: [
                      "string"
                    ]
                  },
                  patternType: {
                    types: [
                      "string"
                    ]
                  },
                  entity: {
                    types: [
                      "string"
                    ]
                  },
                  source: {
                    types: [
                      "string"
                    ]
                  },
                  label: {
                    types: [
                      "string"
                    ]
                  }
                },
                required: [
                  "level",
                  "orbital",
                  "label"
                ]
              }
            }
          ],
          renderCallback: true
        }
      }
    }
  },
  categories: [
    "display",
    "header",
    "form",
    "filter",
    "navigation",
    "state",
    "dashboard",
    "container",
    "layout",
    "game",
    "debug",
    "meta",
    "component",
    "template",
    "visualization",
    "media"
  ]
};

// src/patterns/integrators-registry.json
var integrators_registry_default = {
  version: "1.0.0",
  exportedAt: "2026-10-09T19:11:33.426Z",
  integrators: {
    github: {
      name: "github",
      description: "GitHub integration - Git operations and GitHub REST API",
      category: "devtools",
      actions: [
        {
          name: "cloneRepo",
          description: "Clone a repository via the git CLI into a local target directory, using the configured token for auth.",
          params: [
            {
              name: "repoUrl",
              type: "string",
              required: true,
              description: "HTTPS clone URL, e.g. https://github.com/owner/repo. Owner/repo are parsed from it when not preconfigured."
            },
            {
              name: "targetDir",
              type: "string",
              required: true,
              description: "Local directory to clone into."
            },
            {
              name: "branch",
              type: "string",
              required: false,
              description: "Branch to check out after cloning; defaults to the repo's default branch."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "createBranch",
          description: "Create a new local branch via the git CLI in the configured working directory.",
          params: [
            {
              name: "branchName",
              type: "string",
              required: true,
              description: "Name of the branch to create."
            },
            {
              name: "baseBranch",
              type: "string",
              required: false,
              description: "Branch to branch from; defaults to the current branch."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "commit",
          description: "Commit staged (or given) changes via the git CLI in the configured working directory.",
          params: [
            {
              name: "message",
              type: "string",
              required: true,
              description: "Commit message."
            },
            {
              name: "files",
              type: "string[]",
              required: false,
              description: "Specific files to commit; when omitted, all changes are committed."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "push",
          description: "Push a local branch to the remote via the git CLI, using the configured token for auth.",
          params: [
            {
              name: "branchName",
              type: "string",
              required: true,
              description: "Branch name to push."
            },
            {
              name: "force",
              type: "boolean",
              required: false,
              description: "Force-push (overwrites remote history); never set true in an agent context."
            }
          ],
          responseShape: {
            message: "string"
          }
        },
        {
          name: "createPR",
          description: "Open a pull request via the GitHub REST API.",
          params: [
            {
              name: "title",
              type: "string",
              required: true,
              description: "Pull request title."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Pull request description body."
            },
            {
              name: "baseBranch",
              type: "string",
              required: true,
              description: "Branch the PR merges into, e.g. main."
            },
            {
              name: "headBranch",
              type: "string",
              required: true,
              description: "Branch the PR merges from (the feature branch)."
            },
            {
              name: "draft",
              type: "boolean",
              required: false,
              description: "Open as a draft PR instead of ready-for-review."
            }
          ],
          responseShape: {
            number: "number",
            url: "string",
            title: "string"
          }
        },
        {
          name: "getPRComments",
          description: "List review comments on a pull request via the GitHub REST API.",
          params: [
            {
              name: "prNumber",
              type: "number",
              required: true,
              description: "Pull request number."
            }
          ],
          responseShape: {
            comments: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "number",
                    required: true
                  },
                  body: {
                    type: "string",
                    required: true
                  },
                  user: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "listIssues",
          description: "List issues on the configured repository via the GitHub REST API.",
          params: [
            {
              name: "state",
              type: "string",
              required: false,
              description: "Issue state filter: 'open', 'closed', or 'all'; defaults to open."
            },
            {
              name: "labels",
              type: "string[]",
              required: false,
              description: "Only issues carrying all of these labels."
            },
            {
              name: "per_page",
              type: "number",
              required: false,
              description: "Page size for the GitHub API request."
            }
          ],
          responseShape: {
            issues: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  number: {
                    type: "number",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  state: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "getIssue",
          description: "Fetch a single issue's details via the GitHub REST API.",
          params: [
            {
              name: "issueNumber",
              type: "number",
              required: true,
              description: "Issue number."
            }
          ],
          responseShape: {
            number: "number",
            title: "string",
            body: "string",
            state: "string"
          }
        },
        {
          name: "getFile",
          description: "Read a file's content from a repository at a given ref (declared intent \u2014 dispatch is being wired).",
          params: [
            {
              name: "owner",
              type: "string",
              required: true,
              description: "Repository owner (user or org)."
            },
            {
              name: "repo",
              type: "string",
              required: true,
              description: "Repository name."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "File path within the repository."
            },
            {
              name: "ref",
              type: "string",
              required: false,
              description: "Branch, tag, or commit SHA to read from; defaults to the default branch."
            }
          ],
          responseShape: {
            path: "string",
            content: "string",
            sha: "string",
            size: "number"
          }
        },
        {
          name: "listCommits",
          description: "List commits on a repository, optionally filtered by file path (declared intent \u2014 dispatch is being wired).",
          params: [
            {
              name: "owner",
              type: "string",
              required: true,
              description: "Repository owner (user or org)."
            },
            {
              name: "repo",
              type: "string",
              required: true,
              description: "Repository name."
            },
            {
              name: "path",
              type: "string",
              required: false,
              description: "Only commits touching this file path."
            },
            {
              name: "ref",
              type: "string",
              required: false,
              description: "Branch, tag, or commit SHA to start from; defaults to the default branch."
            },
            {
              name: "per_page",
              type: "number",
              required: false,
              description: "Page size for the GitHub API request."
            }
          ],
          responseShape: {
            commits: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  sha: {
                    type: "string",
                    required: true
                  },
                  message: {
                    type: "string",
                    required: true
                  },
                  author: {
                    type: "string",
                    required: true
                  },
                  date: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "createIssue",
          description: "Open a new issue on a repository (declared intent \u2014 dispatch is being wired).",
          params: [
            {
              name: "owner",
              type: "string",
              required: true,
              description: "Repository owner (user or org)."
            },
            {
              name: "repo",
              type: "string",
              required: true,
              description: "Repository name."
            },
            {
              name: "title",
              type: "string",
              required: true,
              description: "Issue title."
            },
            {
              name: "body",
              type: "string",
              required: false,
              description: "Issue body/description."
            },
            {
              name: "labels",
              type: "string[]",
              required: false,
              description: "Labels to apply to the new issue."
            }
          ],
          responseShape: {
            number: "number",
            title: "string",
            url: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    stripe: {
      name: "stripe",
      description: "Stripe payment processing - create intents, confirm payments, refunds",
      category: "payment",
      actions: [
        {
          name: "createPaymentIntent",
          description: "Create a Stripe PaymentIntent for a one-off charge.",
          params: [
            {
              name: "amount",
              type: "number",
              required: true,
              description: "Charge amount in the currency's smallest unit (e.g. cents for USD)."
            },
            {
              name: "currency",
              type: "string",
              required: true,
              description: "ISO 4217 currency code, e.g. usd."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Arbitrary key-value tags attached to the PaymentIntent (string values only).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            id: "string",
            clientSecret: "string",
            status: "string",
            amount: "number",
            currency: "string"
          },
          synonyms: "charge, pay"
        },
        {
          name: "confirmPayment",
          description: "Confirm a previously created PaymentIntent, attempting to complete the charge.",
          params: [
            {
              name: "paymentIntentId",
              type: "string",
              required: true,
              description: "The PaymentIntent id returned by createPaymentIntent."
            }
          ],
          responseShape: {
            id: "string",
            status: "string"
          }
        },
        {
          name: "refund",
          description: "Refund a charged PaymentIntent, in full or in part.",
          params: [
            {
              name: "paymentIntentId",
              type: "string",
              required: true,
              description: "The PaymentIntent id to refund."
            },
            {
              name: "amount",
              type: "number",
              required: false,
              description: "Amount to refund in the currency's smallest unit; omit for a full refund."
            }
          ],
          responseShape: {
            id: "string",
            status: "string",
            amount: "number"
          }
        },
        {
          name: "createCustomer",
          description: "Create a Stripe Customer for an Almadar user; the user id is stored as Stripe metadata so webhooks resolve back to it.",
          params: [
            {
              name: "email",
              type: "string",
              required: true,
              description: "Billing email."
            },
            {
              name: "almadarUid",
              type: "string",
              required: true,
              description: "The Almadar user the customer belongs to."
            },
            {
              name: "displayName",
              type: "string",
              required: false,
              description: "Customer display name; omitted when unknown."
            }
          ],
          responseShape: {
            customerId: "string",
            email: "string",
            almadarUid: "string"
          }
        },
        {
          name: "createCheckoutSession",
          description: "Create a hosted Checkout Session subscribing a customer to a tier; the caller redirects to `url`.",
          params: [
            {
              name: "customerId",
              type: "string",
              required: false,
              description: "Existing customer id; Stripe creates one at checkout when omitted."
            },
            {
              name: "tier",
              type: "'solo' | 'teams'",
              required: true,
              description: "Tier to subscribe to; its price comes from STRIPE_PRICE_SOLO / STRIPE_PRICE_TEAMS."
            },
            {
              name: "quantity",
              type: "number",
              required: true,
              description: "Seats (solo is always 1)."
            },
            {
              name: "successUrl",
              type: "string",
              required: true,
              description: "Where Stripe returns after a successful checkout."
            },
            {
              name: "cancelUrl",
              type: "string",
              required: true,
              description: "Where Stripe returns when checkout is cancelled."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Tags written onto the created subscription (string values only).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            url: "string",
            sessionId: "string",
            customerId: "string"
          }
        },
        {
          name: "createPortalSession",
          description: "Create a hosted Billing Portal session for a customer's self-service plan management; the caller redirects to `url`.",
          params: [
            {
              name: "customerId",
              type: "string",
              required: true,
              description: "Customer id."
            },
            {
              name: "returnUrl",
              type: "string",
              required: true,
              description: "Where the portal returns to."
            }
          ],
          responseShape: {
            url: "string",
            customerId: "string"
          }
        },
        {
          name: "getSubscription",
          description: "Fetch one subscription in canonical form.",
          params: [
            {
              name: "subscriptionId",
              type: "string",
              required: true,
              description: "Subscription id."
            }
          ],
          responseShape: {}
        },
        {
          name: "createSubscription",
          description: "Subscribe a customer to a tier directly (no Checkout).",
          params: [
            {
              name: "customerId",
              type: "string",
              required: true,
              description: "Customer id."
            },
            {
              name: "tier",
              type: "'solo' | 'teams'",
              required: true,
              description: "Tier to subscribe to."
            },
            {
              name: "quantity",
              type: "number",
              required: true,
              description: "Seats."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Tags written onto the subscription (string values only).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {}
        },
        {
          name: "updateSubscription",
          description: "Change a subscription's seat count or its cancel-at-period-end flag.",
          params: [
            {
              name: "subscriptionId",
              type: "string",
              required: true,
              description: "Subscription id."
            },
            {
              name: "quantity",
              type: "number",
              required: false,
              description: "New seat count; unchanged when omitted."
            },
            {
              name: "cancelAtPeriodEnd",
              type: "boolean",
              required: false,
              description: "Set or clear cancel-at-period-end; unchanged when omitted."
            }
          ],
          responseShape: {}
        },
        {
          name: "cancelSubscription",
          description: "Cancel a subscription, at the period end (default) or immediately.",
          params: [
            {
              name: "subscriptionId",
              type: "string",
              required: true,
              description: "Subscription id."
            },
            {
              name: "atPeriodEnd",
              type: "boolean",
              required: false,
              description: "Cancel at the period end so access lasts until then; defaults to true."
            }
          ],
          responseShape: {}
        }
      ],
      runtimes: [
        "node"
      ]
    },
    llm: {
      name: "llm",
      description: "LLM service for content generation, classification, and structured extraction",
      category: "ai",
      actions: [
        {
          name: "generate",
          description: "Generate text from a prompt.",
          params: [
            {
              name: "systemPrompt",
              type: "string",
              required: false,
              description: "System prompt steering the model's behavior."
            },
            {
              name: "userPrompt",
              type: "string",
              required: true,
              description: "The user/task prompt sent to the model."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            },
            {
              name: "temperature",
              type: "number",
              required: false,
              description: "Sampling temperature; higher is more random."
            },
            {
              name: "maxTokens",
              type: "number",
              required: false,
              description: "Maximum tokens to generate."
            }
          ],
          responseShape: {
            content: "string",
            usage: {
              type: "object",
              properties: {
                promptTokens: {
                  type: "number",
                  required: true
                },
                completionTokens: {
                  type: "number",
                  required: true
                },
                totalTokens: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "classify",
          description: "Classify text into one of the given categories.",
          params: [
            {
              name: "text",
              type: "string",
              required: true,
              description: "Text to classify."
            },
            {
              name: "categories",
              type: "string[]",
              required: true,
              description: "Closed set of category labels the model must choose from."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            category: "string",
            confidence: "number",
            reasoning: "string"
          }
        },
        {
          name: "classifyMany",
          description: "Classify many texts in one model call. Every item's id comes back exactly once with a category from\nthe declared set; there is no reasoning, so the reply stays short.",
          params: [
            {
              name: "items",
              type: "array",
              required: true,
              description: "The texts, each with the id its verdict comes back under.",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  text: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "categories",
              type: "string[]",
              required: true,
              description: "Closed set of category labels the model must choose from."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            verdicts: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  category: {
                    type: "string",
                    required: true
                  },
                  confidence: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "extract",
          description: "Extract structured data from text according to a JSON schema.",
          params: [
            {
              name: "text",
              type: "string",
              required: true,
              description: "Source text to extract data from."
            },
            {
              name: "schema",
              type: "JsonSchemaDefinition",
              required: true,
              description: "JSON Schema describing the fields to extract.",
              properties: {
                type: {
                  type: "string",
                  required: false
                },
                properties: {
                  type: "object",
                  required: false,
                  description: "Keyed by field name \u2014 each named field carries its OWN nested schema\n(real JSON-Schema semantics; a single un-keyed nested schema here would\nsay every field of an object shares one shape).",
                  mapValue: {
                    type: "JsonSchemaDefinition",
                    cyclic: true,
                    cyclicTypeName: "JsonSchemaDefinition"
                  }
                },
                required: {
                  type: "string[]",
                  required: false
                },
                items: {
                  type: "JsonSchemaDefinition",
                  required: false,
                  cyclic: true,
                  cyclicTypeName: "JsonSchemaDefinition"
                },
                additionalProperties: {
                  type: "JsonSchemaDefinition",
                  required: false,
                  cyclic: true,
                  cyclicTypeName: "JsonSchemaDefinition"
                },
                description: {
                  type: "string",
                  required: false
                },
                enum: {
                  type: "string[]",
                  required: false,
                  description: 'Enum values are serialized to strings on the wire \u2014 pass `["a","b"]` even for non-string enums.'
                }
              }
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            data: {
              type: "ExtractedData",
              shapedBy: "schema"
            },
            confidence: "number"
          },
          synonyms: "parse, structure"
        },
        {
          name: "summarize",
          description: "Summarize text with optional length and style constraints.",
          params: [
            {
              name: "text",
              type: "string",
              required: true,
              description: "Text to summarize."
            },
            {
              name: "maxLength",
              type: "number",
              required: false,
              description: "Target maximum summary length in words."
            },
            {
              name: "style",
              type: "string",
              required: false,
              description: "Summary style, e.g. 'bullet' or 'detailed'."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            summary: "string",
            keyPoints: "string[]"
          }
        },
        {
          name: "call-tools",
          description: "Answer a request by operating the running app as the caller: the model may\nfire the declared inputs and read the entities named in `tools`, through the\nhost's ordinary dispatch (guards, policies and listens all apply), until it\nreplies. Hitting `maxSteps` is a failure.",
          params: [
            {
              name: "messages",
              type: "AgentChatMessage[]",
              required: true,
              description: "The conversation so far, oldest first; the last turn is the request.",
              items: {
                type: "AgentChatMessage",
                properties: {
                  role: {
                    type: '"user" | "assistant"',
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "tools",
              type: "AgentTool[]",
              required: true,
              description: "The inputs and entities the model may use.",
              items: {
                type: "AgentTool",
                properties: {
                  event: {
                    type: "EventAddress",
                    required: false
                  },
                  payload: {
                    type: "AgentToolField[]",
                    required: false,
                    items: {
                      type: "AgentToolField",
                      properties: {
                        name: {
                          type: "string",
                          required: true
                        },
                        type: {
                          type: "string",
                          required: true
                        },
                        required: {
                          type: "boolean",
                          required: false
                        },
                        values: {
                          type: "string[]",
                          required: false,
                          description: "Closed value set \u2014 element-wise for an array type."
                        },
                        properties: {
                          type: "AgentToolField[]",
                          required: false,
                          description: "Fields of an object (or `[object]`) type.",
                          items: {
                            type: "AgentToolField",
                            cyclic: true,
                            cyclicTypeName: "AgentToolField"
                          }
                        },
                        description: {
                          type: "string",
                          required: false
                        }
                      }
                    }
                  },
                  read: {
                    type: "string",
                    required: false
                  },
                  scope: {
                    type: '"declared"',
                    required: false
                  }
                }
              }
            },
            {
              name: "instructions",
              type: "string",
              required: false,
              description: "Standing instructions for the model."
            },
            {
              name: "maxSteps",
              type: "number",
              required: false,
              description: "Model round-trips allowed before the call fails (default 8)."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Model id override."
            }
          ],
          responseShape: {
            reply: "string",
            steps: "AgentActivity[]",
            usage: {
              type: "object",
              properties: {
                promptTokens: {
                  type: "number",
                  required: true
                },
                completionTokens: {
                  type: "number",
                  required: true
                },
                totalTokens: {
                  type: "number",
                  required: true
                },
                modelCalls: {
                  type: "number",
                  required: true
                }
              }
            }
          },
          synonyms: "agent, assistant, tool use, act"
        },
        {
          name: "embed",
          description: "Generate embeddings for an array of texts.",
          params: [
            {
              name: "texts",
              type: "string[]",
              required: true,
              description: "Texts to embed, one vector per entry, in order."
            },
            {
              name: "model",
              type: "string",
              required: false,
              description: "Embedding model id override."
            }
          ],
          responseShape: {
            embeddings: "number[][]"
          }
        }
      ],
      runtimes: [
        "node",
        "browser"
      ]
    },
    ml: {
      name: "ml",
      description: "ML service - model inference on a trained or off-the-shelf checkpoint, reached via a deployed serving orbital",
      category: "ai",
      actions: [
        {
          name: "infer",
          description: "Run inference against a deployed model checkpoint.",
          params: [
            {
              name: "model",
              type: "string",
              required: true,
              description: "`hf:<id>` for off-the-shelf weights, or a masar checkpoint id."
            },
            {
              name: "input",
              type: "ServiceParamsValue",
              required: true,
              description: "JSON-safe model input (tensor-shaped payload from `contract/entity-to-tensor`)."
            }
          ],
          responseShape: {
            output: "ServiceParamsValue",
            confidence: "number",
            violations: "ServiceParamsValue[]"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    youtube: {
      name: "youtube",
      description: "YouTube Data API - search videos, get video/channel details",
      category: "media",
      actions: [
        {
          name: "search",
          description: "Search YouTube via the Data API and return matching video snippets.",
          params: [
            {
              name: "query",
              type: "string",
              required: true,
              description: "Search query text."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum number of results; defaults to 10."
            },
            {
              name: "type",
              type: "string",
              required: false,
              description: "Resource type filter passed to the Data API (e.g. 'video', 'channel', 'playlist')."
            }
          ],
          responseShape: {}
        },
        {
          name: "getVideo",
          description: "Fetch a video's snippet and statistics via the YouTube Data API.",
          params: [
            {
              name: "videoId",
              type: "string",
              required: true,
              description: "YouTube video id."
            }
          ],
          responseShape: {
            title: "string",
            description: "string",
            viewCount: "string",
            likeCount: "string"
          }
        },
        {
          name: "getChannel",
          description: "Fetch a channel's snippet and statistics via the YouTube Data API.",
          params: [
            {
              name: "channelId",
              type: "string",
              required: true,
              description: "YouTube channel id."
            }
          ],
          responseShape: {
            name: "string",
            description: "string",
            subscriberCount: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    twilio: {
      name: "twilio",
      description: "Twilio messaging - send SMS, WhatsApp messages",
      category: "messaging",
      actions: [
        {
          name: "sendSMS",
          description: "Send an SMS message via Twilio.",
          params: [
            {
              name: "to",
              type: "string",
              required: true,
              description: "Destination phone number, E.164 format."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Message text."
            },
            {
              name: "from",
              type: "string",
              required: false,
              description: "Sender phone number; defaults to TWILIO_PHONE_NUMBER."
            }
          ],
          responseShape: {
            sid: "string",
            status: "string"
          },
          synonyms: "text message, text"
        },
        {
          name: "sendWhatsApp",
          description: "Send a WhatsApp message via Twilio, using the configured phone number as sender on both ends.",
          params: [
            {
              name: "to",
              type: "string",
              required: true,
              description: "Destination phone number, E.164 format (sent as whatsapp:number)."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Message text."
            }
          ],
          responseShape: {
            sid: "string",
            status: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    email: {
      name: "email",
      description: "Transactional email via SendGrid/Resend",
      category: "messaging",
      actions: [
        {
          name: "send",
          description: "Send a transactional email via the configured provider (SendGrid or Resend).",
          params: [
            {
              name: "to",
              type: "string",
              required: true,
              description: "Recipient email address."
            },
            {
              name: "subject",
              type: "string",
              required: true,
              description: "Email subject line."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Plain-text (or HTML, if `htmlBody` is absent) body content."
            },
            {
              name: "from",
              type: "string",
              required: false,
              description: "Sender address; defaults to FROM_EMAIL."
            },
            {
              name: "htmlBody",
              type: "string",
              required: false,
              description: "HTML content; when present `body` becomes the plain-text alternative."
            },
            {
              name: "replyTo",
              type: "string",
              required: false,
              description: "Reply-To address."
            },
            {
              name: "templateId",
              type: "string",
              required: false,
              description: "Provider template reference (SendGrid dynamic templates; Resend rejects it loudly)."
            }
          ],
          responseShape: {
            id: "string",
            status: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    webhook: {
      name: "webhook",
      description: "Outbound webhook - signed HTTP POST notifications to external endpoints",
      category: "messaging",
      actions: [
        {
          name: "send",
          description: "POST a JSON event notification to an external URL, HMAC-SHA256-signed when a secret is configured; 5xx responses retry, 4xx do not.",
          params: [
            {
              name: "url",
              type: "string",
              required: true,
              description: "Destination URL to POST to."
            },
            {
              name: "event",
              type: "string",
              required: true,
              description: "Event name, carried in the body and the X-Almadar-Event header."
            },
            {
              name: "payload",
              type: "object",
              required: false,
              description: "Event payload; defaults to an empty object.",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "secret",
              type: "string",
              required: false,
              description: "Per-call HMAC signing secret, overriding WEBHOOK_SIGNING_SECRET."
            }
          ],
          responseShape: {
            status: "number",
            ok: "boolean",
            durationMs: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    push: {
      name: "push",
      description: "Web Push - VAPID-signed browser push notifications",
      category: "messaging",
      actions: [
        {
          name: "send",
          description: "Send a VAPID-signed Web Push notification to a browser PushSubscription endpoint.",
          params: [
            {
              name: "subscription",
              type: "object",
              required: true,
              description: "Browser PushSubscription: endpoint URL plus the p256dh/auth encryption keys.",
              properties: {
                endpoint: {
                  type: "string",
                  required: true
                },
                keys: {
                  type: "object",
                  required: true,
                  properties: {
                    p256dh: {
                      type: "string",
                      required: true
                    },
                    auth: {
                      type: "string",
                      required: true
                    }
                  }
                }
              }
            },
            {
              name: "title",
              type: "string",
              required: true,
              description: "Notification title."
            },
            {
              name: "body",
              type: "string",
              required: true,
              description: "Notification body text."
            },
            {
              name: "url",
              type: "string",
              required: false,
              description: "URL to open when the notification is clicked."
            },
            {
              name: "icon",
              type: "string",
              required: false,
              description: "Notification icon URL."
            }
          ],
          responseShape: {
            statusCode: "number",
            ok: "boolean",
            expired: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    calendar: {
      name: "calendar",
      description: "Google Calendar - events, free-busy, watch channels (service account / domain-wide delegation)",
      category: "infrastructure",
      actions: [
        {
          name: "listEvents",
          description: "List events on a Google Calendar; pass `syncToken` for incremental sync instead of a time window (the API rejects combining them).",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID (or 'primary')."
            },
            {
              name: "timeMin",
              type: "string",
              required: false,
              description: "ISO start of the time window (ignored when `syncToken` is set)."
            },
            {
              name: "timeMax",
              type: "string",
              required: false,
              description: "ISO end of the time window (ignored when `syncToken` is set)."
            },
            {
              name: "syncToken",
              type: "string",
              required: false,
              description: "Incremental-sync token from a previous call's `nextSyncToken`; supersedes timeMin/timeMax."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum events to return; defaults to 250."
            }
          ],
          responseShape: {
            events: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  summary: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  location: {
                    type: "string",
                    required: true
                  },
                  start: {
                    type: "string",
                    required: true
                  },
                  end: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  },
                  updated: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            nextSyncToken: "string | null"
          }
        },
        {
          name: "createEvent",
          description: "Create a calendar event; an all-day event uses a 10-char (YYYY-MM-DD) start with no `end`/`durationMinutes`, defaulting to the next day.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "summary",
              type: "string",
              required: true,
              description: "Event title."
            },
            {
              name: "description",
              type: "string",
              required: false,
              description: "Event description."
            },
            {
              name: "location",
              type: "string",
              required: false,
              description: "Event location text."
            },
            {
              name: "start",
              type: "string",
              required: true,
              description: "ISO start (10 chars = all-day date, longer = dateTime)."
            },
            {
              name: "end",
              type: "string",
              required: false,
              description: "ISO end; if omitted, derived from `durationMinutes` or the all-day next-day convention."
            },
            {
              name: "durationMinutes",
              type: "number",
              required: false,
              description: "Duration in minutes used to derive `end` when `end` is omitted (non-all-day events only)."
            }
          ],
          responseShape: {
            id: "string",
            status: "string",
            htmlLink: "string"
          }
        },
        {
          name: "updateEvent",
          description: "Patch an existing event's fields; only fields present in params are changed.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "eventId",
              type: "string",
              required: true,
              description: "Id of the event to update."
            },
            {
              name: "summary",
              type: "string",
              required: false,
              description: "New event title."
            },
            {
              name: "description",
              type: "string",
              required: false,
              description: "New event description."
            },
            {
              name: "location",
              type: "string",
              required: false,
              description: "New event location text."
            },
            {
              name: "start",
              type: "string",
              required: false,
              description: "New ISO start."
            },
            {
              name: "end",
              type: "string",
              required: false,
              description: "New ISO end."
            }
          ],
          responseShape: {
            id: "string",
            status: "string"
          }
        },
        {
          name: "deleteEvent",
          description: "Delete a calendar event.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "eventId",
              type: "string",
              required: true,
              description: "Id of the event to delete."
            }
          ],
          responseShape: {
            id: "string",
            deleted: "boolean"
          }
        },
        {
          name: "freeBusy",
          description: "Query busy time blocks on a calendar within a window.",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "timeMin",
              type: "string",
              required: true,
              description: "ISO start of the query window."
            },
            {
              name: "timeMax",
              type: "string",
              required: true,
              description: "ISO end of the query window."
            }
          ],
          responseShape: {
            busy: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  start: {
                    type: "string",
                    required: true
                  },
                  end: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "watch",
          description: "Register a push-notification channel for calendar change events (Google Calendar API watch).",
          params: [
            {
              name: "calendarId",
              type: "string",
              required: false,
              description: "Calendar id; defaults to GOOGLE_CALENDAR_ID."
            },
            {
              name: "channelId",
              type: "string",
              required: true,
              description: "Caller-chosen id identifying this notification channel."
            },
            {
              name: "address",
              type: "string",
              required: true,
              description: "HTTPS callback URL Google POSTs change notifications to."
            },
            {
              name: "ttlSeconds",
              type: "number",
              required: false,
              description: "Requested channel time-to-live in seconds."
            }
          ],
          responseShape: {
            channelId: "string",
            resourceId: "string",
            expiration: "string"
          }
        },
        {
          name: "stopWatch",
          description: "Stop an active watch channel, ending calendar change notifications.",
          params: [
            {
              name: "channelId",
              type: "string",
              required: true,
              description: "The channel id from the corresponding `watch` call."
            },
            {
              name: "resourceId",
              type: "string",
              required: true,
              description: "The resourceId returned by the corresponding `watch` call."
            }
          ],
          responseShape: {
            stopped: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    drive: {
      name: "drive",
      description: "Google Drive - files, folders, sharing (service account / domain-wide delegation)",
      category: "infrastructure",
      actions: [
        {
          name: "listFiles",
          description: "List non-trashed files in a Google Drive folder, optionally filtered by a Drive API query clause.",
          params: [
            {
              name: "folderId",
              type: "string",
              required: false,
              description: "Restrict to files whose parents include this folder id."
            },
            {
              name: "query",
              type: "string",
              required: false,
              description: "Extra Drive API query clause, ANDed with the folder/trashed filters."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum files to return; defaults to 100."
            }
          ],
          responseShape: {
            files: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  mimeType: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  },
                  modifiedTime: {
                    type: "string",
                    required: true
                  },
                  webViewLink: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "getFile",
          description: "Download a file's content, base64-encoded.",
          params: [
            {
              name: "fileId",
              type: "string",
              required: true,
              description: "Drive file id."
            }
          ],
          responseShape: {
            id: "string",
            name: "string",
            mimeType: "string",
            content: "string",
            size: "number"
          }
        },
        {
          name: "uploadFile",
          description: "Upload a file to Drive; writes go through the user OAuth client when configured (service-account uploads have no storage quota on personal accounts).",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "File name to create."
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: "File content as a base64 data URL (data:mime;base64,...) or raw base64/plain text."
            },
            {
              name: "mimeType",
              type: "string",
              required: false,
              description: "MIME type; inferred from a data URL when omitted."
            },
            {
              name: "folderId",
              type: "string",
              required: false,
              description: "Parent folder id; defaults to GOOGLE_DRIVE_FOLDER_ID."
            }
          ],
          responseShape: {
            id: "string",
            name: "string",
            webViewLink: "string"
          },
          synonyms: "upload file"
        },
        {
          name: "createFolder",
          description: "Create a new Drive folder.",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Folder name to create."
            },
            {
              name: "parentId",
              type: "string",
              required: false,
              description: "Parent folder id; defaults to GOOGLE_DRIVE_FOLDER_ID."
            }
          ],
          responseShape: {
            id: "string",
            name: "string"
          }
        },
        {
          name: "shareFile",
          description: "Grant a user permission on a Drive file.",
          params: [
            {
              name: "fileId",
              type: "string",
              required: true,
              description: "Drive file id."
            },
            {
              name: "email",
              type: "string",
              required: true,
              description: "Email address of the grantee."
            },
            {
              name: "role",
              type: "'reader' | 'writer' | 'commenter'",
              required: false,
              description: "Permission level; defaults to 'reader'."
            }
          ],
          responseShape: {
            shared: "boolean",
            permissionId: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    metaAds: {
      name: "metaAds",
      description: "Meta Ads - read-only Marketing Insights (spend, campaigns)",
      category: "marketing",
      actions: [
        {
          name: "getSpend",
          description: "Fetch total ad spend, impressions, and clicks for an account over a date range via the Meta Graph API Marketing Insights endpoint (read-only).",
          params: [
            {
              name: "accountId",
              type: "string",
              required: false,
              description: "Ad account id (with or without the `act_` prefix); defaults to META_AD_ACCOUNT_ID."
            },
            {
              name: "since",
              type: "string",
              required: true,
              description: "Range start date (YYYY-MM-DD)."
            },
            {
              name: "until",
              type: "string",
              required: true,
              description: "Range end date (YYYY-MM-DD)."
            }
          ],
          responseShape: {
            spend: "number",
            currency: "string",
            impressions: "number",
            clicks: "number"
          }
        },
        {
          name: "listCampaigns",
          description: "List campaigns on an ad account via the Meta Graph API (read-only); dailyBudget is converted from Meta's minor-unit reporting.",
          params: [
            {
              name: "accountId",
              type: "string",
              required: false,
              description: "Ad account id (with or without the `act_` prefix); defaults to META_AD_ACCOUNT_ID."
            },
            {
              name: "status",
              type: "string",
              required: false,
              description: "Filter by campaign effective_status (e.g. 'ACTIVE', 'PAUSED')."
            }
          ],
          responseShape: {
            campaigns: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  },
                  dailyBudget: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    accounting: {
      name: "accounting",
      description: "Accounting export - invoice/journal rows to import-ready CSV",
      category: "finance",
      actions: [
        {
          name: "exportInvoices",
          description: "Shape invoice rows into an import-ready CSV export (no accounting-vendor connection \u2014 generic column set).",
          params: [
            {
              name: "invoices",
              type: "array",
              required: true,
              description: "Invoice rows to export, in column order id/number/customer/issuedAt/dueAt/currency/net/tax/gross/status.",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  number: {
                    type: "string",
                    required: true
                  },
                  customer: {
                    type: "string",
                    required: true
                  },
                  issuedAt: {
                    type: "string",
                    required: true
                  },
                  dueAt: {
                    type: "string",
                    required: true
                  },
                  currency: {
                    type: "string",
                    required: true
                  },
                  net: {
                    type: "number",
                    required: true
                  },
                  tax: {
                    type: "number",
                    required: true
                  },
                  gross: {
                    type: "number",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "format",
              type: "'csv'",
              required: false,
              description: "Export format; only 'csv' is currently supported."
            }
          ],
          responseShape: {
            content: "string",
            filename: "string",
            count: "number"
          }
        },
        {
          name: "exportJournal",
          description: "Shape journal entry rows into an import-ready CSV export.",
          params: [
            {
              name: "entries",
              type: "array",
              required: true,
              description: "Journal entry rows to export, in column order date/account/description/debit/credit/reference.",
              items: {
                type: "object",
                properties: {
                  date: {
                    type: "string",
                    required: true
                  },
                  account: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  debit: {
                    type: "number",
                    required: true
                  },
                  credit: {
                    type: "number",
                    required: true
                  },
                  reference: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "format",
              type: "'csv'",
              required: false,
              description: "Export format; only 'csv' is currently supported."
            }
          ],
          responseShape: {
            content: "string",
            filename: "string",
            count: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    banking: {
      name: "banking",
      description: "Banking - GoCardless Bank Account Data (requisitions, accounts, transactions)",
      category: "finance",
      actions: [
        {
          name: "createRequisition",
          description: "Start a GoCardless Bank Account Data requisition (bank-connection consent flow); the returned `link` is where the end user authorizes access.",
          params: [
            {
              name: "institutionId",
              type: "string",
              required: true,
              description: "GoCardless institution id (the bank to connect)."
            },
            {
              name: "redirectUrl",
              type: "string",
              required: true,
              description: "URL GoCardless redirects the user to after consent."
            },
            {
              name: "reference",
              type: "string",
              required: false,
              description: "Caller-chosen reference to correlate the requisition."
            }
          ],
          responseShape: {
            requisitionId: "string",
            link: "string"
          }
        },
        {
          name: "listAccounts",
          description: "List account ids linked under a completed requisition.",
          params: [
            {
              name: "requisitionId",
              type: "string",
              required: true,
              description: "Requisition id from createRequisition."
            }
          ],
          responseShape: {
            accounts: "string[]"
          }
        },
        {
          name: "listTransactions",
          description: "List booked transactions on a linked bank account, optionally filtered by date range.",
          params: [
            {
              name: "accountId",
              type: "string",
              required: true,
              description: "GoCardless account id."
            },
            {
              name: "dateFrom",
              type: "string",
              required: false,
              description: "Only transactions booked on/after this date (YYYY-MM-DD)."
            },
            {
              name: "dateTo",
              type: "string",
              required: false,
              description: "Only transactions booked on/before this date (YYYY-MM-DD)."
            }
          ],
          responseShape: {
            transactions: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  amount: {
                    type: "number",
                    required: true
                  },
                  currency: {
                    type: "string",
                    required: true
                  },
                  date: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  counterparty: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    esign: {
      name: "esign",
      description: "E-signature - send/track/download envelopes (DocuSign)",
      category: "infrastructure",
      actions: [
        {
          name: "sendEnvelope",
          description: "Send a document for e-signature via DocuSign; accepts the document content as a data URL or raw base64.",
          params: [
            {
              name: "recipientEmail",
              type: "string",
              required: true,
              description: "Signer's email address."
            },
            {
              name: "recipientName",
              type: "string",
              required: true,
              description: "Signer's display name."
            },
            {
              name: "documentName",
              type: "string",
              required: true,
              description: "Document file name (its extension also picks the DocuSign fileExtension)."
            },
            {
              name: "documentContent",
              type: "string",
              required: true,
              description: "Document content as a base64 data URL or raw base64."
            },
            {
              name: "emailSubject",
              type: "string",
              required: false,
              description: 'Envelope email subject; defaults to "Please sign: " + documentName.'
            }
          ],
          responseShape: {
            envelopeId: "string",
            status: "string"
          }
        },
        {
          name: "getEnvelopeStatus",
          description: "Poll a DocuSign envelope's signing status.",
          params: [
            {
              name: "envelopeId",
              type: "string",
              required: true,
              description: "DocuSign envelope id from sendEnvelope."
            }
          ],
          responseShape: {
            status: "string",
            completedAt: "string"
          }
        },
        {
          name: "downloadDocument",
          description: "Download the combined signed document(s) for a completed envelope, base64-encoded PDF.",
          params: [
            {
              name: "envelopeId",
              type: "string",
              required: true,
              description: "DocuSign envelope id."
            }
          ],
          responseShape: {
            content: "string",
            documentName: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    docker: {
      name: "docker",
      description: "Docker \u2014 container and image management",
      category: "devtools",
      actions: [
        {
          name: "build",
          description: "Simulate building a container image (in-memory backend \u2014 no real Docker daemon/SDK; a real build is not yet implemented).",
          params: [
            {
              name: "tag",
              type: "string",
              required: true,
              description: "Image tag to build, e.g. myapp:latest."
            },
            {
              name: "dockerfile",
              type: "string",
              required: false,
              description: "Dockerfile path; defaults to 'Dockerfile'."
            },
            {
              name: "context",
              type: "string",
              required: false,
              description: "Build context directory; defaults to '.'."
            },
            {
              name: "buildArgs",
              type: "object",
              required: false,
              description: "Build-time --build-arg values.",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            imageId: "string",
            tag: "string",
            size: "number",
            buildTime: "number"
          }
        },
        {
          name: "run",
          description: "Simulate starting a container from an image (in-memory backend).",
          params: [
            {
              name: "image",
              type: "string",
              required: true,
              description: "Image to run (as built/tagged by the `build` action, or an arbitrary name)."
            },
            {
              name: "name",
              type: "string",
              required: false,
              description: "Container name; auto-generated when omitted."
            },
            {
              name: "ports",
              type: "array",
              required: false,
              description: "Host/container port mappings.",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "number",
                    required: true
                  },
                  container: {
                    type: "number",
                    required: true
                  },
                  protocol: {
                    type: "string",
                    required: false
                  }
                }
              }
            },
            {
              name: "env",
              type: "object",
              required: false,
              description: "Environment variables set inside the container.",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "volumes",
              type: "array",
              required: false,
              description: "Host/container path mounts.",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "string",
                    required: true
                  },
                  container: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "command",
              type: "string",
              required: false,
              description: "Override command run inside the container."
            }
          ],
          responseShape: {
            containerId: "string",
            name: "string",
            status: "string",
            ports: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "number",
                    required: true
                  },
                  container: {
                    type: "number",
                    required: true
                  },
                  protocol: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "stop",
          description: "Stop a running (or paused) simulated container.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            }
          ],
          responseShape: {
            containerId: "string",
            status: "string",
            stoppedAt: "number"
          }
        },
        {
          name: "remove",
          description: "Remove a simulated container; fails if it is running unless `force` is set.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            },
            {
              name: "force",
              type: "boolean",
              required: false,
              description: "Remove even if the container is currently running."
            }
          ],
          responseShape: {
            containerId: "string",
            removed: "boolean"
          }
        },
        {
          name: "logs",
          description: "Fetch generated log lines for a simulated container.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            },
            {
              name: "tail",
              type: "number",
              required: false,
              description: "Number of most-recent lines to return; defaults to 100."
            },
            {
              name: "since",
              type: "string",
              required: false,
              description: "Only lines timestamped at/after this ISO time."
            }
          ],
          responseShape: {
            containerId: "string",
            logs: "string[]",
            lineCount: "number"
          }
        },
        {
          name: "status",
          description: "Fetch a simulated container's current status and metadata.",
          params: [
            {
              name: "containerId",
              type: "string",
              required: true,
              description: "Container id, or a unique id prefix."
            }
          ],
          responseShape: {
            containerId: "string",
            name: "string",
            image: "string",
            status: "string",
            ports: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  host: {
                    type: "number",
                    required: true
                  },
                  container: {
                    type: "number",
                    required: true
                  },
                  protocol: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            createdAt: "number",
            startedAt: "number | null",
            stoppedAt: "number | null"
          }
        },
        {
          name: "list",
          description: "List simulated containers; by default only running ones (like `docker ps`).",
          params: [
            {
              name: "all",
              type: "boolean",
              required: false,
              description: "Include stopped/exited containers, not just running ones."
            },
            {
              name: "filterByLabel",
              type: "string",
              required: false,
              description: "Filter by a `key` or `key=value` label match."
            }
          ],
          responseShape: {
            containers: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  image: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "string",
                    required: true
                  },
                  ports: {
                    type: "array",
                    required: true,
                    items: {
                      type: "object",
                      properties: {
                        host: {
                          type: "number",
                          required: true
                        },
                        container: {
                          type: "number",
                          required: true
                        },
                        protocol: {
                          type: "string",
                          required: true
                        }
                      }
                    }
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            total: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    storage: {
      name: "storage",
      description: "Object storage \u2014 upload, download, delete, list files",
      category: "infrastructure",
      actions: [
        {
          name: "upload",
          description: "Upload an object to an S3-compatible bucket (real S3/R2/MinIO backend when storage credentials are configured, in-memory dev fallback otherwise \u2014 refused in production). Admits either the canonical `key`+`content` pair or the file-form `file` payload (UploadDropZone's `{ name, size, type, content }` shape); when `file` is present its `key`/`content` are derived from it.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Target bucket; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: false,
              description: "Object key to store under (canonical shape); ignored when `file` is present."
            },
            {
              name: "content",
              type: "string",
              required: false,
              description: "Object content as a base64 data URL, raw base64, or plain text (canonical shape); ignored when `file` is present."
            },
            {
              name: "contentType",
              type: "string",
              required: false,
              description: "MIME type override for the canonical shape; inferred from a data URL when omitted."
            },
            {
              name: "file",
              type: "object",
              required: false,
              description: "File-form upload payload (as emitted by UploadDropZone); `content` is a base64 data URL.",
              properties: {
                name: {
                  type: "string",
                  required: true
                },
                size: {
                  type: "number",
                  required: true
                },
                type: {
                  type: "string",
                  required: true
                },
                content: {
                  type: "string",
                  required: false
                }
              }
            },
            {
              name: "acl",
              type: "'public' | 'private'",
              required: false,
              description: "Object ACL \u2014 'public' produces a public URL, otherwise the URL is signed/private."
            },
            {
              name: "maxSize",
              type: "number",
              required: false,
              description: "Reject the upload if the file-form payload's declared size exceeds this many bytes."
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              description: "Object metadata stored alongside the content (S3-compatible object stores carry metadata as string headers \u2014 never structured values).",
              mapValue: {
                type: "string"
              }
            }
          ],
          responseShape: {
            key: "string",
            bucket: "string",
            size: "number",
            etag: "string",
            id: "string",
            url: "string"
          },
          synonyms: "upload file, upload object"
        },
        {
          name: "uploadMany",
          description: "Upload a whole UploadDropZone batch in one call, one item per file \u2014 the FSM-friendly shape for bulk-persisting uploaded-file rows.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Target bucket; defaults to STORAGE_BUCKET."
            },
            {
              name: "files",
              type: "array",
              required: true,
              description: "Files to upload, in UploadDropZone's `{ name, size, type, content }` shape.",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  },
                  type: {
                    type: "string",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: false
                  }
                }
              }
            },
            {
              name: "acl",
              type: "'public' | 'private'",
              required: false,
              description: "Object ACL applied to every file in the batch."
            },
            {
              name: "maxSize",
              type: "number",
              required: false,
              description: "Reject any file whose declared size exceeds this many bytes."
            }
          ],
          responseShape: {
            items: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  key: {
                    type: "string",
                    required: true
                  },
                  url: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  sizeBytes: {
                    type: "number",
                    required: true
                  },
                  mimeType: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            count: "number"
          }
        },
        {
          name: "download",
          description: "Download an object's content, base64-encoded.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket the object lives in; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: true,
              description: "Object key."
            }
          ],
          responseShape: {
            content: "string",
            contentType: "string",
            size: "number",
            metadata: {
              type: "object",
              mapValue: {
                type: "string"
              }
            }
          }
        },
        {
          name: "list",
          description: "List object keys in a bucket, optionally prefix-filtered, one page at a time.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket to list; defaults to STORAGE_BUCKET."
            },
            {
              name: "prefix",
              type: "string",
              required: false,
              description: "Only keys starting with this prefix."
            },
            {
              name: "maxKeys",
              type: "number",
              required: false,
              description: "Maximum keys per page; defaults to 1000."
            },
            {
              name: "continuationToken",
              type: "string",
              required: false,
              description: "Pagination token from a previous call's `nextToken`."
            }
          ],
          responseShape: {
            keys: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  key: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  },
                  lastModified: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            truncated: "boolean",
            nextToken: "string"
          }
        },
        {
          name: "delete",
          description: "Delete an object from a bucket.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket the object lives in; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: true,
              description: "Object key to delete."
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "getSignedUrl",
          description: "Generate a time-limited pre-signed URL for direct GET or PUT access to an object.",
          params: [
            {
              name: "bucket",
              type: "string",
              required: true,
              description: "Bucket the object lives in; defaults to STORAGE_BUCKET."
            },
            {
              name: "key",
              type: "string",
              required: true,
              description: "Object key."
            },
            {
              name: "expiresIn",
              type: "number",
              required: false,
              description: "URL validity window in seconds; defaults to 3600."
            },
            {
              name: "operation",
              type: "'get' | 'put'",
              required: false,
              description: "Whether the URL authorizes a read or a write; defaults to 'get'."
            }
          ],
          responseShape: {
            url: "string",
            expiresAt: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    queue: {
      name: "queue",
      description: "Message queue \u2014 publish, subscribe, acknowledge messages",
      category: "infrastructure",
      actions: [
        {
          name: "enqueue",
          description: "Add a job to an in-memory job queue, ordered by priority (higher first) among ready jobs.",
          params: [
            {
              name: "queue",
              type: "string",
              required: true,
              description: "Queue name."
            },
            {
              name: "payload",
              type: "ServiceParams",
              required: true,
              description: "Job payload \u2014 the caller declares its own shape.",
              shapedBy: "payload"
            },
            {
              name: "delay",
              type: "number",
              required: false,
              description: "Delay before the job becomes eligible for dequeue, in milliseconds."
            },
            {
              name: "priority",
              type: "number",
              required: false,
              description: "Priority \u2014 higher values are dequeued before lower ones."
            }
          ],
          responseShape: {
            jobId: "string",
            position: "number"
          }
        },
        {
          name: "dequeue",
          description: "Pop the highest-priority pending job whose delay has elapsed, marking it 'processing'; returns null if none is ready.",
          params: [
            {
              name: "queue",
              type: "string",
              required: true,
              description: "Queue name."
            }
          ],
          responseShape: {
            job: {
              type: "QueueJob | null",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                payload: {
                  type: "ServiceParams",
                  required: true,
                  description: "Whatever `enqueue` was given \u2014 the caller declares its own shape.",
                  shapedBy: "payload"
                },
                enqueuedAt: {
                  type: "number",
                  required: true
                },
                priority: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "status",
          description: "Look up a job's current status by id.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id from enqueue."
            }
          ],
          responseShape: {
            status: "'pending' | 'processing' | 'completed' | 'failed'",
            job: {
              type: "QueueJob | null",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                payload: {
                  type: "ServiceParams",
                  required: true,
                  description: "Whatever `enqueue` was given \u2014 the caller declares its own shape.",
                  shapedBy: "payload"
                },
                enqueuedAt: {
                  type: "number",
                  required: true
                },
                priority: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "complete",
          description: "Mark a 'processing' job completed and store its result; no-op (returns false) if the job isn't in 'processing' state.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id."
            },
            {
              name: "result",
              type: "ServiceParams",
              required: false,
              description: "Result payload to attach to the job \u2014 the caller declares its own shape (not necessarily the same shape as the job's `payload`).",
              shapedBy: "result"
            }
          ],
          responseShape: {
            completed: "boolean"
          }
        },
        {
          name: "fail",
          description: "Mark a 'processing' job failed; no-op (returns false) if the job isn't in 'processing' state.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id."
            },
            {
              name: "error",
              type: "string",
              required: false,
              description: "Failure reason to attach to the job."
            }
          ],
          responseShape: {
            failed: "boolean"
          }
        },
        {
          name: "cancel",
          description: "Remove a 'pending' job from its queue; no-op (returns false) once it has been dequeued.",
          params: [
            {
              name: "jobId",
              type: "string",
              required: true,
              description: "Job id."
            }
          ],
          responseShape: {
            cancelled: "boolean"
          }
        },
        {
          name: "size",
          description: "Count pending and processing jobs in a queue.",
          params: [
            {
              name: "queue",
              type: "string",
              required: true,
              description: "Queue name."
            }
          ],
          responseShape: {
            size: "number",
            pending: "number",
            processing: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    redis: {
      name: "redis",
      description: "Redis \u2014 cache get/set/delete, pub/sub",
      category: "infrastructure",
      actions: [
        {
          name: "get",
          description: "Read a key's value from the in-memory cache; returns null (as `value`) if absent or expired.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            }
          ],
          responseShape: {
            value: {
              type: "ServiceParams",
              shapedBy: "value"
            }
          }
        },
        {
          name: "set",
          description: "Write a key's value, optionally with an expiry.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            },
            {
              name: "value",
              type: "ServiceParams",
              required: true,
              description: "Value to store \u2014 the caller declares its own shape.",
              shapedBy: "value"
            },
            {
              name: "ttl",
              type: "number",
              required: false,
              description: "Time-to-live in seconds; omit for no expiry."
            }
          ],
          responseShape: {
            ok: "boolean"
          }
        },
        {
          name: "delete",
          description: "Delete a key.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "lock",
          description: "Acquire a mutex on a key; fails (returns acquired:false) if another lock on the key is still alive.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Lock key."
            },
            {
              name: "ttl",
              type: "number",
              required: false,
              description: "Lock time-to-live in milliseconds; defaults to 30000 (30s)."
            }
          ],
          responseShape: {
            acquired: "boolean",
            lockId: "string"
          }
        },
        {
          name: "unlock",
          description: "Release a lock; only succeeds when `lockId` matches the current holder.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Lock key."
            },
            {
              name: "lockId",
              type: "string",
              required: true,
              description: "Lock id returned by the corresponding `lock` call."
            }
          ],
          responseShape: {
            released: "boolean"
          }
        },
        {
          name: "increment",
          description: "Atomically add to a numeric key's value (creating it at 0 if absent), preserving any existing TTL.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            },
            {
              name: "by",
              type: "number",
              required: false,
              description: "Amount to add; defaults to 1 (can be negative to decrement)."
            }
          ],
          responseShape: {
            value: "number"
          }
        },
        {
          name: "expire",
          description: "Set (or reset) a key's expiry; no-op (returns set:false) if the key doesn't exist or is already expired.",
          params: [
            {
              name: "key",
              type: "string",
              required: true,
              description: "Cache key."
            },
            {
              name: "ttl",
              type: "number",
              required: true,
              description: "New time-to-live in seconds."
            }
          ],
          responseShape: {
            set: "boolean"
          }
        },
        {
          name: "publish",
          description: "Publish a message to a channel; delivered synchronously to in-process subscriber callbacks only.",
          params: [
            {
              name: "channel",
              type: "string",
              required: true,
              description: "Channel name."
            },
            {
              name: "message",
              type: "ServiceParams",
              required: true,
              description: "Message payload \u2014 the caller declares its own shape.",
              shapedBy: "message"
            }
          ],
          responseShape: {
            receivers: "number"
          }
        },
        {
          name: "subscribe",
          description: "Register interest in a channel; does not itself wire a callback (that happens at a higher-level API).",
          params: [
            {
              name: "channel",
              type: "string",
              required: true,
              description: "Channel name."
            }
          ],
          responseShape: {
            subscribed: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    oauth: {
      name: "oauth",
      description: "OAuth/OIDC \u2014 authorization-code + PKCE flow, token management, userinfo",
      category: "auth",
      actions: [
        {
          name: "authorize",
          description: "Build the provider authorization URL (PKCE + state) for an OAuth/OIDC login redirect; OIDC discovery (Google) or static OAuth 2 metadata (GitHub) when OAUTH_CLIENT_ID/SECRET are configured, an in-memory mock otherwise.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Identity provider; OIDC issuer defaults to Google unless OIDC_ISSUER_URL overrides it; GitHub uses its OAuth 2 endpoints."
            },
            {
              name: "scopes",
              type: "string[]",
              required: true,
              description: "OAuth scopes to request."
            },
            {
              name: "redirectUri",
              type: "string",
              required: true,
              description: "Callback URL the provider redirects back to after consent."
            },
            {
              name: "subject",
              type: "string",
              required: false,
              description: "Opaque id of whoever started the flow (the verified user); `token` returns it so the host can refuse anyone else completing."
            }
          ],
          responseShape: {
            authUrl: "string",
            state: "string"
          }
        },
        {
          name: "token",
          description: "Exchange an authorization code for tokens, consuming the one-time-use `state` from the matching `authorize` call.",
          params: [
            {
              name: "code",
              type: "string",
              required: true,
              description: "Authorization code from the provider's redirect callback."
            },
            {
              name: "state",
              type: "string",
              required: true,
              description: "The `state` value returned by `authorize`."
            },
            {
              name: "subject",
              type: "string",
              required: false,
              description: "Who is completing; must equal the `subject` bound at `authorize`, checked before the code is exchanged."
            }
          ],
          responseShape: {
            accessToken: "string",
            refreshToken: "string",
            expiresIn: "number",
            tokenType: "'bearer'",
            scope: "string[]",
            subject: "string"
          }
        },
        {
          name: "refresh",
          description: "Exchange a refresh token for a new access token.",
          params: [
            {
              name: "refreshToken",
              type: "string",
              required: true,
              description: "Refresh token from a prior `token` call."
            }
          ],
          responseShape: {
            accessToken: "string",
            expiresIn: "number"
          }
        },
        {
          name: "revoke",
          description: "Revoke an access or refresh token.",
          params: [
            {
              name: "token",
              type: "string",
              required: true,
              description: "The access or refresh token to revoke."
            }
          ],
          responseShape: {
            revoked: "boolean"
          }
        },
        {
          name: "userinfo",
          description: "Fetch the authenticated user's profile claims for an access token.",
          params: [
            {
              name: "accessToken",
              type: "string",
              required: true,
              description: "Access token from a prior `token`/`refresh` call."
            }
          ],
          responseShape: {
            sub: "string",
            email: "string",
            name: "string",
            picture: "string"
          }
        },
        {
          name: "connect",
          description: "Complete an authorization for the signed-in caller and keep the grant server-side in the host's connection store; the tokens never return to the program.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Provider the authorization was started with."
            },
            {
              name: "code",
              type: "string",
              required: true,
              description: "Authorization code from the provider's redirect."
            },
            {
              name: "state",
              type: "string",
              required: true,
              description: "The `state` returned by `authorize`; it must have been bound to the same caller."
            }
          ],
          responseShape: {}
        },
        {
          name: "connection",
          description: "The signed-in caller's stored connection to a provider.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Provider to report on."
            }
          ],
          responseShape: {}
        },
        {
          name: "disconnect",
          description: "Forget the signed-in caller's stored connection to a provider.",
          params: [
            {
              name: "provider",
              type: "'google' | 'github' | 'auth0'",
              required: true,
              description: "Provider to disconnect."
            }
          ],
          responseShape: {}
        }
      ],
      runtimes: [
        "node"
      ]
    },
    credentials: {
      name: "credentials",
      description: "Tenant credential store \u2014 masked list, set/remove, test connection (settings-page backend)",
      category: "auth",
      actions: [
        {
          name: "list",
          description: "List declared credentials and their masked (last-4) configuration status, plus the integrations mode and each service's status; open to any caller.",
          params: [
            {
              name: "service",
              type: "string",
              required: false,
              description: "Restrict the listing to one service's declared credentials; omit for all services."
            }
          ],
          responseShape: {
            enabled: "boolean",
            mode: "'mock' | 'real'",
            services: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the service name."
                  },
                  service: {
                    type: "string",
                    required: true
                  },
                  configured: {
                    type: "boolean",
                    required: true,
                    description: "The service has a working configuration on this host."
                  },
                  mocked: {
                    type: "boolean",
                    required: true,
                    description: "The service answers with mock data (mock mode)."
                  },
                  status: {
                    type: "'real' | 'emulated' | 'mocked' | 'not configured'",
                    required: true,
                    description: "One word for what a call to this service does now; `emulated` runs on the local Firebase emulator."
                  },
                  backend: {
                    type: "'production' | 'simulated' | 'stub'",
                    required: true
                  },
                  needs: {
                    type: "string",
                    required: true,
                    description: "What a not-configured service is missing (env var names); empty otherwise."
                  }
                }
              }
            },
            entries: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: `<service>/<envVar>`."
                  },
                  service: {
                    type: "string",
                    required: true
                  },
                  envVar: {
                    type: "string",
                    required: true
                  },
                  required: {
                    type: "boolean",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  configured: {
                    type: "boolean",
                    required: true
                  },
                  source: {
                    type: "'store' | 'env' | 'none'",
                    required: true
                  },
                  last4: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "set",
          description: "Save a credential value into the hosted store; the env var must be one declared for the service (or a well-formed connection-ref name for `database`). Admin/owner role required.",
          params: [
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration name the credential belongs to."
            },
            {
              name: "envVar",
              type: "string",
              required: true,
              description: "Declared env-var name being set."
            },
            {
              name: "value",
              type: "string",
              required: true,
              description: "Secret value to store; never logged."
            }
          ],
          responseShape: {
            saved: "boolean",
            service: "string",
            envVar: "string",
            last4: "string"
          }
        },
        {
          name: "remove",
          description: "Delete a stored credential from the hosted store. Admin/owner role required.",
          params: [
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration name the credential belongs to."
            },
            {
              name: "envVar",
              type: "string",
              required: true,
              description: "Env-var name to remove."
            }
          ],
          responseShape: {
            removed: "boolean"
          }
        },
        {
          name: "test",
          description: "Check whether a service's required credentials are present and, if a live probe is declared for it, exercise the service to confirm they actually work. Admin/owner role required.",
          params: [
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration name to test."
            }
          ],
          responseShape: {
            service: "string",
            configured: "boolean",
            missing: "string[]",
            probed: "boolean",
            ok: "boolean",
            message: "string"
          }
        },
        {
          name: "mode",
          description: "Switch every integration between mock data and its real backend, live. Mock is refused under NODE_ENV=production. Admin/owner role required.",
          params: [
            {
              name: "mode",
              type: "'mock' | 'real'",
              required: true,
              description: "Target integrations mode."
            }
          ],
          responseShape: {
            mode: "'mock' | 'real'"
          }
        },
        {
          name: "rotate",
          description: "Re-encrypt all stored credentials under the current master key (ALMADAR_CREDENTIAL_MASTER_KEY), retiring ALMADAR_CREDENTIAL_MASTER_KEY_PREVIOUS. Admin/owner role required.",
          params: [],
          responseShape: {
            rotated: "number",
            alreadyCurrent: "number",
            unreadable: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    otel: {
      name: "otel",
      description: "OpenTelemetry \u2014 spans, metrics, logs",
      category: "infrastructure",
      actions: [
        {
          name: "startSpan",
          description: "Start a trace span (in-memory backend \u2014 no OpenTelemetry SDK/exporter).",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Span name."
            },
            {
              name: "attributes",
              type: "object",
              required: false,
              description: "Key-value attributes attached to the span (OTel attribute values are scalar).",
              mapValue: {
                type: "string | number | boolean"
              }
            },
            {
              name: "traceId",
              type: "string",
              required: false,
              description: "Existing trace id to attach this span to; a new one is generated when omitted."
            }
          ],
          responseShape: {
            spanId: "string",
            traceId: "string"
          }
        },
        {
          name: "endSpan",
          description: "Mark a span ended, optionally recording its final status; no-op (returns ended:false) for an unknown span id.",
          params: [
            {
              name: "spanId",
              type: "string",
              required: true,
              description: "Span id from startSpan."
            },
            {
              name: "status",
              type: "'ok' | 'error'",
              required: false,
              description: "Final span status."
            }
          ],
          responseShape: {
            ended: "boolean"
          }
        },
        {
          name: "addEvent",
          description: "Attach a timestamped event to an open span; no-op (returns added:false) for an unknown span id.",
          params: [
            {
              name: "spanId",
              type: "string",
              required: true,
              description: "Span id from startSpan."
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: "Event name."
            },
            {
              name: "attributes",
              type: "object",
              required: false,
              description: "Key-value attributes attached to the event (OTel attribute values are scalar).",
              mapValue: {
                type: "string | number | boolean"
              }
            }
          ],
          responseShape: {
            added: "boolean"
          }
        },
        {
          name: "recordMetric",
          description: "Record a metric sample: counters accumulate, gauges overwrite, histograms append to the sample list.",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Metric name."
            },
            {
              name: "value",
              type: "number",
              required: true,
              description: "Sample value."
            },
            {
              name: "type",
              type: "'counter' | 'gauge' | 'histogram'",
              required: false,
              description: "Aggregation kind; defaults to 'counter'."
            },
            {
              name: "labels",
              type: "object",
              required: false,
              description: "Key-value labels attached to the metric (replaces prior labels on the same name; OTel label values are scalar).",
              mapValue: {
                type: "string | number | boolean"
              }
            }
          ],
          responseShape: {
            recorded: "boolean"
          }
        },
        {
          name: "getSpan",
          description: "Fetch a stored span's full record by id; null if unknown.",
          params: [
            {
              name: "spanId",
              type: "string",
              required: true,
              description: "Span id from startSpan."
            }
          ],
          responseShape: {}
        },
        {
          name: "getMetrics",
          description: "Fetch all recorded metrics, keyed by metric name.",
          params: [],
          responseShape: {}
        }
      ],
      runtimes: [
        "node"
      ]
    },
    cli: {
      name: "cli",
      description: "Almadar CLI integration - schema validation and compilation",
      category: "devtools",
      actions: [
        {
          name: "validate",
          description: "Validate an orbital schema by writing it to a temp file and shelling out to `npx",
          params: [
            {
              name: "schema",
              type: "string",
              required: true,
              description: "Raw .orb schema content to validate."
            }
          ],
          responseShape: {
            valid: "boolean",
            errors: "string[]",
            warnings: "string[]",
            summary: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    workspace: {
      name: "workspace",
      description: "Workspace - a project's files and their git history through the host's workspace resolver",
      category: "devtools",
      actions: [
        {
          name: "tree",
          description: "List the workspace's files and directories under a path.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id (the app the files belong to)."
            },
            {
              name: "path",
              type: "string",
              required: false,
              description: "Workspace-relative directory to list; the root when omitted."
            }
          ],
          responseShape: {
            nodes: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the workspace-relative path."
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  type: {
                    type: "'file' | 'directory'",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "readFile",
          description: "Read one workspace file.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "Workspace-relative file path."
            }
          ],
          responseShape: {
            path: "string",
            exists: "boolean",
            content: "string"
          }
        },
        {
          name: "writeFile",
          description: "Write one workspace file (read-only mounts refuse).",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "Workspace-relative file path."
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: "New file text."
            }
          ],
          responseShape: {
            path: "string",
            written: "boolean"
          }
        },
        {
          name: "removeFile",
          description: "Delete one workspace file; deleting an absent file is not an error.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: "Workspace-relative file path."
            }
          ],
          responseShape: {
            path: "string",
            removed: "boolean"
          }
        },
        {
          name: "snapshot",
          description: "Commit the current app files as a snapshot.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "reason",
              type: "string",
              required: true,
              description: "Why the snapshot is taken (the commit subject)."
            }
          ],
          responseShape: {
            committed: "boolean",
            oid: "string"
          }
        },
        {
          name: "history",
          description: "The app's snapshots, newest first, and whether undo/redo can step.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Most snapshots to list; all when omitted."
            }
          ],
          responseShape: {
            snapshots: {
              type: "WorkspaceSnapshotRow[]",
              items: {
                type: "WorkspaceSnapshotRow",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Commit oid."
                  },
                  timestamp: {
                    type: "number",
                    required: true,
                    description: "Commit time, epoch ms."
                  },
                  reason: {
                    type: "string",
                    required: true,
                    description: "The commit subject."
                  },
                  kind: {
                    type: "'user' | 'agent' | 'restore' | 'undo' | 'redo' | 'snapshot' | 'merge' | ''",
                    required: true,
                    description: "What made the commit; empty on commits written before kinds were stamped."
                  },
                  target: {
                    type: "string",
                    required: true,
                    description: "A restore, undo or redo commit: the commit it acted on; empty otherwise."
                  }
                }
              }
            },
            canUndo: "boolean",
            canRedo: "boolean"
          }
        },
        {
          name: "changes",
          description: "The app files one snapshot changed, with their text before and after.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "oid",
              type: "string",
              required: true,
              description: "Commit oid."
            }
          ],
          responseShape: {
            changes: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the file path."
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  before: {
                    type: "string",
                    required: true,
                    description: "Text before the commit; empty when `added`."
                  },
                  after: {
                    type: "string",
                    required: true,
                    description: "Text after the commit; empty when `removed`."
                  },
                  change: {
                    type: "'added' | 'removed' | 'modified'",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "restore",
          description: "Make the app what it was at a snapshot, as a new commit.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            },
            {
              name: "oid",
              type: "string",
              required: true,
              description: "Commit oid to restore."
            }
          ],
          responseShape: {
            changed: "boolean",
            oid: "string"
          }
        },
        {
          name: "undo",
          description: "Step back the newest change not already undone, as a new commit.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            }
          ],
          responseShape: {
            changed: "boolean",
            oid: "string"
          }
        },
        {
          name: "redo",
          description: "Re-apply the newest undo not already redone, as a new commit.",
          params: [
            {
              name: "workspace",
              type: "string",
              required: true,
              description: "Workspace id."
            }
          ],
          responseShape: {
            changed: "boolean",
            oid: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    apikeys: {
      name: "apikeys",
      description: "API keys - mint bearer keys an app issues, hashed with the salt the server verifies them by",
      category: "auth",
      actions: [
        {
          name: "mint",
          description: "Mint a new API key. The plaintext is returned once for its owner to copy; store only the hash.",
          params: [],
          responseShape: {
            plaintext: "string",
            keyHash: "string",
            lastFour: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    hosting: {
      name: "hosting",
      description: "Hosting - publish an app as <handle>/<name>, serve it, roll back, read its topology and logs (implemented by @almadar-io/hosting)",
      category: "infrastructure",
      actions: [
        {
          name: "deploy",
          description: "Publish an app: run the pre-deploy gate, make it its handle's next package version and serve it at `<handle>/<name>`. Streams one progress message per step.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: "The app (workspace) to publish."
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: "The published name, lowercase kebab-case."
            },
            {
              name: "description",
              type: "string",
              required: true,
              description: "What the app is."
            },
            {
              name: "tags",
              type: "string[]",
              required: true,
              description: "Catalog tags."
            },
            {
              name: "teamId",
              type: "string",
              required: false,
              description: "Publish as this team (owner or admin only); as the caller when omitted."
            },
            {
              name: "private",
              type: "boolean",
              required: false,
              description: "A paid team's app served to its members only."
            }
          ],
          responseShape: {
            ok: "boolean",
            failedStep: "string",
            findings: "string[]",
            version: "number",
            uses: "string",
            packageVersion: "string",
            liveUrl: "string"
          }
        },
        {
          name: "status",
          description: "What an app is published as, the version served, its history and its masked credentials.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: "The app."
            }
          ],
          responseShape: {
            published: "boolean",
            handle: "string",
            name: "string",
            teamOnly: "boolean",
            liveUrl: "string",
            hasActive: "boolean",
            active: {
              type: "HostingDeploymentRow",
              properties: {
                id: {
                  type: "string",
                  required: true,
                  description: "Row id: the version as text."
                },
                version: {
                  type: "number",
                  required: true
                },
                commit: {
                  type: "string",
                  required: true,
                  description: "The workspace commit deployed."
                },
                target: {
                  type: "'shared-host' | 'own-cloud'",
                  required: true
                },
                packageVersion: {
                  type: "string",
                  required: true,
                  description: "The handle package version a shared-host deployment serves; empty for own-cloud."
                },
                publishedBy: {
                  type: "string",
                  required: true,
                  description: "Who published it; empty when unrecorded."
                },
                recordedAt: {
                  type: "number",
                  required: true,
                  description: "When it was recorded, epoch ms; 0 when unrecorded."
                }
              }
            },
            history: {
              type: "HostingDeploymentRow[]",
              items: {
                type: "HostingDeploymentRow",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the version as text."
                  },
                  version: {
                    type: "number",
                    required: true
                  },
                  commit: {
                    type: "string",
                    required: true,
                    description: "The workspace commit deployed."
                  },
                  target: {
                    type: "'shared-host' | 'own-cloud'",
                    required: true
                  },
                  packageVersion: {
                    type: "string",
                    required: true,
                    description: "The handle package version a shared-host deployment serves; empty for own-cloud."
                  },
                  publishedBy: {
                    type: "string",
                    required: true,
                    description: "Who published it; empty when unrecorded."
                  },
                  recordedAt: {
                    type: "number",
                    required: true,
                    description: "When it was recorded, epoch ms; 0 when unrecorded."
                  }
                }
              }
            },
            credentials: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: `<service>/<envVar>`."
                  },
                  service: {
                    type: "string",
                    required: true
                  },
                  envVar: {
                    type: "string",
                    required: true
                  },
                  last4: {
                    type: "string",
                    required: true
                  },
                  updatedAt: {
                    type: "number",
                    required: true,
                    description: "Epoch ms."
                  }
                }
              }
            },
            credentialsEnabled: "boolean"
          }
        },
        {
          name: "setCredential",
          description: "Store one of a published app's own credentials.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "service",
              type: "string",
              required: true,
              description: "Integration the credential belongs to."
            },
            {
              name: "envVar",
              type: "string",
              required: true,
              description: "Declared env var."
            },
            {
              name: "value",
              type: "string",
              required: true,
              description: "Secret value; never logged."
            }
          ],
          responseShape: {
            saved: "boolean",
            last4: "string"
          }
        },
        {
          name: "rollback",
          description: "Serve an earlier deployment version again.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "version",
              type: "number",
              required: true,
              description: "The deployment version to serve."
            }
          ],
          responseShape: {
            hasActive: "boolean",
            active: {
              type: "HostingDeploymentRow",
              properties: {
                id: {
                  type: "string",
                  required: true,
                  description: "Row id: the version as text."
                },
                version: {
                  type: "number",
                  required: true
                },
                commit: {
                  type: "string",
                  required: true,
                  description: "The workspace commit deployed."
                },
                target: {
                  type: "'shared-host' | 'own-cloud'",
                  required: true
                },
                packageVersion: {
                  type: "string",
                  required: true,
                  description: "The handle package version a shared-host deployment serves; empty for own-cloud."
                },
                publishedBy: {
                  type: "string",
                  required: true,
                  description: "Who published it; empty when unrecorded."
                },
                recordedAt: {
                  type: "number",
                  required: true,
                  description: "When it was recorded, epoch ms; 0 when unrecorded."
                }
              }
            }
          }
        },
        {
          name: "topology",
          description: "The published app's clients, host, services and stores, with the calls observed between them.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "sinceMs",
              type: "number",
              required: false,
              description: "Window start, epoch ms; the last 24 hours when omitted."
            }
          ],
          responseShape: {
            sinceMs: "number",
            units: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  column: {
                    type: "'client' | 'host' | 'service' | 'store' | 'external'",
                    required: true
                  },
                  label: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "'ok' | 'degraded' | 'down' | 'idle'",
                    required: true
                  },
                  metric: {
                    type: "string",
                    required: true,
                    description: "Empty when the unit reports none."
                  }
                }
              }
            },
            edges: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  source: {
                    type: "string",
                    required: true
                  },
                  target: {
                    type: "string",
                    required: true
                  },
                  label: {
                    type: "string",
                    required: true
                  },
                  status: {
                    type: "'ok' | 'degraded' | 'down' | 'idle'",
                    required: true
                  },
                  calls: {
                    type: "number",
                    required: true
                  },
                  errors: {
                    type: "number",
                    required: true
                  },
                  p50Ms: {
                    type: "number",
                    required: true,
                    description: "Median latency in ms; 0 when unmeasured."
                  }
                }
              }
            },
            dashboardUrl: "string"
          }
        },
        {
          name: "logs",
          description: "One topology unit's log tail.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "unit",
              type: "string",
              required: true,
              description: "Topology unit id."
            },
            {
              name: "sinceMs",
              type: "number",
              required: false,
              description: "Window start, epoch ms; the last 24 hours when omitted."
            }
          ],
          responseShape: {
            lines: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the line's position in the tail."
                  },
                  time: {
                    type: "string",
                    required: true
                  },
                  severity: {
                    type: "string",
                    required: true
                  },
                  message: {
                    type: "string",
                    required: true
                  },
                  op: {
                    type: "string",
                    required: true,
                    description: "Empty when the line is not a call."
                  },
                  ok: {
                    type: "boolean",
                    required: true,
                    description: "Whether the call succeeded; true for a line that is not a call."
                  },
                  durationMs: {
                    type: "number",
                    required: true,
                    description: "0 when unmeasured."
                  },
                  error: {
                    type: "string",
                    required: true,
                    description: "Empty when none."
                  }
                }
              }
            }
          }
        },
        {
          name: "export",
          description: "Build the app for its owner's own cloud (compiled code + Terraform) behind the same gate; the archive is stored and offered by URL.",
          params: [
            {
              name: "appId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            ok: "boolean",
            failedStep: "string",
            findings: "string[]",
            url: "string",
            filename: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    registry: {
      name: "registry",
      description: "Registry - handles and the behavior packages published under them (implemented by @almadar-io/hosting)",
      category: "devtools",
      actions: [
        {
          name: "handles",
          description: "The caller's handle and each of their teams' handles.",
          params: [],
          responseShape: {
            handle: "string",
            teams: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the team id."
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  handle: {
                    type: "string",
                    required: true,
                    description: "Empty when the team has not claimed one."
                  }
                }
              }
            }
          }
        },
        {
          name: "claimHandle",
          description: "Claim a handle for the caller or one of their teams (owner or admin).",
          params: [
            {
              name: "handle",
              type: "string",
              required: true,
              description: "The handle to claim."
            },
            {
              name: "teamId",
              type: "string",
              required: false,
              description: "Claim for this team; for the caller when omitted."
            }
          ],
          responseShape: {
            claimed: "boolean",
            refusal: "'' | 'invalid' | 'reserved' | 'taken' | 'kept' | 'forbidden'",
            message: "string"
          }
        },
        {
          name: "packages",
          description: "The latest version of the caller's handle package and the behaviors in it.",
          params: [],
          responseShape: {
            handle: "string",
            version: "string",
            behaviors: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the behavior name."
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  uses: {
                    type: "string",
                    required: true,
                    description: "The import string, `<handle>/<name>`."
                  }
                }
              }
            }
          }
        },
        {
          name: "publishBehavior",
          description: "Offer a behavior to the community library (reviewed as a pull request).",
          params: [
            {
              name: "name",
              type: "string",
              required: true,
              description: "Behavior name, lowercase kebab-case."
            },
            {
              name: "description",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "visibility",
              type: "'public' | 'private'",
              required: true,
              description: ""
            },
            {
              name: "program",
              type: "string",
              required: true,
              description: "The behavior's program as `.orb` JSON text."
            }
          ],
          responseShape: {
            name: "string",
            prUrl: "string"
          }
        },
        {
          name: "teamBehaviors",
          description: "A team's behaviors (team owner or admin).",
          params: [
            {
              name: "teamId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            behaviors: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true,
                    description: "Row id: the behavior name."
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  handle: {
                    type: "string",
                    required: true
                  },
                  version: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  tags: {
                    type: "string[]",
                    required: true
                  },
                  enabled: {
                    type: "boolean",
                    required: true
                  },
                  createdAt: {
                    type: "number",
                    required: true,
                    description: "Epoch ms."
                  },
                  createdBy: {
                    type: "string",
                    required: true
                  },
                  orbitalCount: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "setTeamBehaviorEnabled",
          description: "Enable or disable one of a team's behaviors for its members (team owner or admin).",
          params: [
            {
              name: "teamId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "enabled",
              type: "boolean",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            found: "boolean",
            enabled: "boolean"
          }
        },
        {
          name: "removeTeamBehavior",
          description: "Remove one of a team's behaviors (team owner or admin).",
          params: [
            {
              name: "teamId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            removed: "boolean"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    deepagent: {
      name: "deepagent",
      description: "AI code generation agent with tool use - generates schemas, code, and manages projects",
      category: "ai",
      actions: [
        {
          name: "sendMessage",
          description: "Send a chat message to the DeepAgent server (POST /api/agent/message), continuing an existing thread or starting one.",
          params: [
            {
              name: "message",
              type: "string",
              required: true,
              description: "Message text."
            },
            {
              name: "threadId",
              type: "string",
              required: false,
              description: "Existing thread id to continue; a new thread starts when omitted."
            },
            {
              name: "skill",
              type: "string",
              required: false,
              description: "Named skill to invoke for this message."
            },
            {
              name: "context",
              type: "IntegrationParams",
              required: false,
              description: "Extra context passed through to the agent \u2014 the caller declares its own shape.",
              shapedBy: "context"
            }
          ],
          responseShape: {
            response: "string",
            threadId: "string"
          }
        },
        {
          name: "cancelGeneration",
          description: "Cancel an in-flight generation on a DeepAgent thread (POST /api/agent/cancel).",
          params: [
            {
              name: "threadId",
              type: "string",
              required: true,
              description: "Thread whose generation to cancel."
            }
          ],
          responseShape: {
            cancelled: "boolean"
          }
        },
        {
          name: "validateSchema",
          description: "Validate a schema via the DeepAgent server (POST /api/schema/validate).",
          params: [
            {
              name: "schema",
              type: "string",
              required: true,
              description: "Schema content to validate."
            }
          ],
          responseShape: {
            valid: "boolean",
            errors: "string[]"
          }
        },
        {
          name: "compileSchema",
          description: "Compile a schema to a target shell via the DeepAgent server (POST /api/schema/compile).",
          params: [
            {
              name: "schema",
              type: "string",
              required: true,
              description: "Schema content to compile."
            },
            {
              name: "shell",
              type: "string",
              required: false,
              description: "Target codegen shell (e.g. 'typescript')."
            }
          ],
          responseShape: {
            output: "string",
            files: "string[]"
          }
        },
        {
          name: "getThreadHistory",
          description: "Fetch a thread's message history via the DeepAgent server (POST /api/agent/history).",
          params: [
            {
              name: "threadId",
              type: "string",
              required: true,
              description: "Thread id."
            }
          ],
          responseShape: {
            messages: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  role: {
                    type: "string",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  },
                  timestamp: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    database: {
      name: "database",
      description: "Generic SQL database integration - read-only queries over external connections",
      category: "infrastructure",
      actions: [
        {
          name: "query",
          description: "Run a single read-only SELECT against a Postgres connection (write statements and multi-statement SQL are rejected before execution).",
          params: [
            {
              name: "connectionRef",
              type: "string",
              required: true,
              description: "Env-var NAME whose value is the Postgres connection string \u2014 never the secret itself; resolved from the credential store/environment at run time."
            },
            {
              name: "sql",
              type: "string",
              required: true,
              description: "SQL text; must be exactly one SELECT statement."
            },
            {
              name: "params",
              type: "DatabaseQueryParamValue[]",
              required: false,
              description: "Positional bind parameters for the query."
            }
          ],
          responseShape: {
            rows: {
              type: "DatabaseRow[]",
              shapedBy: "rows"
            },
            rowCount: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    wikimedia: {
      name: "wikimedia",
      description: "Wikipedia API \u2014 redirect-resolved page lookup (description, portrait, lead extract)",
      category: "reference",
      actions: [
        {
          name: "getPage",
          description: "Look up a Wikipedia page (redirect-resolved): a one-line description, a Commons thumbnail, and the lead-section extract; empty fields on a miss.",
          params: [
            {
              name: "title",
              type: "string",
              required: true,
              description: "Page title to look up."
            }
          ],
          responseShape: {
            title: "string",
            description: "string",
            portraitUrl: "string",
            extract: "string"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    iconify: {
      name: "iconify",
      description: "Iconify API \u2014 brand-logo / icon resolution, search, and body fetch",
      category: "reference",
      actions: [
        {
          name: "svgExists",
          description: "Check whether an icon exists at the given Iconify API path (a GET, not a HEAD, despite the name).",
          params: [
            {
              name: "path",
              type: "string",
              required: true,
              description: "Iconify API path segment to probe, e.g. 'prefix/name.svg'."
            }
          ],
          responseShape: {
            exists: "boolean"
          }
        },
        {
          name: "search",
          description: "Search Iconify's icon sets by relevance.",
          params: [
            {
              name: "query",
              type: "string",
              required: true,
              description: "Search query text."
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Maximum icon ids to return; defaults to 1."
            }
          ],
          responseShape: {
            icons: "string[]"
          }
        },
        {
          name: "getIconBody",
          description: "Fetch an icon's raw SVG body (for color-tone classification); null if the icon or its collection isn't found.",
          params: [
            {
              name: "iconId",
              type: "string",
              required: true,
              description: 'Icon id in "prefix:name" form.'
            }
          ],
          responseShape: {
            body: "string | null"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    arxiv: {
      name: "arxiv",
      description: "arXiv API \u2014 frontier academic preprint search (emerging-axis research backend)",
      category: "reference",
      actions: [
        {
          name: "search",
          description: "Search arXiv's public Atom API by relevance.",
          params: [
            {
              name: "query",
              type: "string",
              required: true,
              description: "Search query text (matched across all fields)."
            },
            {
              name: "maxResults",
              type: "number",
              required: false,
              description: "Maximum results to return; defaults to 8."
            }
          ],
          responseShape: {
            results: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  summary: {
                    type: "string",
                    required: true
                  },
                  authors: {
                    type: "string[]",
                    required: true
                  },
                  published: {
                    type: "string",
                    required: true
                  },
                  url: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    rigger: {
      name: "rigger",
      description: "Local rigger service (FastAPI) \u2014 mesh rigging build + render",
      category: "media",
      actions: [
        {
          name: "health",
          description: "Liveness probe of the local rigger service.",
          params: [],
          responseShape: {
            ok: "boolean"
          }
        },
        {
          name: "buildMesh",
          description: "Build a rigged mesh bundle from a source image via the local rigger service.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            }
          ],
          responseShape: {
            bundlePath: "string",
            boneCount: "number",
            vertexCount: "number",
            triangleCount: "number",
            clips: "string[]"
          }
        },
        {
          name: "render",
          description: "Render a rigged mesh bundle to output files via the local rigger service.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "preset",
              type: "string",
              required: true,
              description: "Render preset name."
            },
            {
              name: "outDir",
              type: "string",
              required: true,
              description: "Output directory for rendered files."
            },
            {
              name: "mode",
              type: "string",
              required: false,
              description: "Render path: 'mesh' (skinned, deforming) or 'cutout' (rigid per-part layers). Defaults to 'mesh'."
            },
            {
              name: "supersample",
              type: "number",
              required: false,
              description: "Supersampling factor for the cutout path. Defaults to 1."
            }
          ],
          responseShape: {
            outdir: "string",
            frames: "number",
            framePaths: "string[]",
            sheet: "string",
            gif: "string",
            atlas: "string"
          }
        },
        {
          name: "listPresets",
          description: "List the available animation presets (name, fps, frame count, bone roles) from the local rigger service.",
          params: [],
          responseShape: {
            presets: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  fps: {
                    type: "number",
                    required: true
                  },
                  frames: {
                    type: "number",
                    required: true
                  },
                  bones: {
                    type: "object[]",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "loadRig",
          description: "Load a rig file, resolving `cloneOf` parts to concrete box/pivot geometry.",
          params: [
            {
              name: "rigPath",
              type: "string",
              required: true,
              description: "Path to the rig JSON file."
            }
          ],
          responseShape: {
            rig: "object",
            resolved: "object"
          }
        },
        {
          name: "saveRig",
          description: "Save a rig file; the server validates it and rejects a malformed part.",
          params: [
            {
              name: "rigPath",
              type: "string",
              required: true,
              description: "Path to write the rig JSON file."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            }
          ],
          responseShape: {
            rigPath: "string",
            parts: "number"
          }
        },
        {
          name: "ownership",
          description: "Render the per-part pixel ownership map for a rig over a source image.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "outPath",
              type: "string",
              required: true,
              description: "Path to write the ownership-map PNG."
            }
          ],
          responseShape: {
            outPath: "string",
            legend: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  color: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "buildLayers",
          description: "Build per-part cutout layer images (name, box, z-order, pivot) from a source image and rig.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "outDir",
              type: "string",
              required: true,
              description: "Output directory for the layer images."
            }
          ],
          responseShape: {
            layers: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    required: true
                  },
                  path: {
                    type: "string",
                    required: true
                  },
                  x: {
                    type: "number",
                    required: true
                  },
                  y: {
                    type: "number",
                    required: true
                  },
                  w: {
                    type: "number",
                    required: true
                  },
                  h: {
                    type: "number",
                    required: true
                  },
                  z: {
                    type: "number",
                    required: true
                  },
                  pivot: {
                    type: "[number, number]",
                    required: true
                  },
                  isRest: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "pose",
          description: "Sample a pose/animation preset into per-frame, per-part transforms for a rig.",
          params: [
            {
              name: "imagePath",
              type: "string",
              required: true,
              description: "Path to the source image."
            },
            {
              name: "rig",
              type: "object",
              required: true,
              description: "The rig definition (parts, boxes, pivots, \u2026) as JSON."
            },
            {
              name: "roles",
              type: "object",
              required: true,
              description: "Map of part name to preset bone role.",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "preset",
              type: "string",
              required: false,
              description: "Animation preset name."
            },
            {
              name: "overrides",
              type: "object",
              required: false,
              description: "Per-part motion overrides applied over the preset."
            }
          ],
          responseShape: {
            fps: "number",
            frames: {
              type: "array",
              items: {
                type: "object",
                mapValue: {
                  type: "object",
                  properties: {
                    angle: {
                      type: "number",
                      required: true
                    },
                    bx: {
                      type: "number",
                      required: true
                    },
                    by: {
                      type: "number",
                      required: true
                    }
                  }
                }
              }
            }
          }
        },
        {
          name: "generate",
          description: "Generate one image on the local image server (text-to-image, or a Kontext edit conditioned on `sourcePath`).",
          params: [
            {
              name: "model",
              type: "'z-image-turbo' | 'kontext'",
              required: true,
              description: "Generation model: 'z-image-turbo' (text-to-image) or 'kontext' (edit; requires sourcePath)."
            },
            {
              name: "prompt",
              type: "string",
              required: true,
              description: "The generation/edit prompt."
            },
            {
              name: "seed",
              type: "number",
              required: false,
              description: "Random seed; omit for a fresh one each call."
            },
            {
              name: "size",
              type: "string",
              required: false,
              description: "Output image size, e.g. '1024x1024'. Defaults to '1024x1024'."
            },
            {
              name: "steps",
              type: "number",
              required: false,
              description: "Diffusion step count."
            },
            {
              name: "sourcePath",
              type: "string",
              required: false,
              description: "Path to the source image to edit; REQUIRED for 'kontext'."
            },
            {
              name: "outDir",
              type: "string",
              required: true,
              description: "Output directory the generated PNG is written under."
            }
          ],
          responseShape: {
            outPath: "string",
            model: "string",
            prompt: "string",
            seed: "number | null",
            size: "string",
            sourcePath: "string | null",
            elapsedMs: "number"
          }
        }
      ],
      runtimes: [
        "node"
      ]
    },
    page: {
      name: "page",
      description: "Browser tabs \u2014 open, navigate, query, watch, treat and interact with page elements (generic; selectors are parameters)",
      category: "infrastructure",
      actions: [
        {
          name: "listTabs",
          description: "List open tabs, optionally only those whose URL matches a match pattern.",
          params: [
            {
              name: "match",
              type: "string",
              required: false,
              description: 'URL match pattern, e.g. "https://example.com/*".'
            }
          ],
          responseShape: {
            tabs: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true
                  },
                  url: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  active: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "openTab",
          description: "Open a URL in a new tab.",
          params: [
            {
              name: "url",
              type: "string",
              required: true,
              description: "URL to open."
            },
            {
              name: "params",
              type: "object",
              required: false,
              description: "Query parameters added to the URL, encoded (e.g. a search term).",
              mapValue: {
                type: "string"
              }
            },
            {
              name: "match",
              type: "string",
              required: false,
              description: "URL match pattern of the pages this tab will be read from (query/apply/act). The URL must match\nit, and a browser extension asks for access to these pages."
            },
            {
              name: "active",
              type: "boolean",
              required: true,
              description: "Whether the new tab takes focus."
            }
          ],
          responseShape: {
            tab: "number"
          }
        },
        {
          name: "closeTab",
          description: "Close a tab the program opened.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id from openTab or listTabs."
            }
          ],
          responseShape: {
            closed: "boolean"
          }
        },
        {
          name: "navigate",
          description: "Load a URL in an existing tab.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "url",
              type: "string",
              required: true,
              description: "URL to load."
            }
          ],
          responseShape: {
            url: "string"
          }
        },
        {
          name: "query",
          description: "Read the elements of a tab's page that match a selector, with the requested fields.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "selector",
              type: "string",
              required: true,
              description: "CSS selector of the elements to read."
            },
            {
              name: "fields",
              type: "object",
              required: true,
              description: "Field name to how to read it from each element.",
              mapValue: {
                type: "PageFieldSpec",
                properties: {
                  selector: {
                    type: "string",
                    required: false,
                    description: "Descendant selector within the matched element; absent reads the element itself."
                  },
                  attr: {
                    type: "string",
                    required: false,
                    description: "Attribute to read; absent reads the text content."
                  },
                  resolve: {
                    type: "boolean",
                    required: false,
                    description: "The value is a URL: return it resolved against the page (a relative link becomes absolute)."
                  },
                  template: {
                    type: "string",
                    required: false,
                    description: "Compose the value from other fields of the same element instead of reading it: `{name}` stands\nfor that field's value (a read field, not another template). Empty when any named field is empty.\nExcludes `selector` and `attr`."
                  },
                  required: {
                    type: "boolean",
                    required: false,
                    description: "Leave the element out until this field reads non-empty; a watch reports it once it fills in."
                  }
                }
              }
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Maximum elements to return."
            },
            {
              name: "waitMs",
              type: "number",
              required: false,
              description: "Wait up to this many milliseconds for the page to load and a first match to appear."
            }
          ],
          responseShape: {
            tab: "number",
            elements: {
              type: "PageElement[]",
              items: {
                type: "PageElement",
                properties: {
                  elementId: {
                    type: "string",
                    required: true
                  },
                  fields: {
                    type: "object",
                    required: true,
                    mapValue: {
                      type: "string"
                    }
                  }
                }
              }
            }
          }
        },
        {
          name: "watch",
          description: "Report every element that matches a selector, now and as the page changes, in every tab whose URL\nmatches. Each element arrives once as the program's declared external input `event`, carrying\n`{ tab, elementId, url, fields }`.",
          params: [
            {
              name: "match",
              type: "string",
              required: true,
              description: "URL match pattern of the tabs to watch."
            },
            {
              name: "selector",
              type: "string",
              required: true,
              description: "CSS selector of the elements to report."
            },
            {
              name: "fields",
              type: "object",
              required: true,
              description: "Field name to how to read it from each element.",
              mapValue: {
                type: "PageFieldSpec",
                properties: {
                  selector: {
                    type: "string",
                    required: false,
                    description: "Descendant selector within the matched element; absent reads the element itself."
                  },
                  attr: {
                    type: "string",
                    required: false,
                    description: "Attribute to read; absent reads the text content."
                  },
                  resolve: {
                    type: "boolean",
                    required: false,
                    description: "The value is a URL: return it resolved against the page (a relative link becomes absolute)."
                  },
                  template: {
                    type: "string",
                    required: false,
                    description: "Compose the value from other fields of the same element instead of reading it: `{name}` stands\nfor that field's value (a read field, not another template). Empty when any named field is empty.\nExcludes `selector` and `attr`."
                  },
                  required: {
                    type: "boolean",
                    required: false,
                    description: "Leave the element out until this field reads non-empty; a watch reports it once it fills in."
                  }
                }
              }
            },
            {
              name: "event",
              type: "EventAddress",
              required: true,
              description: "The declared external input each element fires, addressed like a `listens` source from the calling trait."
            }
          ],
          responseShape: {
            watchId: "string"
          }
        },
        {
          name: "unwatch",
          description: "Stop a watch.",
          params: [
            {
              name: "watchId",
              type: "string",
              required: true,
              description: "Id returned by watch."
            }
          ],
          responseShape: {
            stopped: "boolean"
          }
        },
        {
          name: "apply",
          description: "Apply a declared visual treatment to an element, with an optional visible label.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "elementId",
              type: "string",
              required: true,
              description: "Element id from query or watch."
            },
            {
              name: "treatment",
              type: "'hide' | 'blur' | 'highlight' | 'label' | 'restore'",
              required: true,
              description: "The treatment; `restore` undoes any earlier one."
            },
            {
              name: "label",
              type: "string",
              required: false,
              description: "Text shown with the treatment (e.g. why it was applied)."
            }
          ],
          responseShape: {
            applied: "boolean",
            tab: "number",
            elementId: "string",
            treatment: "'hide' | 'blur' | 'highlight' | 'label' | 'restore'"
          }
        },
        {
          name: "applyMany",
          description: "Apply treatments to many elements at once, one command per tab. An element that has left its\npage, or whose tab has closed, is listed in `missing`; it does not fail the others.",
          params: [
            {
              name: "items",
              type: "array",
              required: true,
              description: "",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true,
                    description: "Tab id."
                  },
                  elementId: {
                    type: "string",
                    required: true,
                    description: "Element id from query or watch."
                  },
                  treatment: {
                    type: "'hide' | 'blur' | 'highlight' | 'label' | 'restore'",
                    required: true,
                    description: "The treatment; `restore` undoes any earlier one."
                  },
                  label: {
                    type: "string",
                    required: false,
                    description: "Text shown with the treatment (e.g. why it was applied)."
                  }
                }
              }
            }
          ],
          responseShape: {
            applied: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true
                  },
                  elementId: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            missing: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  tab: {
                    type: "number",
                    required: true
                  },
                  elementId: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "act",
          description: "Interact with an element: click it, focus it, or type text into it.",
          params: [
            {
              name: "tab",
              type: "number",
              required: true,
              description: "Tab id."
            },
            {
              name: "elementId",
              type: "string",
              required: true,
              description: "Element id from query or watch."
            },
            {
              name: "action",
              type: "'click' | 'focus' | 'type'",
              required: true,
              description: "The interaction."
            },
            {
              name: "text",
              type: "string",
              required: false,
              description: 'Text to type (action "type" only).'
            }
          ],
          responseShape: {
            done: "boolean",
            tab: "number",
            elementId: "string"
          }
        }
      ],
      runtimes: [
        "browser"
      ]
    },
    knowledge: {
      name: "knowledge",
      description: "Learner knowledge source \u2014 connection status, sign-in, and the topics currently being learned",
      category: "ai",
      actions: [
        {
          name: "status",
          description: "Whether a learner account is connected, without starting a sign-in.",
          params: [],
          responseShape: {
            connected: "boolean",
            account: "string"
          }
        },
        {
          name: "connect",
          description: "Sign in to the knowledge source and keep the session.",
          params: [],
          responseShape: {
            connected: "boolean",
            account: "string"
          }
        },
        {
          name: "currentTopics",
          description: "The topics the learner is currently working on, most current first.",
          params: [
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Maximum topics to return."
            },
            {
              name: "sinceDays",
              type: "number",
              required: false,
              description: "Only topics touched within this many days."
            }
          ],
          responseShape: {
            topics: {
              type: "KnowledgeTopic[]",
              items: {
                type: "KnowledgeTopic",
                properties: {
                  label: {
                    type: "string",
                    required: true
                  },
                  keywords: {
                    type: "string[]",
                    required: true
                  },
                  weight: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "listPaths",
          description: "The learner's paths, filtered, sorted and paged; `search` ranks by meaning.",
          params: [
            {
              name: "search",
              type: "string",
              required: false,
              description: "Free-text search; ranks paths semantically."
            },
            {
              name: "sort",
              type: "'recent' | 'oldest' | 'az' | 'za'",
              required: false,
              description: ""
            },
            {
              name: "clusterId",
              type: "string",
              required: false,
              description: "Only paths in this cluster."
            },
            {
              name: "page",
              type: "number",
              required: false,
              description: ""
            },
            {
              name: "limit",
              type: "number",
              required: false,
              description: "Paths per page (1\u201348, default 9)."
            }
          ],
          responseShape: {
            items: {
              type: "KnowledgePath[]",
              items: {
                type: "KnowledgePath",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  conceptCount: {
                    type: "number",
                    required: true
                  },
                  learnedCount: {
                    type: "number",
                    required: true,
                    description: "Concepts the viewer has learned (mastery 3)."
                  },
                  levelCount: {
                    type: "number",
                    required: true
                  },
                  seedConcept: {
                    type: "string",
                    required: true,
                    description: "Name of the concept the path grew from; empty when it has none."
                  },
                  clusterId: {
                    type: "string",
                    required: true,
                    description: "The path's cluster id; empty when unclustered."
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  },
                  updatedAt: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            total: "number",
            page: "number",
            totalPages: "number",
            clusters: {
              type: "KnowledgePathCluster[]",
              items: {
                type: "KnowledgePathCluster",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "pathMap",
          description: "Every path with how they relate: similarity edges, shared-concept edges and clusters.",
          params: [],
          responseShape: {
            paths: {
              type: "KnowledgePath[]",
              items: {
                type: "KnowledgePath",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  conceptCount: {
                    type: "number",
                    required: true
                  },
                  learnedCount: {
                    type: "number",
                    required: true,
                    description: "Concepts the viewer has learned (mastery 3)."
                  },
                  levelCount: {
                    type: "number",
                    required: true
                  },
                  seedConcept: {
                    type: "string",
                    required: true,
                    description: "Name of the concept the path grew from; empty when it has none."
                  },
                  clusterId: {
                    type: "string",
                    required: true,
                    description: "The path's cluster id; empty when unclustered."
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  },
                  updatedAt: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            similarity: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  source: {
                    type: "string",
                    required: true
                  },
                  target: {
                    type: "string",
                    required: true
                  },
                  weight: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            sharedConcepts: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  source: {
                    type: "string",
                    required: true
                  },
                  target: {
                    type: "string",
                    required: true
                  },
                  weight: {
                    type: "number",
                    required: true
                  },
                  shared: {
                    type: "string[]",
                    required: true
                  },
                  linked: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            },
            clusters: {
              type: "KnowledgePathCluster[]",
              items: {
                type: "KnowledgePathCluster",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  size: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "deletePath",
          description: "Delete a path with everything derived from it.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "conceptsByLayer",
          description: "A path's goal and its levels of concepts.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            graphId: "string",
            title: "string",
            goal: "string",
            seedConcept: "string",
            layers: {
              type: "KnowledgeLayer[]",
              items: {
                type: "KnowledgeLayer",
                properties: {
                  layerNumber: {
                    type: "number",
                    required: true
                  },
                  goal: {
                    type: "string",
                    required: true
                  },
                  review: {
                    type: "string",
                    required: true
                  },
                  concepts: {
                    type: "KnowledgeConcept[]",
                    required: true,
                    items: {
                      type: "KnowledgeConcept",
                      properties: {
                        id: {
                          type: "string",
                          required: true
                        },
                        name: {
                          type: "string",
                          required: true
                        },
                        description: {
                          type: "string",
                          required: true
                        },
                        layer: {
                          type: "number",
                          required: true
                        },
                        isSeed: {
                          type: "boolean",
                          required: true
                        },
                        sequence: {
                          type: "number",
                          required: true
                        },
                        prerequisites: {
                          type: "string[]",
                          required: true
                        },
                        parents: {
                          type: "string[]",
                          required: true,
                          description: "Names of the concepts this one builds on (the parents a level expansion links it to)."
                        },
                        masteryLevel: {
                          type: "number",
                          required: true,
                          description: "0 not started \u2026 3 learned."
                        },
                        hasLesson: {
                          type: "boolean",
                          required: true,
                          description: "Whether a lesson has been generated for the concept."
                        },
                        canonicalKey: {
                          type: "string",
                          required: true,
                          description: "The graph engine's canonical key for the concept; peers address it as `concept:<canonicalKey>`."
                        },
                        routeKey: {
                          type: "string",
                          required: true,
                          description: "URL-safe key for the concept's page (concept ids are free text); `conceptDetail` takes it."
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        },
        {
          name: "expand",
          description: "Generate the path's next level of concepts. Streams progress: `delta`s, and one `step` per concept name as the model writes it.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "numConcepts",
              type: "number",
              required: false,
              description: "How many concepts to add (default 10)."
            },
            {
              name: "difficulty",
              type: "GraphDifficulty",
              required: false,
              description: "The level the learner chose; recorded on the path's goal and used from then on."
            }
          ],
          responseShape: {
            levelName: "string",
            added: {
              type: "KnowledgeConcept[]",
              items: {
                type: "KnowledgeConcept",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  description: {
                    type: "string",
                    required: true
                  },
                  layer: {
                    type: "number",
                    required: true
                  },
                  isSeed: {
                    type: "boolean",
                    required: true
                  },
                  sequence: {
                    type: "number",
                    required: true
                  },
                  prerequisites: {
                    type: "string[]",
                    required: true
                  },
                  parents: {
                    type: "string[]",
                    required: true,
                    description: "Names of the concepts this one builds on (the parents a level expansion links it to)."
                  },
                  masteryLevel: {
                    type: "number",
                    required: true,
                    description: "0 not started \u2026 3 learned."
                  },
                  hasLesson: {
                    type: "boolean",
                    required: true,
                    description: "Whether a lesson has been generated for the concept."
                  },
                  canonicalKey: {
                    type: "string",
                    required: true,
                    description: "The graph engine's canonical key for the concept; peers address it as `concept:<canonicalKey>`."
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page (concept ids are free text); `conceptDetail` takes it."
                  }
                }
              }
            }
          }
        },
        {
          name: "layerPractice",
          description: "Generate the practice and review for one level. Streams progress.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "layerNumber",
              type: "number",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            review: "string"
          }
        },
        {
          name: "conceptDetail",
          description: "One concept with its lesson, notes, questions and neighbours.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "routeKey",
              type: "string",
              required: true,
              description: "The concept's `routeKey` (from any concept or concept ref); pages carry it in their URL."
            }
          ],
          responseShape: {
            concept: {
              type: "KnowledgeConcept",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                name: {
                  type: "string",
                  required: true
                },
                description: {
                  type: "string",
                  required: true
                },
                layer: {
                  type: "number",
                  required: true
                },
                isSeed: {
                  type: "boolean",
                  required: true
                },
                sequence: {
                  type: "number",
                  required: true
                },
                prerequisites: {
                  type: "string[]",
                  required: true
                },
                parents: {
                  type: "string[]",
                  required: true,
                  description: "Names of the concepts this one builds on (the parents a level expansion links it to)."
                },
                masteryLevel: {
                  type: "number",
                  required: true,
                  description: "0 not started \u2026 3 learned."
                },
                hasLesson: {
                  type: "boolean",
                  required: true,
                  description: "Whether a lesson has been generated for the concept."
                },
                canonicalKey: {
                  type: "string",
                  required: true,
                  description: "The graph engine's canonical key for the concept; peers address it as `concept:<canonicalKey>`."
                },
                routeKey: {
                  type: "string",
                  required: true,
                  description: "URL-safe key for the concept's page (concept ids are free text); `conceptDetail` takes it."
                }
              }
            },
            pathTitle: "string",
            lessonId: "string",
            lesson: "string",
            prerequisites: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            parents: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            previous: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            next: {
              type: "KnowledgeConceptRef[]",
              items: {
                type: "KnowledgeConceptRef",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true,
                    description: "URL-safe key for the concept's page; `conceptDetail` takes it."
                  }
                }
              }
            },
            notes: {
              type: "KnowledgeNote[]",
              items: {
                type: "KnowledgeNote",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  text: {
                    type: "string",
                    required: true
                  },
                  selectedText: {
                    type: "string",
                    required: true
                  },
                  timestamp: {
                    type: "number",
                    required: true
                  }
                }
              }
            },
            questions: {
              type: "KnowledgeQuestion[]",
              items: {
                type: "KnowledgeQuestion",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  question: {
                    type: "string",
                    required: true
                  },
                  answer: {
                    type: "string",
                    required: true
                  },
                  selectedText: {
                    type: "string",
                    required: true
                  },
                  timestamp: {
                    type: "number",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "explain",
          description: "Generate (or regenerate) the lesson for a concept. Streams progress.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "simple",
              type: "boolean",
              required: false,
              description: "A shorter, simpler lesson."
            }
          ],
          responseShape: {
            lessonId: "string",
            lesson: "string"
          }
        },
        {
          name: "saveLesson",
          description: "Save an edited lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            saved: "boolean"
          }
        },
        {
          name: "answerQuestion",
          description: "Answer a question about a lesson and keep it with the lesson. Streams progress.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "question",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "selectedText",
              type: "string",
              required: false,
              description: "The lesson text the question is about."
            }
          ],
          responseShape: {
            question: {
              type: "KnowledgeQuestion",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                question: {
                  type: "string",
                  required: true
                },
                answer: {
                  type: "string",
                  required: true
                },
                selectedText: {
                  type: "string",
                  required: true
                },
                timestamp: {
                  type: "number",
                  required: true
                }
              }
            },
            relatedConcepts: {
              type: "KnowledgeRelatedConcept[]",
              items: {
                type: "KnowledgeRelatedConcept",
                properties: {
                  graphId: {
                    type: "string",
                    required: true
                  },
                  conceptId: {
                    type: "string",
                    required: true
                  },
                  routeKey: {
                    type: "string",
                    required: true
                  },
                  name: {
                    type: "string",
                    required: true
                  },
                  text: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "addNote",
          description: "Attach a note to a lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "text",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "selectedText",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            note: {
              type: "KnowledgeNote",
              properties: {
                id: {
                  type: "string",
                  required: true
                },
                text: {
                  type: "string",
                  required: true
                },
                selectedText: {
                  type: "string",
                  required: true
                },
                timestamp: {
                  type: "number",
                  required: true
                }
              }
            }
          }
        },
        {
          name: "deleteNote",
          description: "Remove a note from a lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "noteId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "deleteQuestion",
          description: "Remove a question and its answer from a lesson.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "lessonId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "questionId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            deleted: "boolean"
          }
        },
        {
          name: "recordLessonProgress",
          description: "Record what the learner did on a concept (activation answer, reflection, a Bloom-level answer) and\nrecompute its mastery: 0 nothing recorded, 1 one signal, 2 two or three, 3 four (an activation answer, a reflection, any Bloom\nanswer, three Bloom answers); once at 2 or above it is kept. A Bloom answer counts once per level; `correct` defaults to false and only a\ncorrect answer adds the level to the completed levels. `markLearned` stays authoritative (3 / 0).",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "activationResponse",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "reflection",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "bloomLevel",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "correct",
              type: "boolean",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            masteryLevel: "number"
          }
        },
        {
          name: "markLearned",
          description: "Mark a concept learned (mastery 3) or not yet (0).",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "learned",
              type: "boolean",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            masteryLevel: "number"
          }
        },
        {
          name: "visualization",
          description: "Choose a std learning instrument for a concept and generate its roster. Streams progress: `start`, then one `step` naming the chosen instrument.\nNo result is cached: every call generates afresh.",
          params: [
            {
              name: "graphId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "conceptId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "markerDescription",
              type: "string",
              required: false,
              description: "What the lesson asks to visualise."
            }
          ],
          responseShape: {
            mode: "string",
            rosterJson: "string"
          }
        },
        {
          name: "createGoalWithGraph",
          description: "Create a learning goal and its path from what the learner wants to learn. Streams progress: `delta`s, and `step`s as the goal is read: its `title` and `description` as they grow, then each `milestone` once.",
          params: [
            {
              name: "anchorAnswer",
              type: "string",
              required: true,
              description: `The learner's answer to "what do you want to learn?".`
            }
          ],
          responseShape: {
            graphId: "string",
            goalTitle: "string",
            description: "string",
            milestones: "string[]",
            difficulty: "GraphDifficulty"
          }
        },
        {
          name: "importPreview",
          description: "Stage an import without writing anything.",
          params: [
            {
              name: "adapter",
              type: "'markdown' | 'paste'",
              required: true,
              description: ""
            },
            {
              name: "documents",
              type: "KnowledgeImportDocument[]",
              required: true,
              description: "",
              items: {
                type: "KnowledgeImportDocument",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  raw: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          ],
          responseShape: {
            units: {
              type: "KnowledgeImportUnit[]",
              items: {
                type: "KnowledgeImportUnit",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  targetEntity: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  parentRef: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            errors: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  message: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "importCommit",
          description: "Import documents into a path (a new one when `graphId` is omitted).",
          params: [
            {
              name: "adapter",
              type: "'markdown' | 'paste'",
              required: true,
              description: ""
            },
            {
              name: "documents",
              type: "KnowledgeImportDocument[]",
              required: true,
              description: "",
              items: {
                type: "KnowledgeImportDocument",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  raw: {
                    type: "string",
                    required: true
                  }
                }
              }
            },
            {
              name: "graphId",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            graphId: "string",
            committed: "number",
            failed: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  reason: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "importItems",
          description: "Commit an approved, already-structured proposal (items the learner reviewed) into a path (a new one when `graphId` is omitted; `title` names it, else the first item's title).\nEach item is `{ ref, targetEntity, title, parentRef, content, front?, back? }`, all strings: `ref` is the item's id inside the proposal; `parentRef` is another item's `ref`, or `''` for top level.\n`Concept` (title = name, content = its lesson text) may sit under a `LearningGoal` or `Concept`; `Lesson` (content = lesson text) and `FlashCard` (`front`/`back`, defaulting to title/content when omitted or blank) need a parent `Concept`; `LearningGoal` (title = name, content = description) is top level.\nItems are validated one by one: a blank title, duplicate `ref`, dangling or cyclic `parentRef`, or a parent of the wrong kind fails that item (and its descendants) in `failed` while the rest commit. A new path with nothing committed is removed and the call fails.",
          params: [
            {
              name: "items",
              type: "KnowledgeImportItem[]",
              required: true,
              description: "",
              items: {
                type: "KnowledgeImportItem",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  targetEntity: {
                    type: "'Concept' | 'Lesson' | 'FlashCard' | 'LearningGoal'",
                    required: true
                  },
                  title: {
                    type: "string",
                    required: true
                  },
                  parentRef: {
                    type: "string",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  },
                  front: {
                    type: "string",
                    required: false
                  },
                  back: {
                    type: "string",
                    required: false
                  }
                }
              }
            },
            {
              name: "graphId",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "title",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            graphId: "string",
            committed: "number",
            failed: {
              type: "object[]",
              items: {
                type: "object",
                properties: {
                  ref: {
                    type: "string",
                    required: true
                  },
                  reason: {
                    type: "string",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "peers",
          description: "Other learners on the same concept or path. `nodeKey` is `concept:<canonicalKey>` (the concept's `canonicalKey`) or `path:<id>`.",
          params: [
            {
              name: "nodeKey",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            peers: {
              type: "KnowledgePeer[]",
              items: {
                type: "KnowledgePeer",
                properties: {
                  peerRef: {
                    type: "string",
                    required: true
                  },
                  handle: {
                    type: "string",
                    required: true
                  },
                  conceptsCompleted: {
                    type: "number",
                    required: true
                  },
                  convergencePct: {
                    type: "number",
                    required: true
                  },
                  activeNow: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            }
          }
        },
        {
          name: "touchNode",
          description: "Record that the learner is on this concept or path, so peers can find them. `nodeKey` is `concept:<canonicalKey>` (the concept's `canonicalKey`) or `path:<id>`.",
          params: [
            {
              name: "nodeKey",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            touched: "boolean"
          }
        },
        {
          name: "openConnection",
          description: "Open an anonymous discussion with a peer.",
          params: [
            {
              name: "peerRef",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "originKind",
              type: "'concept' | 'path'",
              required: true,
              description: ""
            },
            {
              name: "canonicalId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            connectionId: "string"
          }
        },
        {
          name: "connection",
          description: "One discussion with its topics and messages.",
          params: [
            {
              name: "connectionId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            status: "'active' | 'archived'",
            peerHandle: "string",
            originKind: "'concept' | 'path'",
            originLabel: "string",
            badges: {
              type: "KnowledgeBadge[]",
              items: {
                type: "KnowledgeBadge",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  label: {
                    type: "string",
                    required: true
                  },
                  kind: {
                    type: "'subtopic' | 'friction'",
                    required: true
                  },
                  active: {
                    type: "boolean",
                    required: true
                  }
                }
              }
            },
            messages: {
              type: "KnowledgeMessage[]",
              items: {
                type: "KnowledgeMessage",
                properties: {
                  id: {
                    type: "string",
                    required: true
                  },
                  fromViewer: {
                    type: "boolean",
                    required: true
                  },
                  content: {
                    type: "string",
                    required: true
                  },
                  createdAt: {
                    type: "number",
                    required: true
                  },
                  flagged: {
                    type: "boolean",
                    required: true
                  },
                  moderationReason: {
                    type: "string",
                    required: true,
                    description: "Why moderation flagged it; empty when it has no reason."
                  }
                }
              }
            }
          }
        },
        {
          name: "setActiveBadge",
          description: "Choose the discussion topic.",
          params: [
            {
              name: "connectionId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "badgeId",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            saved: "boolean"
          }
        },
        {
          name: "sendMessage",
          description: "Send a message in a discussion.",
          params: [
            {
              name: "connectionId",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "content",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "activeBadgeId",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            messageId: "string"
          }
        },
        {
          name: "conceptPersona",
          description: "The real person behind a concept, grounded in their public biography, for an in-character chat.",
          params: [
            {
              name: "conceptLabel",
              type: "string",
              required: true,
              description: ""
            },
            {
              name: "context",
              type: "string",
              required: false,
              description: "The learner's subject, so an ambiguous concept resolves in the right field."
            }
          ],
          responseShape: {
            name: "string",
            description: "string",
            portraitUrl: "string",
            greeting: "string",
            instructions: "string"
          }
        },
        {
          name: "conceptIcon",
          description: "An icon for a concept label.",
          params: [
            {
              name: "label",
              type: "string",
              required: true,
              description: ""
            }
          ],
          responseShape: {
            icon: "string",
            tone: "'themed' | 'dark' | 'light' | 'none'"
          }
        },
        {
          name: "getPreferences",
          description: "The learner's model choices for generating levels and lessons.",
          params: [],
          responseShape: {
            expandModel: "string",
            explainModel: "string"
          }
        },
        {
          name: "setPreferences",
          description: "Change the learner's model choices.",
          params: [
            {
              name: "expandModel",
              type: "string",
              required: false,
              description: ""
            },
            {
              name: "explainModel",
              type: "string",
              required: false,
              description: ""
            }
          ],
          responseShape: {
            saved: "boolean"
          }
        }
      ],
      runtimes: [
        "node",
        "browser"
      ]
    },
    analytics: {
      name: "analytics",
      description: "Cookieless first-party analytics \u2014 pageviews and named events beaconed to a collector endpoint",
      category: "marketing",
      actions: [
        {
          name: "pageview",
          description: "Record a visit to a page.",
          params: [
            {
              name: "endpoint",
              type: "string",
              required: true,
              description: "Collector ingest URL; empty sends nothing."
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: `The page's path, starting with "/".`
            },
            {
              name: "domain",
              type: "string",
              required: false,
              description: "Site id recorded with the visit; empty or absent records the page's host."
            }
          ],
          responseShape: {
            sent: "boolean"
          }
        },
        {
          name: "track",
          description: "Record a named event (a download, a sign-up) on a page.",
          params: [
            {
              name: "endpoint",
              type: "string",
              required: true,
              description: "Collector ingest URL; empty sends nothing."
            },
            {
              name: "name",
              type: "string",
              required: true,
              description: 'Event name; "pageview" is reserved.'
            },
            {
              name: "path",
              type: "string",
              required: true,
              description: `The page's path, starting with "/".`
            },
            {
              name: "props",
              type: "object",
              required: false,
              description: "Event properties.",
              mapValue: {
                type: "string | number | boolean"
              }
            },
            {
              name: "domain",
              type: "string",
              required: false,
              description: "Site id recorded with the event; empty or absent records the page's host."
            }
          ],
          responseShape: {
            sent: "boolean"
          }
        }
      ],
      runtimes: [
        "browser"
      ]
    }
  },
  categories: [
    "ai",
    "auth",
    "devtools",
    "finance",
    "infrastructure",
    "marketing",
    "media",
    "messaging",
    "payment",
    "reference"
  ]
};

// src/patterns/component-mapping.json
var component_mapping_default = {
  version: "1.0.0",
  exportedAt: "2026-10-10T01:07:16.949Z",
  mappings: {
    "page-header": {
      component: "PageHeader",
      importPath: "@/components/core/molecules/PageHeader",
      category: "header"
    },
    "detail-panel": {
      component: "DetailPanel",
      importPath: "@/components/core/organisms/DetailPanel",
      category: "display"
    },
    form: {
      component: "Form",
      importPath: "@/components/core/organisms/Form",
      category: "form"
    },
    "form-section": {
      component: "Form",
      importPath: "@/components/core/organisms/Form",
      category: "form"
    },
    "form-actions": {
      component: "ButtonGroup",
      importPath: "@/components/core/molecules/ButtonGroup",
      category: "form"
    },
    "filter-group": {
      component: "FilterGroup",
      importPath: "@/components/core/molecules/FilterGroup",
      category: "filter"
    },
    "search-input": {
      component: "SearchInput",
      importPath: "@/components/core/molecules/SearchInput",
      category: "filter"
    },
    "empty-state": {
      component: "EmptyState",
      importPath: "@/components/core/molecules/EmptyState",
      category: "state"
    },
    "loading-state": {
      component: "LoadingState",
      importPath: "@/components/core/molecules/LoadingState",
      category: "state"
    },
    "error-state": {
      component: "ErrorState",
      importPath: "@/components/core/molecules/ErrorState",
      category: "state"
    },
    notification: {
      component: "Toast",
      importPath: "@/components/core/molecules/Toast",
      category: "state"
    },
    tabs: {
      component: "Tabs",
      importPath: "@/components/core/molecules/Tabs",
      category: "navigation"
    },
    breadcrumb: {
      component: "Breadcrumb",
      importPath: "@/components/core/molecules/Breadcrumb",
      category: "navigation"
    },
    "wizard-container": {
      component: "WizardContainer",
      importPath: "@/components/core/molecules/WizardContainer",
      category: "navigation"
    },
    "wizard-progress": {
      component: "WizardProgress",
      importPath: "@/components/core/molecules/WizardProgress",
      category: "navigation"
    },
    "wizard-navigation": {
      component: "WizardNavigation",
      importPath: "@/components/core/molecules/WizardNavigation",
      category: "navigation"
    },
    pagination: {
      component: "Pagination",
      importPath: "@/components/core/molecules/Pagination",
      category: "navigation"
    },
    card: {
      component: "Card",
      importPath: "@/components/core/atoms/Card",
      category: "display"
    },
    modal: {
      component: "Modal",
      importPath: "@/components/core/molecules/Modal",
      category: "layout"
    },
    drawer: {
      component: "Drawer",
      importPath: "@/components/core/molecules/Drawer",
      category: "layout"
    },
    "confirm-dialog": {
      component: "ConfirmDialog",
      importPath: "@/components/core/molecules/ConfirmDialog",
      category: "layout"
    },
    "runtime-debugger": {
      component: "RuntimeDebugger",
      importPath: "@/components/core/organisms/RuntimeDebugger",
      category: "debug"
    },
    "repeatable-form-section": {
      component: "RepeatableFormSection",
      importPath: "@/components/core/molecules/RepeatableFormSection",
      category: "form"
    },
    box: {
      component: "Box",
      importPath: "@/components/core/atoms/Box",
      category: "layout"
    },
    grid: {
      component: "Grid",
      importPath: "@/components/core/molecules/Grid",
      category: "layout"
    },
    center: {
      component: "Center",
      importPath: "@/components/core/atoms/Center",
      category: "layout"
    },
    spacer: {
      component: "Spacer",
      importPath: "@/components/core/atoms/Spacer",
      category: "layout"
    },
    divider: {
      component: "Divider",
      importPath: "@/components/core/atoms/Divider",
      category: "layout"
    },
    button: {
      component: "Button",
      importPath: "@/components/core/atoms/Button",
      category: "atom"
    },
    badge: {
      component: "Badge",
      importPath: "@/components/core/atoms/Badge",
      category: "atom"
    },
    avatar: {
      component: "Avatar",
      importPath: "@/components/core/atoms/Avatar",
      category: "atom"
    },
    icon: {
      component: "Icon",
      importPath: "@/components/core/atoms/Icon",
      category: "atom"
    },
    spinner: {
      component: "Spinner",
      importPath: "@/components/core/atoms/Spinner",
      category: "atom"
    },
    input: {
      component: "Input",
      importPath: "@/components/core/atoms/Input",
      category: "form"
    },
    textarea: {
      component: "Textarea",
      importPath: "@/components/core/atoms/Textarea",
      category: "form"
    },
    select: {
      component: "Select",
      importPath: "@/components/core/atoms/Select",
      category: "form"
    },
    checkbox: {
      component: "Checkbox",
      importPath: "@/components/core/atoms/Checkbox",
      category: "form"
    },
    radio: {
      component: "Radio",
      importPath: "@/components/core/atoms/Radio",
      category: "form"
    },
    switch: {
      component: "Switch",
      importPath: "@/components/core/atoms/Switch",
      category: "form"
    },
    label: {
      component: "Label",
      importPath: "@/components/core/atoms/Label",
      category: "form"
    },
    alert: {
      component: "Alert",
      importPath: "@/components/core/molecules/Alert",
      category: "state"
    },
    accordion: {
      component: "Accordion",
      importPath: "@/components/core/molecules/Accordion",
      category: "display"
    },
    tooltip: {
      component: "Tooltip",
      importPath: "@/components/core/molecules/Tooltip",
      category: "atom"
    },
    popover: {
      component: "Popover",
      importPath: "@/components/core/molecules/Popover",
      category: "atom"
    },
    menu: {
      component: "Menu",
      importPath: "@/components/core/molecules/Menu",
      category: "navigation"
    },
    "progress-bar": {
      component: "ProgressBar",
      importPath: "@/components/core/atoms/ProgressBar",
      category: "display"
    },
    container: {
      component: "Container",
      importPath: "@/components/core/molecules/Container",
      category: "layout"
    },
    "simple-grid": {
      component: "SimpleGrid",
      importPath: "@/components/core/molecules/SimpleGrid",
      category: "layout"
    },
    "studio-home-web": {
      component: "StudioHomeWebTemplate",
      importPath: "@/clients/builder/templates/StudioHomeWebTemplate",
      category: "template",
      client: "builder"
    },
    "studio-home-electron": {
      component: "StudioHomeElectronTemplate",
      importPath: "@/clients/builder/templates/StudioHomeElectronTemplate",
      category: "template",
      client: "builder"
    },
    "studio-project": {
      component: "StudioProjectTemplate",
      importPath: "@/clients/builder/templates/StudioProjectTemplate",
      category: "template",
      client: "builder"
    },
    "hex-battlefield": {
      component: "BattlefieldTemplate",
      importPath: "@/clients/trait-wars/templates/BattlefieldTemplate",
      category: "template",
      client: "trait-wars"
    },
    "hero-selection": {
      component: "HexTraitWarsGame",
      importPath: "@/clients/trait-wars/templates/HexTraitWarsGame",
      category: "template",
      client: "trait-wars"
    },
    "hero-profile": {
      component: "HeroProfileTemplate",
      importPath: "@/clients/trait-wars/templates/HeroProfileTemplate",
      category: "template",
      client: "trait-wars"
    },
    "army-builder": {
      component: "ArmyBuilderTemplate",
      importPath: "@/clients/trait-wars/templates/ArmyBuilderTemplate",
      category: "template",
      client: "trait-wars"
    },
    "conditional-wrapper": {
      component: "ConditionalWrapper",
      importPath: "@/components/core/atoms/ConditionalWrapper",
      category: "component"
    },
    "law-reference-tooltip": {
      component: "LawReferenceTooltip",
      importPath: "@/components/core/atoms/LawReferenceTooltip",
      category: "component"
    },
    overlay: {
      component: "Overlay",
      importPath: "@/components/core/atoms/Overlay",
      category: "container"
    },
    stack: {
      component: "Stack",
      importPath: "@/components/core/atoms/Stack",
      category: "component"
    },
    vstack: {
      component: "VStack",
      importPath: "@/components/core/atoms/Stack",
      category: "component"
    },
    hstack: {
      component: "HStack",
      importPath: "@/components/core/atoms/Stack",
      category: "component"
    },
    "text-highlight": {
      component: "TextHighlight",
      importPath: "@/components/core/atoms/TextHighlight",
      category: "component"
    },
    "theme-toggle": {
      component: "ThemeToggle",
      importPath: "@/components/core/atoms/ThemeToggle",
      category: "component"
    },
    typography: {
      component: "Typography",
      importPath: "@/components/core/atoms/Typography",
      category: "component"
    },
    flex: {
      component: "Flex",
      importPath: "@/components/core/molecules/Flex",
      category: "component"
    },
    "floating-action-button": {
      component: "FloatingActionButton",
      importPath: "@/components/core/molecules/FloatingActionButton",
      category: "component"
    },
    "form-field": {
      component: "FormField",
      importPath: "@/components/core/molecules/FormField",
      category: "form"
    },
    "form-section-header": {
      component: "FormSectionHeader",
      importPath: "@/components/core/molecules/FormSectionHeader",
      category: "form"
    },
    "input-group": {
      component: "InputGroup",
      importPath: "@/components/core/molecules/InputGroup",
      category: "form"
    },
    "relation-select": {
      component: "RelationSelect",
      importPath: "@/components/core/molecules/RelationSelect",
      category: "form"
    },
    "side-panel": {
      component: "SidePanel",
      importPath: "@/components/core/molecules/SidePanel",
      category: "component"
    },
    "violation-alert": {
      component: "ViolationAlert",
      importPath: "@/components/core/molecules/ViolationAlert",
      category: "state"
    },
    "stat-display": {
      component: "StatDisplay",
      importPath: "@/components/core/molecules/StatDisplay",
      category: "display"
    },
    "drawer-slot": {
      component: "DrawerSlot",
      importPath: "@/components/core/organisms/DrawerSlot",
      category: "layout"
    },
    header: {
      component: "Header",
      importPath: "@/components/core/molecules/Header",
      category: "header"
    },
    "modal-slot": {
      component: "ModalSlot",
      importPath: "@/components/core/organisms/ModalSlot",
      category: "layout"
    },
    navigation: {
      component: "Navigation",
      importPath: "@/components/core/molecules/Navigation",
      category: "navigation"
    },
    "orbital-visualization": {
      component: "OrbitalVisualization",
      importPath: "@/components/core/molecules/OrbitalVisualization",
      category: "display"
    },
    section: {
      component: "Section",
      importPath: "@/components/core/molecules/Section",
      category: "display"
    },
    sidebar: {
      component: "Sidebar",
      importPath: "@/components/core/molecules/Sidebar",
      category: "navigation"
    },
    split: {
      component: "Split",
      importPath: "@/components/core/molecules/Split",
      category: "layout"
    },
    "toast-slot": {
      component: "ToastSlot",
      importPath: "@/components/core/organisms/ToastSlot",
      category: "layout"
    },
    "auth-layout": {
      component: "AuthLayout",
      importPath: "@/components/core/templates/AuthLayout",
      category: "template"
    },
    "counter-template": {
      component: "CounterTemplate",
      importPath: "@/components/core/templates/CounterTemplate",
      category: "template"
    },
    "dashboard-layout": {
      component: "DashboardLayout",
      importPath: "@/components/core/templates/DashboardLayout",
      category: "template"
    },
    "generic-app-template": {
      component: "GenericAppTemplate",
      importPath: "@/components/core/templates/GenericAppTemplate",
      category: "template"
    },
    "game-shell": {
      component: "GameShell",
      importPath: "@/components/game/templates/GameShell",
      category: "game"
    },
    chart: {
      component: "Chart",
      importPath: "@/components/core/molecules/Chart",
      category: "visualization"
    },
    meter: {
      component: "Meter",
      importPath: "@/components/core/molecules/Meter",
      category: "visualization"
    },
    timeline: {
      component: "Timeline",
      importPath: "@/components/core/organisms/Timeline",
      category: "display"
    },
    "media-gallery": {
      component: "MediaGallery",
      importPath: "@/components/core/organisms/MediaGallery",
      category: "media"
    },
    "signature-pad": {
      component: "SignaturePad",
      importPath: "@/components/core/molecules/SignaturePad",
      category: "form"
    },
    "document-viewer": {
      component: "DocumentViewer",
      importPath: "@/components/core/molecules/DocumentViewer",
      category: "display"
    },
    "graph-canvas": {
      component: "GraphCanvas",
      importPath: "@/components/core/molecules/GraphCanvas",
      category: "visualization"
    },
    "error-boundary": {
      component: "ErrorBoundary",
      importPath: "@/components/core/molecules/ErrorBoundary",
      category: "state"
    },
    skeleton: {
      component: "Skeleton",
      importPath: "@/components/core/molecules/Skeleton",
      category: "component"
    },
    "quiz-block": {
      component: "QuizBlock",
      importPath: "@/components/core/molecules/QuizBlock",
      category: "component"
    },
    "code-block": {
      component: "CodeBlock",
      importPath: "@/components/core/molecules/CodeBlock",
      category: "component"
    },
    "markdown-content": {
      component: "MarkdownContent",
      importPath: "@/components/core/molecules/MarkdownContent",
      category: "component"
    },
    "content-renderer": {
      component: "ContentRenderer",
      importPath: "@/components/core/molecules/ContentRenderer",
      category: "display"
    },
    "jazari-state-machine": {
      component: "JazariStateMachine",
      importPath: "@/components/core/molecules/JazariStateMachine",
      category: "display"
    },
    "state-machine-view": {
      component: "StateMachineView",
      importPath: "@/components/core/organisms/StateMachineView",
      category: "display"
    },
    "scaled-diagram": {
      component: "ScaledDiagram",
      importPath: "@/components/core/molecules/ScaledDiagram",
      category: "component"
    },
    "day-cell": {
      component: "DayCell",
      importPath: "@/components/core/atoms/DayCell",
      category: "component"
    },
    "flip-container": {
      component: "FlipContainer",
      importPath: "@/components/core/atoms/FlipContainer",
      category: "layout"
    },
    "time-slot-cell": {
      component: "TimeSlotCell",
      importPath: "@/components/core/atoms/TimeSlotCell",
      category: "layout"
    },
    "calendar-grid": {
      component: "CalendarGrid",
      importPath: "@/components/core/molecules/CalendarGrid",
      category: "display"
    },
    "chart-legend": {
      component: "ChartLegend",
      importPath: "@/components/core/molecules/ChartLegend",
      category: "display"
    },
    "date-range-selector": {
      component: "DateRangeSelector",
      importPath: "@/components/core/molecules/DateRangeSelector",
      category: "form"
    },
    "flip-card": {
      component: "FlipCard",
      importPath: "@/components/core/molecules/FlipCard",
      category: "display"
    },
    "graph-view": {
      component: "GraphView",
      importPath: "@/components/core/molecules/GraphView",
      category: "component"
    },
    "data-grid": {
      component: "DataGrid",
      importPath: "@/components/core/molecules/DataGrid",
      category: "display"
    },
    "data-list": {
      component: "DataList",
      importPath: "@/components/core/molecules/DataList",
      category: "display"
    },
    "line-chart": {
      component: "LineChart",
      importPath: "@/components/core/molecules/LineChart",
      category: "display"
    },
    "progress-dots": {
      component: "ProgressDots",
      importPath: "@/components/core/molecules/ProgressDots",
      category: "component"
    },
    "map-view": {
      component: "MapView",
      importPath: "@/components/core/molecules/MapView",
      category: "component"
    },
    "range-slider": {
      component: "RangeSlider",
      importPath: "@/components/core/atoms/RangeSlider",
      category: "component"
    },
    "status-dot": {
      component: "StatusDot",
      importPath: "@/components/core/atoms/StatusDot",
      category: "display"
    },
    "trend-indicator": {
      component: "TrendIndicator",
      importPath: "@/components/core/atoms/TrendIndicator",
      category: "component"
    },
    lightbox: {
      component: "Lightbox",
      importPath: "@/components/core/molecules/Lightbox",
      category: "component"
    },
    "number-stepper": {
      component: "NumberStepper",
      importPath: "@/components/core/molecules/NumberStepper",
      category: "component"
    },
    "star-rating": {
      component: "StarRating",
      importPath: "@/components/core/molecules/StarRating",
      category: "component"
    },
    "upload-drop-zone": {
      component: "UploadDropZone",
      importPath: "@/components/core/molecules/UploadDropZone",
      category: "component"
    },
    "animated-counter": {
      component: "AnimatedCounter",
      importPath: "@/components/core/molecules/AnimatedCounter",
      category: "component"
    },
    "confetti-effect": {
      component: "ConfettiEffect",
      importPath: "@/components/core/atoms/ConfettiEffect",
      category: "component"
    },
    "infinite-scroll-sentinel": {
      component: "InfiniteScrollSentinel",
      importPath: "@/components/core/atoms/InfiniteScrollSentinel",
      category: "component"
    },
    "typewriter-text": {
      component: "TypewriterText",
      importPath: "@/components/core/atoms/TypewriterText",
      category: "component"
    },
    carousel: {
      component: "Carousel",
      importPath: "@/components/core/molecules/Carousel",
      category: "component"
    },
    "pull-to-refresh": {
      component: "PullToRefresh",
      importPath: "@/components/core/molecules/PullToRefresh",
      category: "component"
    },
    "sortable-list": {
      component: "SortableList",
      importPath: "@/components/core/molecules/SortableList",
      category: "display"
    },
    "swipeable-row": {
      component: "SwipeableRow",
      importPath: "@/components/core/molecules/SwipeableRow",
      category: "component"
    },
    "section-header": {
      component: "SectionHeader",
      importPath: "@/components/core/atoms/SectionHeader",
      category: "header"
    },
    "feature-card": {
      component: "FeatureCard",
      importPath: "@/components/core/molecules/FeatureCard",
      category: "display"
    },
    "feature-grid": {
      component: "FeatureGrid",
      importPath: "@/components/core/molecules/FeatureGrid",
      category: "display"
    },
    "social-proof": {
      component: "SocialProof",
      importPath: "@/components/core/molecules/SocialProof",
      category: "component"
    },
    "animated-graphic": {
      component: "AnimatedGraphic",
      importPath: "@/components/core/atoms/AnimatedGraphic",
      category: "component"
    },
    "animated-reveal": {
      component: "AnimatedReveal",
      importPath: "@/components/core/atoms/AnimatedReveal",
      category: "component"
    },
    "pattern-tile": {
      component: "PatternTile",
      importPath: "@/components/core/atoms/PatternTile",
      category: "game"
    },
    "edge-decoration": {
      component: "EdgeDecoration",
      importPath: "@/components/core/molecules/EdgeDecoration",
      category: "component"
    },
    "ui-slot-renderer": {
      component: "UISlotRenderer",
      importPath: "@/components/core/organisms/UISlotRenderer",
      category: "layout"
    },
    "trait-frame": {
      component: "TraitFrame",
      importPath: "@/components/core/atoms/TraitFrame",
      category: "component"
    },
    "file-tree": {
      component: "FileTree",
      importPath: "@/components/core/molecules/FileTree",
      category: "component"
    },
    "filter-pill": {
      component: "FilterPill",
      importPath: "@/components/core/atoms/FilterPill",
      category: "filter"
    },
    "likert-scale": {
      component: "LikertScale",
      importPath: "@/components/core/molecules/LikertScale",
      category: "component"
    },
    "matrix-question": {
      component: "MatrixQuestion",
      importPath: "@/components/core/molecules/MatrixQuestion",
      category: "component"
    },
    "qr-scanner": {
      component: "QrScanner",
      importPath: "@/components/core/molecules/QrScanner",
      category: "component"
    },
    "vote-stack": {
      component: "VoteStack",
      importPath: "@/components/core/molecules/VoteStack",
      category: "component"
    },
    "branching-logic-builder": {
      component: "BranchingLogicBuilder",
      importPath: "@/components/core/molecules/BranchingLogicBuilder",
      category: "display"
    },
    "reply-tree": {
      component: "ReplyTree",
      importPath: "@/components/core/molecules/ReplyTree",
      category: "display"
    },
    "version-diff": {
      component: "VersionDiff",
      importPath: "@/components/core/molecules/VersionDiff",
      category: "display"
    },
    "option-constraint-group": {
      component: "OptionConstraintGroup",
      importPath: "@/components/core/molecules/OptionConstraintGroup",
      category: "component"
    },
    "positioned-canvas": {
      component: "PositionedCanvas",
      importPath: "@/components/core/molecules/PositionedCanvas",
      category: "game"
    },
    "choice-button": {
      component: "ChoiceButton",
      importPath: "@/components/game/atoms/ChoiceButton",
      category: "game"
    },
    "control-button": {
      component: "ControlButton",
      importPath: "@/components/game/atoms/ControlButton",
      category: "game"
    },
    "dialogue-bubble": {
      component: "DialogueBubble",
      importPath: "@/components/game/atoms/DialogueBubble",
      category: "game"
    },
    "health-bar": {
      component: "HealthBar",
      importPath: "@/components/game/atoms/HealthBar",
      category: "game"
    },
    "score-display": {
      component: "ScoreDisplay",
      importPath: "@/components/game/atoms/ScoreDisplay",
      category: "game"
    },
    "timer-display": {
      component: "TimerDisplay",
      importPath: "@/components/game/atoms/TimerDisplay",
      category: "game"
    },
    "svg-branch": {
      component: "SvgBranch",
      importPath: "@/components/core/atoms/SvgBranch",
      category: "component"
    },
    "svg-connection": {
      component: "SvgConnection",
      importPath: "@/components/core/atoms/SvgConnection",
      category: "component"
    },
    "svg-flow": {
      component: "SvgFlow",
      importPath: "@/components/core/atoms/SvgFlow",
      category: "component"
    },
    "svg-grid": {
      component: "SvgGrid",
      importPath: "@/components/core/atoms/SvgGrid",
      category: "display"
    },
    "svg-lobe": {
      component: "SvgLobe",
      importPath: "@/components/core/atoms/SvgLobe",
      category: "component"
    },
    "svg-mesh": {
      component: "SvgMesh",
      importPath: "@/components/core/atoms/SvgMesh",
      category: "component"
    },
    "svg-morph": {
      component: "SvgMorph",
      importPath: "@/components/core/atoms/SvgMorph",
      category: "component"
    },
    "svg-node": {
      component: "SvgNode",
      importPath: "@/components/core/atoms/SvgNode",
      category: "component"
    },
    "svg-pulse": {
      component: "SvgPulse",
      importPath: "@/components/core/atoms/SvgPulse",
      category: "component"
    },
    "svg-ring": {
      component: "SvgRing",
      importPath: "@/components/core/atoms/SvgRing",
      category: "component"
    },
    "svg-shield": {
      component: "SvgShield",
      importPath: "@/components/core/atoms/SvgShield",
      category: "component"
    },
    "svg-stack": {
      component: "SvgStack",
      importPath: "@/components/core/atoms/SvgStack",
      category: "component"
    },
    "doc-breadcrumb": {
      component: "DocBreadcrumb",
      importPath: "@/components/core/molecules/DocBreadcrumb",
      category: "navigation"
    },
    "doc-pagination": {
      component: "DocPagination",
      importPath: "@/components/core/molecules/DocPagination",
      category: "navigation"
    },
    "doc-search": {
      component: "DocSearch",
      importPath: "@/components/core/molecules/DocSearch",
      category: "filter"
    },
    "doc-sidebar": {
      component: "DocSidebar",
      importPath: "@/components/core/molecules/DocSidebar",
      category: "navigation"
    },
    "doc-toc": {
      component: "DocTOC",
      importPath: "@/components/core/molecules/DocTOC",
      category: "component"
    },
    "gradient-divider": {
      component: "GradientDivider",
      importPath: "@/components/core/molecules/GradientDivider",
      category: "component"
    },
    "behavior-view": {
      component: "BehaviorView",
      importPath: "@/components/avl/molecules/BehaviorView",
      category: "component"
    },
    "module-card": {
      component: "ModuleCard",
      importPath: "@/components/avl/molecules/ModuleCard",
      category: "display"
    },
    "game-hud": {
      component: "GameHud",
      importPath: "@/components/game/molecules/GameHud",
      category: "game"
    },
    "game-menu": {
      component: "GameMenu",
      importPath: "@/components/game/molecules/GameMenu",
      category: "game"
    },
    "stat-badge": {
      component: "StatBadge",
      importPath: "@/components/game/molecules/StatBadge",
      category: "game"
    },
    "game-audio-toggle": {
      component: "GameAudioToggle",
      importPath: "@/components/game/atoms/GameAudioToggle",
      category: "game"
    },
    "trait-slot": {
      component: "TraitSlot",
      importPath: "@/components/game/molecules/TraitSlot",
      category: "game"
    },
    "action-palette": {
      component: "ActionPalette",
      importPath: "@/components/game/molecules/ActionPalette",
      category: "game"
    },
    "action-tile": {
      component: "ActionTile",
      importPath: "@/components/game/molecules/ActionTile",
      category: "game"
    },
    "sequence-bar": {
      component: "SequenceBar",
      importPath: "@/components/game/molecules/SequenceBar",
      category: "game"
    },
    "dashboard-grid": {
      component: "DashboardGrid",
      importPath: "@/components/core/organisms/DashboardGrid",
      category: "game"
    },
    "split-pane": {
      component: "SplitPane",
      importPath: "@/components/core/organisms/SplitPane",
      category: "layout"
    },
    "tabbed-container": {
      component: "TabbedContainer",
      importPath: "@/components/core/organisms/TabbedContainer",
      category: "navigation"
    },
    "master-detail-layout": {
      component: "MasterDetailLayout",
      importPath: "@/components/core/organisms/MasterDetailLayout",
      category: "display"
    },
    sparkline: {
      component: "Sparkline",
      importPath: "@/components/core/atoms/Sparkline",
      category: "component"
    },
    "date-range-picker": {
      component: "DateRangePicker",
      importPath: "@/components/core/molecules/DateRangePicker",
      category: "component"
    },
    aside: {
      component: "Aside",
      importPath: "@/components/core/atoms/Aside",
      category: "component"
    },
    dialog: {
      component: "Dialog",
      importPath: "@/components/core/atoms/Dialog",
      category: "container"
    },
    "tag-input": {
      component: "TagInput",
      importPath: "@/components/core/molecules/TagInput",
      category: "form"
    },
    "table-view": {
      component: "TableView",
      importPath: "@/components/core/molecules/TableView",
      category: "display"
    },
    "stat-card": {
      component: "StatCard",
      importPath: "@/components/core/organisms/StatCard",
      category: "display"
    },
    "activation-block": {
      component: "ActivationBlock",
      importPath: "@/components/core/molecules/ActivationBlock",
      category: "component"
    },
    "bloom-quiz-block": {
      component: "BloomQuizBlock",
      importPath: "@/components/core/molecules/BloomQuizBlock",
      category: "component"
    },
    "connection-block": {
      component: "ConnectionBlock",
      importPath: "@/components/core/molecules/ConnectionBlock",
      category: "component"
    },
    "reflection-block": {
      component: "ReflectionBlock",
      importPath: "@/components/core/molecules/ReflectionBlock",
      category: "component"
    },
    "code-runner-panel": {
      component: "CodeRunnerPanel",
      importPath: "@/components/core/organisms/CodeRunnerPanel",
      category: "display"
    },
    "segment-renderer": {
      component: "SegmentRenderer",
      importPath: "@/components/core/organisms/SegmentRenderer",
      category: "display"
    },
    "chat-bar": {
      component: "ChatBar",
      importPath: "@/components/core/organisms/ChatBar",
      category: "display"
    },
    "subagent-trace-panel": {
      component: "SubagentTracePanel",
      importPath: "@/components/core/organisms/SubagentTracePanel",
      category: "display"
    },
    "canvas-2d": {
      component: "Canvas2D",
      importPath: "@/components/game/molecules/Canvas2D",
      category: "game"
    },
    "control-grid": {
      component: "ControlGrid",
      importPath: "@/components/game/molecules/ControlGrid",
      category: "game"
    },
    "game-icon": {
      component: "GameIcon",
      importPath: "@/components/core/atoms/GameIcon",
      category: "game"
    },
    "form-layout": {
      component: "FormLayout",
      importPath: "@/components/core/molecules/FormSection",
      category: "form"
    },
    "learning-canvas": {
      component: "LearningCanvas",
      importPath: "@/components/learning/atoms/LearningCanvas",
      category: "learning"
    },
    "biology-canvas": {
      component: "BiologyCanvas",
      importPath: "@/components/learning/molecules/BiologyCanvas",
      category: "learning"
    },
    "chemistry-canvas": {
      component: "ChemistryCanvas",
      importPath: "@/components/learning/molecules/ChemistryCanvas",
      category: "learning"
    },
    "math-canvas": {
      component: "MathCanvas",
      importPath: "@/components/learning/molecules/MathCanvas",
      category: "learning"
    },
    "physics-canvas": {
      component: "PhysicsCanvas",
      importPath: "@/components/learning/molecules/PhysicsCanvas",
      category: "learning"
    },
    "state-graph": {
      component: "StateGraph",
      importPath: "@/components/game/molecules/StateGraph",
      category: "game"
    },
    "state-json-view": {
      component: "StateJsonView",
      importPath: "@/components/game/molecules/StateJsonView",
      category: "game"
    },
    "algorithm-canvas": {
      component: "AlgorithmCanvas",
      importPath: "@/components/learning/molecules/AlgorithmCanvas",
      category: "learning"
    },
    "atlas-image": {
      component: "AtlasImage",
      importPath: "@/components/core/atoms/AtlasImage",
      category: "component"
    },
    "atlas-panel": {
      component: "AtlasPanel",
      importPath: "@/components/core/atoms/AtlasImage",
      category: "component"
    },
    "draw-shape": {
      component: "DrawShape",
      importPath: "@/components/game/atoms/DrawShape",
      category: "game"
    },
    "draw-sprite": {
      component: "DrawSprite",
      importPath: "@/components/game/atoms/DrawSprite",
      category: "game"
    },
    "draw-skinned-mesh": {
      component: "DrawSkinnedMesh",
      importPath: "@/components/game/atoms/DrawSkinnedMesh",
      category: "game"
    },
    "draw-text": {
      component: "DrawText",
      importPath: "@/components/game/atoms/DrawText",
      category: "game"
    },
    "draw-shape-layer": {
      component: "DrawShapeLayer",
      importPath: "@/components/game/molecules/DrawShapeLayer",
      category: "game"
    },
    "draw-sprite-layer": {
      component: "DrawSpriteLayer",
      importPath: "@/components/game/molecules/DrawSpriteLayer",
      category: "game"
    },
    "draw-text-layer": {
      component: "DrawTextLayer",
      importPath: "@/components/game/molecules/DrawTextLayer",
      category: "game"
    },
    canvas: {
      component: "Canvas",
      importPath: "@/components/game/molecules/Canvas",
      category: "game"
    },
    presence: {
      component: "Presence",
      importPath: "@/components/core/atoms/Presence",
      category: "component"
    },
    "page-transition": {
      component: "PageTransition",
      importPath: "@/components/core/molecules/PageTransition",
      category: "component"
    },
    "import-preview-tree": {
      component: "ImportPreviewTree",
      importPath: "@/components/core/molecules/ImportPreviewTree",
      category: "component"
    },
    "import-progress": {
      component: "ImportProgress",
      importPath: "@/components/core/molecules/ImportProgress",
      category: "component"
    },
    "import-source-picker": {
      component: "ImportSourcePicker",
      importPath: "@/components/core/molecules/ImportSourcePicker",
      category: "component"
    },
    "draw-group": {
      component: "DrawGroup",
      importPath: "@/components/game/atoms/DrawGroup",
      category: "game"
    },
    "emoji-picker": {
      component: "EmojiPicker",
      importPath: "@/components/core/molecules/EmojiPicker",
      category: "component"
    },
    "draw-mesh": {
      component: "DrawMesh",
      importPath: "@/components/game/atoms/DrawMesh",
      category: "game"
    },
    "algo-graph-canvas": {
      component: "AlgoGraphCanvas",
      importPath: "@/components/learning/molecules/AlgoGraphCanvas",
      category: "learning"
    },
    "fx-overlay": {
      component: "FxOverlay",
      importPath: "@/components/core/molecules/FxOverlay",
      category: "container"
    },
    "draw-fx-layer": {
      component: "DrawFxLayer",
      importPath: "@/components/game/molecules/DrawFxLayer",
      category: "game"
    },
    "rich-text-editor": {
      component: "RichTextEditor",
      importPath: "@/components/core/molecules/RichTextEditor",
      category: "component"
    },
    "document-panel": {
      component: "DocumentPanel",
      importPath: "@/components/core/molecules/DocumentPanel",
      category: "component"
    },
    "document-details": {
      component: "DocumentDetails",
      importPath: "@/components/core/molecules/DocumentDetails",
      category: "display"
    },
    "floating-toolbar": {
      component: "FloatingToolbar",
      importPath: "@/components/core/molecules/FloatingToolbar",
      category: "component"
    },
    "dock-layout": {
      component: "DockLayout",
      importPath: "@/components/core/organisms/DockLayout",
      category: "layout"
    },
    "command-palette": {
      component: "CommandPalette",
      importPath: "@/components/core/molecules/CommandPalette",
      category: "component"
    },
    "game-audio-cue": {
      component: "GameAudioCue",
      importPath: "@/components/game/atoms/GameAudioCue",
      category: "game"
    },
    "ui-slot-component": {
      component: "UISlotComponent",
      importPath: "@/components/core/organisms/UISlotRenderer",
      category: "layout"
    },
    gantt: {
      component: "Gantt",
      importPath: "@/components/core/molecules/Gantt",
      category: "component"
    },
    image: {
      component: "Image",
      importPath: "@/components/core/atoms/Image",
      category: "component"
    },
    "geometric-pattern": {
      component: "GeometricPattern",
      importPath: "@/components/core/molecules/GeometricPattern",
      category: "component"
    },
    "icon-button": {
      component: "IconButton",
      importPath: "@/components/core/molecules/IconButton",
      category: "component"
    },
    "top-nav-item": {
      component: "TopNavItem",
      importPath: "@/components/core/molecules/TopNavItem",
      category: "navigation"
    },
    "change-list": {
      component: "ChangeList",
      importPath: "@/components/core/molecules/ChangeList",
      category: "display"
    },
    "commit-row": {
      component: "CommitRow",
      importPath: "@/components/core/molecules/CommitRow",
      category: "component"
    },
    "avl-state-machine": {
      component: "AvlStateMachine",
      importPath: "@/components/avl/molecules/AvlStateMachine",
      category: "display"
    },
    "avl-glyph": {
      component: "AvlGlyph",
      importPath: "@/components/avl/molecules/AvlGlyph",
      category: "component"
    },
    "agent-activity-feed": {
      component: "AgentActivityFeed",
      importPath: "@/components/core/molecules/AgentActivityFeed",
      category: "component"
    },
    "agent-activity-row": {
      component: "AgentActivityRow",
      importPath: "@/components/core/molecules/AgentActivityFeed",
      category: "component"
    },
    "agent-chat-feed": {
      component: "AgentChatFeed",
      importPath: "@/components/core/molecules/AgentChatFeed",
      category: "component"
    },
    "avl-transition-explainer": {
      component: "AvlTransitionExplainer",
      importPath: "@/components/avl/molecules/AvlTransitionExplainer",
      category: "component"
    },
    "flow-canvas": {
      component: "FlowCanvas",
      importPath: "@/components/avl/organisms/FlowCanvas",
      category: "display"
    },
    "layers-panel": {
      component: "LayersPanel",
      importPath: "@/components/avl/organisms/LayersPanel",
      category: "display"
    },
    "orb-inspector": {
      component: "OrbInspector",
      importPath: "@/components/avl/organisms/OrbInspector",
      category: "debug"
    }
  }
};

// src/patterns/event-contracts.json
var event_contracts_default = {
  version: "1.0.0",
  exportedAt: "2026-10-10T01:07:16.949Z",
  contracts: {
    form: {
      emits: [
        {
          event: "GLOBAL_VARIABLE_SET",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VIOLATION_DETECTED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "FIELD_CHANGED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VALIDATION_FAILED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [
        "SAVE",
        "CANCEL"
      ],
      entityAware: true
    },
    "form-section": {
      emits: [
        {
          event: "GLOBAL_VARIABLE_SET",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VIOLATION_DETECTED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "FIELD_CHANGED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "VALIDATION_FAILED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [
        "SAVE",
        "CANCEL"
      ],
      entityAware: true
    },
    "form-actions": {
      emits: [
        {
          event: "DISPATCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true,
      configDriven: true
    },
    "detail-panel": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "page-header": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    "filter-group": {
      emits: [
        {
          event: "FILTER",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLEAR_FILTERS",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true,
      configDriven: true
    },
    "search-input": {
      emits: [
        {
          event: "SEARCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLEAR_SEARCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "empty-state": {
      emits: [
        {
          event: "CREATE",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    "loading-state": {
      emits: [],
      requires: [],
      entityAware: false
    },
    "error-state": {
      emits: [
        {
          event: "RETRY",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    notification: {
      emits: [
        {
          event: "DISMISS",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        },
        {
          event: "ACTION",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    tabs: {
      emits: [
        {
          event: "TAB_CHANGE",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              tabId: "string"
            }
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    breadcrumb: {
      emits: [],
      requires: [],
      entityAware: false
    },
    "wizard-container": {
      emits: [
        {
          event: "WIZARD_NEXT",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_BACK",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_COMPLETE",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_STEP_CHANGE",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              step: "number"
            }
          }
        }
      ],
      requires: [
        "WIZARD_COMPLETE"
      ],
      entityAware: false
    },
    "wizard-progress": {
      emits: [
        {
          event: "WIZARD_STEP_CLICK",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              step: "number"
            }
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    "wizard-navigation": {
      emits: [
        {
          event: "WIZARD_NEXT",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_BACK",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "WIZARD_COMPLETE",
          trigger: "click",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [],
      entityAware: false,
      configDriven: true
    },
    pagination: {
      emits: [
        {
          event: "PAGE_CHANGE",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              page: "number"
            }
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    modal: {
      emits: [
        {
          event: "CLOSE",
          trigger: "close",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [
        "CLOSE"
      ],
      entityAware: false
    },
    drawer: {
      emits: [
        {
          event: "CLOSE",
          trigger: "close",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [
        "CLOSE"
      ],
      entityAware: false
    },
    "confirm-dialog": {
      emits: [
        {
          event: "CONFIRM",
          trigger: "click",
          payload: {
            type: "void"
          }
        },
        {
          event: "CANCEL",
          trigger: "click",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [
        "CONFIRM",
        "CANCEL"
      ],
      entityAware: false
    },
    card: {
      emits: [
        {
          event: "CLICK",
          trigger: "click",
          payload: {
            type: "void"
          },
          optional: true
        }
      ],
      requires: [],
      entityAware: false
    },
    "runtime-debugger": {
      emits: [],
      requires: [],
      entityAware: false
    },
    "repeatable-form-section": {
      emits: [
        {
          event: "SECTION_ADDED",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SECTION_REMOVED",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "studio-home-web": {
      emits: [
        {
          event: "CREATE_PROJECT",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "SELECT_PROJECT",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              project: "object"
            }
          }
        },
        {
          event: "DELETE_PROJECT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              project: "object"
            }
          }
        },
        {
          event: "OPEN_PROJECT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              project: "object"
            }
          }
        },
        {
          event: "SEARCH",
          trigger: "input",
          payload: {
            type: "object",
            shape: {
              query: "string"
            }
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "studio-home-electron": {
      emits: [
        {
          event: "OPEN_FILE",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "CREATE_NEW",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "OPEN_RECENT",
          trigger: "click",
          payload: {
            type: "object",
            shape: {
              file: "object"
            }
          }
        },
        {
          event: "REMOVE_RECENT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              file: "object"
            }
          }
        },
        {
          event: "CLEAR_RECENT",
          trigger: "action",
          payload: {
            type: "void"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "studio-project": {
      emits: [
        {
          event: "MODE_CHANGE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              mode: "string"
            }
          }
        },
        {
          event: "TAB_CHANGE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              tab: "string"
            }
          }
        },
        {
          event: "SAVE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "VALIDATE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "COMPILE",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "PREVIEW",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              projectId: "string"
            }
          }
        },
        {
          event: "BACK",
          trigger: "action",
          payload: {
            type: "void"
          }
        },
        {
          event: "TOGGLE_AGENT",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              visible: "boolean"
            }
          }
        },
        {
          event: "TOGGLE_INSPECTOR",
          trigger: "action",
          payload: {
            type: "object",
            shape: {
              visible: "boolean"
            }
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "drawer-slot": {
      emits: [
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CANCEL",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "modal-slot": {
      emits: [
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CANCEL",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "toast-slot": {
      emits: [
        {
          event: "DISMISS",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "document-viewer": {
      emits: [
        {
          event: "DOCUMENT_DOWNLOAD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "DOCUMENT_PRINT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "DOCUMENT_PAGE_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "media-gallery": {
      emits: [
        {
          event: "MEDIA_SELECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    table: {
      emits: [
        {
          event: "SORT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SELECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "DESELECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "PAGINATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SEARCH",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "code-block": {
      emits: [
        {
          event: "COPY_CODE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "EDITOR_FOCUS",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "EDITOR_BLUR",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "ui-slot-renderer": {
      emits: [
        {
          event: "CLOSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CANCEL",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "game-menu": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "trait-slot": {
      emits: [
        {
          event: "CLICK",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "REMOVE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "activation-block": {
      emits: [
        {
          event: "SAVE_ACTIVATION",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "bloom-quiz-block": {
      emits: [
        {
          event: "ANSWER_BLOOM",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "reflection-block": {
      emits: [
        {
          event: "SAVE_REFLECTION",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "code-runner-panel": {
      emits: [
        {
          event: "COPY_CODE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "chat-bar": {
      emits: [
        {
          event: "CHAT_SEND",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "form-layout": {
      emits: [
        {
          event: "TOGGLE_COLLAPSE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "data-grid": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "dashboard-layout": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "data-list": {
      emits: [
        {
          event: "NAVIGATE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: true
    },
    "flow-canvas": {
      emits: [
        {
          event: "WIRE_VALIDATION_WARNING",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    },
    "orb-inspector": {
      emits: [
        {
          event: "NOTIFY",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "PROP_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "ADD_FIELD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "UPDATE_FIELD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "REMOVE_FIELD",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "GUARD_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "ADD_EFFECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "REMOVE_EFFECT",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "CODE_CHANGE",
          trigger: "action",
          payload: {
            type: "object"
          }
        },
        {
          event: "SERVICE_MODE_TOGGLE",
          trigger: "action",
          payload: {
            type: "object"
          }
        }
      ],
      requires: [],
      entityAware: false
    }
  }
};

// src/patterns/pattern-types.ts
var PATTERN_TYPES = [
  "accordion",
  "action-palette",
  "action-tile",
  "activation-block",
  "agent-activity-feed",
  "agent-activity-row",
  "agent-chat-feed",
  "alert",
  "algo-graph-canvas",
  "algorithm-canvas",
  "animated-counter",
  "animated-graphic",
  "animated-reveal",
  "aside",
  "atlas-image",
  "atlas-panel",
  "auth-layout",
  "avatar",
  "avl-glyph",
  "avl-state-machine",
  "avl-transition-explainer",
  "badge",
  "behavior-view",
  "biology-canvas",
  "bloom-quiz-block",
  "box",
  "branching-logic-builder",
  "breadcrumb",
  "button",
  "calendar-grid",
  "canvas",
  "canvas-2d",
  "card",
  "carousel",
  "center",
  "change-list",
  "chart",
  "chart-legend",
  "chat-bar",
  "checkbox",
  "chemistry-canvas",
  "choice-button",
  "code-block",
  "code-runner-panel",
  "command-palette",
  "commit-row",
  "conditional-wrapper",
  "confetti-effect",
  "confirm-dialog",
  "connection-block",
  "container",
  "content-renderer",
  "control-button",
  "control-grid",
  "counter-template",
  "dashboard-grid",
  "dashboard-layout",
  "data-grid",
  "data-list",
  "date-range-picker",
  "date-range-selector",
  "day-cell",
  "detail-panel",
  "dialog",
  "dialogue-bubble",
  "divider",
  "doc-breadcrumb",
  "doc-pagination",
  "doc-search",
  "doc-sidebar",
  "doc-toc",
  "dock-layout",
  "document-details",
  "document-panel",
  "document-viewer",
  "draw-fx-layer",
  "draw-group",
  "draw-mesh",
  "draw-shape",
  "draw-shape-layer",
  "draw-skinned-mesh",
  "draw-sprite",
  "draw-sprite-layer",
  "draw-text",
  "draw-text-layer",
  "drawer",
  "drawer-slot",
  "edge-decoration",
  "emoji-picker",
  "empty-state",
  "error-boundary",
  "error-state",
  "feature-card",
  "feature-grid",
  "file-tree",
  "filter-group",
  "filter-pill",
  "flex",
  "flip-card",
  "flip-container",
  "floating-action-button",
  "floating-toolbar",
  "flow-canvas",
  "form",
  "form-actions",
  "form-field",
  "form-layout",
  "form-section",
  "form-section-header",
  "fx-overlay",
  "game-audio-cue",
  "game-audio-toggle",
  "game-hud",
  "game-icon",
  "game-menu",
  "game-shell",
  "gantt",
  "generic-app-template",
  "geometric-pattern",
  "gradient-divider",
  "graph-canvas",
  "graph-view",
  "grid",
  "header",
  "health-bar",
  "hstack",
  "icon",
  "icon-button",
  "image",
  "import-preview-tree",
  "import-progress",
  "import-source-picker",
  "infinite-scroll-sentinel",
  "input",
  "input-group",
  "jazari-state-machine",
  "label",
  "law-reference-tooltip",
  "layers-panel",
  "learning-canvas",
  "lightbox",
  "likert-scale",
  "line-chart",
  "loading-state",
  "map-view",
  "markdown-content",
  "master-detail-layout",
  "math-canvas",
  "matrix-question",
  "media-gallery",
  "menu",
  "meter",
  "modal",
  "modal-slot",
  "module-card",
  "navigation",
  "notification",
  "number-stepper",
  "option-constraint-group",
  "orb-inspector",
  "orbital-visualization",
  "overlay",
  "page-header",
  "page-transition",
  "pagination",
  "pattern-tile",
  "physics-canvas",
  "popover",
  "positioned-canvas",
  "presence",
  "progress-bar",
  "progress-dots",
  "pull-to-refresh",
  "qr-scanner",
  "quiz-block",
  "radio",
  "range-slider",
  "reflection-block",
  "relation-select",
  "repeatable-form-section",
  "reply-tree",
  "rich-text-editor",
  "runtime-debugger",
  "scaled-diagram",
  "score-display",
  "search-input",
  "section",
  "section-header",
  "segment-renderer",
  "select",
  "sequence-bar",
  "side-panel",
  "sidebar",
  "signature-pad",
  "simple-grid",
  "skeleton",
  "social-proof",
  "sortable-list",
  "spacer",
  "sparkline",
  "spinner",
  "split",
  "split-pane",
  "stack",
  "star-rating",
  "stat-badge",
  "stat-card",
  "stat-display",
  "state-graph",
  "state-json-view",
  "state-machine-view",
  "status-dot",
  "subagent-trace-panel",
  "svg-branch",
  "svg-connection",
  "svg-flow",
  "svg-grid",
  "svg-lobe",
  "svg-mesh",
  "svg-morph",
  "svg-node",
  "svg-pulse",
  "svg-ring",
  "svg-shield",
  "svg-stack",
  "swipeable-row",
  "switch",
  "tabbed-container",
  "table-view",
  "tabs",
  "tag-input",
  "text-highlight",
  "textarea",
  "theme-toggle",
  "time-slot-cell",
  "timeline",
  "timer-display",
  "toast-slot",
  "tooltip",
  "top-nav-item",
  "trait-frame",
  "trait-slot",
  "trend-indicator",
  "typewriter-text",
  "typography",
  "ui-slot-component",
  "ui-slot-renderer",
  "upload-drop-zone",
  "version-diff",
  "violation-alert",
  "vote-stack",
  "vstack",
  "wizard-container",
  "wizard-navigation",
  "wizard-progress"
];
function isValidPatternType(type) {
  return PATTERN_TYPES.includes(type);
}

// src/patterns/content-grade.ts
function isContentBodyPatternType(patternType) {
  const entry = patterns_registry_default.patterns?.[patternType];
  if (!entry?.propsSchema) return false;
  return Object.values(entry.propsSchema).some((def) => def.kind === "entity");
}
function isContentBodyPattern(pattern) {
  if (pattern === null) return false;
  if (typeof pattern === "string") return true;
  if ("children" in pattern && Array.isArray(pattern.children) && pattern.children.length > 0) return true;
  return isContentBodyPatternType(pattern.type);
}
function isMainSlotRenderUi(effect) {
  return Array.isArray(effect) && effect[0] === "render-ui" && effect[1] === "main";
}

// src/patterns/self-overlay.ts
var SELF_OVERLAY_PATTERN_TYPES = /* @__PURE__ */ new Set(["modal", "confirm-dialog"]);

// src/patterns/types.ts
var PROP_KINDS = [
  "event",
  "event-ref",
  "event-listen",
  "event-list",
  "event-map",
  "callback",
  "entity",
  "entry",
  "skeleton"
];

// src/patterns/helpers/prompt-helpers.ts
function getPatternsGroupedByCategory() {
  const patterns = patterns_registry_default.patterns || {};
  const grouped = {};
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const category = patternDef.category || "uncategorized";
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(patternName);
  }
  return grouped;
}
function getPatternPropsCompact() {
  const patterns = patterns_registry_default.patterns || {};
  const lines = [
    "| Pattern | Key Props |",
    "|---------|-----------|"
  ];
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const propsSchema = patternDef.propsSchema || {};
    const requiredProps = Object.keys(propsSchema).filter(
      (key) => propsSchema[key]?.required === true
    );
    const keyProps = requiredProps.length > 0 ? requiredProps.join(", ") : Object.keys(propsSchema).slice(0, 3).join(", ");
    lines.push(`| ${patternName} | ${keyProps} |`);
  }
  return lines.join("\n");
}
function getPatternActionsRef() {
  const patterns = patterns_registry_default.patterns || {};
  const lines = [
    "## Pattern Action Props",
    "",
    "| Pattern | Action Props | Notes |",
    "|---------|-------------|-------|"
  ];
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const propsSchema = patternDef.propsSchema || {};
    const actionProps = [];
    for (const [propName, propDef] of Object.entries(propsSchema)) {
      if (propName.includes("action") || propName.includes("Action") || propName.includes("event") || propName.includes("Event") || propName === "onSubmit" || propName === "onClick") {
        actionProps.push(propName);
      }
    }
    if (actionProps.length > 0) {
      const notes = patternDef.description || "";
      lines.push(`| ${patternName} | ${actionProps.join(", ")} | ${notes.slice(0, 50)} |`);
    }
  }
  return lines.join("\n");
}
function generatePatternDescription(patternType) {
  const patterns = patterns_registry_default.patterns || {};
  const patternDef = patterns[patternType];
  if (!patternDef) {
    return patternType;
  }
  const desc = patternDef.description;
  if (desc) {
    return desc;
  }
  const propsSchema = patternDef.propsSchema || {};
  const propCount = Object.keys(propsSchema).length;
  const requiredProps = Object.keys(propsSchema).filter(
    (key) => propsSchema[key]?.required === true
  );
  if (requiredProps.length > 0) {
    return `${patternType} (requires: ${requiredProps.join(", ")})`;
  }
  return `${patternType} (${propCount} props)`;
}
function getAllPatternTypes() {
  const patterns = patterns_registry_default.patterns || {};
  return Object.keys(patterns);
}
function getPatternMetadata(patternType) {
  const patterns = patterns_registry_default.patterns || {};
  return patterns[patternType] || null;
}
var ORB_ALLOWED_ENTITY_PATTERNS = /* @__PURE__ */ new Set([
  "data-list",
  "data-grid",
  "search-input",
  "form-section",
  "meter",
  "table-view",
  "filter-group",
  "timeline",
  "media-gallery"
]);
var ORB_EXCLUDED_CATEGORIES = /* @__PURE__ */ new Set([
  "debug",
  "template"
]);
function getOrbAllowedPatterns() {
  const patterns = patterns_registry_default.patterns || {};
  const grouped = {};
  for (const [name, def] of Object.entries(patterns)) {
    const cat = def.category || "uncategorized";
    if (ORB_EXCLUDED_CATEGORIES.has(cat)) continue;
    if (name.includes("3-d") || name.includes("3d")) continue;
    const hasEntity = def.propsSchema && "entity" in def.propsSchema;
    if (hasEntity && !ORB_ALLOWED_ENTITY_PATTERNS.has(name)) continue;
    const propsSchema = def.propsSchema || {};
    const keyProps = Object.keys(propsSchema).slice(0, 5);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({
      name,
      description: def.description || "",
      keyProps
    });
  }
  return grouped;
}
function getOrbAllowedPatternsCompact() {
  const grouped = getOrbAllowedPatterns();
  const lines = [];
  for (const [cat, items] of Object.entries(grouped).sort()) {
    lines.push(`#### ${cat} (${items.length})`);
    lines.push("| Pattern | Description | Key Props |");
    lines.push("|---------|-------------|-----------|");
    for (const item of items) {
      const desc = item.description.split("\n")[0].slice(0, 60);
      lines.push(`| \`${item.name}\` | ${desc} | ${item.keyProps.join(", ")} |`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
function getOrbAllowedPatternsSlim() {
  const grouped = getOrbAllowedPatterns();
  const lines = [];
  for (const [cat, items] of Object.entries(grouped).sort()) {
    lines.push(`## ${cat}`);
    for (const item of items) {
      const desc = item.description.split("\n")[0].slice(0, 80);
      lines.push(`- ${item.name}: ${desc}`);
    }
  }
  return lines.join("\n");
}
function getOrbAllowedPatternsFiltered(patternNames) {
  const nameSet = new Set(patternNames);
  const patterns = patterns_registry_default.patterns || {};
  const grouped = {};
  for (const [name, def] of Object.entries(patterns)) {
    if (!nameSet.has(name)) continue;
    const cat = def.category || "uncategorized";
    const propsSchema = def.propsSchema || {};
    const allProps = Object.keys(propsSchema);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({
      name,
      description: def.description || "",
      allProps
    });
  }
  const lines = [];
  for (const [cat, items] of Object.entries(grouped).sort()) {
    lines.push(`#### ${cat} (${items.length})`);
    lines.push("| Pattern | Description | Props |");
    lines.push("|---------|-------------|-------|");
    for (const item of items) {
      const desc = item.description.split("\n")[0].slice(0, 60);
      lines.push(`| \`${item.name}\` | ${desc} | ${item.allProps.join(", ")} |`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

// src/patterns/helpers/render-ui-pattern-types.ts
function collectPatternTypeTokens(node, out) {
  if (!isJsonObject(node)) return;
  const type = node["type"];
  if (typeof type === "string" && !type.startsWith("@")) out.add(type);
  const children = node["children"];
  if (isJsonArray(children)) {
    for (const child of children) collectPatternTypeTokens(child, out);
  }
}
function collectRenderUiPatternTypes(ir, out) {
  if (isJsonArray(ir)) {
    if (ir.length >= 3 && ir[0] === "render-ui" && typeof ir[1] === "string") {
      collectPatternTypeTokens(ir[2], out);
    }
    for (const item of ir) collectRenderUiPatternTypes(item, out);
  } else if (isJsonObject(ir)) {
    for (const value of Object.values(ir)) collectRenderUiPatternTypes(value, out);
  }
}
function renderUiPatternTypesOf(ir) {
  const out = /* @__PURE__ */ new Set();
  collectRenderUiPatternTypes(ir, out);
  return out;
}
function isPatternNode(value) {
  return value !== null && value !== void 0 && typeof value === "object" && !Array.isArray(value);
}
function collectRenderUiEntries(effect, out) {
  if (!Array.isArray(effect)) return;
  const head = effect[0];
  if (head === "render-ui") {
    const slot = effect[1];
    const pattern = effect[2];
    if (typeof slot === "string" && isPatternNode(pattern)) out.push({ slot, pattern });
    return;
  }
  for (const child of controlBody(effect)) collectRenderUiEntries(child, out);
}
function controlBody(effect) {
  switch (effect[0]) {
    case "do":
    case "atomic":
    case "async/all":
    case "async/sequence":
    case "async/race":
      return effect.slice(1);
    case "if":
    case "when":
    case "async/delay":
    case "async/debounce":
    case "async/throttle":
    case "async/interval":
      return effect.slice(2);
    case "let":
      return effect.slice(2);
    default:
      return [];
  }
}
function renderUiEntriesOf(effects) {
  const out = [];
  for (const effect of effects) collectRenderUiEntries(effect, out);
  return out;
}

// src/patterns/helpers/pattern-recommender.ts
var DOMAIN_KEYWORDS = {
  business: ["admin panels", "data-dense views", "list pages", "data entry", "comparisons"],
  dashboard: ["dashboards", "analytics", "overview pages", "overview screens", "data visualization", "reporting", "KPI indicators"],
  "e-commerce": ["visual items", "images", "previews", "data entry", "list pages"],
  content: ["visual items", "images", "previews", "media", "pages with multiple views"],
  social: ["activity logs", "visual items", "previews"],
  workflow: ["process tracking", "workflow steps", "multi-step forms", "wizards"],
  form: ["data entry", "create forms", "edit forms", "multi-step forms"],
  game: ["game", "real-time", "interactive"]
};
var EVENT_KEYWORDS = {
  INIT: ["list pages", "overview pages", "dashboards", "admin panels"],
  CREATE: ["create forms", "data entry", "multi-step forms"],
  EDIT: ["edit forms", "data entry"],
  VIEW: ["detail pages", "entity management", "pages with multiple views"],
  DELETE: ["destructive actions", "confirmations"],
  FILTER: ["filterable data", "search functionality", "quick filters"]
};
var SLOT_CATEGORY_AFFINITY = {
  main: ["display", "dashboard", "filter", "header", "layout", "visualization", "navigation", "template"],
  modal: ["form", "container", "state"],
  drawer: ["display", "form", "navigation"],
  sidebar: ["navigation", "filter"],
  overlay: ["container", "state"]
};
var STATE_CATEGORY_AFFINITY = {
  Browsing: ["display", "filter", "header", "dashboard", "visualization"],
  Creating: ["form", "navigation"],
  Editing: ["form", "navigation"],
  Viewing: ["display", "navigation"],
  Deleting: ["container", "state"]
};
function recommendPatterns(context, maxResults = 10) {
  const patterns = patterns_registry_default.patterns || {};
  const scores = /* @__PURE__ */ new Map();
  const domainKeywords = context.domainCategory ? DOMAIN_KEYWORDS[context.domainCategory] || [] : [];
  const eventKeywords = context.event ? EVENT_KEYWORDS[context.event] || [] : [];
  const slotCategories = context.slot ? SLOT_CATEGORY_AFFINITY[context.slot] || [] : [];
  const stateCategories = context.state ? STATE_CATEGORY_AFFINITY[context.state] || [] : [];
  for (const [patternName, patternDef] of Object.entries(patterns)) {
    const suggestedFor = patternDef.suggestedFor || [];
    const category = patternDef.category || "";
    let score = 0;
    const reasons = [];
    if (category === "game" && context.domainCategory !== "game") continue;
    if (category === "debug") continue;
    if (category === "template") continue;
    for (const keyword of domainKeywords) {
      if (suggestedFor.some((sf) => sf.toLowerCase().includes(keyword.toLowerCase()))) {
        score += 30;
        reasons.push(`domain:${keyword}`);
        break;
      }
    }
    for (const keyword of eventKeywords) {
      if (suggestedFor.some((sf) => sf.toLowerCase().includes(keyword.toLowerCase()))) {
        score += 25;
        reasons.push(`event:${keyword}`);
        break;
      }
    }
    if (slotCategories.includes(category)) {
      score += 20;
      reasons.push(`slot:${context.slot}`);
    }
    if (stateCategories.includes(category)) {
      score += 15;
      reasons.push(`state:${context.state}`);
    }
    if (context.hasEnumFields && ["filter-group", "tabs", "badge"].includes(patternName)) {
      score += 10;
      reasons.push("enum-fields");
    }
    if (context.hasDateFields && ["timeline", "chart"].includes(patternName)) {
      score += 10;
      reasons.push("date-fields");
    }
    if (context.hasNumericFields && ["stats", "chart", "meter", "progress-bar"].includes(patternName)) {
      score += 10;
      reasons.push("numeric-fields");
    }
    if (context.hasRelationFields && ["tabs", "master-detail-layout"].includes(patternName)) {
      score += 10;
      reasons.push("relation-fields");
    }
    if (context.hasMediaFields && ["data-grid", "media-gallery"].includes(patternName)) {
      score += 10;
      reasons.push("media-fields");
    }
    if (category === "display" || category === "dashboard") {
      const propsSchema = patternDef.propsSchema || {};
      if ("entity" in propsSchema) {
        score += 5;
        reasons.push("entity-aware");
      }
    }
    if (score > 0) {
      scores.set(patternName, { score, reasons });
    }
  }
  const ranked = [...scores.entries()].sort((a, b) => b[1].score - a[1].score).slice(0, maxResults);
  return ranked.map(([patternName, { score, reasons }]) => {
    const patternDef = patterns[patternName];
    const propsSchema = patternDef.propsSchema || {};
    const keyProps = Object.keys(propsSchema).filter((k) => propsSchema[k]?.required === true).slice(0, 5);
    if (keyProps.length === 0) {
      const meaningful = Object.keys(propsSchema).filter(
        (k) => !["className", "isLoading", "error", "entity"].includes(k)
      );
      keyProps.push(...meaningful.slice(0, 4));
    }
    return {
      pattern: patternName,
      score,
      reason: reasons.join(", "),
      category: patternDef.category || "unknown",
      description: patternDef.description || patternName,
      keyProps
    };
  });
}
function buildRecommendationContext(options) {
  const fields = options.entityFields || [];
  const fieldTypes = fields.map((f) => f.type);
  return {
    state: options.state,
    event: options.event,
    slot: options.slot,
    domainCategory: options.domainCategory,
    entityFieldTypes: fieldTypes,
    // Excludes `type: 'union'`: its `values` carries tagged-union variant
    // NAMES or a by-name recursive back-reference, never a real enum
    // vocabulary — counting it here would recommend enum-oriented patterns
    // (filter-group/tabs/badge) for a struct union or recursive struct field.
    hasEnumFields: fields.some((f) => f.type === "enum" || f.type !== "union" && f.values && f.values.length > 0),
    hasDateFields: fields.some((f) => ["date", "datetime", "timestamp"].includes(f.type)),
    hasNumericFields: fields.some((f) => ["number", "integer", "float", "decimal", "currency"].includes(f.type)),
    hasRelationFields: fields.some((f) => f.type === "relation"),
    hasMediaFields: fields.some((f) => ["image", "url", "media", "file"].includes(f.type))
  };
}
function formatRecommendationsForPrompt(recommendations) {
  if (recommendations.length === 0) return "";
  const lines = [
    "## Recommended Patterns",
    "",
    "| Pattern | Category | Key Props | Why |",
    "|---------|----------|-----------|-----|"
  ];
  for (const rec of recommendations) {
    const props = rec.keyProps.length > 0 ? rec.keyProps.join(", ") : "\u2014";
    lines.push(`| ${rec.pattern} | ${rec.category} | ${props} | ${rec.reason} |`);
  }
  lines.push("");
  lines.push("Use these patterns where appropriate. Combine multiple for rich INIT transitions.");
  return lines.join("\n");
}

// src/patterns/helpers/pattern-swap.ts
var PATTERNS = patterns_registry_default;
var CONTRACTS = event_contracts_default;
function entityInlet(patternType) {
  const propsSchema = PATTERNS.patterns?.[patternType]?.propsSchema;
  if (!propsSchema) return null;
  for (const prop of Object.values(propsSchema)) {
    if (prop.kind === "entity") return prop;
  }
  return propsSchema["entity"] ?? null;
}
function getEntityCardinality(patternType) {
  return entityInlet(patternType)?.cardinality ?? null;
}
function getEmittedEvents(patternType) {
  const emits = CONTRACTS.contracts?.[patternType]?.emits ?? [];
  const out = [];
  for (const e of emits) {
    if (typeof e.event === "string") out.push(e.event);
  }
  return out;
}
function findCompatiblePatterns(currentType, gate = {}) {
  const cardinality = getEntityCardinality(currentType);
  const required = gate.requiredEmits ?? [];
  const result = [];
  for (const type of Object.keys(PATTERNS.patterns ?? {})) {
    if (type === currentType) continue;
    if (getEntityCardinality(type) !== cardinality) continue;
    if (required.length > 0) {
      const emits = new Set(getEmittedEvents(type));
      if (!required.every((ev) => emits.has(ev))) continue;
    }
    result.push(type);
  }
  return result;
}

// src/patterns/index.ts
var registry = patterns_registry_default;
var PATTERN_REGISTRY = patterns_registry_default;
var INTEGRATORS_REGISTRY = integrators_registry_default;
var RUNTIMES_BY_SERVICE = new Map(
  Object.entries(integrators_registry_default.integrators).map(([name, entry]) => [name, entry.runtimes])
);
function isBrowserOnlyService(service) {
  const runtimes = RUNTIMES_BY_SERVICE.get(service) ?? [];
  return runtimes.includes("browser") && !runtimes.includes("node");
}
var COMPONENT_MAPPING = component_mapping_default;
var EVENT_CONTRACTS = event_contracts_default;
function getPatternDefinition(patternType) {
  return patterns_registry_default.patterns?.[patternType] ?? null;
}
function getComponentForPattern(patternType) {
  const mapping = component_mapping_default;
  return mapping.mappings?.[patternType]?.component ?? null;
}
function isEntityAwarePattern(patternType) {
  const definition = getPatternDefinition(patternType);
  if (!definition) return false;
  if (definition.entityAware === true) return true;
  const propsSchema = definition.propsSchema;
  if (!propsSchema) return false;
  return "entity" in propsSchema;
}
var EVENT_OUTLET_KINDS = /* @__PURE__ */ new Set(["event", "event-ref", "callback"]);
var eventKeyPropsCache = /* @__PURE__ */ new Map();
var eventListPropsCache = /* @__PURE__ */ new Map();
function eventKeyPropsOf(patternType) {
  const cached = eventKeyPropsCache.get(patternType);
  if (cached) return cached;
  const propsSchema = getPatternDefinition(patternType)?.propsSchema;
  const out = /* @__PURE__ */ new Set();
  for (const [prop, def] of Object.entries(propsSchema ?? {})) {
    if (def.kind && EVENT_OUTLET_KINDS.has(def.kind)) out.add(prop);
  }
  eventKeyPropsCache.set(patternType, out);
  return out;
}
function isValueInputPattern(patternType) {
  const propsSchema = getPatternDefinition(patternType)?.propsSchema;
  if (!propsSchema || !("value" in propsSchema)) return false;
  return eventKeyPropsOf(patternType).size > 0;
}
function eventListPropsOf(patternType) {
  const cached = eventListPropsCache.get(patternType);
  if (cached) return cached;
  const propsSchema = getPatternDefinition(patternType)?.propsSchema;
  const out = /* @__PURE__ */ new Map();
  for (const [prop, def] of Object.entries(propsSchema ?? {})) {
    if (def.kind === "event-list") out.set(prop, def.eventField ?? "event");
  }
  eventListPropsCache.set(patternType, out);
  return out;
}
function getPatternFieldsContract(patternType) {
  return getPatternDefinition(patternType)?.fieldsContract;
}
function getPatternMinUsableWidths() {
  const out = /* @__PURE__ */ new Map();
  for (const [name, entry] of Object.entries(patterns_registry_default.patterns ?? {})) {
    if (typeof entry.minUsableWidth === "number") out.set(name, entry.minUsableWidth);
  }
  return out;
}
function isDrawablePattern(patternType) {
  const definition = getPatternDefinition(patternType);
  if (!definition) return false;
  if (definition.drawable === true) return true;
  const propsSchema = definition.propsSchema;
  if (!propsSchema) return false;
  return Object.values(propsSchema).some(propGroundsDrawable);
}
function propGroundsDrawable(schema) {
  if (schema.scenePos) return true;
  if (schema.items && propGroundsDrawable(schema.items)) return true;
  if (schema.mapValue && propGroundsDrawable(schema.mapValue)) return true;
  if (schema.properties) {
    for (const v of Object.values(schema.properties)) {
      if (v && propGroundsDrawable(v)) return true;
    }
  }
  return false;
}
function isDrawHostPattern(patternType) {
  const definition = getPatternDefinition(patternType);
  if (!definition) return false;
  if (definition.drawHost === true) return true;
  const propsSchema = definition.propsSchema;
  if (!propsSchema) return false;
  return Object.values(propsSchema).some(propHostsDrawables);
}
function propHostsDrawables(schema) {
  if (schema.drawableSlot) return true;
  if (schema.items && propHostsDrawables(schema.items)) return true;
  if (schema.mapValue && propHostsDrawables(schema.mapValue)) return true;
  if (schema.properties) {
    for (const v of Object.values(schema.properties)) {
      if (v && propHostsDrawables(v)) return true;
    }
  }
  return false;
}

// src/types/pattern.ts
var PatternTypeSchema = z.string();

// src/types/changeset.ts
var SEMANTIC_CHANGE_KINDS = [
  "orbital-added",
  "orbital-removed",
  "entity-fields-changed",
  "trait-added",
  "trait-removed",
  "trait-config-changed",
  "state-machine-changed",
  "guard-changed",
  "effect-changed",
  "render-ui-changed",
  "event-wiring-changed",
  "page-changed",
  "theme-changed",
  "behavior-composed"
];

// src/service-types.ts
function createTypedEventBus(bus) {
  return {
    emit(event, payload) {
      bus.emit(event, payload);
    },
    on(event, handler) {
      return bus.on(event, handler);
    }
  };
}
function createLazyService(factory) {
  let instance = null;
  return {
    get() {
      if (instance === null) {
        instance = factory();
      }
      return instance;
    },
    reset() {
      instance = null;
    }
  };
}

// src/types/ir.ts
function createEmptyResolvedTrait(name, source) {
  return {
    name,
    source,
    states: [],
    events: [],
    transitions: [],
    guards: [],
    ticks: [],
    listens: [],
    dataEntities: []
  };
}
function createEmptyResolvedPage(name) {
  return {
    name,
    path: `/${name.toLowerCase()}`,
    featureName: name.toLowerCase(),
    sections: [],
    traits: [],
    entityBindings: [],
    navigation: [],
    singletonEntities: []
  };
}
function inferTsType(schemaType) {
  const typeMap = {
    string: "string",
    number: "number",
    boolean: "boolean",
    date: "Date",
    datetime: "Date",
    timestamp: "number",
    array: "unknown[]",
    object: "Record<string, unknown>",
    any: "unknown",
    // Renderable UI content — the canonical pattern-tree type from
    // render-ui-edit.ts. Without this entry the raw tag `node` would leak
    // out as a (nonexistent) TS type name.
    node: "PatternNode | PatternNode[]"
  };
  if (schemaType.endsWith("[]")) {
    const baseType = schemaType.slice(0, -2);
    return `${typeMap[baseType] || baseType}[]`;
  }
  if (schemaType.startsWith("Map<") && schemaType.endsWith(">")) {
    const inner = schemaType.slice(4, -1);
    const comma = inner.indexOf(",");
    if (comma !== -1) {
      const key = inferTsType(inner.slice(0, comma).trim());
      const value = inferTsType(inner.slice(comma + 1).trim());
      return `Record<${key}, ${value}>`;
    }
  }
  return typeMap[schemaType] || schemaType;
}
function createResolvedField(field) {
  return {
    name: field.name,
    type: field.type,
    tsType: inferTsType(field.type),
    description: field.description,
    default: field.default,
    required: field.required ?? false,
    validation: field.validation,
    values: field.values
  };
}
function isResolvedIR(ir) {
  if (!ir || typeof ir !== "object") return false;
  const r = ir;
  return typeof r.appName === "string" && r.traits instanceof Map && r.pages instanceof Map;
}
var ServerBatchSummarySchema = z.object({
  operations: z.array(EntityRowSchema),
  completedCount: z.number(),
  totalCount: z.number()
});
var ServerEffectResultSchema = z.object({
  effect: z.union([z.enum(SERVER_REPORTED_EFFECTS), z.literal("substrate")]),
  action: z.string().optional(),
  entityType: z.string().optional(),
  name: z.custom((v) => typeof v === "string" && /^[^/]+\/.+$/.test(v)).optional(),
  data: z.union([EntityRowSchema, ServerBatchSummarySchema, EventPayloadValueSchema]).optional(),
  success: z.boolean(),
  denied: z.literal(true).optional(),
  error: z.string().optional(),
  code: z.enum(["not-found", "no-row-key"]).optional()
});

// src/types/bus.ts
function deliveryRecordValue(record) {
  const s = record.source;
  const source = {
    ...s.orbital !== void 0 ? { orbital: s.orbital } : {},
    ...s.orbitalId !== void 0 ? { orbitalId: s.orbitalId } : {},
    ...s.trait !== void 0 ? { trait: s.trait } : {},
    ...s.traitId !== void 0 ? { traitId: s.traitId } : {},
    ...s.eventId !== void 0 ? { eventId: s.eventId } : {},
    ...s.transition !== void 0 ? { transition: s.transition } : {},
    ...s.tick !== void 0 ? { tick: s.tick } : {},
    ...s.fromBridge !== void 0 ? { fromBridge: s.fromBridge } : {},
    ...s.dispatched !== void 0 ? { dispatched: s.dispatched } : {},
    ...s.originClientId !== void 0 ? { originClientId: s.originClientId } : {}
  };
  return { event: record.event, payload: record.payload ?? null, source };
}
var BusEventSourceSchema = z.object({
  orbital: z.string().optional(),
  orbitalId: OrbitalIdSchema.optional(),
  trait: z.string().optional(),
  traitId: TraitIdSchema.optional(),
  eventId: EventIdSchema.optional(),
  transition: z.string().optional(),
  tick: z.string().optional(),
  fromBridge: z.boolean().optional(),
  dispatched: z.boolean().optional(),
  originClientId: z.string().optional(),
  pendingKey: z.string().optional()
});
var DeliveryRecordSchema = z.object({
  event: z.string(),
  payload: EventPayloadSchema.optional(),
  source: BusEventSourceSchema
});
var DispatchLogSchema = z.object({
  prevEvents: z.array(DeliveryRecordSchema),
  prevStates: z.array(z.string())
});
var EmittedEventSchema = z.object({
  event: z.string(),
  payload: EventPayloadSchema.optional(),
  source: BusEventSourceSchema.optional()
});
var ClientRenderUITupleSchema = z.union([
  z.tuple([z.literal("render-ui"), z.string(), PatternConfigSchema]),
  z.tuple([z.literal("render-ui"), z.string(), PatternConfigSchema, ResolvedPatternPropsSchema]),
  z.tuple([
    z.literal("render-ui"),
    z.string(),
    PatternConfigSchema,
    ResolvedPatternPropsSchema.optional(),
    z.number().optional()
  ])
]);
var ClientNavigateTupleSchema = z.union([
  z.tuple([z.literal("navigate"), z.string()]),
  z.tuple([z.literal("navigate"), z.string(), z.record(z.string()).optional()]),
  z.tuple([
    z.literal("navigate"),
    z.string(),
    z.record(z.string()).optional(),
    z.object({ crumb: z.string().optional() })
  ])
]);
var ClientNavigateBackTupleSchema = z.tuple([z.literal("navigate-back")]);
var ClientEffectTupleSchema = z.union([
  ClientRenderUITupleSchema,
  ClientNavigateTupleSchema,
  ClientNavigateBackTupleSchema
]);
var SYSTEM_UI_EVENTS = ["NAVIGATE"];
function isSystemUiEvent(event) {
  return SYSTEM_UI_EVENTS.includes(event);
}
var LIFECYCLE_EVENTS = ["INIT", "LOAD", "$MOUNT"];
var MountSeedSchema = z.object({ trait: z.string(), event: z.enum(LIFECYCLE_EVENTS) });
var OrbitalEventRequestSchema = z.object({
  event: z.string(),
  eventId: EventIdSchema.optional(),
  payload: EventPayloadSchema.optional(),
  entityId: z.string().optional(),
  targetTrait: z.string().optional(),
  user: RawUserClaimsSchema.optional(),
  clientId: z.string().optional(),
  locale: z.string().optional(),
  tick: z.string().optional(),
  sourceTrait: z.string().optional(),
  traits: z.array(z.object({ trait: z.string(), from: z.string() })).optional(),
  entityByTrait: z.record(EntityRowSchema).optional(),
  behavior: z.string().optional(),
  delivery: DeliveryRecordSchema.optional(),
  dispatchLog: DispatchLogSchema.optional(),
  mount: z.array(MountSeedSchema).optional()
});
var OrbitalEventResponseSchema = z.object({
  success: z.boolean(),
  transitioned: z.boolean(),
  states: z.record(z.string()),
  emittedEvents: z.array(EmittedEventSchema),
  data: z.record(z.array(EntityRowSchema)).optional(),
  entityByTrait: z.record(EntityRowSchema).optional(),
  clientEffects: z.array(ClientEffectTupleSchema).optional(),
  clientEffectsByTrait: z.array(z.object({
    traitName: z.string(),
    effect: ClientEffectTupleSchema,
    event: z.string().optional(),
    fromState: z.string().optional()
  })).optional(),
  effectResults: z.array(ServerEffectResultSchema).optional(),
  guardFailed: z.string().optional(),
  rejections: z.array(
    z.object({
      code: z.enum(["no-matching-transition", "guard-rejected", "no-dispatchable-traits", "not-an-external-input", "effect-failed"]),
      trait: z.string().optional(),
      from: z.string().optional(),
      event: z.string().optional(),
      statesDeclaringEvent: z.array(z.string()).optional(),
      transition: z.string().optional(),
      guard: z.unknown().optional(),
      error: z.string().optional()
    })
  ).optional(),
  cascadeTruncated: z.array(z.string()).optional(),
  error: z.string().optional()
});
var ServerTopologySchema = z.enum(["stateless", "stateful"]);
var OrbitalRegisterResponseSchema = z.object({
  success: z.boolean(),
  topology: ServerTopologySchema.optional(),
  message: z.string().optional(),
  orbitals: z.array(z.string()).optional(),
  error: z.string().optional()
});
var LiveBroadcastItemSchema = z.object({
  event: z.string(),
  payload: EventPayloadSchema.optional(),
  source: BusEventSourceSchema,
  originClientId: z.string().optional()
});
var EventDispatchRequestSchema = OrbitalEventRequestSchema;
var EventDispatchResponseSchema = OrbitalEventResponseSchema;

// src/types/visit-key.ts
function isPayloadList(value) {
  return Array.isArray(value);
}
function canonicalJson(value) {
  if (value === void 0 || value === null) return "null";
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (isPayloadList(value)) {
    return `[${value.map((item) => item === void 0 ? "null" : canonicalJson(item)).join(",")}]`;
  }
  if (typeof value === "object") {
    const record = value;
    const fields = Object.keys(record).filter((k) => record[k] !== void 0).sort().map((k) => `${JSON.stringify(k)}:${canonicalJson(record[k])}`);
    return `{${fields.join(",")}}`;
  }
  return JSON.stringify(value);
}
function dispatchVisitKey(trait, event, from, payload, frame) {
  return `${trait}\0${event}\0${from}\0${canonicalJson(payload)}\0${canonicalJson(frame)}`;
}

// src/types/dispatch-memory.ts
function dispatchScopeOf(delivery, log, event, payload) {
  const record = delivery ?? { event, ...payload !== void 0 ? { payload } : {}, source: {} };
  return {
    event: deliveryRecordValue(record),
    prevEvents: (log?.prevEvents ?? []).map(deliveryRecordValue),
    prevStates: [...log?.prevStates ?? []]
  };
}
var DispatchMemory = class {
  constructor() {
    this.deliveries = /* @__PURE__ */ new Map();
    this.left = /* @__PURE__ */ new Map();
  }
  /** This trait's log so far, as it travels on a request. */
  log(trait) {
    return { prevEvents: [...this.deliveries.get(trait) ?? []], prevStates: [...this.left.get(trait) ?? []] };
  }
  view(trait, delivery) {
    return dispatchScopeOf(delivery, this.log(trait), delivery.event, delivery.payload);
  }
  /** Continue a dispatch that began elsewhere: the target trait's log so far. */
  seed(trait, log) {
    this.deliveries.set(trait, [...log.prevEvents]);
    this.left.set(trait, [...log.prevStates]);
  }
  recordDelivery(trait, delivery) {
    const log = this.deliveries.get(trait) ?? [];
    log.push(delivery);
    this.deliveries.set(trait, log);
  }
  recordTransition(trait, from, to) {
    if (from === to) return;
    const log = this.left.get(trait) ?? [];
    log.push(from);
    this.left.set(trait, log);
  }
};

// src/types/verification.ts
var PATTERN_PROP_TYPE_ERROR_TESTID = "pattern-prop-type-error";
var TRAIT_MOUNT_ERROR_TESTID = "trait-mount-error";
var EMPTY_STATE_MARKER = "data-empty-state";
var LOADING_STATE_MARKER = "data-loading-state";
var VERIFICATION_DOM_ATTRS = {
  pattern: "data-pattern",
  rowId: "data-row-id",
  entityRow: "data-entity-row",
  entityId: "data-entity-id",
  fieldName: "data-field-name",
  event: "data-event"
};
var ACTION_TESTID_PREFIX = "action-";
var ACTION_OVERFLOW_TESTID = `${ACTION_TESTID_PREFIX}overflow`;
var FORM_PATTERN = "form-section";
function actionTestId(eventKey) {
  return `${ACTION_TESTID_PREFIX}${eventKey}`;
}
function actionTestIdMatches(testId, event) {
  if (!testId.startsWith(ACTION_TESTID_PREFIX)) return false;
  const key = testId.slice(ACTION_TESTID_PREFIX.length);
  return key === event || key.endsWith(`.${event}`);
}
var NATIVE_ID_PREFIX = {
  pattern: "pattern-",
  row: "row-",
  field: "field-",
  slot: "slot-"
};
function nativePatternId(name) {
  return `${NATIVE_ID_PREFIX.pattern}${name}`;
}
function nativeRowId(id) {
  return `${NATIVE_ID_PREFIX.row}${id}`;
}
function nativeFieldId(name) {
  return `${NATIVE_ID_PREFIX.field}${name}`;
}
function nativeSlotId(name) {
  return `${NATIVE_ID_PREFIX.slot}${name}`;
}

// src/types/validation.ts
var ORBITAL_PATH_PREFIX = /^orbitals\[(\d+)(?: \([^\]]*\))?\]/;
function orbitalIndexOfValidationPath(path) {
  if (Array.isArray(path)) {
    if (path[0] === "orbitals") return typeof path[1] === "number" ? path[1] : null;
    return typeof path[0] === "string" ? orbitalIndexOfValidationPath(path[0]) : null;
  }
  const m = typeof path === "string" ? ORBITAL_PATH_PREFIX.exec(path) : null;
  return m ? Number(m[1]) : null;
}
var KNOWN_VALIDATION_ERROR_CODES = {
  // Binding (`@entity.X`, `@payload.Y`, `@state.Z`, `@now`, ...)
  ORB_BINDING_ENTITY_FIELD_NEVER_SET: "ORB_BINDING_ENTITY_FIELD_NEVER_SET",
  ORB_BINDING_ENTITY_FIELD_NOT_FOUND: "ORB_BINDING_ENTITY_FIELD_NOT_FOUND",
  ORB_BINDING_INVALID_FIELD_NAME: "ORB_BINDING_INVALID_FIELD_NAME",
  ORB_BINDING_INVALID_FORMAT: "ORB_BINDING_INVALID_FORMAT",
  ORB_BINDING_INVALID_PATH: "ORB_BINDING_INVALID_PATH",
  ORB_BINDING_NOW_NO_PATH: "ORB_BINDING_NOW_NO_PATH",
  ORB_BINDING_PAYLOAD_FIELD_UNDECLARED: "ORB_BINDING_PAYLOAD_FIELD_UNDECLARED",
  ORB_BINDING_PAYLOAD_IN_TICK: "ORB_BINDING_PAYLOAD_IN_TICK",
  ORB_BINDING_DISPATCH_ROOT_IN_TICK: "ORB_BINDING_DISPATCH_ROOT_IN_TICK",
  ORB_BINDING_ROOT_NO_PATH: "ORB_BINDING_ROOT_NO_PATH",
  ORB_BINDING_EVENT_INVALID_PATH: "ORB_BINDING_EVENT_INVALID_PATH",
  ORB_BINDING_PAYLOAD_TYPE_INCOMPATIBLE: "ORB_BINDING_PAYLOAD_TYPE_INCOMPATIBLE",
  ORB_BINDING_SET_TARGET_MISSING_PATH: "ORB_BINDING_SET_TARGET_MISSING_PATH",
  ORB_BINDING_STATE_NO_PATH: "ORB_BINDING_STATE_NO_PATH",
  ORB_BINDING_TRAIT_CYCLE: "ORB_BINDING_TRAIT_CYCLE",
  ORB_BINDING_TRAIT_INVALID_FORMAT: "ORB_BINDING_TRAIT_INVALID_FORMAT",
  ORB_BINDING_TRAIT_INVALID_POSITION: "ORB_BINDING_TRAIT_INVALID_POSITION",
  ORB_BINDING_TRAIT_MISSING_NAME: "ORB_BINDING_TRAIT_MISSING_NAME",
  ORB_BINDING_TRAIT_SELF_REFERENCE: "ORB_BINDING_TRAIT_SELF_REFERENCE",
  ORB_BINDING_TRAIT_UNKNOWN: "ORB_BINDING_TRAIT_UNKNOWN",
  ORB_BINDING_UNKNOWN_ENTITY: "ORB_BINDING_UNKNOWN_ENTITY",
  ORB_BINDING_UNKNOWN_ROOT: "ORB_BINDING_UNKNOWN_ROOT",
  // Closed circuit (`ORB_CIRCUIT_*`, `phases/validation/closed_circuit.rs`).
  // Pre-existing gap: most other ORB_CIRCUIT_* codes the compiler already
  // emits (e.g. ORB_CIRCUIT_FAILURE_ARM_MISSING, ORB_CIRCUIT_DEAD_BODILESS_ACTION)
  // are not yet documented here — out of this task's scope, ledgered for a
  // follow-up sweep.
  ORB_CIRCUIT_VIEWER_STRANDED: "ORB_CIRCUIT_VIEWER_STRANDED",
  // Embed / composition (`@trait.X`, `phases/embed_graph.rs`)
  ORB_EMBED_CONFIG_FORWARD_MULTI_REFERRER: "ORB_EMBED_CONFIG_FORWARD_MULTI_REFERRER",
  // Entity / field
  ORB_E_DEFAULT_TYPE_MISMATCH: "ORB_E_DEFAULT_TYPE_MISMATCH",
  ORB_E_DUPLICATE_FIELD: "ORB_E_DUPLICATE_FIELD",
  ORB_E_EMPTY_ENUM_VALUES: "ORB_E_EMPTY_ENUM_VALUES",
  ORB_E_INVALID_FIELD_NAME: "ORB_E_INVALID_FIELD_NAME",
  ORB_E_INVALID_FIELD_TYPE: "ORB_E_INVALID_FIELD_TYPE",
  ORB_E_INVALID_RELATION: "ORB_E_INVALID_RELATION",
  ORB_E_LITERAL_UNION_DEFAULT_MISMATCH: "ORB_E_LITERAL_UNION_DEFAULT_MISMATCH",
  ORB_E_MISSING_COLLECTION: "ORB_E_MISSING_COLLECTION",
  ORB_E_MISSING_NAME: "ORB_E_MISSING_NAME",
  ORB_E_NO_FIELDS: "ORB_E_NO_FIELDS",
  // Entity ownership / access (`owner_fields_from_schema`,
  // `phases/validation/entity_access.rs`)
  ORB_ENTITY_OWNER_COLUMN_UNSCOPED: "ORB_ENTITY_OWNER_COLUMN_UNSCOPED",
  // Effect / emit
  ORB_EFF_CALL_SERVICE_MISSING_ACTION: "ORB_EFF_CALL_SERVICE_MISSING_ACTION",
  ORB_EFF_CALL_SERVICE_MISSING_HANDLERS: "ORB_EFF_CALL_SERVICE_MISSING_HANDLERS",
  ORB_EFF_CALL_SERVICE_MISSING_SERVICE: "ORB_EFF_CALL_SERVICE_MISSING_SERVICE",
  ORB_EFF_EMIT_INVALID_NAME: "ORB_EFF_EMIT_INVALID_NAME",
  ORB_EFF_EMIT_KEY_INVALID: "ORB_EFF_EMIT_KEY_INVALID",
  ORB_EFF_EMIT_UNDECLARED_EVENT: "ORB_EFF_EMIT_UNDECLARED_EVENT",
  ORB_EFF_FETCH_INVALID_ENTITY: "ORB_EFF_FETCH_INVALID_ENTITY",
  ORB_EFF_FETCH_INVALID_INCLUDE: "ORB_EFF_FETCH_INVALID_INCLUDE",
  ORB_EFF_MISSING_FETCH_FOR_ENTITY: "ORB_EFF_MISSING_FETCH_FOR_ENTITY",
  ORB_EFF_MISSING_REQUIRED_EMIT: "ORB_EFF_MISSING_REQUIRED_EMIT",
  ORB_EFF_FETCH_BY_ID_PAYLOAD_LIST: "ORB_EFF_FETCH_BY_ID_PAYLOAD_LIST",
  ORB_EFF_NAVIGATE_MISSING_PAYLOAD: "ORB_EFF_NAVIGATE_MISSING_PAYLOAD",
  ORB_EFF_NAVIGATE_TARGET_UNREACHABLE: "ORB_EFF_NAVIGATE_TARGET_UNREACHABLE",
  ORB_EFF_SET_INVALID_BINDING: "ORB_EFF_SET_INVALID_BINDING",
  ORB_EFF_SET_PAYLOAD_NOT_ALLOWED: "ORB_EFF_SET_PAYLOAD_NOT_ALLOWED",
  ORB_EFF_SLOT_CONFLICT: "ORB_EFF_SLOT_CONFLICT",
  ORB_EFF_UNKNOWN_TYPE: "ORB_EFF_UNKNOWN_TYPE",
  ORB_EMIT_DECLARED_BUT_UNFIRED: "ORB_EMIT_DECLARED_BUT_UNFIRED",
  ORB_EMIT_CONTROL_UNHANDLED: "ORB_EMIT_CONTROL_UNHANDLED",
  ORB_EMIT_PAYLOAD_EXTRA_FIELD: "ORB_EMIT_PAYLOAD_EXTRA_FIELD",
  ORB_EMIT_PAYLOAD_FIELD_TYPE_MISMATCH: "ORB_EMIT_PAYLOAD_FIELD_TYPE_MISMATCH",
  ORB_EMIT_PAYLOAD_MISSING_REQUIRED: "ORB_EMIT_PAYLOAD_MISSING_REQUIRED",
  ORB_EMIT_SUCCESS_PAYLOAD_RETURN_MISMATCH: "ORB_EMIT_SUCCESS_PAYLOAD_RETURN_MISMATCH",
  // Generic / cross-cutting structure
  ORB_GEN_DUPLICATE: "ORB_GEN_DUPLICATE",
  ORB_GEN_INVALID_REFERENCE: "ORB_GEN_INVALID_REFERENCE",
  ORB_GEN_INVALID_VALUE: "ORB_GEN_INVALID_VALUE",
  ORB_GEN_MISSING_FIELD: "ORB_GEN_MISSING_FIELD",
  // Orbital
  ORB_O_MISSING_PAGES: "ORB_O_MISSING_PAGES",
  // Page
  ORB_PAGE_SECOND_CONTENT_BODY: "ORB_PAGE_SECOND_CONTENT_BODY",
  ORB_PAGE_CONTROL_OUTSIDE_SHELL: "ORB_PAGE_CONTROL_OUTSIDE_SHELL",
  ORB_COMPOSED_STATE_UNREACHABLE: "ORB_COMPOSED_STATE_UNREACHABLE",
  ORB_CONTROL_VALUE_DISCARDED: "ORB_CONTROL_VALUE_DISCARDED",
  ORB_PAGE_UNREACHABLE: "ORB_PAGE_UNREACHABLE",
  ORB_P_DUPLICATE_PATH: "ORB_P_DUPLICATE_PATH",
  ORB_P_EMPTY_TRAITS: "ORB_P_EMPTY_TRAITS",
  ORB_P_INVALID_PATH: "ORB_P_INVALID_PATH",
  ORB_P_INVALID_TRAIT_REF: "ORB_P_INVALID_TRAIT_REF",
  ORB_P_INVALID_VIEW_TYPE: "ORB_P_INVALID_VIEW_TYPE",
  ORB_P_MISSING_NAME: "ORB_P_MISSING_NAME",
  ORB_P_MISSING_PATH: "ORB_P_MISSING_PATH",
  ORB_P_MISSING_TRAITS: "ORB_P_MISSING_TRAITS",
  ORB_P_SECTIONS_FORBIDDEN: "ORB_P_SECTIONS_FORBIDDEN",
  // Query / search behaviors
  ORB_QUERY_MISSING_RECOMMENDED: "ORB_QUERY_MISSING_RECOMMENDED",
  ORB_QUERY_MISSING_SEARCH: "ORB_QUERY_MISSING_SEARCH",
  ORB_QUERY_UNKNOWN_SINGLETON: "ORB_QUERY_UNKNOWN_SINGLETON",
  ORB_QUERY_UNSUPPORTED_PATTERN: "ORB_QUERY_UNSUPPORTED_PATTERN",
  ORB_QUERY_UNUSED_SINGLETON: "ORB_QUERY_UNUSED_SINGLETON",
  // Render config vs. linked-entity data shape (`config_type.rs`)
  ORB_RENDER_GROUPBY_ENUM_COLUMN_GAP: "ORB_RENDER_GROUPBY_ENUM_COLUMN_GAP",
  // A render-ui embeds `@config.<knob>` where `<knob>` is a `trait`-typed
  // config field whose resolved value is `none` (unbound), unguarded by an
  // `(if @config.<knob> ...)` wrapping it in the same effect list — an
  // unbound trait knob's embed renders nothing (`trait_validator.rs`,
  // owner ruling 2026-09-13: a `trait`-typed config knob may be `none`).
  ORB_RENDER_UNBOUND_TRAIT_KNOB_EMBEDDED: "ORB_RENDER_UNBOUND_TRAIT_KNOB_EMBEDDED",
  ORB_RENDER_ORBITAL_VALUE_EMBEDDED: "ORB_RENDER_ORBITAL_VALUE_EMBEDDED",
  // Render-UI binding + prop validation
  ORB_RUI_BINDING_TYPE_MISMATCH: "ORB_RUI_BINDING_TYPE_MISMATCH",
  ORB_RUI_ELEMENT_SHAPE_MISMATCH: "ORB_RUI_ELEMENT_SHAPE_MISMATCH",
  ORB_RUI_EVENT_BINDING_PAYLOAD_MISMATCH: "ORB_RUI_EVENT_BINDING_PAYLOAD_MISMATCH",
  ORB_RUI_INVALID_FIELD_PATH: "ORB_RUI_INVALID_FIELD_PATH",
  ORB_RUI_INVALID_ITEM_ACTION: "ORB_RUI_INVALID_ITEM_ACTION",
  ORB_RUI_INVALID_PATTERN: "ORB_RUI_INVALID_PATTERN",
  ORB_RUI_INVALID_PROP: "ORB_RUI_INVALID_PROP",
  ORB_RUI_INVALID_SLOT: "ORB_RUI_INVALID_SLOT",
  ORB_RUI_MISSING_ACTION: "ORB_RUI_MISSING_ACTION",
  ORB_RUI_MISSING_PATTERN_TYPE: "ORB_RUI_MISSING_PATTERN_TYPE",
  ORB_RUI_MISSING_REQUIRED_PROP: "ORB_RUI_MISSING_REQUIRED_PROP",
  ORB_RUI_PROP_TYPE_MISMATCH: "ORB_RUI_PROP_TYPE_MISMATCH",
  ORB_RUI_UNKNOWN_ITEM_ACTION_PROP: "ORB_RUI_UNKNOWN_ITEM_ACTION_PROP",
  // Schema-level
  ORB_S_EMPTY_VERSION: "ORB_S_EMPTY_VERSION",
  ORB_S_MISSING_NAME: "ORB_S_MISSING_NAME",
  ORB_S_NO_ORBITALS: "ORB_S_NO_ORBITALS",
  // Theme (app-level vs. per-orbital config pin, `theme.rs`)
  ORB_THEME_PIN_CONTRADICTS_APP: "ORB_THEME_PIN_CONTRADICTS_APP",
  // UI slots
  ORB_SLOT_CONTENTION: "ORB_SLOT_CONTENTION",
  ORB_SLOT_CONTENTION_RUNTIME: "ORB_SLOT_CONTENTION_RUNTIME",
  ORB_SLOT_HUD_NON_GAME: "ORB_SLOT_HUD_NON_GAME",
  ORB_SLOT_INVALID_NAME: "ORB_SLOT_INVALID_NAME",
  ORB_SLOT_INVALID_NESTING: "ORB_SLOT_INVALID_NESTING",
  ORB_SLOT_MAIN_NOT_COVERED: "ORB_SLOT_MAIN_NOT_COVERED",
  ORB_SLOT_PRIORITY_CONFLICT: "ORB_SLOT_PRIORITY_CONFLICT",
  // Service
  ORB_SVC_DUPLICATE_NAME: "ORB_SVC_DUPLICATE_NAME",
  ORB_SVC_INVALID_INTEGRATOR: "ORB_SVC_INVALID_INTEGRATOR",
  ORB_SVC_INVALID_URL: "ORB_SVC_INVALID_URL",
  ORB_SVC_MISSING_BASE_URL: "ORB_SVC_MISSING_BASE_URL",
  ORB_SVC_MISSING_CAPABILITIES: "ORB_SVC_MISSING_CAPABILITIES",
  ORB_SVC_MISSING_EVENTS: "ORB_SVC_MISSING_EVENTS",
  ORB_SVC_MISSING_SERVER_PATH: "ORB_SVC_MISSING_SERVER_PATH",
  ORB_SVC_RESERVED_EVENT: "ORB_SVC_RESERVED_EVENT",
  ORB_SVC_UNKNOWN_TYPE: "ORB_SVC_UNKNOWN_TYPE",
  // Trait
  ORB_T_CONFIG_OBJECT_ARRAY_FORBIDDEN: "ORB_T_CONFIG_OBJECT_ARRAY_FORBIDDEN",
  ORB_T_CONFIG_SHAPE_MISMATCH: "ORB_T_CONFIG_SHAPE_MISMATCH",
  ORB_T_CONFIG_TIER_INVALID: "ORB_T_CONFIG_TIER_INVALID",
  ORB_T_DEPRECATED_UI: "ORB_T_DEPRECATED_UI",
  ORB_T_DUPLICATE_NAME: "ORB_T_DUPLICATE_NAME",
  ORB_T_DUPLICATE_STATE: "ORB_T_DUPLICATE_STATE",
  ORB_T_DUPLICATE_TRANSITION: "ORB_T_DUPLICATE_TRANSITION",
  ORB_T_EMPTY_EVENT_NAME: "ORB_T_EMPTY_EVENT_NAME",
  ORB_T_ENTITY_NAME_SHADOWS_TRAIT_UNION: "ORB_T_ENTITY_NAME_SHADOWS_TRAIT_UNION",
  ORB_T_EVENT_TYPE_WRONG_KIND: "ORB_T_EVENT_TYPE_WRONG_KIND",
  ORB_T_EVT_DUPLICATE: "ORB_T_EVT_DUPLICATE",
  ORB_T_EVT_EXTERNAL_MISSING_PAYLOAD: "ORB_T_EVT_EXTERNAL_MISSING_PAYLOAD",
  ORB_T_EVT_INVALID_NAME: "ORB_T_EVT_INVALID_NAME",
  ORB_T_EVT_SCOPE_MISMATCH: "ORB_T_EVT_SCOPE_MISMATCH",
  ORB_T_EVT_TICK_UNDECLARED: "ORB_T_EVT_TICK_UNDECLARED",
  ORB_T_EVT_UNDECLARED: "ORB_T_EVT_UNDECLARED",
  ORB_T_INIT_MISSING_FETCH: "ORB_T_INIT_MISSING_FETCH",
  ORB_T_INVALID_CATEGORY: "ORB_T_INVALID_CATEGORY",
  ORB_T_INVALID_FORMAT: "ORB_T_INVALID_FORMAT",
  ORB_T_INVALID_PATTERN_DEFAULT: "ORB_T_INVALID_PATTERN_DEFAULT",
  ORB_T_INVALID_SLOT_DEFAULT: "ORB_T_INVALID_SLOT_DEFAULT",
  ORB_T_INVALID_TRANSITION: "ORB_T_INVALID_TRANSITION",
  ORB_T_MISSING_INIT_TRANSITION: "ORB_T_MISSING_INIT_TRANSITION",
  ORB_T_MISSING_RENDER_UI: "ORB_T_MISSING_RENDER_UI",
  ORB_T_MULTIPLE_INITIAL_STATES: "ORB_T_MULTIPLE_INITIAL_STATES",
  ORB_T_NO_INITIAL_STATE: "ORB_T_NO_INITIAL_STATE",
  ORB_T_TYPE_ANNOTATION_MISMATCH: "ORB_T_TYPE_ANNOTATION_MISMATCH",
  ORB_T_TYPE_COMPOSITION_CONFLICT: "ORB_T_TYPE_COMPOSITION_CONFLICT",
  ORB_T_TYPE_CYCLE: "ORB_T_TYPE_CYCLE",
  ORB_T_TYPE_KIND_MISMATCH: "ORB_T_TYPE_KIND_MISMATCH",
  ORB_T_TYPE_UNKNOWN: "ORB_T_TYPE_UNKNOWN",
  ORB_T_TYPE_UNKNOWN_PARAM: "ORB_T_TYPE_UNKNOWN_PARAM",
  ORB_T_TYPE_WRONG_ARG_COUNT: "ORB_T_TYPE_WRONG_ARG_COUNT",
  ORB_T_UNDEFINED_EVENT: "ORB_T_UNDEFINED_EVENT",
  ORB_T_UNDEFINED_TRAIT: "ORB_T_UNDEFINED_TRAIT",
  // UI primitives (icons, etc.)
  ORB_UI_ICON_EMPTY: "ORB_UI_ICON_EMPTY",
  ORB_UI_ICON_INVALID_FORMAT: "ORB_UI_ICON_INVALID_FORMAT",
  ORB_UI_ICON_NOT_FOUND: "ORB_UI_ICON_NOT_FOUND",
  // Cross-orbital constraints (listens, emits, IDs, payloads)
  ORB_X_CIRCULAR_DEPENDENCY: "ORB_X_CIRCULAR_DEPENDENCY",
  ORB_X_CROSS_EMITTER_DECLARED_PAYLOAD_MISMATCH: "ORB_X_CROSS_EMITTER_DECLARED_PAYLOAD_MISMATCH",
  ORB_X_DUPLICATE_ENTITY: "ORB_X_DUPLICATE_ENTITY",
  ORB_X_EVENT_COLLISION: "ORB_X_EVENT_COLLISION",
  ORB_X_INTERNAL_EVENT_EXPOSED: "ORB_X_INTERNAL_EVENT_EXPOSED",
  ORB_X_LISTEN_SOURCE_NEVER_PRODUCES: "ORB_X_LISTEN_SOURCE_NEVER_PRODUCES",
  ORB_X_LISTEN_SOURCE_UNRESOLVED: "ORB_X_LISTEN_SOURCE_UNRESOLVED",
  ORB_X_MISSING_ORBITAL_NAME: "ORB_X_MISSING_ORBITAL_NAME",
  ORB_X_ORPHAN_LISTENER: "ORB_X_ORPHAN_LISTENER",
  ORB_X_PAYLOAD_MISMATCH: "ORB_X_PAYLOAD_MISMATCH",
  ORB_X_RENDER_UI_EVENT_LITERAL_STALE: "ORB_X_RENDER_UI_EVENT_LITERAL_STALE",
  ORB_X_UNRESOLVED_PATTERN_FIELD_REF: "ORB_X_UNRESOLVED_PATTERN_FIELD_REF",
  ORB_X_UNUSED_EMISSION: "ORB_X_UNUSED_EMISSION",
  // Identity — V4 dual-carry id integrity (`ORB_ID_*`). Mirrors
  // `orbital-compiler/src/phases/validation/id_integrity.rs`.
  ORB_ID_UNKNOWN_REF: "ORB_ID_UNKNOWN_REF",
  ORB_ID_NAME_MISMATCH: "ORB_ID_NAME_MISMATCH",
  ORB_ID_KIND_MISMATCH: "ORB_ID_KIND_MISMATCH",
  ORB_ID_LEDGER_ORPHAN: "ORB_ID_LEDGER_ORPHAN"
};
function isKnownValidationErrorCode(code) {
  return code in KNOWN_VALIDATION_ERROR_CODES;
}

// src/types/living.ts
function widenTier(tier) {
  return tier;
}
var ARIA_LIVE = ["off", "polite", "assertive"];
var ARIA_CURRENT = ["page", "step", "location", "date", "time", "true", "false"];
var ARIA_PRESSED = ["true", "false", "mixed"];
var TEXT_DIRECTIONS = ["ltr", "rtl", "auto"];
var TAB_INDEX_VALUES = [-1, 0];
var ARIA_ROLES = [
  "alert",
  "alertdialog",
  "application",
  "article",
  "banner",
  "blockquote",
  "button",
  "caption",
  "cell",
  "checkbox",
  "code",
  "columnheader",
  "combobox",
  "complementary",
  "contentinfo",
  "definition",
  "deletion",
  "dialog",
  "document",
  "emphasis",
  "feed",
  "figure",
  "form",
  "generic",
  "grid",
  "gridcell",
  "group",
  "heading",
  "img",
  "insertion",
  "link",
  "list",
  "listbox",
  "listitem",
  "log",
  "main",
  "marquee",
  "math",
  "menu",
  "menubar",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "meter",
  "navigation",
  "none",
  "note",
  "option",
  "paragraph",
  "presentation",
  "progressbar",
  "radio",
  "radiogroup",
  "region",
  "row",
  "rowgroup",
  "rowheader",
  "scrollbar",
  "search",
  "searchbox",
  "separator",
  "slider",
  "spinbutton",
  "status",
  "strong",
  "subscript",
  "superscript",
  "switch",
  "tab",
  "table",
  "tablist",
  "tabpanel",
  "term",
  "textbox",
  "time",
  "timer",
  "toolbar",
  "tooltip",
  "tree",
  "treegrid",
  "treeitem"
];
var AriaRoleSchema = z.enum(ARIA_ROLES);
var ARIA_ROLE_SET = new Set(ARIA_ROLES);
function isAriaRole(value) {
  return ARIA_ROLE_SET.has(value);
}
var A11Y_ATTRIBUTES = {
  "aria-label": { types: ["string"], description: "Accessible name read by screen readers when the visible text is missing or not descriptive (e.g. an icon-only control)." },
  "aria-labelledby": { types: ["string"], description: "Id of the element whose text names this one." },
  "aria-describedby": { types: ["string"], description: "Id of the element whose text describes this one (help text, error)." },
  "aria-live": { types: ["string"], enumValues: ARIA_LIVE, description: "Announce changes to this region: polite waits for a pause, assertive interrupts." },
  "aria-current": { types: ["string"], enumValues: ARIA_CURRENT, description: "Marks the current item in a set: the current page, step, location, date or time." },
  "aria-pressed": { types: ["string"], enumValues: ARIA_PRESSED, description: "Toggle-button state." },
  "aria-expanded": { types: ["boolean"], description: "Whether the element it controls is expanded." },
  "aria-selected": { types: ["boolean"], description: "Whether this item is selected (tabs, options, grid cells)." },
  "aria-hidden": { types: ["boolean"], description: "Hide from assistive technology (decorative content only)." },
  "aria-busy": { types: ["boolean"], description: "This region is updating; assistive technology waits before announcing it." },
  role: { types: ["string"], enumValues: ARIA_ROLES, description: "WAI-ARIA role when the element's native semantics don't describe it." },
  lang: { types: ["string"], description: "Language of this element's content (BCP 47, e.g. ar, sl, en)." },
  dir: { types: ["string"], enumValues: TEXT_DIRECTIONS, description: "Text direction of this element's content." },
  tabIndex: { types: ["number"], numericEnumValues: TAB_INDEX_VALUES, description: "0 puts the element in the Tab order; -1 makes it focusable only programmatically." }
};
var ACCESSIBLE_NAME_ATTRIBUTES = ["aria-label", "aria-labelledby"];

// src/types/surface.ts
var SURFACE_MODES = ["auto", "none"];
var DIAGRAM_TONES = [
  "ink",
  "label",
  "guide",
  "axis",
  "grid",
  "highlight",
  "fill",
  "series-1",
  "series-2",
  "series-3",
  "series-4",
  "series-5",
  "series-6",
  "series-7",
  "series-8",
  "primary",
  "accent",
  "success",
  "warning",
  "error",
  "info",
  "muted"
];
var DiagramToneSchema = z.enum(DIAGRAM_TONES);
var DIAGRAM_TONE_COLOR_KEYS = {
  ink: "diagram-ink",
  label: "diagram-label",
  guide: "diagram-guide",
  axis: "diagram-axis",
  grid: "diagram-grid",
  highlight: "diagram-highlight",
  fill: "diagram-fill",
  "series-1": "series-1",
  "series-2": "series-2",
  "series-3": "series-3",
  "series-4": "series-4",
  "series-5": "series-5",
  "series-6": "series-6",
  "series-7": "series-7",
  "series-8": "series-8",
  primary: "primary",
  accent: "accent",
  success: "success",
  warning: "warning",
  error: "error",
  info: "info",
  muted: "muted-foreground"
};
var DIAGRAM_STROKE_WEIGHTS = ["thin", "normal", "bold"];
var DiagramStrokeWeightSchema = z.enum(DIAGRAM_STROKE_WEIGHTS);
var DIAGRAM_TEXT_SIZES = ["xs", "sm", "base", "lg", "xl", "2xl"];
var DiagramTextSizeSchema = z.enum(DIAGRAM_TEXT_SIZES);
var DIAGRAM_TEXT_CASES = ["theme", "verbatim"];
var DiagramTextCaseSchema = z.enum(DIAGRAM_TEXT_CASES);
var TRAITS_SEGMENT = ".traits.";
var ORBITALS_SEGMENT = ".orbitals.";
var PASCAL = /^[A-Z][A-Za-z0-9]*$/;
var SPECIFIER = /^(?:[a-z0-9][a-z0-9-]*(?:\/behaviors)?|\.(?:\/[A-Za-z0-9_-]+)+)\/[A-Za-z0-9][A-Za-z0-9_-]*$/;
function splitLiteral(raw, segment) {
  const at = raw.lastIndexOf(segment);
  if (at <= 0) return void 0;
  const behavior = raw.slice(0, at);
  const name = raw.slice(at + segment.length);
  if (!SPECIFIER.test(behavior) || !PASCAL.test(name)) return void 0;
  return { behavior, name };
}
function parseTraitValueLiteral(raw) {
  const parts = splitLiteral(raw, TRAITS_SEGMENT);
  return parts === void 0 ? void 0 : { behavior: parts.behavior, trait: parts.name };
}
function parseOrbitalValueLiteral(raw) {
  const parts = splitLiteral(raw, ORBITALS_SEGMENT);
  return parts === void 0 ? void 0 : { behavior: parts.behavior, orbital: parts.name };
}
function parseBehaviorValueLiteral(raw) {
  return parseTraitValueLiteral(raw) ?? parseOrbitalValueLiteral(raw);
}
function formatTraitValueLiteral(value) {
  return `${value.behavior}${TRAITS_SEGMENT}${value.trait}`;
}
function formatOrbitalValueLiteral(value) {
  return `${value.behavior}${ORBITALS_SEGMENT}${value.orbital}`;
}
function formatBehaviorValueLiteral(value) {
  return isOrbitalValueShape(value) ? formatOrbitalValueLiteral(value) : formatTraitValueLiteral(value);
}
var TraitOverridesSchema = TraitReferenceObjectSchema.pick({
  config: true,
  events: true,
  fields: true,
  listens: true,
  emitsScope: true,
  linkedEntity: true
}).strict();
var TraitValueSchema = TraitOverridesSchema.extend({
  behavior: z.string().regex(SPECIFIER),
  trait: z.string().regex(PASCAL)
}).strict();
var OrbitalValueUseSchema = z.object({ from: z.string(), as: z.string().regex(PASCAL) }).strict();
var OrbitalValueSiblingSchema = z.object({
  name: z.string().regex(PASCAL),
  ref: z.string(),
  linkedEntity: z.string().optional(),
  config: z.record(TraitConfigValueSchema).optional()
}).strict();
var OrbitalOverridesSchema = OrbitalRefObjectSchema.omit({ ref: true, refId: true, config: true, traits: true }).extend({
  name: z.string().regex(PASCAL).optional(),
  app: z.record(TraitConfigValueSchema).optional(),
  config: z.record(TraitConfigValueSchema).optional(),
  traits: z.record(TraitOverridesSchema).optional(),
  uses: z.array(OrbitalValueUseSchema).optional(),
  siblings: z.array(OrbitalValueSiblingSchema).optional()
}).strict();
var OrbitalValueSchema = OrbitalOverridesSchema.extend({
  behavior: z.string().regex(SPECIFIER),
  orbital: z.string().regex(PASCAL)
}).strict();
var BehaviorValueSchema = z.union([TraitValueSchema, OrbitalValueSchema]);
function mergeTraitOverrides(base, overrides) {
  const merged = { ...base };
  if (overrides.config !== void 0) merged.config = { ...base.config, ...overrides.config };
  if (overrides.events !== void 0) merged.events = { ...base.events, ...overrides.events };
  if (overrides.fields !== void 0) merged.fields = { ...base.fields, ...overrides.fields };
  if (overrides.listens !== void 0) merged.listens = overrides.listens;
  if (overrides.emitsScope !== void 0) merged.emitsScope = overrides.emitsScope;
  if (overrides.linkedEntity !== void 0) merged.linkedEntity = overrides.linkedEntity;
  return merged;
}
function applyTraitOverrides(value, overrides) {
  return { ...mergeTraitOverrides(value, overrides), behavior: value.behavior, trait: value.trait };
}
function mergeFieldsByName(base, next) {
  const out = [...base ?? []];
  for (const field of next) {
    const at = field.name === void 0 ? -1 : out.findIndex((f) => f.name === field.name);
    if (at >= 0) out[at] = field;
    else out.push(field);
  }
  return out;
}
function applyOrbitalOverrides(value, overrides) {
  const merged = { ...value, ...overrides, behavior: value.behavior, orbital: value.orbital };
  if (overrides.app !== void 0) merged.app = { ...value.app, ...overrides.app };
  if (overrides.config !== void 0) merged.config = { ...value.config, ...overrides.config };
  if (overrides.events !== void 0) merged.events = { ...value.events, ...overrides.events };
  if (overrides.fields !== void 0) merged.fields = { ...value.fields, ...overrides.fields };
  if (overrides.pages !== void 0) merged.pages = { ...value.pages, ...overrides.pages };
  if (overrides.pageModifiers !== void 0) merged.pageModifiers = { ...value.pageModifiers, ...overrides.pageModifiers };
  if (overrides.roles !== void 0) merged.roles = { ...value.roles, ...overrides.roles };
  if (overrides.entities !== void 0) merged.entities = { ...value.entities, ...overrides.entities };
  if (overrides.mounts !== void 0) merged.mounts = { ...value.mounts, ...overrides.mounts };
  if (overrides.mock !== void 0) merged.mock = { ...value.mock, ...overrides.mock };
  if (overrides.extend !== void 0) merged.extend = mergeFieldsByName(value.extend, overrides.extend);
  if (overrides.retype !== void 0) merged.retype = mergeFieldsByName(value.retype, overrides.retype);
  if (overrides.uses !== void 0) {
    const byAlias = new Map((value.uses ?? []).map((u) => [u.as, u]));
    for (const use of overrides.uses) byAlias.set(use.as, use);
    merged.uses = [...byAlias.values()];
  }
  if (overrides.siblings !== void 0) {
    const byName = new Map((value.siblings ?? []).map((t) => [t.name, t]));
    for (const sibling of overrides.siblings) {
      const prior = byName.get(sibling.name);
      byName.set(sibling.name, { ...prior, ...sibling, ...prior?.config !== void 0 || sibling.config !== void 0 ? { config: { ...prior?.config, ...sibling.config } } : {} });
    }
    merged.siblings = [...byName.values()];
  }
  if (overrides.traits !== void 0) {
    const traits = { ...value.traits };
    for (const [name, entry] of Object.entries(overrides.traits)) {
      traits[name] = mergeTraitOverrides(traits[name] ?? {}, entry);
    }
    merged.traits = traits;
  }
  return merged;
}
function applyBehaviorOverrides(value, overrides) {
  if (isOrbitalValueShape(value)) return applyOrbitalOverrides(value, OrbitalOverridesSchema.parse(overrides));
  return applyTraitOverrides(value, TraitOverridesSchema.parse(overrides));
}
function isTraitValue(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  if (!("behavior" in value) || !("trait" in value)) return false;
  const { behavior, trait } = value;
  return typeof behavior === "string" && SPECIFIER.test(behavior) && typeof trait === "string" && PASCAL.test(trait);
}
function isOrbitalValue(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  if (!("behavior" in value) || !("orbital" in value)) return false;
  const { behavior, orbital } = value;
  return typeof behavior === "string" && SPECIFIER.test(behavior) && typeof orbital === "string" && PASCAL.test(orbital);
}
function behaviorRef(specifier) {
  if (!SPECIFIER.test(specifier)) {
    throw new Error(`behavior/ref: "${specifier}" is not a behavior specifier (<prefix>/<name>, std/behaviors/<name> or ./<dir>/<name>)`);
  }
  return { behavior: specifier };
}
function isBehaviorRefValue(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 1 || keys[0] !== "behavior" || !("behavior" in value)) return false;
  return typeof value.behavior === "string" && SPECIFIER.test(value.behavior);
}
function isOrbitalValueShape(value) {
  return "orbital" in value;
}
var EXPOSURES = ["app", "experience", "palette", "both", "internal"];
var VectorSchema = z.array(z.number());
var CatalogScopeSchema = z.object({
  paths: z.array(z.string().min(1)).min(1),
  exposure: z.array(z.enum(EXPOSURES)).optional()
}).strict();
var BehaviorRefSchema = z.object({ behavior: z.string().regex(SPECIFIER) }).strict();
var CatalogEntrySchema = z.object({
  /** `<prefix>/<name>`. */
  behavior: z.string(),
  package: z.string(),
  topic: z.string(),
  level: z.enum(["atom", "molecule", "organism"]),
  exposure: z.enum(EXPOSURES).optional(),
  description: z.string(),
  capabilities: z.array(z.string()),
  /** The behavior's traits as ready-to-bind values. */
  traits: z.array(TraitValueSchema),
  /** The behavior as a value (`.lolo` type `behavior`), ready to embed or store. */
  value: BehaviorRefSchema,
  /** Shipped embedding vectors, keyed by facet (`description`, `capabilities`). */
  vectors: z.record(VectorSchema),
  /** The embedding model the vectors were stamped with. */
  model: z.string()
}).strict();
var TraitKnobSchema = z.object({
  key: z.string(),
  type: z.string(),
  default: TraitConfigValueSchema.optional(),
  label: z.string().optional(),
  description: z.string().optional(),
  tier: z.string().optional(),
  vector: VectorSchema.optional()
}).strict();
var TraitDescriptionSchema = z.object({
  value: TraitValueSchema,
  knobs: z.array(TraitKnobSchema),
  events: z.object({
    emits: z.array(z.string()),
    listens: z.array(z.string()),
    /** Events the trait's own transitions fire on — the only valid `listens` triggers. */
    transitions: z.array(z.string())
  }).strict(),
  model: z.string()
}).strict();
var DescribeTargetSchema = z.union([TraitValueSchema, OrbitalValueSchema, BehaviorRefSchema]);
var KnobFieldSchema = z.lazy(
  () => z.object({
    name: z.string(),
    type: z.string(),
    required: z.boolean().optional(),
    values: z.array(z.string()).optional(),
    properties: z.array(KnobFieldSchema).optional(),
    default: TraitConfigValueSchema.optional(),
    label: z.string().optional(),
    description: z.string().optional(),
    synonyms: z.string().optional(),
    tier: z.string().optional()
  }).strict()
);
var OrbitalDescriptionSchema = z.object({
  value: OrbitalValueSchema,
  config: DeclaredTraitConfigSchema,
  knobs: z.array(KnobFieldSchema),
  entity: z.object({ name: z.string(), fields: z.array(EntityFieldSchema), identity: z.boolean().optional(), persistence: EntityPersistenceSchema.optional() }).strict().optional(),
  /** Entities a sibling orbital owns that this one references: what an import of it maps through `entities {}`. */
  borrows: z.array(z.string()),
  /** What the orbital needs from its siblings at runtime, derived by `orb` (identity, entities, pages, events). */
  expects: z.array(ExpectDeclarationSchema),
  traits: z.array(z.object({
    trait: z.string(),
    config: DeclaredTraitConfigSchema,
    knobs: z.array(KnobFieldSchema),
    events: z.object({ emits: z.array(z.string()), listens: z.array(z.string()), transitions: z.array(z.string()) }).strict(),
    /** Declared `-> @rebindable`: the trait may be bound to another record. */
    rebindable: z.boolean().optional()
  }).strict()),
  /** Each page with the traits it mounts: what the behavior declares as standing on its own. */
  pages: z.array(z.object({ name: z.string(), path: z.string(), traits: z.array(z.string()).optional() }).strict())
}).strict();
var BehaviorDescriptionSchema = z.object({
  behavior: z.string(),
  config: DeclaredTraitConfigSchema,
  knobs: z.array(KnobFieldSchema),
  orbitals: z.array(OrbitalDescriptionSchema),
  /** The behaviors this one imports (`uses … from`): what it already contains. */
  uses: z.array(z.string()).optional()
}).strict();

// src/render-ui-edit.ts
function childrenOf(node) {
  if (Array.isArray(node.children)) return node.children;
  const props = node.props;
  if (props && typeof props === "object" && !Array.isArray(props) && !(props instanceof Date)) {
    const nested = props.children;
    if (Array.isArray(nested)) return nested;
  }
  return null;
}
function navigatePatternPath(root, path) {
  if (!path || path === "root") return root;
  const parts = (path.startsWith("root.") ? path.slice("root.".length) : path).split(".");
  let current = root;
  for (const part of parts) {
    if (current === null) return null;
    if (Array.isArray(current)) {
      const idx = Number.parseInt(part, 10);
      if (Number.isNaN(idx) || idx < 0 || idx >= current.length) return null;
      const item = current[idx];
      current = item && typeof item === "object" && !Array.isArray(item) ? item : null;
    } else if (part === "children") {
      current = childrenOf(current);
    } else {
      const value = current[part];
      current = value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date) ? value : null;
    }
  }
  return Array.isArray(current) ? null : current;
}
function splitPatternChildPath(path) {
  const match = /^(.*)\.children\.(\d+)$/.exec(path) ?? /^children\.(\d+)$/.exec(path);
  if (!match) return null;
  if (match.length === 2) return { parentPath: "root", index: Number.parseInt(match[1], 10) };
  return { parentPath: match[1], index: Number.parseInt(match[2], 10) };
}
function setPropAtPath(root, path, prop, value) {
  const node = navigatePatternPath(root, path);
  if (!node) return false;
  node[prop] = value;
  return true;
}
function replaceChildAtPath(root, path, node) {
  const split = splitPatternChildPath(path);
  if (!split) return false;
  const parent = navigatePatternPath(root, split.parentPath);
  const children = parent ? childrenOf(parent) : null;
  if (!children || split.index >= children.length) return false;
  children[split.index] = node;
  return true;
}
function insertChildAtPath(root, parentPath, index, node) {
  const parent = navigatePatternPath(root, parentPath);
  if (!parent) return false;
  const children = childrenOf(parent) ?? [];
  const i = index < 0 || index > children.length ? children.length : index;
  children.splice(i, 0, node);
  if (!childrenOf(parent)) parent.children = children;
  return true;
}
function removeChildAtPath(root, path) {
  const split = splitPatternChildPath(path);
  if (!split) return false;
  const parent = navigatePatternPath(root, split.parentPath);
  const children = parent ? childrenOf(parent) : null;
  if (!children || split.index >= children.length) return false;
  children.splice(split.index, 1);
  return true;
}
function replaceNodeInPlace(target, source) {
  for (const key of Object.keys(target)) {
    delete target[key];
  }
  Object.assign(target, source);
}
function stableValue(value) {
  if (value === void 0) return "u";
  if (value === null) return "n";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return `[${value.map(stableValue).join(",")}]`;
  const nodeType = "type" in value ? value.type : void 0;
  return `{${typeof nodeType === "string" ? nodeType : ""}}`;
}
function fingerprintNode(node) {
  const props = Object.keys(node).filter((k) => k !== "children").sort().map((k) => `${k}=${stableValue(node[k])}`).join(",");
  const childCount = Array.isArray(node.children) ? node.children.length : 0;
  return `t:${node.type ?? ""}|${props}|c:${childCount}`;
}
function findByFingerprint(node, path, fp) {
  if (fingerprintNode(node) === fp) return path;
  if (Array.isArray(node.children)) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      if (typeof child === "string") continue;
      const r = findByFingerprint(child, `${path}.children.${i}`, fp);
      if (r) return r;
    }
  }
  return null;
}
function asInlineTrait(ref2) {
  if (typeof ref2 === "object" && ref2 !== null && "stateMachine" in ref2) {
    return ref2;
  }
  return null;
}
function renderRootOf(trait, address) {
  const wantTransition = address.transition !== "";
  const wantState = address.state !== void 0 && address.state !== "";
  let fallback = null;
  for (const t of trait.stateMachine?.transitions ?? []) {
    if (wantState && t.from !== address.state) continue;
    for (const entry of renderUiEntriesOf(t.effects ?? [])) {
      if (entry.slot !== address.slot) continue;
      const node = entry.pattern;
      if (wantTransition && t.event === address.transition) return node;
      if (!wantTransition) fallback ?? (fallback = node);
    }
  }
  return fallback;
}
function findRenderUiRoot(orbital, address) {
  for (const ref2 of orbital.traits) {
    const trait = asInlineTrait(ref2);
    if (trait && trait.name === address.trait) return renderRootOf(trait, address);
  }
  return null;
}
function resolvePath(root, patch) {
  if (patch.op === "insert") {
    return navigatePatternPath(root, patch.address.path) ? patch.address.path : null;
  }
  const atPath = navigatePatternPath(root, patch.address.path);
  if (atPath && (patch.fingerprint === void 0 || fingerprintNode(atPath) === patch.fingerprint)) {
    return patch.address.path;
  }
  if (patch.fingerprint !== void 0) {
    return findByFingerprint(root, "root", patch.fingerprint);
  }
  return null;
}
function applyOnePatch(orbital, patch) {
  const root = findRenderUiRoot(orbital, patch.address);
  if (!root) return false;
  const path = resolvePath(root, patch);
  if (path === null) return false;
  switch (patch.op) {
    case "set-prop":
    case "rebind":
      return patch.prop !== void 0 && setPropAtPath(root, path, patch.prop, patch.value);
    case "remove":
      return removeChildAtPath(root, path);
    case "insert":
      return patch.node !== void 0 && insertChildAtPath(root, path, patch.index ?? Number.MAX_SAFE_INTEGER, patch.node);
    case "replace": {
      if (patch.node === void 0) return false;
      if (path === "root") {
        const target = navigatePatternPath(root, "root");
        if (!target) return false;
        replaceNodeInPlace(target, patch.node);
        return true;
      }
      return replaceChildAtPath(root, path, patch.node);
    }
    default:
      return false;
  }
}
function applyRenderOverlay(orbital, patches) {
  let applied = 0;
  const stale = [];
  for (const patch of patches) {
    if (applyOnePatch(orbital, patch)) {
      applied += 1;
    } else {
      stale.push(patch);
    }
  }
  return { applied, stale };
}

// src/agent-trace-view.ts
var TRACE_ACTIVITY_TYPES = [
  "message",
  "tool_call",
  "tool_result",
  "file_operation",
  "schema_diff",
  "error",
  "coordinator_decision",
  "plan_committed",
  "pending_question",
  "clarification_question",
  "analysis",
  "orbital_started",
  "orbital_done",
  "schema_change",
  "done",
  "cancelled",
  "llm_response"
];
function selectTraceActivities(activities, audience) {
  return activities.filter((activity) => {
    if (!audience.types.includes(activity.type)) return false;
    if (activity.type === "message" && audience.messageRoles) return audience.messageRoles.includes(activity.role);
    if (activity.type === "schema_change" && audience.schemaChangeKinds) {
      return audience.schemaChangeKinds.includes(activity.changeKind);
    }
    return true;
  });
}
function traceActivityPayload(activity) {
  return EventPayloadSchema.parse({ activity: JSON.parse(JSON.stringify(activity)) });
}

// src/resolver.ts
var schemaCache = /* @__PURE__ */ new WeakMap();
function clearSchemaCache() {
  schemaCache = /* @__PURE__ */ new WeakMap();
}
function getSchemaCacheStats() {
  return { size: -1, keys: [] };
}
function schemaToIR(schema, useCache = true) {
  if (useCache) {
    const cached = schemaCache.get(schema);
    if (cached) return cached;
  }
  const ir = {
    appName: schema.name,
    description: schema.description,
    version: schema.version || "1.0.0",
    entities: /* @__PURE__ */ new Map(),
    traits: /* @__PURE__ */ new Map(),
    pages: /* @__PURE__ */ new Map(),
    entityBindings: [],
    generatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  for (const orbital of schema.orbitals) {
    if (orbital.entity && typeof orbital.entity !== "string") {
      const entityDef = orbital.entity;
      const entity = {
        name: entityDef.name,
        description: entityDef.description,
        icon: entityDef.icon,
        collection: entityDef.collection || entityDef.name.toLowerCase() + "s",
        fields: (entityDef.fields || []).filter(
          (field) => typeof field.name === "string" && field.name.length > 0
        ).map((field) => ({
          name: field.name,
          type: field.type,
          tsType: inferTsType2(field.type),
          description: field.description,
          default: field.default,
          required: field.required ?? false,
          // lolo string unions lower to `type:'string'` + a `values` sidecar
          // (FieldType::Enum is never emitted) — read values off any variant
          // that carries them, not just `enum`. EXCLUDES `type:'union'`
          // deliberately: its `values` carries tagged-union variant NAMES or a
          // by-name recursive back-reference (`type MenuItem = { subMenu :
          // [MenuItem] }`), never legal literal values — treating either as
          // an enum vocabulary would let a downstream option-list render the
          // variant/alias NAME as if it were a selectable value (mirrors the
          // Rust-side skip in `orbital-core/src/type_compat.rs`).
          values: field.type !== "union" && "values" in field && field.values?.length ? field.values : void 0,
          enumValues: field.type !== "union" && "values" in field && field.values?.length ? field.values : void 0,
          relation: field.type === "relation" ? field.relation : void 0
        })),
        runtime: entityDef.persistence === "runtime",
        shared: entityDef.shared,
        hasInstances: (entityDef.instances?.length ?? 0) > 0,
        instances: entityDef.instances,
        defaults: entityDef.defaults,
        usedByTraits: [],
        usedByPages: []
      };
      ir.entities.set(entity.name, entity);
    }
    for (const rawTrait of orbital.traits || []) {
      const maybeResolved = typeof rawTrait === "object" && "ref" in rawTrait ? rawTrait._resolved : void 0;
      const trait = maybeResolved && maybeResolved.stateMachine ? maybeResolved : rawTrait;
      if (!trait.name) continue;
      const resolvedTrait = {
        name: trait.name,
        description: trait.description,
        source: "schema",
        category: trait.category,
        // V4 dual-carry ids + emit contracts, preserved when the source
        // trait carries them — the emit-source stamp needs `id`/`eventId`
        // for id-scoped `listens` matching (`buildSourceMatcher` compares
        // ids ONLY, so a name-only stamp never matches a `traitId`-carrying
        // listen). Absent on id-free schemas, which keep the legacy
        // name-only stamp exactly as before.
        ...trait.id !== void 0 ? { id: trait.id } : {},
        ...trait.emits !== void 0 ? { emits: trait.emits.map((e) => ({ event: e.event, eventId: e.eventId })) } : {},
        states: (trait.stateMachine?.states || []).map((s) => ({
          name: s.name,
          isInitial: s.isInitial ?? false,
          isFinal: s.isFinal ?? s.isTerminal ?? false
        })),
        events: (trait.stateMachine?.events || []).map((e) => ({
          key: e.key,
          name: e.name,
          payload: e.payloadSchema
        })),
        transitions: (trait.stateMachine?.transitions || []).map((t) => ({
          from: t.from,
          to: t.to,
          event: t.event,
          guard: t.guard,
          effects: t.effects || []
        })),
        guards: [],
        ticks: (trait.ticks || []).map((tk) => ({
          name: tk.name,
          interval: tk.interval,
          guard: tk.guard,
          effects: tk.effects,
          priority: tk.priority ?? 0,
          appliesTo: tk.appliesTo ?? []
        })),
        listens: (trait.listens || []).map((l) => ({
          event: l.event,
          triggers: l.triggers,
          guard: l.guard,
          payloadMapping: l.payloadMapping,
          source: l.source
        })),
        dataEntities: [],
        linkedEntity: trait.linkedEntity,
        config: trait.config
      };
      ir.traits.set(trait.name, resolvedTrait);
    }
    for (const page of orbital.pages || []) {
      const resolvedPage = {
        name: page.name,
        path: page.path,
        featureName: page.name.toLowerCase(),
        viewType: page.viewType && ["list", "detail", "create", "edit", "dashboard"].includes(page.viewType) ? page.viewType : void 0,
        ...pageModifiers(page),
        sections: [],
        traits: (page.traits || []).map((traitRef) => {
          const resolvedTrait = resolveTraitRef(traitRef, ir.traits, orbital.traits || []);
          const orbitalTrait = (orbital.traits || []).find(
            (t) => typeof t === "object" && t !== null && ("name" in t && t.name === traitRef.ref || "ref" in t && t.ref === traitRef.ref)
          );
          const orbitalTraitWrapped = orbitalTrait;
          const wrapperLinked = orbitalTraitWrapped?.linkedEntity ?? orbitalTraitWrapped?._resolved?.linkedEntity;
          const resolvedLinked = resolvedTrait?.linkedEntity;
          return {
            ref: traitRef.ref,
            trait: resolvedTrait,
            linkedEntity: traitRef.linkedEntity ?? wrapperLinked ?? resolvedLinked ?? (orbital.entity ? typeof orbital.entity === "string" ? orbital.entity.replace(".entity", "") : orbital.entity.name : void 0),
            config: traitRef.config
          };
        }),
        entityBindings: [],
        navigation: [],
        singletonEntities: []
      };
      ir.pages.set(page.name, resolvedPage);
    }
  }
  if (useCache) {
    schemaCache.set(schema, ir);
  }
  return ir;
}
function inferTsType2(schemaType) {
  const typeMap = {
    string: "string",
    number: "number",
    boolean: "boolean",
    date: "Date",
    datetime: "Date",
    timestamp: "number",
    money: "number",
    file: "FileValue",
    array: "unknown[]",
    object: "Record<string, unknown>",
    any: "unknown"
  };
  if (schemaType.endsWith("[]")) {
    const baseType = schemaType.slice(0, -2);
    return `${typeMap[baseType] || baseType}[]`;
  }
  if (schemaType.startsWith("Map<") && schemaType.endsWith(">")) {
    const inner = schemaType.slice(4, -1);
    const comma = inner.indexOf(",");
    if (comma !== -1) {
      const key = inferTsType2(inner.slice(0, comma).trim());
      const value = inferTsType2(inner.slice(comma + 1).trim());
      return `Record<${key}, ${value}>`;
    }
  }
  return typeMap[schemaType] || schemaType;
}
function resolveTraitRef(traitRef, traitsMap, schemaTraits) {
  const refName = typeof traitRef === "string" ? traitRef : traitRef.ref || "";
  if (traitsMap.has(refName)) {
    return traitsMap.get(refName);
  }
  const schemaTrait = schemaTraits.find((t) => t.name === refName);
  if (schemaTrait) {
    return {
      name: refName,
      source: "inline",
      states: [],
      events: [],
      transitions: [],
      guards: [],
      ticks: [],
      listens: [],
      dataEntities: []
    };
  }
  return {
    name: refName,
    source: "schema",
    states: [],
    events: [],
    transitions: [],
    guards: [],
    ticks: [],
    listens: [],
    dataEntities: []
  };
}
function getPage(ir, pageName) {
  if (!pageName) {
    return ir.pages.values().next().value;
  }
  return ir.pages.get(pageName);
}
function getPages(ir) {
  return Array.from(ir.pages.values());
}
function getEntity(ir, entityName2) {
  return ir.entities.get(entityName2);
}
function getTrait(ir, traitName) {
  return ir.traits.get(traitName);
}
function pageModifiers(page) {
  return {
    ...page.access !== void 0 ? { access: page.access } : {},
    ...page.indexing !== void 0 ? { indexing: page.indexing } : {},
    ...page.sourcePage !== void 0 ? { sourcePage: page.sourcePage } : {},
    ...page.translationOf !== void 0 ? { translationOf: page.translationOf } : {},
    ...page.title !== void 0 ? { title: page.title } : {},
    ...page.description !== void 0 ? { description: page.description } : {}
  };
}

// src/embedded-trait-config.ts
var TRAIT_BINDING_PREFIX = "@trait.";
var CONFIG_FORWARD_RE = /^@config\.([A-Za-z_][A-Za-z0-9_]*)$/;
var CALLSITE_PAYLOAD_PREFIX = "@callsitePayload.";
function valueContainsPrefixedString(value, prefix) {
  if (value === null || value === void 0) return false;
  if (typeof value === "string") return value.startsWith(prefix);
  if (Array.isArray(value)) return value.some((item) => valueContainsPrefixedString(item, prefix));
  if (typeof value === "object") {
    return Object.values(value).some((v) => valueContainsPrefixedString(v, prefix));
  }
  return false;
}
function collectTraitRefsFromValue(value, into) {
  if (value === null || value === void 0) return;
  if (typeof value === "string") {
    if (value.startsWith(TRAIT_BINDING_PREFIX)) {
      const rest = value.slice(TRAIT_BINDING_PREFIX.length);
      const dot = rest.indexOf(".");
      const traitName = dot === -1 ? rest : rest.slice(0, dot);
      if (traitName.length > 0) into.add(traitName);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectTraitRefsFromValue(item, into);
    return;
  }
  if (typeof value === "object") {
    for (const v of Object.values(value)) {
      collectTraitRefsFromValue(v, into);
    }
  }
}
var CONFIG_BINDING_PREFIX = "@config.";
function collectConfigKeysFromValue(value, into) {
  if (value === null || value === void 0) return;
  if (typeof value === "string") {
    if (value.startsWith(CONFIG_BINDING_PREFIX)) {
      const key = value.slice(CONFIG_BINDING_PREFIX.length).split(".")[0];
      if (key.length > 0) into.add(key);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectConfigKeysFromValue(item, into);
    return;
  }
  if (typeof value === "object") {
    for (const v of Object.values(value)) collectConfigKeysFromValue(v, into);
  }
}
function traitsEmbeddedByEffects(trait, effects) {
  const refs = /* @__PURE__ */ new Set();
  const configKeys = /* @__PURE__ */ new Set();
  collectTraitRefsFromValue(effects, refs);
  collectConfigKeysFromValue(effects, configKeys);
  const config = trait.config;
  if (config) for (const key of configKeys) collectTraitRefsFromValue(config[key] ?? null, refs);
  refs.delete(trait.name);
  return refs;
}
function traitsEmbeddedByEvent(trait, eventKey) {
  const transitions = trait.stateMachine?.transitions ?? [];
  const out = /* @__PURE__ */ new Set();
  for (const transition of transitions) {
    if (transition.event !== eventKey) continue;
    for (const child of traitsEmbeddedByEffects(trait, transition.effects ?? null)) out.add(child);
  }
  return out;
}
function targetTraitOf(traitRef) {
  if (!traitRef || typeof traitRef !== "object") return void 0;
  const resolved = traitRef._resolved;
  return resolved && typeof resolved === "object" ? resolved : traitRef;
}
function collectEmbeddedTraitReferrers(schema) {
  const out = /* @__PURE__ */ new Map();
  if (!schema?.orbitals) return out;
  for (const orbital of schema.orbitals) {
    const traits = orbital.traits;
    if (!Array.isArray(traits)) continue;
    for (const traitRef of traits) {
      const target = targetTraitOf(traitRef);
      if (!target) continue;
      const referrerName = target.name;
      if (typeof referrerName !== "string" || referrerName.length === 0) continue;
      const refs = /* @__PURE__ */ new Set();
      if (target.config) collectTraitRefsFromValue(target.config, refs);
      const stateMachine = target.stateMachine;
      if (stateMachine) collectTraitRefsFromValue(stateMachine, refs);
      if (refs.size === 0) continue;
      for (const child of refs) {
        if (child === referrerName) continue;
        if (!out.has(child)) out.set(child, referrerName);
      }
    }
  }
  return out;
}
function collectTraitEmbedAdjacency(orbital) {
  const out = /* @__PURE__ */ new Map();
  const traits = orbital.traits;
  if (!Array.isArray(traits)) return out;
  for (const traitRef of traits) {
    const target = targetTraitOf(traitRef);
    if (!target) continue;
    const referrerName = target.name;
    if (typeof referrerName !== "string" || referrerName.length === 0) continue;
    const refs = /* @__PURE__ */ new Set();
    if (target.config) collectTraitRefsFromValue(target.config, refs);
    const stateMachine = target.stateMachine;
    if (stateMachine) collectTraitRefsFromValue(stateMachine, refs);
    refs.delete(referrerName);
    if (refs.size > 0) out.set(referrerName, refs);
  }
  return out;
}
function collectTraitConfigRefAdjacency(orbital) {
  const out = /* @__PURE__ */ new Map();
  const traits = orbital.traits;
  if (!Array.isArray(traits)) return out;
  for (const traitRef of traits) {
    const target = targetTraitOf(traitRef);
    if (!target) continue;
    const referrerName = target.name;
    if (typeof referrerName !== "string" || referrerName.length === 0) continue;
    if (!target.config) continue;
    const refs = /* @__PURE__ */ new Set();
    collectTraitRefsFromValue(target.config, refs);
    refs.delete(referrerName);
    if (refs.size > 0) out.set(referrerName, refs);
  }
  return out;
}
function traitDeclaresConfigForward(trait) {
  const config = trait?.config;
  if (!config) return false;
  let found = false;
  const walk = (value) => {
    if (found || value === null || value === void 0) return;
    if (typeof value === "string") {
      const knob = CONFIG_FORWARD_RE.exec(value)?.[1];
      if (knob !== void 0 && !(knob in config)) found = true;
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) walk(item);
      return;
    }
    if (typeof value === "object") {
      for (const v of Object.values(value)) walk(v);
    }
  };
  for (const field of Object.values(config)) {
    const value = field?.default;
    if (typeof value === "string") {
      if (CONFIG_FORWARD_RE.test(value)) return true;
      continue;
    }
    walk(value);
    if (found) return true;
  }
  return false;
}
function resolveForwardsDeep(value, referrerConfig, consumed) {
  if (typeof value === "string") {
    const match = CONFIG_FORWARD_RE.exec(value);
    if (match) {
      const forwarded = referrerConfig?.[match[1]];
      if (forwarded !== void 0) {
        consumed?.set(match[1], forwarded);
        return forwarded;
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => resolveForwardsDeep(item, referrerConfig, consumed));
  }
  if (value !== null && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = resolveForwardsDeep(v, referrerConfig, consumed);
    }
    return out;
  }
  return value;
}
function fromDeclared(declared) {
  if (!declared) return void 0;
  const out = {};
  let any = false;
  for (const [key, field] of Object.entries(declared)) {
    if (field.default !== void 0) {
      out[key] = field.default;
      any = true;
    }
  }
  return any ? out : void 0;
}
function applyForwardRung(out, rungConfig) {
  if (!rungConfig) return;
  const consumed = /* @__PURE__ */ new Map();
  for (const [key, value] of Object.entries(out)) {
    out[key] = resolveForwardsDeep(value, rungConfig, consumed);
  }
  for (const [key, value] of consumed) {
    if (!(key in out)) {
      out[key] = value;
    }
  }
}
function buildResolvedTraitConfigs(schema) {
  const rawByName = {};
  const orbitalByTrait = {};
  if (!schema?.orbitals) return {};
  for (const orbital of schema.orbitals) {
    const traitRefs = orbital.traits;
    if (!traitRefs) continue;
    for (const t of traitRefs) {
      if (typeof t === "string") continue;
      const name = t.name ?? t.ref;
      if (typeof name === "string") {
        orbitalByTrait[name] = orbital;
      }
      const config = t.config;
      if (typeof name === "string" && config !== void 0) {
        rawByName[name] = { ...t, config };
      }
    }
  }
  const referrerByChild = collectEmbeddedTraitReferrers(schema);
  const resolved = /* @__PURE__ */ new Map();
  const resolving = /* @__PURE__ */ new Set();
  function resolveConfig(name) {
    const cached = resolved.get(name);
    if (cached) return cached;
    const raw = rawByName[name]?.config;
    const base = normalizeCallSiteConfigToValues(raw);
    if (!base) return void 0;
    if (resolving.has(name)) return base;
    resolving.add(name);
    const out = { ...base };
    const referrer = referrerByChild.get(name);
    applyForwardRung(out, referrer && referrer !== name ? resolveConfig(referrer) : void 0);
    applyForwardRung(out, fromDeclared(orbitalByTrait[name]?.config));
    applyForwardRung(out, fromDeclared(schema?.config));
    resolving.delete(name);
    resolved.set(name, out);
    return out;
  }
  const map = {};
  for (const name of Object.keys(rawByName)) {
    const cfg = resolveConfig(name);
    if (cfg !== void 0) map[name] = cfg;
  }
  return map;
}
function configReferencesCallsitePayload(config) {
  return config !== void 0 && valueContainsPrefixedString(config, CALLSITE_PAYLOAD_PREFIX);
}
function traitReferencesCallsitePayload(trait) {
  if (!trait) return false;
  if (trait.config && valueContainsPrefixedString(trait.config, CALLSITE_PAYLOAD_PREFIX)) {
    return true;
  }
  const stateMachine = trait.stateMachine;
  if (stateMachine && valueContainsPrefixedString(stateMachine, CALLSITE_PAYLOAD_PREFIX)) {
    return true;
  }
  return false;
}
function collectCallsiteCaptureChildren(orbital) {
  const adjacency = collectTraitEmbedAdjacency(orbital);
  const traitsByName = /* @__PURE__ */ new Map();
  const traits = orbital.traits;
  if (Array.isArray(traits)) {
    for (const traitRef of traits) {
      const target = targetTraitOf(traitRef);
      if (target?.name) traitsByName.set(target.name, target);
    }
  }
  const reachesCapture = /* @__PURE__ */ new Map();
  function traitReachesCapture(name, seen) {
    const cached = reachesCapture.get(name);
    if (cached !== void 0) return cached;
    if (seen.has(name)) return false;
    seen.add(name);
    const trait = traitsByName.get(name);
    if (trait && traitReferencesCallsitePayload(trait)) {
      reachesCapture.set(name, true);
      return true;
    }
    const children = adjacency.get(name);
    if (children) {
      for (const child of children) {
        if (traitReachesCapture(child, seen)) {
          reachesCapture.set(name, true);
          return true;
        }
      }
    }
    reachesCapture.set(name, false);
    return false;
  }
  const out = /* @__PURE__ */ new Map();
  for (const [referrer, children] of adjacency) {
    const keep = /* @__PURE__ */ new Set();
    for (const child of children) {
      if (traitReachesCapture(child, /* @__PURE__ */ new Set())) keep.add(child);
    }
    if (keep.size > 0) out.set(referrer, keep);
  }
  return out;
}
function configForwardTokens(entry) {
  const tokens = [];
  const walk = (value) => {
    if (typeof value === "string") {
      if (value.startsWith("@config.")) tokens.push(value);
    } else if (Array.isArray(value)) {
      for (const item of value) walk(item);
    } else if (value !== null && typeof value === "object") {
      for (const item of Object.values(value)) walk(item);
    }
  };
  walk(entry.default);
  if (entry.forwardedFrom !== void 0) tokens.push(entry.forwardedFrom);
  tokens.push(...entry.nestedForwardedFrom ?? []);
  return tokens;
}
function collectForwardedConfigKeys(traits) {
  const forwarded = /* @__PURE__ */ new Set();
  for (const traitRef of traits) {
    const config = typeof traitRef === "string" ? void 0 : traitRef.config;
    if (!config) continue;
    for (const entry of Object.values(config)) {
      if (!isCallSiteConfigDeclaration(entry)) continue;
      for (const token of configForwardTokens(entry)) {
        const knob = CONFIG_FORWARD_RE.exec(token)?.[1];
        if (knob) forwarded.add(knob);
      }
    }
  }
  return forwarded;
}
function collectOrbitalForwardedConfigKeys(orbital) {
  const forwarded = new Set(collectForwardedConfigKeys(orbital.traits ?? []));
  for (const use of orbital.uses ?? []) {
    for (const entry of Object.values(use.config ?? {})) {
      for (const token of configForwardTokens(entry)) {
        const knob = CONFIG_FORWARD_RE.exec(token)?.[1];
        if (knob) forwarded.add(knob);
      }
    }
  }
  return forwarded;
}
function filterConfigToForwardedKeys(config, forwarded) {
  const kept = Object.entries(config).filter(([key]) => forwarded.has(key));
  return kept.length > 0 ? Object.fromEntries(kept) : void 0;
}
function orbitalFileOf(schema, orbital) {
  const config = schema.config !== void 0 ? filterConfigToForwardedKeys(schema.config, collectOrbitalForwardedConfigKeys(orbital)) : void 0;
  return { name: orbital.name, version: schema.version ?? "1.0.0", orbitals: [orbital], ...config !== void 0 ? { config } : {} };
}
function navItemsList(field) {
  return field.type === "[NavItem]" && Array.isArray(field.default) ? field.default : null;
}
function isConfigObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function navItemHref(item) {
  return isConfigObject(item) && typeof item.href === "string" ? item.href : void 0;
}
function unionOrganismConfigs(schemas) {
  let merged;
  for (const schema of schemas) {
    if (schema.config === void 0) continue;
    merged ?? (merged = {});
    for (const [key, field] of Object.entries(schema.config)) {
      const held = merged[key];
      if (held === void 0) {
        merged[key] = field;
        continue;
      }
      const heldList = navItemsList(held);
      const addedList = navItemsList(field);
      if (heldList === null || addedList === null) continue;
      const listed = new Set(heldList.map(navItemHref).filter((href) => href !== void 0));
      const added = addedList.filter((item) => {
        const href = navItemHref(item);
        return href === void 0 || !listed.has(href);
      });
      if (added.length > 0) merged[key] = { ...held, default: [...heldList, ...added] };
    }
  }
  return merged;
}

// src/lib/get-nested-value.ts
function getNestedValue(obj, path) {
  if (obj === null || obj === void 0 || !path) return void 0;
  if (typeof obj !== "object") return void 0;
  if (!path.includes(".") && !Array.isArray(obj)) {
    return obj[path];
  }
  let value = obj;
  for (const part of path.split(".")) {
    if (value === null || value === void 0 || typeof value !== "object") return void 0;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return void 0;
      value = value[Number(part)];
      continue;
    }
    value = value[part];
  }
  return value;
}

// src/lib/theme-tone-colors.ts
var INDISTINCT_DELTA_E = 10;
function themeModes(name, theme) {
  const base = theme.tokens.colors ?? {};
  const modes = Object.keys(base).length > 0 ? [{ id: name, colors: base }] : [];
  for (const [variant, v] of Object.entries(theme.variants ?? {})) modes.push({ id: `${name}:${variant}`, colors: v.colors ?? {} });
  return modes;
}
function toneColor(mode, tone) {
  return mode.colors[DIAGRAM_TONE_COLOR_KEYS[tone]];
}
function hexToLab(hex) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(hex.trim());
  if (!m) return void 0;
  const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1].slice(0, 6);
  const lin = (i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = [lin(0), lin(2), lin(4)];
  const f = (t) => t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116;
  const fx = f((0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047);
  const fy = f(0.2126729 * r + 0.7151522 * g + 0.072175 * b);
  const fz = f((0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}
function ciede2000(lab1, lab2) {
  const [L1, a1, b1] = lab1;
  const [L2, a2, b2] = lab2;
  const rad = Math.PI / 180;
  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cbar7 = ((C1 + C2) / 2) ** 7;
  const G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + 25 ** 7)));
  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);
  const hue = (b, a) => {
    if (a === 0 && b === 0) return 0;
    const h = Math.atan2(b, a) / rad;
    return h >= 0 ? h : h + 360;
  };
  const h1p = hue(b1, a1p);
  const h2p = hue(b2, a2p);
  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  let dhp = 0;
  if (C1p * C2p !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360;
    else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(dhp / 2 * rad);
  const Lbarp = (L1 + L2) / 2;
  const Cbarp = (C1p + C2p) / 2;
  let hbarp = h1p + h2p;
  if (C1p * C2p !== 0) {
    if (Math.abs(h1p - h2p) <= 180) hbarp = (h1p + h2p) / 2;
    else hbarp = h1p + h2p < 360 ? (h1p + h2p + 360) / 2 : (h1p + h2p - 360) / 2;
  }
  const T = 1 - 0.17 * Math.cos((hbarp - 30) * rad) + 0.24 * Math.cos(2 * hbarp * rad) + 0.32 * Math.cos((3 * hbarp + 6) * rad) - 0.2 * Math.cos((4 * hbarp - 63) * rad);
  const dTheta = 30 * Math.exp(-(((hbarp - 275) / 25) ** 2));
  const Cbarp7 = Cbarp ** 7;
  const RC = 2 * Math.sqrt(Cbarp7 / (Cbarp7 + 25 ** 7));
  const SL = 1 + 0.015 * (Lbarp - 50) ** 2 / Math.sqrt(20 + (Lbarp - 50) ** 2);
  const SC = 1 + 0.045 * Cbarp;
  const SH = 1 + 0.015 * Cbarp * T;
  const RT = -Math.sin(2 * dTheta * rad) * RC;
  return Math.sqrt((dLp / SL) ** 2 + (dCp / SC) ** 2 + (dHp / SH) ** 2 + RT * (dCp / SC) * (dHp / SH));
}
function colorDifference(a, b) {
  const la = hexToLab(a);
  const lb = hexToLab(b);
  return la && lb ? ciede2000(la, lb) : void 0;
}

// src/listen-payload-mapping.ts
function applyListenPayloadMapping(payloadMapping, payload, evaluate) {
  if (!payloadMapping || !payload) return payload;
  const mapped = {};
  for (const [key, expr] of Object.entries(payloadMapping)) {
    let value;
    try {
      value = evaluate(expr, payload);
    } catch {
      continue;
    }
    if (isEventPayloadValue(value)) mapped[key] = value;
  }
  return mapped;
}

// src/ml-contract.ts
function contractFieldName(f) {
  return typeof f === "string" ? f : f.name;
}
function isTensorValue(v) {
  if (typeof v === "number") return true;
  return Array.isArray(v) && v.every(isTensorValue);
}
function describeTensorMismatch(v) {
  if (v === null) return "null";
  if (Array.isArray(v)) return "array containing non-numeric values";
  return typeof v;
}
function tensorShape(t) {
  if (typeof t === "number") return [];
  if (t.length === 0) return [0];
  const first = t[0];
  if (typeof first === "number") return [t.length];
  return [t.length, ...tensorShape(first)];
}
function tensorLastDimSize(t) {
  const shape = tensorShape(t);
  return shape.length === 0 ? 0 : shape[shape.length - 1];
}
function arraysEqual(a, b) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
function gatherTensorLastDim(t, dim) {
  if (typeof t === "number") return [];
  if (t.length === 0) return [];
  if (typeof t[0] === "number") return [t[dim]];
  const out = [];
  for (const sub of t) out.push(...gatherTensorLastDim(sub, dim));
  return out;
}
function mapTensorLastDim(t, dim, fn) {
  if (typeof t === "number") return t;
  if (t.length === 0) return t;
  if (typeof t[0] === "number") {
    const copy = [...t];
    copy[dim] = fn(copy[dim]);
    return copy;
  }
  return t.map((sub) => mapTensorLastDim(sub, dim, fn));
}
function validateContract(tensor, contract) {
  if (!isTensorValue(tensor)) {
    return {
      valid: false,
      violations: [{ type: "not_a_tensor", actualType: describeTensorMismatch(tensor) }]
    };
  }
  const violations = [];
  const shape = tensorShape(tensor);
  if (contract.shape && !arraysEqual(shape, contract.shape)) {
    violations.push({ type: "shape_mismatch", expected: contract.shape, actual: shape });
  }
  const ranges = contract.ranges ?? {};
  const size = tensorLastDimSize(tensor);
  for (const [dimStr, bounds] of Object.entries(ranges)) {
    const dim = Number(dimStr);
    if (dim >= size) continue;
    const vals = gatherTensorLastDim(tensor, dim);
    const min = bounds.min ?? -Infinity;
    const max = bounds.max ?? Infinity;
    const actualMin = Math.min(...vals);
    const actualMax = Math.max(...vals);
    if (actualMin < min || actualMax > max) {
      violations.push({ type: "range_violation", dim, min, max, actualMin, actualMax });
    }
  }
  return { valid: violations.length === 0, violations };
}

// src/diff.ts
function diffSchemas(before, after) {
  const changes = [];
  let changeId = 0;
  const nextId = () => `change-${++changeId}`;
  if (before.name !== after.name) {
    changes.push({
      id: nextId(),
      operation: "modify",
      target: "schema",
      path: ["name"],
      before: before.name,
      after: after.name,
      description: `Renamed schema from "${before.name}" to "${after.name}"`
    });
  }
  if (before.description !== after.description) {
    changes.push({
      id: nextId(),
      operation: "modify",
      target: "schema",
      path: ["description"],
      before: before.description,
      after: after.description,
      description: `Modified schema description`
    });
  }
  const beforeOrbitals = new Map(
    (before.orbitals || []).map((o, i) => [o.name, { orbital: o, index: i }])
  );
  const afterOrbitals = new Map(
    (after.orbitals || []).map((o, i) => [o.name, { orbital: o, index: i }])
  );
  for (const [name, { index }] of beforeOrbitals) {
    if (!afterOrbitals.has(name)) {
      changes.push({
        id: nextId(),
        operation: "remove",
        target: "orbital",
        path: ["orbitals", index],
        before: name,
        description: `Removed orbital: ${name}`
      });
    }
  }
  for (const [name, { index }] of afterOrbitals) {
    if (!beforeOrbitals.has(name)) {
      changes.push({
        id: nextId(),
        operation: "add",
        target: "orbital",
        path: ["orbitals", index],
        after: name,
        description: `Added orbital: ${name}`
      });
    }
  }
  for (const [name, { orbital: afterOrbital, index }] of afterOrbitals) {
    const beforeEntry = beforeOrbitals.get(name);
    if (beforeEntry) {
      const beforeJson = JSON.stringify(beforeEntry.orbital);
      const afterJson = JSON.stringify(afterOrbital);
      if (beforeJson !== afterJson) {
        changes.push({
          id: nextId(),
          operation: "modify",
          target: "orbital",
          path: ["orbitals", index],
          description: `Modified orbital: ${name}`
        });
        const beforeEntity = beforeEntry.orbital.entity;
        const afterEntity = afterOrbital.entity;
        if (JSON.stringify(beforeEntity) !== JSON.stringify(afterEntity)) {
          const entityName2 = typeof afterEntity === "string" ? afterEntity : afterEntity && typeof afterEntity === "object" && "name" in afterEntity ? afterEntity.name : "entity";
          changes.push({
            id: nextId(),
            operation: "modify",
            target: "entity",
            path: ["orbitals", index, "entity"],
            description: `Modified entity: ${entityName2}`
          });
        }
        const beforeTraits = beforeEntry.orbital.traits || [];
        const afterTraits = afterOrbital.traits || [];
        if (JSON.stringify(beforeTraits) !== JSON.stringify(afterTraits)) {
          changes.push({
            id: nextId(),
            operation: "modify",
            target: "traits",
            path: ["orbitals", index, "traits"],
            description: `Modified traits in orbital: ${name}`
          });
        }
        const beforePages = beforeEntry.orbital.pages || [];
        const afterPages = afterOrbital.pages || [];
        if (JSON.stringify(beforePages) !== JSON.stringify(afterPages)) {
          changes.push({
            id: nextId(),
            operation: "modify",
            target: "pages",
            path: ["orbitals", index, "pages"],
            description: `Modified pages in orbital: ${name}`
          });
        }
      }
    }
  }
  const beforeServices = JSON.stringify(before.services || []);
  const afterServices = JSON.stringify(after.services || []);
  if (beforeServices !== afterServices) {
    changes.push({
      id: nextId(),
      operation: "modify",
      target: "services",
      path: ["services"],
      description: "Modified services"
    });
  }
  return { changes };
}
function isDestructiveChange(changeSet) {
  return changeSet.changes.some((c) => c.operation === "remove");
}
function getRemovals(changeSet) {
  return changeSet.changes.filter((c) => c.operation === "remove");
}
function categorizeRemovals(changeSet) {
  const critical = [];
  const standard = [];
  const minor = [];
  for (const change of changeSet.changes) {
    if (change.operation !== "remove") continue;
    const target = change.target.toLowerCase();
    if (target === "orbital" || target === "entity" || target === "state" || target === "page") {
      critical.push(change);
    } else if (target === "field" || target === "action" || target === "trait" || target === "traits") {
      standard.push(change);
    } else {
      minor.push(change);
    }
  }
  return { critical, standard, minor, pageContentReductions: [] };
}
function requiresConfirmation(removals) {
  return removals.critical.length > 0;
}
function detectPageContentReduction(beforePages, afterPages) {
  const reductions = [];
  const getPageName = (p) => {
    if (p && typeof p === "object" && "name" in p) {
      return p.name;
    }
    return null;
  };
  const countContent = (page) => {
    if (!page || typeof page !== "object") return { sections: 0, actions: 0 };
    const p = page;
    const traits = Array.isArray(p.traits) ? p.traits : [];
    return { sections: traits.length, actions: 0 };
  };
  const beforeMap = /* @__PURE__ */ new Map();
  for (const p of beforePages) {
    const name = getPageName(p);
    if (name) beforeMap.set(name, p);
  }
  for (const afterPage of afterPages) {
    const name = getPageName(afterPage);
    if (!name) continue;
    const beforePage = beforeMap.get(name);
    if (!beforePage) continue;
    const beforeContent = countContent(beforePage);
    const afterContent = countContent(afterPage);
    const sectionsRemoved = beforeContent.sections - afterContent.sections;
    const actionsRemoved = beforeContent.actions - afterContent.actions;
    if (sectionsRemoved > 0 || actionsRemoved > 0) {
      reductions.push({
        pageName: name,
        componentsRemoved: sectionsRemoved,
        actionsRemoved,
        displaysRemoved: 0,
        before: beforeContent,
        after: afterContent,
        isSignificant: sectionsRemoved > 0
      });
    }
  }
  return reductions;
}
function hasSignificantPageReduction(reductions) {
  return reductions.some((r) => r.isSignificant);
}
function jsonEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}
function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}
function namedTraitMap(refs) {
  const map = /* @__PURE__ */ new Map();
  for (const r of refs) {
    const key = typeof r === "string" ? r : isInlineTrait(r) ? r.name : r.name ?? r.ref;
    map.set(key, r);
  }
  return map;
}
function composedSource(r) {
  if (typeof r === "string") return r;
  return isInlineTrait(r) ? null : r.ref;
}
function traitConfigOf(r) {
  return typeof r === "string" ? void 0 : r.config;
}
function inlinePageMap(refs) {
  const map = /* @__PURE__ */ new Map();
  for (const r of refs) {
    if (typeof r === "string") continue;
    if ("ref" in r && !("name" in r)) continue;
    const page = r;
    if (page.path) map.set(page.path, page);
  }
  return map;
}
function isRenderUIEffect(effect) {
  if (!Array.isArray(effect)) return false;
  return String(effect[0]) === "render-ui" || String(effect[0]) === "renderUI";
}
function tKey(t) {
  return `${t.from ?? ""}+${t.event}`;
}
function diffSemanticTransitions(orbitalName, traitName, before, after) {
  const changes = [];
  const bMap = new Map(before.map((t) => [tKey(t), t]));
  const aMap = new Map(after.map((t) => [tKey(t), t]));
  for (const [key, aT] of aMap) {
    const bT = bMap.get(key);
    if (!bT) continue;
    if (!jsonEqual(bT.guard, aT.guard)) {
      changes.push({ kind: "guard-changed", orbitalName, traitName, transitionEvent: aT.event });
    }
    if (!jsonEqual(bT.effects, aT.effects)) {
      const bEffects = bT.effects ?? [];
      const aEffects = aT.effects ?? [];
      const bRUI = bEffects.filter(isRenderUIEffect);
      const aRUI = aEffects.filter(isRenderUIEffect);
      if (bRUI.length !== aRUI.length || !jsonEqual(bRUI, aRUI)) {
        changes.push({ kind: "render-ui-changed", orbitalName, traitName, transitionEvent: aT.event });
      } else {
        changes.push({ kind: "effect-changed", orbitalName, traitName, transitionEvent: aT.event });
      }
    }
  }
  return changes;
}
function diffSemanticOrbital(name, before, after) {
  const changes = [];
  const bEntity = typeof before.entity === "object" ? before.entity : null;
  const aEntity = typeof after.entity === "object" ? after.entity : null;
  if (bEntity && aEntity) {
    if (!jsonEqual(bEntity.fields, aEntity.fields)) {
      changes.push({ kind: "entity-fields-changed", orbitalName: name });
    }
  } else if (bEntity !== aEntity) {
    changes.push({ kind: "entity-fields-changed", orbitalName: name });
  }
  const bTraits = namedTraitMap(before.traits ?? []);
  const aTraits = namedTraitMap(after.traits ?? []);
  for (const [tName] of aTraits) {
    if (!bTraits.has(tName)) changes.push({ kind: "trait-added", orbitalName: name, traitName: tName });
  }
  for (const [tName] of bTraits) {
    if (!aTraits.has(tName)) changes.push({ kind: "trait-removed", orbitalName: name, traitName: tName });
  }
  for (const [tName, aRef] of aTraits) {
    const bRef = bTraits.get(tName);
    if (bRef === void 0) continue;
    if (composedSource(bRef) !== composedSource(aRef)) {
      changes.push({ kind: "trait-removed", orbitalName: name, traitName: tName });
      changes.push({ kind: "trait-added", orbitalName: name, traitName: tName });
      continue;
    }
    if (!jsonEqual(traitConfigOf(bRef), traitConfigOf(aRef))) {
      changes.push({ kind: "trait-config-changed", orbitalName: name, traitName: tName });
    }
    if (!isInlineTrait(bRef) || !isInlineTrait(aRef)) {
      if (typeof bRef !== "string" && typeof aRef !== "string" && (!jsonEqual(bRef.linkedEntity, aRef.linkedEntity) || !jsonEqual(bRef.events, aRef.events))) {
        changes.push({ kind: "event-wiring-changed", orbitalName: name, traitName: tName });
      }
      continue;
    }
    const bTrait = bRef;
    const aTrait = aRef;
    const bSM = bTrait.stateMachine;
    const aSM = aTrait.stateMachine;
    if (!bSM && !aSM) continue;
    if (!bSM || !aSM) {
      changes.push({ kind: "state-machine-changed", orbitalName: name, traitName: tName });
      continue;
    }
    const bStates = new Set((bSM.states ?? []).map((s) => s.name));
    const aStates = new Set((aSM.states ?? []).map((s) => s.name));
    if (!setsEqual(bStates, aStates)) {
      changes.push({ kind: "state-machine-changed", orbitalName: name, traitName: tName });
    }
    const bTKeys = new Set((bSM.transitions ?? []).map(tKey));
    const aTKeys = new Set((aSM.transitions ?? []).map(tKey));
    if (!setsEqual(bTKeys, aTKeys)) {
      if (!changes.some((c) => c.kind === "state-machine-changed" && c.traitName === tName)) {
        changes.push({ kind: "state-machine-changed", orbitalName: name, traitName: tName });
      }
    }
    changes.push(...diffSemanticTransitions(name, tName, bSM.transitions ?? [], aSM.transitions ?? []));
    if (!jsonEqual(bTrait.emits, aTrait.emits) || !jsonEqual(bTrait.listens, aTrait.listens)) {
      changes.push({ kind: "event-wiring-changed", orbitalName: name, traitName: tName });
    }
  }
  if (!jsonEqual(before.theme, after.theme)) {
    changes.push({ kind: "theme-changed", orbitalName: name });
  }
  const bPages = inlinePageMap(before.pages ?? []);
  const aPages = inlinePageMap(after.pages ?? []);
  const bPaths = new Set(bPages.keys());
  const aPaths = new Set(aPages.keys());
  if (!setsEqual(bPaths, aPaths)) {
    changes.push({ kind: "page-changed", orbitalName: name });
  } else {
    for (const [path, aPage] of aPages) {
      if (!jsonEqual(bPages.get(path), aPage)) {
        changes.push({ kind: "page-changed", orbitalName: name });
        break;
      }
    }
  }
  return changes;
}
function diffSchemaSemantics(before, after) {
  const changes = [];
  const bOrbitals = /* @__PURE__ */ new Map();
  for (const o of before.orbitals ?? []) {
    const orb = o;
    bOrbitals.set(orb.name, orb);
  }
  const aOrbitals = /* @__PURE__ */ new Map();
  for (const o of after.orbitals ?? []) {
    const orb = o;
    aOrbitals.set(orb.name, orb);
  }
  const added = [];
  for (const [name] of aOrbitals) {
    if (!bOrbitals.has(name)) {
      changes.push({ kind: "orbital-added", orbitalName: name });
      added.push(name);
    }
  }
  for (const [name] of bOrbitals) {
    if (!aOrbitals.has(name)) {
      changes.push({ kind: "orbital-removed", orbitalName: name });
    }
  }
  if (added.length > 1) {
    changes.push({ kind: "behavior-composed", orbitalName: added[0] });
  }
  for (const [name, aOrb] of aOrbitals) {
    const bOrb = bOrbitals.get(name);
    if (bOrb) changes.push(...diffSemanticOrbital(name, bOrb, aOrb));
  }
  if (!jsonEqual(before.theme, after.theme) || !jsonEqual(before.designTokens, after.designTokens)) {
    for (const [name] of aOrbitals) {
      if (!changes.some((c) => c.kind === "theme-changed" && c.orbitalName === name)) {
        changes.push({ kind: "theme-changed", orbitalName: name });
      }
    }
  }
  return changes;
}
function diffOrbitalSchemas(before, after, options) {
  const summary = {
    orbitalsAdded: [],
    orbitalsRemoved: [],
    orbitalsModified: [],
    traitsAdded: [],
    traitsModified: [],
    traitsRemoved: []
  };
  const changes = [];
  if (!before) {
    for (const orbital of after.orbitals ?? []) {
      const orb = orbital;
      summary.orbitalsAdded.push(orb.name);
      changes.push({
        operation: "add",
        path: `orbitals[${summary.orbitalsAdded.length - 1}]`,
        value: orb
      });
    }
  } else {
    const beforeOrbitals = /* @__PURE__ */ new Map();
    for (const o of before.orbitals ?? []) {
      const orb = o;
      beforeOrbitals.set(orb.name, orb);
    }
    const afterOrbitals = /* @__PURE__ */ new Map();
    for (const o of after.orbitals ?? []) {
      const orb = o;
      afterOrbitals.set(orb.name, orb);
    }
    let idx = 0;
    for (const [name, orb] of afterOrbitals) {
      const prev = beforeOrbitals.get(name);
      if (!prev) {
        summary.orbitalsAdded.push(name);
        changes.push({
          operation: "add",
          path: `orbitals[${idx}]`,
          value: orb
        });
      } else if (JSON.stringify(prev) !== JSON.stringify(orb)) {
        summary.orbitalsModified.push(name);
        changes.push({
          operation: "merge",
          path: `orbitals[${idx}]`,
          previousValue: prev,
          value: orb
        });
      }
      idx++;
    }
    for (const [name, orb] of beforeOrbitals) {
      if (!afterOrbitals.has(name)) {
        summary.orbitalsRemoved.push(name);
        changes.push({
          operation: "remove",
          path: `orbitals[${name}]`,
          previousValue: orb
        });
      }
    }
    if (before.name !== after.name) {
      changes.push({
        operation: "set",
        path: "name",
        previousValue: before.name,
        value: after.name
      });
    }
    if (before.version !== after.version) {
      changes.push({
        operation: "set",
        path: "version",
        previousValue: before.version,
        value: after.version
      });
    }
    if (before.description !== after.description) {
      changes.push({
        operation: "set",
        path: "description",
        previousValue: before.description,
        value: after.description
      });
    }
    if (before.summary !== after.summary) {
      changes.push({
        operation: "set",
        path: "summary",
        previousValue: before.summary,
        value: after.summary
      });
    }
  }
  const hasChanges = changes.length > 0;
  const description = describeDiff(summary, options.mode, after.name);
  return {
    hasChanges,
    changeCount: changes.length,
    changeset: {
      id: generateChangesetId(after.name, changes.length),
      source: options.source,
      author: options.author,
      description,
      changes
    },
    summary
  };
}
function describeDiff(summary, mode, schemaName) {
  const parts = [];
  if (summary.orbitalsAdded.length > 0) {
    parts.push(`+${summary.orbitalsAdded.length} orbital(s)`);
  }
  if (summary.orbitalsModified.length > 0) {
    parts.push(`~${summary.orbitalsModified.length} orbital(s)`);
  }
  if (summary.orbitalsRemoved.length > 0) {
    parts.push(`-${summary.orbitalsRemoved.length} orbital(s)`);
  }
  if (parts.length === 0) return "No changes";
  return `${mode === "initial" ? "Initial" : "Update"} ${schemaName}: ${parts.join(", ")}`;
}
function generateChangesetId(seed, count) {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  let hash = 0;
  const source = `${seed}:${count}:${Date.now()}`;
  for (let i = 0; i < source.length; i++) {
    hash = hash * 31 + source.charCodeAt(i) | 0;
  }
  return `chg_${(hash >>> 0).toString(16)}_${count}`;
}

// src/summarize.ts
var CRUD_STATES = /* @__PURE__ */ new Set(["browsing", "creating", "editing", "viewing", "deleting"]);
var STEP_PATTERN = /^step\d+$/i;
function classifyWorkflow(states) {
  const names = states.map((s) => s.name.toLowerCase());
  const crudMatch = names.filter((n) => CRUD_STATES.has(n)).length;
  if (crudMatch >= 3) return "crud";
  const stepCount = names.filter((n) => STEP_PATTERN.test(n)).length;
  if (stepCount >= 2) return "wizard";
  const hasTerminal = states.some((s) => s.isTerminal || s.isFinal);
  const hasComplete = names.some(
    (n) => n === "complete" || n === "completed" || n === "done" || n === "finished"
  );
  if ((hasTerminal || hasComplete) && states.length >= 4 && crudMatch <= 1) {
    return "wizard";
  }
  return "custom";
}
function isPrimaryKey(field) {
  return field.primaryKey === true;
}
function isBusinessField(field) {
  if (isPrimaryKey(field)) return false;
  if (field.type === "enum" || field.type === "relation") return true;
  if (field.required) return true;
  return false;
}
function summarizeEntity(entity) {
  const fields = entity.fields.filter(isBusinessField).map(summarizeField);
  return {
    name: entity.name,
    collection: entity.collection,
    fields
  };
}
function summarizeField(f) {
  if (f.type === "enum") {
    return {
      ...f.name !== void 0 ? { name: f.name } : {},
      type: "enum",
      values: f.values,
      ...f.required ? { required: true } : {}
    };
  }
  if (f.type === "union") {
    return {
      ...f.name !== void 0 ? { name: f.name } : {},
      type: "union",
      values: f.values,
      ...f.required ? { required: true } : {}
    };
  }
  if (f.type === "relation") {
    return {
      ...f.name !== void 0 ? { name: f.name } : {},
      type: "relation",
      relation: f.relation,
      ...f.required ? { required: true } : {}
    };
  }
  if (f.type === "array") {
    return {
      ...f.name !== void 0 ? { name: f.name } : {},
      type: "array",
      items: f.items,
      ...f.required ? { required: true } : {}
    };
  }
  if (f.type === "tuple") {
    return {
      ...f.name !== void 0 ? { name: f.name } : {},
      type: "tuple",
      properties: f.properties,
      ...f.required ? { required: true } : {}
    };
  }
  return {
    ...f.name !== void 0 ? { name: f.name } : {},
    type: f.type,
    ...f.required ? { required: true } : {}
  };
}
function summarizeTransitions(transitions) {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const t of transitions) {
    const key = `${t.from}::${t.to}::${t.event}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ from: t.from, to: t.to, event: t.event });
    }
  }
  return result;
}
function summarizeEvents(events) {
  return events.map((e) => ({ key: e.key, name: e.key }));
}
function summarizeTrait(trait) {
  const states = trait.stateMachine?.states ?? [];
  const events = trait.stateMachine?.events ?? [];
  const transitions = trait.stateMachine?.transitions ?? [];
  const result = { name: trait.name, scope: trait.scope };
  if (trait.category) result.category = trait.category;
  if (trait.linkedEntity) result.linkedEntity = trait.linkedEntity;
  if (trait.stateMachine) {
    result.stateMachine = {
      states: states.map((s) => {
        const st = { name: s.name };
        if (s.isInitial) st.isInitial = true;
        if (s.isTerminal) st.isTerminal = true;
        if (s.isFinal) st.isFinal = true;
        return st;
      }),
      events: summarizeEvents(events),
      transitions: summarizeTransitions(transitions)
    };
  }
  if (trait.emits && trait.emits.length > 0) {
    result.emits = trait.emits.map((e) => ({
      event: e.event,
      ...e.scope ? { scope: e.scope } : {}
    }));
  }
  if (trait.listens && trait.listens.length > 0) {
    result.listens = trait.listens.map((l) => ({
      event: l.event,
      triggers: l.triggers,
      ...l.scope ? { scope: l.scope } : {}
    }));
  }
  return result;
}
function summarizePage(page) {
  const result = { name: page.name, path: page.path };
  if (page.traits && page.traits.length > 0) {
    result.traits = page.traits.map((t) => ({ ref: t.ref }));
  }
  return result;
}
function summarizeOrbital(orbital) {
  let entity;
  if (isEntityReference(orbital.entity)) {
    entity = { name: orbital.entity, fields: [] };
  } else if (isEntityCall(orbital.entity)) {
    const call = orbital.entity;
    const name = call.name ?? parseEntityRef(call.extends)?.alias ?? call.extends;
    entity = {
      name,
      fields: call.fields ?? [],
      ...call.persistence ? { persistence: call.persistence } : {},
      ...call.collection ? { collection: call.collection } : {}
    };
  } else {
    entity = summarizeEntity(orbital.entity);
  }
  const traits = orbital.traits.map((traitRef) => {
    if (isInlineTrait(traitRef)) {
      return summarizeTrait(traitRef);
    }
    return traitRef;
  });
  const pages = orbital.pages.filter((p) => typeof p === "object" && "name" in p && !("ref" in p)).map(summarizePage);
  return { name: orbital.name, entity, traits, pages };
}
function summarizeSchema(schema) {
  return {
    name: schema.name,
    description: schema.description,
    version: schema.version,
    orbitals: schema.orbitals.map(summarizeOrbital)
  };
}

// src/factory/knob-defs.ts
function rehydrateKnobDefs(catalog) {
  const table = catalog.knobDefs;
  if (table === void 0 || table.length === 0) return catalog;
  for (const sig of catalog.signatures) {
    for (const trait of sig.traits ?? []) {
      const refs = trait.overridableConfigKeyRefs;
      if (refs === void 0) continue;
      const knobs = [];
      for (const i of refs) {
        const knob = table[i];
        if (knob !== void 0) knobs.push(knob);
      }
      const mutable = trait;
      mutable.overridableConfigKeys = knobs;
      delete mutable.overridableConfigKeyRefs;
    }
    const configRefs = sig.configRefs;
    if (configRefs === void 0) continue;
    const configKnobs = [];
    for (const i of configRefs) {
      const knob = table[i];
      if (knob !== void 0) configKnobs.push(knob);
    }
    const mutableSig = sig;
    mutableSig.config = configKnobs;
    delete mutableSig.configRefs;
  }
  return catalog;
}

// src/factory/merge.ts
function canonical(v) {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (isJsonObject(v)) return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k] ?? null)}`).join(",")}}`;
  return JSON.stringify(v);
}
var same = (a, b) => a === void 0 || b === void 0 ? a === b : canonical(a) === canonical(b);
function isIdList(v) {
  return Array.isArray(v) && v.length > 0 && v.every((i) => isJsonObject(i) && typeof i["id"] === "string");
}
var idList = (v) => v === void 0 || Array.isArray(v) && v.length === 0 || isIdList(v);
function mergeParams(base, ours, theirs) {
  const conflicts = [];
  const merged = mergeValue([], base, ours, theirs, conflicts);
  return { merged: merged === void 0 ? null : merged, conflicts };
}
function mergeValue(path, base, ours, theirs, conflicts) {
  if (same(ours, theirs)) return ours;
  if (same(base, ours)) return theirs;
  if (same(base, theirs)) return ours;
  if (ours !== void 0 && theirs !== void 0 && isJsonObject(ours) && isJsonObject(theirs) && (base === void 0 || isJsonObject(base))) {
    return mergeObject(path, base, ours, theirs, conflicts);
  }
  if (ours !== void 0 && theirs !== void 0 && (isIdList(ours) || isIdList(theirs)) && idList(ours) && idList(theirs) && idList(base)) {
    return mergeIdList(path, base ?? [], ours, theirs, conflicts);
  }
  conflicts.push({
    path,
    kind: ours === void 0 || theirs === void 0 ? "delete-vs-edit" : "both-changed",
    base: base ?? null,
    ours: ours ?? null,
    theirs: theirs ?? null
  });
  return ours;
}
function mergeObject(path, base, ours, theirs, conflicts) {
  const out = {};
  const keys = [.../* @__PURE__ */ new Set([...Object.keys(base ?? {}), ...Object.keys(ours), ...Object.keys(theirs)])];
  for (const key of keys) {
    const value = mergeValue([...path, key], base?.[key], ours[key], theirs[key], conflicts);
    if (value !== void 0) out[key] = value;
  }
  return out;
}
var idsOf = (list) => list.map((i) => i.id);
function mergeIdList(path, base, ours, theirs, conflicts) {
  const byId = (list) => new Map(list.map((i) => [i.id, i]));
  const b = byId(base);
  const o = byId(ours);
  const t = byId(theirs);
  const result = /* @__PURE__ */ new Map();
  for (const id of /* @__PURE__ */ new Set([...b.keys(), ...o.keys(), ...t.keys()])) {
    const value = mergeValue([...path, id], b.get(id), o.get(id), t.get(id), conflicts);
    if (value !== void 0) result.set(id, value);
  }
  const common = (list) => idsOf(list).filter((id) => b.has(id));
  const reordered = (list) => canonical(common(list).filter((id) => o.has(id) && t.has(id))) !== canonical(idsOf(base).filter((id) => o.has(id) && t.has(id)));
  const [primary, secondary] = reordered(ours) || !reordered(theirs) ? [ours, theirs] : [theirs, ours];
  const order = idsOf(primary).filter((id) => result.has(id));
  const secondaryIds = idsOf(secondary);
  secondaryIds.forEach((id, index) => {
    if (!result.has(id) || order.includes(id)) return;
    const before = secondaryIds.slice(0, index).reverse().find((prev) => order.includes(prev));
    order.splice(before === void 0 ? 0 : order.indexOf(before) + 1, 0, id);
  });
  for (const id of result.keys()) if (!order.includes(id)) order.push(id);
  return order.map((id) => result.get(id) ?? null);
}

// src/factory/lolo-behavior-contracts.ts
var JS_TO_LOLO_TYPE = {
  string: "string",
  number: "number",
  integer: "number",
  boolean: "boolean",
  array: "array",
  object: "object"
};
function jsToLolo(t) {
  return JS_TO_LOLO_TYPE[t ?? ""] ?? (t || "string");
}
function inferConfigType(spec) {
  if (spec === void 0 || !isJsonObject(spec)) return void 0;
  const explicit = spec["type"];
  if (typeof explicit === "string" && explicit.length > 0) return explicit;
  const d = spec["default"];
  if (Array.isArray(d)) return "array";
  if (d === null || d === void 0) return void 0;
  return typeof d;
}
function sortKeysDeep(v) {
  if (Array.isArray(v)) return v.map(sortKeysDeep);
  if (isJsonObject(v)) {
    const out = {};
    for (const k of Object.keys(v).sort()) out[k] = sortKeysDeep(v[k] ?? null);
    return out;
  }
  return v;
}
var objectsOf = (v) => Array.isArray(v) ? v.filter(isJsonObject) : [];
var objectOf = (v) => v !== void 0 && isJsonObject(v) ? v : {};
var stringsOf = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
function configTypes(config) {
  const out = {};
  for (const [key, spec] of Object.entries(objectOf(config))) out[key] = jsToLolo(inferConfigType(spec));
  return out;
}
function buildLoloBehaviorContracts(catalogs, orbs) {
  const behaviors = {};
  const entryFor = (name) => behaviors[name] ?? (behaviors[name] = { traits: {}, pages: [], orbitals: {}, appConfig: {} });
  for (const raw of catalogs) {
    const catalog = rehydrateKnobDefs(raw);
    for (const s of catalog.signatures) {
      if (!s.organism) continue;
      const entry = entryFor(s.organism);
      for (const t of s.traits) {
        if (t.name in entry.traits) continue;
        const config = {};
        for (const ck of t.overridableConfigKeys ?? []) {
          if (ck?.key) config[ck.key] = jsToLolo(ck.type);
        }
        entry.traits[t.name] = { requires: [...t.entityContract?.requires ?? []], config };
      }
      for (const p of s.pages) {
        if (p?.name && !entry.pages.includes(p.name)) entry.pages.push(p.name);
      }
    }
  }
  for (const entry of Object.values(behaviors)) entry.pages.sort();
  for (const { name, orb } of orbs) {
    if (behaviors[name]) continue;
    const traits = {};
    const pages = [];
    for (const orbital of objectsOf(objectOf(orb)["orbitals"])) {
      for (const t of objectsOf(orbital["traits"])) {
        const traitName = t["name"];
        if (typeof traitName !== "string") continue;
        traits[traitName] = { requires: stringsOf(objectOf(t["entityContract"])["requires"]), config: configTypes(t["config"]) };
      }
      for (const p of objectsOf(orbital["pages"])) {
        if (typeof p["name"] === "string" && p["name"]) pages.push(p["name"]);
      }
    }
    behaviors[name] = { traits, pages, orbitals: {}, appConfig: {} };
  }
  for (const { name, orb } of orbs) {
    const target = entryFor(name);
    const doc = objectOf(orb);
    Object.assign(target.appConfig, configTypes(doc["config"]));
    for (const orbital of objectsOf(doc["orbitals"])) {
      const orbitalName = orbital["name"];
      if (typeof orbitalName !== "string" || !orbitalName) continue;
      target.orbitals[orbitalName] = { config: configTypes(orbital["config"]) };
    }
  }
  return behaviors;
}
function serializeLoloBehaviorContracts(behaviors) {
  const doc = {};
  for (const [name, b] of Object.entries(behaviors)) {
    const traits = {};
    for (const [t, c] of Object.entries(b.traits)) traits[t] = { requires: c.requires, config: c.config };
    const orbitals = {};
    for (const [o, c] of Object.entries(b.orbitals)) orbitals[o] = { config: c.config };
    doc[name] = { traits, pages: b.pages, orbitals, appConfig: b.appConfig };
  }
  return JSON.stringify(sortKeysDeep({ behaviors: doc }));
}

// src/factory/questions/generate.ts
function generateQuestions(plan, catalog, ruleOverlay, _userPrompt) {
  const out = [];
  const ruleCapabilities = collectRuleCapabilities(ruleOverlay);
  for (const call of plan) {
    const signature = findSignature(catalog, call.organism, call.orbital);
    if (!signature) continue;
    out.push(...configKeyQuestions(call, signature));
    out.push(...entityFieldQuestions(call, signature));
    out.push(...capabilityQuestions(call, signature, ruleCapabilities));
  }
  return out;
}
var TIER_D_NAMED_PRIMITIVES = /* @__PURE__ */ new Set([
  "layoutMode",
  "layoutPattern",
  "viewPattern",
  "detailPattern",
  "detailSlot",
  "contentTrait"
]);
var TIER_D_TYPES = /* @__PURE__ */ new Set(["trait", "slot", "pattern", "event"]);
function configKeyQuestions(call, signature) {
  const out = [];
  for (const trait of signature.traits) {
    for (const param of trait.overridableConfigKeys) {
      if (param.tier === "internal") continue;
      if (TIER_D_TYPES.has(param.type)) continue;
      if (TIER_D_NAMED_PRIMITIVES.has(param.key)) continue;
      out.push(buildConfigKeyQuestion(call, trait, param));
    }
  }
  return out;
}
function buildConfigKeyQuestion(call, trait, param) {
  const question = param.label ?? humanizeKey(param.key);
  const callSiteOverride = callSiteOverrideValue(call, trait.name, param.key);
  const effectiveDefault = callSiteOverride !== void 0 ? callSiteOverride : param.default;
  const helpText = param.description;
  const syntheticReason = `Customizes "${param.key}" on the ${trait.name} trait. ` + (effectiveDefault !== void 0 ? `Default: ${stringifyDefault(effectiveDefault)}.` : `No default \u2014 leave blank to inherit the factory's behavior.`);
  const reason = helpText !== void 0 ? void 0 : syntheticReason;
  const isAdvanced = !isPrimaryTier(param.tier);
  const out = {
    id: `${factoryCallPlanOrbitalName(call)}.${trait.name}.${param.key}`,
    orbitalName: factoryCallPlanOrbitalName(call),
    question,
    inputType: deriveInputType(param),
    mutationTemplate: {
      kind: "set-trait-override-config",
      orbitalName: factoryCallPlanOrbitalName(call),
      traitName: trait.name,
      configKey: param.key
    }
  };
  if (reason !== void 0) {
    out.reason = reason;
  }
  if (helpText !== void 0) {
    out.helpText = helpText;
  }
  if (effectiveDefault !== void 0) {
    out.defaultValue = effectiveDefault;
  }
  if (param.enumValues && param.enumValues.length > 0) {
    out.suggestedAnswers = param.enumValues;
  }
  if (param.items) {
    out.itemSchema = param.items;
  }
  if (param.properties) {
    out.objectSchema = param.properties;
  }
  if (param.tier) {
    out.tier = param.tier;
  }
  if (isAdvanced) {
    out.advanced = true;
  }
  const weight = tierWeight(param.tier);
  out.weight = weight;
  out.priority = weight;
  return out;
}
function tierWeight(tier) {
  switch (tier) {
    case "domain":
    case "policy":
      return 3;
    case "infra":
      return 2;
    case "internal":
      return 0;
    case "presentation":
    default:
      return 1;
  }
}
function isPrimaryTier(tier) {
  return tier === "domain" || tier === "policy";
}
function callSiteOverrideValue(call, traitName, configKey) {
  const override = call.params.traitOverrides?.[traitName];
  if (!override?.config) return void 0;
  if (!Object.prototype.hasOwnProperty.call(override.config, configKey)) {
    return void 0;
  }
  return override.config[configKey];
}
function deriveInputType(param) {
  if (param.type.startsWith("[")) {
    if (param.items?.properties && Object.keys(param.items.properties).length > 0) {
      return "listOfObjects";
    }
    if (param.enumValues && param.enumValues.length > 0) {
      return "multiselect";
    }
    return "tagList";
  }
  if (param.type.startsWith("Map<")) {
    return "objectForm";
  }
  if (param.type === "object") {
    if (param.properties && Object.keys(param.properties).length > 0) {
      return "objectForm";
    }
    return "text";
  }
  switch (param.type) {
    case "number":
    case "integer":
      return "number";
    case "boolean":
      return "boolean";
    case "enum":
      return "enum";
    case "string":
      return param.enumValues && param.enumValues.length > 0 ? "enum" : "text";
    default:
      return "text";
  }
}
function humanizeKey(key) {
  const parts = key.replace(/[-_]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").split(" ").filter((s) => s.length > 0);
  return parts.map((p) => /^[A-Z]+$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
}
function stringifyDefault(v) {
  if (typeof v === "string") return JSON.stringify(v);
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
  return "object";
}
function entityFieldQuestions(call, signature) {
  if (signature.entities.length === 0) return [];
  const entity = signature.entities[0];
  const entityName2 = call.params.entityName ?? entity.name;
  const candidateFields = entity.fields.map(signatureFieldToEntityField);
  const fieldCandidates = {};
  const fieldDescriptions = {};
  const candidateNames = [];
  for (let i = 0; i < entity.fields.length; i++) {
    const sig = entity.fields[i];
    const ef = candidateFields[i];
    const name = sig.name;
    candidateNames.push(name);
    fieldCandidates[name] = ef;
    if (sig.description) {
      fieldDescriptions[name] = sig.description;
    }
  }
  const weight = tierWeight("domain");
  const q = {
    id: `${factoryCallPlanOrbitalName(call)}.__entityFields`,
    orbitalName: factoryCallPlanOrbitalName(call),
    question: `Fields for ${entityName2}`,
    reason: `Defines the data model for ${entityName2}. Pick the fields that matter for your use case.`,
    inputType: "multiselect",
    mutationTemplate: {
      kind: "set-orbital-entity-fields",
      orbitalName: factoryCallPlanOrbitalName(call)
    },
    suggestedAnswers: candidateNames,
    defaultValue: candidateNames,
    fieldCandidates,
    tier: "domain",
    weight,
    priority: weight
  };
  if (Object.keys(fieldDescriptions).length > 0) {
    q.fieldDescriptions = fieldDescriptions;
  }
  return [q];
}
function signatureFieldToEntityField(f) {
  const base = {
    name: f.name,
    required: f.required,
    description: f.description,
    synonyms: f.synonyms,
    intrinsic: f.intrinsic
  };
  switch (f.type) {
    case "string":
    case "number":
    case "boolean":
    case "date":
    case "timestamp":
    case "datetime":
    // Semantic string domains carry no dependent payload, so they pass through
    // as-is and the question widget narrows the input accordingly.
    case "email":
    case "url":
    case "phone":
    case "uuid":
    case "image":
      return { ...base, type: f.type };
    case "array":
      return { ...base, type: "array" };
    case "object":
      return { ...base, type: "object" };
    case "enum":
      if (Array.isArray(f.values) && f.values.length > 0) {
        return { ...base, type: "enum", values: f.values };
      }
      return { ...base, type: "string" };
    case "relation":
      if (f.relation !== void 0) {
        return { ...base, type: "relation", relation: f.relation };
      }
      return { ...base, type: "string" };
    case "node":
      return { ...base, type: "node" };
    case "event":
      return { ...base, type: "event" };
    case "money":
    case "file":
      return { ...base, type: f.type };
    default: {
      f.type;
      return { ...base, type: "string" };
    }
  }
}
function capabilityQuestions(call, signature, ruleCapabilities) {
  const entityName2 = call.params.entityName ?? signature.entities[0]?.name ?? factoryCallPlanOrbitalName(call);
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const trait of signature.traits) {
    const policyKnobKeys = new Set(
      trait.overridableConfigKeys.filter((p) => p.tier === "policy").map((p) => p.key)
    );
    for (const cap of trait.capabilities) {
      if (seen.has(cap)) continue;
      seen.add(cap);
      if (ruleCapabilities.has(cap)) continue;
      if (policyKnobKeys.has(cap)) continue;
      out.push({
        id: `${factoryCallPlanOrbitalName(call)}.capability.${cap}`,
        orbitalName: factoryCallPlanOrbitalName(call),
        question: capabilityPrompt(cap, entityName2),
        helpText: capabilityHelp(cap, trait),
        capability: cap,
        inputType: "boolean",
        mutationTemplate: {
          kind: "set-rule",
          capability: cap,
          appliesTo: [entityName2]
        },
        // Capabilities encode governance/policy decisions — high impact.
        weight: 3,
        priority: 3
      });
    }
  }
  return out;
}
function capabilityPrompt(capability, entity) {
  const label = humanizeKey(capability);
  return `Enable ${label} for ${entity}?`;
}
function capabilityHelp(capability, trait) {
  return `Turns on the "${capability}" behavior from the ${trait.name} trait. Say yes to wire a governance rule into the schema.`;
}
function findSignature(catalog, organism, orbital) {
  return catalog.find((s) => s.organism === organism && s.orbital === orbital);
}
function collectRuleCapabilities(overlay) {
  const out = /* @__PURE__ */ new Set();
  for (const r of overlay?.rules ?? []) {
    if (typeof r.capability === "string") out.add(r.capability);
  }
  return out;
}
function factoryCallPlanOrbitalName(call) {
  return call.orbitalName ?? call.orbital;
}

// src/factory/questions/reducer.ts
var PERSISTENCE_VALUES = [
  "persistent",
  "runtime"
];
function answersToMutations(answers, questions) {
  const out = [];
  for (const q of questions) {
    const answer = answers[q.id];
    if (answer === void 0 || answer === null) continue;
    out.push(...answerToMutations(q.mutationTemplate, answer));
  }
  return out;
}
function answerToMutations(template, answer, _question) {
  switch (template.kind) {
    case "set-orbital-entity-name": {
      if (typeof answer !== "string" || answer.length === 0) return [];
      return [
        {
          kind: "set-orbital-entity-name",
          orbitalName: template.orbitalName,
          name: answer
        }
      ];
    }
    case "set-orbital-entity-fields": {
      if (!isEntityFieldArray(answer)) return [];
      if (answer.length === 0) return [];
      return [
        {
          kind: "set-orbital-entity-fields",
          orbitalName: template.orbitalName,
          fields: answer
        }
      ];
    }
    case "set-orbital-persistence": {
      if (!isPersistence(answer)) return [];
      return [
        {
          kind: "set-orbital-persistence",
          orbitalName: template.orbitalName,
          persistence: answer
        }
      ];
    }
    case "set-orbital-collection": {
      if (typeof answer !== "string" || answer.length === 0) return [];
      return [
        {
          kind: "set-orbital-collection",
          orbitalName: template.orbitalName,
          collection: answer
        }
      ];
    }
    case "set-orbital-page-path": {
      if (typeof answer !== "string" || answer.length === 0) return [];
      return [
        {
          kind: "set-orbital-page-path",
          orbitalName: template.orbitalName,
          pageName: template.pageName,
          path: answer
        }
      ];
    }
    case "set-trait-override-config": {
      if (!isFactoryParamValue(answer)) return [];
      return [
        {
          kind: "set-trait-override-config",
          orbitalName: template.orbitalName,
          traitName: template.traitName,
          key: template.configKey,
          value: answer
        }
      ];
    }
    case "add-extra-trait": {
      if (!isTraitReference(answer)) return [];
      return [
        {
          kind: "add-extra-trait",
          orbitalName: template.orbitalName,
          trait: answer
        }
      ];
    }
    case "remove-extra-trait": {
      if (typeof answer !== "string" || answer.length === 0) return [];
      return [
        {
          kind: "remove-extra-trait",
          orbitalName: template.orbitalName,
          ref: answer
        }
      ];
    }
    case "set-rule": {
      if (typeof answer !== "string") return [];
      if (answer !== "yes") return [];
      const rule = {
        id: `${template.capability}::${template.appliesTo.join(",")}`,
        capability: template.capability,
        description: `User accepted ${template.capability} for ${template.appliesTo.join(", ")}.`,
        appliesTo: template.appliesTo
      };
      return [{ kind: "set-rule", rule }];
    }
    case "remove-rule": {
      return [{ kind: "remove-rule", ruleId: template.ruleId }];
    }
  }
}
function isEntityFieldArray(a) {
  if (!Array.isArray(a)) return false;
  for (const x of a) {
    if (typeof x !== "object" || x === null) return false;
    if (!("name" in x) || !("type" in x)) return false;
    if (typeof x.name !== "string" || typeof x.type !== "string") return false;
  }
  return true;
}
function isPersistence(a) {
  return typeof a === "string" && PERSISTENCE_VALUES.includes(a);
}
function isFactoryParamValue(a) {
  if (a === null) return false;
  const t = typeof a;
  if (t === "string" || t === "number" || t === "boolean") return true;
  if (Array.isArray(a)) return true;
  if (t === "object") return true;
  return false;
}
function isTraitReference(a) {
  if (typeof a !== "object" || a === null || Array.isArray(a)) return false;
  if (!("ref" in a)) return false;
  return typeof a.ref === "string";
}

// src/factory/translate.ts
function translateOverlaysToParams(binding, signature, presentation, ruleOverlay, traitOverlay, catalog) {
  const warnings = [];
  const params = {};
  applyEntityName(binding, signature, params);
  applyEntityFields(binding, signature, params);
  applyPersistence(binding, signature, params, warnings);
  applyCollection(binding, params);
  applyPagePaths(binding, signature, params, warnings);
  applyTheme(presentation, params);
  applyPresentation(presentation, signature, params, warnings);
  applyTraitOverlay(traitOverlay, signature, params, warnings);
  applyRuleOverlay(ruleOverlay, signature, binding, catalog, params, warnings);
  return {
    callSite: {
      organism: signature.organism,
      orbital: signature.orbital,
      factoryPath: signature.factoryPath,
      params
    },
    warnings
  };
}
function applyEntityName(binding, signature, params) {
  if (signature.entities.length === 0) return;
  if (binding.entityName === signature.entities[0].name) return;
  params.entityName = binding.entityName;
}
function applyEntityFields(binding, signature, params) {
  if (!binding.entityFields || binding.entityFields.length === 0) return;
  if (signature.entities.length === 0) {
    params.entityFields = [...binding.entityFields];
    return;
  }
  const canonical2 = new Set(signature.entities[0].fields.map((f) => f.name));
  const extras = [];
  for (const f of binding.entityFields) {
    if (f.name && canonical2.has(f.name)) continue;
    extras.push(f);
  }
  if (extras.length > 0) params.entityFields = extras;
}
function applyPersistence(binding, signature, params, warnings) {
  if (!binding.persistence) return;
  if (signature.entities.length === 0) {
    warnings.push({
      field: `entity.${binding.entityName}.persistence`,
      reason: "factory signature has no entity to apply persistence to"
    });
    return;
  }
  if (binding.persistence === signature.entities[0].persistence) return;
  params.persistence = binding.persistence;
}
function applyCollection(binding, params) {
  if (!binding.collection) return;
  params.collection = binding.collection;
}
function applyPagePaths(binding, signature, params, warnings) {
  if (!binding.pagePaths) return;
  const sigPages = new Map(signature.pages.map((p) => [p.name, p]));
  const overrides = {};
  for (const [pageName, url] of Object.entries(binding.pagePaths)) {
    const sig = sigPages.get(pageName);
    if (!sig) {
      warnings.push({
        field: `page.${pageName}.url`,
        reason: `factory signature has no page named "${pageName}"`
      });
      continue;
    }
    if (sig.defaultPath !== url) overrides[pageName] = url;
  }
  if (Object.keys(overrides).length > 0) params.pagePaths = overrides;
}
function applyTheme(presentation, params) {
  if (presentation?.themeRef) {
    params.theme = presentation.themeRef;
  }
}
function applyPresentation(overlay, signature, params, warnings) {
  if (!overlay?.navAdditions || overlay.navAdditions.length === 0) return;
  const target = signature.traits.find(
    (t) => t.overridableConfigKeys.some((p) => p.key === "navItems")
  );
  if (!target) {
    warnings.push({
      field: "presentation.navAdditions",
      reason: "factory signature has no trait advertising a `navItems` config key"
    });
    return;
  }
  const existing = params.traitOverrides?.[target.name] ?? {};
  const existingConfig = existing.config ?? {};
  const items = overlay.navAdditions.map(
    navItemToParamValue
  );
  params.traitOverrides = {
    ...params.traitOverrides,
    [target.name]: {
      ...existing,
      config: { ...existingConfig, navItems: items }
    }
  };
}
function applyTraitOverlay(overlay, signature, params, warnings) {
  if (!overlay) return;
  const traitsByName = new Map(signature.traits.map((t) => [t.name, t]));
  for (const [traitName, entry] of Object.entries(overlay)) {
    const trait = traitsByName.get(traitName);
    if (!trait) {
      warnings.push({
        field: `traitOverlay.${traitName}`,
        reason: `factory signature has no trait named "${traitName}"`
      });
      continue;
    }
    mergeTraitOverride(traitName, entry, trait, params, warnings);
  }
}
function mergeTraitOverride(traitName, entry, trait, params, warnings) {
  const advertised = new Set(trait.overridableConfigKeys.map((p) => p.key));
  const existing = params.traitOverrides?.[traitName] ?? {};
  const existingConfig = existing.config ?? {};
  const mergedConfig = { ...existingConfig };
  if (entry.config) {
    for (const [k, v] of Object.entries(entry.config)) {
      if (!advertised.has(k)) {
        const advertisedKeys = trait.overridableConfigKeys.map((p) => p.key).join(", ");
        warnings.push({
          field: `traitOverlay.${traitName}.config.${k}`,
          reason: `trait does not advertise config key "${k}" (overridableConfigKeys: [${advertisedKeys}])`
        });
        continue;
      }
      mergedConfig[k] = v;
    }
  }
  params.traitOverrides = {
    ...params.traitOverrides,
    [traitName]: {
      ...existing,
      ...Object.keys(mergedConfig).length > 0 ? { config: mergedConfig } : {},
      ...entry.linkedEntity !== void 0 ? { linkedEntity: entry.linkedEntity } : {},
      ...entry.events !== void 0 ? { events: entry.events } : {},
      ...entry.name !== void 0 ? { name: entry.name } : {},
      ...entry.emitsScope !== void 0 ? { emitsScope: entry.emitsScope } : {},
      ...entry.listens !== void 0 ? { listens: entry.listens } : {}
    }
  };
}
function applyRuleOverlay(overlay, signature, binding, catalog, params, warnings) {
  if (!overlay) return;
  const matchableNames = collectMatchableEntityNames(signature, binding);
  for (const rule of overlay.rules) {
    if (!ruleAppliesToAnyName(rule, matchableNames)) continue;
    if (!catalog) {
      warnings.push({
        field: `ruleOverlay.rules.${rule.id}`,
        reason: "rule overlay supplied without a catalog \u2014 capability lookup requires one"
      });
      continue;
    }
    const match = findTraitByCapability(catalog, rule.capability);
    if (!match) {
      warnings.push({
        field: `ruleOverlay.rules.${rule.id}`,
        reason: `no trait in the catalog advertises capability "${rule.capability}"`
      });
      continue;
    }
    appendRuleExtraTrait(rule, match, binding, params);
    if (rule.config) {
      threadRuleConfig(rule, match.trait, params, warnings);
    }
  }
  if (overlay.ownership) {
    for (const entry of overlay.ownership) {
      if (!matchableNames.includes(entry.entity)) continue;
      applyOwnership(entry, params, signature, catalog, warnings);
    }
  }
}
function collectMatchableEntityNames(signature, binding) {
  const out = /* @__PURE__ */ new Set();
  if (binding.entityName.length > 0) out.add(binding.entityName);
  for (const ent of signature.entities) {
    if (typeof ent.name === "string" && ent.name.length > 0) out.add(ent.name);
  }
  return [...out];
}
function ruleAppliesToAnyName(rule, matchableNames) {
  if (rule.appliesTo.length === 0) return true;
  for (const n of rule.appliesTo) {
    if (matchableNames.includes(n)) return true;
  }
  return false;
}
function findTraitByCapability(catalog, capability) {
  for (const sig of catalog) {
    for (const trait of sig.traits) {
      if (trait.capabilities.includes(capability)) {
        return { signature: sig, trait };
      }
    }
  }
  return void 0;
}
function appendRuleExtraTrait(_rule, match, binding, params) {
  const fromPath = `std/behaviors/${match.signature.organism}`;
  const alias = organismToAlias(match.signature.organism);
  const ref2 = {
    from: fromPath,
    ref: `${alias}.traits.${match.trait.name}`,
    linkedEntity: binding.entityName
  };
  const existing = params.extraTraits ?? [];
  params.extraTraits = [...existing, ref2];
}
function organismToAlias(organism) {
  const trimmed = organism.startsWith("std-") ? organism.slice(4) : organism;
  return trimmed.split("-").filter((s) => s.length > 0).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("");
}
function threadRuleConfig(rule, trait, params, warnings) {
  if (!rule.config) return;
  const advertised = new Set(trait.overridableConfigKeys.map((p) => p.key));
  const existing = params.traitOverrides?.[trait.name] ?? {};
  const existingConfig = existing.config ?? {};
  const merged = { ...existingConfig };
  for (const [k, v] of Object.entries(rule.config)) {
    if (!advertised.has(k)) {
      warnings.push({
        field: `ruleOverlay.rules.${rule.id}.config.${k}`,
        reason: `trait "${trait.name}" does not advertise config key "${k}"`
      });
      continue;
    }
    merged[k] = v;
  }
  if (Object.keys(merged).length > 0) {
    params.traitOverrides = {
      ...params.traitOverrides,
      [trait.name]: { ...existing, config: merged }
    };
  }
}
function applyOwnership(entry, params, signature, catalog, warnings) {
  const local = signature.traits.find(
    (t) => t.overridableConfigKeys.some((p) => p.key === "ownerField")
  );
  if (local) {
    writeOwnerField(local.name, entry.ownerField, params);
    return;
  }
  if (catalog) {
    for (const sig of catalog) {
      const trait = sig.traits.find(
        (t) => t.overridableConfigKeys.some((p) => p.key === "ownerField")
      );
      if (trait) {
        writeOwnerField(trait.name, entry.ownerField, params);
        return;
      }
    }
  }
  warnings.push({
    field: `ruleOverlay.ownership.${entry.entity}`,
    reason: "no trait in the bound signature or catalog advertises an `ownerField` config knob"
  });
}
function writeOwnerField(traitName, ownerField, params) {
  const existing = params.traitOverrides?.[traitName] ?? {};
  const existingConfig = existing.config ?? {};
  params.traitOverrides = {
    ...params.traitOverrides,
    [traitName]: {
      ...existing,
      config: { ...existingConfig, ownerField }
    }
  };
}
function navItemToParamValue(item) {
  const out = {
    label: item.label,
    path: item.path
  };
  if (item.icon) out.icon = item.icon;
  return out;
}

// src/factory/diff.ts
function diffFactoryCalls(prior, next) {
  const out = [];
  const priorByKey = new Map(prior.map((c) => [callKey(c), c]));
  const nextByKey = new Map(next.map((c) => [callKey(c), c]));
  for (const [key, p] of priorByKey) {
    const n = nextByKey.get(key);
    if (!n) {
      out.push({ kind: "delete", orbitalName: p.orbital, prior: p });
      continue;
    }
    if (paramsEqual(p.params, n.params)) {
      out.push({ kind: "keep", orbitalName: p.orbital, call: n });
    } else {
      out.push({ kind: "edit", orbitalName: p.orbital, prior: p, next: n });
    }
  }
  for (const [key, n] of nextByKey) {
    if (!priorByKey.has(key)) {
      out.push({ kind: "add", orbitalName: n.orbital, next: n });
    }
  }
  return out;
}
function callKey(c) {
  return `${c.organism}::${c.orbital}`;
}
function paramsEqual(a, b) {
  return canonicalParams(a) === canonicalParams(b);
}
function canonicalParams(p) {
  const keys = [
    "collection",
    "entityFields",
    "entityName",
    "extraTraits",
    "pagePaths",
    "persistence",
    "traitOverrides"
  ];
  const parts = [];
  for (const k of keys) {
    const v = p[k];
    if (v === void 0) continue;
    parts.push(`${k}:${canonicalNode(v)}`);
  }
  return `{${parts.join(",")}}`;
}
function canonicalNode(v) {
  if (v === void 0) return "u";
  if (typeof v === "string") return JSON.stringify(v);
  if (Array.isArray(v)) {
    return `[${v.map((x) => canonicalParamValue(x)).join(",")}]`;
  }
  const keys = Object.keys(v).sort();
  const parts = [];
  for (const k of keys) {
    const child = v[k];
    parts.push(`${JSON.stringify(k)}:${canonicalParamValue(child)}`);
  }
  return `{${parts.join(",")}}`;
}
function canonicalParamValue(v) {
  if (v === null || v === void 0) return "u";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) {
    return `[${v.map(canonicalParamValue).join(",")}]`;
  }
  const keys = Object.keys(v).sort();
  const parts = [];
  for (const k of keys) {
    const child = v[k];
    if (child === void 0) continue;
    parts.push(`${JSON.stringify(k)}:${canonicalParamValue(child)}`);
  }
  return `{${parts.join(",")}}`;
}

// src/factory/mutate.ts
function applyFactoryCallPlanMutation(state, m) {
  switch (m.kind) {
    case "set-orbital-entity-fields":
      return patchOrbital(state, m.orbitalName, (o) => ({
        ...o,
        entityFields: m.fields
      }));
    case "set-orbital-entity-name":
      return patchOrbital(state, m.orbitalName, (o) => ({
        ...o,
        entityName: m.name
      }));
    case "set-orbital-persistence":
      return patchOrbital(state, m.orbitalName, (o) => ({
        ...o,
        persistence: m.persistence
      }));
    case "set-orbital-collection":
      return patchOrbital(state, m.orbitalName, (o) => ({
        ...o,
        collection: m.collection
      }));
    case "set-orbital-page-path":
      return patchOrbital(state, m.orbitalName, (o) => ({
        ...o,
        pagePaths: { ...o.pagePaths ?? {}, [m.pageName]: m.path }
      }));
    case "set-trait-override-config":
      return patchOrbital(state, m.orbitalName, (o) => {
        const overlay = o.traitOverlay ?? {};
        const existing = overlay[m.traitName] ?? {};
        const existingConfig = existing.config ?? {};
        return {
          ...o,
          traitOverlay: {
            ...overlay,
            [m.traitName]: {
              ...existing,
              config: { ...existingConfig, [m.key]: m.value }
            }
          }
        };
      });
    case "add-extra-trait":
      return patchOrbital(state, m.orbitalName, (o) => {
        const existing = o.extraTraits ?? [];
        if (existing.some((t) => t.ref === m.trait.ref)) return o;
        return { ...o, extraTraits: [...existing, m.trait] };
      });
    case "remove-extra-trait":
      return patchOrbital(state, m.orbitalName, (o) => ({
        ...o,
        extraTraits: (o.extraTraits ?? []).filter((t) => t.ref !== m.ref)
      }));
    case "set-rule": {
      const rules = state.ruleOverlay?.rules ?? [];
      const idx = rules.findIndex((r) => r.id === m.rule.id);
      const next = idx >= 0 ? rules.map((r, i) => i === idx ? m.rule : r) : [...rules, m.rule];
      return {
        ...state,
        ruleOverlay: { ...state.ruleOverlay ?? { rules: [] }, rules: next }
      };
    }
    case "remove-rule": {
      const rules = state.ruleOverlay?.rules ?? [];
      const next = rules.filter((r) => r.id !== m.ruleId);
      return {
        ...state,
        ruleOverlay: { ...state.ruleOverlay ?? { rules: [] }, rules: next }
      };
    }
  }
}
function patchOrbital(state, orbitalName, patch) {
  return {
    ...state,
    orbitals: state.orbitals.map(
      (o) => o.orbitalName === orbitalName ? patch(o) : o
    )
  };
}

// src/factory/params-schema.ts
function signatureToParamsSchema(signature, options) {
  const traitRefValues = signature.traits.map((t) => `@trait.${t.name}`).sort();
  const traitProps = {};
  for (const trait of signature.traits) {
    if (trait.importedFrom !== void 0) continue;
    const selectableRefs = traitRefValues.filter((v) => v !== `@trait.${trait.name}`);
    const traitSchema = traitToOverrideSchema(trait, selectableRefs);
    if (traitSchema !== null) {
      traitProps[trait.name] = traitSchema;
    }
  }
  const allowPersistenceOverride = persistenceModeAllowsOverrides(
    signature.entities[0]?.persistence,
    signature.entities[0]?.dataBearing ?? false
  );
  const properties = {
    entityName: {
      type: "string",
      description: 'PascalCase singular entity rename (e.g. "Listing", "Member"). Renames the DOMAIN concept and rewrites every `@Entity.*` reference. Use this when the user wants to rename the THING, not where it\'s stored.'
    },
    pagePath: {
      type: "string",
      description: "URL path override for the orbital's first page."
    },
    fields: {
      type: "array",
      description: "Extra entity fields appended to the canonical entity. Caller wins on name collision.",
      items: ENTITY_FIELD_SCHEMA
    },
    traitOverrides: Object.keys(traitProps).length > 0 ? {
      type: "object",
      additionalProperties: false,
      description: "Per-trait knob overrides keyed on the canonical trait name. Each value can include `config` (knob values), `linkedEntity` (rebind), `events` (per-key rename map), `listens` (cross-orbital subscriptions), `emitsScope` (`internal` keeps emits local; `external` lets sibling orbitals listen), and `name` (rename the inlined trait). Only the traits + knobs shown here are valid \u2014 the schema rejects unknown keys.",
      properties: traitProps
    } : { type: "object", additionalProperties: false, properties: {} },
    extraTraits: {
      type: "array",
      description: "Sibling traits to compose beyond the factory-authored stack.",
      items: EXTRA_TRAIT_SCHEMA
    },
    pages: {
      type: "array",
      description: "Per-page overrides keyed by canonical page name.",
      items: PAGE_OVERRIDE_SCHEMA
    },
    theme: {
      type: "string",
      // Theme names are injected by the caller rather than harvested from
      // `@almadar-ui/themes/*.css` — core is UPSTREAM of `@almadar/ui`, which
      // owns the theme CSS, and cannot read the filesystem to discover it.
      ...options?.themeNames !== void 0 && options.themeNames.length > 0 ? { enum: [...options.themeNames] } : {},
      description: "Theme key \u2014 the full `data-theme` value applied to the layout root (e.g. linear-clean-light, terminal-dark, game-adventure-dark). Plan-level: pick ONE for the whole app \u2014 it is threaded to every orbital. Omit to leave the factory default."
    }
  };
  if (signature.config && signature.config.length > 0) {
    const configProps = {};
    for (const knob of signature.config) {
      configProps[knob.key] = knobToSchema(knob, void 0, traitRefValues);
    }
    properties.config = {
      type: "object",
      additionalProperties: false,
      description: "This orbital's own declared knobs (Orbital.config). Only the keys shown are valid.",
      properties: configProps
    };
  }
  if (allowPersistenceOverride) {
    properties.collection = {
      type: "string",
      description: 'Override the persistence collection / table name. Defaults to `plural(entityName).toLowerCase()`. Use this when the user names the database/table/storage location ("save them under X", "the table is called X", "in our database it\'s X") \u2014 NOT when they rename the domain concept (that\'s `entityName`).'
    };
    properties.persistence = {
      type: "string",
      enum: ["persistent", "runtime"],
      description: "Override the entity persistence mode. `persistent` = stored in the configured backend; `runtime` = in-memory session-scoped only."
    };
  }
  const schema = {
    type: "object",
    additionalProperties: false,
    description: `Typed params for ${signature.organism} / ${signature.orbital}. Empty {} = factory defaults.`,
    properties
  };
  assertNoUnsatisfiableEnum(schema, `${signature.organism}/${signature.orbital}`);
  return schema;
}
function assertNoUnsatisfiableEnum(node, where, path = "") {
  if (Array.isArray(node.enum) && node.enum.length === 0) {
    throw new Error(
      `[signatures] ${where}: empty enum at "${path || "<root>"}" \u2014 the schema permits no value, so every fill for this knob is rejected. Omit the knob instead of offering an unsatisfiable one.`
    );
  }
  for (const [key, child] of Object.entries(node.properties ?? {})) {
    assertNoUnsatisfiableEnum(child, where, path === "" ? key : `${path}.${key}`);
  }
  if (node.items !== void 0) assertNoUnsatisfiableEnum(node.items, where, `${path}[]`);
}
function traitToOverrideSchema(trait, traitRefValues) {
  if (trait.overridableConfigKeys.length === 0 && trait.entityRebindable !== true) return null;
  const knobProps = {};
  for (const knob of trait.overridableConfigKeys) {
    if (knob.type === "trait" && traitRefValues !== void 0 && traitRefValues.length === 0) {
      continue;
    }
    knobProps[knob.key] = knobToSchema(knob, trait, traitRefValues);
  }
  const properties = {
    config: {
      type: "object",
      additionalProperties: false,
      properties: knobProps
    }
  };
  if (trait.entityRebindable === true) {
    properties.linkedEntity = {
      type: "string",
      description: "Rebind this trait to a different entity in the organism (rewrites every @entity.* / ref / fetch / persist binding inside it)."
    };
  }
  const triggersSchema = trait.transitionEvents !== void 0 && trait.transitionEvents.length > 0 ? {
    type: "string",
    enum: [...trait.transitionEvents],
    description: "Local state-machine event fired when the subscription matches. Must be one of the events this trait's own transitions already react to."
  } : {
    type: "string",
    description: "Local state-machine event fired when the subscription matches."
  };
  const listensProperty = trait.transitionEvents !== void 0 && trait.transitionEvents.length === 0 ? {} : {
    listens: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["event", "triggers"],
        properties: {
          event: { type: "string", description: "Event key to subscribe to (bare name, no orbital prefix)." },
          triggers: triggersSchema,
          scope: { type: "string", enum: ["internal", "external"], description: "`internal` = same-orbital events. `external` = events from sibling orbitals (use this for cross-orbital reactivity)." }
        }
      },
      description: "Replace this trait's `listens` entirely. Empty array clears upstream listeners. For cross-orbital reactivity, add an entry with `scope: \"external\"` matching the source orbital's emit event."
    }
  };
  return {
    type: "object",
    additionalProperties: false,
    description: `Override knobs for ${trait.name}.`,
    properties: {
      ...properties,
      name: {
        type: "string",
        description: "Rename the inlined trait at the call site. Does NOT change topology \u2014 just the trait's local name."
      },
      events: {
        type: "object",
        additionalProperties: { type: "string" },
        description: 'Per-key event rename map (e.g. `{ "OPEN": "ADD_ITEM" }`). Rewrites the events array, transition triggers, emit/listen event keys, and `["emit", X]` SExpression literals inside the trait.'
      },
      ...listensProperty,
      emitsScope: {
        type: "string",
        enum: ["internal", "external"],
        description: "`internal` = emits stay local to this orbital. `external` = sibling orbitals can listen. Set to `external` when another orbital needs to react to this trait's events (counter updates, list refreshes, badges, etc.)."
      }
    }
  };
}
function knobToSchema(knob, trait, traitRefValues) {
  const out = {};
  const baseType = mapKnobType(knob.type);
  if (baseType !== null) out.type = baseType;
  if (knob.enumValues && knob.enumValues.length > 0) {
    out.enum = [...knob.enumValues];
    if (!out.type) out.type = "string";
  }
  if (knob.type === "slot" && out.enum === void 0) {
    out.enum = [...UI_SLOTS];
  }
  if (knob.type === "pattern" && out.enum === void 0) {
    const patternTypes = getAllPatternTypes();
    const selfConsistent = typeof knob.default !== "string" || knob.default === "" || patternTypes.includes(knob.default);
    if (patternTypes.length > 0 && selfConsistent) {
      out.enum = [...patternTypes].sort();
    }
  }
  if (knob.type === "trait" && out.enum === void 0 && traitRefValues !== void 0 && traitRefValues.length > 0) {
    const selfConsistent = typeof knob.default !== "string" || knob.default === "" || traitRefValues.includes(knob.default);
    if (selfConsistent) {
      out.enum = typeof knob.default === "string" && knob.default === "" ? ["", ...traitRefValues] : [...traitRefValues];
    }
  }
  const isDefinerKnob = trait?.definerKnobs?.includes(knob.key) ?? false;
  if (knob.type === "event" && isDefinerKnob) {
    out["x-role"] = "definer";
  }
  if (knob.type === "event" && !isDefinerKnob && out.enum === void 0 && trait !== void 0) {
    const vocabulary = /* @__PURE__ */ new Set([
      ...trait.emittedEvents,
      ...trait.listenedEvents,
      ...trait.transitionEvents ?? []
    ]);
    vocabulary.delete("");
    const selfConsistent = typeof knob.default !== "string" || knob.default === "" || vocabulary.has(knob.default);
    if (vocabulary.size > 0 && selfConsistent) {
      out.enum = ["", ...[...vocabulary].sort()];
    }
  }
  if (knob.items !== void 0) {
    out.items = entityFieldToSchema(knob.items);
    if (!out.type) out.type = "array";
  }
  if (knob.properties !== void 0 && Object.keys(knob.properties).length > 0) {
    const props = {};
    for (const [k, ef] of Object.entries(knob.properties)) {
      props[k] = entityFieldToSchema(ef);
    }
    out.properties = props;
    if (!out.type) out.type = "object";
  }
  if (knob.default !== void 0) {
    out.default = toJsonValue(knob.default);
  }
  if (knob.description) out.description = knob.description;
  if (knob.label) out["x-label"] = knob.label;
  if (knob.synonyms) out["x-synonyms"] = knob.synonyms;
  if (knob.tier) out["x-tier"] = knob.tier;
  return out;
}
function entityFieldToSchema(field) {
  if (field.type === "union") {
    const variants = field.values.map((name) => field.properties?.[name]).filter((v) => v !== void 0);
    if (variants.length === 0) return {};
    return { oneOf: variants.map((v) => ({ ...entityFieldToSchema(v), additionalProperties: false })) };
  }
  const out = {};
  const baseType = mapEntityFieldType(field.type);
  if (baseType !== null) out.type = baseType;
  if ((field.type === "enum" || field.type === "string" || field.type === "number" || field.type === "boolean" || field.type === "date" || field.type === "timestamp" || field.type === "datetime") && Array.isArray(field.values) && field.values.length > 0) {
    out.enum = field.values.filter(isEnumLeaf);
    if (!out.type) out.type = "string";
  }
  if (field.type === "array" && field.items !== void 0) {
    out.items = entityFieldToSchema(field.items);
    if (!out.type) out.type = "array";
  }
  if (field.properties !== void 0 && Object.keys(field.properties).length > 0) {
    const props = {};
    const required = [];
    for (const [k, ef] of Object.entries(field.properties)) {
      props[k] = entityFieldToSchema(ef);
      if (ef.required === true) required.push(k);
    }
    out.properties = props;
    if (required.length > 0) out.required = required;
    if (!out.type) out.type = "object";
  }
  if (field.default !== void 0) {
    out.default = toJsonValue(field.default);
  }
  return out;
}
function isEnumLeaf(v) {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}
function mapKnobType(knobType) {
  if (knobType.startsWith("[") && knobType.endsWith("]")) return "array";
  switch (knobType) {
    case "string":
      return "string";
    case "number":
    case "integer":
    case "float":
      return knobType === "integer" ? "integer" : "number";
    case "boolean":
    case "bool":
      return "boolean";
    case "object":
    // An orbital value is `{ behavior, orbital, …import body }` on the wire; a behavior value is `{ behavior }`.
    case "orbital":
    case "behavior":
      return "object";
    case "array":
      return "array";
    case "enum":
      return "string";
    // String-valued reference tags: a trait ref is "@trait.<Name>", a
    // slot/pattern/icon/asset is a registry name, an event is an event
    // key, a date is an ISO string. These were mis-emitted as `object`
    // (11k knobs catalog-wide carried an object type with a string
    // default), which was harmless while tool schemas were advisory but
    // breaks hard validation.
    case "trait":
    case "slot":
    case "pattern":
    case "secret":
    case "icon":
    case "asset":
    case "event":
    case "EventAddress":
    case "date":
      return "string";
    case "render-ui":
      return "object";
    // Polymorphic values: a `node` accepts a string leaf OR a render-ui
    // tree; `json`/`unknown` carry arbitrary JSON. No `type` — the
    // schema stays structurally honest instead of over-constraining.
    case "node":
    case "json":
    case "unknown":
      return null;
    default:
      return null;
  }
}
function mapEntityFieldType(fieldType) {
  switch (fieldType) {
    case "string":
    case "date":
    case "timestamp":
    case "datetime":
    // JSON Schema has no email/url/phone/uuid/image primitive — the declared
    // domain is carried on the signature's own `type` field (see
    // `asSchemaFieldType`), not here. This maps to the WIRE shape only.
    case "email":
    case "url":
    case "phone":
    case "uuid":
    case "image":
      return "string";
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "array":
      return "array";
    case "object":
    case "relation":
    case "orbital":
    case "behavior":
      return "object";
    case "enum":
      return "string";
    case "trait":
    case "slot":
    case "pattern":
    case "secret":
      return "string";
    // Phase B (SCAN-EVENT-KNOB-2): event-name reference — a string at the
    // wire level, same as `mapKnobType`'s `case 'event'` (that asymmetry
    // — this function fell to `default: null`, leaving struct-field
    // `event` members with NO json-schema type — was the bug).
    case "event":
    case "EventAddress":
      return "string";
    // Polymorphic renderable content: a node accepts a string leaf OR a
    // render-ui tree — no JSON-Schema `type` constraint, mirroring
    // `mapKnobType`'s `case 'node'`. (The default would return null too;
    // explicit for parity and self-documentation.)
    case "node":
      return null;
    default:
      return null;
  }
}
function toJsonValue(value) {
  if (value === null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    return value.map(toJsonValue);
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = toJsonValue(v);
  }
  return out;
}
var ENTITY_FIELD_BASE_PROPS = {
  name: { type: "string", description: "Field name (camelCase)." },
  required: { type: "boolean" },
  primaryKey: { type: "boolean" },
  intrinsic: { type: "boolean" },
  min: { type: "number" },
  max: { type: "number" },
  description: { type: "string" },
  synonyms: { type: "string" },
  mock: { type: "string" },
  mergedFrom: { type: "string" },
  projectedFrom: {
    type: "object",
    additionalProperties: false,
    required: ["type", "field"],
    properties: { type: { type: "string" }, field: { type: "string" } }
  },
  properties: { type: "object", additionalProperties: { $ref: "urn:almadar:entity-field#/$defs/field" } },
  values: { type: "array", items: { type: "string" } },
  default: {
    type: ["string", "number", "boolean", "null", "array", "object"]
  }
};
var SCALAR_FIELD_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
  "timestamp",
  "datetime",
  "email",
  "url",
  "phone",
  "uuid",
  "image",
  "money",
  "file",
  "trait",
  "slot",
  "pattern",
  "node",
  "event",
  "EventAddress",
  "scalar",
  "SExpr"
];
var SCALAR_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type"],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: SCALAR_FIELD_TYPES, description: "Field type tag." }
  }
};
var ENUM_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type", "values"],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: ["enum"], description: "Enum field." },
    values: {
      type: "array",
      description: 'Closed string vocabulary. e.g. ["active","inactive","pending"].',
      minItems: 1,
      items: { type: "string" }
    }
  }
};
var RELATION_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type", "relation"],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: ["relation"], description: "Relation (FK) field." },
    relation: {
      type: "object",
      additionalProperties: false,
      required: ["entity"],
      properties: {
        entity: { type: "string", description: "Sibling entity name (PascalCase singular)." },
        entityId: { type: "string" },
        cardinality: { type: "string", enum: ["one", "many"] },
        field: { type: "string" },
        onDelete: { type: "string", enum: ["cascade", "nullify", "restrict"] }
      },
      description: 'Relation target. `cardinality: "one"` for FK (customerId \u2192 one Customer); "many" for collection (tags \u2192 many Tag).'
    }
  }
};
var ARRAY_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type"],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: ["array"], description: "Array field." },
    items: { $ref: "urn:almadar:entity-field#/$defs/field" }
  }
};
var OBJECT_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type"],
  anyOf: [{ required: ["properties"] }, { required: ["items"] }],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: ["object"], description: "Struct members or map value shape." },
    items: { $ref: "urn:almadar:entity-field#/$defs/field" }
  }
};
var UNION_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type", "values"],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: ["union"] },
    values: { ...ENTITY_FIELD_BASE_PROPS.values, minItems: 1 }
  }
};
var TUPLE_FIELD_BRANCH = {
  type: "object",
  additionalProperties: false,
  required: ["name", "type", "properties"],
  properties: {
    ...ENTITY_FIELD_BASE_PROPS,
    type: { type: "string", enum: ["tuple"] },
    properties: { ...ENTITY_FIELD_BASE_PROPS.properties, minProperties: 1 }
  }
};
var ENTITY_FIELD_BRANCHES = [
  SCALAR_FIELD_BRANCH,
  ENUM_FIELD_BRANCH,
  RELATION_FIELD_BRANCH,
  ARRAY_FIELD_BRANCH,
  OBJECT_FIELD_BRANCH,
  UNION_FIELD_BRANCH,
  TUPLE_FIELD_BRANCH
];
var ENTITY_FIELD_SCHEMA = {
  $id: "urn:almadar:entity-field",
  oneOf: ENTITY_FIELD_BRANCHES,
  $defs: {
    field: {
      oneOf: ENTITY_FIELD_BRANCHES.map((branch) => ({ ...branch, required: branch.required?.filter((key) => key !== "name") }))
    }
  }
};
var EXTRA_TRAIT_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["from", "as", "ref"],
  properties: {
    from: { type: "string", description: "Registry path (e.g. std/behaviors/std-approval-request)." },
    as: { type: "string", description: "Local alias used in `ref`." },
    ref: { type: "string", description: "Trait reference (Alias.traits.X)." },
    name: { type: "string", description: "Optional rename of the inlined trait." },
    linkedEntity: { type: "string", description: "Optional entity rebinding." },
    emitsScope: { type: "string", enum: ["internal", "external"] }
  }
};
var PAGE_OVERRIDE_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["name"],
  properties: {
    name: { type: "string", description: "Canonical page name from the orbital." },
    path: { type: "string", description: "URL path override." },
    linkedEntity: { type: "string", description: "Rebind the primary entity." }
  }
};

// src/builders/layout-strategy.ts
function hasSequentialChain(wiring) {
  if (wiring.length < 2) {
    return false;
  }
  const adjacency = /* @__PURE__ */ new Map();
  const allTargets = /* @__PURE__ */ new Set();
  for (const entry of wiring) {
    let targets = adjacency.get(entry.from);
    if (!targets) {
      targets = /* @__PURE__ */ new Set();
      adjacency.set(entry.from, targets);
    }
    targets.add(entry.to);
    allTargets.add(entry.to);
  }
  const roots = [];
  for (const from of adjacency.keys()) {
    if (!allTargets.has(from)) {
      roots.push(from);
    }
  }
  const starts = roots.length > 0 ? roots : [...adjacency.keys()];
  for (const start of starts) {
    let current = start;
    let length = 1;
    const visited = /* @__PURE__ */ new Set([current]);
    while (true) {
      const nexts = adjacency.get(current);
      if (!nexts || nexts.size === 0) break;
      let advanced = false;
      for (const next of nexts) {
        if (!visited.has(next)) {
          visited.add(next);
          current = next;
          length++;
          advanced = true;
          break;
        }
      }
      if (!advanced) break;
    }
    if (length >= 3) {
      return true;
    }
  }
  return false;
}
function detectLayoutStrategy(orbitals, eventWiring) {
  if (eventWiring && eventWiring.length > 0 && hasSequentialChain(eventWiring)) {
    return "wizard-flow";
  }
  const count = orbitals.length;
  if (count <= 1) {
    return "single";
  }
  if (count <= 4) {
    return "tabs";
  }
  return "sidebar";
}

// src/builders/event-wiring.ts
function findInlineTrait(orbitals, name) {
  for (const orbital of orbitals) {
    for (const traitRef of orbital.traits) {
      if (isInlineTrait(traitRef) && traitRef.name === name) {
        return traitRef;
      }
    }
  }
  return null;
}
function hasEmit(emits, event) {
  return emits.some((e) => e.event === event);
}
function hasListen(listens, event, triggers) {
  return listens.some((l) => l.event === event && l.triggers === triggers);
}
function applyEventWiring(orbitals, wiring) {
  const cloned = JSON.parse(
    JSON.stringify(orbitals)
  );
  for (const entry of wiring) {
    const sourceTrait = findInlineTrait(cloned, entry.from);
    if (sourceTrait) {
      if (!sourceTrait.emits) {
        sourceTrait.emits = [];
      }
      if (!hasEmit(sourceTrait.emits, entry.event)) {
        sourceTrait.emits.push({
          event: entry.event,
          scope: "external"
        });
      }
    }
    const targetTrait = findInlineTrait(cloned, entry.to);
    if (targetTrait) {
      if (!targetTrait.listens) {
        targetTrait.listens = [];
      }
      if (!hasListen(targetTrait.listens, entry.event, entry.triggers)) {
        targetTrait.listens.push({
          event: entry.event,
          triggers: entry.triggers,
          scope: "external"
        });
      }
    }
  }
  return cloned;
}

// src/builders/compose-behaviors.ts
function isSchema(input) {
  return "orbitals" in input && Array.isArray(input.orbitals);
}
function asDefinitions(inputs) {
  return inputs.flatMap(
    (input) => isSchema(input) ? input.orbitals : [input]
  );
}
function mergeLedgers(inputs) {
  const merged = /* @__PURE__ */ new Map();
  let sawLedger = false;
  for (const input of inputs) {
    if (!isSchema(input) || input.ledger === void 0) continue;
    sawLedger = true;
    for (const [id, entry] of Object.entries(input.ledger.entries)) {
      if (!merged.has(id)) merged.set(id, entry);
    }
  }
  if (!sawLedger) return void 0;
  const entries = {};
  for (const [id, entry] of [...merged.entries()].sort(
    ([a], [b]) => a < b ? -1 : a > b ? 1 : 0
  )) {
    entries[id] = entry;
  }
  return { schemaVersion: 1, entries };
}
function mergeSchemaVersions(inputs) {
  let max;
  for (const input of inputs) {
    if (!isSchema(input) || input.schemaVersion === void 0) continue;
    max = max === void 0 ? input.schemaVersion : Math.max(max, input.schemaVersion);
  }
  return max;
}
function toKebabCase(name) {
  return name.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
function generatePages(orbitals, strategy) {
  switch (strategy) {
    case "single": {
      const orbital = orbitals[0];
      const name = orbital?.name ?? "Main";
      return [
        {
          name: `${name}Page`,
          path: "/",
          isInitial: true,
          primaryEntity: getEntityName(orbital)
        }
      ];
    }
    case "dashboard": {
      return [
        {
          name: "DashboardPage",
          path: "/",
          viewType: "dashboard",
          isInitial: true
        }
      ];
    }
    case "sidebar":
    case "tabs":
    case "wizard-flow": {
      return orbitals.map((orbital, index) => ({
        name: `${orbital.name}Page`,
        path: index === 0 ? "/" : `/${toKebabCase(orbital.name)}`,
        isInitial: index === 0,
        primaryEntity: getEntityName(orbital)
      }));
    }
  }
}
function getEntityName(orbital) {
  if (!orbital) return void 0;
  const entity = orbital.entity;
  if (typeof entity === "string") {
    return entity.replace(".entity", "");
  }
  return entity.name;
}
function composeBehaviors(input) {
  const {
    appName,
    orbitals: rawInputs,
    layoutStrategy: strategyInput,
    eventWiring
  } = input;
  const rawOrbitals = asDefinitions(rawInputs);
  const wiredOrbitals = eventWiring && eventWiring.length > 0 ? applyEventWiring(rawOrbitals, eventWiring) : rawOrbitals;
  const strategy = !strategyInput || strategyInput === "auto" ? detectLayoutStrategy(wiredOrbitals, eventWiring) : strategyInput;
  const pages = generatePages(wiredOrbitals, strategy);
  const orbitalsWithPages = wiredOrbitals.map((orbital, index) => {
    if (orbital.pages && orbital.pages.length > 0) {
      return orbital;
    }
    if (orbital.reference !== void 0) {
      return orbital;
    }
    const page = strategy === "dashboard" || strategy === "single" ? pages[0] : pages[index];
    return {
      ...orbital,
      pages: page ? [page] : []
    };
  });
  const ledger = mergeLedgers(rawInputs);
  const schemaVersion = mergeSchemaVersions(rawInputs);
  const schema = {
    name: appName,
    version: "1.0.0",
    orbitals: orbitalsWithPages,
    ...schemaVersion !== void 0 ? { schemaVersion } : {},
    ...ledger !== void 0 ? { ledger } : {}
  };
  return {
    schema,
    layout: {
      strategy,
      pageCount: pages.length
    },
    wiring: {
      connections: eventWiring?.length ?? 0
    }
  };
}

// src/expectation-shape.ts
function mergeExpectationShape(existing, incoming) {
  if (existing === void 0 && incoming === void 0) return void 0;
  const mergeField = (prior, field) => {
    const fail = () => {
      throw new Error(`expectation shape mismatch \u2014 field "${field.name ?? prior.name}" has incompatible type constraints`);
    };
    const priorType = prior.type === "enum" ? "string" : prior.type;
    const fieldType = field.type === "enum" ? "string" : field.type;
    if (priorType !== fieldType) return fail();
    if (prior.type === "relation" && field.type === "relation") {
      if (prior.relation.entityId !== void 0 && field.relation.entityId !== void 0 && prior.relation.entityId !== field.relation.entityId) return fail();
      const relation = (value) => ({ entity: value.entity, cardinality: value.cardinality, field: value.field, onDelete: value.onDelete });
      if (canonicalJson(relation(prior.relation)) !== canonicalJson(relation(field.relation))) return fail();
    }
    if (prior.type === "tuple" && field.type === "tuple" && canonicalJson(Object.keys(prior.properties).sort()) !== canonicalJson(Object.keys(field.properties).sort())) return fail();
    const min = prior.min === void 0 ? field.min : field.min === void 0 ? prior.min : Math.max(prior.min, field.min);
    const max = prior.max === void 0 ? field.max : field.max === void 0 ? prior.max : Math.min(prior.max, field.max);
    if (min !== void 0 && max !== void 0 && min > max) return fail();
    let merged2 = { ...prior, ...prior.required === true || field.required === true ? { required: true } : {}, ...min !== void 0 ? { min } : {}, ...max !== void 0 ? { max } : {} };
    if (prior.type === "relation" && field.type === "relation") merged2 = { ...merged2, type: "relation", relation: { ...prior.relation, ...prior.relation.entityId === void 0 && field.relation.entityId !== void 0 ? { entityId: field.relation.entityId } : {} } };
    if ("values" in prior || "values" in field) {
      const values = [.../* @__PURE__ */ new Set([..."values" in prior ? prior.values ?? [] : [], ..."values" in field ? field.values ?? [] : []])];
      if (values.length > 0) merged2 = EntityFieldSchema.parse({ ...merged2, values });
    }
    if (prior.properties !== void 0 || field.properties !== void 0) {
      const properties = { ...prior.properties };
      for (const [key, value] of Object.entries(field.properties ?? {})) properties[key] = properties[key] === void 0 ? value : mergeField(properties[key], value);
      merged2 = { ...merged2, properties };
    }
    if ("items" in prior || "items" in field) {
      const priorItems = "items" in prior ? prior.items : void 0;
      const incomingItems = "items" in field ? field.items : void 0;
      const items = priorItems === void 0 ? incomingItems : incomingItems === void 0 ? priorItems : mergeField(priorItems, incomingItems);
      if (items !== void 0) merged2 = EntityFieldSchema.parse({ ...merged2, items });
    }
    return merged2;
  };
  const merged = [...existing ?? []];
  for (const field of incoming ?? []) {
    const index = merged.findIndex((candidate) => candidate.name === field.name);
    const prior = merged[index];
    if (prior === void 0) merged.push(field);
    else merged[index] = mergeField(prior, field);
  }
  return merged;
}

// src/data-bearing.ts
function touchesRecord(effect, name) {
  if (!Array.isArray(effect)) return false;
  if (effect[0] === "fetch" && effect[1] === name) return true;
  if (effect[0] === "persist" && effect[2] === name) return true;
  for (const item of effect) {
    if (Array.isArray(item) && touchesRecord(item, name)) return true;
  }
  return false;
}
function orbitalTouchesOwnRecord(orbital) {
  const entity = orbital.entity;
  const name = typeof entity === "object" && entity !== null && "name" in entity ? entity.name : void 0;
  if (typeof name !== "string") return false;
  return (orbital.traits ?? []).some(
    (trait) => isInlineTrait(trait) && (trait.stateMachine?.transitions ?? []).some((t) => (t.effects ?? []).some((e) => touchesRecord(e, name)))
  );
}

// src/builders/layout-trait.ts
function makeSlot(traitName) {
  return `@trait.${traitName}`;
}
function makeRenderUI(slot, root) {
  if (root === null) return ["render-ui", slot, null];
  if (typeof root === "string") return ["render-ui", slot, root];
  return ["render-ui", slot, root];
}
function makeLayoutTrait(opts) {
  const slot = opts.slot ?? "main";
  const effects = [];
  const emits = [];
  const eventDefs = [
    { key: "INIT", name: "Initialize" }
  ];
  if (opts.fetchEntity) {
    if (!opts.loadedEvent || !opts.loadFailedEvent) {
      throw new Error(
        `makeLayoutTrait: fetchEntity '${opts.fetchEntity}' set but loadedEvent / loadFailedEvent are required`
      );
    }
    const payloadSchema = opts.loadedPayloadSchema ?? [{ name: "data", type: `[${opts.fetchEntity}]` }];
    emits.push(
      { event: opts.loadedEvent, scope: "internal", payloadSchema },
      {
        event: opts.loadFailedEvent,
        scope: "internal",
        payloadSchema: [
          { name: "error", type: "string" },
          { name: "code", type: "string" }
        ]
      }
    );
    eventDefs.push(
      { key: opts.loadedEvent, name: `${opts.fetchEntity} loaded`, payloadSchema },
      {
        key: opts.loadFailedEvent,
        name: `${opts.fetchEntity} load failed`,
        payloadSchema: [
          { name: "error", type: "string" },
          { name: "code", type: "string" }
        ]
      }
    );
    const fetchFx = [
      "fetch",
      opts.fetchEntity,
      { emit: { success: opts.loadedEvent, failure: opts.loadFailedEvent } }
    ];
    effects.push(fetchFx);
  }
  effects.push(makeRenderUI(slot, opts.renderUI));
  if (opts.extraEffects && opts.extraEffects.length > 0) {
    effects.push(...opts.extraEffects);
  }
  const trait = {
    name: opts.name,
    ...opts.description ? { description: opts.description } : {},
    category: "interaction",
    linkedEntity: opts.linkedEntity,
    scope: "instance",
    ...emits.length > 0 ? { emits } : {},
    stateMachine: {
      states: [{ name: "composing", isInitial: true }],
      events: eventDefs,
      transitions: [
        {
          from: "composing",
          to: "composing",
          event: "INIT",
          effects
        }
      ]
    }
  };
  return trait;
}

// src/builders.ts
function makeTraitRef(opts) {
  const ref2 = { ref: opts.ref };
  if (opts.from !== void 0) ref2.from = opts.from;
  if (opts.name !== void 0) ref2.name = opts.name;
  if (opts.linkedEntity !== void 0) ref2.linkedEntity = opts.linkedEntity;
  if (opts.linkedEntityId !== void 0) ref2.linkedEntityId = opts.linkedEntityId;
  if (opts.events !== void 0) ref2.events = opts.events;
  if (opts.typeArgs !== void 0) ref2.typeArgs = opts.typeArgs;
  if (opts.fields !== void 0) ref2.fields = opts.fields;
  if (opts.effects !== void 0) ref2.effects = opts.effects;
  if (opts.listens !== void 0) ref2.listens = opts.listens;
  if (opts.emitsScope !== void 0) ref2.emitsScope = opts.emitsScope;
  if (opts.config !== void 0) ref2.config = opts.config;
  return ref2;
}
function makeOrbitalWithUses(opts) {
  const orbital = {
    name: opts.name,
    uses: opts.uses,
    ...opts.expects !== void 0 ? { expects: opts.expects } : {},
    entity: opts.entity,
    ...opts.auxiliaryEntities !== void 0 ? { auxiliaryEntities: opts.auxiliaryEntities } : {},
    traits: opts.traits,
    pages: opts.pages ?? [],
    ...opts.config !== void 0 ? { config: opts.config } : {}
  };
  return orbital;
}
var ORBITAL_VALUE_IMPORT_ALIAS = "Source";
function orbitalImportFromValue(value) {
  const { behavior, orbital, name, app, config, traits, uses, siblings, ...body } = value;
  const ref2 = `${ORBITAL_VALUE_IMPORT_ALIAS}.orbitals.${orbital}`;
  const clash = (uses ?? []).find((u) => u.as === ORBITAL_VALUE_IMPORT_ALIAS);
  if (clash !== void 0) {
    throw new Error(`orbital value ${orbital}: an import aliased '${ORBITAL_VALUE_IMPORT_ALIAS}' (from ${clash.from}) collides with the value's own source alias`);
  }
  const traitOverrides = traits === void 0 ? void 0 : Object.fromEntries(
    Object.entries(traits).map(([trait, overrides]) => {
      const { config: traitConfig, ...rest } = overrides;
      return [trait, { ...rest, ...traitConfig !== void 0 ? { config: mergeCallSiteConfigOverrides({}, traitConfig) } : {} }];
    })
  );
  return {
    name: name ?? orbital,
    uses: [{ from: behavior, as: ORBITAL_VALUE_IMPORT_ALIAS, ...app !== void 0 ? { config: mergeCallSiteConfigOverrides({}, app) } : {} }, ...uses ?? []],
    entity: `${ref2}.entity`,
    traits: (siblings ?? []).map((sibling) => ({
      name: sibling.name,
      ref: sibling.ref,
      ...sibling.linkedEntity !== void 0 ? { linkedEntity: sibling.linkedEntity } : {},
      ...sibling.config !== void 0 ? { config: mergeCallSiteConfigOverrides({}, sibling.config) } : {}
    })),
    pages: [],
    reference: {
      ref: ref2,
      ...body,
      ...config !== void 0 ? { config: mergeCallSiteConfigOverrides({}, config) } : {},
      ...traitOverrides !== void 0 ? { traits: traitOverrides } : {}
    }
  };
}
function parseProgram(data) {
  if (data === null || typeof data !== "object" || Array.isArray(data) || !("orbitals" in data) || !Array.isArray(data.orbitals)) {
    return parseOrbitalSchema(data);
  }
  return parseOrbitalSchema({
    ...data,
    orbitals: data.orbitals.map((entry) => isOrbitalValue(entry) ? orbitalImportFromValue(OrbitalValueSchema.parse(entry)) : entry)
  });
}

// src/factory/composition.ts
function eventNamer(contract, config) {
  const effective = {};
  for (const [knob, field] of Object.entries(contract.declaredConfig ?? {})) {
    const override = config?.[knob];
    if (typeof override === "string") effective[knob] = override;
    else if (field.default !== void 0) effective[knob] = field.default;
  }
  return (event) => {
    if (configRefEventKnob(event) === void 0) return event;
    const r = resolveConfigRefEventName(event, contract.declaredConfig, effective);
    return r.ok ? r.value : void 0;
  };
}
function followEventName(contract, side, name, fromConfig, toConfig) {
  const before = eventNamer(contract, fromConfig);
  const after = eventNamer(contract, toConfig);
  const sources = contract[side].filter((e) => before(e) === name);
  if (sources.length === 0) return { ok: true, value: name };
  const values = [...new Set(sources.flatMap((e) => after(e) ?? []))];
  if (values.length > 1) return { ok: false, message: `${name} comes from ${sources.join(", ")}, which this block names differently (${values.join(", ")}) \u2014 wire one of those names` };
  return { ok: true, value: values[0] };
}
function resolveBlockContract(contract, config) {
  const unresolved = [];
  const named = eventNamer(contract, config);
  const resolve = (event) => {
    const v = named(event);
    if (v === void 0 && !unresolved.includes(event)) unresolved.push(event);
    return v;
  };
  const names = (events) => events.flatMap((e) => {
    const v = resolve(e);
    return v === void 0 ? [] : [v];
  });
  const listens = contract.listens.flatMap((l) => {
    const event = resolve(l.event);
    const triggers = resolve(l.triggers);
    return event === void 0 || triggers === void 0 ? [] : [{ ...l, event, triggers }];
  });
  return { contract: { ...contract, emits: names(contract.emits), triggers: names(contract.triggers), listens }, unresolved };
}
function bindsRecord(contract, record) {
  return contract.entityRebindable || contract.linkedEntity === void 0 || contract.linkedEntity === record;
}
function placedBlocks(node, out) {
  if (node.kind === "block") out.push(node.block);
  else for (const child of node.children) placedBlocks(child, out);
}
function checkComposition(spec, ctx) {
  const issues = [];
  if (spec.entity.kind === "declare" && spec.entity.entity.persistence === "persistent" && !spec.entity.entity.collection) {
    issues.push({ code: "missing-collection", path: "entity.collection", message: `persistent entity ${spec.entity.entity.name} declares no collection` });
  }
  const contracts = /* @__PURE__ */ new Map();
  const names = /* @__PURE__ */ new Set();
  const aliases = /* @__PURE__ */ new Map();
  if (spec.entity.kind === "import") aliases.set(spec.entity.as, spec.entity.from);
  for (const b of spec.blocks) {
    const at = `blocks.${b.name}`;
    if (names.has(b.name)) issues.push({ code: "duplicate-name", path: at, message: `two blocks are named "${b.name}"` });
    names.add(b.name);
    const prior = aliases.get(b.as);
    if (prior !== void 0 && prior !== b.from) {
      issues.push({ code: "alias-conflict", path: `${at}.as`, message: `alias "${b.as}" already names ${prior}` });
    }
    aliases.set(b.as, b.from);
    const contract = ctx.block(b.from, b.trait);
    if (contract === null) {
      issues.push({ code: "unknown-block", path: at, message: `${b.from} declares no trait ${b.trait}` });
      continue;
    }
    contracts.set(b.name, resolveBlockContract(contract, b.config).contract);
    for (const key of Object.keys(b.config ?? {})) {
      if (!contract.configKeys.includes(key)) {
        issues.push({ code: "unknown-config-key", path: `${at}.config.${key}`, message: `${b.trait} declares no knob "${key}" (declares: ${contract.configKeys.join(", ")})` });
      }
    }
    const recordName = spec.entity.kind === "import" ? spec.entity.entity : spec.entity.entity.name;
    if (!bindsRecord(contract, recordName)) {
      issues.push({
        code: "entity-mismatch",
        path: at,
        message: `${b.trait} is bound to the record "${contract.linkedEntity}" and cannot be rebound \u2014 name this page's record "${contract.linkedEntity}" or use another part`
      });
    }
    if (b.linkedEntity !== void 0 && !contract.entityRebindable) {
      issues.push({ code: "not-rebindable", path: `${at}.linkedEntity`, message: `${b.trait} has a fixed entity binding` });
    }
  }
  for (const b of spec.blocks) {
    const declared = ctx.block(b.from, b.trait)?.declaredConfig ?? {};
    for (const [key, value] of Object.entries(b.config ?? {})) {
      if (declared[key]?.type !== "trait" || typeof value !== "string") continue;
      const target = value.startsWith("@trait.") ? value.slice("@trait.".length) : void 0;
      if (target === void 0 || !names.has(target)) {
        issues.push({
          code: "unknown-trait-ref",
          path: `blocks.${b.name}.config.${key}`,
          message: `${key} must be "@trait.<block>" naming a block of this composition (${[...names].join(", ")}), got "${value}"`
        });
      }
    }
  }
  const keptNames = /* @__PURE__ */ new Set();
  for (const k of spec.kept ?? []) {
    if (names.has(k.name) || keptNames.has(k.name)) issues.push({ code: "duplicate-name", path: `kept.${k.name}`, message: `two parts are named "${k.name}"` });
    keptNames.add(k.name);
  }
  const placed = [];
  placedBlocks(spec.layout, placed);
  for (const p of placed) {
    if (!names.has(p) && !keptNames.has(p)) issues.push({ code: "layout-unknown-block", path: `layout.${p}`, message: `the layout places "${p}", which is not a block` });
  }
  const slotted = /* @__PURE__ */ new Set();
  const collectSlots = (v) => {
    if (typeof v === "string") {
      if (v.startsWith("@trait.")) slotted.add(v.slice("@trait.".length));
    } else if (Array.isArray(v)) {
      for (const x of v) collectSlots(x);
    } else if (typeof v === "object" && v !== null) {
      for (const x of Object.values(v)) collectSlots(x);
    }
  };
  for (const b of spec.blocks) for (const v of Object.values(b.config ?? {})) collectSlots(v);
  for (const b of spec.blocks) {
    if (!placed.includes(b.name) && !slotted.has(b.name)) issues.push({ code: "unplaced-block", path: `blocks.${b.name}`, message: `"${b.name}" is not placed in the layout` });
  }
  for (const [i, w] of (spec.wiring ?? []).entries()) {
    const at = `wiring.${i}`;
    const src = contracts.get(w.fromBlock);
    const dst = contracts.get(w.toBlock);
    if (!names.has(w.fromBlock) || !names.has(w.toBlock)) {
      issues.push({ code: "unknown-wire-block", path: at, message: `wire ${w.fromBlock} \u2192 ${w.toBlock} names a missing block` });
      continue;
    }
    if (src !== void 0 && !src.emits.includes(w.event)) {
      issues.push({ code: "unknown-event", path: `${at}.event`, message: `${w.fromBlock} does not emit ${w.event} (emits: ${src.emits.join(", ")})` });
    }
    if (dst !== void 0 && !dst.triggers.includes(w.trigger)) {
      issues.push({ code: "unknown-trigger", path: `${at}.trigger`, message: `${w.toBlock} handles no ${w.trigger} (handles: ${dst.triggers.join(", ")})` });
    }
  }
  return issues;
}
function layoutTree(node) {
  if (node.kind === "block") return makeSlot(node.block);
  return {
    type: "stack",
    ...node.direction !== void 0 ? { direction: node.direction } : {},
    ...node.gap !== void 0 ? { gap: node.gap } : {},
    ...node.align !== void 0 ? { align: node.align } : {},
    ...node.justify !== void 0 ? { justify: node.justify } : {},
    ...node.className !== void 0 ? { className: node.className } : {},
    children: node.children.map(layoutTree)
  };
}
function entityName(e) {
  return e.kind === "import" ? e.entity : e.entity.name;
}
function assembleComposition(spec, ctx) {
  const issues = checkComposition(spec, ctx);
  if (issues.length > 0) return { ok: false, issues };
  const entity = entityName(spec.entity);
  const uses = [];
  const seen = /* @__PURE__ */ new Set();
  const addUse = (from, as) => {
    if (seen.has(as)) return;
    seen.add(as);
    uses.push({ from, as });
  };
  if (spec.entity.kind === "import") addUse(spec.entity.from, spec.entity.as);
  for (const b of spec.blocks) addUse(b.from, b.as);
  const root = layoutTree(spec.layout);
  const viewName = `${spec.orbital}View`;
  const view = makeLayoutTrait({
    name: viewName,
    linkedEntity: entity,
    renderUI: typeof root === "string" ? { type: "stack", children: [root] } : root
  });
  const blockTraits = spec.blocks.map((b) => {
    const declared = ctx.block(b.from, b.trait);
    const contract = declared !== null ? resolveBlockContract(declared, b.config).contract : null;
    const wires = (spec.wiring ?? []).filter((w) => w.toBlock === b.name);
    const listens = wires.length > 0 && contract !== null ? [
      ...contract.listens,
      ...wires.map((w) => ({ event: w.event, triggers: w.trigger, source: { kind: "trait", trait: w.fromBlock } }))
    ] : void 0;
    const rebind = b.linkedEntity ?? (contract?.entityRebindable === true ? entity : void 0);
    return makeTraitRef({
      ref: `${b.as}.traits.${b.trait}`,
      name: b.name,
      ...rebind !== void 0 ? { linkedEntity: rebind } : {},
      ...b.config !== void 0 ? { config: mergeCallSiteConfigOverrides({}, b.config) } : {},
      ...listens !== void 0 ? { listens } : {}
    });
  });
  if (spec.entity.kind === "import") {
    const e = spec.entity;
    const orbital = {
      ...makeOrbitalWithUses({ name: spec.orbital, uses, entity: `${e.as}.orbitals.${e.orbital}.entity`, traits: [view, ...blockTraits, ...spec.kept ?? []] }),
      reference: {
        ref: `${e.as}.orbitals.${e.orbital}`,
        entity: e.entity,
        ...e.only !== void 0 ? { only: [...e.only] } : {},
        pages: { [e.page]: spec.page.path },
        mounts: { [e.page]: [viewName] }
      }
    };
    return { ok: true, orbital };
  }
  return {
    ok: true,
    orbital: makeOrbitalWithUses({
      name: spec.orbital,
      uses,
      entity: spec.entity.entity,
      traits: [view, ...blockTraits, ...spec.kept ?? []],
      pages: [{ name: spec.page.name, path: spec.page.path, traits: [{ ref: viewName }] }]
    })
  };
}
function blockContractOf(trait, orbitalEntity) {
  const triggers = [];
  for (const t of trait.stateMachine?.transitions ?? []) if (!triggers.includes(t.event)) triggers.push(t.event);
  const sets = /* @__PURE__ */ new Set();
  const visitEffect = (effect) => {
    if (!Array.isArray(effect)) return;
    if (effect[0] === "set" && typeof effect[1] === "string" && effect[1].startsWith("@entity.")) {
      const field = effect[1].slice("@entity.".length).split(".")[0];
      if (field !== void 0 && field.length > 0) sets.add(field);
    }
    for (const child of controlBody(effect)) visitEffect(child);
  };
  for (const t of trait.stateMachine?.transitions ?? []) for (const e of t.effects ?? []) visitEffect(e);
  const linkedEntity = trait.linkedEntity ?? orbitalEntity;
  return {
    configKeys: Object.keys(trait.config ?? {}),
    ...trait.config !== void 0 ? { declaredConfig: trait.config } : {},
    emits: (trait.emits ?? []).map((e) => e.event),
    triggers,
    listens: trait.listens ?? [],
    entityRebindable: trait.entityRebindable === true,
    setsFields: [...sets],
    ...linkedEntity !== void 0 ? { linkedEntity } : {}
  };
}
function toParamValue(value) {
  if (value === void 0 || value === null) return void 0;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    const out = [];
    for (const v of value) {
      const p = toParamValue(v);
      if (p !== void 0) out.push(p);
    }
    return out;
  }
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const p = toParamValue(v);
      if (p !== void 0) out[k] = p;
    }
    return out;
  }
  return void 0;
}
function decomposeOrbital(orbital) {
  const fromOf = new Map((orbital.uses ?? []).map((u) => [u.as, u.from]));
  const blocks = [];
  for (const t of orbital.traits) {
    if (typeof t === "string" || isInlineTrait(t)) continue;
    const m = /^([A-Za-z_]\w*)\.traits\.([A-Za-z_]\w*)$/.exec(t.ref);
    if (m === null) continue;
    const from = fromOf.get(m[1]);
    if (from === void 0) continue;
    const config = {};
    for (const [key, entry] of Object.entries(t.config ?? {})) {
      const value = toParamValue(isCallSiteConfigDeclaration(entry) ? entry.default : entry);
      if (value !== void 0) config[key] = value;
    }
    blocks.push({
      name: t.name ?? m[2],
      from,
      as: m[1],
      trait: m[2],
      ...t.linkedEntity !== void 0 ? { linkedEntity: t.linkedEntity } : {},
      ...Object.keys(config).length > 0 ? { config } : {}
    });
  }
  return blocks;
}
function isTraitRefObject(t) {
  return typeof t !== "string" && !isInlineTrait(t) && "ref" in t;
}
function refListens(t) {
  return t.listens ?? [];
}
function sameListen(a, b) {
  const src = (l) => l.source?.kind === "trait" ? `trait:${l.source.trait}` : JSON.stringify(l.source ?? null);
  return a.event === b.event && a.triggers === b.triggers && src(a) === src(b);
}
function compositionSpecOf(orbital, ctx, chromeRefs) {
  const kept = [];
  for (const t of orbital.traits) {
    if (typeof t === "string") return { ok: false, reason: `trait "${t}" is a bare name, not a block reference` };
    if (isInlineTrait(t)) kept.push(t);
  }
  const refs = orbital.traits.filter(isTraitRefObject);
  const chromeNames = new Set(refs.filter((t) => chromeRefs.has(t.ref)).map((t) => t.name ?? t.ref));
  const blocks = decomposeOrbital(orbital).filter((b) => !chromeRefs.has(`${b.as}.traits.${b.trait}`));
  if (blocks.length !== refs.length - chromeNames.size) {
    return { ok: false, reason: "a trait reference names no `uses` alias" };
  }
  const names = new Set(blocks.map((b) => b.name));
  const wiring = [];
  for (const block of blocks) {
    const ref2 = refs.find((t) => (t.name ?? "") === block.name || t.name === void 0 && t.ref.endsWith(`.traits.${block.name}`));
    const contract = ctx.block(block.from, block.trait);
    const own = contract !== null ? resolveBlockContract(contract, block.config).contract.listens : [];
    const listens = ref2 !== void 0 ? refListens(ref2) : [];
    for (const l of listens) {
      if (l.source?.kind !== "trait" || !names.has(l.source.trait) || own.some((o) => sameListen(o, l))) continue;
      wiring.push({ fromBlock: l.source.trait, event: l.event, toBlock: block.name, trigger: l.triggers });
    }
  }
  let entity;
  let page;
  let order;
  if (typeof orbital.entity === "object" && !("extends" in orbital.entity)) {
    entity = { kind: "declare", entity: orbital.entity };
    const first = (orbital.pages ?? []).find((p) => typeof p !== "string" && !("ref" in p) && typeof p.path === "string");
    if (first === void 0 || typeof first === "string" || "ref" in first) return { ok: false, reason: "the orbital has no inline page" };
    page = { name: first.name, path: first.path };
    order = (first.traits ?? []).flatMap((t) => typeof t === "string" ? [t] : "ref" in t && typeof t.ref === "string" ? [t.ref] : []);
  } else {
    const reference = orbital.reference;
    const m = reference !== void 0 ? /^([A-Za-z_]\w*)\.orbitals\.([A-Za-z_]\w*)$/.exec(reference.ref) : null;
    const from = m !== null ? (orbital.uses ?? []).find((u) => u.as === m[1])?.from : void 0;
    const mounted = Object.entries(reference?.mounts ?? {})[0];
    if (reference === void 0 || m === null || from === void 0 || reference.entity === void 0 || mounted === void 0) {
      return { ok: false, reason: "the orbital neither declares its entity nor imports one with a mounted page" };
    }
    const [upstream, traits] = mounted;
    entity = { kind: "import", from, as: m[1], orbital: m[2], entity: reference.entity, page: upstream, ...reference.only !== void 0 ? { only: [...reference.only] } : {} };
    page = { name: `${orbital.name}Page`, path: reference.pages?.[upstream] ?? upstream };
    order = [...traits];
  }
  const keptNames = new Set(kept.map((k) => k.name));
  const placed = order.filter((n) => names.has(n) || keptNames.has(n));
  for (const b of blocks) if (!placed.includes(b.name)) placed.push(b.name);
  return {
    ok: true,
    spec: {
      orbital: orbital.name,
      entity,
      blocks,
      layout: { kind: "stack", direction: "vertical", children: placed.map((block) => ({ kind: "block", block })) },
      wiring,
      page,
      ...kept.length > 0 ? { kept } : {}
    }
  };
}

// src/state-machine/graph.ts
function buildStateGraph(transitions) {
  const graph = /* @__PURE__ */ new Map();
  for (const t of transitions) {
    if (t.from === "*") continue;
    if (!graph.has(t.from)) graph.set(t.from, []);
    graph.get(t.from).push({ event: t.event, to: t.to });
  }
  return graph;
}

// src/state-machine/bfs.ts
function collectReachableStates(transitions, initialState, maxDepth = 5) {
  const graph = buildStateGraph(transitions);
  const visited = /* @__PURE__ */ new Set([initialState]);
  const queue = [{ state: initialState, depth: 0 }];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current.depth >= maxDepth) continue;
    const edges = graph.get(current.state) ?? [];
    for (const edge of edges) {
      if (!visited.has(edge.to)) {
        visited.add(edge.to);
        queue.push({ state: edge.to, depth: current.depth + 1 });
      }
    }
  }
  return visited;
}
async function walkStatePairs(transitions, initialState, maxDepth, visitor) {
  const graph = buildStateGraph(transitions);
  const visitedPairs = /* @__PURE__ */ new Set();
  const queue = [{ state: initialState, depth: 0 }];
  let walkedEdges = 0;
  while (queue.length > 0) {
    const current = queue.shift();
    if (current.depth >= maxDepth) continue;
    const edges = graph.get(current.state) ?? [];
    for (const edge of edges) {
      const pairKey = `${current.state}:${edge.event}`;
      if (visitedPairs.has(pairKey)) continue;
      visitedPairs.add(pairKey);
      const shouldEnqueue = await visitor(current.state, edge, current.depth);
      walkedEdges++;
      if (shouldEnqueue) {
        const stateVisited = [...visitedPairs].some((k) => k.startsWith(`${edge.to}:`));
        if (!stateVisited) {
          queue.push({ state: edge.to, depth: current.depth + 1 });
        }
      }
    }
  }
  return { visitedPairs, walkedEdges };
}

// src/state-machine/guard-payloads.ts
function extractPayloadFieldRef(ref2) {
  if (typeof ref2 !== "string") return null;
  const match = ref2.match(/^@payload\.([A-Za-z0-9_]+)/);
  return match ? match[1] : null;
}
function extractPayloadFieldPath(ref2) {
  if (typeof ref2 !== "string") return null;
  const match = ref2.match(/^@payload\.([A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*)/);
  return match ? match[1].split(".") : null;
}
function strDefaultPayloadPath(ref2) {
  if (!Array.isArray(ref2) || ref2[0] !== "str/default" || typeof ref2[2] !== "string") return null;
  return extractPayloadFieldPath(ref2[1]);
}
function isComparedLiteral(x) {
  return x === null || typeof x === "number" || typeof x === "boolean" || typeof x === "string" && !x.startsWith("@");
}
function payloadComparison(a, b) {
  const plain = extractPayloadFieldPath(a);
  if (plain && b !== void 0) return { path: plain, val: b };
  for (const [side, other] of [[a, b], [b, a]]) {
    const path = extractPayloadFieldPath(side) ?? strDefaultPayloadPath(side);
    if (path && isComparedLiteral(other)) return { path, val: other };
  }
  return null;
}
function nestedPayload(path, leaf) {
  let value = leaf;
  for (let i = path.length - 1; i >= 1; i--) {
    value = { [path[i]]: value };
  }
  return { [path[0]]: value };
}
function isPayloadObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date);
}
function extractArrayLenPath(ref2) {
  if (!Array.isArray(ref2) || ref2.length !== 2 || ref2[0] !== "array/len") return null;
  return extractPayloadFieldPath(ref2[1]);
}
function arrayOfLength(n) {
  return Array.from({ length: Math.max(0, n) }, (_, i) => ({ id: `mock-test-id-${i}` }));
}
var SYNTHESIZED_GUARD_OPERATORS = /* @__PURE__ */ new Set([
  "not-nil",
  "not_nil",
  "nil",
  "eq",
  "==",
  "=",
  "not-eq",
  "!=",
  "neq",
  "gt",
  ">",
  "gte",
  ">=",
  "lt",
  "<",
  "lte",
  "<=",
  "and",
  "or",
  "not",
  "object/has",
  "object/get",
  "array/includes",
  "str/startsWith",
  "validate/url",
  "agent/is-pinned",
  "agent/memory-strength"
]);
var ACKNOWLEDGED_UNSYNTHESIZED_GUARD_OPERATORS = /* @__PURE__ */ new Set(["if", "let", "array/some", "grid/in-bounds"]);
var CONSTANT_GUARD_FORMS = /* @__PURE__ */ new Set(["quote"]);
function isRecognizedGuardOperator(op) {
  return SYNTHESIZED_GUARD_OPERATORS.has(op) || ACKNOWLEDGED_UNSYNTHESIZED_GUARD_OPERATORS.has(op) || CONSTANT_GUARD_FORMS.has(op) || op.startsWith("agent/");
}
var unhandledGuardOperators = [];
function recordUnhandledOperator(op) {
  if (unhandledGuardOperators.includes(op)) return;
  unhandledGuardOperators.push(op);
  console.warn(
    `[@almadar/core buildGuardPayloads] unrecognized guard operator "${op}" \u2014 synthesizing {pass:{}, fail:{}}; this guard arm cannot be walked pass/fail by verify. Add synthesis (or a tracked-gap entry) in guard-payloads.ts.`
  );
}
function splitPayloadArg(a, b) {
  const pathA = extractPayloadFieldPath(a);
  if (pathA) return { path: pathA, collection: b };
  const pathB = extractPayloadFieldPath(b);
  if (pathB) return { path: pathB, collection: a };
  return null;
}
function isPlainRecord(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date);
}
function absentKey(keys) {
  let candidate = `${keys.reduce((longest, k) => k.length > longest.length ? k : longest, "")}_`;
  while (keys.includes(candidate)) candidate += "_";
  return candidate;
}
function mergePayloads(a, b) {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const prev = out[k];
    out[k] = prev !== void 0 && isPayloadObject(prev) && v !== void 0 && isPayloadObject(v) ? mergePayloads(prev, v) : v;
  }
  return out;
}
function extractObjectGetOperand(x) {
  if (!Array.isArray(x) || x[0] !== "object/get") return null;
  const split = splitPayloadArg(x[1], x[2]);
  if (split && isPlainRecord(split.collection)) {
    return { path: split.path, map: split.collection };
  }
  return null;
}
function resolveObjectGetComparison(a, b) {
  const getA = extractObjectGetOperand(a);
  if (getA) return { path: getA.path, map: getA.map, literal: b };
  const getB = extractObjectGetOperand(b);
  if (getB) return { path: getB.path, map: getB.map, literal: a };
  return null;
}
function buildObjectGetComparisonPayload(a, b, negate) {
  const resolved = resolveObjectGetComparison(a, b);
  if (!resolved) return null;
  const { path, map, literal } = resolved;
  const keys = Object.keys(map);
  const passKey = keys.find((k) => negate ? map[k] !== literal : map[k] === literal);
  const failKey = keys.find((k) => negate ? map[k] === literal : map[k] !== literal);
  return {
    pass: passKey !== void 0 ? nestedPayload(path, passKey) : {},
    fail: failKey !== void 0 ? nestedPayload(path, failKey) : nestedPayload(path, absentKey(keys))
  };
}
function guardPassCandidateSet(guard) {
  if (!Array.isArray(guard) || guard.length === 0) return null;
  const op = String(guard[0]);
  if (op === "object/has") {
    const split = splitPayloadArg(guard[1], guard[2]);
    if (split && isPlainRecord(split.collection)) {
      return { path: split.path, values: Object.keys(split.collection) };
    }
    return null;
  }
  if (op === "eq" || op === "==" || op === "=" || op === "not-eq" || op === "!=" || op === "neq") {
    const negate = op === "not-eq" || op === "!=" || op === "neq";
    const resolved = resolveObjectGetComparison(guard[1], guard[2]);
    if (resolved) {
      const values = Object.keys(resolved.map).filter(
        (k) => negate ? resolved.map[k] !== resolved.literal : resolved.map[k] === resolved.literal
      );
      return { path: resolved.path, values };
    }
    if (!negate) {
      const path = extractPayloadFieldPath(guard[1]);
      if (path && typeof guard[2] === "string") return { path, values: [guard[2]] };
    }
    return null;
  }
  return null;
}
function withoutField(payload, path) {
  const [head, ...tail] = path;
  if (tail.length === 0) {
    const rest = {};
    for (const k of Object.keys(payload)) {
      if (k !== head) rest[k] = payload[k];
    }
    return rest;
  }
  const nested = payload[head];
  if (!isPayloadObject(nested)) return payload;
  return { ...payload, [head]: withoutField(nested, tail) };
}
function applyFieldIntersections(subs, pass) {
  const byField = /* @__PURE__ */ new Map();
  for (const sub of subs) {
    const candidate = guardPassCandidateSet(sub);
    if (!candidate) continue;
    const key = JSON.stringify(candidate.path);
    const entry = byField.get(key);
    if (entry) entry.sets.push(candidate.values);
    else byField.set(key, { path: candidate.path, sets: [candidate.values] });
  }
  let out = pass;
  for (const { path, sets } of byField.values()) {
    if (sets.length < 2) continue;
    const intersection = sets.reduce((acc, set2) => acc.filter((v) => set2.includes(v)));
    out = intersection.length > 0 ? mergePayloads(out, nestedPayload(path, intersection[0])) : withoutField(out, path);
  }
  return out;
}
function constTruth(guard) {
  if (typeof guard === "boolean") return guard;
  if (typeof guard === "string") {
    if (guard.startsWith("@")) return null;
    return guard.length > 0;
  }
  if (!Array.isArray(guard) || guard.length === 0) return null;
  const op = String(guard[0]);
  const isLiteral = (x) => x === null || typeof x === "number" || typeof x === "boolean" || typeof x === "string" && !x.startsWith("@");
  if (op === "=" || op === "==" || op === "eq") {
    return isLiteral(guard[1]) && isLiteral(guard[2]) ? guard[1] === guard[2] : null;
  }
  if (op === "!=" || op === "ne" || op === "not-eq" || op === "neq") {
    return isLiteral(guard[1]) && isLiteral(guard[2]) ? guard[1] !== guard[2] : null;
  }
  if (op === "not") {
    const inner = constTruth(guard[1]);
    return inner === null ? null : !inner;
  }
  if (op === "or") {
    const subs = guard.slice(1).map(constTruth);
    if (subs.some((s) => s === true)) return true;
    if (subs.every((s) => s === false)) return false;
    return null;
  }
  if (op === "and") {
    const subs = guard.slice(1).map(constTruth);
    if (subs.some((s) => s === false)) return false;
    if (subs.every((s) => s === true)) return true;
    return null;
  }
  return null;
}
function buildGuardPayloads(guard) {
  if (constTruth(guard) !== null) {
    return { pass: {}, fail: {} };
  }
  if (Array.isArray(guard) && typeof guard[0] === "string" && CONSTANT_GUARD_FORMS.has(guard[0])) {
    return { pass: {}, fail: {} };
  }
  if (typeof guard === "string") {
    const path = extractPayloadFieldPath(guard);
    if (path) {
      return path.length === 1 ? { pass: { [path[0]]: { id: "mock-test-id", name: "mock-test-name" } }, fail: { [path[0]]: null } } : { pass: nestedPayload(path, "mock-test-value"), fail: nestedPayload(path, null) };
    }
  }
  if (!Array.isArray(guard) || guard.length === 0) {
    return { pass: {}, fail: {} };
  }
  const op = String(guard[0]);
  if (op === "not-nil" || op === "not_nil") {
    const path = extractPayloadFieldPath(guard[1]);
    if (path) return { pass: nestedPayload(path, "mock-test-value"), fail: nestedPayload(path, null) };
  }
  if (op === "nil") {
    const path = extractPayloadFieldPath(guard[1]);
    if (path) return { pass: {}, fail: nestedPayload(path, "mock-test-value") };
  }
  if (op === "eq" || op === "==" || op === "=" || op === "not-eq" || op === "!=" || op === "neq") {
    const arrayPath = extractArrayLenPath(guard[1]);
    if (arrayPath && typeof guard[2] === "number") {
      const n = guard[2];
      const equal = { hit: nestedPayload(arrayPath, arrayOfLength(n)), miss: nestedPayload(arrayPath, arrayOfLength(n + 1)) };
      return op === "eq" || op === "==" || op === "=" ? { pass: equal.hit, fail: equal.miss } : { pass: equal.miss, fail: equal.hit };
    }
  }
  if (op === "eq" || op === "==" || op === "=") {
    const compared = payloadComparison(guard[1], guard[2]);
    if (compared) {
      const { path, val } = compared;
      const failVal = typeof val === "number" ? val + 1 : typeof val === "string" ? `not-${val}` : null;
      return { pass: nestedPayload(path, val), fail: nestedPayload(path, failVal) };
    }
    const objectGetPayload = buildObjectGetComparisonPayload(guard[1], guard[2], false);
    if (objectGetPayload) return objectGetPayload;
  }
  if (op === "not-eq" || op === "!=" || op === "neq") {
    const compared = payloadComparison(guard[1], guard[2]);
    if (compared) {
      const { path, val } = compared;
      const passVal = typeof val === "number" ? val + 1 : typeof val === "string" ? `not-${val}` : "other";
      return { pass: nestedPayload(path, passVal), fail: nestedPayload(path, val) };
    }
    const objectGetPayload = buildObjectGetComparisonPayload(guard[1], guard[2], true);
    if (objectGetPayload) return objectGetPayload;
  }
  if (op === "gt" || op === ">") {
    const arrayPath = extractArrayLenPath(guard[1]);
    const n = typeof guard[2] === "number" ? guard[2] : 0;
    if (arrayPath) return { pass: nestedPayload(arrayPath, arrayOfLength(n + 1)), fail: nestedPayload(arrayPath, arrayOfLength(n)) };
    const path = extractPayloadFieldPath(guard[1]);
    if (path) return { pass: nestedPayload(path, n + 1), fail: nestedPayload(path, n - 1) };
  }
  if (op === "gte" || op === ">=") {
    const arrayPath = extractArrayLenPath(guard[1]);
    const n = typeof guard[2] === "number" ? guard[2] : 0;
    if (arrayPath) return { pass: nestedPayload(arrayPath, arrayOfLength(n)), fail: nestedPayload(arrayPath, arrayOfLength(n - 1)) };
    const path = extractPayloadFieldPath(guard[1]);
    if (path) return { pass: nestedPayload(path, n), fail: nestedPayload(path, n - 1) };
  }
  if (op === "lt" || op === "<") {
    const arrayPath = extractArrayLenPath(guard[1]);
    const n = typeof guard[2] === "number" ? guard[2] : 0;
    if (arrayPath) return { pass: nestedPayload(arrayPath, arrayOfLength(n - 1)), fail: nestedPayload(arrayPath, arrayOfLength(n + 1)) };
    const path = extractPayloadFieldPath(guard[1]);
    if (path) return { pass: nestedPayload(path, n - 1), fail: nestedPayload(path, n + 1) };
  }
  if (op === "lte" || op === "<=") {
    const arrayPath = extractArrayLenPath(guard[1]);
    const n = typeof guard[2] === "number" ? guard[2] : 0;
    if (arrayPath) return { pass: nestedPayload(arrayPath, arrayOfLength(n)), fail: nestedPayload(arrayPath, arrayOfLength(n + 1)) };
    const path = extractPayloadFieldPath(guard[1]);
    if (path) return { pass: nestedPayload(path, n), fail: nestedPayload(path, n + 1) };
  }
  if (op === "and") {
    const subs = guard.slice(1);
    if (subs.length >= 2) {
      const built = subs.map(buildGuardPayloads);
      const merged = built.reduce((acc, b) => mergePayloads(acc, b.pass), {});
      const pass = applyFieldIntersections(subs, merged);
      const canFail = subs.findIndex((g) => constTruth(g) !== true);
      return { pass, fail: built[canFail >= 0 ? canFail : 0].fail };
    }
    if (subs.length === 1) return buildGuardPayloads(subs[0]);
  }
  if (op === "or") {
    const subs = guard.slice(1);
    if (subs.length >= 2) {
      const s1 = buildGuardPayloads(subs[0]);
      const s2 = buildGuardPayloads(subs[1]);
      const combinedPass = Object.keys(s1.pass).length > 0 ? s1.pass : s2.pass;
      return { pass: combinedPass, fail: mergePayloads(s1.fail, s2.fail) };
    }
    if (subs.length === 1) return buildGuardPayloads(subs[0]);
  }
  if (op === "not") {
    const inner = buildGuardPayloads(guard[1]);
    return { pass: inner.fail, fail: inner.pass };
  }
  if (op === "object/has") {
    const split = splitPayloadArg(guard[1], guard[2]);
    if (split && isPlainRecord(split.collection)) {
      const keys = Object.keys(split.collection);
      if (keys.length > 0) {
        return { pass: nestedPayload(split.path, keys[0]), fail: nestedPayload(split.path, absentKey(keys)) };
      }
    }
  }
  if (op === "object/get") {
    const split = splitPayloadArg(guard[1], guard[2]);
    if (split && isPlainRecord(split.collection)) {
      const map = split.collection;
      const keys = Object.keys(map);
      const passKey = keys.find((k) => Boolean(map[k]));
      if (passKey !== void 0) {
        return { pass: nestedPayload(split.path, passKey), fail: nestedPayload(split.path, absentKey(keys)) };
      }
    }
  }
  if (op === "array/includes") {
    const split = splitPayloadArg(guard[1], guard[2]);
    if (split && Array.isArray(split.collection) && split.collection.length > 0) {
      const passVal = split.collection[0];
      const failVal = typeof passVal === "string" ? `${passVal}-not-in-list` : "not-in-list";
      return { pass: nestedPayload(split.path, passVal), fail: nestedPayload(split.path, failVal) };
    }
  }
  if (op === "str/startsWith") {
    const path = extractPayloadFieldPath(guard[1]);
    const prefix = typeof guard[2] === "string" ? guard[2] : "";
    if (path) {
      return {
        pass: nestedPayload(path, `${prefix}mock-suffix`),
        fail: nestedPayload(path, prefix.length > 0 ? `not-${prefix}` : "mock-test-value")
      };
    }
  }
  if (op === "validate/url") {
    const path = extractPayloadFieldPath(guard[1]);
    if (path) {
      return { pass: nestedPayload(path, "https://example.test/mock"), fail: nestedPayload(path, "not-a-url") };
    }
  }
  if (op === "agent/is-pinned") {
    const field = extractPayloadFieldRef(guard[1]);
    if (field) return { pass: { [field]: "mem_test_unpinned" }, fail: { [field]: "mem_test_pinned" } };
  }
  if (op === "agent/memory-strength") {
    const field = extractPayloadFieldRef(guard[1]);
    if (field) return { pass: { [field]: "mem_test_id" }, fail: { [field]: "mem_nonexistent" } };
  }
  if (op.startsWith("agent/")) {
    return { pass: {}, fail: {} };
  }
  if (!isRecognizedGuardOperator(op)) {
    recordUnhandledOperator(op);
  }
  return { pass: {}, fail: {} };
}

// src/state-machine/replay-paths.ts
var ENTITY_PAYLOAD_FIELDS = /* @__PURE__ */ new Set(["data", "row", "item", "id"]);
function buildReplayPaths(transitions, initialState, maxDepth = 3) {
  const queue = [{ state: initialState, path: [] }];
  const replayPaths = /* @__PURE__ */ new Map();
  replayPaths.set(initialState, []);
  while (queue.length > 0) {
    const { state, path } = queue.shift();
    if (path.length >= maxDepth) continue;
    const fromHere = transitions.filter(
      (t) => t.from === state && t.event !== "INIT"
    );
    for (const transition of fromHere) {
      if (replayPaths.has(transition.to)) continue;
      const renderEffect = transition.renderEffects.find((re) => re.patternType !== null);
      const stepNeedsEntityData = transition.hasGuard || transition.payloadFields.some((f) => ENTITY_PAYLOAD_FIELDS.has(f));
      const step = {
        event: transition.event,
        fromState: state,
        toState: transition.to,
        slot: renderEffect?.slot ?? "main",
        expectedPattern: renderEffect?.patternType ?? void 0,
        needsEntityData: stepNeedsEntityData,
        payloadSchema: transition.payloadSchema.length > 0 ? transition.payloadSchema : void 0
      };
      const newPath = [...path, step];
      replayPaths.set(transition.to, newPath);
      queue.push({ state: transition.to, path: newPath });
    }
  }
  return replayPaths;
}

// src/state-machine/edge-walk.ts
function buildEdgeCoveringWalk(transitions, initialState) {
  const filtered = transitions.filter(
    (t) => t.from !== "*" && !(t.event === "INIT" && t.from === initialState)
  );
  const graph = /* @__PURE__ */ new Map();
  const allStates = /* @__PURE__ */ new Set();
  for (const t of filtered) {
    allStates.add(t.from);
    allStates.add(t.to);
    if (!graph.has(t.from)) graph.set(t.from, []);
    graph.get(t.from).push({ event: t.event, to: t.to, transition: t });
  }
  allStates.add(initialState);
  const uncovered = /* @__PURE__ */ new Set();
  const edgeMeta = /* @__PURE__ */ new Map();
  for (const t of filtered) {
    if (t.hasGuard) {
      const keyPass = `${t.from}+${t.event}->${t.to}[pass]`;
      const keyFail = `${t.from}+${t.event}->${t.to}[fail]`;
      uncovered.add(keyPass);
      uncovered.add(keyFail);
      edgeMeta.set(keyPass, { transition: t, guardCase: "pass" });
      edgeMeta.set(keyFail, { transition: t, guardCase: "fail" });
    } else {
      const key = `${t.from}+${t.event}->${t.to}`;
      uncovered.add(key);
      edgeMeta.set(key, { transition: t, guardCase: null });
    }
  }
  if (uncovered.size === 0) return [];
  const shortestPaths = /* @__PURE__ */ new Map();
  for (const state of allStates) {
    shortestPaths.set(state, bfsShortestPaths(state, graph));
  }
  const walk = [];
  let currentState = initialState;
  const maxIterations = uncovered.size * allStates.size * 2;
  let iterations = 0;
  while (uncovered.size > 0 && iterations < maxIterations) {
    iterations++;
    const outgoing = findUncoveredEdges(currentState, graph, uncovered);
    if (outgoing.length > 0) {
      const pick = outgoing[0];
      const payload = buildPayloadForEdge(pick.transition, pick.guardCase);
      walk.push({
        from: currentState,
        event: pick.transition.event,
        to: pick.transition.to,
        guardCase: pick.guardCase,
        payload,
        isRepositioning: false
      });
      uncovered.delete(pick.key);
      if (pick.guardCase !== "fail") {
        currentState = pick.transition.to;
      }
    } else {
      const target = findNearestUncoveredState(currentState, graph, uncovered, shortestPaths);
      if (!target) {
        break;
      }
      const repoPath = shortestPaths.get(currentState)?.get(target);
      if (!repoPath || repoPath.length === 0) break;
      for (const step of repoPath) {
        walk.push({
          from: currentState,
          event: step.event,
          to: step.to,
          guardCase: null,
          payload: {},
          isRepositioning: true
        });
        currentState = step.to;
      }
    }
  }
  return walk;
}
function bfsShortestPaths(source, graph) {
  const paths = /* @__PURE__ */ new Map();
  const visited = /* @__PURE__ */ new Set([source]);
  const queue = [
    { state: source, path: [] }
  ];
  while (queue.length > 0) {
    const { state, path } = queue.shift();
    const edges = graph.get(state) ?? [];
    for (const edge of edges) {
      if (visited.has(edge.to)) continue;
      visited.add(edge.to);
      const newPath = [...path, { event: edge.event, to: edge.to }];
      paths.set(edge.to, newPath);
      queue.push({ state: edge.to, path: newPath });
    }
  }
  return paths;
}
function findUncoveredEdges(state, graph, uncovered) {
  const edges = graph.get(state) ?? [];
  const result = [];
  for (const edge of edges) {
    if (edge.transition.hasGuard) {
      const keyPass = `${state}+${edge.event}->${edge.to}[pass]`;
      const keyFail = `${state}+${edge.event}->${edge.to}[fail]`;
      if (uncovered.has(keyPass)) {
        result.push({ key: keyPass, transition: edge.transition, guardCase: "pass" });
      }
      if (uncovered.has(keyFail)) {
        result.push({ key: keyFail, transition: edge.transition, guardCase: "fail" });
      }
    } else {
      const key = `${state}+${edge.event}->${edge.to}`;
      if (uncovered.has(key)) {
        result.push({ key, transition: edge.transition, guardCase: null });
      }
    }
  }
  return result;
}
function findNearestUncoveredState(currentState, graph, uncovered, shortestPaths) {
  const statesWithUncovered = /* @__PURE__ */ new Set();
  for (const key of uncovered) {
    const fromState = key.split("+")[0];
    statesWithUncovered.add(fromState);
  }
  if (statesWithUncovered.has(currentState)) return currentState;
  const paths = shortestPaths.get(currentState);
  if (!paths) return null;
  let nearestState = null;
  let nearestDist = Infinity;
  for (const target of statesWithUncovered) {
    const path = paths.get(target);
    if (path && path.length < nearestDist) {
      nearestDist = path.length;
      nearestState = target;
    }
  }
  return nearestState;
}
function buildPayloadForEdge(transition, guardCase) {
  if (transition.hasGuard && transition.guard && guardCase) {
    const payloads = buildGuardPayloads(transition.guard);
    return guardCase === "pass" ? payloads.pass : payloads.fail;
  }
  if (transition.payloadSchema && transition.payloadSchema.length > 0) {
    const payload = {};
    for (const field of transition.payloadSchema) {
      if (field.mockValue !== void 0) {
        payload[field.name] = field.mockValue;
      } else if (field.type === "string") {
        payload[field.name] = `mock-${field.name}`;
      } else if (field.type === "number") {
        payload[field.name] = 1;
      } else if (field.type === "boolean") {
        payload[field.name] = true;
      } else if (field.type === "object" || field.type === "any") {
        payload[field.name] = {};
      } else {
        payload[field.name] = `mock-${field.name}`;
      }
    }
    return payload;
  }
  return {};
}

// src/access/entityAccess.ts
function orbitalInlineEntities(orbital) {
  const out = [];
  const refs = [orbital.entity, ...orbital.auxiliaryEntities ?? []];
  for (const ref2 of refs) {
    if (typeof ref2 === "object" && ref2 !== null && "fields" in ref2) {
      out.push(ref2);
    }
  }
  return out;
}
function inlineEntities(schema) {
  return (schema.orbitals ?? []).flatMap(orbitalInlineEntities);
}
function isDeclared(p) {
  return p.read !== void 0 || p.create !== void 0 || p.update !== void 0 || p.delete !== void 0;
}
function entityAccessTable(schema) {
  const table = /* @__PURE__ */ new Map();
  const collectionOf = /* @__PURE__ */ new Map();
  for (const def of inlineEntities(schema)) {
    table.set(def.name, {
      read: def.read_policy,
      create: def.create_policy,
      update: def.update_policy,
      delete: def.delete_policy
    });
    if (def.collection) collectionOf.set(def.name, def.collection);
  }
  const byCollection = /* @__PURE__ */ new Map();
  for (const [name, policies] of table) {
    const collection = collectionOf.get(name);
    if (collection && isDeclared(policies) && !byCollection.has(collection)) {
      byCollection.set(collection, policies);
    }
  }
  for (const [name, policies] of table) {
    if (isDeclared(policies)) continue;
    const collection = collectionOf.get(name);
    const inherited = collection ? byCollection.get(collection) : void 0;
    if (inherited) table.set(name, { ...inherited });
  }
  return table;
}
function entityAccessPolicies(schema, entityName2) {
  return entityAccessTable(schema).get(entityName2);
}

// src/access/externalInputs.ts
function externalInputsOf(schema) {
  const out = [];
  for (const orbital of schema.orbitals ?? []) {
    for (const traitRef of orbital.traits ?? []) {
      if (!isInlineTrait(traitRef)) continue;
      for (const ev of traitRef.stateMachine?.events ?? []) {
        if (ev.external !== true) continue;
        out.push({
          orbital: orbital.name,
          trait: traitRef.name,
          event: ev.key,
          payloadSchema: ev.payloadSchema ?? [],
          ...ev.description !== void 0 ? { description: ev.description } : {},
          ...ev.synonyms !== void 0 ? { synonyms: ev.synonyms } : {},
          ...ev.tier !== void 0 ? { tier: ev.tier } : {}
        });
      }
    }
  }
  return out;
}
function readableEntitiesOf(schema) {
  const out = [];
  for (const orbital of schema.orbitals ?? []) {
    for (const entity of orbitalInlineEntities(orbital)) {
      if (entity.persistence === "runtime" || out.includes(entity.name)) continue;
      out.push(entity.name);
    }
  }
  return out;
}
function findExternalInput(schema, orbital, trait, event) {
  return externalInputsOf(schema).find((i) => i.orbital === orbital && i.trait === trait && i.event === event);
}
function findInputByAddress(inputs, address, caller) {
  const { event, source } = splitEventAddress(address);
  if (source?.kind === "any") return void 0;
  const orbital = source?.kind === "orbital" ? source.orbital : caller.orbital;
  const trait = source === void 0 ? caller.trait : source.trait;
  return inputs.find((i) => i.orbital === orbital && i.trait === trait && i.event === event);
}
function payloadSchemaToJsonSchema(fields) {
  const properties = {};
  const required = [];
  for (const field of fields) {
    if (field.properties !== void 0 && field.properties.length === 0 && field.type !== "union") continue;
    properties[field.name] = payloadFieldToJsonSchema(field);
    if (field.required === true) required.push(field.name);
  }
  return {
    type: "object",
    ...fields.length > 0 ? { additionalProperties: false } : {},
    properties,
    ...required.length > 0 ? { required } : {}
  };
}
function elementOf(type) {
  const isArray = type.startsWith("[") && type.endsWith("]");
  return { isArray, element: isArray ? type.slice(1, -1) : type };
}
function payloadFieldToJsonSchema(field) {
  const { isArray, element } = elementOf(field.type);
  const base = field.properties !== void 0 ? payloadSchemaToJsonSchema(field.properties) : (() => {
    const t = mapEntityFieldType(element);
    return t === null ? {} : { type: t };
  })();
  const shape = field.values !== void 0 ? { ...base, enum: [...field.values] } : base;
  return isArray ? { type: "array", items: shape } : shape;
}
function isPayloadObject2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);
}
function payloadIssues(fields, value) {
  return objectIssues(fields, value, "");
}
function objectIssues(fields, value, at) {
  if (!isPayloadObject2(value)) {
    return [{ path: at === "" ? "." : at, reason: "wrong-type", expected: "object" }];
  }
  const join = (key) => at === "" ? key : `${at}.${key}`;
  const issues = [];
  for (const field of fields) {
    const v = value[field.name];
    if (v === void 0 || v === null) {
      if (field.required === true) issues.push({ path: join(field.name), reason: "missing" });
      continue;
    }
    issues.push(...fieldIssues(field, v, join(field.name)));
  }
  const declared = new Set(fields.map((f) => f.name));
  const allowed = fields.length > 0 ? fields.map((f) => f.name).join(" | ") : "(no keys)";
  for (const key of Object.keys(value)) {
    if (!declared.has(key)) issues.push({ path: join(key), reason: "unknown", expected: allowed });
  }
  return issues;
}
function fieldIssues(field, value, at) {
  const { isArray, element } = elementOf(field.type);
  if (isArray || field.type === "array") {
    if (!Array.isArray(value)) return [{ path: at, reason: "wrong-type", expected: field.type }];
    return value.flatMap((item, i) => elementIssues(field, element, item, `${at}[${i}]`));
  }
  return elementIssues(field, element, value, at);
}
function elementIssues(field, element, value, at) {
  if (field.properties !== void 0 && field.type !== "union") return objectIssues(field.properties, value, at);
  if (field.type === "union" && field.properties !== void 0) {
    const variants = field.properties;
    return variants.some((v) => elementIssues(v, elementOf(v.type).element, value, at).length === 0) ? [] : [{ path: at, reason: "wrong-type", expected: variants.map((v) => v.type).join(" | ") }];
  }
  const json = element === "object" ? "object" : mapEntityFieldType(element);
  const matches = json === null || json === "object" && isPayloadObject2(value) || json === "array" && Array.isArray(value) || json === "string" && (typeof value === "string" || value instanceof Date) || json === "number" && typeof value === "number" || json === "integer" && typeof value === "number" && Number.isInteger(value) || json === "boolean" && typeof value === "boolean";
  if (!matches) return [{ path: at, reason: "wrong-type", expected: element }];
  if (field.values !== void 0 && (typeof value !== "string" || !field.values.includes(value))) {
    return [{ path: at, reason: "not-one-of", expected: field.values.join(" | ") }];
  }
  return [];
}

// src/route-match.ts
function matchPath(pattern, path) {
  const normalizeSegment = (p) => {
    let normalized = p.trim();
    if (!normalized.startsWith("/")) normalized = "/" + normalized;
    if (normalized.length > 1 && normalized.endsWith("/")) {
      normalized = normalized.slice(0, -1);
    }
    return normalized;
  };
  const normalizedPattern = normalizeSegment(pattern);
  const normalizedPath = normalizeSegment(path);
  const patternParts = normalizedPattern.split("/").filter(Boolean);
  const pathParts = normalizedPath.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) {
    return null;
  }
  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];
    if (patternPart.startsWith(":")) {
      const paramName = patternPart.slice(1);
      params[paramName] = decodeURIComponent(pathPart);
    } else if (patternPart !== pathPart) {
      return null;
    }
  }
  return params;
}
function comparePathSpecificity(a, b) {
  const aParts = a.split("/").filter(Boolean);
  const bParts = b.split("/").filter(Boolean);
  const shared = Math.min(aParts.length, bParts.length);
  for (let i = 0; i < shared; i++) {
    const aParam = aParts[i].startsWith(":");
    const bParam = bParts[i].startsWith(":");
    if (aParam !== bParam) return aParam ? 1 : -1;
  }
  return 0;
}
function matchPathAmong(candidates, path, pathOf) {
  let best = null;
  for (const candidate of candidates) {
    const pattern = pathOf(candidate);
    if (!pattern) continue;
    const params = matchPath(pattern, path);
    if (params === null) continue;
    if (best === null || comparePathSpecificity(pattern, best.pattern) < 0) {
      best = { candidate, params, pattern };
    }
  }
  return best === null ? null : { candidate: best.candidate, params: best.params };
}
function defaultPage(schema) {
  for (const orbital of schema.orbitals) {
    for (const page of orbital.pages ?? []) {
      if (!isPageReference(page)) return { page, orbitalName: orbital.name };
    }
  }
  return null;
}
function pathMatchesPattern(path, pattern) {
  return matchPath(pattern, path) !== null;
}

// src/shared-entity/merge.ts
function mergeEntityFrame(current, orderedWrites) {
  if (orderedWrites.length === 0) return current;
  const next = { ...current };
  for (const write of orderedWrites) {
    next[write.field] = write.value;
  }
  return next;
}
function resolveEntityView(input) {
  const { frame, row, id, allowedFrameKeys } = input;
  const view = {};
  if (frame) {
    for (const key of allowedFrameKeys) {
      if (key === "id") continue;
      if (Object.prototype.hasOwnProperty.call(frame, key)) {
        view[key] = frame[key];
      }
    }
  }
  Object.assign(view, row ?? {});
  if (id !== void 0) view.id = id;
  return view;
}
function omitFrameFields(row, frameKeys) {
  const next = { ...row };
  for (const key of frameKeys) {
    delete next[key];
  }
  return next;
}

// src/i18n/orb-notation.json
var orb_notation_default = {
  reason: "Design-system token NAMES reachable only under a theme `tokens` node. They are keys of the .orb schema but notation, not language vocabulary, so they are excluded from the `orb` i18n section (owner ruling 2026-09-07). Regenerate with: pnpm --filter @almadar/core run gen:orb-notation",
  keys: [
    "2xl",
    "3xl",
    "4xl",
    "accent",
    "accentForeground",
    "accentImage",
    "awaitingSkeleton",
    "backdrop",
    "background",
    "base",
    "bodyDefault",
    "bodyEmphasis",
    "bodyFamily",
    "bodyQuiet",
    "border",
    "borderHairline",
    "borderHeavy",
    "borderHover",
    "borderStandard",
    "borderStyle",
    "borderStyleInteractive",
    "busyDelay",
    "buttonHeightLg",
    "buttonHeightMd",
    "buttonHeightSm",
    "caption",
    "card",
    "cardElevation",
    "cardForeground",
    "cardImage",
    "cardPaddingLg",
    "cardPaddingMd",
    "cardPaddingSm",
    "corner",
    "cornerShape",
    "cornerShapeInteractive",
    "cornerShapePill",
    "dash",
    "defaultSize",
    "dialogElevation",
    "dialogPadding",
    "display-1",
    "display-2",
    "displayFamily",
    "dot",
    "dramatic",
    "duration",
    "durations",
    "easing",
    "easings",
    "edgeHeight",
    "edgeMask",
    "emphasized",
    "emptyAsset",
    "enter",
    "enterRise",
    "enterScale",
    "enterSlide",
    "entry",
    "error",
    "errorAsset",
    "errorForeground",
    "exit",
    "expand",
    "family",
    "fast",
    "fillOpacity",
    "fillStyle",
    "fog",
    "fontImport",
    "foreground",
    "glow",
    "headingMajor",
    "headingMinor",
    "headingShadow",
    "headingStyle",
    "headingTracking",
    "headingTransform",
    "headingWeight",
    "hover",
    "info",
    "infoForeground",
    "input",
    "inputHeightLg",
    "inputHeightMd",
    "inputHeightSm",
    "instant",
    "intents",
    "interactiveElevation",
    "labelCase",
    "labelFont",
    "labelSize",
    "labelWeight",
    "lg",
    "lightAmbient",
    "lightKey",
    "lightKeyColor",
    "lineCap",
    "lineHeight",
    "lineJoin",
    "linear",
    "loadingAsset",
    "marker",
    "materialFlat",
    "materialMetalness",
    "materialOutline",
    "materialRoughness",
    "modalEnter",
    "modalExit",
    "monoFamily",
    "muted",
    "mutedForeground",
    "normal",
    "numeric",
    "onboardingAsset",
    "pageEnter",
    "pageImage",
    "pageImageSize",
    "placeholder",
    "popoverElevation",
    "popoverEnter",
    "popoverExit",
    "press",
    "pressedElevation",
    "primary",
    "primaryForeground",
    "primaryHover",
    "radiusContainer",
    "radiusInteractive",
    "radiusPill",
    "ring",
    "roughness",
    "rowHeightCompact",
    "rowHeightNormal",
    "rowHeightSpacious",
    "scale",
    "scrim",
    "secondary",
    "secondaryForeground",
    "secondaryHover",
    "sectionGap",
    "shadow",
    "shapes",
    "size",
    "slow",
    "sm",
    "space0",
    "space1",
    "space10",
    "space11",
    "space12",
    "space2",
    "space3",
    "space4",
    "space5",
    "space6",
    "space7",
    "space8",
    "space9",
    "spring",
    "stagger",
    "standard",
    "strokeBold",
    "strokeNormal",
    "strokeThin",
    "strokeWidth",
    "success",
    "successForeground",
    "surfaceHover",
    "tableBorder",
    "tableHeader",
    "tableRowHover",
    "textureMask",
    "textureSize",
    "toastElevation",
    "toastEnter",
    "toastExit",
    "warning",
    "warningForeground",
    "weight",
    "xl",
    "xs"
  ]
};

// src/i18n/en.json
var en_default = {
  meta: {
    code: "en",
    name: "English",
    rtl: false
  },
  keywords: {
    app: "app",
    orbital: "orbital",
    uses: "uses",
    expects: "expects",
    entity: "entity",
    type: "type",
    derived: "derived",
    trait: "trait",
    initial: "initial",
    state: "state",
    for: "for",
    page: "page",
    as: "as",
    theme: "theme",
    emits: "emits",
    listens: "listens",
    ticks: "ticks",
    config: "config",
    when: "when",
    with: "with",
    from: "from",
    to: "to",
    every: "every",
    capabilities: "capabilities",
    match: "match",
    external: "external",
    internal: "internal",
    on: "on",
    name: "name",
    fields: "fields",
    events: "events",
    event: "event",
    emitsScope: "emitsScope",
    identity: "identity",
    pages: "pages",
    omit: "omit",
    only: "only",
    roles: "roles",
    entities: "entities",
    extend: "extend",
    retype: "retype",
    traits: "traits",
    mock: "mock",
    lazy: "lazy",
    locales: "locales",
    public: "public",
    instances: "instances",
    access: "access",
    indexing: "indexing",
    title: "title",
    description: "description",
    origin: "origin",
    icon: "icon",
    siteName: "siteName",
    signIn: "signIn",
    authenticated: "authenticated",
    index: "index",
    noindex: "noindex",
    version: "version",
    translationOf: "translationOf",
    label: "label"
  },
  shapes: {
    Entity: "Entity",
    Trait: "Trait",
    Page: "Page",
    Orbital: "Orbital",
    Event: "Event"
  },
  tags: {
    persistent: "persistent",
    runtime: "runtime",
    shared: "shared",
    local: "local",
    identity: "identity",
    instance: "instance",
    collection: "collection",
    background: "background",
    mock: "mock"
  },
  categories: {
    interaction: "interaction",
    integration: "integration",
    lifecycle: "lifecycle",
    "game-core": "game-core",
    "game-entity": "game-entity",
    "game-ui": "game-ui"
  },
  capabilities: {
    read: "read",
    write: "write",
    browse: "browse",
    create: "create",
    update: "update",
    delete: "delete",
    notify: "notify",
    schedule: "schedule",
    invoke: "invoke",
    batch: "batch"
  },
  annotations: {
    key: "key",
    unique: "unique",
    intrinsic: "intrinsic",
    label: "label",
    description: "description",
    synonyms: "synonyms",
    mock: "mock",
    tier: "tier",
    rebindable: "rebindable",
    read: "read",
    create: "create",
    update: "update",
    delete: "delete",
    icon: "icon",
    roles: "roles"
  },
  sigils: {
    entity: "entity",
    payload: "payload",
    state: "state",
    now: "now",
    config: "config",
    user: "user",
    trait: "trait",
    event: "event",
    prevEvents: "prevEvents",
    prevStates: "prevStates",
    fromState: "fromState",
    toState: "toState",
    locale: "locale"
  },
  effects: {
    set: "set",
    fetch: "fetch",
    persist: "persist",
    emit: "emit",
    navigate: "navigate",
    "navigate-back": "navigate-back",
    "render-ui": "render-ui",
    notify: "notify",
    ref: "ref",
    "call-service": "call-service",
    "cancel-call": "cancel-call",
    invoke: "invoke",
    do: "do",
    if: "if",
    let: "let",
    fn: "fn",
    lambda: "lambda",
    quote: "quote",
    quasiquote: "quasiquote",
    unquote: "unquote",
    match: "match",
    when: "when"
  },
  types: {
    string: "string",
    number: "number",
    int: "int",
    float: "float",
    bool: "bool",
    boolean: "boolean",
    timestamp: "timestamp",
    date: "date",
    datetime: "datetime",
    duration: "duration",
    money: "money",
    scalar: "scalar",
    file: "file",
    email: "email",
    url: "url",
    phone: "phone",
    uuid: "uuid",
    image: "image",
    icon: "icon",
    asset: "asset",
    component: "component",
    object: "object",
    array: "array",
    enum: "enum",
    relation: "relation",
    node: "node",
    union: "union",
    tuple: "tuple",
    void: "void",
    null: "null",
    any: "any",
    trait: "trait",
    orbital: "orbital",
    behavior: "behavior",
    entity: "entity",
    event: "event",
    EventAddress: "EventAddress",
    slot: "slot",
    pattern: "pattern",
    Map: "Map",
    secret: "secret",
    SExpr: "SExpr"
  },
  units: {
    ms: "ms",
    s: "s",
    m: "m",
    h: "h",
    d: "d"
  },
  literals: {
    true: "true",
    false: "false",
    null: "null"
  },
  reservedEvents: {
    INIT: "INIT"
  },
  orb: {
    _metadata: "_metadata",
    _resolved: "_resolved",
    access_waivers: "access_waivers",
    alias: "alias",
    animations: "animations",
    appliesTo: "appliesTo",
    as: "as",
    aspect: "aspect",
    assetRef: "assetRef",
    at: "at",
    auth: "auth",
    auxiliaryEntities: "auxiliaryEntities",
    awaitRender: "awaitRender",
    bakedName: "bakedName",
    baseUrl: "baseUrl",
    behavior: "behavior",
    capabilities: "capabilities",
    cardinality: "cardinality",
    category: "category",
    className: "className",
    classification: "classification",
    collection: "collection",
    color: "color",
    colors: "colors",
    columns: "columns",
    component: "component",
    config: "config",
    configProvenance: "configProvenance",
    container: "container",
    content: "content",
    create: "create",
    create_policy: "create_policy",
    createdAt: "createdAt",
    curName: "curName",
    customPatterns: "customPatterns",
    darkMode: "darkMode",
    dataEntities: "dataEntities",
    default: "default",
    definerKnob: "definerKnob",
    delayMs: "delayMs",
    delete: "delete",
    delete_policy: "delete_policy",
    density: "density",
    description: "description",
    description_visual_prompt: "description_visual_prompt",
    design: "design",
    designTokens: "designTokens",
    detailPattern: "detailPattern",
    device: "device",
    dimension: "dimension",
    displayName: "displayName",
    domainContext: "domainContext",
    effectRow: "effectRow",
    effects: "effects",
    elevation: "elevation",
    emitIds: "emitIds",
    emits: "emits",
    emitsScope: "emitsScope",
    enabled: "enabled",
    entities: "entities",
    entity: "entity",
    entityBindingDescription: "entityBindingDescription",
    entityBindingSynonyms: "entityBindingSynonyms",
    entityContract: "entityContract",
    entityId: "entityId",
    entityRebindable: "entityRebindable",
    entityRefIds: "entityRefIds",
    entries: "entries",
    env: "env",
    equals: "equals",
    event: "event",
    eventId: "eventId",
    eventIds: "eventIds",
    events: "events",
    expects: "expects",
    exposes: "exposes",
    extend: "extend",
    retype: "retype",
    extends: "extends",
    extendsId: "extendsId",
    external: "external",
    field: "field",
    fields: "fields",
    flowPattern: "flowPattern",
    foreignKey: "foreignKey",
    formPattern: "formPattern",
    forwardedFrom: "forwardedFrom",
    from: "from",
    geometry: "geometry",
    guard: "guard",
    guards: "guards",
    headers: "headers",
    icon: "icon",
    iconography: "iconography",
    id: "id",
    identity: "identity",
    illustration: "illustration",
    inbound: "inbound",
    initialEffects: "initialEffects",
    instances: "instances",
    interactive: "interactive",
    interval: "interval",
    intrinsic: "intrinsic",
    isFinal: "isFinal",
    isInitial: "isInitial",
    isTerminal: "isTerminal",
    item: "item",
    items: "items",
    key: "key",
    keyName: "keyName",
    kind: "kind",
    knob: "knob",
    label: "label",
    lazy: "lazy",
    lazyPages: "lazyPages",
    ledger: "ledger",
    linkedEntity: "linkedEntity",
    linkedEntityId: "linkedEntityId",
    listPattern: "listPattern",
    listens: "listens",
    local: "local",
    locales: "locales",
    location: "location",
    max: "max",
    maxAttempts: "maxAttempts",
    mergedFrom: "mergedFrom",
    projectedFrom: "projectedFrom",
    messageSources: "messageSources",
    min: "min",
    mock: "mock",
    motion: "motion",
    mounts: "mounts",
    name: "name",
    nestedForwardedFrom: "nestedForwardedFrom",
    omit: "omit",
    onDelete: "onDelete",
    onEntry: "onEntry",
    onExit: "onExit",
    only: "only",
    orbRef: "orbRef",
    orbital: "orbital",
    orbitalId: "orbitalId",
    orbitals: "orbitals",
    originalEvent: "originalEvent",
    originalName: "originalName",
    outbound: "outbound",
    owner: "owner",
    pageIds: "pageIds",
    pages: "pages",
    parent: "parent",
    path: "path",
    pattern: "pattern",
    patternPath: "patternPath",
    payload: "payload",
    payloadEntity: "payloadEntity",
    payloadFor: "payloadFor",
    payloadMapping: "payloadMapping",
    payloadSchema: "payloadSchema",
    persistence: "persistence",
    personas: "personas",
    presentation: "presentation",
    primaryColor: "primaryColor",
    primaryEntity: "primaryEntity",
    primaryKey: "primaryKey",
    priority: "priority",
    prop: "prop",
    properties: "properties",
    props: "props",
    provides: "provides",
    publicDir: "publicDir",
    radii: "radii",
    read: "read",
    read_policy: "read_policy",
    reconnect: "reconnect",
    ref: "ref",
    refId: "refId",
    reference: "reference",
    relatedLinks: "relatedLinks",
    relation: "relation",
    renames: "renames",
    request: "request",
    requestFragment: "requestFragment",
    required: "required",
    requiredFields: "requiredFields",
    requires: "requires",
    resolved: "resolved",
    resource: "resource",
    role: "role",
    roles: "roles",
    rows: "rows",
    runsInBackground: "runsInBackground",
    runtime: "runtime",
    schemaVersion: "schemaVersion",
    scope: "scope",
    scopeOverridden: "scopeOverridden",
    secretEnv: "secretEnv",
    sections: "sections",
    semanticRole: "semanticRole",
    serverPath: "serverPath",
    services: "services",
    shadows: "shadows",
    shape: "shape",
    shared: "shared",
    singleton: "singleton",
    site: "site",
    skeleton: "skeleton",
    slot: "slot",
    slots: "slots",
    softDelete: "softDelete",
    source: "source",
    sourceBehavior: "sourceBehavior",
    sourceEntityDefinition: "sourceEntityDefinition",
    spacing: "spacing",
    stack: "stack",
    stateMachine: "stateMachine",
    states: "states",
    style: "style",
    subDomain: "subDomain",
    suggestedGuards: "suggestedGuards",
    summary: "summary",
    surface: "surface",
    surfaces: "surfaces",
    synonyms: "synonyms",
    target: "target",
    targetView: "targetView",
    text: "text",
    theme: "theme",
    tick: "tick",
    ticks: "ticks",
    tier: "tier",
    timeBoundMs: "timeBoundMs",
    timeout: "timeout",
    timestamps: "timestamps",
    title: "title",
    to: "to",
    tokens: "tokens",
    trait: "trait",
    traitEmbedIds: "traitEmbedIds",
    traitId: "traitId",
    traitName: "traitName",
    traitRefIds: "traitRefIds",
    traits: "traits",
    transition: "transition",
    transitions: "transitions",
    triggers: "triggers",
    triggersId: "triggersId",
    type: "type",
    typeArgs: "typeArgs",
    typeParams: "typeParams",
    typeScale: "typeScale",
    typeWhen: "typeWhen",
    types: "types",
    typography: "typography",
    ui: "ui",
    unique: "unique",
    update: "update",
    update_policy: "update_policy",
    updatedAt: "updatedAt",
    url: "url",
    uses: "uses",
    uxHints: "uxHints",
    values: "values",
    variant: "variant",
    variants: "variants",
    version: "version",
    viewType: "viewType",
    visual_prompt: "visual_prompt",
    vocabulary: "vocabulary",
    seedMock: "seedMock",
    localeInstances: "localeInstances",
    diagram: "diagram",
    scene: "scene",
    access: "access",
    indexing: "indexing",
    origin: "origin",
    signIn: "signIn",
    sourcePage: "sourcePage",
    translationOf: "translationOf",
    pageModifiers: "pageModifiers"
  }
};

// src/i18n/ar.json
var ar_default = {
  meta: {
    code: "ar",
    name: "Arabic",
    rtl: true
  },
  keywords: {
    app: "\u062A\u0637\u0628\u064A\u0642",
    orbital: "\u0645\u062F\u0627\u0631",
    uses: "\u064A\u0633\u062A\u062E\u062F\u0645",
    expects: "\u064A\u062A\u0648\u0642\u0639",
    entity: "\u0643\u064A\u0627\u0646",
    type: "\u0646\u0648\u0639",
    derived: "\u0645\u0634\u062A\u0642",
    trait: "\u0633\u0645\u0629",
    initial: "\u0623\u0648\u0644\u064A",
    state: "\u062D\u0627\u0644\u0629",
    for: "\u0645\u0646_\u0623\u062C\u0644",
    page: "\u0635\u0641\u062D\u0629",
    as: "\u0643\u0640",
    theme: "\u0645\u0638\u0647\u0631",
    emits: "\u064A\u0635\u062F\u0631",
    listens: "\u064A\u0633\u062A\u0645\u0639",
    ticks: "\u0646\u0628\u0636\u0627\u062A",
    config: "\u062A\u0643\u0648\u064A\u0646",
    when: "\u0639\u0646\u062F\u0645\u0627",
    with: "\u0645\u0639",
    from: "\u0645\u0646",
    to: "\u0625\u0644\u0649",
    every: "\u0643\u0644",
    capabilities: "\u0642\u062F\u0631\u0627\u062A",
    match: "\u0645\u0637\u0627\u0628\u0642\u0629",
    external: "\u062E\u0627\u0631\u062C\u064A",
    internal: "\u062F\u0627\u062E\u0644\u064A",
    on: "\u0639\u0646\u062F",
    name: "\u0627\u0633\u0645",
    fields: "\u062D\u0642\u0648\u0644",
    events: "\u0623\u062D\u062F\u0627\u062B",
    event: "\u062D\u062F\u062B",
    emitsScope: "\u0646\u0637\u0627\u0642_\u0627\u0644\u0625\u0635\u062F\u0627\u0631",
    identity: "\u0647\u0648\u064A\u0629",
    pages: "\u0635\u0641\u062D\u0627\u062A",
    omit: "\u0627\u0633\u062A\u0628\u0639\u0627\u062F",
    only: "\u0641\u0642\u0637",
    roles: "\u0623\u062F\u0648\u0627\u0631",
    entities: "\u0643\u064A\u0627\u0646\u0627\u062A",
    extend: "\u0627\u0645\u062A\u062F\u0627\u062F",
    retype: "\u0625\u0639\u0627\u062F\u0629_\u0627\u0644\u0646\u0648\u0639",
    traits: "\u0633\u0645\u0627\u062A",
    mock: "\u0648\u0647\u0645\u064A",
    lazy: "\u0645\u0624\u062C\u0644",
    locales: "\u0644\u063A\u0627\u062A",
    public: "\u0639\u0627\u0645",
    instances: "\u0646\u0633\u062E",
    access: "\u0648\u0635\u0648\u0644",
    indexing: "\u0641\u0647\u0631\u0633\u0629",
    title: "\u0639\u0646\u0648\u0627\u0646",
    description: "\u0648\u0635\u0641",
    origin: "\u0623\u0635\u0644",
    icon: "\u0623\u064A\u0642\u0648\u0646\u0629",
    siteName: "\u0627\u0633\u0645_\u0627\u0644\u0645\u0648\u0642\u0639",
    signIn: "\u062A\u0633\u062C\u064A\u0644_\u0627\u0644\u062F\u062E\u0648\u0644",
    authenticated: "\u0645\u0648\u062B\u0642",
    index: "\u0641\u0647\u0631\u0633",
    noindex: "\u0628\u0644\u0627_\u0641\u0647\u0631\u0633\u0629",
    version: "\u0625\u0635\u062F\u0627\u0631",
    translationOf: "\u062A\u0631\u062C\u0645\u0629_\u0645\u0646",
    label: "\u062A\u0633\u0645\u064A\u0629"
  },
  shapes: {
    Entity: "\u0643\u064A\u0627\u0646",
    Trait: "\u0633\u0645\u0629",
    Page: "\u0635\u0641\u062D\u0629",
    Orbital: "\u0645\u062F\u0627\u0631",
    Event: "\u062D\u062F\u062B"
  },
  tags: {
    persistent: "\u062F\u0627\u0626\u0645",
    runtime: "\u0648\u0642\u062A_\u0627\u0644\u062A\u0634\u063A\u064A\u0644",
    shared: "\u0645\u0634\u062A\u0631\u0643",
    local: "\u0645\u062D\u0644\u064A",
    identity: "\u0647\u0648\u064A\u0629",
    instance: "\u0646\u0633\u062E\u0629",
    collection: "\u0645\u062C\u0645\u0648\u0639\u0629",
    background: "\u062E\u0644\u0641\u064A",
    mock: "\u0648\u0647\u0645\u064A"
  },
  categories: {
    interaction: "\u062A\u0641\u0627\u0639\u0644",
    integration: "\u062A\u0643\u0627\u0645\u0644",
    lifecycle: "\u062F\u0648\u0631\u0629_\u062D\u064A\u0627\u0629",
    "game-core": "\u0623\u0633\u0627\u0633\u064A\u0627\u062A_\u0627\u0644\u0644\u0639\u0628\u0629",
    "game-entity": "\u0643\u064A\u0627\u0646_\u0627\u0644\u0644\u0639\u0628\u0629",
    "game-ui": "\u0648\u0627\u062C\u0647\u0629_\u0627\u0644\u0644\u0639\u0628\u0629"
  },
  capabilities: {
    read: "\u0642\u0631\u0627\u0621\u0629",
    write: "\u0643\u062A\u0627\u0628\u0629",
    browse: "\u062A\u0635\u0641\u062D",
    create: "\u0625\u0646\u0634\u0627\u0621",
    update: "\u062A\u062D\u062F\u064A\u062B",
    delete: "\u062D\u0630\u0641",
    notify: "\u0625\u0634\u0639\u0627\u0631",
    schedule: "\u062C\u062F\u0648\u0644\u0629",
    invoke: "\u0627\u0633\u062A\u062F\u0639\u0627\u0621",
    batch: "\u062F\u0641\u0639\u0629"
  },
  annotations: {
    key: "\u0645\u0641\u062A\u0627\u062D",
    unique: "\u0641\u0631\u064A\u062F",
    intrinsic: "\u062C\u0648\u0647\u0631\u064A",
    label: "\u062A\u0633\u0645\u064A\u0629",
    description: "\u0648\u0635\u0641",
    synonyms: "\u0645\u0631\u0627\u062F\u0641\u0627\u062A",
    mock: "\u0648\u0647\u0645\u064A",
    tier: "\u0645\u0633\u062A\u0648\u0649",
    rebindable: "\u0642\u0627\u0628\u0644_\u0644\u0625\u0639\u0627\u062F\u0629_\u0627\u0644\u0631\u0628\u0637",
    read: "\u0642\u0631\u0627\u0621\u0629",
    create: "\u0625\u0646\u0634\u0627\u0621",
    update: "\u062A\u062D\u062F\u064A\u062B",
    delete: "\u062D\u0630\u0641",
    icon: "\u0623\u064A\u0642\u0648\u0646\u0629",
    roles: "\u0623\u062F\u0648\u0627\u0631"
  },
  sigils: {
    entity: "\u0643\u064A\u0627\u0646",
    payload: "\u062D\u0645\u0648\u0644\u0629",
    state: "\u062D\u0627\u0644\u0629",
    now: "\u0627\u0644\u0622\u0646",
    config: "\u062A\u0643\u0648\u064A\u0646",
    user: "\u0645\u0633\u062A\u062E\u062F\u0645",
    trait: "\u0633\u0645\u0629",
    event: "\u062D\u062F\u062B",
    prevEvents: "\u0627\u0644\u0623\u062D\u062F\u0627\u062B_\u0627\u0644\u0633\u0627\u0628\u0642\u0629",
    prevStates: "\u0627\u0644\u062D\u0627\u0644\u0627\u062A_\u0627\u0644\u0633\u0627\u0628\u0642\u0629",
    fromState: "\u0645\u0646_\u062D\u0627\u0644\u0629",
    toState: "\u0625\u0644\u0649_\u062D\u0627\u0644\u0629",
    locale: "\u0627\u0644\u0644\u063A\u0629"
  },
  effects: {
    set: "\u062A\u0639\u064A\u064A\u0646",
    fetch: "\u062C\u0644\u0628",
    persist: "\u062D\u0641\u0638",
    emit: "\u0628\u062B",
    navigate: "\u0627\u0646\u062A\u0642\u0627\u0644",
    "navigate-back": "\u0631\u062C\u0648\u0639",
    "render-ui": "\u0639\u0631\u0636_\u0648\u0627\u062C\u0647\u0629",
    notify: "\u0625\u0634\u0639\u0627\u0631",
    ref: "\u0645\u0631\u062C\u0639",
    "call-service": "\u0627\u0633\u062A\u062F\u0639\u0627\u0621_\u062E\u062F\u0645\u0629",
    "cancel-call": "\u0625\u0644\u063A\u0627\u0621_\u0627\u0633\u062A\u062F\u0639\u0627\u0621",
    invoke: "\u0627\u0633\u062A\u062F\u0639\u0627\u0621",
    do: "\u062A\u0646\u0641\u064A\u0630",
    if: "\u0625\u0630\u0627",
    let: "\u0644\u064A\u0643\u0646",
    fn: "\u062F\u0627\u0644\u0629",
    lambda: "\u0644\u0627\u0645\u062F\u0627",
    quote: "\u0627\u0642\u062A\u0628\u0627\u0633",
    quasiquote: "\u0634\u0628\u0647_\u0627\u0642\u062A\u0628\u0627\u0633",
    unquote: "\u0625\u0644\u063A\u0627\u0621_\u0627\u0642\u062A\u0628\u0627\u0633",
    match: "\u0645\u0637\u0627\u0628\u0642\u0629",
    when: "\u0639\u0646\u062F\u0645\u0627"
  },
  types: {
    string: "\u0646\u0635",
    number: "\u0631\u0642\u0645",
    int: "\u0639\u062F\u062F_\u0635\u062D\u064A\u062D",
    float: "\u0639\u0634\u0631\u064A",
    bool: "\u062B\u0646\u0627\u0626\u064A",
    boolean: "\u0645\u0646\u0637\u0642\u064A",
    timestamp: "\u0637\u0627\u0628\u0639_\u0632\u0645\u0646\u064A",
    date: "\u062A\u0627\u0631\u064A\u062E",
    datetime: "\u062A\u0627\u0631\u064A\u062E_\u0648\u0648\u0642\u062A",
    duration: "\u0645\u062F\u0629",
    money: "\u0645\u0628\u0644\u063A_\u0645\u0627\u0644\u064A",
    scalar: "\u0639\u062F\u062F\u064A",
    file: "\u0645\u0644\u0641",
    email: "\u0628\u0631\u064A\u062F_\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    url: "\u0631\u0627\u0628\u0637",
    phone: "\u0647\u0627\u062A\u0641",
    uuid: "\u0645\u0639\u0631\u0641_\u0641\u0631\u064A\u062F",
    image: "\u0635\u0648\u0631\u0629",
    icon: "\u0623\u064A\u0642\u0648\u0646\u0629",
    asset: "\u0623\u0635\u0644",
    component: "\u0645\u0643\u0648\u0646",
    object: "\u0643\u0627\u0626\u0646",
    array: "\u0645\u0635\u0641\u0648\u0641\u0629",
    enum: "\u062A\u0639\u062F\u0627\u062F",
    relation: "\u0639\u0644\u0627\u0642\u0629",
    node: "\u0639\u0642\u062F\u0629",
    union: "\u0627\u062A\u062D\u0627\u062F",
    tuple: "\u0635\u0641_\u0645\u0631\u062A\u0628",
    void: "\u0641\u0631\u0627\u063A",
    null: "\u0639\u062F\u0645",
    any: "\u0623\u064A",
    trait: "\u0633\u0645\u0629",
    orbital: "\u0645\u062F\u0627\u0631",
    behavior: "\u0633\u0644\u0648\u0643",
    entity: "\u0643\u064A\u0627\u0646",
    event: "\u062D\u062F\u062B",
    EventAddress: "\u0639\u0646\u0648\u0627\u0646_\u062D\u062F\u062B",
    slot: "\u0641\u062A\u062D\u0629",
    pattern: "\u0646\u0645\u0637",
    Map: "\u062E\u0631\u064A\u0637\u0629",
    secret: "\u0633\u0631",
    SExpr: "\u062A\u0639\u0628\u064A\u0631_\u0631\u0645\u0632\u064A"
  },
  units: {
    ms: "\u0645\u0644\u0644\u064A_\u062B\u0627\u0646\u064A\u0629",
    s: "\u062B\u0627\u0646\u064A\u0629",
    m: "\u062F\u0642\u064A\u0642\u0629",
    h: "\u0633\u0627\u0639\u0629",
    d: "\u064A\u0648\u0645"
  },
  literals: {
    true: "\u0635\u062D\u064A\u062D",
    false: "\u062E\u0627\u0637\u0626",
    null: "\u0639\u062F\u0645"
  },
  reservedEvents: {
    INIT: "\u062A\u0647\u064A\u0626\u0629"
  },
  orb: {
    _metadata: "\u0628\u064A\u0627\u0646\u0627\u062A_\u0648\u0635\u0641\u064A\u0629",
    _resolved: "_\u0645\u062D\u0644\u0648\u0644",
    access_waivers: "\u0625\u0639\u0641\u0627\u0621\u0627\u062A_\u0627\u0644\u0648\u0635\u0648\u0644",
    alias: "\u0627\u0633\u0645_\u0645\u0633\u062A\u0639\u0627\u0631",
    animations: "\u0631\u0633\u0648\u0645_\u0645\u062A\u062D\u0631\u0643\u0629",
    appliesTo: "\u064A\u0646\u0637\u0628\u0642_\u0639\u0644\u0649",
    as: "\u0643\u0640",
    aspect: "\u0646\u0633\u0628\u0629",
    assetRef: "\u0645\u0631\u062C\u0639_\u0627\u0644\u0623\u0635\u0644",
    at: "\u0641\u064A",
    auth: "\u0645\u0635\u0627\u062F\u0642\u0629",
    auxiliaryEntities: "\u0643\u064A\u0627\u0646\u0627\u062A_\u0645\u0633\u0627\u0639\u062F\u0629",
    awaitRender: "\u0639\u0631\u0636_\u0645\u0646\u062A\u0638\u0631",
    bakedName: "\u0627\u0633\u0645_\u0645\u062E\u0628\u0648\u0632",
    baseUrl: "\u0631\u0627\u0628\u0637_\u0623\u0633\u0627\u0633\u064A",
    behavior: "\u0633\u0644\u0648\u0643",
    capabilities: "\u0642\u062F\u0631\u0627\u062A",
    cardinality: "\u062A\u0639\u062F\u062F\u064A\u0629",
    category: "\u0641\u0626\u0629",
    className: "\u0627\u0633\u0645_\u0627\u0644\u0641\u0626\u0629",
    classification: "\u062A\u0635\u0646\u064A\u0641",
    collection: "\u0645\u062C\u0645\u0648\u0639\u0629",
    color: "\u0644\u0648\u0646",
    colors: "\u0623\u0644\u0648\u0627\u0646",
    columns: "\u0623\u0639\u0645\u062F\u0629",
    component: "\u0645\u0643\u0648\u0646",
    config: "\u062A\u0643\u0648\u064A\u0646",
    configProvenance: "\u0645\u0635\u062F\u0631_\u0627\u0644\u062A\u0643\u0648\u064A\u0646",
    container: "\u062D\u0627\u0648\u064A\u0629",
    content: "\u0645\u062D\u062A\u0648\u0649",
    create: "\u0625\u0646\u0634\u0627\u0621",
    create_policy: "\u0633\u064A\u0627\u0633\u0629_\u0627\u0644\u0625\u0646\u0634\u0627\u0621",
    createdAt: "\u062A\u0627\u0631\u064A\u062E_\u0627\u0644\u0625\u0646\u0634\u0627\u0621",
    curName: "\u0627\u0644\u0627\u0633\u0645_\u0627\u0644\u062D\u0627\u0644\u064A",
    customPatterns: "\u0623\u0646\u0645\u0627\u0637_\u0645\u062E\u0635\u0635\u0629",
    darkMode: "\u0627\u0644\u0648\u0636\u0639_\u0627\u0644\u062F\u0627\u0643\u0646",
    dataEntities: "\u0643\u064A\u0627\u0646\u0627\u062A_\u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A",
    default: "\u0627\u0641\u062A\u0631\u0627\u0636\u064A",
    definerKnob: "\u0645\u0642\u0628\u0636_\u0627\u0644\u0645\u064F\u0639\u0631\u0650\u0651\u0641",
    delayMs: "\u062A\u0623\u062E\u064A\u0631_\u0645\u0644\u0644\u064A_\u062B\u0627\u0646\u064A\u0629",
    delete: "\u062D\u0630\u0641",
    delete_policy: "\u0633\u064A\u0627\u0633\u0629_\u0627\u0644\u062D\u0630\u0641",
    density: "\u0643\u062B\u0627\u0641\u0629",
    description: "\u0648\u0635\u0641",
    description_visual_prompt: "\u0648\u0635\u0641_\u0627\u0644\u0645\u0648\u062C\u0647_\u0627\u0644\u0628\u0635\u0631\u064A",
    design: "\u062A\u0635\u0645\u064A\u0645",
    designTokens: "\u0631\u0645\u0648\u0632_\u0627\u0644\u062A\u0635\u0645\u064A\u0645",
    detailPattern: "\u0646\u0645\u0637_\u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644",
    device: "\u062C\u0647\u0627\u0632",
    dimension: "\u0628\u064F\u0639\u062F",
    displayName: "\u0627\u0633\u0645_\u0645\u0639\u0631\u0648\u0636",
    domainContext: "\u0633\u064A\u0627\u0642_\u0627\u0644\u0645\u062C\u0627\u0644",
    effectRow: "\u0635\u0641_\u0627\u0644\u062A\u0623\u062B\u064A\u0631",
    effects: "\u062A\u0623\u062B\u064A\u0631\u0627\u062A",
    elevation: "\u0627\u0631\u062A\u0641\u0627\u0639",
    emitIds: "\u0645\u0639\u0631\u0641\u0627\u062A_\u0627\u0644\u0628\u062B",
    emits: "\u064A\u0635\u062F\u0631",
    emitsScope: "\u0646\u0637\u0627\u0642_\u0627\u0644\u0625\u0635\u062F\u0627\u0631",
    enabled: "\u0645\u064F\u0641\u0639\u0651\u0644",
    entities: "\u0643\u064A\u0627\u0646\u0627\u062A",
    entity: "\u0643\u064A\u0627\u0646",
    entityBindingDescription: "\u0648\u0635\u0641_\u0631\u0628\u0637_\u0627\u0644\u0643\u064A\u0627\u0646",
    entityBindingSynonyms: "\u0645\u0631\u0627\u062F\u0641\u0627\u062A_\u0631\u0628\u0637_\u0627\u0644\u0643\u064A\u0627\u0646",
    entityContract: "\u0639\u0642\u062F_\u0627\u0644\u0643\u064A\u0627\u0646",
    entityId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0643\u064A\u0627\u0646",
    entityRebindable: "\u0642\u0627\u0628\u0644_\u0644\u0625\u0639\u0627\u062F\u0629_\u0631\u0628\u0637_\u0627\u0644\u0643\u064A\u0627\u0646",
    entityRefIds: "\u0645\u0639\u0631\u0641\u0627\u062A_\u0645\u0631\u062C\u0639_\u0627\u0644\u0643\u064A\u0627\u0646",
    entries: "\u0625\u062F\u062E\u0627\u0644\u0627\u062A",
    env: "\u0628\u064A\u0626\u0629",
    equals: "\u064A\u0633\u0627\u0648\u064A",
    event: "\u062D\u062F\u062B",
    eventId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u062D\u062F\u062B",
    eventIds: "\u0645\u0639\u0631\u0641\u0627\u062A_\u0627\u0644\u062D\u062F\u062B",
    events: "\u0623\u062D\u062F\u0627\u062B",
    expects: "\u064A\u062A\u0648\u0642\u0639",
    exposes: "\u064A\u0639\u0631\u0636",
    extend: "\u0627\u0645\u062A\u062F\u0627\u062F",
    retype: "\u0625\u0639\u0627\u062F\u0629_\u0627\u0644\u0646\u0648\u0639",
    extends: "\u064A\u0645\u062A\u062F",
    extendsId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0627\u0645\u062A\u062F\u0627\u062F",
    external: "\u062E\u0627\u0631\u062C\u064A",
    field: "\u062D\u0642\u0644",
    fields: "\u062D\u0642\u0648\u0644",
    flowPattern: "\u0646\u0645\u0637_\u0627\u0644\u062A\u062F\u0641\u0642",
    foreignKey: "\u0645\u0641\u062A\u0627\u062D_\u0623\u062C\u0646\u0628\u064A",
    formPattern: "\u0646\u0645\u0637_\u0627\u0644\u0646\u0645\u0648\u0630\u062C",
    forwardedFrom: "\u0645\u0648\u062C\u0647_\u0645\u0646",
    from: "\u0645\u0646",
    geometry: "\u0647\u0646\u062F\u0633\u0629",
    guard: "\u062D\u0627\u0631\u0633",
    guards: "\u062D\u0631\u0627\u0633",
    headers: "\u062A\u0631\u0648\u064A\u0633\u0627\u062A",
    icon: "\u0623\u064A\u0642\u0648\u0646\u0629",
    iconography: "\u0623\u064A\u0642\u0648\u0646\u0627\u062A",
    id: "\u0645\u0639\u0631\u0641",
    identity: "\u0647\u0648\u064A\u0629",
    illustration: "\u0631\u0633\u0645_\u062A\u0648\u0636\u064A\u062D\u064A",
    inbound: "\u0648\u0627\u0631\u062F",
    initialEffects: "\u0627\u0644\u062A\u0623\u062B\u064A\u0631\u0627\u062A_\u0627\u0644\u0623\u0648\u0644\u064A\u0629",
    instances: "\u0646\u0633\u062E",
    interactive: "\u062A\u0641\u0627\u0639\u0644\u064A",
    interval: "\u0641\u0627\u0635\u0644_\u0632\u0645\u0646\u064A",
    intrinsic: "\u062C\u0648\u0647\u0631\u064A",
    isFinal: "\u0623\u062E\u064A\u0631",
    isInitial: "\u0623\u0648\u0644\u064A",
    isTerminal: "\u0646\u0647\u0627\u0626\u064A",
    item: "\u0639\u0646\u0635\u0631",
    items: "\u0639\u0646\u0627\u0635\u0631",
    key: "\u0645\u0641\u062A\u0627\u062D",
    keyName: "\u0627\u0633\u0645_\u0627\u0644\u0645\u0641\u062A\u0627\u062D",
    kind: "\u0635\u0646\u0641",
    knob: "\u0645\u0642\u0628\u0636",
    label: "\u062A\u0633\u0645\u064A\u0629",
    lazy: "\u0645\u0624\u062C\u0644",
    lazyPages: "\u0635\u0641\u062D\u0627\u062A_\u0645\u0624\u062C\u0644\u0629",
    ledger: "\u0633\u062C\u0644",
    linkedEntity: "\u0643\u064A\u0627\u0646_\u0645\u0631\u062A\u0628\u0637",
    linkedEntityId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0643\u064A\u0627\u0646_\u0627\u0644\u0645\u0631\u062A\u0628\u0637",
    listPattern: "\u0646\u0645\u0637_\u0627\u0644\u0642\u0627\u0626\u0645\u0629",
    listens: "\u064A\u0633\u062A\u0645\u0639",
    local: "\u0645\u062D\u0644\u064A",
    locales: "\u0644\u063A\u0627\u062A",
    location: "\u0645\u0648\u0642\u0639",
    max: "\u0623\u0642\u0635\u0649",
    maxAttempts: "\u0623\u0642\u0635\u0649_\u0639\u062F\u062F_\u0645\u062D\u0627\u0648\u0644\u0627\u062A",
    mergedFrom: "\u0645\u062F\u0645\u062C_\u0645\u0646",
    projectedFrom: "\u0645\u0633\u0642\u0637_\u0645\u0646",
    messageSources: "\u0645\u0635\u0627\u062F\u0631_\u0627\u0644\u0631\u0633\u0627\u0626\u0644",
    min: "\u0623\u062F\u0646\u0649",
    mock: "\u0648\u0647\u0645\u064A",
    motion: "\u062D\u0631\u0643\u0629",
    mounts: "\u062A\u0631\u0643\u064A\u0628\u0627\u062A",
    name: "\u0627\u0633\u0645",
    nestedForwardedFrom: "\u0645\u0648\u062C\u0647_\u0645\u0646_\u0645\u062A\u062F\u0627\u062E\u0644",
    omit: "\u0627\u0633\u062A\u0628\u0639\u0627\u062F",
    onDelete: "\u0639\u0646\u062F_\u0627\u0644\u062D\u0630\u0641",
    onEntry: "\u0639\u0646\u062F_\u0627\u0644\u062F\u062E\u0648\u0644",
    onExit: "\u0639\u0646\u062F_\u0627\u0644\u062E\u0631\u0648\u062C",
    only: "\u0641\u0642\u0637",
    orbRef: "\u0645\u0631\u062C\u0639_\u0627\u0644\u0645\u062F\u0627\u0631",
    orbital: "\u0645\u062F\u0627\u0631",
    orbitalId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0645\u062F\u0627\u0631",
    orbitals: "\u0645\u062F\u0627\u0631\u0627\u062A",
    originalEvent: "\u0627\u0644\u062D\u062F\u062B_\u0627\u0644\u0623\u0635\u0644\u064A",
    originalName: "\u0627\u0644\u0627\u0633\u0645_\u0627\u0644\u0623\u0635\u0644\u064A",
    outbound: "\u0635\u0627\u062F\u0631",
    owner: "\u0645\u0627\u0644\u0643",
    pageIds: "\u0645\u0639\u0631\u0641\u0627\u062A_\u0627\u0644\u0635\u0641\u062D\u0629",
    pages: "\u0635\u0641\u062D\u0627\u062A",
    parent: "\u0623\u0628",
    path: "\u0645\u0633\u0627\u0631",
    pattern: "\u0646\u0645\u0637",
    patternPath: "\u0645\u0633\u0627\u0631_\u0627\u0644\u0646\u0645\u0637",
    payload: "\u062D\u0645\u0648\u0644\u0629",
    payloadEntity: "\u0643\u064A\u0627\u0646_\u0627\u0644\u062D\u0645\u0648\u0644\u0629",
    payloadFor: "\u062D\u0645\u0648\u0644\u0629_\u0644\u0623\u062C\u0644",
    payloadMapping: "\u062A\u062E\u0637\u064A\u0637_\u0627\u0644\u062D\u0645\u0648\u0644\u0629",
    payloadSchema: "\u0645\u062E\u0637\u0637_\u0627\u0644\u062D\u0645\u0648\u0644\u0629",
    persistence: "\u0627\u0633\u062A\u0645\u0631\u0627\u0631\u064A\u0629",
    personas: "\u0634\u062E\u0635\u064A\u0627\u062A",
    presentation: "\u0639\u0631\u0636_\u062A\u0642\u062F\u064A\u0645\u064A",
    primaryColor: "\u0627\u0644\u0644\u0648\u0646_\u0627\u0644\u0623\u0633\u0627\u0633\u064A",
    primaryEntity: "\u0627\u0644\u0643\u064A\u0627\u0646_\u0627\u0644\u0623\u0633\u0627\u0633\u064A",
    primaryKey: "\u0645\u0641\u062A\u0627\u062D_\u0623\u0633\u0627\u0633\u064A",
    priority: "\u0623\u0648\u0644\u0648\u064A\u0629",
    prop: "\u062E\u0627\u0635\u064A\u0629",
    properties: "\u062E\u0635\u0627\u0626\u0635",
    props: "\u062E\u0648\u0627\u0635",
    provides: "\u064A\u0648\u0641\u0631",
    publicDir: "\u0645\u062C\u0644\u062F_\u0639\u0627\u0645",
    radii: "\u0623\u0646\u0635\u0627\u0641_\u0623\u0642\u0637\u0627\u0631",
    read: "\u0642\u0631\u0627\u0621\u0629",
    read_policy: "\u0633\u064A\u0627\u0633\u0629_\u0627\u0644\u0642\u0631\u0627\u0621\u0629",
    reconnect: "\u0625\u0639\u0627\u062F\u0629_\u0627\u0644\u0627\u062A\u0635\u0627\u0644",
    ref: "\u0645\u0631\u062C\u0639",
    refId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0645\u0631\u062C\u0639",
    reference: "\u0645\u0631\u062C\u0639\u064A\u0629",
    relatedLinks: "\u0631\u0648\u0627\u0628\u0637_\u0630\u0627\u062A_\u0635\u0644\u0629",
    relation: "\u0639\u0644\u0627\u0642\u0629",
    renames: "\u0625\u0639\u0627\u062F\u0629_\u062A\u0633\u0645\u064A\u0629",
    request: "\u0637\u0644\u0628",
    requestFragment: "\u062C\u0632\u0621_\u0627\u0644\u0637\u0644\u0628",
    required: "\u0645\u0637\u0644\u0648\u0628",
    requiredFields: "\u0627\u0644\u062D\u0642\u0648\u0644_\u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
    requires: "\u064A\u062A\u0637\u0644\u0628",
    resolved: "\u0645\u062D\u0644\u0648\u0644",
    resource: "\u0645\u0648\u0631\u062F",
    role: "\u062F\u0648\u0631",
    roles: "\u0623\u062F\u0648\u0627\u0631",
    rows: "\u0635\u0641\u0648\u0641",
    runsInBackground: "\u064A\u0639\u0645\u0644_\u0641\u064A_\u0627\u0644\u062E\u0644\u0641\u064A\u0629",
    runtime: "\u0648\u0642\u062A_\u0627\u0644\u062A\u0634\u063A\u064A\u0644",
    schemaVersion: "\u0625\u0635\u062F\u0627\u0631_\u0627\u0644\u0645\u062E\u0637\u0637",
    scope: "\u0646\u0637\u0627\u0642",
    scopeOverridden: "\u0627\u0644\u0646\u0637\u0627\u0642_\u0645\u064F\u062A\u062C\u0627\u0648\u064E\u0632",
    secretEnv: "\u0628\u064A\u0626\u0629_\u0633\u0631\u064A\u0629",
    sections: "\u0623\u0642\u0633\u0627\u0645",
    semanticRole: "\u062F\u0648\u0631_\u062F\u0644\u0627\u0644\u064A",
    serverPath: "\u0645\u0633\u0627\u0631_\u0627\u0644\u062E\u0627\u062F\u0645",
    services: "\u062E\u062F\u0645\u0627\u062A",
    shadows: "\u0638\u0644\u0627\u0644",
    shape: "\u0634\u0643\u0644",
    shared: "\u0645\u0634\u062A\u0631\u0643",
    singleton: "\u0648\u062D\u064A\u062F",
    site: "\u0645\u0648\u0642\u0639_\u0627\u0644\u0648\u064A\u0628",
    skeleton: "\u0647\u064A\u0643\u0644",
    slot: "\u0641\u062A\u062D\u0629",
    slots: "\u0641\u062A\u062D\u0627\u062A",
    softDelete: "\u062D\u0630\u0641_\u0646\u0627\u0639\u0645",
    source: "\u0645\u0635\u062F\u0631",
    sourceBehavior: "\u0633\u0644\u0648\u0643_\u0627\u0644\u0645\u0635\u062F\u0631",
    sourceEntityDefinition: "\u062A\u0639\u0631\u064A\u0641_\u0643\u064A\u0627\u0646_\u0627\u0644\u0645\u0635\u062F\u0631",
    spacing: "\u062A\u0628\u0627\u0639\u062F",
    stack: "\u0643\u0648\u0645\u0629",
    stateMachine: "\u0622\u0644\u0629_\u062D\u0627\u0644\u0629",
    states: "\u062D\u0627\u0644\u0627\u062A",
    style: "\u0623\u0633\u0644\u0648\u0628",
    subDomain: "\u0645\u062C\u0627\u0644_\u0641\u0631\u0639\u064A",
    suggestedGuards: "\u0627\u0644\u062D\u0631\u0627\u0633_\u0627\u0644\u0645\u0642\u062A\u0631\u062D\u0648\u0646",
    summary: "\u0645\u0644\u062E\u0635",
    surface: "\u0633\u0637\u062D",
    surfaces: "\u0623\u0633\u0637\u062D",
    synonyms: "\u0645\u0631\u0627\u062F\u0641\u0627\u062A",
    target: "\u0647\u062F\u0641",
    targetView: "\u0627\u0644\u0639\u0631\u0636_\u0627\u0644\u0645\u0633\u062A\u0647\u062F\u0641",
    text: "\u0646\u0635",
    theme: "\u0645\u0638\u0647\u0631",
    tick: "\u0646\u0628\u0636\u0629",
    ticks: "\u0646\u0628\u0636\u0627\u062A",
    tier: "\u0645\u0633\u062A\u0648\u0649",
    timeBoundMs: "\u062D\u062F_\u0632\u0645\u0646\u064A_\u0645\u0644\u0644\u064A_\u062B\u0627\u0646\u064A\u0629",
    timeout: "\u0645\u0647\u0644\u0629",
    timestamps: "\u0637\u0648\u0627\u0628\u0639_\u0632\u0645\u0646\u064A\u0629",
    title: "\u0639\u0646\u0648\u0627\u0646",
    to: "\u0625\u0644\u0649",
    tokens: "\u0631\u0645\u0648\u0632",
    trait: "\u0633\u0645\u0629",
    traitEmbedIds: "\u0645\u0639\u0631\u0641\u0627\u062A_\u062A\u0636\u0645\u064A\u0646_\u0627\u0644\u0633\u0645\u0629",
    traitId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0633\u0645\u0629",
    traitName: "\u0627\u0633\u0645_\u0627\u0644\u0633\u0645\u0629",
    traitRefIds: "\u0645\u0639\u0631\u0641\u0627\u062A_\u0645\u0631\u062C\u0639_\u0627\u0644\u0633\u0645\u0629",
    traits: "\u0633\u0645\u0627\u062A",
    transition: "\u0627\u0646\u062A\u0642\u0627\u0644_\u062D\u0627\u0644\u0629",
    transitions: "\u0627\u0646\u062A\u0642\u0627\u0644\u0627\u062A",
    triggers: "\u0645\u062D\u0641\u0632\u0627\u062A",
    triggersId: "\u0645\u0639\u0631\u0641_\u0627\u0644\u0645\u062D\u0641\u0632\u0627\u062A",
    type: "\u0646\u0648\u0639",
    typeArgs: "\u0648\u0633\u0627\u0626\u0637_\u0627\u0644\u0646\u0648\u0639",
    typeParams: "\u0645\u0639\u0627\u0645\u0644\u0627\u062A_\u0627\u0644\u0646\u0648\u0639",
    typeScale: "\u0645\u0642\u064A\u0627\u0633_\u0627\u0644\u0637\u0628\u0627\u0639\u0629",
    typeWhen: "\u0646\u0648\u0639_\u0639\u0646\u062F\u0645\u0627",
    types: "\u0623\u0646\u0648\u0627\u0639",
    typography: "\u0637\u0628\u0627\u0639\u0629",
    ui: "\u0648\u0627\u062C\u0647\u0629",
    unique: "\u0641\u0631\u064A\u062F",
    update: "\u062A\u062D\u062F\u064A\u062B",
    update_policy: "\u0633\u064A\u0627\u0633\u0629_\u0627\u0644\u062A\u062D\u062F\u064A\u062B",
    updatedAt: "\u062A\u0627\u0631\u064A\u062E_\u0627\u0644\u062A\u062D\u062F\u064A\u062B",
    url: "\u0631\u0627\u0628\u0637",
    uses: "\u064A\u0633\u062A\u062E\u062F\u0645",
    uxHints: "\u062A\u0644\u0645\u064A\u062D\u0627\u062A_\u062A\u062C\u0631\u0628\u0629_\u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645",
    values: "\u0642\u064A\u0645",
    variant: "\u0645\u062A\u063A\u064A\u0631",
    variants: "\u0645\u062A\u063A\u064A\u0631\u0627\u062A",
    version: "\u0625\u0635\u062F\u0627\u0631",
    viewType: "\u0646\u0648\u0639_\u0627\u0644\u0639\u0631\u0636",
    visual_prompt: "\u0645\u0648\u062C\u0647_\u0628\u0635\u0631\u064A",
    vocabulary: "\u0645\u0641\u0631\u062F\u0627\u062A",
    seedMock: "\u0628\u0630\u0631_\u0648\u0647\u0645\u064A",
    localeInstances: "\u0646\u0633\u062E_\u0627\u0644\u0644\u063A\u0629",
    diagram: "\u0645\u062E\u0637\u0637",
    scene: "\u0645\u0634\u0647\u062F",
    access: "\u0648\u0635\u0648\u0644",
    indexing: "\u0641\u0647\u0631\u0633\u0629",
    origin: "\u0623\u0635\u0644",
    signIn: "\u062A\u0633\u062C\u064A\u0644_\u0627\u0644\u062F\u062E\u0648\u0644",
    sourcePage: "\u0627\u0644\u0635\u0641\u062D\u0629_\u0627\u0644\u0623\u0635\u0644\u064A\u0629",
    translationOf: "\u062A\u0631\u062C\u0645\u0629_\u0645\u0646",
    pageModifiers: "\u0645\u0639\u062F\u0644\u0627\u062A_\u0627\u0644\u0635\u0641\u062D\u0627\u062A"
  }
};

// src/i18n/sl.json
var sl_default = {
  meta: {
    code: "sl",
    name: "Slovenian",
    rtl: false
  },
  keywords: {
    app: "aplikacija",
    orbital: "orbitala",
    uses: "uporablja",
    expects: "pricakuje",
    entity: "entiteta",
    type: "tip",
    derived: "izpeljano",
    trait: "znacilnost",
    initial: "zacetno",
    state: "stanje",
    for: "za",
    page: "stran",
    as: "kot",
    theme: "tema",
    emits: "oddaja",
    listens: "poslusa",
    ticks: "utripi",
    config: "konfiguracija",
    when: "ko",
    with: "s",
    from: "iz",
    to: "do",
    every: "vsak",
    capabilities: "zmoznosti",
    match: "ujemaj",
    external: "zunanje",
    internal: "notranje",
    on: "ob",
    name: "ime",
    fields: "polja",
    events: "dogodki",
    event: "dogodek",
    emitsScope: "oddajaObseg",
    identity: "identiteta",
    pages: "strani",
    omit: "izloci",
    only: "samo",
    roles: "vloge",
    entities: "entitete",
    extend: "razsiritev",
    retype: "prevrsta",
    traits: "znacilnosti",
    mock: "lazno",
    lazy: "odlozeno",
    locales: "jeziki",
    public: "javno",
    instances: "primerki",
    access: "dostop",
    indexing: "indeksiranje",
    title: "naziv",
    description: "opis",
    origin: "poreklo",
    icon: "ikona",
    siteName: "imeStrani",
    signIn: "prijava",
    authenticated: "prijavljen",
    index: "indeksiraj",
    noindex: "brezIndeksa",
    version: "razlicica",
    translationOf: "prevodOd",
    label: "oznaka"
  },
  shapes: {
    Entity: "Entiteta",
    Trait: "Znacilnost",
    Page: "Stran",
    Orbital: "Orbitala",
    Event: "Dogodek"
  },
  tags: {
    persistent: "trajno",
    runtime: "izvajanje",
    shared: "skupno",
    local: "lokalno",
    identity: "identiteta",
    instance: "primerek",
    collection: "zbirka",
    background: "ozadno",
    mock: "lazno"
  },
  categories: {
    interaction: "interakcija",
    integration: "integracija",
    lifecycle: "zivljenjskiCikel",
    "game-core": "igra-jedro",
    "game-entity": "igra-entiteta",
    "game-ui": "igra-vmesnik"
  },
  capabilities: {
    read: "beri",
    write: "pisi",
    browse: "brskaj",
    create: "ustvari",
    update: "posodobi",
    delete: "izbrisi",
    notify: "obvesti",
    schedule: "razporedi",
    invoke: "sprozi",
    batch: "paket"
  },
  annotations: {
    key: "kljuc",
    unique: "edinstveno",
    intrinsic: "lastno",
    label: "oznaka",
    description: "opis",
    synonyms: "sopomenke",
    mock: "lazno",
    tier: "nivo",
    rebindable: "prevezljivo",
    read: "beri",
    create: "ustvari",
    update: "posodobi",
    delete: "izbrisi",
    icon: "ikona",
    roles: "vloge"
  },
  sigils: {
    entity: "entiteta",
    payload: "podatki",
    state: "stanje",
    now: "zdaj",
    config: "konfiguracija",
    user: "uporabnik",
    trait: "znacilnost",
    event: "dogodek",
    prevEvents: "prejsnjiDogodki",
    prevStates: "prejsnjaStanja",
    fromState: "izStanja",
    toState: "vStanje",
    locale: "jezik"
  },
  effects: {
    set: "nastavi",
    fetch: "pridobi",
    persist: "shrani",
    emit: "oddaj",
    navigate: "pojdi",
    "navigate-back": "pojdi-nazaj",
    "render-ui": "izrisi-vmesnik",
    notify: "obvesti",
    ref: "sklic",
    "call-service": "poklici-storitev",
    "cancel-call": "preklici-klic",
    invoke: "sprozi",
    do: "naredi",
    if: "ce",
    let: "naj",
    fn: "fn",
    lambda: "lambda",
    quote: "citat",
    quasiquote: "kvazicitat",
    unquote: "odcitat",
    match: "ujemaj",
    when: "ko"
  },
  types: {
    string: "niz",
    number: "stevilo",
    int: "celo",
    float: "plavajoce",
    bool: "logicna",
    boolean: "logicno",
    timestamp: "casovniZig",
    date: "datum",
    datetime: "datumCas",
    duration: "trajanje",
    money: "denar",
    scalar: "skalar",
    file: "datoteka",
    email: "eposta",
    url: "url",
    phone: "telefon",
    uuid: "uuid",
    image: "slika",
    icon: "ikona",
    asset: "sredstvo",
    component: "komponenta",
    object: "objekt",
    array: "seznam",
    enum: "nastevanje",
    relation: "relacija",
    node: "vozlisce",
    union: "unija",
    tuple: "terka",
    void: "prazno",
    null: "nic",
    any: "karkoli",
    trait: "znacilnost",
    orbital: "orbitala",
    behavior: "vedenje",
    entity: "entiteta",
    event: "dogodek",
    EventAddress: "NaslovDogodka",
    slot: "reza",
    pattern: "vzorec",
    Map: "Mapa",
    secret: "skrivnost",
    SExpr: "SIzraz"
  },
  units: {
    ms: "ms",
    s: "sek",
    m: "min",
    h: "ura",
    d: "dan"
  },
  literals: {
    true: "drzi",
    false: "nedrzi",
    null: "nic"
  },
  reservedEvents: {
    INIT: "ZACETEK"
  },
  orb: {
    _metadata: "_metapodatki",
    _resolved: "_razresen",
    access_waivers: "dostop_izjeme",
    alias: "vzdevek",
    animations: "animacije",
    appliesTo: "veljaZa",
    as: "kot",
    aspect: "razmerje",
    assetRef: "sredstvoSklic",
    at: "pri",
    auth: "avtentikacija",
    auxiliaryEntities: "pomozneEntitete",
    awaitRender: "pricakovanIzris",
    bakedName: "zapecenoIme",
    baseUrl: "osnovaUrl",
    behavior: "vedenje",
    capabilities: "zmoznosti",
    cardinality: "kardinalnost",
    category: "kategorija",
    className: "imeRazreda",
    classification: "klasifikacija",
    collection: "zbirka",
    color: "barva",
    colors: "barve",
    columns: "stolpci",
    component: "komponenta",
    config: "konfiguracija",
    configProvenance: "konfiguracijaIzvor",
    container: "vsebnik",
    content: "vsebina",
    create: "ustvari",
    create_policy: "ustvari_politika",
    createdAt: "ustvarjenoPri",
    curName: "trenutnoIme",
    customPatterns: "lastniVzorci",
    darkMode: "temniNacin",
    dataEntities: "podatkiEntitete",
    default: "privzeto",
    definerKnob: "definitorGumbek",
    delayMs: "zakasnitevMs",
    delete: "izbrisi",
    delete_policy: "izbrisi_politika",
    density: "gostota",
    description: "opis",
    description_visual_prompt: "opis_vizualno_poziv",
    design: "oblikovanje",
    designTokens: "oblikovanjeZetoni",
    detailPattern: "vzorecPodrobnosti",
    device: "naprava",
    dimension: "dimenzija",
    displayName: "prikaznoIme",
    domainContext: "domenaKontekst",
    effectRow: "ucinekVrstica",
    effects: "ucinki",
    elevation: "dvig",
    emitIds: "oddajIdji",
    emits: "oddaja",
    emitsScope: "oddajaObseg",
    enabled: "omogoceno",
    entities: "entitete",
    entity: "entiteta",
    entityBindingDescription: "entitetaVezavaOpis",
    entityBindingSynonyms: "entitetaVezavaSopomenke",
    entityContract: "entitetaPogodba",
    entityId: "entitetaId",
    entityRebindable: "entitetaPrevezljivo",
    entityRefIds: "entitetaSklicIdji",
    entries: "vnosi",
    env: "okolje",
    equals: "enako",
    event: "dogodek",
    eventId: "dogodekId",
    eventIds: "dogodekIdji",
    events: "dogodki",
    expects: "pricakuje",
    exposes: "izpostavlja",
    extend: "razsiritev",
    retype: "prevrsta",
    extends: "razsiri",
    extendsId: "razsiriId",
    external: "zunanje",
    field: "polje",
    fields: "polja",
    flowPattern: "vzorecPoteka",
    foreignKey: "tujiKljuc",
    formPattern: "vzorecObrazca",
    forwardedFrom: "posredovanoOd",
    from: "iz",
    geometry: "geometrija",
    guard: "strazar",
    guards: "strazarji",
    headers: "glave",
    icon: "ikona",
    iconography: "ikonografija",
    id: "id",
    identity: "identiteta",
    illustration: "ilustracija",
    inbound: "dohodno",
    initialEffects: "zacetnoUcinki",
    instances: "primerki",
    interactive: "interaktivno",
    interval: "interval",
    intrinsic: "lastno",
    isFinal: "jeKoncno",
    isInitial: "jeZacetno",
    isTerminal: "jeTerminalno",
    item: "element",
    items: "elementi",
    key: "kljuc",
    keyName: "imeKljuca",
    kind: "vrsta",
    knob: "gumbek",
    label: "oznaka",
    lazy: "odlozeno",
    lazyPages: "odlozeneStrani",
    ledger: "knjiga",
    linkedEntity: "povezanaEntiteta",
    linkedEntityId: "povezanaEntitetaId",
    listPattern: "vzorecSeznama",
    listens: "poslusa",
    local: "lokalno",
    locales: "jeziki",
    location: "lokacija",
    max: "najvec",
    maxAttempts: "najvecPoskusov",
    mergedFrom: "zdruzenoIz",
    projectedFrom: "projiciranoIz",
    messageSources: "viriSporocil",
    min: "najmanj",
    mock: "lazno",
    motion: "gibanje",
    mounts: "priklopi",
    name: "ime",
    nestedForwardedFrom: "gnezdenoPosredovanoOd",
    omit: "izloci",
    onDelete: "obIzbrisi",
    onEntry: "obVnos",
    onExit: "obIzhod",
    only: "samo",
    orbRef: "sklicOrb",
    orbital: "orbitala",
    orbitalId: "orbitalaId",
    orbitals: "orbitale",
    originalEvent: "izvirniDogodek",
    originalName: "izvirnoIme",
    outbound: "odhodno",
    owner: "lastnik",
    pageIds: "stranIdji",
    pages: "strani",
    parent: "stars",
    path: "pot",
    pattern: "vzorec",
    patternPath: "potVzorca",
    payload: "podatki",
    payloadEntity: "podatkiEntiteta",
    payloadFor: "podatkiZa",
    payloadMapping: "podatkiPreslikava",
    payloadSchema: "podatkiShema",
    persistence: "trajnost",
    personas: "osebe",
    presentation: "predstavitev",
    primaryColor: "primarnaBarva",
    primaryEntity: "primarnaEntiteta",
    primaryKey: "primarniKljuc",
    priority: "prioriteta",
    prop: "rekvizit",
    properties: "lastnosti",
    props: "rekviziti",
    provides: "zagotavlja",
    publicDir: "javnaMapa",
    radii: "polmeri",
    read: "beri",
    read_policy: "beri_politika",
    reconnect: "ponovnaPovezava",
    ref: "sklic",
    refId: "sklicId",
    reference: "referenca",
    relatedLinks: "povezanePovezave",
    relation: "relacija",
    renames: "preimenovanja",
    request: "zahtevek",
    requestFragment: "zahtevekFragment",
    required: "obvezno",
    requiredFields: "obveznaPolja",
    requires: "zahteva",
    resolved: "razresen",
    resource: "vir",
    role: "vloga",
    roles: "vloge",
    rows: "vrstice",
    runsInBackground: "teceVOzadju",
    runtime: "izvajanje",
    schemaVersion: "shemaRazlicica",
    scope: "obseg",
    scopeOverridden: "obsegPreglasen",
    secretEnv: "skrivnostOkolje",
    sections: "odseki",
    semanticRole: "semanticnaVloga",
    serverPath: "streznikPot",
    services: "storitve",
    shadows: "sence",
    shape: "oblika",
    shared: "skupno",
    singleton: "edinec",
    site: "mesto",
    skeleton: "ogrodje",
    slot: "reza",
    slots: "reze",
    softDelete: "mehkoIzbrisi",
    source: "izvor",
    sourceBehavior: "izvorVedenje",
    sourceEntityDefinition: "izvorEntitetaDefinicija",
    spacing: "razmik",
    stack: "sklad",
    stateMachine: "strojStanj",
    states: "stanja",
    style: "slog",
    subDomain: "poddomena",
    suggestedGuards: "predlaganoStrazarji",
    summary: "povzetek",
    surface: "povrsina",
    surfaces: "povrsine",
    synonyms: "sopomenke",
    target: "cilj",
    targetView: "ciljPogled",
    text: "besedilo",
    theme: "tema",
    tick: "utrip",
    ticks: "utripi",
    tier: "nivo",
    timeBoundMs: "casMejaMs",
    timeout: "casovnaOmejitev",
    timestamps: "casovniZigi",
    title: "naziv",
    to: "do",
    tokens: "zetoni",
    trait: "znacilnost",
    traitEmbedIds: "znacilnostVgraditevIdji",
    traitId: "znacilnostId",
    traitName: "znacilnostIme",
    traitRefIds: "znacilnostSklicIdji",
    traits: "znacilnosti",
    transition: "prehod",
    transitions: "prehodi",
    triggers: "sprozilci",
    triggersId: "sprozilciId",
    type: "tip",
    typeArgs: "tipArgumenti",
    typeParams: "tipParametri",
    typeScale: "tipMerilo",
    typeWhen: "tipKo",
    types: "tipi",
    typography: "tipografija",
    ui: "vmesnik",
    unique: "edinstveno",
    update: "posodobi",
    update_policy: "posodobi_politika",
    updatedAt: "posodobljenoPri",
    url: "url",
    uses: "uporablja",
    uxHints: "uxNamigi",
    values: "vrednosti",
    variant: "varianta",
    variants: "variante",
    version: "razlicica",
    viewType: "tipPogleda",
    visual_prompt: "vizualno_poziv",
    vocabulary: "besednjak",
    seedMock: "lazniZacetek",
    localeInstances: "jezikovniPrimerki",
    diagram: "diagram",
    scene: "prizor",
    access: "dostop",
    indexing: "indeksiranje",
    origin: "poreklo",
    signIn: "prijava",
    sourcePage: "izvornaStran",
    translationOf: "prevodOd",
    pageModifiers: "modifikatorjiStrani"
  }
};

// src/i18n/lolo-lexer.ts
var IDENT_START = /[\p{L}_]/u;
var IDENT_CONTINUE = /[\p{L}\p{N}_/-]/u;
var SIGIL_CONTINUE = /[\p{L}\p{N}_.]/u;
var DIGIT = /[0-9]/;
function lexLolo(source) {
  const tokens = [];
  let i = 0;
  const n = source.length;
  const push = (kind, start, end, text, textStart) => {
    tokens.push({
      kind,
      start,
      end,
      text: text ?? source.slice(start, end),
      textStart: textStart ?? start
    });
  };
  while (i < n) {
    const c = source[i];
    if (c === " " || c === "	" || c === "\r" || c === "\n") {
      i++;
      continue;
    }
    if (c === "#") {
      const start2 = i;
      if (source[i + 1] === "=") {
        const close = source.indexOf("=#", i + 2);
        i = close === -1 ? n : close + 2;
      } else {
        const nl = source.indexOf("\n", i);
        i = nl === -1 ? n : nl;
      }
      push("comment", start2, i);
      continue;
    }
    if (c === ";" && source[i + 1] === ";") {
      const start2 = i;
      const nl = source.indexOf("\n", i);
      i = nl === -1 ? n : nl;
      push("comment", start2, i);
      continue;
    }
    if (c === '"') {
      const start2 = i;
      i++;
      while (i < n) {
        if (source[i] === "\\") {
          i += 2;
          continue;
        }
        if (source[i] === '"') {
          i++;
          break;
        }
        i++;
      }
      push("string", start2, Math.min(i, n));
      continue;
    }
    if (c === "@") {
      const start2 = i;
      i++;
      if (source[i] === "(") {
        i++;
        push("punct", start2, i);
        continue;
      }
      const nameStart = i;
      while (i < n && SIGIL_CONTINUE.test(source[i])) i++;
      push("sigil", start2, i, source.slice(nameStart, i), nameStart);
      continue;
    }
    if (c === "?") {
      const start2 = i;
      i++;
      if (i < n && IDENT_START.test(source[i])) {
        const nameStart = i;
        while (i < n && SIGIL_CONTINUE.test(source[i])) i++;
        push("payload-sigil", start2, i, source.slice(nameStart, i), nameStart);
      } else {
        push("payload-sigil", start2, i, "", i);
      }
      continue;
    }
    if (c === "-") {
      const start2 = i;
      if (source[i + 1] === ">") {
        i += 2;
        push("punct", start2, i);
      } else if (source[i + 1] === "-") {
        i += 2;
        push("punct", start2, i);
      } else if (source[i + 1] !== void 0 && DIGIT.test(source[i + 1])) {
        i++;
        while (i < n && (DIGIT.test(source[i]) || source[i] === ".")) i++;
        push("number", start2, i);
      } else {
        i++;
        push("identifier", start2, i);
      }
      continue;
    }
    if (DIGIT.test(c)) {
      const start2 = i;
      while (i < n && (DIGIT.test(source[i]) || source[i] === ".")) i++;
      push("number", start2, i);
      continue;
    }
    if (c === "=" || c === "!" || c === "<" || c === ">") {
      const start2 = i;
      i++;
      if (source[i] === "=") i++;
      const two = i - start2 === 2;
      push(two ? "identifier" : "punct", start2, i);
      continue;
    }
    if (c === "+" || c === "*" || c === "/" || c === "%") {
      const start2 = i;
      i++;
      push("identifier", start2, i);
      continue;
    }
    if (IDENT_START.test(c)) {
      const start2 = i;
      while (i < n && IDENT_CONTINUE.test(source[i])) i++;
      const text = source.slice(start2, i);
      push(text === "true" || text === "false" || text === "null" ? "literal" : "identifier", start2, i, text);
      continue;
    }
    const start = i;
    i++;
    if (c === ":" && source[i] === ":") i++;
    push("punct", start, i);
  }
  return tokens;
}

// src/i18n/localize.ts
var LOLO_SECTIONS = [
  "keywords",
  "shapes",
  "tags",
  "categories",
  "capabilities",
  "annotations",
  "sigils",
  "effects",
  "types",
  "units",
  "literals",
  "reservedEvents"
];
var CANON_ANY_ORDER = [
  "keywords",
  "shapes",
  "tags",
  "categories",
  "capabilities",
  "annotations",
  "sigils",
  "types",
  "units",
  "literals",
  "reservedEvents"
];
function forward(lang, sections, resolutionOrder, operators) {
  const canon = (native) => {
    for (const section of resolutionOrder) {
      for (const [english, value] of Object.entries(coreTables[lang][section])) {
        if (value === native) return english;
      }
    }
    if (operators) {
      for (const [english, value] of Object.entries(operators.operators)) {
        if (value === native) return english;
      }
    }
    return void 0;
  };
  const map = /* @__PURE__ */ new Map();
  const add = (english, native) => {
    if (canon(native) !== english) return;
    map.set(english, native);
  };
  for (const section of sections) {
    for (const [english, native] of Object.entries(coreTables[lang][section])) {
      add(english, native);
    }
  }
  if (operators) {
    for (const [english, native] of Object.entries(operators.operators)) {
      add(english, native);
    }
  }
  return map;
}
function localizeMap(lang, operators) {
  return forward(lang, LOLO_SECTIONS, CANON_ANY_ORDER, operators);
}
function isWordShaped(english) {
  return /^[A-Za-z_][A-Za-z0-9_/-]*$/.test(english);
}
var FIELD_BLOCK_HEADS = /* @__PURE__ */ new Set(["entity", "type", "config", "params", "variants", "tokens", "event"]);
var EVENT_BLOCK_HEADS = /* @__PURE__ */ new Set(["listens", "emits", "events"]);
var NAME_INTRODUCING = /* @__PURE__ */ new Set([
  "app",
  "orbital",
  "entity",
  "trait",
  "state",
  "page",
  "type",
  "event",
  "uses",
  "theme",
  "as",
  "extend"
]);
var NAME_PUNCT = /* @__PURE__ */ new Set(["->", "-->", "\u2192", ".", "--"]);
function localizeLoloSource(source, lang, options = {}) {
  if (lang === "en") return source;
  const { operators } = options;
  const keywords = forward(lang, ["keywords"], CANON_ANY_ORDER);
  const shapes = forward(lang, ["shapes"], CANON_ANY_ORDER);
  const types = forward(lang, ["types"], ["types"]);
  const modifiers = forward(lang, ["tags", "categories", "capabilities", "keywords"], CANON_ANY_ORDER);
  const heads = forward(lang, ["effects"], ["effects"], operators);
  const sigils = forward(lang, ["sigils", "annotations"], ["sigils", "annotations"]);
  const bindingSigils = forward(lang, ["sigils"], ["sigils", "annotations"]);
  const literals = forward(lang, ["literals"], ["literals"]);
  const units = forward(lang, ["units"], ["units"]);
  const reservedEvents = forward(lang, ["reservedEvents"], ["reservedEvents"]);
  const tokens = lexLolo(source);
  const edits = [];
  const stack = [
    {
      kind: "root",
      stmtHead: void 0,
      blockHead: void 0,
      afterColon: false,
      sawEquals: false,
      refBody: false,
      parenHead: void 0,
      binderSeen: false
    }
  ];
  const top = () => stack[stack.length - 1];
  let prev;
  const replace = (token, map) => {
    if (!isWordShaped(token.text)) return;
    const native = map.get(token.text);
    if (native === void 0 || native === token.text) return;
    edits.push({ start: token.textStart, end: token.end, text: native });
  };
  for (let index = 0; index < tokens.length; index++) {
    const token = tokens[index];
    if (token.kind === "comment") continue;
    const frame = top();
    const startsStatement = prev === void 0 || prev.text === "{" || prev.text === "}" || source.slice(prev.end, token.start).includes("\n");
    if (startsStatement) {
      frame.stmtHead = void 0;
      frame.afterColon = false;
      frame.sawEquals = false;
    }
    if (token.kind === "punct") {
      const text = source.slice(token.start, token.end);
      const child = (kind) => ({
        kind,
        stmtHead: void 0,
        blockHead: frame.stmtHead,
        afterColon: false,
        sawEquals: false,
        refBody: frame.refBody || frame.sawEquals,
        parenHead: void 0,
        binderSeen: false
      });
      if (text === "(" || text === "@(") {
        stack.push({ ...child(parenFrame(frame, prev)), blockHead: void 0 });
        if (top().kind === "binder-list") frame.binderSeen = true;
      } else if (text === "{") {
        stack.push(child(braceFrame(frame, prev)));
      } else if (text === "[") {
        stack.push(child(bracketFrame(frame, prev)));
      } else if (text === ")" || text === "}" || text === "]") {
        if (stack.length > 1) stack.pop();
        top().afterColon = false;
        top().sawEquals = false;
      } else if (text === ":") {
        frame.afterColon = true;
      } else if (text === "=") {
        frame.afterColon = false;
        frame.sawEquals = true;
      } else if (text === ",") {
        frame.afterColon = false;
        frame.sawEquals = false;
        frame.stmtHead = void 0;
      }
      prev = token;
      continue;
    }
    const opaque = frame.kind === "object" || frame.kind === "array" || frame.kind === "binder-list" || frame.kind === "binder-pair" && prev !== void 0 && prev.text === "(";
    if (token.kind === "sigil") {
      const dot = token.text.indexOf(".");
      const root = dot === -1 ? token.text : token.text.slice(0, dot);
      const native = (opaque || frame.kind === "paren" ? bindingSigils : sigils).get(root);
      if (native !== void 0 && native !== root) {
        edits.push({ start: token.textStart, end: token.textStart + root.length, text: native });
      }
      prev = token;
      continue;
    }
    if (token.kind === "payload-sigil" || token.kind === "string" || token.kind === "number") {
      prev = token;
      continue;
    }
    if (token.kind === "literal") {
      if (!opaque) replace(token, literals);
      prev = token;
      continue;
    }
    const prevText = prev === void 0 ? void 0 : source.slice(prev.start, prev.end);
    const afterName = prevText !== void 0 && (NAME_INTRODUCING.has(prevText) || NAME_PUNCT.has(prevText));
    if (token.text === "INIT" && !opaque && !frame.sawEquals) {
      replace(token, reservedEvents);
      if (frame.stmtHead === void 0) frame.stmtHead = token.text;
      prev = token;
      continue;
    }
    if (!opaque && !startsStatement && prev !== void 0 && prev.kind === "number") {
      replace(token, units);
      prev = token;
      continue;
    }
    switch (frame.kind) {
      case "paren": {
        if (prevText === "(") {
          frame.parenHead = token.text;
          replace(token, heads);
        }
        break;
      }
      case "binder-list":
      case "binder-pair":
        break;
      case "object":
      case "array":
        break;
      case "mods":
        if (!frame.afterColon) replace(token, modifiers);
        break;
      case "type-array":
        replace(token, types);
        break;
      case "block-fields": {
        if (frame.afterColon && !afterName) replace(token, types);
        break;
      }
      case "root":
      case "block-body": {
        if (!afterName) replace(token, frame.afterColon ? types : keywords);
        break;
      }
    }
    if ((frame.kind === "root" || frame.kind === "block-body") && prevText === "=") {
      replace(token, shapes);
    }
    if (frame.stmtHead === void 0) frame.stmtHead = token.text;
    prev = token;
  }
  return applyEdits(source, edits);
}
function parenFrame(frame, prev) {
  if (frame.kind === "binder-list") return "binder-pair";
  if (frame.kind === "paren" && !frame.binderSeen && prev !== void 0 && prev.text === frame.parenHead && (frame.parenHead === "let" || frame.parenHead === "fn" || frame.parenHead === "lambda")) {
    return "binder-list";
  }
  return "paren";
}
function braceFrame(frame, prev) {
  if (isExpression(frame.kind)) return "object";
  if (frame.kind === "block-fields" && prev !== void 0 && prev.text === "=") return "object";
  if (frame.refBody && frame.stmtHead === "config") return "object";
  if (frame.blockHead !== void 0 && EVENT_BLOCK_HEADS.has(frame.blockHead)) return "block-fields";
  if (frame.kind === "type-array") return "block-fields";
  if (frame.kind === "block-fields" && frame.afterColon) return "block-fields";
  const head = frame.stmtHead;
  return head !== void 0 && FIELD_BLOCK_HEADS.has(head) ? "block-fields" : "block-body";
}
function bracketFrame(frame, prev) {
  if (isExpression(frame.kind)) return "array";
  if (prev !== void 0 && prev.text === "=") return "array";
  if (frame.afterColon) return frame.kind === "block-fields" ? "type-array" : "array";
  return "mods";
}
function isExpression(kind) {
  return kind === "paren" || kind === "object" || kind === "array" || kind === "binder-list" || kind === "binder-pair";
}
function applyEdits(source, edits) {
  if (edits.length === 0) return source;
  edits.sort((a, b) => a.start - b.start);
  let out = "";
  let cursor = 0;
  for (const edit of edits) {
    if (edit.start < cursor) continue;
    out += source.slice(cursor, edit.start) + edit.text;
    cursor = edit.end;
  }
  return out + source.slice(cursor);
}
function localizeOrbValue(value, lang, options = {}) {
  if (lang === "en") return value;
  const orbKeys = forward(lang, ["orb"], ["orb"]);
  const types = forward(lang, ["types"], ["types"]);
  const tags = forward(lang, ["tags"], ["tags"]);
  const heads = forward(lang, ["effects"], ["effects"], options.operators);
  const sigils = forward(lang, ["sigils"], ["sigils", "annotations"]);
  const walk = (node, inExpression) => {
    if (Array.isArray(node)) {
      const head = node[0];
      const native = typeof head === "string" ? heads.get(head) : void 0;
      const isExpression2 = native !== void 0;
      const items = node.map(
        (child, index) => index === 0 && isExpression2 ? child : walk(child, inExpression || isExpression2)
      );
      if (native !== void 0) items[0] = native;
      return items;
    }
    if (node !== null && typeof node === "object") {
      const out = {};
      for (const [key, child] of Object.entries(node)) {
        const nativeKey = orbKeys.get(key) ?? key;
        let next = walk(child, inExpression);
        if (typeof next === "string") {
          if (key === "type" && !inExpression) next = types.get(next) ?? next;
          else if (key === "persistence") next = tags.get(next) ?? next;
        }
        out[nativeKey] = next;
      }
      return out;
    }
    if (typeof node === "string" && node.startsWith("@")) {
      const rest = node.slice(1);
      const dot = rest.indexOf(".");
      const root = dot === -1 ? rest : rest.slice(0, dot);
      const native = sigils.get(root);
      if (native === void 0) return node;
      return dot === -1 ? `@${native}` : `@${native}${rest.slice(dot)}`;
    }
    return node;
  };
  return walk(value, false);
}

// src/i18n/index.ts
var LANGUAGE_CODES = ["en", "ar", "sl"];
var RTL_LOCALES = ["ar", "he", "fa", "ur"];
function localeDirection(locale) {
  return RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
}
function pathLocale(path, locales) {
  const first = path.replace(/^\/+/, "").split("/")[0] ?? "";
  return locales.find((l) => l === first);
}
function localeOfPath(path, locales) {
  return pathLocale(path, locales) ?? locales[0];
}
function localeAlternates(pages, page, locales) {
  const indexable = (p) => p.access === "public" && p.indexing !== "noindex";
  const group = (p) => p.translationOf ?? p.sourcePage;
  const key = group(page);
  if (!indexable(page) || key === void 0) return void 0;
  const byLocale = [];
  for (const other of pages) {
    if (group(other) !== key || !indexable(other)) continue;
    const locale = localeOfPath(other.path, locales);
    if (locale === void 0 || byLocale.some((a) => a.locale === locale)) return void 0;
    byLocale.push({ locale, path: other.path });
  }
  if (byLocale.length < 2) return void 0;
  return byLocale.sort((a, b) => locales.indexOf(a.locale) - locales.indexOf(b.locale));
}
var I18N_SECTIONS = [
  "keywords",
  "shapes",
  "tags",
  "categories",
  "capabilities",
  "annotations",
  "sigils",
  "effects",
  "types",
  "units",
  "literals",
  "reservedEvents",
  "orb"
];
function isPlainObject(x) {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}
function isRawI18nTable(x) {
  return isPlainObject(x);
}
function isStringRecord(x) {
  if (!isPlainObject(x)) return false;
  for (const value of Object.values(x)) {
    if (typeof value !== "string") return false;
  }
  return true;
}
function isLanguageCode(x) {
  return x === "en" || x === "ar" || x === "sl";
}
function isI18nMeta(x) {
  if (!isPlainObject(x) || !("code" in x) || !("name" in x) || !("rtl" in x)) return false;
  return isLanguageCode(x.code) && typeof x.name === "string" && typeof x.rtl === "boolean";
}
function emptySections() {
  return {
    keywords: {},
    shapes: {},
    tags: {},
    categories: {},
    capabilities: {},
    annotations: {},
    sigils: {},
    effects: {},
    types: {},
    units: {},
    literals: {},
    reservedEvents: {},
    orb: {}
  };
}
function parseI18nTables(raw, source) {
  if (!isRawI18nTable(raw)) {
    throw new Error(`i18n table "${source}": expected a JSON object, got ${typeof raw}`);
  }
  if (!isI18nMeta(raw.meta)) {
    throw new Error(`i18n table "${source}": missing or malformed "meta" (expected { code, name, rtl })`);
  }
  const badSections = [];
  const sections = emptySections();
  for (const section of I18N_SECTIONS) {
    const value = raw[section];
    if (value === void 0) continue;
    if (!isStringRecord(value)) {
      badSections.push(section);
      continue;
    }
    sections[section] = value;
  }
  if (badSections.length > 0) {
    throw new Error(`i18n table "${source}": sections not Record<string,string>: ${badSections.join(", ")}`);
  }
  return { meta: raw.meta, ...sections };
}
function parseNotationKeys(raw, source) {
  if (!isPlainObject(raw) || !("keys" in raw) || !Array.isArray(raw.keys)) {
    throw new Error(`notation file "${source}": expected { keys: string[] }`);
  }
  const keys = [];
  for (const key of raw.keys) {
    if (typeof key !== "string") {
      throw new Error(`notation file "${source}": every key must be a string`);
    }
    keys.push(key);
  }
  return keys;
}
function parseOperatorTables(raw, source) {
  if (!isPlainObject(raw) || !("operators" in raw) || !isStringRecord(raw.operators)) {
    throw new Error(`operator table "${source}": expected { operators: Record<string,string> }`);
  }
  return { operators: raw.operators };
}
var ORB_NOTATION_KEYS = new Set(
  parseNotationKeys(orb_notation_default, "orb-notation.json")
);
var coreTables = {
  en: parseI18nTables(en_default, "en.json"),
  ar: parseI18nTables(ar_default, "ar.json"),
  sl: parseI18nTables(sl_default, "sl.json")
};
function getVocabulary(lang) {
  return coreTables[lang];
}
function aliasMap(lang, section, operators) {
  let table;
  if (section === "operators") {
    if (!operators) {
      throw new Error(`aliasMap("${lang}", "operators") requires an OperatorTables argument`);
    }
    table = operators.operators;
  } else {
    table = coreTables[lang][section];
  }
  const map = /* @__PURE__ */ new Map();
  for (const [english, native] of Object.entries(table)) {
    map.set(native, english);
  }
  return map;
}
var LOLO_FIRST_TOKEN_KEYWORDS = ["app", "uses", "expects", "type", "theme", "orbital"];
function languageOfLoloFirstToken(token) {
  const matches = LANGUAGE_CODES.filter(
    (lang) => LOLO_FIRST_TOKEN_KEYWORDS.some((kw) => coreTables[lang].keywords[kw] === token)
  );
  return matches.length === 1 ? matches[0] : void 0;
}
function languageOfOrbTopLevelKeys(keys) {
  const keySet = new Set(keys);
  const matches = LANGUAGE_CODES.filter((lang) => keySet.has(coreTables[lang].orb.orbitals));
  return matches.length === 1 ? matches[0] : void 0;
}
function checkI18nCoverage(input) {
  const problems = [];
  const { core, std, canonicalOperators } = input;
  const enTable = core.en;
  for (const lang of LANGUAGE_CODES) {
    for (const key of Object.keys(core[lang].orb)) {
      if (ORB_NOTATION_KEYS.has(key)) {
        problems.push({
          lang,
          section: "orb",
          kind: "notation-in-vocabulary",
          key,
          detail: "design-system token name \u2014 notation, not language vocabulary"
        });
      }
    }
  }
  for (const lang of LANGUAGE_CODES) {
    const table = core[lang];
    for (const section of I18N_SECTIONS) {
      const values = table[section];
      if (lang !== "en") {
        const enKeys = new Set(Object.keys(enTable[section]));
        const langKeys = new Set(Object.keys(values));
        for (const key of enKeys) {
          if (!langKeys.has(key)) {
            problems.push({ lang, section, kind: "missing", key });
          }
        }
        for (const key of langKeys) {
          if (!enKeys.has(key)) {
            problems.push({ lang, section, kind: "orphan", key });
          }
        }
      }
      for (const [key, value] of Object.entries(values)) {
        if (value === "") {
          problems.push({ lang, section, kind: "empty", key });
        }
      }
    }
  }
  for (const lang of LANGUAGE_CODES) {
    const table = core[lang];
    const occurrences = /* @__PURE__ */ new Map();
    for (const section of I18N_SECTIONS) {
      for (const [key, value] of Object.entries(table[section])) {
        const list = occurrences.get(key) ?? [];
        list.push({ section, value });
        occurrences.set(key, list);
      }
    }
    const opTable = std?.[lang]?.operators;
    if (opTable) {
      for (const [key, value] of Object.entries(opTable)) {
        const list = occurrences.get(key) ?? [];
        list.push({ section: "operators", value });
        occurrences.set(key, list);
      }
    }
    for (const [key, entries] of occurrences) {
      if (entries.length < 2) continue;
      const baseline = entries[0];
      for (let i = 1; i < entries.length; i++) {
        const entry = entries[i];
        if (entry.value !== baseline.value) {
          problems.push({
            lang,
            section: entry.section,
            kind: "inconsistent",
            key,
            detail: `"${key}" is "${baseline.value}" in ${baseline.section} but "${entry.value}" in ${entry.section}`
          });
        }
      }
    }
  }
  for (const lang of LANGUAGE_CODES) {
    const table = core[lang];
    for (const section of I18N_SECTIONS) {
      const byValue = /* @__PURE__ */ new Map();
      for (const [key, value] of Object.entries(table[section])) {
        const keys = byValue.get(value) ?? [];
        keys.push(key);
        byValue.set(value, keys);
      }
      for (const [value, keys] of byValue) {
        if (keys.length < 2) continue;
        for (let i = 1; i < keys.length; i++) {
          problems.push({
            lang,
            section,
            kind: "collision",
            key: keys[i],
            detail: `shares native value "${value}" with "${keys[0]}" in ${section}`
          });
        }
      }
    }
    const combined = /* @__PURE__ */ new Map();
    for (const [key, value] of Object.entries(table.effects)) {
      const list = combined.get(value) ?? [];
      list.push({ section: "effects", key });
      combined.set(value, list);
    }
    const opTable = std?.[lang]?.operators;
    if (opTable) {
      for (const [key, value] of Object.entries(opTable)) {
        const list = combined.get(value) ?? [];
        list.push({ section: "operators", key });
        combined.set(value, list);
      }
    }
    for (const [value, entries] of combined) {
      const hasEffects = entries.some((e) => e.section === "effects");
      const hasOperators = entries.some((e) => e.section === "operators");
      if (!(hasEffects && hasOperators)) continue;
      const first = entries[0];
      for (const entry of entries.slice(1)) {
        if (entry.key === first.key) continue;
        problems.push({
          lang,
          section: "effects\u222Aoperators",
          kind: "collision",
          key: entry.key,
          detail: `shares native value "${value}" with "${first.key}" (${first.section}) in effects\u222Aoperators`
        });
      }
    }
  }
  const selectorUses = /* @__PURE__ */ new Map();
  for (const lang of LANGUAGE_CODES) {
    const table = core[lang];
    const selectors = [
      ...LOLO_FIRST_TOKEN_KEYWORDS.map((key) => ({ section: "keywords", key, value: table.keywords[key] })),
      { section: "orb", key: "orbitals", value: table.orb.orbitals }
    ];
    for (const { section, key, value } of selectors) {
      if (value === void 0) continue;
      const uses = selectorUses.get(value) ?? [];
      uses.push({ lang, section, key });
      selectorUses.set(value, uses);
    }
  }
  for (const [value, uses] of selectorUses) {
    const langs = new Set(uses.map((u) => u.lang));
    if (langs.size < 2) continue;
    for (const use of uses) {
      const others = uses.filter((u) => u.lang !== use.lang).map((u) => `${u.lang}:${u.section}.${u.key}`);
      problems.push({
        lang: use.lang,
        section: use.section,
        kind: "selector-not-unique",
        key: use.key,
        detail: `${use.section}.${use.key} = "${value}" is also a language selector in: ${others.join(", ")}`
      });
    }
  }
  if (std && canonicalOperators) {
    const canonicalSet = new Set(canonicalOperators);
    for (const lang of LANGUAGE_CODES) {
      const opTable = std[lang]?.operators;
      if (!opTable) continue;
      const langKeys = new Set(Object.keys(opTable));
      for (const op of canonicalSet) {
        if (!langKeys.has(op)) {
          problems.push({ lang, section: "operators", kind: "operators-mismatch", key: op, detail: "missing from language operator table" });
        }
      }
      for (const op of langKeys) {
        if (!canonicalSet.has(op)) {
          problems.push({ lang, section: "operators", kind: "operators-mismatch", key: op, detail: "not present in canonical operator set" });
        }
      }
    }
  }
  return { ok: problems.length === 0, problems };
}
function reportIdenticalToEnglish(input) {
  const problems = [];
  const enTable = input.core.en;
  for (const lang of LANGUAGE_CODES) {
    if (lang === "en") continue;
    const table = input.core[lang];
    for (const section of I18N_SECTIONS) {
      for (const [key, value] of Object.entries(table[section])) {
        const enValue = enTable[section][key];
        if (enValue !== void 0 && value === enValue) {
          problems.push({ lang, section, kind: "identical-to-english", key, detail: `matches English value "${enValue}"` });
        }
      }
    }
  }
  return problems;
}

// themes/almadar.json
var almadar_default = {
  name: "almadar",
  displayName: "Almadar",
  tokens: {
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.75",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)"
    },
    geometry: {
      radiusContainer: "16px",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "squircle"
    },
    colors: {
      primary: "#0f766e",
      "primary-hover": "#115e59",
      "primary-foreground": "#ffffff",
      secondary: "#f1f5f9",
      "secondary-hover": "#e2e8f0",
      "secondary-foreground": "#0f172a",
      accent: "#0e7490",
      "accent-foreground": "#ffffff",
      muted: "#f1f5f9",
      "muted-foreground": "#607080",
      background: "#f8fafc",
      foreground: "#0f172a",
      card: "#ffffff",
      "card-foreground": "#0f172a",
      surface: "#f1f5f9",
      border: "rgba(20, 184, 166, 0.3)",
      input: "rgba(20, 184, 166, 0.2)",
      ring: "#14b8a6",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#ca8a04",
      "warning-foreground": "#000000",
      info: "#0369a1",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#0f766e",
      "series-2": "#2b92a3",
      "series-3": "#4f46e5",
      "series-4": "#a739eb",
      "series-5": "#b45309",
      "series-6": "#be185d",
      "series-7": "#65807a",
      "series-8": "#1e6db9",
      "diagram-ink": "#0f2a3a",
      "diagram-label": "#334155",
      "diagram-guide": "#8590a0",
      "diagram-axis": "#64748b",
      "diagram-grid": "#dbe7ee",
      "diagram-highlight": "#0e7490",
      "diagram-fill": "#0f766e"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "10px",
      lg: "14px",
      xl: "20px",
      full: "9999px"
    },
    shadows: {
      main: "0 4px 14px rgba(20, 184, 166, 0.12), 0 1px 3px rgba(0, 0, 0, 0.06)",
      sm: "0 2px 4px rgba(20, 184, 166, 0.08)",
      lg: "0 8px 30px rgba(20, 184, 166, 0.18), 0 4px 10px rgba(0, 0, 0, 0.06)",
      inner: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
      none: "none",
      hover: "0 12px 40px rgba(20, 184, 166, 0.2)",
      active: "0 2px 8px rgba(20, 184, 166, 0.15)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "icon-color": "#0d9488",
      "transition-fast": "150ms",
      "transition-normal": "250ms",
      "transition-slow": "400ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.02",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0",
      "active-scale": "0.98",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#14b8a6"
    },
    typeScale: {
      displayFamily: '"Inter", "Noto Sans Arabic", system-ui, sans-serif',
      fontImport: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap",
      headingWeight: "800",
      headingTracking: "-0.03em"
    },
    surface: {
      cardImage: "linear-gradient(160deg, rgba(20,184,166,0.07), rgba(20,184,166,0) 55%)",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3px",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.18",
      roughness: "0",
      glow: "0",
      shadow: "0 2px 8px rgba(20, 184, 166, 0.18)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "8px"
    },
    scene: {
      materialRoughness: "0.45",
      materialMetalness: "0.35",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.55",
      lightKey: "1.1",
      lightKeyColor: "#e6fffb",
      fog: "0.08"
    }
  },
  variants: {
    dark: {
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.2, 0, 0, 1)",
          spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        }
      },
      iconography: {
        family: "lucide",
        strokeWidth: "1.75",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)"
      },
      geometry: {
        radiusContainer: "16px",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)",
        cornerShape: "squircle"
      },
      colors: {
        primary: "#14b8a6",
        "primary-hover": "#2dd4bf",
        "primary-foreground": "#042f2e",
        secondary: "#1e293b",
        "secondary-hover": "#334155",
        "secondary-foreground": "#f1f5f9",
        accent: "#06b6d4",
        "accent-foreground": "#083344",
        muted: "#1e293b",
        "muted-foreground": "#94a3b8",
        background: "#0f172a",
        foreground: "#f1f5f9",
        card: "#1e293b",
        "card-foreground": "#f1f5f9",
        surface: "#1e293b",
        border: "rgba(20, 184, 166, 0.25)",
        input: "rgba(20, 184, 166, 0.3)",
        ring: "#14b8a6",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#38bdf8",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#1fa399",
        "series-2": "#1d90b8",
        "series-3": "#9584e4",
        "series-4": "#c56ddd",
        "series-5": "#e17019",
        "series-6": "#e26499",
        "series-7": "#7d9690",
        "series-8": "#608ac4",
        "diagram-ink": "#e2e8f0",
        "diagram-label": "#cbd5e1",
        "diagram-guide": "#7a8aa3",
        "diagram-axis": "#94a3b8",
        "diagram-grid": "#2f3e55",
        "diagram-highlight": "#06b6d4",
        "diagram-fill": "#14b8a6"
      },
      radii: {
        none: "0px",
        sm: "6px",
        md: "10px",
        lg: "14px",
        xl: "20px",
        full: "9999px"
      },
      shadows: {
        main: "0 4px 14px rgba(20, 184, 166, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)",
        sm: "0 2px 4px rgba(20, 184, 166, 0.1)",
        lg: "0 8px 30px rgba(20, 184, 166, 0.25), 0 4px 10px rgba(0, 0, 0, 0.1)",
        inner: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
        none: "none",
        hover: "0 12px 40px rgba(20, 184, 166, 0.35), 0 4px 12px rgba(6, 182, 212, 0.2)",
        active: "0 2px 8px rgba(20, 184, 166, 0.2)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "icon-color": "#14b8a6",
        "transition-fast": "150ms",
        "transition-normal": "250ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.02",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0",
        "active-scale": "0.98",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#14b8a6"
      }
    }
  }
};

// themes/almadar-heritage.json
var almadar_heritage_default = {
  name: "almadar-heritage",
  displayName: "Almadar Heritage",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", system-ui, sans-serif',
      bodyFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", system-ui, sans-serif',
      monoFamily: '"IBM Plex Mono", "IBM Plex Sans Arabic", ui-monospace, SFMono-Regular, Menlo, monospace',
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "-0.02em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        spring: "cubic-bezier(0.25, 0.8, 0.25, 1)"
      },
      shapes: {
        modalEnter: "scale(0.96)",
        modalExit: "scale(0.96)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.97)",
        popoverExit: "scale(0.97)",
        toastEnter: "translateY(-12px)",
        toastExit: "translateY(-12px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "none",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'><g fill='none' stroke='%2300767c' stroke-opacity='0.07' stroke-width='1'><path d='M24 0V48M0 24H48'/><path d='M24 18L30 24L24 30L18 24Z'/></g></svg>")`,
      pageImageSize: "48px 48px"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "6px",
      lg: "10px",
      xl: "14px",
      full: "9999px"
    },
    colors: {
      primary: "#00767c",
      "primary-hover": "#006369",
      "primary-foreground": "#ffffff",
      secondary: "#eef2f2",
      "secondary-hover": "#e2e9e9",
      "secondary-foreground": "#0f172a",
      accent: "#c9a227",
      "accent-foreground": "#0f172a",
      tertiary: "#b3412f",
      "tertiary-hover": "#9c3727",
      "tertiary-foreground": "#ffffff",
      "tertiary-muted": "rgba(179, 65, 47, 0.12)",
      muted: "#f1eee8",
      "muted-foreground": "#475569",
      background: "#faf8f5",
      foreground: "#0f172a",
      card: "#ffffff",
      "card-foreground": "#0f172a",
      surface: "#f3f0ea",
      border: "#e4e0d8",
      input: "#d6d1c6",
      ring: "#00b1b8",
      error: "#b42318",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#b45309",
      "warning-foreground": "#ffffff",
      info: "#00767c",
      "info-foreground": "#ffffff",
      scrim: "rgba(15, 23, 42, 0.55)",
      "series-1": "#00767c",
      "series-2": "#7d7d12",
      "series-3": "#215eac",
      "series-4": "#a4444c",
      "series-5": "#536225",
      "series-6": "#7a4a8a",
      "series-7": "#875c27",
      "series-8": "#6b7689",
      "diagram-ink": "#1f2d3d",
      "diagram-label": "#334155",
      "diagram-guide": "#8a8f98",
      "diagram-axis": "#64748b",
      "diagram-grid": "#e4e0d8",
      "diagram-highlight": "#a07a10",
      "diagram-fill": "#00767c"
    },
    shadows: {
      main: "0 6px 20px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.05)",
      sm: "0 1px 3px rgba(15, 23, 42, 0.06)",
      lg: "0 16px 40px rgba(15, 23, 42, 0.12)",
      inner: "inset 0 1px 3px rgba(31,63,143,0.12)",
      none: "none",
      hover: "0 18px 44px rgba(15, 23, 42, 0.12)",
      active: "0 1px 2px rgba(31,63,143,0.15)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Cairo", "Noto Naskh Arabic", "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#0f766e",
      "transition-fast": "150ms",
      "transition-normal": "250ms",
      "transition-slow": "400ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#14b8a6"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "2.5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.16",
      roughness: "0",
      glow: "0",
      shadow: "0 2px 6px rgba(15, 23, 42, 0.12)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.55",
      materialMetalness: "0.25",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.55",
      lightKey: "1",
      lightKeyColor: "#fff4d6",
      fog: "0.05"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Source Serif 4", "IBM Plex Sans Arabic", Georgia, serif',
        bodyFamily: '"Inter", "IBM Plex Sans Arabic", system-ui, sans-serif',
        monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
        headingWeight: "600",
        headingTransform: "none",
        headingTracking: "-0.02em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "700ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
          spring: "cubic-bezier(0.25, 0.8, 0.25, 1)"
        },
        shapes: {
          modalEnter: "scale(0.96)",
          modalExit: "scale(0.96)",
          pageEnter: "translateY(8px)",
          popoverEnter: "scale(0.97)",
          popoverExit: "scale(0.97)",
          toastEnter: "translateY(-12px)",
          toastExit: "translateY(-12px)"
        }
      },
      iconography: {
        family: "phosphor-duotone",
        strokeWidth: "1.5",
        defaultSize: "18px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "none",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'><g fill='none' stroke='%2300b1b8' stroke-opacity='0.06' stroke-width='1'><path d='M24 0V48M0 24H48'/><path d='M24 18L30 24L24 30L18 24Z'/></g></svg>")`,
        pageImageSize: "72px 72px"
      },
      radii: {
        none: "0px",
        sm: "6px",
        md: "10px",
        lg: "14px",
        xl: "20px",
        full: "9999px"
      },
      colors: {
        primary: "#00b1b8",
        "primary-hover": "#2cc7cd",
        "primary-foreground": "#0f172a",
        secondary: "#1e293b",
        "secondary-hover": "#283548",
        "secondary-foreground": "#f1f5f9",
        accent: "#d4b44a",
        "accent-foreground": "#0f172a",
        tertiary: "#e0715e",
        "tertiary-hover": "#e8887a",
        "tertiary-foreground": "#0f172a",
        "tertiary-muted": "rgba(224, 113, 94, 0.16)",
        muted: "#1a2436",
        "muted-foreground": "#9aa6b8",
        background: "#0f172a",
        foreground: "#f5f2ea",
        card: "#152036",
        "card-foreground": "#f5f2ea",
        surface: "#152036",
        border: "#2a3750",
        input: "#334360",
        ring: "#00b1b8",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#2cc7cd",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#0a98a0",
        "series-2": "#a9841b",
        "series-3": "#568acf",
        "series-4": "#b86a60",
        "series-5": "#7d9146",
        "series-6": "#a379b1",
        "series-7": "#b57b43",
        "series-8": "#758091",
        "diagram-ink": "#e6e1d4",
        "diagram-label": "#c9ced8",
        "diagram-guide": "#7f8aa0",
        "diagram-axis": "#98a2b6",
        "diagram-grid": "#2a3650",
        "diagram-highlight": "#c3ae41",
        "diagram-fill": "#00b1b8"
      },
      shadows: {
        main: "0 6px 20px rgba(0, 0, 0, 0.45)",
        sm: "0 1px 3px rgba(0, 0, 0, 0.35)",
        lg: "0 16px 40px rgba(0, 0, 0, 0.55)",
        inner: "inset 0 1px 3px rgba(0,0,0,0.4)",
        none: "none",
        hover: "0 18px 44px rgba(0, 0, 0, 0.55)",
        active: "0 1px 2px rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Cairo", "Noto Naskh Arabic", "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#2dd4bf",
        "transition-fast": "150ms",
        "transition-normal": "250ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#2dd4bf"
      }
    }
  }
};

// themes/almadar-website.json
var almadar_website_default = {
  name: "almadar-website",
  displayName: "Almadar Website",
  tokens: {
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.75",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    colors: {
      primary: "#0f766e",
      "primary-hover": "#115e59",
      "primary-foreground": "#ffffff",
      secondary: "#f1f5f9",
      "secondary-hover": "#e2e8f0",
      "secondary-foreground": "#0f172a",
      accent: "#c9a227",
      "accent-foreground": "#0f172a",
      muted: "#f1f5f9",
      "muted-foreground": "#5c6b80",
      background: "#ffffff",
      foreground: "#0f172a",
      card: "#ffffff",
      "card-foreground": "#0f172a",
      surface: "#f8fafc",
      border: "#e2e8f0",
      input: "#e2e8f0",
      ring: "#14b8a6",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#f59e0b",
      "warning-foreground": "#000000",
      info: "#1d4ed8",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#0f766e",
      "series-2": "#b8702a",
      "series-3": "#1f5dac",
      "series-4": "#9a3b3b",
      "series-5": "#6b7d2a",
      "series-6": "#7c4a8d",
      "series-7": "#a54803",
      "series-8": "#484a60",
      "diagram-ink": "#1e293b",
      "diagram-label": "#334155",
      "diagram-guide": "#8590a0",
      "diagram-axis": "#64748b",
      "diagram-grid": "#e2e8f0",
      "diagram-highlight": "#a67c0a",
      "diagram-fill": "#0f766e"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "8px",
      lg: "12px",
      xl: "16px",
      full: "9999px"
    },
    shadows: {
      main: "0 4px 14px rgba(15, 23, 42, 0.1), 0 1px 3px rgba(15, 23, 42, 0.06)",
      sm: "0 1px 3px rgba(15, 23, 42, 0.08)",
      lg: "0 10px 15px rgba(15, 23, 42, 0.15)",
      inner: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
      none: "none",
      hover: "0 12px 40px rgba(15, 23, 42, 0.15)",
      active: "0 2px 8px rgba(15, 23, 42, 0.1)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "icon-color": "#14b8a6",
      "transition-fast": "150ms",
      "transition-normal": "250ms",
      "transition-slow": "400ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.02",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0",
      "active-scale": "0.98",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#14b8a6"
    },
    typeScale: {
      displayFamily: '"Crimson Pro", Georgia, serif',
      fontImport: "https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap",
      headingWeight: "600",
      headingTracking: "-0.01em"
    },
    diagram: {
      strokeThin: "0.75px",
      strokeNormal: "1.5px",
      strokeBold: "2.25px",
      lineCap: "round",
      lineJoin: "round",
      dash: "5 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.14",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.7",
      materialMetalness: "0.1",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "0.9",
      lightKeyColor: "#fff6e0",
      fog: "0.05"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    }
  },
  variants: {
    dark: {
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.2, 0, 0, 1)",
          spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        }
      },
      iconography: {
        family: "lucide",
        strokeWidth: "1.75",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)"
      },
      geometry: {
        radiusContainer: "var(--radius-md)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      colors: {
        primary: "#14b8a6",
        "primary-hover": "#2dd4bf",
        "primary-foreground": "#042f2e",
        secondary: "#1e293b",
        "secondary-hover": "#334155",
        "secondary-foreground": "#f1f5f9",
        accent: "#d4b44a",
        "accent-foreground": "#0f172a",
        muted: "#1e293b",
        "muted-foreground": "#94a3b8",
        background: "#0f172a",
        foreground: "#f8fafc",
        card: "#1e293b",
        "card-foreground": "#f8fafc",
        surface: "#1e293b",
        border: "#334155",
        input: "#334155",
        ring: "#14b8a6",
        tertiary: "#c9a227",
        "tertiary-hover": "#d4b44a",
        "tertiary-foreground": "#0f172a",
        "tertiary-muted": "rgba(201, 162, 39, 0.15)",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#38bdf8",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#1fa399",
        "series-2": "#b9861a",
        "series-3": "#5e8ac3",
        "series-4": "#bb7178",
        "series-5": "#85993c",
        "series-6": "#a981b7",
        "series-7": "#d3782f",
        "series-8": "#767e95",
        "diagram-ink": "#e2e8f0",
        "diagram-label": "#cbd5e1",
        "diagram-guide": "#7d8aa0",
        "diagram-axis": "#94a3b8",
        "diagram-grid": "#334155",
        "diagram-highlight": "#c3ae41",
        "diagram-fill": "#14b8a6"
      },
      radii: {
        none: "0px",
        sm: "4px",
        md: "8px",
        lg: "12px",
        xl: "16px",
        full: "9999px"
      },
      shadows: {
        main: "0 4px 14px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3)",
        sm: "0 1px 3px rgba(0, 0, 0, 0.3)",
        lg: "0 10px 15px rgba(0, 0, 0, 0.5)",
        inner: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
        none: "none",
        hover: "0 12px 40px rgba(0, 0, 0, 0.5)",
        active: "0 2px 8px rgba(0, 0, 0, 0.3)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "icon-color": "#14b8a6",
        "transition-fast": "150ms",
        "transition-normal": "250ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.02",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0",
        "active-scale": "0.98",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#14b8a6"
      }
    }
  }
};

// themes/aqua.json
var aqua_default = {
  name: "aqua",
  displayName: "Aqua Gel",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "26px",
      buttonHeightMd: "34px",
      buttonHeightLg: "44px",
      inputHeightSm: "26px",
      inputHeightMd: "34px",
      inputHeightLg: "44px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Noto Sans", "Lucida Grande", system-ui, sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Noto Sans Mono", Monaco, ui-monospace, monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;700;800&family=Noto+Sans+Mono:wght@400;500&display=swap",
      headingWeight: "800",
      headingTransform: "none",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "11px",
          lineHeight: "16px"
        },
        sm: {
          size: "13px",
          lineHeight: "18px"
        },
        base: {
          size: "14px",
          lineHeight: "20px"
        },
        lg: {
          size: "16px",
          lineHeight: "24px"
        },
        xl: {
          size: "18px",
          lineHeight: "26px"
        },
        "2xl": {
          size: "22px",
          lineHeight: "28px"
        },
        "3xl": {
          size: "28px",
          lineHeight: "34px"
        },
        "4xl": {
          size: "34px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "44px",
          lineHeight: "48px"
        },
        "display-2": {
          size: "56px",
          lineHeight: "60px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "150ms",
        normal: "250ms",
        slow: "400ms",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.22, 1, 0.36, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.9)",
        modalExit: "scale(0.9)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.95)",
        popoverExit: "scale(0.95)",
        toastEnter: "translateY(-16px)",
        toastExit: "translateY(-16px)"
      }
    },
    iconography: {
      family: "phosphor-fill",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "var(--shadow-hover)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "11px",
      radiusInteractive: "var(--radius-full)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      cardImage: "linear-gradient(180deg, rgba(255,255,255,.65), rgba(255,255,255,0) 50%)",
      pageImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.5) 0px, rgba(255,255,255,.5) 2px, rgba(150,170,200,.10) 2px, rgba(150,170,200,.10) 4px)",
      pageImageSize: "auto"
    },
    colors: {
      primary: "#1f6fe0",
      "primary-hover": "#1859b8",
      "primary-foreground": "#ffffff",
      secondary: "#dde5f0",
      "secondary-hover": "#cbd6e6",
      "secondary-foreground": "#1c2a3a",
      accent: "#56657a",
      "accent-foreground": "#ffffff",
      muted: "#e4e9f1",
      "muted-foreground": "#4a5a70",
      background: "#e8ecf2",
      foreground: "#1c2a3a",
      card: "#f8fafd",
      "card-foreground": "#1c2a3a",
      surface: "#eef2f8",
      border: "#6b7f9c",
      input: "#8a9bb4",
      ring: "#3b8cff",
      "table-header": "#eef2f8",
      "table-border": "#6b7f9c",
      "table-row-hover": "#dfe6f1",
      "surface-hover": "#dfe6f1",
      "border-hover": "#1f6fe0",
      placeholder: "#6b7a90",
      error: "#c4271b",
      "error-foreground": "#ffffff",
      success: "#1b7a34",
      "success-foreground": "#ffffff",
      warning: "#f5b301",
      "warning-foreground": "#2a1c00",
      info: "#1f6fe0",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#1990fd",
      "series-2": "#c4631b",
      "series-3": "#1b7a8a",
      "series-4": "#8a3fc0",
      "series-5": "#63780a",
      "series-6": "#c4306b",
      "series-7": "#8a6a1b",
      "series-8": "#71738c",
      "diagram-ink": "#1c2a3a",
      "diagram-label": "#2a3a50",
      "diagram-guide": "#8896ab",
      "diagram-axis": "#56657a",
      "diagram-grid": "#d4dce8",
      "diagram-highlight": "#1f6fe0",
      "diagram-fill": "#1f6fe0"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "9px",
      lg: "11px",
      xl: "16px",
      full: "9999px"
    },
    shadows: {
      main: "0 4px 12px rgba(20,50,100,.28), 0 1px 2px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.7)",
      sm: "0 2px 5px rgba(20,50,100,.25), 0 1px 1px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.7)",
      lg: "0 10px 28px rgba(20,50,100,.35), 0 2px 4px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.7)",
      inner: "inset 0 2px 4px rgba(20,50,100,.3)",
      none: "none",
      hover: "0 4px 10px rgba(0,60,160,.45), 0 1px 2px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.85)",
      active: "inset 0 2px 5px rgba(0,30,90,.5)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Noto Sans", "Lucida Grande", system-ui, sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.5",
      "icon-color": "#1f6fe0",
      "transition-fast": "150ms",
      "transition-normal": "250ms",
      "transition-slow": "400ms",
      "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0px",
      "active-scale": "0.98",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "1px",
      "focus-ring-color": "#3b8cff"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "2.5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "5 3",
      dot: "1 3",
      fillStyle: "tint",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "0",
      shadow: "0 2px 3px rgba(20, 50, 100, 0.35)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "9px"
    },
    scene: {
      materialRoughness: "0.2",
      materialMetalness: "0.15",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "1.2",
      lightKeyColor: "#f2f8ff",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      typeScale: {
        displayFamily: '"Noto Sans", "Lucida Grande", system-ui, sans-serif',
        bodyFamily: "var(--font-family)",
        monoFamily: '"Noto Sans Mono", Monaco, ui-monospace, monospace',
        fontImport: "https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;700;800&family=Noto+Sans+Mono:wght@400;500&display=swap",
        headingWeight: "800",
        headingTransform: "none",
        headingTracking: "-0.01em",
        headingStyle: "normal",
        scale: {
          xs: {
            size: "11px",
            lineHeight: "16px"
          },
          sm: {
            size: "13px",
            lineHeight: "18px"
          },
          base: {
            size: "14px",
            lineHeight: "20px"
          },
          lg: {
            size: "16px",
            lineHeight: "24px"
          },
          xl: {
            size: "18px",
            lineHeight: "26px"
          },
          "2xl": {
            size: "22px",
            lineHeight: "28px"
          },
          "3xl": {
            size: "28px",
            lineHeight: "34px"
          },
          "4xl": {
            size: "34px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "44px",
            lineHeight: "48px"
          },
          "display-2": {
            size: "56px",
            lineHeight: "60px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      surface: {
        cardImage: "linear-gradient(180deg, rgba(255,255,255,.18), rgba(255,255,255,0) 50%)",
        pageImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.04) 0px, rgba(255,255,255,.04) 2px, rgba(0,0,0,.10) 2px, rgba(0,0,0,.10) 4px)",
        pageImageSize: "auto"
      },
      colors: {
        primary: "#4a9bff",
        "primary-hover": "#2f82ea",
        "primary-foreground": "#001a3a",
        secondary: "#343c49",
        "secondary-hover": "#414b5b",
        "secondary-foreground": "#e8eef8",
        accent: "#9aa7b8",
        "accent-foreground": "#10161f",
        muted: "#2a313c",
        "muted-foreground": "#b4c0d2",
        background: "#1a1f27",
        foreground: "#e8eef8",
        card: "#262d38",
        "card-foreground": "#e8eef8",
        surface: "#20262f",
        border: "#56647a",
        input: "#56647a",
        ring: "#7db4ff",
        "table-header": "#20262f",
        "table-border": "#56647a",
        "table-row-hover": "#313a47",
        "surface-hover": "#313a47",
        "border-hover": "#4a9bff",
        placeholder: "#8494ac",
        error: "#ff6b5e",
        "error-foreground": "#2a0500",
        success: "#4ed97a",
        "success-foreground": "#032010",
        warning: "#ffc933",
        "warning-foreground": "#2a1c00",
        info: "#4a9bff",
        "info-foreground": "#001a3a",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#657ecc",
        "series-2": "#d87d3a",
        "series-3": "#2ea1b4",
        "series-4": "#af82cf",
        "series-5": "#79a02d",
        "series-6": "#d5759c",
        "series-7": "#b8912f",
        "series-8": "#8996a8",
        "diagram-ink": "#e8eef8",
        "diagram-label": "#c3cddd",
        "diagram-guide": "#7f8da3",
        "diagram-axis": "#9aa7b8",
        "diagram-grid": "#3a4452",
        "diagram-highlight": "#4a9bff",
        "diagram-fill": "#4a9bff"
      },
      shadows: {
        main: "0 4px 14px rgba(0,0,0,.6), 0 1px 2px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.14)",
        sm: "0 2px 6px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.14)",
        lg: "0 12px 32px rgba(0,0,0,.7), 0 2px 4px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.14)",
        inner: "inset 0 2px 4px rgba(0,0,0,.6)",
        none: "none",
        hover: "0 4px 12px rgba(40,120,255,.5), inset 0 1px 0 rgba(255,255,255,.35)",
        active: "inset 0 2px 5px rgba(0,0,0,.65)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Noto Sans", "Lucida Grande", system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.5",
        "icon-color": "#6fb0ff",
        "transition-fast": "150ms",
        "transition-normal": "250ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0px",
        "active-scale": "0.98",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "1px",
        "focus-ring-color": "#7db4ff"
      }
    }
  }
};

// themes/arabesque.json
var arabesque_default = {
  name: "arabesque",
  displayName: "Arabesque",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Reem Kufi", "Amiri", "Noto Naskh Arabic", serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "0.02em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Reem+Kufi:wght@400;500;600;700&family=Amiri:wght@400;700&family=Cairo:wght@300;400;500;600;700&family=Noto+Naskh+Arabic:wght@400;500;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        spring: "cubic-bezier(0.25, 0.8, 0.25, 1)"
      },
      shapes: {
        modalEnter: "scale(0.96)",
        modalExit: "scale(0.96)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.97)",
        popoverExit: "scale(0.97)",
        toastEnter: "translateY(-12px)",
        toastExit: "translateY(-12px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "scoop",
      cornerShapePill: "round",
      borderStyle: "double",
      borderStyleInteractive: "double",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'><g fill='none' stroke='%23b8892b' stroke-opacity='0.14' stroke-width='1'><rect x='14' y='14' width='36' height='36'/><rect x='14' y='14' width='36' height='36' transform='rotate(45 32 32)'/><circle cx='32' cy='32' r='12'/><path d='M0 0L14 14M64 0L50 14M0 64L14 50M64 64L50 50'/><circle cx='0' cy='0' r='6'/><circle cx='64' cy='0' r='6'/><circle cx='0' cy='64' r='6'/><circle cx='64' cy='64' r='6'/></g></svg>")`,
      pageImageSize: "64px 64px"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "8px",
      lg: "12px",
      xl: "16px",
      full: "9999px"
    },
    colors: {
      primary: "#1f3f8f",
      "primary-hover": "#183275",
      "primary-foreground": "#fff8e7",
      secondary: "#247a74",
      "secondary-hover": "#1d6964",
      "secondary-foreground": "#fff8e7",
      accent: "#7d5a0c",
      "accent-foreground": "#fff8e7",
      muted: "#ebe0c6",
      "muted-foreground": "#5c4a22",
      background: "#f8f1e0",
      foreground: "#1b2a4a",
      card: "#fffaf0",
      "card-foreground": "#1b2a4a",
      surface: "#f1e7cf",
      border: "#b8892b",
      input: "#8a6410",
      ring: "#1f3f8f",
      "table-header": "#ebe0c6",
      "table-border": "#b8892b",
      "table-row-hover": "#f4ead2",
      "surface-hover": "#ede2c8",
      "border-hover": "#1f3f8f",
      placeholder: "#75653f",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#1e3f90",
      "series-2": "#247a74",
      "series-3": "#9b8432",
      "series-4": "#ab5221",
      "series-5": "#537622",
      "series-6": "#6b3a7a",
      "series-7": "#a3365a",
      "series-8": "#4a5568",
      "diagram-ink": "#1b2a4a",
      "diagram-label": "#1b2a4a",
      "diagram-guide": "#8f8268",
      "diagram-axis": "#7d5a0c",
      "diagram-grid": "#e6d9b8",
      "diagram-highlight": "#8a6410",
      "diagram-fill": "#1f3f8f"
    },
    shadows: {
      main: "0 4px 14px rgba(31,63,143,0.14)",
      sm: "0 1px 3px rgba(31,63,143,0.10)",
      lg: "0 12px 32px rgba(31,63,143,0.18)",
      inner: "inset 0 1px 3px rgba(31,63,143,0.12)",
      none: "none",
      hover: "0 6px 18px rgba(184,137,43,0.25)",
      active: "0 1px 2px rgba(31,63,143,0.15)"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Cairo", "Noto Naskh Arabic", "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#1f3f8f",
      "transition-fast": "200ms",
      "transition-normal": "350ms",
      "transition-slow": "550ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#1f3f8f"
    },
    diagram: {
      strokeThin: "1.5px",
      strokeNormal: "2.5px",
      strokeBold: "4px",
      lineCap: "round",
      lineJoin: "miter",
      dash: "8 3",
      dot: "1 3",
      fillStyle: "hatch",
      fillOpacity: "0.25",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "4px"
    },
    scene: {
      materialRoughness: "0.5",
      materialMetalness: "0.4",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.5",
      lightKey: "1",
      lightKeyColor: "#ffe9b0",
      fog: "0.05"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Reem Kufi", "Amiri", "Noto Naskh Arabic", serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "600",
        headingTransform: "none",
        headingTracking: "0.02em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Reem+Kufi:wght@400;500;600;700&family=Amiri:wght@400;700&family=Cairo:wght@300;400;500;600;700&family=Noto+Naskh+Arabic:wght@400;500;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "700ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
          spring: "cubic-bezier(0.25, 0.8, 0.25, 1)"
        },
        shapes: {
          modalEnter: "scale(0.96)",
          modalExit: "scale(0.96)",
          pageEnter: "translateY(8px)",
          popoverEnter: "scale(0.97)",
          popoverExit: "scale(0.97)",
          toastEnter: "translateY(-12px)",
          toastExit: "translateY(-12px)"
        }
      },
      iconography: {
        family: "phosphor-duotone",
        strokeWidth: "1.5",
        defaultSize: "18px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-md)",
        radiusInteractive: "var(--radius-sm)",
        radiusPill: "var(--radius-full)",
        cornerShape: "scoop",
        cornerShapePill: "round",
        borderStyle: "double",
        borderStyleInteractive: "double",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'><g fill='none' stroke='%23e0b64a' stroke-opacity='0.12' stroke-width='1'><rect x='14' y='14' width='36' height='36'/><rect x='14' y='14' width='36' height='36' transform='rotate(45 32 32)'/><circle cx='32' cy='32' r='12'/><path d='M0 0L14 14M64 0L50 14M0 64L14 50M64 64L50 50'/><circle cx='0' cy='0' r='6'/><circle cx='64' cy='0' r='6'/><circle cx='0' cy='64' r='6'/><circle cx='64' cy='64' r='6'/></g></svg>")`,
        pageImageSize: "64px 64px"
      },
      radii: {
        none: "0px",
        sm: "4px",
        md: "8px",
        lg: "12px",
        xl: "16px",
        full: "9999px"
      },
      colors: {
        primary: "#e0b64a",
        "primary-hover": "#efc866",
        "primary-foreground": "#1a1200",
        secondary: "#4fb3a9",
        "secondary-hover": "#66c1b8",
        "secondary-foreground": "#04201d",
        accent: "#8fb0ff",
        "accent-foreground": "#0a1530",
        muted: "#1b2b50",
        "muted-foreground": "#c5cde6",
        background: "#0e1a33",
        foreground: "#f3ead2",
        card: "#14244a",
        "card-foreground": "#f3ead2",
        surface: "#122040",
        border: "#c9a13a",
        input: "#c9a13a",
        ring: "#4fb3a9",
        "table-header": "#122040",
        "table-border": "#c9a13a",
        "table-row-hover": "#1a2c55",
        "surface-hover": "#1f3262",
        "border-hover": "#e0b64a",
        placeholder: "#98a3c6",
        error: "#ff8a80",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#6c8ad6",
        "series-2": "#369b94",
        "series-3": "#b28520",
        "series-4": "#c06e5c",
        "series-5": "#24a45f",
        "series-6": "#ab7ab9",
        "series-7": "#c8728e",
        "series-8": "#838ca1",
        "diagram-ink": "#f3ead2",
        "diagram-label": "#e0d6b8",
        "diagram-guide": "#9aa6c8",
        "diagram-axis": "#b8a878",
        "diagram-grid": "#26386a",
        "diagram-highlight": "#c8b337",
        "diagram-fill": "#8fb0ff"
      },
      shadows: {
        main: "0 4px 14px rgba(0,0,0,0.45)",
        sm: "0 1px 3px rgba(0,0,0,0.4)",
        lg: "0 12px 32px rgba(0,0,0,0.55)",
        inner: "inset 0 1px 3px rgba(0,0,0,0.4)",
        none: "none",
        hover: "0 6px 18px rgba(224,182,74,0.22)",
        active: "0 1px 2px rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Cairo", "Noto Naskh Arabic", "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#e0b64a",
        "transition-fast": "200ms",
        "transition-normal": "350ms",
        "transition-slow": "550ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#4fb3a9"
      }
    }
  }
};

// themes/art-deco.json
var art_deco_default = {
  name: "art-deco",
  displayName: "Art Deco 1925",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "44px",
      buttonHeightLg: "54px",
      inputHeightSm: "32px",
      inputHeightMd: "44px",
      inputHeightLg: "54px",
      rowHeightCompact: "36px",
      rowHeightNormal: "46px",
      rowHeightSpacious: "58px",
      cardPaddingSm: "16px",
      cardPaddingMd: "24px",
      cardPaddingLg: "32px",
      dialogPadding: "32px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"Limelight", "Poiret One", "Josefin Sans", serif',
      bodyFamily: '"Josefin Sans", "Futura", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Limelight&family=Poiret+One&family=Josefin+Sans:wght@400;500;700&display=swap",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.16em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "900ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.65, 0, 0.35, 1)",
        spring: "cubic-bezier(0.45, 0, 0.55, 1)"
      },
      shapes: {
        modalEnter: "translateY(-16px)",
        modalExit: "translateY(-16px)",
        pageEnter: "translateY(-8px)",
        popoverEnter: "translateY(-8px)",
        popoverExit: "translateY(-8px)",
        toastEnter: "translateY(-16px)",
        toastExit: "translateY(-16px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "bevel",
      cornerShapePill: "round",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "double",
      borderStyleInteractive: "double"
    },
    colors: {
      primary: "#7d6024",
      "primary-foreground": "#fff6d8",
      secondary: "#0f5a45",
      "secondary-hover": "#0b4937",
      "secondary-foreground": "#f2fff9",
      accent: "#0b1026",
      "accent-foreground": "#e8c766",
      muted: "#ece3c8",
      "muted-foreground": "#4d4526",
      background: "#f6efdb",
      foreground: "#0b1026",
      card: "#fffaea",
      "card-foreground": "#0b1026",
      surface: "#efe6cb",
      border: "#8a6a10",
      input: "#8a6a10",
      ring: "#0f5a45",
      "table-header": "#efe6cb",
      "table-border": "#8a6a10",
      "table-row-hover": "#f6efdb",
      "surface-hover": "#e8dcb6",
      "border-hover": "#0f5a45",
      placeholder: "#6b6238",
      error: "#a3122a",
      "error-foreground": "#ffffff",
      success: "#0f6a45",
      "success-foreground": "#ffffff",
      warning: "#e0a81c",
      "warning-foreground": "#1a1200",
      info: "#1f4a8a",
      "info-foreground": "#ffffff",
      "primary-hover": "#664e1c",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#626300",
      "series-2": "#004024",
      "series-3": "#243a7a",
      "series-4": "#75052f",
      "series-5": "#17787a",
      "series-6": "#a5551f",
      "series-7": "#5a2d6e",
      "series-8": "#5a6678",
      "diagram-ink": "#0b1026",
      "diagram-label": "#0b1026",
      "diagram-guide": "#9a8a5a",
      "diagram-axis": "#8a6a10",
      "diagram-grid": "#e8dcb4",
      "diagram-highlight": "#8a6a10",
      "diagram-fill": "#0f5a45"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "8px",
      lg: "12px",
      xl: "16px",
      full: "9999px"
    },
    shadows: {
      main: "0 0 10px rgba(138,106,16,0.35)",
      sm: "0 0 0 1px rgba(138,106,16,0.5)",
      lg: "0 0 18px rgba(138,106,16,0.45)",
      inner: "inset 0 0 0 1px rgba(138,106,16,0.4)",
      none: "none",
      hover: "0 0 14px rgba(138,106,16,0.55)",
      active: "0 0 4px rgba(138,106,16,0.6)"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "1px",
      "border-width-thick": "5px",
      "font-family": '"Josefin Sans", "Futura", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.02em",
      "line-height": "1.55",
      "icon-color": "#7d6024",
      "transition-fast": "240ms",
      "transition-normal": "420ms",
      "transition-slow": "700ms",
      "transition-timing": "cubic-bezier(0.65, 0, 0.35, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#0f5a45"
    },
    surface: {
      cardImage: "linear-gradient(180deg, rgba(212,175,55,0.10), transparent 40%)",
      pageImage: "repeating-conic-gradient(from 0deg at 50% 100%, rgba(138,106,16,0.07) 0deg 4deg, transparent 4deg 12deg)",
      pageImageSize: "160px 80px"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "3px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "12 3 2 3",
      dot: "1 3",
      fillStyle: "outline",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "line",
      labelFont: "display",
      labelSize: "xs",
      labelWeight: "600",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.25",
      materialMetalness: "0.8",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.45",
      lightKey: "1.2",
      lightKeyColor: "#ffe9a8",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#e8c766",
        "primary-foreground": "#0a0f24",
        "primary-hover": "#f2d884",
        secondary: "#1f9d78",
        "secondary-hover": "#2bb58c",
        "secondary-foreground": "#04140e",
        accent: "#f2d884",
        "accent-foreground": "#0a0f24",
        muted: "#1a2246",
        "muted-foreground": "#cfc39a",
        background: "#080c1e",
        foreground: "#f3e7bd",
        card: "#0f1530",
        "card-foreground": "#f3e7bd",
        surface: "#0c1228",
        border: "#d4af37",
        input: "#d4af37",
        ring: "#2bd39e",
        "table-header": "#0c1228",
        "table-border": "#d4af37",
        "table-row-hover": "#141b3a",
        "surface-hover": "#1a2246",
        "border-hover": "#f2d884",
        placeholder: "#a39a78",
        error: "#ff6b7d",
        "error-foreground": "#1a0308",
        success: "#3fe0a0",
        "success-foreground": "#03140c",
        warning: "#f2c14e",
        "warning-foreground": "#1a1200",
        info: "#79a8f2",
        "info-foreground": "#051226",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#9d7b1c",
        "series-2": "#219071",
        "series-3": "#647ec9",
        "series-4": "#ce597b",
        "series-5": "#248e91",
        "series-6": "#c26b30",
        "series-7": "#a26aba",
        "series-8": "#788194",
        "diagram-ink": "#fdfcd0",
        "diagram-label": "#e6d6a0",
        "diagram-guide": "#a4975f",
        "diagram-axis": "#c9b46a",
        "diagram-grid": "#232a52",
        "diagram-highlight": "#d2d15b",
        "diagram-fill": "#e8c766"
      },
      shadows: {
        main: "0 0 10px rgba(212,175,55,0.35)",
        sm: "0 0 0 1px rgba(212,175,55,0.5)",
        lg: "0 0 20px rgba(212,175,55,0.45)",
        inner: "inset 0 0 0 1px rgba(212,175,55,0.4)",
        none: "none",
        hover: "0 0 16px rgba(212,175,55,0.55)",
        active: "0 0 4px rgba(212,175,55,0.6)"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "1px",
        "border-width-thick": "5px",
        "font-family": '"Josefin Sans", "Futura", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.02em",
        "line-height": "1.55",
        "icon-color": "#e8c766",
        "transition-fast": "240ms",
        "transition-normal": "420ms",
        "transition-slow": "700ms",
        "transition-timing": "cubic-bezier(0.65, 0, 0.35, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#2bd39e"
      },
      radii: {
        none: "0px",
        sm: "4px",
        md: "8px",
        lg: "12px",
        xl: "16px",
        full: "9999px"
      },
      surface: {
        cardImage: "linear-gradient(180deg, rgba(212,175,55,0.08), transparent 40%)",
        pageImage: "repeating-conic-gradient(from 0deg at 50% 100%, rgba(212,175,55,0.07) 0deg 4deg, transparent 4deg 12deg)",
        pageImageSize: "160px 80px"
      }
    }
  }
};

// themes/art-nouveau.json
var art_nouveau_default = {
  name: "art-nouveau",
  displayName: "Art Nouveau",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "42px",
      buttonHeightLg: "50px",
      inputHeightSm: "32px",
      inputHeightMd: "42px",
      inputHeightLg: "50px",
      rowHeightCompact: "36px",
      rowHeightNormal: "46px",
      rowHeightSpacious: "56px",
      cardPaddingSm: "16px",
      cardPaddingMd: "24px",
      cardPaddingLg: "32px",
      dialogPadding: "32px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"Cormorant Garamond", "Cinzel Decorative", Georgia, serif',
      bodyFamily: '"Lora", Georgia, serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,700;1,500;1,700&family=Cinzel+Decorative:wght@400;700&family=Lora:wght@400;500;700&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "0.01em",
      headingStyle: "italic",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "800ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.45, 0, 0.2, 1)",
        spring: "cubic-bezier(0.4, 0, 0.2, 1)"
      },
      shapes: {
        modalEnter: "scale(1.02) translateY(-4px)",
        modalExit: "scale(1.02) translateY(-4px)",
        pageEnter: "translateY(4px)",
        popoverEnter: "scale(0.98)",
        popoverExit: "scale(0.98)",
        toastEnter: "translateY(8px)",
        toastExit: "translateY(8px)"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1.25",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      cornerShape: "scoop",
      cornerShapePill: "round",
      cornerShapeInteractive: "round",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#5a6320",
      "primary-hover": "#485019",
      "primary-foreground": "#fbf6e6",
      secondary: "#e9c8c0",
      "secondary-hover": "#dfb5ab",
      "secondary-foreground": "#4a2620",
      accent: "#7a4f00",
      "accent-foreground": "#fbf6e6",
      muted: "#eee4c6",
      "muted-foreground": "#5a4f2e",
      background: "#f7f0dc",
      foreground: "#2f2a16",
      card: "#fbf6e6",
      "card-foreground": "#2f2a16",
      surface: "#f1e8cd",
      border: "#6d7430",
      input: "#6d7430",
      ring: "#9a4a54",
      "table-header": "#f1e8cd",
      "table-border": "#6d7430",
      "table-row-hover": "#f7f0dc",
      "surface-hover": "#ebdfbc",
      "border-hover": "#9a4a54",
      placeholder: "#7a6f48",
      error: "#a8323e",
      "error-foreground": "#ffffff",
      success: "#3f6b2a",
      "success-foreground": "#ffffff",
      warning: "#d9a13a",
      "warning-foreground": "#2a1c00",
      info: "#3a6a80",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#696a19",
      "series-2": "#b1596e",
      "series-3": "#aa731b",
      "series-4": "#2f6f6a",
      "series-5": "#6a3a5c",
      "series-6": "#8c482b",
      "series-7": "#6f8c49",
      "series-8": "#4a5f8a",
      "diagram-ink": "#2f2a16",
      "diagram-label": "#2f2a16",
      "diagram-guide": "#8a8660",
      "diagram-axis": "#6d7430",
      "diagram-grid": "#e3dbb8",
      "diagram-highlight": "#7a4f00",
      "diagram-fill": "#5a6320"
    },
    radii: {
      none: "0px",
      sm: "8px",
      md: "16px",
      lg: "28px",
      xl: "40px",
      full: "9999px"
    },
    shadows: {
      main: "0 3px 10px rgba(90,99,32,0.2)",
      sm: "0 1px 3px rgba(90,99,32,0.18)",
      lg: "0 8px 22px rgba(90,99,32,0.24)",
      inner: "inset 0 1px 3px rgba(90,99,32,0.16)",
      none: "none",
      hover: "0 5px 14px rgba(90,99,32,0.26)",
      active: "0 1px 2px rgba(90,99,32,0.22)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Lora", Georgia, serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.65",
      "icon-color": "#5a6320",
      "transition-fast": "220ms",
      "transition-normal": "380ms",
      "transition-slow": "620ms",
      "transition-timing": "cubic-bezier(0.45, 0, 0.55, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#9a4a54"
    },
    surface: {
      cardImage: "linear-gradient(160deg, rgba(255,250,235,0.7), rgba(233,200,192,0.12))",
      pageImage: "radial-gradient(ellipse at 20% 10%, rgba(201,153,58,0.10), transparent 55%), radial-gradient(ellipse at 85% 90%, rgba(154,74,84,0.08), transparent 55%)",
      pageImageSize: "auto"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3.5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "7 5",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.22",
      roughness: "0.4",
      glow: "0",
      shadow: "0 2px 6px rgba(90, 99, 32, 0.2)",
      marker: "dot",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "12px"
    },
    scene: {
      materialRoughness: "0.7",
      materialMetalness: "0.1",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "0.9",
      lightKeyColor: "#ffefc4",
      fog: "0.12"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#b9c46a",
        "primary-hover": "#cbd583",
        "primary-foreground": "#1c2008",
        secondary: "#6b4048",
        "secondary-hover": "#7d4e57",
        "secondary-foreground": "#f8e4df",
        accent: "#e0b04a",
        "accent-foreground": "#241800",
        muted: "#2e301c",
        "muted-foreground": "#cfc79e",
        background: "#1b1d10",
        foreground: "#f1ead0",
        card: "#252818",
        "card-foreground": "#f1ead0",
        surface: "#20230f",
        border: "#8d9646",
        input: "#8d9646",
        ring: "#e3a0a8",
        "table-header": "#20230f",
        "table-border": "#8d9646",
        "table-row-hover": "#2a2d1a",
        "surface-hover": "#33361f",
        "border-hover": "#e3a0a8",
        placeholder: "#a39c78",
        error: "#ff8c95",
        "error-foreground": "#2a0508",
        success: "#8fd06a",
        "success-foreground": "#0b1a04",
        warning: "#ecb84e",
        "warning-foreground": "#241800",
        info: "#86c2dc",
        "info-foreground": "#05202c",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#8a9639",
        "series-2": "#bc7f88",
        "series-3": "#c08318",
        "series-4": "#4a9c95",
        "series-5": "#b479ab",
        "series-6": "#c67b4e",
        "series-7": "#5f9254",
        "series-8": "#7d8eb3",
        "diagram-ink": "#fdfadf",
        "diagram-label": "#d8d0b0",
        "diagram-guide": "#8f9468",
        "diagram-axis": "#aab070",
        "diagram-grid": "#3a3e26",
        "diagram-highlight": "#b59b27",
        "diagram-fill": "#b9c46a"
      },
      shadows: {
        main: "0 3px 10px rgba(0,0,0,0.45)",
        sm: "0 1px 3px rgba(0,0,0,0.4)",
        lg: "0 8px 22px rgba(0,0,0,0.5)",
        inner: "inset 0 1px 3px rgba(0,0,0,0.4)",
        none: "none",
        hover: "0 5px 14px rgba(0,0,0,0.5)",
        active: "0 1px 2px rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Lora", Georgia, serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.65",
        "icon-color": "#b9c46a",
        "transition-fast": "220ms",
        "transition-normal": "380ms",
        "transition-slow": "620ms",
        "transition-timing": "cubic-bezier(0.45, 0, 0.55, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#e3a0a8"
      },
      radii: {
        none: "0px",
        sm: "8px",
        md: "16px",
        lg: "28px",
        xl: "40px",
        full: "9999px"
      },
      surface: {
        cardImage: "linear-gradient(160deg, rgba(185,196,106,0.07), rgba(227,160,168,0.04))",
        pageImage: "radial-gradient(ellipse at 20% 10%, rgba(224,176,74,0.07), transparent 55%), radial-gradient(ellipse at 85% 90%, rgba(227,160,168,0.05), transparent 55%)",
        pageImageSize: "auto"
      }
    }
  }
};

// themes/atelier.json
var atelier_default = {
  name: "atelier",
  displayName: "Atelier",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "6px",
        space3: "10px",
        space4: "14px",
        space5: "20px",
        space6: "28px",
        space7: "36px",
        space8: "44px",
        space9: "52px",
        space10: "60px",
        space11: "68px",
        space12: "80px"
      },
      buttonHeightSm: "36px",
      buttonHeightMd: "44px",
      buttonHeightLg: "56px",
      inputHeightSm: "36px",
      inputHeightMd: "44px",
      inputHeightLg: "56px",
      rowHeightCompact: "48px",
      rowHeightNormal: "64px",
      rowHeightSpacious: "80px",
      cardPaddingSm: "20px",
      cardPaddingMd: "32px",
      cardPaddingLg: "40px",
      dialogPadding: "40px",
      sectionGap: "56px"
    },
    typeScale: {
      displayFamily: '"Schibsted Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "12px",
          lineHeight: "18px"
        },
        sm: {
          size: "14px",
          lineHeight: "22px"
        },
        base: {
          size: "16px",
          lineHeight: "26px"
        },
        lg: {
          size: "20px",
          lineHeight: "30px"
        },
        xl: {
          size: "24px",
          lineHeight: "32px"
        },
        "2xl": {
          size: "32px",
          lineHeight: "38px"
        },
        "3xl": {
          size: "40px",
          lineHeight: "44px"
        },
        "4xl": {
          size: "52px",
          lineHeight: "56px"
        },
        "display-1": {
          size: "64px",
          lineHeight: "68px"
        },
        "display-2": {
          size: "80px",
          lineHeight: "84px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "3xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "xl",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "base",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "medium"
        },
        numeric: {
          slot: "mono",
          size: "4xl",
          weight: "bold"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400;500;600;700&family=Hanken+Grotesk:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap",
      headingWeight: "500",
      headingTransform: "none",
      headingTracking: "-0.03em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "300ms",
        normal: "500ms",
        slow: "800ms",
        dramatic: "1200ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "translateY(10px)",
        modalExit: "translateY(10px)",
        pageEnter: "translateY(8px)",
        popoverEnter: "translateY(4px)",
        popoverExit: "translateY(4px)",
        toastEnter: "translateY(10px)",
        toastExit: "translateY(10px)"
      }
    },
    iconography: {
      family: "tabler",
      strokeWidth: "1.25",
      defaultSize: "22px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "12px",
      radiusInteractive: "24px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "1px",
      cornerShape: "squircle",
      cornerShapePill: "squircle",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#1a3db8",
      "primary-hover": "#15319a",
      "primary-foreground": "#fafaf7",
      secondary: "#f2f1ec",
      "secondary-hover": "#e8e7e1",
      "secondary-foreground": "#0a0a0a",
      accent: "#0a0a0a",
      "accent-foreground": "#fafaf7",
      muted: "#f2f1ec",
      "muted-foreground": "#6b6b66",
      background: "#fafaf7",
      foreground: "#0a0a0a",
      card: "#ffffff",
      "card-foreground": "#0a0a0a",
      surface: "#f2f1ec",
      border: "#e5e4de",
      input: "#d6d5cf",
      ring: "#1a3db8",
      "table-header": "#f2f1ec",
      "table-border": "#e5e4de",
      "table-row-hover": "#f6f5f0",
      "surface-hover": "#e8e7e1",
      "border-hover": "#c7c6c0",
      placeholder: "#9a998f",
      error: "#b91c1c",
      "error-foreground": "#fafaf7",
      success: "#15803d",
      "success-foreground": "#fafaf7",
      warning: "#a16207",
      "warning-foreground": "#fafaf7",
      info: "#1f51ff",
      "info-foreground": "#fafaf7",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#6d2bb0",
      "series-2": "#cf4813",
      "series-3": "#0f766e",
      "series-4": "#8446f8",
      "series-5": "#dd783c",
      "series-6": "#be185d",
      "series-7": "#618305",
      "series-8": "#52525b",
      "diagram-ink": "#0a0a0a",
      "diagram-label": "#27272a",
      "diagram-guide": "#8a8a8a",
      "diagram-axis": "#52525b",
      "diagram-grid": "#e5e4de",
      "diagram-highlight": "#1a3db8",
      "diagram-fill": "#ffffff"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "12px",
      lg: "18px",
      xl: "24px",
      full: "9999px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "1px",
      "font-family": '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "-0.015em",
      "line-height": "1.65",
      "intent-numeric-family": "var(--font-family-display)",
      "icon-color": "currentColor",
      "transition-fast": "300ms",
      "transition-normal": "500ms",
      "transition-slow": "800ms",
      "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "3px",
      "focus-ring-color": "#1a3db8"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "2px",
      lineCap: "square",
      lineJoin: "miter",
      dash: "4 4",
      dot: "1 4",
      fillStyle: "outline",
      fillOpacity: "0.12",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "line",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.85",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "0.8",
      lightKeyColor: "#ffffff",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#7088f0",
        "primary-hover": "#8a9ff5",
        "primary-foreground": "#0d0d0d",
        secondary: "#1a1a1a",
        "secondary-hover": "#242424",
        "secondary-foreground": "#fafafa",
        accent: "#fafafa",
        "accent-foreground": "#0d0d0d",
        muted: "#1a1a1a",
        "muted-foreground": "#999999",
        background: "#0d0d0d",
        foreground: "#fafafa",
        card: "#141414",
        "card-foreground": "#fafafa",
        surface: "#1a1a1a",
        border: "#262626",
        input: "#333333",
        ring: "#7088f0",
        "table-header": "#1a1a1a",
        "table-border": "#262626",
        "table-row-hover": "#181818",
        "surface-hover": "#242424",
        "border-hover": "#3a3a3a",
        placeholder: "#666666",
        error: "#cf6679",
        "error-foreground": "#0d0d0d",
        success: "#8aa899",
        "success-foreground": "#0d0d0d",
        warning: "#c9a96e",
        "warning-foreground": "#0d0d0d",
        info: "#5b7fff",
        "info-foreground": "#0d0d0d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#416cca",
        "series-2": "#db531b",
        "series-3": "#1b8e85",
        "series-4": "#9464e7",
        "series-5": "#d67820",
        "series-6": "#ce3d89",
        "series-7": "#5a8b19",
        "series-8": "#777681",
        "diagram-ink": "#fafafa",
        "diagram-label": "#d4d4d4",
        "diagram-guide": "#808080",
        "diagram-axis": "#a3a3a3",
        "diagram-grid": "#2a2a2a",
        "diagram-highlight": "#7088f0",
        "diagram-fill": "#141414"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "1px",
        "font-family": '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "-0.015em",
        "line-height": "1.65",
        "intent-numeric-family": "var(--font-family-display)",
        "icon-color": "currentColor",
        "transition-fast": "300ms",
        "transition-normal": "500ms",
        "transition-slow": "800ms",
        "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "3px",
        "focus-ring-color": "#7088f0"
      }
    }
  }
};

// themes/bauhaus.json
var bauhaus_default = {
  name: "bauhaus",
  displayName: "Bauhaus",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "42px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "42px",
      inputHeightLg: "52px",
      rowHeightCompact: "34px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "54px",
      cardPaddingSm: "14px",
      cardPaddingMd: "20px",
      cardPaddingLg: "28px",
      dialogPadding: "28px",
      sectionGap: "36px"
    },
    typeScale: {
      displayFamily: '"Jost", "Futura", "Century Gothic", sans-serif',
      bodyFamily: '"Jost", "Futura", "Century Gothic", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Jost:wght@400;500;700;800&display=swap",
      headingWeight: "800",
      headingTransform: "lowercase",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "240ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "linear",
        spring: "linear"
      },
      shapes: {
        modalEnter: "translateX(-32px)",
        modalExit: "translateX(-32px)",
        pageEnter: "translateX(24px)",
        popoverEnter: "translateY(-8px)",
        popoverExit: "translateY(-8px)",
        toastEnter: "translateX(32px)",
        toastExit: "translateX(32px)"
      }
    },
    iconography: {
      family: "tabler",
      strokeWidth: "2.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "var(--radius-none)",
      radiusInteractive: "var(--radius-none)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderHairline: "2px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#d21f26",
      "primary-hover": "#b0181e",
      "primary-foreground": "#ffffff",
      secondary: "#0f4fa8",
      "secondary-hover": "#0c3f87",
      "secondary-foreground": "#ffffff",
      accent: "#ffd21f",
      "accent-foreground": "#111111",
      muted: "#e8e4d8",
      "muted-foreground": "#4a4a4a",
      background: "#f4f1e8",
      foreground: "#111111",
      card: "#ffffff",
      "card-foreground": "#111111",
      surface: "#ece8dc",
      border: "#111111",
      input: "#111111",
      ring: "#0f4fa8",
      "table-header": "#ece8dc",
      "table-border": "#111111",
      "table-row-hover": "#f4f1e8",
      "surface-hover": "#e2ddce",
      "border-hover": "#d21f26",
      placeholder: "#6a6a6a",
      error: "#c4161c",
      "error-foreground": "#ffffff",
      success: "#0f7a3a",
      "success-foreground": "#ffffff",
      warning: "#ffd21f",
      "warning-foreground": "#111111",
      info: "#0f4fa8",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#fd5437",
      "series-2": "#0f4fa8",
      "series-3": "#a67c00",
      "series-4": "#000a22",
      "series-5": "#51933a",
      "series-6": "#c8500a",
      "series-7": "#747474",
      "series-8": "#6a2c91",
      "diagram-ink": "#111111",
      "diagram-label": "#111111",
      "diagram-guide": "#6b6b6b",
      "diagram-axis": "#111111",
      "diagram-grid": "#cfcabd",
      "diagram-highlight": "#e7384c",
      "diagram-fill": "#0f4fa8"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Jost", "Futura", "Century Gothic", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.5",
      "icon-color": "#d21f26",
      "transition-fast": "80ms",
      "transition-normal": "140ms",
      "transition-slow": "200ms",
      "transition-timing": "linear",
      "hover-scale": "1.0",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1.0",
      "active-translate-y": "0",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#0f4fa8"
    },
    diagram: {
      strokeThin: "3px",
      strokeNormal: "5px",
      strokeBold: "8px",
      lineCap: "square",
      lineJoin: "miter",
      dash: "10 6",
      dot: "1 6",
      fillStyle: "solid",
      fillOpacity: "0.35",
      roughness: "0",
      glow: "0",
      shadow: "3px 3px 0 #111111",
      marker: "triangle",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "1",
      lightKeyColor: "#ffffff",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff5a5f",
        "primary-hover": "#ff7478",
        "primary-foreground": "#140202",
        secondary: "#5b96ee",
        "secondary-hover": "#79aaf3",
        "secondary-foreground": "#04132b",
        accent: "#ffd21f",
        "accent-foreground": "#111111",
        muted: "#2a2a2a",
        "muted-foreground": "#cfcfcf",
        background: "#121212",
        foreground: "#f4f1e8",
        card: "#1c1c1c",
        "card-foreground": "#f4f1e8",
        surface: "#181818",
        border: "#f4f1e8",
        input: "#f4f1e8",
        ring: "#ffd21f",
        "table-header": "#181818",
        "table-border": "#f4f1e8",
        "table-row-hover": "#222222",
        "surface-hover": "#2a2a2a",
        "border-hover": "#ff5a5f",
        placeholder: "#9a9a9a",
        error: "#ff6b70",
        "error-foreground": "#190204",
        success: "#4ed68a",
        "success-foreground": "#03170b",
        warning: "#ffd21f",
        "warning-foreground": "#111111",
        info: "#5b96ee",
        "info-foreground": "#04132b",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#d14c61",
        "series-2": "#4084e2",
        "series-3": "#a67e0b",
        "series-4": "#848484",
        "series-5": "#1d9a4f",
        "series-6": "#d85f19",
        "series-7": "#5e8da2",
        "series-8": "#a66dca",
        "diagram-ink": "#fcf2c4",
        "diagram-label": "#f4f1e8",
        "diagram-guide": "#8a8780",
        "diagram-axis": "#c8c4b8",
        "diagram-grid": "#333333",
        "diagram-highlight": "#e7ea41",
        "diagram-fill": "#ff5a5f"
      },
      shadows: {
        main: "none",
        sm: "none",
        lg: "none",
        inner: "none",
        none: "none",
        hover: "none",
        active: "none"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Jost", "Futura", "Century Gothic", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.5",
        "icon-color": "#ff5a5f",
        "transition-fast": "80ms",
        "transition-normal": "140ms",
        "transition-slow": "200ms",
        "transition-timing": "linear",
        "hover-scale": "1.0",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1.0",
        "active-translate-y": "0",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ffd21f"
      },
      radii: {
        none: "0px",
        sm: "0px",
        md: "0px",
        lg: "0px",
        xl: "0px",
        full: "9999px"
      }
    }
  }
};

// themes/bloomberg-dense.json
var bloomberg_dense_default = {
  name: "bloomberg-dense",
  displayName: "Bloomberg Dense",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "22px",
      buttonHeightMd: "26px",
      buttonHeightLg: "32px",
      inputHeightSm: "22px",
      inputHeightMd: "26px",
      inputHeightLg: "32px",
      rowHeightCompact: "22px",
      rowHeightNormal: "26px",
      rowHeightSpacious: "32px",
      cardPaddingSm: "6px",
      cardPaddingMd: "10px",
      cardPaddingLg: "14px",
      dialogPadding: "14px",
      sectionGap: "14px"
    },
    typeScale: {
      displayFamily: '"IBM Plex Sans Condensed", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"IBM Plex Sans Condensed", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "11px",
          lineHeight: "15px"
        },
        sm: {
          size: "13px",
          lineHeight: "18px"
        },
        base: {
          size: "14px",
          lineHeight: "20px"
        },
        lg: {
          size: "16px",
          lineHeight: "22px"
        },
        xl: {
          size: "18px",
          lineHeight: "24px"
        },
        "2xl": {
          size: "21px",
          lineHeight: "27px"
        },
        "3xl": {
          size: "25px",
          lineHeight: "32px"
        },
        "4xl": {
          size: "31px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "40px",
          lineHeight: "50px"
        },
        "display-2": {
          size: "52px",
          lineHeight: "62px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "base",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "sm",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "medium"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "bold"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Condensed:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap",
      headingWeight: "700",
      headingTransform: "uppercase",
      headingTracking: "0.05em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "120ms",
        normal: "180ms",
        slow: "260ms",
        dramatic: "420ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        emphasized: "cubic-bezier(0.3, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.4, 0.64, 1)"
      },
      shapes: {
        modalEnter: "none",
        modalExit: "none",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "translateX(12px)",
        toastExit: "translateX(12px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.5",
      defaultSize: "13px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "1px",
      radiusInteractive: "2px",
      radiusPill: "2px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "2px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#1e3a6e",
      "primary-hover": "#172e58",
      "primary-foreground": "#ffffff",
      secondary: "#eef2f8",
      "secondary-hover": "#e1e7f0",
      "secondary-foreground": "#0c1424",
      accent: "#b45309",
      "accent-foreground": "#ffffff",
      muted: "#eef2f8",
      "muted-foreground": "#4a5876",
      background: "#f6f8fc",
      foreground: "#0c1424",
      card: "#ffffff",
      "card-foreground": "#0c1424",
      surface: "#eef2f8",
      border: "#d6deeb",
      input: "#c2cee0",
      ring: "#1e3a6e",
      "table-header": "#eef2f8",
      "table-border": "#d6deeb",
      "table-row-hover": "#eef2f8",
      "surface-hover": "#e1e7f0",
      "border-hover": "#aab8cd",
      placeholder: "#6b7a96",
      error: "#b91c1c",
      "error-foreground": "#ffffff",
      success: "#047857",
      "success-foreground": "#ffffff",
      warning: "#b45309",
      "warning-foreground": "#ffffff",
      info: "#0e7490",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#163265",
      "series-2": "#e55c32",
      "series-3": "#0e7490",
      "series-4": "#7c3aed",
      "series-5": "#db2777",
      "series-6": "#4d7c0f",
      "series-7": "#a67d14",
      "series-8": "#0e6fb8",
      "diagram-ink": "#0c1424",
      "diagram-label": "#2a3a58",
      "diagram-guide": "#8895ad",
      "diagram-axis": "#475a7a",
      "diagram-grid": "#e1e7f1",
      "diagram-highlight": "#cc7511",
      "diagram-fill": "#1e3a6e"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "1px",
      lg: "2px",
      xl: "2px",
      full: "9999px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"IBM Plex Sans Condensed", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "600",
      "letter-spacing": "0",
      "line-height": "1.3",
      "intent-numeric-family": "var(--font-family-mono)",
      "icon-color": "currentColor",
      "transition-fast": "60ms",
      "transition-normal": "100ms",
      "transition-slow": "260ms",
      "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#1e3a6e"
    },
    diagram: {
      strokeThin: "0.75px",
      strokeNormal: "1px",
      strokeBold: "2px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "3 2",
      dot: "1 2",
      fillStyle: "tint",
      fillOpacity: "0.15",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "500",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0.05",
      materialFlat: "1",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "0.8",
      lightKeyColor: "#f0f6ff",
      fog: "0"
    },
    surface: {
      edgeMask: "none",
      edgeHeight: "0px",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "none"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#f5a524",
        "primary-hover": "#f7b54a",
        "primary-foreground": "#1a1206",
        secondary: "#16233d",
        "secondary-hover": "#1d2d4c",
        "secondary-foreground": "#d6e0ef",
        accent: "#5ab0d8",
        "accent-foreground": "#06131a",
        muted: "#131f36",
        "muted-foreground": "#8e9db8",
        background: "#0a0f1c",
        foreground: "#e6edf7",
        card: "#0f1626",
        "card-foreground": "#e6edf7",
        surface: "#131f36",
        border: "#20304e",
        input: "#2d4163",
        ring: "#f5a524",
        "table-header": "#131f36",
        "table-border": "#20304e",
        "table-row-hover": "#16233d",
        "surface-hover": "#1d2d4c",
        "border-hover": "#36506f",
        placeholder: "#5a6b88",
        error: "#f87171",
        "error-foreground": "#1a0a0a",
        success: "#34d399",
        "success-foreground": "#06170f",
        warning: "#fbbf24",
        "warning-foreground": "#1a1206",
        info: "#38bdf8",
        "info-foreground": "#061318",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#587fc8",
        "series-2": "#ca6417",
        "series-3": "#1b8cab",
        "series-4": "#9464e7",
        "series-5": "#d74f8b",
        "series-6": "#5d8f1a",
        "series-7": "#ae7504",
        "series-8": "#4b9fe8",
        "diagram-ink": "#e6edf7",
        "diagram-label": "#b8c4d8",
        "diagram-guide": "#6a7a96",
        "diagram-axis": "#8a9ab6",
        "diagram-grid": "#1e2a42",
        "diagram-highlight": "#f19721",
        "diagram-fill": "#5ab0d8"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"IBM Plex Sans Condensed", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "600",
        "letter-spacing": "0",
        "line-height": "1.3",
        "intent-numeric-family": "var(--font-family-mono)",
        "icon-color": "currentColor",
        "transition-fast": "60ms",
        "transition-normal": "100ms",
        "transition-slow": "260ms",
        "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#f5a524"
      }
    }
  }
};

// themes/blueprint.json
var blueprint_default = {
  name: "blueprint",
  displayName: "Blueprint",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "26px",
      buttonHeightMd: "32px",
      buttonHeightLg: "40px",
      inputHeightSm: "26px",
      inputHeightMd: "32px",
      inputHeightLg: "40px",
      rowHeightCompact: "28px",
      rowHeightNormal: "34px",
      rowHeightSpacious: "42px",
      cardPaddingSm: "10px",
      cardPaddingMd: "14px",
      cardPaddingLg: "20px",
      dialogPadding: "20px",
      sectionGap: "24px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Share+Tech+Mono&display=swap",
      displayFamily: '"IBM Plex Mono", "Share Tech Mono", monospace',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Share Tech Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "500",
      headingTransform: "uppercase",
      headingTracking: "0.14em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "100ms",
        normal: "160ms",
        slow: "240ms",
        dramatic: "320ms"
      },
      easings: {
        linear: "linear",
        standard: "linear",
        emphasized: "linear",
        spring: "linear"
      },
      shapes: {
        modalEnter: "scaleX(0.02)",
        modalExit: "scaleX(0.02)",
        pageEnter: "translateX(-8px)",
        popoverEnter: "scaleY(0.2)",
        popoverExit: "scaleY(0.2)",
        toastEnter: "translateX(16px)",
        toastExit: "translateX(16px)"
      }
    },
    iconography: {
      family: "tabler",
      strokeWidth: "1.25",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "dashed",
      borderStyleInteractive: "dashed",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "linear-gradient(rgba(26, 74, 138, 0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(26, 74, 138, 0.14) 1px, transparent 1px), linear-gradient(rgba(26, 74, 138, 0.26) 1px, transparent 1px), linear-gradient(90deg, rgba(26, 74, 138, 0.26) 1px, transparent 1px)",
      pageImageSize: "8px 8px, 8px 8px, 40px 40px, 40px 40px",
      diagram: "#1a4a8a"
    },
    colors: {
      primary: "#1a4a8a",
      "primary-hover": "#143b70",
      "primary-foreground": "#ffffff",
      secondary: "#dbe8fb",
      "secondary-hover": "#c8dcf7",
      "secondary-foreground": "#0f2f63",
      accent: "#087391",
      "accent-foreground": "#ffffff",
      muted: "#e6eefb",
      "muted-foreground": "#3d5a8a",
      background: "#f5f9ff",
      foreground: "#0f2f63",
      card: "#ffffff",
      "card-foreground": "#0f2f63",
      surface: "#eaf1fc",
      border: "#1a4a8a",
      input: "#1a4a8a",
      ring: "#087391",
      "table-header": "#eaf1fc",
      "table-border": "#1a4a8a",
      "table-row-hover": "#edf3fd",
      "surface-hover": "#e0ebfa",
      "border-hover": "#087391",
      placeholder: "#56709c",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#1b6e3c",
      "success-foreground": "#ffffff",
      warning: "#ffd966",
      "warning-foreground": "#332600",
      info: "#1a4a8a",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#b3c4ca",
      "series-2": "#7fe3ff",
      "series-3": "#c6ac2f",
      "series-4": "#8ff0b0",
      "series-5": "#ffa3a3",
      "series-6": "#d2b4ff",
      "series-7": "#ffb36b",
      "series-8": "#ff9ed2",
      "diagram-ink": "#e8f3ff",
      "diagram-label": "#cfe6ff",
      "diagram-guide": "#7fb2e8",
      "diagram-axis": "#a9cdf5",
      "diagram-grid": "#3a6aa8",
      "diagram-highlight": "#fafa53",
      "diagram-fill": "#cfe6ff"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "none",
      main: "none",
      lg: "none",
      inner: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Share Tech Mono", "IBM Plex Mono", ui-monospace, monospace',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.03em",
      "line-height": "1.5",
      "icon-color": "currentColor",
      "transition-fast": "100ms",
      "transition-normal": "160ms",
      "transition-slow": "240ms",
      "transition-timing": "linear",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#087391"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "2.5px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "10 4",
      dot: "2 4",
      fillStyle: "outline",
      fillOpacity: "0.12",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "1",
      lightAmbient: "0.7",
      lightKey: "0.9",
      lightKeyColor: "#cfe6ff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ffffff",
        "primary-hover": "#dbe9ff",
        "primary-foreground": "#1a4a8a",
        secondary: "#2a63b0",
        "secondary-hover": "#3470bd",
        "secondary-foreground": "#ffffff",
        accent: "#7fe3ff",
        "accent-foreground": "#0e2f5e",
        muted: "#20549a",
        "muted-foreground": "#cfe6ff",
        background: "#1a4a8a",
        foreground: "#ffffff",
        card: "#1f5299",
        "card-foreground": "#ffffff",
        surface: "#1d4f94",
        border: "#cfe6ff",
        input: "#cfe6ff",
        ring: "#7fe3ff",
        "table-header": "#1d4f94",
        "table-border": "#cfe6ff",
        "table-row-hover": "#225aa6",
        "surface-hover": "#2460ad",
        "border-hover": "#7fe3ff",
        placeholder: "#a9cdf5",
        error: "#ff8a8a",
        "error-foreground": "#3a0000",
        success: "#7dffb0",
        "success-foreground": "#00301a",
        warning: "#ffd966",
        "warning-foreground": "#332600",
        info: "#7fe3ff",
        "info-foreground": "#0e2f5e",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#b2c5c3",
        "series-2": "#7fe3ff",
        "series-3": "#c6ac2f",
        "series-4": "#68c88b",
        "series-5": "#f9a2c3",
        "series-6": "#d2b4ff",
        "series-7": "#ffb36b",
        "series-8": "#fc87e5",
        "diagram-ink": "#eafdfb",
        "diagram-label": "#cfe6ff",
        "diagram-guide": "#7fb2e8",
        "diagram-axis": "#a9cdf5",
        "diagram-grid": "#3a6aa8",
        "diagram-highlight": "#fafa53",
        "diagram-fill": "#cfe6ff"
      },
      shadows: {
        none: "none",
        sm: "none",
        main: "none",
        lg: "none",
        inner: "none",
        hover: "none",
        active: "none"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Share Tech Mono", "IBM Plex Mono", ui-monospace, monospace',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.03em",
        "line-height": "1.5",
        "icon-color": "currentColor",
        "transition-fast": "100ms",
        "transition-normal": "160ms",
        "transition-slow": "240ms",
        "transition-timing": "linear",
        "hover-scale": "1",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#7fe3ff"
      },
      surface: {
        pageImage: "linear-gradient(rgba(207, 230, 255, 0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(207, 230, 255, 0.12) 1px, transparent 1px), linear-gradient(rgba(207, 230, 255, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(207, 230, 255, 0.3) 1px, transparent 1px)",
        pageImageSize: "8px 8px, 8px 8px, 40px 40px, 40px 40px",
        diagram: "#143f7c"
      }
    }
  }
};

// themes/clay.json
var clay_default = {
  name: "clay",
  displayName: "Clay",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "40px",
      buttonHeightLg: "50px",
      inputHeightSm: "32px",
      inputHeightMd: "40px",
      inputHeightLg: "50px",
      rowHeightCompact: "36px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "56px",
      cardPaddingSm: "16px",
      cardPaddingMd: "20px",
      cardPaddingLg: "32px",
      dialogPadding: "32px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"Baloo 2", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap",
      headingWeight: "800",
      headingTransform: "none",
      headingTracking: "0",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.9)",
        modalExit: "scale(0.9)",
        pageEnter: "translateY(12px)",
        popoverEnter: "scale(0.94)",
        popoverExit: "scale(0.94)",
        toastEnter: "translateY(20px)",
        toastExit: "translateY(20px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2.25",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-xl)",
      radiusInteractive: "var(--radius-lg)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#b97fd8",
      "primary-hover": "#a569c7",
      "primary-foreground": "#251038",
      secondary: "#c9ecdc",
      "secondary-hover": "#b2e3cd",
      "secondary-foreground": "#1f4a38",
      accent: "#f2a172",
      "accent-foreground": "#4a2410",
      muted: "#f2e9e4",
      "muted-foreground": "#6a5560",
      background: "#faf5f0",
      foreground: "#3d2c3a",
      card: "#fffdf9",
      "card-foreground": "#3d2c3a",
      surface: "#f2e8e2",
      border: "rgba(93, 62, 110, 0.14)",
      input: "rgba(93, 62, 110, 0.2)",
      ring: "#b97fd8",
      error: "#c92847",
      "error-foreground": "#ffffff",
      success: "#20835b",
      "success-foreground": "#ffffff",
      warning: "#e3a63d",
      "warning-foreground": "#3d2a00",
      info: "#3f6fd1",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#9a55c4",
      "series-2": "#dc5b56",
      "series-3": "#229d8a",
      "series-4": "#d6457a",
      "series-5": "#3b82c4",
      "series-6": "#a87a0a",
      "series-7": "#5b55c4",
      "series-8": "#8a5a4a",
      "diagram-ink": "#4a3250",
      "diagram-label": "#3d2c3a",
      "diagram-guide": "#a898ac",
      "diagram-axis": "#6e5a76",
      "diagram-grid": "#eadfe6",
      "diagram-highlight": "#d9622b",
      "diagram-fill": "#b97fd8"
    },
    radii: {
      none: "0px",
      sm: "16px",
      md: "22px",
      lg: "28px",
      xl: "32px",
      full: "9999px"
    },
    shadows: {
      main: "0 14px 28px -8px rgba(93,62,110,0.30), inset 6px 6px 12px rgba(255,255,255,0.85), inset -6px -6px 12px rgba(93,62,110,0.14)",
      sm: "0 8px 16px -6px rgba(93,62,110,0.24), inset 3px 3px 6px rgba(255,255,255,0.8), inset -3px -3px 6px rgba(93,62,110,0.12)",
      lg: "0 26px 50px -10px rgba(93,62,110,0.36), inset 8px 8px 16px rgba(255,255,255,0.85), inset -8px -8px 16px rgba(93,62,110,0.16)",
      inner: "inset 4px 4px 8px rgba(93,62,110,0.16), inset -4px -4px 8px rgba(255,255,255,0.8)",
      none: "none",
      hover: "0 18px 34px -8px rgba(93,62,110,0.36), inset 6px 6px 12px rgba(255,255,255,0.9), inset -6px -6px 12px rgba(93,62,110,0.14)",
      active: "0 4px 8px -2px rgba(93,62,110,0.22), inset 5px 5px 10px rgba(93,62,110,0.18), inset -3px -3px 6px rgba(255,255,255,0.7)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "500",
      "font-weight-medium": "600",
      "font-weight-bold": "800",
      "letter-spacing": "0.005em",
      "line-height": "1.55",
      "icon-color": "#b97fd8",
      "transition-fast": "120ms",
      "transition-normal": "180ms",
      "transition-slow": "280ms",
      "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
      "hover-scale": "1.03",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.97",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "rgba(185, 127, 216, 0.5)"
    },
    diagram: {
      strokeThin: "2px",
      strokeNormal: "3px",
      strokeBold: "5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "2 6",
      dot: "0.1 6",
      fillStyle: "solid",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "0",
      shadow: "0 6px 12px rgba(93, 62, 110, 0.25)",
      marker: "dot",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "14px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.75",
      lightKey: "0.8",
      lightKeyColor: "#fff1e0",
      fog: "0.1"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#cf9ce8",
        "primary-hover": "#bb84d8",
        "primary-foreground": "#2a1538",
        secondary: "#35544a",
        "secondary-hover": "#406457",
        "secondary-foreground": "#c8ecdc",
        accent: "#f0ab82",
        "accent-foreground": "#3a1c0a",
        muted: "#3a2d42",
        "muted-foreground": "#bfa8c4",
        background: "#241c2a",
        foreground: "#f3e9f2",
        card: "#2f2438",
        "card-foreground": "#f3e9f2",
        surface: "#3a2d42",
        border: "rgba(207, 156, 232, 0.16)",
        input: "rgba(207, 156, 232, 0.22)",
        ring: "#cf9ce8",
        error: "#f08a9a",
        "error-foreground": "#3d0f18",
        success: "#8fd8b0",
        "success-foreground": "#0f2a1d",
        warning: "#ecc56a",
        "warning-foreground": "#332400",
        info: "#9ab8f0",
        "info-foreground": "#0f1f3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ad7ccb",
        "series-2": "#d4754a",
        "series-3": "#2ea380",
        "series-4": "#cc658b",
        "series-5": "#5e93c5",
        "series-6": "#b58716",
        "series-7": "#969ce5",
        "series-8": "#b18779",
        "diagram-ink": "#f3e9f2",
        "diagram-label": "#dccbdc",
        "diagram-guide": "#9a86a4",
        "diagram-axis": "#b8a4c0",
        "diagram-grid": "#46385a",
        "diagram-highlight": "#f0ab82",
        "diagram-fill": "#cf9ce8"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "500",
        "font-weight-medium": "600",
        "font-weight-bold": "800",
        "letter-spacing": "0.005em",
        "line-height": "1.55",
        "icon-color": "#cf9ce8",
        "transition-fast": "120ms",
        "transition-normal": "180ms",
        "transition-slow": "280ms",
        "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
        "hover-scale": "1.03",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.97",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "rgba(207, 156, 232, 0.5)"
      },
      shadows: {
        main: "0 14px 28px -8px rgba(0,0,0,0.6), inset 6px 6px 12px rgba(255,255,255,0.09), inset -6px -6px 12px rgba(0,0,0,0.35)",
        sm: "0 8px 16px -6px rgba(0,0,0,0.55), inset 3px 3px 6px rgba(255,255,255,0.08), inset -3px -3px 6px rgba(0,0,0,0.3)",
        lg: "0 26px 50px -10px rgba(0,0,0,0.7), inset 8px 8px 16px rgba(255,255,255,0.09), inset -8px -8px 16px rgba(0,0,0,0.38)",
        inner: "inset 4px 4px 8px rgba(0,0,0,0.4), inset -4px -4px 8px rgba(255,255,255,0.06)",
        none: "none",
        hover: "0 18px 34px -8px rgba(0,0,0,0.65), inset 6px 6px 12px rgba(255,255,255,0.11), inset -6px -6px 12px rgba(0,0,0,0.35)",
        active: "0 4px 8px -2px rgba(0,0,0,0.5), inset 5px 5px 10px rgba(0,0,0,0.4), inset -3px -3px 6px rgba(255,255,255,0.05)"
      }
    }
  }
};

// themes/comic.json
var comic_default = {
  name: "comic",
  displayName: "Comic Studio",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Bangers", "Comic Sans MS", cursive',
      bodyFamily: '"Fredoka", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Bangers&family=Fredoka:wght@400;500;600;700&display=swap",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.05em",
      headingStyle: "normal"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.8) rotate(-2deg)",
        modalExit: "scale(0.8) rotate(-2deg)",
        pageEnter: "translateY(12px)",
        popoverEnter: "scale(0.85)",
        popoverExit: "scale(0.85)",
        toastEnter: "translateY(20px) scale(0.9)",
        toastExit: "translateY(20px) scale(0.9)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#c2301b",
      "primary-hover": "#a92814",
      "primary-foreground": "#fff8e7",
      secondary: "#fde68a",
      "secondary-hover": "#fcd95c",
      "secondary-foreground": "#1c1c28",
      accent: "#1d5bbf",
      "accent-foreground": "#ffffff",
      muted: "#f5e9c9",
      "muted-foreground": "#6b5a34",
      background: "#fff8e7",
      foreground: "#1c1c28",
      card: "#ffffff",
      "card-foreground": "#1c1c28",
      surface: "#fff1d6",
      border: "#1c1c28",
      input: "#2b2b3d",
      ring: "#2f6fed",
      "table-header": "#fff1d6",
      "table-border": "#1c1c28",
      "table-row-hover": "#fff8e7",
      "surface-hover": "#ffe9bc",
      "border-hover": "#c2301b",
      placeholder: "#8a7a54",
      error: "#e0293b",
      "error-foreground": "#ffffff",
      success: "#12874f",
      "success-foreground": "#ffffff",
      warning: "#f5a623",
      "warning-foreground": "#2a1a00",
      info: "#2f6fed",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#e15325",
      "series-2": "#1d5bbf",
      "series-3": "#b87400",
      "series-4": "#156a18",
      "series-5": "#7a35b0",
      "series-6": "#d6336c",
      "series-7": "#0e7490",
      "series-8": "#7c4824",
      "diagram-ink": "#1c1c28",
      "diagram-label": "#1c1c28",
      "diagram-guide": "#6a6a7a",
      "diagram-axis": "#1c1c28",
      "diagram-grid": "#ddd5c0",
      "diagram-highlight": "#b7240e",
      "diagram-fill": "#fde68a"
    },
    radii: {
      none: "0px",
      sm: "8px",
      md: "14px",
      lg: "20px",
      xl: "28px",
      full: "9999px"
    },
    shadows: {
      main: "4px 4px 0 0 #1c1c28",
      sm: "2px 2px 0 0 #1c1c28",
      lg: "6px 6px 0 0 #1c1c28",
      inner: "inset 2px 2px 0 0 rgba(28, 28, 40, 0.15)",
      none: "none",
      hover: "6px 6px 0 0 #1c1c28",
      active: "1px 1px 0 0 #1c1c28"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Fredoka", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.55",
      "icon-color": "#c2301b",
      "transition-fast": "120ms",
      "transition-normal": "200ms",
      "transition-slow": "320ms",
      "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
      "hover-scale": "1.03",
      "hover-translate-y": "-2px",
      "hover-translate-x": "-1px",
      "active-scale": "0.97",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#2f6fed"
    },
    surface: {
      pageImage: "radial-gradient(rgba(28, 28, 40, 0.13) 1.4px, transparent 1.8px)",
      pageImageSize: "10px 10px"
    },
    diagram: {
      strokeThin: "2.5px",
      strokeNormal: "4px",
      strokeBold: "6px",
      lineCap: "round",
      lineJoin: "round",
      dash: "4 4",
      dot: "0.1 7",
      fillStyle: "solid",
      fillOpacity: "0.35",
      roughness: "1.2",
      glow: "0",
      shadow: "3px 3px 0 #1c1c28",
      marker: "triangle",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "10px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "3",
      lightAmbient: "0.6",
      lightKey: "1.2",
      lightKeyColor: "#fff3c4",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff7a5c",
        "primary-hover": "#ff9174",
        "primary-foreground": "#180a05",
        secondary: "#f2c94c",
        "secondary-hover": "#f5d570",
        "secondary-foreground": "#1c1c28",
        accent: "#7fb0ff",
        "accent-foreground": "#0b1530",
        muted: "#262a4d",
        "muted-foreground": "#b7bbe3",
        background: "#14162c",
        foreground: "#f5efe0",
        card: "#1d2044",
        "card-foreground": "#f5efe0",
        surface: "#1a1d3c",
        border: "#f0e9d8",
        input: "#c9c2a9",
        ring: "#6fa8ff",
        "table-header": "#1a1d3c",
        "table-border": "#f0e9d8",
        "table-row-hover": "#20244a",
        "surface-hover": "#262a52",
        "border-hover": "#ff7a5c",
        placeholder: "#8e92c0",
        error: "#ff6b6b",
        "error-foreground": "#180505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#2a1a00",
        info: "#6fa8ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#d26142",
        "series-2": "#578adb",
        "series-3": "#c07e0c",
        "series-4": "#1f9e61",
        "series-5": "#a877ce",
        "series-6": "#d7658d",
        "series-7": "#1e97b9",
        "series-8": "#d98c56",
        "diagram-ink": "#f5efe0",
        "diagram-label": "#f5efe0",
        "diagram-guide": "#8a90c0",
        "diagram-axis": "#c0c4e0",
        "diagram-grid": "#2e3260",
        "diagram-highlight": "#eedb1d",
        "diagram-fill": "#ff7a5c"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Fredoka", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.55",
        "icon-color": "#ff7a5c",
        "transition-fast": "120ms",
        "transition-normal": "200ms",
        "transition-slow": "320ms",
        "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
        "hover-scale": "1.03",
        "hover-translate-y": "-2px",
        "hover-translate-x": "-1px",
        "active-scale": "0.97",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#6fa8ff"
      },
      shadows: {
        main: "4px 4px 0 0 #f0e9d8",
        sm: "2px 2px 0 0 #f0e9d8",
        lg: "6px 6px 0 0 #f0e9d8",
        inner: "inset 2px 2px 0 0 rgba(240, 233, 216, 0.15)",
        none: "none",
        hover: "6px 6px 0 0 #f0e9d8",
        active: "1px 1px 0 0 #f0e9d8"
      },
      typeScale: {
        displayFamily: '"Bangers", "Comic Sans MS", cursive',
        bodyFamily: '"Fredoka", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        fontImport: "https://fonts.googleapis.com/css2?family=Bangers&family=Fredoka:wght@400;500;600;700&display=swap",
        headingWeight: "400",
        headingTransform: "uppercase",
        headingTracking: "0.05em",
        headingStyle: "normal"
      },
      surface: {
        pageImage: "radial-gradient(rgba(240, 233, 216, 0.09) 1.4px, transparent 1.8px)",
        pageImageSize: "10px 10px"
      }
    }
  }
};

// themes/constructivist.json
var constructivist_default = {
  name: "constructivist",
  displayName: "Constructivist",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "42px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "42px",
      inputHeightLg: "52px",
      rowHeightCompact: "34px",
      rowHeightNormal: "42px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "12px",
      cardPaddingMd: "20px",
      cardPaddingLg: "28px",
      dialogPadding: "28px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Russo One", "Oswald", "Impact", sans-serif',
      bodyFamily: '"Roboto Condensed", "Arial Narrow", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Oswald:wght@500;700&family=Russo+One&family=Roboto+Condensed:wght@400;500;700&display=swap",
      headingWeight: "700",
      headingTransform: "uppercase",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "160ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "linear",
        spring: "linear"
      },
      shapes: {
        modalEnter: "rotate(-3deg) translateY(24px)",
        modalExit: "rotate(-3deg) translateY(24px)",
        pageEnter: "translateX(-24px)",
        popoverEnter: "rotate(-2deg)",
        popoverExit: "rotate(-2deg)",
        toastEnter: "translateX(32px) rotate(2deg)",
        toastExit: "translateX(32px) rotate(2deg)"
      }
    },
    iconography: {
      family: "phosphor-fill",
      strokeWidth: "2.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-sm)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-none)",
      cornerShape: "notch",
      cornerShapePill: "notch",
      borderHairline: "2px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#c8101e",
      "primary-hover": "#a00c18",
      "primary-foreground": "#fff3dc",
      secondary: "#111111",
      "secondary-hover": "#2a2a2a",
      "secondary-foreground": "#f3e6c8",
      accent: "#e8d9b0",
      "accent-foreground": "#111111",
      muted: "#e6d8b4",
      "muted-foreground": "#3f3826",
      background: "#f3e6c8",
      foreground: "#111111",
      card: "#faf0d8",
      "card-foreground": "#111111",
      surface: "#ecddb8",
      border: "#111111",
      input: "#111111",
      ring: "#c8101e",
      "table-header": "#ecddb8",
      "table-border": "#111111",
      "table-row-hover": "#f3e6c8",
      "surface-hover": "#e3d3a8",
      "border-hover": "#c8101e",
      placeholder: "#5f563c",
      error: "#b00c18",
      "error-foreground": "#ffffff",
      success: "#1d6a3a",
      "success-foreground": "#ffffff",
      warning: "#e8a20c",
      "warning-foreground": "#111111",
      info: "#1a3a6a",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#d11e4d",
      "series-2": "#000a22",
      "series-3": "#a86a00",
      "series-4": "#2a4a7a",
      "series-5": "#5a6a2a",
      "series-6": "#883c0d",
      "series-7": "#1d6a6a",
      "series-8": "#6a6358",
      "diagram-ink": "#111111",
      "diagram-label": "#111111",
      "diagram-guide": "#6a6358",
      "diagram-axis": "#111111",
      "diagram-grid": "#ddd0b0",
      "diagram-highlight": "#dd3516",
      "diagram-fill": "#111111"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "6px",
      lg: "10px",
      xl: "14px",
      full: "0px"
    },
    shadows: {
      main: "6px 6px 0 0 #111111",
      sm: "3px 3px 0 0 #111111",
      lg: "10px 10px 0 0 #111111",
      inner: "inset 3px 3px 0 0 rgba(17,17,17,0.2)",
      none: "none",
      hover: "8px 8px 0 0 #c8101e",
      active: "1px 1px 0 0 #111111"
    },
    typography: {
      "border-width": "4px",
      "border-width-thin": "2px",
      "border-width-thick": "6px",
      "font-family": '"Roboto Condensed", "Arial Narrow", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.4",
      "icon-color": "currentColor",
      "transition-fast": "50ms",
      "transition-normal": "90ms",
      "transition-slow": "140ms",
      "transition-timing": "linear",
      "hover-scale": "1.0",
      "hover-translate-y": "-2px",
      "hover-translate-x": "-2px",
      "active-scale": "1.0",
      "active-translate-y": "2px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#c8101e"
    },
    surface: {
      cardImage: "none",
      pageImage: "radial-gradient(rgba(17,17,17,0.12) 1.2px, transparent 1.6px)",
      pageImageSize: "7px 7px"
    },
    diagram: {
      strokeThin: "3px",
      strokeNormal: "5px",
      strokeBold: "8px",
      lineCap: "butt",
      lineJoin: "bevel",
      dash: "14 6",
      dot: "1 6",
      fillStyle: "hatch",
      fillOpacity: "0.35",
      roughness: "0",
      glow: "0",
      shadow: "5px 5px 0 #111111",
      marker: "triangle",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.6",
      lightKey: "1.1",
      lightKeyColor: "#fff0cc",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff4a55",
        "primary-hover": "#ff6a73",
        "primary-foreground": "#140204",
        secondary: "#f3e6c8",
        "secondary-hover": "#ffffff",
        "secondary-foreground": "#111111",
        accent: "#e8d9b0",
        "accent-foreground": "#111111",
        muted: "#2b2622",
        "muted-foreground": "#d8ccae",
        background: "#14110f",
        foreground: "#f3e6c8",
        card: "#1e1a17",
        "card-foreground": "#f3e6c8",
        surface: "#191512",
        border: "#f3e6c8",
        input: "#f3e6c8",
        ring: "#ff4a55",
        "table-header": "#191512",
        "table-border": "#f3e6c8",
        "table-row-hover": "#24201c",
        "surface-hover": "#2e2924",
        "border-hover": "#ff4a55",
        placeholder: "#a39878",
        error: "#ff6b75",
        "error-foreground": "#190204",
        success: "#4ed68a",
        "success-foreground": "#03170b",
        warning: "#f0b030",
        "warning-foreground": "#111111",
        info: "#7fa6e8",
        "info-foreground": "#051226",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#cf3857",
        "series-2": "#848484",
        "series-3": "#b6770c",
        "series-4": "#5f86bf",
        "series-5": "#798d3f",
        "series-6": "#cc6930",
        "series-7": "#309090",
        "series-8": "#96805a",
        "diagram-ink": "#f6fddd",
        "diagram-label": "#f3e6c8",
        "diagram-guide": "#8a8070",
        "diagram-axis": "#c8b890",
        "diagram-grid": "#342e28",
        "diagram-highlight": "#f44138",
        "diagram-fill": "#e8d9b0"
      },
      shadows: {
        main: "6px 6px 0 0 #ff4a55",
        sm: "3px 3px 0 0 #ff4a55",
        lg: "10px 10px 0 0 #ff4a55",
        inner: "inset 3px 3px 0 0 rgba(243,230,200,0.2)",
        none: "none",
        hover: "8px 8px 0 0 #f3e6c8",
        active: "1px 1px 0 0 #ff4a55"
      },
      typography: {
        "border-width": "4px",
        "border-width-thin": "2px",
        "border-width-thick": "6px",
        "font-family": '"Roboto Condensed", "Arial Narrow", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.4",
        "icon-color": "currentColor",
        "transition-fast": "50ms",
        "transition-normal": "90ms",
        "transition-slow": "140ms",
        "transition-timing": "linear",
        "hover-scale": "1.0",
        "hover-translate-y": "-2px",
        "hover-translate-x": "-2px",
        "active-scale": "1.0",
        "active-translate-y": "2px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ff4a55"
      },
      radii: {
        none: "0px",
        sm: "6px",
        md: "6px",
        lg: "10px",
        xl: "14px",
        full: "0px"
      },
      surface: {
        cardImage: "none",
        pageImage: "radial-gradient(rgba(243,230,200,0.08) 1.2px, transparent 1.6px)",
        pageImageSize: "7px 7px"
      }
    }
  }
};

// themes/corporate.json
var corporate_default = {
  name: "corporate",
  displayName: "Corporate",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "34px",
      buttonHeightLg: "46px",
      inputHeightSm: "30px",
      inputHeightMd: "34px",
      inputHeightLg: "46px",
      rowHeightCompact: "36px",
      rowHeightNormal: "36px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "16px",
      cardPaddingMd: "16px",
      cardPaddingLg: "28px",
      dialogPadding: "28px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.005em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "300ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.4, 0, 0.2, 1)",
        emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "translateY(8px)",
        modalExit: "translateY(8px)",
        pageEnter: "translateY(4px)",
        popoverEnter: "translateY(4px)",
        popoverExit: "translateY(4px)",
        toastEnter: "translateY(8px)",
        toastExit: "translateY(8px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.75",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "2px",
      radiusInteractive: "2px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "2px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#1d4ed8",
      "primary-hover": "#1e40af",
      "primary-foreground": "#ffffff",
      secondary: "#e2e8f0",
      "secondary-hover": "#cbd5e1",
      "secondary-foreground": "#1e293b",
      accent: "#a16207",
      "accent-foreground": "#ffffff",
      muted: "#eef1f6",
      "muted-foreground": "#5b6b80",
      background: "#f4f6f9",
      foreground: "#1e293b",
      card: "#ffffff",
      "card-foreground": "#1e293b",
      surface: "#e8edf3",
      border: "#d7dee8",
      input: "#c3cdd9",
      ring: "#1d4ed8",
      error: "#b91c1c",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#b45309",
      "warning-foreground": "#ffffff",
      info: "#1d4ed8",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#1d4ed8",
      "series-2": "#d77c36",
      "series-3": "#0f766e",
      "series-4": "#7c3aed",
      "series-5": "#c026d3",
      "series-6": "#484a60",
      "series-7": "#618305",
      "series-8": "#0284c7",
      "diagram-ink": "#1e293b",
      "diagram-label": "#334155",
      "diagram-guide": "#8590a0",
      "diagram-axis": "#64748b",
      "diagram-grid": "#e2e8f0",
      "diagram-highlight": "#a66e0e",
      "diagram-fill": "#1d4ed8"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "2px",
      lg: "2px",
      xl: "4px",
      full: "9999px"
    },
    shadows: {
      main: "0 1px 0 rgba(15, 23, 42, 0.12)",
      sm: "0 1px 0 rgba(15, 23, 42, 0.08)",
      lg: "0 2px 0 rgba(15, 23, 42, 0.14), 0 6px 12px rgba(15, 23, 42, 0.08)",
      inner: "inset 0 1px 0 rgba(15, 23, 42, 0.06)",
      none: "none",
      hover: "0 1px 0 rgba(15, 23, 42, 0.18)",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "600",
      "letter-spacing": "normal",
      "line-height": "1.45",
      "icon-color": "#475569",
      "transition-fast": "100ms",
      "transition-normal": "150ms",
      "transition-slow": "200ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#1d4ed8"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "2px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "5 3",
      dot: "1 3",
      fillStyle: "tint",
      fillOpacity: "0.12",
      roughness: "0",
      glow: "0",
      shadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.5",
      materialMetalness: "0.2",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "1",
      lightKeyColor: "#ffffff",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#5b8def",
        "primary-hover": "#7aa5f5",
        "primary-foreground": "#0d1526",
        secondary: "#1c2942",
        "secondary-hover": "#24344f",
        "secondary-foreground": "#dbe3f0",
        accent: "#e0b24a",
        "accent-foreground": "#0d1526",
        muted: "#1a2438",
        "muted-foreground": "#8b98ad",
        background: "#0d1526",
        foreground: "#dbe3f0",
        card: "#14203a",
        "card-foreground": "#dbe3f0",
        surface: "#182742",
        border: "#2a3a58",
        input: "#35486b",
        ring: "#5b8def",
        error: "#f87171",
        "error-foreground": "#0d1526",
        success: "#4ade80",
        "success-foreground": "#0d1526",
        warning: "#fbbf24",
        "warning-foreground": "#0d1526",
        info: "#7aa5f5",
        "info-foreground": "#0d1526",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#6686e1",
        "series-2": "#bd7814",
        "series-3": "#1d9b91",
        "series-4": "#9d71e9",
        "series-5": "#d156cc",
        "series-6": "#6d768f",
        "series-7": "#62981c",
        "series-8": "#1090d3",
        "diagram-ink": "#dbe3f0",
        "diagram-label": "#b8c4d8",
        "diagram-guide": "#6a7a98",
        "diagram-axis": "#8a9ab8",
        "diagram-grid": "#243452",
        "diagram-highlight": "#bea635",
        "diagram-fill": "#5b8def"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "600",
        "letter-spacing": "normal",
        "line-height": "1.45",
        "icon-color": "#94a3b8",
        "transition-fast": "100ms",
        "transition-normal": "150ms",
        "transition-slow": "200ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#5b8def"
      },
      shadows: {
        main: "0 1px 0 rgba(0, 0, 0, 0.45)",
        sm: "0 1px 0 rgba(0, 0, 0, 0.35)",
        lg: "0 2px 0 rgba(0, 0, 0, 0.5), 0 6px 12px rgba(0, 0, 0, 0.4)",
        inner: "inset 0 1px 0 rgba(0, 0, 0, 0.3)",
        none: "none",
        hover: "0 1px 0 rgba(0, 0, 0, 0.55)",
        active: "none"
      }
    }
  }
};

// themes/cyberpunk.json
var cyberpunk_default = {
  name: "cyberpunk",
  displayName: "Cyberpunk HUD",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "7px",
        space3: "11px",
        space4: "15px",
        space5: "18px",
        space6: "22px",
        space7: "26px",
        space8: "29px",
        space9: "33px",
        space10: "37px",
        space11: "40px",
        space12: "44px"
      },
      buttonHeightSm: "26px",
      buttonHeightMd: "34px",
      buttonHeightLg: "44px",
      inputHeightSm: "26px",
      inputHeightMd: "34px",
      inputHeightLg: "44px",
      rowHeightCompact: "30px",
      rowHeightNormal: "38px",
      rowHeightSpacious: "46px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "22px",
      dialogPadding: "22px",
      sectionGap: "28px"
    },
    typeScale: {
      displayFamily: '"Orbitron", "Chakra Petch", sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Share Tech Mono", ui-monospace, monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&display=swap",
      headingWeight: "700",
      headingTransform: "uppercase",
      headingTracking: "0.14em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "11px",
          lineHeight: "16px"
        },
        sm: {
          size: "13px",
          lineHeight: "18px"
        },
        base: {
          size: "15px",
          lineHeight: "22px"
        },
        lg: {
          size: "17px",
          lineHeight: "24px"
        },
        xl: {
          size: "19px",
          lineHeight: "26px"
        },
        "2xl": {
          size: "22px",
          lineHeight: "30px"
        },
        "3xl": {
          size: "28px",
          lineHeight: "34px"
        },
        "4xl": {
          size: "34px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "44px",
          lineHeight: "48px"
        },
        "display-2": {
          size: "56px",
          lineHeight: "60px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "60ms",
        normal: "120ms",
        slow: "200ms",
        dramatic: "300ms"
      },
      easings: {
        linear: "steps(2, end)",
        standard: "steps(2, end)",
        emphasized: "steps(3, end)",
        spring: "steps(2, end)"
      },
      shapes: {
        modalEnter: "scaleY(0.02)",
        modalExit: "scaleY(0.02)",
        pageEnter: "translateX(-12px)",
        popoverEnter: "scaleY(0.1)",
        popoverExit: "scaleY(0.1)",
        toastEnter: "translateX(24px)",
        toastExit: "translateX(24px)"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "12px",
      radiusInteractive: "10px",
      radiusPill: "12px",
      cornerShape: "bevel",
      cornerShapePill: "bevel",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.07) 0px, rgba(0, 0, 0, 0.07) 1px, transparent 1px, transparent 3px)",
      pageImageSize: "auto"
    },
    colors: {
      primary: "#000000",
      "primary-hover": "#2a2a2a",
      "primary-foreground": "#fcee0a",
      secondary: "#ff2a6d",
      "secondary-hover": "#ff5c8d",
      "secondary-foreground": "#000000",
      accent: "#00606b",
      "accent-foreground": "#ffffff",
      muted: "#f0e200",
      "muted-foreground": "#2a2600",
      background: "#fcee0a",
      foreground: "#000000",
      card: "#fff9a8",
      "card-foreground": "#000000",
      surface: "#fff36a",
      border: "#000000",
      input: "#000000",
      ring: "#000000",
      "table-header": "#fff36a",
      "table-border": "#000000",
      "table-row-hover": "#ffe600",
      "surface-hover": "#ffe600",
      "border-hover": "#000000",
      placeholder: "#4a4400",
      error: "#b0002a",
      "error-foreground": "#ffffff",
      success: "#006b2e",
      "success-foreground": "#ffffff",
      warning: "#ff9500",
      "warning-foreground": "#000000",
      info: "#0050d0",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#c0045c",
      "series-2": "#006b8a",
      "series-3": "#5a1fb0",
      "series-4": "#353535",
      "series-5": "#8a4a00",
      "series-6": "#006163",
      "series-7": "#9a0a9a",
      "series-8": "#0244bc",
      "diagram-ink": "#000000",
      "diagram-label": "#000000",
      "diagram-guide": "#6a6430",
      "diagram-axis": "#111111",
      "diagram-grid": "#e6dd70",
      "diagram-highlight": "#ff2a6d",
      "diagram-fill": "#000000"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "10px",
      lg: "12px",
      xl: "14px",
      full: "12px"
    },
    shadows: {
      main: "3px 3px 0 #000000, 6px 6px 0 #ff2a6d",
      sm: "2px 2px 0 #000000, 4px 4px 0 #ff2a6d",
      lg: "4px 4px 0 #000000, 8px 8px 0 #ff2a6d",
      inner: "inset 2px 2px 0 rgba(0,0,0,.25)",
      none: "none",
      hover: "4px 4px 0 #000000, 8px 8px 0 #00b8c8",
      active: "0 0 0 #000000"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Rajdhani", "Chakra Petch", system-ui, sans-serif',
      "font-weight-normal": "500",
      "font-weight-medium": "600",
      "font-weight-bold": "700",
      "letter-spacing": "0.02em",
      "line-height": "1.4",
      "icon-color": "#000000",
      "transition-fast": "60ms",
      "transition-normal": "120ms",
      "transition-slow": "200ms",
      "transition-timing": "steps(2, end)",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "1px",
      "active-scale": "0.98",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#000000"
    },
    diagram: {
      strokeThin: "1.5px",
      strokeNormal: "2.5px",
      strokeBold: "4px",
      lineCap: "square",
      lineJoin: "miter",
      dash: "4 3",
      dot: "1 3",
      fillStyle: "tint",
      fillOpacity: "0.25",
      roughness: "0",
      glow: "6",
      shadow: "2px 2px 0 #ff2a6d",
      marker: "line",
      labelFont: "display",
      labelSize: "xs",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.3",
      materialMetalness: "0.7",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.35",
      lightKey: "1.2",
      lightKeyColor: "#ff5ad0",
      fog: "0.2"
    }
  },
  variants: {
    dark: {
      typeScale: {
        displayFamily: '"Orbitron", "Chakra Petch", sans-serif',
        bodyFamily: "var(--font-family)",
        monoFamily: '"Share Tech Mono", ui-monospace, monospace',
        fontImport: "https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&display=swap",
        headingWeight: "700",
        headingTransform: "uppercase",
        headingTracking: "0.14em",
        headingStyle: "normal",
        scale: {
          xs: {
            size: "11px",
            lineHeight: "16px"
          },
          sm: {
            size: "13px",
            lineHeight: "18px"
          },
          base: {
            size: "15px",
            lineHeight: "22px"
          },
          lg: {
            size: "17px",
            lineHeight: "24px"
          },
          xl: {
            size: "19px",
            lineHeight: "26px"
          },
          "2xl": {
            size: "22px",
            lineHeight: "30px"
          },
          "3xl": {
            size: "28px",
            lineHeight: "34px"
          },
          "4xl": {
            size: "34px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "44px",
            lineHeight: "48px"
          },
          "display-2": {
            size: "56px",
            lineHeight: "60px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      surface: {
        pageImage: "repeating-linear-gradient(0deg, rgba(0, 240, 255, 0.05) 0px, rgba(0, 240, 255, 0.05) 1px, transparent 1px, transparent 3px)",
        pageImageSize: "auto"
      },
      colors: {
        primary: "#fcee0a",
        "primary-hover": "#fff35c",
        "primary-foreground": "#000000",
        secondary: "#ff2a6d",
        "secondary-hover": "#ff5c8d",
        "secondary-foreground": "#000000",
        accent: "#00f0ff",
        "accent-foreground": "#001418",
        muted: "#1d1d33",
        "muted-foreground": "#b8c4e0",
        background: "#0a0a14",
        foreground: "#e8f4ff",
        card: "#12121f",
        "card-foreground": "#e8f4ff",
        surface: "#0f0f1c",
        border: "#00f0ff",
        input: "#00f0ff",
        ring: "#fcee0a",
        "table-header": "#0f0f1c",
        "table-border": "#00f0ff",
        "table-row-hover": "#1a1a30",
        "surface-hover": "#1a1a30",
        "border-hover": "#fcee0a",
        placeholder: "#7a86a8",
        error: "#ff4d6d",
        "error-foreground": "#1a0008",
        success: "#2bff9a",
        "success-foreground": "#002a14",
        warning: "#ffb000",
        "warning-foreground": "#1a1000",
        info: "#4dc3ff",
        "info-foreground": "#001a2e",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#00f0ff",
        "series-2": "#ed1d86",
        "series-3": "#c6f246",
        "series-4": "#b36bff",
        "series-5": "#63da3c",
        "series-6": "#ff8a00",
        "series-7": "#5a8aff",
        "series-8": "#ff6bd6",
        "diagram-ink": "#e8f4ff",
        "diagram-label": "#cfe8ff",
        "diagram-guide": "#5a6a9a",
        "diagram-axis": "#7ad8e8",
        "diagram-grid": "#1f2040",
        "diagram-highlight": "#fcee0a",
        "diagram-fill": "#00f0ff"
      },
      shadows: {
        main: "0 0 0 1px rgba(0,240,255,.45), 0 0 12px rgba(0,240,255,.35), 0 0 32px rgba(255,42,109,.2)",
        sm: "0 0 0 1px rgba(0,240,255,.35), 0 0 6px rgba(0,240,255,.3)",
        lg: "0 0 0 1px rgba(0,240,255,.55), 0 0 20px rgba(0,240,255,.45), 0 0 48px rgba(255,42,109,.3)",
        inner: "inset 0 0 10px rgba(0,240,255,.25)",
        none: "none",
        hover: "0 0 0 1px rgba(252,238,10,.8), 0 0 16px rgba(252,238,10,.55), 0 0 36px rgba(255,42,109,.3)",
        active: "inset 0 0 12px rgba(252,238,10,.45)"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Rajdhani", "Chakra Petch", system-ui, sans-serif',
        "font-weight-normal": "500",
        "font-weight-medium": "600",
        "font-weight-bold": "700",
        "letter-spacing": "0.02em",
        "line-height": "1.4",
        "icon-color": "#00f0ff",
        "transition-fast": "60ms",
        "transition-normal": "120ms",
        "transition-slow": "200ms",
        "transition-timing": "steps(2, end)",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "1px",
        "active-scale": "0.98",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#fcee0a"
      }
    }
  }
};

// themes/frutiger-aero.json
var frutiger_aero_default = {
  name: "frutiger-aero",
  displayName: "Frutiger Aero",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "13px",
        space4: "17px",
        space5: "21px",
        space6: "25px",
        space7: "29px",
        space8: "34px",
        space9: "38px",
        space10: "42px",
        space11: "46px",
        space12: "50px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "38px",
      buttonHeightLg: "48px",
      inputHeightSm: "28px",
      inputHeightMd: "38px",
      inputHeightLg: "48px",
      rowHeightCompact: "34px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "14px",
      cardPaddingMd: "20px",
      cardPaddingLg: "28px",
      dialogPadding: "28px",
      sectionGap: "36px"
    },
    typeScale: {
      displayFamily: '"Quicksand", "Nunito", system-ui, sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Quicksand:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap",
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "0em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "150ms",
        normal: "250ms",
        slow: "400ms",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.22, 1, 0.36, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.9) translateY(16px)",
        modalExit: "scale(0.9) translateY(16px)",
        pageEnter: "translateY(12px)",
        popoverEnter: "scale(0.94)",
        popoverExit: "scale(0.94)",
        toastEnter: "translateY(20px)",
        toastExit: "translateY(20px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "18px",
      radiusInteractive: "16px",
      radiusPill: "var(--radius-full)",
      cornerShape: "squircle",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "blur(14px) saturate(1.4)",
      cardImage: "linear-gradient(180deg, rgba(255,255,255,.55), rgba(255,255,255,0) 45%)",
      pageImage: "radial-gradient(circle at 12% 18%, rgba(255,255,255,.85) 0 5%, transparent 5.5%), radial-gradient(circle at 84% 10%, rgba(255,255,255,.7) 0 3.5%, transparent 4%), radial-gradient(circle at 72% 72%, rgba(255,255,255,.45) 0 7%, transparent 7.5%), radial-gradient(circle at 25% 80%, rgba(255,255,255,.4) 0 4%, transparent 4.5%), radial-gradient(ellipse at 50% 115%, rgba(90,200,120,.5), transparent 55%), linear-gradient(180deg, #8fd3ff 0%, #d9f2ff 55%, #e9fbe8 100%)",
      pageImageSize: "cover",
      diagram: "#f4fbff"
    },
    colors: {
      primary: "#0873b8",
      "primary-hover": "#065f9b",
      "primary-foreground": "#ffffff",
      secondary: "#7ddb8a",
      "secondary-hover": "#66cf76",
      "secondary-foreground": "#06300c",
      accent: "#177a31",
      "accent-foreground": "#ffffff",
      muted: "#dcf0fb",
      "muted-foreground": "#3d5a70",
      background: "#d9f2ff",
      foreground: "#0d3350",
      card: "rgba(255, 255, 255, 0.78)",
      "card-foreground": "#0d3350",
      surface: "#eaf7ff",
      border: "rgba(255, 255, 255, 0.85)",
      input: "rgba(8, 115, 184, 0.45)",
      ring: "#0873b8",
      "table-header": "#eaf7ff",
      "table-border": "rgba(255, 255, 255, 0.85)",
      "table-row-hover": "#cdeafb",
      "surface-hover": "#cdeafb",
      "border-hover": "#0873b8",
      placeholder: "#56718a",
      error: "#c62a2a",
      "error-foreground": "#ffffff",
      success: "#177a31",
      "success-foreground": "#ffffff",
      warning: "#ffc93c",
      "warning-foreground": "#2a1e00",
      info: "#0873b8",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#5081f4",
      "series-2": "#3f9d4c",
      "series-3": "#0e8fa0",
      "series-4": "#b8820a",
      "series-5": "#c84a8a",
      "series-6": "#6a5ac8",
      "series-7": "#dc6a14",
      "series-8": "#e26144",
      "diagram-ink": "#0d3350",
      "diagram-label": "#0d3350",
      "diagram-guide": "#7a9ab0",
      "diagram-axis": "#3a6a8a",
      "diagram-grid": "#cfe6f2",
      "diagram-highlight": "#0873b8",
      "diagram-fill": "#0873b8"
    },
    radii: {
      none: "0px",
      sm: "10px",
      md: "16px",
      lg: "18px",
      xl: "24px",
      full: "9999px"
    },
    shadows: {
      main: "0 8px 24px rgba(10,90,150,.18), 0 2px 6px rgba(10,90,150,.12), inset 0 1px 0 rgba(255,255,255,.9)",
      sm: "0 3px 10px rgba(10,90,150,.16), 0 1px 3px rgba(10,90,150,.1), inset 0 1px 0 rgba(255,255,255,.9)",
      lg: "0 16px 40px rgba(10,90,150,.22), 0 4px 10px rgba(10,90,150,.14), inset 0 1px 0 rgba(255,255,255,.9)",
      inner: "inset 0 2px 6px rgba(10,90,150,.2)",
      none: "none",
      hover: "0 6px 16px rgba(10,90,150,.28), inset 0 1px 0 rgba(255,255,255,.75)",
      active: "inset 0 2px 6px rgba(10,90,150,.35)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Nunito", "Quicksand", system-ui, sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.55",
      "icon-color": "#0873b8",
      "transition-fast": "150ms",
      "transition-normal": "250ms",
      "transition-slow": "400ms",
      "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
      "hover-scale": "1.02",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0px",
      "active-scale": "0.98",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#0873b8"
    },
    diagram: {
      strokeThin: "1.5px",
      strokeNormal: "2.5px",
      strokeBold: "4px",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.28",
      roughness: "0",
      glow: "3",
      shadow: "0 3px 8px rgba(10, 90, 150, 0.25)",
      marker: "dot",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "12px"
    },
    scene: {
      materialRoughness: "0.2",
      materialMetalness: "0.1",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "1.1",
      lightKeyColor: "#f0fbff",
      fog: "0.2"
    }
  },
  variants: {
    dark: {
      typeScale: {
        displayFamily: '"Quicksand", "Nunito", system-ui, sans-serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontImport: "https://fonts.googleapis.com/css2?family=Quicksand:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap",
        headingWeight: "700",
        headingTransform: "none",
        headingTracking: "0em",
        headingStyle: "normal",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      surface: {
        backdrop: "blur(14px) saturate(1.4)",
        cardImage: "linear-gradient(180deg, rgba(255,255,255,.14), rgba(255,255,255,0) 45%)",
        pageImage: "radial-gradient(circle at 12% 18%, rgba(160,230,255,.28) 0 5%, transparent 5.5%), radial-gradient(circle at 84% 10%, rgba(160,230,255,.22) 0 3.5%, transparent 4%), radial-gradient(circle at 72% 72%, rgba(160,230,255,.16) 0 7%, transparent 7.5%), radial-gradient(ellipse at 50% 115%, rgba(40,160,90,.45), transparent 55%), linear-gradient(180deg, #0b3a66 0%, #0a2a4a 60%, #0a2a30 100%)",
        pageImageSize: "cover"
      },
      colors: {
        primary: "#5cc4ff",
        "primary-hover": "#3aaef2",
        "primary-foreground": "#04243a",
        secondary: "#49c463",
        "secondary-hover": "#36b052",
        "secondary-foreground": "#032a0c",
        accent: "#8ce99a",
        "accent-foreground": "#032a0c",
        muted: "#16406a",
        "muted-foreground": "#b9dcf5",
        background: "#0a2a4a",
        foreground: "#e6f6ff",
        card: "rgba(18, 62, 104, 0.78)",
        "card-foreground": "#e6f6ff",
        surface: "#0f3558",
        border: "rgba(255, 255, 255, 0.28)",
        input: "rgba(140, 215, 255, 0.5)",
        ring: "#7fd4ff",
        "table-header": "#0f3558",
        "table-border": "rgba(255, 255, 255, 0.28)",
        "table-row-hover": "#184a78",
        "surface-hover": "#184a78",
        "border-hover": "#5cc4ff",
        placeholder: "#8fb6d6",
        error: "#ff7a7a",
        "error-foreground": "#2a0505",
        success: "#57d878",
        "success-foreground": "#032010",
        warning: "#ffd45c",
        "warning-foreground": "#2a1e00",
        info: "#5cc4ff",
        "info-foreground": "#04243a",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#3394cf",
        "series-2": "#93c842",
        "series-3": "#0b9f82",
        "series-4": "#c97505",
        "series-5": "#bb7a9b",
        "series-6": "#8e77ae",
        "series-7": "#eb621a",
        "series-8": "#bf7a5f",
        "diagram-ink": "#ffffff",
        "diagram-label": "#cdeaff",
        "diagram-guide": "#7ab4d8",
        "diagram-axis": "#9ad0f0",
        "diagram-grid": "#1f5a8c",
        "diagram-highlight": "#719e38",
        "diagram-fill": "#5cc4ff"
      },
      shadows: {
        main: "0 8px 24px rgba(0,0,0,.45), 0 2px 6px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.2)",
        sm: "0 3px 10px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.2)",
        lg: "0 16px 40px rgba(0,0,0,.55), 0 4px 10px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.2)",
        inner: "inset 0 2px 6px rgba(0,0,0,.5)",
        none: "none",
        hover: "0 6px 16px rgba(80,200,255,.35), inset 0 1px 0 rgba(255,255,255,.3)",
        active: "inset 0 2px 6px rgba(0,0,0,.55)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Nunito", "Quicksand", system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.55",
        "icon-color": "#7fd4ff",
        "transition-fast": "150ms",
        "transition-normal": "250ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
        "hover-scale": "1.02",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0px",
        "active-scale": "0.98",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#7fd4ff"
      }
    }
  }
};

// themes/game-adventure.json
var game_adventure_default = {
  name: "game-adventure",
  displayName: "Game: Adventure",
  tokens: {},
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "28px",
        buttonHeightMd: "36px",
        buttonHeightLg: "44px",
        inputHeightSm: "28px",
        inputHeightMd: "36px",
        inputHeightLg: "44px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "16px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: "var(--font-family)",
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        headingWeight: "700",
        headingTransform: "uppercase",
        headingTracking: "0.05em",
        headingStyle: "normal"
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "cubic-bezier(0.34, 1.3, 0.64, 1)",
          emphasized: "cubic-bezier(0.2, 0, 0, 1)",
          spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        },
        shapes: {
          modalEnter: "scale(0.8)",
          modalExit: "scale(0.8)",
          pageEnter: "translateY(12px)",
          popoverEnter: "scale(0.9)",
          popoverExit: "scale(0.9)",
          toastEnter: "translateY(20px)",
          toastExit: "translateY(20px)"
        }
      },
      iconography: {
        family: "fa-solid",
        strokeWidth: "0",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-main)",
        popoverElevation: "var(--shadow-lg)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "outset",
        borderStyleInteractive: "outset",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      colors: {
        primary: "#b5793a",
        "primary-hover": "#c98d4c",
        "primary-foreground": "#241608",
        secondary: "#3a2712",
        "secondary-hover": "#4a3319",
        "secondary-foreground": "#f3e3c4",
        accent: "#e0a52c",
        "accent-foreground": "#241608",
        muted: "#e8d6ae",
        "muted-foreground": "#6b4f2a",
        background: "#241608",
        foreground: "#f3e3c4",
        card: "#fff1d2",
        "card-foreground": "#4a2f14",
        surface: "#2e1d0d",
        border: "#7a5228",
        input: "#3a2712",
        ring: "#e0a52c",
        error: "#d9534f",
        "error-foreground": "#1a0808",
        success: "#7ba05b",
        "success-foreground": "#0d1a08",
        warning: "#e0a52c",
        "warning-foreground": "#241608",
        info: "#b5793a",
        "info-foreground": "#241608",
        scrim: "rgba(0, 0, 0, 0.7)",
        "diagram-ink": "#3a2712",
        "diagram-label": "#4a3319",
        "diagram-guide": "#9a7a4a",
        "diagram-axis": "#6b4f2a",
        "diagram-grid": "#e3cfa3",
        "diagram-highlight": "#ab3f20",
        "diagram-fill": "#f3dcae",
        "series-1": "#701e08",
        "series-2": "#2f5f8a",
        "series-3": "#5f7f2a",
        "series-4": "#7a3f7a",
        "series-5": "#b0702a",
        "series-6": "#2a7a6a",
        "series-7": "#8a2a3a",
        "series-8": "#5a5a2a"
      },
      radii: {
        none: "0px",
        sm: "8px",
        md: "10px",
        lg: "14px",
        xl: "18px",
        full: "9999px"
      },
      shadows: {
        main: "inset 0 2px 0 rgba(255,220,160,0.28), inset 0 -3px 0 rgba(0,0,0,0.35), 0 3px 8px rgba(0,0,0,0.55)",
        sm: "inset 0 1px 0 rgba(255,220,160,0.25), inset 0 -2px 0 rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.5)",
        lg: "inset 0 2px 0 rgba(255,220,160,0.3), inset 0 -4px 0 rgba(0,0,0,0.4), 0 10px 24px rgba(0,0,0,0.6)",
        inner: "inset 0 3px 6px rgba(0,0,0,0.5)",
        none: "none",
        hover: "inset 0 2px 0 rgba(255,220,160,0.4), inset 0 -3px 0 rgba(0,0,0,0.35), 0 5px 12px rgba(0,0,0,0.55)",
        active: "inset 0 3px 0 rgba(0,0,0,0.45)"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "game-panel-border-image": "url('https://almadar-kflow-assets.web.app/shared/ui-game-shell/kenney-ui-pack-adventure/ui/panel_brownimg.png')",
        "game-panel-border-image-slice": "17 fill",
        "game-panel-border-image-width": "14px",
        "font-family": '"Kenney Blocks", ui-sans-serif, system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.02em",
        "line-height": "1.5",
        "icon-color": "#e0a52c",
        "transition-fast": "100ms",
        "transition-normal": "200ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#e0a52c"
      },
      surface: {
        cardImage: "linear-gradient(180deg, rgba(122,82,40,0.06), rgba(122,82,40,0.14))",
        pageImage: "repeating-linear-gradient(90deg, rgba(0,0,0,0.10) 0px, rgba(0,0,0,0.10) 2px, transparent 2px, transparent 9px)",
        pageImageSize: "auto"
      }
    }
  }
};

// themes/game-rpg.json
var game_rpg_default = {
  name: "game-rpg",
  displayName: "Game: RPG",
  tokens: {},
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "28px",
        buttonHeightMd: "36px",
        buttonHeightLg: "44px",
        inputHeightSm: "28px",
        inputHeightMd: "36px",
        inputHeightLg: "44px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "16px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"MedievalSharp", Georgia, serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        fontImport: "https://fonts.googleapis.com/css2?family=MedievalSharp&display=swap",
        headingWeight: "400",
        headingTransform: "none",
        headingTracking: "0.02em",
        headingStyle: "normal"
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "cubic-bezier(0.4, 0, 0.2, 1)",
          emphasized: "cubic-bezier(0.2, 0, 0, 1)",
          spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        },
        shapes: {
          modalEnter: "perspective(800px) rotateX(-20deg)",
          modalExit: "perspective(800px) rotateX(-20deg)",
          pageEnter: "translateY(8px)",
          popoverEnter: "scale(0.95)",
          popoverExit: "scale(0.95)",
          toastEnter: "translateY(-16px)",
          toastExit: "translateY(-16px)"
        }
      },
      iconography: {
        family: "phosphor-duotone",
        strokeWidth: "1.5",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-main)",
        popoverElevation: "var(--shadow-lg)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-inner)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        cornerShape: "scoop",
        cornerShapePill: "scoop",
        borderStyle: "ridge",
        borderStyleInteractive: "ridge",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      colors: {
        primary: "#d99a4e",
        "primary-hover": "#e6ac66",
        "primary-foreground": "#1a1108",
        secondary: "#4a3520",
        "secondary-hover": "#5a4227",
        "secondary-foreground": "#f5e6cf",
        accent: "#f0c869",
        "accent-foreground": "#1a1108",
        muted: "#3a2a18",
        "muted-foreground": "#e0cba8",
        background: "#1a1108",
        foreground: "#f5e6cf",
        card: "#6e4f30",
        "card-foreground": "#fff5e0",
        surface: "#2a1d10",
        border: "#886644",
        input: "#4a3520",
        ring: "#f0c869",
        error: "#d9534f",
        "error-foreground": "#1a0808",
        success: "#7ba05b",
        "success-foreground": "#0d1a08",
        warning: "#f0c869",
        "warning-foreground": "#1a1108",
        info: "#d99a4e",
        "info-foreground": "#1a1108",
        scrim: "rgba(0, 0, 0, 0.7)",
        "diagram-ink": "#fbfcd8",
        "diagram-label": "#e0cba8",
        "diagram-guide": "#a8875c",
        "diagram-axis": "#c9ad7f",
        "diagram-grid": "#4a3520",
        "diagram-highlight": "#e9e990",
        "diagram-fill": "#3a2a18",
        "series-1": "#d99a4e",
        "series-2": "#7fb0d9",
        "series-3": "#9ccf6a",
        "series-4": "#d98ab0",
        "series-5": "#b4ad64",
        "series-6": "#7fd0c0",
        "series-7": "#ea8368",
        "series-8": "#b39ddb"
      },
      radii: {
        none: "0px",
        sm: "6px",
        md: "10px",
        lg: "14px",
        xl: "18px",
        full: "9999px"
      },
      shadows: {
        main: "inset 0 0 0 1px rgba(240,200,105,0.30), 0 4px 10px rgba(0,0,0,0.55)",
        sm: "inset 0 0 0 1px rgba(240,200,105,0.25), 0 2px 5px rgba(0,0,0,0.5)",
        lg: "inset 0 0 0 1px rgba(240,200,105,0.35), 0 12px 28px rgba(0,0,0,0.65)",
        inner: "inset 0 2px 6px rgba(0,0,0,0.55)",
        none: "none",
        hover: "inset 0 0 0 1px rgba(240,200,105,0.5), 0 6px 16px rgba(240,200,105,0.22)",
        active: "inset 0 3px 6px rgba(0,0,0,0.6)"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "game-panel-border-image": "url('https://almadar-kflow-assets.web.app/shared/ui-game-shell/kenney-ui-adventure-pack/ui/panel_brown.png')",
        "game-panel-border-image-slice": "26 fill",
        "game-panel-border-image-width": "22px",
        "game-button-bg-image": "url('https://almadar-kflow-assets.web.app/shared/ui-game-shell/kenney-ui-adventure-pack/ui/buttonLong_brown.png')",
        "font-family": '"Kenney Mini", ui-sans-serif, system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.02em",
        "line-height": "1.5",
        "icon-color": "#f0c869",
        "transition-fast": "100ms",
        "transition-normal": "200ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#f0c869"
      },
      surface: {
        cardImage: "linear-gradient(180deg, rgba(240,200,105,0.10) 0%, rgba(0,0,0,0.12) 100%)",
        pageImage: "radial-gradient(ellipse at 50% 0%, rgba(240,200,105,0.10), transparent 60%)",
        pageImageSize: "100% 100%",
        diagram: "#22170c"
      }
    }
  }
};

// themes/game-sci-fi.json
var game_sci_fi_default = {
  name: "game-sci-fi",
  displayName: "Game: Sci-Fi",
  tokens: {},
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "28px",
        buttonHeightMd: "36px",
        buttonHeightLg: "44px",
        inputHeightSm: "28px",
        inputHeightMd: "36px",
        inputHeightLg: "44px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "16px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Orbitron", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        bodyFamily: '"Rajdhani", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        fontImport: "https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@400;500;600;700&display=swap",
        headingWeight: "700",
        headingTransform: "uppercase",
        headingTracking: "0.14em",
        headingStyle: "normal"
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "cubic-bezier(0.25, 0, 0.35, 1)",
          emphasized: "cubic-bezier(0.25, 0, 0.35, 1)",
          spring: "cubic-bezier(0.25, 0, 0.35, 1)"
        },
        shapes: {
          modalEnter: "scaleY(0.04)",
          modalExit: "scaleY(0.04)",
          pageEnter: "translateX(-12px)",
          popoverEnter: "scaleY(0.1)",
          popoverExit: "scaleY(0.1)",
          toastEnter: "translateX(24px)",
          toastExit: "translateX(24px)"
        }
      },
      iconography: {
        family: "tabler",
        strokeWidth: "1.5",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-md)",
        radiusInteractive: "var(--radius-sm)",
        radiusPill: "var(--radius-full)",
        cornerShape: "bevel",
        cornerShapePill: "bevel",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      colors: {
        primary: "#3ea6ff",
        "primary-hover": "#6cc3ff",
        "primary-foreground": "#071018",
        secondary: "#182338",
        "secondary-hover": "#22314c",
        "secondary-foreground": "#dceaf7",
        accent: "#ffcc4d",
        "accent-foreground": "#1a1408",
        muted: "#22314c",
        "muted-foreground": "#8b9bb6",
        background: "#0a0e1a",
        foreground: "#e8f4fb",
        card: "#141d30",
        "card-foreground": "#e8f4fb",
        surface: "#141d30",
        border: "#3a4a66",
        input: "#182338",
        ring: "#3ea6ff",
        error: "#ff5c5c",
        "error-foreground": "#1a0808",
        success: "#4ade80",
        "success-foreground": "#082014",
        warning: "#ffcc4d",
        "warning-foreground": "#1a1408",
        info: "#3ea6ff",
        "info-foreground": "#071018",
        scrim: "rgba(0, 0, 0, 0.7)",
        "diagram-ink": "#dbe7ff",
        "diagram-label": "#b8c6e0",
        "diagram-guide": "#5a6f94",
        "diagram-axis": "#8b9bb6",
        "diagram-grid": "#22314c",
        "diagram-highlight": "#7df9ff",
        "diagram-fill": "#1c2942",
        "series-1": "#3ea6ff",
        "series-2": "#ff7ab6",
        "series-3": "#b6f36a",
        "series-4": "#c49bff",
        "series-5": "#ff9f43",
        "series-6": "#5ef1d2",
        "series-7": "#ece86e",
        "series-8": "#dd4b6c"
      },
      radii: {
        none: "0px",
        sm: "2px",
        md: "4px",
        lg: "6px",
        xl: "8px",
        full: "9999px"
      },
      shadows: {
        main: "0 0 14px rgba(62,166,255,0.35), inset 0 0 0 1px rgba(62,166,255,0.3)",
        sm: "0 0 6px rgba(62,166,255,0.3)",
        lg: "0 0 28px rgba(62,166,255,0.45), inset 0 0 0 1px rgba(62,166,255,0.4)",
        inner: "inset 0 0 10px rgba(62,166,255,0.2)",
        none: "none",
        hover: "0 0 22px rgba(62,166,255,0.55), inset 0 0 0 1px rgba(62,166,255,0.5)",
        active: "0 0 4px rgba(62,166,255,0.5), inset 0 0 14px rgba(62,166,255,0.3)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "game-panel-border-image": "url('https://almadar-kflow-assets.web.app/shared/ui-game-shell/kenney-ui-pack-sci-fi/ui/metalPanel_blue.png')",
        "game-panel-border-image-slice": "26 fill",
        "game-panel-border-image-width": "22px",
        "font-family": '"Rajdhani", "Kenney Future", ui-sans-serif, system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.02em",
        "line-height": "1.5",
        "icon-color": "#3ea6ff",
        "transition-fast": "100ms",
        "transition-normal": "200ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#3ea6ff"
      },
      surface: {
        cardImage: "linear-gradient(180deg, rgba(62,166,255,0.10), rgba(62,166,255,0.00) 60%)",
        pageImage: "repeating-linear-gradient(0deg, rgba(62,166,255,0.06) 0px, rgba(62,166,255,0.06) 1px, transparent 1px, transparent 3px)",
        pageImageSize: "auto"
      }
    }
  }
};

// themes/game-ui-pack.json
var game_ui_pack_default = {
  name: "game-ui-pack",
  displayName: "Game: Flat UI",
  tokens: {},
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "28px",
        buttonHeightMd: "36px",
        buttonHeightLg: "44px",
        inputHeightSm: "28px",
        inputHeightMd: "36px",
        inputHeightLg: "44px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "16px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: "var(--font-family)",
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        headingWeight: "600",
        headingTransform: "none",
        headingTracking: "0.02em",
        headingStyle: "normal",
        headingShadow: "none"
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          emphasized: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        },
        shapes: {
          modalEnter: "translateY(-24px)",
          modalExit: "translateY(-24px)",
          pageEnter: "translateY(12px)",
          popoverEnter: "scale(0.9)",
          popoverExit: "scale(0.9)",
          toastEnter: "translateY(-20px)",
          toastExit: "translateY(-20px)"
        }
      },
      iconography: {
        family: "lucide",
        strokeWidth: "2.5",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-main)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "none",
        borderStyleInteractive: "none",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      colors: {
        primary: "#2ea7e1",
        "primary-hover": "#4fb8ea",
        "primary-foreground": "#06202c",
        secondary: "#3a3f47",
        "secondary-hover": "#474d56",
        "secondary-foreground": "#f2f3f4",
        accent: "#ffb703",
        "accent-foreground": "#241a00",
        muted: "#b9bec6",
        "muted-foreground": "#454a51",
        background: "#1b1e22",
        foreground: "#f2f3f4",
        card: "#eeeeee",
        "card-foreground": "#22262b",
        surface: "#24272c",
        border: "#999999",
        input: "#3a3f47",
        ring: "#2ea7e1",
        error: "#ff5c5c",
        "error-foreground": "#1a0808",
        success: "#4ade80",
        "success-foreground": "#082014",
        warning: "#ffb703",
        "warning-foreground": "#241a00",
        info: "#2ea7e1",
        "info-foreground": "#06202c",
        scrim: "rgba(0, 0, 0, 0.7)",
        "diagram-ink": "#1b1e22",
        "diagram-label": "#2e3238",
        "diagram-guide": "#8a9099",
        "diagram-axis": "#454a51",
        "diagram-grid": "#d0d4da",
        "diagram-highlight": "#e85d04",
        "diagram-fill": "#dde1e6",
        "series-1": "#1988c7",
        "series-2": "#db2c3d",
        "series-3": "#1b9486",
        "series-4": "#8338ec",
        "series-5": "#c03806",
        "series-6": "#4978f5",
        "series-7": "#619045",
        "series-8": "#b5179e"
      },
      radii: {
        none: "0px",
        sm: "10px",
        md: "12px",
        lg: "16px",
        xl: "22px",
        full: "9999px"
      },
      shadows: {
        main: "0 4px 0 0 rgba(0,0,0,0.45)",
        sm: "0 2px 0 0 rgba(0,0,0,0.45)",
        lg: "0 6px 0 0 rgba(0,0,0,0.5)",
        inner: "inset 0 2px 0 0 rgba(0,0,0,0.35)",
        none: "none",
        hover: "0 6px 0 0 rgba(0,0,0,0.45)",
        active: "0 1px 0 0 rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "game-panel-border-image": "url('https://almadar-kflow-assets.web.app/shared/ui-game-shell/kenney-ui-pack/ui/grey_panel.png')",
        "game-panel-border-image-slice": "26 fill",
        "game-panel-border-image-width": "22px",
        "game-button-bg-image": "url('https://almadar-kflow-assets.web.app/shared/ui-game-shell/kenney-ui-pack/ui/grey_button00.png')",
        "font-family": '"Kenney Future", ui-sans-serif, system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.5",
        "icon-color": "#2ea7e1",
        "transition-fast": "100ms",
        "transition-normal": "200ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#2ea7e1"
      },
      surface: {}
    }
  }
};

// themes/gazette.json
var gazette_default = {
  name: "gazette",
  displayName: "Gazette",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "32px",
        space8: "40px",
        space9: "48px",
        space10: "56px",
        space11: "64px",
        space12: "80px"
      },
      buttonHeightSm: "36px",
      buttonHeightMd: "40px",
      buttonHeightLg: "44px",
      inputHeightSm: "36px",
      inputHeightMd: "40px",
      inputHeightLg: "44px",
      rowHeightCompact: "44px",
      rowHeightNormal: "56px",
      rowHeightSpacious: "64px",
      cardPaddingSm: "16px",
      cardPaddingMd: "28px",
      cardPaddingLg: "32px",
      dialogPadding: "32px",
      sectionGap: "48px"
    },
    typeScale: {
      displayFamily: '"Fraunces", Georgia, "Times New Roman", serif',
      bodyFamily: '"Newsreader", Georgia, "Times New Roman", serif',
      monoFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "13px",
          lineHeight: "18px"
        },
        sm: {
          size: "14px",
          lineHeight: "22px"
        },
        base: {
          size: "16px",
          lineHeight: "28px"
        },
        lg: {
          size: "19px",
          lineHeight: "30px"
        },
        xl: {
          size: "22px",
          lineHeight: "32px"
        },
        "2xl": {
          size: "26px",
          lineHeight: "34px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "38px"
        },
        "4xl": {
          size: "38px",
          lineHeight: "46px"
        },
        "display-1": {
          size: "52px",
          lineHeight: "60px"
        },
        "display-2": {
          size: "68px",
          lineHeight: "76px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "3xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "xl",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "base",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&family=Newsreader:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.02em",
      headingStyle: "italic",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "250ms",
        normal: "400ms",
        slow: "600ms",
        dramatic: "900ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "translateY(10px)",
        modalExit: "translateY(10px)",
        pageEnter: "translateY(8px)",
        popoverEnter: "translateY(4px)",
        popoverExit: "translateY(4px)",
        toastEnter: "translateY(10px)",
        toastExit: "translateY(10px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "20px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "0px",
      radiusInteractive: "0px",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "2px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#1f3a5a",
      "primary-hover": "#172c45",
      "primary-foreground": "#fdfbf6",
      secondary: "#5c7a4d",
      "secondary-hover": "#4f6c41",
      "secondary-foreground": "#fdfbf6",
      accent: "#a85436",
      "accent-foreground": "#fdfbf6",
      muted: "#f3eee3",
      "muted-foreground": "#6b5f55",
      background: "#fdfbf6",
      foreground: "#1a1614",
      card: "#ffffff",
      "card-foreground": "#1a1614",
      surface: "#f8f3e8",
      border: "#e6dccb",
      input: "#e6dccb",
      ring: "#1f3a5a",
      error: "#b3402c",
      "error-foreground": "#fdfbf6",
      success: "#5a7a4b",
      "success-foreground": "#fdfbf6",
      warning: "#c08a2e",
      "warning-foreground": "#1a1614",
      info: "#4a6b8a",
      "info-foreground": "#fdfbf6",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#1f3a5a",
      "series-2": "#669875",
      "series-3": "#e15637",
      "series-4": "#8e7600",
      "series-5": "#6a3a5a",
      "series-6": "#2a6a6a",
      "series-7": "#614726",
      "series-8": "#4a6a8a",
      "diagram-ink": "#1a1614",
      "diagram-label": "#3a322c",
      "diagram-guide": "#8a8070",
      "diagram-axis": "#5a5046",
      "diagram-grid": "#e6dccb",
      "diagram-highlight": "#ae612e",
      "diagram-fill": "#1f3a5a"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Newsreader", Georgia, "Times New Roman", serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0",
      "line-height": "1.7",
      "icon-color": "#1a1614",
      "transition-fast": "250ms",
      "transition-normal": "400ms",
      "transition-slow": "600ms",
      "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
      "hover-scale": "1",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "3px",
      "focus-ring-color": "#1f3a5a"
    },
    diagram: {
      strokeThin: "0.75px",
      strokeNormal: "1px",
      strokeBold: "2px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "4 3",
      dot: "1 3",
      fillStyle: "hatch",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.8",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.65",
      lightKey: "0.9",
      lightKeyColor: "#fff6e0",
      fog: "0.05"
    },
    surface: {
      edgeMask: "none",
      edgeHeight: "0px",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><path d='M0 31.5H32' stroke='black' stroke-width='1'/></svg>")`,
      textureSize: "32px 32px",
      accentImage: "none"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#8fb0d0",
        "primary-hover": "#a5c0db",
        "primary-foreground": "#14110d",
        secondary: "#8aac76",
        "secondary-hover": "#9dbf89",
        "secondary-foreground": "#14110d",
        accent: "#c0664a",
        "accent-foreground": "#14110d",
        muted: "#1f1a16",
        "muted-foreground": "#a89e91",
        background: "#14110d",
        foreground: "#f5efe6",
        card: "#1f1a16",
        "card-foreground": "#f5efe6",
        surface: "#1a1612",
        border: "#2e2620",
        input: "#2e2620",
        ring: "#8fb0d0",
        error: "#d96650",
        "error-foreground": "#14110d",
        success: "#8aac76",
        "success-foreground": "#14110d",
        warning: "#d9a85a",
        "warning-foreground": "#14110d",
        info: "#7a9ec0",
        "info-foreground": "#14110d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#5a87bd",
        "series-2": "#658455",
        "series-3": "#d88e61",
        "series-4": "#ac7d25",
        "series-5": "#ac7299",
        "series-6": "#419090",
        "series-7": "#8b7763",
        "series-8": "#47798c",
        "diagram-ink": "#f5efe6",
        "diagram-label": "#d8cfc0",
        "diagram-guide": "#8a8070",
        "diagram-axis": "#a89e8c",
        "diagram-grid": "#342e28",
        "diagram-highlight": "#b2612f",
        "diagram-fill": "#8fb0d0"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Newsreader", Georgia, "Times New Roman", serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0",
        "line-height": "1.7",
        "icon-color": "#f5efe6",
        "transition-fast": "250ms",
        "transition-normal": "400ms",
        "transition-slow": "600ms",
        "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
        "hover-scale": "1",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "3px",
        "focus-ring-color": "#8fb0d0"
      }
    }
  }
};

// themes/glass.json
var glass_default = {
  name: "glass",
  displayName: "Glass",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "32px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "36px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "16px",
      cardPaddingMd: "20px",
      cardPaddingLg: "28px",
      dialogPadding: "28px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "medium"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=Outfit:wght@300;400;500;600&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.02em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.22, 1, 0.36, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.92) translateY(12px)",
        modalExit: "scale(0.92) translateY(12px)",
        pageEnter: "translateY(12px)",
        popoverEnter: "scale(0.95)",
        popoverExit: "scale(0.95)",
        toastEnter: "translateY(16px)",
        toastExit: "translateY(16px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-inner)"
    },
    geometry: {
      radiusContainer: "var(--radius-xl)",
      radiusInteractive: "var(--radius-lg)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#4058d6",
      "primary-hover": "#3448b8",
      "primary-foreground": "#ffffff",
      secondary: "rgba(255, 255, 255, 0.55)",
      "secondary-hover": "rgba(255, 255, 255, 0.7)",
      "secondary-foreground": "#3f3d56",
      accent: "#c2417a",
      "accent-foreground": "#ffffff",
      muted: "rgba(255, 255, 255, 0.5)",
      "muted-foreground": "#5d6a7f",
      background: "#eef1f8",
      foreground: "#1e2235",
      card: "rgba(255, 255, 255, 0.62)",
      "card-foreground": "#1e2235",
      surface: "rgba(255, 255, 255, 0.55)",
      border: "rgba(255, 255, 255, 0.35)",
      input: "rgba(64, 88, 214, 0.25)",
      ring: "#4058d6",
      error: "#e11d48",
      "error-foreground": "#ffffff",
      success: "#047857",
      "success-foreground": "#ffffff",
      warning: "#d97706",
      "warning-foreground": "#000000",
      info: "#0270ae",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#4479f5",
      "series-2": "#c2417a",
      "series-3": "#1f8aa8",
      "series-4": "#834bca",
      "series-5": "#2e9c7b",
      "series-6": "#b05b0a",
      "series-7": "#0e6ba2",
      "series-8": "#974890",
      "diagram-ink": "#1e2235",
      "diagram-label": "#44506a",
      "diagram-guide": "#7c88a2",
      "diagram-axis": "#5d6a7f",
      "diagram-grid": "rgba(30,34,53,0.10)",
      "diagram-highlight": "#4058d6",
      "diagram-fill": "#c9d3f5"
    },
    radii: {
      none: "0px",
      sm: "12px",
      md: "16px",
      lg: "22px",
      xl: "28px",
      full: "9999px"
    },
    shadows: {
      main: "0 8px 32px rgba(31,38,135,0.14)",
      sm: "0 4px 16px rgba(31,38,135,0.10)",
      lg: "0 16px 48px rgba(31,38,135,0.20)",
      inner: "inset 0 1px 0 rgba(255,255,255,0.6)",
      none: "none",
      hover: "0 12px 40px rgba(31,38,135,0.22)",
      active: "0 2px 8px rgba(31,38,135,0.12)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "650",
      "letter-spacing": "-0.005em",
      "line-height": "1.6",
      "icon-color": "#4058d6",
      "transition-fast": "120ms",
      "transition-normal": "300ms",
      "transition-slow": "500ms",
      "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
      "hover-scale": "1.01",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#4058d6"
    },
    surface: {
      backdrop: "blur(20px) saturate(1.6)",
      cardImage: "linear-gradient(135deg, rgba(255,255,255,0.45), rgba(255,255,255,0.05))",
      pageImage: "radial-gradient(60% 50% at 15% 10%, rgba(64,88,214,0.22), transparent 70%), radial-gradient(50% 45% at 85% 20%, rgba(194,65,122,0.14), transparent 70%), radial-gradient(55% 50% at 70% 90%, rgba(56,160,190,0.16), transparent 70%), radial-gradient(45% 40% at 10% 85%, rgba(64,88,214,0.12), transparent 70%)",
      pageImageSize: "100% 100%",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "1.5",
      strokeBold: "2.5",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 5",
      dot: "1 5",
      fillStyle: "tint",
      fillOpacity: "0.22",
      roughness: "0",
      glow: "0",
      shadow: "0 6px 18px rgba(64,88,214,0.18)",
      marker: "open",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "12px"
    },
    scene: {
      materialRoughness: "0.15",
      materialMetalness: "0.1",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "1",
      lightKeyColor: "#f0f4ff",
      fog: "0.15"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#8aa0ff",
        "primary-hover": "#a0b2ff",
        "primary-foreground": "#0b1020",
        secondary: "rgba(30, 36, 66, 0.55)",
        "secondary-hover": "rgba(38, 45, 80, 0.65)",
        "secondary-foreground": "#e2e5f4",
        accent: "#f08cb0",
        "accent-foreground": "#160b2e",
        muted: "rgba(30, 36, 66, 0.55)",
        "muted-foreground": "#8f96b8",
        background: "#0b1020",
        foreground: "#e7e9f5",
        card: "rgba(22, 27, 51, 0.58)",
        "card-foreground": "#e7e9f5",
        surface: "rgba(30, 36, 66, 0.55)",
        border: "rgba(255, 255, 255, 0.35)",
        input: "rgba(255, 255, 255, 0.2)",
        ring: "#8aa0ff",
        error: "#fb7185",
        "error-foreground": "#000000",
        success: "#34d399",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#38bdf8",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#459ae6",
        "series-2": "#ff8fc0",
        "series-3": "#5fd0e8",
        "series-4": "#c099fa",
        "series-5": "#6bffe1",
        "series-6": "#ffb36a",
        "series-7": "#0aa6cb",
        "series-8": "#e273d4",
        "diagram-ink": "#e7e9f5",
        "diagram-label": "#aab2cc",
        "diagram-guide": "#7f8aad",
        "diagram-axis": "#aab2cc",
        "diagram-grid": "rgba(231,233,245,0.12)",
        "diagram-highlight": "#8aa0ff",
        "diagram-fill": "#2a3263"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "650",
        "letter-spacing": "-0.005em",
        "line-height": "1.6",
        "icon-color": "#8aa0ff",
        "transition-fast": "120ms",
        "transition-normal": "300ms",
        "transition-slow": "500ms",
        "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#8aa0ff"
      },
      shadows: {
        main: "0 8px 32px rgba(0,0,0,0.40)",
        sm: "0 4px 16px rgba(0,0,0,0.30)",
        lg: "0 16px 48px rgba(0,0,0,0.55)",
        inner: "inset 0 1px 0 rgba(255,255,255,0.10)",
        none: "none",
        hover: "0 12px 40px rgba(0,0,0,0.50)",
        active: "0 2px 8px rgba(0,0,0,0.35)"
      },
      surface: {
        backdrop: "blur(20px) saturate(1.6)",
        cardImage: "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.01))",
        pageImage: "radial-gradient(60% 50% at 15% 10%, rgba(138,160,255,0.28), transparent 70%), radial-gradient(50% 45% at 85% 20%, rgba(240,140,176,0.16), transparent 70%), radial-gradient(55% 50% at 70% 90%, rgba(56,160,190,0.16), transparent 70%), radial-gradient(45% 40% at 10% 85%, rgba(138,160,255,0.14), transparent 70%)",
        pageImageSize: "100% 100%",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
        edgeHeight: "clamp(24px, 6vw, 96px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
        textureSize: "24px 24px",
        accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
      }
    }
  }
};

// themes/gothic.json
var gothic_default = {
  name: "gothic",
  displayName: "Gothic Manuscript",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "38px",
      buttonHeightLg: "46px",
      inputHeightSm: "30px",
      inputHeightMd: "38px",
      inputHeightLg: "46px",
      rowHeightCompact: "34px",
      rowHeightNormal: "42px",
      rowHeightSpacious: "50px",
      cardPaddingSm: "14px",
      cardPaddingMd: "20px",
      cardPaddingLg: "28px",
      dialogPadding: "26px",
      sectionGap: "36px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=UnifrakturCook:wght@700&family=EB+Garamond:wght@400;500;700&display=swap",
      displayFamily: '"UnifrakturCook", "Pirata One", serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"EB Garamond", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "0.01em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "13px",
          lineHeight: "17px"
        },
        sm: {
          size: "15px",
          lineHeight: "21px"
        },
        base: {
          size: "17px",
          lineHeight: "25px"
        },
        lg: {
          size: "19px",
          lineHeight: "30px"
        },
        xl: {
          size: "21px",
          lineHeight: "30px"
        },
        "2xl": {
          size: "25px",
          lineHeight: "34px"
        },
        "3xl": {
          size: "32px",
          lineHeight: "38px"
        },
        "4xl": {
          size: "38px",
          lineHeight: "42px"
        },
        "display-1": {
          size: "51px",
          lineHeight: "55px"
        },
        "display-2": {
          size: "64px",
          lineHeight: "68px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "200ms",
        normal: "350ms",
        slow: "550ms",
        dramatic: "800ms"
      },
      easings: {
        linear: "linear",
        standard: "ease-in-out",
        emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
        spring: "ease-in-out"
      },
      shapes: {
        modalEnter: "translateY(-20px)",
        modalExit: "translateY(-20px)",
        pageEnter: "none",
        popoverEnter: "translateY(-8px)",
        popoverExit: "translateY(-8px)",
        toastEnter: "translateY(-16px)",
        toastExit: "translateY(-16px)"
      }
    },
    iconography: {
      family: "phosphor-fill",
      strokeWidth: "0",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "10px",
      radiusInteractive: "4px",
      radiusPill: "var(--radius-full)",
      cornerShape: "scoop",
      cornerShapePill: "scoop",
      borderStyle: "groove",
      borderStyleInteractive: "double",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48'><path d='M24 4 L44 24 L24 44 L4 24 Z M24 14 L34 24 L24 34 L14 24 Z' fill='none' stroke='rgba(42, 20, 16, 0.09)' stroke-width='1'/></svg>")`,
      pageImageSize: "48px 48px"
    },
    colors: {
      primary: "#6b1420",
      "primary-hover": "#550f19",
      "primary-foreground": "#f5e9cf",
      secondary: "#d9c59a",
      "secondary-hover": "#cdb886",
      "secondary-foreground": "#2a1410",
      accent: "#7a5a12",
      "accent-foreground": "#ffffff",
      muted: "#e3d3ae",
      "muted-foreground": "#5a4028",
      background: "#efe2c6",
      foreground: "#2a1410",
      card: "#f6ebd2",
      "card-foreground": "#2a1410",
      surface: "#e8d8b4",
      border: "#2a1410",
      input: "#2a1410",
      ring: "#6b1420",
      "table-header": "#e8d8b4",
      "table-border": "#2a1410",
      "table-row-hover": "#eadbb8",
      "surface-hover": "#e0cea3",
      "border-hover": "#6b1420",
      placeholder: "#77603f",
      error: "#a4161a",
      "error-foreground": "#ffffff",
      success: "#2f5f2a",
      "success-foreground": "#ffffff",
      warning: "#d9a521",
      "warning-foreground": "#2a1410",
      info: "#3a3f7a",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#6f1f45",
      "series-2": "#7a5a12",
      "series-3": "#2f4a3a",
      "series-4": "#3b2a5a",
      "series-5": "#8a4a1a",
      "series-6": "#1f4a5a",
      "series-7": "#714640",
      "series-8": "#732b76",
      "diagram-ink": "#2a1410",
      "diagram-label": "#4a2e1a",
      "diagram-guide": "#7a6040",
      "diagram-axis": "#2a1410",
      "diagram-grid": "rgba(42,20,16,0.14)",
      "diagram-highlight": "#6b1420",
      "diagram-fill": "#d9c59a"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "4px",
      lg: "10px",
      xl: "10px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "0 3px 10px 0 rgba(0, 0, 0, 0.4)",
      main: "0 8px 22px 0 rgba(0, 0, 0, 0.45), 0 2px 6px 0 rgba(0, 0, 0, 0.5)",
      lg: "0 18px 44px 0 rgba(0, 0, 0, 0.55), 0 4px 10px 0 rgba(0, 0, 0, 0.5)",
      inner: "inset 0 2px 8px 0 rgba(0, 0, 0, 0.45)",
      hover: "0 12px 30px 0 rgba(0, 0, 0, 0.5)",
      active: "0 1px 4px 0 rgba(0, 0, 0, 0.6)"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "1px",
      "border-width-thick": "5px",
      "font-family": '"EB Garamond", Garamond, Georgia, serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.55",
      "icon-color": "currentColor",
      "transition-fast": "200ms",
      "transition-normal": "350ms",
      "transition-slow": "550ms",
      "transition-timing": "ease-in-out",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#6b1420"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "3.5",
      lineCap: "square",
      lineJoin: "miter",
      dash: "8 3 2 3",
      dot: "1 3",
      fillStyle: "hatch",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "0",
      shadow: "2px 2px 0 rgba(42,20,16,0.35)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "4px"
    },
    scene: {
      materialRoughness: "0.85",
      materialMetalness: "0.2",
      materialFlat: "0",
      materialOutline: "1",
      lightAmbient: "0.35",
      lightKey: "1.1",
      lightKeyColor: "#ffd9a0",
      fog: "0.3"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#8c1c2b",
        "primary-hover": "#a8273a",
        "primary-foreground": "#f5e9cf",
        secondary: "#3b2359",
        "secondary-hover": "#4a2e6e",
        "secondary-foreground": "#eadfff",
        accent: "#c9a227",
        "accent-foreground": "#140d04",
        muted: "#1e1526",
        "muted-foreground": "#bfb3cf",
        background: "#0f0a12",
        foreground: "#e9dfc9",
        card: "#170f1d",
        "card-foreground": "#e9dfc9",
        surface: "#130d18",
        border: "#c9a227",
        input: "#c9a227",
        ring: "#c9a227",
        "table-header": "#130d18",
        "table-border": "#c9a227",
        "table-row-hover": "#1c1324",
        "surface-hover": "#241a2d",
        "border-hover": "#c9a227",
        placeholder: "#8f84a0",
        error: "#ff8a8a",
        "error-foreground": "#140505",
        success: "#7fd69a",
        "success-foreground": "#0a1a10",
        warning: "#e0b43c",
        "warning-foreground": "#140d04",
        info: "#a9a6ff",
        "info-foreground": "#0f0a12",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ca5950",
        "series-2": "#bd8b2b",
        "series-3": "#66a781",
        "series-4": "#a58ad6",
        "series-5": "#e39354",
        "series-6": "#5fa6b8",
        "series-7": "#d193a9",
        "series-8": "#b0a07a",
        "diagram-ink": "#e9dfc9",
        "diagram-label": "#bfb298",
        "diagram-guide": "#8a7d70",
        "diagram-axis": "#bfb298",
        "diagram-grid": "rgba(233,223,201,0.12)",
        "diagram-highlight": "#d4667f",
        "diagram-fill": "#3a2430"
      },
      shadows: {
        none: "none",
        sm: "0 3px 10px 0 rgba(0, 0, 0, 0.7)",
        main: "0 8px 22px 0 rgba(0, 0, 0, 0.75), 0 2px 6px 0 rgba(0, 0, 0, 0.5)",
        lg: "0 18px 44px 0 rgba(0, 0, 0, 0.85), 0 4px 10px 0 rgba(0, 0, 0, 0.5)",
        inner: "inset 0 2px 8px 0 rgba(0, 0, 0, 0.45)",
        hover: "0 12px 30px 0 rgba(0, 0, 0, 0.8)",
        active: "0 1px 4px 0 rgba(0, 0, 0, 0.6)"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "1px",
        "border-width-thick": "5px",
        "font-family": '"EB Garamond", Garamond, Georgia, serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.55",
        "icon-color": "currentColor",
        "transition-fast": "200ms",
        "transition-normal": "350ms",
        "transition-slow": "550ms",
        "transition-timing": "ease-in-out",
        "hover-scale": "1",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#c9a227"
      },
      surface: {
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48'><path d='M24 4 L44 24 L24 44 L4 24 Z M24 14 L34 24 L24 34 L14 24 Z' fill='none' stroke='rgba(201, 162, 39, 0.09)' stroke-width='1'/></svg>")`,
        pageImageSize: "48px 48px"
      }
    }
  }
};

// themes/ink-wash.json
var ink_wash_default = {
  name: "ink-wash",
  displayName: "Ink Wash",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "44px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "44px",
      inputHeightLg: "52px",
      rowHeightCompact: "36px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "16px",
      cardPaddingMd: "24px",
      cardPaddingLg: "32px",
      dialogPadding: "28px",
      sectionGap: "48px"
    },
    typeScale: {
      displayFamily: '"Ma Shan Zheng", "Noto Serif SC", serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "0.05em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Ma+Shan+Zheng&family=Noto+Serif+SC:wght@400;500;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "900ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.25, 0.1, 0.25, 1)",
        spring: "cubic-bezier(0.25, 0.1, 0.25, 1)"
      },
      shapes: {
        modalEnter: "scale(1.02)",
        modalExit: "scale(1.02)",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "var(--radius-sm)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "none",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `radial-gradient(ellipse at 15% 0%, rgba(0,0,0,0.06), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(0,0,0,0.05), transparent 50%), url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.6' numOctaves='2'/><feColorMatrix values='0 0 0 0 .2 0 0 0 0 .2 0 0 0 0 .2 0 0 0 .09 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`,
      pageImageSize: "100% 100%, 100% 100%, 240px 240px"
    },
    radii: {
      none: "0px",
      sm: "3px",
      md: "4px",
      lg: "6px",
      xl: "10px",
      full: "9999px"
    },
    colors: {
      primary: "#2b2b2b",
      "primary-hover": "#111111",
      "primary-foreground": "#f4f1ea",
      secondary: "#d9d6cf",
      "secondary-hover": "#cbc8c0",
      "secondary-foreground": "#262626",
      accent: "#c8102e",
      "accent-foreground": "#ffffff",
      muted: "#e7e3d9",
      "muted-foreground": "#55524b",
      background: "#f4f1ea",
      foreground: "#1d1d1d",
      card: "#faf8f3",
      "card-foreground": "#1d1d1d",
      surface: "#ece8de",
      border: "#c9c5ba",
      input: "#6a665d",
      ring: "#c8102e",
      "table-header": "#ece8de",
      "table-border": "#c9c5ba",
      "table-row-hover": "#efebe1",
      "surface-hover": "#e9e5da",
      "border-hover": "#2b2b2b",
      placeholder: "#6f6b62",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#00273e",
      "series-2": "#334a69",
      "series-3": "#7a3f2a",
      "series-4": "#576740",
      "series-5": "#7a6420",
      "series-6": "#5a3a5a",
      "series-7": "#3a6a72",
      "series-8": "#72736b",
      "diagram-ink": "#1d1d1d",
      "diagram-label": "#55524b",
      "diagram-guide": "#8c887c",
      "diagram-axis": "#3a3a3a",
      "diagram-grid": "rgba(29,29,29,0.10)",
      "diagram-highlight": "#dd2f51",
      "diagram-fill": "#d9d6cf"
    },
    shadows: {
      main: "0 6px 24px rgba(30,30,30,0.10)",
      sm: "0 1px 6px rgba(30,30,30,0.06)",
      lg: "0 16px 48px rgba(30,30,30,0.14)",
      inner: "inset 0 1px 4px rgba(30,30,30,0.08)",
      none: "none",
      hover: "0 8px 28px rgba(30,30,30,0.12)",
      active: "0 1px 4px rgba(30,30,30,0.08)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Noto Serif SC", "Songti SC", serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.8",
      "icon-color": "#2b2b2b",
      "transition-fast": "300ms",
      "transition-normal": "500ms",
      "transition-slow": "700ms",
      "transition-timing": "cubic-bezier(0.25, 0.1, 0.25, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#c8102e"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "4",
      lineCap: "round",
      lineJoin: "round",
      dash: "10 6",
      dot: "1 6",
      fillStyle: "hatch",
      fillOpacity: "0.22",
      roughness: "1.6",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "400",
      labelCase: "none",
      corner: "2px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.7",
      lightKey: "0.8",
      lightKeyColor: "#fff6e6",
      fog: "0.25"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "32px",
        buttonHeightMd: "44px",
        buttonHeightLg: "52px",
        inputHeightSm: "32px",
        inputHeightMd: "44px",
        inputHeightLg: "52px",
        rowHeightCompact: "36px",
        rowHeightNormal: "44px",
        rowHeightSpacious: "52px",
        cardPaddingSm: "16px",
        cardPaddingMd: "24px",
        cardPaddingLg: "32px",
        dialogPadding: "28px",
        sectionGap: "48px"
      },
      typeScale: {
        displayFamily: '"Ma Shan Zheng", "Noto Serif SC", serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "400",
        headingTransform: "none",
        headingTracking: "0.05em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Ma+Shan+Zheng&family=Noto+Serif+SC:wght@400;500;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "900ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.25, 0.1, 0.25, 1)",
          spring: "cubic-bezier(0.25, 0.1, 0.25, 1)"
        },
        shapes: {
          modalEnter: "scale(1.02)",
          modalExit: "scale(1.02)",
          pageEnter: "none",
          popoverEnter: "none",
          popoverExit: "none",
          toastEnter: "none",
          toastExit: "none"
        }
      },
      iconography: {
        family: "phosphor-outline",
        strokeWidth: "1",
        defaultSize: "18px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "none",
        pressedElevation: "none"
      },
      geometry: {
        radiusContainer: "var(--radius-sm)",
        radiusInteractive: "var(--radius-sm)",
        radiusPill: "var(--radius-full)",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "none",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: "radial-gradient(ellipse at 15% 0%, rgba(255,255,255,0.05), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(255,255,255,0.04), transparent 50%)",
        pageImageSize: "100% 100%, 100% 100%"
      },
      radii: {
        none: "0px",
        sm: "3px",
        md: "4px",
        lg: "6px",
        xl: "10px",
        full: "9999px"
      },
      colors: {
        primary: "#e8e4da",
        "primary-hover": "#ffffff",
        "primary-foreground": "#141414",
        secondary: "#3a3a3a",
        "secondary-hover": "#4a4a4a",
        "secondary-foreground": "#e8e4da",
        accent: "#e8485c",
        "accent-foreground": "#1a0205",
        muted: "#242424",
        "muted-foreground": "#b5b0a4",
        background: "#141414",
        foreground: "#e8e4da",
        card: "#1c1c1c",
        "card-foreground": "#e8e4da",
        surface: "#1a1a1a",
        border: "#3a3a38",
        input: "#8a867c",
        ring: "#e8485c",
        "table-header": "#1a1a1a",
        "table-border": "#3a3a38",
        "table-row-hover": "#202020",
        "surface-hover": "#262626",
        "border-hover": "#e8e4da",
        placeholder: "#8a867c",
        error: "#ff8a80",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ffdeba",
        "series-2": "#7fa3c8",
        "series-3": "#ce8c6b",
        "series-4": "#8fb08f",
        "series-5": "#c9b36a",
        "series-6": "#b58ab5",
        "series-7": "#7fb0b5",
        "series-8": "#8a8d84",
        "diagram-ink": "#e8e4da",
        "diagram-label": "#b5b1a6",
        "diagram-guide": "#86837a",
        "diagram-axis": "#cfcbc0",
        "diagram-grid": "rgba(232,228,218,0.12)",
        "diagram-highlight": "#e0475f",
        "diagram-fill": "#33322f"
      },
      shadows: {
        main: "0 6px 24px rgba(0,0,0,0.5)",
        sm: "0 1px 6px rgba(0,0,0,0.4)",
        lg: "0 16px 48px rgba(0,0,0,0.6)",
        inner: "inset 0 1px 4px rgba(0,0,0,0.4)",
        none: "none",
        hover: "0 8px 28px rgba(0,0,0,0.55)",
        active: "0 1px 4px rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Noto Serif SC", "Songti SC", serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.8",
        "icon-color": "#e8e4da",
        "transition-fast": "300ms",
        "transition-normal": "500ms",
        "transition-slow": "700ms",
        "transition-timing": "cubic-bezier(0.25, 0.1, 0.25, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#e8485c"
      }
    }
  }
};

// themes/kawaii.json
var kawaii_default = {
  name: "kawaii",
  displayName: "Kawaii",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"M PLUS Rounded 1c", "Baloo 2", sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "800",
      headingTransform: "none",
      headingTracking: "0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;500;700;800&family=Nunito:wght@400;600;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.8)",
        modalExit: "scale(0.8)",
        pageEnter: "translateY(16px)",
        popoverEnter: "scale(0.85)",
        popoverExit: "scale(0.85)",
        toastEnter: "translateY(24px) scale(0.9)",
        toastExit: "translateY(24px) scale(0.9)"
      }
    },
    iconography: {
      family: "phosphor-fill",
      strokeWidth: "1.5",
      defaultSize: "20px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-full)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><circle cx='14' cy='16' r='4' fill='%23ff7eb6' fill-opacity='0.22'/><circle cx='58' cy='52' r='3' fill='%23ff7eb6' fill-opacity='0.22'/><polygon points='56,10 58.4,16 65,16.6 60,21 61.6,27.4 56,24 50.4,27.4 52,21 47,16.6 53.6,16' fill='%23a98bff' fill-opacity='0.25'/><polygon points='20,56 21.6,60 26,60.4 22.6,63.4 23.8,67.6 20,65.2 16.2,67.6 17.4,63.4 14,60.4 18.4,60' fill='%23a98bff' fill-opacity='0.25'/></svg>")`,
      pageImageSize: "80px 80px"
    },
    radii: {
      none: "0px",
      sm: "14px",
      md: "20px",
      lg: "28px",
      xl: "36px",
      full: "9999px"
    },
    colors: {
      primary: "#c2457f",
      "primary-hover": "#a93569",
      "primary-foreground": "#ffffff",
      secondary: "#d9c7ff",
      "secondary-hover": "#cbb5fa",
      "secondary-foreground": "#3a2366",
      accent: "#247a61",
      "accent-foreground": "#ffffff",
      muted: "#ffe6f1",
      "muted-foreground": "#7a4a6a",
      background: "#fff5fa",
      foreground: "#4a2c4f",
      card: "#ffffff",
      "card-foreground": "#4a2c4f",
      surface: "#ffeaf4",
      border: "#ffc2dd",
      input: "#e89ac0",
      ring: "#5b8cf0",
      "table-header": "#ffe6f1",
      "table-border": "#ffc2dd",
      "table-row-hover": "#fff0f7",
      "surface-hover": "#ffe0ee",
      "border-hover": "#c2457f",
      placeholder: "#86607d",
      error: "#c62a47",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#e76183",
      "series-2": "#7a52d6",
      "series-3": "#1f8f8a",
      "series-4": "#d9701f",
      "series-5": "#3f7fd0",
      "series-6": "#51946e",
      "series-7": "#aa4db1",
      "series-8": "#a8761f",
      "diagram-ink": "#4a2c4f",
      "diagram-label": "#7a4a6a",
      "diagram-guide": "#a06a8f",
      "diagram-axis": "#7a4a6a",
      "diagram-grid": "#ffc2dd",
      "diagram-highlight": "#c2457f",
      "diagram-fill": "#ffd9ec"
    },
    shadows: {
      main: "0 6px 16px -2px rgba(255,110,170,0.30)",
      sm: "0 3px 8px -1px rgba(255,110,170,0.25)",
      lg: "0 14px 32px -4px rgba(169,139,255,0.35)",
      inner: "inset 0 2px 4px rgba(255,110,170,0.18)",
      none: "none",
      hover: "0 8px 20px -2px rgba(255,110,170,0.40)",
      active: "0 1px 3px rgba(255,110,170,0.25)"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Nunito", "Hiragino Maru Gothic ProN", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#c2457f",
      "transition-fast": "150ms",
      "transition-normal": "280ms",
      "transition-slow": "420ms",
      "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
      "hover-scale": "1.05",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0px",
      "active-scale": "0.94",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#5b8cf0"
    },
    diagram: {
      strokeThin: "2",
      strokeNormal: "3",
      strokeBold: "5",
      lineCap: "round",
      lineJoin: "round",
      dash: "3 6",
      dot: "0.1 7",
      fillStyle: "tint",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "0",
      shadow: "0 3px 0 rgba(194,69,127,0.25)",
      marker: "dot",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "12px"
    },
    scene: {
      materialRoughness: "0.7",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "2",
      lightAmbient: "0.75",
      lightKey: "0.8",
      lightKeyColor: "#fff0f8",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"M PLUS Rounded 1c", "Baloo 2", sans-serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "800",
        headingTransform: "none",
        headingTracking: "0.01em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;500;700;800&family=Nunito:wght@400;600;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "600ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
        },
        shapes: {
          modalEnter: "scale(0.8)",
          modalExit: "scale(0.8)",
          pageEnter: "translateY(16px)",
          popoverEnter: "scale(0.85)",
          popoverExit: "scale(0.85)",
          toastEnter: "translateY(24px) scale(0.9)",
          toastExit: "translateY(24px) scale(0.9)"
        }
      },
      iconography: {
        family: "phosphor-fill",
        strokeWidth: "1.5",
        defaultSize: "20px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-full)",
        radiusPill: "var(--radius-full)",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><circle cx='14' cy='16' r='4' fill='%23ff9ccc' fill-opacity='0.22'/><circle cx='58' cy='52' r='3' fill='%23ff9ccc' fill-opacity='0.22'/><polygon points='56,10 58.4,16 65,16.6 60,21 61.6,27.4 56,24 50.4,27.4 52,21 47,16.6 53.6,16' fill='%23b9a2ff' fill-opacity='0.25'/><polygon points='20,56 21.6,60 26,60.4 22.6,63.4 23.8,67.6 20,65.2 16.2,67.6 17.4,63.4 14,60.4 18.4,60' fill='%23b9a2ff' fill-opacity='0.25'/></svg>")`,
        pageImageSize: "80px 80px"
      },
      radii: {
        none: "0px",
        sm: "14px",
        md: "20px",
        lg: "28px",
        xl: "36px",
        full: "9999px"
      },
      colors: {
        primary: "#ff9ccc",
        "primary-hover": "#ffb3d8",
        "primary-foreground": "#3a0f26",
        secondary: "#b9a2ff",
        "secondary-hover": "#ccb9ff",
        "secondary-foreground": "#1d0f44",
        accent: "#8fe8c8",
        "accent-foreground": "#06261b",
        muted: "#3b2748",
        "muted-foreground": "#dcc3e4",
        background: "#2a1b33",
        foreground: "#ffe9f4",
        card: "#35233f",
        "card-foreground": "#ffe9f4",
        surface: "#30203a",
        border: "#6b4a7d",
        input: "#c79ad8",
        ring: "#8fb0ff",
        "table-header": "#30203a",
        "table-border": "#6b4a7d",
        "table-row-hover": "#3a2746",
        "surface-hover": "#44304f",
        "border-hover": "#ff9ccc",
        placeholder: "#b596b5",
        error: "#ff8a9a",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#e964aa",
        "series-2": "#b79cff",
        "series-3": "#5fd6c0",
        "series-4": "#f9a363",
        "series-5": "#7fb4ff",
        "series-6": "#a8db76",
        "series-7": "#e07de8",
        "series-8": "#efdd6d",
        "diagram-ink": "#ffe9f4",
        "diagram-label": "#e0b8d4",
        "diagram-guide": "#b88aab",
        "diagram-axis": "#e0b8d4",
        "diagram-grid": "rgba(255,194,221,0.18)",
        "diagram-highlight": "#fb9dd5",
        "diagram-fill": "#4d3358"
      },
      shadows: {
        main: "0 6px 16px -2px rgba(255,156,204,0.22)",
        sm: "0 3px 8px -1px rgba(0,0,0,0.4)",
        lg: "0 14px 32px -4px rgba(185,162,255,0.25)",
        inner: "inset 0 2px 4px rgba(0,0,0,0.35)",
        none: "none",
        hover: "0 8px 20px -2px rgba(255,156,204,0.32)",
        active: "0 1px 3px rgba(0,0,0,0.4)"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Nunito", "Hiragino Maru Gothic ProN", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#ff9ccc",
        "transition-fast": "150ms",
        "transition-normal": "280ms",
        "transition-slow": "420ms",
        "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
        "hover-scale": "1.05",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0px",
        "active-scale": "0.94",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#8fb0ff"
      }
    }
  }
};

// themes/kflow.json
var kflow_default = {
  name: "kflow",
  displayName: "KFlow",
  tokens: {
    motion: {
      durations: {
        instant: "0ms",
        fast: "120ms",
        normal: "200ms",
        slow: "320ms",
        dramatic: "640ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "translateY(12px) scale(0.98)",
        modalExit: "scale(0.98)",
        pageEnter: "translateY(10px)",
        popoverEnter: "scale(0.97)",
        popoverExit: "scale(0.97)",
        toastEnter: "translateY(-12px)",
        toastExit: "translateY(-12px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.75",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-inner)"
    },
    geometry: {
      radiusContainer: "16px",
      radiusInteractive: "12px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "2px",
      cornerShape: "superellipse(1.5)",
      cornerShapePill: "superellipse(1.5)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#4f46e5",
      "primary-hover": "#4338ca",
      "primary-foreground": "#ffffff",
      secondary: "#eef0fb",
      "secondary-hover": "#e0e4f7",
      "secondary-foreground": "#1e1b4b",
      accent: "#0e7490",
      "accent-foreground": "#ffffff",
      muted: "#eef1f8",
      "muted-foreground": "#535d75",
      background: "#f7f8fc",
      foreground: "#0b1026",
      card: "#ffffff",
      "card-foreground": "#0b1026",
      surface: "#f0f2fa",
      border: "#e1e5f1",
      input: "#c9d0e3",
      ring: "#4f46e5",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#047857",
      "success-foreground": "#ffffff",
      warning: "#b45309",
      "warning-foreground": "#ffffff",
      info: "#0369a1",
      "info-foreground": "#ffffff",
      scrim: "rgba(11, 16, 38, 0.55)",
      "series-1": "#0891b2",
      "series-2": "#c026d3",
      "series-3": "#65a30d",
      "series-4": "#ea580c",
      "series-5": "#db2777",
      "series-6": "#2563eb",
      "series-7": "#854d0e",
      "series-8": "#0d9488",
      "diagram-ink": "#0b1026",
      "diagram-label": "#475069",
      "diagram-guide": "#94a0bd",
      "diagram-axis": "#2f3754",
      "diagram-grid": "#e1e5f1",
      "diagram-highlight": "#4f46e5",
      "diagram-fill": "#e0e7ff"
    },
    radii: {
      none: "0px",
      sm: "8px",
      md: "12px",
      lg: "16px",
      xl: "22px",
      full: "9999px"
    },
    shadows: {
      main: "0 8px 24px -8px rgba(79, 70, 229, 0.20), 0 2px 6px rgba(8, 145, 178, 0.08)",
      sm: "0 2px 8px -2px rgba(79, 70, 229, 0.16)",
      lg: "0 24px 48px -12px rgba(79, 70, 229, 0.28), 0 6px 16px rgba(8, 145, 178, 0.10)",
      inner: "inset 0 1px 2px rgba(79, 70, 229, 0.10)",
      none: "none",
      hover: "0 14px 32px -10px rgba(79, 70, 229, 0.32), 0 4px 10px rgba(16, 185, 129, 0.10)",
      active: "0 2px 6px -2px rgba(79, 70, 229, 0.24)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "icon-color": "currentColor",
      "transition-fast": "120ms",
      "transition-normal": "200ms",
      "transition-slow": "320ms",
      "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0",
      "active-scale": "0.98",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#4f46e5",
      "font-family": '"Plus Jakarta Sans", "IBM Plex Sans Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "letter-spacing": "-0.005em",
      "line-height": "1.6"
    },
    typeScale: {
      displayFamily: '"Fraunces", "IBM Plex Sans Arabic", Georgia, serif',
      bodyFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.02em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    surface: {
      cardImage: "linear-gradient(135deg, rgba(99, 102, 241, 0.07) 0%, rgba(34, 211, 238, 0.05) 55%, rgba(16, 185, 129, 0.06) 100%)"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3px",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 4",
      dot: "1 5",
      fillStyle: "tint",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "2",
      shadow: "0 4px 14px rgba(79, 70, 229, 0.22)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "8px"
    },
    scene: {
      materialRoughness: "0.25",
      materialMetalness: "0.15",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.5",
      lightKey: "1.15",
      lightKeyColor: "#eef2ff",
      fog: "0.05"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#818cf8",
        "primary-hover": "#a5b4fc",
        "primary-foreground": "#0b1026",
        secondary: "#161d40",
        "secondary-hover": "#1f2852",
        "secondary-foreground": "#e6e9f5",
        accent: "#22d3ee",
        "accent-foreground": "#04131a",
        muted: "#141b3a",
        "muted-foreground": "#9aa4c0",
        background: "#070b1a",
        foreground: "#e6e9f5",
        card: "#0e1430",
        "card-foreground": "#e6e9f5",
        surface: "#121a3a",
        border: "#222b52",
        input: "#2e3866",
        ring: "#818cf8",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#34d399",
        "success-foreground": "#022c22",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#38bdf8",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#22d3ee",
        "series-2": "#e879f9",
        "series-3": "#a3e635",
        "series-4": "#fb923c",
        "series-5": "#f472b6",
        "series-6": "#60a5fa",
        "series-7": "#fef08a",
        "series-8": "#2dd4bf",
        "diagram-ink": "#e6e9f5",
        "diagram-label": "#9aa4c0",
        "diagram-guide": "#5d6890",
        "diagram-axis": "#c7cde3",
        "diagram-grid": "#222b52",
        "diagram-highlight": "#818cf8",
        "diagram-fill": "#232c63"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "icon-color": "currentColor",
        "transition-fast": "120ms",
        "transition-normal": "200ms",
        "transition-slow": "320ms",
        "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0",
        "active-scale": "0.98",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#818cf8",
        "font-family": '"Plus Jakarta Sans", "IBM Plex Sans Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "letter-spacing": "-0.005em",
        "line-height": "1.6"
      },
      shadows: {
        main: "0 8px 28px -8px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(129, 140, 248, 0.08)",
        sm: "0 2px 8px -2px rgba(0, 0, 0, 0.5)",
        lg: "0 24px 56px -12px rgba(0, 0, 0, 0.7), 0 0 32px -8px rgba(99, 102, 241, 0.25)",
        inner: "inset 0 1px 2px rgba(0, 0, 0, 0.4)",
        none: "none",
        hover: "0 14px 36px -10px rgba(99, 102, 241, 0.35)",
        active: "0 2px 6px -2px rgba(0, 0, 0, 0.5)"
      },
      surface: {
        cardImage: "linear-gradient(135deg, rgba(129, 140, 248, 0.10) 0%, rgba(34, 211, 238, 0.06) 55%, rgba(52, 211, 153, 0.07) 100%)"
      }
    }
  }
};

// themes/kiosk.json
var kiosk_default = {
  name: "kiosk",
  displayName: "Kiosk",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "18px",
        space5: "24px",
        space6: "32px",
        space7: "40px",
        space8: "48px",
        space9: "56px",
        space10: "64px",
        space11: "72px",
        space12: "80px"
      },
      buttonHeightSm: "44px",
      buttonHeightMd: "60px",
      buttonHeightLg: "72px",
      inputHeightSm: "44px",
      inputHeightMd: "60px",
      inputHeightLg: "72px",
      rowHeightCompact: "56px",
      rowHeightNormal: "64px",
      rowHeightSpacious: "88px",
      cardPaddingSm: "16px",
      cardPaddingMd: "28px",
      cardPaddingLg: "36px",
      dialogPadding: "32px",
      sectionGap: "48px"
    },
    typeScale: {
      displayFamily: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "14px",
          lineHeight: "20px"
        },
        sm: {
          size: "16px",
          lineHeight: "24px"
        },
        base: {
          size: "18px",
          lineHeight: "28px"
        },
        lg: {
          size: "22px",
          lineHeight: "32px"
        },
        xl: {
          size: "26px",
          lineHeight: "34px"
        },
        "2xl": {
          size: "32px",
          lineHeight: "40px"
        },
        "3xl": {
          size: "40px",
          lineHeight: "48px"
        },
        "4xl": {
          size: "48px",
          lineHeight: "56px"
        },
        "display-1": {
          size: "64px",
          lineHeight: "72px"
        },
        "display-2": {
          size: "80px",
          lineHeight: "88px"
        }
      },
      intents: {
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "base",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "sm",
          weight: "medium"
        },
        numeric: {
          slot: "mono",
          size: "base",
          weight: "bold"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=JetBrains+Mono:wght@400;700&display=swap",
      headingWeight: "800",
      headingTransform: "none",
      headingTracking: "-0.02em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "300ms",
        normal: "500ms",
        slow: "750ms",
        dramatic: "1000ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.16, 1, 0.3, 1)",
        emphasized: "cubic-bezier(0.16, 1, 0.3, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "translateY(48px)",
        modalExit: "translateY(48px)",
        pageEnter: "translateY(24px)",
        popoverEnter: "translateY(12px)",
        popoverExit: "translateY(12px)",
        toastEnter: "translateY(32px)",
        toastExit: "translateY(32px)"
      }
    },
    iconography: {
      family: "fa-solid",
      strokeWidth: "0",
      defaultSize: "26px"
    },
    elevation: {
      cardElevation: "var(--shadow-lg)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-main)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-xl)",
      radiusInteractive: "9999px",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#0b57d0",
      "primary-hover": "#0946ab",
      "primary-foreground": "#ffffff",
      secondary: "#eef2f7",
      "secondary-hover": "#e1e7f0",
      "secondary-foreground": "#1a1a1a",
      accent: "#b45309",
      "accent-foreground": "#ffffff",
      muted: "#f5f5f4",
      "muted-foreground": "#595959",
      background: "#fefefe",
      foreground: "#1a1a1a",
      card: "#ffffff",
      "card-foreground": "#1a1a1a",
      surface: "#f6f8fb",
      border: "#8a8a8a",
      input: "#d4d4d4",
      ring: "#0b57d0",
      error: "#b91c1c",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#f59e0b",
      "warning-foreground": "#1a1a1a",
      info: "#0369a1",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#543ac1",
      "series-2": "#b45309",
      "series-3": "#0f766e",
      "series-4": "#9802b2",
      "series-5": "#be185d",
      "series-6": "#618305",
      "series-7": "#374151",
      "series-8": "#6d4c41",
      "diagram-ink": "#1a1a1a",
      "diagram-label": "#1a1a1a",
      "diagram-guide": "#595959",
      "diagram-axis": "#1a1a1a",
      "diagram-grid": "#d0d0d0",
      "diagram-highlight": "#0b57d0",
      "diagram-fill": "#eef2f7"
    },
    radii: {
      none: "0px",
      sm: "14px",
      md: "20px",
      lg: "26px",
      xl: "32px",
      full: "9999px"
    },
    shadows: {
      main: "0 10px 30px rgba(0,0,0,0.16)",
      sm: "0 5px 14px rgba(0,0,0,0.14)",
      lg: "0 24px 60px rgba(0,0,0,0.26)",
      inner: "inset 0 3px 6px rgba(0,0,0,0.12)",
      none: "none",
      hover: "0 14px 36px rgba(0,0,0,0.22)",
      active: "0 2px 8px rgba(0,0,0,0.18)"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "500",
      "font-weight-medium": "600",
      "font-weight-bold": "800",
      "letter-spacing": "0",
      "line-height": "1.5",
      "icon-color": "#1a1a1a",
      "transition-fast": "300ms",
      "transition-normal": "500ms",
      "transition-slow": "750ms",
      "transition-timing": "cubic-bezier(0.16, 1, 0.3, 1)",
      "hover-scale": "1.03",
      "hover-translate-y": "-3px",
      "hover-translate-x": "0",
      "active-scale": "0.96",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "3px",
      "focus-ring-color": "#0b57d0",
      "intent-heading-major-size": "28px",
      "intent-heading-major-weight": "var(--font-weight-bold)",
      "intent-heading-major-leading": "36px"
    },
    diagram: {
      strokeThin: "2",
      strokeNormal: "3",
      strokeBold: "5",
      lineCap: "round",
      lineJoin: "round",
      dash: "8 6",
      dot: "1 6",
      fillStyle: "tint",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "triangle",
      labelFont: "body",
      labelSize: "base",
      labelWeight: "600",
      labelCase: "none",
      corner: "10px"
    },
    scene: {
      materialRoughness: "0.6",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "1",
      lightKeyColor: "#ffffff",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#6ea0ff",
        "primary-hover": "#8bb3ff",
        "primary-foreground": "#06142e",
        secondary: "#1c2a44",
        "secondary-hover": "#2a3a5c",
        "secondary-foreground": "#ffffff",
        accent: "#f5a524",
        "accent-foreground": "#1a1206",
        muted: "#16243c",
        "muted-foreground": "#b8c4d8",
        background: "#0a1428",
        foreground: "#ffffff",
        card: "#122038",
        "card-foreground": "#ffffff",
        surface: "#16243c",
        border: "rgba(255, 255, 255, 0.38)",
        input: "rgba(255, 255, 255, 0.16)",
        ring: "#6ea0ff",
        error: "#ff7a7a",
        "error-foreground": "#1a0606",
        success: "#4ade80",
        "success-foreground": "#052e14",
        warning: "#fbbf24",
        "warning-foreground": "#1a1106",
        info: "#38bdf8",
        "info-foreground": "#062235",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#25aef4",
        "series-2": "#faa750",
        "series-3": "#4fd1c5",
        "series-4": "#c792ff",
        "series-5": "#ff8fbf",
        "series-6": "#aadc4a",
        "series-7": "#8eb9c6",
        "series-8": "#d4b08a",
        "diagram-ink": "#ffffff",
        "diagram-label": "#d5deee",
        "diagram-guide": "#98a8c4",
        "diagram-axis": "#d5deee",
        "diagram-grid": "rgba(255,255,255,0.18)",
        "diagram-highlight": "#6ea0ff",
        "diagram-fill": "#1e3358"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "500",
        "font-weight-medium": "600",
        "font-weight-bold": "800",
        "letter-spacing": "0",
        "line-height": "1.5",
        "icon-color": "#ffffff",
        "transition-fast": "300ms",
        "transition-normal": "500ms",
        "transition-slow": "750ms",
        "transition-timing": "cubic-bezier(0.16, 1, 0.3, 1)",
        "hover-scale": "1.03",
        "hover-translate-y": "-3px",
        "hover-translate-x": "0",
        "active-scale": "0.96",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "3px",
        "focus-ring-color": "#6ea0ff",
        "intent-heading-major-size": "28px",
        "intent-heading-major-weight": "var(--font-weight-bold)",
        "intent-heading-major-leading": "36px"
      },
      shadows: {
        main: "0 10px 30px rgba(0,0,0,0.5)",
        sm: "0 5px 14px rgba(0,0,0,0.45)",
        lg: "0 24px 60px rgba(0,0,0,0.6)",
        inner: "inset 0 3px 6px rgba(0,0,0,0.4)",
        none: "none",
        hover: "0 14px 36px rgba(0,0,0,0.55)",
        active: "0 2px 8px rgba(0,0,0,0.5)"
      }
    }
  }
};

// themes/linear-clean.json
var linear_clean_default = {
  name: "linear-clean",
  displayName: "Linear Clean",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "30px",
      buttonHeightLg: "36px",
      inputHeightSm: "28px",
      inputHeightMd: "30px",
      inputHeightLg: "36px",
      rowHeightCompact: "32px",
      rowHeightNormal: "34px",
      rowHeightSpacious: "44px",
      cardPaddingSm: "12px",
      cardPaddingMd: "14px",
      cardPaddingLg: "20px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "11px",
          lineHeight: "14px"
        },
        sm: {
          size: "13px",
          lineHeight: "18px"
        },
        base: {
          size: "14px",
          lineHeight: "20px"
        },
        lg: {
          size: "16px",
          lineHeight: "22px"
        },
        xl: {
          size: "18px",
          lineHeight: "24px"
        },
        "2xl": {
          size: "22px",
          lineHeight: "28px"
        },
        "3xl": {
          size: "28px",
          lineHeight: "34px"
        },
        "4xl": {
          size: "34px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "44px",
          lineHeight: "48px"
        },
        "display-2": {
          size: "56px",
          lineHeight: "60px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "sm",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "medium"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600&family=Geist+Mono:wght@400;500&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.022em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "80ms",
        normal: "120ms",
        slow: "180ms",
        dramatic: "240ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.25, 0.1, 0.25, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "linear"
      },
      shapes: {
        modalEnter: "scale(0.98)",
        modalExit: "scale(0.98)",
        pageEnter: "translateY(4px)",
        popoverEnter: "scale(0.98)",
        popoverExit: "scale(0.98)",
        toastEnter: "translateY(8px)",
        toastExit: "translateY(8px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "8px",
      radiusInteractive: "8px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "1px",
      cornerShape: "squircle",
      cornerShapePill: "squircle",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#5e6ad2",
      "primary-hover": "#4e59b8",
      "primary-foreground": "#ffffff",
      secondary: "#f4f4f2",
      "secondary-hover": "#ececea",
      "secondary-foreground": "#0a0a0a",
      accent: "#5e6ad2",
      "accent-foreground": "#ffffff",
      muted: "#f4f4f2",
      "muted-foreground": "#6b6b66",
      background: "#fafafa",
      foreground: "#0a0a0a",
      card: "#ffffff",
      "card-foreground": "#0a0a0a",
      surface: "#f7f7f5",
      border: "#e6e6e2",
      input: "#d8d8d4",
      ring: "#5e6ad2",
      "table-header": "#f7f7f5",
      "table-border": "#e6e6e2",
      "table-row-hover": "#f4f4f2",
      "surface-hover": "#ececea",
      "border-hover": "#c9c9c4",
      placeholder: "#9a998f",
      error: "#d92d20",
      "error-foreground": "#ffffff",
      success: "#067647",
      "success-foreground": "#ffffff",
      warning: "#b54708",
      "warning-foreground": "#ffffff",
      info: "#0066ff",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#8d59c0",
      "series-2": "#1f8a8a",
      "series-3": "#b7791f",
      "series-4": "#a55092",
      "series-5": "#3181c0",
      "series-6": "#5a8a3a",
      "series-7": "#c05a6a",
      "series-8": "#858489",
      "diagram-ink": "#1f1f22",
      "diagram-label": "#6b6b66",
      "diagram-guide": "#8a8a84",
      "diagram-axis": "#6b6b66",
      "diagram-grid": "#e6e6e2",
      "diagram-highlight": "#5e6ad2",
      "diagram-fill": "#f4f4f2"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "8px",
      lg: "10px",
      xl: "12px",
      full: "9999px"
    },
    shadows: {
      main: "0 0 0 1px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
      sm: "inset 0 1px 0 rgba(255, 255, 255, 0.9)",
      lg: "0 8px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
      inner: "inset 0 1px 0 rgba(0, 0, 0, 0.04)",
      none: "none",
      hover: "0 0 0 1px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
      active: "inset 0 1px 0 rgba(0, 0, 0, 0.05)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "1px",
      "font-family": '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "600",
      "letter-spacing": "-0.011em",
      "line-height": "1.45",
      "intent-numeric-family": "var(--font-family-mono)",
      "icon-color": "currentColor",
      "transition-fast": "90ms",
      "transition-normal": "140ms",
      "transition-slow": "180ms",
      "transition-timing": "cubic-bezier(0.25, 0.1, 0.25, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#5e6ad2"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "1",
      strokeBold: "1.5",
      lineCap: "round",
      lineJoin: "round",
      dash: "4 4",
      dot: "1 3",
      fillStyle: "tint",
      fillOpacity: "0.1",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "xs",
      labelWeight: "500",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.7",
      materialMetalness: "0.05",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.65",
      lightKey: "0.9",
      lightKeyColor: "#ffffff",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#8b93e8",
        "primary-hover": "#a1a8ee",
        "primary-foreground": "#0a0a0c",
        secondary: "#18181b",
        "secondary-hover": "#222226",
        "secondary-foreground": "#fafafa",
        accent: "#8b93e8",
        "accent-foreground": "#0a0a0c",
        muted: "#18181b",
        "muted-foreground": "#8b8b90",
        background: "#0a0a0c",
        foreground: "#fafafa",
        card: "#121214",
        "card-foreground": "#fafafa",
        surface: "#18181b",
        border: "#26262b",
        input: "#2e2e34",
        ring: "#8b93e8",
        "table-header": "#18181b",
        "table-border": "#26262b",
        "table-row-hover": "#161618",
        "surface-hover": "#222226",
        "border-hover": "#3a3a40",
        placeholder: "#6b6b70",
        error: "#f97066",
        "error-foreground": "#0a0a0c",
        success: "#47cd89",
        "success-foreground": "#0a0a0c",
        warning: "#fdb022",
        "warning-foreground": "#0a0a0c",
        info: "#4d8bff",
        "info-foreground": "#0a0a0c",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#498bdc",
        "series-2": "#4fc3c3",
        "series-3": "#c09d37",
        "series-4": "#d08ad0",
        "series-5": "#6fb4f0",
        "series-6": "#9bcc66",
        "series-7": "#e08a98",
        "series-8": "#b3b2bb",
        "diagram-ink": "#fafafa",
        "diagram-label": "#a0a0a8",
        "diagram-guide": "#76767e",
        "diagram-axis": "#a0a0a8",
        "diagram-grid": "rgba(255,255,255,0.08)",
        "diagram-highlight": "#8b93e8",
        "diagram-fill": "#1c1c20"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "1px",
        "font-family": '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "600",
        "letter-spacing": "-0.011em",
        "line-height": "1.45",
        "intent-numeric-family": "var(--font-family-mono)",
        "icon-color": "currentColor",
        "transition-fast": "90ms",
        "transition-normal": "140ms",
        "transition-slow": "180ms",
        "transition-timing": "cubic-bezier(0.25, 0.1, 0.25, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#8b93e8"
      },
      shadows: {
        main: "inset 0 1px 0 rgba(255, 255, 255, 0.05)",
        sm: "inset 0 1px 0 rgba(255, 255, 255, 0.05)",
        lg: "0 8px 24px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
        inner: "inset 0 1px 0 rgba(0, 0, 0, 0.3)",
        none: "none",
        hover: "0 0 0 1px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
        active: "inset 0 1px 0 rgba(0, 0, 0, 0.3)"
      }
    }
  }
};

// themes/luxury.json
var luxury_default = {
  name: "luxury",
  displayName: "Haute Luxe",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "6px",
        space2: "12px",
        space3: "18px",
        space4: "24px",
        space5: "30px",
        space6: "36px",
        space7: "42px",
        space8: "48px",
        space9: "54px",
        space10: "60px",
        space11: "66px",
        space12: "72px"
      },
      buttonHeightSm: "40px",
      buttonHeightMd: "48px",
      buttonHeightLg: "56px",
      inputHeightSm: "44px",
      inputHeightMd: "52px",
      inputHeightLg: "60px",
      rowHeightCompact: "48px",
      rowHeightNormal: "60px",
      rowHeightSpacious: "72px",
      cardPaddingSm: "28px",
      cardPaddingMd: "40px",
      cardPaddingLg: "56px",
      dialogPadding: "56px",
      sectionGap: "96px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Jost:wght@300;400;500&display=swap",
      displayFamily: '"Cormorant Garamond", "Bodoni Moda", Georgia, serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Jost", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "300",
      headingTransform: "uppercase",
      headingTracking: "0.25em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "17px"
        },
        sm: {
          size: "15px",
          lineHeight: "21px"
        },
        base: {
          size: "17px",
          lineHeight: "25px"
        },
        lg: {
          size: "19px",
          lineHeight: "29px"
        },
        xl: {
          size: "21px",
          lineHeight: "29px"
        },
        "2xl": {
          size: "25px",
          lineHeight: "33px"
        },
        "3xl": {
          size: "31px",
          lineHeight: "37px"
        },
        "4xl": {
          size: "37px",
          lineHeight: "42px"
        },
        "display-1": {
          size: "50px",
          lineHeight: "54px"
        },
        "display-2": {
          size: "62px",
          lineHeight: "67px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "500ms",
        normal: "650ms",
        slow: "800ms",
        dramatic: "1200ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.22, 1, 0.36, 1)",
        emphasized: "cubic-bezier(0.16, 1, 0.3, 1)",
        spring: "cubic-bezier(0.22, 1, 0.36, 1)"
      },
      shapes: {
        modalEnter: "scale(1.03)",
        modalExit: "scale(1.03)",
        pageEnter: "none",
        popoverEnter: "scale(1.02)",
        popoverExit: "scale(1.02)",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    colors: {
      primary: "#141210",
      "primary-hover": "#2e2a24",
      "primary-foreground": "#faf7f0",
      secondary: "#ebe5d6",
      "secondary-hover": "#e0d8c4",
      "secondary-foreground": "#141210",
      accent: "#7d6229",
      "accent-foreground": "#ffffff",
      muted: "#f1ecdf",
      "muted-foreground": "#5b554a",
      background: "#faf7f0",
      foreground: "#141210",
      card: "#fffdf8",
      "card-foreground": "#141210",
      surface: "#f5f1e6",
      border: "#b9ad90",
      input: "#b9ad90",
      ring: "#7d6229",
      "table-header": "#f5f1e6",
      "table-border": "#b9ad90",
      "table-row-hover": "#f3eee2",
      "surface-hover": "#ece6d6",
      "border-hover": "#7d6229",
      placeholder: "#6f685a",
      error: "#9b1c1c",
      "error-foreground": "#ffffff",
      success: "#2f6b3f",
      "success-foreground": "#ffffff",
      warning: "#e8d29a",
      "warning-foreground": "#2a1f05",
      info: "#2b4a6b",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#5e6327",
      "series-2": "#2a3a5a",
      "series-3": "#5a1f2a",
      "series-4": "#1f5245",
      "series-5": "#6a5a7a",
      "series-6": "#8a4a2a",
      "series-7": "#526264",
      "series-8": "#988343",
      "diagram-ink": "#141210",
      "diagram-label": "#5b554a",
      "diagram-guide": "#8f8670",
      "diagram-axis": "#5b554a",
      "diagram-grid": "rgba(125,98,41,0.18)",
      "diagram-highlight": "#7d6229",
      "diagram-fill": "#ebe5d6"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "none",
      main: "none",
      lg: "none",
      inner: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Jost", "Helvetica Neue", Arial, sans-serif',
      "font-weight-normal": "300",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.04em",
      "line-height": "1.75",
      "icon-color": "currentColor",
      "transition-fast": "500ms",
      "transition-normal": "650ms",
      "transition-slow": "800ms",
      "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#7d6229"
    },
    diagram: {
      strokeThin: "0.75",
      strokeNormal: "1",
      strokeBold: "1.5",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "10 5",
      dot: "1 5",
      fillStyle: "outline",
      fillOpacity: "0.12",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "line",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "uppercase",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.3",
      materialMetalness: "0.7",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.4",
      lightKey: "1.3",
      lightKeyColor: "#ffe8c0",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#f1ebdc",
        "primary-hover": "#d8d1bf",
        "primary-foreground": "#0c0b0a",
        secondary: "#1f1d1a",
        "secondary-hover": "#2a2723",
        "secondary-foreground": "#f1ebdc",
        accent: "#c8a864",
        "accent-foreground": "#0c0b0a",
        muted: "#191715",
        "muted-foreground": "#b9b2a2",
        background: "#0c0b0a",
        foreground: "#f1ebdc",
        card: "#131210",
        "card-foreground": "#f1ebdc",
        surface: "#100f0d",
        border: "#4a4336",
        input: "#4a4336",
        ring: "#c8a864",
        "table-header": "#100f0d",
        "table-border": "#4a4336",
        "table-row-hover": "#171513",
        "surface-hover": "#1d1b18",
        "border-hover": "#c8a864",
        placeholder: "#8f887a",
        error: "#ff8a8a",
        "error-foreground": "#0c0b0a",
        success: "#7fd69a",
        "success-foreground": "#0c0b0a",
        warning: "#e8d29a",
        "warning-foreground": "#0c0b0a",
        info: "#8fb4dc",
        "info-foreground": "#0c0b0a",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#feba55",
        "series-2": "#8aa4d4",
        "series-3": "#ca6d76",
        "series-4": "#5cac88",
        "series-5": "#a89ac4",
        "series-6": "#d4905a",
        "series-7": "#9ab0a8",
        "series-8": "#cbdbb0",
        "diagram-ink": "#f1ebdc",
        "diagram-label": "#bdb5a2",
        "diagram-guide": "#8a826f",
        "diagram-axis": "#bdb5a2",
        "diagram-grid": "rgba(201,168,92,0.2)",
        "diagram-highlight": "#bf9f53",
        "diagram-fill": "#26221a"
      },
      shadows: {
        none: "none",
        sm: "none",
        main: "none",
        lg: "none",
        inner: "none",
        hover: "none",
        active: "none"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Jost", "Helvetica Neue", Arial, sans-serif',
        "font-weight-normal": "300",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.04em",
        "line-height": "1.75",
        "icon-color": "currentColor",
        "transition-fast": "500ms",
        "transition-normal": "650ms",
        "transition-slow": "800ms",
        "transition-timing": "cubic-bezier(0.22, 1, 0.36, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#c8a864"
      }
    }
  }
};

// themes/mac-classic.json
var mac_classic_default = {
  name: "mac-classic",
  displayName: "Classic Mac OS",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "3px",
        space2: "7px",
        space3: "10px",
        space4: "14px",
        space5: "17px",
        space6: "20px",
        space7: "24px",
        space8: "27px",
        space9: "31px",
        space10: "34px",
        space11: "37px",
        space12: "41px"
      },
      buttonHeightSm: "24px",
      buttonHeightMd: "30px",
      buttonHeightLg: "38px",
      inputHeightSm: "24px",
      inputHeightMd: "30px",
      inputHeightLg: "38px",
      rowHeightCompact: "28px",
      rowHeightNormal: "34px",
      rowHeightSpacious: "42px",
      cardPaddingSm: "10px",
      cardPaddingMd: "14px",
      cardPaddingLg: "20px",
      dialogPadding: "20px",
      sectionGap: "28px"
    },
    typeScale: {
      displayFamily: '"Silkscreen", "VT323", monospace',
      bodyFamily: "var(--font-family)",
      monoFamily: '"VT323", ui-monospace, monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&family=IBM+Plex+Sans:wght@400;500;700&family=VT323&display=swap",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "0.02em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "11px",
          lineHeight: "15px"
        },
        sm: {
          size: "13px",
          lineHeight: "18px"
        },
        base: {
          size: "14px",
          lineHeight: "20px"
        },
        lg: {
          size: "16px",
          lineHeight: "22px"
        },
        xl: {
          size: "18px",
          lineHeight: "24px"
        },
        "2xl": {
          size: "20px",
          lineHeight: "26px"
        },
        "3xl": {
          size: "24px",
          lineHeight: "30px"
        },
        "4xl": {
          size: "28px",
          lineHeight: "34px"
        },
        "display-1": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-2": {
          size: "44px",
          lineHeight: "48px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "xl",
          weight: "normal"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "normal"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "0ms",
        normal: "0ms",
        slow: "0ms",
        dramatic: "0ms"
      },
      easings: {
        linear: "steps(1, end)",
        standard: "steps(1, end)",
        emphasized: "steps(1, end)",
        spring: "steps(1, end)"
      },
      shapes: {
        modalEnter: "none",
        modalExit: "none",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "5px",
      radiusInteractive: "4px",
      radiusPill: "5px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.12) 0px, rgba(0, 0, 0, 0.12) 1px, transparent 1px, transparent 3px)",
      pageImageSize: "auto"
    },
    colors: {
      primary: "#000000",
      "primary-hover": "#2b2b2b",
      "primary-foreground": "#ffffff",
      secondary: "#ffffff",
      "secondary-hover": "#e8e8e8",
      "secondary-foreground": "#000000",
      accent: "#000000",
      "accent-foreground": "#ffffff",
      muted: "#e8e8e8",
      "muted-foreground": "#3a3a3a",
      background: "#ffffff",
      foreground: "#000000",
      card: "#ffffff",
      "card-foreground": "#000000",
      surface: "#f2f2f2",
      border: "#000000",
      input: "#000000",
      ring: "#000000",
      "table-header": "#f2f2f2",
      "table-border": "#000000",
      "table-row-hover": "#e0e0e0",
      "surface-hover": "#e0e0e0",
      "border-hover": "#000000",
      placeholder: "#5a5a5a",
      error: "#b30000",
      "error-foreground": "#ffffff",
      success: "#006411",
      "success-foreground": "#ffffff",
      warning: "#8a5a00",
      "warning-foreground": "#ffffff",
      info: "#000000",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#00001c",
      "series-2": "#373ffe",
      "series-3": "#358838",
      "series-4": "#562c05",
      "series-5": "#7605a0",
      "series-6": "#b30069",
      "series-7": "#1f6b8a",
      "series-8": "#7a7a7a",
      "diagram-ink": "#000000",
      "diagram-label": "#000000",
      "diagram-guide": "#595959",
      "diagram-axis": "#000000",
      "diagram-grid": "rgba(0,0,0,0.25)",
      "diagram-highlight": "#0000d3",
      "diagram-fill": "#e6e6e6"
    },
    radii: {
      none: "0px",
      sm: "3px",
      md: "4px",
      lg: "5px",
      xl: "6px",
      full: "6px"
    },
    shadows: {
      main: "2px 2px 0 0 #000000",
      sm: "1px 1px 0 0 #000000",
      lg: "2px 2px 0 0 #000000",
      inner: "inset 1px 1px 0 0 #000000",
      none: "none",
      hover: "2px 2px 0 0 #000000",
      active: "0 0 0 0 #000000"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"IBM Plex Sans", "Chivo", system-ui, sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.45",
      "icon-color": "#000000",
      "transition-fast": "0ms",
      "transition-normal": "0ms",
      "transition-slow": "0ms",
      "transition-timing": "steps(1, end)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "1px",
      "focus-ring-color": "#000000"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "3",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "2 2",
      dot: "1 1",
      fillStyle: "hatch",
      fillOpacity: "0.35",
      roughness: "0",
      glow: "0",
      shadow: "2px 2px 0 #000000",
      marker: "triangle",
      labelFont: "display",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "none",
      corner: "0px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "1",
      lightAmbient: "0.7",
      lightKey: "0.9",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      surface: {
        pageImage: "repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.14) 0px, rgba(255, 255, 255, 0.14) 1px, transparent 1px, transparent 3px)",
        pageImageSize: "auto"
      },
      colors: {
        primary: "#ffffff",
        "primary-hover": "#d8d8d8",
        "primary-foreground": "#000000",
        secondary: "#000000",
        "secondary-hover": "#2a2a2a",
        "secondary-foreground": "#ffffff",
        accent: "#ffffff",
        "accent-foreground": "#000000",
        muted: "#1c1c1c",
        "muted-foreground": "#d0d0d0",
        background: "#000000",
        foreground: "#ffffff",
        card: "#000000",
        "card-foreground": "#ffffff",
        surface: "#101010",
        border: "#ffffff",
        input: "#ffffff",
        ring: "#ffffff",
        "table-header": "#101010",
        "table-border": "#ffffff",
        "table-row-hover": "#262626",
        "surface-hover": "#262626",
        "border-hover": "#ffffff",
        placeholder: "#a0a0a0",
        error: "#ff6b6b",
        "error-foreground": "#000000",
        success: "#5fd070",
        "success-foreground": "#000000",
        warning: "#d4a06a",
        "warning-foreground": "#000000",
        info: "#ffffff",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#c6d78c",
        "series-2": "#7f6de4",
        "series-3": "#38f3b4",
        "series-4": "#ca866a",
        "series-5": "#b78aff",
        "series-6": "#ff6fb0",
        "series-7": "#5fd0e0",
        "series-8": "#a0a0a0",
        "diagram-ink": "#ffffff",
        "diagram-label": "#ffffff",
        "diagram-guide": "#a0a0a0",
        "diagram-axis": "#ffffff",
        "diagram-grid": "rgba(255,255,255,0.25)",
        "diagram-highlight": "#6a8cff",
        "diagram-fill": "#222222"
      },
      shadows: {
        main: "2px 2px 0 0 #ffffff",
        sm: "1px 1px 0 0 #ffffff",
        lg: "2px 2px 0 0 #ffffff",
        inner: "inset 1px 1px 0 0 #ffffff",
        none: "none",
        hover: "2px 2px 0 0 #ffffff",
        active: "0 0 0 0 #ffffff"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"IBM Plex Sans", "Chivo", system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.45",
        "icon-color": "#ffffff",
        "transition-fast": "0ms",
        "transition-normal": "0ms",
        "transition-slow": "0ms",
        "transition-timing": "steps(1, end)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "1px",
        "focus-ring-color": "#ffffff"
      }
    }
  }
};

// themes/material.json
var material_default = {
  name: "material",
  displayName: "Material 3",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "40px",
      inputHeightMd: "48px",
      inputHeightLg: "56px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Roboto Flex", "Roboto", system-ui, sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Roboto Mono", ui-monospace, monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=Roboto+Flex:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Roboto+Mono:wght@400;500&display=swap",
      headingWeight: "500",
      headingTransform: "none",
      headingTracking: "0em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "22px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "28px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "32px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "45px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "57px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "medium"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "medium"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "100ms",
        normal: "200ms",
        slow: "300ms",
        dramatic: "500ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.2, 0, 0, 1)",
        emphasized: "cubic-bezier(0.05, 0.7, 0.1, 1)",
        spring: "cubic-bezier(0.3, 1.3, 0.5, 1)"
      },
      shapes: {
        modalEnter: "scale(0.85)",
        modalExit: "scale(0.85)",
        pageEnter: "translateY(24px)",
        popoverEnter: "scale(0.9)",
        popoverExit: "scale(0.9)",
        toastEnter: "translateY(24px)",
        toastExit: "translateY(24px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.75",
      defaultSize: "24px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "12px",
      radiusInteractive: "var(--radius-full)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      cardImage: "linear-gradient(rgba(103, 80, 164, 0.06), rgba(103, 80, 164, 0.06))",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    },
    colors: {
      primary: "#6750A4",
      "primary-hover": "#5a4593",
      "primary-foreground": "#ffffff",
      secondary: "#E8DEF8",
      "secondary-hover": "#dccff2",
      "secondary-foreground": "#1D192B",
      accent: "#7D5260",
      "accent-foreground": "#ffffff",
      muted: "#E7E0EC",
      "muted-foreground": "#49454F",
      background: "#FEF7FF",
      foreground: "#1D1B20",
      card: "#F3EDF7",
      "card-foreground": "#1D1B20",
      surface: "#F7F2FA",
      border: "#79747E",
      input: "#79747E",
      ring: "#6750A4",
      "table-header": "#F7F2FA",
      "table-border": "#79747E",
      "table-row-hover": "#ECE6F0",
      "surface-hover": "#ECE6F0",
      "border-hover": "#6750A4",
      placeholder: "#6b6672",
      error: "#B3261E",
      "error-foreground": "#ffffff",
      success: "#386A20",
      "success-foreground": "#ffffff",
      warning: "#FFDEA0",
      "warning-foreground": "#261900",
      info: "#00639B",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#9a3ba4",
      "series-2": "#00696b",
      "series-3": "#0061a4",
      "series-4": "#8d5100",
      "series-5": "#237f56",
      "series-6": "#984061",
      "series-7": "#6a5f00",
      "series-8": "#676777",
      "diagram-ink": "#1D1B20",
      "diagram-label": "#49454F",
      "diagram-guide": "#7a757f",
      "diagram-axis": "#49454F",
      "diagram-grid": "#cac4d0",
      "diagram-highlight": "#6750A4",
      "diagram-fill": "#E8DEF8"
    },
    radii: {
      none: "0px",
      sm: "8px",
      md: "12px",
      lg: "16px",
      xl: "28px",
      full: "9999px"
    },
    shadows: {
      main: "0 1px 2px rgba(0,0,0,.30), 0 2px 6px 2px rgba(0,0,0,.15)",
      sm: "0 1px 2px rgba(0,0,0,.30), 0 1px 3px 1px rgba(0,0,0,.15)",
      lg: "0 1px 3px rgba(0,0,0,.30), 0 4px 8px 3px rgba(0,0,0,.15)",
      inner: "none",
      none: "none",
      hover: "0 1px 2px rgba(0,0,0,.30), 0 1px 3px 1px rgba(0,0,0,.15)",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Roboto", system-ui, sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.5",
      "icon-color": "#6750A4",
      "transition-fast": "100ms",
      "transition-normal": "200ms",
      "transition-slow": "300ms",
      "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#6750A4"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "3",
      lineCap: "round",
      lineJoin: "round",
      dash: "8 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "0",
      shadow: "0 1px 3px rgba(0,0,0,0.3)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "8px"
    },
    scene: {
      materialRoughness: "0.5",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "1",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      surface: {
        cardImage: "linear-gradient(rgba(208, 188, 255, 0.08), rgba(208, 188, 255, 0.08))",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
        edgeHeight: "clamp(16px, 4vw, 56px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
        textureSize: "40px 40px",
        accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
      },
      colors: {
        primary: "#D0BCFF",
        "primary-hover": "#bfa6f5",
        "primary-foreground": "#381E72",
        secondary: "#4A4458",
        "secondary-hover": "#595267",
        "secondary-foreground": "#E8DEF8",
        accent: "#EFB8C8",
        "accent-foreground": "#492532",
        muted: "#49454F",
        "muted-foreground": "#CAC4D0",
        background: "#141218",
        foreground: "#E6E0E9",
        card: "#211F26",
        "card-foreground": "#E6E0E9",
        surface: "#1D1B20",
        border: "#938F99",
        input: "#938F99",
        ring: "#D0BCFF",
        "table-header": "#1D1B20",
        "table-border": "#938F99",
        "table-row-hover": "#2B2930",
        "surface-hover": "#2B2930",
        "border-hover": "#D0BCFF",
        placeholder: "#a39eaa",
        error: "#F2B8B5",
        "error-foreground": "#601410",
        success: "#A1D489",
        "success-foreground": "#0B3900",
        warning: "#FFD98E",
        "warning-foreground": "#422C00",
        info: "#99CBFF",
        "info-foreground": "#003355",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#e5a9d9",
        "series-2": "#6fd8d9",
        "series-3": "#9ccaff",
        "series-4": "#f5ae5c",
        "series-5": "#1dcd78",
        "series-6": "#f485ab",
        "series-7": "#cccb6d",
        "series-8": "#9a9cac",
        "diagram-ink": "#f5eae4",
        "diagram-label": "#CAC4D0",
        "diagram-guide": "#938F99",
        "diagram-axis": "#CAC4D0",
        "diagram-grid": "#49454F",
        "diagram-highlight": "#D0BCFF",
        "diagram-fill": "#36343b"
      },
      shadows: {
        main: "0 1px 2px rgba(0,0,0,.30), 0 2px 6px 2px rgba(0,0,0,.15)",
        sm: "0 1px 2px rgba(0,0,0,.30), 0 1px 3px 1px rgba(0,0,0,.15)",
        lg: "0 1px 3px rgba(0,0,0,.30), 0 4px 8px 3px rgba(0,0,0,.15)",
        inner: "none",
        none: "none",
        hover: "0 1px 2px rgba(0,0,0,.30), 0 1px 3px 1px rgba(0,0,0,.15)",
        active: "none"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Roboto", system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.5",
        "icon-color": "#D0BCFF",
        "transition-fast": "100ms",
        "transition-normal": "200ms",
        "transition-slow": "300ms",
        "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#D0BCFF"
      }
    }
  }
};

// themes/memphis.json
var memphis_default = {
  name: "memphis",
  displayName: "Memphis 80s",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "42px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "42px",
      inputHeightLg: "52px",
      rowHeightCompact: "34px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "54px",
      cardPaddingSm: "14px",
      cardPaddingMd: "20px",
      cardPaddingLg: "28px",
      dialogPadding: "28px",
      sectionGap: "36px"
    },
    typeScale: {
      displayFamily: '"Bungee", "Lilita One", "Rubik", sans-serif',
      bodyFamily: '"Rubik", "Helvetica Neue", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Bungee&family=Lilita+One&family=Rubik:wght@400;500;700&display=swap",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.03em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        spring: "cubic-bezier(0.68, -0.6, 0.32, 1.6)"
      },
      shapes: {
        modalEnter: "scale(0.8) rotate(-3deg)",
        modalExit: "scale(0.8) rotate(-3deg)",
        pageEnter: "translateY(16px)",
        popoverEnter: "scale(0.85)",
        popoverExit: "scale(0.85)",
        toastEnter: "translateY(24px) rotate(2deg)",
        toastExit: "translateY(24px) rotate(2deg)"
      }
    },
    iconography: {
      family: "phosphor-fill",
      strokeWidth: "2",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-none)",
      radiusInteractive: "var(--radius-full)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderHairline: "2px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "dashed",
      borderStyleInteractive: "dashed"
    },
    colors: {
      primary: "#d81b7a",
      "primary-hover": "#b31565",
      "primary-foreground": "#ffffff",
      secondary: "#19c1cc",
      "secondary-hover": "#12a9b4",
      "secondary-foreground": "#05262a",
      accent: "#ffd21f",
      "accent-foreground": "#1a1400",
      muted: "#f2e9ff",
      "muted-foreground": "#4a3a6a",
      background: "#fff7e0",
      foreground: "#14101f",
      card: "#ffffff",
      "card-foreground": "#14101f",
      surface: "#ffeef7",
      border: "#14101f",
      input: "#14101f",
      ring: "#6a3df0",
      "table-header": "#ffeef7",
      "table-border": "#14101f",
      "table-row-hover": "#fff7e0",
      "surface-hover": "#ffe0f0",
      "border-hover": "#d81b7a",
      placeholder: "#6b5f85",
      error: "#d6141f",
      "error-foreground": "#ffffff",
      success: "#087a40",
      "success-foreground": "#ffffff",
      warning: "#ffb800",
      "warning-foreground": "#1a1000",
      info: "#5a2ee0",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#ef4ab2",
      "series-2": "#0f8f99",
      "series-3": "#b07d00",
      "series-4": "#6b3fe0",
      "series-5": "#e8590c",
      "series-6": "#2f6fe0",
      "series-7": "#439326",
      "series-8": "#8a2f6a",
      "diagram-ink": "#14101f",
      "diagram-label": "#14101f",
      "diagram-guide": "#4a3a6a",
      "diagram-axis": "#14101f",
      "diagram-grid": "rgba(20,16,31,0.15)",
      "diagram-highlight": "#d81b7a",
      "diagram-fill": "#ffd21f"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      main: "6px 6px 0 0 #ff5fa2",
      sm: "3px 3px 0 0 #ff5fa2",
      lg: "9px 9px 0 0 #15b8c4",
      inner: "inset 3px 3px 0 0 rgba(123,77,255,0.25)",
      none: "none",
      hover: "8px 8px 0 0 #ffd21f",
      active: "1px 1px 0 0 #ff5fa2"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "5px",
      "font-family": '"Rubik", "Helvetica Neue", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.55",
      "icon-color": "#d81b7a",
      "transition-fast": "120ms",
      "transition-normal": "220ms",
      "transition-slow": "380ms",
      "transition-timing": "cubic-bezier(0.68, -0.4, 0.32, 1.5)",
      "hover-scale": "1.04",
      "hover-translate-y": "-2px",
      "hover-translate-x": "-1px",
      "active-scale": "0.96",
      "active-translate-y": "2px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#6a3df0"
    },
    surface: {
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><g fill='none' stroke-width='3' stroke-linecap='round'><path d='M10 20 q8 -10 16 0 t16 0' stroke='%23ff5fa2' opacity='.35'/><path d='M70 90 q8 -10 16 0 t16 0' stroke='%2315b8c4' opacity='.35'/></g><circle cx='90' cy='25' r='5' fill='%23ffd21f' opacity='.4'/><rect x='20' y='80' width='10' height='10' fill='%237b4dff' opacity='.3' transform='rotate(20 25 85)'/><path d='M55 50 l6 10 l-12 0 z' fill='%23ff5fa2' opacity='.3'/></svg>")`,
      pageImageSize: "120px 120px"
    },
    diagram: {
      strokeThin: "2",
      strokeNormal: "3",
      strokeBold: "5",
      lineCap: "round",
      lineJoin: "round",
      dash: "10 6",
      dot: "1 7",
      fillStyle: "solid",
      fillOpacity: "0.35",
      roughness: "0",
      glow: "0",
      shadow: "3px 3px 0 #14101f",
      marker: "dot",
      labelFont: "display",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.8",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "3",
      lightAmbient: "0.7",
      lightKey: "1",
      lightKeyColor: "#fff4c4",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff6fb5",
        "primary-hover": "#ff8ac3",
        "primary-foreground": "#1a0310",
        secondary: "#3fe0e8",
        "secondary-hover": "#66e8ee",
        "secondary-foreground": "#03202a",
        accent: "#ffd21f",
        "accent-foreground": "#1a1400",
        muted: "#2a2050",
        "muted-foreground": "#d3c8f5",
        background: "#170f2e",
        foreground: "#fdf3d8",
        card: "#21183f",
        "card-foreground": "#fdf3d8",
        surface: "#1c1436",
        border: "#fdf3d8",
        input: "#fdf3d8",
        ring: "#ffd21f",
        "table-header": "#1c1436",
        "table-border": "#fdf3d8",
        "table-row-hover": "#261c48",
        "surface-hover": "#312459",
        "border-hover": "#ff6fb5",
        placeholder: "#a596cf",
        error: "#ff7a82",
        "error-foreground": "#200306",
        success: "#4ee39a",
        "success-foreground": "#03190d",
        warning: "#ffc933",
        "warning-foreground": "#1a1000",
        info: "#a48bff",
        "info-foreground": "#0f0630",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ff6fb5",
        "series-2": "#3fd8e0",
        "series-3": "#b4c705",
        "series-4": "#a58aff",
        "series-5": "#ff9a4a",
        "series-6": "#6fa8ff",
        "series-7": "#75d650",
        "series-8": "#e887f6",
        "diagram-ink": "#fdf3d8",
        "diagram-label": "#fdf3d8",
        "diagram-guide": "#b8aed8",
        "diagram-axis": "#fdf3d8",
        "diagram-grid": "rgba(253,243,216,0.18)",
        "diagram-highlight": "#fef043",
        "diagram-fill": "#3a2c6a"
      },
      shadows: {
        main: "6px 6px 0 0 #ff6fb5",
        sm: "3px 3px 0 0 #ff6fb5",
        lg: "9px 9px 0 0 #3fe0e8",
        inner: "inset 3px 3px 0 0 rgba(255,210,31,0.25)",
        none: "none",
        hover: "8px 8px 0 0 #ffd21f",
        active: "1px 1px 0 0 #ff6fb5"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "5px",
        "font-family": '"Rubik", "Helvetica Neue", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.55",
        "icon-color": "#ff6fb5",
        "transition-fast": "120ms",
        "transition-normal": "220ms",
        "transition-slow": "380ms",
        "transition-timing": "cubic-bezier(0.68, -0.4, 0.32, 1.5)",
        "hover-scale": "1.04",
        "hover-translate-y": "-2px",
        "hover-translate-x": "-1px",
        "active-scale": "0.96",
        "active-translate-y": "2px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ffd21f"
      },
      radii: {
        none: "0px",
        sm: "0px",
        md: "0px",
        lg: "0px",
        xl: "0px",
        full: "9999px"
      },
      surface: {
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><g fill='none' stroke-width='3' stroke-linecap='round'><path d='M10 20 q8 -10 16 0 t16 0' stroke='%23ff5fa2' opacity='.35'/><path d='M70 90 q8 -10 16 0 t16 0' stroke='%2315b8c4' opacity='.35'/></g><circle cx='90' cy='25' r='5' fill='%23ffd21f' opacity='.4'/><rect x='20' y='80' width='10' height='10' fill='%237b4dff' opacity='.3' transform='rotate(20 25 85)'/><path d='M55 50 l6 10 l-12 0 z' fill='%23ff5fa2' opacity='.3'/></svg>")`,
        pageImageSize: "120px 120px"
      }
    }
  }
};

// themes/mid-century.json
var mid_century_default = {
  name: "mid-century",
  displayName: "Mid-Century Modern",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "42px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "42px",
      inputHeightLg: "52px",
      rowHeightCompact: "36px",
      rowHeightNormal: "46px",
      rowHeightSpacious: "56px",
      cardPaddingSm: "16px",
      cardPaddingMd: "22px",
      cardPaddingLg: "30px",
      dialogPadding: "30px",
      sectionGap: "36px"
    },
    typeScale: {
      displayFamily: '"DM Serif Display", Georgia, serif',
      bodyFamily: '"Work Sans", "Helvetica Neue", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Work+Sans:wght@400;500;700&display=swap",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "0em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "500ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.25, 0.8, 0.25, 1)",
        spring: "cubic-bezier(0.3, 1.25, 0.5, 1)"
      },
      shapes: {
        modalEnter: "scale(0.9)",
        modalExit: "scale(0.9)",
        pageEnter: "translateY(10px)",
        popoverEnter: "scale(0.95)",
        popoverExit: "scale(0.95)",
        toastEnter: "translateX(16px)",
        toastExit: "translateX(16px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.75",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      cornerShape: "squircle",
      cornerShapePill: "round",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#b5531a",
      "primary-hover": "#94430f",
      "primary-foreground": "#fff8ea",
      secondary: "#1f7a7a",
      "secondary-hover": "#186262",
      "secondary-foreground": "#f2fffe",
      accent: "#ddaa24",
      "accent-foreground": "#2a1c00",
      muted: "#efe3c6",
      "muted-foreground": "#5a4630",
      background: "#f8efd9",
      foreground: "#33231a",
      card: "#fff9ea",
      "card-foreground": "#33231a",
      surface: "#f1e5c8",
      border: "#6b4a32",
      input: "#6b4a32",
      ring: "#1f7a7a",
      "table-header": "#f1e5c8",
      "table-border": "#6b4a32",
      "table-row-hover": "#f8efd9",
      "surface-hover": "#eadbb8",
      "border-hover": "#b5531a",
      placeholder: "#7c6650",
      error: "#a82a1c",
      "error-foreground": "#ffffff",
      success: "#2f7a3a",
      "success-foreground": "#ffffff",
      warning: "#ddaa24",
      "warning-foreground": "#2a1c00",
      info: "#1f6a8a",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#cd7520",
      "series-2": "#1f7a7a",
      "series-3": "#9a6e0a",
      "series-4": "#616c24",
      "series-5": "#3a5a7a",
      "series-6": "#8a3a4a",
      "series-7": "#805847",
      "series-8": "#7a5a8a",
      "diagram-ink": "#33231a",
      "diagram-label": "#5a4630",
      "diagram-guide": "#8a7358",
      "diagram-axis": "#5a4630",
      "diagram-grid": "rgba(107,74,50,0.18)",
      "diagram-highlight": "#b5531a",
      "diagram-fill": "#f0d9a8"
    },
    radii: {
      none: "0px",
      sm: "8px",
      md: "14px",
      lg: "20px",
      xl: "28px",
      full: "9999px"
    },
    shadows: {
      main: "0 4px 10px rgba(107,74,50,0.2)",
      sm: "0 2px 4px rgba(107,74,50,0.16)",
      lg: "0 10px 24px rgba(107,74,50,0.24)",
      inner: "inset 0 2px 4px rgba(107,74,50,0.14)",
      none: "none",
      hover: "0 6px 14px rgba(107,74,50,0.26)",
      active: "0 1px 2px rgba(107,74,50,0.2)"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Work Sans", "Helvetica Neue", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.55",
      "icon-color": "#b5531a",
      "transition-fast": "140ms",
      "transition-normal": "240ms",
      "transition-slow": "380ms",
      "transition-timing": "cubic-bezier(0.25, 0.8, 0.25, 1)",
      "hover-scale": "1.02",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.98",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#1f7a7a"
    },
    surface: {
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><g stroke='%23c8772a' stroke-width='1.5' opacity='.18' stroke-linecap='round'><path d='M70 54V86M54 70H86M58.7 58.7L81.3 81.3M81.3 58.7L58.7 81.3'/></g><circle cx='70' cy='70' r='3' fill='%232a8a8a' opacity='.22'/><circle cx='70' cy='54' r='2' fill='%23d9a21b' opacity='.3'/><circle cx='70' cy='86' r='2' fill='%23d9a21b' opacity='.3'/><circle cx='54' cy='70' r='2' fill='%23d9a21b' opacity='.3'/><circle cx='86' cy='70' r='2' fill='%23d9a21b' opacity='.3'/></svg>")`,
      pageImageSize: "140px 140px"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "3",
      lineCap: "round",
      lineJoin: "round",
      dash: "7 4",
      dot: "1 5",
      fillStyle: "solid",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "0",
      shadow: "2px 2px 0 rgba(107,74,50,0.3)",
      marker: "dot",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.65",
      materialMetalness: "0.05",
      materialFlat: "1",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "1",
      lightKeyColor: "#ffe9c0",
      fog: "0.05"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#f08a4a",
        "primary-hover": "#f4a06a",
        "primary-foreground": "#2a1004",
        secondary: "#3fb0b0",
        "secondary-hover": "#62c4c4",
        "secondary-foreground": "#042222",
        accent: "#f0c04a",
        "accent-foreground": "#2a1c00",
        muted: "#3a2b22",
        "muted-foreground": "#dccdb0",
        background: "#26190f",
        foreground: "#f7ecd2",
        card: "#33241a",
        "card-foreground": "#f7ecd2",
        surface: "#2d1f15",
        border: "#b38a64",
        input: "#b38a64",
        ring: "#3fb0b0",
        "table-header": "#2d1f15",
        "table-border": "#b38a64",
        "table-row-hover": "#3a2a1f",
        "surface-hover": "#443226",
        "border-hover": "#f08a4a",
        placeholder: "#ae9a7c",
        error: "#ff8a78",
        "error-foreground": "#2a0804",
        success: "#7ed08a",
        "success-foreground": "#05190a",
        warning: "#f0c04a",
        "warning-foreground": "#2a1c00",
        info: "#7fc4e0",
        "info-foreground": "#05222e",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#c7651e",
        "series-2": "#4fc0b8",
        "series-3": "#b4a22b",
        "series-4": "#9fb661",
        "series-5": "#7aa4d0",
        "series-6": "#d4788a",
        "series-7": "#c4a07a",
        "series-8": "#b09ac4",
        "diagram-ink": "#f7fade",
        "diagram-label": "#d4c4a4",
        "diagram-guide": "#a89478",
        "diagram-axis": "#d4c4a4",
        "diagram-grid": "rgba(247,236,210,0.15)",
        "diagram-highlight": "#f08a4a",
        "diagram-fill": "#4a3626"
      },
      shadows: {
        main: "0 4px 10px rgba(0,0,0,0.4)",
        sm: "0 2px 4px rgba(0,0,0,0.35)",
        lg: "0 10px 24px rgba(0,0,0,0.45)",
        inner: "inset 0 2px 4px rgba(0,0,0,0.35)",
        none: "none",
        hover: "0 6px 14px rgba(0,0,0,0.45)",
        active: "0 1px 2px rgba(0,0,0,0.45)"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Work Sans", "Helvetica Neue", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.55",
        "icon-color": "#f08a4a",
        "transition-fast": "140ms",
        "transition-normal": "240ms",
        "transition-slow": "380ms",
        "transition-timing": "cubic-bezier(0.25, 0.8, 0.25, 1)",
        "hover-scale": "1.02",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.98",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#3fb0b0"
      },
      radii: {
        none: "0px",
        sm: "8px",
        md: "14px",
        lg: "20px",
        xl: "28px",
        full: "9999px"
      },
      surface: {
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><g stroke='%23c8772a' stroke-width='1.5' opacity='.18' stroke-linecap='round'><path d='M70 54V86M54 70H86M58.7 58.7L81.3 81.3M81.3 58.7L58.7 81.3'/></g><circle cx='70' cy='70' r='3' fill='%232a8a8a' opacity='.22'/><circle cx='70' cy='54' r='2' fill='%23d9a21b' opacity='.3'/><circle cx='70' cy='86' r='2' fill='%23d9a21b' opacity='.3'/><circle cx='54' cy='70' r='2' fill='%23d9a21b' opacity='.3'/><circle cx='86' cy='70' r='2' fill='%23d9a21b' opacity='.3'/></svg>")`,
        pageImageSize: "140px 140px"
      }
    }
  }
};

// themes/minimalist.json
var minimalist_default = {
  name: "minimalist",
  displayName: "Minimalist",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "44px",
      buttonHeightLg: "44px",
      inputHeightSm: "28px",
      inputHeightMd: "44px",
      inputHeightLg: "44px",
      rowHeightCompact: "32px",
      rowHeightNormal: "52px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "32px",
      cardPaddingLg: "40px",
      dialogPadding: "24px",
      sectionGap: "64px"
    },
    typeScale: {
      displayFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap",
      headingWeight: "300",
      headingTransform: "none",
      headingTracking: "-0.03em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "none",
        modalExit: "none",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "0px",
      radiusInteractive: "0px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "1px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "none",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#18181b",
      "primary-hover": "#27272a",
      "primary-foreground": "#fafafa",
      secondary: "#fafafa",
      "secondary-hover": "#f4f4f5",
      "secondary-foreground": "#18181b",
      accent: "#18181b",
      "accent-foreground": "#fafafa",
      muted: "#f4f4f5",
      "muted-foreground": "#65656e",
      background: "#f8fafc",
      foreground: "#18181b",
      card: "#ffffff",
      "card-foreground": "#18181b",
      surface: "#f4f4f5",
      border: "#e4e4e7",
      input: "#d4d4d8",
      ring: "#18181b",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#ca8a04",
      "warning-foreground": "#000000",
      info: "#2563eb",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#0b618e",
      "series-2": "#b45f3a",
      "series-3": "#589373",
      "series-4": "#8a5a9a",
      "series-5": "#a3902c",
      "series-6": "#536d73",
      "series-7": "#a04a5a",
      "series-8": "#3a8a9a",
      "diagram-ink": "#18181b",
      "diagram-label": "#65656e",
      "diagram-guide": "#8d8d96",
      "diagram-axis": "#65656e",
      "diagram-grid": "#e4e4e7",
      "diagram-highlight": "#2563eb",
      "diagram-fill": "#f4f4f5"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "1px",
      "font-family": '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "600",
      "letter-spacing": "-0.005em",
      "line-height": "1.75",
      "icon-color": "currentColor",
      "transition-fast": "150ms",
      "transition-normal": "240ms",
      "transition-slow": "400ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.01",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#18181b"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "1",
      strokeBold: "2",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "3 3",
      dot: "1 3",
      fillStyle: "outline",
      fillOpacity: "0.08",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "line",
      labelFont: "body",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "none",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.8",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "0.8",
      lightKeyColor: "#ffffff",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#fafafa",
        "primary-hover": "#e4e4e7",
        "primary-foreground": "#18181b",
        secondary: "#27272a",
        "secondary-hover": "#3f3f46",
        "secondary-foreground": "#fafafa",
        accent: "#fafafa",
        "accent-foreground": "#18181b",
        muted: "#27272a",
        "muted-foreground": "#a1a1aa",
        background: "#09090b",
        foreground: "#fafafa",
        card: "#18181b",
        "card-foreground": "#fafafa",
        surface: "#27272a",
        border: "#27272a",
        input: "#3f3f46",
        ring: "#fafafa",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#60a5fa",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#929bdf",
        "series-2": "#e0906a",
        "series-3": "#6fc09a",
        "series-4": "#c49ad4",
        "series-5": "#cebb6b",
        "series-6": "#758996",
        "series-7": "#de8aa1",
        "series-8": "#6fbfd0",
        "diagram-ink": "#fafafa",
        "diagram-label": "#a1a1aa",
        "diagram-guide": "#78787f",
        "diagram-axis": "#a1a1aa",
        "diagram-grid": "#27272a",
        "diagram-highlight": "#60a5fa",
        "diagram-fill": "#27272a"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "1px",
        "font-family": '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "600",
        "letter-spacing": "-0.005em",
        "line-height": "1.75",
        "icon-color": "currentColor",
        "transition-fast": "150ms",
        "transition-normal": "240ms",
        "transition-slow": "400ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#fafafa"
      }
    }
  }
};

// themes/mudcloth.json
var mudcloth_default = {
  name: "mudcloth",
  displayName: "Mudcloth",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Archivo Black", "Rubik", sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.05em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Archivo+Black&family=Rubik:wght@400;500;700&family=Work+Sans:wght@400;500;600;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "500ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.3, 0, 0, 1)",
        spring: "cubic-bezier(0.3, 0, 0, 1)"
      },
      shapes: {
        modalEnter: "translateY(12px)",
        modalExit: "translateY(12px)",
        pageEnter: "translateY(8px)",
        popoverEnter: "translateY(6px)",
        popoverExit: "translateY(6px)",
        toastEnter: "translateY(16px)",
        toastExit: "translateY(16px)"
      }
    },
    iconography: {
      family: "fa-solid",
      strokeWidth: "0",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "0px",
      radiusInteractive: "2px",
      radiusPill: "2px",
      cornerShape: "notch",
      cornerShapePill: "notch",
      borderStyle: "solid",
      borderStyleInteractive: "dashed",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'><g fill='none' stroke='%236b3f24' stroke-opacity='0.09' stroke-width='1.5'><polyline points='0,12 6,6 12,12 18,6 24,12 30,6 36,12 42,6 48,12'/><polyline points='0,40 6,34 12,40 18,34 24,40 30,34 36,40 42,34 48,40'/><path d='M24 18L30 24L24 30L18 24Z'/></g><g fill='%236b3f24' fill-opacity='0.09'><circle cx='6' cy='24' r='1.5'/><circle cx='42' cy='24' r='1.5'/></g></svg>")`,
      pageImageSize: "48px 48px"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "1px",
      lg: "2px",
      xl: "2px",
      full: "9999px"
    },
    colors: {
      primary: "#6b3f24",
      "primary-hover": "#56321c",
      "primary-foreground": "#f7efdc",
      secondary: "#d19a3a",
      "secondary-hover": "#c48c2c",
      "secondary-foreground": "#1f1206",
      accent: "#1a1410",
      "accent-foreground": "#f1e9d8",
      muted: "#e3d7bd",
      "muted-foreground": "#57462f",
      background: "#f1e9d8",
      foreground: "#1f1710",
      card: "#f8f1e0",
      "card-foreground": "#1f1710",
      surface: "#e9dec4",
      border: "#1f1710",
      input: "#1f1710",
      ring: "#b7791f",
      "table-header": "#e3d7bd",
      "table-border": "#1f1710",
      "table-row-hover": "#ece2c9",
      "surface-hover": "#e6dabf",
      "border-hover": "#6b3f24",
      placeholder: "#74654d",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#754630",
      "series-2": "#771a16",
      "series-3": "#ad6035",
      "series-4": "#455925",
      "series-5": "#2a5266",
      "series-6": "#5a3a62",
      "series-7": "#7a6a48",
      "series-8": "#4d3e3f",
      "diagram-ink": "#1f1710",
      "diagram-label": "#57462f",
      "diagram-guide": "#86724f",
      "diagram-axis": "#1f1710",
      "diagram-grid": "rgba(31,23,16,0.15)",
      "diagram-highlight": "#a06a14",
      "diagram-fill": "#e6c98a"
    },
    shadows: {
      main: "4px 4px 0 0 #6b3f24",
      sm: "2px 2px 0 0 #6b3f24",
      lg: "7px 7px 0 0 #6b3f24",
      inner: "inset 2px 2px 0 0 rgba(107,63,36,0.2)",
      none: "none",
      hover: "6px 6px 0 0 #6b3f24",
      active: "1px 1px 0 0 #6b3f24"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Work Sans", "Helvetica Neue", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#6b3f24",
      "transition-fast": "120ms",
      "transition-normal": "220ms",
      "transition-slow": "360ms",
      "transition-timing": "cubic-bezier(0.3, 0, 0, 1)",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "2px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#b7791f"
    },
    diagram: {
      strokeThin: "1.5",
      strokeNormal: "3",
      strokeBold: "5",
      lineCap: "square",
      lineJoin: "miter",
      dash: "6 6",
      dot: "2 6",
      fillStyle: "hatch",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "0",
      shadow: "3px 3px 0 rgba(31,23,16,0.35)",
      marker: "triangle",
      labelFont: "display",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.6",
      lightKey: "0.9",
      lightKeyColor: "#ffe2b0",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Archivo Black", "Rubik", sans-serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "400",
        headingTransform: "uppercase",
        headingTracking: "0.05em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Archivo+Black&family=Rubik:wght@400;500;700&family=Work+Sans:wght@400;500;600;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "500ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.3, 0, 0, 1)",
          spring: "cubic-bezier(0.3, 0, 0, 1)"
        },
        shapes: {
          modalEnter: "translateY(12px)",
          modalExit: "translateY(12px)",
          pageEnter: "translateY(8px)",
          popoverEnter: "translateY(6px)",
          popoverExit: "translateY(6px)",
          toastEnter: "translateY(16px)",
          toastExit: "translateY(16px)"
        }
      },
      iconography: {
        family: "fa-solid",
        strokeWidth: "0",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "0px",
        radiusInteractive: "2px",
        radiusPill: "2px",
        cornerShape: "notch",
        cornerShapePill: "notch",
        borderStyle: "solid",
        borderStyleInteractive: "dashed",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'><g fill='none' stroke='%23d19a3a' stroke-opacity='0.08' stroke-width='1.5'><polyline points='0,12 6,6 12,12 18,6 24,12 30,6 36,12 42,6 48,12'/><polyline points='0,40 6,34 12,40 18,34 24,40 30,34 36,40 42,34 48,40'/><path d='M24 18L30 24L24 30L18 24Z'/></g><g fill='%23d19a3a' fill-opacity='0.08'><circle cx='6' cy='24' r='1.5'/><circle cx='42' cy='24' r='1.5'/></g></svg>")`,
        pageImageSize: "48px 48px"
      },
      radii: {
        none: "0px",
        sm: "0px",
        md: "1px",
        lg: "2px",
        xl: "2px",
        full: "9999px"
      },
      colors: {
        primary: "#d19a3a",
        "primary-hover": "#e0ab4c",
        "primary-foreground": "#1a1005",
        secondary: "#b98259",
        "secondary-hover": "#c8926a",
        "secondary-foreground": "#150c05",
        accent: "#efe4cc",
        "accent-foreground": "#1a1410",
        muted: "#2a211a",
        "muted-foreground": "#c9bca3",
        background: "#1a1410",
        foreground: "#efe4cc",
        card: "#231b15",
        "card-foreground": "#efe4cc",
        surface: "#20180f",
        border: "#8a6b4a",
        input: "#c9bca3",
        ring: "#d19a3a",
        "table-header": "#20180f",
        "table-border": "#8a6b4a",
        "table-row-hover": "#2a2118",
        "surface-hover": "#30261c",
        "border-hover": "#d19a3a",
        placeholder: "#9a8c74",
        error: "#ff8a80",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#f9a153",
        "series-2": "#d4835a",
        "series-3": "#8fb06a",
        "series-4": "#6fa8bb",
        "series-5": "#b78ac4",
        "series-6": "#b19576",
        "series-7": "#c6b458",
        "series-8": "#9a9a8a",
        "diagram-ink": "#f4f0d6",
        "diagram-label": "#c9bb9e",
        "diagram-guide": "#968872",
        "diagram-axis": "#c9bb9e",
        "diagram-grid": "rgba(239,228,204,0.15)",
        "diagram-highlight": "#c7912e",
        "diagram-fill": "#3a2d22"
      },
      shadows: {
        main: "4px 4px 0 0 #000000",
        sm: "2px 2px 0 0 #000000",
        lg: "7px 7px 0 0 #000000",
        inner: "inset 2px 2px 0 0 rgba(0,0,0,0.4)",
        none: "none",
        hover: "6px 6px 0 0 #8a6b4a",
        active: "1px 1px 0 0 #000000"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Work Sans", "Helvetica Neue", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#d19a3a",
        "transition-fast": "120ms",
        "transition-normal": "220ms",
        "transition-slow": "360ms",
        "transition-timing": "cubic-bezier(0.3, 0, 0, 1)",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "2px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#d19a3a"
      }
    }
  }
};

// themes/neon.json
var neon_default = {
  name: "neon",
  displayName: "Neon",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "44px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "44px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Tilt Neon", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Exo 2", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Tilt+Neon&family=Exo+2:wght@300;400;500;600;700&display=swap",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.08em",
      headingStyle: "normal",
      headingShadow: "0 0 2px rgba(255,255,255,0.8), 0 0 10px rgba(34,211,238,0.55)"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "400ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.4, 0, 0.6, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(1.04)",
        modalExit: "scale(1.04)",
        pageEnter: "none",
        popoverEnter: "scale(1.02)",
        popoverExit: "scale(1.02)",
        toastEnter: "translateX(16px)",
        toastExit: "translateX(16px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#0e7490",
      "primary-hover": "#155e75",
      "primary-foreground": "#ffffff",
      secondary: "#ccfbf1",
      "secondary-hover": "#99f6e4",
      "secondary-foreground": "#134e4a",
      accent: "#be185d",
      "accent-foreground": "#ffffff",
      muted: "#ccfbf1",
      "muted-foreground": "#475569",
      background: "#f0fdfa",
      foreground: "#134e4a",
      card: "#ffffff",
      "card-foreground": "#134e4a",
      surface: "#ccfbf1",
      border: "rgba(34, 211, 238, 0.3)",
      input: "rgba(34, 211, 238, 0.35)",
      ring: "#22d3ee",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#ca8a04",
      "warning-foreground": "#000000",
      info: "#2563eb",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#0e7490",
      "series-2": "#d74291",
      "series-3": "#6d28d9",
      "series-4": "#b45309",
      "series-5": "#618305",
      "series-6": "#1d4ed8",
      "series-7": "#a914a4",
      "series-8": "#0f766e",
      "diagram-ink": "#134e4a",
      "diagram-label": "#475569",
      "diagram-guide": "#7a8aa0",
      "diagram-axis": "#134e4a",
      "diagram-grid": "rgba(34,211,238,0.3)",
      "diagram-highlight": "#be185d",
      "diagram-fill": "#ccfbf1"
    },
    radii: {
      none: "0px",
      sm: "3px",
      md: "6px",
      lg: "10px",
      xl: "14px",
      full: "9999px"
    },
    shadows: {
      main: "0 0 8px rgba(34,211,238,0.6), 0 0 22px rgba(34,211,238,0.35), inset 0 0 10px rgba(34,211,238,0.15)",
      sm: "0 0 5px rgba(34,211,238,0.6), 0 0 12px rgba(34,211,238,0.3)",
      lg: "0 0 12px rgba(34,211,238,0.75), 0 0 40px rgba(34,211,238,0.45), 0 0 80px rgba(244,114,182,0.25)",
      inner: "inset 0 0 12px rgba(34,211,238,0.25)",
      none: "none",
      hover: "0 0 12px rgba(244,114,182,0.75), 0 0 30px rgba(244,114,182,0.45)",
      active: "0 0 4px rgba(34,211,238,0.8), inset 0 0 14px rgba(34,211,238,0.35)"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Exo 2", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "500",
      "font-weight-medium": "600",
      "font-weight-bold": "800",
      "letter-spacing": "0.03em",
      "line-height": "1.5",
      "icon-color": "#22d3ee",
      "transition-fast": "80ms",
      "transition-normal": "160ms",
      "transition-slow": "150ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.6, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "0.98",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#22d3ee"
    },
    surface: {
      pageImage: "radial-gradient(circle at 20% 10%, rgba(34,211,238,0.10), transparent 45%), radial-gradient(circle at 80% 90%, rgba(244,114,182,0.10), transparent 45%)",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 L1440 0 V100 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='80'><path d='M0 20 C40 0 80 40 120 20 S160 0 160 20 M0 60 C40 40 80 80 120 60 S160 40 160 60' fill='none' stroke='black' stroke-width='1.5'/></svg>")`,
      textureSize: "160px 80px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "3",
      lineCap: "round",
      lineJoin: "round",
      dash: "8 5",
      dot: "1 5",
      fillStyle: "outline",
      fillOpacity: "0.15",
      roughness: "0",
      glow: "4",
      shadow: "none",
      marker: "triangle",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "500",
      labelCase: "uppercase",
      corner: "4px"
    },
    scene: {
      materialRoughness: "0.25",
      materialMetalness: "0.4",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.45",
      lightKey: "1.2",
      lightKeyColor: "#c4f8ff",
      fog: "0.2"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#22d3ee",
        "primary-hover": "#06b6d4",
        "primary-foreground": "#042f2e",
        secondary: "#0f172a",
        "secondary-hover": "#1e293b",
        "secondary-foreground": "#e2e8f0",
        accent: "#f472b6",
        "accent-foreground": "#4a044e",
        muted: "#1e293b",
        "muted-foreground": "#67e8f9",
        background: "#020617",
        foreground: "#e2e8f0",
        card: "#0f172a",
        "card-foreground": "#e2e8f0",
        surface: "#1e293b",
        border: "rgba(34, 211, 238, 0.85)",
        input: "rgba(34, 211, 238, 0.35)",
        ring: "#22d3ee",
        error: "#ff6b6b",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#38bdf8",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#00cec2",
        "series-2": "#f472b6",
        "series-3": "#a78bfa",
        "series-4": "#fb9e21",
        "series-5": "#a8f470",
        "series-6": "#60a5fa",
        "series-7": "#e879f9",
        "series-8": "#6cf2c8",
        "diagram-ink": "#e2e8f0",
        "diagram-label": "#94a3b8",
        "diagram-guide": "#64748b",
        "diagram-axis": "#94a3b8",
        "diagram-grid": "rgba(34,211,238,0.18)",
        "diagram-highlight": "#1abbe7",
        "diagram-fill": "#0c2a3a"
      },
      surface: {
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='30'><path d='M0 .5h60M0 15.5h60M30 .5v15M0 15.5v15M60 15.5v15' stroke='%23ffffff' stroke-opacity='.06' fill='none'/></svg>")`,
        pageImageSize: "60px 30px",
        diagram: "#050a1a",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 L1440 0 V100 Z'/></svg>")`,
        edgeHeight: "clamp(24px, 6vw, 96px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='80'><path d='M0 20 C40 0 80 40 120 20 S160 0 160 20 M0 60 C40 40 80 80 120 60 S160 40 160 60' fill='none' stroke='black' stroke-width='1.5'/></svg>")`,
        textureSize: "160px 80px",
        accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
      },
      diagram: {
        strokeThin: "1",
        strokeNormal: "2",
        strokeBold: "3",
        lineCap: "round",
        lineJoin: "round",
        dash: "8 5",
        dot: "1 5",
        fillStyle: "outline",
        fillOpacity: "0.2",
        roughness: "0",
        glow: "10",
        shadow: "none",
        marker: "triangle",
        labelFont: "mono",
        labelSize: "xs",
        labelWeight: "500",
        labelCase: "uppercase",
        corner: "4px"
      },
      scene: {
        materialRoughness: "0.2",
        materialMetalness: "0.5",
        materialFlat: "0",
        materialOutline: "0",
        lightAmbient: "0.3",
        lightKey: "1.3",
        lightKeyColor: "#9defff",
        fog: "0.3"
      }
    }
  }
};

// themes/neumorphic.json
var neumorphic_default = {
  name: "neumorphic",
  displayName: "Neumorphic",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "40px",
      buttonHeightLg: "50px",
      inputHeightSm: "32px",
      inputHeightMd: "40px",
      inputHeightLg: "50px",
      rowHeightCompact: "36px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "56px",
      cardPaddingSm: "16px",
      cardPaddingMd: "20px",
      cardPaddingLg: "32px",
      dialogPadding: "32px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Nunito:wght@400;500;600;700&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "0",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.4, 0, 0.2, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.94)",
        modalExit: "scale(0.94)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.96)",
        popoverExit: "scale(0.96)",
        toastEnter: "translateY(16px)",
        toastExit: "translateY(16px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2.25",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "superellipse(1.3)",
      cornerShapePill: "superellipse(1.3)",
      borderStyle: "none",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#4353b8",
      "primary-hover": "#3746a3",
      "primary-foreground": "#ffffff",
      secondary: "#d3d9e3",
      "secondary-hover": "#c6cdda",
      "secondary-foreground": "#2d3748",
      accent: "#a8325e",
      "accent-foreground": "#ffffff",
      muted: "#d3d9e3",
      "muted-foreground": "#4a5568",
      background: "#e0e5ec",
      foreground: "#2d3748",
      card: "#e0e5ec",
      "card-foreground": "#2d3748",
      surface: "#e0e5ec",
      border: "#b9c2d0",
      input: "#c5ccd9",
      ring: "#4353b8",
      error: "#b42323",
      "error-foreground": "#ffffff",
      success: "#1c7346",
      "success-foreground": "#ffffff",
      warning: "#f0a93a",
      "warning-foreground": "#2d1b00",
      info: "#2b6cb0",
      "info-foreground": "#ffffff",
      "table-header": "#d3d9e3",
      "table-border": "#c5ccd9",
      "table-row-hover": "#d8dde7",
      "surface-hover": "#d8dde7",
      "border-hover": "#4353b8",
      placeholder: "#5f6b7e",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#7d45bc",
      "series-2": "#a8325e",
      "series-3": "#1f7a8a",
      "series-4": "#8a5a1a",
      "series-5": "#3b2e8e",
      "series-6": "#5d884b",
      "series-7": "#ae4d1c",
      "series-8": "#3e5a5e",
      "diagram-ink": "#2d3748",
      "diagram-label": "#4a5568",
      "diagram-guide": "#718096",
      "diagram-axis": "#4a5568",
      "diagram-grid": "rgba(45,55,72,0.12)",
      "diagram-highlight": "#4353b8",
      "diagram-fill": "#d3d9e3"
    },
    radii: {
      none: "0px",
      sm: "12px",
      md: "16px",
      lg: "20px",
      xl: "26px",
      full: "9999px"
    },
    shadows: {
      main: "8px 8px 16px #a3b1c6, -8px -8px 16px #ffffff",
      sm: "4px 4px 8px #a3b1c6, -4px -4px 8px #ffffff",
      lg: "14px 14px 28px #a3b1c6, -14px -14px 28px #ffffff",
      inner: "inset 4px 4px 8px #a3b1c6, inset -4px -4px 8px #ffffff",
      none: "none",
      hover: "10px 10px 20px #a3b1c6, -10px -10px 20px #ffffff",
      active: "inset 5px 5px 10px #a3b1c6, inset -5px -5px 10px #ffffff"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "500",
      "font-weight-medium": "600",
      "font-weight-bold": "800",
      "letter-spacing": "0.005em",
      "line-height": "1.6",
      "icon-color": "#b97fd8",
      "transition-fast": "120ms",
      "transition-normal": "240ms",
      "transition-slow": "280ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.03",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.97",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "rgba(185, 127, 216, 0.5)"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "2",
      strokeBold: "3",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 6",
      dot: "1 5",
      fillStyle: "tint",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "0",
      shadow: "4px 4px 8px #b9c2d0, -4px -4px 8px #ffffff",
      marker: "dot",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "12px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.75",
      lightKey: "0.7",
      lightKeyColor: "#ffffff",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#9aabff",
        "primary-hover": "#b1bfff",
        "primary-foreground": "#0f1438",
        secondary: "#323748",
        "secondary-hover": "#3b4155",
        "secondary-foreground": "#e4e8f2",
        accent: "#ff9bb9",
        "accent-foreground": "#3a0a1c",
        muted: "#323748",
        "muted-foreground": "#b0b8cf",
        background: "#2a2e3b",
        foreground: "#e4e8f2",
        card: "#2a2e3b",
        "card-foreground": "#e4e8f2",
        surface: "#2a2e3b",
        border: "#434a5e",
        input: "#3a4054",
        ring: "#9aabff",
        error: "#ff7a7a",
        "error-foreground": "#2a0808",
        success: "#5fdc98",
        "success-foreground": "#062414",
        warning: "#ffbf5e",
        "warning-foreground": "#2d1b00",
        info: "#7ab8f5",
        "info-foreground": "#071b3d",
        "table-header": "#323748",
        "table-border": "#3a4054",
        "table-row-hover": "#313647",
        "surface-hover": "#313647",
        "border-hover": "#9aabff",
        placeholder: "#8f98b2",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#638bd7",
        "series-2": "#ff8fb4",
        "series-3": "#5fd0d8",
        "series-4": "#f8a367",
        "series-5": "#65ecd6",
        "series-6": "#c89dfa",
        "series-7": "#f66c3f",
        "series-8": "#8a99a4",
        "diagram-ink": "#e4e8f2",
        "diagram-label": "#b0b8cc",
        "diagram-guide": "#8089a0",
        "diagram-axis": "#b0b8cc",
        "diagram-grid": "rgba(228,232,242,0.12)",
        "diagram-highlight": "#9aabff",
        "diagram-fill": "#353a4a"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "500",
        "font-weight-medium": "600",
        "font-weight-bold": "800",
        "letter-spacing": "0.005em",
        "line-height": "1.6",
        "icon-color": "#cf9ce8",
        "transition-fast": "120ms",
        "transition-normal": "240ms",
        "transition-slow": "280ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.03",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.97",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "rgba(207, 156, 232, 0.5)"
      },
      shadows: {
        main: "8px 8px 16px #1d2029, -8px -8px 16px #373c4d",
        sm: "4px 4px 8px #1d2029, -4px -4px 8px #373c4d",
        lg: "14px 14px 28px #1d2029, -14px -14px 28px #373c4d",
        inner: "inset 4px 4px 8px #1d2029, inset -4px -4px 8px #373c4d",
        none: "none",
        hover: "10px 10px 20px #1d2029, -10px -10px 20px #373c4d",
        active: "inset 5px 5px 10px #1d2029, inset -5px -5px 10px #373c4d"
      }
    }
  }
};

// themes/newsprint.json
var newsprint_default = {
  name: "newsprint",
  displayName: "Newsprint",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "3.5px",
        space2: "7px",
        space3: "10.5px",
        space4: "14px",
        space5: "17.5px",
        space6: "21px",
        space7: "24.5px",
        space8: "28px",
        space9: "31.5px",
        space10: "35px",
        space11: "38.5px",
        space12: "42px"
      },
      buttonHeightSm: "26px",
      buttonHeightMd: "32px",
      buttonHeightLg: "40px",
      inputHeightSm: "26px",
      inputHeightMd: "32px",
      inputHeightLg: "40px",
      rowHeightCompact: "26px",
      rowHeightNormal: "32px",
      rowHeightSpacious: "40px",
      cardPaddingSm: "8px",
      cardPaddingMd: "12px",
      cardPaddingLg: "16px",
      dialogPadding: "16px",
      sectionGap: "18px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Source+Serif+4:wght@400;600;700&family=Old+Standard+TT&display=swap",
      displayFamily: '"Playfair Display", "Old Standard TT", Georgia, serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Old Standard TT", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "900",
      headingTransform: "none",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "11px",
          lineHeight: "15px"
        },
        sm: {
          size: "13px",
          lineHeight: "19px"
        },
        base: {
          size: "15px",
          lineHeight: "23px"
        },
        lg: {
          size: "17px",
          lineHeight: "26px"
        },
        xl: {
          size: "19px",
          lineHeight: "26px"
        },
        "2xl": {
          size: "23px",
          lineHeight: "30px"
        },
        "3xl": {
          size: "28px",
          lineHeight: "34px"
        },
        "4xl": {
          size: "34px",
          lineHeight: "38px"
        },
        "display-1": {
          size: "45px",
          lineHeight: "49px"
        },
        "display-2": {
          size: "56px",
          lineHeight: "60px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "100ms",
        normal: "150ms",
        slow: "250ms",
        dramatic: "350ms"
      },
      easings: {
        linear: "linear",
        standard: "ease",
        emphasized: "ease-in-out",
        spring: "ease-out"
      },
      shapes: {
        modalEnter: "translateY(8px)",
        modalExit: "translateY(8px)",
        pageEnter: "translateY(6px)",
        popoverEnter: "translateY(4px)",
        popoverExit: "translateY(4px)",
        toastEnter: "translateY(8px)",
        toastExit: "translateY(8px)"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "double",
      borderStyleInteractive: "double",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.65' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.25 0 0 0 0 0.25 0 0 0 0 0.25 0 0 0 .07 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
      pageImageSize: "200px 200px"
    },
    colors: {
      primary: "#111111",
      "primary-hover": "#333333",
      "primary-foreground": "#f4f1ea",
      secondary: "#e6e1d3",
      "secondary-hover": "#dad4c3",
      "secondary-foreground": "#111111",
      accent: "#b3261e",
      "accent-foreground": "#ffffff",
      muted: "#ebe6d8",
      "muted-foreground": "#4a463d",
      background: "#f4f1ea",
      foreground: "#111111",
      card: "#f9f7f1",
      "card-foreground": "#111111",
      surface: "#efeadc",
      border: "#111111",
      input: "#111111",
      ring: "#b3261e",
      "table-header": "#efeadc",
      "table-border": "#111111",
      "table-row-hover": "#eee9dc",
      "surface-hover": "#e5dfcf",
      "border-hover": "#b3261e",
      placeholder: "#6a655a",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#1f6b3a",
      "success-foreground": "#ffffff",
      warning: "#f2c94c",
      "warning-foreground": "#111111",
      info: "#1f4e8c",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#1f3a5f",
      "series-2": "#883c0d",
      "series-3": "#0f4a34",
      "series-4": "#7a5a1a",
      "series-5": "#6a3a5a",
      "series-6": "#3a6a7a",
      "series-7": "#5b6460",
      "series-8": "#525393",
      "diagram-ink": "#111111",
      "diagram-label": "#4a463d",
      "diagram-guide": "#77726a",
      "diagram-axis": "#111111",
      "diagram-grid": "rgba(17,17,17,0.15)",
      "diagram-highlight": "#de5042",
      "diagram-fill": "#e6e1d3"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "none",
      main: "none",
      lg: "none",
      inner: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "1px",
      "border-width-thick": "4px",
      "font-family": '"Source Serif 4", "Libre Baskerville", Georgia, serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.3",
      "icon-color": "currentColor",
      "transition-fast": "100ms",
      "transition-normal": "150ms",
      "transition-slow": "250ms",
      "transition-timing": "ease",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#b3261e"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "1.5",
      strokeBold: "3",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "5 3",
      dot: "1 3",
      fillStyle: "hatch",
      fillOpacity: "0.25",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "triangle",
      labelFont: "display",
      labelSize: "xs",
      labelWeight: "600",
      labelCase: "uppercase",
      corner: "0px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "1",
      lightAmbient: "0.65",
      lightKey: "0.8",
      lightKeyColor: "#fff8e8",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ece7da",
        "primary-hover": "#cfc9ba",
        "primary-foreground": "#1a1915",
        secondary: "#2a2822",
        "secondary-hover": "#35322a",
        "secondary-foreground": "#ece7da",
        accent: "#ff8a80",
        "accent-foreground": "#1a1915",
        muted: "#24221d",
        "muted-foreground": "#bcb6a6",
        background: "#1a1915",
        foreground: "#ece7da",
        card: "#201e19",
        "card-foreground": "#ece7da",
        surface: "#1d1b17",
        border: "#ece7da",
        input: "#ece7da",
        ring: "#ff8a80",
        "table-header": "#1d1b17",
        "table-border": "#ece7da",
        "table-row-hover": "#23211c",
        "surface-hover": "#2b2923",
        "border-hover": "#ff8a80",
        placeholder: "#948e7f",
        error: "#ff8a80",
        "error-foreground": "#1a1915",
        success: "#7fd69a",
        "success-foreground": "#1a1915",
        warning: "#f2c94c",
        "warning-foreground": "#1a1915",
        info: "#8fb4f0",
        "info-foreground": "#1a1915",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#7fa0d0",
        "series-2": "#d08a6a",
        "series-3": "#76a781",
        "series-4": "#c1a157",
        "series-5": "#b58ab0",
        "series-6": "#6fb0c0",
        "series-7": "#8f9489",
        "series-8": "#af9de2",
        "diagram-ink": "#ece7da",
        "diagram-label": "#bdb8a8",
        "diagram-guide": "#8a8678",
        "diagram-axis": "#ece7da",
        "diagram-grid": "rgba(236,231,218,0.15)",
        "diagram-highlight": "#d66157",
        "diagram-fill": "#2e2b24"
      },
      shadows: {
        none: "none",
        sm: "none",
        main: "none",
        lg: "none",
        inner: "none",
        hover: "none",
        active: "none"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "1px",
        "border-width-thick": "4px",
        "font-family": '"Source Serif 4", "Libre Baskerville", Georgia, serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.3",
        "icon-color": "currentColor",
        "transition-fast": "100ms",
        "transition-normal": "150ms",
        "transition-slow": "250ms",
        "transition-timing": "ease",
        "hover-scale": "1",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ff8a80"
      },
      surface: {
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.65' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 .07 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
        pageImageSize: "200px 200px"
      }
    }
  }
};

// themes/notion-editorial.json
var notion_editorial_default = {
  name: "notion-editorial",
  displayName: "Notion Editorial",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "16px",
        space4: "24px",
        space5: "32px",
        space6: "40px",
        space7: "48px",
        space8: "56px",
        space9: "64px",
        space10: "72px",
        space11: "80px",
        space12: "96px"
      },
      buttonHeightSm: "36px",
      buttonHeightMd: "44px",
      buttonHeightLg: "52px",
      inputHeightSm: "36px",
      inputHeightMd: "44px",
      inputHeightLg: "52px",
      rowHeightCompact: "48px",
      rowHeightNormal: "56px",
      rowHeightSpacious: "72px",
      cardPaddingSm: "24px",
      cardPaddingMd: "32px",
      cardPaddingLg: "48px",
      dialogPadding: "48px",
      sectionGap: "80px"
    },
    typeScale: {
      displayFamily: '"Lora", Georgia, "Times New Roman", serif',
      bodyFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "13px",
          lineHeight: "22px"
        },
        sm: {
          size: "15px",
          lineHeight: "26px"
        },
        base: {
          size: "17px",
          lineHeight: "30px"
        },
        lg: {
          size: "21px",
          lineHeight: "34px"
        },
        xl: {
          size: "26px",
          lineHeight: "38px"
        },
        "2xl": {
          size: "32px",
          lineHeight: "44px"
        },
        "3xl": {
          size: "42px",
          lineHeight: "52px"
        },
        "4xl": {
          size: "56px",
          lineHeight: "64px"
        },
        "display-1": {
          size: "72px",
          lineHeight: "80px"
        },
        "display-2": {
          size: "96px",
          lineHeight: "104px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "3xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "xl",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "base",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "medium"
        },
        numeric: {
          slot: "mono",
          size: "4xl",
          weight: "bold"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "200ms",
        normal: "350ms",
        slow: "500ms",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.0, 0.0, 0.2, 1)",
        emphasized: "cubic-bezier(0.2, 0.0, 0.0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "translateY(6px) scale(0.98)",
        modalExit: "translateY(6px) scale(0.98)",
        pageEnter: "translateY(4px)",
        popoverEnter: "scale(0.98)",
        popoverExit: "scale(0.98)",
        toastEnter: "translateY(8px)",
        toastExit: "translateY(8px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "20px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "4px",
      radiusInteractive: "4px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "1px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "none",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#1f6fc5",
      "primary-hover": "#195ca4",
      "primary-foreground": "#fefdf9",
      secondary: "#f4f1ea",
      "secondary-hover": "#ebe7dc",
      "secondary-foreground": "#191919",
      accent: "#b5462a",
      "accent-foreground": "#fefdf9",
      muted: "#f4f1ea",
      "muted-foreground": "#6b6660",
      background: "#fefdf9",
      foreground: "#191919",
      card: "#ffffff",
      "card-foreground": "#191919",
      surface: "#f9f6ef",
      border: "#e8e3d6",
      input: "#d8d2c2",
      ring: "#1f6fc5",
      "table-header": "#f4f1ea",
      "table-border": "#e8e3d6",
      "table-row-hover": "#f9f6ef",
      "surface-hover": "#ebe7dc",
      "border-hover": "#c8c1ad",
      placeholder: "#a39c8f",
      error: "#b42318",
      "error-foreground": "#fefdf9",
      success: "#067647",
      "success-foreground": "#fefdf9",
      warning: "#b54708",
      "warning-foreground": "#fefdf9",
      info: "#1849a9",
      "info-foreground": "#fefdf9",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#6366de",
      "series-2": "#ca5749",
      "series-3": "#017b72",
      "series-4": "#9a6700",
      "series-5": "#6940a5",
      "series-6": "#ad1a72",
      "series-7": "#64473a",
      "series-8": "#464a4c",
      "diagram-ink": "#191919",
      "diagram-label": "#6b6660",
      "diagram-guide": "#8f8a82",
      "diagram-axis": "#6b6660",
      "diagram-grid": "#e8e3d6",
      "diagram-highlight": "#1f6fc5",
      "diagram-fill": "#f4f1ea"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "4px",
      lg: "4px",
      xl: "6px",
      full: "9999px"
    },
    shadows: {
      main: "0 1px 2px rgba(25, 25, 25, 0.06)",
      sm: "0 1px 1px rgba(25, 25, 25, 0.05)",
      lg: "0 8px 20px rgba(25, 25, 25, 0.12)",
      inner: "inset 0 1px 1px rgba(25, 25, 25, 0.05)",
      none: "none",
      hover: "0 2px 6px rgba(25, 25, 25, 0.08)",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "1px",
      "font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "-0.005em",
      "line-height": "1.7",
      "intent-numeric-family": "var(--font-family-display)",
      "icon-color": "currentColor",
      "transition-fast": "200ms",
      "transition-normal": "350ms",
      "transition-slow": "500ms",
      "transition-timing": "cubic-bezier(0.0, 0.0, 0.2, 1)",
      "hover-scale": "1.02",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0",
      "active-scale": "0.98",
      "active-translate-y": "0",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#1f6fc5"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "1.5",
      strokeBold: "2.5",
      lineCap: "round",
      lineJoin: "round",
      dash: "4 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.14",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "400",
      labelCase: "none",
      corner: "3px"
    },
    scene: {
      materialRoughness: "0.75",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "0.85",
      lightKeyColor: "#fffaf0",
      fog: "0"
    },
    surface: {
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><path d='M0 31.5H32' stroke='black' stroke-width='1'/></svg>")`,
      textureSize: "32px 32px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#529cca",
        "primary-hover": "#6aaedb",
        "primary-foreground": "#1a1a17",
        secondary: "#26241f",
        "secondary-hover": "#2f2d27",
        "secondary-foreground": "#f5f1e6",
        accent: "#e07a5f",
        "accent-foreground": "#1a1a17",
        muted: "#26241f",
        "muted-foreground": "#a39c8f",
        background: "#1a1a17",
        foreground: "#f5f1e6",
        card: "#22211d",
        "card-foreground": "#f5f1e6",
        surface: "#26241f",
        border: "#3a3730",
        input: "#45413a",
        ring: "#529cca",
        "table-header": "#26241f",
        "table-border": "#3a3730",
        "table-row-hover": "#201f1b",
        "surface-hover": "#2f2d27",
        "border-hover": "#524d44",
        placeholder: "#807a6e",
        error: "#f97066",
        "error-foreground": "#1a1a17",
        success: "#75e0a7",
        "success-foreground": "#1a1a17",
        warning: "#fdb022",
        "warning-foreground": "#1a1a17",
        info: "#84caff",
        "info-foreground": "#1a1a17",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#7088c8",
        "series-2": "#de8251",
        "series-3": "#44b69f",
        "series-4": "#cdb34b",
        "series-5": "#a58ad6",
        "series-6": "#e07ab0",
        "series-7": "#c0a199",
        "series-8": "#b5beba",
        "diagram-ink": "#f5f1e6",
        "diagram-label": "#b5b0a4",
        "diagram-guide": "#878278",
        "diagram-axis": "#b5b0a4",
        "diagram-grid": "#3a3832",
        "diagram-highlight": "#529cca",
        "diagram-fill": "#2e2d28"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "1px",
        "font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "-0.005em",
        "line-height": "1.7",
        "intent-numeric-family": "var(--font-family-display)",
        "icon-color": "currentColor",
        "transition-fast": "200ms",
        "transition-normal": "350ms",
        "transition-slow": "500ms",
        "transition-timing": "cubic-bezier(0.0, 0.0, 0.2, 1)",
        "hover-scale": "1.02",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0",
        "active-scale": "0.98",
        "active-translate-y": "0",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#529cca"
      },
      shadows: {
        main: "0 1px 2px rgba(0, 0, 0, 0.30)",
        sm: "0 1px 1px rgba(0, 0, 0, 0.25)",
        lg: "0 8px 20px rgba(0, 0, 0, 0.45)",
        inner: "inset 0 1px 1px rgba(0, 0, 0, 0.25)",
        none: "none",
        hover: "0 2px 6px rgba(0, 0, 0, 0.35)",
        active: "none"
      }
    }
  }
};

// themes/orb.json
var orb_default = {
  name: "orb",
  displayName: "Orb",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"IBM Plex Sans", "IBM Plex Sans Arabic", system-ui, sans-serif',
      bodyFamily: '"IBM Plex Sans", "IBM Plex Sans Arabic", system-ui, sans-serif',
      monoFamily: '"IBM Plex Mono", "IBM Plex Sans Arabic", ui-monospace, SFMono-Regular, Menlo, monospace',
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.015em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        spring: "cubic-bezier(0.25, 0.8, 0.25, 1)"
      },
      shapes: {
        modalEnter: "scale(0.96)",
        modalExit: "scale(0.96)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.97)",
        popoverExit: "scale(0.97)",
        toastEnter: "translateY(-12px)",
        toastExit: "translateY(-12px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "none",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: "repeating-radial-gradient(circle at 92% -8%, transparent 0 95px, rgba(20,22,26,0.05) 96px 97px)",
      pageImageSize: "auto",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "2px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    colors: {
      primary: "#14161a",
      "primary-hover": "#2a2d33",
      "primary-foreground": "#ffffff",
      secondary: "#efede7",
      "secondary-hover": "#e6e3db",
      "secondary-foreground": "#14161a",
      accent: "#00767c",
      "accent-foreground": "#ffffff",
      tertiary: "#00b1b8",
      "tertiary-hover": "#0096a0",
      "tertiary-foreground": "#14161a",
      "tertiary-muted": "rgba(0, 177, 184, 0.14)",
      muted: "#f0eee8",
      "muted-foreground": "#4a4d55",
      background: "#f7f6f2",
      foreground: "#14161a",
      card: "#fffefb",
      "card-foreground": "#14161a",
      surface: "#f1efe9",
      border: "#e2dfd6",
      input: "#d6d2c7",
      ring: "#00b1b8",
      error: "#c0392b",
      "error-foreground": "#ffffff",
      success: "#1e7a4c",
      "success-foreground": "#ffffff",
      warning: "#a35a0f",
      "warning-foreground": "#ffffff",
      info: "#00767c",
      "info-foreground": "#ffffff",
      scrim: "rgba(20, 22, 43, 0.5)",
      "series-1": "#267090",
      "series-2": "#2f4a8a",
      "series-3": "#9f761c",
      "series-4": "#7a3d78",
      "series-5": "#627087",
      "series-6": "#8e3809",
      "series-7": "#5f7a2a",
      "series-8": "#5f6967",
      "diagram-ink": "#14161a",
      "diagram-label": "#4a4d55",
      "diagram-guide": "#8a8d94",
      "diagram-axis": "#3a3d44",
      "diagram-grid": "#e2dfd6",
      "diagram-highlight": "#00767c",
      "diagram-fill": "#efede7"
    },
    shadows: {
      main: "0 1px 2px rgba(20, 22, 43, 0.05), 0 4px 16px rgba(79, 70, 229, 0.06)",
      sm: "0 1px 2px rgba(20, 22, 43, 0.06)",
      lg: "0 18px 48px rgba(20, 22, 43, 0.12)",
      inner: "inset 0 1px 3px rgba(31,63,143,0.12)",
      none: "none",
      hover: "0 10px 30px rgba(79, 70, 229, 0.12)",
      active: "0 1px 2px rgba(31,63,143,0.15)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Cairo", "Noto Naskh Arabic", "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#14161a",
      "transition-fast": "140ms",
      "transition-normal": "220ms",
      "transition-slow": "360ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#00b1b8"
    },
    diagram: {
      strokeThin: "1",
      strokeNormal: "1.5",
      strokeBold: "2.5",
      lineCap: "round",
      lineJoin: "round",
      dash: "5 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.14",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "xs",
      labelWeight: "500",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.55",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.6",
      lightKey: "0.95",
      lightKeyColor: "#fff8ee",
      fog: "0.05"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Geist", "IBM Plex Sans Arabic", system-ui, sans-serif',
        bodyFamily: '"Geist", "IBM Plex Sans Arabic", system-ui, sans-serif',
        monoFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
        headingWeight: "600",
        headingTransform: "none",
        headingTracking: "-0.025em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500;600&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "700ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.22, 0.61, 0.36, 1)",
          spring: "cubic-bezier(0.25, 0.8, 0.25, 1)"
        },
        shapes: {
          modalEnter: "scale(0.96)",
          modalExit: "scale(0.96)",
          pageEnter: "translateY(8px)",
          popoverEnter: "scale(0.97)",
          popoverExit: "scale(0.97)",
          toastEnter: "translateY(-12px)",
          toastExit: "translateY(-12px)"
        }
      },
      iconography: {
        family: "phosphor-duotone",
        strokeWidth: "1.5",
        defaultSize: "18px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: "repeating-radial-gradient(circle at 92% -8%, transparent 0 95px, rgba(236,235,230,0.05) 96px 97px)",
        pageImageSize: "auto",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
        edgeHeight: "clamp(16px, 4vw, 56px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
        textureSize: "24px 24px",
        accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
      },
      radii: {
        none: "0px",
        sm: "6px",
        md: "8px",
        lg: "12px",
        xl: "16px",
        full: "9999px"
      },
      colors: {
        primary: "#ecebe6",
        "primary-hover": "#ffffff",
        "primary-foreground": "#0e1013",
        secondary: "#181b20",
        "secondary-hover": "#20242a",
        "secondary-foreground": "#ecebe6",
        accent: "#2cc7cd",
        "accent-foreground": "#0e1013",
        tertiary: "#2cc7cd",
        "tertiary-hover": "#4fd4d9",
        "tertiary-foreground": "#0e1013",
        "tertiary-muted": "rgba(44, 199, 205, 0.14)",
        muted: "#14171b",
        "muted-foreground": "#9aa0a8",
        background: "#0e1013",
        foreground: "#ecebe6",
        card: "#15181c",
        "card-foreground": "#ecebe6",
        surface: "#15181c",
        border: "#262a31",
        input: "#30353d",
        ring: "#2cc7cd",
        error: "#f2877a",
        "error-foreground": "#000000",
        success: "#5fd39a",
        "success-foreground": "#000000",
        warning: "#f0b46a",
        "warning-foreground": "#000000",
        info: "#2cc7cd",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#85f6f2",
        "series-2": "#7f9ad8",
        "series-3": "#caa021",
        "series-4": "#c08ac0",
        "series-5": "#7aabbf",
        "series-6": "#bf7051",
        "series-7": "#a0bc6a",
        "series-8": "#b0b0da",
        "diagram-ink": "#ecebe6",
        "diagram-label": "#b0b3ba",
        "diagram-guide": "#82858c",
        "diagram-axis": "#b0b3ba",
        "diagram-grid": "rgba(236,235,230,0.10)",
        "diagram-highlight": "#4fc4c8",
        "diagram-fill": "#1f2328"
      },
      shadows: {
        main: "0 1px 2px rgba(0, 0, 0, 0.5), 0 6px 20px rgba(139, 140, 247, 0.08)",
        sm: "0 1px 2px rgba(0, 0, 0, 0.45)",
        lg: "0 18px 48px rgba(0, 0, 0, 0.6)",
        inner: "inset 0 1px 3px rgba(0,0,0,0.4)",
        none: "none",
        hover: "0 10px 30px rgba(139, 140, 247, 0.16)",
        active: "0 1px 2px rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Cairo", "Noto Naskh Arabic", "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#ecebe6",
        "transition-fast": "140ms",
        "transition-normal": "220ms",
        "transition-slow": "360ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#2cc7cd"
      }
    }
  }
};

// themes/pixel.json
var pixel_default = {
  name: "pixel",
  displayName: "Pixel Quest",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "3px",
        space2: "7px",
        space3: "10px",
        space4: "14px",
        space5: "17px",
        space6: "20px",
        space7: "24px",
        space8: "27px",
        space9: "31px",
        space10: "34px",
        space11: "37px",
        space12: "41px"
      },
      buttonHeightSm: "26px",
      buttonHeightMd: "34px",
      buttonHeightLg: "42px",
      inputHeightSm: "26px",
      inputHeightMd: "34px",
      inputHeightLg: "42px",
      rowHeightCompact: "30px",
      rowHeightNormal: "36px",
      rowHeightSpacious: "44px",
      cardPaddingSm: "10px",
      cardPaddingMd: "14px",
      cardPaddingLg: "20px",
      dialogPadding: "20px",
      sectionGap: "28px"
    },
    typeScale: {
      displayFamily: '"Press Start 2P", "Silkscreen", monospace',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Pixelify Sans", "Silkscreen", monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Pixelify+Sans:wght@400;500;700&display=swap",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.02em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "11px",
          lineHeight: "16px"
        },
        sm: {
          size: "13px",
          lineHeight: "18px"
        },
        base: {
          size: "15px",
          lineHeight: "22px"
        },
        lg: {
          size: "14px",
          lineHeight: "22px"
        },
        xl: {
          size: "16px",
          lineHeight: "24px"
        },
        "2xl": {
          size: "18px",
          lineHeight: "28px"
        },
        "3xl": {
          size: "22px",
          lineHeight: "32px"
        },
        "4xl": {
          size: "26px",
          lineHeight: "36px"
        },
        "display-1": {
          size: "32px",
          lineHeight: "40px"
        },
        "display-2": {
          size: "40px",
          lineHeight: "48px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "xl",
          weight: "normal"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "normal"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "80ms",
        normal: "160ms",
        slow: "280ms",
        dramatic: "400ms"
      },
      easings: {
        linear: "steps(4, end)",
        standard: "steps(4, end)",
        emphasized: "steps(4, end)",
        spring: "steps(4, end)"
      },
      shapes: {
        modalEnter: "scale(0.6)",
        modalExit: "scale(0.6)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.8)",
        popoverExit: "scale(0.8)",
        toastEnter: "translateY(16px)",
        toastExit: "translateY(16px)"
      }
    },
    iconography: {
      family: "fa-solid",
      strokeWidth: "0",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "4px",
      radiusInteractive: "4px",
      radiusPill: "4px",
      cornerShape: "notch",
      cornerShapePill: "notch",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "2px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    colors: {
      primary: "#d62828",
      "primary-hover": "#b51e1e",
      "primary-foreground": "#ffffff",
      secondary: "#ffd23f",
      "secondary-hover": "#ffc107",
      "secondary-foreground": "#1a1a2e",
      accent: "#2a5bd6",
      "accent-foreground": "#ffffff",
      muted: "#e8deb0",
      "muted-foreground": "#4a4030",
      background: "#f8f0d0",
      foreground: "#1a1a2e",
      card: "#ffffff",
      "card-foreground": "#1a1a2e",
      surface: "#fff8dc",
      border: "#000000",
      input: "#000000",
      ring: "#2a5bd6",
      "table-header": "#fff8dc",
      "table-border": "#000000",
      "table-row-hover": "#f0e4b0",
      "surface-hover": "#f0e4b0",
      "border-hover": "#d62828",
      placeholder: "#6a6048",
      error: "#b71c1c",
      "error-foreground": "#ffffff",
      success: "#1a7f37",
      "success-foreground": "#ffffff",
      warning: "#ff9f1c",
      "warning-foreground": "#1a1000",
      info: "#2a5bd6",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#f64f2f",
      "series-2": "#2a5bd6",
      "series-3": "#b86e00",
      "series-4": "#6a2fb5",
      "series-5": "#0a8a8a",
      "series-6": "#8a5a2b",
      "series-7": "#c2185b",
      "series-8": "#678a0b",
      "diagram-ink": "#1a1a2e",
      "diagram-label": "#1a1a2e",
      "diagram-guide": "#6a6048",
      "diagram-axis": "#000000",
      "diagram-grid": "#e8deb0",
      "diagram-highlight": "#e13245",
      "diagram-fill": "#ffd23f"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "4px",
      lg: "4px",
      xl: "4px",
      full: "4px"
    },
    shadows: {
      main: "4px 4px 0 0 #000000",
      sm: "2px 2px 0 0 #000000",
      lg: "6px 6px 0 0 #000000",
      inner: "inset 2px 2px 0 0 rgba(0,0,0,.25)",
      none: "none",
      hover: "6px 6px 0 0 #000000",
      active: "0 0 0 0 #000000"
    },
    typography: {
      "border-width": "4px",
      "border-width-thin": "2px",
      "border-width-thick": "6px",
      "font-family": '"Pixelify Sans", "Silkscreen", monospace',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.4",
      "icon-color": "#d62828",
      "transition-fast": "80ms",
      "transition-normal": "160ms",
      "transition-slow": "280ms",
      "transition-timing": "steps(4, end)",
      "hover-scale": "1",
      "hover-translate-y": "-2px",
      "hover-translate-x": "-2px",
      "active-scale": "1",
      "active-translate-y": "2px",
      "focus-ring-width": "4px",
      "focus-ring-offset": "0px",
      "focus-ring-color": "#2a5bd6"
    },
    diagram: {
      strokeThin: "2px",
      strokeNormal: "4px",
      strokeBold: "6px",
      lineCap: "square",
      lineJoin: "miter",
      dash: "8 8",
      dot: "4 4",
      fillStyle: "solid",
      fillOpacity: "0.35",
      roughness: "0",
      glow: "0",
      shadow: "4px 4px 0 0 #000000",
      marker: "triangle",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "0"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.7",
      lightKey: "0.9",
      lightKeyColor: "#fff8dc",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff5a5a",
        "primary-hover": "#ff7a7a",
        "primary-foreground": "#1a0000",
        secondary: "#ffd23f",
        "secondary-hover": "#ffe066",
        "secondary-foreground": "#1a1a2e",
        accent: "#5aa0ff",
        "accent-foreground": "#00122e",
        muted: "#33336a",
        "muted-foreground": "#d8d8f0",
        background: "#22224a",
        foreground: "#f8f0d0",
        card: "#2e2e5e",
        "card-foreground": "#f8f0d0",
        surface: "#28285a",
        border: "#f8f0d0",
        input: "#f8f0d0",
        ring: "#ffd23f",
        "table-header": "#28285a",
        "table-border": "#f8f0d0",
        "table-row-hover": "#3a3a72",
        "surface-hover": "#3a3a72",
        "border-hover": "#ff5a5a",
        placeholder: "#a0a0d0",
        error: "#ff6b6b",
        "error-foreground": "#1a0000",
        success: "#4ddb7a",
        "success-foreground": "#02200c",
        warning: "#ffb03a",
        "warning-foreground": "#1a1000",
        info: "#5aa0ff",
        "info-foreground": "#00122e",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#fe6034",
        "series-2": "#5aa0ff",
        "series-3": "#f5f44c",
        "series-4": "#c792ff",
        "series-5": "#3fe0d0",
        "series-6": "#ea8734",
        "series-7": "#ff7ac6",
        "series-8": "#9be04a",
        "diagram-ink": "#f8f0d0",
        "diagram-label": "#f8f0d0",
        "diagram-guide": "#a0a0d0",
        "diagram-axis": "#f8f0d0",
        "diagram-grid": "#44447a",
        "diagram-highlight": "#ffd23f",
        "diagram-fill": "#5a5aa0"
      },
      shadows: {
        main: "4px 4px 0 0 #000000",
        sm: "2px 2px 0 0 #000000",
        lg: "6px 6px 0 0 #000000",
        inner: "inset 2px 2px 0 0 rgba(0,0,0,.25)",
        none: "none",
        hover: "6px 6px 0 0 #000000",
        active: "0 0 0 0 #000000"
      },
      typography: {
        "border-width": "4px",
        "border-width-thin": "2px",
        "border-width-thick": "6px",
        "font-family": '"Pixelify Sans", "Silkscreen", monospace',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.4",
        "icon-color": "#ffd23f",
        "transition-fast": "80ms",
        "transition-normal": "160ms",
        "transition-slow": "280ms",
        "transition-timing": "steps(4, end)",
        "hover-scale": "1",
        "hover-translate-y": "-2px",
        "hover-translate-x": "-2px",
        "active-scale": "1",
        "active-translate-y": "2px",
        "focus-ring-width": "4px",
        "focus-ring-offset": "0px",
        "focus-ring-color": "#ffd23f"
      }
    }
  }
};

// themes/prism.json
var prism_default = {
  name: "prism",
  displayName: "Prism",
  tokens: {
    motion: {
      durations: {
        instant: "0ms",
        fast: "80ms",
        normal: "120ms",
        slow: "180ms",
        dramatic: "240ms"
      },
      easings: {
        linear: "linear",
        standard: "linear",
        emphasized: "linear",
        spring: "linear"
      },
      shapes: {
        modalEnter: "scale(0.95)",
        modalExit: "scale(0.95)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.96)",
        popoverExit: "scale(0.96)",
        toastEnter: "translateY(12px)",
        toastExit: "translateY(12px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.5",
      defaultSize: "14px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-inner)"
    },
    geometry: {
      radiusContainer: "10px",
      radiusInteractive: "10px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "1px",
      borderHeavy: "2px",
      cornerShape: "superellipse(1.5)",
      cornerShapePill: "superellipse(1.5)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#6d3fd6",
      "primary-hover": "#5b31bd",
      "primary-foreground": "#ffffff",
      secondary: "#f1f5f9",
      "secondary-hover": "#e2e8f0",
      "secondary-foreground": "#0f172a",
      accent: "#c2410c",
      "accent-foreground": "#ffffff",
      muted: "#f1f5f9",
      "muted-foreground": "#59677c",
      background: "#fafafa",
      foreground: "#0f172a",
      card: "#ffffff",
      "card-foreground": "#0f172a",
      surface: "#f5f5f7",
      border: "#e2e8f0",
      input: "#cbd5e1",
      ring: "#6d3fd6",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#b45309",
      "warning-foreground": "#ffffff",
      info: "#2563eb",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#ae3cc4",
      "series-2": "#a41c00",
      "series-3": "#be185d",
      "series-4": "#0e7490",
      "series-5": "#618305",
      "series-6": "#2d62ed",
      "series-7": "#a66e0e",
      "series-8": "#2708bb",
      "diagram-ink": "#0f172a",
      "diagram-label": "#475569",
      "diagram-guide": "#94a3b8",
      "diagram-axis": "#334155",
      "diagram-grid": "#e2e8f0",
      "diagram-highlight": "#6d3fd6",
      "diagram-fill": "#ede9fe"
    },
    radii: {
      none: "0px",
      sm: "6px",
      md: "10px",
      lg: "14px",
      xl: "18px",
      full: "9999px"
    },
    shadows: {
      main: "0 6px 20px -4px rgba(109, 63, 214, 0.22), 0 2px 6px rgba(194, 65, 12, 0.10)",
      sm: "0 3px 10px -2px rgba(109, 63, 214, 0.18)",
      lg: "0 16px 40px -8px rgba(109, 63, 214, 0.30), 0 4px 12px rgba(194, 65, 12, 0.12)",
      inner: "inset 0 1px 2px rgba(109, 63, 214, 0.10)",
      none: "none",
      hover: "0 10px 28px -6px rgba(109, 63, 214, 0.34), 0 3px 8px rgba(194, 65, 12, 0.14)",
      active: "0 2px 6px -2px rgba(109, 63, 214, 0.25)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "icon-color": "currentColor",
      "transition-fast": "80ms",
      "transition-normal": "120ms",
      "transition-slow": "180ms",
      "transition-timing": "linear",
      "hover-scale": "1.0",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#6d3fd6",
      "font-family": '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "letter-spacing": "-0.01em",
      "line-height": "1.55"
    },
    typeScale: {
      displayFamily: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      bodyFamily: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap",
      headingWeight: "800",
      headingTransform: "none",
      headingTracking: "-0.035em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    surface: {
      cardImage: "linear-gradient(135deg, rgba(109, 63, 214, 0.08) 0%, rgba(236, 72, 153, 0.05) 50%, rgba(194, 65, 12, 0.06) 100%)",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3px",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 4",
      dot: "1 5",
      fillStyle: "tint",
      fillOpacity: "0.22",
      roughness: "0",
      glow: "2",
      shadow: "0 4px 12px rgba(109, 63, 214, 0.25)",
      marker: "triangle",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "8px"
    },
    scene: {
      materialRoughness: "0.25",
      materialMetalness: "0.15",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.5",
      lightKey: "1.15",
      lightKeyColor: "#f5e9ff",
      fog: "0.05"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#a78bfa",
        "primary-hover": "#c4b5fd",
        "primary-foreground": "#1e1b4b",
        secondary: "#1f1d3a",
        "secondary-hover": "#2a2748",
        "secondary-foreground": "#e2e8f0",
        accent: "#fb923c",
        "accent-foreground": "#1a0e05",
        muted: "#1f1d3a",
        "muted-foreground": "#94a3b8",
        background: "#0c0a1a",
        foreground: "#e2e8f0",
        card: "#14122a",
        "card-foreground": "#e2e8f0",
        surface: "#1f1d3a",
        border: "#2a2748",
        input: "#3a3658",
        ring: "#a78bfa",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#60a5fa",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#6987ef",
        "series-2": "#fb923c",
        "series-3": "#f472b6",
        "series-4": "#22d3ee",
        "series-5": "#a3e635",
        "series-6": "#51a8f8",
        "series-7": "#e6d50d",
        "series-8": "#e879f9",
        "diagram-ink": "#e2e8f0",
        "diagram-label": "#94a3b8",
        "diagram-guide": "#64748b",
        "diagram-axis": "#cbd5e1",
        "diagram-grid": "#2a2748",
        "diagram-highlight": "#a78bfa",
        "diagram-fill": "#2e2a5a"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "icon-color": "currentColor",
        "transition-fast": "80ms",
        "transition-normal": "120ms",
        "transition-slow": "180ms",
        "transition-timing": "linear",
        "hover-scale": "1.0",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#a78bfa",
        "font-family": '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "letter-spacing": "-0.01em",
        "line-height": "1.55"
      },
      shadows: {
        main: "0 6px 20px -4px rgba(167, 139, 250, 0.35), 0 2px 6px rgba(251, 146, 60, 0.10)",
        sm: "0 3px 10px -2px rgba(167, 139, 250, 0.30)",
        lg: "0 16px 40px -8px rgba(167, 139, 250, 0.45), 0 4px 12px rgba(251, 146, 60, 0.12)",
        inner: "inset 0 1px 2px rgba(0, 0, 0, 0.35)",
        none: "none",
        hover: "0 10px 28px -6px rgba(167, 139, 250, 0.50), 0 3px 8px rgba(251, 146, 60, 0.14)",
        active: "0 2px 6px -2px rgba(167, 139, 250, 0.40)"
      },
      surface: {
        cardImage: "linear-gradient(135deg, rgba(167, 139, 250, 0.14) 0%, rgba(236, 72, 153, 0.08) 50%, rgba(190, 242, 100, 0.10) 100%)",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
        edgeHeight: "clamp(24px, 6vw, 96px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
        textureSize: "24px 24px",
        accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
      }
    }
  }
};

// themes/retro.json
var retro_default = {
  name: "retro",
  displayName: "Retro",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "38px",
      buttonHeightLg: "46px",
      inputHeightSm: "30px",
      inputHeightMd: "38px",
      inputHeightLg: "46px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Shrikhand", Georgia, "Times New Roman", serif',
      bodyFamily: '"Karla", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Shrikhand&family=Karla:wght@400;500;700&family=IBM+Plex+Mono:wght@400;500&display=swap",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "0.01em",
      headingStyle: "normal"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "500ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.34, 1.56, 0.64, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "scale(0.85)",
        modalExit: "scale(0.85)",
        pageEnter: "translateY(12px)",
        popoverEnter: "scale(0.9)",
        popoverExit: "scale(0.9)",
        toastEnter: "translateY(20px)",
        toastExit: "translateY(20px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#d12368",
      "primary-hover": "#b81a57",
      "primary-foreground": "#ffffff",
      secondary: "#e9d5ff",
      "secondary-hover": "#d8b4fe",
      "secondary-foreground": "#3b1a63",
      accent: "#00798c",
      "accent-foreground": "#ffffff",
      muted: "#e9d5ff",
      "muted-foreground": "#6b4a9e",
      background: "#f3e8ff",
      foreground: "#2a1650",
      card: "#ffffff",
      "card-foreground": "#2a1650",
      surface: "#e9d5ff",
      border: "rgba(196, 36, 107, 0.85)",
      input: "rgba(0, 152, 170, 0.35)",
      ring: "#0098aa",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#d97706",
      "warning-foreground": "#000000",
      info: "#0e7490",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#ff4fa3",
      "series-2": "#3ac9d8",
      "series-3": "#f5f44c",
      "series-4": "#b388ff",
      "series-5": "#9be564",
      "series-6": "#f77d4d",
      "series-7": "#5aa0ff",
      "series-8": "#ff7aef",
      "diagram-ink": "#efe3ff",
      "diagram-label": "#cdb8f5",
      "diagram-guide": "#9d86d6",
      "diagram-axis": "#efe3ff",
      "diagram-grid": "#3a2160",
      "diagram-highlight": "#ffd23f",
      "diagram-fill": "#3a2160"
    },
    radii: {
      none: "0px",
      sm: "10px",
      md: "18px",
      lg: "26px",
      xl: "34px",
      full: "9999px"
    },
    shadows: {
      main: "5px 5px 0 0 #d12368",
      sm: "3px 3px 0 0 #d12368",
      lg: "8px 8px 0 0 #d12368",
      inner: "inset 3px 3px 0 0 rgba(109,40,217,0.15)",
      none: "none",
      hover: "8px 8px 0 0 #00798c",
      active: "1px 1px 0 0 #d12368"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "2px",
      "border-width-thick": "4px",
      "font-family": '"Karla", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.5",
      "icon-color": "#d12368",
      "transition-fast": "120ms",
      "transition-normal": "220ms",
      "transition-slow": "120ms",
      "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "2px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#0098aa"
    },
    surface: {
      pageImage: "repeating-conic-gradient(from 0deg at 50% 100%, rgba(209,35,104,0.08) 0deg 9deg, transparent 9deg 18deg)",
      pageImageSize: "100% 100%",
      diagram: "#1a0b2e",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 L1440 0 V100 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='80'><path d='M0 20 C40 0 80 40 120 20 S160 0 160 20 M0 60 C40 40 80 80 120 60 S160 40 160 60' fill='none' stroke='black' stroke-width='1.5'/></svg>")`,
      textureSize: "160px 80px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "4px",
      lineCap: "round",
      lineJoin: "round",
      dash: "8 4",
      dot: "2 4",
      fillStyle: "tint",
      fillOpacity: "0.25",
      roughness: "0",
      glow: "3",
      shadow: "3px 3px 0 #d12368",
      marker: "triangle",
      labelFont: "mono",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "uppercase",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.35",
      materialMetalness: "0.4",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.45",
      lightKey: "1.1",
      lightKeyColor: "#ff9ad0",
      fog: "0.25"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#e04c8a",
        "primary-hover": "#ec6a9f",
        "primary-foreground": "#2b0216",
        secondary: "#2d1b4e",
        "secondary-hover": "#3b2566",
        "secondary-foreground": "#efe3ff",
        accent: "#3ac9d8",
        "accent-foreground": "#00252b",
        muted: "#2d1b4e",
        "muted-foreground": "#9d86d6",
        background: "#1a0b2e",
        foreground: "#efe3ff",
        card: "#241242",
        "card-foreground": "#efe3ff",
        surface: "#2d1b4e",
        border: "rgba(224, 76, 138, 0.85)",
        input: "rgba(58, 201, 216, 0.34)",
        ring: "#3ac9d8",
        error: "#ff5252",
        "error-foreground": "#1a0000",
        success: "#2ee66e",
        "success-foreground": "#00290f",
        warning: "#ffb300",
        "warning-foreground": "#2a1a00",
        info: "#3ac9d8",
        "info-foreground": "#00252b",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ff4fa3",
        "series-2": "#3ac9d8",
        "series-3": "#b8c534",
        "series-4": "#e291ff",
        "series-5": "#bef66a",
        "series-6": "#ff8a4a",
        "series-7": "#5aa0ff",
        "series-8": "#d552c7",
        "diagram-ink": "#efe3ff",
        "diagram-label": "#cdb8f5",
        "diagram-guide": "#9d86d6",
        "diagram-axis": "#efe3ff",
        "diagram-grid": "#3a2160",
        "diagram-highlight": "#f5d642",
        "diagram-fill": "#4a2a7a"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "2px",
        "border-width-thick": "4px",
        "font-family": '"Karla", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.5",
        "icon-color": "#3ac9d8",
        "transition-fast": "120ms",
        "transition-normal": "220ms",
        "transition-slow": "120ms",
        "transition-timing": "cubic-bezier(0.34, 1.56, 0.64, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "2px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#3ac9d8"
      },
      shadows: {
        main: "5px 5px 0 0 #e04c8a",
        sm: "3px 3px 0 0 #e04c8a",
        lg: "8px 8px 0 0 #e04c8a",
        inner: "inset 3px 3px 0 0 rgba(255,255,255,0.08)",
        none: "none",
        hover: "8px 8px 0 0 #3ac9d8",
        active: "1px 1px 0 0 #e04c8a"
      },
      typeScale: {
        displayFamily: '"Shrikhand", Georgia, "Times New Roman", serif',
        bodyFamily: '"Karla", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        fontImport: "https://fonts.googleapis.com/css2?family=Shrikhand&family=Karla:wght@400;500;700&family=IBM+Plex+Mono:wght@400;500&display=swap",
        headingWeight: "400",
        headingTransform: "none",
        headingTracking: "0.01em",
        headingStyle: "normal"
      },
      surface: {
        pageImage: "repeating-conic-gradient(from 0deg at 50% 100%, rgba(224,76,138,0.12) 0deg 9deg, transparent 9deg 18deg)",
        pageImageSize: "100% 100%",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 L1440 0 V100 Z'/></svg>")`,
        edgeHeight: "clamp(24px, 6vw, 96px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='80'><path d='M0 20 C40 0 80 40 120 20 S160 0 160 20 M0 60 C40 40 80 80 120 60 S160 40 160 60' fill='none' stroke='black' stroke-width='1.5'/></svg>")`,
        textureSize: "160px 80px",
        accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
      }
    }
  }
};

// themes/risograph.json
var risograph_default = {
  name: "risograph",
  displayName: "Risograph",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "44px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "44px",
      rowHeightCompact: "30px",
      rowHeightNormal: "38px",
      rowHeightSpacious: "46px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "22px",
      dialogPadding: "22px",
      sectionGap: "28px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Rubik+Mono+One&display=swap",
      displayFamily: '"Rubik Mono One", "Space Mono", monospace',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Space Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.02em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "80ms",
        normal: "120ms",
        slow: "180ms",
        dramatic: "240ms"
      },
      easings: {
        linear: "linear",
        standard: "ease-out",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "ease-out"
      },
      shapes: {
        modalEnter: "translate(6px, 6px)",
        modalExit: "translate(6px, 6px)",
        pageEnter: "translate(3px, 3px)",
        popoverEnter: "translate(3px, 3px)",
        popoverExit: "translate(3px, 3px)",
        toastEnter: "translate(6px, 6px)",
        toastExit: "translate(6px, 6px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .09 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
      pageImageSize: "160px 160px",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    colors: {
      primary: "#ff48b0",
      "primary-hover": "#ff6ec0",
      "primary-foreground": "#1a1630",
      secondary: "#0078bf",
      "secondary-hover": "#00659f",
      "secondary-foreground": "#ffffff",
      accent: "#ffe800",
      "accent-foreground": "#1a1630",
      muted: "#ece6d2",
      "muted-foreground": "#4a4560",
      background: "#f7f3e8",
      foreground: "#1a1630",
      card: "#fffdf5",
      "card-foreground": "#1a1630",
      surface: "#f1ecd9",
      border: "#0078bf",
      input: "#0078bf",
      ring: "#ff48b0",
      "table-header": "#f1ecd9",
      "table-border": "#0078bf",
      "table-row-hover": "#f2ecd8",
      "surface-hover": "#ebe4cc",
      "border-hover": "#ff48b0",
      placeholder: "#6b6680",
      error: "#d1002f",
      "error-foreground": "#ffffff",
      success: "#00744a",
      "success-foreground": "#ffffff",
      warning: "#ffe800",
      "warning-foreground": "#1a1630",
      info: "#0078bf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#f62d69",
      "series-2": "#0078bf",
      "series-3": "#00838a",
      "series-4": "#c26a00",
      "series-5": "#6b3fa0",
      "series-6": "#477c2a",
      "series-7": "#8a5a2b",
      "series-8": "#a07800",
      "diagram-ink": "#1a1630",
      "diagram-label": "#4a4560",
      "diagram-guide": "#8a859e",
      "diagram-axis": "#1a1630",
      "diagram-grid": "#e6dfc8",
      "diagram-highlight": "#e0258f",
      "diagram-fill": "#ffe800"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "3px",
      lg: "4px",
      xl: "4px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "2px 2px 0 0 #0078bf",
      main: "3px 3px 0 0 #0078bf",
      lg: "6px 6px 0 0 #0078bf",
      inner: "inset 2px 2px 0 0 rgba(0, 120, 191, 0.18)",
      hover: "4px 4px 0 0 #ff48b0",
      active: "1px 1px 0 0 #0078bf"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Space Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.5",
      "icon-color": "currentColor",
      "transition-fast": "60ms",
      "transition-normal": "100ms",
      "transition-slow": "160ms",
      "transition-timing": "ease-out",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "-1px",
      "active-scale": "1",
      "active-translate-y": "1px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#ff48b0"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3px",
      lineCap: "butt",
      lineJoin: "round",
      dash: "5 3",
      dot: "1 3",
      fillStyle: "hatch",
      fillOpacity: "0.3",
      roughness: "0.5",
      glow: "0",
      shadow: "2px 2px 0 #0078bf",
      marker: "line",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "1",
      lightAmbient: "0.65",
      lightKey: "0.8",
      lightKeyColor: "#fff4d0",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff5cb8",
        "primary-hover": "#ff7cc6",
        "primary-foreground": "#1a1630",
        secondary: "#39a9ee",
        "secondary-hover": "#5bbcf5",
        "secondary-foreground": "#0a1a2a",
        accent: "#ffe800",
        "accent-foreground": "#1a1630",
        muted: "#231f45",
        "muted-foreground": "#c4bfdc",
        background: "#14122a",
        foreground: "#f3eedd",
        card: "#1c1940",
        "card-foreground": "#f3eedd",
        surface: "#1a1736",
        border: "#39a9ee",
        input: "#39a9ee",
        ring: "#ffe800",
        "table-header": "#1a1736",
        "table-border": "#39a9ee",
        "table-row-hover": "#221f48",
        "surface-hover": "#2a2656",
        "border-hover": "#ff5cb8",
        placeholder: "#9d98bc",
        error: "#ff6b81",
        "error-foreground": "#1a0508",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffe800",
        "warning-foreground": "#1a1630",
        info: "#6fbfff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ff5cb8",
        "series-2": "#39a9ee",
        "series-3": "#3ed6c8",
        "series-4": "#ff9a3d",
        "series-5": "#b79cf0",
        "series-6": "#98cf6a",
        "series-7": "#dba17e",
        "series-8": "#49a918",
        "diagram-ink": "#f3eedd",
        "diagram-label": "#c4bfdc",
        "diagram-guide": "#8a86b0",
        "diagram-axis": "#f3eedd",
        "diagram-grid": "#2e2a5a",
        "diagram-highlight": "#cbed3d",
        "diagram-fill": "#2e2a5a"
      },
      shadows: {
        none: "none",
        sm: "2px 2px 0 0 #ff48b0",
        main: "3px 3px 0 0 #ff48b0",
        lg: "6px 6px 0 0 #ff48b0",
        inner: "inset 2px 2px 0 0 rgba(255, 72, 176, 0.25)",
        hover: "4px 4px 0 0 #39a9ee",
        active: "1px 1px 0 0 #ff48b0"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Space Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.5",
        "icon-color": "currentColor",
        "transition-fast": "60ms",
        "transition-normal": "100ms",
        "transition-slow": "160ms",
        "transition-timing": "ease-out",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "-1px",
        "active-scale": "1",
        "active-translate-y": "1px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ffe800"
      }
    }
  }
};

// themes/scandi.json
var scandi_default = {
  name: "scandi",
  displayName: "Scandinavian",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "44px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "44px",
      inputHeightLg: "52px",
      rowHeightCompact: "36px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "16px",
      cardPaddingMd: "24px",
      cardPaddingLg: "32px",
      dialogPadding: "28px",
      sectionGap: "48px"
    },
    typeScale: {
      displayFamily: '"Jost", "DM Sans", sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500&family=DM+Sans:wght@400;500;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "500ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
        spring: "cubic-bezier(0.4, 0, 0.2, 1)"
      },
      shapes: {
        modalEnter: "translateY(8px)",
        modalExit: "translateY(8px)",
        pageEnter: "translateY(6px)",
        popoverEnter: "translateY(4px)",
        popoverExit: "translateY(4px)",
        toastEnter: "translateY(12px)",
        toastExit: "translateY(12px)"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1.25",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "none",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "8px",
      radiusInteractive: "6px",
      radiusPill: "var(--radius-full)",
      cornerShape: "squircle",
      cornerShapePill: "squircle",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: "repeating-linear-gradient(0deg, rgba(60,50,30,0.025) 0 1px, transparent 1px 3px), repeating-linear-gradient(90deg, rgba(60,50,30,0.025) 0 1px, transparent 1px 3px)",
      pageImageSize: "auto",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
      edgeHeight: "clamp(16px, 4vw, 56px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "6px",
      lg: "8px",
      xl: "12px",
      full: "9999px"
    },
    colors: {
      primary: "#3f6275",
      "primary-hover": "#335162",
      "primary-foreground": "#ffffff",
      secondary: "#e3d3b8",
      "secondary-hover": "#d9c7a6",
      "secondary-foreground": "#3a2e1c",
      accent: "#4f6b58",
      "accent-foreground": "#ffffff",
      muted: "#efeeea",
      "muted-foreground": "#5a5f63",
      background: "#fafaf8",
      foreground: "#23272a",
      card: "#ffffff",
      "card-foreground": "#23272a",
      surface: "#f3f2ee",
      border: "#e2e0da",
      input: "#8d8a82",
      ring: "#3f6275",
      "table-header": "#f3f2ee",
      "table-border": "#e2e0da",
      "table-row-hover": "#f6f5f1",
      "surface-hover": "#f0eee9",
      "border-hover": "#3f6275",
      placeholder: "#74797c",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#315c66",
      "series-2": "#a8743a",
      "series-3": "#4f6b58",
      "series-4": "#8a4f5e",
      "series-5": "#6b6f9a",
      "series-6": "#7a7a2a",
      "series-7": "#be5d45",
      "series-8": "#2f7a7a",
      "diagram-ink": "#23272a",
      "diagram-label": "#5a5f63",
      "diagram-guide": "#a8a69e",
      "diagram-axis": "#3f4448",
      "diagram-grid": "#e2e0da",
      "diagram-highlight": "#13638f",
      "diagram-fill": "#e3d3b8"
    },
    shadows: {
      main: "0 4px 16px rgba(35,39,42,0.06)",
      sm: "0 1px 2px rgba(35,39,42,0.05)",
      lg: "0 12px 36px rgba(35,39,42,0.08)",
      inner: "inset 0 1px 2px rgba(35,39,42,0.06)",
      none: "none",
      hover: "0 6px 20px rgba(35,39,42,0.08)",
      active: "0 1px 2px rgba(35,39,42,0.06)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"DM Sans", "Helvetica Neue", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.7",
      "icon-color": "#3f6275",
      "transition-fast": "180ms",
      "transition-normal": "300ms",
      "transition-slow": "450ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#3f6275"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1.5px",
      strokeBold: "2.5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "6 5",
      dot: "1 5",
      fillStyle: "tint",
      fillOpacity: "0.15",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "400",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "0.85",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.65",
      lightKey: "0.85",
      lightKeyColor: "#fff6e6",
      fog: "0.1"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "32px",
        buttonHeightMd: "44px",
        buttonHeightLg: "52px",
        inputHeightSm: "32px",
        inputHeightMd: "44px",
        inputHeightLg: "52px",
        rowHeightCompact: "36px",
        rowHeightNormal: "44px",
        rowHeightSpacious: "52px",
        cardPaddingSm: "16px",
        cardPaddingMd: "24px",
        cardPaddingLg: "32px",
        dialogPadding: "28px",
        sectionGap: "48px"
      },
      typeScale: {
        displayFamily: '"Jost", "DM Sans", sans-serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "400",
        headingTransform: "none",
        headingTracking: "-0.01em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500&family=DM+Sans:wght@400;500;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "500ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
          spring: "cubic-bezier(0.4, 0, 0.2, 1)"
        },
        shapes: {
          modalEnter: "translateY(8px)",
          modalExit: "translateY(8px)",
          pageEnter: "translateY(6px)",
          popoverEnter: "translateY(4px)",
          popoverExit: "translateY(4px)",
          toastEnter: "translateY(12px)",
          toastExit: "translateY(12px)"
        }
      },
      iconography: {
        family: "phosphor-outline",
        strokeWidth: "1.25",
        defaultSize: "18px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "none",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "8px",
        radiusInteractive: "6px",
        radiusPill: "var(--radius-full)",
        cornerShape: "squircle",
        cornerShapePill: "squircle",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 3px), repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 3px)",
        pageImageSize: "auto",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 100 Q720 0 1440 100 Z'/></svg>")`,
        edgeHeight: "clamp(16px, 4vw, 56px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
        textureSize: "40px 40px",
        accentImage: "linear-gradient(180deg, color-mix(in oklab, var(--color-primary) 8%, var(--color-background)), var(--color-background))"
      },
      radii: {
        none: "0px",
        sm: "4px",
        md: "6px",
        lg: "8px",
        xl: "12px",
        full: "9999px"
      },
      colors: {
        primary: "#8fb4c8",
        "primary-hover": "#a3c2d3",
        "primary-foreground": "#0f1c24",
        secondary: "#b89c72",
        "secondary-hover": "#c7ab80",
        "secondary-foreground": "#1a1205",
        accent: "#8fb99b",
        "accent-foreground": "#0b1a11",
        muted: "#262a2c",
        "muted-foreground": "#b3b6b8",
        background: "#1b1e20",
        foreground: "#ecebe6",
        card: "#222628",
        "card-foreground": "#ecebe6",
        surface: "#202325",
        border: "#383d40",
        input: "#7d8286",
        ring: "#8fb4c8",
        "table-header": "#202325",
        "table-border": "#383d40",
        "table-row-hover": "#25292b",
        "surface-hover": "#2b3033",
        "border-hover": "#8fb4c8",
        placeholder: "#868b8e",
        error: "#ff8a80",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#91a6c4",
        "series-2": "#dda46d",
        "series-3": "#8fb99b",
        "series-4": "#d49aa8",
        "series-5": "#b9b1db",
        "series-6": "#c9c76a",
        "series-7": "#e17a50",
        "series-8": "#6fc4c4",
        "diagram-ink": "#ecebe6",
        "diagram-label": "#b3b6b8",
        "diagram-guide": "#6e7376",
        "diagram-axis": "#cfcec8",
        "diagram-grid": "#383d40",
        "diagram-highlight": "#67b9e2",
        "diagram-fill": "#3a4144"
      },
      shadows: {
        main: "0 4px 16px rgba(0,0,0,0.3)",
        sm: "0 1px 2px rgba(0,0,0,0.3)",
        lg: "0 12px 36px rgba(0,0,0,0.4)",
        inner: "inset 0 1px 2px rgba(0,0,0,0.3)",
        none: "none",
        hover: "0 6px 20px rgba(0,0,0,0.35)",
        active: "0 1px 2px rgba(0,0,0,0.3)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"DM Sans", "Helvetica Neue", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.7",
        "icon-color": "#8fb4c8",
        "transition-fast": "180ms",
        "transition-normal": "300ms",
        "transition-slow": "450ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#8fb4c8"
      }
    }
  }
};

// themes/sketch.json
var sketch_default = {
  name: "sketch",
  displayName: "Sketchbook",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "38px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "38px",
      inputHeightLg: "48px",
      rowHeightCompact: "34px",
      rowHeightNormal: "42px",
      rowHeightSpacious: "50px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&family=Patrick+Hand&display=swap",
      displayFamily: '"Kalam", "Caveat", cursive',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Patrick Hand", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "13px",
          lineHeight: "17px"
        },
        sm: {
          size: "15px",
          lineHeight: "21px"
        },
        base: {
          size: "17px",
          lineHeight: "25px"
        },
        lg: {
          size: "19px",
          lineHeight: "30px"
        },
        xl: {
          size: "21px",
          lineHeight: "30px"
        },
        "2xl": {
          size: "25px",
          lineHeight: "34px"
        },
        "3xl": {
          size: "32px",
          lineHeight: "38px"
        },
        "4xl": {
          size: "38px",
          lineHeight: "42px"
        },
        "display-1": {
          size: "51px",
          lineHeight: "55px"
        },
        "display-2": {
          size: "64px",
          lineHeight: "68px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "120ms",
        normal: "200ms",
        slow: "320ms",
        dramatic: "500ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.3, 1.6, 0.5, 0.9)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.3, 1.8, 0.5, 0.85)"
      },
      shapes: {
        modalEnter: "rotate(-2deg) scale(0.96)",
        modalExit: "rotate(-2deg) scale(0.96)",
        pageEnter: "rotate(-0.5deg) translateY(8px)",
        popoverEnter: "rotate(-1deg)",
        popoverExit: "rotate(-1deg)",
        toastEnter: "rotate(2deg) translateY(16px)",
        toastExit: "rotate(2deg) translateY(16px)"
      }
    },
    iconography: {
      family: "tabler",
      strokeWidth: "2.25",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "linear-gradient(90deg, transparent 56px, rgba(217, 48, 37, 0.35) 56px, rgba(217, 48, 37, 0.35) 58px, transparent 58px), repeating-linear-gradient(transparent 0, transparent 27px, rgba(70, 110, 190, 0.22) 27px, rgba(70, 110, 190, 0.22) 28px)",
      pageImageSize: "auto"
    },
    colors: {
      primary: "#2b2b2b",
      "primary-hover": "#444444",
      "primary-foreground": "#fdfcf7",
      secondary: "#ecebe4",
      "secondary-hover": "#dddcd3",
      "secondary-foreground": "#2b2b2b",
      accent: "#ffe14d",
      "accent-foreground": "#2b2b2b",
      muted: "#efeee7",
      "muted-foreground": "#555555",
      background: "#fdfcf7",
      foreground: "#2b2b2b",
      card: "#fffffe",
      "card-foreground": "#2b2b2b",
      surface: "#f6f5ee",
      border: "#3a3a3a",
      input: "#3a3a3a",
      ring: "#d93025",
      "table-header": "#f6f5ee",
      "table-border": "#3a3a3a",
      "table-row-hover": "#f3f2ea",
      "surface-hover": "#e9e8df",
      "border-hover": "#d93025",
      placeholder: "#6e6e6e",
      error: "#c5221f",
      "error-foreground": "#ffffff",
      success: "#1e7e34",
      "success-foreground": "#ffffff",
      warning: "#ffe14d",
      "warning-foreground": "#2b2b2b",
      info: "#1a56b0",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#110000",
      "series-2": "#c84a00",
      "series-3": "#2c6fbb",
      "series-4": "#b87a00",
      "series-5": "#169173",
      "series-6": "#7b4aa0",
      "series-7": "#c2548a",
      "series-8": "#6b4a2a",
      "diagram-ink": "#2b2b2b",
      "diagram-label": "#555555",
      "diagram-guide": "#8a8a80",
      "diagram-axis": "#2b2b2b",
      "diagram-grid": "#d6d8e8",
      "diagram-highlight": "#e25443",
      "diagram-fill": "#ffe14d"
    },
    radii: {
      none: "0px",
      sm: "4px 8px 5px 9px / 8px 4px 9px 5px",
      md: "15px 225px 15px 255px / 255px 15px 225px 15px",
      lg: "255px 15px 225px 15px / 15px 225px 15px 255px",
      xl: "255px 15px 225px 15px / 15px 225px 15px 255px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "1px 2px 0 0 rgba(0, 0, 0, 0.6)",
      main: "2px 3px 0 0 rgba(0, 0, 0, 0.6)",
      lg: "3px 4px 0 0 rgba(0, 0, 0, 0.6)",
      inner: "inset 1px 2px 0 0 rgba(128, 128, 128, 0.3)",
      hover: "3px 4px 0 0 rgba(0, 0, 0, 0.6)",
      active: "0 1px 0 0 rgba(0, 0, 0, 0.6)"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Patrick Hand", "Kalam", "Comic Sans MS", cursive',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.5",
      "icon-color": "currentColor",
      "transition-fast": "120ms",
      "transition-normal": "200ms",
      "transition-slow": "320ms",
      "transition-timing": "cubic-bezier(0.3, 1.6, 0.5, 0.9)",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "1px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#d93025"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3.5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "7 5",
      dot: "1 5",
      fillStyle: "hatch",
      fillOpacity: "0.25",
      roughness: "1.4",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "base",
      labelWeight: "400",
      labelCase: "none",
      corner: "6px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.75",
      lightKey: "0.6",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ecebe4",
        "primary-hover": "#d2d1c9",
        "primary-foreground": "#25262b",
        secondary: "#3a3b42",
        "secondary-hover": "#45464e",
        "secondary-foreground": "#ecebe4",
        accent: "#ffe14d",
        "accent-foreground": "#25262b",
        muted: "#303137",
        "muted-foreground": "#c4c3bb",
        background: "#25262b",
        foreground: "#ecebe4",
        card: "#2d2e34",
        "card-foreground": "#ecebe4",
        surface: "#2a2b30",
        border: "#b9b8b0",
        input: "#b9b8b0",
        ring: "#ff8a80",
        "table-header": "#2a2b30",
        "table-border": "#b9b8b0",
        "table-row-hover": "#33343a",
        "surface-hover": "#3b3c43",
        "border-hover": "#ffe14d",
        placeholder: "#9d9c95",
        error: "#ff8a80",
        "error-foreground": "#25262b",
        success: "#6fdc8c",
        "success-foreground": "#25262b",
        warning: "#ffe14d",
        "warning-foreground": "#25262b",
        info: "#7fb2ff",
        "info-foreground": "#25262b",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#f4ecbf",
        "series-2": "#ee6240",
        "series-3": "#7ab6ff",
        "series-4": "#f2bc57",
        "series-5": "#6fdcc0",
        "series-6": "#c9a0f0",
        "series-7": "#ff9ac8",
        "series-8": "#d7a67b",
        "diagram-ink": "#faf8f1",
        "diagram-label": "#c4c3bb",
        "diagram-guide": "#8a8a82",
        "diagram-axis": "#ecebe4",
        "diagram-grid": "#44464f",
        "diagram-highlight": "#c0d11f",
        "diagram-fill": "#4a4b52"
      },
      shadows: {
        none: "none",
        sm: "1px 2px 0 0 rgba(236, 235, 228, 0.4)",
        main: "2px 3px 0 0 rgba(236, 235, 228, 0.4)",
        lg: "3px 4px 0 0 rgba(236, 235, 228, 0.4)",
        inner: "inset 1px 2px 0 0 rgba(128, 128, 128, 0.3)",
        hover: "3px 4px 0 0 rgba(236, 235, 228, 0.4)",
        active: "0 1px 0 0 rgba(236, 235, 228, 0.4)"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Patrick Hand", "Kalam", "Comic Sans MS", cursive',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.5",
        "icon-color": "currentColor",
        "transition-fast": "120ms",
        "transition-normal": "200ms",
        "transition-slow": "320ms",
        "transition-timing": "cubic-bezier(0.3, 1.6, 0.5, 0.9)",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "1px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ff8a80"
      },
      surface: {
        pageImage: "linear-gradient(90deg, transparent 56px, rgba(255, 130, 120, 0.35) 56px, rgba(255, 130, 120, 0.35) 58px, transparent 58px), repeating-linear-gradient(transparent 0, transparent 27px, rgba(200, 210, 235, 0.22) 27px, rgba(200, 210, 235, 0.22) 28px)",
        pageImageSize: "auto"
      }
    }
  }
};

// themes/solarpunk.json
var solarpunk_default = {
  name: "solarpunk",
  displayName: "Solarpunk",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Fraunces", Georgia, serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Fraunces:wght@400;600;700&family=Nunito+Sans:wght@400;600;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.37, 0, 0.63, 1)",
        spring: "cubic-bezier(0.45, 1.2, 0.55, 1)"
      },
      shapes: {
        modalEnter: "translateY(16px) scale(0.98)",
        modalExit: "translateY(16px) scale(0.98)",
        pageEnter: "translateY(10px)",
        popoverEnter: "scale(0.97)",
        popoverExit: "scale(0.97)",
        toastEnter: "translateY(20px)",
        toastExit: "translateY(20px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.75",
      defaultSize: "20px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-lg)",
      radiusInteractive: "var(--radius-md)",
      radiusPill: "var(--radius-full)",
      cornerShape: "squircle",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: "conic-gradient(from 200deg at 100% 0%, rgba(242,193,78,0.16), transparent 22%, rgba(242,193,78,0.10) 34%, transparent 48%), radial-gradient(ellipse at 0% 100%, rgba(46,125,79,0.14), transparent 55%)",
      pageImageSize: "100% 100%, 100% 100%",
      edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
      edgeHeight: "clamp(24px, 6vw, 96px)",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
      textureSize: "24px 24px",
      accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
    },
    radii: {
      none: "0px",
      sm: "10px",
      md: "16px",
      lg: "24px",
      xl: "32px",
      full: "9999px"
    },
    colors: {
      primary: "#2e7d4f",
      "primary-hover": "#246a42",
      "primary-foreground": "#ffffff",
      secondary: "#a8d8f0",
      "secondary-hover": "#94cbe8",
      "secondary-foreground": "#0d3347",
      accent: "#8a5d00",
      "accent-foreground": "#fff8e0",
      muted: "#ece8c9",
      "muted-foreground": "#4d5a3c",
      background: "#fbf5e3",
      foreground: "#1f3a2a",
      card: "#fffdf3",
      "card-foreground": "#1f3a2a",
      surface: "#f3edcf",
      border: "#7fb08a",
      input: "#4f8a62",
      ring: "#2e7d4f",
      "table-header": "#ece8c9",
      "table-border": "#7fb08a",
      "table-row-hover": "#f4efd6",
      "surface-hover": "#efe9cb",
      "border-hover": "#2e7d4f",
      placeholder: "#6b7559",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#558d51",
      "series-2": "#c3760a",
      "series-3": "#1f7aa8",
      "series-4": "#7f7110",
      "series-5": "#7a5aa8",
      "series-6": "#b0446a",
      "series-7": "#587804",
      "series-8": "#c45a2a",
      "diagram-ink": "#1f3a2a",
      "diagram-label": "#4d5a3c",
      "diagram-guide": "#7fb08a",
      "diagram-axis": "#2d5a3c",
      "diagram-grid": "#cfe3c4",
      "diagram-highlight": "#8a5d00",
      "diagram-fill": "#a8d8f0"
    },
    shadows: {
      main: "0 6px 18px -2px rgba(180,120,20,0.22)",
      sm: "0 2px 6px rgba(180,120,20,0.14)",
      lg: "0 16px 40px -6px rgba(180,120,20,0.28)",
      inner: "inset 0 2px 4px rgba(46,125,79,0.12)",
      none: "none",
      hover: "0 10px 26px -4px rgba(180,120,20,0.30)",
      active: "0 1px 3px rgba(180,120,20,0.18)"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Nunito Sans", "Segoe UI", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#2e7d4f",
      "transition-fast": "220ms",
      "transition-normal": "380ms",
      "transition-slow": "560ms",
      "transition-timing": "cubic-bezier(0.37, 0, 0.63, 1)",
      "hover-scale": "1",
      "hover-translate-y": "-2px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#2e7d4f"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3.5px",
      lineCap: "round",
      lineJoin: "round",
      dash: "2 6",
      dot: "0.5 5",
      fillStyle: "tint",
      fillOpacity: "0.25",
      roughness: "0.3",
      glow: "0",
      shadow: "0 3px 8px rgba(46, 125, 79, 0.2)",
      marker: "open",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "12px"
    },
    scene: {
      materialRoughness: "0.7",
      materialMetalness: "0.05",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.65",
      lightKey: "1.05",
      lightKeyColor: "#fff0c0",
      fog: "0.12"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Fraunces", Georgia, serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "600",
        headingTransform: "none",
        headingTracking: "-0.01em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Fraunces:wght@400;600;700&family=Nunito+Sans:wght@400;600;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "700ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.37, 0, 0.63, 1)",
          spring: "cubic-bezier(0.45, 1.2, 0.55, 1)"
        },
        shapes: {
          modalEnter: "translateY(16px) scale(0.98)",
          modalExit: "translateY(16px) scale(0.98)",
          pageEnter: "translateY(10px)",
          popoverEnter: "scale(0.97)",
          popoverExit: "scale(0.97)",
          toastEnter: "translateY(20px)",
          toastExit: "translateY(20px)"
        }
      },
      iconography: {
        family: "phosphor-duotone",
        strokeWidth: "1.75",
        defaultSize: "20px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "var(--radius-lg)",
        radiusInteractive: "var(--radius-md)",
        radiusPill: "var(--radius-full)",
        cornerShape: "squircle",
        cornerShapePill: "round",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: "conic-gradient(from 200deg at 100% 0%, rgba(242,193,78,0.10), transparent 22%, rgba(242,193,78,0.06) 34%, transparent 48%), radial-gradient(ellipse at 0% 100%, rgba(127,217,154,0.10), transparent 55%)",
        pageImageSize: "100% 100%, 100% 100%",
        edgeMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'><path d='M0 60 C240 100 480 100 720 60 C960 20 1200 20 1440 60 V100 H0 Z'/></svg>")`,
        edgeHeight: "clamp(24px, 6vw, 96px)",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24'><circle cx='3' cy='3' r='1.5'/></svg>")`,
        textureSize: "24px 24px",
        accentImage: "radial-gradient(60% 80% at 15% 20%, color-mix(in oklab, var(--color-primary) 22%, transparent), transparent 70%), radial-gradient(50% 70% at 85% 30%, color-mix(in oklab, var(--color-accent) 20%, transparent), transparent 70%)"
      },
      radii: {
        none: "0px",
        sm: "10px",
        md: "16px",
        lg: "24px",
        xl: "32px",
        full: "9999px"
      },
      colors: {
        primary: "#7fd99a",
        "primary-hover": "#95e3ab",
        "primary-foreground": "#07200f",
        secondary: "#5bb4e0",
        "secondary-hover": "#72c2ea",
        "secondary-foreground": "#04202e",
        accent: "#f2c14e",
        "accent-foreground": "#1f1500",
        muted: "#1b3a29",
        "muted-foreground": "#b9cdb2",
        background: "#10261a",
        foreground: "#f2ecd0",
        card: "#173224",
        "card-foreground": "#f2ecd0",
        surface: "#142c20",
        border: "#3f7a55",
        input: "#7fb08a",
        ring: "#7fd99a",
        "table-header": "#142c20",
        "table-border": "#3f7a55",
        "table-row-hover": "#1a3828",
        "surface-hover": "#204630",
        "border-hover": "#7fd99a",
        placeholder: "#8ba58f",
        error: "#ff8a80",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#8dc06d",
        "series-2": "#ede069",
        "series-3": "#5bb4e0",
        "series-4": "#eca565",
        "series-5": "#c4a8f0",
        "series-6": "#f08ab0",
        "series-7": "#b1e24e",
        "series-8": "#e07e4e",
        "diagram-ink": "#f2ecd0",
        "diagram-label": "#b9cdb2",
        "diagram-guide": "#5f8f6e",
        "diagram-axis": "#d8e6c8",
        "diagram-grid": "#2a4f38",
        "diagram-highlight": "#c5ac2c",
        "diagram-fill": "#245a3c"
      },
      shadows: {
        main: "0 6px 18px -2px rgba(0,0,0,0.45)",
        sm: "0 2px 6px rgba(0,0,0,0.35)",
        lg: "0 16px 40px -6px rgba(0,0,0,0.55)",
        inner: "inset 0 2px 4px rgba(0,0,0,0.35)",
        none: "none",
        hover: "0 10px 26px -4px rgba(242,193,78,0.22)",
        active: "0 1px 3px rgba(0,0,0,0.4)"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Nunito Sans", "Segoe UI", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#7fd99a",
        "transition-fast": "220ms",
        "transition-normal": "380ms",
        "transition-slow": "560ms",
        "transition-timing": "cubic-bezier(0.37, 0, 0.63, 1)",
        "hover-scale": "1",
        "hover-translate-y": "-2px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#7fd99a"
      }
    }
  }
};

// themes/steampunk.json
var steampunk_default = {
  name: "steampunk",
  displayName: "Steampunk",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "38px",
      buttonHeightLg: "46px",
      inputHeightSm: "30px",
      inputHeightMd: "38px",
      inputHeightLg: "46px",
      rowHeightCompact: "34px",
      rowHeightNormal: "42px",
      rowHeightSpacious: "50px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "22px",
      dialogPadding: "22px",
      sectionGap: "28px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=IM+Fell+English&display=swap",
      displayFamily: '"Cinzel", "IM Fell English", Georgia, serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Special Elite", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "700",
      headingTransform: "uppercase",
      headingTracking: "0.08em",
      headingStyle: "normal",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "29px"
        },
        xl: {
          size: "20px",
          lineHeight: "29px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "33px"
        },
        "3xl": {
          size: "31px",
          lineHeight: "37px"
        },
        "4xl": {
          size: "37px",
          lineHeight: "41px"
        },
        "display-1": {
          size: "49px",
          lineHeight: "53px"
        },
        "display-2": {
          size: "61px",
          lineHeight: "65px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "100ms",
        normal: "180ms",
        slow: "280ms",
        dramatic: "400ms"
      },
      easings: {
        linear: "linear",
        standard: "steps(6)",
        emphasized: "linear",
        spring: "steps(6)"
      },
      shapes: {
        modalEnter: "perspective(800px) rotateX(-18deg)",
        modalExit: "perspective(800px) rotateX(-18deg)",
        pageEnter: "translateY(8px)",
        popoverEnter: "perspective(600px) rotateX(-12deg)",
        popoverExit: "perspective(600px) rotateX(-12deg)",
        toastEnter: "translateY(-16px)",
        toastExit: "translateY(-16px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.75",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "bevel",
      cornerShapePill: "bevel",
      borderStyle: "ridge",
      borderStyleInteractive: "ridge",
      borderHairline: "2px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      cardImage: "repeating-linear-gradient(90deg, rgba(120, 80, 20, 0.07) 0, rgba(120, 80, 20, 0.07) 1px, transparent 1px, transparent 3px)"
    },
    colors: {
      primary: "#6e1a1a",
      "primary-hover": "#551414",
      "primary-foreground": "#f6ead0",
      secondary: "#b5832a",
      "secondary-hover": "#c2912f",
      "secondary-foreground": "#1f1206",
      accent: "#9a4a24",
      "accent-foreground": "#ffffff",
      muted: "#e4d3b0",
      "muted-foreground": "#5a4325",
      background: "#f0e3c8",
      foreground: "#2b1a0e",
      card: "#f7ecd4",
      "card-foreground": "#2b1a0e",
      surface: "#ead9b6",
      border: "#8a5a1c",
      input: "#8a5a1c",
      ring: "#9a4a24",
      "table-header": "#ead9b6",
      "table-border": "#8a5a1c",
      "table-row-hover": "#ecddbd",
      "surface-hover": "#e2cfa6",
      "border-hover": "#b5832a",
      placeholder: "#7a6440",
      error: "#a4161a",
      "error-foreground": "#ffffff",
      success: "#2f6b2f",
      "success-foreground": "#ffffff",
      warning: "#e0a526",
      "warning-foreground": "#1f1206",
      info: "#275c80",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#651012",
      "series-2": "#875c17",
      "series-3": "#2f5f5a",
      "series-4": "#c35946",
      "series-5": "#4a3a6a",
      "series-6": "#616818",
      "series-7": "#b96208",
      "series-8": "#7a2a52",
      "diagram-ink": "#2b1a0e",
      "diagram-label": "#5a4325",
      "diagram-guide": "#8a6a3a",
      "diagram-axis": "#4a2e14",
      "diagram-grid": "#d9c79f",
      "diagram-highlight": "#9a4a24",
      "diagram-fill": "#d8b56a"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "4px",
      lg: "6px",
      xl: "6px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "inset 1px 1px 0 0 rgba(255, 235, 180, 0.6), 1px 2px 3px 0 rgba(40, 20, 0, 0.4)",
      main: "inset 1px 1px 0 0 rgba(255, 235, 180, 0.6), inset -1px -1px 2px 0 rgba(60, 30, 0, 0.35), 2px 3px 6px 0 rgba(40, 20, 0, 0.45)",
      lg: "inset 1px 1px 0 0 rgba(255, 235, 180, 0.6), inset -1px -1px 2px 0 rgba(60, 30, 0, 0.4), 4px 6px 12px 0 rgba(40, 20, 0, 0.55)",
      inner: "inset 2px 2px 4px 0 rgba(60, 30, 0, 0.35)",
      hover: "inset 1px 1px 0 0 rgba(255, 235, 180, 0.7), inset -1px -1px 2px 0 rgba(60, 30, 0, 0.35), 3px 4px 8px 0 rgba(40, 20, 0, 0.5)",
      active: "inset 2px 2px 4px 0 rgba(60, 30, 0, 0.45)"
    },
    typography: {
      "border-width": "4px",
      "border-width-thin": "2px",
      "border-width-thick": "6px",
      "font-family": '"IM Fell English", Georgia, "Times New Roman", serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.5",
      "icon-color": "currentColor",
      "transition-fast": "100ms",
      "transition-normal": "180ms",
      "transition-slow": "280ms",
      "transition-timing": "steps(6)",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "1px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#9a4a24"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "4px",
      lineCap: "butt",
      lineJoin: "bevel",
      dash: "10 3 2 3",
      dot: "1 3",
      fillStyle: "tint",
      fillOpacity: "0.3",
      roughness: "0.2",
      glow: "0",
      shadow: "2px 3px 0 rgba(43, 26, 14, 0.5)",
      marker: "triangle",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "3px"
    },
    scene: {
      materialRoughness: "0.3",
      materialMetalness: "0.9",
      materialFlat: "0",
      materialOutline: "1",
      lightAmbient: "0.4",
      lightKey: "1.2",
      lightKeyColor: "#ffd9a0",
      fog: "0.2"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#c9973f",
        "primary-hover": "#dcac55",
        "primary-foreground": "#1e130b",
        secondary: "#7a2a22",
        "secondary-hover": "#8c3329",
        "secondary-foreground": "#f6ead0",
        accent: "#d2774a",
        "accent-foreground": "#1e130b",
        muted: "#33221a",
        "muted-foreground": "#cdb88f",
        background: "#1e130b",
        foreground: "#efdfbd",
        card: "#2a1a10",
        "card-foreground": "#efdfbd",
        surface: "#241710",
        border: "#b98a3a",
        input: "#b98a3a",
        ring: "#d2774a",
        "table-header": "#241710",
        "table-border": "#b98a3a",
        "table-row-hover": "#2e1d13",
        "surface-hover": "#382519",
        "border-hover": "#c9973f",
        placeholder: "#a08c68",
        error: "#ff8a80",
        "error-foreground": "#1e130b",
        success: "#7fd69a",
        "success-foreground": "#1e130b",
        warning: "#e0a526",
        "warning-foreground": "#1e130b",
        info: "#7fb6dc",
        "info-foreground": "#1e130b",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#cb6858",
        "series-2": "#a48e27",
        "series-3": "#6fc0b4",
        "series-4": "#e68a5c",
        "series-5": "#a898d8",
        "series-6": "#b4c860",
        "series-7": "#faba90",
        "series-8": "#d98ab4",
        "diagram-ink": "#f6eac7",
        "diagram-label": "#cdb88f",
        "diagram-guide": "#8a6a3a",
        "diagram-axis": "#d9c79f",
        "diagram-grid": "#4a3320",
        "diagram-highlight": "#bb812d",
        "diagram-fill": "#4a3018"
      },
      shadows: {
        none: "none",
        sm: "inset 1px 1px 0 0 rgba(255, 220, 150, 0.18), 1px 2px 3px 0 rgba(0, 0, 0, 0.6)",
        main: "inset 1px 1px 0 0 rgba(255, 220, 150, 0.18), inset -1px -1px 2px 0 rgba(0, 0, 0, 0.6), 2px 3px 6px 0 rgba(0, 0, 0, 0.6)",
        lg: "inset 1px 1px 0 0 rgba(255, 220, 150, 0.2), inset -1px -1px 2px 0 rgba(0, 0, 0, 0.7), 4px 6px 12px 0 rgba(0, 0, 0, 0.7)",
        inner: "inset 2px 2px 4px 0 rgba(0, 0, 0, 0.6)",
        hover: "inset 1px 1px 0 0 rgba(255, 220, 150, 0.25), inset -1px -1px 2px 0 rgba(0, 0, 0, 0.6), 3px 4px 8px 0 rgba(0, 0, 0, 0.7)",
        active: "inset 2px 2px 4px 0 rgba(0, 0, 0, 0.7)"
      },
      typography: {
        "border-width": "4px",
        "border-width-thin": "2px",
        "border-width-thick": "6px",
        "font-family": '"IM Fell English", Georgia, "Times New Roman", serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.5",
        "icon-color": "currentColor",
        "transition-fast": "100ms",
        "transition-normal": "180ms",
        "transition-slow": "280ms",
        "transition-timing": "steps(6)",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "1",
        "active-translate-y": "1px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#d2774a"
      },
      surface: {
        cardImage: "repeating-linear-gradient(90deg, rgba(255, 235, 180, 0.07) 0, rgba(255, 235, 180, 0.07) 1px, transparent 1px, transparent 3px)"
      }
    }
  }
};

// themes/swiss.json
var swiss_default = {
  name: "swiss",
  displayName: "Swiss International",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "44px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "44px",
      rowHeightCompact: "28px",
      rowHeightNormal: "36px",
      rowHeightSpacious: "44px",
      cardPaddingSm: "8px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Inter Tight", "Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif',
      bodyFamily: '"Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;700;800&family=Archivo:wght@400;500;700;800&display=swap",
      headingWeight: "800",
      headingTransform: "none",
      headingTracking: "-0.04em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "160ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.2, 0, 0, 1)"
      },
      shapes: {
        modalEnter: "translateY(24px)",
        modalExit: "translateY(24px)",
        pageEnter: "translateX(24px)",
        popoverEnter: "translateY(8px)",
        popoverExit: "translateY(8px)",
        toastEnter: "translateY(24px)",
        toastExit: "translateY(24px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "none",
      dialogElevation: "none",
      toastElevation: "none",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "var(--radius-none)",
      radiusInteractive: "var(--radius-none)",
      radiusPill: "var(--radius-none)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#d8001a",
      "primary-hover": "#b00015",
      "primary-foreground": "#ffffff",
      secondary: "#111111",
      "secondary-hover": "#2b2b2b",
      "secondary-foreground": "#ffffff",
      accent: "#d8001a",
      "accent-foreground": "#ffffff",
      muted: "#efefef",
      "muted-foreground": "#444444",
      background: "#ffffff",
      foreground: "#111111",
      card: "#ffffff",
      "card-foreground": "#111111",
      surface: "#f6f6f6",
      border: "#111111",
      input: "#111111",
      ring: "#d8001a",
      "table-header": "#f6f6f6",
      "table-border": "#111111",
      "table-row-hover": "#f6f6f6",
      "surface-hover": "#ebebeb",
      "border-hover": "#d8001a",
      placeholder: "#6b6b6b",
      error: "#d8001a",
      "error-foreground": "#ffffff",
      success: "#0a7a3a",
      "success-foreground": "#ffffff",
      warning: "#ffb800",
      "warning-foreground": "#111111",
      info: "#111111",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#ce0376",
      "series-2": "#000a22",
      "series-3": "#0050b3",
      "series-4": "#d46d00",
      "series-5": "#168c72",
      "series-6": "#6a1b9a",
      "series-7": "#8a8a8a",
      "series-8": "#a62c9f",
      "diagram-ink": "#111111",
      "diagram-label": "#444444",
      "diagram-guide": "#999999",
      "diagram-axis": "#111111",
      "diagram-grid": "#e0e0e0",
      "diagram-highlight": "#e11550",
      "diagram-fill": "#f2f2f2"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "0px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "-0.005em",
      "line-height": "1.4",
      "icon-color": "#d8001a",
      "transition-fast": "60ms",
      "transition-normal": "100ms",
      "transition-slow": "150ms",
      "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1.0",
      "active-translate-y": "0",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#d8001a"
    },
    surface: {
      pageImage: "linear-gradient(rgba(17,17,17,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(17,17,17,0.045) 1px, transparent 1px)",
      pageImageSize: "64px 64px"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "4px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "4 4",
      dot: "1 3",
      fillStyle: "solid",
      fillOpacity: "0.2",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "triangle",
      labelFont: "body",
      labelSize: "xs",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "0"
    },
    scene: {
      materialRoughness: "0.8",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "0.8",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ff4257",
        "primary-hover": "#ff6577",
        "primary-foreground": "#130204",
        secondary: "#f2f2f2",
        "secondary-hover": "#ffffff",
        "secondary-foreground": "#111111",
        accent: "#ff4257",
        "accent-foreground": "#130204",
        muted: "#222222",
        "muted-foreground": "#c8c8c8",
        background: "#0d0d0d",
        foreground: "#f5f5f5",
        card: "#141414",
        "card-foreground": "#f5f5f5",
        surface: "#111111",
        border: "#f5f5f5",
        input: "#f5f5f5",
        ring: "#ff4257",
        "table-header": "#111111",
        "table-border": "#f5f5f5",
        "table-row-hover": "#191919",
        "surface-hover": "#242424",
        "border-hover": "#ff4257",
        placeholder: "#9a9a9a",
        error: "#ff6577",
        "error-foreground": "#190204",
        success: "#4ed68a",
        "success-foreground": "#03170b",
        warning: "#ffc933",
        "warning-foreground": "#111111",
        info: "#f5f5f5",
        "info-foreground": "#111111",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#dc124f",
        "series-2": "#c2bc90",
        "series-3": "#5a9bff",
        "series-4": "#ffa033",
        "series-5": "#23e4c9",
        "series-6": "#c58aff",
        "series-7": "#979797",
        "series-8": "#ff6ab0",
        "diagram-ink": "#f5f5f5",
        "diagram-label": "#c8c8c8",
        "diagram-guide": "#7a7a7a",
        "diagram-axis": "#f5f5f5",
        "diagram-grid": "#333333",
        "diagram-highlight": "#f4383a",
        "diagram-fill": "#262626"
      },
      shadows: {
        main: "none",
        sm: "none",
        lg: "none",
        inner: "none",
        none: "none",
        hover: "none",
        active: "none"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "-0.005em",
        "line-height": "1.4",
        "icon-color": "#ff4257",
        "transition-fast": "60ms",
        "transition-normal": "100ms",
        "transition-slow": "150ms",
        "transition-timing": "cubic-bezier(0.2, 0, 0, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "0",
        "hover-translate-x": "0",
        "active-scale": "1.0",
        "active-translate-y": "0",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ff4257"
      },
      radii: {
        none: "0px",
        sm: "0px",
        md: "0px",
        lg: "0px",
        xl: "0px",
        full: "0px"
      },
      surface: {
        pageImage: "linear-gradient(rgba(245,245,245,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(245,245,245,0.05) 1px, transparent 1px)",
        pageImageSize: "64px 64px"
      }
    }
  }
};

// themes/terminal.json
var terminal_default = {
  name: "terminal",
  displayName: "Terminal",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "3px",
        space2: "6px",
        space3: "9px",
        space4: "12px",
        space5: "15px",
        space6: "18px",
        space7: "21px",
        space8: "24px",
        space9: "27px",
        space10: "30px",
        space11: "33px",
        space12: "36px"
      },
      buttonHeightSm: "20px",
      buttonHeightMd: "24px",
      buttonHeightLg: "30px",
      inputHeightSm: "20px",
      inputHeightMd: "24px",
      inputHeightLg: "30px",
      rowHeightCompact: "22px",
      rowHeightNormal: "28px",
      rowHeightSpacious: "34px",
      cardPaddingSm: "6px",
      cardPaddingMd: "8px",
      cardPaddingLg: "12px",
      dialogPadding: "12px",
      sectionGap: "16px"
    },
    typeScale: {
      displayFamily: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      bodyFamily: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      monoFamily: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      scale: {
        xs: {
          size: "11px",
          lineHeight: "14px"
        },
        sm: {
          size: "12px",
          lineHeight: "16px"
        },
        base: {
          size: "13px",
          lineHeight: "17px"
        },
        lg: {
          size: "14px",
          lineHeight: "18px"
        },
        xl: {
          size: "15px",
          lineHeight: "20px"
        },
        "2xl": {
          size: "16px",
          lineHeight: "20px"
        },
        "3xl": {
          size: "18px",
          lineHeight: "22px"
        },
        "4xl": {
          size: "20px",
          lineHeight: "24px"
        },
        "display-1": {
          size: "24px",
          lineHeight: "28px"
        },
        "display-2": {
          size: "28px",
          lineHeight: "32px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "sm",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap",
      headingWeight: "600",
      headingTransform: "none",
      headingTracking: "-0.01em",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "200ms"
      },
      easings: {
        linear: "linear",
        standard: "steps(4, end)",
        emphasized: "steps(4, end)",
        spring: "steps(4, end)"
      },
      shapes: {
        modalEnter: "none",
        modalExit: "none",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "phosphor-fill",
      strokeWidth: "0",
      defaultSize: "14px"
    },
    elevation: {
      cardElevation: "none",
      popoverElevation: "0 8px 24px rgba(0,0,0,0.18)",
      dialogElevation: "0 16px 48px rgba(0,0,0,0.28)",
      toastElevation: "0 8px 24px rgba(0,0,0,0.18)",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "8px",
      radiusInteractive: "6px",
      radiusPill: "9999px",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid"
    },
    colors: {
      primary: "#1e66f5",
      "primary-hover": "#1650c4",
      "primary-foreground": "#ffffff",
      secondary: "#4c4f69",
      "secondary-hover": "#3c3f54",
      "secondary-foreground": "#eff1f5",
      accent: "#8839ef",
      "accent-foreground": "#ffffff",
      muted: "#e6e9ef",
      "muted-foreground": "#5c5f77",
      background: "#eff1f5",
      foreground: "#4c4f69",
      card: "#ffffff",
      "card-foreground": "#4c4f69",
      surface: "#e6e9ef",
      border: "#ccd0da",
      input: "#bcc0cc",
      ring: "#1e66f5",
      "table-header": "#4c4f69",
      "table-border": "#ccd0da",
      "table-row-hover": "#e6e9ef",
      "surface-hover": "#dce0e8",
      "border-hover": "#acb0be",
      placeholder: "#8c8fa1",
      error: "#d20f39",
      "error-foreground": "#ffffff",
      success: "#40a02b",
      "success-foreground": "#ffffff",
      warning: "#df8e1d",
      "warning-foreground": "#1e1e2e",
      info: "#04a5e5",
      "info-foreground": "#ffffff",
      scrim: "rgba(17, 17, 27, 0.5)",
      "series-1": "#dc8a78",
      "series-2": "#8839ef",
      "series-3": "#7c7f93",
      "series-4": "#fe640b",
      "series-5": "#179299",
      "series-6": "#ea76cb",
      "series-7": "#7287fd",
      "series-8": "#06d6a0",
      "diagram-ink": "#1e66f5",
      "diagram-label": "#4c4f69",
      "diagram-guide": "#9ca0b0",
      "diagram-axis": "#6c6f85",
      "diagram-grid": "#dce0e8",
      "diagram-highlight": "#04a5e5",
      "diagram-fill": "#dce6fd"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "6px",
      lg: "8px",
      xl: "10px",
      full: "9999px"
    },
    shadows: {
      main: "none",
      sm: "none",
      lg: "none",
      inner: "none",
      none: "none",
      hover: "none",
      active: "none"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.35",
      "icon-color": "currentColor",
      "transition-fast": "60ms",
      "transition-normal": "100ms",
      "transition-slow": "140ms",
      "transition-timing": "linear",
      "hover-scale": "1",
      "hover-translate-y": "0",
      "hover-translate-x": "0",
      "active-scale": "1",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "0px",
      "focus-ring-color": "#067a3c"
    },
    surface: {
      pageImage: "none",
      pageImageSize: "auto",
      diagram: "#020a04",
      edgeMask: "none",
      edgeHeight: "0px",
      textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
      textureSize: "40px 40px",
      accentImage: "none"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1px",
      strokeBold: "2px",
      lineCap: "square",
      lineJoin: "miter",
      dash: "4 4",
      dot: "1 3",
      fillStyle: "outline",
      fillOpacity: "0.15",
      roughness: "0",
      glow: "6",
      shadow: "none",
      marker: "line",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "uppercase",
      corner: "0"
    },
    scene: {
      materialRoughness: "0.5",
      materialMetalness: "0.1",
      materialFlat: "1",
      materialOutline: "1",
      lightAmbient: "0.3",
      lightKey: "0.9",
      lightKeyColor: "#66ff99",
      fog: "0.3"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#7aa2f7",
        "primary-hover": "#94b4f9",
        "primary-foreground": "#16161e",
        secondary: "#292e42",
        "secondary-hover": "#343a52",
        "secondary-foreground": "#c0caf5",
        accent: "#bb9af7",
        "accent-foreground": "#16161e",
        muted: "#24283b",
        "muted-foreground": "#a9b1d6",
        background: "#1a1b26",
        foreground: "#c0caf5",
        card: "#1f2335",
        "card-foreground": "#c0caf5",
        surface: "#24283b",
        border: "#2f354f",
        input: "#3b4261",
        ring: "#7aa2f7",
        "table-header": "#c0caf5",
        "table-border": "#2f354f",
        "table-row-hover": "#292e42",
        "surface-hover": "#292e42",
        "border-hover": "#414868",
        placeholder: "#737aa2",
        error: "#f7768e",
        "error-foreground": "#16161e",
        success: "#9ece6a",
        "success-foreground": "#16161e",
        warning: "#e0af68",
        "warning-foreground": "#16161e",
        info: "#7dcfff",
        "info-foreground": "#16161e",
        scrim: "rgba(8, 8, 14, 0.65)",
        "series-1": "#7dcfff",
        "series-2": "#bb9af7",
        "series-3": "#db4b4b",
        "series-4": "#ff9e64",
        "series-5": "#2ac3de",
        "series-6": "#06d6a0",
        "series-7": "#ff007c",
        "series-8": "#73daca",
        "diagram-ink": "#7aa2f7",
        "diagram-label": "#c0caf5",
        "diagram-guide": "#565f89",
        "diagram-axis": "#a9b1d6",
        "diagram-grid": "#292e42",
        "diagram-highlight": "#b4f9f8",
        "diagram-fill": "#283457"
      },
      typeScale: {
        displayFamily: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        bodyFamily: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        monoFamily: '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        scale: {
          xs: {
            size: "11px",
            lineHeight: "14px"
          },
          sm: {
            size: "12px",
            lineHeight: "16px"
          },
          base: {
            size: "13px",
            lineHeight: "17px"
          },
          lg: {
            size: "14px",
            lineHeight: "18px"
          },
          xl: {
            size: "15px",
            lineHeight: "20px"
          },
          "2xl": {
            size: "16px",
            lineHeight: "20px"
          },
          "3xl": {
            size: "18px",
            lineHeight: "22px"
          },
          "4xl": {
            size: "20px",
            lineHeight: "24px"
          },
          "display-1": {
            size: "24px",
            lineHeight: "28px"
          },
          "display-2": {
            size: "28px",
            lineHeight: "32px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "sm",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        fontImport: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap",
        headingWeight: "600",
        headingTransform: "none",
        headingTracking: "-0.01em",
        headingStyle: "normal",
        headingShadow: "none"
      },
      surface: {
        pageImage: "none",
        pageImageSize: "auto",
        edgeMask: "none",
        edgeHeight: "0px",
        textureMask: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M0 .5H40M.5 0V40' stroke='black' stroke-width='1' fill='none'/></svg>")`,
        textureSize: "40px 40px",
        accentImage: "none"
      }
    }
  }
};

// themes/trait-wars.json
var trait_wars_default = {
  name: "trait-wars",
  displayName: "Trait Wars",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "44px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "44px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Cinzel", "Palatino Linotype", Georgia, serif',
      bodyFamily: '"EB Garamond", Georgia, "Times New Roman", serif',
      monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=EB+Garamond:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap",
      headingWeight: "700",
      headingTransform: "uppercase",
      headingTracking: "0.08em",
      headingStyle: "normal"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "700ms"
      },
      easings: {
        linear: "linear",
        standard: "cubic-bezier(0.4, 0, 0.2, 1)",
        emphasized: "cubic-bezier(0.2, 0, 0, 1)",
        spring: "cubic-bezier(0.34, 1.56, 0.64, 1)"
      },
      shapes: {
        modalEnter: "perspective(800px) rotateX(-20deg)",
        modalExit: "perspective(800px) rotateX(-20deg)",
        pageEnter: "translateY(8px)",
        popoverEnter: "scale(0.95)",
        popoverExit: "scale(0.95)",
        toastEnter: "translateY(-16px)",
        toastExit: "translateY(-16px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "1.75",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-inner)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "bevel",
      cornerShapePill: "bevel",
      borderStyle: "ridge",
      borderStyleInteractive: "ridge"
    },
    colors: {
      primary: "#785c12",
      "primary-hover": "#5c4a0d",
      "primary-foreground": "#faf6ee",
      secondary: "#f0e8d8",
      "secondary-hover": "#e6dbc4",
      "secondary-foreground": "#3a2f22",
      accent: "#0f766e",
      "accent-foreground": "#faf6ee",
      muted: "#ede4d0",
      "muted-foreground": "#6e5f46",
      background: "#faf6ee",
      foreground: "#2a1f14",
      card: "#ffffff",
      "card-foreground": "#2a1f14",
      surface: "#f5efe3",
      border: "#b08d3c",
      input: "#e6dbc4",
      ring: "#9e7e1e",
      error: "#b91c1c",
      "error-foreground": "#faf6ee",
      success: "#15803d",
      "success-foreground": "#faf6ee",
      warning: "#d97706",
      "warning-foreground": "#2a1f14",
      info: "#0f766e",
      "info-foreground": "#faf6ee",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#6c6d26",
      "series-2": "#0f766e",
      "series-3": "#861c2b",
      "series-4": "#3a5a8e",
      "series-5": "#6a3a7a",
      "series-6": "#3a5918",
      "series-7": "#a0522d",
      "series-8": "#685c7e",
      "diagram-ink": "#2a1f14",
      "diagram-label": "#6e5f46",
      "diagram-guide": "#a89878",
      "diagram-axis": "#4a3a24",
      "diagram-grid": "#e6dcc4",
      "diagram-highlight": "#7a5b01",
      "diagram-fill": "#f0e8d8"
    },
    radii: {
      none: "0px",
      sm: "3px",
      md: "6px",
      lg: "8px",
      xl: "10px",
      full: "9999px"
    },
    shadows: {
      main: "inset 0 0 0 1px rgba(176,141,60,0.35), 0 3px 10px rgba(139,115,85,0.28)",
      sm: "inset 0 0 0 1px rgba(176,141,60,0.3), 0 2px 4px rgba(139,115,85,0.22)",
      lg: "inset 0 0 0 1px rgba(176,141,60,0.4), 0 10px 30px rgba(139,115,85,0.36)",
      inner: "inset 0 2px 5px rgba(0,0,0,0.18)",
      none: "none",
      hover: "inset 0 0 0 1px rgba(176,141,60,0.5), 0 6px 18px rgba(201,168,76,0.35)",
      active: "inset 0 2px 4px rgba(0,0,0,0.25)"
    },
    typography: {
      "border-width": "3px",
      "border-width-thin": "3px",
      "border-width-thick": "4px",
      "tw-gold": "#9e7e1e",
      "tw-gold-mid": "#b8940f",
      "tw-gold-dim": "#d4c4a8",
      "tw-gold-dark": "#e6dbc4",
      "tw-text-primary": "#2a1f14",
      "tw-text-secondary": "#5a4a36",
      "tw-text-dim": "#8a7a60",
      "tw-surface-elevated": "#f0e8d8",
      "tw-surface-hover": "#e6dbc4",
      "tw-faction-resonator": "#7c3aed",
      "tw-faction-resonator-glow": "rgba(124, 58, 237, 0.15)",
      "tw-faction-dominion": "#b91c1c",
      "tw-faction-dominion-glow": "rgba(185, 28, 28, 0.15)",
      "tw-faction-neutral": "#78716c",
      "tw-tier-1": "#7a6340",
      "tw-tier-2": "#808080",
      "tw-tier-3": "#9e7e1e",
      "tw-tier-4": "#b8940f",
      "tw-health": "#b91c1c",
      "tw-health-bg": "rgba(185, 28, 28, 0.1)",
      "tw-heal": "#15803d",
      "tw-heal-bg": "rgba(21, 128, 61, 0.1)",
      "tw-mana": "#6d28d9",
      "tw-mana-bg": "rgba(109, 40, 217, 0.1)",
      "font-family": '"EB Garamond", Georgia, "Times New Roman", serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.55",
      "icon-color": "#9e7e1e",
      "transition-fast": "150ms",
      "transition-normal": "300ms",
      "transition-slow": "500ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.01",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "0",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#9e7e1e"
    },
    surface: {
      cardImage: "linear-gradient(180deg, rgba(201,168,76,0.12) 0%, rgba(139,115,85,0.07) 100%)"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3.5px",
      lineCap: "square",
      lineJoin: "bevel",
      dash: "8 4",
      dot: "1 4",
      fillStyle: "tint",
      fillOpacity: "0.28",
      roughness: "0",
      glow: "2",
      shadow: "0 2px 0 rgba(176, 141, 60, 0.6)",
      marker: "triangle",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "uppercase",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.4",
      materialMetalness: "0.7",
      materialFlat: "0",
      materialOutline: "1",
      lightAmbient: "0.4",
      lightKey: "1.2",
      lightKeyColor: "#ffe0a0",
      fog: "0.25"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#c9a84c",
        "primary-hover": "#eab308",
        "primary-foreground": "#1a1410",
        secondary: "#2a2118",
        "secondary-hover": "#3a2f22",
        "secondary-foreground": "#e8dcc8",
        accent: "#14b8a6",
        "accent-foreground": "#042f2e",
        muted: "#3a2f22",
        "muted-foreground": "#a89878",
        background: "#1a1410",
        foreground: "#e8dcc8",
        card: "#2a2118",
        "card-foreground": "#e8dcc8",
        surface: "#2a2118",
        border: "#c9a84c",
        input: "#3a2f22",
        ring: "#c9a84c",
        error: "#a03030",
        "error-foreground": "#e8dcc8",
        success: "#2a6a2a",
        "success-foreground": "#e8dcc8",
        warning: "#d97706",
        "warning-foreground": "#1a1410",
        info: "#0d6a63",
        "info-foreground": "#e8dcc8",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#acb458",
        "series-2": "#14b8a6",
        "series-3": "#e07070",
        "series-4": "#7aa0e0",
        "series-5": "#b88ad0",
        "series-6": "#84c572",
        "series-7": "#d99a6a",
        "series-8": "#a5a1c1",
        "diagram-ink": "#e8dcc8",
        "diagram-label": "#a89878",
        "diagram-guide": "#6e5f46",
        "diagram-axis": "#d8cdb4",
        "diagram-grid": "#3a2f22",
        "diagram-highlight": "#c9a84c",
        "diagram-fill": "#3a2f20"
      },
      typography: {
        "border-width": "3px",
        "border-width-thin": "3px",
        "border-width-thick": "4px",
        "tw-gold": "#9e7e1e",
        "tw-gold-mid": "#b8940f",
        "tw-gold-dim": "#d4c4a8",
        "tw-gold-dark": "#e6dbc4",
        "tw-text-primary": "#2a1f14",
        "tw-text-secondary": "#5a4a36",
        "tw-text-dim": "#8a7a60",
        "tw-surface-elevated": "#f0e8d8",
        "tw-surface-hover": "#e6dbc4",
        "tw-faction-resonator": "#7c3aed",
        "tw-faction-resonator-glow": "rgba(124, 58, 237, 0.15)",
        "tw-faction-dominion": "#b91c1c",
        "tw-faction-dominion-glow": "rgba(185, 28, 28, 0.15)",
        "tw-faction-neutral": "#78716c",
        "tw-tier-1": "#7a6340",
        "tw-tier-2": "#808080",
        "tw-tier-3": "#9e7e1e",
        "tw-tier-4": "#b8940f",
        "tw-health": "#b91c1c",
        "tw-health-bg": "rgba(185, 28, 28, 0.1)",
        "tw-heal": "#15803d",
        "tw-heal-bg": "rgba(21, 128, 61, 0.1)",
        "tw-mana": "#6d28d9",
        "tw-mana-bg": "rgba(109, 40, 217, 0.1)",
        "font-family": '"EB Garamond", Georgia, "Times New Roman", serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.55",
        "icon-color": "#c9a84c",
        "transition-fast": "150ms",
        "transition-normal": "300ms",
        "transition-slow": "500ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.01",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "0",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#c9a84c"
      },
      shadows: {
        main: "inset 0 0 0 1px rgba(201,168,76,0.4), 0 3px 10px rgba(0,0,0,0.5)",
        sm: "inset 0 0 0 1px rgba(201,168,76,0.35), 0 2px 4px rgba(0,0,0,0.45)",
        lg: "inset 0 0 0 1px rgba(201,168,76,0.45), 0 10px 30px rgba(0,0,0,0.6)",
        inner: "inset 0 2px 5px rgba(0,0,0,0.5)",
        none: "none",
        hover: "inset 0 0 0 1px rgba(201,168,76,0.6), 0 6px 18px rgba(201,168,76,0.3)",
        active: "inset 0 2px 4px rgba(0,0,0,0.55)"
      },
      typeScale: {
        displayFamily: '"Cinzel", "Palatino Linotype", Georgia, serif',
        bodyFamily: '"EB Garamond", Georgia, "Times New Roman", serif',
        monoFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        },
        fontImport: "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=EB+Garamond:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap",
        headingWeight: "700",
        headingTransform: "uppercase",
        headingTracking: "0.08em",
        headingStyle: "normal"
      },
      surface: {
        cardImage: "linear-gradient(180deg, rgba(201,168,76,0.08) 0%, rgba(0,0,0,0.12) 100%)"
      }
    }
  }
};

// themes/ukiyo-e.json
var ukiyo_e_default = {
  name: "ukiyo-e",
  displayName: "Ukiyo-e",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "30px",
      buttonHeightMd: "40px",
      buttonHeightLg: "48px",
      inputHeightSm: "30px",
      inputHeightMd: "40px",
      inputHeightLg: "48px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "18px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Zen Antique", "Kaisei Tokumin", serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "0.04em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Zen+Antique&family=Noto+Serif+JP:wght@400;500;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "400ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
        spring: "cubic-bezier(0.4, 0, 0.2, 1)"
      },
      shapes: {
        modalEnter: "translateX(-24px)",
        modalExit: "translateX(-24px)",
        pageEnter: "translateX(-16px)",
        popoverEnter: "translateX(-8px)",
        popoverExit: "translateX(-8px)",
        toastEnter: "translateX(24px)",
        toastExit: "translateX(24px)"
      }
    },
    iconography: {
      family: "tabler",
      strokeWidth: "2",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "2px",
      radiusInteractive: "1px",
      radiusPill: "2px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='20' viewBox='0 0 40 20'><g fill='none' stroke='%231b3a6b' stroke-opacity='0.07' stroke-width='1'><circle cx='20' cy='20' r='18'/><circle cx='20' cy='20' r='12'/><circle cx='20' cy='20' r='6'/><circle cx='0' cy='10' r='18'/><circle cx='0' cy='10' r='12'/><circle cx='0' cy='10' r='6'/><circle cx='40' cy='10' r='18'/><circle cx='40' cy='10' r='12'/><circle cx='40' cy='10' r='6'/></g></svg>"), url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='2'/><feColorMatrix values='0 0 0 0 .45 0 0 0 0 .35 0 0 0 0 .2 0 0 0 .10 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`,
      pageImageSize: "40px 20px, 240px 240px"
    },
    radii: {
      none: "0px",
      sm: "1px",
      md: "2px",
      lg: "2px",
      xl: "2px",
      full: "9999px"
    },
    colors: {
      primary: "#1b3a6b",
      "primary-hover": "#142d54",
      "primary-foreground": "#f7eed8",
      secondary: "#e4573d",
      "secondary-hover": "#d84a30",
      "secondary-foreground": "#1c0a05",
      accent: "#b2301a",
      "accent-foreground": "#fff8e7",
      muted: "#e6d9b8",
      "muted-foreground": "#54482c",
      background: "#f2e8cf",
      foreground: "#12203a",
      card: "#f8f0da",
      "card-foreground": "#12203a",
      surface: "#ecdfc0",
      border: "#111111",
      input: "#111111",
      ring: "#b2301a",
      "table-header": "#ecdfc0",
      "table-border": "#111111",
      "table-row-hover": "#f5ecd2",
      "surface-hover": "#ebddbc",
      "border-hover": "#b2301a",
      placeholder: "#716445",
      error: "#b3261e",
      "error-foreground": "#ffffff",
      success: "#12733f",
      "success-foreground": "#ffffff",
      warning: "#e9a72b",
      "warning-foreground": "#1a1200",
      info: "#1f5fbf",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#1b3a6b",
      "series-2": "#cc3c4f",
      "series-3": "#7a6a14",
      "series-4": "#367a6f",
      "series-5": "#8a3a5a",
      "series-6": "#c07a1a",
      "series-7": "#526393",
      "series-8": "#6f4b35",
      "diagram-ink": "#111111",
      "diagram-label": "#54482c",
      "diagram-guide": "#8a7a58",
      "diagram-axis": "#12203a",
      "diagram-grid": "#d9ccaa",
      "diagram-highlight": "#d05126",
      "diagram-fill": "#e4573d"
    },
    shadows: {
      main: "3px 3px 0 0 #111111",
      sm: "2px 2px 0 0 #111111",
      lg: "5px 5px 0 0 #111111",
      inner: "inset 1px 1px 0 0 rgba(17,17,17,0.2)",
      none: "none",
      hover: "4px 4px 0 0 #b2301a",
      active: "1px 1px 0 0 #111111"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Noto Serif JP", "Hiragino Mincho ProN", serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.6",
      "icon-color": "#1b3a6b",
      "transition-fast": "100ms",
      "transition-normal": "180ms",
      "transition-slow": "280ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "1px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#b2301a"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2.5px",
      strokeBold: "4px",
      lineCap: "round",
      lineJoin: "round",
      dash: "10 5",
      dot: "1 5",
      fillStyle: "solid",
      fillOpacity: "0.3",
      roughness: "0.6",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "500",
      labelCase: "none",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.9",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.75",
      lightKey: "0.7",
      lightKeyColor: "#fff0d0",
      fog: "0.15"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "30px",
        buttonHeightMd: "40px",
        buttonHeightLg: "48px",
        inputHeightSm: "30px",
        inputHeightMd: "40px",
        inputHeightLg: "48px",
        rowHeightCompact: "32px",
        rowHeightNormal: "40px",
        rowHeightSpacious: "48px",
        cardPaddingSm: "12px",
        cardPaddingMd: "18px",
        cardPaddingLg: "24px",
        dialogPadding: "24px",
        sectionGap: "32px"
      },
      typeScale: {
        displayFamily: '"Zen Antique", "Kaisei Tokumin", serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "400",
        headingTransform: "none",
        headingTracking: "0.04em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Zen+Antique&family=Noto+Serif+JP:wght@400;500;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "400ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
          spring: "cubic-bezier(0.4, 0, 0.2, 1)"
        },
        shapes: {
          modalEnter: "translateX(-24px)",
          modalExit: "translateX(-24px)",
          pageEnter: "translateX(-16px)",
          popoverEnter: "translateX(-8px)",
          popoverExit: "translateX(-8px)",
          toastEnter: "translateX(24px)",
          toastExit: "translateX(24px)"
        }
      },
      iconography: {
        family: "tabler",
        strokeWidth: "2",
        defaultSize: "16px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "var(--shadow-sm)",
        pressedElevation: "var(--shadow-active)"
      },
      geometry: {
        radiusContainer: "2px",
        radiusInteractive: "1px",
        radiusPill: "2px",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='20' viewBox='0 0 40 20'><g fill='none' stroke='%23f1e6c8' stroke-opacity='0.05' stroke-width='1'><circle cx='20' cy='20' r='18'/><circle cx='20' cy='20' r='12'/><circle cx='20' cy='20' r='6'/><circle cx='0' cy='10' r='18'/><circle cx='0' cy='10' r='12'/><circle cx='0' cy='10' r='6'/><circle cx='40' cy='10' r='18'/><circle cx='40' cy='10' r='12'/><circle cx='40' cy='10' r='6'/></g></svg>")`,
        pageImageSize: "40px 20px"
      },
      radii: {
        none: "0px",
        sm: "1px",
        md: "2px",
        lg: "2px",
        xl: "2px",
        full: "9999px"
      },
      colors: {
        primary: "#e4573d",
        "primary-hover": "#ee6a50",
        "primary-foreground": "#1c0a05",
        secondary: "#6f9ad6",
        "secondary-hover": "#86addf",
        "secondary-foreground": "#081428",
        accent: "#e8c35a",
        "accent-foreground": "#1a1200",
        muted: "#1d2c4a",
        "muted-foreground": "#c4cde0",
        background: "#101a2e",
        foreground: "#f1e6c8",
        card: "#172540",
        "card-foreground": "#f1e6c8",
        surface: "#14203a",
        border: "#e9dcbc",
        input: "#e9dcbc",
        ring: "#e8c35a",
        "table-header": "#14203a",
        "table-border": "#e9dcbc",
        "table-row-hover": "#1b2c4d",
        "surface-hover": "#20345a",
        "border-hover": "#e4573d",
        placeholder: "#8d98b4",
        error: "#ff8a80",
        "error-foreground": "#1a0505",
        success: "#4ede93",
        "success-foreground": "#04210f",
        warning: "#ffc24c",
        "warning-foreground": "#1a1200",
        info: "#8ab4ff",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#6f9ad6",
        "series-2": "#e4573d",
        "series-3": "#c19d2e",
        "series-4": "#6fc4b0",
        "series-5": "#d98aa8",
        "series-6": "#ed9a45",
        "series-7": "#a9afe9",
        "series-8": "#ceb181",
        "diagram-ink": "#f1e6c8",
        "diagram-label": "#c4cde0",
        "diagram-guide": "#7a86a8",
        "diagram-axis": "#f1e6c8",
        "diagram-grid": "#2a3a5a",
        "diagram-highlight": "#daca52",
        "diagram-fill": "#2a3a5e"
      },
      shadows: {
        main: "3px 3px 0 0 #000000",
        sm: "2px 2px 0 0 #000000",
        lg: "5px 5px 0 0 #000000",
        inner: "inset 1px 1px 0 0 rgba(0,0,0,0.4)",
        none: "none",
        hover: "4px 4px 0 0 #e4573d",
        active: "1px 1px 0 0 #000000"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Noto Serif JP", "Hiragino Mincho ProN", serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.6",
        "icon-color": "#e4573d",
        "transition-fast": "100ms",
        "transition-normal": "180ms",
        "transition-slow": "280ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "1px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#e8c35a"
      }
    }
  }
};

// themes/vaporwave.json
var vaporwave_default = {
  name: "vaporwave",
  displayName: "Vaporwave",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "46px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "46px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"VT323", "Space Mono", monospace',
      bodyFamily: '"DotGothic16", "Space Mono", monospace',
      monoFamily: '"Space Mono", ui-monospace, monospace',
      fontImport: "https://fonts.googleapis.com/css2?family=VT323&family=DotGothic16&family=Space+Mono:wght@400;700&display=swap",
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.12em",
      headingStyle: "normal",
      headingShadow: "1px 0 rgba(255,0,200,0.7), -1px 0 rgba(0,200,216,0.7)",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "18px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "15px",
          lineHeight: "24px"
        },
        lg: {
          size: "17px",
          lineHeight: "26px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "26px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "32px",
          lineHeight: "38px"
        },
        "4xl": {
          size: "40px",
          lineHeight: "44px"
        },
        "display-1": {
          size: "52px",
          lineHeight: "56px"
        },
        "display-2": {
          size: "68px",
          lineHeight: "72px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "normal"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "normal"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "100ms",
        normal: "180ms",
        slow: "300ms",
        dramatic: "500ms"
      },
      easings: {
        linear: "steps(3, end)",
        standard: "steps(3, end)",
        emphasized: "steps(3, end)",
        spring: "steps(3, end)"
      },
      shapes: {
        modalEnter: "scaleY(0.02)",
        modalExit: "scaleY(0.02)",
        pageEnter: "translateX(-16px)",
        popoverEnter: "scaleY(0.1)",
        popoverExit: "scaleY(0.1)",
        toastEnter: "translateX(24px)",
        toastExit: "translateX(24px)"
      }
    },
    iconography: {
      family: "tabler",
      strokeWidth: "2",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "2px",
      radiusInteractive: "0px",
      radiusPill: "2px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "linear-gradient(180deg, rgba(255, 150, 190, 0.35) 0%, transparent 60%), repeating-linear-gradient(0deg, rgba(106, 27, 209, 0.14) 0px, rgba(106, 27, 209, 0.14) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(106, 27, 209, 0.14) 0px, rgba(106, 27, 209, 0.14) 1px, transparent 1px, transparent 40px)",
      pageImageSize: "auto"
    },
    colors: {
      primary: "#c4008f",
      "primary-hover": "#a00074",
      "primary-foreground": "#ffffff",
      secondary: "#34d6e0",
      "secondary-hover": "#1cc2cc",
      "secondary-foreground": "#00262b",
      accent: "#6a1bd1",
      "accent-foreground": "#ffffff",
      muted: "#f3d4ec",
      "muted-foreground": "#5a2a6a",
      background: "#ffe4f3",
      foreground: "#2a0a4a",
      card: "#fff6fb",
      "card-foreground": "#2a0a4a",
      surface: "#ffeef8",
      border: "#c4008f",
      input: "#6a1bd1",
      ring: "#00a0aa",
      "table-header": "#ffeef8",
      "table-border": "#c4008f",
      "table-row-hover": "#ffd6ee",
      "surface-hover": "#ffd6ee",
      "border-hover": "#c4008f",
      placeholder: "#7a4a8a",
      error: "#c4002a",
      "error-foreground": "#ffffff",
      success: "#00703a",
      "success-foreground": "#ffffff",
      warning: "#ffb000",
      "warning-foreground": "#2a1800",
      info: "#0060c4",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#d741c6",
      "series-2": "#0090a0",
      "series-3": "#6a1bd1",
      "series-4": "#d65a00",
      "series-5": "#2a5fd6",
      "series-6": "#c4165a",
      "series-7": "#188973",
      "series-8": "#934bea",
      "diagram-ink": "#2a0a4a",
      "diagram-label": "#5a2a6a",
      "diagram-guide": "#a060b0",
      "diagram-axis": "#4a1a7a",
      "diagram-grid": "#e8b8e0",
      "diagram-highlight": "#c4008f",
      "diagram-fill": "#ffd0ec"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "2px",
      lg: "2px",
      xl: "4px",
      full: "2px"
    },
    shadows: {
      main: "0 0 14px rgba(196,0,143,.4), 0 0 2px rgba(196,0,143,.9)",
      sm: "0 0 6px rgba(196,0,143,.35)",
      lg: "0 0 24px rgba(196,0,143,.5), 0 0 4px rgba(196,0,143,.9)",
      inner: "inset 0 0 8px rgba(196,0,143,.25)",
      none: "none",
      hover: "0 0 20px rgba(0,183,194,.7), 0 0 3px rgba(0,183,194,1)",
      active: "inset 0 0 10px rgba(196,0,143,.5)"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"DotGothic16", "Space Mono", monospace',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.55",
      "icon-color": "#c4008f",
      "transition-fast": "100ms",
      "transition-normal": "180ms",
      "transition-slow": "300ms",
      "transition-timing": "steps(3, end)",
      "hover-scale": "1.0",
      "hover-translate-y": "-1px",
      "hover-translate-x": "1px",
      "active-scale": "0.98",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#00a0aa"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3px",
      lineCap: "round",
      lineJoin: "round",
      dash: "3 5",
      dot: "1 5",
      fillStyle: "tint",
      fillOpacity: "0.3",
      roughness: "0",
      glow: "8",
      shadow: "0 0 12px rgba(196, 0, 143, 0.5)",
      marker: "triangle",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "uppercase",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.2",
      materialMetalness: "0.5",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.5",
      lightKey: "1.2",
      lightKeyColor: "#ff9ae0",
      fog: "0.35"
    }
  },
  variants: {
    dark: {
      typeScale: {
        displayFamily: '"VT323", "Space Mono", monospace',
        bodyFamily: '"DotGothic16", "Space Mono", monospace',
        monoFamily: '"Space Mono", ui-monospace, monospace',
        fontImport: "https://fonts.googleapis.com/css2?family=VT323&family=DotGothic16&family=Space+Mono:wght@400;700&display=swap",
        headingWeight: "400",
        headingTransform: "uppercase",
        headingTracking: "0.12em",
        headingStyle: "normal",
        headingShadow: "1px 0 rgba(255,0,200,0.7), -1px 0 rgba(0,240,255,0.7)",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "18px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "15px",
            lineHeight: "24px"
          },
          lg: {
            size: "17px",
            lineHeight: "26px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "26px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "32px",
            lineHeight: "38px"
          },
          "4xl": {
            size: "40px",
            lineHeight: "44px"
          },
          "display-1": {
            size: "52px",
            lineHeight: "56px"
          },
          "display-2": {
            size: "68px",
            lineHeight: "72px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "normal"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "normal"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      surface: {
        pageImage: "linear-gradient(180deg, rgba(255, 61, 203, 0.28) 0%, transparent 60%), repeating-linear-gradient(0deg, rgba(0, 240, 255, 0.18) 0px, rgba(0, 240, 255, 0.18) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(0, 240, 255, 0.18) 0px, rgba(0, 240, 255, 0.18) 1px, transparent 1px, transparent 40px)",
        pageImageSize: "auto"
      },
      colors: {
        primary: "#ff3dcb",
        "primary-hover": "#ff70d8",
        "primary-foreground": "#1a0030",
        secondary: "#00f0ff",
        "secondary-hover": "#5cf6ff",
        "secondary-foreground": "#001a2a",
        accent: "#b388ff",
        "accent-foreground": "#1a0030",
        muted: "#3a1d7a",
        "muted-foreground": "#e0cffb",
        background: "#1a0b3a",
        foreground: "#f8e9ff",
        card: "#28125a",
        "card-foreground": "#f8e9ff",
        surface: "#220f4c",
        border: "#ff3dcb",
        input: "#00f0ff",
        ring: "#00f0ff",
        "table-header": "#220f4c",
        "table-border": "#ff3dcb",
        "table-row-hover": "#36197a",
        "surface-hover": "#36197a",
        "border-hover": "#ff3dcb",
        placeholder: "#a58fd0",
        error: "#ff4d6d",
        "error-foreground": "#2a0010",
        success: "#2bff9a",
        "success-foreground": "#002a14",
        warning: "#ffd60a",
        "warning-foreground": "#2a2000",
        info: "#4dc3ff",
        "info-foreground": "#001a2e",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#ff3dcb",
        "series-2": "#3af4d6",
        "series-3": "#b388ff",
        "series-4": "#ff9a5a",
        "series-5": "#5a8aff",
        "series-6": "#f98ceb",
        "series-7": "#26cc95",
        "series-8": "#b66dbf",
        "diagram-ink": "#f9dde5",
        "diagram-label": "#e0cffb",
        "diagram-guide": "#9a7ad0",
        "diagram-axis": "#f8e9ff",
        "diagram-grid": "#4a2a8a",
        "diagram-highlight": "#00f0ff",
        "diagram-fill": "#3a1a7a"
      },
      shadows: {
        main: "0 0 14px rgba(255,61,203,.55), 0 0 2px #ff3dcb",
        sm: "0 0 6px rgba(255,61,203,.5)",
        lg: "0 0 26px rgba(255,61,203,.6), 0 0 4px #ff3dcb",
        inner: "inset 0 0 8px rgba(0,240,255,.3)",
        none: "none",
        hover: "0 0 20px rgba(0,240,255,.75), 0 0 3px #00f0ff",
        active: "inset 0 0 10px rgba(255,61,203,.6)"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"DotGothic16", "Space Mono", monospace',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.55",
        "icon-color": "#00f0ff",
        "transition-fast": "100ms",
        "transition-normal": "180ms",
        "transition-slow": "300ms",
        "transition-timing": "steps(3, end)",
        "hover-scale": "1.0",
        "hover-translate-y": "-1px",
        "hover-translate-x": "1px",
        "active-scale": "0.98",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#00f0ff"
      }
    }
  }
};

// themes/victorian.json
var victorian_default = {
  name: "victorian",
  displayName: "Victorian",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "42px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "42px",
      inputHeightLg: "52px",
      rowHeightCompact: "36px",
      rowHeightNormal: "46px",
      rowHeightSpacious: "58px",
      cardPaddingSm: "16px",
      cardPaddingMd: "24px",
      cardPaddingLg: "32px",
      dialogPadding: "32px",
      sectionGap: "40px"
    },
    typeScale: {
      displayFamily: '"Playfair Display", "Libre Baskerville", Georgia, serif',
      bodyFamily: '"Libre Baskerville", Georgia, serif',
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontImport: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500;1,700&family=Libre+Baskerville:wght@400;700&display=swap",
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "0.005em",
      headingStyle: "italic",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "800ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.4, 0, 0.2, 1)",
        spring: "cubic-bezier(0.4, 0, 0.2, 1)"
      },
      shapes: {
        modalEnter: "translateY(-12px)",
        modalExit: "translateY(-12px)",
        pageEnter: "translateY(6px)",
        popoverEnter: "translateY(-6px)",
        popoverExit: "translateY(-6px)",
        toastEnter: "translateY(-12px)",
        toastExit: "translateY(-12px)"
      }
    },
    iconography: {
      family: "phosphor-duotone",
      strokeWidth: "1.5",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-sm)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      borderStyle: "ridge",
      borderStyleInteractive: "ridge"
    },
    colors: {
      primary: "#7a1f34",
      "primary-hover": "#5e1527",
      "primary-foreground": "#fbf1dc",
      secondary: "#1f4d33",
      "secondary-hover": "#173b27",
      "secondary-foreground": "#f2fbf5",
      accent: "#8a6a14",
      "accent-foreground": "#fff8e4",
      muted: "#eadfc2",
      "muted-foreground": "#54452a",
      background: "#f6ecd4",
      foreground: "#2a1a14",
      card: "#fbf3df",
      "card-foreground": "#2a1a14",
      surface: "#efe2c4",
      border: "#8a6a3a",
      input: "#8a6a3a",
      ring: "#1f4d33",
      "table-header": "#efe2c4",
      "table-border": "#8a6a3a",
      "table-row-hover": "#f6ecd4",
      "surface-hover": "#e6d6b0",
      "border-hover": "#7a1f34",
      placeholder: "#77653f",
      error: "#a3162a",
      "error-foreground": "#ffffff",
      success: "#1f6a3a",
      "success-foreground": "#ffffff",
      warning: "#d9a21b",
      "warning-foreground": "#2a1c00",
      info: "#27507a",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#943b5c",
      "series-2": "#16452b",
      "series-3": "#8a6a14",
      "series-4": "#2a4a7a",
      "series-5": "#5a2a6a",
      "series-6": "#a04a1a",
      "series-7": "#4f5926",
      "series-8": "#6a3a2a",
      "diagram-ink": "#2a1a14",
      "diagram-label": "#54452a",
      "diagram-guide": "#8a6a3a",
      "diagram-axis": "#3a2a1a",
      "diagram-grid": "#dccdaa",
      "diagram-highlight": "#70152c",
      "diagram-fill": "#e8d8b0"
    },
    radii: {
      none: "0px",
      sm: "4px",
      md: "6px",
      lg: "10px",
      xl: "14px",
      full: "9999px"
    },
    shadows: {
      main: "0 5px 14px rgba(42,26,20,0.24)",
      sm: "0 2px 5px rgba(42,26,20,0.2)",
      lg: "0 12px 30px rgba(42,26,20,0.3)",
      inner: "inset 0 2px 5px rgba(42,26,20,0.18)",
      none: "none",
      hover: "0 7px 18px rgba(42,26,20,0.3)",
      active: "0 1px 3px rgba(42,26,20,0.25)"
    },
    typography: {
      "border-width": "4px",
      "border-width-thin": "1px",
      "border-width-thick": "6px",
      "font-family": '"Libre Baskerville", Georgia, serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.7",
      "icon-color": "#7a1f34",
      "transition-fast": "200ms",
      "transition-normal": "360ms",
      "transition-slow": "600ms",
      "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
      "hover-scale": "1.0",
      "hover-translate-y": "-1px",
      "hover-translate-x": "0",
      "active-scale": "0.99",
      "active-translate-y": "1px",
      "focus-ring-width": "3px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#1f4d33"
    },
    surface: {
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><g fill='%237a1f34' opacity='.10'><path d='M48 10c6 10 14 12 14 22s-8 14-14 22c-6-8-14-12-14-22s8-12 14-22z'/><path d='M48 54c6 8 14 12 14 22s-8 12-14 20c-6-8-14-10-14-20s8-14 14-22z' opacity='.7'/><circle cx='0' cy='48' r='6'/><circle cx='96' cy='48' r='6'/></g><g fill='%23b08a2a' opacity='.12'><circle cx='48' cy='32' r='3'/><circle cx='48' cy='76' r='3'/></g></svg>")`,
      pageImageSize: "96px 96px"
    },
    diagram: {
      strokeThin: "0.75px",
      strokeNormal: "1.5px",
      strokeBold: "3px",
      lineCap: "round",
      lineJoin: "bevel",
      dash: "1 3 6 3",
      dot: "1 3",
      fillStyle: "tint",
      fillOpacity: "0.28",
      roughness: "0.15",
      glow: "0",
      shadow: "1px 2px 0 rgba(42, 26, 20, 0.45)",
      marker: "open",
      labelFont: "display",
      labelSize: "sm",
      labelWeight: "600",
      labelCase: "none",
      corner: "2px"
    },
    scene: {
      materialRoughness: "0.35",
      materialMetalness: "0.8",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.4",
      lightKey: "1.15",
      lightKeyColor: "#ffe2b0",
      fog: "0.2"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#e08aa0",
        "primary-hover": "#e9a3b4",
        "primary-foreground": "#2a0510",
        secondary: "#5fae82",
        "secondary-hover": "#7dc09a",
        "secondary-foreground": "#04180d",
        accent: "#e0bf6a",
        "accent-foreground": "#2a1c00",
        muted: "#33221f",
        "muted-foreground": "#dccdae",
        background: "#1e1210",
        foreground: "#f3e6c6",
        card: "#2a1a17",
        "card-foreground": "#f3e6c6",
        surface: "#241613",
        border: "#b08a4a",
        input: "#b08a4a",
        ring: "#e0bf6a",
        "table-header": "#241613",
        "table-border": "#b08a4a",
        "table-row-hover": "#311f1b",
        "surface-hover": "#3b2722",
        "border-hover": "#e08aa0",
        placeholder: "#ae9a78",
        error: "#ff8c98",
        "error-foreground": "#2a050a",
        success: "#7ed09a",
        "success-foreground": "#05190c",
        warning: "#f0c04a",
        "warning-foreground": "#2a1c00",
        info: "#8ab4e0",
        "info-foreground": "#05182e",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#d181a6",
        "series-2": "#56a579",
        "series-3": "#b99943",
        "series-4": "#7aa0e0",
        "series-5": "#c090d8",
        "series-6": "#e89a6a",
        "series-7": "#87b24c",
        "series-8": "#c38b7e",
        "diagram-ink": "#fdfccd",
        "diagram-label": "#dccdae",
        "diagram-guide": "#8a7048",
        "diagram-axis": "#e8d8b8",
        "diagram-grid": "#4a342a",
        "diagram-highlight": "#c2be66",
        "diagram-fill": "#4a2a28"
      },
      shadows: {
        main: "0 5px 14px rgba(0,0,0,0.5)",
        sm: "0 2px 5px rgba(0,0,0,0.45)",
        lg: "0 12px 30px rgba(0,0,0,0.55)",
        inner: "inset 0 2px 5px rgba(0,0,0,0.45)",
        none: "none",
        hover: "0 7px 18px rgba(0,0,0,0.55)",
        active: "0 1px 3px rgba(0,0,0,0.5)"
      },
      typography: {
        "border-width": "4px",
        "border-width-thin": "1px",
        "border-width-thick": "6px",
        "font-family": '"Libre Baskerville", Georgia, serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.7",
        "icon-color": "#e08aa0",
        "transition-fast": "200ms",
        "transition-normal": "360ms",
        "transition-slow": "600ms",
        "transition-timing": "cubic-bezier(0.4, 0, 0.2, 1)",
        "hover-scale": "1.0",
        "hover-translate-y": "-1px",
        "hover-translate-x": "0",
        "active-scale": "0.99",
        "active-translate-y": "1px",
        "focus-ring-width": "3px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#e0bf6a"
      },
      radii: {
        none: "0px",
        sm: "4px",
        md: "6px",
        lg: "10px",
        xl: "14px",
        full: "9999px"
      },
      surface: {
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><g fill='%23d99aa8' opacity='.10'><path d='M48 10c6 10 14 12 14 22s-8 14-14 22c-6-8-14-12-14-22s8-12 14-22z'/><path d='M48 54c6 8 14 12 14 22s-8 12-14 20c-6-8-14-10-14-20s8-14 14-22z' opacity='.7'/><circle cx='0' cy='48' r='6'/><circle cx='96' cy='48' r='6'/></g><g fill='%23e0bf6a' opacity='.12'><circle cx='48' cy='32' r='3'/><circle cx='48' cy='76' r='3'/></g></svg>")`,
        pageImageSize: "96px 96px"
      }
    }
  }
};

// themes/wabi-sabi.json
var wabi_sabi_default = {
  name: "wabi-sabi",
  displayName: "Wabi-Sabi",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "32px",
      buttonHeightMd: "44px",
      buttonHeightLg: "52px",
      inputHeightSm: "32px",
      inputHeightMd: "44px",
      inputHeightLg: "52px",
      rowHeightCompact: "36px",
      rowHeightNormal: "44px",
      rowHeightSpacious: "52px",
      cardPaddingSm: "16px",
      cardPaddingMd: "24px",
      cardPaddingLg: "32px",
      dialogPadding: "28px",
      sectionGap: "48px"
    },
    typeScale: {
      displayFamily: '"Shippori Mincho", "Zen Old Mincho", serif',
      bodyFamily: "var(--font-family)",
      monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      headingWeight: "400",
      headingTransform: "none",
      headingTracking: "0.06em",
      headingStyle: "normal",
      headingShadow: "none",
      fontImport: "https://fonts.googleapis.com/css2?family=Shippori+Mincho:wght@400;500&family=Zen+Kaku+Gothic+New:wght@300;400;500;700&display=swap",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "800ms"
      },
      easings: {
        linear: "linear",
        standard: "var(--transition-timing)",
        emphasized: "cubic-bezier(0.3, 0, 0.2, 1)",
        spring: "cubic-bezier(0.3, 0, 0.2, 1)"
      },
      shapes: {
        modalEnter: "scale(1.02)",
        modalExit: "scale(1.02)",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "phosphor-outline",
      strokeWidth: "1",
      defaultSize: "18px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "none",
      pressedElevation: "none"
    },
    geometry: {
      radiusContainer: "3px 7px 4px 6px",
      radiusInteractive: "2px 5px 3px 4px",
      radiusPill: "8px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "solid",
      borderStyleInteractive: "solid",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      backdrop: "none",
      cardImage: "none",
      pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='2'/><feColorMatrix values='0 0 0 0 .4 0 0 0 0 .35 0 0 0 0 .3 0 0 0 .10 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`,
      pageImageSize: "200px 200px"
    },
    radii: {
      none: "0px",
      sm: "2px",
      md: "4px",
      lg: "6px",
      xl: "8px",
      full: "9999px"
    },
    colors: {
      primary: "#8b4a32",
      "primary-hover": "#733c28",
      "primary-foreground": "#fbf6ea",
      secondary: "#b9c2a3",
      "secondary-hover": "#aab493",
      "secondary-foreground": "#2a3120",
      accent: "#7a5a12",
      "accent-foreground": "#fbf6ea",
      muted: "#e6dfcf",
      "muted-foreground": "#5d564a",
      background: "#f3eee2",
      foreground: "#2f2b26",
      card: "#f8f4ea",
      "card-foreground": "#2f2b26",
      surface: "#ebe5d6",
      border: "#b5ab98",
      input: "#8c8372",
      ring: "#6f7a52",
      "table-header": "#ebe5d6",
      "table-border": "#b5ab98",
      "table-row-hover": "#efe9da",
      "surface-hover": "#e9e2d1",
      "border-hover": "#8b4a32",
      placeholder: "#756d5f",
      error: "#a82d22",
      "error-foreground": "#ffffff",
      success: "#2f6b3a",
      "success-foreground": "#ffffff",
      warning: "#e0a82e",
      "warning-foreground": "#1a1200",
      info: "#3a5f8a",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#ab614f",
      "series-2": "#67713f",
      "series-3": "#7a5a12",
      "series-4": "#4a5a6a",
      "series-5": "#7a4a5a",
      "series-6": "#5f6d61",
      "series-7": "#a56d38",
      "series-8": "#2c645e",
      "diagram-ink": "#2f2b26",
      "diagram-label": "#5d564a",
      "diagram-guide": "#9a9080",
      "diagram-axis": "#3f3a32",
      "diagram-grid": "#d9d1bd",
      "diagram-highlight": "#884d26",
      "diagram-fill": "#b9c2a3"
    },
    shadows: {
      main: "0 2px 10px rgba(60,50,40,0.08)",
      sm: "0 1px 3px rgba(60,50,40,0.06)",
      lg: "0 8px 28px rgba(60,50,40,0.10)",
      inner: "inset 0 1px 2px rgba(60,50,40,0.08)",
      none: "none",
      hover: "0 3px 12px rgba(60,50,40,0.10)",
      active: "0 1px 2px rgba(60,50,40,0.08)"
    },
    typography: {
      "border-width": "1px",
      "border-width-thin": "1px",
      "border-width-thick": "2px",
      "font-family": '"Zen Kaku Gothic New", "Hiragino Sans", sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.02em",
      "line-height": "1.85",
      "icon-color": "#8b4a32",
      "transition-fast": "400ms",
      "transition-normal": "500ms",
      "transition-slow": "600ms",
      "transition-timing": "cubic-bezier(0.3, 0, 0.2, 1)",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#6f7a52"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2px",
      strokeBold: "3px",
      lineCap: "round",
      lineJoin: "round",
      dash: "9 7",
      dot: "1 6",
      fillStyle: "tint",
      fillOpacity: "0.2",
      roughness: "1.8",
      glow: "0",
      shadow: "none",
      marker: "open",
      labelFont: "body",
      labelSize: "sm",
      labelWeight: "400",
      labelCase: "none",
      corner: "4px"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "0",
      materialOutline: "0",
      lightAmbient: "0.7",
      lightKey: "0.7",
      lightKeyColor: "#f5e8d0",
      fog: "0.2"
    }
  },
  variants: {
    dark: {
      density: {
        spacing: {
          space0: "0px",
          space1: "4px",
          space2: "8px",
          space3: "12px",
          space4: "16px",
          space5: "20px",
          space6: "24px",
          space7: "28px",
          space8: "32px",
          space9: "36px",
          space10: "40px",
          space11: "44px",
          space12: "48px"
        },
        buttonHeightSm: "32px",
        buttonHeightMd: "44px",
        buttonHeightLg: "52px",
        inputHeightSm: "32px",
        inputHeightMd: "44px",
        inputHeightLg: "52px",
        rowHeightCompact: "36px",
        rowHeightNormal: "44px",
        rowHeightSpacious: "52px",
        cardPaddingSm: "16px",
        cardPaddingMd: "24px",
        cardPaddingLg: "32px",
        dialogPadding: "28px",
        sectionGap: "48px"
      },
      typeScale: {
        displayFamily: '"Shippori Mincho", "Zen Old Mincho", serif',
        bodyFamily: "var(--font-family)",
        monoFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        headingWeight: "400",
        headingTransform: "none",
        headingTracking: "0.06em",
        headingStyle: "normal",
        headingShadow: "none",
        fontImport: "https://fonts.googleapis.com/css2?family=Shippori+Mincho:wght@400;500&family=Zen+Kaku+Gothic+New:wght@300;400;500;700&display=swap",
        scale: {
          xs: {
            size: "12px",
            lineHeight: "16px"
          },
          sm: {
            size: "14px",
            lineHeight: "20px"
          },
          base: {
            size: "16px",
            lineHeight: "24px"
          },
          lg: {
            size: "18px",
            lineHeight: "28px"
          },
          xl: {
            size: "20px",
            lineHeight: "28px"
          },
          "2xl": {
            size: "24px",
            lineHeight: "32px"
          },
          "3xl": {
            size: "30px",
            lineHeight: "36px"
          },
          "4xl": {
            size: "36px",
            lineHeight: "40px"
          },
          "display-1": {
            size: "48px",
            lineHeight: "52px"
          },
          "display-2": {
            size: "60px",
            lineHeight: "64px"
          }
        },
        intents: {
          headingMajor: {
            slot: "display",
            size: "2xl",
            weight: "bold"
          },
          headingMinor: {
            slot: "display",
            size: "lg",
            weight: "bold"
          },
          bodyEmphasis: {
            slot: "body",
            size: "base",
            weight: "medium"
          },
          bodyDefault: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          bodyQuiet: {
            slot: "body",
            size: "sm",
            weight: "normal"
          },
          caption: {
            slot: "body",
            size: "xs",
            weight: "normal"
          },
          numeric: {
            slot: "mono",
            size: "sm",
            weight: "medium"
          }
        }
      },
      motion: {
        durations: {
          instant: "0ms",
          fast: "var(--transition-fast)",
          normal: "var(--transition-normal)",
          slow: "var(--transition-slow)",
          dramatic: "800ms"
        },
        easings: {
          linear: "linear",
          standard: "var(--transition-timing)",
          emphasized: "cubic-bezier(0.3, 0, 0.2, 1)",
          spring: "cubic-bezier(0.3, 0, 0.2, 1)"
        },
        shapes: {
          modalEnter: "scale(1.02)",
          modalExit: "scale(1.02)",
          pageEnter: "none",
          popoverEnter: "none",
          popoverExit: "none",
          toastEnter: "none",
          toastExit: "none"
        }
      },
      iconography: {
        family: "phosphor-outline",
        strokeWidth: "1",
        defaultSize: "18px"
      },
      elevation: {
        cardElevation: "var(--shadow-sm)",
        popoverElevation: "var(--shadow-main)",
        dialogElevation: "var(--shadow-lg)",
        toastElevation: "var(--shadow-main)",
        interactiveElevation: "none",
        pressedElevation: "none"
      },
      geometry: {
        radiusContainer: "3px 7px 4px 6px",
        radiusInteractive: "2px 5px 3px 4px",
        radiusPill: "8px",
        cornerShape: "round",
        cornerShapePill: "round",
        borderStyle: "solid",
        borderStyleInteractive: "solid",
        borderHairline: "1px",
        borderStandard: "var(--border-width)",
        borderHeavy: "var(--border-width-thick)"
      },
      surface: {
        backdrop: "none",
        cardImage: "none",
        pageImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='2'/><feColorMatrix values='0 0 0 0 .9 0 0 0 0 .85 0 0 0 0 .75 0 0 0 .06 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")`,
        pageImageSize: "200px 200px"
      },
      radii: {
        none: "0px",
        sm: "2px",
        md: "4px",
        lg: "6px",
        xl: "8px",
        full: "9999px"
      },
      colors: {
        primary: "#d0876a",
        "primary-hover": "#dc9b80",
        "primary-foreground": "#1d0f08",
        secondary: "#8f9a74",
        "secondary-hover": "#a1ac86",
        "secondary-foreground": "#1a1f10",
        accent: "#d4af5a",
        "accent-foreground": "#1d1500",
        muted: "#2e2b26",
        "muted-foreground": "#bdb5a4",
        background: "#1f1d1a",
        foreground: "#e9e2d3",
        card: "#262420",
        "card-foreground": "#e9e2d3",
        surface: "#2a2723",
        border: "#5a5448",
        input: "#8a8373",
        ring: "#9aa67c",
        "table-header": "#2a2723",
        "table-border": "#5a5448",
        "table-row-hover": "#2b2925",
        "surface-hover": "#33302b",
        "border-hover": "#d0876a",
        placeholder: "#8f8878",
        error: "#ff8a7a",
        "error-foreground": "#1a0505",
        success: "#6fcf8a",
        "success-foreground": "#04210f",
        warning: "#e8b94f",
        "warning-foreground": "#1a1200",
        info: "#8ab0dc",
        "info-foreground": "#071b3d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#d16554",
        "series-2": "#a8b888",
        "series-3": "#b1963c",
        "series-4": "#9ab0c4",
        "series-5": "#c89aa8",
        "series-6": "#b0c4bb",
        "series-7": "#ed9b45",
        "series-8": "#78ad96",
        "diagram-ink": "#e9e2d3",
        "diagram-label": "#bdb5a4",
        "diagram-guide": "#7a7262",
        "diagram-axis": "#d8d0bf",
        "diagram-grid": "#3a3630",
        "diagram-highlight": "#cf8964",
        "diagram-fill": "#3a3830"
      },
      shadows: {
        main: "0 2px 10px rgba(0,0,0,0.3)",
        sm: "0 1px 3px rgba(0,0,0,0.25)",
        lg: "0 8px 28px rgba(0,0,0,0.4)",
        inner: "inset 0 1px 2px rgba(0,0,0,0.3)",
        none: "none",
        hover: "0 3px 12px rgba(0,0,0,0.35)",
        active: "0 1px 2px rgba(0,0,0,0.3)"
      },
      typography: {
        "border-width": "1px",
        "border-width-thin": "1px",
        "border-width-thick": "2px",
        "font-family": '"Zen Kaku Gothic New", "Hiragino Sans", sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.02em",
        "line-height": "1.85",
        "icon-color": "#d0876a",
        "transition-fast": "400ms",
        "transition-normal": "500ms",
        "transition-slow": "600ms",
        "transition-timing": "cubic-bezier(0.3, 0, 0.2, 1)",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#9aa67c"
      }
    }
  }
};

// themes/win95.json
var win95_default = {
  name: "win95",
  displayName: "Windows 95",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "2px",
        space2: "5px",
        space3: "7px",
        space4: "10px",
        space5: "12px",
        space6: "14px",
        space7: "17px",
        space8: "19px",
        space9: "22px",
        space10: "24px",
        space11: "26px",
        space12: "29px"
      },
      buttonHeightSm: "20px",
      buttonHeightMd: "23px",
      buttonHeightLg: "30px",
      inputHeightSm: "20px",
      inputHeightMd: "23px",
      inputHeightLg: "30px",
      rowHeightCompact: "20px",
      rowHeightNormal: "24px",
      rowHeightSpacious: "30px",
      cardPaddingSm: "6px",
      cardPaddingMd: "8px",
      cardPaddingLg: "12px",
      dialogPadding: "12px",
      sectionGap: "16px"
    },
    typeScale: {
      displayFamily: 'Tahoma, "MS Sans Serif", "Segoe UI", system-ui, sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Courier New", "Lucida Console", ui-monospace, monospace',
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "0em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "10px",
          lineHeight: "13px"
        },
        sm: {
          size: "11px",
          lineHeight: "14px"
        },
        base: {
          size: "12px",
          lineHeight: "16px"
        },
        lg: {
          size: "13px",
          lineHeight: "17px"
        },
        xl: {
          size: "14px",
          lineHeight: "18px"
        },
        "2xl": {
          size: "16px",
          lineHeight: "20px"
        },
        "3xl": {
          size: "20px",
          lineHeight: "24px"
        },
        "4xl": {
          size: "24px",
          lineHeight: "28px"
        },
        "display-1": {
          size: "32px",
          lineHeight: "36px"
        },
        "display-2": {
          size: "40px",
          lineHeight: "44px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "0ms",
        normal: "0ms",
        slow: "0ms",
        dramatic: "0ms"
      },
      easings: {
        linear: "linear",
        standard: "linear",
        emphasized: "linear",
        spring: "linear"
      },
      shapes: {
        modalEnter: "none",
        modalExit: "none",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "fa-solid",
      strokeWidth: "1",
      defaultSize: "14px"
    },
    elevation: {
      cardElevation: "var(--shadow-main)",
      popoverElevation: "var(--shadow-lg)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-lg)",
      interactiveElevation: "var(--shadow-hover)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "0px",
      radiusInteractive: "0px",
      radiusPill: "0px",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "outset",
      borderStyleInteractive: "outset",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "none",
      diagram: "#ffffff"
    },
    colors: {
      primary: "#000080",
      "primary-hover": "#0000a8",
      "primary-foreground": "#ffffff",
      secondary: "#c0c0c0",
      "secondary-hover": "#dfdfdf",
      "secondary-foreground": "#000000",
      accent: "#800080",
      "accent-foreground": "#ffffff",
      muted: "#dfdfdf",
      "muted-foreground": "#404040",
      background: "#008080",
      foreground: "#ffffff",
      card: "#c0c0c0",
      "card-foreground": "#000000",
      surface: "#000080",
      border: "#808080",
      input: "#ffffff",
      ring: "#000080",
      "table-header": "#c0c0c0",
      "table-border": "#808080",
      "table-row-hover": "#d4d0c8",
      "surface-hover": "#d4d0c8",
      "border-hover": "#000080",
      placeholder: "#6e6e6e",
      error: "#b00000",
      "error-foreground": "#ffffff",
      success: "#006400",
      "success-foreground": "#ffffff",
      warning: "#ffff00",
      "warning-foreground": "#000000",
      info: "#000080",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.7)",
      "series-1": "#4f2cab",
      "series-2": "#8a0d29",
      "series-3": "#348830",
      "series-4": "#808000",
      "series-5": "#800080",
      "series-6": "#006a6a",
      "series-7": "#b4521e",
      "series-8": "#1f445d",
      "diagram-ink": "#000000",
      "diagram-label": "#000000",
      "diagram-guide": "#808080",
      "diagram-axis": "#000000",
      "diagram-grid": "#c0c0c0",
      "diagram-highlight": "#000080",
      "diagram-fill": "#c0c0c0"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "0px"
    },
    shadows: {
      main: "1px 1px 0 #000000, inset 1px 1px 0 #ffffff, inset -1px -1px 0 #808080",
      sm: "inset 1px 1px 0 #ffffff, inset -1px -1px 0 #808080",
      lg: "2px 2px 0 #000000, inset 1px 1px 0 #ffffff, inset -1px -1px 0 #808080",
      inner: "inset 1px 1px 0 #808080, inset -1px -1px 0 #ffffff",
      none: "none",
      hover: "inset 1px 1px 0 #ffffff, inset -1px -1px 0 #808080, 1px 1px 0 #000000",
      active: "inset 1px 1px 0 #808080, inset -1px -1px 0 #ffffff"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": 'Tahoma, "MS Sans Serif", "Segoe UI", system-ui, sans-serif',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0em",
      "line-height": "1.3",
      "icon-color": "#000080",
      "transition-fast": "0ms",
      "transition-normal": "0ms",
      "transition-slow": "0ms",
      "transition-timing": "linear",
      "hover-scale": "1",
      "hover-translate-y": "0px",
      "hover-translate-x": "0px",
      "active-scale": "1",
      "active-translate-y": "0px",
      "focus-ring-width": "1px",
      "focus-ring-offset": "-4px",
      "focus-ring-color": "#000080"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1px",
      strokeBold: "2px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "2 2",
      dot: "1 1",
      fillStyle: "solid",
      fillOpacity: "0.35",
      roughness: "0",
      glow: "0",
      shadow: "1px 1px 0 #000000",
      marker: "triangle",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "none",
      corner: "0"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "0",
      lightAmbient: "0.8",
      lightKey: "0.6",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#3a5bd9",
        "primary-hover": "#2c48bd",
        "primary-foreground": "#ffffff",
        secondary: "#3c3c3c",
        "secondary-hover": "#4d4d4d",
        "secondary-foreground": "#ffffff",
        accent: "#c050c0",
        "accent-foreground": "#000000",
        muted: "#2a2a2a",
        "muted-foreground": "#c8c8c8",
        background: "#003c3c",
        foreground: "#ffffff",
        card: "#2f2f2f",
        "card-foreground": "#ffffff",
        surface: "#2f2f2f",
        border: "#5a5a5a",
        input: "#101010",
        ring: "#9db4ff",
        "table-header": "#2f2f2f",
        "table-border": "#5a5a5a",
        "table-row-hover": "#3c3c3c",
        "surface-hover": "#3c3c3c",
        "border-hover": "#3a5bd9",
        placeholder: "#9a9a9a",
        error: "#e04040",
        "error-foreground": "#000000",
        success: "#40c060",
        "success-foreground": "#000000",
        warning: "#ffea00",
        "warning-foreground": "#000000",
        info: "#6a8cff",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#6a8aff",
        "series-2": "#fe6978",
        "series-3": "#7dec6c",
        "series-4": "#aebd05",
        "series-5": "#e070e0",
        "series-6": "#40e0e0",
        "series-7": "#ffa040",
        "series-8": "#999999",
        "diagram-ink": "#ffffff",
        "diagram-label": "#ffffff",
        "diagram-guide": "#a0a0a0",
        "diagram-axis": "#ffffff",
        "diagram-grid": "#5a5a5a",
        "diagram-highlight": "#cbf529",
        "diagram-fill": "#4a4a4a"
      },
      shadows: {
        main: "1px 1px 0 #000000, inset 1px 1px 0 #7a7a7a, inset -1px -1px 0 #1a1a1a",
        sm: "inset 1px 1px 0 #7a7a7a, inset -1px -1px 0 #1a1a1a",
        lg: "2px 2px 0 #000000, inset 1px 1px 0 #7a7a7a, inset -1px -1px 0 #1a1a1a",
        inner: "inset 1px 1px 0 #1a1a1a, inset -1px -1px 0 #7a7a7a",
        none: "none",
        hover: "inset 1px 1px 0 #7a7a7a, inset -1px -1px 0 #1a1a1a, 1px 1px 0 #000000",
        active: "inset 1px 1px 0 #1a1a1a, inset -1px -1px 0 #7a7a7a"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": 'Tahoma, "MS Sans Serif", "Segoe UI", system-ui, sans-serif',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0em",
        "line-height": "1.3",
        "icon-color": "#9db4ff",
        "transition-fast": "0ms",
        "transition-normal": "0ms",
        "transition-slow": "0ms",
        "transition-timing": "linear",
        "hover-scale": "1",
        "hover-translate-y": "0px",
        "hover-translate-x": "0px",
        "active-scale": "1",
        "active-translate-y": "0px",
        "focus-ring-width": "1px",
        "focus-ring-offset": "-4px",
        "focus-ring-color": "#9db4ff"
      },
      surface: {
        pageImage: "none",
        diagram: "#1c1c1c"
      }
    }
  }
};

// themes/wireframe.json
var wireframe_default = {
  name: "wireframe",
  displayName: "Wireframe",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "44px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "44px",
      rowHeightCompact: "32px",
      rowHeightNormal: "40px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "12px",
      cardPaddingMd: "16px",
      cardPaddingLg: "24px",
      dialogPadding: "24px",
      sectionGap: "32px"
    },
    typeScale: {
      displayFamily: '"Kalam", "Comic Sans MS", cursive',
      bodyFamily: '"Space Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      monoFamily: '"Space Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      },
      fontImport: "https://fonts.googleapis.com/css2?family=Kalam:wght@300;400;700&family=Space+Mono:wght@400;700&display=swap",
      headingWeight: "700",
      headingTransform: "none",
      headingTracking: "0",
      headingStyle: "normal",
      headingShadow: "none"
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "var(--transition-fast)",
        normal: "var(--transition-normal)",
        slow: "var(--transition-slow)",
        dramatic: "600ms"
      },
      easings: {
        linear: "linear",
        standard: "linear",
        emphasized: "linear",
        spring: "linear"
      },
      shapes: {
        modalEnter: "none",
        modalExit: "none",
        pageEnter: "none",
        popoverEnter: "none",
        popoverExit: "none",
        toastEnter: "none",
        toastExit: "none"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "2.5",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "0px",
      radiusInteractive: "0px",
      radiusPill: "var(--radius-full)",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "dashed",
      borderStyleInteractive: "dashed"
    },
    colors: {
      primary: "#000000",
      "primary-hover": "#333333",
      "primary-foreground": "#ffffff",
      secondary: "#ffffff",
      "secondary-hover": "#f0f0f0",
      "secondary-foreground": "#000000",
      accent: "#000000",
      "accent-foreground": "#ffffff",
      muted: "#f5f5f5",
      "muted-foreground": "#666666",
      background: "#ffffff",
      foreground: "#000000",
      card: "#ffffff",
      "card-foreground": "#000000",
      surface: "#f5f5f5",
      border: "#000000",
      input: "#000000",
      ring: "#000000",
      error: "#dc2626",
      "error-foreground": "#ffffff",
      success: "#15803d",
      "success-foreground": "#ffffff",
      warning: "#ca8a04",
      "warning-foreground": "#000000",
      info: "#2563eb",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#00001c",
      "series-2": "#3d3d3d",
      "series-3": "#244cb4",
      "series-4": "#a91326",
      "series-5": "#168c72",
      "series-6": "#6a1b9a",
      "series-7": "#b96601",
      "series-8": "#888888",
      "diagram-ink": "#000000",
      "diagram-label": "#666666",
      "diagram-guide": "#999999",
      "diagram-axis": "#000000",
      "diagram-grid": "#dddddd",
      "diagram-highlight": "#0066ff",
      "diagram-fill": "#ffffff"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      main: "4px 4px 0px 0px rgba(0, 0, 0, 1)",
      sm: "2px 2px 0px 0px rgba(0, 0, 0, 1)",
      lg: "6px 6px 0px 0px rgba(0, 0, 0, 1)",
      inner: "inset 2px 2px 0px 0px rgba(0, 0, 0, 0.1)",
      none: "none",
      hover: "6px 6px 0px 0px rgba(0, 0, 0, 1)",
      active: "1px 1px 0px 0px rgba(0, 0, 0, 1)"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Space Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      "font-weight-normal": "500",
      "font-weight-medium": "600",
      "font-weight-bold": "800",
      "letter-spacing": "0",
      "line-height": "1.55",
      "icon-color": "currentColor",
      "transition-fast": "50ms",
      "transition-normal": "100ms",
      "transition-slow": "200ms",
      "transition-timing": "linear",
      "hover-scale": "1",
      "hover-translate-y": "-2px",
      "hover-translate-x": "2px",
      "active-scale": "0.98",
      "active-translate-y": "2px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#000000"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "1px",
      strokeBold: "2px",
      lineCap: "butt",
      lineJoin: "miter",
      dash: "6 4",
      dot: "1 4",
      fillStyle: "outline",
      fillOpacity: "0.15",
      roughness: "0",
      glow: "0",
      shadow: "none",
      marker: "line",
      labelFont: "mono",
      labelSize: "xs",
      labelWeight: "400",
      labelCase: "none",
      corner: "0"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "1",
      lightAmbient: "0.9",
      lightKey: "0.3",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#ffffff",
        "primary-hover": "#e5e5e5",
        "primary-foreground": "#000000",
        secondary: "#1a1a1a",
        "secondary-hover": "#262626",
        "secondary-foreground": "#ffffff",
        accent: "#ffffff",
        "accent-foreground": "#000000",
        muted: "#262626",
        "muted-foreground": "#a3a3a3",
        background: "#111117",
        foreground: "#f5f5f5",
        card: "#1c1c22",
        "card-foreground": "#f5f5f5",
        surface: "#28282e",
        border: "#ffffff",
        input: "#ffffff",
        ring: "#ffffff",
        error: "#f87171",
        "error-foreground": "#000000",
        success: "#4ade80",
        "success-foreground": "#000000",
        warning: "#fbbf24",
        "warning-foreground": "#000000",
        info: "#60a5fa",
        "info-foreground": "#000000",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#fdf7c9",
        "series-2": "#a0bee1",
        "series-3": "#737dea",
        "series-4": "#f96f42",
        "series-5": "#07d0ab",
        "series-6": "#c58aff",
        "series-7": "#f09c38",
        "series-8": "#7f7f7f",
        "diagram-ink": "#ffffff",
        "diagram-label": "#a3a3a3",
        "diagram-guide": "#6a6a72",
        "diagram-axis": "#ffffff",
        "diagram-grid": "#33333a",
        "diagram-highlight": "#5a9bff",
        "diagram-fill": "#1c1c22"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Space Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        "font-weight-normal": "500",
        "font-weight-medium": "600",
        "font-weight-bold": "800",
        "letter-spacing": "0",
        "line-height": "1.55",
        "icon-color": "currentColor",
        "transition-fast": "50ms",
        "transition-normal": "100ms",
        "transition-slow": "200ms",
        "transition-timing": "linear",
        "hover-scale": "1",
        "hover-translate-y": "-2px",
        "hover-translate-x": "2px",
        "active-scale": "0.98",
        "active-translate-y": "2px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ffffff"
      },
      shadows: {
        main: "4px 4px 0px 0px rgba(255, 255, 255, 1)",
        sm: "2px 2px 0px 0px rgba(255, 255, 255, 1)",
        lg: "6px 6px 0px 0px rgba(255, 255, 255, 1)",
        inner: "inset 2px 2px 0px 0px rgba(255, 255, 255, 0.1)",
        none: "none",
        hover: "6px 6px 0px 0px rgba(255, 255, 255, 1)",
        active: "1px 1px 0px 0px rgba(255, 255, 255, 1)"
      }
    }
  }
};

// themes/zine.json
var zine_default = {
  name: "zine",
  displayName: "Punk Zine",
  tokens: {
    density: {
      spacing: {
        space0: "0px",
        space1: "4px",
        space2: "8px",
        space3: "12px",
        space4: "16px",
        space5: "20px",
        space6: "24px",
        space7: "28px",
        space8: "32px",
        space9: "36px",
        space10: "40px",
        space11: "44px",
        space12: "48px"
      },
      buttonHeightSm: "28px",
      buttonHeightMd: "36px",
      buttonHeightLg: "46px",
      inputHeightSm: "28px",
      inputHeightMd: "36px",
      inputHeightLg: "46px",
      rowHeightCompact: "30px",
      rowHeightNormal: "38px",
      rowHeightSpacious: "48px",
      cardPaddingSm: "10px",
      cardPaddingMd: "14px",
      cardPaddingLg: "20px",
      dialogPadding: "20px",
      sectionGap: "24px"
    },
    typeScale: {
      fontImport: "https://fonts.googleapis.com/css2?family=Anton&family=Special+Elite&display=swap",
      displayFamily: '"Anton", Impact, "Arial Narrow", sans-serif',
      bodyFamily: "var(--font-family)",
      monoFamily: '"Special Elite", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      headingWeight: "400",
      headingTransform: "uppercase",
      headingTracking: "0.04em",
      headingStyle: "normal",
      headingShadow: "none",
      scale: {
        xs: {
          size: "12px",
          lineHeight: "16px"
        },
        sm: {
          size: "14px",
          lineHeight: "20px"
        },
        base: {
          size: "16px",
          lineHeight: "24px"
        },
        lg: {
          size: "18px",
          lineHeight: "28px"
        },
        xl: {
          size: "20px",
          lineHeight: "28px"
        },
        "2xl": {
          size: "24px",
          lineHeight: "32px"
        },
        "3xl": {
          size: "30px",
          lineHeight: "36px"
        },
        "4xl": {
          size: "36px",
          lineHeight: "40px"
        },
        "display-1": {
          size: "48px",
          lineHeight: "52px"
        },
        "display-2": {
          size: "60px",
          lineHeight: "64px"
        }
      },
      intents: {
        headingMajor: {
          slot: "display",
          size: "2xl",
          weight: "bold"
        },
        headingMinor: {
          slot: "display",
          size: "lg",
          weight: "bold"
        },
        bodyEmphasis: {
          slot: "body",
          size: "base",
          weight: "medium"
        },
        bodyDefault: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        bodyQuiet: {
          slot: "body",
          size: "sm",
          weight: "normal"
        },
        caption: {
          slot: "body",
          size: "xs",
          weight: "normal"
        },
        numeric: {
          slot: "mono",
          size: "sm",
          weight: "medium"
        }
      }
    },
    motion: {
      durations: {
        instant: "0ms",
        fast: "80ms",
        normal: "120ms",
        slow: "200ms",
        dramatic: "240ms"
      },
      easings: {
        linear: "steps(2)",
        standard: "steps(2)",
        emphasized: "steps(3)",
        spring: "steps(2)"
      },
      shapes: {
        modalEnter: "rotate(1.5deg) scale(0.97)",
        modalExit: "rotate(1.5deg) scale(0.97)",
        pageEnter: "rotate(0.5deg) translateY(8px)",
        popoverEnter: "rotate(1deg)",
        popoverExit: "rotate(1deg)",
        toastEnter: "rotate(-2deg) translateY(16px)",
        toastExit: "rotate(-2deg) translateY(16px)"
      }
    },
    iconography: {
      family: "lucide",
      strokeWidth: "3",
      defaultSize: "16px"
    },
    elevation: {
      cardElevation: "var(--shadow-sm)",
      popoverElevation: "var(--shadow-main)",
      dialogElevation: "var(--shadow-lg)",
      toastElevation: "var(--shadow-main)",
      interactiveElevation: "var(--shadow-sm)",
      pressedElevation: "var(--shadow-active)"
    },
    geometry: {
      radiusContainer: "var(--radius-md)",
      radiusInteractive: "var(--radius-sm)",
      radiusPill: "var(--radius-full)",
      cornerShape: "round",
      cornerShapePill: "round",
      borderStyle: "dashed",
      borderStyleInteractive: "dashed",
      borderHairline: "1px",
      borderStandard: "var(--border-width)",
      borderHeavy: "var(--border-width-thick)"
    },
    surface: {
      pageImage: "radial-gradient(rgba(0, 0, 0, 0.16) 1px, transparent 1.5px)",
      pageImageSize: "6px 6px"
    },
    colors: {
      primary: "#0a0a0a",
      "primary-hover": "#333333",
      "primary-foreground": "#f5f5f0",
      secondary: "#dcdcd6",
      "secondary-hover": "#c8c8c0",
      "secondary-foreground": "#0a0a0a",
      accent: "#ff2d95",
      "accent-foreground": "#0a0a0a",
      muted: "#e4e4de",
      "muted-foreground": "#444444",
      background: "#f2f2ee",
      foreground: "#0a0a0a",
      card: "#ffffff",
      "card-foreground": "#0a0a0a",
      surface: "#ebebe5",
      border: "#0a0a0a",
      input: "#0a0a0a",
      ring: "#ff2d95",
      "table-header": "#ebebe5",
      "table-border": "#0a0a0a",
      "table-row-hover": "#e9e9e3",
      "surface-hover": "#e0e0d9",
      "border-hover": "#ff2d95",
      placeholder: "#666666",
      error: "#c00020",
      "error-foreground": "#ffffff",
      success: "#0a6b2e",
      "success-foreground": "#ffffff",
      warning: "#ffe600",
      "warning-foreground": "#0a0a0a",
      info: "#0040c0",
      "info-foreground": "#ffffff",
      scrim: "rgba(0, 0, 0, 0.6)",
      "series-1": "#180113",
      "series-2": "#d11fa6",
      "series-3": "#1f4fd6",
      "series-4": "#b36b00",
      "series-5": "#3e8c4b",
      "series-6": "#7a2aa8",
      "series-7": "#d4502d",
      "series-8": "#6a6a60",
      "diagram-ink": "#0a0a0a",
      "diagram-label": "#444444",
      "diagram-guide": "#888880",
      "diagram-axis": "#0a0a0a",
      "diagram-grid": "#dcdcd6",
      "diagram-highlight": "#ff2d95",
      "diagram-fill": "#ffe14d"
    },
    radii: {
      none: "0px",
      sm: "0px",
      md: "0px",
      lg: "0px",
      xl: "0px",
      full: "9999px"
    },
    shadows: {
      none: "none",
      sm: "2px 2px 0 0 #0a0a0a",
      main: "3px 3px 0 0 #0a0a0a",
      lg: "6px 6px 0 0 #0a0a0a",
      inner: "inset 2px 2px 0 0 rgba(0, 0, 0, 0.2)",
      hover: "5px 5px 0 0 #ff2d95",
      active: "1px 1px 0 0 #0a0a0a"
    },
    typography: {
      "border-width": "2px",
      "border-width-thin": "1px",
      "border-width-thick": "3px",
      "font-family": '"Special Elite", "Courier New", Courier, monospace',
      "font-weight-normal": "400",
      "font-weight-medium": "500",
      "font-weight-bold": "700",
      "letter-spacing": "0.01em",
      "line-height": "1.5",
      "icon-color": "currentColor",
      "transition-fast": "80ms",
      "transition-normal": "120ms",
      "transition-slow": "200ms",
      "transition-timing": "steps(2)",
      "hover-scale": "1",
      "hover-translate-y": "-2px",
      "hover-translate-x": "-2px",
      "active-scale": "1",
      "active-translate-y": "2px",
      "focus-ring-width": "2px",
      "focus-ring-offset": "2px",
      "focus-ring-color": "#ff2d95"
    },
    diagram: {
      strokeThin: "1px",
      strokeNormal: "2.5px",
      strokeBold: "4px",
      lineCap: "round",
      lineJoin: "bevel",
      dash: "6 6",
      dot: "1 5",
      fillStyle: "hatch",
      fillOpacity: "0.3",
      roughness: "1.1",
      glow: "0",
      shadow: "2px 2px 0 #0a0a0a",
      marker: "open",
      labelFont: "mono",
      labelSize: "sm",
      labelWeight: "700",
      labelCase: "uppercase",
      corner: "0"
    },
    scene: {
      materialRoughness: "1",
      materialMetalness: "0",
      materialFlat: "1",
      materialOutline: "2",
      lightAmbient: "0.8",
      lightKey: "0.6",
      lightKeyColor: "#ffffff",
      fog: "0"
    }
  },
  variants: {
    dark: {
      colors: {
        primary: "#f0f0ea",
        "primary-hover": "#cfcfc9",
        "primary-foreground": "#0d0d0d",
        secondary: "#2a2a2a",
        "secondary-hover": "#3a3a3a",
        "secondary-foreground": "#f0f0ea",
        accent: "#ff4da6",
        "accent-foreground": "#0d0d0d",
        muted: "#1f1f1f",
        "muted-foreground": "#c0c0ba",
        background: "#0d0d0d",
        foreground: "#f0f0ea",
        card: "#171717",
        "card-foreground": "#f0f0ea",
        surface: "#141414",
        border: "#f0f0ea",
        input: "#f0f0ea",
        ring: "#ff4da6",
        "table-header": "#141414",
        "table-border": "#f0f0ea",
        "table-row-hover": "#1c1c1c",
        "surface-hover": "#262626",
        "border-hover": "#ff4da6",
        placeholder: "#9a9a94",
        error: "#ff6b6b",
        "error-foreground": "#0d0d0d",
        success: "#4ede93",
        "success-foreground": "#0d0d0d",
        warning: "#ffe600",
        "warning-foreground": "#0d0d0d",
        info: "#7aa8ff",
        "info-foreground": "#0d0d0d",
        scrim: "rgba(0, 0, 0, 0.7)",
        "series-1": "#fcf0c3",
        "series-2": "#e5226c",
        "series-3": "#6a9aff",
        "series-4": "#ffb347",
        "series-5": "#98cf6a",
        "series-6": "#c58aff",
        "series-7": "#fc7f50",
        "series-8": "#989691",
        "diagram-ink": "#f0f0ea",
        "diagram-label": "#c0c0ba",
        "diagram-guide": "#808078",
        "diagram-axis": "#f0f0ea",
        "diagram-grid": "#3a3a3a",
        "diagram-highlight": "#ff4da6",
        "diagram-fill": "#3a3a3a"
      },
      shadows: {
        none: "none",
        sm: "2px 2px 0 0 #f0f0ea",
        main: "3px 3px 0 0 #f0f0ea",
        lg: "6px 6px 0 0 #f0f0ea",
        inner: "inset 2px 2px 0 0 rgba(255, 255, 255, 0.2)",
        hover: "5px 5px 0 0 #ff4da6",
        active: "1px 1px 0 0 #f0f0ea"
      },
      typography: {
        "border-width": "2px",
        "border-width-thin": "1px",
        "border-width-thick": "3px",
        "font-family": '"Special Elite", "Courier New", Courier, monospace',
        "font-weight-normal": "400",
        "font-weight-medium": "500",
        "font-weight-bold": "700",
        "letter-spacing": "0.01em",
        "line-height": "1.5",
        "icon-color": "currentColor",
        "transition-fast": "80ms",
        "transition-normal": "120ms",
        "transition-slow": "200ms",
        "transition-timing": "steps(2)",
        "hover-scale": "1",
        "hover-translate-y": "-2px",
        "hover-translate-x": "-2px",
        "active-scale": "1",
        "active-translate-y": "2px",
        "focus-ring-width": "2px",
        "focus-ring-offset": "2px",
        "focus-ring-color": "#ff4da6"
      },
      surface: {
        pageImage: "radial-gradient(rgba(255, 255, 255, 0.09) 1px, transparent 1.5px)",
        pageImageSize: "6px 6px"
      }
    }
  }
};

// src/themes/contrast.ts
var MIN_TEXT_CONTRAST = 4.5;
function colorVocabulary() {
  const out = /* @__PURE__ */ new Set();
  for (const preset of Object.values(THEME_PRESETS)) {
    for (const key of Object.keys(preset.tokens.colors ?? {})) out.add(key);
    for (const variant of Object.values(preset.variants ?? {})) for (const key of Object.keys(variant.colors ?? {})) out.add(key);
  }
  return out;
}
function contrastPairs(vocabulary) {
  const pairs = [
    ["background", "foreground"],
    ["surface", "foreground"]
  ];
  for (const key of vocabulary) if (vocabulary.has(`${key}-foreground`)) pairs.push([key, `${key}-foreground`]);
  return pairs;
}
function opaqueRgb(color) {
  const c = color.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(c);
  if (hex) {
    const h = hex[1].length === 3 ? [...hex[1]].map((d) => d + d).join("") : hex[1];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(c);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}
function luminance([r, g, b]) {
  const lin = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
function contrastRatio(a, b) {
  const ra = opaqueRgb(a);
  const rb = opaqueRgb(b);
  if (ra === null || rb === null) return null;
  const [hi, lo] = [luminance(ra), luminance(rb)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}
function themeOverrideIssues(override) {
  const vocabulary = colorVocabulary();
  const pairs = contrastPairs(vocabulary);
  const base = override.tokens?.colors ?? {};
  const layers = [
    { mode: "base", set: base, effective: base },
    ...Object.entries(override.variants ?? {}).map(([mode, v]) => ({ mode, set: v.colors ?? {}, effective: { ...base, ...v.colors ?? {} } }))
  ];
  const issues = [];
  for (const { mode, set: set2, effective } of layers) {
    for (const token of Object.keys(set2)) {
      if (!vocabulary.has(token)) issues.push({ token, mode, message: `"${token}" is not a theme color token` });
    }
    for (const [fill, text] of pairs) {
      if (!(fill in set2) && !(text in set2)) continue;
      const fillColor = effective[fill];
      const textColor = effective[text];
      if (fillColor === void 0 || textColor === void 0) {
        issues.push({ token: fill, mode, message: `${fill} and ${text} are read together \u2014 set both` });
        continue;
      }
      const ratio = contrastRatio(fillColor, textColor);
      if (ratio === null) {
        issues.push({ token: fill, mode, message: `${fill} (${fillColor}) and ${text} (${textColor}) must be opaque hex or rgb() colors` });
      } else if (ratio < MIN_TEXT_CONTRAST) {
        issues.push({ token: fill, mode, message: `${text} on ${fill} is ${ratio.toFixed(2)}:1, below ${MIN_TEXT_CONTRAST}:1` });
      }
    }
  }
  return issues;
}

// src/themes/index.ts
var THEME_PRESETS = {
  almadar: ThemeDefinitionSchema.parse(almadar_default),
  "almadar-heritage": ThemeDefinitionSchema.parse(almadar_heritage_default),
  "almadar-website": ThemeDefinitionSchema.parse(almadar_website_default),
  aqua: ThemeDefinitionSchema.parse(aqua_default),
  arabesque: ThemeDefinitionSchema.parse(arabesque_default),
  "art-deco": ThemeDefinitionSchema.parse(art_deco_default),
  "art-nouveau": ThemeDefinitionSchema.parse(art_nouveau_default),
  atelier: ThemeDefinitionSchema.parse(atelier_default),
  bauhaus: ThemeDefinitionSchema.parse(bauhaus_default),
  "bloomberg-dense": ThemeDefinitionSchema.parse(bloomberg_dense_default),
  blueprint: ThemeDefinitionSchema.parse(blueprint_default),
  clay: ThemeDefinitionSchema.parse(clay_default),
  comic: ThemeDefinitionSchema.parse(comic_default),
  constructivist: ThemeDefinitionSchema.parse(constructivist_default),
  corporate: ThemeDefinitionSchema.parse(corporate_default),
  cyberpunk: ThemeDefinitionSchema.parse(cyberpunk_default),
  "frutiger-aero": ThemeDefinitionSchema.parse(frutiger_aero_default),
  "game-adventure": ThemeDefinitionSchema.parse(game_adventure_default),
  "game-rpg": ThemeDefinitionSchema.parse(game_rpg_default),
  "game-sci-fi": ThemeDefinitionSchema.parse(game_sci_fi_default),
  "game-ui-pack": ThemeDefinitionSchema.parse(game_ui_pack_default),
  gazette: ThemeDefinitionSchema.parse(gazette_default),
  glass: ThemeDefinitionSchema.parse(glass_default),
  gothic: ThemeDefinitionSchema.parse(gothic_default),
  "ink-wash": ThemeDefinitionSchema.parse(ink_wash_default),
  kawaii: ThemeDefinitionSchema.parse(kawaii_default),
  kflow: ThemeDefinitionSchema.parse(kflow_default),
  kiosk: ThemeDefinitionSchema.parse(kiosk_default),
  "linear-clean": ThemeDefinitionSchema.parse(linear_clean_default),
  luxury: ThemeDefinitionSchema.parse(luxury_default),
  "mac-classic": ThemeDefinitionSchema.parse(mac_classic_default),
  material: ThemeDefinitionSchema.parse(material_default),
  memphis: ThemeDefinitionSchema.parse(memphis_default),
  "mid-century": ThemeDefinitionSchema.parse(mid_century_default),
  minimalist: ThemeDefinitionSchema.parse(minimalist_default),
  mudcloth: ThemeDefinitionSchema.parse(mudcloth_default),
  neon: ThemeDefinitionSchema.parse(neon_default),
  neumorphic: ThemeDefinitionSchema.parse(neumorphic_default),
  newsprint: ThemeDefinitionSchema.parse(newsprint_default),
  "notion-editorial": ThemeDefinitionSchema.parse(notion_editorial_default),
  orb: ThemeDefinitionSchema.parse(orb_default),
  pixel: ThemeDefinitionSchema.parse(pixel_default),
  prism: ThemeDefinitionSchema.parse(prism_default),
  retro: ThemeDefinitionSchema.parse(retro_default),
  risograph: ThemeDefinitionSchema.parse(risograph_default),
  scandi: ThemeDefinitionSchema.parse(scandi_default),
  sketch: ThemeDefinitionSchema.parse(sketch_default),
  solarpunk: ThemeDefinitionSchema.parse(solarpunk_default),
  steampunk: ThemeDefinitionSchema.parse(steampunk_default),
  swiss: ThemeDefinitionSchema.parse(swiss_default),
  terminal: ThemeDefinitionSchema.parse(terminal_default),
  "trait-wars": ThemeDefinitionSchema.parse(trait_wars_default),
  "ukiyo-e": ThemeDefinitionSchema.parse(ukiyo_e_default),
  vaporwave: ThemeDefinitionSchema.parse(vaporwave_default),
  victorian: ThemeDefinitionSchema.parse(victorian_default),
  "wabi-sabi": ThemeDefinitionSchema.parse(wabi_sabi_default),
  win95: ThemeDefinitionSchema.parse(win95_default),
  wireframe: ThemeDefinitionSchema.parse(wireframe_default),
  zine: ThemeDefinitionSchema.parse(zine_default)
};

export { A11Y_ATTRIBUTES, ACCESSIBLE_NAME_ATTRIBUTES, ACTION_OVERFLOW_TESTID, ACTION_TESTID_PREFIX, AGENT_DOMAIN_CATEGORIES, ALLOWED_CUSTOM_COMPONENTS, ANIMATION_NAMES, ANONYMOUS_USER, ARIA_CURRENT, ARIA_LIVE, ARIA_PRESSED, ARIA_ROLES, ASSET_ASPECTS, ASSET_DIMENSIONS, AgentDomainCategorySchema, AnimationDefSchema, AnimationNameSchema, AriaRoleSchema, AssetAspectSchema, AssetCatalogEntrySchema, AssetCatalogSchema, AssetDimensionSchema, AssetSchema, AudioManifestSchema, AwaitRenderSchema, BINDING_CONTEXT_RULES, BINDING_DOCS, BINDING_ROOTS, BehaviorDescriptionSchema, BehaviorRefSchema, BehaviorValueSchema, BindingSchema, BusEventSourceSchema, CALL_KINDS, CAMERA_MODES, COLOR_TOKEN_KEYS, COMPONENT_MAPPING, CONFIG_REF_EVENT_PATTERN, CORE_BINDINGS, CameraModeSchema, CameraSchema, CatalogEntrySchema, CatalogScopeSchema, ClientEffectTupleSchema, ClientNavigateBackTupleSchema, ClientNavigateTupleSchema, ClientRenderUITupleSchema, ColorSliceSchema, ColorTokensSchema, ComputedEventContractSchema, ComputedEventListenerSchema, ConfigFieldDeclarationSchema, ConfigProvenanceRecordSchema, CustomPatternDefinitionSchema, CustomPatternMapSchema, DEFAULT_INTERACTION_MODELS, DEFAULT_UNIT_ANIMATION_ROWS, DEFAULT_VIEWER, DIAGRAM_STROKE_WEIGHTS, DIAGRAM_TEXT_CASES, DIAGRAM_TEXT_SIZES, DIAGRAM_TOKEN_KEYS, DIAGRAM_TONES, DIAGRAM_TONE_COLOR_KEYS, DeclaredTraitConfigSchema, DeliveryRecordSchema, DensitySliceSchema, DensityTokensSchema, DescribeTargetSchema, DesignPreferencesSchema, DesignTokensSchema, DiagramFillStyleSchema, DiagramLabelCaseSchema, DiagramLabelFontSchema, DiagramLineCapSchema, DiagramLineJoinSchema, DiagramMarkerSchema, DiagramStrokeWeightSchema, DiagramTextCaseSchema, DiagramTextSizeSchema, DiagramTokensSchema, DiagramToneSchema, DispatchLogSchema, DispatchMemory, DispatchModeSchema, DomainCategorySchema, DomainContextSchema, DomainVocabularySchema, EFFECT_OPERATORS, EFFECT_OPERATOR_FAMILIES, EMPTY_STATE_MARKER, ENTER_ANIMATIONS, ENTER_DELAY_STEPS, ENTITY_FIELD_SCHEMA, ENTITY_ROLES, EVENT_CONTRACTS, EffectSchema, ElevationSliceSchema, ElevationTokensSchema, EmittedEventSchema, EnterAnimationSchema, EntityCallSchema, EntityFieldContractSchema, EntityFieldSchema, EntityIdSchema, EntityPersistenceSchema, EntityRefSchema, EntityRefStringSchema, EntityRoleSchema, EntityRowSchema, EntitySchema, EntitySemanticRoleSchema, EventDispatchRequestSchema, EventDispatchResponseSchema, EventIdSchema, EventListenerSchema, EventPayloadFieldSchema, EventPayloadSchema, EventPayloadValueSchema, EventSchema, EventScopeSchema, EventSemanticRoleSchema, EventSourceSchema, ExpectDeclarationSchema, ExpectationDiagnosticSchema, ExpressionSchema, FIELD_TYPES, FORM_PATTERN, FieldProjectionSchema, FieldSchema, FieldTypeSchema, FieldValueSchema, FileValueSchema, GameSubCategorySchema, GeometrySliceSchema, GeometryTokensSchema, GuardSchema, HOST_SIGN_IN_ROUTE, I18N_SECTIONS, ILLUSTRATION_TOKEN_KEYS, INDISTINCT_DELTA_E, INTEGRATORS_REGISTRY, IconFamilySchema, IconographySliceSchema, IconographyTokensSchema, IdentityLedgerSchema, IllustrationSliceSchema, IllustrationStyleSchema, IllustrationTokensSchema, InteractionModelSchema, JsonValueSchema, KNOWN_VALIDATION_ERROR_CODES, KnobFieldSchema, LANGUAGE_CODES, LIFECYCLE_EVENTS, LOADING_STATE_MARKER, LOG_SEVERITIES, LOLO_FIRST_TOKEN_KEYWORDS, LazyPageSchema, LedgerEntrySchema, LedgerKindSchema, ListenSourceSchema, LiveBroadcastItemSchema, MANIFEST_ASSET_LICENSES, MANIFEST_CANVAS_AFFINITIES, MANIFEST_ENTRY_KINDS, MANIFEST_SOURCE_CATALOGS, MIN_TEXT_CONTRAST, ManifestAssetLicenseSchema, ManifestCanvasAffinitySchema, ManifestEntryKindSchema, ManifestEntrySchema, ManifestFrameSpecSchema, ManifestSourceCatalogSchema, McpServiceDefSchema, MotionDurationKeySchema, MotionDurationPaletteSchema, MotionEasingKeySchema, MotionEasingPaletteSchema, MotionEntryTokensSchema, MotionIntentMapSchema, MotionIntentSchema, MotionShapeTokensSchema, MotionSliceSchema, MotionTokensSchema, MountSeedSchema, NATIVE_ID_PREFIX, NodeClassificationSchema, ORBITAL_VALUE_IMPORT_ALIAS, ORB_NOTATION_KEYS, OrbitalDefinitionSchema, OrbitalDescriptionSchema, OrbitalEntitySchema, OrbitalEventRequestSchema, OrbitalEventResponseSchema, OrbitalIdSchema, OrbitalOverridesSchema, OrbitalPageSchema, OrbitalPageStrictSchema, OrbitalRefObjectSchema, OrbitalRefStringSchema, OrbitalRegisterResponseSchema, OrbitalSchemaSchema, OrbitalTraitRefSchema, OrbitalUnitSchema, OrbitalValueSchema, OrbitalValueSiblingSchema, OrbitalValueUseSchema, OrbitalSchema as OrbitalZodSchema, OrganismExpectationsSchema, PATTERN_PROP_TYPE_ERROR_TESTID, PATTERN_REGISTRY, PATTERN_TYPES, PROP_KINDS, PageAccessSchema, PageIdSchema, PageIndexingSchema, PageMetaSchema, PageModifiersSchema, PageRefObjectSchema, PageRefSchema, PageRefStringSchema, PageSchema, PageTraitRefSchema, PaletteEntryIdSchema, PatternConfigSchema, PatternObjectSchema, PatternTypeSchema, PayloadFieldSchema, PayloadTypeWhenSchema, QUASIQUOTE_HEAD, QUOTE_HEAD, REFERENCE_CONFIG_TYPES, RENDERED_SLOTS, RENDER_BINDING_MARKER, RTL_LOCALES, RawUserClaimsSchema, RelatedLinkSchema, RelationConfigSchema, RequiredFieldSchema, ResolvedPatternPropsSchema, RestAuthConfigSchema, RestServiceDefSchema, SCENE_TOKEN_KEYS, SECRET_CONFIG_TYPES, SELF_OVERLAY_PATTERN_TYPES, SEMANTIC_CHANGE_KINDS, SEMANTIC_STRING_TYPES, SERVER_REPORTED_EFFECTS, SERVICE_TYPES, SExprAtomSchema, SExprDataSchema, SExprSchema, SHEET_PROJECTIONS, SKELETON_VARIANTS, SPRITE_DIRECTIONS, SPRITE_SHEET_LAYOUT, SURFACE_MODES, SYSTEM_UI_EVENTS, ScenePosSchema, SceneTokensSchema, SchemaMetadataSchema, SemanticAssetRefSchema, ServerBatchSummarySchema, ServerEffectResultSchema, ServerTopologySchema, ServiceDefinitionSchema, ServiceIdSchema, ServiceRefObjectSchema, ServiceRefSchema, ServiceRefStringSchema, ServiceTypeSchema, SheetProjectionSchema, SiteMetaSchema, SkeletonNodeSchema, SkeletonShapeSchema, SkeletonSpecSchema, SkeletonVariantSchema, SkinSpecSchema, SocketEventsSchema, SocketServiceDefSchema, SoundEntrySchema, SpacingScaleSchema, SpriteDirectionSchema, SpriteSheetAtlasSchema, StateMachineSchema, StateSchema, StateSemanticRoleSchema, SubTextureSchema, SuggestedGuardSchema, SurfaceSliceSchema, SurfaceTokensSchema, TAB_INDEX_VALUES, TEXT_DIRECTIONS, THEME_PRESETS, TRACE_ACTIVITY_TYPES, TRAIT_MOUNT_ERROR_TESTID, TYPE_POSITION_ONLY, TextureAtlasSchema, ThemeDefinitionSchema, ThemeIdSchema, ThemeRefSchema, ThemeRefStringSchema, ThemeTokensSchema, ThemeVariantSchema, TickIntervalSchema, TilesheetSchema, TraitCategorySchema, TraitConfigSchema, TraitConfigValueSchema, TraitDataEntitySchema, TraitDescriptionSchema, TraitEntityFieldSchema, TraitEventContractSchema, TraitEventListenerSchema, TraitFieldRefSchema, TraitIdSchema, TraitKnobSchema, TraitOverridesSchema, TraitRefSchema, TraitReferenceSchema, TraitSchema, TraitTickSchema, TraitUIBindingSchema, TraitValueSchema, TransitionSchema, TypeIntentMapSchema, TypeIntentSchema, TypeScaleEntrySchema, TypeScaleSchema, TypeScaleTokensSchema, TypeSizeKeySchema, TypeSliceSchema, TypeSlotSchema, TypeWeightSchema, UISlotSchema, UI_SLOTS, UNQUOTE_HEAD, UXHintsSchema, UseDeclarationSchema, UserPersonaSchema, VERIFICATION_DOM_ATTRS, VISUAL_STYLES, ViewTypeSchema, VisualStyleSchema, actionTestId, actionTestIdMatches, aliasMap, answerToMutations, answersToMutations, applyBehaviorOverrides, applyEventWiring, applyFactoryCallPlanMutation, applyListenPayloadMapping, applyOrbitalOverrides, applyRenderOverlay, applyTraitOverrides, asEntityId, asEventId, asOrbitalId, asPageId, asPaletteEntryId, asServiceId, asThemeId, asTraitId, assembleComposition, assertNoUnsatisfiableEnum, atomic, behaviorRef, bindsRecord, blockContractOf, buildEdgeCoveringWalk, buildGuardPayloads, buildLoloBehaviorContracts, buildRecommendationContext, buildReplayPaths, buildResolvedTraitConfigs, buildStateGraph, callService, categorizeRemovals, checkComposition, checkI18nCoverage, ciede2000, classifyWorkflow, clearSchemaCache, collectBindings, collectCallsiteCaptureChildren, collectEmbeddedTraitReferrers, collectForwardedConfigKeys, collectOrbitalForwardedConfigKeys, collectReachableStates, collectRenderUiPatternTypes, collectTraitConfigRefAdjacency, collectTraitEmbedAdjacency, colorDifference, comparePathSpecificity, component_mapping_default as componentMapping, composeBehaviors, compositionSpecOf, computeDispatchMode, computeTraitDispatchMode, configForwardTokens, configRefEventKnob, configReferencesCallsitePayload, constTruth, containsEntityBinding, containsPayloadBinding, contractFieldName, contrastRatio, coreTables, createAssetKey, createEmptyResolvedPage, createEmptyResolvedTrait, createLazyService, createResolvedField, createTypedEventBus, decodeQuoteBody, decomposeOrbital, defaultPage, defaultUnitAtlas, deliveryRecordValue, deref, deriveCollection, deriveId, deriveInputType, describeTensorMismatch, despawn, detectLayoutStrategy, detectPageContentReduction, diffFactoryCalls, diffOrbitalSchemas, diffSchemaSemantics, diffSchemas, dispatchScopeOf, dispatchVisitKey, doEffects, emit, encodeQuoteBody, entityAccessPolicies, entityAccessTable, event_contracts_default as eventContracts, eventKeyPropsOf, eventListPropsOf, expectedEntityName, externalInputsOf, extractPayloadFieldRef, factoryCallPlanOrbitalName, filterConfigToForwardedKeys, findCompatiblePatterns, findExternalInput, findInputByAddress, findPersonaInRoster, findService, fingerprintNode, followEventName, formatBehaviorValueLiteral, formatOrbitalValueLiteral, formatRecommendationsForPrompt, formatSExpr, formatTraitValueLiteral, gatherTensorLastDim, generatePatternDescription, generateQuestions, getAllPatternTypes, getArgs, getBindingExamples, getComponentForPattern, getDefaultAnimationsForRole, getEmittedEvents, getEntity, getEntityCardinality, getInteractionModelForDomain, getNestedValue, getOperator, getOrbAllowedPatterns, getOrbAllowedPatternsCompact, getOrbAllowedPatternsFiltered, getOrbAllowedPatternsSlim, getPage, getPages, getPatternActionsRef, getPatternDefinition, getPatternFieldsContract, getPatternMetadata, getPatternMinUsableWidths, getPatternPropsCompact, getPatternsGroupedByCategory, getRemovals, getSchemaCacheStats, getServiceNames, getTrait, getTraitConfig, getTraitName, getVocabulary, hasService, hasSignificantPageReduction, hexToLab, humanizeKey, idKindOf, idPrefix, inferConfigType, inferTsType, insertChildAtPath, instancesForLocale, instantiateQuasiquote, integrators_registry_default as integratorsRegistry, isAriaRole, isBehaviorRefValue, isBinding, isBrowserOnlyService, isCallRecord, isCallSiteConfigDeclaration, isCircuitEvent, isClientResident, isConfigFieldSchema, isConfigRedeclaration, isContentBodyPattern, isContentBodyPatternType, isDestructiveChange, isDrawHostPattern, isDrawablePattern, isEffect, isEmailValue, isEntityAwarePattern, isEntityCall, isEntityId, isEntityReference, isEntityReferenceAny, isEventId, isEventPayloadValue, isFieldValue, isFileValue, isHostSignInRoute, isImportedTraitRef, isInlineTrait, isJsonArray, isJsonObject, isJsonPrimitive, isKnownValidationErrorCode, isMainSlotRenderUi, isMcpService, isOrbitalDefinition, isOrbitalId, isOrbitalValue, isPageId, isPageReference, isPageReferenceObject, isPageReferenceString, isPaletteEntryId, isPersistBatchOperation, isPhoneValue, isPlanSnapshot, isReferenceConfigType, isRenderBindingMarker, isResolvedIR, isRestService, isRuntimeEntity, isSExpr, isSExprAtom, isSExprCall, isSExprEffect, isSecretConfigType, isSemanticStringType, isSemanticStringValue, isServiceId, isServiceReference, isServiceReferenceObject, isSessionHistoryEntry, isSocketService, isStaticallyFalse, isStructuredLogEntry, isSystemUiEvent, isTensorValue, isThemeId, isThemeReference, isThemeRegistryKey, isTraitFieldRef, isTraitId, isTraitValue, isUrlValue, isUuidValue, isValidBinding, isValidPatternType, isValueInputPattern, isWellFormedFontStack, joinEventAddress, languageOfLoloFirstToken, languageOfOrbTopLevelKeys, ledgerCurName, ledgerRename, ledgerResolveName, lexLolo, localeAlternates, localeDirection, localeOfPath, localizeLoloSource, localizeMap, localizeOrbValue, manifestToAssetCatalog, mapTensorLastDim, maskSecretConfigValues, matchAssetQuery, matchPath, matchPathAmong, mergeCallSiteConfigOverrides, mergeEntityFrame, mergeExpectationShape, mergeParams, mintId, nativeFieldId, nativePatternId, nativeRowId, nativeSlotId, navItemHref, navigate, navigateBack, navigatePatternPath, normalizeCallSiteConfigToValues, normalizeTraitRef, normalizeUserContext, omitFrameFields, orbitalFileOf, orbitalImportFromValue, orbitalIndexOfValidationPath, orbitalInlineEntities, orbitalTouchesOwnRecord, overrideDeclaredKnobs, pageModifiers, parseAssetKey, parseAssetQuery, parseBehaviorValueLiteral, parseBinding, parseEntityRef, parseI18nTables, parseImportedTraitRef, parseOperatorTables, parseOrbitalRef, parseOrbitalSchema, parseOrbitalValueLiteral, parsePageRef, parseProgram, parseServiceRef, parseTraitValueLiteral, pathLocale, pathMatchesPattern, patterns_registry_default as patternsRegistry, payloadIssues, payloadSchemaToJsonSchema, payloadTypeContainer, persist, persistenceModeAllowsOverrides, personaFromIdentityRow, quasiquoteExpr, quasiquoteOf, quoteBodyOf, quoteExpr, readableEntitiesOf, recommendPatterns, ref, registry, rehydrateKnobDefs, removeChildAtPath, renderRootOf, renderUI, renderUiEntriesOf, renderUiPatternTypesOf, replaceChildAtPath, reportIdenticalToEnglish, requiresConfirmation, resolveBlockContract, resolveConfigRefEventName, resolveDefaultViewer, resolveEntityView, resolvePageMeta, resolvePersonaSpec, safeParseOrbitalSchema, schemaToIR, selectTraceActivities, serializeLoloBehaviorContracts, set, setPropAtPath, sexpr, signatureToParamsSchema, sortKeysDeep, spawn, splitEventAddress, splitPatternChildPath, storesRowsInBrowser, summarizeOrbital, summarizeSchema, swap, tensorLastDimSize, tensorShape, themeDataKey, themeModes, themeOverrideIssues, toBindingRoot, toPayloadValue, toProgramData, toneColor, traceActivityPayload, traitDeclaresConfigForward, traitReferencesCallsitePayload, traitsEmbeddedByEffects, traitsEmbeddedByEvent, translateOverlaysToParams, unionOrganismConfigs, validateAssetAnimations, validateBindingInContext, validateContract, walkSExpr, walkStatePairs, watch, widenTier };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map